export const FRONTEND_DEVELOPER_ROADMAP = [
  {
    phase: 1,
    phaseKey: "foundation",
    title: "Foundation Phase - HTML & CSS Fundamentals",
    duration: "6 Weeks",
    level: "Beginner",
    weeks: [
      {
        week: 1,
        title: "HTML Basics & Semantic Structure",
        weekGoal: "Build strong foundations in HTML and semantic structure",
        weekOutcome: "Create a clean, semantic personal portfolio landing page",
        miniProject:
          "Personal Portfolio Landing Page – About, Skills, Projects & Contact sections using semantic HTML",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "HTML Document Structure",
            subTopics: [
              "DOCTYPE declaration",
              "html, head, body tags",
              "meta tags (charset, viewport)",
              "title tag",
              "How browsers read HTML",
            ],
            explanation:
              "Learn how an HTML document is structured and how browsers understand and render a webpage.",

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "HTML (HyperText Markup Language) is the backbone of every website. Think of it as the skeleton that gives structure to web pages. Just like a house needs a proper foundation and structure, every webpage needs a proper HTML structure.",

              sections: [
                {
                  title: "DOCTYPE Declaration",
                  description:
                    "The DOCTYPE tells the browser which version of HTML you're using. Always use <!DOCTYPE html> for HTML5.",
                  keyPoints: [
                    "Must be the very first line in your HTML file",
                    "Not case-sensitive but conventionally written in uppercase",
                    "Ensures the browser renders your page in standards mode",
                  ],
                  analogy:
                    "Think of DOCTYPE as the cover page of a book that tells you what type of book it is",
                },
                {
                  title: "HTML Root Element",
                  description:
                    "The <html> tag is the container for all other HTML elements (except DOCTYPE).",
                  keyPoints: [
                    "Always includes a lang attribute (e.g., lang='en' for English)",
                    "Helps screen readers and search engines understand the page language",
                    "Everything else goes inside this tag",
                  ],
                  code: "<html lang='en'>\n  <!-- All content goes here -->\n</html>",
                },
                {
                  title: "Head Section",
                  description:
                    "The <head> contains metadata about the document — information FOR the browser, not displayed ON the page.",
                  keyPoints: [
                    "Contains meta information, title, and links to CSS/JS files",
                    "Not visible to users but crucial for SEO and performance",
                    "Always comes before the <body> tag",
                  ],
                  commonElements: [
                    "<title> – Shows in browser tab",
                    "<meta charset='UTF-8'> – Character encoding",
                    "<meta name='viewport'> – Mobile responsiveness",
                    "<link> – External CSS files",
                    "<script> – JavaScript files",
                  ],
                },
                {
                  title: "Meta Tags",
                  description:
                    "Meta tags provide metadata about the HTML document and control how it behaves on different devices.",
                  keyPoints: [
                    "Charset ensures special characters display correctly",
                    "Viewport is critical for mobile responsiveness",
                    "Description helps with SEO and search previews",
                  ],
                  code: "<meta charset='UTF-8'>\n<meta name='viewport' content='width=device-width, initial-scale=1.0'>\n<meta name='description' content='My awesome website'>",
                },
                {
                  title: "Body Section",
                  description:
                    "The <body> contains all the visible content of your webpage.",
                  keyPoints: [
                    "Everything users see goes here (text, images, buttons, etc.)",
                    "Only ONE <body> tag is allowed per document",
                    "This is where your actual content lives",
                  ],
                },
                {
                  title: "How Browsers Read HTML",
                  description:
                    "Browsers read HTML from top to bottom and build the page step by step.",
                  process: [
                    "Browser receives HTML file from the server",
                    "Reads DOCTYPE to determine standards mode",
                    "Parses the <head> for metadata and resources",
                    "Builds the DOM (Document Object Model)",
                    "Renders visible content from the <body>",
                  ],
                  analogy:
                    "Like reading a book — the browser starts from the top and moves line by line",
                },
              ],

              visualStructure: `
HTML Document Structure:
┌─────────────────────────────┐
│ <!DOCTYPE html>             │ ← HTML5 declaration
├─────────────────────────────┤
│ <html lang="en">            │ ← Root element
│  ┌─────────────────────┐   │
│  │ <head>              │   │ ← Metadata (not visible)
│  │  - <title>          │   │
│  │  - <meta>           │   │
│  │  - <link>           │   │
│  └─────────────────────┘   │
│  ┌─────────────────────┐   │
│  │ <body>              │   │ ← Visible content
│  │  - <h1>             │   │
│  │  - <p>              │   │
│  │  - <img>            │   │
│  └─────────────────────┘   │
│ </html>                     │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting the DOCTYPE",
                  consequence:
                    "Browser may render the page in quirks mode causing layout issues",
                  fix: "Always start with <!DOCTYPE html>",
                },
                {
                  mistake: "Using multiple <body> tags",
                  consequence:
                    "Invalid HTML and unpredictable browser behavior",
                  fix: "Use only one <body> tag per document",
                },
                {
                  mistake: "Missing viewport meta tag",
                  consequence:
                    "Website will not scale properly on mobile devices",
                  fix: "Add <meta name='viewport' content='width=device-width, initial-scale=1.0'>",
                },
                {
                  mistake: "Placing visible content inside <head>",
                  consequence: "Content will not appear on the page",
                  fix: "Move visible content to the <body>",
                },
              ],

              proTips: [
                "Always write clean and indented HTML for readability",
                "Use meaningful page titles for SEO",
                "Never skip the viewport meta tag",
                "Group related meta tags together in <head>",
                "Lowercase tags are a best practice",
              ],

              realWorldAnalogy:
                "An HTML document is like a house:\n- DOCTYPE is the legal permit\n- <html> is the building\n- <head> is wiring and plumbing (invisible but essential)\n- <body> is the rooms and furniture (what people see)",
            },

            tasks: [
              "Create a basic HTML file from scratch",
              "Add correct DOCTYPE and structure",
              "Set page title and meta viewport",
            ],

            examples: [
              "<!DOCTYPE html>\n<html lang='en'>\n  <head>\n    <meta charset='UTF-8'>\n    <meta name='viewport' content='width=device-width, initial-scale=1.0'>\n    <title>My First Page</title>\n  </head>\n  <body>\n    <h1>Hello World</h1>\n    <p>This is my first webpage!</p>\n  </body>\n</html>",
            ],

            useCases: [
              "Starting any website",
              "Ensuring browser compatibility",
              "Creating mobile-responsive pages",
              "Improving SEO with proper structure",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<!DOCTYPE html> – Always the first line",
                "<html lang='en'> – Root element with language",
                "<head> – Metadata container",
                "<meta charset='UTF-8'> – Character encoding",
                "<meta name='viewport'> – Mobile responsiveness",
                "<title> – Browser tab title",
                "<body> – Visible content container",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "20–25 minutes",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-ready HTML Boilerplate",
                description:
                  "Create a clean, reusable HTML5 boilerplate that you can use for any future project.",
                requirements: [
                  "Correct DOCTYPE and lang attribute",
                  "All essential meta tags",
                  "Meaningful title and description",
                  "Clean indentation & comments",
                ],
                constraint:
                  "Do not copy-paste from the internet. Write everything from memory.",
                outcome:
                  "You’ll have a personal HTML starter template like real frontend engineers use.",
              },
              {
                id: "pt-2",
                title: "Mobile-first Structure Validation",
                description:
                  "Test how your HTML behaves on mobile devices and small screens.",
                steps: [
                  "Open the file in browser",
                  "Enable device toolbar in DevTools",
                  "Resize viewport and observe layout",
                ],
                outcome:
                  "Understand why viewport meta tag is non-negotiable in real projects.",
              },
            ],

            // 🧪 Testing & Quality Mindset
            testingInsight: {
              whyItMatters:
                "HTML structure bugs cause SEO issues, accessibility failures, and layout breakage even before CSS or JavaScript comes into play.",
              realWorldChecks: [
                "W3C HTML Validator for structural correctness",
                "Chrome Lighthouse for SEO & accessibility",
                "Browser DevTools DOM inspection",
              ],
              commonFinding:
                "Many candidates fail frontend interviews due to weak HTML fundamentals.",
            },

            // 🎤 Interview Preparation
            interviewInsights: {
              commonQuestions: [
                "Why is <!DOCTYPE html> important?",
                "What happens if the viewport meta tag is missing?",
                "Difference between <head> and <body>?",
                "How does a browser read and render HTML?",
              ],
              interviewerPerspective:
                "HTML questions are used to quickly judge frontend fundamentals.",
              tip: "Explain concepts using real-world analogies instead of definitions.",
            },

            // 🤖 Editor + AI Mentor Guidance
            editorGuidance: {
              starterHint:
                "Start with DOCTYPE, then build structure step by step: html → head → body.",
              aiMentorPrompts: [
                "Check if my HTML structure is correct",
                "Why is my page not responsive on mobile?",
                "Is my meta setup SEO-friendly?",
                "Explain this HTML structure like an interviewer",
              ],
              validationChecklist: [
                "DOCTYPE present",
                "Single <html>, <head>, <body>",
                "Viewport meta included",
                "Readable indentation",
              ],
            },
            // 🎯 Learning objectives
            objectives: [
              "Understand the purpose of HTML structural elements",
              "Create a valid HTML5 document",
              "Explain how browsers parse HTML",
              "Identify and avoid common structural mistakes",
            ],
          },
          {
            day: 2,
            topic: "Text Elements & Content Structuring",
            subTopics: [
              "Headings (h1–h6)",
              "Paragraphs",
              "span vs div",
              "Line breaks",
              "Content hierarchy",
            ],
            explanation:
              "Understand how to structure readable and meaningful content using proper HTML text elements.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Readable Article Layout",
                description:
                  "Create a blog-style article page using proper heading hierarchy and paragraphs.",
                requirements: [
                  "Single h1 for page title",
                  "At least 3 h2 sections",
                  "Each section must have related paragraphs",
                  "No skipped heading levels",
                ],
                constraint:
                  "Do not use CSS for layout or spacing — focus only on HTML structure.",
                outcome:
                  "You’ll learn how real content-heavy pages (blogs, docs) are structured.",
              },
              {
                id: "pt-2",
                title: "div vs span Practical Usage",
                description:
                  "Refactor a messy HTML snippet by replacing incorrect div/span usage.",
                steps: [
                  "Identify block-level vs inline content",
                  "Replace unnecessary divs with semantic grouping",
                  "Use span only for inline emphasis",
                ],
                outcome:
                  "Clear understanding of when and why to use div or span in production code.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Incorrect content hierarchy breaks screen readers, SEO ranking, and readability — even if the page looks fine visually.",
              realWorldChecks: [
                "Chrome Lighthouse accessibility audit",
                "Screen reader heading navigation",
                "SEO heading structure checks",
              ],
              commonFinding:
                "Many junior developers misuse headings as styling tools instead of structural tools.",
            },
            interviewInsights: {
              commonQuestions: [
                "Why should a page have only one h1?",
                "Difference between div and span?",
                "Why is skipping heading levels bad?",
                "When should <br> be used?",
              ],
              interviewerPerspective:
                "Content structuring questions help interviewers judge frontend fundamentals and accessibility awareness.",
              tip: "Always explain answers from a user and accessibility point of view, not just syntax.",
            },
            editorGuidance: {
              starterHint:
                "Start with the main idea (h1), then break content into sections using h2 and paragraphs.",
              aiMentorPrompts: [
                "Is my heading hierarchy correct?",
                "Did I misuse div or span anywhere?",
                "Can this content be read easily by a screen reader?",
                "Suggest improvements for content structure",
              ],
              validationChecklist: [
                "Only one h1 present",
                "No skipped heading levels",
                "Paragraphs grouped logically",
                "div and span used correctly",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Text elements are the most basic and most used parts of any webpage. Proper use of headings, paragraphs, and containers helps users read content easily and helps search engines understand the importance and hierarchy of information.",

              sections: [
                {
                  title: "Headings (h1–h6)",
                  description:
                    "Headings define the structure and hierarchy of your content. They tell both users and search engines what is important.",
                  keyPoints: [
                    "h1 is the most important heading (usually page title)",
                    "h6 is the least important heading",
                    "Headings should be used in order (h1 → h2 → h3)",
                    "Do NOT use headings just to make text bold or big",
                  ],
                  bestPractices: [
                    "Use only one h1 per page",
                    "Use h2 for main sections",
                    "Use h3–h6 for sub-sections",
                  ],
                  code: "<h1>My Blog</h1>\n<h2>About Me</h2>\n<h3>My Skills</h3>",
                },
                {
                  title: "Paragraphs (<p>)",
                  description:
                    "The <p> tag is used to represent blocks of text content.",
                  keyPoints: [
                    "Each paragraph should represent one idea",
                    "Browsers automatically add spacing before and after paragraphs",
                    "Avoid using <br> for spacing between paragraphs",
                  ],
                  code: "<p>I am a frontend developer.</p>\n<p>I love building clean user interfaces.</p>",
                },
                {
                  title: "div vs span",
                  description:
                    "<div> and <span> are container elements used to group content, but they behave differently.",
                  comparison: [
                    "div is a block-level element (takes full width)",
                    "span is an inline element (takes only required space)",
                    "div is used for layout and structure",
                    "span is used for styling small pieces of text",
                  ],
                  code: "<div class='card'>\n  <p>Hello <span class='highlight'>World</span></p>\n</div>",
                  analogy:
                    "div is like a box, span is like a label inside the box",
                },
                {
                  title: "Line Breaks (<br>)",
                  description:
                    "The <br> tag inserts a single line break inside text.",
                  keyPoints: [
                    "Used for line breaks, not spacing",
                    "Commonly used in addresses or poems",
                    "It is a self-closing tag",
                  ],
                  code: "Address:<br>Street 1<br>City<br>Country",
                },
                {
                  title: "Content Hierarchy",
                  description:
                    "Content hierarchy defines how information flows from most important to least important.",
                  keyPoints: [
                    "Headings create visual and semantic hierarchy",
                    "Paragraphs explain headings",
                    "Containers group related content",
                    "Good hierarchy improves readability and SEO",
                  ],
                  process: [
                    "Start with h1 for page topic",
                    "Use h2 for main sections",
                    "Add paragraphs under each heading",
                    "Group related items using div",
                  ],
                  analogy:
                    "Like a newspaper — headline first, then sections, then details",
                },
              ],

              visualStructure: `
Content Structure Example:
┌─────────────────────────────┐
│ <h1> Page Title </h1>        │
│  ┌─────────────────────┐   │
│  │ <h2> Section </h2>   │
│  │ <p> Paragraph </p>   │
│  │ <p> Paragraph </p>   │
│  └─────────────────────┘   │
│  ┌─────────────────────┐   │
│  │ <h2> Section </h2>   │
│  │ <div>                │
│  │   <span> Text </span>│
│  │ </div>               │
│  └─────────────────────┘   │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using headings only for styling",
                  consequence: "Poor accessibility and SEO",
                  fix: "Use headings for structure, style them with CSS",
                },
                {
                  mistake: "Skipping heading levels (h1 → h4)",
                  consequence: "Breaks content hierarchy",
                  fix: "Follow logical order of headings",
                },
                {
                  mistake: "Using <br> for spacing",
                  consequence: "Messy and unmaintainable layout",
                  fix: "Use CSS margins instead",
                },
                {
                  mistake: "Overusing divs",
                  consequence: "Unclear and bloated HTML",
                  fix: "Use divs only when grouping is needed",
                },
              ],

              proTips: [
                "Structure content first, style later",
                "Readable HTML improves long-term maintainability",
                "Search engines rely heavily on headings",
                "Use span for small inline styling only",
                "Clean hierarchy = better UX",
              ],

              realWorldAnalogy:
                "Think of content like a book:\n- Headings are chapter titles\n- Paragraphs are the content\n- div is a chapter container\n- span is highlighting a word or phrase",
            },

            tasks: [
              "Create a content section using headings and paragraphs",
              "Differentiate between div and span usage",
              "Structure content in logical order",
            ],

            examples: [
              "<h1>About Me</h1>\n<p>I am a frontend developer.</p>\n<div>\n  <span>Skills:</span> HTML, CSS\n</div>",
            ],

            useCases: [
              "Blog articles",
              "About pages",
              "Documentation pages",
              "Landing page content",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<h1> – Page title (only one)",
                "<h2>–<h6> – Section headings",
                "<p> – Paragraph text",
                "<div> – Block-level container",
                "<span> – Inline container",
                "<br> – Line break (use sparingly)",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use headings correctly to define hierarchy",
              "Differentiate between div and span",
              "Write readable and structured HTML content",
              "Avoid common text-structuring mistakes",
            ],
          },
          {
            day: 3,
            topic: "Links & Navigation",
            subTopics: [
              "Anchor tag (a)",
              "href attribute",
              "Internal vs external links",
              "Navigation menus",
              "Opening links in new tabs",
            ],
            explanation:
              "Learn how users move between pages using links and how to build navigation menus.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real Website Navigation Bar",
                description:
                  "Create a realistic top navigation menu for a multi-page website.",
                requirements: [
                  "Use <nav> tag",
                  "Minimum 4 links (Home, About, Services, Contact)",
                  "Internal links must work correctly",
                  "External link must open in new tab with security attributes",
                ],
                constraint:
                  "Do not use CSS or JavaScript. Focus purely on semantic HTML navigation.",
                outcome:
                  "You’ll understand how real websites structure navigation at HTML level.",
              },
              {
                id: "pt-2",
                title: "Single Page Anchor Navigation",
                description:
                  "Build a single-page navigation using section IDs and anchor links.",
                steps: [
                  "Create 3 sections with unique IDs",
                  "Add nav links pointing to those IDs",
                  "Test smooth jumping between sections",
                ],
                outcome:
                  "Clear understanding of how landing pages and documentation sites work.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Broken links, bad navigation, or unsafe external links immediately reduce user trust and SEO ranking.",
              realWorldChecks: [
                "Manual click testing for all links",
                "Keyboard navigation (Tab key)",
                "Lighthouse accessibility & best-practices audit",
                "Security check for target='_blank' links",
              ],
              commonFinding:
                "Many beginners forget rel='noopener noreferrer', which is a real-world security issue.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between internal and external links?",
                "Why should rel='noopener noreferrer' be used?",
                "What is the purpose of the <nav> tag?",
                "When should links open in a new tab?",
                "How do anchor links (#id) work?",
              ],
              interviewerPerspective:
                "Navigation questions help interviewers evaluate HTML semantics, UX sense, and security awareness.",
              tip: "Always explain answers from a user-experience and security perspective, not just syntax.",
            },
            editorGuidance: {
              starterHint:
                "First decide what pages or sections users need, then connect them using meaningful links.",
              aiMentorPrompts: [
                "Is my navigation semantically correct?",
                "Did I use target='_blank' safely?",
                "Are my link texts descriptive enough?",
                "Can this navigation be accessed using keyboard only?",
              ],
              validationChecklist: [
                "<nav> tag used",
                "No empty or '#' links",
                "External links secured with rel attribute",
                "Anchor IDs correctly mapped",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Links are the core of the web. Without links, the web would just be isolated pages. The anchor (<a>) tag allows users to navigate between pages, sections, and even different websites, creating a connected browsing experience.",

              sections: [
                {
                  title: "Anchor Tag (<a>)",
                  description:
                    "The <a> (anchor) tag is used to create hyperlinks in HTML.",
                  keyPoints: [
                    "The clickable text goes between opening and closing <a> tags",
                    "Links can point to pages, sections, files, or external websites",
                    "Anchor tags are inline elements by default",
                  ],
                  code: "<a href='https://example.com'>Visit Example</a>",
                  analogy:
                    "Think of an anchor tag as a door that takes users to another place",
                },
                {
                  title: "href Attribute",
                  description:
                    "The href (Hypertext Reference) attribute specifies the destination of the link.",
                  keyPoints: [
                    "Required for navigation",
                    "Can contain URLs, file paths, or section IDs",
                    "If href is missing, the link won’t navigate",
                  ],
                  examples: [
                    "href='https://google.com' → External website",
                    "href='about.html' → Internal page",
                    "href='#contact' → Section on same page",
                  ],
                  code: "<a href='about.html'>About Us</a>",
                },
                {
                  title: "Internal vs External Links",
                  description:
                    "Links can either navigate within your website or take users to other websites.",
                  comparison: [
                    "Internal links connect pages within the same website",
                    "External links connect to other websites",
                    "Internal links improve site navigation and SEO",
                    "External links often open in new tabs",
                  ],
                  code: "<!-- Internal -->\n<a href='services.html'>Services</a>\n\n<!-- External -->\n<a href='https://twitter.com'>Twitter</a>",
                },
                {
                  title: "Navigation Menus",
                  description:
                    "Navigation menus help users move between major sections of a website.",
                  keyPoints: [
                    "Usually built using <nav> tag",
                    "Contains multiple anchor tags",
                    "Appears consistently across pages",
                  ],
                  bestPractices: [
                    "Keep navigation simple and clear",
                    "Use meaningful link text",
                    "Place nav at top or side of page",
                  ],
                  code: "<nav>\n  <a href='index.html'>Home</a>\n  <a href='about.html'>About</a>\n  <a href='contact.html'>Contact</a>\n</nav>",
                },
                {
                  title: "Opening Links in New Tabs",
                  description:
                    "You can open links in a new browser tab using the target attribute.",
                  keyPoints: [
                    "target='_blank' opens link in new tab",
                    "Commonly used for external links",
                    "Improves user retention on your site",
                  ],
                  securityNote:
                    "Always use rel='noopener noreferrer' with target='_blank' for security.",
                  code: "<a href='https://google.com' target='_blank' rel='noopener noreferrer'>Google</a>",
                },
                {
                  title: "Linking Sections Using IDs",
                  description:
                    "Anchor links can navigate to specific sections on the same page using IDs.",
                  process: [
                    "Add an id to the target element",
                    "Use #id in href",
                    "Browser scrolls to that section",
                  ],
                  code: "<a href='#about'>Go to About</a>\n\n<section id='about'>\n  <h2>About Section</h2>\n</section>",
                  analogy: "Like jumping to a specific page number in a book",
                },
              ],

              visualStructure: `
Navigation Structure:
┌─────────────────────────────┐
│ <nav>                       │
│  <a> Home </a>              │
│  <a> About </a>             │
│  <a> Contact </a>           │
│ </nav>                      │
│                             │
│ <section id="about">        │
│  About content              │
│ </section>                  │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using empty or '#' links",
                  consequence: "Poor accessibility and confusing UX",
                  fix: "Always link to a valid destination",
                },
                {
                  mistake: "Opening all links in new tabs",
                  consequence: "Bad user experience",
                  fix: "Use new tabs mainly for external links",
                },
                {
                  mistake: "Non-descriptive link text (click here)",
                  consequence: "Poor SEO and accessibility",
                  fix: "Use meaningful link text",
                },
                {
                  mistake: "Forgetting rel attribute with target='_blank'",
                  consequence: "Potential security risk",
                  fix: "Always add rel='noopener noreferrer'",
                },
              ],

              proTips: [
                "Good navigation improves user trust",
                "Link text should describe the destination",
                "Internal links help SEO ranking",
                "Avoid broken links at all costs",
                "Use nav tag for semantic navigation",
              ],

              realWorldAnalogy:
                "Links are like roads:\n- Internal links are streets inside a city\n- External links are highways to other cities\n- Navigation menu is the city map",
            },

            tasks: [
              "Create internal and external links",
              "Build a basic navigation menu",
              "Link sections using anchor IDs",
            ],

            examples: [
              "<nav>\n  <a href='#about'>About</a>\n  <a href='#contact'>Contact</a>\n</nav>",
            ],

            useCases: [
              "Website navigation",
              "Landing pages",
              "Single-page websites",
              "Linking external resources",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<a> – Anchor tag",
                "href – Link destination",
                "target='_blank' – Open in new tab",
                "rel='noopener noreferrer' – Security",
                "<nav> – Navigation container",
                "id + #id – Section linking",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Create and use anchor links correctly",
              "Differentiate internal and external links",
              "Build simple navigation menus",
              "Apply best practices for link behavior and security",
            ],
          },
          {
            day: 4,
            topic: "Lists & Content Grouping",
            subTopics: [
              "Unordered lists (ul)",
              "Ordered lists (ol)",
              "List items (li)",
              "Description lists (dl, dt, dd)",
              "When to use which list",
            ],
            explanation:
              "Lists help present grouped or ordered information clearly and accessibly.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real Product Feature List",
                description:
                  "Create a feature section similar to a SaaS product landing page using lists.",
                requirements: [
                  "Use <ul> for features",
                  "Each feature must be a meaningful phrase",
                  "Do not use paragraphs for listing",
                ],
                constraint:
                  "No CSS styling allowed. Focus only on correct HTML structure.",
                outcome:
                  "You’ll understand how real product websites structure feature lists semantically.",
              },
              {
                id: "pt-2",
                title: "Step-by-Step User Guide",
                description:
                  "Build an ordered list explaining a user flow (e.g., signup process).",
                steps: [
                  "Choose a real-life process",
                  "Use <ol> to define exact steps",
                  "Ensure steps are logically ordered",
                ],
                outcome:
                  "Clear understanding of when order truly matters in content.",
              },
              {
                id: "pt-3",
                title: "Glossary Using Description List",
                description:
                  "Create a mini glossary using <dl>, <dt>, and <dd>.",
                requirements: [
                  "At least 3 terms",
                  "Each term must have a clear description",
                ],
                outcome:
                  "Learn when description lists are better than unordered lists.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Lists provide semantic meaning to screen readers and search engines. Incorrect list usage reduces accessibility and content clarity.",
              realWorldChecks: [
                "Screen reader list navigation",
                "Lighthouse accessibility audit",
                "HTML validation for <li> placement",
              ],
              commonFinding:
                "Many beginners misuse lists for visual spacing instead of semantic grouping.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between <ul> and <ol>?",
                "When should <dl> be used?",
                "Can <li> exist outside <ul> or <ol>?",
                "Are lists important for accessibility?",
              ],
              interviewerPerspective:
                "List-related questions reveal how well a candidate understands HTML semantics and real-world content structuring.",
              tip: "Always explain list choice based on meaning, not appearance.",
            },
            editorGuidance: {
              starterHint:
                "Ask yourself: does order matter? If yes, use <ol>. If not, use <ul>.",
              aiMentorPrompts: [
                "Did I choose the correct list type?",
                "Is my list semantically meaningful?",
                "Can this list be understood by a screen reader?",
                "Is my list over-nested?",
              ],
              validationChecklist: [
                "<li> only inside <ul> or <ol>",
                "Correct list type chosen",
                "No lists used purely for styling",
                "Nesting kept minimal and logical",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Lists are used when information belongs together. They improve readability, scannability, and accessibility by clearly grouping related items. From navigation menus to feature lists and step-by-step guides, lists are everywhere on the web.",

              sections: [
                {
                  title: "Unordered Lists (<ul>)",
                  description:
                    "Unordered lists display items where the order does not matter.",
                  keyPoints: [
                    "Items are displayed with bullets by default",
                    "Used when sequence is not important",
                    "Each item must be wrapped inside an <li> tag",
                  ],
                  commonUses: [
                    "Skills list",
                    "Features list",
                    "Navigation menus",
                  ],
                  code: "<ul>\n  <li>HTML</li>\n  <li>CSS</li>\n  <li>JavaScript</li>\n</ul>",
                  analogy: "Like a grocery list — order doesn’t matter",
                },
                {
                  title: "Ordered Lists (<ol>)",
                  description:
                    "Ordered lists display items in a specific sequence.",
                  keyPoints: [
                    "Items are numbered automatically",
                    "Use when order or steps matter",
                    "Supports different numbering styles via CSS",
                  ],
                  commonUses: [
                    "Step-by-step instructions",
                    "Tutorials",
                    "Processes and workflows",
                  ],
                  code: "<ol>\n  <li>Install dependencies</li>\n  <li>Write code</li>\n  <li>Test application</li>\n</ol>",
                  analogy: "Like a recipe — steps must be followed in order",
                },
                {
                  title: "List Items (<li>)",
                  description:
                    "The <li> tag represents an individual item inside a list.",
                  keyPoints: [
                    "Can only be used inside <ul> or <ol>",
                    "Can contain text, images, links, or even other lists",
                    "Supports nesting for sub-lists",
                  ],
                  code: "<ul>\n  <li>Frontend\n    <ul>\n      <li>HTML</li>\n      <li>CSS</li>\n    </ul>\n  </li>\n</ul>",
                },
                {
                  title: "Description Lists (<dl>, <dt>, <dd>)",
                  description:
                    "Description lists are used for terms and their descriptions.",
                  keyPoints: [
                    "<dl> wraps the entire list",
                    "<dt> defines the term",
                    "<dd> defines the description",
                  ],
                  commonUses: ["FAQs", "Glossaries", "Key-value data"],
                  code: "<dl>\n  <dt>HTML</dt>\n  <dd>Markup language for web pages</dd>\n\n  <dt>CSS</dt>\n  <dd>Styles web pages</dd>\n</dl>",
                  analogy: "Like a dictionary — word followed by meaning",
                },
                {
                  title: "Choosing the Right List",
                  description:
                    "Choosing the correct list type improves clarity and accessibility.",
                  guidelines: [
                    "Use <ul> when order does not matter",
                    "Use <ol> when sequence or ranking matters",
                    "Use <dl> for term-definition pairs",
                    "Avoid using lists only for styling",
                  ],
                },
              ],

              visualStructure: `
List Types Overview:
┌─────────────────────────────┐
│ <ul>                        │
│  • Item                     │
│  • Item                     │
│                             │
│ <ol>                        │
│  1. Step                    │
│  2. Step                    │
│                             │
│ <dl>                        │
│  Term                       │
│    Description              │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using paragraphs instead of lists",
                  consequence: "Poor readability and accessibility",
                  fix: "Use lists for grouped content",
                },
                {
                  mistake: "Using lists only for styling",
                  consequence: "Incorrect semantics",
                  fix: "Use CSS for styling, HTML for structure",
                },
                {
                  mistake: "Placing <li> outside <ul> or <ol>",
                  consequence: "Invalid HTML",
                  fix: "Always wrap <li> inside a list container",
                },
                {
                  mistake: "Over-nesting lists",
                  consequence: "Confusing structure",
                  fix: "Keep nesting minimal and meaningful",
                },
              ],

              proTips: [
                "Lists improve accessibility for screen readers",
                "Use lists to make content scannable",
                "Nested lists are powerful but should be used carefully",
                "Lists are great for SEO when used correctly",
                "Always choose the list type based on meaning, not appearance",
              ],

              realWorldAnalogy:
                "Think of lists like organizing drawers:\n- <ul> is a box of items\n- <ol> is a step-by-step checklist\n- <dl> is a label with explanation",
            },

            tasks: [
              "Create a skills list",
              "Build an ordered steps list",
              "Use description list for definitions",
            ],

            examples: ["<ul>\n  <li>HTML</li>\n  <li>CSS</li>\n</ul>"],

            useCases: [
              "Feature lists",
              "Steps or processes",
              "FAQs",
              "Navigation menus",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<ul> – Unordered list",
                "<ol> – Ordered list",
                "<li> – List item",
                "<dl> – Description list",
                "<dt> – Term",
                "<dd> – Description",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Differentiate between list types",
              "Use lists for grouped and ordered content",
              "Create nested and description lists",
              "Avoid common list-usage mistakes",
            ],
          },
          {
            day: 5,
            topic: "Images & Media",
            subTopics: [
              "img tag",
              "src and alt attributes",
              "figure & figcaption",
              "Relative vs absolute paths",
              "Basic accessibility for images",
            ],
            explanation:
              "Images enhance content but must be used correctly for performance and accessibility.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Accessible Profile Section",
                description:
                  "Build a profile section using an image with proper alt text and caption.",
                requirements: [
                  "Use <figure> and <figcaption>",
                  "Alt text must describe the image meaningfully",
                  "Image path must be relative",
                ],
                constraint:
                  "Do not use placeholder alt text like 'image' or 'photo'.",
                outcome:
                  "You’ll learn how real websites handle profile and author images accessibly.",
              },
              {
                id: "pt-2",
                title: "Image Accessibility Audit",
                description:
                  "Review a page with multiple images and improve their accessibility.",
                steps: [
                  "Identify meaningful vs decorative images",
                  "Write proper alt text for meaningful images",
                  "Use empty alt (alt='') for decorative images",
                ],
                outcome:
                  "Clear understanding of inclusive design practices used in production.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Images are one of the biggest reasons for slow websites and poor accessibility when used incorrectly.",
              realWorldChecks: [
                "Broken image detection in DevTools",
                "Screen reader image announcements",
                "Lighthouse accessibility and performance audit",
              ],
              commonFinding:
                "Most beginners add images without thinking about alt text or file size.",
            },
            interviewInsights: {
              commonQuestions: [
                "Why is alt attribute important?",
                "When should alt='' be used?",
                "Difference between relative and absolute image paths?",
                "What is the purpose of <figure> and <figcaption>?",
              ],
              interviewerPerspective:
                "Image-related questions help interviewers evaluate accessibility awareness and HTML semantics.",
              tip: "Always explain alt text from the perspective of a visually impaired user.",
            },
            editorGuidance: {
              starterHint:
                "Before adding an image, ask what role it plays: content or decoration?",
              aiMentorPrompts: [
                "Is my alt text meaningful?",
                "Should this image be decorative?",
                "Is my image path correct?",
                "Can this image be understood without seeing it?",
              ],
              validationChecklist: [
                "All images have alt attribute",
                "Decorative images use empty alt",
                "No broken image paths",
                "<figure> used where captions are needed",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Images make webpages more engaging and informative, but incorrect usage can harm performance, accessibility, and SEO. Understanding how to properly add images ensures your content is inclusive, fast, and meaningful.",

              sections: [
                {
                  title: "Image Tag (<img>)",
                  description:
                    "The <img> tag is used to embed images in a webpage.",
                  keyPoints: [
                    "<img> is a self-closing tag",
                    "It does not wrap content like other HTML tags",
                    "Requires at least src and alt attributes",
                  ],
                  code: "<img src='profile.jpg' alt='Profile photo' />",
                  analogy:
                    "Think of <img> as a frame that loads a picture from a source",
                },
                {
                  title: "src Attribute",
                  description:
                    "The src (source) attribute defines the path to the image file.",
                  keyPoints: [
                    "Can be a relative or absolute path",
                    "Browser fetches the image from this path",
                    "Incorrect src leads to broken images",
                  ],
                  examples: [
                    "src='images/photo.png' → Relative path",
                    "src='https://example.com/photo.png' → Absolute path",
                  ],
                },
                {
                  title: "alt Attribute",
                  description:
                    "The alt attribute provides alternative text for an image.",
                  keyPoints: [
                    "Displayed if image fails to load",
                    "Read by screen readers for visually impaired users",
                    "Important for accessibility and SEO",
                  ],
                  bestPractices: [
                    "Describe the image clearly",
                    "Avoid phrases like 'image of' or 'picture of'",
                    "Use empty alt (alt='') for decorative images",
                  ],
                  code: "<img src='logo.png' alt='Skilll.dev logo' />",
                },
                {
                  title: "Figure & Figcaption",
                  description:
                    "<figure> groups images with captions, and <figcaption> describes the image.",
                  keyPoints: [
                    "Improves semantic meaning",
                    "Caption is associated with the image",
                    "Useful for blogs, articles, and portfolios",
                  ],
                  code: "<figure>\n  <img src='profile.jpg' alt='My profile photo' />\n  <figcaption>Frontend Developer</figcaption>\n</figure>",
                  analogy:
                    "Like a photo in a newspaper with a caption below it",
                },
                {
                  title: "Relative vs Absolute Paths",
                  description:
                    "Paths define where the browser finds the image.",
                  comparison: [
                    "Relative paths depend on current file location",
                    "Absolute paths include full URL",
                    "Relative paths are preferred for internal assets",
                    "Absolute paths are used for external resources",
                  ],
                  code: "<!-- Relative -->\n<img src='images/avatar.png' alt='Avatar' />\n\n<!-- Absolute -->\n<img src='https://example.com/avatar.png' alt='Avatar' />",
                },
                {
                  title: "Basic Image Accessibility",
                  description:
                    "Accessible images ensure everyone can understand your content.",
                  keyPoints: [
                    "Always include meaningful alt text",
                    "Avoid text-heavy images",
                    "Do not rely on images alone to convey information",
                  ],
                  process: [
                    "Ask: What does this image communicate?",
                    "Write alt text conveying the same meaning",
                    "Skip alt text for purely decorative images",
                  ],
                },
              ],

              visualStructure: `
Image with Caption:
┌─────────────────────────────┐
│ <figure>                    │
│  ┌─────────────────────┐   │
│  │ <img />             │   │
│  └─────────────────────┘   │
│  <figcaption> Caption </figcaption>
│ </figure>                  │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Missing alt attribute",
                  consequence: "Poor accessibility and SEO",
                  fix: "Always add alt text to images",
                },
                {
                  mistake: "Using images for important text",
                  consequence: "Unreadable for screen readers",
                  fix: "Use real text styled with CSS",
                },
                {
                  mistake: "Incorrect image paths",
                  consequence: "Broken images",
                  fix: "Verify relative and absolute paths",
                },
                {
                  mistake: "Overusing large images",
                  consequence: "Slow page loading",
                  fix: "Optimize image size and format",
                },
              ],

              proTips: [
                "Use descriptive alt text for meaningful images",
                "Prefer modern formats like WebP when possible",
                "Keep image file sizes small",
                "Group images with captions using <figure>",
                "Test your site with images disabled",
              ],

              realWorldAnalogy:
                "Images are like illustrations in a book:\n- <img> is the illustration\n- alt text is the description for those who can’t see it\n- <figcaption> is the caption below the image",
            },

            tasks: [
              "Add profile image to page",
              "Use alt text properly",
              "Group image with caption",
            ],

            examples: [
              "<figure>\n  <img src='profile.jpg' alt='My profile photo' />\n  <figcaption>Frontend Developer</figcaption>\n</figure>",
            ],

            useCases: [
              "Profile sections",
              "Blog images",
              "Product images",
              "Portfolio websites",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<img> – Image element",
                "src – Image path",
                "alt – Alternative text",
                "<figure> – Image container",
                "<figcaption> – Image caption",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Add images correctly using HTML",
              "Write meaningful alt text for accessibility",
              "Differentiate relative and absolute paths",
              "Use figure and figcaption for semantic grouping",
            ],
          },
          {
            day: 6,
            topic: "Semantic HTML5 Elements",
            subTopics: [
              "header, footer",
              "main, section, article",
              "nav",
              "Why semantic HTML matters",
              "SEO & accessibility benefits",
            ],
            explanation:
              "Semantic HTML gives meaning to content, improving SEO and accessibility.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Semantic Page Refactor",
                description:
                  "Refactor a div-based layout into a fully semantic HTML5 structure.",
                requirements: [
                  "Replace header, footer, nav, main, section, article correctly",
                  "Only one <main> element",
                  "Each <section> must have a heading",
                ],
                constraint:
                  "Do not change content text. Only improve structure using semantic tags.",
                outcome:
                  "You’ll experience how real teams refactor legacy HTML for accessibility and SEO.",
              },
              {
                id: "pt-2",
                title: "Blog Layout Using Semantic Elements",
                description:
                  "Create a blog page layout using header, main, article, and footer.",
                steps: [
                  "Wrap page intro inside <header>",
                  "Place blog post inside <article>",
                  "Group related content using <section>",
                ],
                outcome:
                  "Clear understanding of when content is reusable and self-contained.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Semantic HTML directly impacts accessibility tree creation and how search engines understand page structure.",
              realWorldChecks: [
                "Screen reader landmark navigation",
                "Lighthouse accessibility & SEO audit",
                "HTML outline inspection",
              ],
              commonFinding:
                "Many beginners overuse <section> and still miss meaningful structure.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between <section> and <article>?",
                "Can a page have multiple <header> or <footer>?",
                "Why is only one <main> allowed?",
                "When should you still use <div>?",
              ],
              interviewerPerspective:
                "Semantic HTML questions help interviewers judge accessibility awareness and real-world frontend maturity.",
              tip: "Answer by explaining intent and meaning, not tag definitions.",
            },
            editorGuidance: {
              starterHint:
                "Ask: does this content have meaning on its own? If yes, consider <article>.",
              aiMentorPrompts: [
                "Did I choose the correct semantic element?",
                "Is my page structure logical for screen readers?",
                "Am I overusing <section>?",
                "Where should <div> still be used?",
              ],
              validationChecklist: [
                "Only one <main> element",
                "All sections have headings",
                "Navigation wrapped inside <nav>",
                "Minimal unnecessary div usage",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Semantic HTML elements clearly describe their meaning to both browsers and developers. Instead of using generic <div> tags everywhere, semantic tags explain the purpose of content, making websites more accessible, SEO-friendly, and easier to maintain.",

              sections: [
                {
                  title: "What is Semantic HTML?",
                  description:
                    "Semantic HTML uses meaningful tags that describe the role of the content.",
                  keyPoints: [
                    "Semantic tags describe what the content is",
                    "They replace excessive use of <div>",
                    "They improve readability of code",
                  ],
                  comparison: [
                    "<div class='header'> → Non-semantic",
                    "<header> → Semantic",
                  ],
                  analogy: "Like using proper labels instead of unnamed boxes",
                },
                {
                  title: "Header & Footer",
                  description:
                    "<header> represents introductory content, and <footer> represents concluding content.",
                  keyPoints: [
                    "<header> can contain logo, title, navigation",
                    "<footer> contains copyright, links, legal info",
                    "Multiple headers and footers are allowed",
                  ],
                  code: "<header>\n  <h1>My Website</h1>\n</header>\n\n<footer>\n  <p>© 2025</p>\n</footer>",
                  bestPractices: [
                    "Do not confuse <header> with <head>",
                    "Footer is not just the bottom of the page",
                  ],
                },
                {
                  title: "Main Element",
                  description:
                    "<main> represents the primary content of the page.",
                  keyPoints: [
                    "Only ONE <main> per page",
                    "Excludes headers, footers, sidebars",
                    "Helps screen readers jump to main content",
                  ],
                  code: "<main>\n  <h2>Main Content</h2>\n</main>",
                },
                {
                  title: "Section Element",
                  description:
                    "<section> groups related content with a common theme.",
                  keyPoints: [
                    "Should usually have a heading",
                    "Used to divide content logically",
                    "Better than div when content has meaning",
                  ],
                  code: "<section>\n  <h2>Features</h2>\n  <p>Product features here</p>\n</section>",
                  analogy: "Like chapters in a book",
                },
                {
                  title: "Article Element",
                  description:
                    "<article> represents self-contained content that can stand alone.",
                  keyPoints: [
                    "Reusable independently (blogs, cards)",
                    "Can be shared or syndicated",
                    "Often used inside sections",
                  ],
                  commonUses: ["Blog posts", "News articles", "Product cards"],
                  code: "<article>\n  <h3>Blog Title</h3>\n  <p>Post content...</p>\n</article>",
                },
                {
                  title: "Nav Element",
                  description: "<nav> is used to define navigation links.",
                  keyPoints: [
                    "Wraps major navigation links",
                    "Improves accessibility",
                    "Not every link needs nav",
                  ],
                  code: "<nav>\n  <a href='#home'>Home</a>\n  <a href='#about'>About</a>\n</nav>",
                },
                {
                  title: "Why Semantic HTML Matters",
                  description:
                    "Semantic HTML improves how machines and humans understand your site.",
                  benefits: [
                    "Better SEO rankings",
                    "Improved screen reader experience",
                    "Cleaner and maintainable code",
                    "Better collaboration in teams",
                  ],
                },
                {
                  title: "SEO & Accessibility Benefits",
                  description:
                    "Search engines and assistive technologies rely heavily on semantics.",
                  keyPoints: [
                    "Search engines understand content structure",
                    "Screen readers navigate faster",
                    "Landmarks improve page navigation",
                  ],
                  process: [
                    "Browser reads semantic structure",
                    "Assistive tech builds accessibility tree",
                    "Users navigate content efficiently",
                  ],
                },
              ],

              visualStructure: `
Semantic Page Layout:
┌─────────────────────────────┐
│ <header>                    │
│  Logo | Nav                 │
├─────────────────────────────┤
│ <main>                      │
│  ┌──────── <section> ────┐ │
│  │  <article>            │ │
│  │  Content              │ │
│  └──────────────────────┘ │
│ </main>                     │
├─────────────────────────────┤
│ <footer>                    │
│  Copyright | Links          │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using div everywhere",
                  consequence: "Poor semantics and accessibility",
                  fix: "Replace divs with semantic tags",
                },
                {
                  mistake: "Multiple <main> elements",
                  consequence: "Invalid HTML",
                  fix: "Use only one <main> per page",
                },
                {
                  mistake: "Using <section> without headings",
                  consequence: "Unclear structure",
                  fix: "Add meaningful headings",
                },
                {
                  mistake: "Putting nav links outside <nav>",
                  consequence: "Reduced accessibility",
                  fix: "Wrap main navigation inside <nav>",
                },
              ],

              proTips: [
                "Write HTML like a document, not a layout",
                "If content makes sense on its own, use <article>",
                "If content groups a topic, use <section>",
                "Use div only when no semantic tag fits",
                "Semantic HTML reduces need for ARIA",
              ],

              realWorldAnalogy:
                "Semantic HTML is like organized office files:\n- header is reception\n- main is work area\n- section is department\n- article is a file\n- footer is records room",
            },

            tasks: [
              "Replace divs with semantic tags",
              "Structure page using header, main, footer",
              "Group content logically",
            ],

            examples: [
              "<header>Header</header>\n<main>\n  <section>Content</section>\n</main>\n<footer>Footer</footer>",
            ],

            useCases: [
              "SEO friendly websites",
              "Accessible web apps",
              "Blogs and content platforms",
              "Enterprise web applications",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<header> – Introductory content",
                "<nav> – Navigation links",
                "<main> – Primary content (one per page)",
                "<section> – Thematic grouping",
                "<article> – Self-contained content",
                "<footer> – Closing content",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand semantic HTML5 elements",
              "Structure pages meaningfully",
              "Improve SEO and accessibility using semantics",
              "Replace unnecessary div usage",
            ],
          },
          {
            day: 7,
            topic: "Week 1 Review & Mini Project",
            subTopics: [
              "HTML best practices",
              "Code readability",
              "Semantic structure review",
            ],
            explanation:
              "Apply everything learned this week by building a complete HTML-only project.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "HTML-Only Portfolio (Industry Grade)",
                description:
                  "Build a complete personal portfolio landing page using only semantic HTML.",
                requirements: [
                  "DOCTYPE + proper document structure",
                  "header, nav, main, section, article, footer used correctly",
                  "One h1, logical heading hierarchy",
                  "Lists for skills, articles for projects",
                  "Accessible images with alt text",
                ],
                constraint:
                  "No CSS, no JavaScript, no inline styling. HTML structure only.",
                outcome:
                  "A clean, readable, interview-ready HTML file that proves strong fundamentals.",
              },
              {
                id: "pt-2",
                title: "Semantic Refactor Challenge",
                description:
                  "Take an existing div-heavy HTML page and refactor it using semantic tags.",
                steps: [
                  "Identify divs that represent structure",
                  "Replace them with appropriate semantic elements",
                  "Ensure headings and sections still make sense",
                ],
                outcome:
                  "Experience real-world refactoring done in professional teams.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Week-1 HTML quality decides how scalable, accessible, and maintainable your future CSS and JavaScript will be.",
              realWorldChecks: [
                "W3C HTML Validator (zero errors)",
                "Screen reader landmark navigation",
                "Manual readability check (another dev reading your code)",
              ],
              commonFinding:
                "Many beginners rush projects and miss semantic and accessibility fundamentals.",
            },
            interviewInsights: {
              commonQuestions: [
                "How would you structure a portfolio page using semantic HTML?",
                "Why is semantic HTML important even before CSS?",
                "What mistakes do beginners make in HTML projects?",
                "How do you ensure accessibility without JavaScript?",
              ],
              interviewerPerspective:
                "A clean HTML-only project instantly separates serious learners from tutorial followers.",
              tip: "Interviewers care more about structure and reasoning than visual design at this stage.",
            },
            editorGuidance: {
              starterHint:
                "Think like a browser and a screen reader, not like a designer.",
              aiMentorPrompts: [
                "Review my HTML structure for semantic correctness",
                "Check if my headings follow proper hierarchy",
                "Point out accessibility issues in my page",
                "Suggest structural improvements without adding CSS",
              ],
              validationChecklist: [
                "DOCTYPE present",
                "Only one <main> and <h1>",
                "Semantic tags used instead of divs",
                "Alt text added to all meaningful images",
                "HTML validates without errors",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Learning HTML is not about memorizing tags — it’s about using them correctly together. This mini project helps you combine structure, semantics, accessibility, and readability into one real-world webpage.",

              sections: [
                {
                  title: "What You’ve Learned This Week",
                  description:
                    "Before building the project, let’s recap the core concepts.",
                  checklist: [
                    "HTML document structure (DOCTYPE, html, head, body)",
                    "Text elements and content hierarchy",
                    "Links and navigation",
                    "Lists and content grouping",
                    "Images with accessibility in mind",
                    "Semantic HTML5 elements",
                  ],
                  analogy:
                    "Like collecting tools all week — today you build something with them",
                },
                {
                  title: "Project Overview",
                  description:
                    "You will build a simple personal portfolio landing page using only HTML.",
                  requirements: [
                    "No CSS or JavaScript (HTML only)",
                    "Use semantic HTML elements",
                    "Readable and clean structure",
                    "Accessible content",
                  ],
                  output: "A single HTML file (index.html)",
                },
                {
                  title: "Required Page Sections",
                  description:
                    "Your portfolio page should include the following sections.",
                  structure: [
                    "<header> – Name, title, navigation",
                    "<main> – Core content",
                    "<section> – About, Skills, Projects",
                    "<article> – Individual project or experience",
                    "<footer> – Contact info and copyright",
                  ],
                },
                {
                  title: "HTML Best Practices to Follow",
                  description: "This project is about quality, not quantity.",
                  keyPoints: [
                    "Use only one <h1> on the page",
                    "Follow proper heading order (h1 → h2 → h3)",
                    "Use semantic elements instead of divs",
                    "Add alt text to all meaningful images",
                    "Indent code properly",
                  ],
                },
                {
                  title: "HTML Validation",
                  description:
                    "Valid HTML ensures your page works consistently across browsers.",
                  process: [
                    "Go to an online HTML validator",
                    "Upload or paste your HTML code",
                    "Fix all errors and warnings",
                    "Revalidate until clean",
                  ],
                  tools: ["W3C HTML Validator"],
                  analogy: "Like spell-check for your HTML code",
                },
              ],

              visualStructure: `
Portfolio Page Structure:
┌─────────────────────────────┐
│ <header>                    │
│  Name | Title | Nav         │
├─────────────────────────────┤
│ <main>                      │
│  ┌──────── <section> ────┐ │
│  │ About Me               │ │
│  ├──────── <section> ────┤ │
│  │ Skills (ul)            │ │
│  ├──────── <section> ────┤ │
│  │ Projects (<article>)  │ │
│  └──────────────────────┘ │
│ </main>                     │
├─────────────────────────────┤
│ <footer>                    │
│  Contact | Copyright        │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Rushing the project",
                  consequence: "Messy structure and poor learning",
                  fix: "Focus on correctness, not speed",
                },
                {
                  mistake: "Overusing divs",
                  consequence: "Missed opportunity to practice semantics",
                  fix: "Replace divs with semantic elements",
                },
                {
                  mistake: "Ignoring accessibility",
                  consequence: "Unusable content for some users",
                  fix: "Add alt text and proper headings",
                },
                {
                  mistake: "Skipping validation",
                  consequence: "Hidden HTML errors",
                  fix: "Always validate your final code",
                },
              ],

              proTips: [
                "Write HTML as if someone else will read it",
                "Clean structure matters more than design at this stage",
                "Semantic HTML is a long-term skill",
                "Real learning happens when you build",
                "This page can later be styled with CSS",
              ],

              realWorldAnalogy:
                "This project is like your first house frame:\n- Strong structure now\n- Styling comes later\n- Weak foundations break easily",
            },

            tasks: [
              "Build personal portfolio landing page",
              "Use semantic HTML throughout",
              "Validate HTML using online validator",
            ],

            examples: [],

            useCases: [
              "Personal branding",
              "Portfolio websites",
              "HTML practice projects",
            ],

            editorType: null,

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<!DOCTYPE html>",
                "<header>, <main>, <section>, <article>, <footer>",
                "One <h1> per page",
                "Proper indentation",
                "Alt text for images",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "60–90 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Apply HTML concepts in a real project",
              "Write clean and semantic HTML",
              "Build confidence with structure-first development",
              "Prepare for CSS in Week 2",
            ],
          },
        ],
      },
      {
        week: 2,
        title: "Advanced HTML & Forms",
        weekGoal:
          "Master complex HTML structures, forms, validation and accessibility",
        weekOutcome: "Build real-world forms and structured data layouts",
        canLearn: ["students", "beginners"],
        miniProject:
          "Survey / Registration Form – A fully accessible form with validation, tables and embedded content",

        days: [
          {
            day: 8,
            topic: "HTML Tables – Structured Data",
            subTopics: [
              "table, tr, td, th",
              "thead, tbody, tfoot",
              "Rowspan and colspan",
              "Table accessibility basics",
            ],
            explanation:
              "Tables are used to display structured data like reports, pricing plans and dashboards.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Admin Dashboard Data Table",
                description:
                  "Create a realistic admin-style data table using semantic table elements.",
                requirements: [
                  "Use <caption>, <thead>, <tbody>, <tfoot>",
                  "Column headers must use <th> with proper scope",
                  "At least 5 rows of meaningful data",
                ],
                constraint:
                  "Do not use tables for layout. Data representation only.",
                outcome:
                  "You’ll understand how dashboards and reports display structured data correctly.",
              },
              {
                id: "pt-2",
                title: "Complex Header Table",
                description:
                  "Build a table with grouped headers using rowspan and colspan.",
                steps: [
                  "Create a multi-column header",
                  "Apply rowspan and colspan thoughtfully",
                  "Ensure table remains readable",
                ],
                outcome:
                  "Learn how real-world comparison and pricing tables are built.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Tables are heavily used by screen readers and assistive technologies. Incorrect semantics can completely break data understanding.",
              realWorldChecks: [
                "Screen reader navigation through table headers",
                "Keyboard navigation across cells",
                "HTML validation for table structure",
              ],
              commonFinding:
                "Beginners often skip <caption> and proper <th> usage, reducing accessibility.",
            },
            interviewInsights: {
              commonQuestions: [
                "When should tables be used in HTML?",
                "Difference between <td> and <th>?",
                "Why are <thead> and <tbody> important?",
                "How do rowspan and colspan work?",
                "How do screen readers read tables?",
              ],
              interviewerPerspective:
                "Table questions test a candidate’s understanding of semantics, accessibility, and data-first thinking.",
              tip: "Always explain tables from a data-meaning perspective, not visual layout.",
            },
            editorGuidance: {
              starterHint:
                "Before writing table markup, clearly define what each row and column represents.",
              aiMentorPrompts: [
                "Is my table semantically correct?",
                "Did I use <th> and scope properly?",
                "Is this table accessible to screen readers?",
                "Can this table be simplified?",
              ],
              validationChecklist: [
                "<caption> present",
                "<th> used for headers",
                "<thead>, <tbody>, <tfoot> used correctly",
                "rowspan/colspan used sparingly",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Tables are designed specifically for structured, row-and-column data. While modern layouts should not rely on tables, they are essential for displaying reports, comparisons, and analytics clearly and accessibly.",

              sections: [
                {
                  title: "Table Basics (<table>, <tr>, <td>, <th>)",
                  description: "HTML tables are made of rows and cells.",
                  keyPoints: [
                    "<table> wraps the entire table",
                    "<tr> defines a table row",
                    "<td> defines a data cell",
                    "<th> defines a header cell",
                  ],
                  bestPractices: [
                    "Use <th> for headers, not <td>",
                    "Headers improve accessibility and readability",
                  ],
                  code: "<table>\n  <tr>\n    <th>Name</th>\n    <th>Age</th>\n  </tr>\n  <tr>\n    <td>John</td>\n    <td>25</td>\n  </tr>\n</table>",
                  analogy:
                    "Like an Excel sheet — rows and columns with headers",
                },
                {
                  title: "Table Head, Body, and Footer",
                  description:
                    "<thead>, <tbody>, and <tfoot> group table content logically.",
                  keyPoints: [
                    "<thead> contains column headers",
                    "<tbody> contains main data rows",
                    "<tfoot> contains summary or totals",
                  ],
                  benefits: [
                    "Improves table structure",
                    "Helps screen readers understand layout",
                    "Useful for large tables and styling",
                  ],
                  code: "<table>\n  <thead>\n    <tr><th>Item</th><th>Price</th></tr>\n  </thead>\n  <tbody>\n    <tr><td>Book</td><td>$10</td></tr>\n  </tbody>\n  <tfoot>\n    <tr><th>Total</th><th>$10</th></tr>\n  </tfoot>\n</table>",
                },
                {
                  title: "Rowspan and Colspan",
                  description:
                    "rowspan and colspan allow cells to span multiple rows or columns.",
                  keyPoints: [
                    "rowspan merges rows vertically",
                    "colspan merges columns horizontally",
                    "Useful for grouped headers or summaries",
                  ],
                  code: "<table>\n  <tr>\n    <th rowspan='2'>Name</th>\n    <th colspan='2'>Scores</th>\n  </tr>\n  <tr>\n    <th>Math</th>\n    <th>Science</th>\n  </tr>\n</table>",
                  caution: "Overusing spans can make tables hard to understand",
                },
                {
                  title: "Table Accessibility Basics",
                  description:
                    "Accessible tables ensure data is understandable for all users.",
                  keyPoints: [
                    "Always use <th> for headers",
                    "Use scope attribute when needed",
                    "Avoid complex nested tables",
                  ],
                  bestPractices: [
                    "Use scope='col' or scope='row' for headers",
                    "Keep table structure simple",
                    "Add captions for context",
                  ],
                  code: "<table>\n  <caption>User Data</caption>\n  <thead>\n    <tr>\n      <th scope='col'>Name</th>\n      <th scope='col'>Age</th>\n    </tr>\n  </thead>\n</table>",
                },
                {
                  title: "When to Use Tables (and When Not To)",
                  description: "Tables should be used only for tabular data.",
                  guidelines: [
                    "Use tables for data comparison and reports",
                    "Do NOT use tables for page layout",
                    "Use CSS layout techniques instead of tables",
                  ],
                  analogy: "Tables are spreadsheets, not containers",
                },
              ],

              visualStructure: `
Table Structure:
┌─────────────────────────────┐
│ <table>                     │
│  <caption> Title </caption> │
│  <thead>                    │
│   <tr><th>Header</th></tr>  │
│  </thead>                   │
│  <tbody>                    │
│   <tr><td>Data</td></tr>    │
│  </tbody>                   │
│  <tfoot>                    │
│   <tr><th>Total</th></tr>   │
│  </tfoot>                   │
│ </table>                    │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using tables for layout",
                  consequence: "Poor accessibility and maintainability",
                  fix: "Use CSS for layout, tables only for data",
                },
                {
                  mistake: "Skipping <th> tags",
                  consequence: "Harder to understand data",
                  fix: "Always define headers properly",
                },
                {
                  mistake: "Overusing rowspan/colspan",
                  consequence: "Confusing table structure",
                  fix: "Keep tables simple and readable",
                },
                {
                  mistake: "Missing table captions",
                  consequence: "Lack of context for users",
                  fix: "Add <caption> where helpful",
                },
              ],

              proTips: [
                "Think data-first when designing tables",
                "Tables are powerful but should stay simple",
                "Use captions to explain what the table shows",
                "Screen readers rely heavily on table semantics",
                "Test tables with keyboard navigation",
              ],

              realWorldAnalogy:
                "HTML tables are like spreadsheets:\n- Headers explain columns\n- Rows contain records\n- Footer summarizes data",
            },

            tasks: [
              "Create a simple data table",
              "Add table headers",
              "Use thead and tbody correctly",
            ],

            examples: [
              "<table>\n  <thead>\n    <tr><th>Name</th><th>Age</th></tr>\n  </thead>\n  <tbody>\n    <tr><td>John</td><td>25</td></tr>\n  </tbody>\n</table>",
            ],

            useCases: [
              "Admin dashboards",
              "Reports and analytics",
              "Pricing comparison tables",
              "Data-heavy applications",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<table> – Table container",
                "<tr> – Table row",
                "<td> – Data cell",
                "<th> – Header cell",
                "<thead>, <tbody>, <tfoot> – Logical grouping",
                "rowspan / colspan – Cell spanning",
                "<caption> – Table title",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Create structured HTML tables",
              "Use semantic table sections",
              "Apply rowspan and colspan correctly",
              "Build accessible data tables",
            ],
          },
          {
            day: 9,
            topic: "Advanced Tables & Best Practices",
            subTopics: [
              "colspan & rowspan",
              "caption tag",
              "Table semantics",
              "When NOT to use tables",
            ],
            explanation:
              "Advanced tables improve readability and accessibility for complex datasets.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Pricing Comparison Table (Industry Style)",
                description:
                  "Build a real-world pricing comparison table similar to SaaS websites.",
                requirements: [
                  "Use <caption> to describe pricing context",
                  "Grouped headers using colspan",
                  "Feature names as row headers using <th scope='row'>",
                  "Readable and simple structure",
                ],
                constraint:
                  "Do not overuse rowspan/colspan. Clarity is more important than compactness.",
                outcome:
                  "You’ll learn how professional pricing tables balance clarity, semantics, and accessibility.",
              },
              {
                id: "pt-2",
                title: "Table Simplification Challenge",
                description:
                  "Take an over-complicated table and refactor it for better readability.",
                steps: [
                  "Identify unnecessary row/column spans",
                  "Split data if table becomes too complex",
                  "Ensure headers are still meaningful",
                ],
                outcome:
                  "Understand when complexity hurts usability and how to fix it.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "As tables grow complex, poor semantics can completely break screen reader navigation and user understanding.",
              realWorldChecks: [
                "Screen reader header-to-cell navigation",
                "Keyboard navigation across rows and columns",
                "HTML validation for table semantics",
              ],
              commonFinding:
                "Most accessibility issues in tables come from missing captions and incorrect header relationships.",
            },
            interviewInsights: {
              commonQuestions: [
                "When should colspan or rowspan be used?",
                "Why is <caption> important for tables?",
                "How do screen readers interpret table headers?",
                "Why should tables not be used for layout?",
              ],
              interviewerPerspective:
                "Advanced table questions test understanding of accessibility, semantics, and real-world data presentation.",
              tip: "Always explain tables from a data-relationship and accessibility point of view.",
            },
            editorGuidance: {
              starterHint:
                "Design the data first on paper, then translate it into table markup.",
              aiMentorPrompts: [
                "Is my table structure too complex?",
                "Are headers clearly associated with data cells?",
                "Does my table need to be split?",
                "Is this table accessible without visual cues?",
              ],
              validationChecklist: [
                "<caption> present and descriptive",
                "<th> used correctly with scope",
                "colspan/rowspan used sparingly",
                "Table not used for layout",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Simple tables work for small datasets, but real-world applications often require more structured and accessible tables. Advanced table techniques help present complex data clearly without sacrificing usability or accessibility.",

              sections: [
                {
                  title: "Colspan & Rowspan (Deep Dive)",
                  description:
                    "colspan and rowspan allow table cells to span multiple columns or rows.",
                  keyPoints: [
                    "colspan merges cells horizontally",
                    "rowspan merges cells vertically",
                    "Useful for grouped headers and pricing tables",
                  ],
                  bestPractices: [
                    "Use spans sparingly",
                    "Keep table layout easy to scan",
                    "Ensure headers still make sense",
                  ],
                  code: "<table>\n  <tr>\n    <th rowspan='2'>Plan</th>\n    <th colspan='2'>Pricing</th>\n  </tr>\n  <tr>\n    <th>Monthly</th>\n    <th>Yearly</th>\n  </tr>\n</table>",
                  analogy: "Like merging cells in Excel for grouped headings",
                },
                {
                  title: "Caption Tag (<caption>)",
                  description:
                    "The <caption> tag provides a title or explanation for the table.",
                  keyPoints: [
                    "Appears as the first child of <table>",
                    "Helps users understand table purpose",
                    "Read by screen readers",
                  ],
                  bestPractices: [
                    "Keep captions short and descriptive",
                    "Describe what the table shows, not how it looks",
                  ],
                  code: "<table>\n  <caption>Pricing Plans Comparison</caption>\n</table>",
                },
                {
                  title: "Table Semantics & Accessibility",
                  description:
                    "Proper semantics make tables understandable for assistive technologies.",
                  keyPoints: [
                    "Use <th> for headers, not styling",
                    "Apply scope='row' or scope='col' when needed",
                    "Use <thead>, <tbody>, and <tfoot>",
                  ],
                  code: "<th scope='col'>Feature</th>\n<th scope='row'>Storage</th>",
                  benefits: [
                    "Improved screen reader navigation",
                    "Better SEO understanding",
                    "Clear data relationships",
                  ],
                },
                {
                  title: "When NOT to Use Tables",
                  description: "Tables should only be used for tabular data.",
                  guidelines: [
                    "Do NOT use tables for page layout",
                    "Do NOT use tables for alignment or spacing",
                    "Use CSS Grid or Flexbox for layouts",
                  ],
                  comparison: [
                    "Pricing data → Table ✅",
                    "Page layout → Table ❌",
                  ],
                  analogy: "Tables are spreadsheets, not design tools",
                },
                {
                  title: "Real-World Example: Pricing Comparison",
                  description:
                    "Pricing pages often require advanced table techniques.",
                  exampleStructure: [
                    "Caption explaining pricing context",
                    "Grouped headers using colspan",
                    "Clear feature rows",
                    "Simple, readable layout",
                  ],
                },
              ],

              visualStructure: `
Advanced Table Layout:
┌─────────────────────────────┐
│ <table>                     │
│  <caption> Pricing </caption>│
│  <thead>                    │
│   ┌──────┬──────────────┐  │
│   │ Plan │   Premium    │  │
│   ├──────┼──────┬───────┤  │
│   │      │Monthly│Yearly│  │
│   └──────┴──────┴───────┘  │
│  </thead>                  │
│  <tbody>                   │
│   Features & data rows     │
│  </tbody>                  │
│ </table>                   │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Overusing colspan and rowspan",
                  consequence: "Confusing table structure",
                  fix: "Simplify table or split it",
                },
                {
                  mistake: "Missing caption",
                  consequence: "Users don’t understand table purpose",
                  fix: "Add a clear caption",
                },
                {
                  mistake: "Using tables for layout",
                  consequence: "Poor accessibility and maintenance",
                  fix: "Use CSS layout techniques",
                },
                {
                  mistake: "Styling headers instead of using <th>",
                  consequence: "Lost semantic meaning",
                  fix: "Always use <th> for headers",
                },
              ],

              proTips: [
                "Design tables for readability first",
                "If table looks too complex, rethink data structure",
                "Accessibility matters more as tables grow",
                "Captions are essential for context",
                "Test tables with screen readers if possible",
              ],

              realWorldAnalogy:
                "Advanced tables are like well-designed reports:\n- Clear headings\n- Grouped sections\n- Easy-to-scan data",
            },

            tasks: [
              "Create a pricing comparison table",
              "Add table caption",
              "Use colspan and rowspan",
            ],

            examples: [
              "<caption>Pricing Plan</caption>\n<th colspan='2'>Premium</th>",
            ],

            useCases: [
              "Pricing pages",
              "Product comparison",
              "Business reports",
              "Enterprise dashboards",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "colspan – Merge columns",
                "rowspan – Merge rows",
                "<caption> – Table description",
                "<th> – Header cell",
                "scope attribute – Accessibility",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Build complex HTML tables",
              "Apply table best practices",
              "Improve accessibility for data tables",
              "Know when not to use tables",
            ],
          },
          {
            day: 10,
            topic: "HTML Forms – Basics",
            subTopics: [
              "form tag",
              "input types (text, email, password)",
              "textarea",
              "select & option",
              "button vs input submit",
            ],
            explanation:
              "Forms collect user data and are core to almost every web application.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real Contact Form (HTML-only)",
                description:
                  "Build a realistic contact form similar to what you see on company websites.",
                requirements: [
                  "Use <form> with proper action and method",
                  "Include name, email, message fields",
                  "Use correct input types",
                  "Use <button type='submit'>",
                ],
                constraint:
                  "No CSS or JavaScript. Focus only on HTML structure and usability.",
                outcome:
                  "You’ll understand how real-world contact forms are structured at HTML level.",
              },
              {
                id: "pt-2",
                title: "Signup Form Structure",
                description: "Create a basic signup form for a web app.",
                steps: [
                  "Add text, email, and password inputs",
                  "Ensure logical field order",
                  "Use placeholders meaningfully",
                ],
                outcome:
                  "Learn how form structure affects user experience and completion rate.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Forms are the main point where users interact with your product. Poor structure leads to drop-offs and data issues.",
              realWorldChecks: [
                "Manual form submission testing",
                "Keyboard-only navigation",
                "Mobile keyboard behavior for different input types",
              ],
              commonFinding:
                "Many beginners forget name attributes, causing form data to break silently.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the purpose of the <form> tag?",
                "Difference between <button> and <input type='submit'>?",
                "Why are input types important?",
                "What happens if an input has no name attribute?",
              ],
              interviewerPerspective:
                "Form questions help interviewers judge practical HTML knowledge and UX awareness.",
              tip: "Explain forms from a data-flow and user-experience perspective, not just syntax.",
            },
            editorGuidance: {
              starterHint:
                "Design the form on paper first: what data do you really need?",
              aiMentorPrompts: [
                "Is my form structure logical?",
                "Did I choose correct input types?",
                "Will this form work without JavaScript?",
                "Are my inputs missing name attributes?",
              ],
              validationChecklist: [
                "<form> wraps all inputs",
                "All inputs have name attributes",
                "Correct input types used",
                "Submit button present",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Forms allow users to interact with websites by submitting data. From login pages to contact forms and checkout flows, forms are a critical building block of web applications.",

              sections: [
                {
                  title: "Form Tag (<form>)",
                  description:
                    "The <form> element wraps all input fields and controls how data is submitted.",
                  keyPoints: [
                    "Acts as a container for form controls",
                    "Uses action and method attributes for submission",
                    "Can be submitted using a button or Enter key",
                  ],
                  code: "<form action='/submit' method='post'>\n  <!-- form fields -->\n</form>",
                  analogy:
                    "Think of a form as an envelope that sends user data",
                },
                {
                  title: "Input Types (text, email, password)",
                  description:
                    "The <input> element collects different kinds of user input.",
                  keyPoints: [
                    "type='text' for general text",
                    "type='email' for email addresses",
                    "type='password' hides typed characters",
                  ],
                  bestPractices: [
                    "Choose correct input type for better UX",
                    "Email type enables basic validation",
                  ],
                  code: "<input type='text' placeholder='Name' />\n<input type='email' placeholder='Email' />\n<input type='password' placeholder='Password' />",
                },
                {
                  title: "Textarea",
                  description: "<textarea> is used for multi-line text input.",
                  keyPoints: [
                    "Used for messages, comments, feedback",
                    "Supports rows and cols attributes",
                    "Not self-closing",
                  ],
                  code: "<textarea rows='4' cols='30' placeholder='Your message'></textarea>",
                  analogy: "Like a larger text box for long answers",
                },
                {
                  title: "Select & Option",
                  description: "The <select> element creates a dropdown menu.",
                  keyPoints: [
                    "<option> defines each choice",
                    "Good for predefined values",
                    "Reduces user input errors",
                  ],
                  code: "<select>\n  <option value='student'>Student</option>\n  <option value='developer'>Developer</option>\n</select>",
                },
                {
                  title: "Button vs Input Submit",
                  description:
                    "Forms can be submitted using either <button> or <input type='submit'>.",
                  comparison: [
                    "<button> is more flexible (can contain text or icons)",
                    "<input type='submit'> is simple and self-closing",
                    "Both trigger form submission",
                  ],
                  code: "<button type='submit'>Send</button>\n<input type='submit' value='Submit' />",
                },
                {
                  title: "Grouping Inputs Logically",
                  description:
                    "Logical grouping improves usability and readability.",
                  keyPoints: [
                    "Group related fields together",
                    "Use semantic structure",
                    "Improves form flow",
                  ],
                  exampleStructure: [
                    "Contact information",
                    "Message content",
                    "Submit action",
                  ],
                },
              ],

              visualStructure: `
Basic Form Layout:
┌─────────────────────────────┐
│ <form>                      │
│  <input type='text' />      │
│  <input type='email' />     │
│  <textarea></textarea>      │
│  <button>Submit</button>    │
│ </form>                     │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Not wrapping inputs inside <form>",
                  consequence: "Data cannot be submitted",
                  fix: "Always wrap inputs in a form tag",
                },
                {
                  mistake: "Using wrong input types",
                  consequence: "Poor user experience",
                  fix: "Choose input types carefully",
                },
                {
                  mistake: "Missing name attributes",
                  consequence: "Data not sent to server",
                  fix: "Always add name to inputs",
                },
                {
                  mistake: "Using <br> for spacing",
                  consequence: "Messy structure",
                  fix: "Use proper grouping and CSS later",
                },
              ],

              proTips: [
                "Forms are useless without good UX",
                "Correct input types improve mobile keyboards",
                "Always think about user flow",
                "HTML forms work even without JavaScript",
                "Clean forms are easier to validate later",
              ],

              realWorldAnalogy:
                "Forms are like paper forms:\n- Fields ask questions\n- User fills answers\n- Submit sends the form",
            },

            tasks: [
              "Create a basic contact form",
              "Use different input types",
              "Group inputs logically",
            ],

            examples: [
              "<form>\n  <input type='email' required />\n  <textarea></textarea>\n</form>",
            ],

            useCases: [
              "Contact forms",
              "Login & signup pages",
              "Feedback collection",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<form> – Form container",
                "<input> – User input",
                "type attribute – Input behavior",
                "<textarea> – Multi-line input",
                "<select> & <option> – Dropdown",
                "<button type='submit'> – Form submission",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Create basic HTML forms",
              "Use appropriate input types",
              "Structure forms cleanly",
              "Understand how forms submit data",
            ],
          },
          {
            day: 11,
            topic: "Form Validation & Input Attributes",
            subTopics: [
              "required attribute",
              "pattern",
              "min & max",
              "placeholder vs label",
              "disabled & readonly",
            ],
            explanation:
              "HTML validation improves user experience before JavaScript comes into play.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Signup Form with HTML Validation",
                description:
                  "Build a signup form that relies purely on HTML validation (no JavaScript).",
                requirements: [
                  "Use required on all mandatory fields",
                  "Apply pattern for phone or username",
                  "Use min and max where applicable",
                  "Use labels for every input",
                ],
                constraint:
                  "Do not use JavaScript validation. Rely only on HTML attributes.",
                outcome:
                  "You’ll understand how much validation HTML can handle on its own.",
              },
              {
                id: "pt-2",
                title: "Readonly vs Disabled Data Handling",
                description:
                  "Create a form with both readonly and disabled fields to observe submission behavior.",
                steps: [
                  "Add one readonly input",
                  "Add one disabled input",
                  "Submit form and compare results",
                ],
                outcome:
                  "Clear understanding of which fields are submitted and why.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "HTML validation provides instant feedback to users and prevents bad data from reaching the server.",
              realWorldChecks: [
                "Try submitting empty required fields",
                "Enter invalid data to trigger browser messages",
                "Test number fields beyond min/max limits",
              ],
              commonFinding:
                "Many developers misuse placeholder instead of label, breaking accessibility.",
            },
            interviewInsights: {
              commonQuestions: [
                "What does the required attribute do?",
                "Difference between readonly and disabled?",
                "Why should labels not be replaced by placeholders?",
                "When should pattern be avoided?",
              ],
              interviewerPerspective:
                "Form validation questions reveal a candidate’s UX thinking and accessibility awareness.",
              tip: "Always explain validation from a user-error prevention point of view.",
            },
            editorGuidance: {
              starterHint:
                "Start by deciding what inputs are mandatory and what constraints they need.",
              aiMentorPrompts: [
                "Is my form accessible without placeholders?",
                "Did I overuse pattern?",
                "Will invalid data be blocked before submit?",
                "Are readonly and disabled used correctly?",
              ],
              validationChecklist: [
                "All required fields marked",
                "Labels present for all inputs",
                "Patterns kept simple",
                "Readonly vs disabled used correctly",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "HTML provides built-in validation features that prevent invalid data from being submitted. Using these attributes improves user experience, reduces errors, and minimizes the need for JavaScript validation.",

              sections: [
                {
                  title: "Required Attribute",
                  description:
                    "The required attribute ensures the user must fill in the input before submitting the form.",
                  keyPoints: [
                    "Prevents empty form submission",
                    "Works without JavaScript",
                    "Triggers browser validation messages",
                  ],
                  code: "<input type='email' required />",
                  analogy: "Like a mandatory field on a paper form",
                },
                {
                  title: "Pattern Attribute",
                  description:
                    "The pattern attribute defines a regular expression the input value must match.",
                  keyPoints: [
                    "Used mainly with text inputs",
                    "Useful for phone numbers, usernames, IDs",
                    "Validation happens before form submission",
                  ],
                  bestPractices: [
                    "Keep patterns simple",
                    "Always provide user hints",
                  ],
                  code: "<input type='text' pattern='[0-9]{10}' placeholder='10-digit phone number' />",
                },
                {
                  title: "Min & Max Attributes",
                  description:
                    "min and max restrict the allowed range of values.",
                  keyPoints: [
                    "Commonly used with number, date, and range inputs",
                    "Prevents out-of-range values",
                    "Improves data accuracy",
                  ],
                  code: "<input type='number' min='18' max='60' required />",
                },
                {
                  title: "Placeholder vs Label",
                  description:
                    "Placeholders provide hints, labels describe inputs.",
                  comparison: [
                    "placeholder disappears when typing",
                    "label remains visible at all times",
                    "Labels are essential for accessibility",
                  ],
                  bestPractices: [
                    "Always use labels",
                    "Use placeholder only as a hint",
                  ],
                  code: "<label>Email</label>\n<input type='email' placeholder='example@mail.com' />",
                  analogy:
                    "Label is the question, placeholder is an example answer",
                },
                {
                  title: "Disabled vs Readonly",
                  description:
                    "disabled and readonly control user interaction differently.",
                  comparison: [
                    "disabled inputs cannot be edited or submitted",
                    "readonly inputs cannot be edited but ARE submitted",
                    "disabled fields appear inactive",
                  ],
                  code: "<input type='text' value='India' readonly />\n<input type='text' value='Admin' disabled />",
                },
                {
                  title: "Why HTML Validation Matters",
                  description:
                    "Validation improves UX and reduces server-side errors.",
                  benefits: [
                    "Faster feedback for users",
                    "Cleaner data submission",
                    "Less dependency on JavaScript",
                  ],
                },
              ],

              visualStructure: `
Form Validation Flow:
┌─────────────────────────────┐
│ User fills form             │
│        ↓                    │
│ HTML validation checks      │
│        ↓                    │
│ Errors shown (if any)       │
│        ↓                    │
│ Valid form submitted        │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Relying only on placeholder",
                  consequence: "Poor accessibility",
                  fix: "Always use labels",
                },
                {
                  mistake: "Overly complex patterns",
                  consequence: "User confusion",
                  fix: "Keep regex simple",
                },
                {
                  mistake: "Missing validation",
                  consequence: "Bad data quality",
                  fix: "Use built-in HTML validation",
                },
                {
                  mistake: "Using disabled instead of readonly",
                  consequence: "Data not submitted",
                  fix: "Use readonly when data must be sent",
                },
              ],

              proTips: [
                "HTML validation works even without JavaScript",
                "Combine required + pattern for strong validation",
                "Labels are non-negotiable for accessibility",
                "Let the browser handle basic validation",
                "Always test forms with invalid inputs",
              ],

              realWorldAnalogy:
                "Form validation is like checking an application before submission:\n- Required fields must be filled\n- Values must be within limits\n- Incorrect entries are rejected early",
            },

            tasks: [
              "Add validation to form fields",
              "Use pattern for email/phone",
              "Restrict number inputs",
            ],

            examples: ["<input type='number' min='18' max='60' required />"],

            useCases: ["User registration", "Survey forms", "Checkout forms"],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "required – Mandatory field",
                "pattern – Regex validation",
                "min / max – Range control",
                "label – Accessibility",
                "placeholder – Input hint",
                "disabled / readonly – Input state",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Apply HTML form validation",
              "Improve form accessibility",
              "Restrict user input correctly",
              "Prepare forms for real applications",
            ],
          },
          {
            day: 12,
            topic: "Form Accessibility & Submission",
            subTopics: [
              "label association",
              "aria-label",
              "fieldset & legend",
              "GET vs POST (conceptual)",
              "Keyboard navigation",
            ],
            explanation:
              "Accessible forms ensure everyone can interact with your application.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Accessible Contact Form (WCAG-minded)",
                description:
                  "Build a fully accessible contact form that works for screen readers and keyboard users.",
                requirements: [
                  "Every input must have a properly associated <label>",
                  "Use <fieldset> and <legend> for grouped inputs",
                  "No placeholder-only inputs",
                  "Form must be usable using keyboard only",
                ],
                constraint:
                  "Do not use JavaScript or CSS tricks. Pure HTML accessibility only.",
                outcome:
                  "You’ll understand what makes a form truly accessible in real products.",
              },
              {
                id: "pt-2",
                title: "GET vs POST Decision Exercise",
                description:
                  "Design two forms and choose correct submission methods.",
                steps: [
                  "Create a search form using GET",
                  "Create a signup/login form using POST",
                  "Explain why each method was chosen",
                ],
                outcome:
                  "Clear conceptual understanding of form submission methods used in production.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Forms that fail accessibility checks silently exclude users and often violate legal and ethical standards.",
              realWorldChecks: [
                "Navigate entire form using only Tab and Shift+Tab",
                "Check label announcements in screen readers",
                "Ensure Enter key submits the form",
              ],
              commonFinding:
                "Many developers add aria-label without fixing missing label associations.",
            },
            interviewInsights: {
              commonQuestions: [
                "How do you associate a label with an input?",
                "When should aria-label be used?",
                "Difference between GET and POST?",
                "How do you test form accessibility?",
              ],
              interviewerPerspective:
                "Accessibility questions separate UI coders from real frontend engineers.",
              tip: "Always explain accessibility decisions from a real user’s perspective.",
            },
            editorGuidance: {
              starterHint:
                "Build forms as if someone is using only a keyboard or a screen reader.",
              aiMentorPrompts: [
                "Is every input accessible without a mouse?",
                "Did I associate labels correctly?",
                "Am I misusing aria-label?",
                "Is my form submission method appropriate?",
              ],
              validationChecklist: [
                "All inputs have associated labels",
                "Fieldsets used for grouped controls",
                "Form usable via keyboard only",
                "Correct GET/POST usage",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Forms are only truly complete when everyone can use them. Accessibility ensures that users with disabilities, screen readers, or keyboard-only navigation can interact with forms easily and confidently.",

              sections: [
                {
                  title: "Label Association",
                  description:
                    "Labels describe what an input field is for and must be properly associated.",
                  keyPoints: [
                    "Use the for attribute on <label>",
                    "Match it with the input id",
                    "Improves screen reader experience",
                  ],
                  bestPractices: [
                    "Always use visible labels",
                    "Avoid relying only on placeholders",
                  ],
                  code: "<label for='email'>Email</label>\n<input id='email' type='email' />",
                  analogy: "Label is the question, input is the answer box",
                },
                {
                  title: "ARIA Labels (aria-label)",
                  description:
                    "ARIA labels provide accessible names when visible labels are not possible.",
                  keyPoints: [
                    "Used when a visual label is missing",
                    "Read by screen readers",
                    "Should not replace visible labels unnecessarily",
                  ],
                  bestPractices: [
                    "Prefer visible labels first",
                    "Use aria-label as a fallback",
                  ],
                  code: "<input type='search' aria-label='Search products' />",
                },
                {
                  title: "Fieldset & Legend",
                  description:
                    "<fieldset> groups related inputs, and <legend> describes the group.",
                  keyPoints: [
                    "Improves form structure",
                    "Helps screen readers announce groups",
                    "Useful for radio buttons and checkboxes",
                  ],
                  code: "<fieldset>\n  <legend>Gender</legend>\n  <label><input type='radio' name='gender' /> Male</label>\n  <label><input type='radio' name='gender' /> Female</label>\n</fieldset>",
                  analogy:
                    "Like grouping questions under one heading in a form",
                },
                {
                  title: "GET vs POST (Conceptual)",
                  description: "Forms submit data using HTTP methods.",
                  comparison: [
                    "GET sends data in the URL",
                    "POST sends data in the request body",
                    "GET is used for searches",
                    "POST is used for sensitive data",
                  ],
                  bestPractices: [
                    "Use GET for non-sensitive data",
                    "Use POST for passwords and personal info",
                  ],
                  code: "<form method='post'>\n  <!-- secure submission -->\n</form>",
                },
                {
                  title: "Keyboard Navigation",
                  description:
                    "Accessible forms must be usable without a mouse.",
                  keyPoints: [
                    "Users navigate using Tab key",
                    "Inputs should follow logical order",
                    "Focus should be visible",
                  ],
                  testingChecklist: [
                    "Tab through all inputs",
                    "Submit form using Enter key",
                    "Ensure no keyboard traps",
                  ],
                  analogy:
                    "If you can fill the form without a mouse, it’s accessible",
                },
                {
                  title: "Why Form Accessibility Matters",
                  description:
                    "Accessibility is not optional — it’s essential.",
                  benefits: [
                    "Inclusive user experience",
                    "Legal compliance (WCAG)",
                    "Better usability for everyone",
                    "Improved form completion rates",
                  ],
                },
              ],

              visualStructure: `
Accessible Form Structure:
┌─────────────────────────────┐
│ <form>                      │
│  <label> Email </label>     │
│  <input />                  │
│                             │
│  <fieldset>                 │
│   <legend> Group </legend>  │
│   Inputs                    │
│  </fieldset>                │
│                             │
│  <button> Submit </button>  │
│ </form>                     │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Missing label association",
                  consequence: "Poor screen reader experience",
                  fix: "Use for + id properly",
                },
                {
                  mistake: "Using aria-label everywhere",
                  consequence: "Hidden context for sighted users",
                  fix: "Prefer visible labels",
                },
                {
                  mistake: "Ignoring keyboard navigation",
                  consequence: "Form unusable for many users",
                  fix: "Test using only keyboard",
                },
                {
                  mistake: "Using GET for sensitive data",
                  consequence: "Security risk",
                  fix: "Use POST for private data",
                },
              ],

              proTips: [
                "Accessible forms are more usable for everyone",
                "Labels are mandatory, placeholders are optional",
                "ARIA enhances HTML, it does not replace it",
                "Keyboard testing should be routine",
                "Good accessibility improves form completion",
              ],

              realWorldAnalogy:
                "Accessible forms are like ramps in buildings:\n- Designed for some\n- Helpful for everyone",
            },

            tasks: [
              "Associate labels properly",
              "Group inputs using fieldset",
              "Test form using keyboard only",
            ],

            examples: [
              "<label for='email'>Email</label>\n<input id='email' type='email' />",
            ],

            useCases: [
              "Government & enterprise apps",
              "Inclusive products",
              "Public-facing forms",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<label for='id'> – Input association",
                "aria-label – Accessible name",
                "<fieldset> & <legend> – Grouping",
                "GET – Non-sensitive data",
                "POST – Sensitive data",
                "Tab key – Keyboard navigation",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Build accessible HTML forms",
              "Use labels and ARIA correctly",
              "Understand form submission methods",
              "Ensure keyboard accessibility",
            ],
          },
          {
            day: 13,
            topic: "Embedded Content & HTML Extras",
            subTopics: [
              "iframe usage",
              "video & audio tags",
              "controls & autoplay",
              "HTML entities (&nbsp;, &copy;)",
              "Security considerations",
            ],
            explanation:
              "Embedded content allows integration of media and third-party tools.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Media-Rich Landing Section",
                description:
                  "Create a media section for a landing page using video, audio, and iframe embeds.",
                requirements: [
                  "Embed one video using <video> with controls",
                  "Embed one external video using <iframe>",
                  "Embed one audio clip with <audio>",
                  "Use proper titles and fallback text",
                ],
                constraint:
                  "Do not use autoplay with sound. Respect user experience.",
                outcome:
                  "You’ll understand how modern learning and marketing pages embed media responsibly.",
              },
              {
                id: "pt-2",
                title: "Secure Iframe Embed",
                description: "Embed third-party content with security in mind.",
                steps: [
                  "Choose a trusted external source",
                  "Add sandbox attribute",
                  "Remove unnecessary permissions",
                ],
                outcome:
                  "Hands-on understanding of iframe security risks and mitigation.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Embedded content can slow down pages, harm UX, and introduce security risks if not handled carefully.",
              realWorldChecks: [
                "Check page load impact of embedded media",
                "Verify media controls work with keyboard",
                "Confirm iframe content loads only from trusted domains",
              ],
              commonFinding:
                "Beginners overuse iframes and autoplay media without considering user experience.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between <iframe> and <video>?",
                "Why is autoplay with sound discouraged?",
                "What are HTML entities and why are they needed?",
                "How can iframes create security risks?",
              ],
              interviewerPerspective:
                "Embedded content questions test UX sense, security awareness, and HTML depth.",
              tip: "Always explain embeds from performance, accessibility, and security angles.",
            },
            editorGuidance: {
              starterHint:
                "Before embedding anything, ask: do users really need this content here?",
              aiMentorPrompts: [
                "Is my embedded media accessible?",
                "Did I avoid autoplay with sound?",
                "Is this iframe safe to embed?",
                "Am I using the correct HTML entity?",
              ],
              validationChecklist: [
                "Media has controls",
                "Autoplay avoided or muted",
                "Iframe uses trusted source and sandbox",
                "Entities used for special characters",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern websites often include videos, audio, maps, and third-party tools. HTML provides built-in elements to embed this content safely while maintaining accessibility and performance.",

              sections: [
                {
                  title: "Iframes (<iframe>)",
                  description:
                    "The <iframe> tag embeds another webpage or external content inside your page.",
                  keyPoints: [
                    "Used for YouTube videos, maps, dashboards",
                    "Loads content from another source",
                    "Inline frame inside your page",
                  ],
                  bestPractices: [
                    "Always specify width and height",
                    "Allow only required permissions",
                    "Avoid overusing iframes",
                  ],
                  code: "<iframe\n  src='https://www.youtube.com/embed/VIDEO_ID'\n  width='560'\n  height='315'\n  title='YouTube video'\n  allowfullscreen>\n</iframe>",
                  analogy: "Like placing a TV screen inside your room",
                },
                {
                  title: "Video Tag (<video>)",
                  description:
                    "The <video> tag embeds video files directly into your webpage.",
                  keyPoints: [
                    "Supports multiple formats (mp4, webm)",
                    "Can include controls, autoplay, loop",
                    "Accessible with proper attributes",
                  ],
                  bestPractices: [
                    "Always include controls",
                    "Avoid autoplay with sound",
                    "Provide fallback text",
                  ],
                  code: "<video controls width='400'>\n  <source src='video.mp4' type='video/mp4' />\n  Your browser does not support video.\n</video>",
                },
                {
                  title: "Audio Tag (<audio>)",
                  description:
                    "The <audio> tag embeds sound content such as podcasts or music.",
                  keyPoints: [
                    "Similar to video but audio-only",
                    "Supports controls and autoplay",
                    "Lightweight compared to video",
                  ],
                  code: "<audio controls>\n  <source src='audio.mp3' type='audio/mpeg' />\n  Your browser does not support audio.\n</audio>",
                },
                {
                  title: "Controls & Autoplay",
                  description: "Media behavior is controlled using attributes.",
                  keyPoints: [
                    "controls allow user interaction",
                    "autoplay starts media automatically",
                    "muted is required for autoplay in most browsers",
                  ],
                  bestPractices: [
                    "Avoid autoplay unless necessary",
                    "Always respect user experience",
                  ],
                  code: "<video autoplay muted loop></video>",
                },
                {
                  title: "HTML Entities",
                  description:
                    "HTML entities display reserved or special characters.",
                  keyPoints: [
                    "&nbsp; → Non-breaking space",
                    "&copy; → © symbol",
                    "&lt; and &gt; → < and >",
                  ],
                  commonUses: [
                    "Copyright symbols",
                    "Extra spacing",
                    "Displaying code symbols",
                  ],
                  code: "&copy; 2025 Skilll.dev",
                  analogy: "Entities are escape codes for special characters",
                },
                {
                  title: "Security Considerations",
                  description: "Embedded content can introduce security risks.",
                  risks: [
                    "Malicious third-party content",
                    "Tracking and privacy concerns",
                    "Performance issues",
                  ],
                  bestPractices: [
                    "Embed content only from trusted sources",
                    "Use sandbox attribute when possible",
                    "Avoid unnecessary permissions",
                  ],
                  code: "<iframe src='https://trusted.com' sandbox></iframe>",
                },
              ],

              visualStructure: `
Embedded Media Layout:
┌─────────────────────────────┐
│ <video controls>            │
│                             │
│ <audio controls>            │
│                             │
│ <iframe> External content   │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Autoplaying media with sound",
                  consequence: "Bad user experience",
                  fix: "Avoid autoplay or use muted",
                },
                {
                  mistake: "Missing controls",
                  consequence: "Users cannot control media",
                  fix: "Always add controls",
                },
                {
                  mistake: "Embedding untrusted sources",
                  consequence: "Security and privacy risks",
                  fix: "Use trusted providers only",
                },
                {
                  mistake: "Overusing iframes",
                  consequence: "Poor performance",
                  fix: "Embed only when necessary",
                },
              ],

              proTips: [
                "Native HTML media is better than heavy plugins",
                "Always think about performance",
                "Respect user choice when playing media",
                "Use entities instead of special characters",
                "Security is part of frontend development",
              ],

              realWorldAnalogy:
                "Embedding content is like inviting guests:\n- Trusted guests are welcome\n- Too many guests cause chaos",
            },

            tasks: [
              "Embed a YouTube video",
              "Add audio player",
              "Use HTML entities correctly",
            ],

            examples: [
              "<iframe src='https://www.youtube.com/embed/video'></iframe>",
            ],

            useCases: [
              "Learning platforms",
              "Marketing pages",
              "Content-heavy websites",
            ],

            editorType: "html",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<iframe> – Embed external content",
                "<video> – Video player",
                "<audio> – Audio player",
                "controls – Media controls",
                "autoplay / muted – Playback behavior",
                "HTML entities – Special characters",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Embed videos and audio using HTML",
              "Understand iframe usage and risks",
              "Use HTML entities correctly",
              "Apply security best practices for embeds",
            ],
          },
          {
            day: 14,
            topic: "Week 2 Project – Survey Form",
            subTopics: [
              "Form structure",
              "Validation",
              "Accessibility",
              "Best practices",
            ],
            explanation:
              "Apply everything learned by building a real-world survey / registration form.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Industry-Grade Survey / Registration Form",
                description:
                  "Build a complete, real-world survey or registration form using only HTML.",
                requirements: [
                  "Multiple fieldsets with legends",
                  "Text, email, number, radio, checkbox, select, textarea inputs",
                  "Proper labels for every input",
                  "Built-in HTML validation (required, pattern, min/max)",
                  "Keyboard-only usability",
                ],
                constraint:
                  "No CSS, no JavaScript. HTML-only, accessibility-first implementation.",
                outcome:
                  "A professional HTML form comparable to real job applications and onboarding flows.",
              },
              {
                id: "pt-2",
                title: "Form UX & Accessibility Audit",
                description:
                  "Audit your own form like a professional frontend engineer.",
                steps: [
                  "Tab through the entire form using keyboard",
                  "Check label announcements logically",
                  "Submit form with invalid data to test validation",
                  "Validate HTML using W3C validator",
                ],
                outcome:
                  "You’ll learn how professionals review forms before shipping to production.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Forms are high-risk areas: poor accessibility or validation can block users, reduce conversions, and create legal issues.",
              realWorldChecks: [
                "Keyboard-only navigation (Tab / Shift+Tab)",
                "HTML validation with zero errors",
                "Required fields block empty submission",
                "Screen reader-friendly grouping",
              ],
              commonFinding:
                "Most beginners focus on fields but forget accessibility and validation flow.",
            },
            interviewInsights: {
              commonQuestions: [
                "How would you design an accessible survey form?",
                "How do you validate forms without JavaScript?",
                "Why are fieldset and legend important?",
                "How do you test form accessibility?",
              ],
              interviewerPerspective:
                "A clean, accessible form project signals strong frontend fundamentals and maturity.",
              tip: "Explain form decisions from usability, accessibility, and data-quality perspectives.",
            },
            editorGuidance: {
              starterHint:
                "Think like a real user filling a long form — clarity and flow matter more than number of fields.",
              aiMentorPrompts: [
                "Is my form fully accessible?",
                "Are all required fields clearly enforced?",
                "Does keyboard navigation feel natural?",
                "Can this form be understood without seeing it?",
              ],
              validationChecklist: [
                "All inputs have associated labels",
                "Fieldsets used for grouped inputs",
                "HTML validation passes without errors",
                "Form usable with keyboard only",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "This project combines everything you learned in Week 2. You will build a professional, accessible survey form similar to what is used in job applications, onboarding flows, and feedback systems.",

              sections: [
                {
                  title: "Project Overview",
                  description:
                    "You will create a complete HTML-only survey or registration form.",
                  requirements: [
                    "HTML only (no CSS or JavaScript)",
                    "Clean and readable structure",
                    "Accessible and keyboard-friendly",
                    "Valid HTML with built-in validation",
                  ],
                  output: "A single HTML file (survey.html)",
                  analogy: "This is your first real-world form, not a demo",
                },
                {
                  title: "Required Form Sections",
                  description:
                    "Your survey form should be divided into logical sections.",
                  structure: [
                    "Personal Information",
                    "Contact Details",
                    "Survey Questions",
                    "Preferences / Options",
                    "Submission Section",
                  ],
                  recommendedTags: [
                    "<form>",
                    "<fieldset> & <legend>",
                    "<label>",
                    "<input>, <select>, <textarea>",
                    "<button>",
                  ],
                },
                {
                  title: "Form Fields to Include",
                  description:
                    "Use a variety of input types to simulate a real survey.",
                  requiredFields: [
                    "Full name (text)",
                    "Email (email + required)",
                    "Age (number with min & max)",
                    "Gender (radio buttons)",
                    "Skills / Interests (checkboxes)",
                    "Feedback or comments (textarea)",
                    "Agreement checkbox",
                  ],
                },
                {
                  title: "Validation Requirements",
                  description:
                    "Use built-in HTML validation wherever possible.",
                  rules: [
                    "required for mandatory fields",
                    "pattern for phone or custom IDs",
                    "min & max for age",
                    "Proper input types for better UX",
                  ],
                  reminder:
                    "Do NOT use JavaScript — rely on HTML validation only",
                },
                {
                  title: "Accessibility Checklist",
                  description: "Your form must be accessible to all users.",
                  checklist: [
                    "Every input has an associated label",
                    "Logical tab order",
                    "Grouped inputs using fieldset & legend",
                    "No placeholder-only inputs",
                    "Form is usable with keyboard only",
                  ],
                  analogy:
                    "If a screen reader can use your form, it’s done right",
                },
                {
                  title: "Best Practices to Follow",
                  description: "Focus on quality and clarity.",
                  bestPractices: [
                    "Use semantic HTML",
                    "Indent code properly",
                    "Keep form flow logical",
                    "Use meaningful field names",
                    "Validate HTML before submission",
                  ],
                },
                {
                  title: "HTML Validation",
                  description:
                    "Final step before considering the project complete.",
                  process: [
                    "Open an online HTML validator",
                    "Paste your form HTML",
                    "Fix all errors and warnings",
                    "Re-validate until clean",
                  ],
                  tools: ["W3C HTML Validator"],
                },
              ],

              visualStructure: `
Survey Form Structure:
┌─────────────────────────────┐
│ <form>                      │
│  <fieldset>                 │
│   <legend> Personal Info </legend>
│   Inputs                    │
│  </fieldset>                │
│                             │
│  <fieldset>                 │
│   <legend> Survey </legend> │
│   Radios / Checkboxes       │
│  </fieldset>                │
│                             │
│  <textarea> Feedback </textarea>
│                             │
│  <button> Submit </button>  │
│ </form>                     │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Skipping labels",
                  consequence: "Form becomes inaccessible",
                  fix: "Always associate labels properly",
                },
                {
                  mistake: "Poor grouping of inputs",
                  consequence: "Confusing user experience",
                  fix: "Use fieldset and legend",
                },
                {
                  mistake: "Ignoring validation",
                  consequence: "Bad data submission",
                  fix: "Use required, pattern, min/max",
                },
                {
                  mistake: "Not testing with keyboard",
                  consequence: "Form unusable for some users",
                  fix: "Test using Tab and Enter keys",
                },
              ],

              proTips: [
                "Real forms are judged by usability, not looks",
                "Accessibility is a professional skill",
                "HTML-only forms still power real websites",
                "Clean structure makes future styling easy",
                "This form can later be upgraded with CSS & JS",
              ],

              realWorldAnalogy:
                "This project is like filling a government form:\n- Clear questions\n- Mandatory fields\n- Logical flow\n- Accessible to everyone",
            },

            tasks: [
              "Create multi-field survey form",
              "Add validation & labels",
              "Ensure accessibility compliance",
            ],

            examples: [],

            useCases: [
              "Job applications",
              "User onboarding",
              "Feedback & surveys",
            ],

            editorType: null,

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "<form> with method",
                "<label for='id'>",
                "<fieldset> & <legend>",
                "required / pattern / min / max",
                "Keyboard accessibility",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "90–120 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Build a complete accessible HTML form",
              "Apply validation and best practices",
              "Gain confidence with real-world forms",
              "Be ready for CSS styling in Week 3",
            ],
          },
        ],
      },

      {
        week: 3,
        title: "CSS Fundamentals & Selectors",
        weekGoal: "Understand CSS basics, selectors, styling and box model",
        weekOutcome: "Convert plain HTML pages into visually appealing layouts",
        canLearn: ["students", "beginners"],
        miniProject:
          "Styled Blog Template – Apply professional CSS styling to your existing HTML blog",

        days: [
          {
            day: 15,
            topic: "Introduction to CSS & Syntax",
            subTopics: [
              "What is CSS",
              "CSS syntax (selector, property, value)",
              "Inline vs internal vs external CSS",
              "Linking CSS files",
            ],
            explanation:
              "CSS controls how HTML elements look. Understanding syntax and structure is the foundation of styling.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "HTML to CSS Separation Exercise",
                description:
                  "Refactor an HTML file by removing all inline styles and moving them to an external CSS file.",
                requirements: [
                  "Create styles.css file",
                  "Link it correctly using <link>",
                  "Apply styles using selectors (no inline CSS)",
                ],
                constraint: "Inline CSS is strictly not allowed.",
                outcome:
                  "You’ll understand why professional projects never rely on inline styles.",
              },
              {
                id: "pt-2",
                title: "CSS Syntax Drill",
                description: "Practice writing clean and readable CSS rules.",
                steps: [
                  "Write at least 5 CSS rules",
                  "Use different selectors (element-based)",
                  "Follow proper indentation and formatting",
                ],
                outcome: "Build muscle memory for correct CSS syntax early.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most beginner CSS issues happen because styles are not loaded or syntax is incorrect.",
              realWorldChecks: [
                "Verify CSS file is linked correctly",
                "Check browser DevTools for applied styles",
                "Ensure selectors actually match HTML elements",
              ],
              commonFinding:
                "Beginners often write correct CSS but forget to link the file properly.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is CSS and why is it used?",
                "Difference between inline, internal, and external CSS?",
                "Why is external CSS preferred in real projects?",
                "What happens if multiple CSS rules target the same element?",
              ],
              interviewerPerspective:
                "CSS basics reveal whether a candidate understands scalability and maintainability.",
              tip: "Always explain CSS choices from a long-term project perspective.",
            },
            editorGuidance: {
              starterHint:
                "Write CSS as if multiple developers will read and maintain it.",
              aiMentorPrompts: [
                "Is my CSS file linked correctly?",
                "Am I using inline styles unnecessarily?",
                "Are my CSS rules readable and clean?",
                "Does this selector really target the element?",
              ],
              validationChecklist: [
                "External CSS file linked",
                "Correct selector–property–value syntax",
                "No inline CSS used",
                "CSS file loads without errors",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "CSS (Cascading Style Sheets) is used to control the visual appearance of HTML. While HTML defines structure, CSS defines design. Learning CSS correctly from the start helps you write clean, scalable, and maintainable styles.",

              sections: [
                {
                  title: "What is CSS?",
                  description:
                    "CSS is a stylesheet language used to describe how HTML elements should be displayed.",
                  keyPoints: [
                    "Controls colors, spacing, fonts, layout, and responsiveness",
                    "Keeps design separate from content",
                    "Allows consistent styling across pages",
                  ],
                  analogy: "HTML is the skeleton, CSS is the clothes",
                },
                {
                  title: "CSS Syntax",
                  description:
                    "CSS rules are written using selectors and declarations.",
                  structure: [
                    "Selector – targets HTML elements",
                    "Property – what you want to change",
                    "Value – how you want to change it",
                  ],
                  code: "p {\n  color: blue;\n  font-size: 16px;\n}",
                  breakdown: [
                    "p → selector",
                    "color / font-size → properties",
                    "blue / 16px → values",
                  ],
                  analogy: "Selector is who, property is what, value is how",
                },
                {
                  title: "Inline CSS",
                  description:
                    "Inline CSS is written directly inside an HTML element.",
                  keyPoints: [
                    "Applied using the style attribute",
                    "Overrides internal and external CSS",
                    "Not recommended for large projects",
                  ],
                  code: "<p style='color: red;'>Hello</p>",
                  useCase: "Quick testing or very small changes",
                },
                {
                  title: "Internal CSS",
                  description:
                    "Internal CSS is written inside a <style> tag in the HTML <head>.",
                  keyPoints: [
                    "Applies styles to a single page",
                    "Better than inline, but still limited",
                    "Useful for small projects or demos",
                  ],
                  code: "<style>\n  p { color: green; }\n</style>",
                },
                {
                  title: "External CSS",
                  description:
                    "External CSS is written in a separate .css file and linked to HTML.",
                  keyPoints: [
                    "Best practice for real projects",
                    "Reusable across multiple pages",
                    "Easier to maintain and scale",
                  ],
                  code: "/* styles.css */\np {\n  color: blue;\n}",
                  analogy: "Like one uniform used across an entire school",
                },
                {
                  title: "Linking CSS Files",
                  description:
                    "External CSS files are linked using the <link> tag.",
                  keyPoints: [
                    "Placed inside the <head> tag",
                    "Uses rel and href attributes",
                    "Loads styles before page renders",
                  ],
                  code: "<link rel='stylesheet' href='styles.css' />",
                },
                {
                  title: "Why External CSS is Preferred",
                  description: "Professional projects rely on external CSS.",
                  benefits: [
                    "Separation of concerns",
                    "Cleaner HTML",
                    "Easier collaboration",
                    "Better performance with caching",
                  ],
                },
              ],

              visualStructure: `
CSS Application Flow:
┌───────────────┐
│ HTML Content  │
└───────┬───────┘
        ↓
┌─────────────────────┐
│ External CSS File   │
│ selectors + rules   │
└─────────┬───────────┘
          ↓
┌─────────────────────┐
│ Styled Web Page     │
└─────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Overusing inline CSS",
                  consequence: "Messy and unmaintainable code",
                  fix: "Move styles to external CSS file",
                },
                {
                  mistake: "Forgetting to link CSS file",
                  consequence: "Styles not applied",
                  fix: "Check <link> tag and file path",
                },
                {
                  mistake: "Mixing HTML and CSS logic",
                  consequence: "Hard to scale design",
                  fix: "Keep structure and styling separate",
                },
                {
                  mistake: "Writing CSS without understanding syntax",
                  consequence: "Unexpected results",
                  fix: "Understand selector–property–value clearly",
                },
              ],

              proTips: [
                "Always start with external CSS",
                "Keep CSS readable and well-indented",
                "One rule per line improves clarity",
                "Understand before memorizing",
                "Good CSS starts with good structure",
              ],

              realWorldAnalogy:
                "CSS is like interior design:\n- Structure already exists\n- Design enhances usability\n- Poor design ruins good structure",
            },

            tasks: [
              "Create an external CSS file",
              "Link CSS to HTML",
              "Apply basic styles to text",
            ],

            examples: ["p { color: blue; font-size: 16px; }"],

            useCases: [
              "Styling web pages",
              "Separating content from design",
              "Building scalable UI systems",
            ],

            editorType: "css",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "selector { property: value; }",
                "Inline CSS – avoid",
                "Internal CSS – limited use",
                "External CSS – best practice",
                "<link rel='stylesheet'>",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand what CSS does",
              "Write correct CSS syntax",
              "Differentiate CSS application methods",
              "Use external CSS properly",
            ],
          },
          {
            day: 16,
            topic: "CSS Selectors",
            subTopics: [
              "Element selector",
              "Class selector",
              "ID selector",
              "Attribute selector",
              "Grouping selectors",
            ],
            explanation: "Selectors decide which HTML elements will be styled.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Refactor CSS Using Proper Selectors",
                description:
                  "Refactor an existing CSS file by replacing bad selector choices with better ones.",
                requirements: [
                  "Replace ID selectors with class selectors where possible",
                  "Remove unnecessary classes",
                  "Use element selectors only for base styles",
                ],
                constraint:
                  "Do not change HTML structure — only improve CSS selectors.",
                outcome:
                  "You’ll learn how selector choice impacts scalability and maintainability.",
              },
              {
                id: "pt-2",
                title: "Form Styling with Attribute Selectors",
                description: "Style form inputs without adding extra classes.",
                steps: [
                  "Style text inputs using attribute selectors",
                  "Style disabled inputs differently",
                  "Group common heading styles using grouping selectors",
                ],
                outcome:
                  "Confidence in using attribute and grouping selectors professionally.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most CSS bugs happen because selectors are either too weak or too broad.",
              realWorldChecks: [
                "Verify which selector is actually applied in DevTools",
                "Check if styles are leaking to unintended elements",
                "Ensure selectors are not over-specific",
              ],
              commonFinding:
                "Beginners rely heavily on IDs, making future overrides painful.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between class and ID selectors?",
                "Why are ID selectors discouraged for styling?",
                "When would you use attribute selectors?",
                "How do grouping selectors help maintainability?",
              ],
              interviewerPerspective:
                "Selector questions reveal whether a developer thinks short-term or long-term.",
              tip: "Always justify selector choice based on reuse and override flexibility.",
            },
            editorGuidance: {
              starterHint:
                "Start with the simplest selector that does the job.",
              aiMentorPrompts: [
                "Can this selector be reused?",
                "Am I making this selector too specific?",
                "Will this be easy to override later?",
                "Is this selector targeting exactly what I want?",
              ],
              validationChecklist: [
                "Classes used for reusable styles",
                "IDs avoided for styling",
                "Attribute selectors used meaningfully",
                "No unnecessary selector complexity",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "CSS selectors are patterns used to target HTML elements. If you don’t understand selectors well, your CSS will either not work or will apply in places you didn’t expect. Mastering selectors is the foundation of clean, predictable styling.",

              sections: [
                {
                  title: "Element Selector",
                  description:
                    "Element selectors target all HTML elements of a given type.",
                  keyPoints: [
                    "Applies to every matching element on the page",
                    "Very simple and broad",
                    "Useful for base styling",
                  ],
                  code: "p {\n  color: blue;\n}",
                  commonUses: ["Default text styles", "Reset or base styles"],
                  analogy:
                    "Like giving the same uniform to everyone in a group",
                },
                {
                  title: "Class Selector",
                  description:
                    "Class selectors target elements with a specific class attribute.",
                  keyPoints: [
                    "Reusable across multiple elements",
                    "Most commonly used selector",
                    "Prefixed with a dot (.)",
                  ],
                  bestPractices: [
                    "Use meaningful class names",
                    "Prefer classes over IDs for styling",
                  ],
                  code: ".card {\n  padding: 16px;\n  border: 1px solid #ddd;\n}",
                  analogy: "Like a reusable style badge you can apply anywhere",
                },
                {
                  title: "ID Selector",
                  description: "ID selectors target a single unique element.",
                  keyPoints: [
                    "Each ID must be unique on the page",
                    "Prefixed with #",
                    "Has higher specificity than classes",
                  ],
                  bestPractices: [
                    "Avoid using IDs for styling when possible",
                    "Use IDs mainly for JavaScript or anchors",
                  ],
                  code: "#header {\n  background-color: black;\n  color: white;\n}",
                  analogy: "Like a unique name — only one person has it",
                },
                {
                  title: "Attribute Selector",
                  description:
                    "Attribute selectors target elements based on attributes or attribute values.",
                  keyPoints: [
                    "Useful for form elements",
                    "Does not require extra classes",
                    "Very powerful when used carefully",
                  ],
                  commonPatterns: ["[type='text']", "[disabled]", "[href]"],
                  code: "input[type='text'] {\n  border: 1px solid gray;\n}",
                  analogy:
                    "Like filtering people based on what they are wearing",
                },
                {
                  title: "Grouping Selectors",
                  description:
                    "Grouping selectors apply the same styles to multiple selectors.",
                  keyPoints: [
                    "Selectors are separated by commas",
                    "Reduces duplicate CSS",
                    "Improves readability",
                  ],
                  code: "h1, h2, h3 {\n  font-family: Arial, sans-serif;\n}",
                  analogy:
                    "Like giving the same rule to multiple teams at once",
                },
                {
                  title: "Choosing the Right Selector",
                  description:
                    "The right selector keeps your CSS simple and scalable.",
                  guidelines: [
                    "Use element selectors for base styles",
                    "Use class selectors for reusable components",
                    "Avoid ID selectors for styling",
                    "Use attribute selectors sparingly",
                  ],
                },
              ],

              visualStructure: `
Selector Targeting:
┌─────────────────────────────┐
│ Element → p, div, h1        │
│ Class   → .card, .btn       │
│ ID      → #header           │
│ Attr    → input[type=text] │
│ Group   → h1, h2, h3        │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Overusing ID selectors",
                  consequence: "Hard to override styles later",
                  fix: "Use class selectors instead",
                },
                {
                  mistake: "Using too generic selectors",
                  consequence: "Unintended styling across page",
                  fix: "Narrow down with classes",
                },
                {
                  mistake: "Adding unnecessary classes",
                  consequence: "Bloated HTML",
                  fix: "Use element or attribute selectors where suitable",
                },
                {
                  mistake: "Poor class naming",
                  consequence: "Confusing and unreadable CSS",
                  fix: "Use clear, purpose-based names",
                },
              ],

              proTips: [
                "Classes are your best friend in CSS",
                "Avoid styling by IDs unless necessary",
                "Readable selectors = maintainable CSS",
                "Less specificity means easier overrides",
                "Think long-term when choosing selectors",
              ],

              realWorldAnalogy:
                "Selectors are like targeting rules:\n- Element → everyone in the room\n- Class → people wearing a badge\n- ID → a specific person\n- Attribute → people with specific items",
            },

            tasks: [
              "Style elements using class selectors",
              "Apply unique styles using ID",
              "Use attribute selectors",
            ],

            examples: [
              ".card { padding: 16px; }",
              "#header { background-color: black; }",
              "input[type='text'] { border: 1px solid gray; }",
            ],

            useCases: [
              "Reusable components",
              "Targeting specific elements",
              "Form styling",
            ],

            editorType: "css",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "p {} – Element selector",
                ".class {} – Class selector",
                "#id {} – ID selector",
                "[attr=value] {} – Attribute selector",
                "A, B {} – Grouping selectors",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand different types of CSS selectors",
              "Apply selectors correctly and efficiently",
              "Avoid common selector-related mistakes",
              "Write scalable and maintainable CSS",
            ],
          },
          {
            day: 17,
            topic: "Colors, Backgrounds & Opacity",
            subTopics: [
              "color property",
              "background-color",
              "opacity",
              "hex, rgb, rgba, hsl",
            ],
            explanation:
              "Colors and backgrounds define visual identity and branding.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Build a Simple Color Theme",
                description:
                  "Create a basic color theme for a webpage using consistent colors.",
                requirements: [
                  "Define primary, secondary, and background colors",
                  "Apply text color and background color correctly",
                  "Use the same colors consistently across sections",
                ],
                constraint:
                  "Do not use random colors. Stick to a defined palette.",
                outcome:
                  "You’ll understand how real products maintain visual consistency.",
              },
              {
                id: "pt-2",
                title: "Overlay & Transparency Exercise",
                description:
                  "Create an overlay effect without affecting text readability.",
                steps: [
                  "Create a section with background image or color",
                  "Add an overlay using rgba",
                  "Ensure text remains fully opaque",
                ],
                outcome:
                  "Clear understanding of opacity vs rgba in real UI design.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Color mistakes directly affect readability, accessibility, and brand trust.",
              realWorldChecks: [
                "Check text readability on colored backgrounds",
                "Ensure overlays do not reduce text clarity",
                "Verify contrast between text and background",
              ],
              commonFinding:
                "Beginners misuse opacity, accidentally fading all child elements.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between opacity and rgba?",
                "When would you prefer HSL over hex?",
                "How do you ensure good color contrast?",
                "Why is consistency important in color usage?",
              ],
              interviewerPerspective:
                "Color-related questions reveal UI sense and attention to user experience.",
              tip: "Always explain color choices from readability and accessibility perspectives.",
            },
            editorGuidance: {
              starterHint: "Decide your color palette before writing CSS.",
              aiMentorPrompts: [
                "Is my text readable on this background?",
                "Should I use rgba instead of opacity here?",
                "Am I using too many colors?",
                "Is this color choice consistent with the theme?",
              ],
              validationChecklist: [
                "Text and background colors are readable",
                "RGBA used for overlays, not opacity",
                "Limited and consistent color palette",
                "No unintended transparency on child elements",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Colors influence how users feel about a product. CSS gives you multiple ways to apply colors and transparency, allowing you to create clean, accessible, and visually appealing interfaces.",

              sections: [
                {
                  title: "Color Property",
                  description:
                    "The color property defines the text color of an element.",
                  keyPoints: [
                    "Applies to text and inline content",
                    "Does not affect background",
                    "Inherited by child elements",
                  ],
                  code: "h1 {\n  color: #1f2933;\n}",
                  analogy: "Like choosing ink color for text",
                },
                {
                  title: "Background Color",
                  description:
                    "background-color defines the background color of an element.",
                  keyPoints: [
                    "Applies to block and inline elements",
                    "Covers content + padding area",
                    "Commonly used for sections and cards",
                  ],
                  code: "section {\n  background-color: #f1f5f9;\n}",
                  analogy: "Like painting a wall behind text",
                },
                {
                  title: "Opacity",
                  description:
                    "The opacity property controls transparency of an entire element.",
                  keyPoints: [
                    "Value ranges from 0 (transparent) to 1 (opaque)",
                    "Affects text, background, and children",
                    "Use with caution",
                  ],
                  code: ".box {\n  opacity: 0.6;\n}",
                  caution: "Opacity affects all child elements too",
                },
                {
                  title: "Hex Colors",
                  description:
                    "Hexadecimal color codes represent colors using hex values.",
                  keyPoints: [
                    "Starts with #",
                    "Commonly used in design systems",
                    "Can be shortened (#fff)",
                  ],
                  examples: [
                    "#000000 – Black",
                    "#ffffff – White",
                    "#3b82f6 – Blue",
                  ],
                },
                {
                  title: "RGB & RGBA",
                  description:
                    "RGB defines color using red, green, and blue values.",
                  keyPoints: [
                    "Values range from 0–255",
                    "RGBA adds alpha (opacity)",
                    "Best for overlays and transparency",
                  ],
                  code: ".overlay {\n  background-color: rgba(0, 0, 0, 0.5);\n}",
                  analogy:
                    "Like mixing paint with water to control transparency",
                },
                {
                  title: "HSL Colors",
                  description:
                    "HSL defines colors using hue, saturation, and lightness.",
                  keyPoints: [
                    "Hue: color type (0–360)",
                    "Saturation: intensity (%)",
                    "Lightness: brightness (%)",
                  ],
                  benefits: [
                    "More human-readable",
                    "Easy to create color variations",
                  ],
                  code: "button {\n  background-color: hsl(220, 80%, 56%);\n}",
                },
                {
                  title: "Choosing the Right Color Format",
                  description: "Each color format has its use cases.",
                  guidelines: [
                    "Hex – default and common",
                    "RGB/RGBA – dynamic and transparency",
                    "HSL – theming and design systems",
                  ],
                },
              ],

              visualStructure: `
Color Application:
┌─────────────────────────────┐
│ Text Color → color          │
│ Background → background     │
│ Transparency → opacity/RGBA │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using opacity for overlays",
                  consequence: "Text becomes faded",
                  fix: "Use rgba instead of opacity",
                },
                {
                  mistake: "Poor color contrast",
                  consequence: "Unreadable content",
                  fix: "Ensure sufficient contrast",
                },
                {
                  mistake: "Random color usage",
                  consequence: "Inconsistent UI",
                  fix: "Stick to a color palette",
                },
                {
                  mistake: "Overusing transparency",
                  consequence: "Messy design",
                  fix: "Use transparency subtly",
                },
              ],

              proTips: [
                "RGBA is better than opacity for overlays",
                "HSL is great for theme systems",
                "Always think about accessibility contrast",
                "Use fewer colors, not more",
                "Consistency builds brand identity",
              ],

              realWorldAnalogy:
                "Colors are like spices:\n- Too little is boring\n- Too much ruins the dish\n- Balance is everything",
            },

            tasks: [
              "Apply colors to headings and text",
              "Create colored sections",
              "Use opacity and rgba",
            ],

            examples: [
              "body { background-color: #f1f5f9; }",
              ".overlay { background: rgba(0,0,0,0.5); }",
            ],

            useCases: ["Branding", "UI theming", "Modern web interfaces"],

            editorType: "css",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "color – Text color",
                "background-color – Background",
                "opacity – Transparency",
                "hex / rgb / rgba / hsl – Color formats",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Apply colors using different formats",
              "Understand opacity vs RGBA",
              "Build consistent color themes",
              "Avoid common color-related mistakes",
            ],
          },
          {
            day: 18,
            topic: "Typography & Text Styling",
            subTopics: [
              "font-family",
              "font-size",
              "font-weight",
              "line-height",
              "text-align",
              "text-decoration",
            ],
            explanation:
              "Good typography improves readability and user experience.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Readable Blog Typography Setup",
                description:
                  "Style a blog/article page focusing purely on readability and hierarchy.",
                requirements: [
                  "Set base font-family on body",
                  "Define font-size hierarchy for h1–h3 and paragraphs",
                  "Apply proper line-height for paragraphs",
                  "Limit typography to maximum 2 font families",
                ],
                constraint:
                  "Do not focus on colors or layout. Typography only.",
                outcome:
                  "You’ll understand how typography alone can make content feel premium.",
              },
              {
                id: "pt-2",
                title: "Link & Text Decoration UX Exercise",
                description:
                  "Style links and text decorations without harming usability.",
                steps: [
                  "Remove default underline from links",
                  "Add a clear visual cue (color or weight)",
                  "Apply line-through for completed/disabled text",
                ],
                outcome:
                  "Clear understanding of text-decoration tradeoffs in real products.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Poor typography causes users to leave even when content is good.",
              realWorldChecks: [
                "Read long paragraphs for eye strain",
                "Check spacing between lines and sections",
                "Verify headings clearly stand out from body text",
              ],
              commonFinding:
                "Beginners focus on font-size but ignore line-height and hierarchy.",
            },
            interviewInsights: {
              commonQuestions: [
                "What makes typography readable?",
                "Why is line-height important?",
                "When should you avoid center-aligned text?",
                "How many font families should a site use?",
              ],
              interviewerPerspective:
                "Good typography answers show UI maturity, not just CSS knowledge.",
              tip: "Always talk about readability, hierarchy, and user comfort.",
            },
            editorGuidance: {
              starterHint:
                "Start by making paragraphs comfortable to read before styling headings.",
              aiMentorPrompts: [
                "Is this text easy to read for 2–3 minutes?",
                "Are headings clearly distinct from body text?",
                "Is my line-height helping or hurting readability?",
                "Am I using too many font styles?",
              ],
              validationChecklist: [
                "Consistent font-family usage",
                "Clear heading hierarchy",
                "Readable line-height for paragraphs",
                "Links have clear visual indication",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Typography is not just about fonts — it’s about how easily users can read and understand content. Clean typography reduces eye strain, improves engagement, and makes your website feel professional.",

              sections: [
                {
                  title: "Font Family",
                  description:
                    "font-family defines which font is used to display text.",
                  keyPoints: [
                    "Always provide fallback fonts",
                    "Use web-safe or system fonts initially",
                    "Font choice affects brand personality",
                  ],
                  bestPractices: [
                    "Use sans-serif for UI-heavy apps",
                    "Limit to 1–2 font families",
                  ],
                  code: "body {\n  font-family: Arial, Helvetica, sans-serif;\n}",
                  analogy:
                    "Font-family is the handwriting style of your website",
                },
                {
                  title: "Font Size",
                  description: "font-size controls how large the text appears.",
                  keyPoints: [
                    "Can be set using px, em, rem",
                    "Body text is usually 14–18px",
                    "Headings should follow hierarchy",
                  ],
                  bestPractices: [
                    "Use relative units (rem) when possible",
                    "Keep size differences meaningful",
                  ],
                  code: "p {\n  font-size: 16px;\n}\n\nh1 {\n  font-size: 32px;\n}",
                },
                {
                  title: "Font Weight",
                  description:
                    "font-weight controls how thick or bold the text is.",
                  keyPoints: [
                    "Common values: 400 (normal), 700 (bold)",
                    "Some fonts support multiple weights",
                    "Use weight for emphasis, not overuse",
                  ],
                  code: "h1 {\n  font-weight: 700;\n}\n\np {\n  font-weight: 400;\n}",
                  analogy: "Font weight is like speaking louder or softer",
                },
                {
                  title: "Line Height",
                  description:
                    "line-height controls the vertical spacing between lines of text.",
                  keyPoints: [
                    "Improves readability significantly",
                    "Best written as a unitless value",
                    "Especially important for paragraphs",
                  ],
                  bestPractices: [
                    "1.4–1.8 for body text",
                    "Avoid very tight line spacing",
                  ],
                  code: "p {\n  line-height: 1.6;\n}",
                  analogy: "Line-height is breathing space for text",
                },
                {
                  title: "Text Align",
                  description:
                    "text-align controls horizontal alignment of text.",
                  keyPoints: [
                    "left is default for most languages",
                    "center used for headings or banners",
                    "right and justify used sparingly",
                  ],
                  bestPractices: [
                    "Avoid justify for long text",
                    "Left-align content for readability",
                  ],
                  code: "h1 {\n  text-align: center;\n}\n\np {\n  text-align: left;\n}",
                },
                {
                  title: "Text Decoration",
                  description: "text-decoration adds lines to text.",
                  keyPoints: [
                    "Commonly used for links",
                    "Includes underline, line-through, none",
                    "Often removed for custom link styling",
                  ],
                  code: "a {\n  text-decoration: none;\n}\n\n.del {\n  text-decoration: line-through;\n}",
                  caution:
                    "Don’t remove link underline without clear visual cue",
                },
                {
                  title: "Typography Hierarchy",
                  description: "Hierarchy guides users through content.",
                  guidelines: [
                    "Headings are largest and boldest",
                    "Body text is neutral and readable",
                    "Spacing reinforces importance",
                  ],
                  analogy: "Like a newspaper — headline, subheading, body",
                },
              ],

              visualStructure: `
Typography Hierarchy:
┌─────────────────────────────┐
│ H1 – Big, Bold              │
│ H2 – Section Title          │
│ Paragraph – Easy to read    │
│ Small text – Meta info      │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using too many fonts",
                  consequence: "Messy and unprofessional design",
                  fix: "Limit to 1–2 font families",
                },
                {
                  mistake: "Tight line-height",
                  consequence: "Hard to read text",
                  fix: "Increase line-height",
                },
                {
                  mistake: "Center-aligning long paragraphs",
                  consequence: "Poor readability",
                  fix: "Use left alignment",
                },
                {
                  mistake: "Using font-size instead of hierarchy",
                  consequence: "Confusing content structure",
                  fix: "Use headings properly",
                },
              ],

              proTips: [
                "Good typography is invisible — bad typography is obvious",
                "Readability beats fancy fonts",
                "Spacing is as important as font choice",
                "Typography defines perceived quality",
                "Simple typography scales best",
              ],

              realWorldAnalogy:
                "Typography is like speaking clearly:\n- Right tone\n- Proper pauses\n- Easy to understand",
            },

            tasks: [
              "Style blog typography",
              "Apply different font weights",
              "Align text properly",
            ],

            examples: [
              "h1 { font-size: 32px; font-weight: 700; }",
              "p { line-height: 1.6; }",
            ],

            useCases: [
              "Blogs",
              "Content-heavy websites",
              "Documentation platforms",
            ],

            editorType: "css",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "font-family – Typeface selection",
                "font-size – Text size",
                "font-weight – Text thickness",
                "line-height – Line spacing",
                "text-align – Alignment",
                "text-decoration – Underlines & lines",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Apply clean and readable typography",
              "Use font properties correctly",
              "Build clear text hierarchy",
              "Improve UX through typography",
            ],
          },
          {
            day: 19,
            topic: "CSS Box Model",
            subTopics: [
              "Content box",
              "Padding",
              "Border",
              "Margin",
              "Width & height",
              "box-sizing",
            ],
            explanation:
              "The box model controls spacing and layout of elements.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Build a Reusable Card Component",
                description:
                  "Create a reusable card layout using correct box model principles.",
                requirements: [
                  "Fixed width card using box-sizing: border-box",
                  "Padding for internal spacing",
                  "Margin for spacing between cards",
                  "Border for visual separation",
                ],
                constraint:
                  "Do not use positioning or flexbox. Box model only.",
                outcome:
                  "You’ll clearly understand how real UI components are spaced and sized.",
              },
              {
                id: "pt-2",
                title: "Box Model Debugging Exercise",
                description:
                  "Fix layout issues caused by incorrect box model usage.",
                steps: [
                  "Create two boxes with same width",
                  "Add padding and border",
                  "Fix overflow using box-sizing",
                ],
                outcome:
                  "Hands-on clarity on why box-sizing: border-box is industry standard.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most beginner layout bugs come from misunderstanding padding, margin, and width calculations.",
              realWorldChecks: [
                "Inspect element size in browser DevTools",
                "Toggle box-sizing and observe width changes",
                "Check margin collapse behavior",
              ],
              commonFinding:
                "Developers often fight CSS when the box model is the real problem.",
            },
            interviewInsights: {
              commonQuestions: [
                "Explain the CSS box model",
                "Difference between margin and padding?",
                "What does box-sizing do?",
                "Why is box-sizing: border-box preferred?",
              ],
              interviewerPerspective:
                "Box model questions reveal whether a candidate actually understands layout fundamentals.",
              tip: "Always explain box model using real UI examples like cards or sections.",
            },
            editorGuidance: {
              starterHint:
                "Whenever layout breaks, draw the box model before changing CSS.",
              aiMentorPrompts: [
                "Should this spacing be padding or margin?",
                "Is this width including padding and border?",
                "Why is this element overflowing?",
                "Would border-box simplify this?",
              ],
              validationChecklist: [
                "box-sizing: border-box applied",
                "Padding used for internal spacing",
                "Margin used for external spacing",
                "No unexpected overflow",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Every HTML element is treated as a rectangular box. Understanding the CSS box model is essential for controlling spacing, alignment, and layout. Most layout issues in CSS come from misunderstanding the box model.",

              sections: [
                {
                  title: "Content Box",
                  description:
                    "The content box contains the actual content like text or images.",
                  keyPoints: [
                    "Width and height apply to content by default",
                    "Text, images, and children live here",
                    "Does not include padding, border, or margin",
                  ],
                  code: ".box {\n  width: 200px;\n  height: 100px;\n}",
                  analogy: "The content box is the item inside the package",
                },
                {
                  title: "Padding",
                  description:
                    "Padding creates space between content and the border.",
                  keyPoints: [
                    "Padding increases element’s visible size",
                    "Padding is inside the border",
                    "Can be set individually (top, right, bottom, left)",
                  ],
                  code: ".box {\n  padding: 16px;\n}",
                  analogy: "Padding is the bubble wrap around the item",
                },
                {
                  title: "Border",
                  description: "The border wraps padding and content.",
                  keyPoints: [
                    "Border adds to element size",
                    "Can have width, style, and color",
                    "Optional but useful for separation",
                  ],
                  code: ".box {\n  border: 1px solid #ccc;\n}",
                  analogy: "Border is the box itself",
                },
                {
                  title: "Margin",
                  description: "Margin creates space outside the element.",
                  keyPoints: [
                    "Controls distance between elements",
                    "Does not affect element size",
                    "Margins can collapse vertically",
                  ],
                  code: ".box {\n  margin: 12px;\n}",
                  analogy: "Margin is the space between boxes",
                },
                {
                  title: "Width & Height",
                  description:
                    "Width and height control the size of the content box.",
                  keyPoints: [
                    "By default, they apply to content only",
                    "Padding and border add extra size",
                    "Can cause layout issues if misunderstood",
                  ],
                  caution:
                    "Setting width without box-sizing can cause overflow",
                },
                {
                  title: "Box Sizing",
                  description:
                    "box-sizing controls how width and height are calculated.",
                  keyPoints: [
                    "content-box (default): width excludes padding & border",
                    "border-box: width includes padding & border",
                    "border-box is easier to reason about",
                  ],
                  bestPractices: ["Use box-sizing: border-box globally"],
                  code: "* {\n  box-sizing: border-box;\n}",
                  analogy:
                    "border-box is like measuring the full box, not just the contents",
                },
                {
                  title: "Box Model Summary",
                  description:
                    "Understanding the full box model prevents layout bugs.",
                  structure: ["Content → Padding → Border → Margin"],
                },
              ],

              visualStructure: `
CSS Box Model:
┌─────────────────────────────┐
│ Margin                      │
│  ┌───────────────────────┐ │
│  │ Border                │ │
│  │  ┌─────────────────┐ │ │
│  │  │ Padding         │ │ │
│  │  │  Content        │ │ │
│  │  └─────────────────┘ │ │
│  └───────────────────────┘ │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Ignoring box-sizing",
                  consequence: "Unexpected element sizes",
                  fix: "Use box-sizing: border-box",
                },
                {
                  mistake: "Using margin for internal spacing",
                  consequence: "Inconsistent layout",
                  fix: "Use padding for internal spacing",
                },
                {
                  mistake: "Hardcoding width everywhere",
                  consequence: "Poor responsiveness",
                  fix: "Use flexible sizing where possible",
                },
                {
                  mistake: "Confusing margin and padding",
                  consequence: "Broken spacing",
                  fix: "Remember: padding is inside, margin is outside",
                },
              ],

              proTips: [
                "Most CSS layout bugs come from box model confusion",
                "Use padding for breathing space inside components",
                "Use margin to separate components",
                "border-box simplifies mental math",
                "Draw the box model when stuck",
              ],

              realWorldAnalogy:
                "The box model is like packaging:\n- Content is the product\n- Padding is cushioning\n- Border is the box\n- Margin is the space between boxes",
            },

            tasks: [
              "Create card layout using box model",
              "Add spacing between sections",
              "Use box-sizing: border-box",
            ],

            examples: [
              ".card {\n  padding: 16px;\n  margin: 12px;\n  border: 1px solid #ccc;\n}",
            ],

            useCases: ["Cards", "Layouts", "Spacing control"],

            editorType: "css",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "content – Inner area",
                "padding – Inner spacing",
                "border – Element boundary",
                "margin – Outer spacing",
                "box-sizing: border-box",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand the CSS box model deeply",
              "Control spacing and layout correctly",
              "Avoid common box model mistakes",
              "Build clean and predictable layouts",
            ],
          },
          {
            day: 20,
            topic: "CSS Units, Specificity & Pseudo-classes",
            subTopics: [
              "px, em, rem",
              "%, vh, vw",
              "CSS specificity",
              "Cascade & inheritance",
              ":hover, :active, :focus",
            ],
            explanation:
              "Understanding units and specificity prevents styling bugs.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Typography Scaling with rem",
                description:
                  "Convert a px-based typography system into a rem-based scalable system.",
                requirements: [
                  "Set base font-size on html",
                  "Convert all font sizes from px to rem",
                  "Ensure headings scale consistently",
                ],
                constraint: "Do not use em for font sizes in this task.",
                outcome:
                  "You’ll understand why rem is preferred in professional codebases.",
              },
              {
                id: "pt-2",
                title: "Specificity Conflict Resolution",
                description:
                  "Fix CSS rules that are not applying due to specificity issues.",
                steps: [
                  "Create conflicting selectors (element vs class vs ID)",
                  "Predict which rule will win",
                  "Refactor CSS to remove unnecessary specificity",
                ],
                outcome:
                  "You’ll gain confidence debugging real-world CSS bugs.",
              },
              {
                id: "pt-3",
                title: "Accessible Interaction States",
                description:
                  "Style interactive elements with proper pseudo-classes.",
                requirements: [
                  "Add :hover styles for mouse users",
                  "Add :focus styles for keyboard users",
                  "Ensure :focus is clearly visible",
                ],
                outcome:
                  "You’ll learn accessibility-first interaction styling.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most CSS bugs are not syntax errors — they are logic errors caused by units and specificity.",
              realWorldChecks: [
                "Resize base font-size and observe rem scaling",
                "Toggle selectors in DevTools to inspect specificity",
                "Navigate UI using only keyboard to test :focus",
              ],
              commonFinding:
                "Beginners blame CSS when the cascade rules are doing exactly what they should.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between em and rem?",
                "Explain CSS specificity with an example",
                "Why should !important be avoided?",
                "Why is :focus styling important?",
              ],
              interviewerPerspective:
                "Strong answers here show debugging ability, not just CSS syntax knowledge.",
              tip: "Always explain specificity step-by-step instead of memorizing rules.",
            },
            editorGuidance: {
              starterHint:
                "When a style doesn’t apply, ask: unit, specificity, or order?",
              aiMentorPrompts: [
                "Is this unit scalable?",
                "Why is this selector winning?",
                "Can I simplify this selector?",
                "Is my focus state accessible?",
              ],
              validationChecklist: [
                "rem used for typography",
                "No unnecessary !important",
                "Clear and visible :focus styles",
                "Specificity kept minimal and predictable",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "CSS doesn’t fail randomly — it follows rules. Units decide how sizes scale, specificity decides which styles win, and pseudo-classes control interactivity. Mastering these concepts saves hours of debugging.",

              sections: [
                {
                  title: "CSS Units: px",
                  description:
                    "px is an absolute unit representing a fixed size on the screen.",
                  keyPoints: [
                    "Easy to understand",
                    "Does not scale with user settings",
                    "Not ideal for responsive typography",
                  ],
                  commonUses: ["Borders", "Small spacing values"],
                  code: "p {\n  font-size: 16px;\n}",
                  analogy: "px is like a fixed ruler measurement",
                },
                {
                  title: "Relative Units: em & rem",
                  description: "em and rem scale relative to font sizes.",
                  keyPoints: [
                    "em is relative to parent element’s font-size",
                    "rem is relative to root (html) font-size",
                    "rem is more predictable",
                  ],
                  bestPractices: [
                    "Use rem for font sizing",
                    "Avoid deep em nesting",
                  ],
                  code: "html { font-size: 16px; }\n\nh1 { font-size: 2rem; }",
                  analogy: "rem is like global zoom, em is local zoom",
                },
                {
                  title: "Percentage, vh & vw",
                  description:
                    "These units adapt to container or viewport size.",
                  keyPoints: [
                    "% is relative to parent size",
                    "vh = viewport height",
                    "vw = viewport width",
                  ],
                  commonUses: ["Full-height sections", "Responsive layouts"],
                  code: ".hero {\n  height: 100vh;\n  width: 100vw;\n}",
                },
                {
                  title: "CSS Cascade & Inheritance",
                  description:
                    "CSS stands for Cascading Style Sheets — order matters.",
                  keyPoints: [
                    "Later rules override earlier ones",
                    "Some properties are inherited",
                    "Not all properties inherit by default",
                  ],
                  inheritedExamples: ["color", "font-family", "font-size"],
                  analogy: "Like rules passed down in a family",
                },
                {
                  title: "CSS Specificity",
                  description:
                    "Specificity determines which CSS rule wins when multiple rules apply.",
                  specificityOrder: [
                    "Inline styles (highest)",
                    "ID selectors",
                    "Class / attribute selectors",
                    "Element selectors (lowest)",
                  ],
                  keyPoints: [
                    "More specific selector wins",
                    "!important should be avoided",
                  ],
                  code: "#header { color: red; }\n.header { color: blue; }\n/* red wins */",
                  analogy: "Specificity is like authority — higher rank wins",
                },
                {
                  title: "Pseudo-classes (:hover, :active, :focus)",
                  description:
                    "Pseudo-classes style elements based on user interaction.",
                  keyPoints: [
                    ":hover → mouse over",
                    ":active → while clicking",
                    ":focus → keyboard or input focus",
                  ],
                  accessibilityNote: ":focus is critical for keyboard users",
                  code: "button:hover { background-color: blue; }\n\ninput:focus { outline: 2px solid #3b82f6; }",
                },
                {
                  title: "Common Specificity Problems",
                  description:
                    "Most styling bugs come from specificity misunderstandings.",
                  scenarios: [
                    "Class not overriding element selector",
                    "Styles not applying due to order",
                    "Overuse of !important",
                  ],
                },
              ],

              visualStructure: `
CSS Decision Flow:
┌─────────────────────────────┐
│ Which selector?             │
│        ↓                    │
│ Specificity check           │
│        ↓                    │
│ Cascade order               │
│        ↓                    │
│ Final style applied         │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using px everywhere",
                  consequence: "Poor accessibility and scaling",
                  fix: "Use rem for fonts",
                },
                {
                  mistake: "Overusing !important",
                  consequence: "Hard to maintain CSS",
                  fix: "Fix specificity instead",
                },
                {
                  mistake: "Ignoring :focus styles",
                  consequence: "Bad keyboard accessibility",
                  fix: "Always style focus states",
                },
                {
                  mistake: "Mixing em without understanding",
                  consequence: "Unexpected scaling",
                  fix: "Prefer rem for consistency",
                },
              ],

              proTips: [
                "rem is the safest unit for typography",
                "Specificity bugs are easier to prevent than fix",
                ":focus is not optional — it’s accessibility",
                "Avoid !important unless absolutely necessary",
                "Think in systems, not single rules",
              ],

              realWorldAnalogy:
                "CSS rules are like company policies:\n- Higher authority overrides\n- Latest memo applies\n- Clarity prevents chaos",
            },

            tasks: [
              "Use rem for font sizing",
              "Add hover effects to buttons",
              "Debug specificity issues",
            ],

            examples: [
              "button:hover { background-color: blue; }",
              "html { font-size: 16px; }",
            ],

            useCases: [
              "Interactive UI",
              "Responsive-friendly design",
              "Scalable design systems",
            ],

            editorType: "css",

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "px – Fixed unit",
                "em – Parent-relative",
                "rem – Root-relative (recommended)",
                "% / vh / vw – Responsive units",
                "Specificity hierarchy",
                ":hover / :active / :focus",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Choose correct CSS units",
              "Understand cascade and specificity",
              "Apply interactive pseudo-classes",
              "Prevent common CSS styling bugs",
            ],
          },
          {
            day: 21,
            topic: "Week 3 Project – Styled Blog Template",
            subTopics: [
              "Selectors",
              "Typography",
              "Colors",
              "Box model",
              "Hover effects",
            ],
            explanation:
              "Apply all CSS fundamentals by styling your existing HTML blog.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Professional Blog UI Styling",
                description:
                  "Style a complete blog layout using only CSS fundamentals learned in Week 3.",
                requirements: [
                  "External CSS file only",
                  "Class-based selectors for all components",
                  "Typography hierarchy for headings and content",
                  "Consistent spacing using margin & padding",
                  "Hover effects for links and articles",
                ],
                constraint:
                  "Do not use Flexbox, Grid, or advanced layout systems yet.",
                outcome:
                  "A clean, readable blog UI that looks professional without advanced CSS.",
              },
              {
                id: "pt-2",
                title: "CSS Refactor & Cleanup",
                description:
                  "Refactor your CSS to improve readability and maintainability.",
                steps: [
                  "Remove unused selectors",
                  "Group related styles together",
                  "Rename unclear class names",
                  "Reduce unnecessary specificity",
                ],
                outcome:
                  "You’ll experience how professionals polish CSS before shipping.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Real projects are judged by consistency, not number of CSS properties used.",
              realWorldChecks: [
                "Check spacing consistency across sections",
                "Verify text readability for long content",
                "Hover over all interactive elements",
                "Inspect CSS for unnecessary complexity",
              ],
              commonFinding:
                "Most beginner projects fail due to inconsistent spacing and typography, not lack of features.",
            },
            interviewInsights: {
              commonQuestions: [
                "How did you structure your CSS for this blog?",
                "How did you decide typography sizes and spacing?",
                "Why did you avoid IDs for styling?",
                "How do hover states improve user experience?",
              ],
              interviewerPerspective:
                "A styled blog project shows CSS fundamentals, UI sense, and attention to detail.",
              tip: "Explain design decisions in terms of readability, consistency, and scalability.",
            },
            editorGuidance: {
              starterHint:
                "Treat this project as if it will be reviewed by another developer.",
              aiMentorPrompts: [
                "Is my CSS easy to read and understand?",
                "Are my class names meaningful?",
                "Is spacing consistent across the layout?",
                "Does the page feel comfortable to read?",
              ],
              validationChecklist: [
                "External CSS linked correctly",
                "Consistent typography hierarchy",
                "Box model used correctly everywhere",
                "Hover and focus states applied",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "CSS only starts making sense when you apply it to a real layout. In this project, you will take a plain HTML blog structure (from previous weeks) and turn it into a clean, readable, and professional-looking blog using only CSS fundamentals.",

              sections: [
                {
                  title: "Project Overview",
                  description:
                    "You will style a simple blog page containing headings, paragraphs, images, and links.",
                  requirements: [
                    "Use only HTML + CSS (no JS)",
                    "External CSS file only",
                    "Clean, readable, and consistent styling",
                    "Desktop-first layout",
                  ],
                  output: "index.html + styles.css",
                  analogy:
                    "This is where your HTML skeleton finally gets a proper outfit",
                },
                {
                  title: "Required Blog Sections",
                  description:
                    "Your blog page should contain these core sections.",
                  structure: [
                    "Header – Blog title & navigation",
                    "Main content – Blog articles",
                    "Article – Title, content, image",
                    "Footer – Author info or copyright",
                  ],
                  recommendedTags: [
                    "<header>",
                    "<main>",
                    "<article>",
                    "<section>",
                    "<footer>",
                  ],
                },
                {
                  title: "Styling Requirements",
                  description: "Apply everything you learned in Week 3.",
                  checklist: [
                    "Use class selectors for components",
                    "Apply typography hierarchy (h1, h2, p)",
                    "Use consistent color palette",
                    "Apply padding, margin, and borders correctly",
                    "Use box-sizing: border-box",
                  ],
                },
                {
                  title: "Typography Guidelines",
                  description: "Focus on readability over decoration.",
                  rules: [
                    "Readable font-family (system or sans-serif)",
                    "Body font-size around 16px (1rem)",
                    "Line-height between 1.5–1.7",
                    "Clear spacing between paragraphs",
                  ],
                  reminder: "If content is easy to read, typography is working",
                },
                {
                  title: "Color & Background Usage",
                  description: "Use color intentionally, not randomly.",
                  rules: [
                    "Light background with dark text",
                    "One primary accent color",
                    "Avoid too many colors",
                  ],
                  exampleUse: "Accent color for links and headings",
                },
                {
                  title: "Box Model & Layout",
                  description: "Spacing defines quality of layout.",
                  bestPractices: [
                    "Padding inside cards and sections",
                    "Margin between sections",
                    "No content touching edges",
                  ],
                  analogy: "Good spacing is invisible, bad spacing is obvious",
                },
                {
                  title: "Hover & Interaction Effects",
                  description: "Add subtle interactivity.",
                  interactions: [
                    "Hover effect on links",
                    "Hover effect on article cards",
                    "Focus styles for accessibility",
                  ],
                  example: "a:hover { text-decoration: underline; }",
                },
                {
                  title: "CSS Organization",
                  description: "Well-organized CSS is easier to maintain.",
                  guidelines: [
                    "Group related styles together",
                    "Avoid deeply nested selectors",
                    "Use meaningful class names",
                  ],
                },
              ],

              visualStructure: `
Styled Blog Layout:
┌─────────────────────────────┐
│ Header                      │
│  Blog Title | Nav           │
├─────────────────────────────┤
│ Main                        │
│  ┌──────── Article ──────┐ │
│  │ Title                 │ │
│  │ Image                 │ │
│  │ Content               │ │
│  └──────────────────────┘ │
│  ┌──────── Article ──────┐ │
│  │ Title                 │ │
│  │ Content               │ │
│  └──────────────────────┘ │
├─────────────────────────────┤
│ Footer                      │
│  Author / Copyright         │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Over-styling everything",
                  consequence: "Cluttered and noisy UI",
                  fix: "Keep styles minimal and purposeful",
                },
                {
                  mistake: "Inconsistent spacing",
                  consequence: "Unprofessional look",
                  fix: "Standardize margins and padding",
                },
                {
                  mistake: "Using IDs for styling",
                  consequence: "Hard to scale CSS",
                  fix: "Prefer class selectors",
                },
                {
                  mistake: "Ignoring hover/focus states",
                  consequence: "Poor UX and accessibility",
                  fix: "Always style interaction states",
                },
              ],

              proTips: [
                "CSS quality is judged by consistency",
                "Whitespace is part of design",
                "Good typography beats fancy effects",
                "If something looks off, it’s probably spacing",
                "This project is portfolio-worthy",
              ],

              realWorldAnalogy:
                "Styling a blog is like editing a book:\n- Content already exists\n- Design makes it enjoyable to read",
            },

            tasks: [
              "Style blog layout",
              "Improve typography",
              "Add hover interactions",
              "Ensure spacing consistency",
            ],

            examples: [],

            useCases: [
              "Real-world blog UI",
              "Portfolio-ready project",
              "CSS fundamentals showcase",
            ],

            editorType: null,

            // 📝 Quick Reference
            cheatsheet: {
              mustHave: [
                "External CSS file",
                "Class-based selectors",
                "Consistent spacing system",
                "Readable typography",
                "Hover & focus styles",
              ],
            },

            // ⏱️ Estimated time
            estimatedTime: "90–120 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Apply CSS fundamentals in a real project",
              "Build confidence with layout and styling",
              "Create a portfolio-quality blog UI",
              "Prepare for Flexbox & layout systems",
            ],
          },
        ],
      },
      {
        week: 4,
        weekGoal: "Master CSS Flexbox to build modern responsive layouts",
        weekOutcome: "Ability to design real-world page layouts using Flexbox",
        canLearn: ["students", "beginners"],
        miniProject: "Responsive Product Landing Page",
        days: [
          {
            day: 1,
            topic: "Display Property & Flexbox Introduction",
            subTopics: [
              "display: block vs inline vs inline-block",
              "display: flex",
              "Why Flexbox was introduced",
              "Flex container vs flex items",
            ],
            explanation:
              "Flexbox is a one-dimensional layout system that helps align and distribute space between items efficiently. Understanding display properties is the foundation before using Flexbox.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Flexbox Layout Recreation (Industry Drill)",
                description:
                  "Recreate a real-world horizontal layout using Flexbox only.",
                requirements: [
                  "One parent container with display: flex",
                  "Minimum 4 flex items",
                  "Horizontal alignment without floats",
                  "No margin hacks for spacing",
                ],
                constraint:
                  "Floats, positioning, and inline-block are strictly not allowed.",
                outcome:
                  "You’ll understand why Flexbox replaced old layout techniques in modern UI.",
              },
              {
                id: "pt-2",
                title: "Flexbox Debugging Exercise",
                description:
                  "Intentionally break and fix Flexbox layouts to understand behavior deeply.",
                steps: [
                  "Remove display: flex and observe layout",
                  "Apply flex to child instead of parent",
                  "Fix the layout correctly",
                ],
                outcome:
                  "You’ll develop the habit of debugging layout issues like a professional.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most Flexbox bugs happen because developers misunderstand the parent-child relationship.",
              realWorldChecks: [
                "Confirm display: flex is applied to parent",
                "Check flex direction (row vs column)",
                "Inspect layout using browser DevTools",
              ],
              commonFinding:
                "Beginners apply flex properties to items without defining a flex container.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between block, inline, and inline-block?",
                "Why was Flexbox introduced?",
                "What is a flex container vs flex item?",
                "When would you avoid using Flexbox?",
              ],
              interviewerPerspective:
                "Flexbox basics test layout thinking, not memorization.",
              tip: "Always explain Flexbox from a problem-solving and layout perspective.",
            },
            editorGuidance: {
              starterHint:
                "If Flexbox is not working, first check the parent element.",
              aiMentorPrompts: [
                "Did I set display: flex on the parent?",
                "Am I thinking in one direction (row or column)?",
                "Is Flexbox the right tool here?",
                "Did I confuse container and item properties?",
              ],
              validationChecklist: [
                "display: flex applied on parent",
                "Flex items are direct children",
                "No floats or hacks used",
                "Layout behaves predictably",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Before Flexbox, CSS layouts were built using floats, positioning, and hacks. These methods were hard to maintain and unreliable. Flexbox was introduced to make layout alignment, spacing, and ordering simple and predictable.",

              sections: [
                {
                  title: "Display Property Basics",
                  description:
                    "The display property controls how an element behaves in the layout.",
                  keyPoints: [
                    "block elements take full width and start on a new line",
                    "inline elements take only content width and stay in the same line",
                    "inline-block allows width & height while staying inline",
                  ],
                  code: "div { display: block; }\nspan { display: inline; }\nimg { display: inline-block; }",
                  analogy:
                    "Block is like a full row seat, inline is like sitting shoulder to shoulder",
                },
                {
                  title: "Problems with Old Layout Methods",
                  description:
                    "Traditional CSS layout techniques had major limitations.",
                  problems: [
                    "Vertical centering was difficult",
                    "Equal height columns were hard",
                    "Floats were not designed for layouts",
                    "Complex hacks were common",
                  ],
                  takeaway: "CSS needed a better layout system",
                },
                {
                  title: "What is Flexbox?",
                  description:
                    "Flexbox is a one-dimensional layout system designed for alignment and spacing.",
                  keyPoints: [
                    "Works in one direction at a time (row or column)",
                    "Automatically distributes space",
                    "Solves most alignment problems",
                  ],
                  code: ".container {\n  display: flex;\n}",
                  analogy:
                    "Flexbox is like a smart shelf that arranges items automatically",
                },
                {
                  title: "Flex Container vs Flex Items",
                  description:
                    "Flexbox works using a parent-child relationship.",
                  keyPoints: [
                    "The parent becomes a flex container",
                    "Direct children become flex items",
                    "Layout properties apply differently to container and items",
                  ],
                  code: ".container { display: flex; }\n.box { width: 100px; height: 100px; }",
                  warning:
                    "Flex properties won’t work unless display: flex is set on parent",
                },
                {
                  title: "When to Use Flexbox",
                  description: "Flexbox is best for one-dimensional layouts.",
                  useGuidelines: [
                    "Navbars",
                    "Buttons alignment",
                    "Cards in a row",
                    "Centering content",
                  ],
                  note: "For two-dimensional layouts, CSS Grid is better",
                },
              ],

              visualStructure: `
Display vs Flexbox:
┌─────────────────────────────┐
│ Block → Full width          │
│ Inline → Content width      │
│ Inline-block → Sized inline │
│ Flex → Smart layout system  │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using flex properties without display: flex",
                  consequence: "Flex styles don’t work",
                  fix: "Always define display: flex on parent",
                },
                {
                  mistake: "Trying to use Flexbox everywhere",
                  consequence: "Overcomplicated layouts",
                  fix: "Use Flexbox only for one-dimensional layouts",
                },
                {
                  mistake: "Confusing container and item properties",
                  consequence: "Unexpected behavior",
                  fix: "Know which properties apply to parent vs child",
                },
              ],

              proTips: [
                "Always start Flexbox from the parent",
                "Think in rows or columns",
                "Flexbox replaces many old layout hacks",
                "Practice by breaking layouts intentionally",
              ],

              realWorldAnalogy:
                "Flexbox is like arranging items on a shelf:\n- Shelf decides direction\n- Items adjust automatically\n- Spacing becomes effortless",
            },

            tasks: [
              "Convert block and inline elements into flex containers",
              "Observe layout differences between block, inline, and flex",
              "Create a simple horizontal layout using display: flex",
            ],

            examples: [
              ".container { display: flex; }",
              ".box { width: 100px; height: 100px; }",
            ],

            useCases: [
              "Basic horizontal layouts",
              "Replacing float-based layouts",
              "Navigation bars",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand display property differences",
              "Know why Flexbox was introduced",
              "Identify flex containers and flex items",
              "Create basic flex layouts",
            ],
          },
          {
            day: 2,
            topic: "Flex Container Properties",
            subTopics: [
              "flex-direction (row, column)",
              "justify-content",
              "align-items",
              "Main axis vs Cross axis",
            ],
            explanation:
              "Flex container properties control how items are laid out along the main and cross axis. These are the most used properties in real layouts.",

            // 📚 Detailed Learning Content
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real-World Navbar Alignment (Flexbox)",
                description:
                  "Build a professional navigation bar layout using Flexbox container properties.",
                requirements: [
                  "Logo aligned on left",
                  "Navigation links aligned on right",
                  "Vertical centering of all items",
                  "Use justify-content and align-items only",
                ],
                constraint:
                  "No margins for alignment. Only Flexbox container properties allowed.",
                outcome:
                  "You’ll learn how real navigation bars are aligned in production apps.",
              },
              {
                id: "pt-2",
                title: "Flex Axis Mental Model Drill",
                description:
                  "Train yourself to identify main axis and cross axis before writing CSS.",
                steps: [
                  "Create one row layout",
                  "Convert it into a column layout",
                  "Re-align items correctly after axis change",
                ],
                outcome:
                  "You’ll stop guessing and start thinking correctly about Flexbox axes.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Incorrect axis understanding leads to broken layouts and wasted debugging time.",
              realWorldChecks: [
                "Verify flex-direction before alignment",
                "Check justify-content behavior after axis change",
                "Inspect layout changes using DevTools",
              ],
              commonFinding:
                "Beginners memorize properties but don’t understand axis-based behavior.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the main axis in Flexbox?",
                "How does flex-direction affect alignment?",
                "Difference between justify-content and align-items?",
                "How do you center a div using Flexbox?",
              ],
              interviewerPerspective:
                "Axis clarity shows whether the candidate truly understands Flexbox.",
              tip: "Explain Flexbox answers by first defining the axis, then the alignment.",
            },
            editorGuidance: {
              starterHint:
                "Before writing any Flexbox property, identify the main axis.",
              aiMentorPrompts: [
                "What is my flex-direction?",
                "Which axis am I aligning on?",
                "Am I using justify-content correctly?",
                "Should align-items change after axis switch?",
              ],
              validationChecklist: [
                "display: flex applied on parent",
                "flex-direction defined intentionally",
                "justify-content used for main axis",
                "align-items used for cross axis",
              ],
            },

            content: {
              introduction:
                "Once an element becomes a flex container, you control layout using a small set of powerful properties. Understanding axes and alignment is the key to mastering Flexbox.",

              sections: [
                {
                  title: "Main Axis vs Cross Axis",
                  description: "Everything in Flexbox is based on two axes.",
                  keyPoints: [
                    "Main axis follows flex-direction",
                    "Cross axis is perpendicular to main axis",
                    "Alignment properties depend on axis direction",
                  ],
                  table: [
                    "flex-direction: row → main axis is horizontal",
                    "flex-direction: column → main axis is vertical",
                  ],
                  analogy:
                    "Think of main axis as the flow direction and cross axis as the side alignment",
                },
                {
                  title: "flex-direction",
                  description:
                    "flex-direction defines the direction in which flex items are placed.",
                  values: [
                    "row (default)",
                    "column",
                    "row-reverse",
                    "column-reverse",
                  ],
                  code: ".container {\n  display: flex;\n  flex-direction: row;\n}",
                  useCase: "Switching between horizontal and vertical layouts",
                },
                {
                  title: "justify-content",
                  description:
                    "justify-content aligns items along the main axis.",
                  values: [
                    "flex-start",
                    "center",
                    "flex-end",
                    "space-between",
                    "space-around",
                    "space-evenly",
                  ],
                  code: ".container {\n  justify-content: center;\n}",
                  analogy: "Like deciding how items are spaced along a shelf",
                },
                {
                  title: "align-items",
                  description: "align-items aligns items along the cross axis.",
                  values: [
                    "stretch (default)",
                    "center",
                    "flex-start",
                    "flex-end",
                    "baseline",
                  ],
                  code: ".container {\n  align-items: center;\n}",
                  note: "align-items works perpendicular to justify-content",
                },
                {
                  title: "Centering Content with Flexbox",
                  description: "Flexbox makes centering effortless.",
                  steps: [
                    "Set display: flex on parent",
                    "Use justify-content: center",
                    "Use align-items: center",
                  ],
                  code: ".container {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n}",
                  takeaway: "This is the most common Flexbox pattern",
                },
                {
                  title: "When to Use These Properties",
                  description:
                    "These properties appear in almost every layout.",
                  examples: [
                    "Navbar alignment",
                    "Hero section centering",
                    "Buttons alignment",
                    "Card layouts",
                  ],
                },
              ],

              visualStructure: `
Flex Axes:
┌─────────────────────────────┐
│ Main Axis  →→→              │
│                             │
│ Cross Axis ↓↓↓               │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Confusing main axis and cross axis",
                  consequence: "Incorrect alignment",
                  fix: "Always check flex-direction first",
                },
                {
                  mistake:
                    "Using align-items for horizontal centering in row layout",
                  consequence: "Elements don’t move as expected",
                  fix: "Use justify-content for main axis",
                },
                {
                  mistake: "Forgetting display: flex",
                  consequence: "Flex properties don’t work",
                  fix: "Set display: flex on container",
                },
              ],

              proTips: [
                "Always start by identifying the main axis",
                "Centering is justify-content + align-items",
                "Most layouts use row direction",
                "Master these 3 properties and Flexbox becomes easy",
              ],

              realWorldAnalogy:
                "Flexbox alignment is like arranging items on a shelf:\n- Shelf direction = flex-direction\n- Spacing = justify-content\n- Height alignment = align-items",
            },

            tasks: [
              "Create a horizontal and vertical layout using flex-direction",
              "Center elements using justify-content and align-items",
              "Experiment with different alignment combinations",
            ],

            examples: [
              ".container { display: flex; justify-content: center; align-items: center; }",
            ],

            useCases: [
              "Centering content",
              "Navbar alignment",
              "Hero section layouts",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand main vs cross axis",
              "Use flex-direction correctly",
              "Align items using justify-content and align-items",
              "Center content confidently using Flexbox",
            ],
          },
          {
            day: 3,
            topic: "Flex Item Properties",
            subTopics: [
              "flex-grow",
              "flex-shrink",
              "flex-basis",
              "order",
              "flex shorthand",
            ],
            explanation:
              "Flex item properties control how individual items behave inside a flex container, allowing dynamic resizing and reordering.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Responsive Sidebar + Content Layout",
                description:
                  "Build a real-world two-column layout using Flex item properties.",
                requirements: [
                  "One sidebar and one main content area",
                  "Sidebar smaller than main section",
                  "Use flex-grow / flex-basis for sizing",
                  "No fixed widths allowed",
                ],
                constraint:
                  "Width property is not allowed. Use only flex item properties.",
                outcome:
                  "You’ll learn how dashboards and app layouts handle flexible space distribution.",
              },
              {
                id: "pt-2",
                title: "Flex Item Behavior Experiment",
                description:
                  "Experiment with grow, shrink, and basis to build intuition.",
                steps: [
                  "Set different flex-grow values and observe changes",
                  "Reduce container width to trigger flex-shrink",
                  "Replace width with flex-basis",
                ],
                outcome:
                  "You’ll stop guessing and start predicting Flexbox behavior confidently.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Flex item properties decide how layouts adapt across screen sizes without media queries.",
              realWorldChecks: [
                "Resize browser and observe item resizing",
                "Check how flex-grow ratios affect layout",
                "Ensure layout adapts without overflow",
              ],
              commonFinding:
                "Beginners rely on width instead of flex-basis, breaking responsiveness.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the difference between flex-grow and flex-basis?",
                "How does flex-shrink work?",
                "What does flex: 1 mean?",
                "When should you avoid using order?",
              ],
              interviewerPerspective:
                "Flex item questions reveal understanding of responsive thinking, not syntax.",
              tip: "Explain flex item behavior using ratios and space distribution examples.",
            },
            editorGuidance: {
              starterHint:
                "If spacing feels wrong, inspect flex-grow ratios instead of adding widths.",
              aiMentorPrompts: [
                "Am I using width unnecessarily?",
                "Should this size be flex-basis instead?",
                "Do my grow values make sense as ratios?",
                "Is changing order really needed?",
              ],
              validationChecklist: [
                "flex-grow values used intentionally",
                "flex-basis used instead of width",
                "order used sparingly",
                "Layout adapts on resize",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Once a parent becomes a flex container, its children (flex items) can behave intelligently. Flex item properties decide how much space each item takes, how it shrinks, and even its visual order.",

              sections: [
                {
                  title: "flex-grow",
                  description:
                    "flex-grow defines how much an item can grow relative to other items.",
                  keyPoints: [
                    "Default value is 0",
                    "Higher value = takes more available space",
                    "Works relative to other flex items",
                  ],
                  code: ".item { flex-grow: 1; }\n.item-large { flex-grow: 2; }",
                  analogy:
                    "Like dividing extra space among people based on priority",
                },
                {
                  title: "flex-shrink",
                  description:
                    "flex-shrink defines how items shrink when space is limited.",
                  keyPoints: [
                    "Default value is 1",
                    "Higher value shrinks faster",
                    "Useful when container is smaller than content",
                  ],
                  code: ".item { flex-shrink: 1; }",
                  note: "Often works automatically, rarely changed manually",
                },
                {
                  title: "flex-basis",
                  description:
                    "flex-basis defines the initial size of an item before space distribution.",
                  keyPoints: [
                    "Acts like width in flex layouts",
                    "Can be px, %, auto",
                    "Used before grow and shrink calculations",
                  ],
                  code: ".item { flex-basis: 200px; }",
                  analogy: "Starting size before negotiation begins",
                },
                {
                  title: "Order Property",
                  description:
                    "order changes the visual order of flex items without changing HTML.",
                  keyPoints: [
                    "Default order is 0",
                    "Lower order appears first",
                    "Does NOT change DOM order",
                  ],
                  code: ".item { order: 2; }\n.item-first { order: 1; }",
                  warning:
                    "Changing order can affect accessibility and keyboard navigation",
                },
                {
                  title: "Flex Shorthand",
                  description: "flex is shorthand for grow, shrink, and basis.",
                  syntax: "flex: <grow> <shrink> <basis>",
                  commonPatterns: [
                    "flex: 1 → grow equally",
                    "flex: 2 → grow twice as much",
                  ],
                  code: ".item { flex: 1; }\n.item-wide { flex: 2; }",
                  takeaway: "flex shorthand is used most in real projects",
                },
                {
                  title: "Creating Uneven Columns",
                  description: "Flex item properties make uneven layouts easy.",
                  example: "Sidebar + main content layout",
                  code: ".sidebar { flex: 1; }\n.main { flex: 3; }",
                },
              ],

              visualStructure: `
Flex Item Space Distribution:
┌──────────┬──────────────────┐
│ Item 1   │ Item 2 (larger)  │
│ flex: 1  │ flex: 2          │
└──────────┴──────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using width instead of flex-basis",
                  consequence: "Layout doesn’t adapt well",
                  fix: "Prefer flex-basis in flex layouts",
                },
                {
                  mistake: "Overusing order",
                  consequence: "Accessibility issues",
                  fix: "Keep DOM order logical",
                },
                {
                  mistake: "Not understanding flex shorthand",
                  consequence: "Confusing behavior",
                  fix: "Learn how grow, shrink, basis work together",
                },
              ],

              proTips: [
                "flex: 1 is your best friend",
                "Use flex-basis instead of width",
                "Avoid changing order unless necessary",
                "Think in ratios, not pixels",
              ],

              realWorldAnalogy:
                "Flex items behave like team members:\n- flex-grow decides workload\n- flex-basis sets starting role\n- order changes seating, not identity",
            },

            tasks: [
              "Resize items using flex-grow and flex-basis",
              "Reorder elements visually using order",
              "Create uneven column layouts",
            ],

            examples: [
              ".item { flex: 1; }",
              ".item-large { flex: 2; order: 1; }",
            ],

            useCases: [
              "Responsive columns",
              "Dynamic content resizing",
              "Sidebar layouts",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand flex-grow, shrink, and basis",
              "Use flex shorthand effectively",
              "Create uneven and adaptive layouts",
              "Control layout without changing HTML",
            ],
          },
          {
            day: 4,
            topic: "Flex Wrap & Multi-row Layouts",
            subTopics: [
              "flex-wrap",
              "align-content",
              "Wrapping behavior",
              "Row vs column wrapping",
            ],
            explanation:
              "Flex wrapping allows items to move onto multiple rows or columns, which is essential for responsive layouts.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Responsive Card Grid (Real Product Layout)",
                description:
                  "Build a responsive card layout similar to real product listing pages.",
                requirements: [
                  "Minimum 8 cards",
                  "Cards wrap automatically on smaller screens",
                  "Use flex-wrap and gap for spacing",
                  "No fixed widths for container",
                ],
                constraint:
                  "CSS Grid is not allowed. Use Flexbox wrapping only.",
                outcome:
                  "You’ll understand how e-commerce and dashboard grids are built using Flexbox.",
              },
              {
                id: "pt-2",
                title: "Multi-row Alignment Experiment",
                description:
                  "Understand the difference between align-items and align-content in wrapped layouts.",
                steps: [
                  "Create a wrapped flex layout",
                  "Apply align-items and observe no row spacing change",
                  "Replace it with align-content and compare behavior",
                ],
                outcome:
                  "You’ll clearly understand row-level vs item-level alignment.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most responsive Flexbox layouts fail because wrapping and row alignment are misunderstood.",
              realWorldChecks: [
                "Resize screen and observe wrapping behavior",
                "Check spacing consistency between rows",
                "Ensure no overflow on small screens",
              ],
              commonFinding:
                "Beginners expect wrapping automatically and misuse align-items for row spacing.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the default value of flex-wrap?",
                "Difference between align-items and align-content?",
                "When does align-content work?",
                "How would you build a responsive grid using Flexbox?",
              ],
              interviewerPerspective:
                "Multi-row Flexbox questions test real-world layout experience.",
              tip: "Always mention wrapping behavior before talking about align-content.",
            },
            editorGuidance: {
              starterHint:
                "If your layout looks squished, check whether flex-wrap is enabled.",
              aiMentorPrompts: [
                "Did I enable flex-wrap?",
                "Do I actually have multiple rows?",
                "Should this spacing be gap or align-content?",
                "Am I forcing widths unnecessarily?",
              ],
              validationChecklist: [
                "flex-wrap enabled",
                "gap used for spacing",
                "align-content applied only for multi-row layouts",
                "Layout adapts smoothly on resize",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "By default, Flexbox tries to fit all items in a single line. Real-world layouts rarely work this way. Flex wrapping allows items to flow into multiple rows or columns, making layouts responsive and adaptable.",

              sections: [
                {
                  title: "Default Flexbox Behavior",
                  description: "Flexbox does NOT wrap items by default.",
                  keyPoints: [
                    "flex-wrap default value is nowrap",
                    "Items shrink to fit container",
                    "Can cause overflow or squished layouts",
                  ],
                  code: ".container {\n  display: flex;\n  flex-wrap: nowrap;\n}",
                  analogy: "Like forcing everyone to sit on one bench",
                },
                {
                  title: "flex-wrap",
                  description:
                    "flex-wrap allows items to move onto multiple lines.",
                  values: ["nowrap (default)", "wrap", "wrap-reverse"],
                  code: ".container {\n  display: flex;\n  flex-wrap: wrap;\n}",
                  useCase: "Card layouts, galleries, responsive grids",
                },
                {
                  title: "Wrapping Behavior Explained",
                  description:
                    "When wrapping is enabled, items move to the next line when space runs out.",
                  keyPoints: [
                    "Each row behaves like a mini flex container",
                    "flex-grow and basis still apply",
                    "Item widths control wrapping",
                  ],
                  example: "Cards wrap automatically on smaller screens",
                },
                {
                  title: "Row vs Column Wrapping",
                  description: "Wrapping direction depends on flex-direction.",
                  keyPoints: [
                    "row + wrap → items wrap to new rows",
                    "column + wrap → items wrap to new columns",
                  ],
                  code: ".container {\n  flex-direction: row;\n  flex-wrap: wrap;\n}",
                  note: "Row wrapping is most commonly used",
                },
                {
                  title: "align-content",
                  description:
                    "align-content controls spacing between rows or columns.",
                  keyPoints: [
                    "Works only when wrapping creates multiple lines",
                    "Aligns rows along the cross axis",
                    "Different from align-items",
                  ],
                  values: [
                    "flex-start",
                    "center",
                    "space-between",
                    "space-around",
                    "stretch",
                  ],
                  code: ".container {\n  align-content: space-between;\n}",
                  analogy: "Like spacing shelves inside a cupboard",
                },
                {
                  title: "Using Gap with Flexbox",
                  description: "gap adds spacing between flex items and rows.",
                  keyPoints: [
                    "Cleaner than using margins",
                    "Works with flex-wrap",
                    "Controls row and column spacing",
                  ],
                  code: ".container {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 16px;\n}",
                  takeaway: "gap is the modern way to space flex items",
                },
              ],

              visualStructure: `
Wrapped Flex Layout:
┌────────┬────────┬────────┐
│ Card 1 │ Card 2 │ Card 3 │
├────────┼────────┼────────┤
│ Card 4 │ Card 5 │ Card 6 │
└────────┴────────┴────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Expecting items to wrap automatically",
                  consequence: "Items overflow or shrink too much",
                  fix: "Enable flex-wrap: wrap",
                },
                {
                  mistake: "Using align-items instead of align-content",
                  consequence: "Row spacing doesn’t change",
                  fix: "Use align-content for multi-row layouts",
                },
                {
                  mistake: "Using margins instead of gap",
                  consequence: "Inconsistent spacing",
                  fix: "Prefer gap for flex layouts",
                },
              ],

              proTips: [
                "flex-wrap is essential for responsive grids",
                "gap works beautifully with wrapped flex layouts",
                "align-content works only with multiple rows",
                "Think in rows, not fixed widths",
              ],

              realWorldAnalogy:
                "Flex wrapping is like parking cars:\n- One row fills first\n- Extra cars move to the next row\n- Spacing keeps things organized",
            },

            tasks: [
              "Create a card grid using flex-wrap",
              "Control spacing between rows using align-content",
              "Build a responsive image gallery",
            ],

            examples: [
              ".container { display: flex; flex-wrap: wrap; gap: 16px; }",
            ],

            useCases: ["Card grids", "Product listings", "Image galleries"],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Enable multi-row flex layouts",
              "Understand wrapping behavior",
              "Use align-content correctly",
              "Build responsive grids with Flexbox",
            ],
          },
          {
            day: 5,
            topic: "Common Flexbox Layout Patterns",
            subTopics: [
              "Navbar layouts",
              "Sidebar + content layout",
              "Equal height columns",
              "Sticky footer pattern",
            ],
            explanation:
              "These patterns are widely used in real products and interviews. Mastering them makes layout building intuitive.",

            // 📚 Detailed Learning Content
            premiumTasks: [
              {
                id: "pt-1",
                title: "Dashboard Layout (Navbar + Sidebar + Content)",
                description:
                  "Build a real-world dashboard layout using only Flexbox patterns.",
                requirements: [
                  "Top navbar with left logo and right actions",
                  "Sidebar + main content layout below navbar",
                  "Equal height behavior without fixed heights",
                  "Sticky footer at the bottom",
                ],
                constraint: "No floats, no absolute positioning, no CSS Grid.",
                outcome:
                  "You’ll be able to build most SaaS dashboards confidently using Flexbox.",
              },
              {
                id: "pt-2",
                title: "Flexbox Pattern Identification Drill",
                description:
                  "Train yourself to recognize which Flexbox pattern fits a layout.",
                steps: [
                  "Take 3 real websites (dashboard / landing page)",
                  "Identify navbar, sidebar, and footer patterns",
                  "Rebuild layout using minimal Flexbox rules",
                ],
                outcome:
                  "You’ll stop guessing layouts and start recognizing reusable patterns.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most real-world UI bugs come from misunderstanding common layout patterns, not Flexbox syntax.",
              realWorldChecks: [
                "Resize screen and verify layout stability",
                "Ensure sidebar and content resize proportionally",
                "Confirm footer sticks to bottom on short pages",
              ],
              commonFinding:
                "Beginners overuse fixed heights and widths instead of flex growth.",
            },
            interviewInsights: {
              commonQuestions: [
                "How would you build a navbar layout using Flexbox?",
                "How do you create equal height columns?",
                "Explain the sticky footer pattern using Flexbox",
                "Why is Flexbox preferred over floats for layouts?",
              ],
              interviewerPerspective:
                "Layout pattern questions test practical experience, not theory.",
              tip: "Explain layout answers using patterns rather than individual properties.",
            },
            editorGuidance: {
              starterHint:
                "Before writing CSS, identify which layout pattern you are implementing.",
              aiMentorPrompts: [
                "Is this a navbar, sidebar, or card pattern?",
                "Am I using flex ratios instead of fixed sizes?",
                "Can this layout adapt to content changes?",
                "Is this pattern reusable elsewhere?",
              ],
              validationChecklist: [
                "Navbar uses justify-content and align-items",
                "Sidebar + content uses flex ratios",
                "Equal height achieved naturally",
                "Footer sticks without hacks",
              ],
            },

            content: {
              introduction:
                "Flexbox is not just a concept — it solves real layout problems. These patterns appear again and again in dashboards, landing pages, and admin panels. Once you master them, most layouts feel repetitive and easy.",

              sections: [
                {
                  title: "Navbar Layout Pattern",
                  description:
                    "Navbars are one of the most common Flexbox use cases.",
                  keyPoints: [
                    "Items arranged horizontally",
                    "Logo on left, links on right",
                    "Responsive-friendly",
                  ],
                  code: ".navbar {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}",
                  useCase: "Website headers and navigation bars",
                },
                {
                  title: "Sidebar + Main Content Layout",
                  description: "Used in dashboards and admin panels.",
                  keyPoints: [
                    "Two-column layout",
                    "Sidebar fixed width or ratio",
                    "Main content flexible",
                  ],
                  code: ".layout {\n  display: flex;\n}\n.sidebar { flex: 1; }\n.main { flex: 3; }",
                  analogy: "Like a menu on the left and workspace on the right",
                },
                {
                  title: "Equal Height Columns",
                  description: "Flexbox makes equal height columns automatic.",
                  keyPoints: [
                    "No hacks required",
                    "Height adjusts to tallest item",
                    "Perfect for cards",
                  ],
                  code: ".cards {\n  display: flex;\n}\n.card { flex: 1; }",
                  takeaway: "One of Flexbox’s biggest advantages",
                },
                {
                  title: "Sticky Footer Pattern",
                  description:
                    "Footer stays at the bottom even with less content.",
                  keyPoints: [
                    "Uses flex-direction: column",
                    "Main content grows",
                    "Footer sticks naturally",
                  ],
                  code: "body {\n  display: flex;\n  flex-direction: column;\n  min-height: 100vh;\n}\nmain { flex: 1; }",
                  analogy:
                    "Footer behaves like weight at the bottom of the page",
                },
                {
                  title: "Why These Patterns Matter",
                  description: "These layouts appear everywhere.",
                  examples: [
                    "SaaS dashboards",
                    "Landing pages",
                    "Admin panels",
                    "Portfolio sites",
                  ],
                },
              ],

              visualStructure: `
Flexbox Layout Patterns:
┌──────────── Navbar ────────────┐
├──── Sidebar ────┬── Content ───┤
│                 │              │
│                 │              │
├────────────────────────────────┤
│            Footer              │
└────────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using floats instead of Flexbox",
                  consequence: "Complex and fragile layout",
                  fix: "Replace floats with Flexbox",
                },
                {
                  mistake: "Hardcoding heights",
                  consequence: "Layout breaks with content changes",
                  fix: "Let Flexbox handle sizing",
                },
                {
                  mistake: "Overcomplicating patterns",
                  consequence: "Unreadable CSS",
                  fix: "Use minimal Flexbox properties",
                },
              ],

              proTips: [
                "Navbars = justify-content: space-between",
                "Dashboards = sidebar + main using flex ratios",
                "Equal heights come free with Flexbox",
                "Sticky footer is easier than you think",
              ],

              realWorldAnalogy:
                "Flexbox patterns are like Lego blocks:\n- Same pieces\n- Different combinations\n- Infinite layouts",
            },

            tasks: [
              "Build a responsive navbar using Flexbox",
              "Create sidebar + main content layout",
              "Implement equal-height cards",
            ],

            examples: [
              ".navbar { display: flex; justify-content: space-between; }",
            ],

            useCases: ["Web dashboards", "Marketing websites", "Admin panels"],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "40–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Build common real-world layouts",
              "Understand Flexbox layout patterns",
              "Create scalable and clean layouts",
              "Prepare for interview-level layout questions",
            ],
          },
          {
            day: 6,
            topic: "Flexbox + Responsive Design",
            subTopics: [
              "Flexbox with media queries",
              "Mobile-first layouts",
              "Changing flex-direction on screen size",
            ],
            explanation:
              "Flexbox becomes powerful when combined with media queries to create responsive designs.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Responsive Dashboard Layout (Mobile → Desktop)",
                description:
                  "Convert a desktop Flexbox layout into a fully responsive, mobile-first layout.",
                requirements: [
                  "Mobile-first column layout by default",
                  "Switch to row layout on tablet/desktop",
                  "No fixed widths",
                  "Clean breakpoint usage (1–2 max)",
                ],
                constraint:
                  "Desktop-first CSS is not allowed. Start mobile-first only.",
                outcome:
                  "You’ll gain real-world confidence in building layouts that scale across devices.",
              },
              {
                id: "pt-2",
                title: "Breakpoint Decision Drill",
                description:
                  "Learn to choose breakpoints based on layout needs, not devices.",
                steps: [
                  "Build a layout that breaks naturally",
                  "Add breakpoint only when layout feels uncomfortable",
                  "Refactor CSS to reduce unnecessary media queries",
                ],
                outcome:
                  "You’ll stop guessing breakpoints and start using layout-driven decisions.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most production UI bugs happen on screen sizes developers don’t test.",
              realWorldChecks: [
                "Test layout at 320px, 768px, and 1024px widths",
                "Ensure no horizontal scrolling",
                "Verify content order makes sense on mobile",
              ],
              commonFinding:
                "Beginners design for desktop first and patch mobile later.",
            },
            interviewInsights: {
              commonQuestions: [
                "What does mobile-first design mean?",
                "How does Flexbox help with responsive layouts?",
                "When would you change flex-direction responsively?",
                "How do you choose breakpoints?",
              ],
              interviewerPerspective:
                "Responsive design questions reveal real-world frontend maturity.",
              tip: "Always explain responsive layouts from UX and content flow perspectives.",
            },
            editorGuidance: {
              starterHint:
                "Design the mobile layout first — desktop is just an expansion.",
              aiMentorPrompts: [
                "Does this layout work on a small screen first?",
                "Is flex-direction the only thing I need to change?",
                "Can I remove this breakpoint?",
                "Does content order feel natural on mobile?",
              ],
              validationChecklist: [
                "Mobile-first CSS written",
                "flex-direction changes responsively",
                "No horizontal overflow",
                "Layout tested across sizes",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern websites must work on phones, tablets, and desktops. Flexbox alone handles layout logic, and media queries control when that layout changes. Together, they form the foundation of responsive design.",

              sections: [
                {
                  title: "What is Responsive Design?",
                  description:
                    "Responsive design adapts layouts based on screen size.",
                  keyPoints: [
                    "One codebase for all devices",
                    "Layout changes, not content",
                    "Essential for modern web apps",
                  ],
                  analogy:
                    "Like furniture that rearranges itself in small rooms",
                },
                {
                  title: "Flexbox with Media Queries",
                  description:
                    "Media queries let you change Flexbox behavior at breakpoints.",
                  keyPoints: [
                    "Apply different flex properties at different widths",
                    "Commonly used to change direction or spacing",
                    "Keeps CSS organized",
                  ],
                  code: "@media (max-width: 768px) {\n  .container {\n    flex-direction: column;\n  }\n}",
                },
                {
                  title: "Mobile-First Layouts",
                  description:
                    "Mobile-first means designing for small screens first.",
                  keyPoints: [
                    "Start with column layout",
                    "Enhance layout for larger screens",
                    "Improves performance and UX",
                  ],
                  code: ".container {\n  display: flex;\n  flex-direction: column;\n}\n\n@media (min-width: 768px) {\n  .container {\n    flex-direction: row;\n  }\n}",
                  takeaway:
                    "Mobile-first is easier to scale than desktop-first",
                },
                {
                  title: "Changing flex-direction Responsively",
                  description:
                    "flex-direction is the most commonly changed property.",
                  scenarios: ["Desktop → row layout", "Mobile → column layout"],
                  code: "@media (max-width: 768px) {\n  .layout {\n    flex-direction: column;\n  }\n}",
                  note: "This single change solves many responsive problems",
                },
                {
                  title: "Testing Responsive Layouts",
                  description: "Always test your layouts across devices.",
                  checklist: [
                    "Resize browser window",
                    "Use DevTools device mode",
                    "Test real mobile if possible",
                  ],
                },
              ],

              visualStructure: `
Responsive Flex Layout:
Desktop:
┌────────┬────────┬────────┐
│ Card   │ Card   │ Card   │
└────────┴────────┴────────┘

Mobile:
┌────────┐
│ Card   │
├────────┤
│ Card   │
├────────┤
│ Card   │
└────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Only designing for desktop",
                  consequence: "Broken mobile experience",
                  fix: "Start mobile-first",
                },
                {
                  mistake: "Using too many breakpoints",
                  consequence: "Complex and messy CSS",
                  fix: "Use meaningful breakpoints",
                },
                {
                  mistake: "Forgetting to test",
                  consequence: "Layout breaks on real devices",
                  fix: "Always test responsiveness",
                },
              ],

              proTips: [
                "Mobile-first saves time in the long run",
                "flex-direction change solves 80% of responsive needs",
                "Use fewer, smarter breakpoints",
                "Flexbox + media queries = powerful combo",
              ],

              realWorldAnalogy:
                "Responsive design is like folding furniture:\n- Compact for small spaces\n- Expanded for large rooms",
            },

            tasks: [
              "Convert desktop layout into mobile layout",
              "Change flex-direction on small screens",
              "Test layout responsiveness",
            ],

            examples: [
              "@media (max-width: 768px) { .container { flex-direction: column; } }",
            ],

            useCases: [
              "Mobile-friendly designs",
              "Responsive landing pages",
              "Modern web applications",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Combine Flexbox with media queries",
              "Build mobile-first layouts",
              "Adapt layouts across screen sizes",
              "Test and debug responsive designs",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Responsive Product Landing Page",
            subTopics: [
              "Navbar with flexbox",
              "Hero section alignment",
              "Features section with cards",
              "Footer layout",
            ],
            explanation:
              "Apply everything learned this week to build a real-world product landing page using Flexbox.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Product Landing Page",
                description:
                  "Build a complete, responsive product landing page that looks and behaves like a real startup website.",
                requirements: [
                  "Navbar, Hero, Features, Footer sections",
                  "Flexbox used for all layouts",
                  "Mobile-first or responsive approach",
                  "Clean spacing using gap (no margin hacks)",
                  "Semantic HTML structure",
                ],
                constraint:
                  "No CSS Grid, no frameworks, no libraries. Flexbox only.",
                outcome:
                  "A portfolio-ready landing page you can confidently show in interviews.",
              },
              {
                id: "pt-2",
                title: "Professional Layout Review & Refactor",
                description:
                  "Review and improve your layout like a senior frontend engineer.",
                steps: [
                  "Check responsiveness on mobile, tablet, desktop",
                  "Remove unnecessary CSS rules",
                  "Replace fixed sizes with flex-based sizing",
                  "Improve spacing consistency",
                ],
                outcome:
                  "You’ll learn how professionals polish UI before shipping to production.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Capstone projects expose real-world issues that small examples never reveal.",
              realWorldChecks: [
                "No horizontal scrolling on any screen size",
                "Navbar and hero adapt cleanly on mobile",
                "Cards maintain equal height visually",
                "Footer stays at bottom on short pages",
              ],
              commonFinding:
                "Beginners build sections correctly but fail to integrate them into a cohesive page.",
            },
            interviewInsights: {
              commonQuestions: [
                "How did you make this landing page responsive?",
                "Why did you choose Flexbox over Grid here?",
                "How did you handle layout changes on mobile?",
                "What challenges did you face while building this page?",
              ],
              interviewerPerspective:
                "A complete layout project shows real frontend maturity, not just syntax knowledge.",
              tip: "Explain your layout section-by-section and mention Flexbox patterns you used.",
            },
            editorGuidance: {
              starterHint:
                "Build section by section — don’t try to style the entire page at once.",
              aiMentorPrompts: [
                "Does each section work independently?",
                "Is spacing consistent across sections?",
                "Does content flow naturally on mobile?",
                "Can this layout scale with more content?",
              ],
              validationChecklist: [
                "All sections use Flexbox intentionally",
                "Responsive behavior tested",
                "No fixed heights used",
                "CSS is clean and readable",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Flexbox truly makes sense when you build a complete page. In this mini project, you will design a responsive product landing page similar to real startup websites. This project focuses on layout, alignment, spacing, and responsiveness — not fancy animations.",

              sections: [
                {
                  title: "Project Overview",
                  description:
                    "You will build a single-page product landing layout using only HTML + CSS (Flexbox).",
                  requirements: [
                    "Use semantic HTML structure",
                    "Use Flexbox for all layouts",
                    "Mobile-first or responsive design",
                    "Clean spacing and alignment",
                  ],
                  output: "index.html + styles.css",
                  analogy:
                    "This is where Flexbox turns from theory into real skill",
                },
                {
                  title: "Navbar Layout",
                  description:
                    "The navbar is the first impression of your product.",
                  layoutGoals: [
                    "Logo on the left",
                    "Navigation links on the right",
                    "Responsive stacking on mobile",
                  ],
                  flexboxUsage: [
                    "display: flex",
                    "justify-content: space-between",
                    "align-items: center",
                  ],
                  code: ".navbar {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}",
                },
                {
                  title: "Hero Section Alignment",
                  description: "The hero section highlights the product value.",
                  layoutGoals: [
                    "Text + call-to-action",
                    "Image or illustration",
                    "Vertical and horizontal alignment",
                  ],
                  flexboxUsage: [
                    "Centering content",
                    "Two-column layout on desktop",
                    "Single-column on mobile",
                  ],
                  tip: "Hero sections usually change flex-direction on small screens",
                },
                {
                  title: "Features Section (Cards)",
                  description: "Features are best presented using cards.",
                  layoutGoals: [
                    "Equal-height cards",
                    "Responsive wrapping",
                    "Consistent spacing",
                  ],
                  flexboxUsage: [
                    "display: flex",
                    "flex-wrap: wrap",
                    "gap for spacing",
                  ],
                  code: ".features {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 24px;\n}",
                },
                {
                  title: "Footer Layout",
                  description: "Footer closes the page cleanly.",
                  layoutGoals: [
                    "Multiple columns on desktop",
                    "Stacked layout on mobile",
                    "Clear separation from content",
                  ],
                  flexboxUsage: [
                    "flex-direction: row / column",
                    "Responsive direction change",
                  ],
                },
                {
                  title: "Responsive Behavior",
                  description: "Responsiveness is mandatory for this project.",
                  checklist: [
                    "Navbar stacks on mobile",
                    "Hero becomes column layout",
                    "Cards wrap naturally",
                    "No horizontal scrolling",
                  ],
                  code: "@media (max-width: 768px) {\n  .hero {\n    flex-direction: column;\n  }\n}",
                },
                {
                  title: "CSS Quality Checklist",
                  description: "Code quality matters as much as layout.",
                  checklist: [
                    "Use class selectors (avoid IDs)",
                    "Use box-sizing: border-box",
                    "Consistent margins & padding",
                    "Readable typography",
                    "No hardcoded heights",
                  ],
                },
              ],

              visualStructure: `
Product Landing Page Layout:
┌─────────────────────────────┐
│ Navbar                      │
│ Logo        Links           │
├─────────────────────────────┤
│ Hero Section                │
│ Text        Image           │
├─────────────────────────────┤
│ Features Section            │
│ [Card] [Card] [Card]        │
│ [Card] [Card]               │
├─────────────────────────────┤
│ Footer                      │
│ Links / Info                │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Overusing fixed widths",
                  consequence: "Layout breaks on mobile",
                  fix: "Use flex and relative sizing",
                },
                {
                  mistake: "Ignoring spacing consistency",
                  consequence: "Unprofessional UI",
                  fix: "Standardize gap, margin, and padding",
                },
                {
                  mistake: "Desktop-only thinking",
                  consequence: "Poor mobile experience",
                  fix: "Test mobile layouts first",
                },
                {
                  mistake: "Messy CSS structure",
                  consequence: "Hard to maintain",
                  fix: "Group related styles",
                },
              ],

              proTips: [
                "If it looks clean, spacing is right",
                "Flexbox handles alignment — don’t fight it",
                "Mobile-first reduces CSS complexity",
                "This project is portfolio-worthy",
                "Less CSS often means better CSS",
              ],

              realWorldAnalogy:
                "A landing page is like a shopfront:\n- Navbar invites users in\n- Hero sells the idea\n- Features convince\n- Footer builds trust",
            },

            tasks: [
              "Design navbar, hero, features, and footer",
              "Make layout fully responsive",
              "Ensure clean spacing and alignment",
            ],

            examples: ["Navbar + hero + features + footer using flexbox"],

            useCases: [
              "Startup landing pages",
              "Product marketing sites",
              "Portfolio projects",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "120–150 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Apply Flexbox to a full-page layout",
              "Build responsive real-world UI",
              "Improve layout confidence",
              "Create a portfolio-ready project",
            ],
          },
        ],
      },

      {
        week: 5,
        weekGoal:
          "Master CSS Grid and positioning to build complex page layouts",
        weekOutcome:
          "Ability to design full website layouts and advanced UI structures",
        canLearn: ["students", "beginners"],
        miniProject: "Complete Website Layout using Grid & Flexbox",
        days: [
          {
            day: 1,
            topic: "CSS Grid Fundamentals",
            subTopics: [
              "display: grid",
              "grid-template-columns",
              "grid-template-rows",
              "fr unit",
              "Implicit vs explicit grid",
            ],
            explanation:
              "CSS Grid is a two-dimensional layout system that allows developers to design complex layouts with rows and columns easily.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Page-Level Grid Skeleton (Industry Layout)",
                description:
                  "Create a page-level layout using CSS Grid to understand two-dimensional control.",
                requirements: [
                  "At least 3 columns and 2 rows",
                  "Use fr units only (no fixed pixels)",
                  "Header spanning full width",
                  "Main content and sidebar layout",
                ],
                constraint:
                  "Flexbox is not allowed for layout structure. Use Grid only.",
                outcome:
                  "You’ll understand why Grid is preferred for overall page structure.",
              },
              {
                id: "pt-2",
                title: "Explicit vs Implicit Grid Experiment",
                description:
                  "Observe how Grid behaves when items exceed defined structure.",
                steps: [
                  "Define only grid-template-columns",
                  "Add more grid items than expected",
                  "Observe implicit row creation",
                  "Refine layout by defining rows explicitly",
                ],
                outcome:
                  "You’ll clearly understand how Grid auto-places content.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Grid layouts fail when developers don’t understand how rows and columns are generated.",
              realWorldChecks: [
                "Inspect grid lines using DevTools",
                "Check implicit row creation",
                "Verify fr unit distribution on resize",
              ],
              commonFinding:
                "Beginners define columns but forget that rows are also part of Grid.",
            },
            interviewInsights: {
              commonQuestions: [
                "What problem does CSS Grid solve?",
                "Difference between Flexbox and Grid?",
                "What is the fr unit?",
                "What is implicit vs explicit grid?",
              ],
              interviewerPerspective:
                "Grid questions reveal layout architecture thinking.",
              tip: "Always explain Grid as a page-structure tool, not a Flexbox replacement.",
            },
            editorGuidance: {
              starterHint:
                "Before writing Grid code, draw rows and columns on paper.",
              aiMentorPrompts: [
                "Is this layout one-dimensional or two-dimensional?",
                "Should this be Grid or Flexbox?",
                "Am I relying too much on fixed sizes?",
                "Do I understand how items are being placed?",
              ],
              validationChecklist: [
                "display: grid applied",
                "Columns defined using fr units",
                "Grid adapts on resize",
                "Implicit grid behavior understood",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Flexbox works in one direction at a time (row OR column). CSS Grid was introduced to solve layout problems that need control over both rows AND columns simultaneously. Grid is best for page-level and complex layouts.",

              sections: [
                {
                  title: "Why CSS Grid?",
                  description:
                    "Some layouts are difficult or messy with Flexbox alone.",
                  problemsSolved: [
                    "Complex page layouts",
                    "Overlapping sections",
                    "Precise row and column control",
                  ],
                  comparison:
                    "Flexbox = one-dimensional, Grid = two-dimensional",
                  analogy: "Flexbox is a shelf, Grid is a full table",
                },
                {
                  title: "display: grid",
                  description:
                    "display: grid turns an element into a grid container.",
                  keyPoints: [
                    "Parent becomes grid container",
                    "Direct children become grid items",
                    "Grid rules apply only after display: grid",
                  ],
                  code: ".container {\n  display: grid;\n}",
                  warning: "Grid properties won’t work without display: grid",
                },
                {
                  title: "grid-template-columns",
                  description: "Defines the number and width of columns.",
                  keyPoints: [
                    "Each value represents one column",
                    "Can use px, %, fr, auto",
                    "Most commonly used grid property",
                  ],
                  code: ".container {\n  grid-template-columns: 1fr 1fr 1fr;\n}",
                  analogy: "Like dividing a table into equal columns",
                },
                {
                  title: "grid-template-rows",
                  description: "Defines the height and number of rows.",
                  keyPoints: [
                    "Optional for simple layouts",
                    "Often combined with auto rows",
                    "Useful for fixed-height sections",
                  ],
                  code: ".container {\n  grid-template-rows: 100px auto;\n}",
                },
                {
                  title: "The fr Unit",
                  description: "fr represents a fraction of available space.",
                  keyPoints: [
                    "Responsive by default",
                    "Distributes remaining space",
                    "Preferred over percentages",
                  ],
                  examples: [
                    "1fr 1fr → equal columns",
                    "1fr 2fr → second column twice as wide",
                  ],
                  code: ".container {\n  grid-template-columns: 1fr 2fr;\n}",
                  analogy: "Like splitting a pizza into flexible slices",
                },
                {
                  title: "Explicit vs Implicit Grid",
                  description: "Grids can be defined or created automatically.",
                  explicitGrid: [
                    "Defined using grid-template-columns/rows",
                    "Predictable layout",
                  ],
                  implicitGrid: [
                    "Created automatically when items exceed defined grid",
                    "Uses default sizing",
                  ],
                  takeaway:
                    "Explicit grid gives control, implicit grid fills gaps",
                },
                {
                  title: "When to Use Grid",
                  description: "Grid shines in large, structured layouts.",
                  useGuidelines: [
                    "Page-level layouts",
                    "Dashboards",
                    "Complex content sections",
                  ],
                  note: "Flexbox and Grid work best together, not against each other",
                },
              ],

              visualStructure: `
CSS Grid Layout:
┌──────────┬──────────┬──────────┐
│ Column 1 │ Column 2 │ Column 3 │
├──────────┼──────────┼──────────┤
│ Content  │ Content  │ Content  │
└──────────┴──────────┴──────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Trying to use Grid like Flexbox",
                  consequence: "Confusing layouts",
                  fix: "Think in rows AND columns",
                },
                {
                  mistake: "Hardcoding pixel widths everywhere",
                  consequence: "Poor responsiveness",
                  fix: "Use fr units",
                },
                {
                  mistake: "Ignoring implicit grid behavior",
                  consequence: "Unexpected item placement",
                  fix: "Define grid structure clearly",
                },
              ],

              proTips: [
                "Use Grid for page structure, Flexbox for components",
                "fr unit is your default choice",
                "Start with simple grids",
                "Visualize rows and columns before coding",
              ],

              realWorldAnalogy:
                "CSS Grid is like city planning:\n- Roads (rows)\n- Buildings (columns)\n- Everything has a defined place",
            },

            tasks: [
              "Create a basic grid with rows and columns",
              "Experiment with fr units",
              "Build a simple 3-column layout",
            ],

            examples: [
              ".container { display: grid; grid-template-columns: 1fr 1fr 1fr; }",
            ],

            useCases: [
              "Page-level layouts",
              "Dashboard structures",
              "Complex UI layouts",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand two-dimensional layouts",
              "Create grids using rows and columns",
              "Use fr units effectively",
              "Differentiate explicit and implicit grids",
            ],
          },
          {
            day: 2,
            topic: "Grid Placement & Areas",
            subTopics: [
              "grid-column",
              "grid-row",
              "grid-area",
              "grid-template-areas",
            ],
            explanation:
              "Grid placement properties allow precise control over where elements appear in a grid layout.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Dashboard Layout Using Grid Areas",
                description:
                  "Build a complete dashboard layout using grid-template-areas.",
                requirements: [
                  "Header spanning full width",
                  "Sidebar on the left",
                  "Main content area",
                  "Footer spanning full width",
                  "Readable grid-template-areas definition",
                ],
                constraint:
                  "Flexbox is not allowed for layout structure. Use Grid only.",
                outcome:
                  "You’ll understand why grid-template-areas is preferred for page-level layouts.",
              },
              {
                id: "pt-2",
                title: "Grid Line Placement Drill",
                description:
                  "Practice placing items precisely using grid-column and grid-row.",
                steps: [
                  "Create a 4-column grid",
                  "Place items using line numbers",
                  "Make one item span full width using 1 / -1",
                  "Compare readability with grid-template-areas",
                ],
                outcome:
                  "You’ll learn when to use line-based placement vs named areas.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Grid placement issues often come from misunderstanding grid lines and spans.",
              realWorldChecks: [
                "Turn on Grid overlay in DevTools",
                "Verify start and end line numbers",
                "Ensure grid areas match container definition",
              ],
              commonFinding:
                "Beginners mix grid-column and grid-template-areas incorrectly.",
            },
            interviewInsights: {
              commonQuestions: [
                "What are grid lines?",
                "Difference between grid-column and grid-area?",
                "Why is grid-template-areas useful?",
                "Does Grid placement change DOM order?",
              ],
              interviewerPerspective:
                "Placement questions test layout clarity and code readability.",
              tip: "Always mention readability and maintainability when choosing grid areas.",
            },
            editorGuidance: {
              starterHint:
                "If a layout is hard to explain in words, grid-template-areas will help.",
              aiMentorPrompts: [
                "Is this layout page-level or component-level?",
                "Would named areas make this clearer?",
                "Am I overusing line numbers?",
                "Can another developer read this easily?",
              ],
              validationChecklist: [
                "grid-template-areas defined on container",
                "grid-area names match exactly",
                "Spans use correct grid lines",
                "Layout is readable in CSS",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Once a grid structure is defined, the real power of CSS Grid comes from placing items exactly where you want them. Grid placement lets you control layout visually, without changing HTML order.",

              sections: [
                {
                  title: "Understanding Grid Lines",
                  description:
                    "CSS Grid is based on numbered lines, not just columns and rows.",
                  keyPoints: [
                    "Grid lines start from 1",
                    "Lines exist between columns and rows",
                    "Placement is based on line numbers",
                  ],
                  analogy:
                    "Think of grid lines like coordinates on graph paper",
                },
                {
                  title: "grid-column",
                  description:
                    "grid-column controls horizontal placement of items.",
                  keyPoints: [
                    "Defines start and end column lines",
                    "Can span multiple columns",
                    "Works only on grid items",
                  ],
                  syntax: "grid-column: start / end",
                  code: ".item {\n  grid-column: 1 / 3;\n}",
                  analogy: "Choosing how many columns an item occupies",
                },
                {
                  title: "grid-row",
                  description: "grid-row controls vertical placement of items.",
                  keyPoints: [
                    "Defines start and end row lines",
                    "Can span multiple rows",
                    "Often used with grid-column",
                  ],
                  syntax: "grid-row: start / end",
                  code: ".item {\n  grid-row: 1 / 2;\n}",
                },
                {
                  title: "Spanning Grid Items",
                  description: "Grid items can span multiple rows or columns.",
                  examples: [
                    "Hero section spanning full width",
                    "Sidebar spanning multiple rows",
                  ],
                  code: ".hero {\n  grid-column: 1 / -1;\n}",
                  note: "-1 refers to the last grid line",
                },
                {
                  title: "grid-template-areas",
                  description:
                    "grid-template-areas allows visual layout definition using names.",
                  keyPoints: [
                    "Human-readable layout",
                    "Very easy to understand",
                    "Best for page-level layouts",
                  ],
                  code: ".container {\n  grid-template-areas:\n    'header header'\n    'sidebar content'\n    'footer footer';\n}",
                  analogy: "Like drawing the layout using words",
                },
                {
                  title: "grid-area",
                  description:
                    "grid-area assigns an element to a named grid area.",
                  keyPoints: [
                    "Must match grid-template-areas name",
                    "Cleaner than line numbers",
                    "Improves readability",
                  ],
                  code: ".header { grid-area: header; }\n.sidebar { grid-area: sidebar; }\n.content { grid-area: content; }\n.footer { grid-area: footer; }",
                },
                {
                  title: "Header–Content–Footer Layout",
                  description: "One of the most common Grid use cases.",
                  structure: [
                    "Header at top",
                    "Sidebar + content in middle",
                    "Footer at bottom",
                  ],
                  takeaway:
                    "This layout is extremely hard with floats, easy with Grid",
                },
                {
                  title: "When to Use Placement vs Areas",
                  description: "Both approaches are useful.",
                  guidelines: [
                    "Use grid-column / row for fine control",
                    "Use grid-template-areas for page layouts",
                    "Prefer readability over cleverness",
                  ],
                },
              ],

              visualStructure: `
Grid Areas Layout:
┌───────────────┬───────────────┐
│    header     │    header     │
├───────────────┼───────────────┤
│   sidebar     │    content    │
├───────────────┴───────────────┤
│           footer              │
└───────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting grid-template-areas on container",
                  consequence: "grid-area does nothing",
                  fix: "Define grid-template-areas first",
                },
                {
                  mistake: "Using wrong area names",
                  consequence: "Item not placed correctly",
                  fix: "Ensure names match exactly",
                },
                {
                  mistake: "Overusing line numbers",
                  consequence: "Hard-to-read CSS",
                  fix: "Use grid-template-areas for large layouts",
                },
              ],

              proTips: [
                "grid-template-areas is interview gold",
                "Use -1 to target last grid line",
                "Grid placement doesn’t change DOM order",
                "Readable CSS is more valuable than clever CSS",
              ],

              realWorldAnalogy:
                "Grid placement is like seating at a conference:\n- Rows and columns define space\n- Names decide where people sit\n- Order stays the same, position changes",
            },

            tasks: [
              "Position items using grid-column and grid-row",
              "Create layout using grid-template-areas",
              "Build a header-content-footer layout",
            ],

            examples: [
              ".container { grid-template-areas: 'header header' 'sidebar content' 'footer footer'; }",
            ],

            useCases: [
              "Magazine layouts",
              "Complex page structures",
              "Dashboard layouts",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Place grid items precisely",
              "Use grid-column and grid-row correctly",
              "Build layouts with grid-template-areas",
              "Create readable and scalable grid layouts",
            ],
          },
          {
            day: 3,
            topic: "Grid Alignment & Responsive Grids",
            subTopics: [
              "gap",
              "justify-items",
              "align-items",
              "auto-fit",
              "auto-fill",
              "minmax()",
            ],
            explanation:
              "Grid alignment and responsive units help build layouts that adapt automatically to screen sizes.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Responsive Product Grid (No Media Queries)",
                description:
                  "Build a fully responsive product/card grid using modern CSS Grid features.",
                requirements: [
                  "Use repeat(auto-fit, minmax())",
                  "No media queries allowed",
                  "Consistent spacing using gap",
                  "Cards must stay readable on small screens",
                ],
                constraint:
                  "Media queries are not allowed. Responsiveness must come from Grid itself.",
                outcome:
                  "You’ll understand how modern UIs scale automatically across devices.",
              },
              {
                id: "pt-2",
                title: "Alignment Control Drill",
                description:
                  "Practice controlling alignment at grid level instead of item hacks.",
                steps: [
                  "Align items using justify-items",
                  "Align items using align-items",
                  "Compare stretch vs center behavior",
                  "Remove unnecessary item-level CSS",
                ],
                outcome:
                  "You’ll stop using margins for alignment and rely on Grid properly.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most responsive layout bugs happen because grids don’t adapt gracefully to screen size changes.",
              realWorldChecks: [
                "Resize screen from mobile to desktop",
                "Ensure cards never become too narrow",
                "Verify spacing consistency at all sizes",
              ],
              commonFinding:
                "Beginners add media queries instead of using auto-fit and minmax.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the difference between auto-fit and auto-fill?",
                "How does minmax() help responsiveness?",
                "How can you build responsive grids without media queries?",
                "Difference between justify-items and align-items?",
              ],
              interviewerPerspective:
                "These questions test modern CSS knowledge and real-world experience.",
              tip: "Mention auto-fit + minmax as a preferred modern Grid pattern.",
            },
            editorGuidance: {
              starterHint:
                "If you’re writing media queries first, pause and try Grid functions instead.",
              aiMentorPrompts: [
                "Can Grid handle this without breakpoints?",
                "Are my cards readable at the smallest size?",
                "Should alignment be handled at container level?",
                "Am I misusing margins?",
              ],
              validationChecklist: [
                "auto-fit used correctly",
                "minmax prevents layout collapse",
                "gap used instead of margins",
                "Grid adapts smoothly on resize",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "CSS Grid shines when layouts adapt automatically without relying heavily on media queries. Using alignment properties and responsive grid functions like auto-fit, auto-fill, and minmax(), you can build flexible, future-proof layouts.",

              sections: [
                {
                  title: "Gap in CSS Grid",
                  description:
                    "gap controls spacing between rows and columns in a grid.",
                  keyPoints: [
                    "Replaces margin hacks",
                    "Applies between grid cells",
                    "Works both horizontally and vertically",
                  ],
                  code: ".container {\n  display: grid;\n  gap: 16px;\n}",
                  analogy: "Like spacing tiles evenly on a floor",
                },
                {
                  title: "justify-items",
                  description:
                    "justify-items aligns grid items horizontally within their cells.",
                  keyPoints: [
                    "Applies to all grid items",
                    "Works along the inline (horizontal) axis",
                    "Default value is stretch",
                  ],
                  values: ["start", "center", "end", "stretch"],
                  code: ".container {\n  justify-items: center;\n}",
                },
                {
                  title: "align-items",
                  description:
                    "align-items aligns grid items vertically within their cells.",
                  keyPoints: [
                    "Controls vertical alignment",
                    "Default value is stretch",
                    "Works similarly to Flexbox",
                  ],
                  code: ".container {\n  align-items: center;\n}",
                  analogy: "Like aligning items in the height of a box",
                },
                {
                  title: "Responsive Grids Without Media Queries",
                  description:
                    "CSS Grid can auto-adjust columns based on available space.",
                  keyPoints: [
                    "No need to define breakpoints",
                    "Layout adapts automatically",
                    "Ideal for cards and galleries",
                  ],
                  takeaway:
                    "This is one of Grid’s biggest advantages over Flexbox",
                },
                {
                  title: "minmax() Function",
                  description: "minmax() defines a size range for grid tracks.",
                  keyPoints: [
                    "Sets minimum and maximum size",
                    "Prevents columns from getting too small",
                    "Ensures readable layouts",
                  ],
                  syntax: "minmax(min, max)",
                  code: "grid-template-columns: minmax(250px, 1fr);",
                  analogy: "Like setting minimum comfort size for content",
                },
                {
                  title: "auto-fit",
                  description:
                    "auto-fit collapses empty columns and stretches content.",
                  keyPoints: [
                    "Best for flexible layouts",
                    "Expands items to fill space",
                    "Most commonly used",
                  ],
                  code: "grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));",
                },
                {
                  title: "auto-fill",
                  description:
                    "auto-fill keeps empty columns even if not used.",
                  keyPoints: [
                    "Maintains grid structure",
                    "Leaves empty tracks",
                    "Less commonly used",
                  ],
                  comparison: "auto-fit stretches, auto-fill preserves space",
                },
                {
                  title: "Auto-fit vs Auto-fill (When to Use)",
                  description:
                    "Choosing the right one avoids layout confusion.",
                  guidelines: [
                    "Use auto-fit for responsive cards",
                    "Use auto-fill when grid structure matters",
                    "If unsure → use auto-fit",
                  ],
                },
              ],

              visualStructure: `
Responsive Grid:
┌────────┬────────┬────────┐
│ Card   │ Card   │ Card   │
├────────┼────────┼────────┤
│ Card   │ Card   │        │
└────────┴────────┴────────┘

(auto-fit adjusts automatically)
    `,

              commonMistakes: [
                {
                  mistake: "Using margins instead of gap",
                  consequence: "Inconsistent spacing",
                  fix: "Use gap for grid spacing",
                },
                {
                  mistake: "Confusing auto-fit and auto-fill",
                  consequence: "Unexpected empty columns",
                  fix: "Prefer auto-fit for most cases",
                },
                {
                  mistake: "Not using minmax()",
                  consequence: "Cards become too small",
                  fix: "Always pair auto-fit with minmax",
                },
              ],

              proTips: [
                "gap is cleaner than margins",
                "auto-fit + minmax is a power combo",
                "Grid reduces need for media queries",
                "Stretch is default alignment — override when needed",
              ],

              realWorldAnalogy:
                "Responsive grids are like shelves that adjust automatically:\n- Items rearrange themselves\n- Spacing stays consistent\n- No manual adjustments needed",
            },

            tasks: [
              "Create responsive grid using auto-fit and minmax",
              "Control spacing using gap",
              "Align grid items properly",
            ],

            examples: [
              "grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));",
            ],

            useCases: [
              "Responsive galleries",
              "Card-based layouts",
              "Product grids",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Build responsive grids without media queries",
              "Use gap and alignment correctly",
              "Understand auto-fit vs auto-fill",
              "Create flexible card layouts",
            ],
          },
          {
            day: 4,
            topic: "CSS Position Property",
            subTopics: ["static", "relative", "absolute", "fixed", "sticky"],
            explanation:
              "The position property controls how elements are placed relative to normal document flow, other elements, or the viewport.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "UI Overlay Components (Badges, Dropdowns, Tooltips)",
                description:
                  "Build common UI overlay components using correct positioning techniques.",
                requirements: [
                  "Badge positioned on top of a card",
                  "Dropdown menu positioned relative to button",
                  "Tooltip using absolute positioning",
                  "Parent container must use position: relative",
                ],
                constraint:
                  "Flexbox and Grid are not allowed for positioning overlays.",
                outcome:
                  "You’ll understand how real UI overlays are built without breaking layouts.",
              },
              {
                id: "pt-2",
                title: "Sticky & Fixed UI Elements",
                description:
                  "Implement sticky and fixed elements commonly used in modern apps.",
                steps: [
                  "Create a fixed header that stays on scroll",
                  "Add body offset to prevent content overlap",
                  "Build a sticky sidebar that stops at container end",
                ],
                outcome:
                  "You’ll learn how professionals handle scroll-based UI behavior.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Positioning bugs are some of the hardest UI issues to debug in production.",
              realWorldChecks: [
                "Scroll the page and observe fixed vs sticky behavior",
                "Resize screen to ensure overlays stay attached",
                "Check that positioned elements don’t break layout flow",
              ],
              commonFinding:
                "Beginners misuse absolute positioning for full layouts instead of overlays.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between relative and absolute positioning?",
                "Why does absolute positioning need a relative parent?",
                "Difference between fixed and sticky?",
                "When should you avoid absolute positioning?",
              ],
              interviewerPerspective:
                "Position questions reveal whether a candidate understands document flow.",
              tip: "Always explain position answers in terms of flow and reference context.",
            },
            editorGuidance: {
              starterHint:
                "If you’re positioning many elements absolutely, rethink your layout.",
              aiMentorPrompts: [
                "Is this element part of layout or an overlay?",
                "Does this absolute element have a relative parent?",
                "Should this be sticky instead of fixed?",
                "Am I breaking document flow unnecessarily?",
              ],
              validationChecklist: [
                "Relative parent defined for absolute children",
                "Fixed elements don’t hide content",
                "Sticky elements have top defined",
                "Layout remains stable on resize and scroll",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "By default, HTML elements follow the normal document flow. The CSS position property allows you to break or modify this flow to place elements precisely — like overlays, headers, badges, and floating buttons.",

              sections: [
                {
                  title: "position: static (Default)",
                  description:
                    "static is the default positioning for all elements.",
                  keyPoints: [
                    "Follows normal document flow",
                    "top, right, bottom, left do NOT work",
                    "Most elements are static by default",
                  ],
                  code: ".box {\n  position: static;\n}",
                  analogy:
                    "Like standing in a queue — no special movement allowed",
                },
                {
                  title: "position: relative",
                  description:
                    "relative positions an element relative to its normal position.",
                  keyPoints: [
                    "Element stays in document flow",
                    "Offsets move the element visually",
                    "Creates positioning context for absolute children",
                  ],
                  code: ".box {\n  position: relative;\n  top: 10px;\n  left: 10px;\n}",
                  analogy:
                    "Like taking a step forward without leaving your place",
                },
                {
                  title: "position: absolute",
                  description:
                    "absolute removes the element from normal flow and positions it relative to the nearest positioned ancestor.",
                  keyPoints: [
                    "Removed from document flow",
                    "Positioned relative to nearest relative/absolute/fixed parent",
                    "If none found → positioned relative to viewport",
                  ],
                  code: ".parent { position: relative; }\n.child {\n  position: absolute;\n  top: 20px;\n  left: 20px;\n}",
                  analogy: "Like pinning a note inside a specific container",
                },
                {
                  title: "position: fixed",
                  description:
                    "fixed positions an element relative to the viewport.",
                  keyPoints: [
                    "Does not move on scroll",
                    "Removed from document flow",
                    "Commonly used for headers and buttons",
                  ],
                  code: ".header {\n  position: fixed;\n  top: 0;\n  width: 100%;\n}",
                  analogy:
                    "Like a sticker on your screen — stays visible always",
                },
                {
                  title: "position: sticky",
                  description:
                    "sticky switches between relative and fixed based on scroll position.",
                  keyPoints: [
                    "Behaves like relative initially",
                    "Becomes fixed after crossing threshold",
                    "Requires top, left, etc.",
                  ],
                  code: ".sidebar {\n  position: sticky;\n  top: 20px;\n}",
                  note: "Sticky works only within its parent container",
                  analogy: "Like a bookmark that sticks after scrolling",
                },
                {
                  title: "Top, Right, Bottom, Left",
                  description:
                    "These properties work only with positioned elements.",
                  keyPoints: [
                    "Used with relative, absolute, fixed, sticky",
                    "Control offset from reference point",
                    "Do nothing with static positioning",
                  ],
                  code: ".box {\n  position: absolute;\n  bottom: 10px;\n  right: 10px;\n}",
                },
                {
                  title: "Common Positioning Use Cases",
                  description: "Position is used sparingly but powerfully.",
                  examples: [
                    "Sticky headers",
                    "Floating action buttons",
                    "Dropdown menus",
                    "Badges & overlays",
                  ],
                },
              ],

              visualStructure: `
Positioning Summary:
┌─────────────────────────────┐
│ static   → normal flow      │
│ relative → offset self      │
│ absolute → positioned child │
│ fixed    → viewport bound   │
│ sticky   → scroll-aware     │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using absolute without relative parent",
                  consequence: "Element jumps to viewport",
                  fix: "Add position: relative to parent",
                },
                {
                  mistake: "Overusing absolute positioning",
                  consequence: "Layout breaks easily",
                  fix: "Use Flexbox/Grid for layout, position for overlays",
                },
                {
                  mistake: "Sticky not working",
                  consequence: "Element doesn’t stick",
                  fix: "Ensure parent has enough height and top is defined",
                },
                {
                  mistake: "Fixed header covering content",
                  consequence: "Content hidden behind header",
                  fix: "Add top padding/margin to body",
                },
              ],

              proTips: [
                "Layout → Grid/Flexbox, Overlays → Position",
                "Always pair absolute with relative parent",
                "Sticky is underrated but powerful",
                "Avoid pixel-perfect positioning everywhere",
              ],

              realWorldAnalogy:
                "CSS position is like placing objects in a room:\n- static = furniture stays where placed\n- relative = slight adjustment\n- absolute = pinned to wall\n- fixed = glued to window\n- sticky = follows until it sticks",
            },

            tasks: [
              "Position elements using relative and absolute",
              "Create a fixed header",
              "Implement sticky sidebar",
            ],

            examples: [".box { position: absolute; top: 20px; left: 20px; }"],

            useCases: [
              "Sticky headers",
              "Floating action buttons",
              "Dropdowns & overlays",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand all position values",
              "Use relative and absolute correctly",
              "Build sticky and fixed UI elements",
              "Avoid common positioning bugs",
            ],
          },
          {
            day: 5,
            topic: "Z-index & Stacking Context",
            subTopics: [
              "z-index",
              "stacking order",
              "position dependency",
              "common z-index bugs",
            ],
            explanation:
              "Z-index controls which elements appear on top of others and is critical for overlays and modals.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Modal & Overlay System (Production Style)",
                description:
                  "Build a real-world modal and overlay system that always appears above content.",
                requirements: [
                  "Fullscreen overlay using position: fixed",
                  "Modal centered above overlay",
                  "Proper z-index layering (content < overlay < modal)",
                  "No layout breakage behind modal",
                ],
                constraint:
                  "Random z-index values (9999, 99999) are not allowed. Use a clean scale.",
                outcome:
                  "You’ll understand how professional apps handle modals without z-index chaos.",
              },
              {
                id: "pt-2",
                title: "Stacking Context Debugging Drill",
                description:
                  "Intentionally break and fix z-index issues caused by stacking contexts.",
                steps: [
                  "Create a parent stacking context",
                  "Add child with very high z-index",
                  "Observe why it stays behind",
                  "Fix by restructuring stacking context",
                ],
                outcome:
                  "You’ll develop intuition to debug the most confusing CSS bugs.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Z-index bugs often appear only in complex UIs with overlays, not in small demos.",
              realWorldChecks: [
                "Open modal over dropdowns and headers",
                "Scroll page to ensure fixed overlays stay correct",
                "Inspect parent elements for stacking context",
              ],
              commonFinding:
                "Developers increase z-index numbers instead of fixing stacking context.",
            },
            interviewInsights: {
              commonQuestions: [
                "Why does z-index sometimes not work?",
                "What is stacking context?",
                "Does a higher z-index always appear on top?",
                "Which CSS properties create stacking contexts?",
              ],
              interviewerPerspective:
                "Z-index questions test deep CSS understanding, not memorization.",
              tip: "Always explain z-index bugs by mentioning stacking context first.",
            },
            editorGuidance: {
              starterHint:
                "If z-index feels broken, inspect parent elements before changing numbers.",
              aiMentorPrompts: [
                "Does this element have a positioned parent?",
                "Is there an unexpected stacking context?",
                "Should this overlay live higher in the DOM?",
                "Am I abusing large z-index values?",
              ],
              validationChecklist: [
                "position defined for z-indexed elements",
                "Clean z-index scale followed",
                "Overlays appear above all content",
                "No unnecessary stacking contexts created",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Z-index decides which element sits above or below another. Most bugs around modals, dropdowns, and tooltips come from misunderstanding stacking context — not z-index itself.",

              sections: [
                {
                  title: "What is Z-index?",
                  description:
                    "z-index controls the vertical stacking order of elements.",
                  keyPoints: [
                    "Higher z-index appears on top",
                    "Works only on positioned elements",
                    "Default z-index is auto",
                  ],
                  code: ".box {\n  position: relative;\n  z-index: 10;\n}",
                  analogy:
                    "Like layers of transparent sheets stacked on a table",
                },
                {
                  title: "Position Dependency",
                  description: "z-index works ONLY on positioned elements.",
                  keyPoints: [
                    "Requires position: relative | absolute | fixed | sticky",
                    "Does NOT work with position: static",
                  ],
                  code: ".modal {\n  position: fixed;\n  z-index: 1000;\n}",
                  warning: "No position → no z-index effect",
                },
                {
                  title: "Default Stacking Order",
                  description:
                    "When z-index is not defined, browser follows default rules.",
                  order: [
                    "Background & borders",
                    "Non-positioned elements",
                    "Positioned elements",
                    "Elements with higher z-index",
                  ],
                  note: "HTML order matters when z-index values are equal",
                },
                {
                  title: "What is Stacking Context?",
                  description:
                    "A stacking context is an isolated z-index world.",
                  keyPoints: [
                    "Created by positioned elements with z-index",
                    "Child z-index cannot escape parent context",
                    "Explains why high z-index sometimes fails",
                  ],
                  code: ".parent {\n  position: relative;\n  z-index: 1;\n}\n.child {\n  z-index: 9999;\n}",
                  takeaway: "9999 inside a lower context is still lower",
                },
                {
                  title: "Common Elements That Create Stacking Context",
                  description:
                    "Some CSS properties create new stacking contexts.",
                  creators: [
                    "position + z-index",
                    "opacity < 1",
                    "transform",
                    "filter",
                    "will-change",
                  ],
                  warning: "Transforms often cause unexpected z-index bugs",
                },
                {
                  title: "Z-index for Overlays & Modals",
                  description: "Overlays must appear above everything.",
                  bestPractices: [
                    "Use position: fixed",
                    "Define a high z-index",
                    "Keep z-index values centralized",
                  ],
                  code: ".overlay {\n  position: fixed;\n  inset: 0;\n  z-index: 1000;\n}",
                },
                {
                  title: "Managing Z-index at Scale",
                  description: "Large apps need discipline.",
                  strategies: [
                    "Avoid random z-index numbers",
                    "Use small scale (10, 20, 30)",
                    "Document z-index layers",
                  ],
                  exampleScale: [
                    "Base: 1–10",
                    "Dropdowns: 100",
                    "Modals: 1000",
                  ],
                },
              ],

              visualStructure: `
Stacking Order:
┌─────────────────────────────┐
│ Modal (z-index: 1000)       │
│ Dropdown (z-index: 100)     │
│ Header (z-index: 10)        │
│ Content (default)           │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using z-index without position",
                  consequence: "z-index has no effect",
                  fix: "Add position other than static",
                },
                {
                  mistake: "Using extremely large z-index values",
                  consequence: "Unmaintainable CSS",
                  fix: "Use a clear z-index scale",
                },
                {
                  mistake: "Ignoring stacking context",
                  consequence: "Element stays behind despite high z-index",
                  fix: "Check parent stacking context",
                },
                {
                  mistake: "Applying transform unnecessarily",
                  consequence: "Unexpected stacking issues",
                  fix: "Avoid transform on layout containers",
                },
              ],

              proTips: [
                "z-index problems are usually stacking context problems",
                "Always check parent elements",
                "Fixed + high z-index for modals",
                "Keep z-index values predictable",
              ],

              realWorldAnalogy:
                "Z-index is like floors in a building:\n- Higher floor = more visible\n- Some rooms are locked inside floors\n- You can’t jump floors from inside a room",
            },

            tasks: [
              "Fix overlapping elements using z-index",
              "Create overlay layers",
              "Understand stacking context issues",
            ],

            examples: [".modal { position: fixed; z-index: 1000; }"],

            useCases: ["Modals", "Dropdowns", "Tooltips", "Overlays"],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand z-index behavior",
              "Identify stacking context issues",
              "Fix common overlap bugs",
              "Build reliable overlays and modals",
            ],
          },
          {
            day: 6,
            topic: "Overlays, Modals & Layout Composition",
            subTopics: [
              "Overlay patterns",
              "Modal positioning",
              "Combining Grid & Flexbox",
              "Layout composition strategy",
            ],
            explanation:
              "Modern UIs often combine Grid for structure and Flexbox for components, along with overlays.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Ready Modal System",
                description:
                  "Build a complete modal system using proper overlay, positioning, and layering.",
                requirements: [
                  "Fullscreen overlay using position: fixed",
                  "Modal centered using Flexbox (not absolute hacks)",
                  "Clear z-index layering (page < overlay < modal)",
                  "Background interaction visually blocked",
                ],
                constraint:
                  "Absolute positioning is not allowed for centering the modal.",
                outcome:
                  "You’ll learn how modern apps implement reliable, reusable modal systems.",
              },
              {
                id: "pt-2",
                title: "Grid + Flexbox Layout Composition Drill",
                description:
                  "Practice composing layouts by assigning clear responsibilities to Grid and Flexbox.",
                steps: [
                  "Create a page shell using Grid (sidebar + content)",
                  "Build card components using Flexbox",
                  "Add a modal overlay on top of the layout",
                ],
                outcome:
                  "You’ll develop architectural thinking for real-world UI layouts.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most layout bugs in production come from mixing layout responsibilities incorrectly.",
              realWorldChecks: [
                "Open modal and verify background is visually and functionally blocked",
                "Resize screen and confirm Grid + Flexbox layout remains stable",
                "Ensure overlay covers full viewport at all times",
              ],
              commonFinding:
                "Beginners overuse absolute positioning instead of composing layouts properly.",
            },
            interviewInsights: {
              commonQuestions: [
                "How do you build a modal that appears above all content?",
                "When would you use Grid vs Flexbox?",
                "Why should overlays be removed from document flow?",
                "How do you manage layout complexity in large apps?",
              ],
              interviewerPerspective:
                "Composition questions reveal architectural maturity, not just CSS knowledge.",
              tip: "Always explain layouts in layers: structure, components, overlays.",
            },
            editorGuidance: {
              starterHint:
                "Before writing CSS, decide which tool owns which responsibility.",
              aiMentorPrompts: [
                "Is this page-level structure or component-level alignment?",
                "Should this be Grid or Flexbox?",
                "Is this element part of layout or an overlay?",
                "Am I creating unnecessary stacking contexts?",
              ],
              validationChecklist: [
                "Grid used for page structure",
                "Flexbox used for internal alignment",
                "Overlay removed from document flow",
                "Z-index layering is intentional and clean",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Real-world UIs are not built using a single layout technique. A solid layout strategy uses Grid for page structure, Flexbox for internal alignment, and position + z-index for overlays like modals and popups.",

              sections: [
                {
                  title: "What Are Overlays?",
                  description:
                    "Overlays sit on top of existing content to focus user attention.",
                  keyPoints: [
                    "Cover the entire viewport or section",
                    "Dim background content",
                    "Usually paired with modals",
                  ],
                  code: ".overlay {\n  position: fixed;\n  inset: 0;\n  background: rgba(0, 0, 0, 0.5);\n}",
                  analogy: "Like dimming the lights to highlight the stage",
                },
                {
                  title: "Modal Positioning",
                  description:
                    "Modals are centered content blocks displayed above overlays.",
                  keyPoints: [
                    "Removed from normal document flow",
                    "Positioned relative to viewport",
                    "Must appear above everything else",
                  ],
                  bestPractices: [
                    "Use position: fixed",
                    "Center using Flexbox",
                    "High z-index value",
                  ],
                  code: ".modal-wrapper {\n  position: fixed;\n  inset: 0;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  z-index: 1000;\n}",
                },
                {
                  title: "Overlay + Modal Pattern",
                  description: "Overlay and modal work together as a system.",
                  structure: [
                    "Overlay covers background",
                    "Modal sits above overlay",
                    "Content interaction is blocked",
                  ],
                  code: ".overlay { z-index: 900; }\n.modal { z-index: 1000; }",
                  takeaway: "Layering order matters",
                },
                {
                  title: "Combining Grid & Flexbox",
                  description: "Modern layouts use Grid and Flexbox together.",
                  strategy: [
                    "Grid → page-level structure",
                    "Flexbox → component-level alignment",
                    "Position → overlays and floating UI",
                  ],
                  example:
                    "Dashboard layout with grid + card components with flex",
                },
                {
                  title: "Grid for Page Structure",
                  description: "Grid defines the main layout of the page.",
                  code: ".page {\n  display: grid;\n  grid-template-columns: 240px 1fr;\n}",
                  useCase: "Sidebar + main content layout",
                },
                {
                  title: "Flexbox for Components",
                  description: "Flexbox handles alignment inside components.",
                  code: ".card {\n  display: flex;\n  flex-direction: column;\n  gap: 12px;\n}",
                  analogy:
                    "Grid builds the building, Flexbox arranges furniture",
                },
                {
                  title: "Layout Composition Strategy",
                  description: "Think in layers, not just CSS properties.",
                  steps: [
                    "Define page structure (Grid)",
                    "Design components (Flexbox)",
                    "Add overlays if needed (Position)",
                  ],
                  principle: "Choose the right tool for the right job",
                },
                {
                  title: "Accessibility Considerations",
                  description: "Overlays must be accessible.",
                  keyPoints: [
                    "Trap focus inside modal",
                    "Prevent background scroll",
                    "Ensure sufficient contrast",
                  ],
                  note: "Accessibility improves product quality",
                },
              ],

              visualStructure: `
UI Layering:
┌─────────────────────────────┐
│ Modal (top layer)           │
│ Overlay                     │
│ Page Layout (Grid)          │
│ Components (Flexbox)        │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Using only absolute positioning for layouts",
                  consequence: "Brittle and unresponsive UI",
                  fix: "Use Grid/Flexbox for layout",
                },
                {
                  mistake: "Overlay not covering full screen",
                  consequence: "Clickable background content",
                  fix: "Use position: fixed and inset: 0",
                },
                {
                  mistake: "Modal behind other elements",
                  consequence: "Broken UI",
                  fix: "Fix stacking context and z-index",
                },
                {
                  mistake: "Mixing layout responsibilities",
                  consequence: "Confusing CSS",
                  fix: "Separate structure, alignment, and overlays",
                },
              ],

              proTips: [
                "Grid + Flexbox is the modern layout combo",
                "Overlays are layers, not layout",
                "Always plan z-index early",
                "Clean composition beats clever hacks",
              ],

              realWorldAnalogy:
                "UI composition is like filmmaking:\n- Set design (Grid)\n- Actor positioning (Flexbox)\n- Spotlight & focus (Overlays)",
            },

            tasks: [
              "Build a modal using position and z-index",
              "Combine Grid for page and Flexbox for cards",
              "Create overlay background",
            ],

            examples: [
              ".overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); }",
            ],

            useCases: [
              "Login modals",
              "Popups",
              "Dashboard UIs",
              "Confirmation dialogs",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "40–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Build overlays and modals correctly",
              "Combine Grid and Flexbox effectively",
              "Understand layout composition strategy",
              "Create real-world UI patterns",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Complete Website Layout",
            subTopics: [
              "Grid-based page structure",
              "Flexbox components",
              "Responsive design",
              "Sticky header & sidebar",
            ],
            explanation:
              "Apply Grid and positioning concepts to build a full multi-page website layout.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Multi-page Website Layout",
                description:
                  "Build a complete multi-page website layout using Grid, Flexbox, and position.",
                requirements: [
                  "Grid-based page structure (header, sidebar/nav, content, footer)",
                  "Flexbox for all internal components (nav, cards, lists)",
                  "Responsive behavior across desktop, tablet, and mobile",
                  "At least one sticky element (header or sidebar)",
                  "Single shared CSS file across all pages",
                ],
                constraint:
                  "No CSS frameworks, no JS, no layout hacks. Only Grid, Flexbox, and position.",
                outcome:
                  "A production-ready layout that can be confidently added to your portfolio.",
              },
              {
                id: "pt-2",
                title: "Senior-Level Layout Review & Refactor",
                description:
                  "Refactor your layout as if preparing it for a real production release.",
                steps: [
                  "Audit Grid vs Flexbox responsibilities",
                  "Remove unnecessary position usage",
                  "Standardize spacing and layout variables",
                  "Test layout consistency across all pages",
                ],
                outcome:
                  "You’ll learn how experienced frontend engineers polish layouts before shipping.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Large layouts expose architectural mistakes that don’t appear in small demos.",
              realWorldChecks: [
                "Navigate between pages and confirm layout consistency",
                "Resize viewport and verify responsive behavior",
                "Ensure sticky elements behave correctly within their containers",
                "Check for horizontal scroll on all pages",
              ],
              commonFinding:
                "Beginners build good single pages but struggle with multi-page consistency.",
            },
            interviewInsights: {
              commonQuestions: [
                "How did you structure a multi-page layout using CSS?",
                "Why did you choose Grid for structure and Flexbox for components?",
                "How did you handle responsiveness across pages?",
                "What challenges did you face while composing this layout?",
              ],
              interviewerPerspective:
                "A full website layout signals real-world readiness and architectural thinking.",
              tip: "Explain your layout in layers: structure (Grid), components (Flexbox), utilities (position).",
            },
            editorGuidance: {
              starterHint:
                "Build the layout skeleton first, then fill pages one by one.",
              aiMentorPrompts: [
                "Is my layout structure reusable across pages?",
                "Are Grid and Flexbox responsibilities clearly separated?",
                "Does the layout degrade gracefully on mobile?",
                "Would another developer understand this CSS easily?",
              ],
              validationChecklist: [
                "Consistent Grid structure across pages",
                "Flexbox used only inside sections/components",
                "Sticky elements behave correctly",
                "CSS is clean, readable, and reusable",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "This project brings everything together. You will build a complete website layout similar to real company or portfolio websites, using CSS Grid for structure, Flexbox for components, and position for sticky elements. This is where you start thinking like a frontend engineer, not just a learner.",

              sections: [
                {
                  title: "Project Overview",
                  description:
                    "You will design a multi-page website layout (structure only, no backend).",
                  pages: ["Home page", "About page", "Services page"],
                  requirements: [
                    "HTML + CSS only",
                    "Reusable layout structure",
                    "Responsive across devices",
                  ],
                  output: "index.html, about.html, services.html + styles.css",
                  analogy:
                    "This is your transition from layouts to real websites",
                },
                {
                  title: "Grid-Based Page Structure",
                  description: "CSS Grid defines the skeleton of the website.",
                  layoutParts: [
                    "Header",
                    "Sidebar or navigation",
                    "Main content area",
                    "Footer",
                  ],
                  gridStrategy: [
                    "Use grid-template-columns for layout",
                    "Keep structure consistent across pages",
                  ],
                  code: ".page {\n  display: grid;\n  grid-template-columns: 240px 1fr;\n  min-height: 100vh;\n}",
                },
                {
                  title: "Flexbox Inside Sections",
                  description:
                    "Flexbox handles alignment and spacing inside each section.",
                  useCases: [
                    "Navbar items",
                    "Service cards",
                    "Content alignment",
                  ],
                  code: ".services {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 24px;\n}",
                  analogy:
                    "Grid builds the layout, Flexbox organizes the content",
                },
                {
                  title: "Responsive Design Strategy",
                  description:
                    "Your layout must adapt cleanly to all screen sizes.",
                  checklist: [
                    "Desktop: sidebar + content",
                    "Tablet: stacked or reduced sidebar",
                    "Mobile: single-column layout",
                  ],
                  code: "@media (max-width: 768px) {\n  .page {\n    grid-template-columns: 1fr;\n  }\n}",
                  principle: "One layout, multiple screen experiences",
                },
                {
                  title: "Sticky Header & Sidebar",
                  description:
                    "Sticky elements improve navigation and usability.",
                  options: [
                    "Sticky header for branding",
                    "Sticky sidebar for navigation",
                  ],
                  code: ".header {\n  position: sticky;\n  top: 0;\n}\n\n.sidebar {\n  position: sticky;\n  top: 72px;\n}",
                  note: "Sticky works within parent height",
                },
                {
                  title: "Multi-page Consistency",
                  description: "Consistency is critical in real websites.",
                  bestPractices: [
                    "Reuse same CSS file",
                    "Keep layout grid consistent",
                    "Only content should change between pages",
                  ],
                  takeaway: "Good layouts scale across pages",
                },
                {
                  title: "Production-Ready Checklist",
                  description: "Before considering the project complete.",
                  checklist: [
                    "No horizontal scrolling",
                    "Consistent spacing",
                    "Readable typography",
                    "Clean class names",
                    "No layout hacks",
                  ],
                },
              ],

              visualStructure: `
Complete Website Layout:
┌─────────────────────────────┐
│ Header (Sticky)             │
├─────────────┬───────────────┤
│ Sidebar     │ Main Content  │
│ (Sticky)    │               │
│             │               │
├─────────────┴───────────────┤
│ Footer                      │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Mixing Grid and Flexbox randomly",
                  consequence: "Confusing and hard-to-maintain CSS",
                  fix: "Assign clear responsibility to each",
                },
                {
                  mistake: "Ignoring responsive behavior",
                  consequence: "Broken mobile experience",
                  fix: "Test layouts on small screens early",
                },
                {
                  mistake: "Overusing position for layout",
                  consequence: "Brittle design",
                  fix: "Use position only for sticky or overlays",
                },
                {
                  mistake: "Inconsistent spacing across pages",
                  consequence: "Unprofessional look",
                  fix: "Standardize spacing values",
                },
              ],

              proTips: [
                "Think in layout layers, not CSS properties",
                "Grid for structure, Flexbox for alignment",
                "Sticky improves UX when used sparingly",
                "This project belongs in your portfolio",
              ],

              realWorldAnalogy:
                "Building a full website layout is like designing a house:\n- Blueprint (Grid)\n- Furniture placement (Flexbox)\n- Fixed utilities like stairs & rails (Sticky)",
            },

            tasks: [
              "Create homepage, about, and services layout",
              "Use Grid for main layout",
              "Use Flexbox inside sections",
              "Add sticky header or sidebar",
            ],

            examples: ["Tech company website layout"],

            useCases: [
              "Company websites",
              "Portfolio sites",
              "Real production layouts",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "150–180 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Build complete multi-page layouts",
              "Combine Grid, Flexbox, and Position correctly",
              "Create responsive production-ready UI",
              "Gain confidence in real-world layout building",
            ],
          },
        ],
      },
      {
        week: 6,
        weekGoal:
          "Build fully responsive, animated, modern websites using advanced CSS",
        weekOutcome:
          "Professional-grade responsive UI with animations and reusable styles",
        miniProject: "Fully Responsive Portfolio Website",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "Responsive Design Fundamentals",
            subTopics: [
              "What is responsive design",
              "Mobile-first vs desktop-first",
              "Viewport meta tag",
              "Fluid layouts",
            ],
            explanation:
              "Responsive design ensures websites adapt smoothly across devices by adjusting layout and styles based on screen size.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Non-Responsive to Responsive Refactor",
                description:
                  "Take a fixed-width desktop layout and convert it into a responsive, mobile-first layout.",
                requirements: [
                  "Add proper viewport meta tag",
                  "Remove fixed widths",
                  "Use percentages, max-width, or fr units",
                  "Ensure no horizontal scrolling on mobile",
                ],
                constraint:
                  "Media queries are not allowed at this stage. Responsiveness must come from fluid layout only.",
                outcome:
                  "You’ll understand that responsiveness starts with fluid thinking, not breakpoints.",
              },
              {
                id: "pt-2",
                title: "Mobile-First Mindset Drill",
                description:
                  "Practice designing layouts starting from the smallest screen.",
                steps: [
                  "Design layout for mobile width first",
                  "Enhance layout visually for larger screens",
                  "Identify what actually needs to change",
                ],
                outcome:
                  "You’ll build the habit of thinking mobile-first like real frontend engineers.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most production UI bugs happen because responsiveness is added too late.",
              realWorldChecks: [
                "Open site on a mobile device or DevTools device mode",
                "Check for horizontal scrolling",
                "Verify text readability without zoom",
              ],
              commonFinding:
                "Beginners assume layouts are responsive because they shrink, not because they adapt.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is responsive design?",
                "What does the viewport meta tag do?",
                "Mobile-first vs desktop-first — which do you prefer and why?",
                "What are fluid layouts?",
              ],
              interviewerPerspective:
                "Responsive design questions test mindset, not just CSS knowledge.",
              tip: "Always explain responsiveness in terms of user experience and content flow.",
            },
            editorGuidance: {
              starterHint:
                "If your layout breaks on mobile, it’s usually a width problem.",
              aiMentorPrompts: [
                "Does this layout work at 320px width?",
                "Am I using fixed widths unnecessarily?",
                "Is the viewport meta tag present?",
                "Can this layout adapt without breakpoints?",
              ],
              validationChecklist: [
                "Viewport meta tag added",
                "No fixed-width containers",
                "Layout adapts smoothly on resize",
                "No horizontal overflow",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Users access websites from phones, tablets, laptops, and large screens. Responsive design ensures your website looks usable and intentional on every device — without creating separate versions.",

              sections: [
                {
                  title: "What is Responsive Design?",
                  description:
                    "Responsive design adapts layout, spacing, and content based on screen size.",
                  keyPoints: [
                    "One website for all devices",
                    "Layout adapts, content stays same",
                    "Core requirement in modern frontend",
                  ],
                  analogy: "Like water — it takes the shape of the container",
                },
                {
                  title: "Why Responsive Design Matters",
                  description: "Most traffic today comes from mobile devices.",
                  reasons: [
                    "Better user experience",
                    "SEO ranking factor",
                    "Higher engagement and retention",
                  ],
                  takeaway: "Non-responsive sites feel broken",
                },
                {
                  title: "Viewport Meta Tag",
                  description:
                    "The viewport tells the browser how to scale the page.",
                  keyPoints: [
                    "Required for mobile devices",
                    "Controls initial zoom level",
                    "Without it, layouts appear zoomed-out",
                  ],
                  code: "<meta name='viewport' content='width=device-width, initial-scale=1.0'>",
                  warning: "Missing viewport = broken mobile layout",
                },
                {
                  title: "Mobile-first vs Desktop-first",
                  description: "Two strategies for responsive design.",
                  mobileFirst: [
                    "Design for small screens first",
                    "Add features for larger screens",
                    "Cleaner and scalable",
                  ],
                  desktopFirst: [
                    "Design for desktop first",
                    "Remove features for mobile",
                    "Harder to maintain",
                  ],
                  recommendation: "Mobile-first is the industry standard",
                },
                {
                  title: "Fluid Layouts",
                  description:
                    "Fluid layouts resize naturally with screen size.",
                  keyPoints: [
                    "Avoid fixed widths",
                    "Use %, vw, fr, and auto",
                    "Combine with Flexbox/Grid",
                  ],
                  code: ".container {\n  width: 100%;\n  max-width: 1200px;\n}",
                  analogy: "Like elastic — stretches but has limits",
                },
                {
                  title: "Fixed vs Fluid (Quick Comparison)",
                  description:
                    "Understanding the difference prevents layout bugs.",
                  comparison: [
                    "Fixed: width: 1200px → breaks on mobile",
                    "Fluid: max-width + percentages → adapts",
                  ],
                  note: "Fixed layouts are rarely acceptable today",
                },
                {
                  title: "Responsive Design Mindset",
                  description: "Responsive design is a way of thinking.",
                  principles: [
                    "Content first",
                    "Flexible containers",
                    "Progressive enhancement",
                  ],
                  takeaway: "Don’t design for devices — design for screens",
                },
              ],

              visualStructure: `
Responsive Layout Concept:
Desktop:  [ Content  |  Content  |  Content ]
Tablet:   [ Content  |  Content ]
Mobile:   [ Content ]
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting viewport meta tag",
                  consequence: "Zoomed-out, unusable mobile UI",
                  fix: "Always include viewport meta",
                },
                {
                  mistake: "Using fixed widths everywhere",
                  consequence: "Horizontal scrolling",
                  fix: "Use fluid units",
                },
                {
                  mistake: "Designing only for desktop",
                  consequence: "Poor mobile experience",
                  fix: "Adopt mobile-first approach",
                },
              ],

              proTips: [
                "Mobile-first saves time long-term",
                "Test responsiveness early",
                "Think flexible, not fixed",
                "Most CSS bugs appear on small screens",
              ],

              realWorldAnalogy:
                "Responsive design is like clothing sizes:\n- One design\n- Fits multiple body types\n- Adjusts without breaking",
            },

            tasks: [
              "Add viewport meta tag to existing projects",
              "Analyze layout behavior on mobile vs desktop",
              "Convert fixed widths to fluid units",
            ],

            examples: [
              "<meta name='viewport' content='width=device-width, initial-scale=1.0'>",
            ],

            useCases: [
              "Mobile-friendly websites",
              "Cross-device compatibility",
              "Modern web applications",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand responsive design principles",
              "Differentiate mobile-first and desktop-first",
              "Use viewport meta tag correctly",
              "Build fluid, adaptable layouts",
            ],
          },
          {
            day: 2,
            topic: "Media Queries & Breakpoints",
            subTopics: [
              "Media query syntax",
              "Common breakpoints (mobile, tablet, desktop)",
              "Min-width vs max-width",
              "Responsive layout adjustments",
            ],
            explanation:
              "Media queries allow applying different styles based on screen size and device characteristics.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Mobile-First Breakpoint Refactor",
                description:
                  "Refactor an existing responsive layout to follow a clean mobile-first breakpoint strategy.",
                requirements: [
                  "Base styles written for mobile",
                  "Use min-width media queries only",
                  "Add maximum 2 meaningful breakpoints",
                  "Layout must adapt without horizontal scrolling",
                ],
                constraint: "Mixing min-width and max-width is not allowed.",
                outcome:
                  "You’ll learn how professionals keep responsive CSS readable and scalable.",
              },
              {
                id: "pt-2",
                title: "Content-Driven Breakpoint Drill",
                description:
                  "Practice adding breakpoints only when layout genuinely breaks.",
                steps: [
                  "Start with a fluid layout",
                  "Resize screen until layout feels uncomfortable",
                  "Add a breakpoint only at that point",
                  "Remove any unnecessary breakpoints",
                ],
                outcome:
                  "You’ll stop guessing breakpoints and start letting content drive decisions.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Poor breakpoint decisions are one of the biggest causes of messy responsive CSS.",
              realWorldChecks: [
                "Resize viewport slowly and observe layout changes",
                "Ensure no overlapping or hidden content",
                "Verify readability at common widths (320px, 768px, 1024px)",
              ],
              commonFinding:
                "Beginners add breakpoints based on devices instead of layout needs.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is a media query?",
                "Difference between min-width and max-width?",
                "How do you decide breakpoints?",
                "Why is mobile-first preferred?",
              ],
              interviewerPerspective:
                "Breakpoint questions reveal how clean and scalable a developer’s CSS will be.",
              tip: "Always explain breakpoints as content-driven, not device-driven.",
            },
            editorGuidance: {
              starterHint:
                "If you’re writing many breakpoints, your base layout may be wrong.",
              aiMentorPrompts: [
                "Does this layout work well on mobile first?",
                "Is this breakpoint really necessary?",
                "Am I changing layout or just hiding content?",
                "Can this be solved with Grid/Flex instead?",
              ],
              validationChecklist: [
                "Mobile-first base styles",
                "Consistent breakpoint strategy",
                "No horizontal overflow",
                "CSS remains readable and predictable",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Media queries are the control switch of responsive design. They decide WHEN your layout should change. Used correctly, they make layouts adaptive. Used poorly, they create messy CSS.",

              sections: [
                {
                  title: "Media Query Syntax",
                  description:
                    "Media queries conditionally apply CSS based on screen rules.",
                  keyPoints: [
                    "Written using @media",
                    "Most common condition is width",
                    "Rules apply only when condition is true",
                  ],
                  syntax: "@media (condition) { /* CSS */ }",
                  code: "@media (max-width: 768px) {\n  .nav {\n    flex-direction: column;\n  }\n}",
                  analogy: "Like an IF statement for CSS",
                },
                {
                  title: "Common Breakpoints",
                  description: "Breakpoints represent major layout shifts.",
                  breakpoints: [
                    "Mobile: up to 600–768px",
                    "Tablet: 768–1024px",
                    "Desktop: 1024px and above",
                  ],
                  note: "Breakpoints are guidelines, not rules",
                },
                {
                  title: "Min-width vs Max-width",
                  description: "Choosing the right strategy keeps CSS clean.",
                  minWidth: [
                    "Used in mobile-first design",
                    "Styles apply from this width upward",
                    "Scales better",
                  ],
                  maxWidth: [
                    "Used in desktop-first design",
                    "Styles apply below a width",
                    "Can become messy",
                  ],
                  recommendation: "Prefer min-width for mobile-first",
                  code: "@media (min-width: 768px) {\n  .layout { grid-template-columns: 1fr 3fr; }\n}",
                },
                {
                  title: "Responsive Layout Adjustments",
                  description: "Layouts change, not content.",
                  adjustments: [
                    "Change flex-direction",
                    "Modify grid columns",
                    "Adjust spacing and font size",
                  ],
                  example: "Navbar switches from row to column",
                },
                {
                  title: "Hiding & Showing Elements",
                  description: "Some UI elements are device-specific.",
                  techniques: [
                    "display: none",
                    "visibility: hidden (rare)",
                    "Conditional layouts",
                  ],
                  code: "@media (max-width: 768px) {\n  .desktop-only { display: none; }\n}",
                  warning: "Avoid hiding critical content",
                },
                {
                  title: "Breakpoint Strategy",
                  description: "Good breakpoints follow content, not devices.",
                  guidelines: [
                    "Add breakpoint when layout breaks",
                    "Avoid too many breakpoints",
                    "Keep CSS readable",
                  ],
                  takeaway: "Let content dictate breakpoints",
                },
              ],

              visualStructure: `
Breakpoints Flow:
Mobile   →  Tablet   →  Desktop
Column      2-col        3-col
Layout      Layout       Layout
    `,

              commonMistakes: [
                {
                  mistake: "Too many breakpoints",
                  consequence: "Hard-to-maintain CSS",
                  fix: "Use only necessary breakpoints",
                },
                {
                  mistake: "Mixing min-width and max-width",
                  consequence: "Confusing override logic",
                  fix: "Stick to one strategy",
                },
                {
                  mistake: "Hiding content instead of redesigning",
                  consequence: "Poor UX",
                  fix: "Reflow layout instead",
                },
              ],

              proTips: [
                "Think mobile-first",
                "Breakpoints should feel natural",
                "Change layout, not content",
                "Test responsiveness frequently",
              ],

              realWorldAnalogy:
                "Breakpoints are like traffic signals:\n- They control flow\n- They activate only when needed\n- Too many signals slow everything",
            },

            tasks: [
              "Add breakpoints to existing layout",
              "Adjust grid and flex layouts per screen size",
              "Hide/show elements responsively",
            ],

            examples: [
              "@media (max-width: 768px) { .nav { flex-direction: column; } }",
            ],

            useCases: [
              "Mobile navigation",
              "Tablet-friendly layouts",
              "Responsive dashboards",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Write effective media queries",
              "Choose meaningful breakpoints",
              "Apply responsive layout changes",
              "Follow mobile-first best practices",
            ],
          },
          {
            day: 3,
            topic: "Responsive Images & Assets",
            subTopics: [
              "Responsive images",
              "max-width: 100%",
              "srcset and sizes",
              "Picture element",
            ],
            explanation:
              "Responsive images ensure optimal loading and display quality across different devices and resolutions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Responsive Image System (Production Setup)",
                description:
                  "Implement a complete responsive image strategy for a real-world page.",
                requirements: [
                  "All images scale using max-width: 100%",
                  "Use srcset for high-DPI support",
                  "Use sizes attribute correctly",
                  "Include meaningful alt text for every image",
                ],
                constraint:
                  "Fixed-width images and CSS background images are not allowed for content images.",
                outcome:
                  "You’ll understand how professional websites balance image quality and performance.",
              },
              {
                id: "pt-2",
                title: "Art Direction with Picture Element",
                description:
                  "Serve different images for mobile and desktop using the picture element.",
                steps: [
                  "Create a hero image for desktop",
                  "Create a cropped/optimized image for mobile",
                  "Switch images using media conditions",
                  "Verify correct image loading in DevTools",
                ],
                outcome:
                  "You’ll learn when resolution switching is not enough and art direction is needed.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Images are often the biggest performance bottleneck in real-world websites.",
              realWorldChecks: [
                "Inspect which image is loaded at different screen sizes",
                "Check image file sizes in Network tab",
                "Verify images remain sharp on high-DPI screens",
              ],
              commonFinding:
                "Beginners load one large image everywhere and rely only on CSS scaling.",
            },
            interviewInsights: {
              commonQuestions: [
                "How do you make images responsive?",
                "What is srcset and why is it used?",
                "Difference between srcset and picture?",
                "How do you support high-DPI (retina) screens?",
              ],
              interviewerPerspective:
                "Responsive image questions test performance awareness, not just HTML knowledge.",
              tip: "Always mention performance, bandwidth, and UX when discussing images.",
            },
            editorGuidance: {
              starterHint:
                "Start with CSS scaling, then add srcset only when needed.",
              aiMentorPrompts: [
                "Is this image content or decorative?",
                "Does this image need art direction?",
                "Am I loading a larger image than necessary?",
                "Is alt text meaningful and accessible?",
              ],
              validationChecklist: [
                "Images scale within containers",
                "srcset used for resolution switching",
                "picture used for art direction",
                "Images optimized and accessible",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Images are often the heaviest assets on a website. Responsive images ensure that users get the right image size for their device — not too big, not too small — improving performance, clarity, and user experience.",

              sections: [
                {
                  title: "Why Responsive Images Matter",
                  description: "One image size does not fit all devices.",
                  problemsWithoutResponsiveImages: [
                    "Large images slow down mobile sites",
                    "Small images look blurry on high-DPI screens",
                    "Unnecessary bandwidth usage",
                  ],
                  takeaway: "Responsive images = faster + sharper websites",
                },
                {
                  title: "Making Images Responsive with CSS",
                  description: "The simplest step toward responsive images.",
                  keyPoints: [
                    "Image scales with its container",
                    "Prevents overflow on small screens",
                    "Maintains aspect ratio",
                  ],
                  code: "img {\n  max-width: 100%;\n  height: auto;\n}",
                  analogy: "Like liquid inside a glass — it fits the container",
                },
                {
                  title: "Understanding High-DPI (Retina) Screens",
                  description:
                    "Some devices pack more pixels into the same space.",
                  keyPoints: [
                    "Retina displays need higher-resolution images",
                    "Same CSS size, more physical pixels",
                    "srcset solves this problem",
                  ],
                  example:
                    "iPhone displays sharper images than standard screens",
                },
                {
                  title: "srcset Attribute",
                  description: "srcset provides multiple image versions.",
                  keyPoints: [
                    "Browser chooses best image automatically",
                    "Based on screen resolution or width",
                    "Improves performance and clarity",
                  ],
                  syntax: "srcset='image-1x.jpg 1x, image-2x.jpg 2x'",
                  code: "<img src='image.jpg' srcset='image.jpg 1x, image@2x.jpg 2x' alt='Example' />",
                  takeaway: "Let the browser decide — it knows best",
                },
                {
                  title: "Using sizes with srcset",
                  description:
                    "sizes tells the browser how much space the image occupies.",
                  keyPoints: [
                    "Works with width-based srcset",
                    "Improves image selection accuracy",
                    "Critical for responsive layouts",
                  ],
                  code: "<img\n  src='image.jpg'\n  srcset='small.jpg 480w, medium.jpg 768w, large.jpg 1200w'\n  sizes='(max-width: 768px) 100vw, 50vw'\n  alt='Responsive image'\n/>",
                },
                {
                  title: "Picture Element",
                  description:
                    "The <picture> element gives full control over image sources.",
                  keyPoints: [
                    "Use different images for different conditions",
                    "Great for art direction",
                    "Supports media queries",
                  ],
                  code: "<picture>\n  <source media='(max-width: 768px)' srcset='mobile.jpg'>\n  <source media='(min-width: 769px)' srcset='desktop.jpg'>\n  <img src='fallback.jpg' alt='Example'>\n</picture>",
                  analogy:
                    "Like choosing a different photo for mobile vs desktop",
                },
                {
                  title: "Image Optimization Best Practices",
                  description: "Responsive images work best with optimization.",
                  bestPractices: [
                    "Use modern formats (WebP, AVIF)",
                    "Compress images",
                    "Lazy-load below-the-fold images",
                  ],
                  note: "Performance is part of UX",
                },
                {
                  title: "When to Use What",
                  description: "Choose the right tool for the job.",
                  guidelines: [
                    "Simple scaling → CSS max-width",
                    "Resolution switching → srcset",
                    "Art direction → picture element",
                  ],
                },
              ],

              visualStructure: `
Image Strategy:
Desktop → Large image
Tablet  → Medium image
Mobile  → Small image
(Browser selects automatically)
    `,

              commonMistakes: [
                {
                  mistake: "Using fixed width images",
                  consequence: "Layout breaks on small screens",
                  fix: "Use max-width: 100%",
                },
                {
                  mistake: "Ignoring high-DPI screens",
                  consequence: "Blurry images",
                  fix: "Use srcset",
                },
                {
                  mistake: "Overusing large images everywhere",
                  consequence: "Slow loading pages",
                  fix: "Optimize and resize images",
                },
                {
                  mistake: "Forgetting alt attributes",
                  consequence: "Accessibility issues",
                  fix: "Always include meaningful alt text",
                },
              ],

              proTips: [
                "Start with CSS scaling, then enhance",
                "srcset is easier than it looks",
                "Let browsers handle image selection",
                "Performance is a feature",
              ],

              realWorldAnalogy:
                "Responsive images are like serving food portions:\n- Small plate for kids (mobile)\n- Medium for adults (tablet)\n- Large for athletes (desktop)",
            },

            tasks: [
              "Make images scale responsively",
              "Implement srcset for multiple resolutions",
              "Optimize image loading",
            ],

            examples: ["img { max-width: 100%; height: auto; }"],

            useCases: [
              "Portfolio galleries",
              "High-DPI screens support",
              "Performance-optimized websites",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Create responsive images",
              "Support high-resolution screens",
              "Use srcset and picture correctly",
              "Optimize images for performance",
            ],
          },
          {
            day: 4,
            topic: "CSS Transitions & Hover Effects",
            subTopics: [
              "transition property",
              "transition-duration",
              "transition-timing-function",
              "Hover effects",
            ],
            explanation:
              "CSS transitions add smooth visual feedback and improve user experience without JavaScript.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Micro-Interaction Upgrade for UI Components",
                description:
                  "Enhance existing UI components with subtle, performance-friendly transitions.",
                requirements: [
                  "Add hover transitions to buttons and cards",
                  "Animate only transform and/or opacity",
                  "Use consistent duration and timing-function",
                  "No transition: all usage",
                ],
                constraint:
                  "Layout-affecting properties (width, height, top, left) must not be animated.",
                outcome:
                  "You’ll learn how professionals add polish without hurting performance.",
              },
              {
                id: "pt-2",
                title: "Hover Feedback Consistency Audit",
                description:
                  "Review and standardize hover effects across a page.",
                steps: [
                  "List all interactive elements (buttons, links, cards)",
                  "Apply consistent hover behavior",
                  "Reduce overly strong or distracting effects",
                ],
                outcome:
                  "You’ll develop an eye for UI consistency and restraint.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Poorly implemented animations can make fast apps feel slow.",
              realWorldChecks: [
                "Hover rapidly over elements and observe smoothness",
                "Check for layout shifts during transitions",
                "Test on low-end devices or throttled performance",
              ],
              commonFinding:
                "Beginners animate too many properties and create janky interactions.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the transition property?",
                "Why should you avoid transition: all?",
                "Which CSS properties are best for animations?",
                "How do transitions improve UX?",
              ],
              interviewerPerspective:
                "Transition questions reveal whether a developer understands performance and UX.",
              tip: "Always mention performance (GPU-friendly properties) when talking about animations.",
            },
            editorGuidance: {
              starterHint:
                "If an animation draws attention to itself, it’s probably too much.",
              aiMentorPrompts: [
                "Is this transition helping the user?",
                "Can I reduce duration or intensity?",
                "Am I animating a GPU-friendly property?",
                "Is this effect consistent with other components?",
              ],
              validationChecklist: [
                "Specific properties defined in transition",
                "Durations kept under 0.4s",
                "No layout-breaking animations",
                "Hover effects feel subtle and intentional",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Good UI doesn’t just look good — it feels good. CSS transitions provide smooth feedback when users interact with elements like buttons, cards, and links. These subtle animations improve usability and perceived performance.",

              sections: [
                {
                  title: "What Are CSS Transitions?",
                  description:
                    "Transitions animate changes between two states.",
                  keyPoints: [
                    "Triggered by state changes (hover, focus, active)",
                    "No JavaScript required",
                    "Best for small, smooth animations",
                  ],
                  analogy:
                    "Like easing a car brake instead of stopping suddenly",
                },
                {
                  title: "transition Property",
                  description: "Defines which property should animate.",
                  keyPoints: [
                    "Can target specific properties",
                    "Avoid using transition: all",
                    "Only animatable properties work",
                  ],
                  code: ".button {\n  transition: background-color 0.3s ease;\n}",
                  warning:
                    "Avoid animating layout properties like width excessively",
                },
                {
                  title: "transition-duration",
                  description: "Controls how long the animation lasts.",
                  keyPoints: [
                    "Measured in seconds (s) or milliseconds (ms)",
                    "Short durations feel snappy",
                    "Long durations feel slow",
                  ],
                  bestRange: "0.2s – 0.4s for UI interactions",
                  code: ".card {\n  transition-duration: 0.3s;\n}",
                },
                {
                  title: "transition-timing-function",
                  description: "Controls the speed curve of the animation.",
                  keyPoints: [
                    "ease is default",
                    "ease-in, ease-out control motion",
                    "cubic-bezier allows custom curves",
                  ],
                  commonValues: [
                    "ease",
                    "linear",
                    "ease-in",
                    "ease-out",
                    "ease-in-out",
                  ],
                  code: ".card {\n  transition-timing-function: ease-in-out;\n}",
                  analogy: "Like acceleration and deceleration in real motion",
                },
                {
                  title: "Hover Effects",
                  description: "Hover effects give immediate visual feedback.",
                  commonEffects: [
                    "Color change",
                    "Scale or lift",
                    "Shadow enhancement",
                  ],
                  code: ".card {\n  transition: transform 0.3s ease, box-shadow 0.3s ease;\n}\n.card:hover {\n  transform: scale(1.05);\n  box-shadow: 0 10px 20px rgba(0,0,0,0.15);\n}",
                  takeaway: "Hover effects should feel subtle, not flashy",
                },
                {
                  title: "Best Properties to Animate",
                  description: "Some properties perform better than others.",
                  recommended: ["opacity", "transform"],
                  avoidWhenPossible: ["width", "height", "top / left"],
                  note: "Transforms are GPU-accelerated",
                },
                {
                  title: "UX Guidelines for Transitions",
                  description: "Animations should support usability.",
                  guidelines: [
                    "Use transitions for feedback, not decoration",
                    "Keep animations fast",
                    "Be consistent across components",
                  ],
                },
              ],

              visualStructure: `
Interaction Flow:
Hover → Transition starts → Smooth change → New state
    `,

              commonMistakes: [
                {
                  mistake: "Using transition: all",
                  consequence: "Unexpected animations",
                  fix: "Specify exact properties",
                },
                {
                  mistake: "Over-animating UI",
                  consequence: "Distracting experience",
                  fix: "Use subtle transitions",
                },
                {
                  mistake: "Slow animations",
                  consequence: "UI feels laggy",
                  fix: "Reduce duration",
                },
              ],

              proTips: [
                "0.3s is the sweet spot for most UI transitions",
                "Prefer transform and opacity",
                "Hover effects should guide attention",
                "Consistency matters more than creativity",
              ],

              realWorldAnalogy:
                "Transitions are like body language:\n- Subtle\n- Natural\n- Instantly communicative",
            },

            tasks: [
              "Add hover transitions to buttons",
              "Animate cards on hover",
              "Improve UI interactivity",
            ],

            examples: [
              ".card { transition: transform 0.3s ease; }\n.card:hover { transform: scale(1.05); }",
            ],

            useCases: [
              "Interactive UI components",
              "Modern product cards",
              "Buttons and CTAs",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand CSS transitions",
              "Add smooth hover effects",
              "Improve UI feedback",
              "Create polished user interactions",
            ],
          },
          {
            day: 5,
            topic: "CSS Transforms & Animations",
            subTopics: [
              "transform: rotate, scale, translate",
              "Animation keyframes",
              "Animation duration and delay",
              "Combining animations with transitions",
            ],
            explanation:
              "Transforms and animations bring motion to UI elements, making interfaces feel dynamic and engaging.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Entrance Animation System",
                description:
                  "Design a clean entrance animation system for a landing page.",
                requirements: [
                  "Create at least 2 reusable keyframe animations",
                  "Use animations only on page load (no infinite loops)",
                  "Apply animations to hero text and buttons",
                  "Use transform and opacity only",
                ],
                constraint:
                  "Animations must not affect layout or cause reflow.",
                outcome:
                  "You’ll learn how professionals introduce content with motion without hurting performance.",
              },
              {
                id: "pt-2",
                title: "Interaction vs Storytelling Motion Audit",
                description:
                  "Separate interaction-based motion from automatic animations.",
                steps: [
                  "Use animations for initial appearance",
                  "Use transitions for hover and focus",
                  "Remove any unnecessary looping animations",
                ],
                outcome:
                  "You’ll clearly understand when to use animations vs transitions in real products.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Poor animation choices can make even fast websites feel slow.",
              realWorldChecks: [
                "Reload page and observe entrance animations",
                "Hover elements repeatedly to test responsiveness",
                "Check CPU usage in DevTools performance tab",
              ],
              commonFinding:
                "Beginners overuse animations where transitions are enough.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between CSS transitions and animations?",
                "Why are transform and opacity preferred?",
                "What are keyframes?",
                "How do animations impact performance?",
              ],
              interviewerPerspective:
                "Animation questions reveal a developer’s UX sensitivity and performance awareness.",
              tip: "Always explain animations in terms of purpose, not just syntax.",
            },
            editorGuidance: {
              starterHint:
                "If animation distracts the user, it’s doing more harm than good.",
              aiMentorPrompts: [
                "Is this animation guiding attention?",
                "Can this be a transition instead?",
                "Am I animating GPU-friendly properties?",
                "Does this motion feel natural and fast?",
              ],
              validationChecklist: [
                "Animations use transform/opacity",
                "No infinite or unnecessary animations",
                "Durations feel snappy",
                "Transitions and animations have clear roles",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Animations guide attention, communicate state, and add personality to interfaces. CSS transforms and animations allow you to create smooth, performant motion without JavaScript — when used with restraint and purpose.",

              sections: [
                {
                  title: "CSS Transform Basics",
                  description:
                    "Transforms visually change an element without affecting document flow.",
                  keyPoints: [
                    "Does not reflow layout",
                    "GPU-accelerated",
                    "Best for motion and interaction",
                  ],
                  transformTypes: [
                    "translate() – move element",
                    "scale() – resize element",
                    "rotate() – rotate element",
                  ],
                  code: ".box {\n  transform: translateY(-10px) scale(1.05);\n}",
                  analogy:
                    "Like moving, rotating, or zooming an object without changing its place in line",
                },
                {
                  title: "Common Transform Functions",
                  description:
                    "These are the most frequently used transforms in UI.",
                  examples: [
                    "translateX(20px)",
                    "translateY(-10px)",
                    "scale(1.1)",
                    "rotate(5deg)",
                  ],
                  code: ".icon:hover {\n  transform: rotate(15deg);\n}",
                  note: "Transforms can be combined in a single rule",
                },
                {
                  title: "CSS Animations vs Transitions",
                  description:
                    "Both create motion, but they serve different purposes.",
                  transitions: [
                    "Triggered by user interaction",
                    "Two states (start → end)",
                    "Simple and subtle",
                  ],
                  animations: [
                    "Run automatically",
                    "Multiple steps",
                    "Timeline-based",
                  ],
                  takeaway:
                    "Transitions = interaction, Animations = storytelling",
                },
                {
                  title: "Animation Keyframes",
                  description: "Keyframes define animation steps over time.",
                  keyPoints: [
                    "Use @keyframes to define motion",
                    "from/to or percentage-based",
                    "Reusable across elements",
                  ],
                  code: "@keyframes fadeIn {\n  from { opacity: 0; }\n  to { opacity: 1; }\n}",
                  analogy: "Like frames in a cartoon animation",
                },
                {
                  title: "Applying Animations",
                  description:
                    "Animations are applied using animation properties.",
                  properties: [
                    "animation-name",
                    "animation-duration",
                    "animation-delay",
                    "animation-iteration-count",
                    "animation-timing-function",
                  ],
                  code: ".hero {\n  animation: fadeIn 1s ease-in-out;\n}",
                },
                {
                  title: "Duration & Delay",
                  description: "Timing defines how animation feels.",
                  guidelines: [
                    "Short animations feel snappy",
                    "Long animations feel dramatic",
                    "Delay can guide visual order",
                  ],
                  bestRange: "0.5s – 1.2s for entrance animations",
                  code: ".button {\n  animation-duration: 0.6s;\n  animation-delay: 0.2s;\n}",
                },
                {
                  title: "Combining Transitions & Animations",
                  description:
                    "Transitions and animations often work together.",
                  exampleFlow: [
                    "Animation for page load",
                    "Transition for hover interaction",
                  ],
                  code: ".card {\n  animation: fadeIn 0.8s ease;\n  transition: transform 0.3s ease;\n}\n.card:hover {\n  transform: scale(1.05);\n}",
                  takeaway: "Animations introduce, transitions respond",
                },
                {
                  title: "Performance-Friendly Animations",
                  description: "Not all animations are equal.",
                  recommended: ["transform", "opacity"],
                  avoidWhenPossible: ["top / left", "width / height"],
                  note: "Smooth animation = better UX",
                },
              ],

              visualStructure: `
Motion Types:
Load animation → Draw attention
Hover transition → Give feedback
Loop animation → Indicate activity
    `,

              commonMistakes: [
                {
                  mistake: "Overusing animations",
                  consequence: "Distracting UI",
                  fix: "Animate with purpose",
                },
                {
                  mistake: "Animating layout properties",
                  consequence: "Janky performance",
                  fix: "Use transform and opacity",
                },
                {
                  mistake: "Too long animations",
                  consequence: "Slow-feeling interface",
                  fix: "Reduce duration",
                },
              ],

              proTips: [
                "Less animation = more impact",
                "Entrance animations should run once",
                "Hover animations should be fast",
                "Motion should guide attention, not steal it",
              ],

              realWorldAnalogy:
                "Animations are like gestures in conversation:\n- Subtle movements add meaning\n- Too much movement becomes noise",
            },

            tasks: [
              "Create animated buttons",
              "Build loading animation",
              "Animate hero section elements",
            ],

            examples: [
              "@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }",
            ],

            useCases: [
              "Landing page animations",
              "Attention-grabbing UI",
              "Loaders and feedback indicators",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use CSS transforms effectively",
              "Create animations using keyframes",
              "Combine animations with transitions",
              "Build smooth, performant UI motion",
            ],
          },
          {
            day: 6,
            topic: "CSS Variables & Advanced Styling",
            subTopics: [
              "CSS custom properties",
              ":root usage",
              "Theme variables",
              "Reusability and maintainability",
              "Browser compatibility",
            ],
            explanation:
              "CSS variables allow reusable, theme-friendly styling and simplify large codebases.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Mini Design System with CSS Variables",
                description:
                  "Create a small design system using CSS variables for a real project.",
                requirements: [
                  "Define color palette variables (primary, secondary, neutral)",
                  "Create spacing scale variables (xs, sm, md, lg)",
                  "Define font-size variables",
                  "Refactor an existing layout to use only variables",
                ],
                constraint:
                  "Hardcoded color or spacing values are not allowed after refactor.",
                outcome:
                  "You’ll experience how large teams maintain consistent UI using CSS variables.",
              },
              {
                id: "pt-2",
                title: "Theme Toggle Foundation (CSS-only)",
                description:
                  "Prepare your CSS for light/dark theming using custom properties.",
                steps: [
                  "Define base theme variables in :root",
                  "Create a dark theme override using attribute selector",
                  "Ensure components adapt without changing component CSS",
                ],
                outcome:
                  "You’ll understand how modern apps implement theming at scale.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Without variables, small design changes can break consistency across the entire UI.",
              realWorldChecks: [
                "Change a primary color and observe global update",
                "Switch theme variables and verify component consistency",
                "Search CSS for hardcoded duplicate values",
              ],
              commonFinding:
                "Beginners define variables but still hardcode values out of habit.",
            },
            interviewInsights: {
              commonQuestions: [
                "What are CSS custom properties?",
                "Difference between CSS variables and preprocessor variables?",
                "How would you implement dark mode?",
                "Why are CSS variables useful in large projects?",
              ],
              interviewerPerspective:
                "CSS variable questions test scalability and system-thinking, not syntax.",
              tip: "Always explain variables in terms of maintainability and theming.",
            },
            editorGuidance: {
              starterHint:
                "If the same value appears more than twice, it probably needs a variable.",
              aiMentorPrompts: [
                "Is this value part of a design system?",
                "Should this be a global or scoped variable?",
                "Am I naming variables semantically?",
                "Can this value change in the future?",
              ],
              validationChecklist: [
                "Variables defined in :root",
                "Semantic variable naming",
                "No unnecessary hardcoded values",
                "Theme overrides work correctly",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As projects grow, repeating colors, spacing, and font values becomes hard to maintain. CSS variables (custom properties) solve this by creating a single source of truth for design values — similar to constants in programming.",

              sections: [
                {
                  title: "What Are CSS Variables?",
                  description:
                    "CSS variables store reusable values that can be used throughout your stylesheet.",
                  keyPoints: [
                    "Defined with -- prefix",
                    "Accessed using var()",
                    "Can be updated dynamically",
                  ],
                  code: ":root {\n  --primary-color: #3b82f6;\n}\n.button {\n  background: var(--primary-color);\n}",
                  analogy: "Like constants in JavaScript",
                },
                {
                  title: ":root Selector",
                  description:
                    ":root defines global variables available everywhere.",
                  keyPoints: [
                    ":root targets the highest-level element",
                    "Variables defined here are global",
                    "Best place for theme values",
                  ],
                  code: ":root {\n  --font-base: 16px;\n  --spacing-md: 16px;\n}",
                  note: ":root is preferred over body for variables",
                },
                {
                  title: "Using Variables in Real Styles",
                  description:
                    "Variables can be used anywhere a value is expected.",
                  examples: [
                    "color",
                    "background",
                    "margin / padding",
                    "font-size",
                    "border-radius",
                  ],
                  code: ".card {\n  padding: var(--spacing-md);\n  border-radius: var(--radius-sm);\n}",
                },
                {
                  title: "Theme Variables (Light / Dark Mode)",
                  description:
                    "CSS variables make theming simple and scalable.",
                  strategy: [
                    "Define base variables",
                    "Override variables for themes",
                    "Reuse same components",
                  ],
                  code: ":root {\n  --bg: #ffffff;\n  --text: #111111;\n}\n[data-theme='dark'] {\n  --bg: #0f172a;\n  --text: #f8fafc;\n}",
                  takeaway: "Themes without rewriting CSS",
                },
                {
                  title: "Reusability & Maintainability",
                  description: "Variables drastically reduce duplication.",
                  benefits: [
                    "Change design from one place",
                    "Consistent spacing & colors",
                    "Cleaner CSS files",
                  ],
                  comparison:
                    "Without variables → repetitive values\nWith variables → centralized control",
                },
                {
                  title: "Scoped Variables",
                  description:
                    "Variables can be scoped to specific components.",
                  keyPoints: [
                    "Override global values locally",
                    "Useful for component variants",
                  ],
                  code: ".alert {\n  --alert-color: red;\n  color: var(--alert-color);\n}",
                  analogy: "Like local variables inside a function",
                },
                {
                  title: "Browser Compatibility",
                  description:
                    "CSS variables are well-supported in modern browsers.",
                  facts: [
                    "Supported in all modern browsers",
                    "Not supported in IE11",
                    "Safe for production today",
                  ],
                  fallback: "Provide fallback values using var(--x, fallback)",
                  code: "color: var(--primary, blue);",
                },
                {
                  title: "When to Use CSS Variables",
                  description: "Variables shine in structured systems.",
                  useGuidelines: [
                    "Colors & spacing → always",
                    "One-off values → optional",
                    "Design systems → mandatory",
                  ],
                },
              ],

              visualStructure: `
CSS Variable Flow:
Design Tokens → CSS Variables → Components → UI
    `,

              commonMistakes: [
                {
                  mistake: "Using variables everywhere blindly",
                  consequence: "Overcomplicated CSS",
                  fix: "Use variables for shared values",
                },
                {
                  mistake: "Hardcoding values after defining variables",
                  consequence: "Inconsistent UI",
                  fix: "Stick to variable usage",
                },
                {
                  mistake: "Not providing fallbacks",
                  consequence: "Unexpected rendering issues",
                  fix: "Use var(--x, fallback)",
                },
              ],

              proTips: [
                "Treat CSS variables like a design API",
                "Name variables semantically, not visually",
                "Use variables for spacing systems",
                "CSS variables pair well with JS theming",
              ],

              realWorldAnalogy:
                "CSS variables are like a central control panel:\n- One change\n- Entire system updates\n- Predictable and scalable",
            },

            tasks: [
              "Create color and spacing variables",
              "Refactor existing CSS using variables",
              "Implement light/dark theme base",
            ],

            examples: [
              ":root { --primary: #3b82f6; }\nbutton { background: var(--primary); }",
            ],

            useCases: [
              "Design systems",
              "Theme management",
              "Large-scale CSS projects",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand CSS custom properties",
              "Create reusable styling systems",
              "Implement theme-ready CSS",
              "Write maintainable, scalable styles",
            ],
          },
          {
            day: 7,
            topic: "Major Project – Fully Responsive Portfolio",
            subTopics: [
              "Responsive multi-page layout",
              "Media queries",
              "Animations & transitions",
              "Modern CSS patterns",
              "Performance & polish",
            ],
            explanation:
              "This project combines all responsive and advanced CSS concepts into a production-ready portfolio.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Ready Portfolio Build",
                description:
                  "Build a fully responsive, multi-page developer portfolio that you can confidently share with recruiters.",
                requirements: [
                  "Mobile-first CSS architecture",
                  "CSS Grid for page layout",
                  "Flexbox for components",
                  "CSS variables for colors, spacing, typography",
                  "Responsive images and optimized assets",
                  "Hover transitions and subtle entrance animations",
                ],
                constraint:
                  "Over-animation, fixed-width layouts, and hardcoded repeated values are not allowed.",
                outcome:
                  "A real, shareable portfolio that demonstrates professional frontend fundamentals.",
              },
              {
                id: "pt-2",
                title: "UX, Performance & Accessibility Audit",
                description:
                  "Review your portfolio like a senior frontend engineer before shipping.",
                steps: [
                  "Test site at 320px, 768px, and 1200px widths",
                  "Check keyboard navigation across pages",
                  "Verify color contrast and font readability",
                  "Audit images for size and responsiveness",
                  "Remove unused or redundant CSS",
                ],
                outcome:
                  "You’ll learn how professionals polish projects before calling them complete.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Recruiters judge frontend skills by how a site feels, not just how it looks.",
              realWorldChecks: [
                "Scroll entire site on mobile without zoom",
                "Resize screen and observe layout transitions",
                "Check for layout shifts during load",
                "Ensure animations feel smooth and purposeful",
              ],
              commonFinding:
                "Beginners focus on visuals but ignore spacing, responsiveness, and performance.",
            },
            interviewInsights: {
              commonQuestions: [
                "How did you structure your portfolio layout?",
                "Why did you choose Grid vs Flexbox?",
                "How did you handle responsiveness?",
                "What performance and accessibility considerations did you apply?",
              ],
              interviewerPerspective:
                "A clean, responsive portfolio signals real-world readiness more than certificates.",
              tip: "Always explain your portfolio decisions in terms of UX, scalability, and maintainability.",
            },
            editorGuidance: {
              starterHint:
                "If you hesitate to share the link, the project isn’t finished yet.",
              aiMentorPrompts: [
                "Would I hire myself based on this site?",
                "Does this feel good on mobile?",
                "Is my CSS clean and intentional?",
                "Is every animation serving a purpose?",
              ],
              validationChecklist: [
                "Responsive on all screen sizes",
                "Consistent spacing and typography",
                "CSS variables used correctly",
                "No unnecessary effects or hacks",
                "Portfolio feels fast and readable",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A portfolio is not just a website — it’s your professional identity. This project focuses on building a clean, responsive, accessible, and performant portfolio using modern CSS practices. This is the type of project recruiters actually open and scroll.",

              sections: [
                {
                  title: "Project Overview",
                  description:
                    "You will build a complete developer portfolio with multiple pages and polished UI.",
                  pages: [
                    "Home (intro + hero)",
                    "About (skills & journey)",
                    "Projects (cards + details)",
                    "Contact (CTA or form)",
                  ],
                  requirements: [
                    "HTML + CSS only (optional JS for theme toggle)",
                    "Responsive on all screen sizes",
                    "Clean layout & readable typography",
                  ],
                  output:
                    "index.html, about.html, projects.html, contact.html + styles.css",
                  analogy:
                    "This is your digital resume — design it with intention",
                },
                {
                  title: "Responsive Layout Architecture",
                  description:
                    "Use the right layout tools in the right places.",
                  strategy: [
                    "CSS Grid → page-level structure",
                    "Flexbox → components & alignment",
                    "Media queries → layout shifts",
                  ],
                  code: ".page {\n  display: grid;\n  grid-template-columns: 1fr;\n}\n\n@media (min-width: 768px) {\n  .page {\n    grid-template-columns: 240px 1fr;\n  }\n}",
                  takeaway: "Layout first, styling later",
                },
                {
                  title: "Media Queries & Breakpoints",
                  description:
                    "Your portfolio must feel natural on every device.",
                  breakpoints: [
                    "Mobile: single-column",
                    "Tablet: adjusted spacing",
                    "Desktop: multi-column layout",
                  ],
                  bestPractice: "Use min-width (mobile-first)",
                  note: "Add breakpoints only when layout breaks",
                },
                {
                  title: "Animations & Transitions",
                  description: "Motion adds polish, not distraction.",
                  usageGuidelines: [
                    "Hover transitions on cards & buttons",
                    "Entrance animations for hero section",
                    "No infinite or distracting animations",
                  ],
                  code: ".card {\n  transition: transform 0.3s ease, box-shadow 0.3s ease;\n}\n.card:hover {\n  transform: translateY(-6px);\n}",
                  takeaway: "Subtle motion = premium feel",
                },
                {
                  title: "Modern CSS Patterns",
                  description: "Write CSS like a professional.",
                  patterns: [
                    "CSS variables for colors & spacing",
                    "Reusable utility classes",
                    "Consistent naming conventions",
                  ],
                  code: ":root {\n  --primary: #3b82f6;\n  --space-md: 16px;\n}",
                },
                {
                  title: "Accessibility Essentials",
                  description: "A good portfolio is inclusive.",
                  checklist: [
                    "Proper color contrast",
                    "Readable font sizes",
                    "Keyboard navigation support",
                    "Meaningful alt text",
                  ],
                  note: "Accessibility is a hiring signal",
                },
                {
                  title: "Performance & Polish",
                  description: "Fast sites feel better.",
                  bestPractices: [
                    "Optimized images",
                    "Minimal CSS",
                    "No layout shifts",
                  ],
                  checklist: [
                    "No horizontal scrolling",
                    "Consistent spacing",
                    "Clean visual hierarchy",
                  ],
                },
                {
                  title: "Final Review Checklist",
                  description: "Before calling it complete.",
                  checklist: [
                    "Works on mobile, tablet, desktop",
                    "Animations feel smooth",
                    "Layout looks intentional",
                    "Code is readable",
                    "Proud to share the link",
                  ],
                },
              ],

              visualStructure: `
Portfolio Structure:
┌─────────────────────────────┐
│ Header / Navbar             │
├─────────────────────────────┤
│ Hero / Intro                │
├─────────────────────────────┤
│ Projects (Cards/Grid)       │
├─────────────────────────────┤
│ About / Skills              │
├─────────────────────────────┤
│ Contact / CTA               │
└─────────────────────────────┘
    `,

              commonMistakes: [
                {
                  mistake: "Over-designing with too many effects",
                  consequence: "Distracting UI",
                  fix: "Focus on clarity and readability",
                },
                {
                  mistake: "Desktop-only portfolio",
                  consequence: "Poor mobile impression",
                  fix: "Test mobile first",
                },
                {
                  mistake: "Inconsistent spacing and fonts",
                  consequence: "Unprofessional look",
                  fix: "Use CSS variables",
                },
                {
                  mistake: "Ignoring performance",
                  consequence: "Slow loading site",
                  fix: "Optimize images and CSS",
                },
              ],

              proTips: [
                "Your portfolio is judged in 10 seconds",
                "Clean > fancy",
                "Consistency beats creativity",
                "One great project beats five average ones",
              ],

              realWorldAnalogy:
                "A portfolio is like an interview outfit:\n- Clean\n- Comfortable\n- Confident\n- Not flashy, but memorable",
            },

            tasks: [
              "Create multi-page portfolio",
              "Make layout fully responsive",
              "Add subtle animations",
              "Ensure accessibility and performance",
            ],

            examples: ["Developer portfolio website"],

            useCases: [
              "Job applications",
              "Freelance showcase",
              "Personal branding",
            ],

            editorType: "css",

            // ⏱️ Estimated time
            estimatedTime: "4–6 hours",

            // 🎯 Learning objectives
            objectives: [
              "Build a production-ready responsive website",
              "Apply advanced CSS patterns confidently",
              "Demonstrate real-world frontend skills",
              "Create a portfolio worth sharing",
            ],
          },
        ],
      },
    ],
    questions: [
      {
        id: "f1",
        question: "What is the difference between HTML elements and HTML tags?",
        difficulty: "Easy",
        topics: ["HTML Basics"],
      },
      {
        id: "f2",
        question:
          "Explain the purpose of semantic HTML. Give examples of semantic elements.",
        difficulty: "Easy",
        topics: ["Semantic HTML"],
      },
      {
        id: "f3",
        question: "What is the box model in CSS? Describe its components.",
        difficulty: "Easy",
        topics: ["CSS Box Model"],
      },
      {
        id: "f4",
        question:
          "What's the difference between inline, block, and inline-block elements?",
        difficulty: "Easy",
        topics: ["Display Properties"],
      },
      {
        id: "f5",
        question: "Explain the difference between margin and padding.",
        difficulty: "Easy",
        topics: ["CSS Box Model"],
      },
      {
        id: "f6",
        question:
          "What are CSS selectors? Explain different types (class, id, element, attribute).",
        difficulty: "Easy",
        topics: ["CSS Selectors"],
      },
      {
        id: "f7",
        question: "What is CSS specificity and how is it calculated?",
        difficulty: "Medium",
        topics: ["CSS Specificity"],
      },
      {
        id: "f8",
        question:
          "Explain the difference between absolute, relative, fixed, and sticky positioning.",
        difficulty: "Medium",
        topics: ["CSS Positioning"],
      },
      {
        id: "f9",
        question: "What is Flexbox and what problems does it solve?",
        difficulty: "Medium",
        topics: ["Flexbox"],
      },
      {
        id: "f10",
        question:
          "How does CSS Grid differ from Flexbox? When would you use each?",
        difficulty: "Medium",
        topics: ["CSS Grid", "Flexbox"],
      },
      {
        id: "f11",
        question:
          "What are media queries and how do you implement responsive design?",
        difficulty: "Medium",
        topics: ["Responsive Design"],
      },
      {
        id: "f12",
        question:
          "Explain the difference between em, rem, px, and % units in CSS.",
        difficulty: "Easy",
        topics: ["CSS Units"],
      },
      {
        id: "f13",
        question:
          "What is the difference between display: none and visibility: hidden?",
        difficulty: "Easy",
        topics: ["CSS Display"],
      },
      {
        id: "f14",
        question: "How do you center a div horizontally and vertically?",
        difficulty: "Medium",
        topics: ["CSS Layout"],
      },
      {
        id: "f15",
        question: "What are pseudo-classes and pseudo-elements? Give examples.",
        difficulty: "Easy",
        topics: ["CSS Selectors"],
      },
      {
        id: "f16",
        question: "Explain z-index and stacking context in CSS.",
        difficulty: "Medium",
        topics: ["CSS Positioning"],
      },
      {
        id: "f17",
        question:
          "What is the difference between inline and external CSS? Which is better?",
        difficulty: "Easy",
        topics: ["CSS Basics"],
      },
      {
        id: "f18",
        question:
          "How does CSS inheritance work? Give examples of inherited and non-inherited properties.",
        difficulty: "Medium",
        topics: ["CSS Inheritance"],
      },
      {
        id: "f19",
        question:
          "What are CSS transitions and animations? How do they differ?",
        difficulty: "Medium",
        topics: ["CSS Animations"],
      },
      {
        id: "f20",
        question: "Explain the float property and how to clear floats.",
        difficulty: "Medium",
        topics: ["CSS Float"],
      },
      {
        id: "f21",
        question: "What is the purpose of the viewport meta tag?",
        difficulty: "Easy",
        topics: ["Responsive Design"],
      },
      {
        id: "f22",
        question:
          "What are CSS variables (custom properties) and how do you use them?",
        difficulty: "Easy",
        topics: ["CSS Variables"],
      },
      {
        id: "f23",
        question:
          "Explain the difference between nth-child and nth-of-type selectors.",
        difficulty: "Medium",
        topics: ["CSS Selectors"],
      },
      {
        id: "f24",
        question:
          "What is the difference between link, visited, hover, and active pseudo-classes?",
        difficulty: "Easy",
        topics: ["CSS Pseudo-classes"],
      },
      {
        id: "f25",
        question: "How do you implement a responsive image in HTML/CSS?",
        difficulty: "Easy",
        topics: ["Responsive Design", "Images"],
      },
      {
        id: "f26",
        question: "What is the purpose of the alt attribute in img tags?",
        difficulty: "Easy",
        topics: ["HTML", "Accessibility"],
      },
      {
        id: "f27",
        question:
          "Explain the difference between section, article, aside, and div elements.",
        difficulty: "Medium",
        topics: ["Semantic HTML"],
      },
      {
        id: "f28",
        question: "What are data attributes in HTML and how do you use them?",
        difficulty: "Easy",
        topics: ["HTML Attributes"],
      },
      {
        id: "f29",
        question:
          "How do you create a form in HTML? What are important form elements?",
        difficulty: "Easy",
        topics: ["HTML Forms"],
      },
      {
        id: "f30",
        question:
          "What is CSS Grid's grid-template-areas and how does it work?",
        difficulty: "Hard",
        topics: ["CSS Grid"],
      },
    ],
  },
  {
    phase: 2,
    phaseKey: "javascript",
    title: "JavaScript Fundamentals",
    duration: "8 Weeks",
    level: "Beginner to Intermediate",
    weeks: [
      {
        week: 7,
        weekGoal:
          "Understand JavaScript fundamentals and build logic-based interactions",
        weekOutcome:
          "Strong foundation in JavaScript syntax, logic, and data handling",
        miniProject: "Interactive Calculator",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "Introduction to JavaScript & Console",
            subTopics: [
              "What is JavaScript",
              "How JavaScript runs in browser",
              "Script tag usage",
              "Browser console",
              "console.log, console.error, console.table",
            ],
            explanation:
              "JavaScript adds logic and interactivity to web pages. The browser console helps debug and test JavaScript code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Console Debugging Drill",
                description:
                  "Practice using the browser console like a real developer.",
                requirements: [
                  "Log strings, numbers, arrays, and objects",
                  "Use console.log, console.error, and console.table",
                  "Modify values and re-log them",
                ],
                constraint:
                  "alert() is not allowed. Debug only using console methods.",
                outcome:
                  "You’ll build the habit of inspecting code instead of guessing.",
              },
              {
                id: "pt-2",
                title: "Script Loading Experiment",
                description:
                  "Understand how script placement affects execution.",
                steps: [
                  "Add script tag in <head>",
                  "Add script tag before </body>",
                  "Observe console output timing",
                ],
                outcome:
                  "You’ll understand why script placement matters in real projects.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "90% of beginner JavaScript confusion comes from not checking the console.",
              realWorldChecks: [
                "Open DevTools before running JS code",
                "Look for red error messages",
                "Verify execution order using console logs",
              ],
              commonFinding:
                "Beginners think JavaScript is broken when it’s just a console error.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is JavaScript?",
                "How does JavaScript run in the browser?",
                "What is the browser console used for?",
                "Difference between console.log and console.error?",
              ],
              interviewerPerspective:
                "Basic JavaScript questions test clarity of fundamentals, not coding speed.",
              tip: "Always explain JavaScript in terms of behavior and debugging, not just syntax.",
            },
            editorGuidance: {
              starterHint:
                "If nothing happens, the first place to look is the console.",
              aiMentorPrompts: [
                "Did my script actually load?",
                "Is there any error in the console?",
                "Can I inspect this value step by step?",
                "Am I logging useful information?",
              ],
              validationChecklist: [
                "Script file linked correctly",
                "Console shows expected output",
                "Errors are visible and readable",
                "No alert() used for debugging",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "HTML gives structure, CSS gives style — JavaScript gives life. JavaScript controls behavior, logic, and interactivity on the web. This day focuses on understanding where JavaScript runs and how developers communicate with the browser using the console.",

              sections: [
                {
                  title: "What is JavaScript?",
                  description:
                    "JavaScript is a programming language that runs in the browser.",
                  keyPoints: [
                    "Adds interactivity to websites",
                    "Runs on the client-side",
                    "Works with HTML & CSS",
                  ],
                  examples: [
                    "Button clicks",
                    "Form validation",
                    "Dynamic content updates",
                  ],
                  analogy: "HTML = body, CSS = clothes, JavaScript = brain",
                },
                {
                  title: "How JavaScript Runs in the Browser",
                  description: "Browsers include a JavaScript engine.",
                  keyPoints: [
                    "Chrome uses V8 engine",
                    "JavaScript executes line by line",
                    "Runs in a single-threaded environment",
                  ],
                  process: [
                    "Browser loads HTML",
                    "Script tag is encountered",
                    "JavaScript engine executes code",
                  ],
                  takeaway:
                    "JavaScript executes as the page loads (unless deferred)",
                },
                {
                  title: "Script Tag Usage",
                  description: "JavaScript is added using the <script> tag.",
                  types: [
                    "Inline script",
                    "Internal script",
                    "External script",
                  ],
                  bestPractice: "Use external JavaScript files",
                  code: "<script src='script.js'></script>",
                  warning: "Script placement affects execution timing",
                },
                {
                  title: "Browser Console",
                  description: "The console is a developer tool for debugging.",
                  keyPoints: [
                    "View logs and errors",
                    "Test code live",
                    "Inspect variables and objects",
                  ],
                  access: "Right-click → Inspect → Console",
                  analogy: "Console is the doctor’s report for your code",
                },
                {
                  title: "console.log()",
                  description: "Prints output to the console.",
                  keyPoints: [
                    "Most used debugging tool",
                    "Can print strings, variables, objects",
                  ],
                  code: "console.log('Hello JavaScript');",
                  useCase: "Check if code runs correctly",
                },
                {
                  title: "console.error()",
                  description: "Displays error messages clearly.",
                  keyPoints: ["Shown in red", "Helps identify failures"],
                  code: "console.error('Something went wrong');",
                  useCase: "Highlight critical issues",
                },
                {
                  title: "console.table()",
                  description: "Displays arrays or objects in table format.",
                  keyPoints: [
                    "Great for structured data",
                    "Improves readability",
                  ],
                  code: "console.table([1, 2, 3]);",
                  analogy: "Like viewing data in an Excel sheet",
                },
                {
                  title: "Why Console is Important",
                  description:
                    "Console is every developer’s first debugging tool.",
                  benefits: [
                    "Faster debugging",
                    "Understanding runtime behavior",
                    "Testing logic without UI",
                  ],
                  takeaway: "If you can’t debug, you can’t code",
                },
              ],

              visualStructure: `
JavaScript Flow:
HTML loads
   ↓
Script tag encountered
   ↓
JavaScript engine executes
   ↓
Console displays output
    `,

              commonMistakes: [
                {
                  mistake: "Expecting JavaScript output on page",
                  consequence: "Nothing visible happens",
                  fix: "Check the browser console",
                },
                {
                  mistake: "Forgetting to open console",
                  consequence: "Missed errors",
                  fix: "Keep console open while coding",
                },
                {
                  mistake: "Using alert() everywhere",
                  consequence: "Poor debugging experience",
                  fix: "Use console methods instead",
                },
              ],

              proTips: [
                "console.log is your best friend",
                "Use console.table for arrays & objects",
                "Always check console for errors",
                "Debugging is a core developer skill",
              ],

              realWorldAnalogy:
                "The console is like a car dashboard:\n- Shows warnings\n- Displays system status\n- Helps diagnose problems",
            },

            tasks: [
              "Add JavaScript to an HTML file",
              "Print messages using console.log",
              "Inspect variables in console",
            ],

            examples: [
              "console.log('Hello JavaScript');",
              "console.table([1, 2, 3]);",
            ],

            useCases: [
              "Debugging frontend issues",
              "Testing logic quickly",
              "Learning JavaScript behavior",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand what JavaScript is",
              "Know how JavaScript runs in the browser",
              "Use the browser console effectively",
              "Debug basic JavaScript code",
            ],
          },
          {
            day: 2,
            topic: "Variables in JavaScript",
            subTopics: [
              "var vs let vs const",
              "Reassignment rules",
              "Block scope",
              "Best practices for variables",
              "Variable naming conventions",
            ],
            explanation:
              "Variables store data. Modern JavaScript prefers let and const for predictable behavior.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Var to Let/Const Refactor Drill",
                description:
                  "Refactor an old-style JavaScript file that uses var into modern, safe JavaScript.",
                requirements: [
                  "Replace all var declarations",
                  "Use const wherever reassignment is not required",
                  "Use let only when value must change",
                  "Ensure no redeclaration errors",
                ],
                constraint:
                  "Using var anywhere in the final code is not allowed.",
                outcome:
                  "You’ll learn how modern JavaScript prevents bugs before they happen.",
              },
              {
                id: "pt-2",
                title: "Scope Boundary Experiment",
                description:
                  "Visually understand block scope using let and const.",
                steps: [
                  "Declare variables inside if and for blocks",
                  "Try accessing them outside the block",
                  "Compare behavior with var",
                ],
                outcome: "You’ll never be confused about scope again.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most JavaScript bugs in large codebases come from poor variable scoping.",
              realWorldChecks: [
                "Check console for redeclaration errors",
                "Verify variables are not leaking outside blocks",
                "Ensure constants are not reassigned accidentally",
              ],
              commonFinding:
                "Beginners use let everywhere instead of defaulting to const.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between var, let, and const?",
                "What is block scope?",
                "Why is const preferred in modern JavaScript?",
                "Can you modify objects declared with const?",
              ],
              interviewerPerspective:
                "Variable questions reveal how safely a developer writes JavaScript.",
              tip: "Always explain variables from a bug-prevention and predictability angle.",
            },
            editorGuidance: {
              starterHint:
                "If you’re unsure, use const — JavaScript will protect you.",
              aiMentorPrompts: [
                "Does this variable really need reassignment?",
                "Is this variable scoped as tightly as possible?",
                "Is the variable name self-explanatory?",
                "Would this cause issues in a large file?",
              ],
              validationChecklist: [
                "No var used",
                "const used by default",
                "let used only when necessary",
                "Variables scoped inside blocks properly",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Variables are containers for data. In JavaScript, how you declare variables matters a lot. Choosing the wrong keyword can lead to bugs, unexpected behavior, and hard-to-debug issues. This lesson focuses on writing safe, modern JavaScript.",

              sections: [
                {
                  title: "What is a Variable?",
                  description:
                    "A variable stores data that can be used and changed later.",
                  keyPoints: [
                    "Stores values like numbers, text, objects",
                    "Can be reused multiple times",
                    "Acts as a label for data",
                  ],
                  code: "let age = 25;\nconsole.log(age);",
                  analogy: "Like a labeled box that holds a value",
                },
                {
                  title: "var – The Old Way",
                  description: "var was used before ES6 but has many issues.",
                  problems: [
                    "Function-scoped, not block-scoped",
                    "Allows redeclaration",
                    "Causes unexpected bugs",
                  ],
                  code: "var x = 10;\nvar x = 20; // No error",
                  takeaway: "Avoid var in modern JavaScript",
                },
                {
                  title: "let – The Modern Choice",
                  description:
                    "let allows reassignment and respects block scope.",
                  keyPoints: [
                    "Block-scoped",
                    "Can be reassigned",
                    "Cannot be redeclared in same scope",
                  ],
                  code: "let count = 1;\ncount = 2; // Allowed",
                  analogy: "A box whose value can change",
                },
                {
                  title: "const – Default Choice",
                  description:
                    "const prevents reassignment and increases safety.",
                  keyPoints: [
                    "Block-scoped",
                    "Cannot be reassigned",
                    "Must be initialized",
                  ],
                  code: "const PI = 3.14;\n// PI = 3.15 ❌ Error",
                  note: "const does NOT mean immutable objects",
                },
                {
                  title: "Reassignment Rules",
                  description: "Understanding reassignment avoids confusion.",
                  comparison: [
                    "var → reassignment allowed",
                    "let → reassignment allowed",
                    "const → reassignment NOT allowed",
                  ],
                  code: "const user = { name: 'Alex' };\nuser.name = 'John'; // Allowed",
                  takeaway: "const protects reference, not internal data",
                },
                {
                  title: "Block Scope Explained",
                  description:
                    "Block scope means variable exists only inside {}.",
                  keyPoints: [
                    "let and const are block-scoped",
                    "var ignores block scope",
                    "Blocks include if, for, while",
                  ],
                  code: "if (true) {\n  let x = 10;\n}\n// x is not accessible here",
                  analogy: "Like a room — variables exist only inside it",
                },
                {
                  title: "Variable Naming Conventions",
                  description: "Good naming improves readability.",
                  rules: [
                    "Use camelCase",
                    "Be descriptive",
                    "Avoid single letters (except loops)",
                  ],
                  examples: ["userName ✅", "totalPrice ✅", "x ❌"],
                },
                {
                  title: "Best Practices for Variables",
                  description: "Professional developers follow these rules.",
                  bestPractices: [
                    "Use const by default",
                    "Use let only when reassignment is needed",
                    "Never use var",
                    "Keep variables scoped as small as possible",
                  ],
                  principle: "Safer code = fewer bugs",
                },
              ],

              visualStructure: `
Variable Decision:
Need reassignment? → let
No reassignment?   → const
Never use → var
    `,

              commonMistakes: [
                {
                  mistake: "Using var everywhere",
                  consequence: "Hard-to-debug scope issues",
                  fix: "Replace var with let/const",
                },
                {
                  mistake: "Using let instead of const unnecessarily",
                  consequence: "Less predictable code",
                  fix: "Default to const",
                },
                {
                  mistake: "Poor variable names",
                  consequence: "Unreadable code",
                  fix: "Use descriptive names",
                },
              ],

              proTips: [
                "const should be your default choice",
                "Block scope prevents bugs",
                "Readable variable names save time",
                "Modern JavaScript avoids var completely",
              ],

              realWorldAnalogy:
                "Variables are like labeled storage boxes:\n- const = sealed box\n- let = box you can reopen\n- var = broken box",
            },

            tasks: [
              "Declare variables using let and const",
              "Test reassignment behavior",
              "Refactor var to let/const",
            ],

            examples: ["let age = 25;", "const PI = 3.14;"],

            useCases: [
              "Storing user input",
              "Application state handling",
              "Writing predictable logic",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand variable declaration types",
              "Use let and const correctly",
              "Avoid scope-related bugs",
              "Write clean, modern JavaScript",
            ],
          },
          {
            day: 3,
            topic: "Data Types & typeof",
            subTopics: [
              "String",
              "Number",
              "Boolean",
              "null vs undefined",
              "typeof operator",
            ],
            explanation:
              "JavaScript supports multiple data types. Understanding them avoids logic bugs.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Type Confusion Debugging Drill",
                description:
                  "Identify and fix bugs caused by incorrect assumptions about data types.",
                scenarios: [
                  "'10' + 5",
                  "'10' - 5",
                  "Boolean('false')",
                  "typeof null",
                ],
                requirements: [
                  "Predict output before running code",
                  "Explain why JavaScript behaves this way",
                  "Fix logic using proper type handling",
                ],
                outcome:
                  "You’ll stop trusting assumptions and start verifying types.",
              },
              {
                id: "pt-2",
                title: "Null vs Undefined Decision Exercise",
                description:
                  "Practice choosing between null and undefined in real situations.",
                tasks: [
                  "User not logged in",
                  "API response missing data",
                  "Form field cleared by user",
                ],
                goal: "Decide whether null or undefined is more appropriate and explain why.",
                outcome: "Clear mental model of intent vs default values.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Type-related bugs are among the hardest to detect because JavaScript doesn’t throw errors.",
              realWorldChecks: [
                "Log typeof values before condition checks",
                "Watch for string-number operations",
                "Validate API response types",
              ],
              commonFinding:
                "Most beginners assume '5' and 5 behave the same — they don’t.",
            },
            interviewInsights: {
              commonQuestions: [
                "What are primitive data types in JavaScript?",
                "Difference between null and undefined?",
                "Why does typeof null return 'object'?",
                "Is JavaScript strongly typed?",
              ],
              interviewerPerspective:
                "Type understanding shows how deeply you understand JavaScript internals.",
              tip: "Always explain answers with examples — interviewers love that.",
            },
            editorGuidance: {
              starterHint: "Never guess a variable’s type — verify it.",
              aiMentorPrompts: [
                "What type is this value right now?",
                "Is this value coming from user input or system?",
                "Should this be null or undefined?",
                "What happens if this type changes?",
              ],
              validationChecklist: [
                "typeof used during debugging",
                "null used intentionally",
                "undefined not assigned manually unless needed",
                "No assumptions about implicit conversion",
              ],
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "JavaScript is a dynamically typed language, meaning variables can hold any type of value at runtime. This flexibility is powerful, but it also causes bugs if you don’t clearly understand data types and how JavaScript treats them.",

              sections: [
                {
                  title: "What Are Data Types?",
                  description:
                    "A data type defines the kind of value a variable holds.",
                  keyPoints: [
                    "Determines how data behaves",
                    "Affects operations and conditions",
                    "Checked at runtime in JavaScript",
                  ],
                  analogy:
                    "Like knowing whether a container holds water, sand, or air",
                },
                {
                  title: "String",
                  description: "Strings represent text data.",
                  keyPoints: [
                    "Written inside quotes",
                    "Can be single, double, or template literals",
                    "Used for names, messages, labels",
                  ],
                  code: "let name = 'Gaurav';\nlet message = \"Hello World\";",
                  useCase: "Displaying text on UI",
                },
                {
                  title: "Number",
                  description:
                    "Numbers represent numeric values (integer & decimal).",
                  keyPoints: [
                    "JavaScript has only one number type",
                    "Includes integers and floats",
                    "Used for math and calculations",
                  ],
                  code: "let age = 25;\nlet price = 99.99;",
                  note: "NaN is also a number type",
                },
                {
                  title: "Boolean",
                  description: "Booleans represent true or false values.",
                  keyPoints: [
                    "Used in conditions",
                    "Result of comparisons",
                    "Critical for control flow",
                  ],
                  code: "let isLoggedIn = true;",
                  analogy: "Like a switch: ON or OFF",
                },
                {
                  title: "undefined",
                  description:
                    "undefined means a variable is declared but not assigned.",
                  keyPoints: [
                    "Default value of uninitialized variables",
                    "Set automatically by JavaScript",
                  ],
                  code: "let x;\nconsole.log(x); // undefined",
                  takeaway: "undefined = value not assigned yet",
                },
                {
                  title: "null",
                  description: "null means intentional absence of value.",
                  keyPoints: [
                    "Explicitly assigned by developer",
                    "Represents empty or cleared value",
                  ],
                  code: "let user = null;",
                  analogy:
                    "undefined = box not opened\nnull = box intentionally emptied",
                },
                {
                  title: "null vs undefined (Important!)",
                  description: "They look similar but mean different things.",
                  comparison: [
                    "undefined → system default",
                    "null → developer intent",
                  ],
                  interviewNote: "typeof null is 'object' (JavaScript bug)",
                },
                {
                  title: "typeof Operator",
                  description: "typeof checks the data type of a value.",
                  keyPoints: [
                    "Returns a string",
                    "Used for debugging & validation",
                  ],
                  code: "typeof 'hello'; // 'string'\ntypeof 42; // 'number'\ntypeof true; // 'boolean'\ntypeof undefined; // 'undefined'\ntypeof null; // 'object'",
                  warning: "Be careful with typeof null",
                },
                {
                  title: "Why Data Types Matter",
                  description: "Type mistakes cause unexpected behavior.",
                  examples: ["'5' + 5 → '55'", "'5' - 1 → 4"],
                  takeaway: "Knowing types prevents silent bugs",
                },
              ],

              visualStructure: `
Primitive Types:
String → Text
Number → Numeric values
Boolean → true / false
undefined → Not assigned
null → Intentionally empty
    `,

              commonMistakes: [
                {
                  mistake: "Confusing null and undefined",
                  consequence: "Incorrect condition checks",
                  fix: "Understand intent vs default",
                },
                {
                  mistake: "Ignoring typeof checks",
                  consequence: "Unexpected runtime bugs",
                  fix: "Use typeof while debugging",
                },
                {
                  mistake: "Assuming JS is strongly typed",
                  consequence: "Wrong logic assumptions",
                  fix: "Remember JS is dynamically typed",
                },
              ],

              proTips: [
                "Always know what type your variable holds",
                "typeof is a debugging superpower",
                "null means intentional absence",
                "Most JS bugs are type-related",
              ],

              realWorldAnalogy:
                "Data types are like ingredients:\n- Sugar vs salt look similar\n- But behave very differently\n- Using wrong one ruins the dish",
            },

            tasks: [
              "Create variables of different data types",
              "Check types using typeof",
              "Identify null vs undefined",
            ],

            examples: ["typeof 'hello';", "typeof 42;", "let x; // undefined"],

            useCases: [
              "Data validation",
              "Conditional logic",
              "Debugging runtime issues",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand core JavaScript data types",
              "Differentiate null and undefined",
              "Use typeof operator effectively",
              "Avoid common type-related bugs",
            ],
          },
          {
            day: 4,
            topic: "Operators & Expressions",
            subTopics: [
              "Arithmetic operators",
              "Comparison operators",
              "Logical operators",
              "Assignment operators",
              "Operator precedence",
            ],
            explanation:
              "Operators help perform calculations, comparisons, and logical decisions in programs.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Loose vs Strict Equality Trap",
                description:
                  "Predict output and explain behavior of comparison operators.",
                scenarios: [
                  "5 == '5'",
                  "5 === '5'",
                  "null == undefined",
                  "null === undefined",
                  "0 == false",
                  "0 === false",
                ],
                requirements: [
                  "Predict result before running",
                  "Explain why JavaScript behaves this way",
                  "Rewrite logic using safe comparisons",
                ],
                outcome: "You’ll stop writing comparison bugs unknowingly.",
              },
              {
                id: "pt-2",
                title: "Readable Condition Refactor",
                description:
                  "Convert complex logical expressions into clean, readable code.",
                before:
                  "if (user && user.isActive && user.role === 'admin' && !user.isBlocked)",
                afterGoal: "Use meaningful variables for each condition",
                outcome: "Professional-grade readable logic.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most production bugs are logic bugs, not syntax errors.",
              realWorldChecks: [
                "Log intermediate expressions",
                "Test edge cases (0, null, undefined)",
                "Split conditions into variables",
              ],
              example:
                "const isEligible = age >= 18;\nconst hasAccess = isLoggedIn && !isBlocked;",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between == and ===?",
                "What is operator precedence?",
                "How does && and || short-circuit?",
                "Why does '5' + 5 behave differently from '5' - 1?",
              ],
              interviewerTip:
                "Explain answers with examples — not definitions.",
              redFlag: "Using == without justification raises concern.",
            },
            editorGuidance: {
              mentalChecklist: [
                "What type is each operand?",
                "Is this expression readable?",
                "Can this condition fail silently?",
                "Would future-me understand this logic?",
              ],
              refactorRule: "If a condition needs explanation → refactor it.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Operators are the building blocks of logic in JavaScript. Every condition, validation, calculation, and decision in an app depends on operators. Understanding them clearly prevents silent logic bugs.",

              sections: [
                {
                  title: "What Is an Expression?",
                  description:
                    "An expression is any piece of code that produces a value.",
                  examples: ["10 + 5", "age >= 18", "isLoggedIn && isAdmin"],
                  takeaway: "Expressions always return a value",
                  analogy: "Like a math question that gives an answer",
                },
                {
                  title: "Arithmetic Operators",
                  description: "Used for mathematical calculations.",
                  operators: [
                    "+ (addition)",
                    "- (subtraction)",
                    "* (multiplication)",
                    "/ (division)",
                    "% (modulus)",
                  ],
                  code: "let sum = 10 + 5;\nlet remainder = 10 % 3;",
                  useCases: [
                    "Price calculation",
                    "Counters",
                    "Scores & totals",
                  ],
                },
                {
                  title: "Comparison Operators",
                  description:
                    "Used to compare values and return boolean results.",
                  operators: [
                    "== (loose equality)",
                    "=== (strict equality)",
                    "!= , !==",
                    ">, <, >=, <=",
                  ],
                  code: "age >= 18; // true or false",
                  bestPractice: "Always prefer === over ==",
                  interviewNote: "=== checks value AND type",
                },
                {
                  title: "Logical Operators",
                  description: "Used to combine multiple conditions.",
                  operators: ["&& (AND)", "|| (OR)", "! (NOT)"],
                  code: "isLoggedIn && isAdmin;\n!isBlocked;",
                  truthTable: "true && false → false\ntrue || false → true",
                  analogy: "Like decision-making rules",
                },
                {
                  title: "Assignment Operators",
                  description: "Used to assign and update values.",
                  operators: ["= (assignment)", "+= , -= , *= , /="],
                  code: "let count = 0;\ncount += 1;",
                  useCase: "Updating counters and state",
                },
                {
                  title: "Operator Precedence",
                  description: "Defines which operations run first.",
                  rules: [
                    "Brackets () have highest priority",
                    "Multiplication before addition",
                    "Comparison after arithmetic",
                  ],
                  code: "10 + 5 * 2; // 20\n(10 + 5) * 2; // 30",
                  takeaway: "Use parentheses for clarity",
                },
                {
                  title: "Real-World Conditions",
                  description: "Operators drive real app logic.",
                  examples: ["Form validation", "Auth checks", "Feature flags"],
                  code: "if (age >= 18 && hasID) {\n  // allow access\n}",
                },
              ],

              visualStructure: `
Logic Flow:
Values → Operators → Expression → Boolean / Result
    `,

              commonMistakes: [
                {
                  mistake: "Using == instead of ===",
                  consequence: "Unexpected true results",
                  fix: "Always use strict equality",
                },
                {
                  mistake: "Forgetting operator precedence",
                  consequence: "Wrong calculations",
                  fix: "Use parentheses",
                },
                {
                  mistake: "Complex conditions in one line",
                  consequence: "Hard-to-read logic",
                  fix: "Break into variables",
                },
              ],

              proTips: [
                "=== is your safest comparison",
                "Keep conditions readable",
                "Use parentheses liberally",
                "Readable logic beats clever logic",
              ],

              realWorldAnalogy:
                "Operators are like traffic rules:\n- They decide flow\n- Order matters\n- Small mistakes cause big problems",
            },

            tasks: [
              "Build simple math expressions",
              "Compare values using operators",
              "Create logical conditions",
            ],

            examples: ["10 + 5;", "age >= 18;", "isLoggedIn && isAdmin;"],

            useCases: [
              "Form validations",
              "Decision making in apps",
              "Conditional rendering logic",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use arithmetic operators correctly",
              "Understand comparison and logical operators",
              "Apply operator precedence safely",
              "Write clear logical expressions",
            ],
          },
          {
            day: 5,
            topic: "Type Conversion & Coercion",
            subTopics: [
              "Implicit coercion",
              "Explicit conversion",
              "Number(), String(), Boolean()",
              "Truthy and falsy values",
              "Common conversion bugs",
            ],
            explanation:
              "JavaScript automatically converts types sometimes, which can cause bugs if not understood.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Implicit Coercion Prediction Trap",
                description:
                  "Predict output and explain JavaScript’s implicit type conversion behavior.",
                scenarios: [
                  "'5' + 1",
                  "'5' - 1",
                  "true + false",
                  "false + 1",
                  "null + 1",
                  "undefined + 1",
                ],
                requirements: [
                  "Predict result before running",
                  "Explain which coercion rule applied",
                  "Rewrite logic using explicit conversion",
                ],
                outcome:
                  "You’ll stop trusting implicit coercion in real-world code.",
              },
              {
                id: "pt-2",
                title: "Falsy Value Logic Bug",
                description:
                  "Fix conditions that break due to valid falsy values.",
                before: "if (value) {\n  applyDiscount();\n}",
                afterGoal: "Handle 0, '' and false without breaking logic",
                outcome: "Production-safe conditional checks.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Type coercion bugs silently pass development but fail with real user data.",
              realWorldChecks: [
                "Always test with 0, '', null, undefined",
                "Log value along with typeof",
                "Convert user input explicitly before logic",
              ],
              example:
                "const amount = Number(input);\nif (!Number.isNaN(amount)) {\n  process(amount);\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "What is type coercion?",
                "Difference between implicit and explicit conversion?",
                "What are falsy values in JavaScript?",
                "Why does '5' + 1 differ from '5' - 1?",
                "Why is Boolean('false') true?",
              ],
              interviewerTip: "Always answer with examples and actual outputs.",
              redFlag:
                "Relying on implicit coercion or not knowing falsy values.",
            },

            editorGuidance: {
              mentalChecklist: [
                "What is the current type of this value?",
                "Is JavaScript converting this implicitly?",
                "Can 0 or empty string break this logic?",
                "Should I convert this explicitly?",
              ],
              refactorRule:
                "If a condition depends on truthy/falsy → make conversion explicit.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "JavaScript tries to be helpful by automatically converting data types when needed. This behavior is called type coercion. While convenient, it can silently introduce bugs if you don’t understand when and how conversions happen.",

              sections: [
                {
                  title: "Type Conversion vs Type Coercion",
                  description: "These two are related but different.",
                  conversion: [
                    "Explicit",
                    "Done manually by developer",
                    "Predictable",
                  ],
                  coercion: [
                    "Implicit",
                    "Done automatically by JavaScript",
                    "Can be confusing",
                  ],
                  takeaway:
                    "Explicit conversion is safer than implicit coercion",
                },
                {
                  title: "Implicit Type Coercion",
                  description:
                    "JavaScript converts types automatically during operations.",
                  keyPoints: [
                    "+ operator prefers strings",
                    "-, *, / prefer numbers",
                    "Comparisons can trigger coercion",
                  ],
                  examples: ["'5' + 2 → '52'", "'5' - 2 → 3", "true + 1 → 2"],
                  code: "'5' + 2; // '52'\n'5' - 2; // 3",
                  analogy:
                    "JavaScript guesses what you mean — sometimes wrongly",
                },
                {
                  title: "Explicit Type Conversion",
                  description: "You manually convert data to required type.",
                  methods: ["Number()", "String()", "Boolean()"],
                  code: "Number('10'); // 10\nString(25); // '25'\nBoolean(1); // true",
                  takeaway: "Explicit conversion = predictable behavior",
                },
                {
                  title: "Number() Conversion",
                  description: "Converts values to numbers.",
                  examples: [
                    "Number('10') → 10",
                    "Number('10px') → NaN",
                    "Number(true) → 1",
                    "Number(false) → 0",
                  ],
                  warning: "Invalid numbers result in NaN",
                },
                {
                  title: "String() Conversion",
                  description: "Converts values to strings.",
                  examples: [
                    "String(10) → '10'",
                    "String(true) → 'true'",
                    "String(null) → 'null'",
                  ],
                  useCase: "Displaying values in UI",
                },
                {
                  title: "Boolean() Conversion",
                  description: "Converts values to true or false.",
                  truthy: "Values that become true",
                  falsy: "Values that become false",
                  falsyValues: [
                    "false",
                    "0",
                    "'' (empty string)",
                    "null",
                    "undefined",
                    "NaN",
                  ],
                  code: "Boolean(0); // false\nBoolean('hello'); // true",
                  analogy: "Falsy values behave like OFF switches",
                },
                {
                  title: "Truthy & Falsy in Conditions",
                  description: "Conditions rely on truthy/falsy evaluation.",
                  code: "if ('') {\n  // will not run\n}\nif ('text') {\n  // will run\n}",
                  takeaway: "Know falsy values by heart",
                },
                {
                  title: "Common Conversion Bugs",
                  description: "These bugs appear often in real apps.",
                  bugs: [
                    "'5' + 1 → '51'",
                    "if (value) failing for 0",
                    "Comparing strings to numbers",
                  ],
                  fix: "Convert types explicitly before logic",
                },
                {
                  title: "Best Practices",
                  description: "Professional JS developers follow these rules.",
                  bestPractices: [
                    "Avoid implicit coercion",
                    "Use Number(), String(), Boolean() explicitly",
                    "Use === instead of ==",
                    "Validate user input",
                  ],
                },
              ],

              visualStructure: `
Type Handling Strategy:
User Input (string)
   ↓
Explicit Conversion
   ↓
Safe Logic
    `,

              commonMistakes: [
                {
                  mistake: "Relying on implicit coercion",
                  consequence: "Unexpected behavior",
                  fix: "Convert explicitly",
                },
                {
                  mistake: "Forgetting falsy values",
                  consequence: "Broken conditions",
                  fix: "Handle edge cases",
                },
                {
                  mistake: "Using == comparison",
                  consequence: "Hidden coercion bugs",
                  fix: "Always use ===",
                },
              ],

              proTips: [
                "Treat user input as strings by default",
                "Explicit conversion saves hours of debugging",
                "Falsy values should be memorized",
                "Predictability beats convenience",
              ],

              realWorldAnalogy:
                "Type coercion is like auto-correct:\n- Sometimes helpful\n- Sometimes disastrous\n- Manual control is safer",
            },

            tasks: [
              "Convert strings to numbers",
              "Test truthy/falsy values",
              "Fix coercion issues",
            ],

            examples: ["Number('10');", "Boolean(0);", "'5' + 2"],

            useCases: [
              "User input handling",
              "Form submissions",
              "Conditional logic",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand type coercion",
              "Convert data types explicitly",
              "Identify truthy and falsy values",
              "Avoid common conversion bugs",
            ],
          },
          {
            day: 6,
            topic: "Strings, Template Literals & Math",
            subTopics: [
              "String methods",
              "Template literals",
              "Math object",
              "Rounding numbers",
              "Random numbers",
            ],
            explanation:
              "Strings and math operations are common in real applications like pricing, messages, and calculations.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "User Message Formatter",
                description:
                  "Build clean, readable user-facing messages using template literals.",
                scenarios: [
                  "Welcome message with user name",
                  "Order confirmation message",
                  "Discount applied message",
                ],
                requirements: [
                  "Avoid string concatenation (+)",
                  "Use template literals only",
                  "Handle missing values safely",
                ],
                outcome:
                  "You’ll write readable, production-quality dynamic strings.",
              },
              {
                id: "pt-2",
                title: "Price Calculation & Rounding Trap",
                description: "Fix UI bugs caused by floating-point math.",
                before: "let total = price * qty;\nshow(total);",
                afterGoal: "Display clean, rounded values suitable for UI",
                outcome: "No more ugly decimals in pricing UI.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most UI bugs come from badly formatted strings or unrounded numbers.",
              realWorldChecks: [
                "Test strings with empty or undefined values",
                "Check decimals like 0.1 + 0.2",
                "Validate random number ranges",
              ],
              example:
                "const total = Math.round(price * qty);\nconst message = `Total: ₹${total}`;",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between string concatenation and template literals?",
                "Why is 0.1 + 0.2 not exactly 0.3?",
                "How does Math.random() work?",
                "Difference between Math.floor, ceil and round?",
              ],
              interviewerTip:
                "Explain answers with real UI or pricing examples.",
              redFlag:
                "Using + for complex strings or displaying raw decimals.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Is this string readable at first glance?",
                "Should this number be rounded before display?",
                "Can this value be undefined or empty?",
                "Is Math.random() scaled correctly?",
              ],
              refactorRule:
                "If a string uses + more than once → refactor to template literal.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Almost every application deals with text and numbers — user names, prices, messages, counters, discounts, IDs. This lesson focuses on manipulating strings cleanly and performing mathematical operations safely using JavaScript’s built-in tools.",

              sections: [
                {
                  title: "Working with Strings",
                  description:
                    "Strings represent text and come with powerful built-in methods.",
                  commonMethods: [
                    "length",
                    "toUpperCase()",
                    "toLowerCase()",
                    "trim()",
                    "includes()",
                    "slice()",
                    "replace()",
                  ],
                  code: "let name = '  Gaurav  ';\nname.trim().toUpperCase(); // 'GAURAV'",
                  useCase: "Cleaning and formatting user input",
                  analogy: "String methods are like editing tools for text",
                },
                {
                  title: "Template Literals",
                  description:
                    "Template literals make string creation cleaner and readable.",
                  keyPoints: [
                    "Uses backticks (` `)",
                    "Supports variable interpolation",
                    "Supports multi-line strings",
                  ],
                  code: "let name = 'Alex';\nlet message = `Hello ${name}, welcome back!`;",
                  comparison: "'Hello ' + name + '!' ❌\n`Hello ${name}!` ✅",
                  takeaway: "Template literals are modern and preferred",
                },
                {
                  title: "Why Template Literals Matter",
                  description: "They reduce bugs and improve readability.",
                  benefits: [
                    "No messy string concatenation",
                    "Easier to read conditions",
                    "Cleaner dynamic messages",
                  ],
                  realExample: "`Total price: ₹${price * quantity}`",
                },
                {
                  title: "Math Object Overview",
                  description:
                    "JavaScript provides the Math object for calculations.",
                  commonMethods: [
                    "Math.round()",
                    "Math.floor()",
                    "Math.ceil()",
                    "Math.random()",
                    "Math.max()",
                    "Math.min()",
                  ],
                  note: "Math is not a constructor — no new Math()",
                },
                {
                  title: "Rounding Numbers",
                  description: "Used heavily in pricing and UI display.",
                  methods: [
                    "Math.round() → nearest integer",
                    "Math.floor() → rounds down",
                    "Math.ceil() → rounds up",
                  ],
                  code: "Math.round(4.7); // 5\nMath.floor(4.7); // 4\nMath.ceil(4.2); // 5",
                  useCase: "Prices, ratings, pagination",
                },
                {
                  title: "Generating Random Numbers",
                  description:
                    "Random numbers are used in games, IDs, OTPs, and UI effects.",
                  keyPoints: [
                    "Math.random() returns value between 0 and 1",
                    "Never returns 1",
                  ],
                  code: "Math.random(); // 0 to <1\n\n// Random number between 1 and 10\nMath.floor(Math.random() * 10) + 1;",
                  warning: "Math.random() is not cryptographically secure",
                },
                {
                  title: "Real-World Math Examples",
                  description: "Math logic appears everywhere.",
                  examples: [
                    "Discount calculation",
                    "Cart totals",
                    "Random featured item",
                  ],
                  code: "let total = price * qty;\nlet final = Math.round(total);",
                },
                {
                  title: "Best Practices",
                  description: "Clean code comes from small habits.",
                  bestPractices: [
                    "Use template literals over +",
                    "Round numbers before displaying",
                    "Sanitize strings from user input",
                    "Avoid floating-point surprises",
                  ],
                },
              ],

              visualStructure: `
Common Flow:
Raw Input → String Formatting → Math Logic → Display Output
    `,

              commonMistakes: [
                {
                  mistake: "Using + for string concatenation everywhere",
                  consequence: "Unreadable code",
                  fix: "Use template literals",
                },
                {
                  mistake: "Displaying raw decimal values",
                  consequence: "Ugly UI",
                  fix: "Use rounding methods",
                },
                {
                  mistake: "Assuming Math.random() gives whole numbers",
                  consequence: "Logic bugs",
                  fix: "Use Math.floor / ceil correctly",
                },
              ],

              proTips: [
                "Template literals are industry standard",
                "Always format numbers before UI display",
                "Math.random() needs scaling",
                "Readable strings improve UX",
              ],

              realWorldAnalogy:
                "Strings and Math are like language and numbers:\n- One communicates\n- One calculates\n- Apps need both perfectly balanced",
            },

            tasks: [
              "Create formatted strings",
              "Generate random numbers",
              "Perform calculations using Math",
            ],

            examples: [
              "`Hello ${name}`;",
              "Math.round(4.7);",
              "Math.random();",
            ],

            useCases: [
              "Price calculations",
              "Dynamic messages",
              "User-facing data formatting",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use string methods effectively",
              "Create clean dynamic strings",
              "Perform calculations using Math",
              "Handle numbers safely in UI",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Interactive Calculator",
            subTopics: [
              "Variables & operators",
              "Input handling",
              "Basic UI interaction",
              "JavaScript logic flow",
            ],
            explanation:
              "This project solidifies JavaScript fundamentals by applying them to a real UI-based calculator.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Buggy Calculator Fix",
                description:
                  "Identify and fix logical bugs in a partially working calculator.",
                scenarios: [
                  "Addition working but subtraction failing",
                  "Division by zero crash",
                  "String concatenation instead of addition",
                ],
                requirements: [
                  "Debug step-by-step using console.log",
                  "Fix using Number() conversion",
                  "Handle edge cases gracefully",
                ],
                outcome:
                  "You’ll learn how real-world debugging actually works.",
              },
              {
                id: "pt-2",
                title: "Readable Calculator Logic Refactor",
                description:
                  "Refactor messy calculator logic into clean, readable functions.",
                before:
                  "if(op === '+'){result = a + b}else if(op === '-'){result = a - b}...",
                afterGoal: "Use small functions and clear variable names",
                outcome: "Professional-quality project code structure.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "UI-based logic fails most often with invalid or unexpected user input.",
              realWorldChecks: [
                "Test with empty inputs",
                "Test with 0 and division",
                "Test with non-numeric values",
                "Test rapid button clicks",
              ],
              example:
                "if (Number.isNaN(num1) || Number.isNaN(num2)) {\n  result.textContent = 'Invalid input';\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "How does a calculator handle user input?",
                "Why do we need Number() for input values?",
                "How would you prevent division by zero?",
                "How would you structure calculator logic cleanly?",
              ],
              interviewerTip:
                "Explain your logic flow step-by-step, not just the final code.",
              redFlag:
                "Ignoring input validation or relying on implicit coercion.",
            },

            editorGuidance: {
              mentalChecklist: [
                "What type is this input value right now?",
                "Have I converted user input explicitly?",
                "What happens if input is empty?",
                "Can this logic break with edge cases?",
              ],
              refactorRule:
                "If logic feels confusing → split it into smaller steps.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Learning syntax is one thing, but building something usable is what truly makes concepts stick. This mini project combines variables, operators, type conversion, DOM interaction, and logic flow into a simple but powerful interactive calculator.",

              sections: [
                {
                  title: "Project Goal",
                  description:
                    "Build a working calculator that takes user input and performs basic arithmetic operations.",
                  operations: [
                    "Addition",
                    "Subtraction",
                    "Multiplication",
                    "Division",
                  ],
                  skillsUsed: [
                    "Variables & operators",
                    "Type conversion",
                    "Conditional logic",
                    "DOM interaction",
                  ],
                  outcome: "A calculator that updates results dynamically",
                },
                {
                  title: "UI Structure (HTML)",
                  description: "The UI should be simple and clean.",
                  elements: [
                    "Two input fields for numbers",
                    "Buttons for operations",
                    "Result display area",
                  ],
                  example: "<input /> + <input /> = <span></span>",
                  guideline: "Focus on functionality, not design",
                },
                {
                  title: "Handling User Input",
                  description: "All input values come as strings.",
                  keyPoints: [
                    "Use Number() to convert input values",
                    "Always validate input",
                    "Avoid implicit coercion",
                  ],
                  code: "const num1 = Number(input1.value);\nconst num2 = Number(input2.value);",
                  takeaway: "User input is always untrusted",
                },
                {
                  title: "Calculator Logic Flow",
                  description: "Operations follow a predictable flow.",
                  flow: [
                    "User clicks button",
                    "Read input values",
                    "Convert to numbers",
                    "Perform calculation",
                    "Display result",
                  ],
                  analogy: "Like a vending machine logic pipeline",
                },
                {
                  title: "Using Operators",
                  description: "Operators perform the core calculations.",
                  examples: [
                    "num1 + num2",
                    "num1 - num2",
                    "num1 * num2",
                    "num1 / num2",
                  ],
                  warning: "Handle division by zero",
                },
                {
                  title: "Displaying Results",
                  description: "Results should update instantly.",
                  keyPoints: [
                    "Update DOM dynamically",
                    "Handle invalid results",
                  ],
                  code: "result.textContent = total;",
                  UXTip: "Clear result when input changes",
                },
                {
                  title: "Error Handling & Edge Cases",
                  description: "Real apps must handle invalid input.",
                  cases: [
                    "Empty input",
                    "Non-numeric values",
                    "Division by zero",
                  ],
                  code: "if (isNaN(num1) || isNaN(num2)) {\n  result.textContent = 'Invalid input';\n}",
                },
                {
                  title: "Optional Enhancements",
                  description: "Extra polish for better learning.",
                  ideas: [
                    "Clear button",
                    "Keyboard input support",
                    "Highlight active operation",
                  ],
                  note: "Enhancements are optional but valuable",
                },
              ],

              visualStructure: `
Calculator Flow:
Input → Convert → Calculate → Display
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting Number() conversion",
                  consequence: "String concatenation instead of math",
                  fix: "Convert inputs explicitly",
                },
                {
                  mistake: "No validation",
                  consequence: "NaN results",
                  fix: "Check inputs before calculation",
                },
                {
                  mistake: "Overcomplicating logic",
                  consequence: "Hard-to-read code",
                  fix: "Keep logic simple and clear",
                },
              ],

              proTips: [
                "Small projects build big confidence",
                "Console.log your steps while debugging",
                "Readable logic > clever logic",
                "This project is interview-friendly",
              ],

              realWorldAnalogy:
                "A calculator is like a logic gym:\n- Simple movements\n- Strong fundamentals\n- Builds confidence fast",
            },

            tasks: [
              "Create calculator UI with HTML/CSS",
              "Perform add, subtract, multiply, divide",
              "Handle user input",
              "Display results dynamically",
            ],

            examples: ["Simple calculator logic"],

            useCases: [
              "Understanding JS logic",
              "Interview-ready mini project",
              "Confidence building for beginners",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "2–3 hours",

            // 🎯 Learning objectives
            objectives: [
              "Apply JavaScript fundamentals in a real project",
              "Handle user input safely",
              "Write clear calculation logic",
              "Build confidence with JavaScript",
            ],
          },
        ],
      },
      {
        week: 8,
        weekGoal:
          "Master decision making, loops, and reusable logic using functions",
        weekOutcome:
          "Ability to write structured logic and reusable JavaScript code",
        canLearn: ["students", "beginners"],
        miniProject: "Quiz Application",
        days: [
          {
            day: 1,
            topic: "Conditional Statements (if / else)",
            subTopics: [
              "if statement",
              "else if ladder",
              "else block",
              "Comparison operators recap",
              "Logical conditions",
            ],
            explanation:
              "Conditional statements allow JavaScript to make decisions based on conditions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Condition Prediction Challenge",
                description:
                  "Predict which block of code will execute before running it.",
                scenarios: [
                  "if (0) { } else { }",
                  "if ('') { } else { }",
                  "if (null) { } else { }",
                  "if (undefined) { } else { }",
                  "if ('hello') { } else { }",
                ],
                requirements: [
                  "Predict output mentally first",
                  "Explain truthy/falsy behavior",
                  "Rewrite condition for clarity",
                ],
                outcome:
                  "You’ll stop making silent condition bugs caused by truthy/falsy confusion.",
              },
              {
                id: "pt-2",
                title: "Nested if → Clean Logic Refactor",
                description:
                  "Refactor deeply nested if/else logic into clean, readable conditions.",
                before:
                  "if (user) { if (user.isActive) { if (!user.isBlocked) { ... } } }",
                afterGoal:
                  "Use meaningful boolean variables and flat structure",
                outcome: "Readable, professional-grade conditional logic.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most bugs in real applications come from wrong assumptions in conditions.",
              realWorldChecks: [
                "Test true and false paths",
                "Test empty and edge values",
                "Log condition results",
                "Test negative scenarios intentionally",
              ],
              example:
                "const isAdult = age >= 18;\nif (isAdult) {\n  allowAccess();\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between if and else if?",
                "What are truthy and falsy values?",
                "How does logical AND (&&) work?",
                "How do you avoid deeply nested if statements?",
              ],
              interviewerTip:
                "Explain how your condition handles both success and failure cases.",
              redFlag: "Ignoring false cases or relying on unclear conditions.",
            },

            editorGuidance: {
              mentalChecklist: [
                "What exactly makes this condition true?",
                "Have I handled the false case?",
                "Is this condition readable at a glance?",
                "Can this fail silently?",
              ],
              refactorRule: "If a condition needs a comment → rewrite it.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Programs become useful when they can make decisions. Conditional statements allow JavaScript to execute different code paths based on data, user input, or application state. Almost every real-world feature depends on if/else logic.",

              sections: [
                {
                  title: "What Is a Condition?",
                  description:
                    "A condition is an expression that evaluates to true or false.",
                  keyPoints: [
                    "Conditions use comparison operators",
                    "They return boolean values",
                    "They control program flow",
                  ],
                  examples: ["age >= 18", "isLoggedIn === true"],
                  analogy: "Like asking a yes/no question before acting",
                },
                {
                  title: "if Statement",
                  description:
                    "The if statement runs code only when the condition is true.",
                  syntax: "if (condition) { /* code */ }",
                  code: "if (age >= 18) {\n  console.log('Adult');\n}",
                  takeaway: "If condition is false, code inside does not run",
                },
                {
                  title: "else Block",
                  description: "else runs when the if condition is false.",
                  syntax: "if (condition) { } else { }",
                  code: "if (age >= 18) {\n  console.log('Adult');\n} else {\n  console.log('Minor');\n}",
                  analogy: "If not this, then that",
                },
                {
                  title: "else if Ladder",
                  description: "Used when multiple conditions must be checked.",
                  syntax: "if (...) { } else if (...) { } else { }",
                  code: "if (score >= 90) {\n  console.log('A');\n} else if (score >= 75) {\n  console.log('B');\n} else {\n  console.log('C');\n}",
                  keyRule: "Only the first true condition executes",
                },
                {
                  title: "Comparison Operators Recap",
                  description: "Conditions depend on comparison operators.",
                  operators: ["=== , !==", ">, <, >=, <="],
                  bestPractice: "Always use strict equality (===)",
                  code: "userRole === 'admin'",
                },
                {
                  title: "Logical Conditions",
                  description: "Logical operators combine multiple conditions.",
                  operators: ["&& (AND)", "|| (OR)", "! (NOT)"],
                  code: "if (isLoggedIn && isAdmin) {\n  // allow access\n}",
                  analogy: "Multiple rules deciding one outcome",
                },
                {
                  title: "Real-World Examples",
                  description: "if/else logic is everywhere.",
                  examples: [
                    "Login validation",
                    "Age restriction",
                    "Feature access control",
                  ],
                  code: "if (!isLoggedIn) {\n  console.log('Please login');\n}",
                },
              ],

              visualStructure: `
Decision Flow:
Condition → true → Execute block
         → false → Skip / else
    `,

              commonMistakes: [
                {
                  mistake: "Using = instead of ===",
                  consequence: "Unexpected behavior",
                  fix: "Always use ===",
                },
                {
                  mistake: "Too many nested ifs",
                  consequence: "Hard-to-read code",
                  fix: "Use else if or refactor logic",
                },
                {
                  mistake: "Forgetting else handling",
                  consequence: "Unhandled cases",
                  fix: "Always think about false case",
                },
              ],

              proTips: [
                "Keep conditions readable",
                "Extract complex logic into variables",
                "Think in true/false terms",
                "Clear logic beats clever logic",
              ],

              realWorldAnalogy:
                "if/else is like decision-making in life:\n- If it rains → take umbrella\n- Else → go normally",
            },

            tasks: [
              "Check age eligibility",
              "Validate login credentials",
              "Display messages based on conditions",
            ],

            examples: [
              "if (age >= 18) { console.log('Adult'); } else { console.log('Minor'); }",
            ],

            useCases: [
              "Form validation",
              "Authentication logic",
              "Conditional UI behavior",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand conditional logic",
              "Write if/else statements correctly",
              "Combine conditions using logical operators",
              "Implement real-world decision logic",
            ],
          },
          {
            day: 2,
            topic: "Switch Case & Ternary Operator",
            subTopics: [
              "switch syntax",
              "break keyword",
              "default case",
              "Ternary operator",
              "Choosing switch vs if-else",
            ],
            explanation:
              "Switch and ternary operators make code cleaner for multiple conditions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Switch Fall-through Trap",
                description:
                  "Identify and fix bugs caused by missing break statements in switch cases.",
                scenarios: [
                  "Switch without break causing multiple logs",
                  "Unexpected default execution",
                  "Wrong case matching due to type mismatch",
                ],
                requirements: [
                  "Predict output before running",
                  "Add correct break statements",
                  "Explain why fall-through happens",
                ],
                outcome:
                  "You’ll avoid one of the most common switch-case production bugs.",
              },
              {
                id: "pt-2",
                title: "Ternary Abuse Refactor",
                description:
                  "Refactor unreadable nested ternary expressions into clean logic.",
                before:
                  "isAdmin ? isActive ? 'Active Admin' : 'Inactive Admin' : 'User'",
                afterGoal: "Readable logic using if/else or variables",
                outcome: "Cleaner, maintainable decision-making code.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Decision bugs often appear only in edge cases and uncommon values.",
              realWorldChecks: [
                "Test default switch cases",
                "Test unexpected input values",
                "Test type mismatches (string vs number)",
                "Test readability of ternary logic",
              ],
              example:
                "switch (role) {\n  case 'admin': handleAdmin(); break;\n  default: handleUser();\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between switch and if-else?",
                "What happens if break is missing?",
                "When should ternary be avoided?",
                "Can switch work with strings?",
              ],
              interviewerTip:
                "Explain why you chose switch or ternary — not just how it works.",
              redFlag: "Overusing ternary or forgetting default cases.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Am I checking one value or many conditions?",
                "Did I handle the default case?",
                "Is this ternary still readable?",
                "Would if-else be clearer here?",
              ],
              refactorRule: "If ternary needs nesting → stop and refactor.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As conditions grow, long if-else ladders become hard to read. JavaScript provides switch statements and ternary operators to simplify decision-making and improve code clarity when used correctly.",

              sections: [
                {
                  title: "Why switch Exists",
                  description:
                    "switch is designed for checking one value against many possible cases.",
                  keyPoints: [
                    "Cleaner than long if-else ladders",
                    "Easier to scan and maintain",
                    "Best when comparing the same variable",
                  ],
                  analogy: "Like a menu where one option matches one action",
                },
                {
                  title: "Switch Syntax",
                  description:
                    "switch evaluates an expression and matches it with cases.",
                  syntax: "switch(value) { case x: break; default: }",
                  code: "switch(day) {\n  case 1:\n    console.log('Monday');\n    break;\n  case 2:\n    console.log('Tuesday');\n    break;\n  default:\n    console.log('Invalid day');\n}",
                  takeaway: "Only one matching case executes",
                },
                {
                  title: "The break Keyword",
                  description: "break stops execution after a matching case.",
                  keyPoints: [
                    "Without break, execution falls through",
                    "Used to prevent multiple cases running",
                  ],
                  code: "case 1:\n  console.log('Monday');\n  break;",
                  warning: "Missing break is a common bug",
                },
                {
                  title: "default Case",
                  description: "default runs when no case matches.",
                  keyPoints: ["Acts like else", "Optional but recommended"],
                  code: "default:\n  console.log('Unknown value');",
                  takeaway: "Always handle unexpected values",
                },
                {
                  title: "Ternary Operator",
                  description: "A shorthand for simple if-else conditions.",
                  syntax: "condition ? valueIfTrue : valueIfFalse",
                  code: "const msg = isLogin ? 'Welcome' : 'Please login';",
                  bestUse: "Short, simple conditions",
                  analogy: "One-line decision maker",
                },
                {
                  title: "When NOT to Use Ternary",
                  description: "Ternary can hurt readability if overused.",
                  avoidWhen: [
                    "Multiple conditions",
                    "Nested ternaries",
                    "Complex logic",
                  ],
                  badExample: "isAdmin ? isActive ? 'A' : 'B' : 'C' ❌",
                },
                {
                  title: "Switch vs if-else",
                  description: "Choosing the right tool matters.",
                  comparison: [
                    "switch → one value, many outcomes",
                    "if-else → different conditions",
                    "ternary → very small decisions",
                  ],
                  ruleOfThumb: "Readability first, always",
                },
                {
                  title: "Real-World Examples",
                  description: "switch and ternary are widely used.",
                  examples: [
                    "Menu selection",
                    "Status labels",
                    "Role-based UI text",
                  ],
                  code: "const statusText = status === 'success' ? 'Done' : 'Pending';",
                },
              ],

              visualStructure: `
Decision Tools:
Small condition → Ternary
One value, many cases → Switch
Complex logic → if / else
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting break in switch",
                  consequence: "Multiple cases run unexpectedly",
                  fix: "Always add break",
                },
                {
                  mistake: "Overusing ternary",
                  consequence: "Unreadable code",
                  fix: "Use only for simple conditions",
                },
                {
                  mistake: "Using switch for complex conditions",
                  consequence: "Wrong logic structure",
                  fix: "Use if-else instead",
                },
              ],

              proTips: [
                "switch improves readability for fixed values",
                "Ternary is great but dangerous when nested",
                "Default cases prevent bugs",
                "Code clarity beats shorter code",
              ],

              realWorldAnalogy:
                "Decision tools are like vehicles:\n- Ternary = bicycle (quick & light)\n- switch = bus (many stops)\n- if-else = car (flexible)",
            },

            tasks: [
              "Create weekday name program",
              "Convert if-else to switch",
              "Use ternary for small conditions",
            ],

            examples: [
              "switch(day) { case 1: console.log('Monday'); break; }",
              "const msg = isLogin ? 'Welcome' : 'Please login';",
            ],

            useCases: [
              "Menu selection",
              "Status handling",
              "Conditional UI text",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use switch statements correctly",
              "Understand the role of break and default",
              "Apply ternary operator safely",
              "Choose the right conditional structure",
            ],
          },
          {
            day: 3,
            topic: "Loops – for, while, do-while",
            subTopics: [
              "for loop",
              "while loop",
              "do-while loop",
              "Loop conditions",
              "Infinite loop mistakes",
            ],
            explanation: "Loops repeat logic multiple times efficiently.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Infinite Loop Debug Challenge",
                description:
                  "Identify why a loop becomes infinite and fix it safely.",
                scenarios: [
                  "for loop without increment",
                  "while loop with wrong condition",
                  "do-while loop running unexpectedly",
                ],
                requirements: [
                  "Predict what will happen",
                  "Fix the loop condition or update",
                  "Explain why the bug occurred",
                ],
                outcome:
                  "You’ll instantly recognize and prevent infinite loops.",
              },
              {
                id: "pt-2",
                title: "Right Loop Selection",
                description:
                  "Choose the correct loop type based on problem statements.",
                scenarios: [
                  "Known number of repetitions",
                  "User-driven condition",
                  "At least one execution required",
                ],
                afterGoal: "Correctly justify for vs while vs do-while",
                outcome: "Cleaner logic and better loop decisions.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Loop bugs can freeze browsers, crash apps, and cause performance issues.",
              realWorldChecks: [
                "Check loop exit condition",
                "Verify increment/decrement",
                "Test edge values (0, empty arrays)",
                "Log loop counters during debugging",
              ],
              example:
                "for (let i = 0; i < items.length; i++) {\n  console.log(i, items[i]);\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between for and while?",
                "When does do-while run at least once?",
                "What causes infinite loops?",
                "How do you debug a stuck loop?",
              ],
              interviewerTip:
                "Explain loop choice using real use-cases, not definitions.",
              redFlag: "Not understanding loop exit conditions.",
            },

            editorGuidance: {
              mentalChecklist: [
                "What stops this loop?",
                "Will this condition ever become false?",
                "Is the loop counter updating correctly?",
                "Is this loop readable?",
              ],
              refactorRule:
                "If you can’t explain how the loop ends → refactor immediately.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "When you need to repeat an action multiple times — printing numbers, processing lists, rendering UI items — loops are the solution. Instead of writing the same code again and again, loops automate repetition safely and efficiently.",

              sections: [
                {
                  title: "Why Loops Are Needed",
                  description:
                    "Loops remove duplication and handle repetitive tasks.",
                  examples: [
                    "Printing numbers",
                    "Iterating over data",
                    "Generating tables",
                  ],
                  analogy:
                    "Like a conveyor belt doing the same task repeatedly",
                },
                {
                  title: "for Loop",
                  description:
                    "The most commonly used loop when the number of iterations is known.",
                  structure: [
                    "Initialization",
                    "Condition",
                    "Increment / Decrement",
                  ],
                  syntax: "for (init; condition; update) { }",
                  code: "for (let i = 1; i <= 5; i++) {\n  console.log(i);\n}",
                  bestUse: "Fixed number of repetitions",
                },
                {
                  title: "while Loop",
                  description: "Runs as long as the condition is true.",
                  keyPoints: [
                    "Condition checked before execution",
                    "Risk of infinite loop if condition never changes",
                  ],
                  code: "let count = 1;\nwhile (count <= 5) {\n  console.log(count);\n  count++;\n}",
                  bestUse: "When number of iterations is not fixed",
                },
                {
                  title: "do-while Loop",
                  description:
                    "Runs the loop at least once, even if condition is false.",
                  keyPoints: [
                    "Condition checked after execution",
                    "Guarantees at least one run",
                  ],
                  code: "let num = 1;\ndo {\n  console.log(num);\n  num++;\n} while (num <= 5);",
                  analogy: "Do first, check later",
                },
                {
                  title: "Loop Conditions",
                  description: "Conditions control when loops stop.",
                  bestPractices: [
                    "Ensure condition eventually becomes false",
                    "Update loop variable correctly",
                  ],
                  warning: "Wrong condition leads to infinite loops",
                },
                {
                  title: "Infinite Loop Mistakes",
                  description: "One of the most common beginner errors.",
                  causes: [
                    "Missing increment/decrement",
                    "Wrong condition",
                    "Always-true condition",
                  ],
                  badExample: "while (true) { console.log('Oops'); } ❌",
                  fix: "Always update loop variable",
                },
                {
                  title: "Looping Through Arrays (Preview)",
                  description: "Loops are used to process collections.",
                  code: "const nums = [1, 2, 3];\nfor (let i = 0; i < nums.length; i++) {\n  console.log(nums[i]);\n}",
                  note: "We’ll cover advanced loops later",
                },
                {
                  title: "Real-World Use Cases",
                  description: "Loops power real application features.",
                  examples: [
                    "Rendering product lists",
                    "Processing API data",
                    "Calculating totals",
                  ],
                },
              ],

              visualStructure: `
Loop Cycle:
Start → Check condition → Run code → Update → Repeat
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting increment/decrement",
                  consequence: "Infinite loop",
                  fix: "Always update loop variable",
                },
                {
                  mistake: "Wrong loop condition",
                  consequence: "Loop runs too many or too few times",
                  fix: "Double-check conditions",
                },
                {
                  mistake: "Using wrong loop type",
                  consequence: "Confusing logic",
                  fix: "Choose loop based on need",
                },
              ],

              proTips: [
                "Use for loop when count is known",
                "Use while when condition-driven",
                "Avoid infinite loops at all cost",
                "Readable loops > clever loops",
              ],

              realWorldAnalogy:
                "Loops are like daily routines:\n- Wake up\n- Do task\n- Repeat until goal is met",
            },

            tasks: [
              "Print numbers 1–10",
              "Loop through array",
              "Create multiplication table",
            ],

            examples: [
              "for(let i=1;i<=5;i++){ console.log(i); }",
              "while(count < 5) { count++; }",
            ],

            useCases: [
              "Rendering lists",
              "Processing data",
              "Repeating calculations",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand how loops work",
              "Use for, while, and do-while correctly",
              "Avoid infinite loops",
              "Apply loops to real problems",
            ],
          },
          {
            day: 4,
            topic: "Break, Continue & Loop Control",
            subTopics: [
              "break statement",
              "continue statement",
              "Nested loops",
              "Loop optimization",
              "Common loop bugs",
            ],
            explanation:
              "Loop control helps stop or skip iterations when needed.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Break vs Continue Decision Drill",
                description:
                  "Decide whether break or continue is the correct choice in different scenarios.",
                scenarios: [
                  "Stop searching once item is found",
                  "Skip invalid data but continue processing",
                  "Exit loop on error condition",
                ],
                requirements: [
                  "Choose break or continue correctly",
                  "Explain why the other option is wrong",
                  "Rewrite loop logic cleanly",
                ],
                outcome:
                  "You’ll instinctively know when to stop a loop vs skip an iteration.",
              },
              {
                id: "pt-2",
                title: "Nested Loop Control Fix",
                description:
                  "Fix bugs caused by incorrect break usage inside nested loops.",
                before:
                  "for (let i = 0; i < a.length; i++) { for (let j = 0; j < b.length; j++) { break; } }",
                afterGoal: "Stop only when the correct condition is met",
                outcome: "Better control over complex loop logic.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Incorrect loop control can cause missing data, early exits, or performance issues.",
              realWorldChecks: [
                "Does the loop stop too early?",
                "Is continue skipping required logic?",
                "Are nested loops breaking correctly?",
                "Are edge cases tested?",
              ],
              example:
                "for (let i = 0; i < items.length; i++) {\n  if (!items[i]) continue;\n  if (items[i].id === target) break;\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between break and continue?",
                "What happens when break is used in nested loops?",
                "Can continue cause infinite loops?",
                "When should loops be stopped early?",
              ],
              interviewerTip:
                "Interviewers look for intent — why you used break or continue.",
              redFlag:
                "Using break/continue without clearly explaining loop flow.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Should the loop stop or just skip?",
                "Is this condition filtering or terminating?",
                "Will this break affect outer loops?",
                "Is the loop still readable?",
              ],
              refactorRule:
                "If break/continue changes behavior unexpectedly → simplify the loop.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Loops are powerful, but without control they can do unnecessary work or even run forever. JavaScript provides loop control statements like break and continue to precisely manage how loops execute.",

              sections: [
                {
                  title: "Why Loop Control Matters",
                  description: "Not every iteration is useful.",
                  keyPoints: [
                    "Stop loops early when condition is met",
                    "Skip unnecessary iterations",
                    "Improve performance and readability",
                  ],
                  analogy:
                    "Like skipping floors in an elevator or stopping when you reach your destination",
                },
                {
                  title: "break Statement",
                  description: "break immediately exits the loop.",
                  keyPoints: [
                    "Stops loop execution completely",
                    "Used when target is found",
                    "Works with for, while, do-while, switch",
                  ],
                  code: "for (let i = 1; i <= 10; i++) {\n  if (i === 5) break;\n  console.log(i);\n}",
                  useCase: "Search operations (stop when found)",
                },
                {
                  title: "continue Statement",
                  description:
                    "continue skips the current iteration and moves to the next.",
                  keyPoints: [
                    "Does NOT stop the loop",
                    "Skips only one iteration",
                    "Useful for filtering logic",
                  ],
                  code: "for (let i = 1; i <= 10; i++) {\n  if (i % 2 === 0) continue;\n  console.log(i); // odd numbers only\n}",
                  analogy: "Ignore this step, move on",
                },
                {
                  title: "break vs continue",
                  description: "They serve different purposes.",
                  comparison: [
                    "break → exit loop entirely",
                    "continue → skip current iteration only",
                  ],
                  takeaway: "Use break to stop, continue to skip",
                },
                {
                  title: "Nested Loops",
                  description:
                    "Loops inside loops are common but need careful control.",
                  useCases: [
                    "Tables",
                    "Grid processing",
                    "Comparing multiple lists",
                  ],
                  code: "for (let i = 1; i <= 3; i++) {\n  for (let j = 1; j <= 3; j++) {\n    console.log(i, j);\n  }\n}",
                  warning: "Nested loops increase time complexity",
                },
                {
                  title: "Breaking Nested Loops",
                  description: "break exits only the inner loop by default.",
                  code: "outer: for (let i = 1; i <= 3; i++) {\n  for (let j = 1; j <= 3; j++) {\n    if (i === 2 && j === 2) break outer;\n  }\n}",
                  note: "Labels exist but are rarely used in real projects",
                },
                {
                  title: "Loop Optimization",
                  description: "Efficient loops improve performance.",
                  bestPractices: [
                    "Break early when possible",
                    "Avoid unnecessary nested loops",
                    "Pre-calculate repeated values",
                  ],
                  example: "Stop searching once item is found",
                },
                {
                  title: "Common Loop Bugs",
                  description: "Most loop bugs are logic-related.",
                  bugs: [
                    "Using break instead of continue",
                    "Breaking too early",
                    "Infinite loops",
                  ],
                  fix: "Carefully design conditions and updates",
                },
              ],

              visualStructure: `
Loop Control:
Iteration → Check → break? stop
                 → continue? skip
                 → else run
    `,

              commonMistakes: [
                {
                  mistake: "Using break when continue is needed",
                  consequence: "Loop ends too early",
                  fix: "Understand intent clearly",
                },
                {
                  mistake: "Overusing nested loops",
                  consequence: "Slow code",
                  fix: "Refactor or optimize logic",
                },
                {
                  mistake: "Not testing edge cases",
                  consequence: "Unexpected behavior",
                  fix: "Test with different inputs",
                },
              ],

              proTips: [
                "Break early to save computation",
                "Continue is great for filtering",
                "Avoid deep nesting when possible",
                "Readable loops are easier to debug",
              ],

              realWorldAnalogy:
                "Loop control is like traffic control:\n- break = road closed\n- continue = skip this lane",
            },

            tasks: [
              "Stop loop at a condition",
              "Skip even numbers",
              "Break nested loops",
            ],

            examples: ["if (i === 5) break;", "if (i % 2 === 0) continue;"],

            useCases: [
              "Search operations",
              "Conditional iteration",
              "Data filtering",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "25–30 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Control loop execution using break and continue",
              "Write efficient nested loops",
              "Avoid common loop bugs",
              "Optimize iteration logic",
            ],
          },
          {
            day: 5,
            topic: "Functions – Declaration & Expression",
            subTopics: [
              "Function declaration",
              "Function expression",
              "Arrow functions",
              "Parameters & arguments",
              "Return statement",
            ],
            explanation: "Functions help reuse logic and keep code organized.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Function Type Selection Drill",
                description:
                  "Decide which function type is best suited for different real-world scenarios.",
                scenarios: [
                  "Utility function used across files",
                  "Callback inside array methods",
                  "Conditionally assigned function",
                  "Short calculation logic",
                ],
                requirements: [
                  "Choose declaration / expression / arrow",
                  "Explain WHY that choice fits best",
                  "Refactor incorrect function usage",
                ],
                outcome:
                  "You’ll instinctively choose the right function style in real projects.",
              },
              {
                id: "pt-2",
                title: "Return vs Console.log Fix",
                description:
                  "Fix functions that incorrectly use console.log instead of return.",
                before: "function sum(a, b) { console.log(a + b); }",
                afterGoal: "Function should return the result and be reusable",
                outcome:
                  "Clear understanding of data flow using return values.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most logical bugs happen when functions don’t return expected values.",
              realWorldChecks: [
                "Does this function return or just log?",
                "Can this function be reused?",
                "Is function doing only one job?",
                "Are parameters named clearly?",
              ],
              example:
                "const total = calculatePrice(price, tax);\nif (total > 1000) applyDiscount();",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between function declaration and expression?",
                "What are arrow functions?",
                "When should you avoid arrow functions?",
                "What happens if a function has no return?",
              ],
              interviewerTip:
                "Interviewers care more about function design than syntax.",
              redFlag:
                "Using arrow functions everywhere without understanding trade-offs.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Does this function do ONE thing?",
                "Should this logic be reused?",
                "Is return value required?",
                "Is this function readable without comments?",
              ],
              refactorRule: "If a function feels long or confusing → split it.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications grow, repeating logic becomes a nightmare. Functions solve this by allowing you to write logic once and reuse it everywhere. Almost every feature in real-world apps is built on functions.",

              sections: [
                {
                  title: "What Is a Function?",
                  description:
                    "A function is a reusable block of code that performs a specific task.",
                  keyPoints: [
                    "Avoids code duplication",
                    "Improves readability",
                    "Encapsulates logic",
                  ],
                  code: "function greet() {\n  console.log('Hello');\n}",
                  analogy:
                    "Like a machine that takes input and produces output",
                },
                {
                  title: "Function Declaration",
                  description:
                    "The classic and most readable way to define a function.",
                  keyPoints: [
                    "Can be called before declaration (hoisting)",
                    "Best for core logic",
                  ],
                  code: "function add(a, b) {\n  return a + b;\n}",
                  useCase: "Utility or core business logic",
                },
                {
                  title: "Parameters & Arguments",
                  description: "Parameters receive data, arguments send data.",
                  keyPoints: [
                    "Parameters are placeholders",
                    "Arguments are actual values",
                    "Functions can accept multiple parameters",
                  ],
                  code: "function multiply(x, y) {\n  return x * y;\n}\n\nmultiply(2, 3); // arguments",
                  analogy: "Function = recipe, arguments = ingredients",
                },
                {
                  title: "Return Statement",
                  description: "return sends a value back to the caller.",
                  keyPoints: [
                    "Stops function execution",
                    "Returns undefined if omitted",
                  ],
                  code: "function square(n) {\n  return n * n;\n}",
                  takeaway: "Without return, function gives nothing back",
                },
                {
                  title: "Function Expression",
                  description: "Functions can be stored in variables.",
                  keyPoints: [
                    "Not hoisted like declarations",
                    "Often used in callbacks",
                  ],
                  code: "const subtract = function(a, b) {\n  return a - b;\n};",
                  comparison: "Declaration → hoisted\nExpression → not hoisted",
                },
                {
                  title: "Arrow Functions",
                  description: "Modern, concise syntax for functions.",
                  keyPoints: [
                    "Shorter syntax",
                    "Implicit return for single expressions",
                    "No own this (covered later)",
                  ],
                  code: "const add = (a, b) => a + b;",
                  bestUse: "Small, simple functions",
                },
                {
                  title: "Choosing the Right Function Type",
                  description: "Each style has its place.",
                  guidelines: [
                    "Declaration → main logic",
                    "Expression → conditional assignment",
                    "Arrow → short callbacks & utilities",
                  ],
                  rule: "Readability > clever syntax",
                },
                {
                  title: "Real-World Examples",
                  description: "Functions power almost everything.",
                  examples: [
                    "Form validation",
                    "API data processing",
                    "UI state updates",
                  ],
                  code: "function isAdult(age) {\n  return age >= 18;\n}",
                },
              ],

              visualStructure: `
Function Flow:
Input (arguments)
   ↓
Function logic
   ↓
Output (return value)
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting return",
                  consequence: "undefined results",
                  fix: "Explicitly return values",
                },
                {
                  mistake: "Using arrow functions everywhere",
                  consequence: "Confusion with this",
                  fix: "Use arrow functions thoughtfully",
                },
                {
                  mistake: "Very large functions",
                  consequence: "Hard-to-maintain code",
                  fix: "Break logic into smaller functions",
                },
              ],

              proTips: [
                "Functions should do one thing",
                "Name functions clearly",
                "Prefer small reusable functions",
                "Readable code is professional code",
              ],

              realWorldAnalogy:
                "Functions are like tools:\n- Each tool has one purpose\n- Reuse them instead of rebuilding",
            },

            tasks: [
              "Create add and subtract functions",
              "Return values from functions",
              "Convert functions to arrow syntax",
            ],

            examples: [
              "function add(a,b){ return a+b; }",
              "const add = (a,b) => a+b;",
            ],

            useCases: [
              "Reusable logic",
              "Modular code",
              "Cleaner application structure",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand function fundamentals",
              "Write function declarations and expressions",
              "Use arrow functions appropriately",
              "Organize logic using functions",
            ],
          },
          {
            day: 6,
            topic: "Function Scope, Hoisting & Callbacks",
            subTopics: [
              "Local vs global scope",
              "Function scope",
              "Hoisting basics",
              "Callback functions",
              "Why callbacks exist",
            ],
            explanation:
              "Understanding scope and callbacks avoids bugs and prepares for async JavaScript.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Scope Leak Debug Challenge",
                description:
                  "Identify and fix bugs caused by incorrect variable scope usage.",
                scenarios: [
                  "Accidentally modifying global variables",
                  "Accessing local variables outside function",
                  "Shadowing variables with same name",
                ],
                requirements: [
                  "Predict output before running",
                  "Explain scope behavior",
                  "Refactor code to use safe local scope",
                ],
                outcome:
                  "You’ll stop creating hidden bugs caused by scope misuse.",
              },
              {
                id: "pt-2",
                title: "Callback Mental Model Builder",
                description:
                  "Rewrite normal functions into callback-based execution.",
                before:
                  "function calculate() { console.log('Done'); }\ncalculate();",
                afterGoal:
                  "Execute logic via callback passed into another function",
                outcome:
                  "Clear understanding of why callbacks exist and how execution flows.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Scope and callbacks decide WHEN and WHERE your code runs — most async bugs start here.",
              realWorldChecks: [
                "Is this variable leaking to global scope?",
                "Does this function depend on external state?",
                "Is callback executed immediately or later?",
                "Can this logic run multiple times safely?",
              ],
              example:
                "function fetchData(cb) {\n  // simulate async\n  cb('data');\n}\nfetchData(result => console.log(result));",
            },

            interviewInsights: {
              commonQuestions: [
                "What is scope in JavaScript?",
                "Difference between global and local scope?",
                "What is hoisting?",
                "What is a callback function?",
                "Why are callbacks important in JavaScript?",
              ],
              interviewerTip:
                "Explain scope and callbacks with execution flow, not definitions.",
              redFlag: "Confusion between hoisting and initialization.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Where is this variable declared?",
                "Who can access this variable?",
                "When does this callback execute?",
                "Is execution order obvious?",
              ],
              refactorRule:
                "If execution order is confusing → refactor into smaller functions.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Many JavaScript bugs don’t come from syntax — they come from misunderstanding scope, execution order, and how functions interact with each other. This lesson builds a strong mental model for how JavaScript actually runs code.",

              sections: [
                {
                  title: "What Is Scope?",
                  description:
                    "Scope defines where a variable or function can be accessed.",
                  keyPoints: [
                    "Controls variable visibility",
                    "Prevents name collisions",
                    "Core to predictable code",
                  ],
                  analogy:
                    "Like rooms in a house — some are public, some are private",
                },
                {
                  title: "Global Scope",
                  description:
                    "Variables declared outside functions live in global scope.",
                  keyPoints: [
                    "Accessible everywhere",
                    "Risky if overused",
                    "Can cause conflicts",
                  ],
                  code: "let appName = 'MyApp';\nfunction show() {\n  console.log(appName);\n}",
                  warning: "Avoid unnecessary global variables",
                },
                {
                  title: "Local (Function) Scope",
                  description: "Variables declared inside functions are local.",
                  keyPoints: [
                    "Accessible only inside the function",
                    "Destroyed after function execution",
                  ],
                  code: "function test() {\n  let x = 10;\n}\n// x is not accessible here",
                  analogy: "Temporary workspace",
                },
                {
                  title: "Why Scope Matters",
                  description: "Scope prevents unexpected behavior.",
                  examples: [
                    "Avoid accidental overwrites",
                    "Protect internal logic",
                    "Cleaner, safer code",
                  ],
                  takeaway: "Smaller scope = fewer bugs",
                },
                {
                  title: "Hoisting Basics",
                  description:
                    "Hoisting is JavaScript’s behavior of moving declarations to the top.",
                  keyPoints: [
                    "Function declarations are hoisted",
                    "var is hoisted (but dangerous)",
                    "let and const are hoisted but not accessible",
                  ],
                  code: "sayHi();\nfunction sayHi() {\n  console.log('Hi');\n}",
                  warning: "Hoisting ≠ initialization",
                },
                {
                  title: "Hoisting Pitfalls",
                  description:
                    "Misunderstanding hoisting causes runtime errors.",
                  badExample: "console.log(x);\nlet x = 5; // ReferenceError",
                  takeaway: "Declare before use",
                },
                {
                  title: "What Is a Callback Function?",
                  description:
                    "A callback is a function passed as an argument to another function.",
                  keyPoints: [
                    "Functions are first-class citizens",
                    "Callbacks enable flexibility",
                    "Foundation of async JS",
                  ],
                  code: "function greet(cb) {\n  cb();\n}\ngreet(() => console.log('Hello'));",
                  analogy: "Call me back when you're done",
                },
                {
                  title: "Why Callbacks Exist",
                  description:
                    "Callbacks allow code to run later or conditionally.",
                  useCases: ["Event handling", "Timers", "Async operations"],
                  example: "setTimeout(() => console.log('Done'), 1000);",
                  takeaway: "Callbacks control execution timing",
                },
                {
                  title: "Callbacks in Real Apps",
                  description: "Callbacks appear everywhere in JavaScript.",
                  examples: [
                    "Button click handlers",
                    "Array methods (map, filter)",
                    "API responses (preview)",
                  ],
                },
              ],

              visualStructure: `
Execution Flow:
Function call
   ↓
Local scope created
   ↓
Callback executed
   ↓
Scope destroyed
    `,

              commonMistakes: [
                {
                  mistake: "Overusing global variables",
                  consequence: "Hard-to-debug conflicts",
                  fix: "Use local scope",
                },
                {
                  mistake: "Relying on hoisting",
                  consequence: "Unpredictable code",
                  fix: "Declare before use",
                },
                {
                  mistake: "Not understanding callbacks",
                  consequence: "Confusion in async code",
                  fix: "Practice passing functions",
                },
              ],

              proTips: [
                "Keep scope as small as possible",
                "Avoid globals unless necessary",
                "Callbacks are just functions",
                "Understanding this saves weeks later",
              ],

              realWorldAnalogy:
                "Scope and callbacks are like office workflow:\n- Scope = who has access\n- Callback = who responds when called",
            },

            tasks: [
              "Test variable scope",
              "Observe hoisting behavior",
              "Pass function as argument",
            ],

            examples: [
              "function greet(cb){ cb(); }",
              "greet(() => console.log('Hello'));",
            ],

            useCases: [
              "Event handling",
              "Async programming foundation",
              "Reusable logic control",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand JavaScript scope",
              "Avoid hoisting-related bugs",
              "Use callbacks confidently",
              "Build strong execution mental model",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Quiz Application",
            subTopics: [
              "Conditional logic",
              "Loops",
              "Functions",
              "Score calculation",
              "User interaction flow",
            ],
            explanation:
              "This project applies control flow and functions to build a real interactive quiz.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Quiz Logic Debug Challenge",
                description:
                  "Debug common logical issues that break quiz flow and scoring.",
                scenarios: [
                  "Score increasing incorrectly",
                  "Questions skipping or repeating",
                  "Quiz ending too early or too late",
                ],
                requirements: [
                  "Trace execution step by step",
                  "Identify faulty condition or loop",
                  "Fix logic without rewriting entire code",
                ],
                outcome:
                  "You’ll gain confidence debugging real logical bugs in projects.",
              },
              {
                id: "pt-2",
                title: "Function Responsibility Refactor",
                description:
                  "Refactor quiz logic so each function has a single responsibility.",
                before:
                  "function quiz(){ askQuestion(); checkAnswer(); updateScore(); }",
                afterGoal:
                  "Separate question rendering, answer validation, and result display",
                outcome: "Cleaner, professional-grade project structure.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Mini projects fail mostly due to flow bugs, not syntax errors.",
              realWorldChecks: [
                "Is score reset before quiz starts?",
                "Is current question index updated correctly?",
                "Does quiz stop exactly at last question?",
                "Are edge cases (empty input) handled?",
              ],
              example:
                "let current = 0;\nif (current >= questions.length) {\n  showResult();\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "How would you build a quiz app in JavaScript?",
                "How do you manage state like score and index?",
                "How do loops and functions work together here?",
                "How would you improve this quiz further?",
              ],
              interviewerTip: "Explain logic flow first, code second.",
              redFlag:
                "Unable to explain how score or question flow is controlled.",
            },

            editorGuidance: {
              mentalChecklist: [
                "What is the current question index?",
                "Where is score updated?",
                "What ends the quiz?",
                "Is logic separated from display?",
              ],
              refactorRule: "If one function does too much → split it.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A quiz application looks simple, but internally it uses almost every JavaScript fundamental: loops to move through questions, conditions to validate answers, functions to organize logic, and variables to track score. This makes it a perfect mini project for solidifying core concepts.",

              sections: [
                {
                  title: "Project Goal",
                  description:
                    "Build an interactive quiz that asks questions, accepts answers, and calculates a final score.",
                  features: [
                    "Multiple questions",
                    "Score tracking",
                    "Final result display",
                  ],
                  skillsUsed: ["if / else", "Loops", "Functions", "Variables"],
                  outcome: "A working quiz logic engine",
                },
                {
                  title: "Quiz Data Structure",
                  description:
                    "Questions should be stored in a structured format.",
                  keyPoints: [
                    "Use an array of objects",
                    "Each object represents one question",
                    "Store question, options, and correct answer",
                  ],
                  code: "const questions = [\n  { q: '2 + 2 = ?', answer: 4 },\n  { q: 'Capital of India?', answer: 'Delhi' }\n];",
                  takeaway: "Good data structure simplifies logic",
                },
                {
                  title: "Displaying Questions",
                  description: "Questions should be shown one at a time.",
                  logicFlow: [
                    "Start from first question",
                    "Move to next after answer",
                    "Stop when questions end",
                  ],
                  code: "for (let i = 0; i < questions.length; i++) {\n  console.log(questions[i].q);\n}",
                  analogy: "Like asking questions in an interview",
                },
                {
                  title: "Handling User Answers",
                  description:
                    "User input must be compared with correct answers.",
                  keyPoints: [
                    "Use conditionals",
                    "Compare values carefully",
                    "Handle case sensitivity if needed",
                  ],
                  code: "if (userAnswer === correctAnswer) {\n  score++;\n}",
                  warning: "Always validate user input",
                },
                {
                  title: "Score Calculation",
                  description: "Score should update after each question.",
                  keyPoints: [
                    "Initialize score = 0",
                    "Increment on correct answer",
                  ],
                  code: "let score = 0;\nscore++;",
                  takeaway: "Simple logic, clear outcome",
                },
                {
                  title: "Using Functions",
                  description: "Functions keep code organized.",
                  functions: [
                    "showQuestion()",
                    "checkAnswer()",
                    "showResult()",
                  ],
                  code: "function checkAnswer(user, correct) {\n  return user === correct;\n}",
                  benefit: "Easier to debug and extend",
                },
                {
                  title: "User Interaction Flow",
                  description: "The app should feel natural.",
                  flow: [
                    "Start quiz",
                    "Answer question",
                    "Next question",
                    "Final result",
                  ],
                  UXTip: "Show progress (optional)",
                },
                {
                  title: "Optional Enhancements",
                  description: "Add polish to improve learning.",
                  ideas: [
                    "Percentage score",
                    "Retry quiz",
                    "Time-based questions",
                  ],
                  note: "Enhancements are optional",
                },
              ],

              visualStructure: `
Quiz Flow:
Start → Question → Answer → Check → Score → Next → Result
    `,

              commonMistakes: [
                {
                  mistake: "Not resetting score",
                  consequence: "Wrong final result",
                  fix: "Initialize score properly",
                },
                {
                  mistake: "Incorrect loop condition",
                  consequence: "Skipped or repeated questions",
                  fix: "Use correct array length",
                },
                {
                  mistake: "Mixing logic and UI",
                  consequence: "Hard-to-maintain code",
                  fix: "Separate functions",
                },
              ],

              proTips: [
                "Think in steps, not code",
                "Break logic into small functions",
                "Console.log flow while building",
                "This project is interview-friendly",
              ],

              realWorldAnalogy:
                "A quiz app is like an exam:\n- Question asked\n- Answer checked\n- Marks updated\n- Result declared",
            },

            tasks: [
              "Create quiz questions array",
              "Show questions one by one",
              "Calculate score",
              "Display final result",
            ],

            examples: ["if(answer === correct){ score++; }"],

            useCases: [
              "Interview-level JS project",
              "Logic building confidence",
              "Foundation for bigger apps",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "2–3 hours",

            // 🎯 Learning objectives
            objectives: [
              "Apply JS control flow in a real project",
              "Organize logic using functions",
              "Build confidence with JavaScript",
              "Prepare for interview-style problems",
            ],
          },
        ],
      },
      {
        week: 9,
        weekGoal:
          "Master arrays and data transformation using modern JavaScript methods",
        weekOutcome:
          "Ability to manipulate lists, collections, and API data efficiently",
        canLearn: ["students", "beginners", "professionals"],
        miniProject: "Task Manager App",
        days: [
          {
            day: 1,
            topic: "Array Basics & Creation",
            subTopics: [
              "Creating arrays",
              "Accessing elements",
              "Array length",
              "Updating array values",
              "Common array mistakes",
            ],
            explanation:
              "Arrays store ordered collections of data and are fundamental for frontend development.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Index Confusion Debug Task",
                description:
                  "Identify and fix common index-related bugs in arrays.",
                scenarios: [
                  "Accessing array[length]",
                  "Using index starting from 1",
                  "Updating wrong index value",
                ],
                requirements: [
                  "Predict output before running code",
                  "Explain why undefined is returned",
                  "Correct the index logic",
                ],
                outcome:
                  "You’ll stop making beginner index mistakes in real projects.",
              },
              {
                id: "pt-2",
                title: "Array Mutation Awareness",
                description:
                  "Understand how const arrays change without reassignment.",
                before: "const items = ['a', 'b']; items = ['c']; // ❌",
                afterGoal:
                  "Update values correctly using index without reassigning",
                outcome: "Clear understanding of mutation vs reassignment.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Array bugs often break UI silently — nothing crashes, but data disappears.",
              realWorldChecks: [
                "Is index within array bounds?",
                "Are you looping till length - 1?",
                "Is array mutated or reassigned?",
                "Does UI expect array or single value?",
              ],
              example:
                "if (index >= 0 && index < arr.length) {\n  console.log(arr[index]);\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "What is an array in JavaScript?",
                "Why does array index start from 0?",
                "Can you modify a const array?",
                "What happens if you access arr[arr.length]?",
              ],
              interviewerTip:
                "Interviewers check how clearly you understand indexing and mutation.",
              redFlag: "Confusion between array mutation and reassignment.",
            },

            editorGuidance: {
              mentalChecklist: [
                "What is the array length?",
                "Which index am I accessing?",
                "Is this index valid?",
                "Am I mutating or reassigning?",
              ],
              refactorRule:
                "If index logic feels confusing → console.log index & length.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Most real-world applications deal with lists — products, users, messages, notifications. Arrays allow JavaScript to store and manage ordered collections of data efficiently. Understanding array basics is critical before moving to advanced methods like map, filter, and reduce.",

              sections: [
                {
                  title: "What Is an Array?",
                  description:
                    "An array is a data structure that holds multiple values in a single variable.",
                  keyPoints: [
                    "Ordered collection",
                    "Zero-based indexing",
                    "Can store mixed data types",
                  ],
                  code: "const numbers = [1, 2, 3, 4];",
                  analogy:
                    "Like a numbered list where each item has a position",
                },
                {
                  title: "Creating Arrays",
                  description: "Arrays are created using square brackets.",
                  examples: [
                    "const fruits = ['apple', 'banana', 'mango'];",
                    "const mixed = [1, 'hello', true];",
                  ],
                  bestPractice:
                    "Use const for arrays (mutations are still allowed)",
                },
                {
                  title: "Accessing Array Elements",
                  description: "Elements are accessed using index numbers.",
                  keyPoints: ["Index starts from 0", "Access via array[index]"],
                  code: "const fruits = ['apple', 'banana', 'mango'];\nconsole.log(fruits[0]); // apple",
                  warning: "Accessing invalid index returns undefined",
                },
                {
                  title: "Array Length",
                  description:
                    "length tells how many elements exist in the array.",
                  keyPoints: [
                    "length starts counting from 1",
                    "Highest index is length - 1",
                  ],
                  code: "fruits.length; // 3",
                  useCase: "Loops and validations",
                },
                {
                  title: "Updating Array Values",
                  description: "Arrays are mutable — values can be changed.",
                  keyPoints: [
                    "Use index to update values",
                    "const does not prevent mutation",
                  ],
                  code: "fruits[1] = 'orange';",
                  takeaway: "Array reference stays same, content changes",
                },
                {
                  title: "Arrays Are Objects (Important Concept)",
                  description: "Arrays are special objects in JavaScript.",
                  keyPoints: [
                    "typeof array is 'object'",
                    "Has built-in methods",
                  ],
                  code: "typeof fruits; // 'object'",
                  note: "This is why arrays have methods like push, map, filter",
                },
                {
                  title: "Common Array Mistakes",
                  description: "Beginner mistakes lead to bugs.",
                  mistakes: [
                    "Using 1-based index",
                    "Accessing out-of-range index",
                    "Assuming arrays are immutable",
                  ],
                  badExample: "fruits[3] // undefined",
                },
                {
                  title: "Real-World Examples",
                  description: "Arrays power frontend features.",
                  examples: ["Product listing", "User lists", "Cart items"],
                  code: "const cartItems = ['Shoes', 'T-shirt'];",
                },
              ],

              visualStructure: `
Array Structure:
Index → 0      1        2
Value → apple banana  mango
    `,

              commonMistakes: [
                {
                  mistake: "Using index starting from 1",
                  consequence: "Wrong values or undefined",
                  fix: "Always remember arrays start at 0",
                },
                {
                  mistake: "Accessing array[length]",
                  consequence: "undefined",
                  fix: "Use length - 1 for last element",
                },
                {
                  mistake: "Assuming const array cannot change",
                  consequence: "Confusion",
                  fix: "Understand mutation vs reassignment",
                },
              ],

              proTips: [
                "Arrays are everywhere in frontend",
                "length is heavily used with loops",
                "Mutating arrays is allowed with const",
                "Clean array logic = clean UI",
              ],

              realWorldAnalogy:
                "Arrays are like shelves:\n- Each shelf has a number\n- You place items by position\n- You can replace items anytime",
            },

            tasks: [
              "Create arrays of numbers and strings",
              "Access elements by index",
              "Update array values dynamically",
            ],

            examples: [
              "const fruits = ['apple', 'banana', 'mango'];",
              "console.log(fruits[0]);",
            ],

            useCases: [
              "Product lists",
              "User data storage",
              "Dynamic UI rendering",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand what arrays are",
              "Create and access arrays",
              "Update array values safely",
              "Avoid common array mistakes",
            ],
          },
          {
            day: 2,
            topic: "Add & Remove Array Elements",
            subTopics: [
              "push()",
              "pop()",
              "shift()",
              "unshift()",
              "Mutating vs non-mutating methods",
            ],
            explanation:
              "Array methods allow adding and removing elements dynamically.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Mutation Surprise Debug Task",
                description:
                  "Understand how push/pop/shift/unshift mutate the original array.",
                scenarios: [
                  "Array passed to a function and modified",
                  "UI list updating unexpectedly",
                  "Multiple references pointing to same array",
                ],
                requirements: [
                  "Predict array output before execution",
                  "Identify where mutation happens",
                  "Explain why original array changed",
                ],
                outcome: "You’ll stop accidental data mutation bugs.",
              },
              {
                id: "pt-2",
                title: "Choose the Right Method",
                description:
                  "Decide the correct add/remove method based on use case.",
                scenarios: [
                  "Add item to cart",
                  "Process first task in queue",
                  "Undo last action",
                ],
                requirements: [
                  "Select correct method (push/pop/shift/unshift)",
                  "Explain performance impact",
                ],
                outcome:
                  "Clear decision-making for real-world array operations.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Array mutation bugs are common in UI frameworks where state is shared.",
              realWorldChecks: [
                "Is this array used in multiple places?",
                "Do I expect original array to change?",
                "Should I clone before modifying?",
              ],
              example: "const copy = [...tasks];\ncopy.push('New Task');",
            },

            interviewInsights: {
              commonQuestions: [
                "Does push() return a new array?",
                "Difference between push and unshift?",
                "Why is shift slower than pop?",
                "What does pop() return?",
              ],
              interviewerTip:
                "Interviewers want clarity on mutation and performance.",
              redFlag: "Not knowing which array methods mutate data.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Am I mutating this array?",
                "Is this array shared anywhere else?",
                "Do I need removed value?",
                "Is performance important here?",
              ],
              refactorRule: "If mutation causes confusion → clone array first.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Real applications are dynamic — items are added, removed, or reordered all the time. JavaScript provides built-in array methods to handle these operations cleanly. Understanding which methods mutate arrays is critical for predictable behavior.",

              sections: [
                {
                  title: "push() – Add to End",
                  description:
                    "push() adds one or more elements to the end of an array.",
                  keyPoints: [
                    "Mutates the original array",
                    "Returns new array length",
                  ],
                  code: "const tasks = ['HTML', 'CSS'];\ntasks.push('JavaScript');",
                  useCase: "Add new todo item",
                },
                {
                  title: "pop() – Remove from End",
                  description: "pop() removes the last element from an array.",
                  keyPoints: ["Mutates the array", "Returns removed element"],
                  code: "const lastTask = tasks.pop();",
                  useCase: "Remove last item from cart",
                },
                {
                  title: "unshift() – Add to Start",
                  description:
                    "unshift() adds elements to the beginning of an array.",
                  keyPoints: ["Mutates array", "Shifts all existing indexes"],
                  code: "tasks.unshift('Git');",
                  warning: "Can be expensive on large arrays",
                },
                {
                  title: "shift() – Remove from Start",
                  description:
                    "shift() removes the first element from an array.",
                  keyPoints: ["Mutates array", "Indexes are re-calculated"],
                  code: "const first = tasks.shift();",
                  useCase: "Queue-like operations",
                },
                {
                  title: "Mutating vs Non-Mutating Methods",
                  description:
                    "Some array methods change the original array, others don’t.",
                  mutating: ["push", "pop", "shift", "unshift"],
                  nonMutatingPreview: ["slice", "map", "filter"],
                  takeaway: "Know when your data is changing",
                },
                {
                  title: "Why Mutation Matters",
                  description: "Mutation can cause bugs if not expected.",
                  examples: [
                    "Unexpected UI updates",
                    "State bugs in frameworks",
                  ],
                  bestPractice: "Be explicit when mutating arrays",
                },
                {
                  title: "Real-World Example – Todo List",
                  description: "These methods power simple apps.",
                  flow: [
                    "push → add task",
                    "pop → undo last task",
                    "shift → process first task",
                  ],
                  code: "const todos = [];\ntodos.push('Learn JS');\ntodos.pop();",
                },
              ],

              visualStructure: `
Array Operations:
Start ← unshift | shift → End
Start → push | pop ← End
    `,

              commonMistakes: [
                {
                  mistake: "Assuming methods return a new array",
                  consequence: "Unexpected mutations",
                  fix: "Know which methods mutate",
                },
                {
                  mistake: "Using shift/unshift in large arrays",
                  consequence: "Performance issues",
                  fix: "Prefer push/pop when possible",
                },
                {
                  mistake: "Not tracking removed values",
                  consequence: "Lost data",
                  fix: "Store returned value if needed",
                },
              ],

              proTips: [
                "push/pop are fastest operations",
                "shift/unshift reindex entire array",
                "Mutation is fine when intentional",
                "Always know your data flow",
              ],

              realWorldAnalogy:
                "Arrays are like a queue:\n- push = add person at back\n- pop = remove last person\n- shift = first person leaves\n- unshift = new person enters first",
            },

            tasks: [
              "Add items to todo list",
              "Remove completed items",
              "Understand array mutation",
            ],

            examples: ["tasks.push('Learn JS');", "tasks.pop();"],

            useCases: ["Todo apps", "Dynamic UI updates", "Cart management"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Add elements to arrays dynamically",
              "Remove array elements correctly",
              "Understand mutating behavior",
              "Apply array methods in real apps",
            ],
          },
          {
            day: 3,
            topic: "Iterating Arrays (forEach, map)",
            subTopics: [
              "forEach() method",
              "map() method",
              "Returning new arrays",
              "Difference between forEach & map",
            ],
            explanation:
              "Iteration methods help loop over arrays cleanly and efficiently.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "forEach Return Trap",
                description:
                  "Identify bugs caused by expecting forEach to return values.",
                scenarios: [
                  "Using forEach to build a new array",
                  "Assigning forEach result to a variable",
                  "Chaining forEach with other methods",
                ],
                requirements: [
                  "Predict output before running code",
                  "Explain why result is undefined",
                  "Refactor logic using map",
                ],
                outcome: "You’ll never misuse forEach again.",
              },
              {
                id: "pt-2",
                title: "Pure map Transformation Challenge",
                description:
                  "Transform data using map without mutating original array.",
                scenarios: [
                  "Convert prices to discounted prices",
                  "Format user names for UI",
                  "Extract specific fields from objects",
                ],
                requirements: [
                  "Do not modify original array",
                  "Return new transformed array",
                ],
                outcome:
                  "You’ll write predictable, framework-ready transformations.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Incorrect iteration logic leads to broken UI rendering and state bugs.",
              realWorldChecks: [
                "Am I expecting a returned value?",
                "Should this logic mutate data?",
                "Is this transformation reusable?",
              ],
              example:
                "const doubled = nums.map(n => n * 2);\n// nums remains unchanged",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between forEach and map?",
                "Does forEach return anything?",
                "Why is map preferred in React?",
                "Can map mutate the original array?",
              ],
              interviewerTip: "Explain with examples, not definitions.",
              redFlag: "Using forEach where map is clearly required.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Do I need a new array?",
                "Am I using the returned value?",
                "Is my map callback pure?",
                "Would this logic work in React?",
              ],
              refactorRule:
                "If data is transformed → map. If side-effect → forEach.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Once you have arrays, the next step is doing something with every item inside them. JavaScript provides powerful iteration methods like forEach and map that make code cleaner, more readable, and closer to how real frontend frameworks work.",

              sections: [
                {
                  title: "Why Array Iteration Matters",
                  description:
                    "Most UI and data logic involves processing lists.",
                  examples: [
                    "Rendering products",
                    "Formatting API responses",
                    "Calculating derived values",
                  ],
                  analogy: "Like checking every item on a checklist one by one",
                },
                {
                  title: "forEach() Method",
                  description:
                    "forEach executes a function once for each array element.",
                  keyPoints: [
                    "Does NOT return a new array",
                    "Used for side effects",
                    "Cannot break out early",
                  ],
                  syntax: "array.forEach(callback)",
                  code: "const items = ['a', 'b', 'c'];\nitems.forEach(item => {\n  console.log(item);\n});",
                  bestUse: "Logging, updating UI, side effects",
                },
                {
                  title: "map() Method",
                  description:
                    "map transforms each element and returns a new array.",
                  keyPoints: [
                    "Returns a new array",
                    "Does NOT mutate original array",
                    "Same length as original",
                  ],
                  syntax: "array.map(callback)",
                  code: "const nums = [1, 2, 3];\nconst doubled = nums.map(n => n * 2);",
                  bestUse: "Transforming data",
                },
                {
                  title: "Returning New Arrays",
                  description: "map is preferred when you need new data.",
                  example: "const prices = products.map(p => p.price);",
                  takeaway: "map = transform, forEach = act",
                },
                {
                  title: "forEach vs map (Core Difference)",
                  description: "Choosing the wrong one leads to bugs.",
                  comparison: [
                    "forEach → no return value",
                    "map → returns new array",
                    "forEach → side effects",
                    "map → pure transformation",
                  ],
                  ruleOfThumb: "If you need a new array → use map",
                },
                {
                  title: "Real-World UI Example",
                  description: "map is heavily used in UI rendering.",
                  code: "const names = ['A', 'B'];\nconst list = names.map(n => `<li>${n}</li>`);",
                  note: "This mindset is crucial for React",
                },
                {
                  title: "Common Pitfalls",
                  description: "Beginners often misuse these methods.",
                  mistakes: [
                    "Using forEach expecting return",
                    "Mutating data inside map",
                    "Using map but ignoring returned array",
                  ],
                },
              ],

              visualStructure: `
Iteration Flow:
Array → callback → (forEach: side effect)
Array → callback → (map: new array)
    `,

              commonMistakes: [
                {
                  mistake: "Using forEach instead of map",
                  consequence: "undefined results",
                  fix: "Use map when returning data",
                },
                {
                  mistake: "Mutating inside map",
                  consequence: "Unexpected behavior",
                  fix: "Keep map pure",
                },
                {
                  mistake: "Not using returned array",
                  consequence: "Wasted computation",
                  fix: "Always store map result",
                },
              ],

              proTips: [
                "map is a frontend developer’s best friend",
                "Keep map callbacks pure",
                "Use forEach for side effects only",
                "Clean iteration = clean UI",
              ],

              realWorldAnalogy:
                "forEach is like inspecting items\nmap is like transforming raw materials into a new product",
            },

            tasks: [
              "Display list of items",
              "Transform array values",
              "Render UI data",
            ],

            examples: [
              "items.forEach(item => console.log(item));",
              "const prices = products.map(p => p.price);",
            ],

            useCases: [
              "Rendering lists in UI",
              "Formatting API data",
              "Creating derived datasets",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Iterate arrays using forEach",
              "Transform arrays using map",
              "Understand return behavior",
              "Choose correct iteration method",
            ],
          },
          {
            day: 4,
            topic: "Filter, Find & Search Methods",
            subTopics: [
              "filter()",
              "find()",
              "findIndex()",
              "includes()",
              "indexOf()",
            ],
            explanation:
              "Search methods help find specific data inside large datasets.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "filter vs find Confusion Trap",
                description:
                  "Choose the correct search method based on real-world requirements.",
                scenarios: [
                  "Find a logged-in user",
                  "Filter completed todos",
                  "Search products under a price range",
                ],
                requirements: [
                  "Decide whether filter or find should be used",
                  "Explain why the other method is wrong",
                  "Refactor incorrect usage",
                ],
                outcome:
                  "You’ll stop misusing filter and find in production code.",
              },
              {
                id: "pt-2",
                title: "Search Robustness Challenge",
                description:
                  "Handle not-found cases safely without breaking the app.",
                scenarios: [
                  "User ID not found",
                  "Empty search results",
                  "Invalid search keyword",
                ],
                requirements: [
                  "Handle undefined and empty arrays safely",
                  "Show fallback logic",
                ],
                outcome: "You’ll write crash-proof search logic.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most UI bugs happen when search results are empty or undefined.",
              realWorldChecks: [
                "What happens if nothing is found?",
                "Am I expecting an array or an object?",
                "Is my UI ready for empty results?",
              ],
              example:
                "const user = users.find(u => u.id === 10);\nif (!user) {\n  console.log('User not found');\n}",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between filter and find?",
                "What does find return if no match?",
                "When should you use includes?",
                "Why indexOf is less used today?",
              ],
              interviewerTip: "Explain return type first (array vs value).",
              redFlag: "Using filter when only one item is expected.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Do I need one item or many?",
                "What should happen if nothing matches?",
                "Am I checking objects or primitives?",
                "Is this logic UI-safe?",
              ],
              refactorRule:
                "If result should be one item → find. If list → filter.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Real-world applications constantly search, filter, and locate data — users, products, tasks, messages. JavaScript provides powerful array search methods that replace messy loops and make code more readable and expressive.",

              sections: [
                {
                  title: "Why Search Methods Matter",
                  description: "Frontend apps rarely display all data at once.",
                  examples: [
                    "Filtering completed tasks",
                    "Finding a user by ID",
                    "Searching products by name",
                  ],
                  analogy: "Like searching contacts in your phone",
                },
                {
                  title: "filter() Method",
                  description:
                    "filter returns a NEW array containing elements that match a condition.",
                  keyPoints: [
                    "Returns a new array",
                    "Does not mutate original array",
                    "Can return multiple items",
                  ],
                  syntax: "array.filter(callback)",
                  code: "const active = tasks.filter(t => !t.done);",
                  bestUse: "Filtering lists and dashboards",
                },
                {
                  title: "find() Method",
                  description:
                    "find returns the FIRST element that matches a condition.",
                  keyPoints: [
                    "Returns a single value",
                    "Returns undefined if not found",
                    "Stops after first match",
                  ],
                  syntax: "array.find(callback)",
                  code: "const user = users.find(u => u.id === 1);",
                  bestUse: "Finding one specific item",
                },
                {
                  title: "filter vs find",
                  description: "They solve different problems.",
                  comparison: [
                    "filter → many results (array)",
                    "find → first result (single item)",
                  ],
                  ruleOfThumb: "Need many → filter | Need one → find",
                },
                {
                  title: "findIndex() Method",
                  description:
                    "findIndex returns the index of the first matching element.",
                  keyPoints: [
                    "Returns index number",
                    "Returns -1 if not found",
                  ],
                  code: "const index = users.findIndex(u => u.id === 1);",
                  useCase: "Updating or deleting specific items",
                },
                {
                  title: "includes() Method",
                  description: "includes checks if a value exists in an array.",
                  keyPoints: [
                    "Returns true or false",
                    "Best for primitive values",
                  ],
                  code: "['admin', 'user'].includes(role);",
                  bestUse: "Simple existence checks",
                },
                {
                  title: "indexOf() Method",
                  description: "indexOf returns the index of a value.",
                  keyPoints: [
                    "Returns -1 if not found",
                    "Works best with primitives",
                  ],
                  code: "const idx = items.indexOf('apple');",
                  note: "Less flexible than findIndex",
                },
                {
                  title: "Real-World Examples",
                  description: "Search methods power UI features.",
                  examples: [
                    "Search bars",
                    "Task filters",
                    "User management panels",
                  ],
                  code: "products.filter(p => p.price < 1000);",
                },
              ],

              visualStructure: `
Search Tools:
filter → many results
find → first match
findIndex → position
includes → yes/no
indexOf → position (simple)
    `,

              commonMistakes: [
                {
                  mistake: "Using filter when only one item is needed",
                  consequence: "Unnecessary array creation",
                  fix: "Use find instead",
                },
                {
                  mistake: "Forgetting filter returns array",
                  consequence: "Logic bugs",
                  fix: "Handle array result correctly",
                },
                {
                  mistake: "Using includes on objects",
                  consequence: "Always false",
                  fix: "Use find or some",
                },
              ],

              proTips: [
                "filter and map are core frontend tools",
                "Prefer find for unique items",
                "Always handle not-found cases",
                "Clean search logic improves UX",
              ],

              realWorldAnalogy:
                "Searching arrays is like searching files:\n- filter = search all matches\n- find = open first match\n- includes = file exists or not",
            },

            tasks: [
              "Filter completed tasks",
              "Find user by id",
              "Search products",
            ],

            examples: [
              "const active = tasks.filter(t => !t.done);",
              "const user = users.find(u => u.id === 1);",
            ],

            useCases: [
              "Search bars",
              "Filtering dashboards",
              "User management",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Search arrays efficiently",
              "Use filter, find, and includes correctly",
              "Choose correct method per use case",
              "Build real-world search logic",
            ],
          },
          {
            day: 5,
            topic: "Reduce, Sort & Reverse",
            subTopics: [
              "reduce() basics",
              "Accumulating values",
              "sort() method",
              "reverse() method",
              "Sorting numbers vs strings",
            ],
            explanation:
              "Advanced methods help calculate totals and reorder data.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Reduce Mental Model Challenge",
                description:
                  "Explain reduce without using code first, then implement it.",
                scenarios: [
                  "Calculate total cart value",
                  "Count frequency of items",
                  "Convert array into object summary",
                ],
                requirements: [
                  "Explain accumulator and current value in words",
                  "Write reduce logic with initial value",
                  "Handle empty array safely",
                ],
                outcome:
                  "You’ll stop fearing reduce and start using it confidently.",
              },
              {
                id: "pt-2",
                title: "Sort Mutation Trap",
                description:
                  "Identify and fix bugs caused by unintended array mutation.",
                scenarios: [
                  "Sorting original product list",
                  "Breaking UI order after sort",
                ],
                requirements: [
                  "Demonstrate how sort mutates array",
                  "Fix bug using array cloning",
                ],
                outcome: "You’ll never break app state using sort again.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Aggregation and sorting bugs silently corrupt UI and analytics data.",
              realWorldChecks: [
                "Is my original data getting mutated?",
                "What happens with empty arrays?",
                "Is reduce returning the expected type?",
              ],
              example:
                "const total = prices.reduce((sum, p) => sum + p, 0);\n// Always check initial value",
            },

            interviewInsights: {
              commonQuestions: [
                "Explain reduce in simple terms",
                "Why is initial value important in reduce?",
                "Why does sort behave strangely with numbers?",
                "Does sort mutate the array?",
              ],
              interviewerTip:
                "Interviewers care more about reduce thinking than syntax.",
              redFlag: "Using sort without knowing it mutates the array.",
            },

            editorGuidance: {
              mentalChecklist: [
                "What should reduce finally return?",
                "Do I need a number, object, or array?",
                "Am I mutating original data?",
                "Should I clone before sorting?",
              ],
              refactorRule:
                "If logic feels complex → break reduce into readable steps first.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern applications don’t just display data — they calculate totals, generate analytics, and reorder information dynamically. Methods like reduce, sort, and reverse allow you to transform raw data into meaningful results.",

              sections: [
                {
                  title: "Why reduce() Is Important",
                  description:
                    "reduce is used when you want a single result from an array.",
                  keyPoints: [
                    "Returns one value",
                    "Can return number, object, or array",
                    "Very powerful but often misunderstood",
                  ],
                  analogy:
                    "Like summarizing a long report into one final conclusion",
                },
                {
                  title: "reduce() Basics",
                  description:
                    "reduce iterates over an array and accumulates a result.",
                  syntax:
                    "array.reduce((accumulator, current) => newAccumulator, initialValue)",
                  code: "const nums = [1, 2, 3];\nconst sum = nums.reduce((acc, n) => acc + n, 0);",
                  takeaway: "Accumulator holds the running result",
                },
                {
                  title: "Accumulating Values",
                  description: "reduce is commonly used for totals and counts.",
                  examples: ["Cart total", "Sum of marks", "Counting items"],
                  code: "const prices = [100, 200, 300];\nconst total = prices.reduce((sum, p) => sum + p, 0);",
                  bestPractice: "Always provide an initial value",
                },
                {
                  title: "reduce Beyond Numbers",
                  description: "reduce can return objects or arrays.",
                  example: "Grouping data by category",
                  code: "const items = ['a', 'b', 'a'];\nconst count = items.reduce((acc, i) => {\n  acc[i] = (acc[i] || 0) + 1;\n  return acc;\n}, {});",
                  note: "This is used heavily in analytics",
                },
                {
                  title: "sort() Method",
                  description: "sort reorders array elements.",
                  keyPoints: [
                    "Mutates the original array",
                    "Sorts values as strings by default",
                  ],
                  badExample: "[10, 2, 5].sort(); // ['10', '2', '5'] ❌",
                  warning: "Default sorting can be misleading",
                },
                {
                  title: "Sorting Numbers Correctly",
                  description: "Numeric sorting requires a compare function.",
                  code: "items.sort((a, b) => a - b); // ascending\nitems.sort((a, b) => b - a); // descending",
                  takeaway: "Always use compare function for numbers",
                },
                {
                  title: "Sorting Strings",
                  description:
                    "String sorting works by default but can be customized.",
                  code: "names.sort();\nnames.sort((a, b) => a.localeCompare(b));",
                  useCase: "Alphabetical lists",
                },
                {
                  title: "reverse() Method",
                  description: "reverse flips the array order.",
                  keyPoints: [
                    "Mutates original array",
                    "Often used after sort",
                  ],
                  code: "items.reverse();",
                  useCase: "Latest-first UI display",
                },
                {
                  title: "Real-World Examples",
                  description: "These methods power real features.",
                  examples: [
                    "Shopping cart total",
                    "Leaderboard ranking",
                    "Recent activity list",
                  ],
                },
              ],

              visualStructure: `
Advanced Array Flow:
Array → reduce → single value
Array → sort → ordered array
Array → reverse → flipped order
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting initial value in reduce",
                  consequence: "Unexpected results",
                  fix: "Always pass initial value",
                },
                {
                  mistake: "Using sort without compare function",
                  consequence: "Wrong numeric order",
                  fix: "Provide (a, b) => a - b",
                },
                {
                  mistake: "Mutating original data unknowingly",
                  consequence: "State bugs",
                  fix: "Clone array before sorting if needed",
                },
              ],

              proTips: [
                "reduce can replace many loops",
                "sort & reverse mutate arrays — be careful",
                "reduce is frequently asked in interviews",
                "Master reduce → master data logic",
              ],

              realWorldAnalogy:
                "Advanced array methods are like data tools:\n- reduce = calculator\n- sort = organizer\n- reverse = reorder switch",
            },

            tasks: [
              "Calculate cart total",
              "Sort products by price",
              "Reverse task order",
            ],

            examples: [
              "const total = prices.reduce((sum, p) => sum + p, 0);",
              "items.sort((a,b) => a-b);",
            ],

            useCases: [
              "Shopping carts",
              "Analytics dashboards",
              "Leaderboards",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use reduce for data aggregation",
              "Sort arrays correctly",
              "Understand mutating behavior",
              "Apply advanced array logic in real apps",
            ],
          },
          {
            day: 6,
            topic: "Advanced Array Techniques",
            subTopics: [
              "Array destructuring",
              "Spread operator",
              "Multi-dimensional arrays",
              "Chaining methods",
              "Immutability patterns",
            ],
            explanation:
              "Modern JavaScript relies on clean and immutable array operations.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Immutability Refactor Challenge",
                description:
                  "Refactor mutating array logic into immutable patterns.",
                scenarios: [
                  "Replace push with spread",
                  "Replace splice with filter/map",
                  "Fix UI state mutation bug",
                ],
                requirements: [
                  "Do not mutate original array",
                  "Return a new array every time",
                  "Explain why immutability matters",
                ],
                outcome: "You’ll write React-safe, bug-free array logic.",
              },
              {
                id: "pt-2",
                title: "Chaining Like a Pro",
                description:
                  "Convert step-by-step array logic into clean method chaining.",
                before: "Loop → condition → push → temp array",
                afterGoal: "filter → map → reduce pipeline",
                requirements: [
                  "Use correct order of methods",
                  "Keep callbacks readable",
                ],
                outcome: "You’ll think in data pipelines instead of loops.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Mutation and shallow copies cause silent bugs in modern frontend frameworks.",
              realWorldChecks: [
                "Am I modifying the original array?",
                "Is spread enough or do I have nested objects?",
                "Is chaining readable or too complex?",
              ],
              example:
                "const updated = items.map(i =>\n  i.id === id ? { ...i, active: true } : i\n);",
            },

            interviewInsights: {
              commonQuestions: [
                "What is array destructuring?",
                "Difference between spread and slice?",
                "What is immutability and why does React care?",
                "Explain method chaining with example",
              ],
              interviewerTip:
                "Interviewers look for immutability mindset, not syntax tricks.",
              redFlag: "Mutating arrays directly inside state updates.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Am I returning a new array?",
                "Is spread creating shallow or deep copy?",
                "Can this chain be simplified?",
                "Would this break React state?",
              ],
              refactorRule:
                "If mutation sneaks in → stop and rewrite immutably.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications grow, simple push/pop logic is no longer enough. Modern JavaScript emphasizes immutability, clean data transformations, and readable pipelines. These advanced array techniques are essential for frameworks like React and for writing bug-free scalable code.",

              sections: [
                {
                  title: "Array Destructuring",
                  description:
                    "Destructuring allows extracting values from arrays into variables easily.",
                  keyPoints: [
                    "Order matters",
                    "Improves readability",
                    "Avoids manual indexing",
                  ],
                  code: "const colors = ['red', 'green', 'blue'];\nconst [first, second] = colors;",
                  useCase: "Extracting values cleanly",
                  analogy: "Unpacking items from a box",
                },
                {
                  title: "Spread Operator (...) with Arrays",
                  description:
                    "The spread operator copies elements into a new array.",
                  keyPoints: [
                    "Creates shallow copy",
                    "Does NOT mutate original array",
                    "Widely used in state updates",
                  ],
                  code: "const oldArr = ['a', 'b'];\nconst newArr = [...oldArr, 'c'];",
                  takeaway: "Spread is the safest way to clone arrays",
                },
                {
                  title: "Why Immutability Matters",
                  description:
                    "Mutating data directly can cause bugs in modern apps.",
                  problemsWithMutation: [
                    "Unexpected UI behavior",
                    "State bugs in React",
                    "Hard-to-track changes",
                  ],
                  bestPractice:
                    "Always return new arrays instead of modifying old ones",
                  analogy: "Never overwrite original documents — create a copy",
                },
                {
                  title: "Multi-Dimensional Arrays",
                  description: "Arrays can contain other arrays.",
                  keyPoints: [
                    "Used for grids and tables",
                    "Access using multiple indexes",
                  ],
                  code: "const matrix = [[1,2],[3,4]];\nconsole.log(matrix[0][1]); // 2",
                  useCase: "Game boards, table layouts",
                },
                {
                  title: "Iterating Multi-Dimensional Arrays",
                  description: "Nested loops are commonly used.",
                  code: "for (let row of matrix) {\n  for (let value of row) {\n    console.log(value);\n  }\n}",
                  warning: "Avoid deep nesting when possible",
                },
                {
                  title: "Chaining Array Methods",
                  description:
                    "Chaining makes transformations readable and powerful.",
                  keyPoints: [
                    "map, filter, reduce work well together",
                    "Order of chaining matters",
                  ],
                  code: "const result = items\n  .filter(x => x.active)\n  .map(x => x.name);",
                  takeaway: "Think in data pipelines",
                },
                {
                  title: "Immutability Patterns",
                  description: "Common patterns used in real apps.",
                  patterns: [
                    "Add item → [...arr, item]",
                    "Remove item → filter",
                    "Update item → map",
                  ],
                  code: "const updated = items.map(i =>\n  i.id === 1 ? { ...i, active: true } : i\n);",
                  note: "This pattern is used heavily in React",
                },
                {
                  title: "Real-World Examples",
                  description: "Advanced array techniques power modern UI.",
                  examples: [
                    "React state updates",
                    "Data transformation pipelines",
                    "Complex UI filtering",
                  ],
                },
              ],

              visualStructure: `
Modern Array Flow:
Original Data
   ↓
filter → map → reduce
   ↓
New Transformed Data
    `,

              commonMistakes: [
                {
                  mistake: "Mutating arrays directly",
                  consequence: "State bugs",
                  fix: "Use spread or non-mutating methods",
                },
                {
                  mistake: "Overcomplicating chains",
                  consequence: "Hard-to-read code",
                  fix: "Break into steps when needed",
                },
                {
                  mistake: "Assuming deep copy with spread",
                  consequence: "Nested mutation bugs",
                  fix: "Understand shallow copy behavior",
                },
              ],

              proTips: [
                "Immutability is a mindset, not a rule",
                "Spread operator is your best friend",
                "Method chaining improves readability",
                "This skill directly maps to React",
              ],

              realWorldAnalogy:
                "Advanced array techniques are like assembly lines:\n- Each step transforms data\n- Original stays untouched\n- Final product is predictable",
            },

            tasks: [
              "Clone arrays safely",
              "Chain map + filter",
              "Work with nested arrays",
            ],

            examples: [
              "const newArr = [...oldArr, 'new'];",
              "const result = items.filter(x => x.active).map(x => x.name);",
            ],

            useCases: [
              "React state updates",
              "Complex data pipelines",
              "Modern frontend architecture",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use destructuring and spread confidently",
              "Write immutable array logic",
              "Work with nested arrays",
              "Build clean data transformation pipelines",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Task Manager App",
            subTopics: [
              "Array CRUD operations",
              "Filtering tasks",
              "State management logic",
              "Data rendering",
              "Project structuring",
            ],
            explanation:
              "This project uses arrays heavily to manage tasks and state.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Immutable CRUD Challenge",
                description:
                  "Rebuild all task operations using pure immutable logic.",
                scenarios: [
                  "Add task without mutating array",
                  "Delete task safely by id",
                  "Toggle task completion immutably",
                ],
                requirements: [
                  "No push, splice, or direct mutation",
                  "Use spread, map, and filter only",
                  "Return new array for every operation",
                ],
                outcome:
                  "You’ll build React-ready, production-safe state logic.",
              },
              {
                id: "pt-2",
                title: "State → UI Derivation Test",
                description: "Prove that UI can be derived only from state.",
                scenarios: [
                  "Generate active task list",
                  "Generate completed task list",
                  "Show task count dynamically",
                ],
                requirements: [
                  "Do not store derived data separately",
                  "Use filter/map for every view",
                ],
                outcome: "You’ll understand true state-driven UI architecture.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "State bugs are the #1 reason UI behaves unpredictably in real apps.",
              realWorldChecks: [
                "Am I mutating the tasks array?",
                "Is UI derived from state or stored separately?",
                "Will this logic break when tasks grow?",
              ],
              example:
                "const completed = tasks.filter(t => t.completed);\n// derived, not stored",
            },

            interviewInsights: {
              commonQuestions: [
                "How would you implement a todo app?",
                "Why use map for update and filter for delete?",
                "What does immutability mean in state management?",
                "How does this logic translate to React?",
              ],
              interviewerTip:
                "Interviewers look for state thinking, not UI styling.",
              redFlag: "Mutating task array directly inside logic.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Is tasks array my single source of truth?",
                "Am I returning a new array every time?",
                "Is this logic reusable and testable?",
                "Would this break if tasks = 1000?",
              ],
              refactorRule: "If logic mixes UI + data → split immediately.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A Task Manager (Todo App) is one of the most important beginner-to-intermediate JavaScript projects. It closely mirrors real-world application logic: adding data, updating state, filtering views, and keeping everything predictable using arrays.",

              sections: [
                {
                  title: "Project Goal",
                  description:
                    "Build a task manager that allows users to add, delete, complete, and filter tasks.",
                  features: [
                    "Add new tasks",
                    "Mark tasks as completed",
                    "Delete tasks",
                    "Filter by status",
                  ],
                  skillsUsed: [
                    "Arrays",
                    "map / filter",
                    "Immutability patterns",
                    "Functions",
                  ],
                  outcome: "A fully working task logic engine",
                },
                {
                  title: "Task Data Structure",
                  description: "Each task should be represented as an object.",
                  keyPoints: [
                    "Use unique id",
                    "Track completion state",
                    "Store task text",
                  ],
                  code: "const tasks = [\n  { id: 1, text: 'Learn JS', completed: false }\n];",
                  takeaway: "Good structure simplifies all operations",
                },
                {
                  title: "Adding New Tasks (Create)",
                  description:
                    "New tasks should not mutate the original array.",
                  logic: "Create new task → append using spread",
                  code: "tasks = [...tasks, { id: Date.now(), text, completed: false }];",
                  bestPractice: "Always return a new array",
                },
                {
                  title: "Deleting Tasks (Delete)",
                  description: "Use filter to remove a task by id.",
                  keyPoints: [
                    "filter returns new array",
                    "Perfect for delete operations",
                  ],
                  code: "tasks = tasks.filter(t => t.id !== id);",
                  analogy:
                    "Removing one item from a list without touching others",
                },
                {
                  title: "Marking Tasks Complete (Update)",
                  description: "Use map to update a specific task.",
                  logic: "Check id → return updated object",
                  code: "tasks = tasks.map(t =>\n  t.id === id ? { ...t, completed: !t.completed } : t\n);",
                  takeaway: "map is used for updates",
                },
                {
                  title: "Filtering Tasks (Read)",
                  description: "Different views are created using filter.",
                  filters: ["All tasks", "Active tasks", "Completed tasks"],
                  code: "const active = tasks.filter(t => !t.completed);\nconst done = tasks.filter(t => t.completed);",
                  UXTip: "Filtering does NOT change original tasks",
                },
                {
                  title: "State Management Logic",
                  description: "State is just data + rules.",
                  keyPoints: [
                    "tasks array = single source of truth",
                    "UI is derived from state",
                  ],
                  rule: "Never mutate state directly",
                },
                {
                  title: "Rendering Tasks (Conceptual)",
                  description: "Rendering means converting data into UI.",
                  logic: "Use map to generate UI items",
                  example: "tasks.map(t => `<li>${t.text}</li>`)",
                  note: "Exact rendering depends on HTML/React",
                },
                {
                  title: "Project Structuring",
                  description: "Keep logic clean and readable.",
                  suggestedFunctions: [
                    "addTask()",
                    "deleteTask()",
                    "toggleTask()",
                    "getFilteredTasks()",
                  ],
                  benefit: "Easier debugging and extension",
                },
                {
                  title: "Optional Enhancements",
                  description: "Extra features for practice.",
                  ideas: [
                    "Task counter",
                    "Clear completed tasks",
                    "Persist tasks in localStorage",
                  ],
                  note: "Enhancements are optional but valuable",
                },
              ],

              visualStructure: `
Task Manager Flow:
Add → Store in array
Update → map
Delete → filter
View → filter + map
    `,

              commonMistakes: [
                {
                  mistake: "Mutating tasks array directly",
                  consequence: "Unpredictable state",
                  fix: "Use spread, map, filter",
                },
                {
                  mistake: "Mixing UI and logic",
                  consequence: "Hard-to-maintain code",
                  fix: "Separate data logic",
                },
                {
                  mistake: "No unique id",
                  consequence: "Wrong updates/deletes",
                  fix: "Always use unique identifiers",
                },
              ],

              proTips: [
                "This project mirrors React state logic",
                "filter = delete, map = update",
                "Immutability prevents bugs",
                "Interviewers love this project",
              ],

              realWorldAnalogy:
                "A task manager is like a notebook:\n- You add tasks\n- Cross them out\n- Tear pages cleanly\n- Never rewrite the whole book",
            },

            tasks: [
              "Add new tasks",
              "Delete tasks",
              "Mark tasks complete",
              "Filter tasks (all / active / completed)",
            ],

            examples: ["tasks = tasks.filter(t => t.id !== id);"],

            useCases: [
              "Real-world productivity apps",
              "Interview-level JS project",
              "Foundation for React apps",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "2–3 hours",

            // 🎯 Learning objectives
            objectives: [
              "Apply array CRUD operations",
              "Manage state using immutable patterns",
              "Build real-world task logic",
              "Gain confidence with JavaScript arrays",
            ],
          },
        ],
      },
      {
        week: 10,
        weekGoal:
          "Master objects, structured data handling, and real-world data modeling",
        weekOutcome:
          "Ability to work confidently with complex objects and JSON-based data",
        canLearn: ["students", "beginners", "professionals"],
        miniProject: "Recipe Book Application",
        days: [
          {
            day: 1,
            topic: "Object Basics & Properties",
            subTopics: [
              "Creating objects",
              "Key-value pairs",
              "Accessing properties (dot vs bracket)",
              "Updating and deleting properties",
              "Object reference behavior",
            ],
            explanation:
              "Objects are used to represent real-world entities with properties and values.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Immutability Refactor Challenge",
                description:
                  "Refactor mutating array logic into immutable patterns.",
                scenarios: [
                  "Replace push with spread",
                  "Replace splice with filter/map",
                  "Fix UI state mutation bug",
                ],
                requirements: [
                  "Do not mutate original array",
                  "Return a new array every time",
                  "Explain why immutability matters",
                ],
                outcome: "You’ll write React-safe, bug-free array logic.",
              },
              {
                id: "pt-2",
                title: "Chaining Like a Pro",
                description:
                  "Convert step-by-step array logic into clean method chaining.",
                before: "Loop → condition → push → temp array",
                afterGoal: "filter → map → reduce pipeline",
                requirements: [
                  "Use correct order of methods",
                  "Keep callbacks readable",
                ],
                outcome: "You’ll think in data pipelines instead of loops.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Mutation and shallow copies cause silent bugs in modern frontend frameworks.",
              realWorldChecks: [
                "Am I modifying the original array?",
                "Is spread enough or do I have nested objects?",
                "Is chaining readable or too complex?",
              ],
              example:
                "const updated = items.map(i =>\n  i.id === id ? { ...i, active: true } : i\n);",
            },

            interviewInsights: {
              commonQuestions: [
                "What is array destructuring?",
                "Difference between spread and slice?",
                "What is immutability and why does React care?",
                "Explain method chaining with example",
              ],
              interviewerTip:
                "Interviewers look for immutability mindset, not syntax tricks.",
              redFlag: "Mutating arrays directly inside state updates.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Am I returning a new array?",
                "Is spread creating shallow or deep copy?",
                "Can this chain be simplified?",
                "Would this break React state?",
              ],
              refactorRule:
                "If mutation sneaks in → stop and rewrite immutably.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "While arrays handle lists, objects represent entities — users, products, settings, responses from APIs. Understanding objects is critical because almost all real application data is object-based.",

              sections: [
                {
                  title: "What Is an Object?",
                  description:
                    "An object groups related data using key-value pairs.",
                  keyPoints: [
                    "Keys are strings (or symbols)",
                    "Values can be any data type",
                    "Represents real-world entities",
                  ],
                  code: "const user = { name: 'John', age: 25 };",
                  analogy: "Like a dictionary where each word has a meaning",
                },
                {
                  title: "Creating Objects",
                  description: "Objects are created using curly braces {}.",
                  examples: [
                    "{ name: 'Laptop', price: 50000 }",
                    "{ id: 1, isActive: true }",
                  ],
                  bestPractice: "Use const for objects (mutation is allowed)",
                },
                {
                  title: "Key-Value Pairs",
                  description: "Each property consists of a key and a value.",
                  keyPoints: [
                    "Keys describe the property",
                    "Values hold actual data",
                  ],
                  code: "const product = {\n  name: 'Phone',\n  price: 30000,\n  inStock: true\n};",
                  takeaway: "Clear keys make objects readable",
                },
                {
                  title: "Accessing Properties",
                  description: "Properties can be accessed in two ways.",
                  methods: ["Dot notation", "Bracket notation"],
                  code: "user.name;        // dot\nuser['age'];       // bracket",
                  ruleOfThumb:
                    "Use dot by default, bracket when key is dynamic",
                },
                {
                  title: "Dot vs Bracket Notation",
                  description: "Each access method has its use case.",
                  comparison: [
                    "Dot → static keys",
                    "Bracket → dynamic keys or special characters",
                  ],
                  example: "const key = 'name';\nuser[key];",
                  warning: "Dot notation fails with variables",
                },
                {
                  title: "Updating Properties",
                  description: "Object properties can be updated anytime.",
                  keyPoints: [
                    "Objects are mutable",
                    "const does not prevent mutation",
                  ],
                  code: "user.age = 26;",
                  useCase: "Updating user profile data",
                },
                {
                  title: "Adding New Properties",
                  description: "New properties can be added dynamically.",
                  code: "user.email = 'john@example.com';",
                  takeaway: "Objects grow dynamically",
                },
                {
                  title: "Deleting Properties",
                  description: "Properties can be removed using delete.",
                  code: "delete user.email;",
                  warning:
                    "Avoid frequent deletes in performance-critical code",
                },
                {
                  title: "Object Reference Behavior",
                  description: "Objects are stored by reference, not value.",
                  keyPoints: [
                    "Assigning copies the reference",
                    "Changes affect all references",
                  ],
                  code: "const a = { x: 1 };\nconst b = a;\nb.x = 2;\n// a.x is also 2",
                  analogy: "Two remotes controlling the same TV",
                },
                {
                  title: "Why Reference Matters",
                  description:
                    "Reference behavior can cause bugs if misunderstood.",
                  problems: ["Unexpected mutations", "State bugs in apps"],
                  preview: "Immutability patterns fix this (covered later)",
                },
              ],

              visualStructure: `
Object Structure:
Key → name     age
Value → John    25
    `,

              commonMistakes: [
                {
                  mistake: "Using dot notation with dynamic keys",
                  consequence: "undefined access",
                  fix: "Use bracket notation",
                },
                {
                  mistake: "Assuming object copies are independent",
                  consequence: "Unexpected data changes",
                  fix: "Understand reference behavior",
                },
                {
                  mistake: "Overusing delete",
                  consequence: "Performance issues",
                  fix: "Prefer immutability patterns",
                },
              ],

              proTips: [
                "Objects model real-world data",
                "Clear property names matter",
                "Understand reference vs value early",
                "This concept is critical for React & APIs",
              ],

              realWorldAnalogy:
                "Objects are like ID cards:\n- Fields describe a person\n- Updating the card updates the person’s data",
            },

            tasks: [
              "Create user and product objects",
              "Access and update object properties",
              "Delete unused properties",
            ],

            examples: [
              "const user = { name: 'John', age: 25 };",
              "user.age = 26;",
            ],

            useCases: [
              "User profiles",
              "Product data modeling",
              "API response handling",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand object fundamentals",
              "Access and modify object properties",
              "Understand reference behavior",
              "Model real-world data using objects",
            ],
          },
          {
            day: 2,
            topic: "Object Methods & 'this' Keyword",
            subTopics: [
              "Methods inside objects",
              "'this' keyword",
              "Arrow functions vs normal functions",
              "Method binding basics",
            ],
            explanation:
              "Object methods allow behavior to be attached to data using the 'this' keyword.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "`this` Context Prediction Drill",
                description:
                  "Predict what `this` refers to in different calling scenarios.",
                scenarios: [
                  "Method called directly on object",
                  "Method assigned to variable and called",
                  "Method used inside setTimeout",
                ],
                requirements: [
                  "Predict output before running code",
                  "Explain why `this` changes",
                  "Fix broken cases using bind",
                ],
                outcome: "You’ll never be confused by `this` again.",
              },
              {
                id: "pt-2",
                title: "Arrow Function Trap",
                description:
                  "Identify and fix bugs caused by arrow functions inside objects.",
                scenarios: [
                  "Arrow function used as object method",
                  "Arrow function inside normal method",
                  "Arrow function used as callback correctly",
                ],
                requirements: [
                  "Replace arrow functions where needed",
                  "Justify when arrow is correct vs wrong",
                ],
                outcome: "Clear mental model of arrow vs normal functions.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "`this` bugs are logic bugs — tests help catch silent failures.",
              realWorldChecks: [
                "Is this method ever passed as callback?",
                "Does `this` still point to expected object?",
                "Should this function be bound?",
              ],
              example:
                "const user = { name: 'A', greet() { return this.name; } };\nconst fn = user.greet;\nfn(); // undefined",
            },

            interviewInsights: {
              commonQuestions: [
                "What decides the value of `this`?",
                "Why arrow functions should not be used as methods?",
                "What happens when you pass a method as a callback?",
                "Difference between arrow and normal function `this`?",
              ],
              interviewerTip:
                "Explain `this` using call examples, not definitions.",
              redFlag:
                "Saying 'this always refers to the object' is incorrect.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Who is calling this function?",
                "Is this a method or a callback?",
                "Should I use arrow or normal function?",
                "Will this function be passed around?",
              ],
              refactorRule:
                "If function uses `this` → never use arrow function.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Objects don’t just store data — they also define behavior. When functions live inside objects, they become methods. The 'this' keyword allows methods to access the object’s own data, making objects powerful and self-contained.",

              sections: [
                {
                  title: "What Is a Method?",
                  description:
                    "A method is a function stored as an object property.",
                  keyPoints: [
                    "Methods define behavior",
                    "They operate on object data",
                    "Access properties using this",
                  ],
                  code: "const user = {\n  name: 'Alex',\n  greet() {\n    return `Hello ${this.name}`;\n  }\n};",
                  analogy: "Data + behavior packaged together",
                },
                {
                  title: "Understanding the 'this' Keyword",
                  description:
                    "'this' refers to the object that is calling the method.",
                  keyPoints: [
                    "Value of this depends on how function is called",
                    "In object methods, this usually refers to the object",
                  ],
                  code: "user.greet(); // this === user",
                  takeaway:
                    "this is determined at call time, not definition time",
                },
                {
                  title: "Why 'this' Exists",
                  description: "'this' makes methods reusable across objects.",
                  example: "Two users can share same method logic",
                  code: "function greet() { return this.name; }",
                  benefit: "Avoids hardcoding object names",
                },
                {
                  title: "Arrow Functions vs Normal Functions",
                  description: "Arrow functions handle 'this' differently.",
                  comparison: [
                    "Normal function → this is dynamic",
                    "Arrow function → this is lexical (inherited)",
                  ],
                  code: "const user = {\n  name: 'Alex',\n  greet: () => this.name // ❌\n};",
                  warning:
                    "Arrow functions should NOT be used as object methods",
                },
                {
                  title: "Correct Method Definition",
                  description: "Use normal functions for object methods.",
                  code: "const user = {\n  name: 'Alex',\n  greet() {\n    return this.name;\n  }\n};",
                  ruleOfThumb:
                    "Method → normal function | Callback → arrow function",
                },
                {
                  title: "Method Binding Basics",
                  description:
                    "When methods are passed around, this can be lost.",
                  example: "const greet = user.greet; greet(); // undefined",
                  solutionPreview: [
                    "bind()",
                    "call()",
                    "apply() (covered later)",
                  ],
                  takeaway: "Context matters when calling methods",
                },
                {
                  title: "Real-World Examples",
                  description: "this is everywhere in real apps.",
                  examples: [
                    "User actions",
                    "Component logic",
                    "Class methods (later)",
                  ],
                  code: "product.calculatePrice()",
                },
              ],

              visualStructure: `
Object Method Flow:
Object → method call
        ↓
      this → object reference
    `,

              commonMistakes: [
                {
                  mistake: "Using arrow functions as methods",
                  consequence: "this becomes undefined",
                  fix: "Use normal function syntax",
                },
                {
                  mistake: "Assuming this refers to object always",
                  consequence: "Unexpected bugs",
                  fix: "Check how function is called",
                },
                {
                  mistake: "Losing this when passing methods",
                  consequence: "Broken logic",
                  fix: "Bind methods if needed",
                },
              ],

              proTips: [
                "this is decided by call-site",
                "Never use arrow functions for object methods",
                "Think: who is calling this?",
                "Mastering this saves a lot of debugging time",
              ],

              realWorldAnalogy:
                "'this' is like saying 'me' in a conversation:\n- Who 'me' refers to depends on who is speaking",
            },

            tasks: [
              "Create objects with methods",
              "Understand how 'this' works",
              "Compare arrow vs normal functions",
            ],

            examples: [
              "const user = { name: 'Alex', greet() { return this.name; } };",
            ],

            useCases: [
              "Business logic in objects",
              "Encapsulating behavior",
              "Preparing for classes & frameworks",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Create and use object methods",
              "Understand the 'this' keyword clearly",
              "Choose correct function type",
              "Avoid common this-related bugs",
            ],
          },
          {
            day: 3,
            topic: "Object Destructuring & Spread Operator",
            subTopics: [
              "Destructuring objects",
              "Renaming variables",
              "Default values",
              "Spread operator with objects",
              "Immutable updates",
            ],
            explanation:
              "Destructuring and spread syntax make object handling clean and immutable.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Destructuring Debug Challenge",
                description:
                  "Predict values extracted using object destructuring in different scenarios.",
                scenarios: [
                  "Missing property with no default",
                  "Renamed variables",
                  "Nested destructuring",
                  "Default values applied",
                ],
                requirements: [
                  "Predict output before execution",
                  "Identify undefined cases",
                  "Fix using defaults or correct keys",
                ],
                outcome: "You’ll confidently destructure any object structure.",
              },
              {
                id: "pt-2",
                title: "Immutable Update Refactor",
                description:
                  "Refactor mutating object updates into immutable patterns using spread.",
                scenarios: [
                  "Direct property mutation",
                  "Nested object update",
                  "State-like object update",
                ],
                requirements: [
                  "Remove direct mutations",
                  "Use spread operator correctly",
                  "Ensure original object stays unchanged",
                ],
                outcome:
                  "Bug-free immutable object updates like a React developer.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Mutation bugs are silent — tests catch reference-related issues early.",
              realWorldChecks: [
                "Is original object unchanged?",
                "Are nested objects copied correctly?",
                "Did destructuring pull correct keys?",
              ],
              example:
                "const user = { name: 'A', profile: { city: 'Delhi' } };\nconst updated = { ...user, profile: { city: 'Mumbai' } };\n// user.profile.city should still be 'Delhi'",
            },

            interviewInsights: {
              commonQuestions: [
                "What is object destructuring?",
                "How do you rename variables during destructuring?",
                "Does spread create deep copy?",
                "How do you update nested objects immutably?",
              ],
              interviewerTip:
                "Use real code examples instead of theory-heavy explanations.",
              redFlag: "Saying spread operator creates deep copy is incorrect.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Am I mutating the original object?",
                "Do I need default values?",
                "Is this a nested update?",
                "Will this code be used in state management?",
              ],
              refactorRule:
                "If object is updated → always return a new object using spread.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern JavaScript focuses on readability, immutability, and clarity. Object destructuring and the spread operator reduce boilerplate, prevent bugs, and are used heavily in frameworks like React.",

              sections: [
                {
                  title: "What Is Object Destructuring?",
                  description:
                    "Destructuring allows extracting object properties into variables.",
                  keyPoints: [
                    "Avoids repetitive object.property access",
                    "Improves readability",
                    "Works based on property names",
                  ],
                  code: "const user = { name: 'Alex', age: 25 };\nconst { name, age } = user;",
                  analogy: "Unpacking values from a bag by label",
                },
                {
                  title: "Renaming Variables",
                  description: "You can rename properties while destructuring.",
                  keyPoints: [
                    "Useful to avoid naming conflicts",
                    "Common in API responses",
                  ],
                  code: "const { name: userName, age: userAge } = user;",
                  useCase: "Cleaner variable naming",
                },
                {
                  title: "Default Values in Destructuring",
                  description: "Defaults prevent undefined values.",
                  keyPoints: [
                    "Applied when property is missing",
                    "Does not override existing values",
                  ],
                  code: "const { role = 'user' } = user;",
                  takeaway: "Safer data extraction",
                },
                {
                  title: "Nested Object Destructuring",
                  description: "You can destructure nested objects.",
                  code: "const user = { profile: { city: 'Delhi' } };\nconst { profile: { city } } = user;",
                  warning: "Be careful with deeply nested structures",
                },
                {
                  title: "Spread Operator with Objects",
                  description: "Spread copies properties into a new object.",
                  keyPoints: [
                    "Creates shallow copy",
                    "Does NOT mutate original object",
                  ],
                  code: "const copy = { ...user };",
                  analogy: "Photocopying a document",
                },
                {
                  title: "Immutable Object Updates",
                  description: "Spread is used to update objects immutably.",
                  keyPoints: [
                    "Original object remains unchanged",
                    "New object contains updated fields",
                  ],
                  code: "const updated = { ...user, age: 30 };",
                  bestPractice: "Always use spread for state updates",
                },
                {
                  title: "Updating Nested Objects (Conceptual)",
                  description: "Nested updates require multiple spreads.",
                  code: "const updatedUser = {\n  ...user,\n  profile: { ...user.profile, city: 'Mumbai' }\n};",
                  note: "Very common in React state logic",
                },
                {
                  title: "Real-World Usage",
                  description: "Destructuring and spread are everywhere.",
                  examples: ["React props", "API responses", "State updates"],
                  code: "function Profile({ name, age }) {}",
                },
              ],

              visualStructure: `
Object Handling Flow:
Original Object
   ↓
Destructure → use values
   ↓
Spread → create updated copy
    `,

              commonMistakes: [
                {
                  mistake: "Assuming spread makes deep copy",
                  consequence: "Nested mutation bugs",
                  fix: "Spread nested objects explicitly",
                },
                {
                  mistake: "Wrong property names in destructuring",
                  consequence: "undefined values",
                  fix: "Match exact keys",
                },
                {
                  mistake: "Mutating object instead of spreading",
                  consequence: "State bugs",
                  fix: "Always create new object",
                },
              ],

              proTips: [
                "Destructuring improves readability instantly",
                "Spread is the foundation of immutability",
                "This syntax appears heavily in React",
                "Clean object handling = fewer bugs",
              ],

              realWorldAnalogy:
                "Destructuring is picking items by label\nSpread is making a clean copy before editing",
            },

            tasks: [
              "Extract values using destructuring",
              "Clone and update objects immutably",
              "Refactor old code",
            ],

            examples: [
              "const { name, age } = user;",
              "const updated = { ...user, age: 30 };",
            ],

            useCases: [
              "React state updates",
              "API response handling",
              "Clean function parameters",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use object destructuring confidently",
              "Rename and set default values",
              "Apply spread for immutable updates",
              "Write modern, clean JavaScript",
            ],
          },
          {
            day: 4,
            topic: "Object Utilities & Iteration",
            subTopics: [
              "Object.keys()",
              "Object.values()",
              "Object.entries()",
              "Looping through objects",
              "Comparing objects",
            ],
            explanation:
              "Utility methods help iterate and transform object data.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Dynamic Object Rendering Task",
                description:
                  "Convert object data into UI-friendly arrays using Object utilities.",
                scenarios: [
                  "User profile object rendering",
                  "Settings object display",
                  "Key-value list generation",
                ],
                requirements: [
                  "Use Object.entries() for iteration",
                  "Destructure key and value correctly",
                  "Return formatted output",
                ],
                outcome:
                  "You’ll confidently render dynamic object data in real apps.",
              },
              {
                id: "pt-2",
                title: "Shallow Object Comparison Challenge",
                description:
                  "Implement a safe shallow object comparison utility.",
                scenarios: [
                  "Same keys & values",
                  "Missing keys",
                  "Different value types",
                ],
                requirements: [
                  "Reject === comparison",
                  "Compare keys length",
                  "Compare each key-value pair",
                ],
                outcome:
                  "Clear understanding of object comparison limitations.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most UI bugs happen when object data is rendered or compared incorrectly.",
              realWorldChecks: [
                "Are you iterating keys or values intentionally?",
                "Does comparison handle missing keys?",
                "Are inherited properties ignored?",
              ],
              example:
                "const a = { name: 'A', age: 20 };\nconst b = { name: 'A', age: 20 };\n// Shallow comparison should return true",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between Object.keys, values, and entries?",
                "How do you loop through an object?",
                "Why === fails for object comparison?",
                "How would you compare two objects?",
              ],
              interviewerTip:
                "Explain object iteration with a UI or API response example.",
              redFlag: "Saying objects can be compared directly using ===.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Do I need keys, values, or both?",
                "Should I convert object to array first?",
                "Is this comparison shallow or deep?",
                "Will inherited properties cause issues?",
              ],
              refactorRule:
                "If looping an object → convert it using Object.entries first.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Objects often come from APIs, forms, and configuration files. To render them dynamically or transform their data, JavaScript provides powerful utility methods like Object.keys, Object.values, and Object.entries.",

              sections: [
                {
                  title: "Why Object Iteration Matters",
                  description:
                    "Unlike arrays, objects are not directly iterable.",
                  examples: [
                    "Rendering form fields dynamically",
                    "Displaying user details",
                    "Converting object data for UI",
                  ],
                  analogy: "You need a way to list all fields of a form",
                },
                {
                  title: "Object.keys()",
                  description:
                    "Returns an array of an object’s property names.",
                  keyPoints: [
                    "Returns array of strings",
                    "Order follows property insertion",
                  ],
                  syntax: "Object.keys(obj)",
                  code: "const user = { name: 'Alex', age: 25 };\nObject.keys(user); // ['name', 'age']",
                  useCase: "Iterating over property names",
                },
                {
                  title: "Object.values()",
                  description: "Returns an array of an object’s values.",
                  keyPoints: [
                    "Ignores keys",
                    "Useful for value-only operations",
                  ],
                  syntax: "Object.values(obj)",
                  code: "Object.values(user); // ['Alex', 25]",
                  useCase: "Calculations, summaries",
                },
                {
                  title: "Object.entries()",
                  description: "Returns key-value pairs as arrays.",
                  keyPoints: ["Most flexible utility", "Perfect for looping"],
                  syntax: "Object.entries(obj)",
                  code: "Object.entries(user);\n// [['name','Alex'], ['age',25]]",
                  bestPractice: "Use entries when you need both key & value",
                },
                {
                  title: "Looping Through Objects",
                  description: "Objects are usually looped via entries.",
                  code: "Object.entries(user).forEach(([key, value]) => {\n  console.log(key, value);\n});",
                  takeaway: "Convert object → array → iterate",
                },
                {
                  title: "for...in Loop (Brief)",
                  description: "An older way to loop over object properties.",
                  warning: "Includes inherited properties",
                  code: "for (let key in user) {\n  console.log(user[key]);\n}",
                  recommendation: "Prefer Object.keys / entries",
                },
                {
                  title: "Converting Objects to Arrays",
                  description: "Utilities help bridge objects and arrays.",
                  examples: [
                    "Object.entries → map/filter",
                    "Dynamic rendering pipelines",
                  ],
                  code: "Object.entries(user).map(([k, v]) => `${k}: ${v}`);",
                },
                {
                  title: "Comparing Objects (Shallow)",
                  description: "Objects cannot be compared directly.",
                  wrongExample: "obj1 === obj2 // false",
                  correctApproach: "Compare keys and values",
                  code: "const isEqual = (a, b) =>\n  Object.keys(a).every(key => a[key] === b[key]);",
                  note: "Only works for shallow objects",
                },
                {
                  title: "Real-World Examples",
                  description: "Object utilities power dynamic features.",
                  examples: [
                    "Form value rendering",
                    "Profile pages",
                    "Admin dashboards",
                  ],
                },
              ],

              visualStructure: `
Object → Utilities → Array
keys    → ['name', 'age']
values  → ['Alex', 25]
entries → [['name','Alex'], ['age',25]]
    `,

              commonMistakes: [
                {
                  mistake: "Trying to loop object like array",
                  consequence: "Runtime errors",
                  fix: "Use Object.entries or keys",
                },
                {
                  mistake: "Using for...in blindly",
                  consequence: "Unexpected inherited properties",
                  fix: "Use Object utilities",
                },
                {
                  mistake: "Comparing objects with ===",
                  consequence: "Always false",
                  fix: "Compare keys and values",
                },
              ],

              proTips: [
                "Object.entries is the most powerful utility",
                "Convert objects to arrays for flexibility",
                "Shallow comparison is usually enough",
                "These utilities are common in React apps",
              ],

              realWorldAnalogy:
                "Object utilities are like inventory tools:\n- keys = labels\n- values = items\n- entries = label + item pairs",
            },

            tasks: [
              "Loop through object properties",
              "Convert object to array",
              "Compare two objects shallowly",
            ],

            examples: [
              "Object.entries(user).forEach(([k,v]) => console.log(k,v));",
            ],

            useCases: [
              "Form data handling",
              "Dynamic UI rendering",
              "Dashboard data mapping",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Iterate over object data safely",
              "Use Object.keys, values, and entries",
              "Transform objects into arrays",
              "Handle dynamic data confidently",
            ],
          },
          {
            day: 5,
            topic: "Nested Objects & JSON",
            subTopics: [
              "Nested objects",
              "Arrays inside objects",
              "JSON format",
              "JSON.parse()",
              "JSON.stringify()",
            ],
            explanation:
              "JSON is the standard format for data exchange in frontend apps.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Nested Data Access Drill",
                description:
                  "Safely access and manipulate deeply nested object data.",
                scenarios: [
                  "User profile with address & preferences",
                  "API response with nested arrays",
                  "Missing nested keys handling",
                ],
                requirements: [
                  "Access nested values correctly",
                  "Avoid runtime errors",
                  "Explain how nesting affects logic",
                ],
                outcome:
                  "You’ll confidently work with real-world API data structures.",
              },
              {
                id: "pt-2",
                title: "JSON Storage Workflow Task",
                description:
                  "Implement full JSON → localStorage → JSON workflow.",
                scenarios: [
                  "Save user object to localStorage",
                  "Retrieve and parse stored data",
                  "Handle invalid or missing JSON",
                ],
                requirements: [
                  "Use JSON.stringify before storing",
                  "Use JSON.parse after retrieving",
                  "Add basic safety checks",
                ],
                outcome: "Clear understanding of frontend data persistence.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most frontend bugs happen when nested data or JSON is handled incorrectly.",
              realWorldChecks: [
                "Is this value an object or a JSON string?",
                "Am I accessing a nested path safely?",
                "Does this JSON contain unsupported data (functions, dates)?",
              ],
              example:
                "const raw = localStorage.getItem('user');\nconst user = raw ? JSON.parse(raw) : null;",
            },

            interviewInsights: {
              commonQuestions: [
                "What is JSON and why is it used?",
                "Difference between JSON and JavaScript object?",
                "What does JSON.stringify remove?",
                "How do you store objects in localStorage?",
              ],
              interviewerTip:
                "Always explain JSON with API or localStorage examples.",
              redFlag: "Confusing JSON string with JavaScript object.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Is this data nested?",
                "Am I dealing with JSON or JS object?",
                "Did I stringify before storing?",
                "Did I parse before using?",
              ],
              refactorRule:
                "If data crosses system boundaries → convert to JSON.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Real-world data is rarely flat. User profiles, API responses, and app state usually contain nested objects and arrays. JSON (JavaScript Object Notation) is the universal format used to send and store this data across systems.",

              sections: [
                {
                  title: "What Are Nested Objects?",
                  description: "Objects can contain other objects as values.",
                  keyPoints: [
                    "Represents real-world hierarchy",
                    "Common in API responses",
                  ],
                  code: "const user = {\n  name: 'Alex',\n  address: {\n    city: 'Delhi',\n    pincode: 110001\n  }\n};",
                  analogy: "Like a file inside a folder",
                },
                {
                  title: "Accessing Nested Properties",
                  description:
                    "Nested properties are accessed using chained dot notation.",
                  code: "user.address.city; // 'Delhi'",
                  warning: "Accessing missing paths causes errors",
                  bestPractice: "Check existence before access",
                },
                {
                  title: "Arrays Inside Objects",
                  description:
                    "Objects often contain arrays for lists of data.",
                  keyPoints: [
                    "Very common pattern",
                    "Used for orders, posts, tasks",
                  ],
                  code: "const user = {\n  name: 'Alex',\n  skills: ['HTML', 'CSS', 'JS']\n};",
                  useCase: "User skills, product images, order items",
                },
                {
                  title: "Iterating Nested Data",
                  description: "Nested arrays require iteration methods.",
                  code: "user.skills.forEach(skill => console.log(skill));",
                  takeaway: "Arrays + objects work together",
                },
                {
                  title: "What Is JSON?",
                  description: "JSON is a string-based data format.",
                  keyPoints: [
                    "Looks like JS object but is a string",
                    "Keys must be in double quotes",
                    "No functions allowed",
                  ],
                  example: '{ "name": "Alex", "age": 25 }',
                  analogy: "A universal language for data",
                },
                {
                  title: "Why JSON Exists",
                  description: "Different systems need a common format.",
                  examples: ["Frontend ↔ Backend", "APIs", "Local storage"],
                  takeaway: "JSON works everywhere",
                },
                {
                  title: "JSON.stringify()",
                  description: "Converts JavaScript objects into JSON strings.",
                  keyPoints: [
                    "Required before storing data",
                    "Removes functions",
                  ],
                  code: "const json = JSON.stringify(user);",
                  useCase: "Saving data to localStorage",
                },
                {
                  title: "JSON.parse()",
                  description:
                    "Converts JSON strings back into JavaScript objects.",
                  keyPoints: [
                    "Required after reading stored data",
                    "Returns usable JS object",
                  ],
                  code: "const data = JSON.parse(localStorage.getItem('user'));",
                  warning: "Parsing invalid JSON throws error",
                },
                {
                  title: "JSON + localStorage Workflow",
                  description: "Common frontend storage pattern.",
                  steps: [
                    "Convert object → JSON",
                    "Store in localStorage",
                    "Retrieve JSON",
                    "Parse back to object",
                  ],
                  code: "localStorage.setItem('user', JSON.stringify(user));",
                },
                {
                  title: "Real-World API Example",
                  description: "APIs always return JSON.",
                  example: "fetch('/api/users').then(res => res.json())",
                  note: "Understanding JSON is mandatory for frontend devs",
                },
              ],

              visualStructure: `
Data Flow:
JS Object
   ↓ JSON.stringify
JSON String
   ↓ JSON.parse
JS Object
    `,

              commonMistakes: [
                {
                  mistake: "Treating JSON as object",
                  consequence: "Type errors",
                  fix: "Parse JSON before use",
                },
                {
                  mistake: "Forgetting stringify before storage",
                  consequence: "[object Object] stored",
                  fix: "Always stringify objects",
                },
                {
                  mistake: "Assuming deep copy with JSON",
                  consequence: "Data loss (functions, dates)",
                  fix: "Understand JSON limitations",
                },
              ],

              proTips: [
                "APIs always speak JSON",
                "JSON.parse & stringify are daily tools",
                "Nested data is normal — don’t fear it",
                "Master this before async & APIs",
              ],

              realWorldAnalogy:
                "JSON is like packing data into a sealed envelope:\n- stringify = packing\n- parse = unpacking",
            },

            tasks: [
              "Work with nested object structures",
              "Store and retrieve JSON from localStorage",
              "Parse API-like data",
            ],

            examples: [
              "const data = JSON.parse(localStorage.getItem('recipes'));",
            ],

            useCases: [
              "Local storage apps",
              "API communication",
              "State persistence",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Access and manipulate nested objects",
              "Understand JSON format clearly",
              "Use JSON.parse and JSON.stringify",
              "Handle real-world API data confidently",
            ],
          },
          {
            day: 6,
            topic: "Sets & Maps (Advanced Data Structures)",
            subTopics: [
              "Set basics",
              "Removing duplicates using Set",
              "Map basics",
              "Key-value differences (Map vs Object)",
              "When to use Map or Set",
            ],
            explanation:
              "Sets and Maps offer better performance and specialized use cases compared to arrays and plain objects.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Duplicate Killer with Set",
                description:
                  "Use Set to clean and manage duplicate-heavy datasets.",
                scenarios: [
                  "Remove duplicate user IDs",
                  "Track unique visited pages",
                  "Prevent duplicate selections",
                ],
                requirements: [
                  "Convert array to Set",
                  "Convert Set back to array",
                  "Explain why Set is better than filter here",
                ],
                outcome:
                  "You’ll instinctively use Set whenever uniqueness is required.",
              },
              {
                id: "pt-2",
                title: "High-Performance Lookup with Map",
                description:
                  "Replace object-based lookups with Map for dynamic keys.",
                scenarios: [
                  "Cache API responses",
                  "Store user session data",
                  "Use objects as keys",
                ],
                requirements: [
                  "Use non-string keys",
                  "Compare Map vs Object behavior",
                  "Demonstrate faster lookups conceptually",
                ],
                outcome: "Clear understanding of when Map beats Object.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Incorrect data structure choice leads to slow apps and complex logic.",
              realWorldChecks: [
                "Do I need uniqueness or ordering?",
                "Are my keys dynamic or non-string?",
                "Am I doing frequent add/delete/lookups?",
              ],
              example:
                "const visited = new Set();\nvisited.add(pageId);\nif (visited.has(pageId)) { /* skip */ }",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between Set and Array?",
                "Map vs Object — when would you use Map?",
                "Can Map keys be objects?",
                "How do you remove duplicates efficiently?",
              ],
              interviewerTip:
                "Always answer with a real use-case (cache, uniqueness, lookup).",
              redFlag:
                "Using Object or Array blindly without understanding alternatives.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Do I need unique values?",
                "Do keys need to be non-strings?",
                "Is performance critical here?",
                "Am I overengineering?",
              ],
              refactorRule:
                "If logic feels forced → reconsider the data structure.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "While arrays and objects solve most problems, JavaScript also provides Sets and Maps for specific scenarios like fast lookups, uniqueness, and advanced key-value handling. Knowing when to use them separates average developers from good ones.",

              sections: [
                {
                  title: "What Is a Set?",
                  description: "A Set is a collection of unique values.",
                  keyPoints: [
                    "No duplicate values allowed",
                    "Order is preserved",
                    "Can store any data type",
                  ],
                  code: "const numbers = new Set([1, 2, 2, 3]);",
                  result: "Set {1, 2, 3}",
                  analogy: "Like a guest list — one entry per person",
                },
                {
                  title: "Common Set Operations",
                  description: "Sets provide simple but powerful methods.",
                  methods: [
                    "add(value)",
                    "delete(value)",
                    "has(value)",
                    "size",
                  ],
                  code: "numbers.add(4);\nnumbers.has(2); // true\nnumbers.delete(1);",
                  useCase: "Tracking unique values",
                },
                {
                  title: "Removing Duplicates Using Set",
                  description: "The most popular real-world use of Set.",
                  code: "const arr = [1, 2, 2, 3, 3];\nconst uniqueArr = [...new Set(arr)];",
                  takeaway: "One-liner duplicate removal",
                },
                {
                  title: "What Is a Map?",
                  description:
                    "A Map stores key-value pairs like objects, but with more flexibility.",
                  keyPoints: [
                    "Keys can be any data type",
                    "Maintains insertion order",
                    "Better performance for frequent operations",
                  ],
                  code: "const map = new Map();\nmap.set('a', 1);\nmap.set(1, 'number');",
                  analogy: "A smarter dictionary",
                },
                {
                  title: "Common Map Methods",
                  description: "Maps come with built-in utilities.",
                  methods: [
                    "set(key, value)",
                    "get(key)",
                    "has(key)",
                    "delete(key)",
                    "size",
                  ],
                  code: "map.get('a'); // 1\nmap.has(1); // true",
                  useCase: "Fast lookups and caches",
                },
                {
                  title: "Map vs Object",
                  description: "Although similar, they serve different needs.",
                  comparison: [
                    "Object → string/symbol keys only",
                    "Map → any type of key",
                    "Map → predictable iteration order",
                    "Map → better performance for large datasets",
                  ],
                  ruleOfThumb:
                    "Use Object for simple data, Map for dynamic data",
                },
                {
                  title: "Iterating Sets & Maps",
                  description: "Both are iterable using for...of.",
                  code: "for (let val of numbers) {\n  console.log(val);\n}\n\nfor (let [k, v] of map) {\n  console.log(k, v);\n}",
                  note: "Cleaner than Object iteration in some cases",
                },
                {
                  title: "When to Use Set",
                  description: "Choose Set when uniqueness matters.",
                  examples: ["Unique tags", "Visited pages", "Selected IDs"],
                },
                {
                  title: "When to Use Map",
                  description:
                    "Choose Map when key flexibility & performance matter.",
                  examples: [
                    "Caching API responses",
                    "User sessions",
                    "Complex key-value storage",
                  ],
                },
                {
                  title: "Real-World Examples",
                  description:
                    "Sets and Maps are used behind the scenes in many systems.",
                  examples: [
                    "Autocomplete suggestions",
                    "Permission systems",
                    "Optimized lookups",
                  ],
                },
              ],

              visualStructure: `
Data Structure Choice:
Need uniqueness → Set
Need key-value (any key) → Map
Simple data → Object / Array
    `,

              commonMistakes: [
                {
                  mistake: "Using Set expecting key-value pairs",
                  consequence: "Wrong data structure",
                  fix: "Use Map instead",
                },
                {
                  mistake: "Using Object when keys are dynamic",
                  consequence: "Unexpected behavior",
                  fix: "Switch to Map",
                },
                {
                  mistake: "Overusing Map/Set unnecessarily",
                  consequence: "Overengineering",
                  fix: "Prefer arrays/objects when simple",
                },
              ],

              proTips: [
                "Set is perfect for uniqueness checks",
                "Map outperforms Object in frequent operations",
                "Most apps don’t need them — but some REALLY do",
                "Knowing these impresses interviewers",
              ],

              realWorldAnalogy:
                "Set is like a VIP list (no duplicates)\nMap is like a smart locker system (any key, any value)",
            },

            tasks: [
              "Remove duplicates from array",
              "Store data using Map",
              "Compare Set vs Array",
            ],

            examples: [
              "const unique = new Set([1,2,2,3]);",
              "const map = new Map([['a',1]]);",
            ],

            useCases: ["Caching", "Fast lookups", "Optimized data handling"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand Set and Map fundamentals",
              "Choose correct data structure",
              "Use Sets for uniqueness",
              "Use Maps for flexible key-value storage",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Recipe Book Application",
            subTopics: [
              "Object-based data modeling",
              "CRUD operations",
              "Search & filter logic",
              "LocalStorage integration",
              "Project structuring",
            ],
            explanation:
              "This project uses objects and arrays to manage structured recipe data.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Recipe CRUD Engine",
                description:
                  "Implement full CRUD logic using immutable patterns.",
                scenarios: [
                  "Add a new recipe",
                  "Edit existing recipe details",
                  "Delete recipe safely",
                ],
                requirements: [
                  "Use spread operator for updates",
                  "Avoid mutating original array",
                  "Generate unique recipe IDs",
                ],
                outcome:
                  "You’ll confidently build real-world CRUD logic without bugs.",
              },
              {
                id: "pt-2",
                title: "Smart Search & Category Grouping",
                description:
                  "Build efficient search and grouping logic for recipes.",
                scenarios: [
                  "Search recipes by name",
                  "Group recipes by category",
                  "Handle empty search results",
                ],
                requirements: [
                  "Use filter for search",
                  "Use reduce for grouping",
                  "Keep original data untouched",
                ],
                outcome: "Strong grip on data transformation pipelines.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most frontend bugs happen in CRUD + state persistence logic.",
              realWorldChecks: [
                "Does add/edit/delete update UI correctly?",
                "Is data preserved after refresh?",
                "Are edge cases (empty search, missing data) handled?",
              ],
              example:
                "const save = data => localStorage.setItem('recipes', JSON.stringify(data));",
            },

            interviewInsights: {
              commonQuestions: [
                "How would you design a CRUD app without a backend?",
                "Why use map for update and filter for delete?",
                "How would you group data efficiently?",
                "How does localStorage differ from a database?",
              ],
              interviewerTip: "Explain logic flow first, then code.",
              redFlag:
                "Mutating arrays or objects directly in CRUD operations.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Am I mutating data accidentally?",
                "Is every CRUD action saving state?",
                "Is search logic case-insensitive?",
                "Can this logic scale with more data?",
              ],
              refactorRule:
                "If CRUD logic feels repetitive → extract functions.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "The Recipe Book application is a real-world CRUD project that closely resembles how frontend apps manage data from APIs or databases. It combines object modeling, array operations, search logic, and persistence using localStorage.",

              sections: [
                {
                  title: "Project Goal",
                  description:
                    "Build a Recipe Book app where users can manage recipes efficiently.",
                  features: [
                    "Add, edit, and delete recipes",
                    "Search recipes by name",
                    "Group recipes by category",
                    "Persist data using localStorage",
                  ],
                  skillsUsed: [
                    "Objects & arrays",
                    "map / filter / find",
                    "JSON.parse & JSON.stringify",
                    "Immutability patterns",
                  ],
                  outcome: "A complete data-driven frontend application",
                },
                {
                  title: "Recipe Data Model",
                  description:
                    "Each recipe should be represented as an object.",
                  keyProperties: [
                    "id",
                    "name",
                    "category",
                    "ingredients",
                    "instructions",
                  ],
                  code: "const recipes = [\n  {\n    id: 1,\n    name: 'Pasta',\n    category: 'Italian',\n    ingredients: ['Pasta', 'Sauce'],\n    instructions: 'Boil pasta and add sauce'\n  }\n];",
                  takeaway: "Good data modeling simplifies everything else",
                },
                {
                  title: "Adding Recipes (Create)",
                  description: "New recipes should be added immutably.",
                  logic: "Create recipe object → append using spread",
                  code: "recipes = [...recipes, newRecipe];",
                  bestPractice: "Always generate a unique id",
                },
                {
                  title: "Editing Recipes (Update)",
                  description: "Use map to update a specific recipe.",
                  logic: "Match id → return updated object",
                  code: "recipes = recipes.map(r =>\n  r.id === id ? { ...r, name: updatedName } : r\n);",
                  takeaway: "map is ideal for updates",
                },
                {
                  title: "Deleting Recipes (Delete)",
                  description: "Use filter to remove a recipe.",
                  keyPoints: [
                    "filter returns new array",
                    "Clean delete operation",
                  ],
                  code: "recipes = recipes.filter(r => r.id !== id);",
                  analogy: "Removing one card from a deck",
                },
                {
                  title: "Searching Recipes",
                  description: "Search logic improves user experience.",
                  logic: "Filter recipes by name",
                  code: "const results = recipes.filter(r =>\n  r.name.toLowerCase().includes(query.toLowerCase())\n);",
                  useCase: "Search bars",
                },
                {
                  title: "Grouping Recipes by Category",
                  description: "Use reduce to group recipes.",
                  code: "const grouped = recipes.reduce((acc, r) => {\n  acc[r.category] = acc[r.category] || [];\n  acc[r.category].push(r);\n  return acc;\n}, {});",
                  note: "This pattern is very interview-friendly",
                },
                {
                  title: "LocalStorage Integration",
                  description: "Persist data across page reloads.",
                  workflow: [
                    "Stringify recipes",
                    "Save to localStorage",
                    "Parse on load",
                  ],
                  code: "localStorage.setItem('recipes', JSON.stringify(recipes));",
                  warning: "Always parse data before using it",
                },
                {
                  title: "Loading Saved Data",
                  description: "Restore recipes when app loads.",
                  code: "const saved = JSON.parse(localStorage.getItem('recipes')) || [];",
                  takeaway: "Persistence makes apps feel real",
                },
                {
                  title: "Project Structuring",
                  description: "Keep logic clean and maintainable.",
                  suggestedFunctions: [
                    "addRecipe()",
                    "updateRecipe()",
                    "deleteRecipe()",
                    "searchRecipes()",
                    "saveToStorage()",
                  ],
                  benefit: "Easy to scale and debug",
                },
                {
                  title: "Optional Enhancements",
                  description: "Extra features for practice.",
                  ideas: [
                    "Favorite recipes",
                    "Sort by name/category",
                    "Recipe count per category",
                  ],
                },
              ],

              visualStructure: `
Recipe App Flow:
Add → Update → Delete
        ↓
Search / Group
        ↓
Save → localStorage
    `,

              commonMistakes: [
                {
                  mistake: "Mutating recipe objects directly",
                  consequence: "State bugs",
                  fix: "Use spread + map/filter",
                },
                {
                  mistake: "Forgetting to persist changes",
                  consequence: "Data lost on refresh",
                  fix: "Save after every update",
                },
                {
                  mistake: "No unique id",
                  consequence: "Wrong recipe updates",
                  fix: "Use Date.now() or UUID",
                },
              ],

              proTips: [
                "This project mirrors real CRUD apps",
                "reduce for grouping is highly valuable",
                "localStorage makes apps production-like",
                "Great portfolio & interview project",
              ],

              realWorldAnalogy:
                "A recipe book is like a database:\n- Add entries\n- Update details\n- Remove old ones\n- Organize by category",
            },

            tasks: [
              "Add new recipes",
              "Edit and delete recipes",
              "Search recipes by name",
              "Group recipes by category",
              "Persist data using localStorage",
            ],

            examples: ["recipes = recipes.filter(r => r.id !== id);"],

            useCases: [
              "Real-world CRUD apps",
              "Interview-level project",
              "Foundation for API-based apps",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "3–4 hours",

            // 🎯 Learning objectives
            objectives: [
              "Model structured data using objects",
              "Perform full CRUD operations",
              "Implement search and grouping logic",
              "Persist application state",
            ],
          },
        ],
      },
      {
        week: 11,
        weekGoal:
          "Understand and control the DOM to build interactive web interfaces",
        weekOutcome: "Ability to dynamically manipulate UI using JavaScript",
        canLearn: ["students", "beginners", "professionals"],
        miniProject: "Interactive Blog Platform",
        days: [
          {
            day: 1,
            topic: "Selecting DOM Elements",
            subTopics: [
              "getElementById",
              "getElementsByClassName",
              "getElementsByTagName",
              "querySelector",
              "querySelectorAll",
              "NodeList vs HTMLCollection",
            ],
            explanation:
              "DOM selection methods allow JavaScript to target and manipulate HTML elements.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Selector Mastery Drill",
                description:
                  "Practice selecting elements using different DOM APIs and understand their return types.",
                scenarios: [
                  "Select single element by id",
                  "Select multiple elements using class selector",
                  "Convert HTMLCollection to array and loop",
                ],
                requirements: [
                  "Use getElementById and querySelector",
                  "Explain NodeList vs HTMLCollection",
                  "Loop safely without errors",
                ],
                outcome:
                  "You’ll confidently select and iterate DOM elements without bugs.",
              },
              {
                id: "pt-2",
                title: "Modern DOM Refactor",
                description:
                  "Refactor legacy DOM selection code into modern querySelector-based code.",
                scenarios: [
                  "Replace getElementsByClassName",
                  "Replace getElementsByTagName",
                  "Use CSS selectors effectively",
                ],
                requirements: [
                  "Use querySelector / querySelectorAll only",
                  "Ensure functionality remains same",
                  "Improve readability",
                ],
                outcome: "Clean, modern DOM code aligned with React mindset.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Incorrect DOM selection is one of the biggest sources of silent UI bugs.",
              realWorldChecks: [
                "Does selector return one element or many?",
                "Is the returned collection live or static?",
                "Does iteration break after DOM changes?",
              ],
              example:
                "const items = document.querySelectorAll('.item');\nitems.forEach(i => i.classList.add('active'));",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between querySelector and getElementById?",
                "What is NodeList vs HTMLCollection?",
                "Why is querySelectorAll preferred in modern JS?",
                "Why is direct DOM access discouraged in React?",
              ],
              interviewerTip: "Explain return types before explaining syntax.",
              redFlag:
                "Not knowing the difference between NodeList and HTMLCollection.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Do I need one element or many?",
                "Is my selector CSS-based?",
                "Can this be simplified with querySelector?",
                "Will this code work after DOM updates?",
              ],
              refactorRule:
                "If using old selectors → rewrite using querySelector APIs.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "Before React handles the UI, JavaScript interacts directly with the DOM. Understanding how to select elements is essential for event handling, dynamic updates, and legacy codebases. Modern JavaScript prefers querySelector APIs because they align closely with CSS and React mental models.",

              sections: [
                {
                  title: "What Is the DOM?",
                  description:
                    "The DOM (Document Object Model) is a tree representation of your HTML.",
                  keyPoints: [
                    "Browser converts HTML into a DOM tree",
                    "JavaScript interacts with this tree",
                    "DOM is live and mutable",
                  ],
                  analogy: "DOM is like a live blueprint of your webpage",
                },
                {
                  title: "getElementById (Legacy but Valid)",
                  description: "Selects a single element by its unique id.",
                  keyPoints: [
                    "Returns a single element",
                    "Fast and direct",
                    "ID must be unique",
                  ],
                  code: "const title = document.getElementById('title');",
                  modernNote:
                    "Still valid, but less flexible than querySelector",
                },
                {
                  title: "getElementsByClassName & TagName",
                  description: "Older DOM selection methods.",
                  keyPoints: [
                    "Return HTMLCollection (live)",
                    "Do not support CSS selectors",
                  ],
                  code: "document.getElementsByClassName('card');\ndocument.getElementsByTagName('p');",
                  warning: "Rarely used in modern code",
                },
                {
                  title: "querySelector (Modern Standard)",
                  description:
                    "Selects the first matching element using CSS selectors.",
                  keyPoints: [
                    "Most flexible selector",
                    "Works like CSS",
                    "Preferred in modern JS",
                  ],
                  code: "document.querySelector('#title');\ndocument.querySelector('.card');",
                  ruleOfThumb: "If you know CSS, you know querySelector",
                },
                {
                  title: "querySelectorAll",
                  description: "Selects all matching elements.",
                  keyPoints: [
                    "Returns NodeList",
                    "Supports forEach",
                    "Static (not live)",
                  ],
                  code: "document.querySelectorAll('.card');",
                  bestPractice: "Use this for multiple elements",
                },
                {
                  title: "NodeList vs HTMLCollection",
                  description: "Understanding the difference prevents bugs.",
                  comparison: [
                    "NodeList → static",
                    "HTMLCollection → live",
                    "NodeList → supports forEach",
                  ],
                  code: "document.querySelectorAll('.item').forEach(el => console.log(el));",
                  modernNote: "Prefer NodeList (querySelectorAll)",
                },
                {
                  title: "Looping Through Selected Elements",
                  description: "Modern iteration is clean and readable.",
                  code: "const cards = document.querySelectorAll('.card');\ncards.forEach(card => {\n  card.classList.add('active');\n});",
                  takeaway: "forEach is the modern default",
                },
                {
                  title: "How This Relates to React",
                  description: "React minimizes direct DOM access.",
                  keyPoints: [
                    "React manages DOM internally",
                    "Direct selection is rare in React",
                    "Used mainly with refs",
                  ],
                  preview: "You’ll use this knowledge when learning useRef",
                },
              ],

              visualStructure: `
HTML → DOM Tree → JavaScript
           ↓
     querySelector APIs
    `,

              commonMistakes: [
                {
                  mistake: "Using old selectors unnecessarily",
                  consequence: "Verbose and outdated code",
                  fix: "Prefer querySelector / querySelectorAll",
                },
                {
                  mistake: "Assuming HTMLCollection supports forEach",
                  consequence: "Runtime errors",
                  fix: "Convert or use querySelectorAll",
                },
                {
                  mistake: "Overusing DOM selection in React",
                  consequence: "Anti-pattern",
                  fix: "Let React control the DOM",
                },
              ],

              proTips: [
                "querySelector is your go-to API",
                "Think in CSS selectors",
                "DOM manipulation is foundational, not final",
                "This knowledge supports React, not replaces it",
              ],

              realWorldAnalogy:
                "DOM selection is like selecting elements in CSS — but now you can control them",
            },

            tasks: [
              "Select elements by id, class, and tag",
              "Compare querySelector vs getElementById",
              "Loop through NodeList and HTMLCollection",
            ],

            examples: [
              "document.getElementById('title')",
              "document.querySelectorAll('.card')",
            ],

            useCases: [
              "Targeting UI elements",
              "Preparing elements for interactivity",
              "Understanding React internals",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand DOM structure",
              "Use modern DOM selection APIs",
              "Differentiate NodeList and HTMLCollection",
              "Prepare for React DOM concepts",
            ],
          },
          {
            day: 2,
            topic: "Modifying DOM Content",
            subTopics: [
              "textContent",
              "innerText",
              "innerHTML",
              "Difference between textContent and innerHTML",
              "Security concerns (XSS basics)",
            ],
            explanation:
              "DOM content manipulation allows dynamic updates to UI based on user actions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Safe Content Update Drill",
                description:
                  "Practice updating DOM content safely using the correct APIs.",
                scenarios: [
                  "Update heading text dynamically",
                  "Render API-like data safely",
                  "Show error messages on UI",
                ],
                requirements: [
                  "Use textContent for all text updates",
                  "Avoid innerHTML for user data",
                  "Explain why textContent is safer",
                ],
                outcome:
                  "You’ll instinctively choose the safe DOM API every time.",
              },
              {
                id: "pt-2",
                title: "XSS Risk Simulation",
                description:
                  "Understand how innerHTML can introduce security vulnerabilities.",
                scenarios: [
                  "Inject plain text using innerHTML",
                  "Inject user input with script tags",
                  "Refactor code to prevent XSS",
                ],
                requirements: [
                  "Demonstrate unsafe innerHTML usage",
                  "Fix using textContent",
                  "Explain the security difference",
                ],
                outcome: "You’ll understand XSS risks at a practical level.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Many real-world frontend security bugs come from unsafe DOM content updates.",
              realWorldChecks: [
                "Is this content coming from user or API?",
                "Do I really need HTML parsing?",
                "Can this be rendered as plain text?",
              ],
              example:
                "msgEl.textContent = errorMessage; // safe\n// instead of\nmsgEl.innerHTML = errorMessage; // risky",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between textContent and innerHTML?",
                "Why is innerHTML dangerous?",
                "What is XSS and how does it happen?",
                "How does React prevent XSS by default?",
              ],
              interviewerTip:
                "Security awareness impresses interviewers more than syntax knowledge.",
              redFlag:
                "Using innerHTML casually without understanding XSS risks.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Is this plain text or actual HTML?",
                "Is this data user-generated?",
                "Can textContent solve this safely?",
                "Would this be safe in production?",
              ],
              refactorRule:
                "If innerHTML is used for text → replace with textContent.",
            },

            // 📚 Detailed Learning Content (Modern & Secure)
            content: {
              introduction:
                "Dynamic websites constantly update text and content based on user actions, API responses, or application state. JavaScript provides multiple ways to modify DOM content, but choosing the wrong one can cause performance issues or serious security vulnerabilities.",

              sections: [
                {
                  title: "Why DOM Content Manipulation Matters",
                  description: "UI is data-driven, not static.",
                  examples: [
                    "Updating counters",
                    "Showing error messages",
                    "Rendering API responses",
                  ],
                  analogy:
                    "Like changing text on a digital display in real time",
                },
                {
                  title: "textContent (Modern & Safe)",
                  description: "Sets or gets the text content of an element.",
                  keyPoints: [
                    "Treats everything as plain text",
                    "Fast and secure",
                    "Ignores HTML tags",
                  ],
                  code: "element.textContent = 'Hello World';",
                  bestPractice: "Default choice for text updates",
                },
                {
                  title: "innerText (Legacy Behavior)",
                  description: "Similar to textContent but affected by CSS.",
                  keyPoints: [
                    "Respects hidden elements",
                    "Triggers layout recalculation",
                  ],
                  code: "element.innerText = 'Hello';",
                  warning: "Avoid unless CSS-dependent behavior is required",
                },
                {
                  title: "textContent vs innerText",
                  description: "They look similar but behave differently.",
                  comparison: [
                    "textContent → faster, ignores CSS",
                    "innerText → slower, respects CSS",
                  ],
                  ruleOfThumb: "Use textContent in almost all cases",
                },
                {
                  title: "innerHTML (Powerful but Dangerous)",
                  description: "Reads or writes HTML inside an element.",
                  keyPoints: [
                    "Parses HTML",
                    "Can inject elements",
                    "High XSS risk",
                  ],
                  code: "element.innerHTML = '<strong>Bold</strong>';",
                  warning: "Never trust user input with innerHTML",
                },
                {
                  title: "Difference: textContent vs innerHTML",
                  description:
                    "Choosing the wrong one leads to bugs or security holes.",
                  example:
                    "element.textContent = '<b>Test</b>' → displays text\n" +
                    "element.innerHTML = '<b>Test</b>' → renders bold",
                  takeaway: "textContent = text | innerHTML = markup",
                },
                {
                  title: "Security: XSS (Cross-Site Scripting)",
                  description:
                    "XSS occurs when attackers inject scripts into your UI.",
                  example: "User input: <script>alert('hacked')</script>",
                  risk: "innerHTML executes injected scripts",
                  prevention: [
                    "Never inject raw user input",
                    "Use textContent",
                    "Sanitize HTML if absolutely required",
                  ],
                  modernNote:
                    "React escapes content by default for this reason",
                },
                {
                  title: "Rendering API Data Safely",
                  description: "Most API responses should be rendered as text.",
                  code: "titleEl.textContent = data.title;",
                  bestPractice: "Treat API data as untrusted",
                },
                {
                  title: "How React Changes This",
                  description: "React handles DOM updates safely.",
                  keyPoints: [
                    "JSX escapes values by default",
                    "innerHTML is replaced by dangerouslySetInnerHTML",
                    "Direct DOM updates are rare",
                  ],
                  preview: "You’ll rarely touch innerHTML after React",
                },
              ],

              visualStructure: `
Data → JavaScript → DOM
        ↓
  textContent (safe)
  innerHTML (danger)
    `,

              commonMistakes: [
                {
                  mistake: "Using innerHTML for plain text",
                  consequence: "Security vulnerabilities",
                  fix: "Use textContent",
                },
                {
                  mistake: "Rendering user input directly",
                  consequence: "XSS attacks",
                  fix: "Never trust user data",
                },
                {
                  mistake: "Using innerText unnecessarily",
                  consequence: "Performance hit",
                  fix: "Prefer textContent",
                },
              ],

              proTips: [
                "Default to textContent",
                "innerHTML should be rare and intentional",
                "Security is part of frontend development",
                "React exists partly to avoid these problems",
              ],

              realWorldAnalogy:
                "textContent is writing plain text on paper\ninnerHTML is pasting unknown documents into your system",
            },

            tasks: [
              "Change text dynamically",
              "Inject HTML safely",
              "Compare textContent vs innerHTML",
            ],

            examples: [
              "element.textContent = 'Hello'",
              "element.innerHTML = '<strong>Bold</strong>'",
            ],

            useCases: [
              "Dynamic content updates",
              "Rendering API data",
              "UI feedback messages",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Safely update DOM text",
              "Understand innerHTML risks",
              "Prevent XSS vulnerabilities",
              "Prepare for React rendering concepts",
            ],
          },
          {
            day: 3,
            topic: "Styling Elements & Class Manipulation",
            subTopics: [
              "style property",
              "classList.add",
              "classList.remove",
              "classList.toggle",
              "classList.contains",
            ],
            explanation:
              "Classes and styles allow visual changes based on user interaction.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "State-Based Styling Challenge",
                description:
                  "Implement UI state changes using classList instead of inline styles.",
                scenarios: [
                  "Toggle active state on button click",
                  "Show / hide modal using classes",
                  "Switch dark / light mode",
                ],
                requirements: [
                  "Use classList.add/remove/toggle only",
                  "Avoid element.style for main styling",
                  "Explain which class represents which state",
                ],
                outcome: "You’ll think in UI states instead of visual hacks.",
              },
              {
                id: "pt-2",
                title: "Inline Style vs Class Refactor",
                description:
                  "Refactor inline styles into clean, reusable CSS classes.",
                scenarios: [
                  "Background color change",
                  "Padding & spacing updates",
                  "Visibility toggle",
                ],
                requirements: [
                  "Move styles from JS to CSS classes",
                  "Use a single toggle class",
                  "Keep JS logic minimal",
                ],
                outcome: "Cleaner, scalable styling logic ready for real apps.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most UI bugs come from incorrect state-to-style mapping, not CSS itself.",
              realWorldChecks: [
                "Is this a UI state or a one-off style?",
                "Can a single class represent this state?",
                "Will this scale when UI grows?",
              ],
              example:
                "btn.classList.toggle('active'); // state-driven\n// instead of\nbtn.style.backgroundColor = 'blue'; // brittle",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between style and classList?",
                "When should you use inline styles?",
                "How would you implement dark mode?",
                "How does this relate to React className?",
              ],
              interviewerTip:
                "Interviewers look for state-based thinking, not CSS tricks.",
              redFlag:
                "Hardcoding multiple inline styles for UI state changes.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Is this a visual style or a UI state?",
                "Can CSS handle this instead of JS?",
                "Would a single class toggle be enough?",
                "How would I do this in React?",
              ],
              refactorRule:
                "If JS is setting many styles → convert to a CSS class.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "Dynamic styling is at the core of interactive UIs. While React manages styles declaratively, understanding class and style manipulation in plain JavaScript builds the foundation for how state-driven styling works in modern frameworks.",

              sections: [
                {
                  title: "Two Ways to Style Elements with JavaScript",
                  description:
                    "JavaScript can change styles either directly or via CSS classes.",
                  methods: [
                    "Inline styles using element.style",
                    "Class-based styling using classList",
                  ],
                  ruleOfThumb: "Prefer classList over inline styles",
                },
                {
                  title: "Inline Styles using style Property",
                  description:
                    "The style property directly modifies CSS styles on an element.",
                  keyPoints: [
                    "Applies inline CSS",
                    "Overrides stylesheet rules",
                    "Uses camelCase properties",
                  ],
                  code: "box.style.backgroundColor = 'red';\nbox.style.padding = '16px';",
                  warning: "Avoid excessive inline styling",
                },
                {
                  title: "Why Inline Styles Are Limited",
                  description: "Inline styles are harder to manage at scale.",
                  problems: [
                    "Hard to maintain",
                    "No hover/media queries",
                    "Mixes logic with presentation",
                  ],
                  takeaway:
                    "Inline styles are okay for small dynamic tweaks only",
                },
                {
                  title: "classList API (Modern Standard)",
                  description:
                    "classList provides a clean way to manage CSS classes.",
                  keyPoints: [
                    "Clean and readable",
                    "Works with CSS files",
                    "Preferred in modern JS",
                  ],
                  code: "element.classList.add('active');",
                  bestPractice: "Use classes for most styling changes",
                },
                {
                  title: "classList.add() & remove()",
                  description: "Explicitly control element state.",
                  code: "element.classList.add('visible');\nelement.classList.remove('hidden');",
                  useCase: "Show / hide UI elements",
                },
                {
                  title: "classList.toggle()",
                  description: "Toggles a class on and off.",
                  keyPoints: ["Perfect for switches", "Returns boolean"],
                  code: "element.classList.toggle('dark');",
                  useCase: "Dark / light mode toggle",
                },
                {
                  title: "classList.contains()",
                  description: "Checks if an element has a specific class.",
                  code: "if (element.classList.contains('active')) {\n  console.log('Active');\n}",
                  useCase: "Conditional logic based on UI state",
                },
                {
                  title: "Dark / Light Mode Example",
                  description: "A real-world pattern.",
                  logic: "Toggle a single class on body",
                  code: "document.body.classList.toggle('dark');",
                  cssNote: "CSS handles actual styling",
                },
                {
                  title: "style vs classList (Comparison)",
                  description: "Choosing the right approach matters.",
                  comparison: [
                    "style → quick, inline, limited",
                    "classList → scalable, clean, powerful",
                  ],
                  ruleOfThumb: "State change = classList",
                },
                {
                  title: "How This Maps to React",
                  description: "React uses the same concept declaratively.",
                  examples: [
                    "className={isDark ? 'dark' : ''}",
                    "style={{ backgroundColor: color }}",
                  ],
                  takeaway: "Today’s logic becomes React state tomorrow",
                },
              ],

              visualStructure: `
User Action
    ↓
JavaScript Logic
    ↓
classList / style
    ↓
CSS handles visuals
    `,

              commonMistakes: [
                {
                  mistake: "Overusing inline styles",
                  consequence: "Messy and unscalable code",
                  fix: "Use CSS classes",
                },
                {
                  mistake: "Toggling multiple classes unnecessarily",
                  consequence: "Complex logic",
                  fix: "Toggle a single parent class",
                },
                {
                  mistake: "Hardcoding styles in JS",
                  consequence: "Poor maintainability",
                  fix: "Let CSS do the styling",
                },
              ],

              proTips: [
                "CSS controls design, JS controls state",
                "Toggle one class → change entire theme",
                "classList is clean and readable",
                "This pattern directly maps to React state",
              ],

              realWorldAnalogy:
                "classList is like flipping switches\nCSS decides what happens when switch is on",
            },

            tasks: [
              "Toggle dark/light mode",
              "Add/remove classes dynamically",
              "Apply inline styles",
            ],

            examples: [
              "element.classList.toggle('dark')",
              "box.style.backgroundColor = 'red'",
            ],

            useCases: [
              "Theme toggles",
              "Active state indicators",
              "Interactive UI feedback",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Manipulate classes using classList",
              "Apply styles dynamically",
              "Choose between style and classes",
              "Prepare for React state-based styling",
            ],
          },
          {
            day: 4,
            topic: "Creating & Removing Elements",
            subTopics: [
              "createElement",
              "appendChild",
              "prepend",
              "remove",
              "cloneNode",
            ],
            explanation:
              "Dynamic element creation is key to interactive applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Dynamic List Builder",
                description:
                  "Build a dynamic list where users can add and remove items using DOM APIs.",
                scenarios: [
                  "Add item using input + button",
                  "Remove item on delete click",
                  "Prepend newest item to top",
                ],
                requirements: [
                  "Use createElement (no innerHTML)",
                  "Attach classes before appending",
                  "Use remove() for deletion",
                ],
                outcome:
                  "You’ll understand how real-time UIs are built from scratch.",
              },
              {
                id: "pt-2",
                title: "Template Cloning Pattern",
                description:
                  "Reuse a DOM template using cloneNode for scalable rendering.",
                scenarios: [
                  "Clone a card layout",
                  "Populate cloned content dynamically",
                  "Append multiple clones efficiently",
                ],
                requirements: [
                  "Use cloneNode(true)",
                  "Avoid duplicate IDs",
                  "Explain shallow vs deep clone",
                ],
                outcome:
                  "You’ll learn a React-like rendering mindset without React.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most performance issues in vanilla JS apps come from careless DOM creation and removal.",
              realWorldChecks: [
                "Is this element created once or repeatedly?",
                "Can I reuse a template instead of recreating?",
                "Am I removing unused nodes properly?",
              ],
              example:
                "const li = document.createElement('li');\nli.textContent = text;\nlist.append(li);",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between appendChild and innerHTML?",
                "What does cloneNode(true) do?",
                "Why is frequent DOM manipulation expensive?",
                "How does React optimize this?",
              ],
              interviewerTip: "Explain the DOM cost, not just the API syntax.",
              redFlag: "Using innerHTML repeatedly for dynamic lists.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Is this element really needed?",
                "Can this be reused or cloned?",
                "Am I leaking DOM nodes?",
                "How would React handle this?",
              ],
              refactorRule:
                "If you are concatenating HTML strings → switch to createElement.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "Modern web apps rarely rely on static HTML. Lists, cards, comments, notifications, and feeds are created and removed dynamically. Understanding how JavaScript creates and removes DOM nodes helps you appreciate how React automates this process later.",

              sections: [
                {
                  title: "Why Dynamic Element Creation Matters",
                  description:
                    "User actions often change what appears on screen.",
                  examples: [
                    "Adding todo items",
                    "Rendering API data",
                    "Showing notifications",
                  ],
                  analogy:
                    "Like adding or removing items from a live dashboard",
                },
                {
                  title: "createElement()",
                  description: "Creates a new DOM element in memory.",
                  keyPoints: [
                    "Does NOT add element to DOM automatically",
                    "Element exists only in JavaScript until appended",
                  ],
                  code: "const li = document.createElement('li');",
                  takeaway: "Creation ≠ rendering",
                },
                {
                  title: "Setting Content & Attributes",
                  description: "After creation, configure the element.",
                  code: "li.textContent = 'New Item';\nli.classList.add('item');",
                  bestPractice: "Always set content before appending",
                },
                {
                  title: "appendChild()",
                  description: "Adds an element as the last child.",
                  keyPoints: [
                    "Moves existing nodes if already in DOM",
                    "Returns appended node",
                  ],
                  code: "parent.appendChild(li);",
                  useCase: "Adding items to lists",
                },
                {
                  title: "prepend() (Modern API)",
                  description: "Adds an element as the first child.",
                  keyPoints: [
                    "Cleaner than insertBefore",
                    "Supported in modern browsers",
                  ],
                  code: "parent.prepend(li);",
                  ruleOfThumb: "prepend for newest-first UI",
                },
                {
                  title: "remove()",
                  description: "Removes an element from the DOM.",
                  keyPoints: [
                    "No parent reference needed",
                    "Cleaner than removeChild",
                  ],
                  code: "li.remove();",
                  useCase: "Delete buttons, dismiss actions",
                },
                {
                  title: "cloneNode()",
                  description: "Creates a copy of an existing node.",
                  keyPoints: [
                    "cloneNode(false) → shallow copy",
                    "cloneNode(true) → deep copy",
                  ],
                  code: "const clone = card.cloneNode(true);",
                  useCase: "Template-based rendering",
                },
                {
                  title: "Dynamic List Example (Conceptual)",
                  description: "Typical pattern for list creation.",
                  steps: [
                    "Create element",
                    "Set content & classes",
                    "Append to container",
                  ],
                  code: "const li = document.createElement('li');\nli.textContent = text;\nlist.appendChild(li);",
                },
                {
                  title: "Performance Note",
                  description: "Frequent DOM manipulation is expensive.",
                  bestPractices: [
                    "Batch updates when possible",
                    "Avoid unnecessary reflows",
                  ],
                  modernNote: "React optimizes this automatically",
                },
                {
                  title: "How React Changes This",
                  description: "React removes manual DOM creation.",
                  comparison: [
                    "JS → createElement + append",
                    "React → render via state",
                  ],
                  example: "{items.map(item => <li>{item}</li>)}",
                  takeaway:
                    "Today you learn the manual way — React abstracts it",
                },
              ],

              visualStructure: `
User Action
    ↓
createElement
    ↓
Configure element
    ↓
append / prepend
    ↓
Rendered in DOM
    `,

              commonMistakes: [
                {
                  mistake: "Creating elements but not appending",
                  consequence: "Nothing appears on UI",
                  fix: "Append to DOM container",
                },
                {
                  mistake: "Using innerHTML for lists",
                  consequence: "Security + performance issues",
                  fix: "Use createElement",
                },
                {
                  mistake: "Manipulating DOM repeatedly in loops",
                  consequence: "Performance lag",
                  fix: "Minimize direct DOM operations",
                },
              ],

              proTips: [
                "createElement is safer than innerHTML",
                "prepend is great for recent-first feeds",
                "remove() is clean and readable",
                "React automates this entire flow",
              ],

              realWorldAnalogy:
                "DOM creation is like assembling furniture:\n- Create parts\n- Assemble\n- Place in room\n- Remove when needed",
            },

            tasks: [
              "Create list items dynamically",
              "Append cards to container",
              "Remove elements on user action",
            ],

            examples: [
              "const li = document.createElement('li')",
              "parent.appendChild(li)",
            ],

            useCases: [
              "Dynamic lists",
              "User-generated content",
              "Live UI updates",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Create DOM elements programmatically",
              "Insert and remove elements cleanly",
              "Understand performance implications",
              "Prepare for React’s declarative rendering",
            ],
          },
          {
            day: 5,
            topic: "Attributes & Data Handling",
            subTopics: [
              "getAttribute",
              "setAttribute",
              "removeAttribute",
              "data-* attributes",
              "Using dataset API",
            ],
            explanation:
              "Attributes allow storing metadata and controlling element behavior.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Delete Button Using data-id",
                description:
                  "Implement delete functionality using data-id attributes instead of closures.",
                scenarios: [
                  "Render multiple items dynamically",
                  "Attach unique data-id to each element",
                  "Delete correct item on click",
                ],
                requirements: [
                  "Use data-id attribute",
                  "Access value via dataset API",
                  "Avoid inline onclick handlers",
                ],
                outcome:
                  "You’ll understand how large lists handle actions efficiently.",
              },
              {
                id: "pt-2",
                title: "Attribute vs Property Experiment",
                description:
                  "Explore differences between attributes and properties using form inputs.",
                scenarios: [
                  "Set input value using setAttribute",
                  "Update value via input.value",
                  "Compare results after user typing",
                ],
                requirements: [
                  "Use input elements",
                  "Log both attribute and property values",
                  "Explain mismatch in behavior",
                ],
                outcome: "You’ll never confuse attribute vs property again.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Many UI bugs come from reading attributes when the live DOM state has already changed.",
              realWorldChecks: [
                "Am I reading attribute or property?",
                "Is this value dynamic or static?",
                "Should this data live in HTML or JS?",
              ],
              example:
                "console.log(input.getAttribute('value'));\nconsole.log(input.value);",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between attribute and property?",
                "What is data-* used for?",
                "Why is dataset preferred over getAttribute?",
                "How does React avoid data-* usage?",
              ],
              interviewerTip: "Explain real-world usage, not just definitions.",
              redFlag: "Using data-* as application state storage.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Is this value configuration or state?",
                "Does this belong in HTML or JS?",
                "Can React props replace this later?",
              ],
              refactorRule:
                "If logic depends heavily on data-* → consider state-based approach.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "HTML attributes are the bridge between markup and JavaScript logic. They control element behavior and allow you to attach metadata to DOM elements. Understanding attributes and data-* is crucial before moving fully into React, where props and state replace most of this logic.",

              sections: [
                {
                  title: "What Are HTML Attributes?",
                  description:
                    "Attributes define extra information and behavior for elements.",
                  examples: [
                    "id, class, href, src",
                    "disabled, checked, value",
                  ],
                  analogy: "Attributes are configuration settings for elements",
                },
                {
                  title: "getAttribute()",
                  description:
                    "Reads the value of an attribute from an element.",
                  keyPoints: [
                    "Returns string or null",
                    "Reads raw HTML attribute",
                  ],
                  code: "const href = link.getAttribute('href');",
                  useCase: "Reading dynamic element configuration",
                },
                {
                  title: "setAttribute()",
                  description: "Adds or updates an attribute.",
                  keyPoints: [
                    "Always sets value as string",
                    "Useful for toggling behavior",
                  ],
                  code: "button.setAttribute('disabled', 'true');",
                  warning:
                    "Prefer properties for boolean attributes when possible",
                },
                {
                  title: "removeAttribute()",
                  description: "Removes an attribute completely.",
                  code: "button.removeAttribute('disabled');",
                  useCase: "Enable/disable UI interactions",
                },
                {
                  title: "Attributes vs Properties (Important)",
                  description:
                    "Attributes and DOM properties are not always the same.",
                  comparison: [
                    "Attributes → initial HTML",
                    "Properties → live DOM state",
                  ],
                  example: "input.value vs input.getAttribute('value')",
                  takeaway: "Use properties for form values",
                },
                {
                  title: "data-* Attributes (Modern Pattern)",
                  description: "Custom attributes for storing metadata.",
                  keyPoints: [
                    "Always start with data-",
                    "Safe and valid HTML",
                    "Perfect for linking UI to data",
                  ],
                  code: "<button data-id='42'>Delete</button>",
                  useCase: "Storing IDs or types",
                },
                {
                  title: "dataset API",
                  description:
                    "JavaScript-friendly access to data-* attributes.",
                  keyPoints: ["CamelCase access", "Cleaner than getAttribute"],
                  code: "button.dataset.id; // '42'",
                  bestPractice:
                    "Prefer dataset over getAttribute for custom data",
                },
                {
                  title: "Using data-* for Logic",
                  description: "Common pattern for dynamic elements.",
                  code: "button.addEventListener('click', e => {\n  const id = e.target.dataset.id;\n  deleteItem(id);\n});",
                  takeaway: "data-* connects UI and logic",
                },
                {
                  title: "Delete / Edit Logic Example",
                  description: "Real-world CRUD pattern.",
                  steps: [
                    "Store id in data-id",
                    "Read id on click",
                    "Perform action",
                  ],
                  code: "items = items.filter(i => i.id !== Number(id));",
                },
                {
                  title: "How React Replaces This",
                  description: "React avoids manual attribute handling.",
                  comparison: [
                    "DOM → data-* attributes",
                    "React → props & state",
                  ],
                  example:
                    "<button onClick={() => deleteItem(id)}>Delete</button>",
                  takeaway:
                    "This knowledge helps you understand why React exists",
                },
              ],

              visualStructure: `
Element
  ↓
Attributes (HTML)
  ↓
dataset API
  ↓
JavaScript Logic
    `,

              commonMistakes: [
                {
                  mistake: "Using getAttribute for form values",
                  consequence: "Outdated data",
                  fix: "Use element.value",
                },
                {
                  mistake: "Storing complex data in data-*",
                  consequence: "Messy HTML",
                  fix: "Store IDs only",
                },
                {
                  mistake: "Overusing setAttribute",
                  consequence: "Hard-to-debug UI",
                  fix: "Prefer properties & classes",
                },
              ],

              proTips: [
                "data-* is perfect for IDs, not state",
                "dataset API is clean and readable",
                "Attributes configure, JS controls logic",
                "React replaces most attribute logic with props",
              ],

              realWorldAnalogy:
                "data-* attributes are like labels on boxes\nThey tell JS what’s inside without opening them",
            },

            tasks: [
              "Read and update attributes",
              "Use data-id for elements",
              "Build delete/edit logic using data attributes",
            ],

            examples: [
              "element.setAttribute('disabled', true)",
              "element.dataset.id",
            ],

            useCases: [
              "Linking UI to data",
              "Managing dynamic elements",
              "Pre-React state handling",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Work confidently with DOM attributes",
              "Use data-* and dataset API",
              "Link UI elements to logic cleanly",
              "Prepare for React props & events",
            ],
          },
          {
            day: 6,
            topic: "DOM Performance & Best Practices",
            subTopics: [
              "DocumentFragment",
              "Template elements",
              "Minimizing reflows",
              "Batch DOM updates",
              "Separation of logic and UI",
            ],
            explanation:
              "Efficient DOM manipulation improves performance and scalability.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Large List Rendering Optimization",
                description:
                  "Render a list of 500+ items efficiently using DocumentFragment instead of direct DOM appends.",
                scenarios: [
                  "Render list items using appendChild inside a loop",
                  "Refactor to use DocumentFragment",
                  "Compare perceived performance",
                ],
                requirements: [
                  "Use document.createDocumentFragment()",
                  "Append fragment only once",
                  "Avoid innerHTML",
                ],
                outcome:
                  "You’ll understand how batching DOM updates drastically improves performance.",
              },
              {
                id: "pt-2",
                title: "Template-Based Card Rendering",
                description:
                  "Use <template> element to render reusable card UI blocks efficiently.",
                scenarios: [
                  "Define template in HTML",
                  "Clone template for multiple cards",
                  "Populate dynamic data",
                ],
                requirements: [
                  "Use template.content.cloneNode(true)",
                  "Avoid duplicating HTML strings",
                  "Keep JS focused on data",
                ],
                outcome: "You’ll learn scalable UI rendering without React.",
              },
              {
                id: "pt-3",
                title: "Reflow Debugging Exercise",
                description:
                  "Identify layout thrashing caused by mixed DOM reads and writes.",
                scenarios: [
                  "Measure element dimensions",
                  "Modify styles repeatedly",
                  "Refactor to batch operations",
                ],
                requirements: [
                  "Group DOM writes together",
                  "Avoid reading layout between writes",
                ],
                outcome:
                  "You’ll recognize and fix one of the most common performance bugs.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Performance bugs rarely show up in small demos but become critical in real applications.",
              realWorldChecks: [
                "Am I updating DOM inside loops?",
                "Can this UI be built off-screen first?",
                "Am I mixing layout reads and writes?",
              ],
              example:
                "const height = el.offsetHeight;\nel.style.height = '100px';",
            },

            interviewInsights: {
              commonQuestions: [
                "What is reflow and repaint?",
                "Why is DocumentFragment faster?",
                "How does React optimize DOM updates?",
                "What is layout thrashing?",
              ],
              interviewerTip:
                "Explain with real scenarios like rendering lists or dashboards.",
              redFlag:
                "Blindly manipulating DOM inside loops without batching.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Can I batch these DOM updates?",
                "Is this UI repeated?",
                "Should CSS handle visual changes?",
              ],
              refactorRule:
                "If DOM updates are frequent → rethink structure before adding logic.",
            },

            // 📚 Detailed Learning Content (Modern & Performance-Focused)
            content: {
              introduction:
                "DOM manipulation is expensive. Every change can trigger layout recalculations, repaints, and reflows. As applications grow, inefficient DOM updates lead to slow, janky UIs. This lesson teaches you how to write performance-aware JavaScript — and helps you understand why frameworks like React exist.",

              sections: [
                {
                  title: "Why DOM Performance Matters",
                  description:
                    "The browser must recalculate layout and repaint the screen after DOM changes.",
                  keyPoints: [
                    "DOM updates are slow",
                    "Frequent changes cause jank",
                    "Large lists amplify the problem",
                  ],
                  analogy:
                    "Like rearranging furniture every time someone enters a room",
                },
                {
                  title: "What Are Reflow & Repaint?",
                  description: "Two costly browser operations.",
                  definitions: [
                    "Reflow → recalculating layout (sizes & positions)",
                    "Repaint → redrawing pixels",
                  ],
                  takeaway: "More DOM changes = more reflows",
                },
                {
                  title: "DocumentFragment (Key Optimization)",
                  description: "A lightweight container for batch DOM updates.",
                  keyPoints: [
                    "Not part of the live DOM",
                    "No reflows while building",
                    "Appended once",
                  ],
                  code: "const fragment = document.createDocumentFragment();\nitems.forEach(text => {\n  const li = document.createElement('li');\n  li.textContent = text;\n  fragment.appendChild(li);\n});\nlist.appendChild(fragment);",
                  bestPractice: "Use for rendering large lists",
                },
                {
                  title: "Why DocumentFragment Is Fast",
                  description: "Build everything off-screen.",
                  flow: [
                    "Create elements",
                    "Append to fragment",
                    "Attach fragment once",
                  ],
                  takeaway: "One reflow instead of many",
                },
                {
                  title: "Template Element (<template>)",
                  description: "HTML-based reusable UI blueprint.",
                  keyPoints: [
                    "Not rendered by default",
                    "Perfect for repeated UI",
                    "Keeps HTML clean",
                  ],
                  code: "<template id='card-template'>\n  <div class='card'>\n    <h3></h3>\n  </div>\n</template>",
                },
                {
                  title: "Using template with JavaScript",
                  description: "Clone and customize UI efficiently.",
                  code: "const template = document.getElementById('card-template');\nconst clone = template.content.cloneNode(true);\nclone.querySelector('h3').textContent = 'Title';\ncontainer.appendChild(clone);",
                  useCase: "Cards, list items, repeated blocks",
                },
                {
                  title: "Batch DOM Updates (Golden Rule)",
                  description: "Avoid interleaving reads and writes.",
                  badExample:
                    "element.style.width = '100px';\nconsole.log(element.offsetHeight);\nelement.style.height = '100px';",
                  goodPractice: "Group writes together",
                  takeaway: "Write → then read, not mixed",
                },
                {
                  title: "Minimizing Reflows",
                  description: "Small habits make big performance gains.",
                  techniques: [
                    "Use class toggles instead of inline styles",
                    "Avoid layout thrashing",
                    "Batch DOM changes",
                  ],
                },
                {
                  title: "Separation of Logic and UI",
                  description:
                    "Cleaner architecture improves performance and maintainability.",
                  principle: "JS decides WHAT, CSS decides HOW",
                  example: "JS toggles class → CSS handles visuals",
                  takeaway: "Cleaner code, fewer bugs",
                },
                {
                  title: "How React Solves This",
                  description: "React abstracts performance optimizations.",
                  comparison: [
                    "Manual DOM → many reflows",
                    "React → virtual DOM diffing",
                  ],
                  note: "Your understanding helps you write better React",
                },
              ],

              visualStructure: `
Bad Pattern:
JS → DOM → JS → DOM → Reflow → Reflow

Good Pattern:
JS → Fragment / Template → DOM → One Reflow
    `,

              commonMistakes: [
                {
                  mistake: "Appending elements one by one",
                  consequence: "Multiple reflows",
                  fix: "Use DocumentFragment",
                },
                {
                  mistake: "Using innerHTML for large lists",
                  consequence: "Security + performance issues",
                  fix: "Use createElement + fragment",
                },
                {
                  mistake: "Mixing layout reads and writes",
                  consequence: "Layout thrashing",
                  fix: "Batch operations",
                },
              ],

              proTips: [
                "DOM is slow — treat it carefully",
                "Build UI off-screen first",
                "Classes > inline styles",
                "React exists to solve these problems",
              ],

              realWorldAnalogy:
                "Updating DOM is like construction work:\nDo all planning first, then build once",
            },

            tasks: [
              "Render lists using DocumentFragment",
              "Use template tag for repeated UI",
              "Optimize DOM updates",
            ],

            examples: [
              "const fragment = document.createDocumentFragment()",
              "template.content.cloneNode(true)",
            ],

            useCases: [
              "Large lists rendering",
              "Performance-sensitive UIs",
              "Pre-React optimization mindset",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand DOM performance costs",
              "Use DocumentFragment and templates",
              "Minimize reflows and repaints",
              "Build React-ready performance mindset",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Interactive Blog Platform",
            subTopics: [
              "Dynamic DOM rendering",
              "CRUD operations in UI",
              "Theme toggle",
              "Data attributes usage",
              "UI state management",
            ],
            explanation:
              "This project combines all DOM concepts into a real-world interactive app.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Post Rendering Engine",
                description:
                  "Build a reusable function that renders the entire blog UI from data.",
                scenarios: [
                  "Clear container before render",
                  "Loop over posts array",
                  "Create DOM nodes dynamically",
                ],
                requirements: [
                  "Use createElement (no innerHTML)",
                  "Use a single renderPosts() function",
                  "UI must fully re-render from data",
                ],
                outcome:
                  "You’ll understand how data-driven rendering works without frameworks.",
              },
              {
                id: "pt-2",
                title: "Edit Post Workflow",
                description:
                  "Implement edit functionality using data-id instead of array index.",
                scenarios: [
                  "Click edit button",
                  "Load post data into inputs",
                  "Update post object",
                  "Re-render UI",
                ],
                requirements: [
                  "Use data-id to identify post",
                  "Use map() for updating post",
                  "Persist updated data",
                ],
                outcome:
                  "You’ll learn safe update logic used in real applications.",
              },
              {
                id: "pt-3",
                title: "Theme State Persistence",
                description:
                  "Persist dark/light theme choice using localStorage.",
                scenarios: [
                  "Toggle theme class",
                  "Save theme preference",
                  "Restore theme on page load",
                ],
                requirements: [
                  "Store theme value in localStorage",
                  "Apply class on initial load",
                ],
                outcome: "You’ll understand UI state persistence beyond data.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Projects fail when UI and data fall out of sync — this project fixes that mindset.",
              realWorldChecks: [
                "Does UI always reflect data state?",
                "Am I updating DOM directly or via data?",
                "Is re-render predictable?",
              ],
              example: "posts.push(newPost);\nrenderPosts();",
            },

            interviewInsights: {
              commonQuestions: [
                "How would you build CRUD without a framework?",
                "How do you keep UI and data in sync?",
                "Why should UI be data-driven?",
                "How does this map to React state?",
              ],
              interviewerTip:
                "Explain the flow: data → render → user action → data update.",
              redFlag: "Manipulating DOM directly without updating data model.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Data first, DOM second",
                "Every button maps to a data action",
                "UI is disposable — data is truth",
              ],
              refactorRule:
                "If you are fixing UI bugs by touching DOM nodes directly, redesign data flow.",
            },

            // 📚 Detailed Learning Content (Capstone Project)
            content: {
              introduction:
                "This mini project brings together everything learned in DOM manipulation. You will build a small but powerful interactive blog platform where users can add, edit, delete, and persist blog posts. This mirrors how frontend applications work before frameworks like React abstract the complexity.",

              sections: [
                {
                  title: "Project Overview",
                  description:
                    "You are building a blog-like interface using only HTML, CSS, and JavaScript.",
                  features: [
                    "Create blog posts dynamically",
                    "Edit and delete posts",
                    "Toggle dark/light theme",
                    "Persist data using localStorage",
                  ],
                  analogy: "This is a mini CMS built with vanilla JavaScript",
                },
                {
                  title: "Data Model (Mental Model)",
                  description:
                    "UI should be driven by data, not hardcoded HTML.",
                  dataShape: "{ id, title, content, createdAt }",
                  rule: "Array of objects = single source of truth",
                  takeaway: "This mindset directly maps to React state",
                },
                {
                  title: "Dynamic DOM Rendering",
                  description: "Posts are rendered from JavaScript data.",
                  steps: [
                    "Loop through posts array",
                    "Create DOM elements",
                    "Append to container",
                  ],
                  code: "posts.forEach(post => {\n  const el = createPostElement(post);\n  postsContainer.appendChild(el);\n});",
                },
                {
                  title: "Create (Add New Post)",
                  description: "User adds a new blog post.",
                  flow: [
                    "Read input values",
                    "Create post object",
                    "Push into posts array",
                    "Re-render UI",
                    "Save to localStorage",
                  ],
                  takeaway: "Data change → UI update",
                },
                {
                  title: "Read (Render Posts)",
                  description: "Render all posts dynamically.",
                  bestPractice: "Clear container before re-rendering",
                  note: "Same logic React uses internally",
                },
                {
                  title: "Update (Edit Post)",
                  description:
                    "Editing existing content using data attributes.",
                  technique: "Use data-id to identify post",
                  code: "const id = e.target.dataset.id;",
                  takeaway: "UI elements are linked to data, not indexes",
                },
                {
                  title: "Delete (Remove Post)",
                  description: "Remove post from data and UI.",
                  flow: [
                    "Get post id from data-id",
                    "Filter array",
                    "Re-render UI",
                    "Update localStorage",
                  ],
                  code: "posts = posts.filter(p => p.id !== id);",
                },
                {
                  title: "Theme Toggle (Dark / Light Mode)",
                  description: "Visual state controlled by class toggling.",
                  technique: "Toggle a single class on body",
                  code: "document.body.classList.toggle('dark');",
                  bestPractice: "CSS handles visuals, JS handles state",
                },
                {
                  title: "Persisting Data with localStorage",
                  description: "Keep posts after page reload.",
                  flow: [
                    "Save posts array as JSON",
                    "Read data on page load",
                    "Render stored posts",
                  ],
                  code: "localStorage.setItem('posts', JSON.stringify(posts));",
                  warning: "localStorage is synchronous – keep data small",
                },
                {
                  title: "UI State Management (Core Concept)",
                  description: "The UI reflects the current state of data.",
                  principles: [
                    "No hardcoded HTML",
                    "Data drives UI",
                    "Single source of truth",
                  ],
                  takeaway: "This is React thinking without React",
                },
                {
                  title: "Performance Considerations",
                  description: "Avoid unnecessary DOM operations.",
                  bestPractices: [
                    "Use DocumentFragment if needed",
                    "Batch DOM updates",
                    "Avoid innerHTML for large content",
                  ],
                },
                {
                  title: "How This Transitions to React",
                  description:
                    "Everything you did manually becomes declarative.",
                  comparison: [
                    "DOM manipulation → JSX rendering",
                    "posts array → React state",
                    "event listeners → React handlers",
                  ],
                  example: "{posts.map(post => <Post key={post.id} />)}",
                  takeaway: "You are now React-ready",
                },
              ],

              visualStructure: `
User Action
    ↓
Update Data (Array of Posts)
    ↓
Re-render DOM
    ↓
Persist to localStorage
    `,

              commonMistakes: [
                {
                  mistake: "Manipulating DOM without updating data",
                  consequence: "UI and data go out of sync",
                  fix: "Always update data first",
                },
                {
                  mistake: "Using index instead of id",
                  consequence: "Wrong item edited/deleted",
                  fix: "Use unique IDs with data-*",
                },
                {
                  mistake: "Overusing innerHTML",
                  consequence: "Security and performance issues",
                  fix: "Use createElement",
                },
              ],

              proTips: [
                "Think data-first, not DOM-first",
                "One class toggle can control full theme",
                "localStorage simulates backend persistence",
                "If this makes sense, React will feel natural",
              ],

              realWorldAnalogy:
                "This project is like running a small publishing system without a framework",
            },

            tasks: [
              "Add new blog posts",
              "Edit post titles",
              "Delete posts",
              "Toggle dark/light mode",
              "Persist posts using localStorage",
            ],

            examples: ["postsContainer.appendChild(postElement)"],

            useCases: [
              "Content management systems",
              "Interview-ready DOM project",
              "Strong bridge to React",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "2–3 hours",

            // 🎯 Learning objectives
            objectives: [
              "Build a complete interactive DOM app",
              "Apply CRUD operations in UI",
              "Manage UI state manually",
              "Understand React’s value clearly",
            ],
          },
        ],
      },
      {
        week: 12,
        weekGoal:
          "Master JavaScript events to build highly interactive web applications",
        weekOutcome:
          "Ability to handle user interactions efficiently using events and delegation",
        canLearn: ["students", "beginners", "professionals"],
        miniProject: "Interactive Browser Game",
        days: [
          {
            day: 1,
            topic: "Introduction to Events & addEventListener",
            subTopics: [
              "What are events",
              "addEventListener syntax",
              "Inline events vs event listeners",
              "Multiple event listeners on same element",
              "Removing event listeners (removeEventListener)",
            ],
            explanation:
              "Events allow JavaScript to react to user actions like clicks, typing, and page load.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Event Wiring Drill",
                description:
                  "Attach multiple event listeners to the same element and observe execution order.",
                scenarios: [
                  "Attach 2 click listeners to one button",
                  "Log different messages",
                  "Confirm both fire independently",
                ],
                requirements: [
                  "Use addEventListener only",
                  "Use named handler functions",
                ],
                outcome:
                  "Clear understanding that one event can trigger multiple behaviors.",
              },
              {
                id: "pt-2",
                title: "One-Time Action Handler",
                description:
                  "Create an event that runs only once and then removes itself.",
                scenarios: [
                  "Click button once",
                  "Perform action",
                  "Listener auto-removes",
                ],
                requirements: [
                  "Use removeEventListener",
                  "Do not use anonymous functions",
                ],
                outcome:
                  "You’ll understand why function references matter in events.",
              },
              {
                id: "pt-3",
                title: "Inline vs Modern Refactor",
                description:
                  "Convert inline HTML events into clean addEventListener-based logic.",
                scenarios: [
                  "Remove onclick from HTML",
                  "Move logic to JS file",
                  "Attach event programmatically",
                ],
                requirements: [
                  "HTML must contain no JS logic",
                  "JS controls all event wiring",
                ],
                outcome: "Professional separation of concerns mindset.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most UI bugs happen because event handlers fire unexpectedly or multiple times.",
              realWorldChecks: [
                "Is my handler running only when expected?",
                "Am I accidentally calling the function?",
                "Can I remove this listener safely?",
              ],
              example:
                "button.addEventListener('click', handleClick); // ✅\nbutton.addEventListener('click', handleClick()); // ❌",
            },

            interviewInsights: {
              commonQuestions: [
                "What is the difference between onclick and addEventListener?",
                "Can multiple event listeners exist on one element?",
                "Why can't anonymous functions be removed?",
                "How does event handling work in React?",
              ],
              interviewerTip:
                "Always explain events in terms of user action → handler → state/UI change.",
              redFlag:
                "Using inline events or calling handler functions during attachment.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Event = signal, not logic",
                "Handler = reaction, not execution",
                "HTML should never contain JS logic",
              ],
              refactorRule:
                "If HTML knows about JavaScript functions, your architecture is wrong.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "Websites are not static documents anymore — they respond to user actions. Clicking buttons, typing in inputs, scrolling pages — all of this is powered by events. Understanding JavaScript events is the foundation of interactive UI and a direct bridge to React’s event system.",

              sections: [
                {
                  title: "What Are Events?",
                  description:
                    "Events are signals that something has happened.",
                  examples: [
                    "User clicks a button",
                    "User types in input",
                    "Page finishes loading",
                  ],
                  analogy: "Events are like notifications sent to JavaScript",
                },
                {
                  title: "Common Event Types",
                  description: "JavaScript listens to many kinds of events.",
                  categories: [
                    "Mouse → click, dblclick, mouseenter",
                    "Keyboard → keydown, keyup",
                    "Form → submit, input, change",
                    "Window → load, resize, scroll",
                  ],
                  takeaway: "Everything user does generates events",
                },
                {
                  title: "addEventListener (Modern Standard)",
                  description: "The recommended way to listen to events.",
                  syntax: "element.addEventListener(eventType, callback)",
                  code: "button.addEventListener('click', () => {\n  console.log('Clicked');\n});",
                  keyPoints: [
                    "Separation of HTML and JavaScript",
                    "Supports multiple listeners",
                    "Cleaner and scalable",
                  ],
                },
                {
                  title: "Inline Events vs addEventListener",
                  description: "Inline events are outdated.",
                  comparison: [
                    "Inline → <button onclick='doSomething()'> ❌",
                    "addEventListener → JS controlled ✅",
                  ],
                  problemsWithInline: [
                    "Mixes HTML and JS",
                    "Hard to maintain",
                    "No multiple handlers",
                  ],
                  ruleOfThumb: "Never use inline events in modern apps",
                },
                {
                  title: "Event Handler Functions",
                  description: "Events trigger callback functions.",
                  example:
                    "function handleClick() {\n  console.log('Button clicked');\n}\nbutton.addEventListener('click', handleClick);",
                  note: "Do NOT call the function while passing it",
                },
                {
                  title: "Multiple Event Listeners on Same Element",
                  description: "One element can respond to multiple actions.",
                  code: "button.addEventListener('click', logClick);\nbutton.addEventListener('click', trackAnalytics);",
                  takeaway: "Each listener runs independently",
                },
                {
                  title: "Removing Event Listeners",
                  description: "Sometimes listeners should be removed.",
                  keyPoints: [
                    "Requires same function reference",
                    "Anonymous functions cannot be removed",
                  ],
                  code: "function once() {\n  console.log('Clicked once');\n  button.removeEventListener('click', once);\n}\nbutton.addEventListener('click', once);",
                  useCase: "One-time actions",
                },
                {
                  title: "Common Beginner Mistakes",
                  description: "These cause bugs frequently.",
                  mistakes: [
                    "Calling function instead of passing reference",
                    "Using anonymous function when removal is needed",
                    "Using inline event handlers",
                  ],
                },
                {
                  title: "Event Flow Preview (Teaser)",
                  description: "Events don’t just fire once.",
                  note: "Events bubble and capture — covered next",
                  takeaway: "Understanding this helps avoid UI bugs",
                },
                {
                  title: "How This Maps to React",
                  description: "React uses the same concept declaratively.",
                  comparison: [
                    "JS → addEventListener",
                    "React → onClick={handleClick}",
                  ],
                  example: "<button onClick={handleClick}>Click</button>",
                  takeaway: "Same idea, cleaner syntax",
                },
              ],

              visualStructure: `
User Action
    ↓
Event Fired
    ↓
Event Listener
    ↓
Callback Function
    ↓
UI / Data Update
    `,

              commonMistakes: [
                {
                  mistake: "Using inline onclick",
                  consequence: "Unmaintainable code",
                  fix: "Use addEventListener",
                },
                {
                  mistake: "Calling handler while attaching",
                  consequence: "Event runs immediately",
                  fix: "Pass function reference",
                },
                {
                  mistake: "Unable to remove listener",
                  consequence: "Memory leaks",
                  fix: "Use named functions",
                },
              ],

              proTips: [
                "Events drive UI behavior",
                "One action can trigger many listeners",
                "Keep logic inside handlers small",
                "This knowledge directly transfers to React",
              ],

              realWorldAnalogy:
                "Events are like doorbells — JavaScript decides what to do when they ring",
            },

            tasks: [
              "Attach click event to button",
              "Trigger multiple actions on single click",
              "Remove event listener after first click",
            ],

            examples: [
              "button.addEventListener('click', handleClick)",
              "element.removeEventListener('click', handler)",
            ],

            useCases: [
              "Button interactions",
              "User-triggered actions",
              "UI event handling foundation",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand what events are",
              "Use addEventListener correctly",
              "Attach and remove event listeners",
              "Prepare for React event handling",
            ],
          },
          {
            day: 2,
            topic: "Common Event Types",
            subTopics: [
              "Mouse events (click, dblclick, mouseover, mouseout)",
              "Keyboard events (keydown, keyup)",
              "Input and change events",
              "Load and DOMContentLoaded",
            ],
            explanation:
              "Different event types respond to different user actions in the browser.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Event Selection Drill",
                description:
                  "Identify the correct event type for different real-world UI behaviors.",
                scenarios: [
                  "Live search input",
                  "Submit form after selection",
                  "Keyboard shortcut (Ctrl + S)",
                  "Hover-based tooltip",
                ],
                requirements: [
                  "Explain why a specific event was chosen",
                  "Avoid using generic click everywhere",
                ],
                outcome:
                  "You’ll stop guessing events and start choosing them intentionally.",
              },
              {
                id: "pt-2",
                title: "Input vs Change Experiment",
                description:
                  "Compare input and change events by logging when each fires.",
                scenarios: [
                  "Type text slowly",
                  "Paste content",
                  "Blur input field",
                ],
                requirements: [
                  "Attach both input and change listeners",
                  "Log timestamps for comparison",
                ],
                outcome:
                  "Clear mental model of real-time vs committed input behavior.",
              },
              {
                id: "pt-3",
                title: "Keyboard Power User Task",
                description: "Build a simple keyboard shortcut using keydown.",
                scenarios: [
                  "Detect specific key press",
                  "Prevent default browser behavior",
                  "Trigger custom action",
                ],
                requirements: ["Use event.key", "Handle only intended keys"],
                outcome:
                  "Confidence in building accessible, keyboard-driven UX.",
              },
              {
                id: "pt-4",
                title: "DOM Ready Safety Check",
                description:
                  "Demonstrate what breaks when DOMContentLoaded is ignored.",
                scenarios: [
                  "Access element before DOM loads",
                  "Fix using DOMContentLoaded",
                ],
                requirements: [
                  "Log null reference issue",
                  "Explain why it happened",
                ],
                outcome: "You’ll never again wonder why elements are null.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Wrong event choices lead to laggy UI, missed updates, or broken accessibility.",
              realWorldChecks: [
                "Do I need live updates or final value?",
                "Is this action mouse-only or keyboard-friendly?",
                "Does this code depend on DOM being ready?",
              ],
              example:
                "input.addEventListener('input', handleLive);\nselect.addEventListener('change', handleFinal);",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between input and change events?",
                "keydown vs keyup — when to use which?",
                "DOMContentLoaded vs load?",
                "How does React handle events internally?",
              ],
              interviewerTip:
                "Explain events in terms of UX impact, not just definitions.",
              redFlag:
                "Using click for everything without understanding user intent.",
            },

            editorGuidance: {
              mentalChecklist: [
                "User intent decides event type",
                "Live UX → input",
                "Final action → change / submit",
                "Keyboard support = better accessibility",
              ],
              refactorRule:
                "If UI feels delayed or unresponsive, the event choice is probably wrong.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "Events represent how users interact with your application. Choosing the correct event type is crucial for performance, correctness, and user experience. This lesson covers the most commonly used events in real-world apps and how they map to modern React usage.",

              sections: [
                {
                  title: "Event Categories (Mental Model)",
                  description: "Events can be grouped by what triggers them.",
                  categories: [
                    "Mouse → pointer interactions",
                    "Keyboard → typing & shortcuts",
                    "Form/Input → user data entry",
                    "Document/Window → page lifecycle",
                  ],
                  takeaway: "Pick the event based on user intent, not habit",
                },
                {
                  title: "Mouse Events",
                  description: "Triggered by mouse or pointer actions.",
                  keyEvents: [
                    "click → single click",
                    "dblclick → double click",
                    "mouseover → pointer enters element",
                    "mouseout → pointer leaves element",
                  ],
                  code: "box.addEventListener('mouseover', () => {\n  box.classList.add('hover');\n});\n\nbox.addEventListener('mouseout', () => {\n  box.classList.remove('hover');\n});",
                  bestPractice:
                    "Use CSS :hover when possible; JS when logic is needed",
                },
                {
                  title: "Keyboard Events",
                  description: "Triggered when user presses keys.",
                  keyEvents: [
                    "keydown → fires when key is pressed",
                    "keyup → fires when key is released",
                  ],
                  code: "document.addEventListener('keydown', (e) => {\n  console.log(e.key);\n});",
                  notes: [
                    "keydown fires continuously when held",
                    "keyup fires once on release",
                  ],
                  useCase: "Keyboard shortcuts, accessibility",
                },
                {
                  title: "Input Event (Modern Standard)",
                  description: "Fires whenever input value changes.",
                  keyPoints: [
                    "Works for typing, paste, delete",
                    "Fires immediately",
                  ],
                  code: "input.addEventListener('input', e => {\n  console.log(e.target.value);\n});",
                  bestPractice: "Prefer input over change for live updates",
                },
                {
                  title: "Change Event",
                  description: "Fires when value is committed.",
                  behavior: [
                    "Input → fires on blur",
                    "Select → fires on selection",
                  ],
                  code: "select.addEventListener('change', e => {\n  console.log(e.target.value);\n});",
                  takeaway: "Use change when you want final value only",
                },
                {
                  title: "Load vs DOMContentLoaded",
                  description: "Two important lifecycle events.",
                  comparison: [
                    "DOMContentLoaded → HTML parsed",
                    "load → all assets loaded (images, CSS)",
                  ],
                  code: "document.addEventListener('DOMContentLoaded', () => {\n  initApp();\n});",
                  bestPractice: "Use DOMContentLoaded for JS initialization",
                },
                {
                  title: "Why DOMContentLoaded Matters",
                  description: "Ensures elements exist before selection.",
                  problem: "JS runs before DOM → null errors",
                  solution: "Wait for DOMContentLoaded",
                },
                {
                  title: "Combining Event Types",
                  description: "Real apps use multiple events together.",
                  example: "Input + keydown + click",
                  takeaway: "Events work together to create UX",
                },
                {
                  title: "How This Maps to React",
                  description: "Same concepts, different syntax.",
                  mapping: [
                    "click → onClick",
                    "input → onChange",
                    "keydown → onKeyDown",
                  ],
                  example: "<input onChange={handleChange} />",
                  note: "React normalizes events across browsers",
                },
              ],

              visualStructure: `
User Action
    ↓
Event Type
    ↓
Event Listener
    ↓
Callback Logic
    `,

              commonMistakes: [
                {
                  mistake: "Using change instead of input",
                  consequence: "Delayed UI updates",
                  fix: "Use input for live feedback",
                },
                {
                  mistake: "Running JS before DOM loads",
                  consequence: "null element errors",
                  fix: "Use DOMContentLoaded",
                },
                {
                  mistake: "Overusing JS for hover",
                  consequence: "Unnecessary complexity",
                  fix: "Prefer CSS :hover",
                },
              ],

              proTips: [
                "Use input for real-time UX",
                "Keyboard events improve accessibility",
                "DOMContentLoaded is your safe start point",
                "React events are just abstractions over these",
              ],

              realWorldAnalogy:
                "Events are sensors — each detects a different kind of user action",
            },

            tasks: [
              "Log key presses",
              "Change UI on mouse hover",
              "Run JS after DOM is fully loaded",
            ],

            examples: [
              "document.addEventListener('DOMContentLoaded', fn)",
              "input.addEventListener('input', fn)",
            ],

            useCases: [
              "Hover effects",
              "Keyboard shortcuts",
              "Live UI updates",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Recognize common browser event types",
              "Choose correct events for UX needs",
              "Handle keyboard and input events",
              "Prepare for React event handlers",
            ],
          },
          {
            day: 3,
            topic: "Event Object & Prevent Default",
            subTopics: [
              "Event object (e)",
              "target vs currentTarget",
              "preventDefault()",
              "stopPropagation()",
              "Event properties (type, key, clientX)",
            ],
            explanation:
              "The event object provides contextual information about the triggered event.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Event Object Inspection Drill",
                description:
                  "Deeply inspect the event object to understand what data is available for different event types.",
                scenarios: [
                  "Click event on nested elements",
                  "Keydown event on document",
                  "Mouse move inside a container",
                ],
                requirements: [
                  "Log full event object",
                  "Identify at least 5 useful properties per event",
                  "Explain when each property is useful",
                ],
                outcome:
                  "You’ll stop treating events as magic and start using them intentionally.",
              },
              {
                id: "pt-2",
                title: "target vs currentTarget Bug Fix",
                description:
                  "Reproduce a real-world bug caused by misunderstanding target and currentTarget.",
                scenarios: [
                  "Clicking child inside a parent listener",
                  "Using wrong element for logic",
                ],
                requirements: [
                  "Log both target and currentTarget",
                  "Fix logic using correct reference",
                ],
                outcome: "You’ll never break event delegation logic again.",
              },
              {
                id: "pt-3",
                title: "Form Hijacking Task",
                description:
                  "Take full control of a form submission using JavaScript.",
                scenarios: [
                  "Prevent page reload",
                  "Validate input before submit",
                  "Conditionally allow submission",
                ],
                requirements: [
                  "Use preventDefault() correctly",
                  "Handle invalid input gracefully",
                ],
                outcome: "Confidence in building JS-controlled forms.",
              },
              {
                id: "pt-4",
                title: "Propagation Control Experiment",
                description:
                  "Observe how stopPropagation affects nested event listeners.",
                scenarios: [
                  "Parent and child click listeners",
                  "Blocking parent behavior intentionally",
                ],
                requirements: [
                  "Demonstrate bubbling order",
                  "Use stopPropagation only where required",
                ],
                outcome: "Clear understanding of event flow and control.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most UI bugs come from incorrect assumptions about event targets or browser default behavior.",
              realWorldChecks: [
                "Am I using target or currentTarget intentionally?",
                "Should the browser handle this or JavaScript?",
                "Is event bubbling helping or hurting here?",
              ],
              example:
                "list.addEventListener('click', e => {\n  if (e.target.matches('li')) {\n    handleItem(e.target.dataset.id);\n  }\n});",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between target and currentTarget?",
                "Why is preventDefault important in forms?",
                "What happens if stopPropagation is overused?",
                "How does event bubbling work?",
              ],
              interviewerTip:
                "Explain bugs you’ve seen due to wrong event handling — interviewers love real stories.",
              redFlag:
                "Using stopPropagation everywhere without understanding event flow.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Every event comes with context (event object)",
                "Forms almost always need preventDefault",
                "target can change, currentTarget doesn’t",
                "stopPropagation is a scalpel, not a hammer",
              ],
              refactorRule:
                "If event-based logic feels confusing, log the event object first.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "Whenever an event occurs, the browser sends detailed information about that event to JavaScript. This information is packaged inside the event object. Mastering the event object is critical for form handling, keyboard shortcuts, mouse tracking, and clean UI logic — and it directly maps to how React handles events.",

              sections: [
                {
                  title: "What Is the Event Object?",
                  description:
                    "The event object contains information about what happened.",
                  keyPoints: [
                    "Automatically passed to event handlers",
                    "Commonly named e or event",
                    "Different events expose different properties",
                  ],
                  code: "button.addEventListener('click', (e) => {\n  console.log(e);\n});",
                  analogy: "The event object is a report of what just happened",
                },
                {
                  title: "Common Event Object Properties",
                  description: "Frequently used properties across event types.",
                  properties: [
                    "type → event name (click, keydown, etc.)",
                    "target → element that triggered the event",
                    "currentTarget → element listening to the event",
                    "key → key pressed (keyboard events)",
                    "clientX / clientY → mouse position",
                  ],
                  code: "console.log(e.type);\nconsole.log(e.target);\nconsole.log(e.clientX, e.clientY);",
                },
                {
                  title: "target vs currentTarget (Very Important)",
                  description: "These two are often confused and cause bugs.",
                  comparison: [
                    "target → actual element clicked",
                    "currentTarget → element with event listener",
                  ],
                  example:
                    "ul.addEventListener('click', e => {\n  console.log(e.target);        // clicked li\n  console.log(e.currentTarget); // ul\n});",
                  takeaway: "target changes, currentTarget does not",
                },
                {
                  title: "preventDefault()",
                  description: "Stops the browser’s default behavior.",
                  commonDefaults: [
                    "Form submit → page reload",
                    "Link click → navigation",
                    "Checkbox → auto toggle",
                  ],
                  code: "form.addEventListener('submit', e => {\n  e.preventDefault();\n  console.log('Form handled manually');\n});",
                  bestPractice: "Always prevent default for JS-handled forms",
                },
                {
                  title: "stopPropagation()",
                  description: "Stops event from bubbling up the DOM tree.",
                  keyPoints: ["Controls event flow", "Used sparingly"],
                  code: "button.addEventListener('click', e => {\n  e.stopPropagation();\n});",
                  warning: "Overuse makes debugging harder",
                },
                {
                  title: "Event Bubbling (Quick Recap)",
                  description: "Events bubble from child → parent.",
                  flow: "button → div → body → document",
                  note: "Covered in depth with delegation next",
                },
                {
                  title: "Keyboard Event Properties",
                  description: "Keyboard events expose key-related data.",
                  code: "document.addEventListener('keydown', e => {\n  if (e.key === 'Escape') closeModal();\n});",
                  bestPractice: "Use e.key instead of keyCode (deprecated)",
                },
                {
                  title: "Mouse Event Properties",
                  description: "Useful for custom interactions.",
                  code: "box.addEventListener('mousemove', e => {\n  console.log(e.clientX, e.clientY);\n});",
                  useCase: "Custom sliders, drag logic",
                },
                {
                  title: "Form Handling Example (Real World)",
                  description: "Prevent default and read values.",
                  code: "form.addEventListener('submit', e => {\n  e.preventDefault();\n  const value = input.value;\n  save(value);\n});",
                  takeaway: "JS controls behavior, browser steps aside",
                },
                {
                  title: "How This Maps to React",
                  description: "React passes a synthetic event object.",
                  comparison: [
                    "JS → e.preventDefault()",
                    "React → e.preventDefault()",
                  ],
                  example: "<form onSubmit={e => { e.preventDefault(); }}>",
                  takeaway: "Same logic, different environment",
                },
              ],

              visualStructure: `
User Action
    ↓
Event Fired
    ↓
Event Object (e)
    ↓
target / properties
    ↓
Logic / UI Update
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting preventDefault on forms",
                  consequence: "Unexpected page reload",
                  fix: "Call e.preventDefault()",
                },
                {
                  mistake: "Confusing target and currentTarget",
                  consequence: "Wrong element logic",
                  fix: "Understand bubbling",
                },
                {
                  mistake: "Overusing stopPropagation",
                  consequence: "Broken event chains",
                  fix: "Use only when required",
                },
              ],

              proTips: [
                "Always inspect the event object when unsure",
                "Forms almost always need preventDefault",
                "Use e.key, not keyCode",
                "React events follow same mental model",
              ],

              realWorldAnalogy:
                "The event object is a detailed incident report of a user action",
            },

            tasks: [
              "Inspect event object",
              "Prevent form submission",
              "Track mouse coordinates",
            ],

            examples: ["e.preventDefault()", "e.target.value"],

            useCases: [
              "Form handling",
              "Custom interaction logic",
              "Keyboard and mouse-driven UI",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand the event object deeply",
              "Differentiate target vs currentTarget",
              "Control default browser behavior",
              "Prepare for React form and event handling",
            ],
          },
          {
            day: 4,
            topic: "Event Bubbling & Capturing",
            subTopics: [
              "Event propagation",
              "Bubbling phase",
              "Capturing phase",
              "stopPropagation()",
              "Event flow order",
            ],
            explanation:
              "Events propagate through the DOM in bubbling and capturing phases.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Visual Event Flow Tracing",
                description:
                  "Visually trace how a single click event travels through capturing, target, and bubbling phases.",
                setup: [
                  "document → body → parent → child",
                  "Attach listeners at multiple levels",
                ],
                requirements: [
                  "Log phase order clearly",
                  "Mark which listener fires in which phase",
                ],
                outcome:
                  "You’ll build a rock-solid mental model of event flow.",
              },
              {
                id: "pt-2",
                title: "Capturing Phase Use-Case Simulation",
                description:
                  "Implement a real scenario where capturing phase is actually useful.",
                scenario:
                  "Intercept clicks globally before they reach sensitive child elements",
                requirements: [
                  "Use addEventListener with capture=true",
                  "Explain why bubbling alone wouldn’t work",
                ],
                outcome:
                  "You’ll know WHEN capturing is justified (rare but powerful).",
              },
              {
                id: "pt-3",
                title: "stopPropagation Debug Drill",
                description:
                  "Intentionally break a UI using stopPropagation, then fix it.",
                steps: [
                  "Add stopPropagation in child",
                  "Observe broken parent behavior",
                  "Refactor to a cleaner solution",
                ],
                requirements: [
                  "Explain why the bug happened",
                  "Justify final fix",
                ],
                outcome:
                  "You’ll stop misusing stopPropagation in real projects.",
              },
              {
                id: "pt-4",
                title: "Nested Card + Button Pattern",
                description:
                  "Implement a common UI pattern used in dashboards and feeds.",
                scenario:
                  "Card is clickable, button inside card has separate action",
                requirements: [
                  "Prevent unwanted card click",
                  "Keep intent clear and readable",
                ],
                outcome: "Confidence handling nested interactive components.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most unexpected UI bugs are caused by misunderstanding event propagation.",
              realWorldChecks: [
                "Is this handler firing because of bubbling?",
                "Should parent react to child action?",
                "Am I blocking behavior instead of designing it?",
              ],
              debugTip:
                "Add logs at document, parent, and child to instantly see flow.",
            },

            interviewInsights: {
              commonQuestions: [
                "Explain event bubbling vs capturing",
                "Default phase of addEventListener?",
                "What happens when stopPropagation is called?",
                "Why does React rely on event bubbling?",
              ],
              strongAnswerPattern:
                "Explain flow → give example → mention real bug → explain fix",
              redFlag: "Saying ‘I always use stopPropagation to fix issues’.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Events travel, they don’t stay put",
                "Bubbling is default, capturing is opt-in",
                "stopPropagation should feel uncomfortable",
                "Design intent > blocking behavior",
              ],
              refactorRule:
                "If stopPropagation feels necessary everywhere, redesign event structure.",
            },

            // 📚 Detailed Learning Content (Modern & React-Ready)
            content: {
              introduction:
                "When an event occurs, it doesn’t just affect a single element. It travels through the DOM tree in a specific order called event propagation. Understanding bubbling and capturing is essential for building predictable UI behavior, avoiding duplicate actions, and writing performance-efficient code.",

              sections: [
                {
                  title: "What Is Event Propagation?",
                  description:
                    "Event propagation defines how an event travels through the DOM tree.",
                  keyPoints: [
                    "Events move through parent and child elements",
                    "More than one element can respond to the same event",
                  ],
                  analogy: "Like a ripple traveling through water",
                },
                {
                  title: "The Three Phases of Event Flow",
                  description: "Every event goes through three phases.",
                  phases: [
                    "1. Capturing phase (top → target)",
                    "2. Target phase (actual element)",
                    "3. Bubbling phase (target → top)",
                  ],
                  takeaway: "Most events you handle are in the bubbling phase",
                },
                {
                  title: "Bubbling Phase (Most Common)",
                  description: "Event bubbles up from the target to ancestors.",
                  keyPoints: [
                    "Default behavior for addEventListener",
                    "Child first, then parent",
                  ],
                  code: "child.addEventListener('click', () => console.log('Child'));\nparent.addEventListener('click', () => console.log('Parent'));",
                  outputOrder: "Child → Parent",
                  bestPractice: "This is the phase used most often",
                },
                {
                  title: "Capturing Phase (Rare but Useful)",
                  description: "Event travels from parent to child.",
                  keyPoints: [
                    "Enabled by third parameter = true",
                    "Runs before bubbling",
                  ],
                  code: "parent.addEventListener('click', () => console.log('Parent'), true);\nchild.addEventListener('click', () => console.log('Child'));",
                  outputOrder: "Parent → Child",
                  useCase: "Global interception logic",
                },
                {
                  title: "Event Flow Order (Visual)",
                  description: "Understanding execution order avoids bugs.",
                  flow: [
                    "Capturing: document → body → parent",
                    "Target: clicked element",
                    "Bubbling: parent → body → document",
                  ],
                  takeaway: "Same event, multiple listeners",
                },
                {
                  title: "stopPropagation()",
                  description: "Stops the event from moving further.",
                  keyPoints: [
                    "Stops bubbling and capturing",
                    "Prevents parent handlers from firing",
                  ],
                  code: "child.addEventListener('click', e => {\n  e.stopPropagation();\n});",
                  warning: "Use sparingly",
                },
                {
                  title: "When NOT to Use stopPropagation",
                  description: "It can hide bugs.",
                  problems: ["Breaks parent logic", "Harder to debug"],
                  ruleOfThumb: "Prefer event delegation instead",
                },
                {
                  title: "Nested UI Example",
                  description: "Common real-world case.",
                  scenario: "Card clickable + button inside card",
                  solution: "Button stops propagation",
                  takeaway: "Control intent carefully",
                },
                {
                  title: "How This Relates to Event Delegation",
                  description: "Bubbling enables delegation.",
                  note: "Covered next lesson",
                  teaser: "One listener for many elements",
                },
                {
                  title: "How This Maps to React",
                  description: "React uses event delegation internally.",
                  facts: ["Single listener at root", "Synthetic event system"],
                  takeaway:
                    "Understanding bubbling = understanding React events",
                },
              ],

              visualStructure: `
Click on Button
    ↓
Capturing: document → body → parent
    ↓
Target: button
    ↓
Bubbling: parent → body → document
    `,

              commonMistakes: [
                {
                  mistake: "Ignoring bubbling behavior",
                  consequence: "Unexpected multiple actions",
                  fix: "Understand event flow",
                },
                {
                  mistake: "Overusing stopPropagation",
                  consequence: "Broken parent logic",
                  fix: "Use only when necessary",
                },
                {
                  mistake: "Using capture phase unnecessarily",
                  consequence: "Complex debugging",
                  fix: "Stick to bubbling by default",
                },
              ],

              proTips: [
                "Bubbling is your default friend",
                "Capturing is advanced, use intentionally",
                "stopPropagation is a scalpel, not a hammer",
                "React events rely heavily on bubbling",
              ],

              realWorldAnalogy:
                "An event is like a message passed through a chain of command",
            },

            tasks: [
              "Create nested click handlers",
              "Observe bubbling behavior",
              "Stop event propagation",
            ],

            examples: [
              "element.addEventListener('click', fn, true)",
              "e.stopPropagation()",
            ],

            useCases: [
              "Nested UI components",
              "Preventing unintended actions",
              "Understanding React event behavior",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–35 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand event propagation",
              "Differentiate bubbling and capturing",
              "Control event flow using stopPropagation",
              "Prepare for event delegation and React",
            ],
          },
          {
            day: 5,
            topic: "Event Delegation (MOST IMPORTANT)",
            subTopics: [
              "What is event delegation",
              "Why delegation is needed",
              "Using event.target",
              "Handling dynamic elements",
              "Performance benefits",
            ],
            explanation:
              "Event delegation allows handling events efficiently using parent elements.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Delegation vs Direct Listener Benchmark",
                description:
                  "Compare performance and code complexity between direct listeners and event delegation.",
                setup: [
                  "Create a list with 500+ items",
                  "Approach A: listener on each item",
                  "Approach B: single delegated listener on parent",
                ],
                requirements: [
                  "Measure number of listeners",
                  "Observe memory & responsiveness difference",
                ],
                outcome:
                  "You’ll clearly see why delegation is the industry default.",
              },
              {
                id: "pt-2",
                title: "Dynamic Element Survival Test",
                description:
                  "Prove that event delegation works even when elements are added later.",
                steps: [
                  "Attach listener only once to parent",
                  "Dynamically append new child elements",
                  "Interact with newly added elements",
                ],
                requirements: [
                  "No new event listeners added",
                  "All new elements respond correctly",
                ],
                outcome: "You’ll fully trust delegation for dynamic UIs.",
              },
              {
                id: "pt-3",
                title: "matches() vs closest() Drill",
                description:
                  "Learn when to use matches() and when closest() is safer.",
                scenarios: [
                  "Click on nested elements inside a button",
                  "Click on icon inside button inside list item",
                ],
                requirements: [
                  "Implement both approaches",
                  "Explain which is more robust and why",
                ],
                outcome:
                  "You’ll avoid fragile delegation logic in complex DOMs.",
              },
              {
                id: "pt-4",
                title: "Real CRUD Delegation System",
                description:
                  "Build a mini CRUD UI using ONLY event delegation.",
                features: [
                  "Add items dynamically",
                  "Edit item using data-id",
                  "Delete item using delegated clicks",
                ],
                constraints: [
                  "Only one event listener allowed",
                  "No direct listeners on children",
                ],
                outcome: "Production-ready delegation mindset.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Event delegation bugs are subtle and usually appear only at scale.",
              realWorldChecks: [
                "Will this element exist at page load?",
                "Am I attaching too many listeners?",
                "Does this logic survive DOM changes?",
              ],
              debugTip:
                "console.log(e.target, e.currentTarget) to instantly validate delegation logic.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is event delegation and why is it important?",
                "How does event bubbling enable delegation?",
                "Difference between target, currentTarget, matches, closest?",
                "How does React implement event handling?",
              ],
              strongAnswerPattern:
                "Problem → naive approach → delegation solution → performance gain",
              redFlag:
                "Saying delegation is only for performance, not for dynamic elements.",
            },

            editorGuidance: {
              mentalChecklist: [
                "Lists should almost always use delegation",
                "One listener > many listeners",
                "target identifies intent, not structure",
                "closest() is safer for nested DOM",
              ],
              refactorRule:
                "If you’re adding listeners inside a loop, stop and rethink.",
            },

            // 📚 Detailed Learning Content (CRITICAL CONCEPT)
            content: {
              introduction:
                "Event delegation is a core JavaScript pattern that enables scalable, high-performance event handling. Instead of attaching event listeners to many child elements, you attach ONE listener to a parent and use event bubbling to determine what was clicked. This is how React, Vue, and other frameworks handle events internally.",

              sections: [
                {
                  title: "The Problem with Direct Event Listeners",
                  description:
                    "Attaching listeners to each element does not scale.",
                  problems: [
                    "Too many event listeners",
                    "Poor performance for large lists",
                    "Dynamic elements don’t have listeners",
                  ],
                  example: "1000 list items → 1000 listeners ❌",
                  analogy:
                    "Hiring a guard for every room instead of one at the gate",
                },
                {
                  title: "What Is Event Delegation?",
                  description:
                    "A pattern where a single event listener on a parent handles events for all children.",
                  keyPoints: [
                    "Relies on event bubbling",
                    "Uses event.target to identify source",
                    "Works for dynamic elements",
                  ],
                  rule: "Listen high, act low",
                },
                {
                  title: "How Event Delegation Works",
                  description: "Events bubble from child to parent.",
                  flow: [
                    "User clicks child element",
                    "Event bubbles to parent",
                    "Parent listener runs",
                    "JS checks event.target",
                  ],
                  takeaway: "Parent hears everything",
                },
                {
                  title: "Basic Delegation Example",
                  description: "Handle all list item clicks with one listener.",
                  code: "list.addEventListener('click', e => {\n  if (e.target.tagName === 'LI') {\n    console.log(e.target.textContent);\n  }\n});",
                  bestPractice: "Always guard with a condition",
                },
                {
                  title: "Using matches() (Modern & Clean)",
                  description: "matches() checks if element fits selector.",
                  code: "list.addEventListener('click', e => {\n  if (e.target.matches('li')) {\n    console.log('Item clicked');\n  }\n});",
                  takeaway: "matches() = CSS selector power",
                },
                {
                  title: "Handling Dynamic Elements (KEY BENEFIT)",
                  description:
                    "Delegation works even for elements added later.",
                  scenario: "New list item added after page load",
                  result: "Still clickable without new listener",
                  takeaway: "Delegation future-proofs your code",
                },
                {
                  title: "Delete / Edit Buttons (Real-World Pattern)",
                  description: "Common CRUD UI logic.",
                  code: "list.addEventListener('click', e => {\n  if (e.target.matches('.delete')) {\n    const id = e.target.dataset.id;\n    deleteItem(id);\n  }\n});",
                  principle: "UI event → data-id → logic",
                },
                {
                  title: "Performance Benefits",
                  description: "Delegation is highly efficient.",
                  benefits: [
                    "Fewer event listeners",
                    "Lower memory usage",
                    "Faster rendering",
                  ],
                  comparison: "1000 items → 1 listener ✅",
                },
                {
                  title: "Delegation vs Direct Listeners",
                  description: "Know when to use what.",
                  comparison: [
                    "Direct → few static elements",
                    "Delegation → lists & dynamic UI",
                  ],
                  ruleOfThumb: "Lists = delegation",
                },
                {
                  title: "Common Mistakes",
                  description: "These cause unexpected behavior.",
                  mistakes: [
                    "Not checking target",
                    "Using stopPropagation unnecessarily",
                    "Attaching listener too low",
                  ],
                },
                {
                  title: "How React Uses Event Delegation",
                  description: "React attaches one listener at the root.",
                  facts: [
                    "Single listener per event type",
                    "Synthetic event system",
                    "Delegation by default",
                  ],
                  example: "onClick in JSX uses delegation internally",
                  takeaway: "If you understand delegation, React feels logical",
                },
              ],

              visualStructure: `
Click on <li>
     ↓
Event bubbles up
     ↓
<ul> listener catches it
     ↓
Check e.target
     ↓
Run logic
    `,

              commonMistakes: [
                {
                  mistake: "Adding listeners to each child",
                  consequence: "Performance issues",
                  fix: "Use delegation",
                },
                {
                  mistake: "Not validating event.target",
                  consequence: "Wrong logic execution",
                  fix: "Use matches() or closest()",
                },
                {
                  mistake: "Using delegation everywhere",
                  consequence: "Overcomplication",
                  fix: "Use only for groups/lists",
                },
              ],

              proTips: [
                "Event delegation is a senior-level concept",
                "Always combine delegation with data-*",
                "This pattern scales infinitely",
                "React event handling is delegation-based",
              ],

              realWorldAnalogy:
                "One receptionist handles all visitors instead of one per room",
            },

            tasks: [
              "Handle clicks on dynamic list items",
              "Build delete/edit buttons using delegation",
              "Compare delegated vs direct listeners",
            ],

            examples: [
              "list.addEventListener('click', e => e.target.matches('li'))",
            ],

            useCases: [
              "Todo lists",
              "Dynamic UI rendering",
              "Large data lists",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand event delegation deeply",
              "Handle dynamic elements efficiently",
              "Improve performance using delegation",
              "Understand how React handles events internally",
            ],
          },
          {
            day: 6,
            topic: "Form Events & Live Validation",
            subTopics: [
              "submit event",
              "input vs change",
              "Keyboard validation",
              "Custom error messages",
              "Accessibility in forms",
            ],
            explanation:
              "Form events enable real-time validation and better user experience.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real-Time Signup Form Validator",
                description:
                  "Build a complete live-validation system similar to modern signup forms.",
                fields: ["Email", "Password", "Confirm Password"],
                requirements: [
                  "Validate on input (not submit)",
                  "Show inline error messages",
                  "Disable submit until all fields are valid",
                ],
                accessibilityRules: [
                  "Use <label> for every input",
                  "Errors announced via aria-live",
                ],
                outcome: "You’ll build production-grade form UX.",
              },
              {
                id: "pt-2",
                title: "Keyboard-First Form Experience",
                description: "Ensure form works perfectly without a mouse.",
                scenarios: [
                  "Submit using Enter key",
                  "Tab through inputs",
                  "Error feedback visible via keyboard only",
                ],
                requirements: [
                  "No mouse dependency",
                  "Same validation logic for keyboard & mouse",
                ],
                outcome: "You’ll internalize accessibility-first thinking.",
              },
              {
                id: "pt-3",
                title: "input vs change UX Experiment",
                description:
                  "Compare user experience using input vs change events.",
                setup: ["Form A uses change", "Form B uses input"],
                observationPoints: [
                  "Error timing",
                  "User confusion",
                  "Correction speed",
                ],
                outcome: "You’ll never misuse change event again.",
              },
              {
                id: "pt-4",
                title: "Reusable Validation Engine",
                description:
                  "Create reusable validation functions instead of inline logic.",
                constraints: [
                  "No validation logic inside event listener",
                  "All rules in separate functions",
                ],
                exampleRules: ["isRequired", "minLength", "isEmail"],
                outcome: "Cleaner, scalable validation architecture.",
              },
            ],

            testingInsight: {
              whyItMatters:
                "Most real-world bugs in forms are UX bugs, not logic bugs.",
              realWorldChecks: [
                "Does error appear too late?",
                "Can user submit invalid data?",
                "Does keyboard flow feel broken?",
              ],
              debugTip:
                "Log validation state object instead of DOM when debugging.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between input and change events?",
                "How do you prevent form submission?",
                "How would you build live validation?",
                "How do you make forms accessible?",
              ],
              strongAnswerPattern:
                "Explain UX intent first, then event choice, then accessibility",
              redFlag: "Relying only on submit-time validation.",
            },

            editorGuidance: {
              mentalChecklist: [
                "input = live UX",
                "submit = final gate",
                "Validation logic ≠ DOM logic",
                "Accessibility is not optional",
              ],
              refactorRule:
                "If validation logic is duplicated, extract it immediately.",
            },

            // 📚 Detailed Learning Content (Modern, UX & React-Ready)
            content: {
              introduction:
                "Forms are the primary way users interact with applications. A good form feels responsive, helpful, and accessible. This lesson focuses on handling form events correctly, validating input in real time, and building user-friendly form experiences — the same principles used in modern React applications.",

              sections: [
                {
                  title: "Why Form Events Matter",
                  description: "Forms collect critical user data.",
                  examples: [
                    "Signup & login",
                    "Payments",
                    "Surveys & feedback",
                  ],
                  analogy: "Forms are conversations — validation is feedback",
                },
                {
                  title: "submit Event",
                  description: "Triggered when a form is submitted.",
                  keyPoints: [
                    "Fires on button click or Enter key",
                    "Default behavior reloads page",
                  ],
                  code: "form.addEventListener('submit', e => {\n  e.preventDefault();\n  handleSubmit();\n});",
                  bestPractice: "Always prevent default for JS-handled forms",
                },
                {
                  title: "input vs change (CRITICAL)",
                  description: "Choosing the correct event affects UX.",
                  comparison: [
                    "input → fires on every keystroke",
                    "change → fires on blur or selection",
                  ],
                  code: "input.addEventListener('input', validateLive);\nselect.addEventListener('change', validateFinal);",
                  ruleOfThumb: "Live feedback → input | Final check → change",
                },
                {
                  title: "Live Validation (While Typing)",
                  description: "Validate user input in real time.",
                  steps: [
                    "Listen to input event",
                    "Check value",
                    "Show error immediately",
                  ],
                  code: "input.addEventListener('input', e => {\n  if (e.target.value.length < 5) {\n    showError('Minimum 5 characters');\n  }\n});",
                  takeaway: "Early feedback reduces user frustration",
                },
                {
                  title: "Keyboard Validation",
                  description: "Users may submit via keyboard.",
                  keyPoints: [
                    "Enter key triggers submit",
                    "Keyboard users must get same feedback",
                  ],
                  code: "input.addEventListener('keydown', e => {\n  if (e.key === 'Enter') validateLive();\n});",
                  accessibilityNote: "Never rely only on mouse events",
                },
                {
                  title: "Custom Error Messages",
                  description: "Clear errors improve form success rate.",
                  bestPractices: [
                    "Show errors near inputs",
                    "Use simple language",
                    "Update errors dynamically",
                  ],
                  example: "errorEl.textContent = 'Email is required';",
                },
                {
                  title: "Disabling Submit Button",
                  description: "Prevent invalid submissions.",
                  logic: "Disable submit until all inputs are valid",
                  code: "submitBtn.disabled = !isFormValid;",
                  takeaway: "Guide users instead of punishing them",
                },
                {
                  title: "Accessibility in Forms (VERY IMPORTANT)",
                  description: "Forms must work for everyone.",
                  keyPoints: [
                    "Use labels with inputs",
                    "Use aria-live for error messages",
                    "Errors must be announced to screen readers",
                  ],
                  example: "<div aria-live='polite'></div>",
                  rule: "Accessible forms = professional apps",
                },
                {
                  title: "Form Validation Flow (Mental Model)",
                  description: "A clean validation pipeline.",
                  flow: [
                    "User types",
                    "Validate input",
                    "Show/hide error",
                    "Enable/disable submit",
                  ],
                },
                {
                  title: "How This Maps to React",
                  description: "Same logic, declarative syntax.",
                  comparison: [
                    "JS → addEventListener",
                    "React → onChange, onSubmit",
                  ],
                  example:
                    "<form onSubmit={handleSubmit}>\n  <input onChange={validate} />\n</form>",
                  takeaway: "Understanding JS forms makes React forms easy",
                },
              ],

              visualStructure: `
User Input
    ↓
input / change event
    ↓
Validation logic
    ↓
Error message / Button state
    `,

              commonMistakes: [
                {
                  mistake: "Using change instead of input",
                  consequence: "Delayed validation",
                  fix: "Use input for live feedback",
                },
                {
                  mistake: "Not preventing default submit",
                  consequence: "Page reload",
                  fix: "Use e.preventDefault()",
                },
                {
                  mistake: "Ignoring accessibility",
                  consequence: "Unusable forms for some users",
                  fix: "Use labels & aria-live",
                },
              ],

              proTips: [
                "Live validation improves conversion",
                "Disable submit instead of showing alerts",
                "Keyboard users are first-class users",
                "React form handling follows same principles",
              ],

              realWorldAnalogy:
                "Form validation is like a guide correcting you while filling a form, not after submission",
            },

            tasks: [
              "Build live form validation",
              "Show errors while typing",
              "Disable submit on invalid input",
            ],

            examples: [
              "form.addEventListener('submit', fn)",
              "input.addEventListener('input', validate)",
            ],

            useCases: ["Signup forms", "Login systems", "User-friendly UX"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Handle form submit events",
              "Implement live input validation",
              "Improve UX with real-time feedback",
              "Prepare for React form handling",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Interactive Browser Game",
            subTopics: [
              "Game state handling",
              "Event-driven logic",
              "Timers and intervals",
              "Dynamic DOM updates",
              "Score tracking",
            ],
            explanation:
              "Apply all event concepts to build a fun, interactive browser game.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Game Architecture Planning (Before Coding)",
                description:
                  "Design the complete game flow before writing any DOM code.",
                deliverables: [
                  "Game state object",
                  "Event list (keyboard / mouse / timer)",
                  "Win / lose conditions",
                ],
                rules: [
                  "No DOM manipulation before state design",
                  "State must drive UI, not vice versa",
                ],
                outcome: "You’ll think like a game engineer, not a DOM hacker.",
              },
              {
                id: "pt-2",
                title: "Single Source of Truth State Engine",
                description:
                  "Implement a centralized state system for the game.",
                stateRules: [
                  "No direct DOM reads for logic",
                  "All UI updates come from state",
                  "State reset must fully restart game",
                ],
                exampleStateKeys: [
                  "score",
                  "timeLeft",
                  "isPlaying",
                  "level",
                  "activeTargetId",
                ],
                outcome:
                  "You’ll master state-driven architecture (React mindset).",
              },
              {
                id: "pt-3",
                title: "Event Delegation for Game Targets",
                description:
                  "Handle all dynamic targets with a single event listener.",
                constraints: [
                  "No per-target event listeners",
                  "Must use matches() or closest()",
                ],
                successCriteria: [
                  "New targets auto-work",
                  "Listeners count stays constant",
                ],
                outcome: "High-performance event handling at scale.",
              },
              {
                id: "pt-4",
                title: "Timer Lifecycle Management",
                description: "Control all timers safely and predictably.",
                requirements: [
                  "No orphan intervals",
                  "Pause & resume supported",
                  "Restart clears everything",
                ],
                checklist: [
                  "Store timer IDs",
                  "Clear on end",
                  "Clear on restart",
                ],
                outcome: "You’ll avoid the #1 game bug: runaway timers.",
              },
              {
                id: "pt-5",
                title: "Difficulty Scaling Logic",
                description: "Increase game difficulty based on score or time.",
                examples: [
                  "Faster timer",
                  "Smaller targets",
                  "Multiple targets",
                ],
                rule: "Difficulty logic must not touch DOM directly",
                outcome: "Professional-level game logic design.",
              },
            ],

            testingInsight: {
              mindset:
                "Games break differently than forms — timing bugs are the hardest.",
              criticalChecks: [
                "Does score update exactly once per action?",
                "Do timers stop immediately on game end?",
                "Does restart fully reset state?",
              ],
              debugTip:
                "Console.log(state) on every loop tick during debugging.",
            },

            interviewInsights: {
              whyInterviewersLoveThis:
                "Games test real engineering skills, not tutorial memory.",
              strongTalkingPoints: [
                "Event delegation usage",
                "State-driven UI",
                "Timer cleanup strategy",
                "Performance considerations",
              ],
              redFlags: [
                "Direct DOM reads for logic",
                "Multiple intervals running",
                "Listeners added repeatedly",
              ],
            },

            editorGuidance: {
              mentalModel: [
                "Game = State Machine",
                "Events mutate state",
                "UI reflects state",
                "Timers are just events",
              ],
              refactorRule:
                "If UI logic and game logic mix, split immediately.",
              seniorRule:
                "If restarting the game feels complex, architecture is wrong.",
            },

            portfolioUpgrade: {
              howToPresent: "Explain game logic before visuals.",
              mustShow: [
                "State object",
                "Game loop",
                "Delegated events",
                "Reset logic",
              ],
              bonus: "Add difficulty scaling to stand out.",
            },

            // 📚 Detailed Learning Content (Capstone Project)
            content: {
              introduction:
                "This mini project brings together everything learned about events, DOM manipulation, and state handling. You’ll build a small browser-based game where the entire experience is driven by user actions, timers, and real-time UI updates — exactly how interactive apps and games work in production.",

              sections: [
                {
                  title: "Project Goal",
                  description:
                    "Build a fully interactive browser game using vanilla JavaScript.",
                  allowedGames: [
                    "Typing speed game",
                    "Memory card game",
                    "Whack-a-mole style game",
                  ],
                  rule: "Focus on logic and interaction, not graphics",
                },
                {
                  title: "Core Concepts Used",
                  description: "This project is event-heavy by design.",
                  concepts: [
                    "addEventListener",
                    "Event delegation",
                    "Timers (setInterval / setTimeout)",
                    "Dynamic DOM rendering",
                    "State-driven UI",
                  ],
                  takeaway:
                    "If this project feels comfortable, React will feel easy",
                },
                {
                  title: "Game State (MOST IMPORTANT)",
                  description: "All game behavior depends on state.",
                  exampleState: {
                    score: 0,
                    timeLeft: 30,
                    isPlaying: false,
                    level: 1,
                  },
                  rule: "State is the single source of truth",
                  takeaway: "UI must reflect state at all times",
                },
                {
                  title: "Event-Driven Game Logic",
                  description: "User actions drive the game forward.",
                  examples: [
                    "Key press → score update",
                    "Click → hit or miss",
                    "Timer tick → countdown",
                  ],
                  flow: "Event → Logic → State update → UI update",
                },
                {
                  title: "Keyboard & Mouse Events",
                  description: "Games rely heavily on fast input.",
                  codeExamples: [
                    "document.addEventListener('keydown', handleKey)",
                    "container.addEventListener('click', handleClick)",
                  ],
                  bestPractice: "Always remove listeners when game ends",
                },
                {
                  title: "Timers & Intervals",
                  description: "Timers control game flow.",
                  keyPoints: [
                    "setInterval → repeated logic",
                    "setTimeout → delayed actions",
                  ],
                  code: "const timer = setInterval(gameLoop, 1000);",
                  warning: "Always clear intervals on game end",
                },
                {
                  title: "Dynamic DOM Updates",
                  description: "The UI changes constantly.",
                  examples: [
                    "Update score display",
                    "Show/hide game elements",
                    "Render new targets",
                  ],
                  rule: "Never hardcode game UI",
                },
                {
                  title: "Score Tracking System",
                  description: "Score motivates user interaction.",
                  logic:
                    "Correct action → +points\nWrong action → no change / penalty",
                  codeExample: "scoreEl.textContent = score;",
                },
                {
                  title: "Game Loop (Mental Model)",
                  description: "Most games follow a loop.",
                  steps: [
                    "Check time / state",
                    "Update positions or values",
                    "Render UI",
                    "Check end conditions",
                  ],
                  takeaway: "This is core game programming logic",
                },
                {
                  title: "Reset & Restart Logic",
                  description: "Game should be replayable.",
                  steps: [
                    "Clear timers",
                    "Reset state",
                    "Clear UI",
                    "Restart listeners",
                  ],
                  code: "clearInterval(timer);\nresetState();",
                },
                {
                  title: "Performance & UX Tips",
                  description: "Smooth gameplay matters.",
                  bestPractices: [
                    "Use delegation for dynamic elements",
                    "Minimize DOM updates",
                    "Avoid layout thrashing",
                  ],
                },
                {
                  title: "How This Transitions to React",
                  description:
                    "Everything you did manually becomes declarative.",
                  mapping: [
                    "Game state → useState",
                    "Timers → useEffect",
                    "Events → JSX handlers",
                  ],
                  example:
                    "useEffect(() => {\n  const id = setInterval(loop, 1000);\n  return () => clearInterval(id);\n}, []);",
                  takeaway: "This project is a direct bridge to React",
                },
              ],

              visualStructure: `
User Input / Timer
        ↓
    Event Handler
        ↓
     Update State
        ↓
     Update UI
        ↓
   Check Game End
    `,

              commonMistakes: [
                {
                  mistake: "Not clearing intervals",
                  consequence: "Multiple game loops running",
                  fix: "Always clear timers",
                },
                {
                  mistake: "Manipulating DOM without state",
                  consequence: "Unpredictable UI",
                  fix: "State drives UI",
                },
                {
                  mistake: "Too many event listeners",
                  consequence: "Performance issues",
                  fix: "Use delegation",
                },
              ],

              proTips: [
                "Games are state machines",
                "Timers + events = interactivity",
                "This project impresses interviewers",
                "If you can build this, React is easy",
              ],

              realWorldAnalogy:
                "A game is just a loop reacting to user actions and time",
            },

            tasks: [
              "Choose game type (typing / memory / whack-a-mole)",
              "Handle user actions with events",
              "Track score and time",
              "Reset and restart game",
            ],

            examples: [
              "setInterval(gameLoop, 1000)",
              "document.addEventListener('keydown', fn)",
            ],

            useCases: [
              "Games",
              "Highly interactive apps",
              "Interview demo project",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "3–4 hours",

            // 🎯 Learning objectives
            objectives: [
              "Build a complete event-driven application",
              "Manage state manually",
              "Use timers and events together",
              "Be fully ready for React",
            ],
          },
        ],
      },
      {
        week: 13,
        weekGoal:
          "Understand asynchronous JavaScript and handle async operations using Promises",
        weekOutcome:
          "Ability to manage async code, avoid callback hell, and build API-driven features",
        miniProject: "Weather Dashboard Application",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Synchronous vs Asynchronous JavaScript",
            subTopics: [
              "What is synchronous code",
              "What is asynchronous code",
              "Blocking vs non-blocking behavior",
              "JavaScript runtime overview",
              "Why async is needed in frontend",
            ],
            explanation:
              "JavaScript runs single-threaded, but asynchronous behavior allows handling time-consuming tasks without blocking UI.",

            // 📚 Detailed Learning Content (Critical Foundation)
            premiumTasks: [
              {
                id: "pt-1",
                title: "Blocking vs Non-Blocking Simulator",
                description:
                  "Create a demo that visually proves how blocking code freezes the UI and async code does not.",
                setup: [
                  "Button A runs a heavy synchronous loop",
                  "Button B runs same logic using setTimeout",
                ],
                requirements: [
                  "Show UI freeze in sync version",
                  "UI must remain responsive in async version",
                  "Console logs must clearly show execution order",
                ],
                outcome:
                  "You will *feel* why blocking the main thread is dangerous.",
              },
              {
                id: "pt-2",
                title: "Execution Order Prediction Drill",
                description:
                  "Train your async intuition by predicting log order before running code.",
                scenarios: [
                  "console.log with setTimeout",
                  "Nested setTimeout",
                  "Async task mixed with sync logs",
                ],
                rules: [
                  "Write expected output first",
                  "Then execute and compare",
                ],
                outcome:
                  "You’ll stop guessing async behavior and start reasoning correctly.",
              },
              {
                id: "pt-3",
                title: "Fake API Call Without Promises",
                description:
                  "Simulate a network request using callbacks to understand async roots.",
                constraints: [
                  "No Promises",
                  "Use setTimeout only",
                  "Result handled in callback",
                ],
                learningFocus: [
                  "Async delegation",
                  "Result handling later",
                  "Non-blocking flow",
                ],
                outcome:
                  "Strong foundation before learning Promises & async/await.",
              },
              {
                id: "pt-4",
                title: "Sync → Async Refactor Exercise",
                description:
                  "Refactor a blocking function into a non-blocking version.",
                constraints: [
                  "Same functionality",
                  "Different execution model",
                ],
                comparisonPoints: [
                  "UI responsiveness",
                  "Execution order",
                  "User experience",
                ],
                outcome:
                  "You’ll learn how to *think async*, not just write async code.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Async bugs are rarely syntax errors — they are timing and order mistakes.",
              realWorldChecks: [
                "Does UI freeze during async operations?",
                "Is the result used before it’s ready?",
                "Are logs misleading due to async timing?",
              ],
              debugTip:
                "Always log execution order first before fixing async bugs.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the difference between synchronous and asynchronous code?",
                "Why is JavaScript called single-threaded?",
                "How does async code run if JS is single-threaded?",
                "What problems does async solve in frontend?",
              ],
              strongAnswerPattern:
                "Start with UI impact → explain blocking vs non-blocking → mention event loop at high level",
              redFlag: "Saying async means multi-threading in JavaScript.",
            },
            editorGuidance: {
              mentalChecklist: [
                "JS runs one thing at a time",
                "Blocking = frozen UI",
                "Async work runs later, not parallel",
                "Execution order matters more than syntax",
              ],
              refactorRule:
                "If a function can block UI, redesign it using async APIs.",
            },
            content: {
              introduction:
                "JavaScript executes code in a single thread, meaning it can do only one thing at a time. If long-running tasks blocked execution, modern websites would freeze constantly. Asynchronous JavaScript solves this problem and makes real-world applications possible.",

              sections: [
                {
                  title: "What Is Synchronous Code?",
                  description:
                    "Synchronous code runs line by line, one task at a time.",
                  keyPoints: [
                    "Each statement waits for the previous one",
                    "Blocking by nature",
                    "Easy to reason about",
                  ],
                  code: "console.log('Start');\nconsole.log('Middle');\nconsole.log('End');",
                  output: "Start → Middle → End",
                  analogy:
                    "Standing in a single queue — next person waits until previous is done",
                },
                {
                  title: "Problems with Synchronous Execution",
                  description: "Long tasks block everything.",
                  example: "Heavy calculation or network request",
                  consequences: [
                    "UI freezes",
                    "Buttons stop responding",
                    "Bad user experience",
                  ],
                  takeaway:
                    "Blocking the main thread is unacceptable in frontend",
                },
                {
                  title: "What Is Asynchronous Code?",
                  description:
                    "Asynchronous code allows tasks to run in the background.",
                  keyPoints: [
                    "Non-blocking",
                    "Main thread stays free",
                    "Results handled later",
                  ],
                  code: "console.log('Start');\nsetTimeout(() => console.log('Async'), 1000);\nconsole.log('End');",
                  output: "Start → End → Async",
                  analogy: "Ordering food and doing other work while waiting",
                },
                {
                  title: "Blocking vs Non-Blocking (Core Difference)",
                  description: "This difference defines modern JavaScript.",
                  comparison: [
                    "Blocking → UI freezes",
                    "Non-blocking → UI remains responsive",
                  ],
                  realWorldExample: "Fetching data from server",
                  takeaway: "Async keeps apps usable",
                },
                {
                  title: "JavaScript Is Single-Threaded (Important)",
                  description:
                    "JavaScript itself does NOT run multiple threads.",
                  facts: ["Only one call stack", "No parallel JS execution"],
                  mythBusted: "Async ≠ multi-threading",
                },
                {
                  title: "How Async Works Internally (High Level)",
                  description: "Browser helps JavaScript handle async tasks.",
                  components: [
                    "Call Stack",
                    "Web APIs",
                    "Callback Queue",
                    "Event Loop",
                  ],
                  simplifiedFlow: [
                    "JS starts task",
                    "Browser handles async work",
                    "Result queued",
                    "Event loop pushes it back",
                  ],
                  note: "Event loop deep dive comes next days",
                },
                {
                  title: "Common Async Operations in Frontend",
                  description: "You already use async without realizing it.",
                  examples: [
                    "setTimeout / setInterval",
                    "API calls (fetch)",
                    "Event listeners",
                    "User interactions",
                  ],
                },
                {
                  title: "Why Async Is Mandatory in Frontend",
                  description: "Frontend apps constantly wait for things.",
                  scenarios: [
                    "Server responses",
                    "User input",
                    "Timers",
                    "Animations",
                  ],
                  takeaway: "Without async, modern web apps cannot exist",
                },
                {
                  title: "Execution Order (Mental Model)",
                  description: "Understanding order prevents bugs.",
                  example:
                    "console.log('A');\nsetTimeout(() => console.log('B'));\nconsole.log('C');",
                  result: "A → C → B",
                  takeaway: "Async code runs later, not in sequence",
                },
                {
                  title: "How This Maps to React",
                  description: "React apps are async-heavy.",
                  mapping: [
                    "API calls → async/await",
                    "Effects → useEffect",
                    "Events → async handlers",
                  ],
                  takeaway: "Async understanding = React confidence",
                },
              ],

              visualStructure: `
Synchronous:
Task 1 → Task 2 → Task 3 (Blocking)

Asynchronous:
Task 1 → Background Task
         ↓
      Continue UI
         ↓
   Handle result later
    `,

              commonMistakes: [
                {
                  mistake: "Assuming async runs immediately",
                  consequence: "Wrong execution order",
                  fix: "Understand event loop",
                },
                {
                  mistake: "Blocking main thread",
                  consequence: "Frozen UI",
                  fix: "Use async APIs",
                },
                {
                  mistake: "Thinking JS is multi-threaded",
                  consequence: "Wrong mental model",
                  fix: "Learn runtime behavior",
                },
              ],

              proTips: [
                "Async is not parallel — it’s cooperative",
                "UI responsiveness is top priority",
                "Most frontend bugs are async-related",
                "This topic unlocks promises & async/await",
              ],

              realWorldAnalogy:
                "Async JavaScript is like delegating tasks while continuing your own work",
            },

            tasks: [
              "Compare sync and async logs",
              "Simulate blocking code",
              "Understand execution order",
            ],

            examples: [
              "console.log('Start');",
              "setTimeout(() => console.log('Async'), 1000);",
              "console.log('End');",
            ],

            useCases: [
              "API calls",
              "Timers",
              "User interactions without freezing UI",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand sync vs async execution",
              "Recognize blocking behavior",
              "Explain why async is needed",
              "Prepare for event loop & promises",
            ],
          },
          {
            day: 2,
            topic: "Timers: setTimeout & setInterval",
            subTopics: [
              "setTimeout syntax",
              "setInterval syntax",
              "clearTimeout",
              "clearInterval",
              "Timers and event loop",
            ],
            explanation:
              "Timers allow delayed and repeated execution of code asynchronously.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Timer Leak Demonstration",
                description:
                  "Build a demo that proves how forgetting clearInterval causes hidden bugs.",
                setup: [
                  "Button starts an interval",
                  "Clicking button multiple times creates multiple intervals",
                ],
                requirements: [
                  "Show duplicate logs/ticks",
                  "Add a fix using clearInterval",
                  "Explain what caused the bug",
                ],
                outcome:
                  "You’ll clearly understand why uncleared intervals are dangerous.",
              },
              {
                id: "pt-2",
                title: "Precise Countdown with Safe Stop",
                description:
                  "Create a countdown timer that never goes below zero.",
                constraints: [
                  "Use setInterval",
                  "Stop exactly at zero",
                  "No extra ticks allowed",
                ],
                edgeCases: ["User refreshes page", "Timer already running"],
                outcome:
                  "You’ll learn defensive timer logic used in production apps.",
              },
              {
                id: "pt-3",
                title: "setInterval vs Recursive setTimeout",
                description:
                  "Compare timing behavior using two different approaches.",
                setup: [
                  "Version A uses setInterval",
                  "Version B uses recursive setTimeout",
                ],
                observationPoints: [
                  "Timing drift",
                  "Control over execution",
                  "Ease of stopping",
                ],
                outcome: "You’ll know when NOT to use setInterval.",
              },
              {
                id: "pt-4",
                title: "Real-World Auto Refresh Controller",
                description:
                  "Build an auto-refresh system with start, stop, and reset.",
                requirements: [
                  "Only one timer allowed at a time",
                  "Clear interval on stop",
                  "Restart cleanly on reset",
                ],
                outcome:
                  "You’ll build timer logic similar to dashboards & admin panels.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Timer bugs don’t crash apps — they silently degrade performance.",
              realWorldChecks: [
                "Is this timer running more than once?",
                "Does it stop when UI is destroyed?",
                "Is cleanup guaranteed?",
              ],
              debugTip:
                "Always log timer IDs to detect duplicate or leaked timers.",
            },
            interviewInsights: {
              commonQuestions: [
                "Difference between setTimeout and setInterval?",
                "Why are timers not precise?",
                "How do you prevent memory leaks with timers?",
                "How are timers handled in React?",
              ],
              strongAnswerPattern:
                "Explain async nature → mention event loop → stress cleanup responsibility",
              redFlag:
                "Saying setInterval stops automatically or timers are exact.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Timers are async, not guaranteed",
                "Intervals never stop by themselves",
                "One feature = one timer",
                "Cleanup is part of logic",
              ],
              refactorRule:
                "If a timer can start twice, redesign the control flow immediately.",
            },
            // 📚 Detailed Learning Content (Async Foundation)
            content: {
              introduction:
                "Timers are one of the simplest and most commonly used asynchronous features in JavaScript. They allow code to run later or repeatedly without blocking the main thread. From countdowns to auto-refresh dashboards, timers power many real-world frontend features.",

              sections: [
                {
                  title: "What Are Timers?",
                  description: "Timers schedule code to run in the future.",
                  keyPoints: [
                    "Asynchronous by nature",
                    "Handled by browser Web APIs",
                    "Do not block the main thread",
                  ],
                  analogy: "Setting an alarm while continuing your work",
                },
                {
                  title: "setTimeout()",
                  description: "Executes a function once after a delay.",
                  syntax: "setTimeout(callback, delay)",
                  keyPoints: [
                    "Delay is in milliseconds",
                    "Returns a timer ID",
                    "Execution is NOT guaranteed exactly on time",
                  ],
                  code: "setTimeout(() => {\n  console.log('Runs after 2 seconds');\n}, 2000);",
                  takeaway: "Runs once, after delay",
                },
                {
                  title: "Why setTimeout Is Not Exact",
                  description: "Timers depend on the event loop.",
                  reasons: [
                    "Main thread may be busy",
                    "Callbacks wait in queue",
                  ],
                  takeaway: "Delay means minimum wait time, not exact time",
                },
                {
                  title: "setInterval()",
                  description:
                    "Executes a function repeatedly at fixed intervals.",
                  syntax: "setInterval(callback, delay)",
                  keyPoints: [
                    "Runs continuously",
                    "Returns interval ID",
                    "Must be cleared manually",
                  ],
                  code: "const id = setInterval(() => {\n  console.log('Tick');\n}, 1000);",
                  useCase: "Clocks, polling, animations",
                },
                {
                  title: "clearTimeout()",
                  description: "Cancels a scheduled timeout.",
                  code: "const timeoutId = setTimeout(fn, 3000);\nclearTimeout(timeoutId);",
                  useCase: "Cancel delayed actions",
                },
                {
                  title: "clearInterval() (VERY IMPORTANT)",
                  description: "Stops a running interval.",
                  keyPoints: [
                    "Intervals never stop automatically",
                    "Always clear when no longer needed",
                  ],
                  code: "const id = setInterval(fn, 1000);\nclearInterval(id);",
                  warning: "Uncleared intervals cause memory leaks",
                },
                {
                  title: "Countdown Timer Example",
                  description: "Common real-world pattern.",
                  logic: [
                    "Initialize time",
                    "Decrease every second",
                    "Stop at zero",
                  ],
                  code: "let time = 10;\nconst id = setInterval(() => {\n  console.log(time);\n  time--;\n  if (time < 0) clearInterval(id);\n}, 1000);",
                },
                {
                  title: "Digital Clock Example",
                  description: "Using interval with Date.",
                  code: "setInterval(() => {\n  const now = new Date();\n  clock.textContent = now.toLocaleTimeString();\n}, 1000);",
                  takeaway: "Intervals drive real-time UI",
                },
                {
                  title: "Timers & Event Loop",
                  description: "Timers don’t run immediately after delay.",
                  flow: [
                    "Timer finishes in Web API",
                    "Callback pushed to queue",
                    "Event loop waits for call stack",
                  ],
                  takeaway: "Timers respect async execution rules",
                },
                {
                  title: "Common Mistakes",
                  description: "Timers are easy to misuse.",
                  mistakes: [
                    "Forgetting to clear interval",
                    "Creating multiple intervals accidentally",
                    "Assuming exact timing",
                  ],
                },
                {
                  title: "setTimeout vs setInterval",
                  description: "Choosing the right tool.",
                  comparison: [
                    "setTimeout → run once",
                    "setInterval → run repeatedly",
                  ],
                  ruleOfThumb:
                    "If you can stop it → interval | If one-time → timeout",
                },
                {
                  title: "How This Maps to React",
                  description: "Timers live inside effects in React.",
                  mapping: [
                    "setInterval → useEffect",
                    "clearInterval → cleanup function",
                  ],
                  example:
                    "useEffect(() => {\n  const id = setInterval(fn, 1000);\n  return () => clearInterval(id);\n}, []);",
                  takeaway: "Understanding timers prevents React bugs",
                },
              ],

              visualStructure: `
setTimeout:
Start → Wait → Callback → Done

setInterval:
Start → Tick → Tick → Tick → clearInterval
    `,

              commonMistakes: [
                {
                  mistake: "Not clearing intervals",
                  consequence: "Memory leaks, duplicate logic",
                  fix: "Always store and clear interval IDs",
                },
                {
                  mistake: "Expecting precise timing",
                  consequence: "Logic errors",
                  fix: "Design for async delays",
                },
                {
                  mistake: "Starting multiple intervals",
                  consequence: "Unexpected behavior",
                  fix: "Track running timers",
                },
              ],

              proTips: [
                "Timers are async, not guaranteed",
                "Always clear intervals",
                "Prefer setTimeout recursion for complex timing",
                "Timers + state = real-world apps",
              ],

              realWorldAnalogy:
                "Timers are reminders that run in the background until you handle them",
            },

            tasks: [
              "Create countdown timer",
              "Build digital clock",
              "Stop interval on button click",
            ],

            examples: [
              "setTimeout(() => {}, 2000);",
              "const id = setInterval(fn, 1000);",
              "clearInterval(id);",
            ],

            useCases: ["Countdowns", "Auto-refresh", "Animations"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use setTimeout and setInterval correctly",
              "Control and clear timers",
              "Understand timers in async execution",
              "Prepare for React effects with timers",
            ],
          },
          {
            day: 3,
            topic: "Callbacks & Callback Hell",
            subTopics: [
              "What is a callback",
              "Passing functions as arguments",
              "Nested callbacks",
              "Callback hell problem",
              "Why callbacks become unmanageable",
            ],
            explanation:
              "Callbacks were the first solution for async code but become hard to manage when deeply nested.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Callback-Driven Fake API Pipeline",
                description:
                  "Simulate a real backend flow using pure callbacks.",
                flow: [
                  "loginUser(callback)",
                  "fetchProfile(callback)",
                  "fetchPermissions(callback)",
                  "renderUI(callback)",
                ],
                constraints: [
                  "Use only callbacks",
                  "No Promises or async/await",
                  "Each step depends on previous result",
                ],
                outcome:
                  "You’ll experience how quickly callback-based async becomes complex.",
              },
              {
                id: "pt-2",
                title: "Callback Hell Creation & Analysis",
                description:
                  "Intentionally create callback hell to understand why it’s a problem.",
                requirements: [
                  "At least 4 levels of nested callbacks",
                  "Each step logs its execution",
                ],
                analysisPoints: [
                  "Readability",
                  "Error handling difficulty",
                  "Mental overhead",
                ],
                outcome: "You’ll never ignore indentation growth again.",
              },
              {
                id: "pt-3",
                title: "Error-First Callback Handling Drill",
                description:
                  "Practice the classic Node-style error-first callback pattern.",
                pattern: [
                  "callback(error, data)",
                  "Handle error at every level",
                ],
                constraints: [
                  "Simulate both success and failure cases",
                  "Do not skip error checks",
                ],
                outcome:
                  "You’ll understand why centralized error handling became necessary.",
              },
              {
                id: "pt-4",
                title: "Flattening Callback Logic",
                description:
                  "Refactor deeply nested callbacks using named functions.",
                constraints: [
                  "Still use callbacks",
                  "No Promises allowed",
                  "Reduce nesting depth",
                ],
                comparisonPoints: [
                  "Before vs after readability",
                  "Debugging ease",
                  "Flow clarity",
                ],
                outcome:
                  "You’ll learn early refactoring techniques used before Promises.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Callback-heavy code hides bugs inside execution order and error paths.",
              realWorldChecks: [
                "What happens if one callback fails?",
                "Is every async step handling errors?",
                "Can the execution flow be followed easily?",
              ],
              debugTip:
                "Log entry and exit of every callback to trace async flow.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is a callback in JavaScript?",
                "What is callback hell?",
                "Why is callback hell a problem?",
                "How did JavaScript solve callback hell?",
              ],
              strongAnswerPattern:
                "Explain callbacks → show nesting problem → explain maintainability issues → mention promises",
              redFlag: "Saying callbacks are obsolete or should never be used.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Callback executes later, not immediately",
                "More nesting = more risk",
                "Error handling multiplies with depth",
                "Readability matters as much as correctness",
              ],
              refactorRule:
                "If callbacks are nested more than 2 levels, redesign the flow.",
            },
            // 📚 Detailed Learning Content (Async Evolution Step)
            content: {
              introduction:
                "Before Promises and async/await existed, callbacks were the primary way to handle asynchronous operations in JavaScript. Understanding callbacks is essential to appreciate modern async patterns and to work with legacy codebases.",

              sections: [
                {
                  title: "What Is a Callback?",
                  description:
                    "A callback is a function passed as an argument to another function and executed later.",
                  keyPoints: [
                    "Functions are first-class citizens in JavaScript",
                    "Callbacks run AFTER some operation completes",
                    "Common in async code",
                  ],
                  code: "function greet(cb) {\n  cb();\n}\n\ngreet(() => console.log('Hello'))",
                  takeaway: "Callbacks defer execution",
                },
                {
                  title: "Why Callbacks Exist",
                  description: "They allow handling async results.",
                  examples: ["Timers", "Event listeners", "Network requests"],
                  analogy: "Leaving your phone number and getting a call later",
                },
                {
                  title: "Callbacks in Asynchronous Code",
                  description: "Callbacks are commonly used with async APIs.",
                  code: "setTimeout(() => {\n  console.log('Runs later');\n}, 1000);",
                  takeaway: "Async result → callback",
                },
                {
                  title: "Passing Functions as Arguments",
                  description: "This is the foundation of callbacks.",
                  code: "function fetchData(cb) {\n  const data = 'User Data';\n  cb(data);\n}\n\nfetchData(result => console.log(result));",
                  keyRule: "The function decides WHEN to call the callback",
                },
                {
                  title: "Nested Callbacks",
                  description:
                    "Problems start when async operations depend on each other.",
                  example: "Do A → then B → then C",
                  code: "setTimeout(() => {\n  console.log('Step 1');\n  setTimeout(() => {\n    console.log('Step 2');\n    setTimeout(() => {\n      console.log('Step 3');\n    }, 1000);\n  }, 1000);\n}, 1000);",
                  takeaway: "Indentation grows quickly",
                },
                {
                  title: "What Is Callback Hell?",
                  description:
                    "Deeply nested callbacks that are hard to read and maintain.",
                  characteristics: [
                    "Pyramid shape",
                    "Hard to debug",
                    "Difficult error handling",
                  ],
                  nickname: "Pyramid of Doom",
                },
                {
                  title: "Why Callback Hell Is Dangerous",
                  description: "Code quality degrades fast.",
                  problems: [
                    "Unreadable code",
                    "Hard error handling",
                    "Tight coupling",
                    "No clear flow",
                  ],
                  takeaway: "Scaling becomes painful",
                },
                {
                  title: "Error Handling in Callbacks",
                  description: "Callbacks make error handling messy.",
                  pattern: "error-first callbacks",
                  code: "function readFile(cb) {\n  cb(error, data);\n}",
                  problem: "Nested error checks everywhere",
                },
                {
                  title: "Real-World Callback Hell Example",
                  description: "Legacy API flow.",
                  flow: [
                    "Login user",
                    "Fetch profile",
                    "Fetch permissions",
                    "Render UI",
                  ],
                  takeaway: "Logic becomes tangled",
                },
                {
                  title: "Why JavaScript Moved Away from Callbacks",
                  description: "Developers needed better abstraction.",
                  reasons: [
                    "Readability",
                    "Maintainability",
                    "Centralized error handling",
                  ],
                  solutionPreview: "Promises → async/await",
                },
                {
                  title: "How This Maps to Modern Code",
                  description: "You still see callbacks today.",
                  examples: [
                    "Array methods",
                    "Event listeners",
                    "Some legacy APIs",
                  ],
                  rule: "Callbacks are fine — deep nesting is not",
                },
                {
                  title: "How This Maps to React",
                  description: "React still uses callbacks.",
                  mapping: ["Event handlers", "State setters"],
                  example: "setState(prev => prev + 1)",
                  takeaway: "Understanding callbacks prevents confusion",
                },
              ],

              visualStructure: `
Callback Flow:
Async Task → Callback → Logic

Callback Hell:
Task
 └─ Task
    └─ Task
       └─ Task
    `,

              commonMistakes: [
                {
                  mistake: "Deep nesting",
                  consequence: "Unreadable code",
                  fix: "Flatten logic or use promises",
                },
                {
                  mistake: "Ignoring errors",
                  consequence: "Silent failures",
                  fix: "Handle errors explicitly",
                },
                {
                  mistake: "Mixing sync & async assumptions",
                  consequence: "Unexpected execution order",
                  fix: "Understand callback timing",
                },
              ],

              proTips: [
                "Callbacks are not bad — misuse is",
                "If indentation keeps increasing, refactor",
                "Promises exist because of callback hell",
                "This lesson makes async/await intuitive",
              ],

              realWorldAnalogy:
                "Callback hell is like giving instructions where each step depends on calling the next person",
            },

            tasks: [
              "Write callback-based async function",
              "Create nested callbacks",
              "Identify callback hell",
            ],

            examples: [
              "function fetchData(cb) { cb(data); }",
              "setTimeout(() => setTimeout(() => {}, 1000), 1000);",
            ],

            useCases: ["Legacy codebases", "Understanding async evolution"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand what callbacks are",
              "Use callbacks correctly",
              "Identify callback hell",
              "Be ready for promises & async/await",
            ],
          },
          {
            day: 4,
            topic: "Promises – Creation & Consumption",
            subTopics: [
              "Promise constructor",
              "resolve and reject",
              "then() method",
              "catch() method",
              "finally() method",
            ],
            explanation:
              "Promises provide a cleaner way to handle asynchronous operations compared to callbacks.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Build a Promise-Based Fake API",
                description:
                  "Create a custom function that mimics a real API using Promises.",
                setup: [
                  "Function returns a Promise",
                  "Uses setTimeout internally",
                  "Randomly resolves or rejects",
                ],
                requirements: [
                  "Handle success using then()",
                  "Handle error using catch()",
                  "Log final cleanup using finally()",
                ],
                outcome:
                  "You’ll understand how real APIs behave under the hood.",
              },
              {
                id: "pt-2",
                title: "Callback Hell → Promise Refactor",
                description:
                  "Convert deeply nested callbacks into clean Promise-based flow.",
                constraints: [
                  "No callbacks in final version",
                  "Use then chaining",
                  "Single catch for errors",
                ],
                comparisonPoints: [
                  "Readability",
                  "Error handling",
                  "Execution flow",
                ],
                outcome: "You’ll feel why Promises replaced callbacks.",
              },
              {
                id: "pt-3",
                title: "Promise Chain Execution Drill",
                description:
                  "Practice chaining multiple then() calls correctly.",
                scenarios: [
                  "Transform data in then",
                  "Return values vs return promises",
                  "Throw error inside then",
                ],
                rules: [
                  "Each then must return something",
                  "Errors handled only in catch",
                ],
                outcome: "You’ll stop breaking promise chains unknowingly.",
              },
              {
                id: "pt-4",
                title: "Loader & Cleanup Controller",
                description:
                  "Simulate a UI loader that always stops correctly.",
                requirements: [
                  "Start loader before async task",
                  "Stop loader in finally()",
                  "Works for both success & error",
                ],
                outcome:
                  "You’ll learn real-world async UI patterns used in production apps.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most async bugs come from broken promise chains and unhandled rejections.",
              realWorldChecks: [
                "Is every promise returned properly?",
                "Is there exactly one catch handling errors?",
                "Does cleanup always run?",
              ],
              debugTip:
                "Log promise resolution values at each then to trace data flow.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is a Promise in JavaScript?",
                "What are promise states?",
                "Difference between then, catch, and finally?",
                "Why are promises better than callbacks?",
              ],
              strongAnswerPattern:
                "Explain async problem → callback hell → promise lifecycle → benefits",
              redFlag:
                "Using then for error handling or not knowing promise states.",
            },
            editorGuidance: {
              mentalChecklist: [
                "A promise settles only once",
                "then always returns a new promise",
                "Errors bubble until caught",
                "finally is for cleanup, not data",
              ],
              refactorRule:
                "If async flow is hard to read, convert it into a promise chain.",
            },
            // 📚 Detailed Learning Content (Async Core)
            content: {
              introduction:
                "Promises were introduced to solve callback hell and make asynchronous code predictable, readable, and maintainable. A Promise represents a value that will be available now, later, or never.",

              sections: [
                {
                  title: "What Is a Promise?",
                  description:
                    "A Promise is an object that represents the eventual completion or failure of an async operation.",
                  keyPoints: [
                    "Represents future value",
                    "Can succeed or fail",
                    "Consumed using then/catch",
                  ],
                  analogy: "Ordering food and receiving it later",
                },
                {
                  title: "Promise States",
                  description: "A promise can be in only ONE state at a time.",
                  states: [
                    "Pending → initial state",
                    "Fulfilled → resolved successfully",
                    "Rejected → failed",
                  ],
                  rule: "Once settled, state never changes",
                },
                {
                  title: "Creating a Promise",
                  description:
                    "Promises are created using the Promise constructor.",
                  syntax: "new Promise((resolve, reject) => { })",
                  code: "const promise = new Promise((resolve, reject) => {\n  const success = true;\n  if (success) resolve('Data loaded');\n  else reject('Something went wrong');\n});",
                  keyRule: "resolve = success | reject = failure",
                },
                {
                  title: "Consuming a Promise with then()",
                  description: "then() runs when promise is fulfilled.",
                  code: "promise.then(result => {\n  console.log(result);\n});",
                  takeaway: "then handles success",
                },
                {
                  title: "Handling Errors with catch()",
                  description: "catch() runs when promise is rejected.",
                  code: "promise.catch(error => {\n  console.error(error);\n});",
                  takeaway: "catch handles failure",
                },
                {
                  title: "finally() – Always Runs",
                  description:
                    "finally() executes regardless of success or failure.",
                  useCases: ["Stop loader", "Cleanup resources"],
                  code: "promise.finally(() => {\n  console.log('Done');\n});",
                  takeaway: "Cleanup logic belongs here",
                },
                {
                  title: "Promise Consumption Flow",
                  description: "Promises create a clear async pipeline.",
                  flow: [
                    "Create promise",
                    "Resolve or reject",
                    "then → success",
                    "catch → error",
                    "finally → cleanup",
                  ],
                },
                {
                  title: "Simulating Async with setTimeout",
                  description: "Promises usually wrap async operations.",
                  code: "const fetchData = () => new Promise((resolve, reject) => {\n  setTimeout(() => resolve('Fetched Data'), 1000);\n});",
                },
                {
                  title: "Why Promises Are Better Than Callbacks",
                  description: "Promises flatten async logic.",
                  benefits: [
                    "Readable code",
                    "Chainable",
                    "Centralized error handling",
                  ],
                  comparison: "Callbacks → nested | Promises → flat",
                },
                {
                  title: "Common Promise Mistakes",
                  description: "Small mistakes cause big bugs.",
                  mistakes: [
                    "Forgetting to return promise",
                    "Handling errors in then instead of catch",
                    "Creating promises unnecessarily",
                  ],
                },
                {
                  title: "How This Maps to Real APIs",
                  description: "fetch() returns a Promise.",
                  example:
                    "fetch('/api').then(res => res.json()).then(data => console.log(data));",
                  takeaway: "Promises power modern APIs",
                },
                {
                  title: "How This Maps to React",
                  description: "React effects use promises heavily.",
                  mapping: [
                    "API calls inside useEffect",
                    "then/catch or async/await",
                  ],
                  example:
                    "useEffect(() => { fetchData().then(setData); }, []);",
                },
              ],

              visualStructure: `
Promise Lifecycle:
Pending
   ↓
Fulfilled → then()
   ↓
Rejected → catch()
   ↓
finally()
    `,

              commonMistakes: [
                {
                  mistake: "Not returning a promise",
                  consequence: "Broken chains",
                  fix: "Always return promises",
                },
                {
                  mistake: "Ignoring catch",
                  consequence: "Unhandled promise rejection",
                  fix: "Always handle errors",
                },
                {
                  mistake: "Using finally for data",
                  consequence: "Data loss",
                  fix: "Use finally only for cleanup",
                },
              ],

              proTips: [
                "Promises settle once",
                "then returns a new promise",
                "catch handles all upstream errors",
                "Promises are foundation of async/await",
              ],

              realWorldAnalogy:
                "A promise is a contract: it will either deliver or fail",
            },

            tasks: [
              "Create custom promise",
              "Handle resolve and reject",
              "Simulate async success and error",
            ],

            examples: [
              "new Promise((resolve, reject) => {})",
              "promise.then(fn).catch(err)",
            ],

            useCases: ["API calls", "Async UI flows", "Error handling"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Create and consume promises",
              "Handle async success & errors",
              "Understand promise lifecycle",
              "Be ready for chaining & async/await",
            ],
          },
          {
            day: 5,
            topic: "Promise Chaining & Error Handling",
            subTopics: [
              "Chaining multiple then()",
              "Returning promises",
              "Centralized error handling",
              "finally() usage",
              "Promise flow control",
            ],
            explanation:
              "Promise chaining helps manage sequential async tasks without nesting.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Multi-Step API Workflow Builder",
                description:
                  "Build a real-world sequential async flow using promise chaining.",
                flow: [
                  "authenticateUser()",
                  "fetchUserProfile()",
                  "fetchUserPermissions()",
                  "initializeDashboard()",
                ],
                requirements: [
                  "Each step must return a promise",
                  "No nested then() blocks",
                  "Single centralized catch",
                ],
                outcome:
                  "You’ll learn how large apps manage dependent async tasks cleanly.",
              },
              {
                id: "pt-2",
                title: "Broken Chain Debugging Challenge",
                description: "Fix a deliberately broken promise chain.",
                bugsIncluded: [
                  "Missing return statements",
                  "then() not returning promises",
                  "Errors swallowed silently",
                ],
                goals: [
                  "Restore correct execution order",
                  "Ensure errors reach catch()",
                  "Verify finally() always runs",
                ],
                outcome:
                  "You’ll learn to debug real promise bugs faster than most developers.",
              },
              {
                id: "pt-3",
                title: "Centralized Error Handling System",
                description:
                  "Design a promise chain where all errors are handled in one place.",
                constraints: [
                  "No try/catch inside then()",
                  "Throw errors intentionally in steps",
                  "Single catch handles everything",
                ],
                observationPoints: [
                  "Error propagation",
                  "Chain interruption",
                  "Cleanup behavior",
                ],
                outcome: "You’ll master predictable error flow in async code.",
              },
              {
                id: "pt-4",
                title: "Loading State Controller",
                description:
                  "Implement a UI-style loading system using promises.",
                requirements: [
                  "Start loading before chain begins",
                  "Stop loading only in finally()",
                  "Works for both success & failure",
                ],
                outcome:
                  "You’ll build async UI logic exactly like production React apps.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Promise chains fail silently when returns are missing — tests catch this early.",
              realWorldChecks: [
                "Is each then() returning a value or promise?",
                "Do errors reach the final catch()?",
                "Does cleanup run in all cases?",
              ],
              debugTip:
                "Temporarily add logs between then() calls to verify execution order.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is promise chaining?",
                "Why must you return a promise from then()?",
                "How does error propagation work in promise chains?",
                "Difference between catch() and finally()?",
              ],
              strongAnswerPattern:
                "Explain chaining → return behavior → centralized error handling → real-world use",
              redFlag: "Not knowing that then() always returns a new promise.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Every then() must return something",
                "Promises flow top to bottom",
                "Errors bubble until caught",
                "finally is cleanup only",
              ],
              refactorRule:
                "If async logic looks nested, flatten it into a promise chain.",
            },
            // 📚 Detailed Learning Content (Async Control Flow)
            content: {
              introduction:
                "Promise chaining allows multiple asynchronous operations to run in sequence without creating nested callbacks. Each then() receives the result of the previous step, making async flows predictable and readable.",

              sections: [
                {
                  title: "What Is Promise Chaining?",
                  description:
                    "Running async operations one after another using then().",
                  keyRule: "Each then() returns a new promise",
                  code: "doStep1()\n  .then(result1 => doStep2(result1))\n  .then(result2 => doStep3(result2));",
                  takeaway: "Flat, readable async flow",
                },
                {
                  title: "Returning Values vs Returning Promises",
                  description: "This decides how the chain behaves.",
                  rules: [
                    "Return value → wrapped in Promise.resolve",
                    "Return promise → chain waits",
                  ],
                  code: "then(data => data.id)\nthen(id => fetchUser(id))",
                  takeaway: "Always return something",
                },
                {
                  title: "Sequential Async Example",
                  description: "Real-world API flow.",
                  exampleFlow: [
                    "Login user",
                    "Fetch profile",
                    "Fetch permissions",
                  ],
                  code: "login()\n  .then(user => fetchProfile(user.id))\n  .then(profile => fetchPermissions(profile.role));",
                },
                {
                  title: "Centralized Error Handling",
                  description: "One catch handles all previous errors.",
                  rule: "catch() catches any rejection above it",
                  code: "fetchData()\n  .then(process)\n  .then(save)\n  .catch(err => console.error(err));",
                  takeaway: "No need for multiple try-catch",
                },
                {
                  title: "Error Propagation in Chains",
                  description: "Errors move down the chain.",
                  examples: ["Rejected promise", "Thrown error inside then"],
                  code: "then(() => { throw new Error('Fail'); })",
                  takeaway: "Thrown errors behave like reject()",
                },
                {
                  title: "Using finally() in Chains",
                  description: "Runs regardless of success or failure.",
                  useCases: ["Stop loader", "Enable button", "Cleanup UI"],
                  code: ".finally(() => setLoading(false));",
                  rule: "Never use finally() for data",
                },
                {
                  title: "Promise Flow Control",
                  description: "Chaining creates a pipeline.",
                  pipeline: "Async Task → then → then → catch → finally",
                  takeaway: "Promises create deterministic flow",
                },
                {
                  title: "Anti-Patterns to Avoid",
                  description: "These break promise chains.",
                  antiPatterns: [
                    "Not returning promises",
                    "Nested then() blocks",
                    "Handling errors in every then",
                  ],
                },
                {
                  title: "Promise Chaining vs Callback Hell",
                  description: "Why promises win.",
                  comparison: [
                    "Callbacks → nested & fragile",
                    "Promises → flat & maintainable",
                  ],
                  takeaway: "Promises solved async readability",
                },
                {
                  title: "How This Translates to async/await",
                  description: "async/await is syntax sugar over chains.",
                  equivalent: "await step1(); await step2();",
                  takeaway: "Understanding chains = mastering async/await",
                },
                {
                  title: "How This Maps to React",
                  description: "React data fetching often uses chains.",
                  example:
                    "useEffect(() => {\n  fetchData()\n    .then(setData)\n    .catch(setError)\n    .finally(() => setLoading(false));\n}, []);",
                },
              ],

              visualStructure: `
Promise Chain:
Start
  ↓
then → then → then
  ↓
catch (errors)
  ↓
finally (cleanup)
    `,

              commonMistakes: [
                {
                  mistake: "Not returning a promise",
                  consequence: "Broken sequence",
                  fix: "Always return from then()",
                },
                {
                  mistake: "Multiple catch blocks everywhere",
                  consequence: "Messy code",
                  fix: "Use one centralized catch",
                },
                {
                  mistake: "Using finally for logic",
                  consequence: "Unexpected bugs",
                  fix: "Use finally only for cleanup",
                },
              ],

              proTips: [
                "then always returns a promise",
                "Thrown errors go to catch",
                "Chains are predictable",
                "Async/await is built on this",
              ],

              realWorldAnalogy:
                "Promise chaining is an assembly line — each step waits for the previous one",
            },

            tasks: [
              "Chain multiple async operations",
              "Handle errors in chain",
              "Add loading and completion states",
            ],

            examples: ["fetchData().then().then().catch()"],

            useCases: ["Sequential API calls", "Multi-step async workflows"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Chain promises correctly",
              "Return promises properly",
              "Handle errors centrally",
              "Prepare for async/await syntax",
            ],
          },
          {
            day: 6,
            topic: "Promise Utilities (all, race)",
            subTopics: [
              "Promise.all()",
              "Promise.race()",
              "Handling multiple async tasks",
              "Partial failure handling",
              "Performance optimization",
            ],
            explanation:
              "Promise utilities allow running multiple async operations in parallel.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Parallel Dashboard Data Loader",
                description:
                  "Build a dashboard-like data loader using Promise.all().",
                setup: ["fetchUser()", "fetchPosts()", "fetchNotifications()"],
                requirements: [
                  "All APIs must start in parallel",
                  "Destructure results correctly",
                  "Handle global failure with catch()",
                ],
                outcome:
                  "You’ll implement the most common real-world Promise.all use case.",
              },
              {
                id: "pt-2",
                title: "Sequential vs Parallel Performance Test",
                description:
                  "Compare execution time of sequential promises vs Promise.all.",
                steps: [
                  "Run APIs one after another",
                  "Run same APIs using Promise.all",
                  "Measure total time",
                ],
                observationPoints: [
                  "Total execution time",
                  "UI wait duration",
                  "User experience impact",
                ],
                outcome: "You’ll know exactly when parallel execution matters.",
              },
              {
                id: "pt-3",
                title: "Partial Failure Safe Loader",
                description:
                  "Design a system where one API failure does not crash everything.",
                constraints: [
                  "Use Promise.all",
                  "Handle individual failures inside promises",
                  "Allow partial UI rendering",
                ],
                learningFocus: [
                  "Failure isolation",
                  "Graceful degradation",
                  "Production-safe async design",
                ],
                outcome: "You’ll learn how large apps survive unreliable APIs.",
              },
              {
                id: "pt-4",
                title: "Timeout Controller with Promise.race",
                description:
                  "Implement a real timeout system using Promise.race().",
                requirements: [
                  "Create a timeout promise",
                  "Race it against a fetch call",
                  "Handle timeout error cleanly",
                ],
                outcome:
                  "You’ll build a pattern used in almost every serious frontend app.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Parallel async bugs don’t show in logic tests — they show in timing and failure scenarios.",
              realWorldChecks: [
                "Do all promises really start together?",
                "What happens if one promise fails?",
                "Is UI blocked waiting for unnecessary data?",
              ],
              debugTip:
                "Log start timestamps of each promise to confirm parallel execution.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is Promise.all and how does it work?",
                "What happens if one promise fails in Promise.all?",
                "Difference between Promise.all and Promise.race?",
                "When would you use Promise.race in real apps?",
              ],
              strongAnswerPattern:
                "Explain parallel execution → failure behavior → real-world use cases",
              redFlag: "Saying Promise.all runs promises sequentially.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Independent tasks should run in parallel",
                "Promise.all is all-or-nothing by default",
                "Promise.race is about speed, not completeness",
                "Failure strategy must be intentional",
              ],
              refactorRule:
                "If async tasks don’t depend on each other, refactor into Promise.all.",
            },
            // 📚 Detailed Learning Content (Parallel Async Control)
            content: {
              introduction:
                "Real-world applications rarely depend on a single async task. Dashboards, feeds, and apps often need multiple API calls at once. Promise utilities help run tasks in parallel, improving performance and user experience.",

              sections: [
                {
                  title: "Why Promise Utilities Exist",
                  description:
                    "Sequential async execution is slow when tasks are independent.",
                  example: "Fetching user, posts, and notifications one-by-one",
                  problem: "Unnecessary waiting time",
                  solution: "Run independent tasks in parallel",
                },
                {
                  title: "Promise.all() – Run All in Parallel",
                  description: "Promise.all waits for ALL promises to resolve.",
                  syntax: "Promise.all([p1, p2, p3])",
                  keyPoints: [
                    "Runs promises in parallel",
                    "Resolves with array of results",
                    "Rejects if ANY promise fails",
                  ],
                  code: "Promise.all([\n  fetchUser(),\n  fetchPosts(),\n  fetchNotifications()\n]).then(([user, posts, notifications]) => {\n  console.log(user, posts, notifications);\n});",
                  takeaway: "All-or-nothing behavior",
                },
                {
                  title: "Execution Behavior of Promise.all",
                  description: "Important performance insight.",
                  facts: [
                    "Promises start immediately",
                    "They do NOT wait for each other",
                    "Order of results matches input order",
                  ],
                  takeaway: "Fastest way to load independent data",
                },
                {
                  title: "Promise.all() Failure Case",
                  description: "Single failure rejects entire promise.",
                  example: "One API fails",
                  behavior: "Promise.all rejects immediately",
                  code: "Promise.all([p1, p2]).catch(err => console.error(err));",
                  warning: "Be careful when partial data is acceptable",
                },
                {
                  title: "Partial Failure Handling Strategy",
                  description: "When all-or-nothing is not desired.",
                  approaches: [
                    "Wrap promises with catch",
                    "Use Promise.allSettled (preview)",
                  ],
                  example: "p.catch(err => null)",
                  note: "allSettled covered later if needed",
                },
                {
                  title: "Promise.race() – First One Wins",
                  description:
                    "Resolves or rejects with the first settled promise.",
                  syntax: "Promise.race([p1, p2])",
                  keyPoints: [
                    "Returns fastest result",
                    "Ignores slower promises",
                  ],
                  code: "Promise.race([\n  fetchFromCache(),\n  fetchFromServer()\n]).then(data => console.log(data));",
                  takeaway: "Speed-based decision",
                },
                {
                  title: "Common Use Cases for Promise.race",
                  description: "race is about responsiveness.",
                  useCases: [
                    "Timeout handling",
                    "Fastest API response",
                    "Fallback systems",
                  ],
                },
                {
                  title: "Timeout Pattern with Promise.race",
                  description: "Prevent infinite waiting.",
                  code: "const timeout = new Promise((_, reject) =>\n  setTimeout(() => reject('Timeout'), 3000)\n);\n\nPromise.race([fetchData(), timeout]);",
                  takeaway: "Essential production pattern",
                },
                {
                  title: "Sequential vs Parallel Execution",
                  description: "Performance comparison.",
                  comparison: [
                    "Sequential → Slow but controlled",
                    "Parallel → Fast and efficient",
                  ],
                  rule: "Use parallel when tasks are independent",
                },
                {
                  title: "Promise Utilities & Performance",
                  description: "Parallel execution improves UX.",
                  benefits: [
                    "Faster load time",
                    "Better perceived performance",
                    "Reduced waiting",
                  ],
                },
                {
                  title: "How This Maps to React",
                  description: "Dashboards use Promise.all heavily.",
                  example:
                    "useEffect(() => {\n  Promise.all([fetchUser(), fetchStats()])\n    .then(([user, stats]) => setData({ user, stats }))\n    .catch(setError);\n}, []);",
                  takeaway: "Parallel data loading is standard in React apps",
                },
                {
                  title: "Mental Model to Remember",
                  description: "Choosing the right utility.",
                  rules: [
                    "Need all results → Promise.all",
                    "Need fastest result → Promise.race",
                  ],
                },
              ],

              visualStructure: `
Promise.all:
p1 ─┐
p2 ─┼──► All resolved → results[]
p3 ─┘

Promise.race:
p1 ─┐
p2 ─┼──► First settled wins
p3 ─┘
    `,

              commonMistakes: [
                {
                  mistake: "Using Promise.all when partial data is OK",
                  consequence: "Unnecessary failure",
                  fix: "Handle partial failures or use allSettled",
                },
                {
                  mistake: "Assuming sequential execution",
                  consequence: "Wrong performance expectations",
                  fix: "Understand parallel execution",
                },
                {
                  mistake: "Not handling rejection",
                  consequence: "Unhandled promise rejection",
                  fix: "Always add catch",
                },
              ],

              proTips: [
                "Promise.all is for speed",
                "Promise.race is for responsiveness",
                "Parallel execution improves UX",
                "These utilities are heavily used in production",
              ],

              realWorldAnalogy:
                "Promise.all is waiting for everyone to arrive; Promise.race is taking the first cab that shows up",
            },

            tasks: [
              "Fetch multiple APIs together",
              "Handle fastest response",
              "Compare sequential vs parallel execution",
            ],

            examples: ["Promise.all([p1, p2])", "Promise.race([p1, p2])"],

            useCases: ["Dashboard data loading", "Parallel API requests"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Run async tasks in parallel",
              "Use Promise.all correctly",
              "Use Promise.race for speed-based logic",
              "Optimize async performance",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Weather Dashboard",
            subTopics: [
              "Async data fetching",
              "Promise-based API calls",
              "Loading and error states",
              "DOM update with async data",
              "User input handling",
            ],
            explanation:
              "Apply async JavaScript concepts to build a real-world weather application.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Weather Dashboard",
                description:
                  "Build a complete weather dashboard with real async UX behavior.",
                coreFeatures: [
                  "City-based weather search",
                  "Live API data rendering",
                  "Loading indicator",
                  "Graceful error handling",
                ],
                requirements: [
                  "Use Promise-based fetch flow",
                  "No DOM updates before data arrives",
                  "Clear old state before new request",
                ],
                outcome:
                  "You’ll build a real-world async project suitable for interviews.",
              },
              {
                id: "pt-2",
                title: "Failure Simulation & Recovery",
                description:
                  "Test how your app behaves under failure conditions.",
                scenarios: [
                  "Invalid city name",
                  "Network failure",
                  "Slow API response",
                ],
                rules: [
                  "UI must not crash",
                  "Errors must be user-friendly",
                  "Loader must always stop",
                ],
                outcome:
                  "You’ll learn defensive async programming used in production apps.",
              },
              {
                id: "pt-3",
                title: "Parallel Weather Fetch Enhancement",
                description:
                  "Enhance the app using Promise.all for better performance.",
                setup: ["Fetch current weather", "Fetch forecast data"],
                constraints: [
                  "Both APIs must run in parallel",
                  "Render only when both succeed",
                  "Handle partial failure gracefully",
                ],
                outcome:
                  "You’ll experience real performance optimization techniques.",
              },
              {
                id: "pt-4",
                title: "Async UX Polish Pass",
                description:
                  "Improve UX details that separate beginners from professionals.",
                checklist: [
                  "Disable search during loading",
                  "Clear previous errors on new search",
                  "Prevent duplicate API calls",
                  "Handle empty input safely",
                ],
                outcome: "Your project will feel polished, not demo-level.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most bugs in async UI apps are state bugs, not API bugs.",
              realWorldChecks: [
                "Does loader always stop?",
                "Can UI recover after an error?",
                "Is stale data ever shown?",
              ],
              debugTip:
                "Log state transitions (loading → success/error) instead of raw API data.",
            },
            interviewInsights: {
              commonQuestions: [
                "How do you handle loading and error states in async apps?",
                "How does fetch work with Promises?",
                "How would you optimize multiple API calls?",
                "How would you convert this project to React?",
              ],
              strongAnswerPattern:
                "Explain async flow → state handling → error strategy → performance decisions",
              redFlag:
                "Ignoring loading/error states or updating UI before data arrives.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Async data drives UI",
                "State controls rendering",
                "Errors are first-class citizens",
                "Cleanup is part of async logic",
              ],
              refactorRule:
                "If async flow is unclear, rewrite it as an explicit step-by-step pipeline.",
            },
            // 📚 Detailed Learning Content (Capstone Async Project)
            content: {
              introduction:
                "This mini project brings together everything learned in Week 13 — asynchronous JavaScript, Promises, chaining, parallel requests, error handling, and dynamic UI updates. A weather dashboard is a classic real-world use case that mirrors how modern frontend apps fetch and display live data.",

              sections: [
                {
                  title: "Project Goal",
                  description:
                    "Build a weather dashboard that fetches live weather data based on user input.",
                  features: [
                    "Search weather by city",
                    "Show temperature and conditions",
                    "Display loading state",
                    "Handle API errors gracefully",
                  ],
                  rule: "Focus on logic, async flow, and UX clarity",
                },
                {
                  title: "API & Async Data Fetching",
                  description: "Weather data comes from an external API.",
                  keyPoints: [
                    "fetch() returns a Promise",
                    "Response must be converted using res.json()",
                    "Network calls are asynchronous",
                  ],
                  code: "fetch(url)\n  .then(res => res.json())\n  .then(data => console.log(data));",
                  takeaway: "All real data in frontend is async",
                },
                {
                  title: "Promise-Based Flow",
                  description:
                    "Weather fetch follows a predictable async pipeline.",
                  flow: [
                    "User searches city",
                    "Show loading UI",
                    "Fetch weather data",
                    "Update UI with result",
                    "Handle error if any",
                  ],
                  takeaway: "Clear async flow prevents bugs",
                },
                {
                  title: "Handling Loading State",
                  description: "Users must know something is happening.",
                  patterns: ["Show spinner or text", "Disable search button"],
                  code: "setLoading(true);\nfetchWeather()\n  .finally(() => setLoading(false));",
                  rule: "Never leave users guessing",
                },
                {
                  title: "Error Handling (CRITICAL)",
                  description: "APIs can fail for many reasons.",
                  scenarios: [
                    "Invalid city name",
                    "Network error",
                    "API limit exceeded",
                  ],
                  code: ".catch(err => showError('City not found'));",
                  takeaway: "Error handling is mandatory, not optional",
                },
                {
                  title: "DOM Update with Async Data",
                  description: "UI updates happen after data arrives.",
                  examples: [
                    "Update temperature text",
                    "Change weather icon",
                    "Show city name",
                  ],
                  rule: "DOM updates must be data-driven",
                },
                {
                  title: "User Input Handling",
                  description: "User input triggers async logic.",
                  patterns: ["Form submit", "Button click"],
                  code: "form.addEventListener('submit', e => {\n  e.preventDefault();\n  fetchWeather(input.value);\n});",
                },
                {
                  title: "Using Promise.all (Optional Enhancement)",
                  description: "Fetch multiple weather endpoints together.",
                  useCase: "Current weather + forecast",
                  code: "Promise.all([\n  fetchCurrent(),\n  fetchForecast()\n]).then(([current, forecast]) => {\n  render(current, forecast);\n});",
                  takeaway: "Parallel fetching improves performance",
                },
                {
                  title: "State Management (Mental Model)",
                  description: "Even without frameworks, state exists.",
                  exampleState: {
                    loading: false,
                    error: null,
                    weatherData: null,
                  },
                  rule: "UI reflects state",
                },
                {
                  title: "UX & Performance Tips",
                  description: "Small details matter.",
                  bestPractices: [
                    "Debounce input if needed",
                    "Clear previous errors",
                    "Avoid unnecessary DOM updates",
                  ],
                },
                {
                  title: "How This Maps to React",
                  description: "This project directly converts to React.",
                  mapping: [
                    "weatherData → useState",
                    "loading → useState",
                    "fetch logic → useEffect",
                    "Form submit → onSubmit",
                  ],
                  example:
                    "useEffect(() => {\n  fetchWeather(city)\n    .then(setData)\n    .catch(setError)\n    .finally(() => setLoading(false));\n}, [city]);",
                  takeaway: "You are React-ready after this project",
                },
              ],

              visualStructure: `
User Input
    ↓
Show Loader
    ↓
Fetch API (Promise)
    ↓
Success → Update UI
Failure → Show Error
    ↓
Hide Loader
    `,

              commonMistakes: [
                {
                  mistake: "Not handling loading state",
                  consequence: "Confusing UX",
                  fix: "Always show loading feedback",
                },
                {
                  mistake: "Ignoring API errors",
                  consequence: "Broken UI",
                  fix: "Handle errors explicitly",
                },
                {
                  mistake: "Updating DOM before data arrives",
                  consequence: "Undefined values",
                  fix: "Update UI only after promise resolves",
                },
              ],

              proTips: [
                "Weather apps are classic interview projects",
                "Async + UI state = real frontend skill",
                "Promise flow matters more than UI design",
                "This project maps 1:1 to React apps",
              ],

              realWorldAnalogy:
                "A weather app is like asking someone for information and waiting — you don’t respond until the answer arrives",
            },

            tasks: [
              "Fetch weather data using Promises",
              "Display temperature and conditions",
              "Handle loading and error UI",
              "Search weather by city",
            ],

            examples: [
              "fetch(url).then(res => res.json())",
              "Promise.all for forecast data",
            ],

            useCases: [
              "Real-world API-driven apps",
              "Interview portfolio project",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "3–4 hours",

            // 🎯 Learning objectives
            objectives: [
              "Build a complete async JavaScript app",
              "Handle loading and error states correctly",
              "Fetch and render API data",
              "Be fully prepared for React async patterns",
            ],
          },
        ],
      },
      {
        week: 14,
        weekGoal:
          "Master async/await and integrate real-world APIs in frontend applications",
        weekOutcome:
          "Ability to fetch, handle, display and manage API data like a professional frontend developer",
        canLearn: ["students", "beginners", "professionals"],
        miniProject: "API-Powered Application",
        days: [
          {
            day: 1,
            topic: "Async/Await Fundamentals",
            subTopics: [
              "Why async/await was introduced",
              "async keyword",
              "await keyword",
              "Async functions return promises",
              "Replacing promise chains with async/await",
            ],
            explanation:
              "Async/await is a cleaner and more readable way to work with promises, making asynchronous code look synchronous.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Promise Chain → Async/Await Refactor",
                description:
                  "Refactor an existing promise chain into async/await syntax.",
                setup: [
                  "Given code uses then/catch chaining",
                  "Multiple dependent async steps",
                ],
                requirements: [
                  "Replace then() with await",
                  "Use try/catch for error handling",
                  "Preserve exact execution logic",
                ],
                outcome:
                  "You’ll clearly see how async/await improves readability without changing behavior.",
              },
              {
                id: "pt-2",
                title: "Async Function Behavior Experiment",
                description:
                  "Prove that async functions always return promises.",
                scenarios: [
                  "Return a value",
                  "Throw an error",
                  "Return an awaited promise",
                ],
                rules: [
                  "Consume async function using then/catch",
                  "Log resolved and rejected values",
                ],
                outcome:
                  "You’ll eliminate confusion around what async functions actually return.",
              },
              {
                id: "pt-3",
                title: "Error Handling Discipline Drill",
                description:
                  "Practice safe async error handling using try/catch.",
                constraints: [
                  "At least one awaited rejection",
                  "No .catch() allowed",
                  "All errors handled in try/catch",
                ],
                learningFocus: [
                  "Rejected promise → thrown error",
                  "Centralized error handling",
                  "Readable control flow",
                ],
                outcome: "You’ll develop production-safe async habits.",
              },
              {
                id: "pt-4",
                title: "Sequential vs Parallel Await Test",
                description: "Compare sequential awaits with Promise.all.",
                setup: [
                  "Version A: await task1; await task2;",
                  "Version B: await Promise.all([task1(), task2()])",
                ],
                observationPoints: [
                  "Execution time",
                  "Code readability",
                  "Performance difference",
                ],
                outcome:
                  "You’ll learn when async/await can accidentally slow apps.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Async/await bugs are usually logic bugs, not syntax bugs.",
              realWorldChecks: [
                "Are awaited values actually resolved?",
                "Is error handling consistent?",
                "Is async flow accidentally sequential?",
              ],
              debugTip:
                "Log awaited values immediately after await to catch unresolved promises.",
            },
            interviewInsights: {
              commonQuestions: [
                "Why was async/await introduced?",
                "Do async functions return promises?",
                "How does error handling work with async/await?",
                "Does async/await make code faster?",
              ],
              strongAnswerPattern:
                "Explain promise pain → async/await readability → error handling → performance reality",
              redFlag:
                "Saying async/await replaces promises or makes code faster.",
            },
            editorGuidance: {
              mentalChecklist: [
                "async = promise-returning function",
                "await pauses function, not thread",
                "Rejected promise = thrown error",
                "Parallel work still needs Promise.all",
              ],
              refactorRule:
                "If promise chains reduce readability, refactor to async/await — not the other way around.",
            },
            // 📚 Detailed Learning Content (Modern Async JavaScript)
            content: {
              introduction:
                "Async/await was introduced to solve the readability problems of promise chains. It does NOT replace promises — it is built on top of them. Async/await allows developers to write asynchronous code that looks and behaves like synchronous code, while still being non-blocking.",

              sections: [
                {
                  title: "Why async/await Was Introduced",
                  description:
                    "Promise chains solved callback hell, but long chains still became hard to read.",
                  problemsWithPromises: [
                    "Multiple then() calls",
                    "Indentation increases with logic",
                    "Error handling feels indirect",
                  ],
                  solution: "Write async code in a top-down, readable style",
                  takeaway: "async/await improves readability, not performance",
                },
                {
                  title: "The async Keyword",
                  description:
                    "Declaring a function as async changes how it behaves.",
                  keyPoints: [
                    "async functions ALWAYS return a Promise",
                    "Returned values are wrapped in Promise.resolve",
                  ],
                  code: "async function getData() {\n  return 'data';\n}\n\ngetData().then(console.log);",
                  takeaway: "async = promise-returning function",
                },
                {
                  title: "The await Keyword",
                  description:
                    "await pauses execution until a Promise settles.",
                  rules: [
                    "Can only be used inside async functions",
                    "Waits for promise resolution",
                  ],
                  code: "async function load() {\n  const data = await fetchData();\n  console.log(data);\n}",
                  importantNote: "await does NOT block the main thread",
                },
                {
                  title: "Execution Flow with async/await",
                  description:
                    "Code looks synchronous but runs asynchronously.",
                  example:
                    "console.log('A');\nawait fetchData();\nconsole.log('B');",
                  behavior: "A → (wait async) → B",
                  takeaway: "Readability improves, async behavior remains",
                },
                {
                  title: "Async Functions Return Promises (CRITICAL)",
                  description:
                    "This explains compatibility with .then() and .catch().",
                  code: "async function foo() { return 42; }\n\nfoo().then(v => console.log(v));",
                  takeaway: "async/await and promises are interoperable",
                },
                {
                  title: "Replacing Promise Chains",
                  description: "Compare old vs modern syntax.",
                  promiseVersion:
                    "fetchUser()\n  .then(user => fetchProfile(user.id))\n  .then(profile => console.log(profile))\n  .catch(console.error);",
                  asyncAwaitVersion:
                    "async function loadProfile() {\n  try {\n    const user = await fetchUser();\n    const profile = await fetchProfile(user.id);\n    console.log(profile);\n  } catch (err) {\n    console.error(err);\n  }\n}",
                  takeaway: "Same logic, clearer flow",
                },
                {
                  title: "Error Handling with try/catch",
                  description: "Errors are handled like synchronous code.",
                  code: "try {\n  const data = await fetchData();\n} catch (err) {\n  console.error(err);\n}",
                  rule: "Rejected promise → thrown error",
                },
                {
                  title: "Common async/await Mistakes",
                  description: "These cause confusion for beginners.",
                  mistakes: [
                    "Forgetting await",
                    "Using await outside async",
                    "Not wrapping in try/catch",
                  ],
                  takeaway: "Syntax is simple, discipline matters",
                },
                {
                  title: "Performance Misconception",
                  description: "async/await does NOT make code faster.",
                  fact: "It is syntax sugar over promises",
                  realRule: "Parallelism still requires Promise.all",
                },
                {
                  title: "How This Maps to React",
                  description: "Modern React uses async/await everywhere.",
                  mapping: [
                    "Data fetching in useEffect",
                    "Form submissions",
                    "Event handlers",
                  ],
                  example:
                    "useEffect(() => {\n  const load = async () => {\n    const data = await fetchData();\n    setData(data);\n  };\n  load();\n}, []);",
                  takeaway: "Async/await is the default in React",
                },
              ],

              visualStructure: `
Promise Chain:
then → then → catch

Async/Await:
await
await
try/catch
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting await",
                  consequence: "Working with unresolved promises",
                  fix: "Always await async calls",
                },
                {
                  mistake: "No error handling",
                  consequence: "Unhandled rejections",
                  fix: "Use try/catch",
                },
                {
                  mistake: "Sequential awaits when parallel needed",
                  consequence: "Slow performance",
                  fix: "Use Promise.all",
                },
              ],

              proTips: [
                "Async/await is about readability",
                "await pauses function, not browser",
                "Promises still exist under the hood",
                "Master this before React data fetching",
              ],

              realWorldAnalogy:
                "Async/await is like waiting for a response before continuing a conversation",
            },

            tasks: [
              "Convert promise-based code to async/await",
              "Understand execution flow",
              "Handle resolved values",
            ],

            examples: [
              "async function getData() { return 'data'; }",
              "const result = await fetchData();",
            ],

            useCases: [
              "API calls",
              "Sequential async operations",
              "Cleaner code in React apps",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand why async/await exists",
              "Write async functions correctly",
              "Replace promise chains cleanly",
              "Prepare for production async code",
            ],
          },
          {
            day: 2,
            topic: "Error Handling with try-catch",
            subTopics: [
              "try-catch syntax",
              "Handling rejected promises",
              "finally block",
              "Custom error messages",
              "Graceful UI error handling",
            ],
            explanation:
              "try-catch allows handling errors in async/await just like synchronous code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "API Failure Simulation Lab",
                description:
                  "Simulate different API failure scenarios and handle them safely.",
                scenarios: [
                  "Network failure",
                  "404 / 500 response",
                  "Invalid JSON response",
                ],
                requirements: [
                  "Wrap all async calls in try-catch",
                  "Manually check response.ok",
                  "Show user-friendly error messages",
                ],
                outcome:
                  "You’ll learn how to protect your app from real-world API failures.",
              },
              {
                id: "pt-2",
                title: "Loader Safety Guarantee",
                description:
                  "Ensure loading state always stops — no matter what fails.",
                constraints: [
                  "Loader starts before async call",
                  "Loader stops only in finally()",
                  "Works for both success and error",
                ],
                verificationSteps: [
                  "Force error manually",
                  "Check loader behavior",
                  "Retry action after failure",
                ],
                outcome:
                  "You’ll eliminate one of the most common production UI bugs.",
              },
              {
                id: "pt-3",
                title: "Custom Error Message Builder",
                description: "Create meaningful, user-facing error messages.",
                setup: [
                  "Throw custom Error objects",
                  "Map technical errors to friendly messages",
                ],
                rules: [
                  "No stack traces in UI",
                  "Console logs allowed for developers",
                ],
                outcome:
                  "You’ll separate developer diagnostics from user experience.",
              },
              {
                id: "pt-4",
                title: "Centralized Async Error Strategy",
                description:
                  "Design a consistent error-handling pattern for async functions.",
                constraints: [
                  "Multiple await calls inside one try block",
                  "Single catch handles all failures",
                  "finally handles cleanup only",
                ],
                outcome: "You’ll build predictable, maintainable async flows.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most production crashes happen during failures, not success paths.",
              realWorldChecks: [
                "Does the UI recover after an error?",
                "Is loading state always reset?",
                "Are errors logged and communicated properly?",
              ],
              debugTip:
                "Force errors intentionally to test async resilience before shipping.",
            },
            interviewInsights: {
              commonQuestions: [
                "How do you handle errors in async/await?",
                "How does try-catch work with rejected promises?",
                "Why doesn’t fetch reject on HTTP errors?",
                "What should go inside finally?",
              ],
              strongAnswerPattern:
                "Explain rejected promise behavior → try-catch flow → UI cleanup strategy",
              redFlag:
                "Using empty catch blocks or showing raw error objects to users.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Rejected promise = thrown error",
                "fetch requires manual response.ok check",
                "finally is for cleanup only",
                "Errors must not break UI",
              ],
              refactorRule:
                "If async code can fail, it must be wrapped in try-catch.",
            },
            // 📚 Detailed Learning Content (Production-Safe Async Code)
            content: {
              introduction:
                "In real-world applications, failures are normal — APIs fail, networks drop, users input wrong data. try-catch allows developers to handle these failures gracefully instead of crashing the app. With async/await, error handling becomes predictable and readable.",

              sections: [
                {
                  title: "Why Error Handling Matters",
                  description: "Unhandled errors break user trust.",
                  realWorldFailures: [
                    "No internet connection",
                    "Invalid API response",
                    "Server errors (500)",
                    "Unauthorized access (401)",
                  ],
                  takeaway: "Errors are expected — crashes are not",
                },
                {
                  title: "try-catch Syntax",
                  description:
                    "JavaScript handles errors using try-catch blocks.",
                  syntax:
                    "try { /* risky code */ } catch (error) { /* handle error */ }",
                  code: "try {\n  const data = await fetchData();\n} catch (error) {\n  console.error(error);\n}",
                  takeaway: "Rejected promises throw errors",
                },
                {
                  title: "How Async Errors Work",
                  description: "await automatically unwraps promises.",
                  behavior: [
                    "Resolved promise → returns value",
                    "Rejected promise → throws error",
                  ],
                  rule: "Rejected promise = thrown exception",
                },
                {
                  title: "Handling API Errors Correctly",
                  description: "fetch does NOT reject on HTTP errors.",
                  example: "fetch resolves even for 404/500",
                  correctPattern: "Check response.ok manually",
                  code: "const res = await fetch(url);\nif (!res.ok) throw new Error('API Error');",
                  takeaway: "Not all errors are network errors",
                },
                {
                  title: "The finally Block",
                  description: "finally always runs.",
                  useCases: [
                    "Stop loading spinner",
                    "Enable buttons",
                    "Cleanup state",
                  ],
                  code: "try {\n  await loadData();\n} catch (e) {\n  setError(e.message);\n} finally {\n  setLoading(false);\n}",
                  rule: "Never put logic-dependent code in finally",
                },
                {
                  title: "Custom Error Messages",
                  description: "Raw errors are not user-friendly.",
                  examples: [
                    "Technical error → console",
                    "Friendly message → UI",
                  ],
                  code: "throw new Error('City not found');",
                  takeaway: "Users need clarity, not stack traces",
                },
                {
                  title: "Graceful UI Error Handling",
                  description: "Error handling is part of UX.",
                  patterns: [
                    "Show error message",
                    "Allow retry",
                    "Keep UI usable",
                  ],
                  rule: "Never leave UI in broken state",
                },
                {
                  title: "Multiple await Calls in try-catch",
                  description: "One try-catch can cover many awaits.",
                  code: "try {\n  const user = await fetchUser();\n  const posts = await fetchPosts(user.id);\n} catch (e) {\n  console.error(e);\n}",
                  takeaway: "Centralized error handling",
                },
                {
                  title: "Common Error Handling Mistakes",
                  description: "These cause silent bugs.",
                  mistakes: [
                    "Empty catch blocks",
                    "Catching but not handling",
                    "Showing technical errors to users",
                  ],
                },
                {
                  title: "Error Handling Strategy (Mental Model)",
                  description: "Separate concerns.",
                  layers: [
                    "Developer logs (console)",
                    "User-facing messages (UI)",
                    "Fallback behavior (retry)",
                  ],
                },
                {
                  title: "How This Maps to React",
                  description: "React apps rely heavily on try-catch.",
                  example:
                    "useEffect(() => {\n  const load = async () => {\n    try {\n      const data = await fetchData();\n      setData(data);\n    } catch (e) {\n      setError(e.message);\n    } finally {\n      setLoading(false);\n    }\n  };\n  load();\n}, []);",
                  takeaway: "Production React = strong error handling",
                },
              ],

              visualStructure: `
try
  ↓
await async call
  ↓
success → continue
error → catch
  ↓
finally → cleanup
    `,

              commonMistakes: [
                {
                  mistake: "Ignoring response.ok",
                  consequence: "False success",
                  fix: "Manually check API responses",
                },
                {
                  mistake: "No finally block",
                  consequence: "Loading spinner never stops",
                  fix: "Use finally for cleanup",
                },
                {
                  mistake: "Showing raw errors to users",
                  consequence: "Bad UX",
                  fix: "Show friendly messages",
                },
              ],

              proTips: [
                "Errors are part of normal flow",
                "catch should do something meaningful",
                "finally is for cleanup only",
                "Good error handling = professional code",
              ],

              realWorldAnalogy:
                "Error handling is like airbags — you hope you don’t need them, but you must have them",
            },

            tasks: [
              "Wrap async calls in try-catch",
              "Handle API failure",
              "Show error message in UI",
            ],

            examples: [
              "try { await fetch(url); } catch(e) { console.error(e); }",
            ],

            useCases: [
              "API error handling",
              "Network failure scenarios",
              "User-friendly error messages",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Handle async errors safely",
              "Use try-catch and finally correctly",
              "Build resilient UI flows",
              "Write production-ready async code",
            ],
          },
          {
            day: 3,
            topic: "Fetch API & HTTP Methods",
            subTopics: [
              "Fetch API basics",
              "GET requests",
              "POST requests",
              "PUT and DELETE requests",
              "Request options (method, headers, body)",
            ],
            explanation:
              "Fetch API is the standard way to make HTTP requests in modern frontend applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "CRUD API Playground",
                description:
                  "Build a mini CRUD system using a public API or mock server.",
                flow: [
                  "GET → load list",
                  "POST → add new item",
                  "PUT/PATCH → update item",
                  "DELETE → remove item",
                ],
                requirements: [
                  "Use correct HTTP method for each action",
                  "Set headers properly",
                  "Parse and render responses",
                ],
                outcome:
                  "You’ll understand how frontend talks to backend in real applications.",
              },
              {
                id: "pt-2",
                title: "Fetch Error Reality Check",
                description: "Prove that fetch does not reject on HTTP errors.",
                scenarios: [
                  "404 response",
                  "500 response",
                  "Successful 200 response",
                ],
                requirements: [
                  "Manually check response.ok",
                  "Throw custom errors",
                  "Handle errors in catch",
                ],
                outcome: "You’ll never mishandle fetch errors again.",
              },
              {
                id: "pt-3",
                title: "POST Request with Validation",
                description: "Submit form data to an API using POST.",
                constraints: [
                  "Send JSON body",
                  "Set Content-Type header",
                  "Validate input before sending",
                ],
                learningFocus: [
                  "Request options",
                  "Headers vs body",
                  "API expectations",
                ],
                outcome:
                  "You’ll build safe, production-style form submissions.",
              },
              {
                id: "pt-4",
                title: "HTTP Method Decision Drill",
                description:
                  "Choose the correct HTTP method for different scenarios.",
                scenarios: [
                  "Updating user profile",
                  "Deleting a comment",
                  "Fetching dashboard data",
                  "Submitting a signup form",
                ],
                rules: [
                  "Explain why the method is chosen",
                  "Map each to CRUD operation",
                ],
                outcome:
                  "You’ll gain interview-ready clarity on HTTP semantics.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most frontend API bugs come from incorrect request setup, not backend issues.",
              realWorldChecks: [
                "Is the correct HTTP method used?",
                "Are headers and body formatted correctly?",
                "Is response.ok checked before parsing?",
              ],
              debugTip:
                "Log the full Response object before parsing JSON when debugging fetch issues.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the Fetch API?",
                "Difference between GET and POST?",
                "Why doesn’t fetch reject on HTTP errors?",
                "How do HTTP methods map to CRUD?",
              ],
              strongAnswerPattern:
                "Explain fetch flow → HTTP method choice → error handling → real-world usage",
              redFlag:
                "Assuming fetch throws errors for 404/500 automatically.",
            },
            editorGuidance: {
              mentalChecklist: [
                "fetch returns a Promise of Response",
                "HTTP errors must be handled manually",
                "Headers and body must match API expectations",
                "CRUD mapping guides method choice",
              ],
              refactorRule:
                "If fetch logic repeats, extract it into a reusable API helper.",
            },
            // 📚 Detailed Learning Content (Real-World Networking)
            content: {
              introduction:
                "Frontend applications constantly communicate with servers — fetching data, submitting forms, updating records, and deleting resources. The Fetch API is the modern, promise-based way to perform HTTP requests in the browser and is used everywhere from vanilla JS to React, Next.js, and beyond.",

              sections: [
                {
                  title: "What Is the Fetch API?",
                  description:
                    "fetch() is a browser API used to make HTTP requests.",
                  keyPoints: [
                    "Promise-based",
                    "Non-blocking",
                    "Replaces XMLHttpRequest",
                  ],
                  code: "fetch(url).then(res => res.json()).then(data => console.log(data));",
                  takeaway: "fetch returns a Promise of a Response",
                },
                {
                  title: "HTTP Request–Response Lifecycle",
                  description: "Every fetch follows a standard flow.",
                  steps: [
                    "Client sends request",
                    "Server processes request",
                    "Server sends response",
                    "Client parses response",
                  ],
                  takeaway: "Understanding this flow prevents API bugs",
                },
                {
                  title: "GET Requests (Read Data)",
                  description: "GET is used to retrieve data from server.",
                  characteristics: [
                    "No request body",
                    "Safe & idempotent",
                    "Most common request",
                  ],
                  code: "const res = await fetch(url);\nconst data = await res.json();",
                  useCases: ["Fetching users", "Loading dashboards"],
                },
                {
                  title: "POST Requests (Create Data)",
                  description: "POST is used to send data to server.",
                  keyPoints: ["Includes request body", "Often sends JSON"],
                  code: "fetch(url, {\n  method: 'POST',\n  headers: { 'Content-Type': 'application/json' },\n  body: JSON.stringify(data)\n});",
                  takeaway: "POST creates new resources",
                },
                {
                  title: "PUT & PATCH Requests (Update Data)",
                  description: "Used to update existing data.",
                  difference: [
                    "PUT → replace entire resource",
                    "PATCH → partial update",
                  ],
                  code: "fetch(url, { method: 'PUT', body: JSON.stringify(data) });",
                },
                {
                  title: "DELETE Requests (Remove Data)",
                  description: "Deletes a resource from server.",
                  code: "fetch(url, { method: 'DELETE' });",
                  takeaway: "DELETE usually has no response body",
                },
                {
                  title: "Request Options Explained",
                  description: "fetch accepts a configuration object.",
                  options: ["method", "headers", "body", "credentials"],
                  rule: "Body must be stringified JSON",
                },
                {
                  title: "Headers & Content-Type",
                  description: "Headers describe request metadata.",
                  commonHeaders: ["Content-Type", "Authorization"],
                  code: "headers: { 'Content-Type': 'application/json' }",
                  takeaway: "Wrong headers = broken API calls",
                },
                {
                  title: "Handling Fetch Errors Properly",
                  description: "fetch does not reject on HTTP errors.",
                  correctPattern: "Manually check response.ok",
                  code: "const res = await fetch(url);\nif (!res.ok) throw new Error('Request failed');",
                  takeaway: "Network success ≠ API success",
                },
                {
                  title: "CRUD Mapping (IMPORTANT)",
                  description: "HTTP methods map directly to CRUD.",
                  mapping: [
                    "Create → POST",
                    "Read → GET",
                    "Update → PUT / PATCH",
                    "Delete → DELETE",
                  ],
                  takeaway: "This mapping is interview-favorite",
                },
                {
                  title: "How This Maps to React",
                  description: "Fetch is everywhere in React apps.",
                  example:
                    "useEffect(() => {\n  fetch('/api/users')\n    .then(res => res.json())\n    .then(setUsers)\n    .catch(setError);\n}, []);",
                  takeaway: "Fetch + state = data-driven UI",
                },
              ],

              visualStructure: `
Client (fetch)
     ↓
HTTP Request
     ↓
Server
     ↓
HTTP Response
     ↓
Parse JSON
     ↓
Update UI
    `,

              commonMistakes: [
                {
                  mistake: "Not checking response.ok",
                  consequence: "False success handling",
                  fix: "Throw error manually",
                },
                {
                  mistake: "Forgetting headers",
                  consequence: "Server rejects request",
                  fix: "Always set Content-Type",
                },
                {
                  mistake: "Sending object directly in body",
                  consequence: "Invalid request",
                  fix: "Use JSON.stringify",
                },
              ],

              proTips: [
                "Fetch always returns a Promise",
                "HTTP errors must be handled manually",
                "Master CRUD mapping early",
                "This skill is non-negotiable for frontend jobs",
              ],

              realWorldAnalogy:
                "Fetch is like sending a request form to an office and waiting for approval",
            },

            tasks: [
              "Fetch data from public API",
              "Send POST request with JSON body",
              "Understand request-response lifecycle",
            ],

            examples: [
              "fetch(url)",
              "fetch(url, { method: 'POST', body: JSON.stringify(data) })",
            ],

            useCases: [
              "CRUD operations",
              "Form submissions",
              "Data-driven UIs",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Use Fetch API correctly",
              "Understand HTTP methods",
              "Perform CRUD operations",
              "Build API-driven frontend features",
            ],
          },
          {
            day: 4,
            topic: "Working with JSON & API Responses",
            subTopics: [
              "JSON structure",
              "res.json() method",
              "Parsing nested data",
              "Handling empty responses",
              "Optional chaining",
            ],
            explanation:
              "Most APIs return JSON data which must be parsed and safely accessed.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Crash-Proof API Data Renderer",
                description:
                  "Render deeply nested API data without crashing the app.",
                setup: [
                  "Use a real or mock API response",
                  "Include missing and optional fields",
                ],
                requirements: [
                  "Use optional chaining for all nested access",
                  "Provide fallback values for UI",
                  "App must not crash with partial data",
                ],
                outcome:
                  "You’ll build UI that survives unpredictable API responses.",
              },
              {
                id: "pt-2",
                title: "Broken API Response Simulation",
                description:
                  "Intentionally break API data and handle it safely.",
                scenarios: [
                  "null response",
                  "empty array",
                  "missing nested object",
                ],
                rules: [
                  "No runtime errors allowed",
                  "UI must show meaningful fallback state",
                ],
                outcome: "You’ll stop assuming APIs always behave correctly.",
              },
              {
                id: "pt-3",
                title: "Nested Data Access Refactor",
                description:
                  "Refactor unsafe JSON access into defensive patterns.",
                constraints: [
                  "Convert direct access into optional chaining",
                  "Add default values using ??",
                ],
                comparisonPoints: [
                  "Before vs after stability",
                  "Readability",
                  "Crash resistance",
                ],
                outcome:
                  "You’ll learn how senior devs refactor fragile API code.",
              },
              {
                id: "pt-4",
                title: "Empty State UI Design",
                description:
                  "Design UI behavior for empty or missing API data.",
                requirements: [
                  "Handle empty arrays gracefully",
                  "Show fallback messages",
                  "Avoid rendering undefined values",
                ],
                outcome: "Your UI will feel intentional, not broken.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most frontend crashes happen due to unexpected API data, not logic bugs.",
              realWorldChecks: [
                "What if this field is undefined?",
                "What if the array is empty?",
                "What if the API returns null?",
              ],
              debugTip:
                "Log API responses once and code defensively instead of guessing data shape.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is JSON?",
                "Why is res.json() asynchronous?",
                "How do you safely access nested API data?",
                "What is optional chaining and why is it useful?",
              ],
              strongAnswerPattern:
                "Explain JSON parsing → async nature → defensive access → UI safety",
              redFlag: "Accessing deep API data directly without checks.",
            },
            editorGuidance: {
              mentalChecklist: [
                "APIs are unreliable by nature",
                "res.json() must always be awaited",
                "Deep access requires safety checks",
                "UI must survive missing data",
              ],
              refactorRule:
                "If API data access can crash the app, refactor immediately using optional chaining and defaults.",
            },
            // 📚 Detailed Learning Content (API Data Handling)
            content: {
              introduction:
                "Almost all modern APIs return data in JSON format. Understanding how to parse, read, and safely access JSON data is a core frontend skill. Many production crashes happen not because APIs fail, but because frontend code assumes data always exists.",

              sections: [
                {
                  title: "What Is JSON?",
                  description:
                    "JSON (JavaScript Object Notation) is a lightweight data exchange format.",
                  keyPoints: [
                    "Looks like JavaScript objects",
                    "Text-based format",
                    "Language independent",
                  ],
                  example: '{ "name": "John", "age": 25 }',
                  takeaway: "APIs speak JSON",
                },
                {
                  title: "JSON Structure Basics",
                  description: "JSON supports only specific data types.",
                  supportedTypes: [
                    "Object",
                    "Array",
                    "String",
                    "Number",
                    "Boolean",
                    "null",
                  ],
                  rule: "Functions and undefined are NOT valid JSON",
                },
                {
                  title: "res.json() Method",
                  description: "Converts response body into JavaScript object.",
                  importantFacts: [
                    "res.json() returns a Promise",
                    "Must be awaited",
                  ],
                  code: "const res = await fetch(url);\nconst data = await res.json();",
                  takeaway: "Parsing is asynchronous",
                },
                {
                  title: "Why res.json() Is Async",
                  description: "Response body arrives as a stream.",
                  explanation: "Browser reads and parses data asynchronously",
                  takeaway: "Always await res.json()",
                },
                {
                  title: "Parsing Nested API Data",
                  description: "Real APIs return deeply nested data.",
                  exampleStructure: "{ user: { profile: { name: 'Alex' } } }",
                  code: "const name = data.user.profile.name;",
                  risk: "Crashes if any level is missing",
                },
                {
                  title: "The Problem with Missing Data",
                  description: "APIs are not guaranteed to return full data.",
                  scenarios: [
                    "Empty response",
                    "Partial data",
                    "Optional fields",
                  ],
                  takeaway: "Never trust API shape blindly",
                },
                {
                  title: "Optional Chaining (?.)",
                  description: "Safely access nested properties.",
                  code: "const name = data?.user?.profile?.name;",
                  behavior: "Returns undefined instead of throwing error",
                  takeaway: "Optional chaining prevents crashes",
                },
                {
                  title: "Handling Empty or Null Responses",
                  description: "APIs may return empty data.",
                  examples: ["null", "[]", "{}"],
                  pattern: "Use fallback values",
                  code: "const users = data?.users ?? [];",
                },
                {
                  title: "Defensive Data Access Pattern",
                  description: "Production-safe approach.",
                  rules: [
                    "Check before access",
                    "Use optional chaining",
                    "Provide defaults",
                  ],
                  takeaway: "Defensive coding = stable apps",
                },
                {
                  title: "Common JSON Handling Mistakes",
                  description: "These cause runtime crashes.",
                  mistakes: [
                    "Accessing deeply nested data directly",
                    "Forgetting await res.json()",
                    "Assuming arrays are non-empty",
                  ],
                },
                {
                  title: "Rendering JSON Data in UI",
                  description: "Data must be formatted before display.",
                  examples: [
                    "Map arrays",
                    "Format dates",
                    "Handle empty states",
                  ],
                  rule: "Never render raw JSON blindly",
                },
                {
                  title: "How This Maps to React",
                  description: "React apps rely heavily on safe data access.",
                  example: "return <h1>{user?.profile?.name || 'Guest'}</h1>;",
                  takeaway: "Optional chaining is used everywhere in React",
                },
              ],

              visualStructure: `
API Response (JSON)
       ↓
res.json()
       ↓
JavaScript Object
       ↓
Safe Access (?.)
       ↓
UI Rendering
    `,

              commonMistakes: [
                {
                  mistake: "Direct deep access",
                  consequence: "App crashes",
                  fix: "Use optional chaining",
                },
                {
                  mistake: "Not handling empty arrays",
                  consequence: "Broken UI",
                  fix: "Use default fallbacks",
                },
                {
                  mistake: "Forgetting await res.json()",
                  consequence: "Promise instead of data",
                  fix: "Always await parsing",
                },
              ],

              proTips: [
                "APIs change — your code must be resilient",
                "Optional chaining is your best friend",
                "Handle empty states explicitly",
                "Defensive data handling is senior-level skill",
              ],

              realWorldAnalogy:
                "API data is like a delivery box — always check what’s inside before using it",
            },

            tasks: [
              "Parse API response",
              "Display nested data",
              "Handle missing values",
            ],

            examples: ["const data = await res.json();", "data?.user?.name"],

            useCases: ["User profiles", "Product listings", "Dashboard views"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand JSON structure",
              "Parse API responses correctly",
              "Safely access nested data",
              "Build crash-resistant UIs",
            ],
          },
          {
            day: 5,
            topic: "API Authentication & Headers",
            subTopics: [
              "API keys",
              "Authorization headers",
              "Bearer tokens",
              "Basic auth concept",
              "Security best practices (frontend limits)",
            ],
            explanation:
              "Many APIs require authentication using headers or tokens.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Authenticated API Request Builder",
                description:
                  "Build a fetch wrapper that sends authenticated requests.",
                setup: [
                  "Use Authorization header",
                  "Support Bearer token format",
                ],
                requirements: [
                  "Token injected via headers",
                  "No tokens in URL",
                  "Reusable fetch function",
                ],
                outcome:
                  "You’ll learn how real apps attach auth to every protected API call.",
              },
              {
                id: "pt-2",
                title: "Frontend Security Failure Simulation",
                description:
                  "Demonstrate why frontend cannot securely store secrets.",
                scenarios: [
                  "Inspect API key in DevTools",
                  "View token in Network tab",
                  "Access localStorage values",
                ],
                rules: [
                  "Document how secrets are exposed",
                  "Explain why this is unavoidable",
                ],
                outcome:
                  "You’ll deeply understand frontend security limits — not just memorize them.",
              },
              {
                id: "pt-3",
                title: "Token Storage Strategy Comparison",
                description: "Compare different token storage approaches.",
                storageOptions: [
                  "In-memory variable",
                  "localStorage",
                  "sessionStorage",
                  "HttpOnly cookie (conceptual)",
                ],
                analysisPoints: [
                  "XSS risk",
                  "Persistence",
                  "Security trade-offs",
                ],
                outcome:
                  "You’ll be able to justify token storage choices in interviews.",
              },
              {
                id: "pt-4",
                title: "Auth Mistake Detection Drill",
                description:
                  "Identify security mistakes in sample frontend code.",
                mistakesIncluded: [
                  "Hardcoded API key",
                  "Token in query params",
                  "Long-lived token in localStorage",
                ],
                goal: "Rewrite the code using safer patterns",
                outcome:
                  "You’ll learn to spot dangerous auth patterns instantly.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Security bugs are rarely caught by tests — they’re caught by attackers.",
              realWorldChecks: [
                "Can this token be read in DevTools?",
                "Is sensitive data visible in Network tab?",
                "What happens if token leaks?",
              ],
              debugTip:
                "Always inspect Network requests to verify headers are sent correctly.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is an API key?",
                "What is a Bearer token?",
                "Where should tokens be stored in frontend?",
                "Why can’t frontend securely store secrets?",
              ],
              strongAnswerPattern:
                "Explain auth types → show header usage → clearly state frontend limitations",
              redFlag:
                "Claiming frontend can securely hide API keys or secrets.",
            },
            editorGuidance: {
              mentalChecklist: [
                "All frontend code is visible",
                "Auth data goes in headers",
                "Bearer tokens are user identity",
                "Security is primarily backend’s job",
              ],
              refactorRule:
                "If sensitive data exists in frontend code, move responsibility to backend.",
            },
            // 📚 Detailed Learning Content (API Security – Frontend Perspective)
            content: {
              introduction:
                "Most real-world APIs are protected. They require some form of authentication to identify the client or user. Frontend developers must understand how to send credentials securely — and more importantly, understand what cannot be secured from the frontend.",

              sections: [
                {
                  title: "Why APIs Require Authentication",
                  description:
                    "APIs are protected to prevent abuse and unauthorized access.",
                  reasons: [
                    "Identify the requester",
                    "Limit usage",
                    "Protect sensitive data",
                  ],
                  takeaway: "Public APIs without auth are rare in production",
                },
                {
                  title: "What Are HTTP Headers?",
                  description: "Headers carry metadata about a request.",
                  examples: ["Authorization", "Content-Type", "Accept"],
                  code: "fetch(url, { headers: { 'Content-Type': 'application/json' } });",
                  takeaway: "Auth data is usually sent in headers",
                },
                {
                  title: "API Keys (Basic Protection)",
                  description:
                    "API keys identify the application making requests.",
                  characteristics: [
                    "Static string",
                    "Often tied to usage limits",
                    "NOT user authentication",
                  ],
                  code: "headers: { 'x-api-key': API_KEY }",
                  warnings: [
                    "Never hardcode keys in public frontend",
                    "Anyone can inspect frontend code",
                  ],
                  takeaway: "API keys are weak protection on frontend",
                },
                {
                  title: "Authorization Header",
                  description: "Standard way to send authentication data.",
                  format: "Authorization: <type> <credentials>",
                  example: "Authorization: Bearer TOKEN",
                  takeaway: "Most modern APIs use this pattern",
                },
                {
                  title: "Bearer Tokens (Most Common)",
                  description:
                    "Bearer tokens represent authenticated user sessions.",
                  characteristics: [
                    "Issued by backend",
                    "Short-lived (usually)",
                    "Sent with every request",
                  ],
                  code: "headers: { Authorization: `Bearer ${token}` }",
                  takeaway: "Bearer tokens power modern auth systems",
                },
                {
                  title: "Basic Authentication (Concept)",
                  description:
                    "Old-school authentication using username and password.",
                  format: "Authorization: Basic base64(username:password)",
                  warning: "Rarely used in modern frontend apps",
                  takeaway: "Know the concept for interviews",
                },
                {
                  title: "Where Frontend Should Store Tokens",
                  description: "Storage choice impacts security.",
                  options: [
                    "Memory (safest)",
                    "localStorage (XSS risk)",
                    "sessionStorage",
                    "HttpOnly cookies (backend-controlled)",
                  ],
                  takeaway: "Frontend cannot fully secure secrets",
                },
                {
                  title: "Frontend Security LIMITS (VERY IMPORTANT)",
                  description: "Frontend code is always visible.",
                  truths: [
                    "Anyone can inspect JS",
                    "API keys can be stolen",
                    "Frontend cannot hide secrets",
                  ],
                  rule: "Never trust frontend with secrets",
                },
                {
                  title: "Best Practices for Frontend Auth",
                  description: "Production-safe guidelines.",
                  rules: [
                    "Use backend to protect secrets",
                    "Use short-lived tokens",
                    "Rotate keys",
                    "Use HTTPS always",
                  ],
                  takeaway: "Security is a backend responsibility",
                },
                {
                  title: "Common Auth Mistakes",
                  description: "These cause security breaches.",
                  mistakes: [
                    "Hardcoding API keys",
                    "Storing long-lived tokens in localStorage",
                    "Sending tokens in query params",
                  ],
                },
                {
                  title: "How This Maps to React Apps",
                  description: "Auth is central in React apps.",
                  example:
                    "fetch(url, { headers: { Authorization: `Bearer ${token}` } });",
                  takeaway: "Auth headers are set on every protected request",
                },
              ],

              visualStructure: `
Frontend App
     ↓
Authorization Header
     ↓
API Gateway
     ↓
Backend Validation
     ↓
Response / Error
    `,

              commonMistakes: [
                {
                  mistake: "Hardcoding API keys",
                  consequence: "Keys exposed publicly",
                  fix: "Use backend proxy",
                },
                {
                  mistake: "Trusting frontend for security",
                  consequence: "Account compromise",
                  fix: "Move secrets to backend",
                },
                {
                  mistake: "Using HTTP instead of HTTPS",
                  consequence: "Token leakage",
                  fix: "Always use HTTPS",
                },
              ],

              proTips: [
                "Frontend security is limited by design",
                "Auth headers are sent with every request",
                "Bearer tokens dominate modern APIs",
                "Understanding limits = senior mindset",
              ],

              realWorldAnalogy:
                "Frontend is like a glass door — everyone can see inside, so never store valuables there",
            },

            tasks: [
              "Call API with API key",
              "Set custom headers",
              "Understand security risks",
            ],

            examples: ["headers: { Authorization: 'Bearer TOKEN' }"],

            useCases: ["Weather APIs", "Payment APIs", "User-specific data"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand API authentication",
              "Use Authorization headers correctly",
              "Recognize frontend security limits",
              "Avoid common auth mistakes",
            ],
          },
          {
            day: 6,
            topic: "Advanced API Concepts",
            subTopics: [
              "Pagination",
              "Rate limiting",
              "CORS basics",
              "Debouncing API calls",
              "Environment variables",
            ],
            explanation:
              "Real-world APIs come with limits, pagination and security restrictions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Paginated Data Loader",
                description:
                  "Build a paginated API data loader with proper state handling.",
                setup: [
                  "API supports page or offset pagination",
                  "Initial load + load more action",
                ],
                requirements: [
                  "Track current page in state",
                  "Append or replace data correctly",
                  "Disable load-more when no data left",
                ],
                outcome:
                  "You’ll implement pagination exactly as done in production dashboards.",
              },
              {
                id: "pt-2",
                title: "Search Input with Debounced API Calls",
                description:
                  "Prevent excessive API calls during fast user input.",
                constraints: [
                  "Debounce input by 300–500ms",
                  "Cancel previous scheduled calls",
                  "Only latest query triggers API call",
                ],
                learningFocus: [
                  "Rate-limit safety",
                  "UX smoothness",
                  "API protection",
                ],
                outcome:
                  "You’ll build search-as-you-type without abusing APIs.",
              },
              {
                id: "pt-3",
                title: "Rate Limit Failure Handling",
                description: "Handle API rate-limit errors gracefully.",
                scenarios: [
                  "429 Too Many Requests response",
                  "Temporary API block",
                ],
                requirements: [
                  "Show user-friendly message",
                  "Delay retry or disable input temporarily",
                  "Avoid repeated failing requests",
                ],
                outcome:
                  "You’ll learn how production apps behave under API pressure.",
              },
              {
                id: "pt-4",
                title: "CORS & Env Variable Reality Drill",
                description: "Understand what frontend can and cannot control.",
                tasks: [
                  "Trigger a CORS error intentionally",
                  "Identify why frontend cannot fix it",
                  "Move API base URL to env variable",
                ],
                rules: [
                  "No attempt to bypass CORS in JS",
                  "Explain backend responsibility clearly",
                ],
                outcome:
                  "You’ll stop wasting time on impossible frontend fixes.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Advanced API bugs appear under scale, not during happy paths.",
              realWorldChecks: [
                "Are API calls limited during fast input?",
                "Does pagination stop correctly?",
                "What happens when rate limits hit?",
              ],
              debugTip:
                "Monitor Network tab to detect unnecessary or repeated API calls.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is pagination and why is it needed?",
                "How do you handle rate-limited APIs?",
                "What is CORS and who controls it?",
                "Are environment variables secure in frontend?",
              ],
              strongAnswerPattern:
                "Explain scale problem → API limits → frontend mitigation → backend responsibility",
              redFlag:
                "Claiming CORS or API security can be fixed purely in frontend.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Large data must be paginated",
                "APIs have limits — respect them",
                "CORS is browser-enforced",
                "Env variables are configuration, not secrets",
              ],
              refactorRule:
                "If an API call triggers too often, debounce, cache, or paginate it.",
            },
            // 📚 Detailed Learning Content (Production API Handling)
            content: {
              introduction:
                "In real applications, APIs are never infinite or unrestricted. They paginate data, limit request rates, enforce security rules (CORS), and require safe handling of secrets. Understanding these concepts separates tutorial-level developers from production-ready frontend engineers.",

              sections: [
                {
                  title: "Pagination – Why It Exists",
                  description: "APIs rarely return all data at once.",
                  reasons: ["Performance", "Reduced payload size", "Better UX"],
                  takeaway: "Large datasets must be paginated",
                },
                {
                  title: "Pagination Patterns",
                  description: "Common ways APIs paginate data.",
                  patterns: [
                    "Page-based (?page=1&limit=10)",
                    "Offset-based (?offset=0&limit=10)",
                    "Cursor-based (cursor=abc123)",
                  ],
                  example: "fetch('/users?page=1&limit=10')",
                  takeaway: "Always read API docs for pagination style",
                },
                {
                  title: "Frontend Pagination Handling",
                  description: "Frontend controls page state.",
                  steps: [
                    "Track current page",
                    "Fetch next page",
                    "Append or replace data",
                  ],
                  code: "setPage(prev => prev + 1);",
                  useCases: ["Tables", "Search results"],
                },
                {
                  title: "Infinite Scroll (Pagination UX)",
                  description: "Alternative to numbered pagination.",
                  behavior: ["Load data as user scrolls", "Append results"],
                  risk: "Too many API calls",
                  takeaway: "Requires throttling or debouncing",
                },
                {
                  title: "Rate Limiting – API Protection",
                  description: "APIs limit how many requests you can make.",
                  reasons: ["Prevent abuse", "Control server load"],
                  symptoms: ["429 Too Many Requests"],
                  takeaway: "Frontend must respect limits",
                },
                {
                  title: "Handling Rate Limits Gracefully",
                  description: "Apps must not spam APIs.",
                  strategies: [
                    "Debounce requests",
                    "Cache results",
                    "Delay retries",
                  ],
                  takeaway: "Aggressive requests = blocked app",
                },
                {
                  title: "CORS (Cross-Origin Resource Sharing)",
                  description: "Browser security mechanism.",
                  explanation: "Controls which origins can access an API",
                  importantTruth: "CORS is enforced by browser, not JavaScript",
                  takeaway: "Frontend cannot bypass CORS",
                },
                {
                  title: "Common CORS Errors",
                  description: "Very common during development.",
                  examples: [
                    "Blocked by CORS policy",
                    "No Access-Control-Allow-Origin header",
                  ],
                  fix: "Handled on backend or proxy",
                },
                {
                  title: "Debouncing API Calls",
                  description:
                    "Prevents excessive requests during rapid input.",
                  exampleUse: "Search input",
                  code: "setTimeout(() => fetchData(), 300);",
                  takeaway: "Debounce = performance + safety",
                },
                {
                  title: "Environment Variables",
                  description: "Used to store configuration values.",
                  examples: ["API base URLs", "Public keys"],
                  frontendRule: "Only non-secret values allowed",
                  takeaway: "Env vars ≠ secure storage",
                },
                {
                  title: "Environment Variables in Frontend",
                  description: "Handled by build tools.",
                  examples: [
                    "process.env.API_KEY",
                    "import.meta.env.VITE_API_KEY",
                  ],
                  warning: "Still visible in bundled code",
                },
                {
                  title: "Frontend Security Reality Check",
                  description: "Critical mindset for professionals.",
                  truths: [
                    "Frontend cannot hide secrets",
                    "Security lives on backend",
                    "Frontend reduces risk, not eliminates it",
                  ],
                },
                {
                  title: "How This Maps to React Apps",
                  description: "Advanced API handling is everywhere in React.",
                  examples: [
                    "Infinite scroll feeds",
                    "Search-as-you-type",
                    "Paginated dashboards",
                  ],
                  takeaway: "These patterns appear in real jobs",
                },
              ],

              visualStructure: `
User Action
    ↓
Debounce / Throttle
    ↓
Paginated API Call
    ↓
Rate Limit Check
    ↓
CORS Validation
    ↓
Response → UI Update
    `,

              commonMistakes: [
                {
                  mistake: "Fetching all data at once",
                  consequence: "Slow app, crashes",
                  fix: "Use pagination",
                },
                {
                  mistake: "Ignoring rate limits",
                  consequence: "API blocks requests",
                  fix: "Debounce and cache",
                },
                {
                  mistake: "Trying to fix CORS in frontend",
                  consequence: "Wasted debugging time",
                  fix: "Fix backend or proxy",
                },
              ],

              proTips: [
                "Pagination is not optional in real apps",
                "Debouncing saves APIs and UX",
                "CORS errors are NOT frontend bugs",
                "Environment variables are not secrets",
              ],

              realWorldAnalogy:
                "APIs are like elevators — limited capacity, rules, and security guards",
            },

            tasks: [
              "Implement pagination",
              "Prevent excessive API calls",
              "Store API keys safely",
            ],

            examples: ["?page=1&limit=10", "process.env.API_KEY"],

            useCases: ["Search results", "Infinite scroll", "Production apps"],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Handle paginated APIs",
              "Respect rate limits",
              "Understand CORS clearly",
              "Apply debouncing techniques",
              "Use environment variables correctly",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – API Powered Application",
            subTopics: [
              "Async/await architecture",
              "Search and filter",
              "Loading & error states",
              "Pagination or detailed view",
              "Clean UI updates",
            ],
            explanation:
              "Build a real-world application using async/await and public APIs.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "End-to-End API App Build",
                description:
                  "Build a complete API-driven application using async/await.",
                coreRequirements: [
                  "Search input",
                  "API fetch with async/await",
                  "Loading & error states",
                  "Paginated or detailed data view",
                ],
                constraints: [
                  "No promise chains (async/await only)",
                  "All fetch calls wrapped in try-catch",
                  "Clear UI before new fetch",
                ],
                outcome:
                  "You’ll build a real-world app suitable for portfolio and interviews.",
              },
              {
                id: "pt-2",
                title: "Failure & Edge Case Simulation",
                description:
                  "Test your app against real-world failure scenarios.",
                scenarios: [
                  "No results found",
                  "Network failure",
                  "Rate limit hit",
                  "Invalid user input",
                ],
                rules: [
                  "App must not crash",
                  "User must get clear feedback",
                  "Retry should be possible",
                ],
                outcome:
                  "You’ll learn how professional apps survive bad conditions.",
              },
              {
                id: "pt-3",
                title: "Search UX Optimization Pass",
                description: "Optimize search behavior for performance and UX.",
                requirements: [
                  "Debounced search input",
                  "Cancel or ignore stale requests",
                  "Prevent duplicate renders",
                ],
                learningFocus: [
                  "API efficiency",
                  "UI responsiveness",
                  "State correctness",
                ],
                outcome:
                  "Your app will feel fast and intentional, not jittery.",
              },
              {
                id: "pt-4",
                title: "Architecture & Code Quality Review",
                description:
                  "Refactor the project to improve structure and readability.",
                checklist: [
                  "Separate API logic from UI logic",
                  "Centralize state updates",
                  "Remove duplicated async logic",
                  "Add meaningful function names",
                ],
                outcome:
                  "Your code will read like a professional codebase, not a demo.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Capstone apps fail not because APIs break, but because state and UI go out of sync.",
              realWorldChecks: [
                "Does loader always stop?",
                "Is old data cleared before new fetch?",
                "Can the app recover after an error?",
              ],
              debugTip:
                "Log state transitions (idle → loading → success/error) instead of only API data.",
            },
            interviewInsights: {
              commonQuestions: [
                "How did you structure async logic in this project?",
                "How do you handle loading and error states?",
                "How do you prevent excessive API calls?",
                "What would you improve if this app scaled?",
              ],
              strongAnswerPattern:
                "Explain async flow → state handling → UX decisions → scalability trade-offs",
              redFlag:
                "Focusing only on API success path and ignoring failures or UX.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Async logic drives UI state",
                "Every fetch has loading, success, error",
                "User actions must be debounced or controlled",
                "Readable structure beats clever shortcuts",
              ],
              refactorRule:
                "If async logic feels tangled, extract it into a clear, reusable function.",
            },
            // 📚 Detailed Learning Content (Capstone Project)
            content: {
              introduction:
                "This project brings together everything you've learned about asynchronous JavaScript, Fetch API, error handling, pagination, and UI updates. The goal is to build a real, production-style app that fetches live data and handles real-world edge cases.",

              sections: [
                {
                  title: "Choosing the Right Public API",
                  description:
                    "Pick an API that demonstrates real-world patterns.",
                  goodChoices: [
                    "GitHub Users API",
                    "Movie Database API",
                    "Crypto price APIs",
                  ],
                  selectionCriteria: [
                    "Supports search",
                    "Returns paginated data",
                    "Stable public access",
                  ],
                  takeaway: "Choose APIs that show practical skill",
                },
                {
                  title: "Async/Await Architecture",
                  description: "Structure async logic cleanly.",
                  pattern: [
                    "fetchData()",
                    "setLoading(true)",
                    "try / catch",
                    "setData()",
                    "setError()",
                  ],
                  code: "async function fetchData() {\n  setLoading(true);\n  try {\n    const res = await fetch(url);\n    if (!res.ok) throw new Error('Failed');\n    const data = await res.json();\n    render(data);\n  } catch (err) {\n    showError(err.message);\n  } finally {\n    setLoading(false);\n  }\n}",
                  takeaway: "Clear async flow = readable code",
                },
                {
                  title: "Loading State Handling",
                  description: "Users must know when data is loading.",
                  techniques: [
                    "Spinners",
                    "Skeleton loaders",
                    "Disabled buttons",
                  ],
                  rule: "Never leave user guessing",
                },
                {
                  title: "Error Handling & Empty States",
                  description: "Errors are inevitable.",
                  cases: [
                    "Network failure",
                    "Invalid input",
                    "No results found",
                  ],
                  pattern: "Error UI ≠ console.log",
                  takeaway: "Graceful failure = professional UX",
                },
                {
                  title: "Search & Filter Logic",
                  description: "Allow users to explore data.",
                  bestPractices: [
                    "Debounce search input",
                    "Avoid API spamming",
                    "Show 'no results' state",
                  ],
                  example:
                    "input.addEventListener('input', debounce(searchFn, 300));",
                },
                {
                  title: "Pagination or Detailed View",
                  description: "Choose based on API design.",
                  options: [
                    "Pagination (Next / Prev)",
                    "Infinite scroll",
                    "Details page/modal",
                  ],
                  takeaway: "Pagination = scalability",
                },
                {
                  title: "Clean UI Updates",
                  description: "UI must reflect state changes accurately.",
                  rules: [
                    "Clear old data before loading",
                    "Avoid duplicate rendering",
                    "Keep DOM updates minimal",
                  ],
                },
                {
                  title: "Folder & Code Structure",
                  description: "Structure matters even in small apps.",
                  suggestion: ["api.js", "ui.js", "state.js", "main.js"],
                  takeaway: "Good structure scales easily",
                },
                {
                  title: "Performance Considerations",
                  description: "Avoid unnecessary re-renders.",
                  techniques: [
                    "Debounce",
                    "Cache responses",
                    "Batch DOM updates",
                  ],
                },
                {
                  title: "What Interviewers Look For",
                  description: "This project shows real-world readiness.",
                  signals: [
                    "Async handling",
                    "Error states",
                    "Clean architecture",
                    "UX thinking",
                  ],
                  takeaway: "This project can carry interviews",
                },
              ],

              visualStructure: `
User Input
   ↓
Debounced Search
   ↓
Async Fetch
   ↓
Loading State
   ↓
Success / Error
   ↓
UI Update
    `,

              commonMistakes: [
                {
                  mistake: "Ignoring loading states",
                  consequence: "Bad UX",
                  fix: "Always show loader",
                },
                {
                  mistake: "No error handling",
                  consequence: "Broken app",
                  fix: "Use try-catch",
                },
                {
                  mistake: "Messy UI updates",
                  consequence: "Duplicate content",
                  fix: "Clear container before render",
                },
              ],

              proTips: [
                "One solid API project > 10 small demos",
                "Focus on UX, not just data",
                "Readable code beats clever code",
                "This project fits perfectly in portfolio",
              ],

              realWorldAnalogy:
                "This app is like a live dashboard — always updating, always handling failures gracefully",
            },

            tasks: [
              "Choose a public API",
              "Fetch and display data",
              "Add search and filter",
              "Handle errors and loading",
            ],

            examples: [
              "GitHub Users API",
              "Movie Database API",
              "Crypto price API",
            ],

            useCases: [
              "Portfolio project",
              "Interview showcase",
              "Real production patterns",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "2–3 hours",

            // 🎯 Learning objectives
            objectives: [
              "Build complete API-driven app",
              "Apply async/await properly",
              "Handle real-world API constraints",
              "Create interview-ready project",
            ],
          },
        ],
      },
    ],
    questions: [
      {
        id: "j1",
        question: "What are the different data types in JavaScript?",
        difficulty: "Easy",
        topics: ["Data Types"],
      },
      {
        id: "j2",
        question: "Explain the difference between var, let, and const.",
        difficulty: "Easy",
        topics: ["Variable Declaration"],
      },
      {
        id: "j3",
        question: "What is hoisting in JavaScript?",
        difficulty: "Medium",
        topics: ["Hoisting"],
      },
      {
        id: "j4",
        question: "Explain the difference between == and === operators.",
        difficulty: "Easy",
        topics: ["Operators", "Type Coercion"],
      },
      {
        id: "j5",
        question: "What is the difference between null and undefined?",
        difficulty: "Easy",
        topics: ["Data Types"],
      },
      {
        id: "j6",
        question: "Explain how 'this' keyword works in JavaScript.",
        difficulty: "Hard",
        topics: ["this keyword", "Context"],
      },
      {
        id: "j7",
        question: "What are closures in JavaScript? Provide an example.",
        difficulty: "Hard",
        topics: ["Closures", "Scope"],
      },
      {
        id: "j8",
        question:
          "What is the difference between function declaration and function expression?",
        difficulty: "Medium",
        topics: ["Functions"],
      },
      {
        id: "j9",
        question:
          "Explain arrow functions and how they differ from regular functions.",
        difficulty: "Medium",
        topics: ["Arrow Functions", "this keyword"],
      },
      {
        id: "j10",
        question:
          "What are array methods like map, filter, and reduce? Explain with examples.",
        difficulty: "Medium",
        topics: ["Array Methods"],
      },
      {
        id: "j11",
        question: "What is the event loop in JavaScript?",
        difficulty: "Hard",
        topics: ["Event Loop", "Asynchronous JavaScript"],
      },
      {
        id: "j12",
        question: "Explain the concept of callback functions.",
        difficulty: "Medium",
        topics: ["Callbacks", "Asynchronous JavaScript"],
      },
      {
        id: "j13",
        question:
          "What is the DOM and how do you manipulate it using JavaScript?",
        difficulty: "Medium",
        topics: ["DOM Manipulation"],
      },
      {
        id: "j14",
        question: "Explain event bubbling and event capturing.",
        difficulty: "Medium",
        topics: ["Event Propagation"],
      },
      {
        id: "j15",
        question: "What is event delegation and why is it useful?",
        difficulty: "Medium",
        topics: ["Event Delegation"],
      },
      {
        id: "j16",
        question:
          "What is the difference between for, while, and do-while loops?",
        difficulty: "Easy",
        topics: ["Loops"],
      },
      {
        id: "j17",
        question:
          "Explain the difference between synchronous and asynchronous code.",
        difficulty: "Medium",
        topics: ["Asynchronous JavaScript"],
      },
      {
        id: "j18",
        question:
          "What is the difference between call, apply, and bind methods?",
        difficulty: "Hard",
        topics: ["Function Methods"],
      },
      {
        id: "j19",
        question:
          "Explain the concept of prototypal inheritance in JavaScript.",
        difficulty: "Hard",
        topics: ["Prototypes", "Inheritance"],
      },
      {
        id: "j20",
        question: "What are objects in JavaScript and how do you create them?",
        difficulty: "Easy",
        topics: ["Objects"],
      },
      {
        id: "j21",
        question:
          "Explain the difference between dot notation and bracket notation for accessing object properties.",
        difficulty: "Easy",
        topics: ["Objects"],
      },
      {
        id: "j22",
        question: "What is the spread operator with objects and arrays?",
        difficulty: "Easy",
        topics: ["Spread Operator"],
      },
      {
        id: "j23",
        question: "Explain what truthy and falsy values are in JavaScript.",
        difficulty: "Easy",
        topics: ["Type Coercion"],
      },
      {
        id: "j24",
        question: "What is the ternary operator and how do you use it?",
        difficulty: "Easy",
        topics: ["Operators"],
      },
      {
        id: "j25",
        question:
          "Explain the difference between forEach, map, filter, and reduce.",
        difficulty: "Medium",
        topics: ["Array Methods"],
      },
      {
        id: "j26",
        question: "What is setTimeout and setInterval? How do they work?",
        difficulty: "Easy",
        topics: ["Timers", "Asynchronous JavaScript"],
      },
      {
        id: "j27",
        question: "How do you handle errors in JavaScript? Explain try-catch.",
        difficulty: "Medium",
        topics: ["Error Handling"],
      },
      {
        id: "j28",
        question: "What is JSON and how do you parse and stringify it?",
        difficulty: "Easy",
        topics: ["JSON"],
      },
      {
        id: "j29",
        question:
          "Explain the difference between querySelector and getElementById.",
        difficulty: "Easy",
        topics: ["DOM Manipulation"],
      },
      {
        id: "j30",
        question:
          "What are template strings and how are they different from regular strings?",
        difficulty: "Easy",
        topics: ["Strings"],
      },
      {
        id: "j31",
        question:
          "Explain scope in JavaScript (global, function, block scope).",
        difficulty: "Medium",
        topics: ["Scope"],
      },
      {
        id: "j32",
        question: "What is the difference between shallow copy and deep copy?",
        difficulty: "Medium",
        topics: ["Objects", "Arrays"],
      },
      {
        id: "j33",
        question: "How do you prevent default behavior in events?",
        difficulty: "Easy",
        topics: ["Events"],
      },
      {
        id: "j34",
        question: "What is strict mode in JavaScript?",
        difficulty: "Medium",
        topics: ["Strict Mode"],
      },
      {
        id: "j35",
        question:
          "Explain the difference between slice and splice array methods.",
        difficulty: "Easy",
        topics: ["Array Methods"],
      },
    ],
  },
  {
    phase: 3,
    phaseKey: "modernJs",
    title: "Modern JavaScript & Tools",
    duration: "4 Weeks",
    level: "Intermediate",
    weeks: [
      {
        week: 15,
        weekGoal:
          "Master modern JavaScript (ES6+) features and write clean, modular, scalable code",
        weekOutcome:
          "Ability to write professional, modular JavaScript using ES6+ syntax used in real-world frontend projects",
        miniProject: "Modular Web Application",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Destructuring in JavaScript",
            subTopics: [
              "Array destructuring",
              "Object destructuring",
              "Default values",
              "Nested destructuring",
              "Renaming variables during destructuring",
            ],
            explanation:
              "Destructuring allows you to extract values from arrays or properties from objects into variables in a clean and readable way.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Legacy Code → Destructuring Refactor",
                description:
                  "Refactor verbose object and array access into clean destructuring.",
                setup: [
                  "Given code uses repetitive dot notation",
                  "Multiple variables extracted manually",
                ],
                requirements: [
                  "Use object and array destructuring",
                  "Apply default values where needed",
                  "Do not reduce readability",
                ],
                outcome:
                  "You’ll learn how destructuring improves clarity in real codebases.",
              },
              {
                id: "pt-2",
                title: "API Response Safety Drill",
                description:
                  "Safely destructure data coming from an API response.",
                scenarios: [
                  "Missing fields",
                  "Undefined nested objects",
                  "Optional properties",
                ],
                rules: [
                  "Use default values",
                  "Avoid destructuring undefined",
                  "Prevent runtime crashes",
                ],
                outcome:
                  "You’ll use destructuring without breaking apps when data is incomplete.",
              },
              {
                id: "pt-3",
                title: "Nested Destructuring Readability Challenge",
                description:
                  "Balance power and readability when destructuring nested objects.",
                constraints: [
                  "Extract nested values",
                  "Avoid one-line unreadable destructuring",
                ],
                comparisonPoints: [
                  "Single-line vs step-by-step destructuring",
                  "Maintainability",
                  "Debuggability",
                ],
                outcome: "You’ll know when NOT to use deep destructuring.",
              },
              {
                id: "pt-4",
                title: "Function Parameter Destructuring Practice",
                description:
                  "Refactor function signatures using destructured parameters.",
                requirements: [
                  "Destructure only required properties",
                  "Provide default values",
                  "Keep function signature readable",
                ],
                outcome:
                  "You’ll write modern function APIs like professional JS & React devs.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Destructuring bugs usually appear at runtime, not compile time.",
              realWorldChecks: [
                "Is the object ever undefined?",
                "Are all destructured properties guaranteed?",
                "Are defaults provided where needed?",
              ],
              debugTip:
                "Log the full object before destructuring when debugging undefined errors.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is destructuring in JavaScript?",
                "Difference between array and object destructuring?",
                "How do default values work in destructuring?",
                "When should you avoid nested destructuring?",
              ],
              strongAnswerPattern:
                "Explain purpose → show syntax → mention safety & readability trade-offs",
              redFlag:
                "Overusing deeply nested destructuring without defaults.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Destructure only what you need",
                "Defaults protect against missing data",
                "Arrays are position-based, objects are key-based",
                "Readability beats clever one-liners",
              ],
              refactorRule:
                "If destructuring hurts readability, break it into smaller steps.",
            },
            // 📚 Detailed Learning Content (Modern JS Core)
            content: {
              introduction:
                "Destructuring is one of the most important ES6 features. It allows developers to unpack values from arrays and objects into variables in a single, readable line. In modern frontend development — especially React — destructuring is not optional, it’s standard.",

              sections: [
                {
                  title: "Why Destructuring Exists",
                  description:
                    "Before destructuring, extracting values was repetitive and verbose.",
                  before: "const name = user.name;\nconst age = user.age;",
                  after: "const { name, age } = user;",
                  takeaway: "Less code, more clarity",
                },
                {
                  title: "Array Destructuring",
                  description: "Extract values from arrays based on position.",
                  keyPoints: ["Order matters", "Variable names are flexible"],
                  code: "const arr = [10, 20, 30];\nconst [a, b] = arr;",
                  example: "a → 10, b → 20",
                  takeaway: "Used heavily with hooks in React",
                },
                {
                  title: "Skipping Values in Arrays",
                  description: "You can ignore unwanted values.",
                  code: "const [first, , third] = [1, 2, 3];",
                  takeaway: "Useful when only partial data is needed",
                },
                {
                  title: "Object Destructuring",
                  description: "Extract properties from objects using keys.",
                  keyPoints: ["Order does NOT matter", "Key names must match"],
                  code: "const user = { name: 'Alex', age: 25 };\nconst { name, age } = user;",
                  takeaway: "Most common destructuring pattern",
                },
                {
                  title: "Default Values",
                  description: "Prevent undefined values.",
                  code: "const { role = 'user' } = user;",
                  behavior: "Uses default only if value is undefined",
                  takeaway: "Protects against missing API fields",
                },
                {
                  title: "Renaming Variables During Destructuring",
                  description:
                    "Rename extracted values for clarity or conflict avoidance.",
                  code: "const { name: userName, age: userAge } = user;",
                  takeaway: "Very common in large codebases",
                },
                {
                  title: "Nested Destructuring",
                  description: "Extract deeply nested data in one step.",
                  exampleObject: "{ user: { profile: { name: 'Sam' } } }",
                  code: "const { user: { profile: { name } } } = data;",
                  warning: "Can reduce readability if overused",
                  takeaway: "Balance power with clarity",
                },
                {
                  title: "Destructuring Function Parameters",
                  description: "Extremely common in modern JavaScript.",
                  code: "function greet({ name }) { console.log(name); }",
                  takeaway: "Used everywhere in React props",
                },
                {
                  title: "Destructuring with API Responses",
                  description: "Extract only required data.",
                  code: "const { data, error } = response;",
                  takeaway: "Cleaner data handling",
                },
                {
                  title: "Common Destructuring Mistakes",
                  description: "These cause runtime bugs.",
                  mistakes: [
                    "Destructuring undefined",
                    "Overusing nested destructuring",
                    "Forgetting default values",
                  ],
                },
                {
                  title: "How This Maps to React",
                  description: "Destructuring is everywhere in React.",
                  examples: [
                    "const { title } = props;",
                    "const [state, setState] = useState()",
                  ],
                  takeaway: "If you know destructuring, React feels natural",
                },
              ],

              visualStructure: `
Object / Array
     ↓
Destructuring Syntax
     ↓
Clean Variables
     ↓
Readable Code
    `,

              commonMistakes: [
                {
                  mistake: "Destructuring null or undefined",
                  consequence: "App crashes",
                  fix: "Provide defaults or checks",
                },
                {
                  mistake: "Too deep nested destructuring",
                  consequence: "Unreadable code",
                  fix: "Break into steps",
                },
                {
                  mistake: "Assuming properties exist",
                  consequence: "Undefined values",
                  fix: "Use default values",
                },
              ],

              proTips: [
                "Destructure only what you need",
                "Use defaults for API data",
                "Readable code > clever code",
                "Master this before React",
              ],

              realWorldAnalogy:
                "Destructuring is like unpacking a suitcase and taking only what you need",
            },

            tasks: [
              "Destructure arrays and objects",
              "Use default values",
              "Refactor old code using destructuring",
            ],

            examples: [
              "const [a, b] = [1, 2];",
              "const { name, age = 18 } = user;",
            ],

            useCases: [
              "Handling API responses",
              "Working with props in React",
              "Cleaner variable assignments",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "35–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand array and object destructuring",
              "Use defaults and renaming safely",
              "Handle nested data cleanly",
              "Prepare for React props and hooks",
            ],
          },
          {
            day: 2,
            topic: "Spread & Rest Operators",
            subTopics: [
              "Spread operator with arrays",
              "Spread operator with objects",
              "Rest parameters in functions",
              "Cloning arrays and objects",
              "Merging data structures",
            ],
            explanation:
              "Spread and rest operators help in copying, merging, and handling variable numbers of arguments efficiently.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Mutation → Immutable Refactor Challenge",
                description:
                  "Refactor mutating code into immutable updates using spread.",
                setup: [
                  "Given code uses push(), pop(), direct assignment",
                  "State-like objects and arrays",
                ],
                requirements: [
                  "Replace all mutations with spread-based updates",
                  "Ensure original data remains unchanged",
                  "Create new references every time",
                ],
                outcome:
                  "You’ll understand why immutability is critical in React and modern JS.",
              },
              {
                id: "pt-2",
                title: "Shallow Copy Trap Demonstration",
                description: "Prove that spread creates only shallow copies.",
                scenarios: [
                  "Object with nested object",
                  "Array with nested object",
                ],
                tasks: [
                  "Modify nested value in copied object",
                  "Observe mutation in original reference",
                ],
                outcome:
                  "You’ll stop assuming spread is a deep clone — a common senior-level pitfall.",
              },
              {
                id: "pt-3",
                title: "Rest-Powered Utility Functions",
                description:
                  "Build flexible utility functions using rest parameters.",
                requirements: [
                  "Use rest to accept variable arguments",
                  "Avoid using arguments object",
                  "Write clean, readable APIs",
                ],
                examples: ["sum(...numbers)", "mergeObjects(...configs)"],
                outcome: "You’ll design function APIs that scale cleanly.",
              },
              {
                id: "pt-4",
                title: "React-Style State Update Simulator",
                description:
                  "Simulate React-style state updates using spread & rest.",
                constraints: [
                  "Never mutate previous state",
                  "Always return new object/array",
                  "Handle nested updates carefully",
                ],
                outcome:
                  "You’ll write state update logic exactly like production React apps.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Mutation bugs are silent — the app doesn’t crash, it just behaves wrong.",
              realWorldChecks: [
                "Did this update create a new reference?",
                "Is any nested data accidentally mutated?",
                "Would React detect this change?",
              ],
              debugTip:
                "Use Object.is or === checks to verify new references after updates.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the spread operator?",
                "Difference between spread and rest?",
                "Is spread a deep copy?",
                "Why is immutability important in React?",
              ],
              strongAnswerPattern:
                "Explain syntax → immutability → shallow copy caveat → real-world React usage",
              redFlag:
                "Saying spread creates a deep clone or mutating state directly.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Spread creates new reference",
                "Rest collects remaining values",
                "Spread is shallow, not deep",
                "Never mutate state directly",
              ],
              refactorRule:
                "If code mutates arrays or objects directly, refactor using spread immediately.",
            },
            // 📚 Detailed Learning Content (Immutability Core)
            content: {
              introduction:
                "The spread (...) and rest (...) operators look the same but serve different purposes depending on where they are used. These operators are fundamental to writing immutable, predictable code — especially in React, where direct mutation breaks state updates.",

              sections: [
                {
                  title: "Why Spread & Rest Exist",
                  description:
                    "Before ES6, copying and merging data was verbose and error-prone.",
                  before: "const copy = arr.slice();",
                  after: "const copy = [...arr];",
                  takeaway: "Cleaner syntax, safer code",
                },
                {
                  title: "Spread Operator with Arrays",
                  description:
                    "Spread expands array elements into a new array.",
                  keyPoints: ["Creates shallow copy", "Prevents mutation"],
                  code: "const nums = [1, 2, 3];\nconst copy = [...nums];",
                  example: "copy !== nums (new reference)",
                  takeaway: "Essential for React state updates",
                },
                {
                  title: "Adding Elements Using Spread",
                  description:
                    "Append or prepend without mutating original array.",
                  code: "const newArr = [...oldArr, 4];",
                  alternative: "[0, ...oldArr]",
                  takeaway: "Avoid push() in state updates",
                },
                {
                  title: "Spread Operator with Objects",
                  description: "Creates a shallow copy of objects.",
                  code: "const userCopy = { ...user };",
                  example: "{ ...user, age: 30 }",
                  takeaway: "Most used pattern in React reducers",
                },
                {
                  title: "Merging Objects",
                  description: "Combine multiple objects easily.",
                  code: "const merged = { ...obj1, ...obj2 };",
                  rule: "Later properties override earlier ones",
                  takeaway: "Useful for config & state updates",
                },
                {
                  title: "Shallow Copy Warning ⚠️",
                  description: "Spread does NOT deep clone.",
                  example: "Nested objects still share reference",
                  code: "const copy = { ...user }; // user.profile still same reference",
                  takeaway: "Important to understand for complex state",
                },
                {
                  title: "Rest Parameters in Functions",
                  description: "Collects multiple arguments into an array.",
                  code: "function sum(...nums) { return nums.reduce((a,b)=>a+b); }",
                  behavior: "nums is a real array",
                  takeaway: "Replaces arguments object",
                },
                {
                  title: "Rest vs Spread (Mental Model)",
                  description: "Same syntax, opposite behavior.",
                  comparison: ["Spread → expands", "Rest → collects"],
                  takeaway: "Context decides behavior",
                },
                {
                  title: "Rest in Destructuring",
                  description: "Capture remaining properties.",
                  code: "const { id, ...rest } = user;",
                  useCase: "Removing properties immutably",
                  takeaway: "Very common in reducers",
                },
                {
                  title: "Common Spread & Rest Mistakes",
                  description: "These break immutability.",
                  mistakes: [
                    "Using push instead of spread",
                    "Assuming deep copy",
                    "Mutating nested data",
                  ],
                },
                {
                  title: "How This Maps to React",
                  description: "React relies on immutability.",
                  examples: [
                    "setState(prev => ({ ...prev, count: prev.count + 1 }))",
                    "setItems(prev => [...prev, newItem])",
                  ],
                  takeaway: "If you misuse spread, React bugs appear",
                },
              ],

              visualStructure: `
Original Data
     ↓
Spread / Rest
     ↓
New Reference
     ↓
Predictable Updates
    `,

              commonMistakes: [
                {
                  mistake: "Mutating arrays directly",
                  consequence: "React doesn't re-render",
                  fix: "Use spread operator",
                },
                {
                  mistake: "Assuming deep clone",
                  consequence: "Unexpected mutations",
                  fix: "Manually copy nested data",
                },
                {
                  mistake: "Confusing rest with spread",
                  consequence: "Logic errors",
                  fix: "Remember: spread expands, rest collects",
                },
              ],

              proTips: [
                "Never mutate state directly",
                "Spread is shallow — remember this",
                "Use rest to simplify function APIs",
                "This topic is heavily tested in interviews",
              ],

              realWorldAnalogy:
                "Spread is like unpacking items on a table, rest is collecting leftovers into a box",
            },

            tasks: [
              "Clone arrays and objects",
              "Merge multiple objects",
              "Create functions using rest parameters",
            ],

            examples: [
              "const newArr = [...oldArr];",
              "function sum(...nums) { return nums.reduce((a,b)=>a+b); }",
            ],

            useCases: [
              "Immutable state updates",
              "React state management",
              "Utility functions",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand spread vs rest",
              "Write immutable update logic",
              "Clone and merge data safely",
              "Prepare for React state patterns",
            ],
          },
          {
            day: 3,
            topic: "Arrow Functions & This Keyword",
            subTopics: [
              "Arrow function syntax",
              "Implicit return",
              "Arrow functions vs regular functions",
              "Lexical this",
              "Common pitfalls",
            ],
            explanation:
              "Arrow functions provide a shorter syntax and handle `this` differently, making them ideal for callbacks and modern JavaScript code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Regular vs Arrow Function Conversion Drill",
                description:
                  "Convert existing regular functions into arrow functions where appropriate.",
                setup: [
                  "Standalone functions",
                  "Callback functions",
                  "Array method callbacks",
                ],
                requirements: [
                  "Use arrow functions only where behavior remains correct",
                  "Preserve return values",
                  "Avoid unnecessary arrows",
                ],
                outcome:
                  "You’ll learn where arrow functions are safe — and where they are not.",
              },
              {
                id: "pt-2",
                title: "`this` Context Debugging Challenge",
                description:
                  "Debug broken code caused by incorrect `this` usage.",
                scenarios: [
                  "Arrow used as object method",
                  "Regular function losing `this` in callback",
                  "Arrow function inside setTimeout",
                ],
                tasks: [
                  "Identify why `this` is wrong",
                  "Fix using correct function type",
                ],
                outcome:
                  "You’ll stop guessing how `this` works and start reasoning correctly.",
              },
              {
                id: "pt-3",
                title: "Implicit Return Mastery",
                description:
                  "Practice implicit vs explicit returns in arrow functions.",
                constraints: [
                  "Use implicit return where readable",
                  "Return objects correctly",
                  "Avoid accidental undefined returns",
                ],
                comparisonPoints: [
                  "With braces vs without braces",
                  "Readability in teams",
                ],
                outcome:
                  "You’ll write concise arrow functions without hidden bugs.",
              },
              {
                id: "pt-4",
                title: "Callback & Event Handler Patterns",
                description:
                  "Use arrow functions correctly in callbacks and event handlers.",
                requirements: [
                  "Use arrow functions in array methods",
                  "Use event object instead of `this`",
                  "Avoid arrow functions as object methods",
                ],
                outcome:
                  "You’ll use arrow functions exactly like modern React & JS codebases.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "`this` bugs don’t throw errors — they silently break logic.",
              realWorldChecks: [
                "Is this function used as a method or callback?",
                "Does it rely on `this`?",
                "Would arrow change execution context?",
              ],
              debugTip:
                "Log `this` explicitly when debugging arrow vs regular function issues.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is an arrow function?",
                "Difference between arrow and regular functions?",
                "How does `this` behave in arrow functions?",
                "When should you NOT use arrow functions?",
              ],
              strongAnswerPattern:
                "Explain syntax → lexical `this` → real-world use cases → limitations",
              redFlag:
                "Saying arrow functions should replace all regular functions.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Arrow functions have lexical `this`",
                "Regular functions have dynamic `this`",
                "No `arguments` in arrow functions",
                "Callbacks = arrow, methods = regular (usually)",
              ],
              refactorRule:
                "If a function relies on `this`, verify arrow usage before refactoring.",
            },
            // 📚 Detailed Learning Content (Modern Functions)
            content: {
              introduction:
                "Arrow functions were introduced in ES6 to make functions shorter, cleaner, and safer—especially when dealing with callbacks and `this`. In modern frontend development (React, async code, array methods), arrow functions are the default choice.",

              sections: [
                {
                  title: "Basic Arrow Function Syntax",
                  description:
                    "Arrow functions are a concise way to write functions.",
                  before: "function add(a, b) { return a + b; }",
                  after: "const add = (a, b) => a + b;",
                  takeaway: "Less boilerplate, same logic",
                },
                {
                  title: "Implicit Return",
                  description: "Arrow functions can return values implicitly.",
                  rules: [
                    "No curly braces → implicit return",
                    "Curly braces → must use return",
                  ],
                  code: "const square = n => n * n;",
                  example: "const getUser = () => ({ name: 'Alex' });",
                  takeaway: "Very common in array methods",
                },
                {
                  title: "Single Parameter Shortcut",
                  description: "Parentheses are optional for one parameter.",
                  code: "item => item.price",
                  warning: "Use parentheses for clarity in teams",
                  takeaway: "Readable code matters",
                },
                {
                  title: "Arrow Functions vs Regular Functions",
                  description: "They look similar but behave differently.",
                  differences: [
                    "No own `this`",
                    "No arguments object",
                    "Cannot be used as constructors",
                  ],
                  takeaway:
                    "Arrow functions are not replacements for everything",
                },
                {
                  title: "Understanding `this` in JavaScript",
                  description: "`this` depends on how a function is called.",
                  examples: [
                    "Object method",
                    "Event handler",
                    "Standalone function",
                  ],
                  takeaway: "`this` is dynamic in regular functions",
                },
                {
                  title: "Lexical `this` (MOST IMPORTANT)",
                  description: "Arrow functions do NOT have their own `this`.",
                  behavior: "`this` is taken from surrounding scope",
                  code: "const obj = { count: 1, inc() { setTimeout(() => { this.count++; }, 1000); } };",
                  takeaway: "Arrow functions fix `this` confusion",
                },
                {
                  title: "Why Arrow Functions Are Perfect for Callbacks",
                  description: "Callbacks often lose `this` context.",
                  example: "array.map(item => item.price)",
                  takeaway: "Arrow functions prevent context bugs",
                },
                {
                  title: "Arrow Functions in Event Handlers",
                  description: "Commonly used in modern JS & React.",
                  example:
                    "button.addEventListener('click', () => console.log('clicked'));",
                  warning: "`this` inside arrow ≠ event target",
                  takeaway: "Use event object instead",
                },
                {
                  title: "Common Pitfalls",
                  description: "Arrow functions can cause subtle bugs.",
                  pitfalls: [
                    "Using arrow as object method",
                    "Expecting `this` to refer to element",
                    "Forgetting return with {}",
                  ],
                },
                {
                  title: "When NOT to Use Arrow Functions",
                  description: "Some cases require regular functions.",
                  cases: [
                    "Object methods needing `this`",
                    "Constructors",
                    "Prototype methods",
                  ],
                  takeaway: "Choose function type intentionally",
                },
                {
                  title: "How This Maps to React",
                  description: "React uses arrow functions everywhere.",
                  examples: [
                    "onClick={() => handleClick()}",
                    "items.map(item => <Card />)",
                  ],
                  takeaway: "Arrow functions feel natural in React",
                },
              ],

              visualStructure: `
Regular Function
     ↓
Dynamic this
     ↓
Confusion

Arrow Function
     ↓
Lexical this
     ↓
Predictable behavior
    `,

              commonMistakes: [
                {
                  mistake: "Using arrow function as object method",
                  consequence: "`this` becomes undefined",
                  fix: "Use regular function",
                },
                {
                  mistake: "Forgetting return in arrow with {}",
                  consequence: "Returns undefined",
                  fix: "Use implicit return or add return keyword",
                },
                {
                  mistake: "Expecting `this` in arrow event handler",
                  consequence: "Broken logic",
                  fix: "Use event.target",
                },
              ],

              proTips: [
                "Use arrow functions for callbacks",
                "Avoid arrows for object methods needing `this`",
                "Implicit return improves readability",
                "Understanding `this` separates juniors from seniors",
              ],

              realWorldAnalogy:
                "Arrow functions borrow identity from their surroundings, regular functions create their own",
            },

            tasks: [
              "Rewrite functions using arrow syntax",
              "Compare this behavior",
              "Use arrow functions in callbacks",
            ],

            examples: [
              "const add = (a, b) => a + b;",
              "items.map(item => item.price)",
            ],

            useCases: [
              "Event handlers",
              "Array methods",
              "Cleaner functional code",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand arrow function syntax",
              "Master lexical this",
              "Avoid common arrow function pitfalls",
              "Write clean modern JavaScript",
            ],
          },
          {
            day: 4,
            topic: "Classes & Object-Oriented JavaScript",
            subTopics: [
              "Class syntax",
              "Constructor method",
              "Instance methods",
              "Inheritance with extends",
              "super keyword",
            ],
            explanation:
              "Classes provide a cleaner syntax for creating objects and implementing object-oriented programming patterns in JavaScript.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Function Constructor → Class Refactor",
                description:
                  "Refactor old prototype-based code into modern class syntax.",
                setup: [
                  "Function constructor with prototype methods",
                  "Manual property assignments",
                ],
                requirements: [
                  "Convert to ES6 class",
                  "Move methods inside class body",
                  "Preserve behavior exactly",
                ],
                outcome:
                  "You’ll understand that classes improve readability, not functionality.",
              },
              {
                id: "pt-2",
                title: "Inheritance & super() Mastery Drill",
                description: "Build a parent-child class hierarchy correctly.",
                constraints: [
                  "Parent class with shared logic",
                  "Child class extending functionality",
                ],
                requirements: [
                  "Call super() correctly",
                  "Override at least one method",
                  "Reuse parent behavior safely",
                ],
                outcome:
                  "You’ll implement inheritance without breaking initialization logic.",
              },
              {
                id: "pt-3",
                title: "`this` Loss Debugging in Classes",
                description: "Debug common `this` issues in class methods.",
                scenarios: [
                  "Method passed as callback",
                  "Event handler using class method",
                  "Async callback inside class",
                ],
                tasks: [
                  "Identify why `this` is lost",
                  "Fix using bind or arrow function",
                ],
                outcome:
                  "You’ll stop getting undefined `this` bugs in class-based code.",
              },
              {
                id: "pt-4",
                title: "Composition vs Inheritance Decision Exercise",
                description: "Decide when inheritance is a bad idea.",
                setup: ["Given problem with multiple shared behaviors"],
                analysisPoints: [
                  "Overuse of extends",
                  "Tight coupling risks",
                  "Alternative composition approach",
                ],
                outcome:
                  "You’ll think like a senior engineer when designing class hierarchies.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "OOP bugs usually appear when code scales, not when it’s first written.",
              realWorldChecks: [
                "Is super() always called correctly?",
                "Does this method rely on instance context?",
                "Would composition be simpler here?",
              ],
              debugTip:
                "Log `this` inside class methods when behavior feels unpredictable.",
            },
            interviewInsights: {
              commonQuestions: [
                "What are classes in JavaScript?",
                "How does inheritance work with extends?",
                "What is the super keyword?",
                "Are classes truly object-oriented in JavaScript?",
                "When should you avoid inheritance?",
              ],
              strongAnswerPattern:
                "Explain syntax → prototype reality → inheritance rules → modern usage trade-offs",
              redFlag:
                "Claiming classes add new capabilities beyond prototypes.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Class is a blueprint, instance is object",
                "Methods live on prototype",
                "super() before this in child constructors",
                "Prefer composition over deep inheritance",
              ],
              refactorRule:
                "If inheritance tree becomes deep, refactor toward composition.",
            },
            // 📚 Detailed Learning Content (OOP in Modern JavaScript)
            content: {
              introduction:
                "JavaScript is prototype-based, but ES6 classes provide a clean, familiar syntax for object-oriented programming. While modern React prefers functions and hooks, classes are still widely used in JavaScript, backend Node.js, libraries, and legacy React codebases.",

              sections: [
                {
                  title: "Why Classes Were Introduced",
                  description:
                    "Before ES6, object creation was verbose and confusing.",
                  before:
                    "function User(name) { this.name = name; }\nUser.prototype.sayHi = function() {};",
                  after:
                    "class User { constructor(name) { this.name = name; } }",
                  takeaway: "Classes improve readability, not functionality",
                },
                {
                  title: "Basic Class Syntax",
                  description: "A class is a blueprint for creating objects.",
                  code: "class User {\n  constructor(name) {\n    this.name = name;\n  }\n}",
                  explanation:
                    "constructor runs automatically when new instance is created",
                  takeaway: "Class ≠ object, instance is object",
                },
                {
                  title: "Constructor Method",
                  description: "Special method used for initialization.",
                  rules: [
                    "Runs once per instance",
                    "Used to assign properties",
                  ],
                  code: "const user = new User('Alex');",
                  takeaway: "Constructor sets initial state",
                },
                {
                  title: "Instance Methods",
                  description:
                    "Methods defined inside class are shared across instances.",
                  code: "class User {\n  greet() { return `Hi ${this.name}`; }\n}",
                  behavior: "Methods live on prototype",
                  takeaway: "Memory efficient design",
                },
                {
                  title: "Understanding `this` in Classes",
                  description: "`this` refers to the current instance.",
                  example: "user.greet() → this === user",
                  warning:
                    "`this` can be lost when methods are passed as callbacks",
                  takeaway: "Bind or use arrow functions carefully",
                },
                {
                  title: "Inheritance with extends",
                  description: "Create a child class from a parent class.",
                  code: "class Admin extends User {}",
                  behavior: "Admin inherits User properties & methods",
                  takeaway: "Promotes code reuse",
                },
                {
                  title: "super Keyword",
                  description:
                    "Used to call parent class constructor or methods.",
                  code: "class Admin extends User {\n  constructor(name, role) {\n    super(name);\n    this.role = role;\n  }\n}",
                  rule: "super() must be called before using this",
                  takeaway: "Mandatory in child constructors",
                },
                {
                  title: "Method Overriding",
                  description: "Child class can override parent methods.",
                  code: "greet() { return 'Admin Access'; }",
                  takeaway: "Polymorphism in action",
                },
                {
                  title: "Classes Are Just Syntax Sugar",
                  description: "Under the hood, JS still uses prototypes.",
                  truth: "class === function + prototype",
                  takeaway: "Understanding prototypes helps debugging",
                },
                {
                  title: "Common Class Mistakes",
                  description: "Very common interview & production bugs.",
                  mistakes: [
                    "Forgetting new keyword",
                    "Not calling super()",
                    "Using arrow methods incorrectly",
                  ],
                },
                {
                  title: "When to Use Classes Today",
                  description: "Classes are not dead.",
                  useCases: [
                    "Domain models",
                    "Libraries",
                    "Node.js backends",
                    "Legacy React class components",
                  ],
                  takeaway: "Know classes, prefer functions in React",
                },
                {
                  title: "How This Maps to React",
                  description: "React historically used class components.",
                  example: "class MyComponent extends React.Component {}",
                  modernReality: "Hooks replaced most class usage",
                  takeaway: "Understand classes, but master hooks",
                },
              ],

              visualStructure: `
Class (Blueprint)
      ↓
new Keyword
      ↓
Instance (Object)
      ↓
Methods & Properties
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting super()",
                  consequence: "ReferenceError",
                  fix: "Call super before using this",
                },
                {
                  mistake: "Losing this in callbacks",
                  consequence: "Undefined behavior",
                  fix: "Bind methods or use arrow functions",
                },
                {
                  mistake: "Overusing inheritance",
                  consequence: "Tight coupling",
                  fix: "Prefer composition",
                },
              ],

              proTips: [
                "Classes improve readability, not power",
                "Prefer composition over inheritance",
                "Understand prototypes conceptually",
                "React favors functions, not classes",
              ],

              realWorldAnalogy:
                "A class is like a blueprint, an instance is the actual building",
            },

            tasks: [
              "Create classes with constructors",
              "Implement inheritance",
              "Build reusable class-based logic",
            ],

            examples: [
              "class User { constructor(name){ this.name = name } }",
              "class Admin extends User { }",
            ],

            useCases: [
              "Data modeling",
              "Complex business logic",
              "Reusable application structures",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "45–55 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand class-based OOP",
              "Use constructors and methods",
              "Implement inheritance correctly",
              "Know when to use classes vs functions",
            ],
          },
          {
            day: 5,
            topic: "Advanced Class Features",
            subTopics: [
              "Static methods",
              "Getters and setters",
              "Private properties (concept)",
              "Encapsulation principles",
              "Class best practices",
            ],
            explanation:
              "Advanced class features help control access to data and create more maintainable, predictable code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Static Method Design Challenge",
                description:
                  "Identify and refactor logic that should live as static methods.",
                setup: ["Class with mixed instance and utility logic"],
                requirements: [
                  "Move non-instance logic to static methods",
                  "Call static methods via class name",
                  "Avoid using this inside static methods",
                ],
                outcome:
                  "You’ll clearly understand when logic belongs to the class vs the instance.",
              },
              {
                id: "pt-2",
                title: "Getter & Setter Validation System",
                description:
                  "Protect internal state using getters and setters.",
                constraints: [
                  "Internal value must not be directly mutated",
                  "Setter must validate input",
                  "Getter must expose read-only access",
                ],
                scenarios: [
                  "Invalid value assignment",
                  "Read-only derived property",
                ],
                outcome:
                  "You’ll enforce business rules automatically through class APIs.",
              },
              {
                id: "pt-3",
                title: "Encapsulation Refactor Exercise",
                description: "Refactor a leaky class with exposed internals.",
                problemsGiven: [
                  "Public properties mutated freely",
                  "No control over state changes",
                ],
                requirements: [
                  "Hide internal properties",
                  "Expose minimal public API",
                  "Prevent illegal state changes",
                ],
                outcome:
                  "You’ll experience how encapsulation reduces bugs and misuse.",
              },
              {
                id: "pt-4",
                title: "Overengineering Detection Drill",
                description: "Decide when NOT to use advanced class features.",
                analysisTasks: [
                  "Identify unnecessary getters/setters",
                  "Spot misuse of static methods",
                  "Recommend simpler alternatives",
                ],
                outcome:
                  "You’ll develop the senior instinct to keep classes simple.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most class-related bugs come from uncontrolled state mutation.",
              realWorldChecks: [
                "Can this property be changed directly?",
                "Is validation guaranteed?",
                "Does this logic depend on instance state?",
              ],
              debugTip:
                "Log when setters are triggered to track illegal state changes.",
            },
            interviewInsights: {
              commonQuestions: [
                "What are static methods and when do you use them?",
                "Difference between getters/setters and direct properties?",
                "What is encapsulation in OOP?",
                "How do private fields work in JavaScript?",
              ],
              strongAnswerPattern:
                "Explain concept → show controlled access → discuss real-world safety benefits",
              redFlag: "Using getters/setters everywhere without clear reason.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Static = no instance state",
                "Getters read, setters protect",
                "Encapsulation hides internals",
                "Simple classes scale better",
              ],
              refactorRule:
                "If a class exposes too much state, hide it behind controlled methods.",
            },
            // 📚 Detailed Learning Content (Professional OOP Patterns)
            content: {
              introduction:
                "Once you understand basic classes, the next step is controlling how data is accessed and modified. Advanced class features like static methods, getters/setters, and encapsulation help build safer, cleaner, and more predictable systems—commonly used in libraries, frameworks, and backend logic.",

              sections: [
                {
                  title: "Static Methods",
                  description:
                    "Static methods belong to the class itself, not to instances.",
                  keyPoints: [
                    "Called on the class, not objects",
                    "Used for utility or factory logic",
                  ],
                  code: "class User {\n  static createGuest() {\n    return new User('Guest');\n  }\n}",
                  usage: "User.createGuest()",
                  takeaway: "Static = behavior related to class, not instance",
                },
                {
                  title: "When to Use Static Methods",
                  description:
                    "Static methods are ideal when logic doesn’t depend on instance data.",
                  examples: [
                    "Validation helpers",
                    "Factory methods",
                    "Utility logic",
                  ],
                  takeaway: "If no `this` is needed → static",
                },
                {
                  title: "Getters",
                  description:
                    "Getters allow controlled read access to properties.",
                  code: "class Counter {\n  get value() {\n    return this._value;\n  }\n}",
                  behavior: "Accessed like a property, not a function",
                  takeaway: "Getters improve API readability",
                },
                {
                  title: "Setters",
                  description:
                    "Setters allow controlled write access with validation.",
                  code: "set value(v) {\n  if (v < 0) throw Error('Invalid');\n  this._value = v;\n}",
                  benefit: "Protects internal state",
                  takeaway: "Setters enforce rules automatically",
                },
                {
                  title: "Why Getters & Setters Matter",
                  description: "They prevent direct uncontrolled mutation.",
                  comparison: [
                    "Direct property access → unsafe",
                    "Getter/Setter → controlled",
                  ],
                  takeaway: "Encapsulation in action",
                },
                {
                  title: "Private Properties (Concept)",
                  description: "Private properties hide internal state.",
                  approaches: [
                    "Naming convention (_value)",
                    "Modern #privateFields",
                  ],
                  example: "class User { #password; }",
                  takeaway: "Not everything should be public",
                },
                {
                  title: "Encapsulation Principles",
                  description:
                    "Encapsulation means hiding implementation details.",
                  rules: [
                    "Expose only what is necessary",
                    "Protect internal data",
                    "Control mutations",
                  ],
                  takeaway: "Encapsulation reduces bugs",
                },
                {
                  title: "Real-World Encapsulation Example",
                  description: "Business logic protection.",
                  scenario: "BankAccount balance should not be freely changed",
                  takeaway: "Critical for sensitive logic",
                },
                {
                  title: "Common Advanced Class Mistakes",
                  description: "Seen in production codebases.",
                  mistakes: [
                    "Overusing getters/setters unnecessarily",
                    "Making everything public",
                    "Using static where instance logic is needed",
                  ],
                },
                {
                  title: "Class Best Practices",
                  description: "Professional guidelines.",
                  practices: [
                    "Prefer composition over inheritance",
                    "Keep classes small",
                    "Avoid deep inheritance chains",
                  ],
                  takeaway: "Simple classes scale better",
                },
                {
                  title: "How This Maps to React & Modern JS",
                  description:
                    "Classes are less common in React UI, but still relevant.",
                  usage: ["Service classes", "API helpers", "State machines"],
                  takeaway: "Classes support architecture, not UI",
                },
              ],

              visualStructure: `
Class
 ├─ Private State
 ├─ Getter / Setter
 ├─ Instance Methods
 └─ Static Methods
    `,

              commonMistakes: [
                {
                  mistake: "Directly exposing internal data",
                  consequence: "Uncontrolled mutations",
                  fix: "Use getters/setters",
                },
                {
                  mistake: "Overengineering classes",
                  consequence: "Complex code",
                  fix: "Keep classes simple",
                },
                {
                  mistake: "Misusing static methods",
                  consequence: "Confusing APIs",
                  fix: "Use static only when instance data isn’t required",
                },
              ],

              proTips: [
                "Encapsulation is more important than inheritance",
                "Static methods are great for factories",
                "Not every app needs heavy OOP",
                "Understand classes for interviews & architecture",
              ],

              realWorldAnalogy:
                "A class is like a machine — users interact with buttons, not internal wiring",
            },

            tasks: [
              "Create getters and setters",
              "Use static methods",
              "Apply encapsulation concepts",
            ],

            examples: [
              "class Counter { get value(){ return this._v } }",
              "static createUser() { }",
            ],

            useCases: [
              "Utility classes",
              "Business logic layers",
              "Framework-style code",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand static methods",
              "Use getters and setters safely",
              "Apply encapsulation principles",
              "Write maintainable class-based code",
            ],
          },
          {
            day: 6,
            topic: "ES6 Modules (import / export)",
            subTopics: [
              "Named exports",
              "Default exports",
              "Importing modules",
              "Module file structure",
              "Bundling concept overview",
            ],
            explanation:
              "Modules allow splitting code into reusable files, improving maintainability and scalability.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Single File → Modular Refactor",
                description:
                  "Refactor a large single JavaScript file into clean ES6 modules.",
                setup: [
                  "One file containing utilities, API logic, and UI helpers",
                ],
                requirements: [
                  "Split code by responsibility",
                  "Use named exports for utilities",
                  "Use default export only where appropriate",
                ],
                outcome:
                  "You’ll learn how real codebases are broken into logical modules.",
              },
              {
                id: "pt-2",
                title: "Named vs Default Export Decision Drill",
                description:
                  "Choose the correct export type for different scenarios.",
                scenarios: [
                  "Utility functions",
                  "Service classes",
                  "Main app entry file",
                ],
                rules: [
                  "Explain why named or default is chosen",
                  "Avoid mixing styles unnecessarily",
                ],
                outcome:
                  "You’ll stop guessing export types and start designing module APIs.",
              },
              {
                id: "pt-3",
                title: "Project Folder Structure Design",
                description:
                  "Design a scalable folder structure using modules.",
                requirements: [
                  "Group files by responsibility",
                  "Separate utilities, services, and UI",
                  "Avoid deep nesting",
                ],
                reviewCriteria: [
                  "Readability",
                  "Scalability",
                  "Team friendliness",
                ],
                outcome:
                  "You’ll think like a lead engineer when organizing projects.",
              },
              {
                id: "pt-4",
                title: "Circular Dependency Debugging Exercise",
                description:
                  "Identify and fix circular imports in a broken project.",
                scenarios: ["Module A imports B", "Module B imports A"],
                tasks: [
                  "Detect circular dependency",
                  "Refactor shared logic into separate module",
                ],
                outcome:
                  "You’ll learn to avoid one of the most painful real-world module bugs.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Module bugs usually appear at runtime and are hard to trace.",
              realWorldChecks: [
                "Does this module have a single responsibility?",
                "Are imports explicit and readable?",
                "Could this create circular dependencies?",
              ],
              debugTip:
                "When something is undefined after import, check export type first.",
            },
            interviewInsights: {
              commonQuestions: [
                "What are ES6 modules?",
                "Difference between named and default exports?",
                "How do you organize modules in large projects?",
                "What is a circular dependency?",
              ],
              strongAnswerPattern:
                "Explain module purpose → export types → structure decisions → real-world pitfalls",
              redFlag: "Using default exports everywhere without clear reason.",
            },
            editorGuidance: {
              mentalChecklist: [
                "One responsibility per module",
                "Named exports for shared utilities",
                "Default export for primary module purpose",
                "Avoid circular imports",
              ],
              refactorRule:
                "If a file grows too large, split it into focused modules immediately.",
            },
            // 📚 Detailed Learning Content (Modern Code Architecture)
            content: {
              introduction:
                "As applications grow, keeping all code in one file becomes impossible. ES6 modules allow developers to split code into logical, reusable files. Modern frontend frameworks like React, Vue, and Angular are completely built on top of the module system.",

              sections: [
                {
                  title: "What Is a Module?",
                  description:
                    "A module is a file that encapsulates related code.",
                  keyPoints: [
                    "Each file is its own scope",
                    "Variables are NOT global by default",
                    "Explicit exports are required",
                  ],
                  takeaway: "Modules prevent global scope pollution",
                },
                {
                  title: "Why Modules Were Introduced",
                  description: "Before ES6, large JS apps were hard to manage.",
                  problemsBefore: [
                    "Global variables",
                    "Script load order issues",
                    "Hard-to-maintain code",
                  ],
                  solution: "Modules bring structure and predictability",
                  takeaway: "Modules enable scalable applications",
                },
                {
                  title: "Named Exports",
                  description: "Export multiple values from a module.",
                  rules: [
                    "Must use exact exported name when importing",
                    "Can export many things",
                  ],
                  code: "export const add = (a, b) => a + b;\nexport const sub = (a, b) => a - b;",
                  importExample: "import { add, sub } from './math.js';",
                  takeaway: "Best for utility libraries",
                },
                {
                  title: "Default Exports",
                  description: "Export a single main value from a module.",
                  rules: [
                    "Only one default export per file",
                    "Can be renamed during import",
                  ],
                  code: "export default function multiply(a, b) { return a * b; }",
                  importExample: "import multiply from './multiply.js';",
                  takeaway: "Best for main components or services",
                },
                {
                  title: "Named vs Default Exports",
                  description: "Choosing the right export type matters.",
                  comparison: [
                    "Named → multiple exports, explicit imports",
                    "Default → single export, flexible naming",
                  ],
                  takeaway: "Teams often prefer named exports for clarity",
                },
                {
                  title: "Importing Modules",
                  description: "Import syntax depends on export type.",
                  examples: [
                    "import { add } from './utils.js'",
                    "import User from './User.js'",
                    "import * as utils from './utils.js'",
                  ],
                  takeaway: "Import style reflects module design",
                },
                {
                  title: "Module File Structure",
                  description: "Organizing files properly is critical.",
                  recommendedStructure: [
                    "utils/",
                    "services/",
                    "components/",
                    "constants/",
                  ],
                  takeaway: "Good structure = maintainable codebase",
                },
                {
                  title: "Modules Are Strict by Default",
                  description: "Modules automatically use strict mode.",
                  effects: ["No accidental globals", "Safer assignments"],
                  takeaway: "Fewer silent bugs",
                },
                {
                  title: "How Browsers Load Modules",
                  description: "Modules are loaded differently than scripts.",
                  facts: [
                    "Use <script type='module'>",
                    "Deferred by default",
                    "Loaded once",
                  ],
                  takeaway: "Modern browsers handle modules efficiently",
                },
                {
                  title: "Bundling Concept (High-Level)",
                  description:
                    "Browsers don’t understand large dependency trees well.",
                  tools: ["Vite", "Webpack", "Parcel"],
                  role: "Bundle modules into optimized files",
                  takeaway: "Bundlers power modern frontend tooling",
                },
                {
                  title: "Common Module Mistakes",
                  description: "Very common beginner errors.",
                  mistakes: [
                    "Forgetting file extensions",
                    "Mixing default & named incorrectly",
                    "Circular dependencies",
                  ],
                },
                {
                  title: "How This Maps to React (CRITICAL)",
                  description: "React apps are pure module systems.",
                  examples: [
                    "import React from 'react'",
                    "import Header from './Header'",
                    "export default App",
                  ],
                  takeaway: "If modules make sense → React feels natural",
                },
              ],

              visualStructure: `
utils.js ──┐
api.js  ───┼──▶ Bundler ▶ Browser
ui.js   ───┘
    `,

              commonMistakes: [
                {
                  mistake: "Using default export everywhere",
                  consequence: "Unclear imports",
                  fix: "Prefer named exports for utilities",
                },
                {
                  mistake: "Poor folder structure",
                  consequence: "Unmaintainable project",
                  fix: "Group by responsibility",
                },
                {
                  mistake: "Circular imports",
                  consequence: "Runtime errors",
                  fix: "Refactor shared logic",
                },
              ],

              proTips: [
                "Named exports improve readability",
                "One responsibility per file",
                "Clean module structure saves months later",
                "Modules are the backbone of React",
              ],

              realWorldAnalogy:
                "Modules are like departments in a company — each handles its own responsibility",
            },

            tasks: [
              "Create multiple JS modules",
              "Use named and default exports",
              "Refactor existing code into modules",
            ],

            examples: [
              "export const add = () => {}",
              "import add from './utils.js';",
            ],

            useCases: [
              "Large projects",
              "Team-based development",
              "React and modern frameworks",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand ES6 module system",
              "Use named and default exports correctly",
              "Organize code into clean modules",
              "Be fully ready for React imports/exports",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Modular Web Application",
            subTopics: [
              "Project folder structure",
              "Using modules for logic separation",
              "Class-based architecture",
              "Reusable utilities",
              "Code refactoring",
            ],
            explanation:
              "Apply all ES6+ concepts to refactor an existing project into a clean, modular, and maintainable architecture.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Single-File App → Modular Architecture Refactor",
                description:
                  "Take an existing JavaScript app and refactor it into a clean modular architecture.",
                startingPoint: [
                  "All logic in one or two files",
                  "Global variables",
                  "Mixed UI + logic code",
                ],
                requirements: [
                  "Split code into meaningful modules",
                  "Remove all global variables",
                  "Use import/export correctly",
                ],
                outcome:
                  "You’ll experience how real projects evolve from messy to maintainable.",
              },
              {
                id: "pt-2",
                title: "Responsibility Mapping Exercise",
                description:
                  "Explicitly define responsibilities for each module.",
                tasks: [
                  "List each module and its responsibility",
                  "Ensure no module does more than one thing",
                  "Refactor if responsibilities overlap",
                ],
                rules: ["One responsibility per file", "Clear naming required"],
                outcome:
                  "You’ll learn to design code before writing it — a senior skill.",
              },
              {
                id: "pt-3",
                title: "Class vs Function Decision Audit",
                description:
                  "Audit where classes are used and justify each decision.",
                analysisPoints: [
                  "Is state + behavior modeled?",
                  "Would a pure function be simpler?",
                  "Is this class adding clarity or complexity?",
                ],
                requirement:
                  "Replace unnecessary classes with functions where appropriate",
                outcome:
                  "You’ll stop overengineering and start designing intentionally.",
              },
              {
                id: "pt-4",
                title: "Final Code Quality & Readability Pass",
                description:
                  "Perform a professional refactor pass focused on readability.",
                checklist: [
                  "Meaningful variable and function names",
                  "Small focused functions",
                  "Consistent folder structure",
                  "No unused exports or imports",
                ],
                outcome:
                  "Your codebase will look like something a team would accept.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Refactoring bugs are dangerous because behavior changes silently.",
              realWorldChecks: [
                "Does the app behave exactly the same as before?",
                "Are imports resolving correctly?",
                "Is any logic duplicated across modules?",
              ],
              debugTip:
                "Refactor in small steps and test after every structural change.",
            },
            interviewInsights: {
              commonQuestions: [
                "How do you structure a JavaScript project?",
                "How do you decide module boundaries?",
                "When do you use classes vs functions?",
                "How do you refactor legacy code safely?",
              ],
              strongAnswerPattern:
                "Explain problem → describe refactor strategy → show structure → discuss trade-offs",
              redFlag:
                "Focusing only on features and ignoring structure or maintainability.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Architecture before implementation",
                "One responsibility per module",
                "Functions first, classes when modeling behavior",
                "Refactor without breaking behavior",
              ],
              refactorRule:
                "If a file feels hard to explain, it probably has too many responsibilities.",
            },
            // 📚 Detailed Learning Content (Architecture & Code Quality)
            content: {
              introduction:
                "Real-world frontend development is not about writing code that works once — it's about writing code that is readable, reusable, scalable, and easy to maintain. This mini project focuses on architecture: splitting logic into modules, structuring folders, and refactoring messy code into a professional codebase. This is the exact mindset required before starting React.",

              sections: [
                {
                  title: "Goal of This Project",
                  description:
                    "Transform a small app into a scalable codebase.",
                  focusAreas: [
                    "Separation of concerns",
                    "Clean imports/exports",
                    "Readable structure",
                  ],
                  takeaway: "Architecture matters more than features",
                },
                {
                  title: "Choosing the Base Project",
                  description: "Start with an existing JS project.",
                  examples: ["Todo app", "Calculator", "Notes app"],
                  rule: "Pick something you already built",
                  takeaway: "Refactoring skill > greenfield coding",
                },
                {
                  title: "Recommended Folder Structure",
                  description: "Organize code by responsibility.",
                  structure: [
                    "src/",
                    " ├─ components/",
                    " ├─ services/",
                    " ├─ utils/",
                    " ├─ models/",
                    " ├─ state/",
                    " └─ main.js",
                  ],
                  takeaway: "Good structure scales naturally",
                },
                {
                  title: "Using Modules for Logic Separation",
                  description: "Each file should have one responsibility.",
                  examples: [
                    "api.js → API calls",
                    "dom.js → UI rendering",
                    "state.js → data handling",
                  ],
                  takeaway: "One responsibility per file",
                },
                {
                  title: "Reusable Utility Functions",
                  description: "Utilities reduce duplication.",
                  examples: ["formatDate()", "debounce()", "generateId()"],
                  rule: "Utilities should be pure functions",
                  takeaway: "Reusable code saves time later",
                },
                {
                  title: "Class-Based Architecture (Where Appropriate)",
                  description: "Use classes for domain logic, not UI.",
                  goodUseCases: ["TaskManager", "Cart", "UserService"],
                  badUseCases: ["DOM rendering", "Simple helpers"],
                  takeaway: "Classes model behavior, not layout",
                },
                {
                  title: "State Management Separation",
                  description: "Centralize data handling.",
                  pattern: [
                    "state.js holds data",
                    "UI reads state",
                    "Actions update state",
                  ],
                  takeaway: "Predictable data flow",
                },
                {
                  title: "Refactoring Strategy",
                  description: "Refactor step-by-step.",
                  steps: [
                    "Move code into modules",
                    "Replace globals with imports",
                    "Clean function names",
                    "Remove duplication",
                  ],
                  rule: "Refactor without breaking features",
                },
                {
                  title: "Improving Readability",
                  description: "Readable code is professional code.",
                  techniques: [
                    "Clear naming",
                    "Small functions",
                    "Consistent formatting",
                  ],
                  takeaway: "Code is read more than written",
                },
                {
                  title: "Testing the Modular App",
                  description: "Ensure behavior remains correct.",
                  checklist: [
                    "App still works",
                    "No console errors",
                    "Imports resolved correctly",
                  ],
                  takeaway: "Refactor ≠ rewrite",
                },
                {
                  title: "How This Prepares You for React",
                  description: "This project mirrors React architecture.",
                  parallels: [
                    "Components ↔ modules",
                    "Services ↔ API layers",
                    "State ↔ React state",
                  ],
                  takeaway: "React will feel familiar after this",
                },
                {
                  title: "What Interviewers Look For",
                  description: "This project signals senior thinking.",
                  signals: [
                    "Clean structure",
                    "Logical separation",
                    "Maintainability mindset",
                  ],
                  takeaway: "Architecture wins interviews",
                },
              ],

              visualStructure: `
UI (dom.js)
     ↑
State (state.js)
     ↑
Logic (services / models)
     ↑
Utils (pure helpers)
    `,

              commonMistakes: [
                {
                  mistake: "Everything in one file",
                  consequence: "Unmaintainable code",
                  fix: "Split by responsibility",
                },
                {
                  mistake: "Overusing classes",
                  consequence: "Complexity",
                  fix: "Use functions where simpler",
                },
                {
                  mistake: "Circular imports",
                  consequence: "Runtime crashes",
                  fix: "Refactor shared logic",
                },
              ],

              proTips: [
                "Structure first, code second",
                "Refactoring is a senior skill",
                "Small files are easier to reason about",
                "This project transitions you into React mindset",
              ],

              realWorldAnalogy:
                "This refactor is like organizing a messy room into labeled shelves",
            },

            tasks: [
              "Refactor an old project using modules",
              "Create utility and service files",
              "Use classes where appropriate",
              "Improve readability and structure",
            ],

            examples: [
              "Modular todo app",
              "Utility-based calculator",
              "Component-like JS structure",
            ],

            useCases: [
              "Interview-ready JavaScript",
              "Scalable frontend apps",
              "Preparation for React architecture",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "2–3 hours",

            // 🎯 Learning objectives
            objectives: [
              "Design modular JavaScript apps",
              "Apply ES6+ features together",
              "Refactor code professionally",
              "Be fully prepared for React",
            ],
          },
        ],
      },
      {
        week: 16,
        weekGoal:
          "Learn professional Git & GitHub workflow used in real-world frontend teams",
        weekOutcome:
          "Ability to manage code versions, collaborate with teams, and deploy projects professionally",
        miniProject: "Deploy Portfolio on GitHub Pages",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Git Fundamentals",
            subTopics: [
              "What is version control",
              "Git vs GitHub",
              "git init",
              "git status",
              "git add",
              "git commit",
            ],
            explanation:
              "Git helps track code changes and manage different versions of a project. It is essential for every frontend developer.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "First Real Git Repository Setup",
                description:
                  "Set up Git properly for a real project instead of a demo folder.",
                setup: [
                  "Create a new project folder",
                  "Initialize Git repository",
                ],
                requirements: [
                  "Configure username and email",
                  "Run git init",
                  "Verify .git folder exists",
                  "Explain what git init actually does",
                ],
                outcome:
                  "You’ll understand that Git is a project-level decision, not a command.",
              },
              {
                id: "pt-2",
                title: "Working Directory → Staging → Commit Drill",
                description: "Practice the full Git flow multiple times.",
                tasks: [
                  "Create a new file",
                  "Modify an existing file",
                  "Check git status at each step",
                  "Stage selectively",
                  "Commit with message",
                ],
                rules: [
                  "Do NOT use git add . blindly",
                  "Observe file states carefully",
                ],
                outcome: "You’ll build muscle memory for Git’s core workflow.",
              },
              {
                id: "pt-3",
                title: "Commit Message Quality Exercise",
                description: "Write professional commit messages.",
                scenarios: ["UI change", "Bug fix", "Refactor"],
                requirements: [
                  "Each commit message must explain WHAT changed",
                  "Avoid generic messages like 'update' or 'fix'",
                ],
                outcome:
                  "Your Git history will look readable and professional.",
              },
              {
                id: "pt-4",
                title: "Mistake Simulation & Recovery",
                description: "Simulate common beginner mistakes safely.",
                scenarios: [
                  "Modify file after staging",
                  "Forget to add file before commit",
                  "Commit wrong file",
                ],
                learningGoal:
                  "Understand that mistakes are normal and Git always shows the truth",
                outcome:
                  "You’ll stop being afraid of Git and start trusting it.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Git mistakes are rarely catastrophic — panic causes more damage than Git itself.",
              realWorldChecks: [
                "Did I check git status before committing?",
                "Is this commit small and logical?",
                "Can I clearly explain this commit later?",
              ],
              debugTip:
                "If unsure what’s happening, run git status — it never lies.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is version control?",
                "What is Git and why is it used?",
                "Difference between Git and GitHub?",
                "Explain git add vs git commit",
              ],
              strongAnswerPattern:
                "Explain problem → show Git workflow → highlight safety & collaboration",
              redFlag: "Saying Git is only for backup or only for GitHub.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Git tracks changes, not just files",
                "Staging is a deliberate step",
                "Commits are snapshots, not saves",
                "git status is your compass",
              ],
              refactorRule:
                "If a commit feels too big to describe in one sentence, split it.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As a developer, your code will change constantly. Git is a version control system that helps you track those changes, go back in time when something breaks, and collaborate with other developers safely. In real-world teams, Git is not optional — it is mandatory.",

              sections: [
                {
                  title: "What is Version Control?",
                  description:
                    "Version control is a system that records changes to files over time so you can recall specific versions later.",
                  keyPoints: [
                    "Keeps history of code changes",
                    "Allows rollback to previous versions",
                    "Enables collaboration without overwriting code",
                  ],
                  realLifeExample:
                    "Like Google Docs version history for your code",
                },
                {
                  title: "Why Git is Important for Developers",
                  description: "Git protects your work and enables teamwork.",
                  benefits: [
                    "Undo mistakes safely",
                    "Experiment without fear",
                    "Track who changed what and why",
                    "Industry-standard tool",
                  ],
                  takeaway:
                    "If you don’t know Git, you are not production-ready",
                },
                {
                  title: "Git vs GitHub (Very Important)",
                  description: "Git and GitHub are NOT the same thing.",
                  comparison: [
                    "Git → Tool installed on your computer",
                    "GitHub → Online platform that hosts Git repositories",
                    "Git works offline, GitHub requires internet",
                  ],
                  analogy: "Git is the camera, GitHub is Google Photos",
                },
                {
                  title: "Installing Git",
                  description: "Git must be installed before use.",
                  steps: [
                    "Download Git from official website",
                    "Verify installation using git --version",
                    "Configure username and email",
                  ],
                  commands: [
                    "git --version",
                    "git config --global user.name 'Your Name'",
                    "git config --global user.email 'you@email.com'",
                  ],
                },
                {
                  title: "git init – Initialize Repository",
                  description: "Creates a new Git repository.",
                  keyPoints: [
                    "Creates a .git folder",
                    "Run only once per project",
                    "Marks project as version-controlled",
                  ],
                  command: "git init",
                },
                {
                  title: "git status – Check File State",
                  description: "Shows the current state of working directory.",
                  shows: [
                    "Untracked files",
                    "Modified files",
                    "Files ready to commit",
                  ],
                  takeaway: "Run git status often",
                },
                {
                  title: "git add – Stage Changes",
                  description: "Moves changes to staging area.",
                  keyPoints: [
                    "Staging ≠ committing",
                    "Allows selective commits",
                  ],
                  commands: ["git add file.js", "git add ."],
                },
                {
                  title: "git commit – Save Snapshot",
                  description: "Commits create a snapshot of staged changes.",
                  bestPractices: [
                    "Write clear commit messages",
                    "Commit small logical changes",
                    "Avoid committing broken code",
                  ],
                  example: "git commit -m 'Add login form UI'",
                },
                {
                  title: "Git Workflow (Mental Model)",
                  description: "Git works in three stages.",
                  flow: [
                    "Working Directory → edit files",
                    "Staging Area → git add",
                    "Repository → git commit",
                  ],
                  analogy: "Edit → Select → Save",
                },
              ],

              visualStructure: `
Working Directory
       ↓
   git add
       ↓
 Staging Area
       ↓
  git commit
       ↓
 Git Repository
    `,

              commonMistakes: [
                {
                  mistake: "Not using Git at all",
                  consequence: "No backup, no history",
                  fix: "Initialize Git on every project",
                },
                {
                  mistake: "One huge commit",
                  consequence: "Hard to debug later",
                  fix: "Commit small logical changes",
                },
                {
                  mistake: "Vague commit messages",
                  consequence: "Unreadable history",
                  fix: "Explain WHAT and WHY",
                },
              ],

              proTips: [
                "Commit early, commit often",
                "Think of commits as checkpoints",
                "Never fear breaking code — Git has your back",
                "Git skill = confidence in development",
              ],

              realWorldAnalogy:
                "Git is like a time machine for your code — you can go back to any safe point whenever needed",
            },

            tasks: [
              "Install Git",
              "Initialize a Git repository",
              "Track files using git add",
              "Create meaningful commits",
            ],

            examples: [
              "git init",
              "git add .",
              "git status",
              "git commit -m 'Initial commit'",
            ],

            useCases: [
              "Tracking project progress",
              "Undoing mistakes",
              "Working safely on features",
              "Professional development workflow",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand version control fundamentals",
              "Initialize and manage a Git repository",
              "Use add and commit correctly",
              "Build Git confidence before GitHub",
            ],
          },
          {
            day: 2,
            topic: "Working with Branches",
            subTopics: [
              "What are branches",
              "Creating branches",
              "Switching branches",
              "Merging branches",
              "Branching strategies",
            ],
            explanation:
              "Branches allow developers to work on features independently without breaking the main codebase.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Feature Branch Creation Drill",
                description:
                  "Practice creating and switching branches the professional way.",
                tasks: [
                  "Check current branch",
                  "Create a feature branch using git checkout -b",
                  "Verify branch switch",
                ],
                requirements: [
                  "Use meaningful branch name (feature-login / feature-ui)",
                  "Explain why branch naming matters",
                ],
                outcome:
                  "You’ll be confident creating branches without hesitation.",
              },
              {
                id: "pt-2",
                title: "Isolated Feature Development",
                description: "Experience how branches isolate work.",
                steps: [
                  "Switch to feature branch",
                  "Make multiple commits",
                  "Switch back to main",
                  "Confirm changes are NOT visible on main",
                ],
                learningGoal: "Understand that commits live inside branches",
                outcome: "You’ll trust branches as safe sandboxes.",
              },
              {
                id: "pt-3",
                title: "Merge Like a Professional",
                description: "Merge a feature branch cleanly into main.",
                rules: [
                  "Switch to main before merging",
                  "Ensure main is clean (git status)",
                  "Run git merge feature-branch",
                ],
                bonus: "Observe whether merge is fast-forward or merge commit",
                outcome: "You’ll understand merging instead of fearing it.",
              },
              {
                id: "pt-4",
                title: "Branch Cleanup Habit",
                description: "Practice repository hygiene.",
                tasks: [
                  "Delete merged branch",
                  "List remaining branches",
                  "Explain why cleanup matters",
                ],
                outcome: "You’ll develop clean-repo discipline used in teams.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Branches allow fearless development. If you’re scared to experiment, you’re not using branches correctly.",
              realWorldChecks: [
                "Am I on the correct branch?",
                "Is main clean before merge?",
                "Did I isolate one task per branch?",
              ],
              debugTip:
                "Before every commit or merge, run git status and git branch.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is a Git branch?",
                "Why don’t we work directly on main?",
                "Explain feature branch workflow",
                "What happens during a merge?",
              ],
              strongAnswerPattern:
                "Explain safety → isolation → collaboration → merge",
              redFlag: "Saying branches are optional or only for teams.",
            },
            editorGuidance: {
              mentalChecklist: [
                "One task = one branch",
                "Commits belong to current branch",
                "Merge happens INTO main, not FROM main",
                "Delete branches after merge",
              ],
              confidenceRule:
                "If you’re afraid to try something, create a new branch.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world development, you never work directly on the main codebase. Branches allow you to safely build features, fix bugs, and experiment without affecting stable code. Understanding branches is a MUST for teamwork, GitHub workflows, and professional development.",

              sections: [
                {
                  title: "What is a Branch?",
                  description:
                    "A branch is an independent line of development in Git.",
                  keyPoints: [
                    "Main branch holds stable production-ready code",
                    "Branches allow parallel development",
                    "Each branch has its own commit history",
                  ],
                  realLifeAnalogy:
                    "Like creating a copy of a document to experiment without touching the original",
                },
                {
                  title: "Why Branches Are Important",
                  description: "Branches protect the main codebase.",
                  benefits: [
                    "Work on features safely",
                    "Fix bugs without breaking live code",
                    "Multiple developers can work simultaneously",
                    "Easy rollback if something goes wrong",
                  ],
                  takeaway: "No branch = risky development",
                },
                {
                  title: "Main vs Feature Branch",
                  description: "Different branches serve different purposes.",
                  comparison: [
                    "main → stable, production-ready code",
                    "feature/* → new features",
                    "bugfix/* → fixing issues",
                  ],
                  bestPractice: "Never experiment directly on main",
                },
                {
                  title: "Creating a Branch",
                  description: "Branches can be created in two ways.",
                  commands: [
                    "git branch feature-ui",
                    "git checkout -b feature-ui",
                  ],
                  explanation:
                    "The checkout -b command creates and switches branch in one step",
                },
                {
                  title: "Switching Between Branches",
                  description: "You can move between branches anytime.",
                  commands: ["git checkout main", "git checkout feature-ui"],
                  warning: "Uncommitted changes may block switching",
                },
                {
                  title: "Making Changes in a Branch",
                  description: "Each branch has its own history.",
                  workflow: [
                    "Switch to branch",
                    "Make changes",
                    "git add",
                    "git commit",
                  ],
                  takeaway: "Commits belong to the current branch only",
                },
                {
                  title: "Merging Branches",
                  description:
                    "Merging combines branch changes into another branch.",
                  steps: [
                    "Switch to target branch (main)",
                    "Run git merge feature-branch",
                  ],
                  example: "git checkout main\n git merge feature-ui",
                },
                {
                  title: "Fast-forward vs Merge Commit (Conceptual)",
                  description: "Git decides how to merge based on history.",
                  types: [
                    "Fast-forward → simple linear merge",
                    "Merge commit → creates a merge node",
                  ],
                  takeaway: "Both are normal in real projects",
                },
                {
                  title: "Branching Strategies (Beginner-Friendly)",
                  description: "Simple strategies used in teams.",
                  strategies: [
                    "Main + feature branches (most common)",
                    "One branch per task",
                    "Delete branch after merge",
                  ],
                  rule: "Short-lived branches are best",
                },
                {
                  title: "Deleting Branches",
                  description: "Clean up after merging.",
                  commands: ["git branch -d feature-ui"],
                  takeaway: "Clean repo = professional repo",
                },
                {
                  title: "How Branches Are Used in Real Teams",
                  description: "This is how companies work.",
                  flow: [
                    "Create feature branch",
                    "Develop feature",
                    "Merge to main",
                    "Deploy",
                  ],
                  takeaway: "This workflow scales from solo dev to large teams",
                },
              ],

              visualStructure: `
main ────────●─────────────●──────
               \\ 
                ●──●──● feature-ui
    `,

              commonMistakes: [
                {
                  mistake: "Working directly on main",
                  consequence: "Risk of breaking production code",
                  fix: "Always create a feature branch",
                },
                {
                  mistake: "Forgetting to switch branch",
                  consequence: "Commits go to wrong branch",
                  fix: "Run git status before committing",
                },
                {
                  mistake: "Never deleting old branches",
                  consequence: "Messy repository",
                  fix: "Delete branch after merge",
                },
              ],

              proTips: [
                "One task = one branch",
                "Commit before switching branches",
                "Name branches clearly (feature-login, bugfix-header)",
                "Branches give confidence to experiment",
              ],

              realWorldAnalogy:
                "Branches are like parallel universes of your code — you merge only the good ones into reality",
            },

            tasks: [
              "Create a feature branch",
              "Make changes in a branch",
              "Merge branch into main",
            ],

            examples: [
              "git branch feature-ui",
              "git checkout -b feature-ui",
              "git checkout main",
              "git merge feature-ui",
            ],

            useCases: [
              "Feature development",
              "Bug fixes",
              "Safe experimentation",
              "Team collaboration",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "30–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand what branches are",
              "Create and switch branches confidently",
              "Merge branches safely",
              "Follow professional Git workflows",
            ],
          },
          {
            day: 3,
            topic: "GitHub & Remote Repositories",
            subTopics: [
              "Creating GitHub repository",
              "git remote",
              "git push",
              "git pull",
              "git clone",
            ],
            explanation:
              "GitHub stores your code online and enables collaboration with other developers.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "First Professional GitHub Repository",
                description:
                  "Create and configure a clean GitHub repository like a real developer.",
                tasks: [
                  "Create a public repository on GitHub",
                  "Choose a meaningful repo name",
                  "Add a short description",
                ],
                requirements: [
                  "No spaces or random names",
                  "Repo name reflects project purpose",
                ],
                outcome: "You’ll have your first portfolio-ready repository.",
              },
              {
                id: "pt-2",
                title: "Local ↔ Remote Connection Drill",
                description: "Connect a local Git project to GitHub correctly.",
                steps: [
                  "Check current branch",
                  "Add remote origin",
                  "Verify remote using git remote -v",
                  "Push main branch",
                ],
                learningGoal:
                  "Understand how local and remote repos stay in sync",
                outcome:
                  "You’ll stop guessing how GitHub connects to your code.",
              },
              {
                id: "pt-3",
                title: "Daily Push Habit Simulation",
                description:
                  "Simulate a real developer’s daily GitHub workflow.",
                workflow: [
                  "Make a small change",
                  "Commit with clear message",
                  "Push to GitHub",
                  "Verify commit on GitHub UI",
                ],
                rule: "If code isn’t pushed, it doesn’t exist professionally",
                outcome: "You’ll build the habit recruiters expect.",
              },
              {
                id: "pt-4",
                title: "Clone & Explore",
                description:
                  "Learn how developers start work on existing projects.",
                tasks: [
                  "Clone any public GitHub repository",
                  "Open it locally",
                  "Explore commit history",
                  "Identify project structure",
                ],
                outcome: "You’ll feel confident joining existing codebases.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most Git mistakes happen at the boundary between local and remote.",
              realWorldChecks: [
                "Is my branch correct before pushing?",
                "Is origin pointing to the correct repo?",
                "Did my commit actually appear on GitHub?",
              ],
              debugTip:
                "If push fails, run: git status → git branch → git remote -v",
            },
            interviewInsights: {
              commonQuestions: [
                "What is the difference between Git and GitHub?",
                "What is a remote repository?",
                "Explain git push vs git pull",
                "How do you start working on an existing project?",
              ],
              strongAnswerPattern:
                "Local control → remote backup → collaboration",
              redFlag: "Saying GitHub automatically saves your code.",
            },
            editorGuidance: {
              mentalChecklist: [
                "Commit locally first",
                "Push regularly",
                "Pull before starting work",
                "Never rely on GitHub without commits",
              ],
              confidenceRule:
                "Your GitHub is your developer mirror — keep it clean.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Git works locally, but GitHub takes your code to the cloud. GitHub allows you to back up your projects, collaborate with other developers, showcase your work, and contribute to open-source. In modern development, GitHub is your public resume.",

              sections: [
                {
                  title: "What is GitHub?",
                  description:
                    "GitHub is a cloud-based platform that hosts Git repositories.",
                  keyPoints: [
                    "Stores code online",
                    "Tracks commit history",
                    "Enables collaboration via pull requests",
                    "Used by companies worldwide",
                  ],
                  realLifeAnalogy: "GitHub is Google Drive for Git projects",
                },
                {
                  title: "Git vs GitHub (Quick Recap)",
                  description: "Understanding the difference avoids confusion.",
                  comparison: [
                    "Git → Version control tool (local)",
                    "GitHub → Hosting & collaboration platform (online)",
                  ],
                  takeaway: "You use Git commands to interact with GitHub",
                },
                {
                  title: "Creating a GitHub Repository",
                  description: "A repository is a container for your project.",
                  steps: [
                    "Login to GitHub",
                    "Click New Repository",
                    "Give repo name",
                    "Choose public or private",
                    "Create repository",
                  ],
                  bestPractice: "Use meaningful repository names",
                },
                {
                  title: "What is a Remote Repository?",
                  description:
                    "A remote repository is a version of your project hosted online.",
                  keyPoints: [
                    "Local repo → your computer",
                    "Remote repo → GitHub",
                    "Both stay in sync using push & pull",
                  ],
                  takeaway: "Remote = backup + collaboration",
                },
                {
                  title: "git remote – Connecting Local to GitHub",
                  description: "Links your local repository to a remote one.",
                  command: "git remote add origin <repo-url>",
                  explanation: "origin is the default remote name",
                },
                {
                  title: "git push – Upload Code to GitHub",
                  description: "Push sends your commits to GitHub.",
                  command: "git push origin main",
                  keyPoints: [
                    "First push uploads entire history",
                    "Later pushes send only new commits",
                  ],
                  takeaway: "If it’s not pushed, it doesn’t exist online",
                },
                {
                  title: "git pull – Download Latest Changes",
                  description: "Pull fetches and merges remote changes.",
                  command: "git pull origin main",
                  keyPoints: [
                    "Used when working with others",
                    "Keeps local repo updated",
                  ],
                  warning: "Always pull before starting work",
                },
                {
                  title: "git clone – Copy Existing Repository",
                  description: "Clone downloads a complete repo with history.",
                  command: "git clone <repo-url>",
                  keyPoints: [
                    "Creates folder automatically",
                    "Includes all commits",
                  ],
                  useCase: "Starting work on existing project",
                },
                {
                  title: "Typical GitHub Workflow",
                  description: "How developers work daily.",
                  flow: [
                    "Clone repository",
                    "Create branch",
                    "Make changes",
                    "Commit",
                    "Push to GitHub",
                  ],
                  takeaway: "This flow is used in every company",
                },
                {
                  title: "Public vs Private Repositories",
                  description: "Choose visibility wisely.",
                  comparison: [
                    "Public → visible to everyone (portfolio)",
                    "Private → restricted access (client work)",
                  ],
                  bestPractice: "Make learning & portfolio projects public",
                },
                {
                  title: "Why GitHub is Important for Your Career",
                  description: "Recruiters check GitHub.",
                  benefits: [
                    "Proof of skills",
                    "Consistency of commits",
                    "Real projects > certificates",
                  ],
                  takeaway: "GitHub is your developer identity",
                },
              ],

              visualStructure: `
Local Repo (Git)
      ↓ push
GitHub Repo (Remote)
      ↑ pull
    `,

              commonMistakes: [
                {
                  mistake: "Not pushing code",
                  consequence: "No backup, no portfolio",
                  fix: "Push regularly",
                },
                {
                  mistake: "Pushing directly to main always",
                  consequence: "Risky workflow",
                  fix: "Use feature branches",
                },
                {
                  mistake: "Forgetting to pull",
                  consequence: "Merge conflicts later",
                  fix: "Pull before working",
                },
              ],

              proTips: [
                "Push code daily",
                "Write clean README files",
                "Keep repos organized",
                "Your GitHub reflects your discipline",
              ],

              realWorldAnalogy:
                "GitHub is your public code diary — anyone can see how you grow as a developer",
            },

            tasks: [
              "Create a GitHub repository",
              "Connect local repo to GitHub",
              "Push code to remote",
              "Clone an existing repo",
            ],

            examples: [
              "git remote add origin <repo-url>",
              "git push origin main",
              "git pull origin main",
              "git clone <repo-url>",
            ],

            useCases: [
              "Backup code online",
              "Showcase projects",
              "Team collaboration",
              "Open-source contribution",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand remote repositories",
              "Use push, pull, and clone confidently",
              "Connect local projects to GitHub",
              "Build a public developer presence",
            ],
          },
          {
            day: 4,
            topic: "Collaboration & Pull Requests",
            subTopics: [
              "Forking repositories",
              "Pull requests",
              "Code reviews",
              "Merge conflicts",
              "Conflict resolution",
            ],
            explanation:
              "Pull requests are used to review and merge code changes in professional teams.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Fork & Clone Like Open-Source Contributors",
                description:
                  "Practice contributing to a repository you don’t own.",
                tasks: [
                  "Fork a public GitHub repository",
                  "Clone your fork locally",
                  "Verify origin points to your fork",
                ],
                requirements: [
                  "Understand difference between origin and upstream",
                  "Explain why fork is needed for open-source",
                ],
                outcome:
                  "You’ll feel confident contributing to open-source projects.",
              },
              {
                id: "pt-2",
                title: "Create a Clean Pull Request",
                description: "Open a professional-quality pull request.",
                steps: [
                  "Create a feature branch",
                  "Make a focused change",
                  "Push branch to GitHub",
                  "Open pull request with title & description",
                ],
                rules: [
                  "One feature or fix per PR",
                  "Explain WHAT changed and WHY",
                ],
                outcome:
                  "You’ll know how to submit PRs that get merged faster.",
              },
              {
                id: "pt-3",
                title: "Code Review Simulation",
                description: "Learn to review code without ego.",
                tasks: [
                  "Review a PR (real or dummy)",
                  "Leave at least 2 constructive comments",
                  "Suggest one improvement",
                ],
                learningGoal: "Understand what reviewers look for",
                outcome: "You’ll become comfortable with review culture.",
              },
              {
                id: "pt-4",
                title: "Merge Conflict Resolution Drill",
                description: "Handle conflicts calmly like a senior developer.",
                scenario: [
                  "Same file edited in two branches",
                  "Merge produces conflict markers",
                ],
                steps: [
                  "Open conflicted file",
                  "Understand both changes",
                  "Resolve conflict correctly",
                  "Commit resolution",
                ],
                outcome: "You’ll stop panicking when conflicts appear.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Most real Git problems happen during collaboration, not solo work.",
              realWorldChecks: [
                "Is my branch up to date before opening PR?",
                "Is this PR small enough to review easily?",
                "Did I test after resolving conflicts?",
              ],
              debugTip:
                "When confused in conflicts: slow down, read markers, choose logic—not lines.",
            },
            interviewInsights: {
              commonQuestions: [
                "What is a pull request?",
                "Why do teams use PRs instead of direct pushes?",
                "What are merge conflicts?",
                "How do you resolve a conflict?",
              ],
              strongAnswerPattern: "Safety → review → quality → collaboration",
              redFlag: "Saying conflicts mean something went wrong.",
            },
            editorGuidance: {
              mentalChecklist: [
                "PRs are proposals, not commands",
                "Reviews are about code, not ego",
                "Conflicts are normal",
                "Clear communication > clever code",
              ],
              confidenceRule:
                "If you can handle PRs calmly, you’re team-ready.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In professional development, you rarely push code directly to the main branch. Instead, changes go through pull requests (PRs), where other developers review your code, suggest improvements, and ensure quality. This workflow is standard in companies and open-source projects.",

              sections: [
                {
                  title: "Why Collaboration Matters",
                  description:
                    "Modern software is built by teams, not individuals.",
                  keyPoints: [
                    "Multiple developers work on same codebase",
                    "Code must be reviewed before merging",
                    "Mistakes are caught early",
                  ],
                  takeaway: "Good collaboration = stable software",
                },
                {
                  title: "What is Forking?",
                  description:
                    "Forking creates a personal copy of someone else's repository on your GitHub account.",
                  useCases: [
                    "Contributing to open-source",
                    "Experimenting without permission",
                  ],
                  analogy:
                    "Forking is like making your own copy of a shared document",
                },
                {
                  title: "Fork vs Clone (Important)",
                  description: "They are often confused.",
                  comparison: [
                    "Fork → copy repo to your GitHub account",
                    "Clone → download repo to your computer",
                  ],
                  rule: "Open-source contribution = fork first",
                },
                {
                  title: "What is a Pull Request (PR)?",
                  description:
                    "A pull request is a request to merge your code into another branch.",
                  keyPoints: [
                    "PR shows code changes clearly",
                    "Allows discussion and review",
                    "Nothing is merged automatically",
                  ],
                  takeaway: "PR = proposal, not command",
                },
                {
                  title: "Creating a Pull Request (Step-by-Step)",
                  description: "Typical PR flow on GitHub.",
                  steps: [
                    "Push branch to GitHub",
                    "Open repository on GitHub",
                    "Click 'Compare & pull request'",
                    "Add title and description",
                    "Create pull request",
                  ],
                  bestPractice: "Explain WHAT you changed and WHY",
                },
                {
                  title: "Code Reviews",
                  description:
                    "Code reviews improve quality and knowledge sharing.",
                  reviewersLookFor: [
                    "Code readability",
                    "Logic correctness",
                    "Edge cases",
                    "Consistency with standards",
                  ],
                  mindset: "Reviews are about code, not ego",
                },
                {
                  title: "What are Merge Conflicts?",
                  description:
                    "Conflicts happen when Git cannot automatically merge changes.",
                  commonCauses: [
                    "Same file edited in two branches",
                    "Outdated branch",
                  ],
                  takeaway: "Conflicts are normal, not errors",
                },
                {
                  title: "Understanding Conflict Markers",
                  description: "Git marks conflicting sections clearly.",
                  example: [
                    "<<<<<<< HEAD",
                    "Your code",
                    "=======",
                    "Incoming code",
                    ">>>>>>> branch-name",
                  ],
                  rule: "You must decide what to keep",
                },
                {
                  title: "Resolving Merge Conflicts",
                  description: "Steps to fix conflicts.",
                  steps: [
                    "Open conflicted file",
                    "Decide correct code",
                    "Remove conflict markers",
                    "Save file",
                    "git add",
                    "git commit",
                  ],
                  takeaway: "Conflict resolution is a core skill",
                },
                {
                  title: "Best Practices for PRs",
                  description: "Clean PRs get merged faster.",
                  practices: [
                    "Small PRs",
                    "Clear commit history",
                    "Descriptive titles",
                    "Respond to review comments",
                  ],
                },
                {
                  title: "How This Works in Real Companies",
                  description: "Industry-standard workflow.",
                  flow: [
                    "Create feature branch",
                    "Push to remote",
                    "Open PR",
                    "Code review",
                    "Fix feedback",
                    "Merge",
                  ],
                  takeaway: "This is used everywhere",
                },
              ],

              visualStructure: `
Feature Branch
      ↓
Pull Request
      ↓
Code Review
      ↓
Fix Feedback
      ↓
Merge into Main
    `,

              commonMistakes: [
                {
                  mistake: "Taking reviews personally",
                  consequence: "Poor collaboration",
                  fix: "Focus on improving code",
                },
                {
                  mistake: "Huge pull requests",
                  consequence: "Hard to review",
                  fix: "Keep PRs small and focused",
                },
                {
                  mistake: "Ignoring merge conflicts",
                  consequence: "Broken code",
                  fix: "Resolve conflicts carefully",
                },
              ],

              proTips: [
                "Write PR descriptions like documentation",
                "Review your own PR before submitting",
                "Communicate clearly with reviewers",
                "Good PRs build trust",
              ],

              realWorldAnalogy:
                "A pull request is like submitting an assignment for review before final submission",
            },

            tasks: [
              "Create a pull request",
              "Review someone else's PR",
              "Resolve a merge conflict",
            ],

            examples: [
              "Compare & pull request",
              "Resolve conflict markers (<<<<<<, ======, >>>>>>)",
            ],

            useCases: [
              "Team development",
              "Open-source contributions",
              "Maintaining code quality",
              "Professional collaboration",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand pull request workflow",
              "Collaborate using GitHub",
              "Review and improve code",
              "Resolve merge conflicts confidently",
            ],
          },
          {
            day: 5,
            topic: "Git Best Practices & Documentation",
            subTopics: [
              "Commit message conventions",
              ".gitignore usage",
              "README.md writing",
              "Project documentation",
              "Clean commit history",
            ],
            explanation:
              "Good Git practices make your projects professional, maintainable, and easy for others to understand.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Professional Commit Message Rewrite",
                description:
                  "Transform bad commit messages into professional ones.",
                tasks: [
                  "Take 5 bad commit messages (e.g. 'final', 'fix', 'changes')",
                  "Rewrite them using proper conventions",
                  "Explain WHY each new message is better",
                ],
                rules: [
                  "Use imperative tone (Add, Fix, Update)",
                  "Max 50 characters for title",
                  "Explain intent, not implementation",
                ],
                outcome:
                  "You’ll develop instinct for writing meaningful commit history.",
              },
              {
                id: "pt-2",
                title: "Production-Ready .gitignore Setup",
                description: "Create a secure and clean .gitignore file.",
                tasks: [
                  "Create .gitignore for a JS/React project",
                  "Add node_modules, env files, OS files",
                  "Verify ignored files are not tracked",
                ],
                securityCheck: [
                  "Confirm .env is never committed",
                  "Explain why secrets must stay local",
                ],
                outcome:
                  "You’ll avoid one of the biggest beginner security mistakes.",
              },
              {
                id: "pt-3",
                title: "Write a Recruiter-Grade README.md",
                description:
                  "Write a README that explains your project in 60 seconds.",
                requiredSections: [
                  "Project description",
                  "Features",
                  "Tech stack",
                  "Installation steps",
                  "Usage",
                ],
                qualityRules: [
                  "Simple language",
                  "No buzzwords",
                  "Honest feature list",
                ],
                outcome: "Your repository will look interview-ready.",
              },
              {
                id: "pt-4",
                title: "Git History Cleanup Simulation",
                description: "Learn to think in clean commits.",
                tasks: [
                  "Review git log --oneline",
                  "Identify noisy or meaningless commits",
                  "Group changes logically (conceptual)",
                ],
                learningGoal:
                  "Understand how clean history helps debugging & reviews",
                outcome: "You’ll start committing with intention, not habit.",
              },
            ],
            testingInsight: {
              whyItMatters:
                "Recruiters judge projects faster than they judge code.",
              checklist: [
                "Does README explain project clearly?",
                "Can someone run project without asking questions?",
                "Do commits tell a story?",
              ],
              proDebugTip:
                "If your past self can understand the repo in 6 months, you did it right.",
            },
            interviewInsights: {
              commonQuestions: [
                "What makes a good commit message?",
                "What files should never be committed?",
                "What do you include in a README?",
                "How do you keep Git history clean?",
              ],
              strongAnswerTheme: "Clarity, safety, and collaboration",
              redFlag: "Saying documentation is optional or unnecessary.",
            },
            editorGuidance: {
              mindsetShift: [
                "Code is written once, read many times",
                "Commits are communication",
                "README is your first impression",
                "Git history reflects discipline",
              ],
              seniorRule:
                "If someone understands your repo without asking you, you win.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Writing code is only half the job. Professional developers also focus on clarity, documentation, and history. Clean commits, proper documentation, and ignored unnecessary files make your project readable for teammates, recruiters, and even your future self.",

              sections: [
                {
                  title: "Why Git Best Practices Matter",
                  description:
                    "Professional repositories are easy to understand at a glance.",
                  benefits: [
                    "Faster onboarding for new developers",
                    "Clear project purpose",
                    "Readable history for debugging",
                    "Positive impression on recruiters",
                  ],
                  takeaway: "Messy Git history = unprofessional project",
                },
                {
                  title: "Commit Message Conventions",
                  description: "Commit messages explain what changed and why.",
                  structure: [
                    "Short title (50 chars max)",
                    "Imperative tone (Add, Fix, Update)",
                    "Optional detailed description",
                  ],
                  goodExamples: [
                    "Add login form validation",
                    "Fix navbar alignment issue",
                    "Update README with setup steps",
                  ],
                  badExamples: ["changes", "final commit", "asdf"],
                  takeaway: "Commits are documentation",
                },
                {
                  title: ".gitignore – Ignoring Unnecessary Files",
                  description:
                    ".gitignore tells Git which files should NOT be tracked.",
                  commonIgnoredFiles: [
                    "node_modules/",
                    ".env",
                    "dist/",
                    ".DS_Store",
                  ],
                  example: "node_modules/\n.env\n.DS_Store",
                  rule: "Never commit secrets or dependencies",
                },
                {
                  title: "Why .gitignore is Critical",
                  description: "Some files should never be pushed.",
                  risksIfIgnored: [
                    "Huge repositories",
                    "Leaked API keys",
                    "Security vulnerabilities",
                  ],
                  takeaway: ".gitignore protects your project",
                },
                {
                  title: "README.md – Your Project’s Front Page",
                  description: "README is the first thing people read.",
                  mustHaveSections: [
                    "Project title",
                    "Description",
                    "Features",
                    "Tech stack",
                    "Installation steps",
                    "Usage",
                  ],
                  basicTemplate: [
                    "# Project Title",
                    "## Description",
                    "## Features",
                    "## Tech Stack",
                    "## Installation",
                    "## Usage",
                  ],
                  takeaway: "No README = incomplete project",
                },
                {
                  title: "Writing a Good Project Description",
                  description:
                    "Explain what the project does in simple language.",
                  tips: [
                    "1–2 short paragraphs",
                    "Mention problem it solves",
                    "Mention who it’s for",
                  ],
                  example:
                    "This project is a task manager app that helps users organize daily tasks efficiently.",
                },
                {
                  title: "Documenting Features Clearly",
                  description:
                    "Features help reviewers understand project scope.",
                  examples: [
                    "Add / delete tasks",
                    "Filter tasks",
                    "Persistent storage",
                  ],
                  rule: "Be honest — don’t exaggerate",
                },
                {
                  title: "Clean Commit History",
                  description: "A clean history tells a story.",
                  practices: [
                    "Avoid committing broken code",
                    "Avoid committing generated files",
                    "Group related changes",
                  ],
                  takeaway: "Readable history = easy debugging",
                },
                {
                  title: "Using Git Log Effectively",
                  description: "git log helps understand project evolution.",
                  commands: ["git log", "git log --oneline"],
                  takeaway: "Logs help during interviews",
                },
                {
                  title: "How Recruiters Evaluate GitHub Repos",
                  description: "They don’t read every line of code.",
                  theyCheck: [
                    "README quality",
                    "Commit consistency",
                    "Project structure",
                    "Clarity of purpose",
                  ],
                  takeaway: "Your GitHub is your silent interviewer",
                },
              ],

              visualStructure: `
Clean Repo =
Good README
+ Meaningful Commits
+ Ignored Junk Files
+ Clear History
    `,

              commonMistakes: [
                {
                  mistake: "No README file",
                  consequence: "Project feels incomplete",
                  fix: "Always add README.md",
                },
                {
                  mistake: "Committing node_modules",
                  consequence: "Huge, messy repository",
                  fix: "Use .gitignore",
                },
                {
                  mistake: "Poor commit messages",
                  consequence: "Unreadable history",
                  fix: "Follow commit conventions",
                },
              ],

              proTips: [
                "Imagine a stranger reading your repo",
                "Document as you build",
                "Small commits are easier to review",
                "Good Git habits build trust",
              ],

              realWorldAnalogy:
                "Your repository is like a product manual — if it’s confusing, people won’t use it",
            },

            tasks: [
              "Add .gitignore file",
              "Write a proper README",
              "Improve commit messages",
            ],

            examples: ["node_modules/", ".env", "# Project Title\n## Features"],

            useCases: [
              "Professional portfolios",
              "Team onboarding",
              "Interview readiness",
              "Long-term project maintenance",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Write professional commit messages",
              "Use .gitignore correctly",
              "Document projects effectively",
              "Maintain clean Git history",
            ],
          },
          {
            day: 6,
            topic: "GitHub Pages & Deployment",
            subTopics: [
              "What is GitHub Pages",
              "Deploy static websites",
              "Custom domain basics",
              "Deployment workflow",
              "Updating live projects",
            ],
            explanation:
              "GitHub Pages allows you to deploy frontend projects for free and share them with the world.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Deploy Your First Live Project",
                description:
                  "Deploy a real project using GitHub Pages and make it publicly accessible.",
                tasks: [
                  "Choose a completed frontend project",
                  "Push latest code to GitHub",
                  "Enable GitHub Pages from main branch",
                  "Open the generated live URL",
                ],
                validationChecklist: [
                  "Site loads without errors",
                  "CSS and JS work correctly",
                  "No broken images or paths",
                ],
                outcome:
                  "You’ll experience your first real deployment confidence boost.",
              },
              {
                id: "pt-2",
                title: "README Live Link Upgrade",
                description:
                  "Make your repository instantly useful to recruiters.",
                tasks: [
                  "Add a 'Live Demo' section in README.md",
                  "Paste GitHub Pages URL",
                  "Ensure link opens correctly",
                ],
                qualityRule:
                  "Live link should be visible in first 10 seconds of scrolling",
                outcome: "Your repo becomes clickable proof, not just code.",
              },
              {
                id: "pt-3",
                title: "Deployment Debugging Drill",
                description: "Learn how to fix common broken deployments.",
                scenarios: [
                  "CSS not loading",
                  "Images missing",
                  "JavaScript not running",
                ],
                tasks: [
                  "Open browser console on live site",
                  "Identify path-related errors",
                  "Fix using relative paths",
                ],
                outcome: "You’ll stop fearing broken live sites.",
              },
              {
                id: "pt-4",
                title: "Continuous Deployment Habit",
                description: "Understand how real developers treat deployment.",
                tasks: [
                  "Make a small UI change",
                  "Commit with meaningful message",
                  "Push to GitHub",
                  "Verify update on live site",
                ],
                mindset: "Every push is a deployment",
                outcome:
                  "You’ll internalize real-world CI/CD thinking (at beginner level).",
              },
            ],
            testingInsight: {
              recruiterReality: "Recruiters click links before reading code.",
              checklist: [
                "Does the site open?",
                "Does it look intentional?",
                "Does it break on refresh?",
              ],
              goldenRule: "A broken live link hurts more than no link.",
            },
            interviewInsights: {
              commonQuestions: [
                "How do you deploy your frontend projects?",
                "What is GitHub Pages used for?",
                "How do you update a live website?",
                "What kind of projects should be deployed?",
              ],
              strongAnswerPattern: "Explain push → deploy → verify",
              redFlag:
                "Saying you only show screenshots instead of live demos.",
            },
            editorGuidance: {
              careerMindset: [
                "Code must be accessible",
                "Live work builds instant trust",
                "Deployment is part of development",
                "Visibility > perfection",
              ],
              seniorAdvice:
                "If someone can’t open your project, it doesn’t matter how good the code is.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Writing code is not enough — your work must be visible. GitHub Pages lets frontend developers deploy static websites for free and make projects accessible through a live URL. This is crucial for portfolios, demos, and recruiter visibility.",

              sections: [
                {
                  title: "What is Deployment?",
                  description:
                    "Deployment means making your application available to users over the internet.",
                  keyPoints: [
                    "Local code → Live website",
                    "Anyone can access via URL",
                    "Required for real-world usage",
                  ],
                  takeaway: "If it’s not deployed, it doesn’t exist",
                },
                {
                  title: "What is GitHub Pages?",
                  description:
                    "GitHub Pages is a free static hosting service provided by GitHub.",
                  supports: [
                    "HTML, CSS, JavaScript",
                    "Static assets",
                    "Single-page apps",
                  ],
                  limitations: [
                    "No backend/server code",
                    "Frontend-only projects",
                  ],
                  takeaway: "Perfect for frontend portfolios",
                },
                {
                  title: "When to Use GitHub Pages",
                  description: "GitHub Pages fits many frontend use cases.",
                  useCases: [
                    "Portfolio websites",
                    "Landing pages",
                    "Mini frontend projects",
                    "Client demos",
                  ],
                  rule: "Static site only",
                },
                {
                  title: "Deploying a Project (Step-by-Step)",
                  description: "Basic deployment workflow.",
                  steps: [
                    "Push project to GitHub",
                    "Open repository on GitHub",
                    "Go to Settings → Pages",
                    "Select branch (main)",
                    "Save and wait",
                  ],
                  result: "Live URL generated automatically",
                },
                {
                  title: "Understanding the Live URL",
                  description: "GitHub generates a public URL.",
                  format: "https://username.github.io/project-name",
                  takeaway: "This URL can be shared anywhere",
                },
                {
                  title: "Updating a Live Website",
                  description: "Deployment is continuous.",
                  workflow: [
                    "Make code changes",
                    "git add & commit",
                    "git push",
                    "GitHub Pages auto-updates",
                  ],
                  takeaway: "Every push updates your live site",
                },
                {
                  title: "Custom Domain Basics (Conceptual)",
                  description: "You can use your own domain name.",
                  examples: ["www.yourname.dev", "portfolio.yourdomain.com"],
                  stepsHighLevel: [
                    "Buy domain",
                    "Add custom domain in GitHub Pages",
                    "Update DNS records",
                  ],
                  takeaway: "Optional but professional",
                },
                {
                  title: "Common Deployment Mistakes",
                  description: "Small mistakes cause broken sites.",
                  mistakes: [
                    "Wrong branch selected",
                    "Incorrect file paths",
                    "Using absolute local paths",
                  ],
                  fix: "Use relative paths and check console",
                },
                {
                  title: "Deployment Best Practices",
                  description: "Make deployments clean.",
                  practices: [
                    "Test locally before push",
                    "Use meaningful commit messages",
                    "Check site after deployment",
                  ],
                  takeaway: "Deployment is part of development",
                },
                {
                  title: "Why Deployment Matters for Career",
                  description: "Recruiters prefer live projects.",
                  benefits: [
                    "Clickable proof of skills",
                    "Shows confidence",
                    "Demonstrates real-world workflow",
                  ],
                  takeaway: "Live projects > screenshots",
                },
              ],

              visualStructure: `
Local Code
    ↓ git push
GitHub Repo
    ↓ GitHub Pages
Live Website 🌍
    `,

              commonMistakes: [
                {
                  mistake: "Forgetting to push latest code",
                  consequence: "Live site not updated",
                  fix: "Always push before checking URL",
                },
                {
                  mistake: "Using absolute file paths",
                  consequence: "Broken assets on live site",
                  fix: "Use relative paths",
                },
                {
                  mistake: "Not testing deployment",
                  consequence: "Broken portfolio",
                  fix: "Always open live URL",
                },
              ],

              proTips: [
                "Deploy every serious project",
                "Add live links to README",
                "Use GitHub Pages as default hosting",
                "Live work builds trust instantly",
              ],

              realWorldAnalogy:
                "Deployment is like publishing a book — until it’s published, nobody can read it",
            },

            tasks: [
              "Enable GitHub Pages",
              "Deploy portfolio project",
              "Update live site with commits",
            ],

            examples: [
              "Settings → Pages → main branch",
              "Live URL: username.github.io/project",
            ],

            useCases: [
              "Live portfolio",
              "Sharing projects with recruiters",
              "Client demos",
              "Proof of frontend skills",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "30–40 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Deploy frontend projects",
              "Understand deployment workflow",
              "Maintain live websites",
              "Use deployment for career growth",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Portfolio Deployment",
            subTopics: [
              "Portfolio structure",
              "Project showcases",
              "Live demo links",
              "GitHub repository linking",
              "Final review",
            ],
            explanation:
              "This project makes you publicly visible as a frontend developer with real, deployed work.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Portfolio Reality Check",
                description:
                  "Audit your portfolio like a recruiter would (10-second scan).",
                tasks: [
                  "Open your portfolio in an incognito window",
                  "Check if your name & role are visible immediately",
                  "Verify projects section is reachable within one scroll",
                  "Confirm at least 1 live demo opens correctly",
                ],
                validationRule:
                  "If someone understands what you do in 10 seconds, it passes",
                outcome:
                  "You’ll see your portfolio from an outsider’s perspective.",
              },
              {
                id: "pt-2",
                title: "Project Storytelling Upgrade",
                description: "Turn projects into problem–solution stories.",
                tasks: [
                  "Pick 2–3 strongest projects",
                  "For each project, write:",
                  "- What problem it solves",
                  "- Why you built it",
                  "- What you learned",
                ],
                rule: "Avoid buzzwords, use simple honest language",
                outcome: "Your projects will feel intentional, not random.",
              },
              {
                id: "pt-3",
                title: "Live Demo & Repo Hygiene",
                description: "Make every project link recruiter-safe.",
                checklist: [
                  "Live demo link works",
                  "GitHub repo is public",
                  "README exists",
                  "README contains live link",
                  "No broken images or paths",
                ],
                outcome: "Your portfolio won’t break under scrutiny.",
              },
              {
                id: "pt-4",
                title: "Mobile & Console Check",
                description: "Ensure professional polish.",
                tasks: [
                  "Open portfolio on mobile device",
                  "Check layout & readability",
                  "Open DevTools console",
                  "Ensure no red errors appear",
                ],
                mindset: "Small details separate amateurs from professionals",
                outcome: "You’ll eliminate silent trust-breakers.",
              },
              {
                id: "pt-5",
                title: "Portfolio Growth Plan",
                description: "Define how your portfolio evolves after today.",
                tasks: [
                  "Decide next project to add",
                  "Set rule: update portfolio every 2–3 weeks",
                  "Plan removal of weak/old projects",
                ],
                outcome:
                  "Your portfolio becomes a living system, not a one-time task.",
              },
            ],
            testingInsight: {
              recruiterBehavior:
                "Most recruiters do not run your code locally.",
              theyDoInstead: [
                "Open portfolio",
                "Click live demo",
                "Skim GitHub repo",
                "Check README & commits",
              ],
              goldenRule: "If links fail, trust breaks instantly.",
            },
            interviewInsights: {
              commonQuestions: [
                "Can you show your portfolio?",
                "Which project are you most proud of?",
                "What challenges did you face building it?",
                "How do you deploy your projects?",
              ],
              strongAnswerPattern: "Live demo + short explanation",
              redFlag:
                "Only showing screenshots or saying ‘code is there but not deployed’",
            },
            editorGuidance: {
              mindsetShift: [
                "You now build for visibility",
                "You think like a professional",
                "You own your public identity",
                "You’re no longer invisible",
              ],
              seniorAdvice:
                "Your portfolio should answer questions before they’re asked.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A portfolio is not just a website — it is your professional identity. Recruiters, clients, and interviewers judge your skills based on what they can see live. This mini project focuses on creating and deploying a clean, honest, and professional frontend portfolio using everything you have learned so far.",

              sections: [
                {
                  title: "Why a Portfolio Matters",
                  description: "A portfolio is proof, not promise.",
                  keyPoints: [
                    "Shows real skills, not claims",
                    "Builds trust instantly",
                    "Works even when you’re not present",
                  ],
                  takeaway: "No portfolio = invisible developer",
                },
                {
                  title: "What Makes a Good Frontend Portfolio",
                  description:
                    "Simple, clear, and honest portfolios perform best.",
                  mustHave: [
                    "Your name and role",
                    "Short introduction",
                    "Projects section",
                    "Live demo links",
                    "GitHub source links",
                  ],
                  rule: "Clarity > fancy design",
                },
                {
                  title: "Recommended Portfolio Structure",
                  description: "Keep structure predictable.",
                  structure: [
                    "Hero section (intro)",
                    "Skills summary",
                    "Projects showcase",
                    "About / contact",
                    "Footer",
                  ],
                  takeaway: "Easy navigation matters",
                },
                {
                  title: "Project Showcase Best Practices",
                  description: "Projects sell your skills.",
                  includeForEachProject: [
                    "Project name",
                    "Short description",
                    "Tech used (HTML, CSS, JS)",
                    "Live demo link",
                    "GitHub repository link",
                  ],
                  rule: "Show 3–5 strong projects, not everything",
                },
                {
                  title: "Choosing Projects to Display",
                  description: "Quality beats quantity.",
                  goodExamples: [
                    "Task Manager App",
                    "Weather App",
                    "Quiz / Game App",
                    "API-based Dashboard",
                  ],
                  avoid: ["Hello World demos", "Incomplete projects"],
                  takeaway: "Every project should tell a story",
                },
                {
                  title: "Linking GitHub Repositories",
                  description: "Source code matters to reviewers.",
                  bestPractices: [
                    "Clean README in each repo",
                    "Meaningful commit history",
                    "Deployed link added in README",
                  ],
                  takeaway: "Good repos show professionalism",
                },
                {
                  title: "Deploying Portfolio Using GitHub Pages",
                  description: "Your portfolio must be live.",
                  steps: [
                    "Create portfolio repository",
                    "Push portfolio code to GitHub",
                    "Enable GitHub Pages",
                    "Get live URL",
                  ],
                  result: "Publicly accessible portfolio",
                },
                {
                  title: "Updating Portfolio Over Time",
                  description: "Portfolio is never finished.",
                  workflow: [
                    "Add new projects",
                    "Improve existing ones",
                    "Refine UI",
                    "Update skills",
                  ],
                  takeaway: "Portfolio grows with you",
                },
                {
                  title: "Final Review Checklist",
                  description: "Before sharing your portfolio.",
                  checklist: [
                    "All links working",
                    "Live demos accessible",
                    "No console errors",
                    "Responsive on mobile",
                    "Clean UI",
                  ],
                  takeaway: "First impression matters",
                },
                {
                  title: "How Recruiters Evaluate Portfolios",
                  description: "They scan quickly.",
                  theyCheck: [
                    "Live projects",
                    "Code quality",
                    "Consistency",
                    "Problem-solving ability",
                  ],
                  takeaway: "Make it easy to judge you positively",
                },
                {
                  title: "Mindset Shift After This Project",
                  description: "You move from learner to developer.",
                  changes: [
                    "Confidence increases",
                    "You have proof of skills",
                    "Ready for frameworks",
                  ],
                  takeaway: "This is a career milestone",
                },
              ],

              visualStructure: `
Portfolio Website
     ↓
Live Projects
     ↓
GitHub Repos
     ↓
Recruiter / Client
    `,

              commonMistakes: [
                {
                  mistake: "No live demo links",
                  consequence: "Low trust",
                  fix: "Deploy every project",
                },
                {
                  mistake: "Too many weak projects",
                  consequence: "Diluted impression",
                  fix: "Show only best work",
                },
                {
                  mistake: "Broken links",
                  consequence: "Unprofessional image",
                  fix: "Test everything",
                },
              ],

              proTips: [
                "Keep portfolio simple and fast",
                "Focus on fundamentals, not over-design",
                "Update portfolio every 2–3 weeks",
                "Share portfolio confidently",
              ],

              realWorldAnalogy:
                "Your portfolio is your shop window — people decide in seconds whether to step in",
            },

            tasks: [
              "Push all major projects to GitHub",
              "Create portfolio repository",
              "Deploy portfolio on GitHub Pages",
              "Add live demo and source links",
            ],

            examples: [
              "Portfolio with HTML/CSS/JS projects",
              "Deployed GitHub Pages site",
            ],

            useCases: [
              "Job applications",
              "Recruiter outreach",
              "Personal branding",
              "Freelancing opportunities",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "2–3 hours",

            // 🎯 Learning objectives
            objectives: [
              "Create a professional frontend portfolio",
              "Deploy and maintain live projects",
              "Present work confidently",
              "Be ready for job applications",
            ],
          },
        ],
      },
      {
        week: 17,
        weekGoal:
          "Understand modern frontend build tools and package management",
        weekOutcome:
          "Ability to setup, configure, and maintain professional frontend development environments",
        miniProject: "Configured Development Environment with Webpack & Babel",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "NPM Fundamentals",
            subTopics: [
              "What is npm",
              "package.json structure",
              "npm init",
              "npm install",
              "npm scripts",
            ],
            explanation:
              "npm is the default package manager for JavaScript that helps manage project dependencies and scripts.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Initialize a Real npm Project",
                description:
                  "Set up a real npm project exactly the way professionals do.",
                tasks: [
                  "Create a new empty project folder",
                  "Run npm init -y",
                  "Open package.json and read every field",
                  "Identify name, scripts, dependencies",
                ],
                validationChecklist: [
                  "package.json exists",
                  "Project name is valid",
                  "scripts object is present",
                ],
                outcome:
                  "You’ll understand how every modern JavaScript project actually starts.",
              },
              {
                id: "pt-2",
                title: "Dependencies vs DevDependencies Drill",
                description:
                  "Learn how developers decide where a package belongs.",
                tasks: [
                  "Install lodash as a dependency",
                  "Install vite as a devDependency",
                  "Compare dependencies and devDependencies in package.json",
                ],
                qualityRule:
                  "You must be able to explain WHY a package is devDependency.",
                outcome: "You’ll stop blindly installing packages.",
              },
              {
                id: "pt-3",
                title: "node_modules Reality Check",
                description: "Understand why node_modules is never committed.",
                tasks: [
                  "Delete node_modules folder",
                  "Run npm install",
                  "Verify project works again",
                ],
                outcome: "You’ll never fear deleting node_modules again.",
              },
              {
                id: "pt-4",
                title: "npm Scripts Power Usage",
                description: "Use npm scripts the way teams actually do.",
                tasks: [
                  "Add a custom script in package.json",
                  "Run it using npm run",
                  "Modify the script and rerun",
                ],
                mindset: "Scripts standardize workflows across teams",
                outcome:
                  "You’ll understand why npm scripts are critical in real projects.",
              },
            ],

            testingInsight: {
              setupReality:
                "Most project failures happen during setup, not during feature coding.",
              checklist: [
                "Does npm install work on a fresh machine?",
                "Do npm scripts run without errors?",
                "Can the project be recreated without node_modules?",
              ],
              goldenRule: "If project setup breaks, nothing else matters.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is npm?",
                "What is package.json?",
                "Difference between dependencies and devDependencies?",
                "Why is node_modules not pushed to GitHub?",
                "What happens when you run npm install?",
              ],
              strongAnswerPattern:
                "Explain package.json → install process → scripts → reproducibility",
              redFlag:
                "Not knowing what package.json or node_modules is used for.",
            },

            editorGuidance: {
              careerMindset: [
                "Understand tools before frameworks",
                "package.json is project documentation",
                "npm knowledge is non-negotiable for React",
              ],
              seniorAdvice:
                "If you deeply understand npm, onboarding into any JS project becomes easy.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern frontend development heavily relies on external libraries and tools. npm (Node Package Manager) is the system that allows developers to install, manage, update, and run these tools. If Git is how you manage code history, npm is how you manage code dependencies.",

              sections: [
                {
                  title: "What is npm?",
                  description:
                    "npm is a package manager that comes with Node.js and is used to manage JavaScript libraries and tools.",
                  keyPoints: [
                    "Largest package registry in the world",
                    "Used for frontend, backend, and tooling",
                    "Works through command line",
                  ],
                  realWorldAnalogy:
                    "npm is like Play Store for JavaScript packages",
                },
                {
                  title: "Why npm is Important",
                  description:
                    "Modern projects rarely use plain JavaScript only.",
                  benefits: [
                    "Reuse existing solutions",
                    "Standardize project setup",
                    "Save development time",
                    "Required for React and modern frameworks",
                  ],
                  takeaway: "No npm = no modern frontend",
                },
                {
                  title: "Understanding package.json",
                  description: "package.json is the heart of an npm project.",
                  keyFields: [
                    "name – project name",
                    "version – project version",
                    "description – what project does",
                    "scripts – custom commands",
                    "dependencies – required packages",
                    "devDependencies – development-only tools",
                  ],
                  takeaway:
                    "If you understand package.json, you understand the project",
                },
                {
                  title: "npm init – Starting a Project",
                  description: "Initializes a new npm project.",
                  commands: ["npm init", "npm init -y"],
                  explanation:
                    "The -y flag skips questions and creates a default package.json",
                  takeaway: "Always run npm init before installing packages",
                },
                {
                  title: "Installing Packages",
                  description: "Packages can be installed locally or globally.",
                  types: [
                    "Local install → project-specific",
                    "Global install → system-wide tools",
                  ],
                  commands: [
                    "npm install lodash",
                    "npm install vite --save-dev",
                  ],
                  takeaway: "Most frontend packages are installed locally",
                },
                {
                  title: "Dependencies vs DevDependencies",
                  description: "Not all packages are equal.",
                  comparison: [
                    "dependencies → needed in production",
                    "devDependencies → needed only during development",
                  ],
                  example: "React → dependency, Vite → devDependency",
                  takeaway: "This distinction matters in production builds",
                },
                {
                  title: "node_modules Folder (Important)",
                  description: "node_modules stores installed packages.",
                  keyPoints: [
                    "Auto-generated folder",
                    "Should NOT be committed to Git",
                    "Recreated using npm install",
                  ],
                  takeaway: "Never touch node_modules manually",
                },
                {
                  title: "npm scripts – Running Commands",
                  description: "Scripts automate common tasks.",
                  examples: ["npm run start", "npm run build", "npm run dev"],
                  explanation:
                    "Scripts are shortcuts defined inside package.json",
                  takeaway: "Scripts standardize workflows across teams",
                },
                {
                  title: "How npm Fits in Real Projects",
                  description: "Every modern frontend project uses npm.",
                  workflow: [
                    "npm init",
                    "Install dependencies",
                    "Run dev server",
                    "Build for production",
                  ],
                  takeaway: "npm is the foundation of modern tooling",
                },
                {
                  title: "npm vs yarn vs pnpm (Conceptual)",
                  description: "Multiple package managers exist.",
                  note: "npm is enough for learning and professional work",
                  takeaway: "Learn npm first, others later if needed",
                },
              ],

              visualStructure: `
Project
  ├─ package.json
  ├─ node_modules/
  └─ src/
      ↓
npm install
      ↓
Dependencies ready
    `,

              commonMistakes: [
                {
                  mistake: "Committing node_modules",
                  consequence: "Huge repository",
                  fix: "Add node_modules to .gitignore",
                },
                {
                  mistake: "Not understanding scripts",
                  consequence: "Confusion in real projects",
                  fix: "Read package.json scripts",
                },
                {
                  mistake: "Blindly installing packages",
                  consequence: "Bloated project",
                  fix: "Install only what you need",
                },
              ],

              proTips: [
                "Always read package.json of any project",
                "Use npm init -y for quick setup",
                "Treat node_modules as disposable",
                "npm knowledge is mandatory for React",
              ],

              realWorldAnalogy:
                "npm is like a toolbox — you don’t build everything yourself, you use the right tools",
            },

            tasks: [
              "Initialize a project using npm",
              "Explore package.json fields",
              "Install a package locally",
              "Run npm scripts",
            ],

            examples: ["npm init -y", "npm install lodash", "npm run start"],

            useCases: [
              "Managing libraries",
              "Running build commands",
              "Standard project setup",
              "Modern frontend development",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "35–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand npm fundamentals",
              "Work confidently with package.json",
              "Install and manage dependencies",
              "Prepare for React tooling",
            ],
          },
          {
            day: 2,
            topic: "Dependencies & Versioning",
            subTopics: [
              "dependencies vs devDependencies",
              "Semantic versioning",
              "Lock files (package-lock.json)",
              "Updating packages",
              "Removing packages",
            ],
            explanation:
              "Understanding dependency types and versioning prevents bugs and breaking changes in production apps.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Dependency Classification Drill",
                description:
                  "Learn to correctly classify packages like a professional developer.",
                tasks: [
                  "Install react as a dependency",
                  "Install eslint as a devDependency",
                  "Explain why each belongs in its category",
                ],
                validationChecklist: [
                  "react appears in dependencies",
                  "eslint appears in devDependencies",
                  "You can justify the decision",
                ],
                outcome:
                  "You’ll stop misclassifying packages and bloating production builds.",
              },
              {
                id: "pt-2",
                title: "Semantic Versioning Decoding",
                description: "Understand what version numbers actually mean.",
                tasks: [
                  "Pick a dependency version (e.g., ^2.3.1)",
                  "Explain what updates are allowed",
                  "Predict what could break on MAJOR update",
                ],
                qualityRule:
                  "You must explain MAJOR vs MINOR vs PATCH without notes",
                outcome: "You’ll no longer fear dependency updates.",
              },
              {
                id: "pt-3",
                title: "package-lock.json Consistency Test",
                description: "See why lock files are critical in teams.",
                tasks: [
                  "Delete node_modules",
                  "Run npm install",
                  "Compare regenerated structure",
                  "Observe consistency with lock file",
                ],
                outcome:
                  "You’ll understand how teams avoid 'works on my machine' bugs.",
              },
              {
                id: "pt-4",
                title: "Safe Dependency Update Simulation",
                description:
                  "Practice updating dependencies without breaking apps.",
                tasks: [
                  "Run npm outdated",
                  "Update one package only",
                  "Test application after update",
                ],
                mindset:
                  "Dependencies are upgraded intentionally, not casually",
                outcome: "You’ll adopt a production-safe update mindset.",
              },
            ],

            testingInsight: {
              stabilityFocus:
                "Dependency issues often appear only after deployment.",
              checklist: [
                "Does fresh npm install work?",
                "Are exact versions locked?",
                "Does app behave same after reinstall?",
              ],
              goldenRule: "Uncontrolled dependencies cause unpredictable bugs.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is the difference between dependencies and devDependencies?",
                "What is semantic versioning?",
                "Why is package-lock.json important?",
                "Should package-lock.json be committed?",
                "How do you safely update dependencies?",
              ],
              strongAnswerPattern:
                "Explain versioning → lock file → controlled updates",
              redFlag: "Saying you delete package-lock.json to fix issues.",
            },

            editorGuidance: {
              careerMindset: [
                "Dependencies are part of system design",
                "Version control is stability control",
                "Small dependency mistakes scale badly",
              ],
              seniorAdvice:
                "Most production bugs are not logic bugs — they are dependency bugs.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As projects grow, managing dependencies becomes critical. A small version mismatch can break an entire application. This day focuses on how npm manages dependency versions, why lock files exist, and how professional teams keep projects stable across machines.",

              sections: [
                {
                  title: "What Are Dependencies?",
                  description:
                    "Dependencies are external libraries your project relies on.",
                  keyPoints: [
                    "Installed via npm",
                    "Listed in package.json",
                    "Recreated using npm install",
                  ],
                  takeaway:
                    "Dependencies are part of your project’s foundation",
                },
                {
                  title: "dependencies vs devDependencies",
                  description: "Not all dependencies are needed in production.",
                  comparison: [
                    "dependencies → required for the app to run",
                    "devDependencies → required only during development",
                  ],
                  examples: [
                    "react → dependency",
                    "vite, eslint → devDependencies",
                  ],
                  takeaway: "Production builds depend only on dependencies",
                },
                {
                  title: "Why This Distinction Matters",
                  description: "It affects build size and deployment.",
                  benefits: [
                    "Smaller production bundles",
                    "Faster installs",
                    "Cleaner deployment environments",
                  ],
                  takeaway: "Correct dependency type = optimized app",
                },
                {
                  title: "Semantic Versioning (SemVer)",
                  description: "Versions follow a standard format.",
                  format: "MAJOR.MINOR.PATCH (e.g., 2.4.1)",
                  meaning: [
                    "MAJOR → breaking changes",
                    "MINOR → new features (backward compatible)",
                    "PATCH → bug fixes",
                  ],
                  takeaway: "Understanding versions prevents surprises",
                },
                {
                  title: "Version Prefixes (^, ~)",
                  description: "npm uses symbols to control updates.",
                  prefixes: [
                    "^1.2.3 → allows minor & patch updates",
                    "~1.2.3 → allows patch updates only",
                    "1.2.3 → exact version",
                  ],
                  bestPractice: "Default ^ is usually safe",
                },
                {
                  title: "package-lock.json (VERY IMPORTANT)",
                  description: "Lock file freezes exact dependency versions.",
                  keyPoints: [
                    "Ensures same versions for all developers",
                    "Prevents 'works on my machine' bugs",
                    "Auto-generated by npm",
                  ],
                  rule: "Always commit package-lock.json",
                  takeaway:
                    "package.json says WHAT, lock file says EXACTLY WHICH",
                },
                {
                  title: "Why Lock Files Exist",
                  description: "Without lock files, installs can differ.",
                  problemSolved: [
                    "Different versions across machines",
                    "Unexpected breaking changes",
                  ],
                  takeaway: "Lock files guarantee consistency",
                },
                {
                  title: "Updating Packages Safely",
                  description: "Dependencies should be updated carefully.",
                  commands: ["npm outdated", "npm update"],
                  bestPractice: [
                    "Update one package at a time",
                    "Test after updates",
                  ],
                  takeaway: "Never blindly update everything",
                },
                {
                  title: "Removing Packages",
                  description: "Unused dependencies should be removed.",
                  command: "npm uninstall lodash",
                  effects: [
                    "Removes from node_modules",
                    "Updates package.json",
                    "Updates package-lock.json",
                  ],
                  takeaway: "Clean dependencies = clean project",
                },
                {
                  title: "How Teams Manage Dependencies",
                  description: "Professional workflow.",
                  workflow: [
                    "Install exact versions",
                    "Commit lock file",
                    "Review dependency changes",
                  ],
                  takeaway: "Dependency management is a team responsibility",
                },
              ],

              visualStructure: `
package.json → allowed versions
package-lock.json → exact versions
node_modules → installed code
    `,

              commonMistakes: [
                {
                  mistake: "Deleting package-lock.json",
                  consequence: "Inconsistent installs",
                  fix: "Always commit lock file",
                },
                {
                  mistake: "Installing everything as dependency",
                  consequence: "Bloated production build",
                  fix: "Use devDependencies correctly",
                },
                {
                  mistake: "Updating packages without testing",
                  consequence: "Breaking app",
                  fix: "Test after each update",
                },
              ],

              proTips: [
                "Trust the lock file",
                "Update dependencies intentionally",
                "Smaller dependency list = healthier project",
                "Dependency issues are real-world bugs",
              ],

              realWorldAnalogy:
                "Dependencies are ingredients — wrong version can ruin the recipe",
            },

            tasks: [
              "Install a dev dependency",
              "Compare dependency types",
              "Update a package version",
              "Remove unused dependency",
            ],

            examples: [
              "npm install webpack --save-dev",
              "npm uninstall lodash",
              "npm outdated",
              "npm update",
            ],

            useCases: [
              "Optimized production builds",
              "Safe upgrades",
              "Team consistency",
              "React project stability",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "35–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand dependency types",
              "Read and control package versions",
              "Use package-lock.json correctly",
              "Avoid dependency-related bugs",
            ],
          },
          {
            day: 3,
            topic: "Module Bundlers Overview",
            subTopics: [
              "Why bundlers are needed",
              "Webpack vs Vite vs Parcel",
              "Entry and output concepts",
              "Bundling JavaScript modules",
              "Asset bundling overview",
            ],
            explanation:
              "Module bundlers combine multiple files into optimized bundles for browser compatibility and performance.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Bundler Mental Model Exercise",
                description:
                  "Build a clear mental model of how bundlers actually work.",
                tasks: [
                  "Draw a flow from entry file to final bundle",
                  "List all files included via imports",
                  "Explain what the browser finally receives",
                ],
                validationChecklist: [
                  "Entry file clearly identified",
                  "Dependency chain understood",
                  "Difference between source and bundle explained",
                ],
                outcome: "You’ll stop treating bundlers as black boxes.",
              },
              {
                id: "pt-2",
                title: "Bundler Comparison Decision Drill",
                description:
                  "Learn to choose the right bundler like a professional.",
                tasks: [
                  "Compare Webpack, Vite, and Parcel",
                  "Decide which you’d use for a React app",
                  "Explain your decision in 3 points",
                ],
                qualityRule:
                  "Decision must be based on speed, DX, and complexity",
                outcome:
                  "You’ll confidently justify tool choices in interviews.",
              },
              {
                id: "pt-3",
                title: "Entry & Output Tracing Task",
                description: "Understand how bundlers trace your application.",
                tasks: [
                  "Identify entry file in a sample project",
                  "List files pulled into bundle via imports",
                  "Predict output bundle purpose",
                ],
                outcome: "You’ll clearly understand how one file becomes many.",
              },
              {
                id: "pt-4",
                title: "Dev vs Production Build Thinking",
                description: "Adopt production-level build awareness.",
                tasks: [
                  "List differences between dev and prod builds",
                  "Explain why prod builds are unreadable",
                  "Explain why dev builds are faster",
                ],
                mindset: "Build output changes based on environment",
                outcome:
                  "You’ll stop being confused by minified production code.",
              },
            ],

            testingInsight: {
              buildReality: "Most frontend bugs appear only after bundling.",
              checklist: [
                "Does the bundle include all modules?",
                "Are assets resolved correctly?",
                "Does prod build behave differently?",
              ],
              goldenRule:
                "If it works in dev but not in prod, check the bundler.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why do we need bundlers?",
                "What is an entry file?",
                "Difference between development and production builds?",
                "Why is Vite faster than Webpack?",
                "Is npm a bundler?",
              ],
              strongAnswerPattern:
                "Explain problem → bundler role → modern tooling",
              redFlag: "Saying bundlers are just for React or optional tools.",
            },

            editorGuidance: {
              careerMindset: [
                "Bundlers are infrastructure, not magic",
                "Tooling knowledge reduces debugging fear",
                "Concepts matter more than config",
              ],
              seniorAdvice:
                "Once you understand bundlers, React stops feeling scary.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As projects grow, code is split into many files: JavaScript modules, CSS files, images, fonts, etc. Browsers, however, need optimized assets to load fast. Module bundlers solve this gap by processing, optimizing, and bundling everything into browser-ready output. Without bundlers, modern frontend frameworks like React are impossible.",

              sections: [
                {
                  title: "The Problem Bundlers Solve",
                  description:
                    "Browsers were not designed for large modular codebases.",
                  problems: [
                    "Too many JS files",
                    "Slow network requests",
                    "Incompatible JS features",
                    "Unoptimized assets",
                  ],
                  takeaway:
                    "Bundlers turn developer-friendly code into browser-friendly code",
                },
                {
                  title: "What is a Module Bundler?",
                  description:
                    "A module bundler processes project files and outputs optimized bundles.",
                  does: [
                    "Combines JS modules",
                    "Optimizes and minifies code",
                    "Handles CSS, images, fonts",
                    "Prepares code for production",
                  ],
                  takeaway: "Bundlers sit between your code and the browser",
                },
                {
                  title: "Why Bundlers Are Mandatory Today",
                  description: "Modern JavaScript relies on tooling.",
                  reasons: [
                    "ES modules (import/export)",
                    "JSX (React)",
                    "TypeScript",
                    "Code splitting",
                  ],
                  takeaway: "No bundler = no modern frontend",
                },
                {
                  title: "Entry Point Concept",
                  description: "Bundlers start from a single file.",
                  explanation:
                    "The entry file tells the bundler where the application starts.",
                  example: "entry: './src/index.js'",
                  takeaway: "All dependencies are traced from the entry file",
                },
                {
                  title: "Output Concept",
                  description: "Bundlers generate optimized output files.",
                  explanation:
                    "The output defines where and how bundled files are created.",
                  example: "output: { filename: 'bundle.js' }",
                  takeaway: "Output files are what browsers actually load",
                },
                {
                  title: "Bundling JavaScript Modules",
                  description: "Bundlers follow import/export relationships.",
                  flow: [
                    "Entry file imports modules",
                    "Bundler resolves dependencies",
                    "All code merged into bundles",
                  ],
                  takeaway:
                    "You write modular code, browser gets one optimized bundle",
                },
                {
                  title: "Asset Bundling (High-Level)",
                  description: "Bundlers handle more than JavaScript.",
                  assets: ["CSS files", "Images", "Fonts", "SVGs"],
                  takeaway: "Everything becomes part of the build pipeline",
                },
                {
                  title: "Webpack (Conceptual)",
                  description: "Webpack was the industry standard for years.",
                  characteristics: [
                    "Very powerful",
                    "Highly configurable",
                    "Complex setup",
                  ],
                  takeaway: "Webpack is flexible but heavy",
                },
                {
                  title: "Vite (Modern Standard)",
                  description: "Vite is the modern, fast bundler for frontend.",
                  characteristics: [
                    "Extremely fast dev server",
                    "Uses native ES modules",
                    "Minimal configuration",
                  ],
                  takeaway: "Vite is the default choice for React today",
                },
                {
                  title: "Parcel (Zero Config)",
                  description: "Parcel focuses on simplicity.",
                  characteristics: [
                    "Zero configuration",
                    "Good for small projects",
                  ],
                  limitation: "Less control for complex apps",
                },
                {
                  title: "Webpack vs Vite vs Parcel",
                  description: "High-level comparison.",
                  comparison: [
                    "Webpack → powerful, complex",
                    "Vite → fast, modern, recommended",
                    "Parcel → simple, limited",
                  ],
                  takeaway: "Learn Vite first",
                },
                {
                  title: "Development vs Production Bundling",
                  description: "Bundlers behave differently.",
                  differences: [
                    "Dev → fast reload, readable code",
                    "Prod → minified, optimized, compressed",
                  ],
                  takeaway: "Same code, different output",
                },
                {
                  title: "How This Prepares You for React",
                  description: "React relies on bundlers heavily.",
                  connections: [
                    "JSX needs bundling",
                    "Modules need bundling",
                    "Assets need bundling",
                  ],
                  takeaway: "Understanding bundlers removes React fear",
                },
              ],

              visualStructure: `
Your Source Code
 (JS, CSS, Assets)
        ↓
     Bundler
        ↓
 Optimized Bundles
        ↓
      Browser
    `,

              commonMistakes: [
                {
                  mistake: "Trying to configure bundlers too early",
                  consequence: "Overwhelm",
                  fix: "Understand concepts first",
                },
                {
                  mistake: "Confusing bundlers with npm",
                  consequence: "Tooling confusion",
                  fix: "npm installs, bundlers build",
                },
                {
                  mistake: "Ignoring build output",
                  consequence: "Debugging issues",
                  fix: "Understand dev vs prod builds",
                },
              ],

              proTips: [
                "You don’t need to master Webpack",
                "Vite is enough for modern frontend",
                "Focus on concepts, not configuration",
                "Bundlers make React possible",
              ],

              realWorldAnalogy:
                "Bundlers are like a factory — raw materials go in, finished product comes out",
            },

            tasks: [
              "Understand bundler workflow",
              "Identify entry and output files",
              "Compare popular bundlers",
            ],

            examples: [
              "entry: './src/index.js'",
              "output: { filename: 'bundle.js' }",
            ],

            useCases: [
              "Production optimization",
              "Large application structure",
              "Modern JavaScript support",
              "React project setup",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand why bundlers exist",
              "Differentiate Webpack, Vite, Parcel",
              "Understand entry/output concepts",
              "Be ready for React tooling",
            ],
          },
          {
            day: 4,
            topic: "Webpack Fundamentals",
            subTopics: [
              "Webpack configuration file",
              "Entry and output",
              "Loaders concept",
              "Plugins concept",
              "Development vs production mode",
            ],

            explanation:
              "Webpack is a powerful module bundler that transforms modern JavaScript, CSS, and assets into optimized files for the browser using a configurable pipeline of loaders and plugins.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Webpack Config Reading Drill",
                description:
                  "Learn to read and understand real Webpack configurations.",
                tasks: [
                  "Open a sample webpack.config.js",
                  "Identify entry, output, loaders, and plugins",
                  "Explain each section in plain English",
                ],
                validationChecklist: [
                  "Entry file identified",
                  "Loaders purpose understood",
                  "Plugins role explained",
                ],
                outcome: "You’ll stop being intimidated by Webpack configs.",
              },
              {
                id: "pt-2",
                title: "Loader vs Plugin Classification Task",
                description:
                  "Build a crystal-clear distinction between loaders and plugins.",
                tasks: [
                  "List 3 loaders and what they transform",
                  "List 3 plugins and what they enhance",
                  "Explain why loaders run before plugins",
                ],
                qualityRule:
                  "Explanation must include transformation vs enhancement",
                outcome: "You’ll never confuse loaders and plugins again.",
              },
              {
                id: "pt-3",
                title: "Dev vs Prod Mode Comparison Exercise",
                description:
                  "Understand why the same code behaves differently.",
                tasks: [
                  "List features of development mode",
                  "List features of production mode",
                  "Explain why prod code is minified",
                ],
                mindset: "Developer experience ≠ user experience",
                outcome:
                  "You’ll understand build differences instead of fearing them.",
              },
              {
                id: "pt-4",
                title: "Webpack Mental Model Mapping",
                description:
                  "Create a visual mental map of Webpack’s pipeline.",
                tasks: [
                  "Draw the flow: entry → loaders → plugins → output",
                  "Add file types at each stage",
                  "Explain the pipeline verbally",
                ],
                outcome: "Webpack will feel like a system, not chaos.",
              },
            ],

            testingInsight: {
              buildReality:
                "Most Webpack issues surface only after configuration changes.",
              checklist: [
                "Is the correct entry file used?",
                "Are loaders applied in the right order?",
                "Is the correct mode (dev/prod) enabled?",
              ],
              goldenRule:
                "If the build breaks, read the config before changing code.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is Webpack?",
                "What are loaders in Webpack?",
                "Difference between loaders and plugins?",
                "What is entry and output?",
                "Difference between dev and production mode?",
              ],
              strongAnswerPattern:
                "Explain pipeline → transformation → optimization",
              redFlag: "Saying Webpack is outdated and irrelevant.",
            },

            editorGuidance: {
              careerMindset: [
                "Webpack knowledge is for understanding, not daily usage",
                "Most modern tools abstract Webpack internally",
                "Reading configs matters more than writing them",
              ],
              seniorAdvice:
                "If you understand Webpack, every other frontend tool becomes easier.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Webpack was the backbone of modern frontend tooling for many years. While newer tools like Vite are now preferred for most projects, understanding Webpack is still important because many enterprise codebases and interviews rely on it.",

              sections: [
                {
                  title: "What Webpack Actually Does",
                  description:
                    "Webpack builds a dependency graph starting from an entry file.",
                  does: [
                    "Bundles JavaScript modules",
                    "Transforms code using loaders",
                    "Extends functionality using plugins",
                    "Outputs optimized assets",
                  ],
                  takeaway: "Webpack is a build pipeline, not just a bundler",
                },
                {
                  title: "Webpack Configuration File",
                  description: "Webpack is configured using a JavaScript file.",
                  explanation:
                    "webpack.config.js tells Webpack how to process your project.",
                  example: "module.exports = { entry, output }",
                  takeaway: "Everything in Webpack is configuration-driven",
                },
                {
                  title: "Entry Point in Webpack",
                  description:
                    "The entry defines where Webpack starts building the dependency graph.",
                  example: "entry: './src/index.js'",
                  takeaway: "Webpack follows all imports starting from entry",
                },
                {
                  title: "Output in Webpack",
                  description:
                    "The output defines where bundled files are generated.",
                  example: "output: { path, filename }",
                  takeaway: "Only output files are served to the browser",
                },
                {
                  title: "What Are Loaders?",
                  description: "Loaders transform files before bundling.",
                  explanation:
                    "Webpack only understands JavaScript by default. Loaders teach it how to process other files.",
                  commonLoaders: [
                    "babel-loader → modern JS",
                    "css-loader → CSS files",
                    "style-loader → inject CSS",
                    "file-loader / asset modules → images",
                  ],
                  takeaway: "Loaders transform code",
                },
                {
                  title: "Loader Flow (Important)",
                  description: "Loaders run from right to left.",
                  example: "use: ['style-loader', 'css-loader']",
                  takeaway: "Order of loaders matters",
                },
                {
                  title: "What Are Plugins?",
                  description: "Plugins extend Webpack capabilities.",
                  explanation: "Plugins work at a broader level than loaders.",
                  commonPlugins: [
                    "HtmlWebpackPlugin",
                    "DefinePlugin",
                    "MiniCssExtractPlugin",
                  ],
                  takeaway:
                    "Loaders transform files, plugins enhance the build",
                },
                {
                  title: "Development Mode",
                  description: "Optimized for speed and debugging.",
                  features: ["Readable code", "Fast rebuilds", "Source maps"],
                  takeaway: "Dev mode helps developers",
                },
                {
                  title: "Production Mode",
                  description: "Optimized for performance.",
                  features: [
                    "Minified code",
                    "Smaller bundles",
                    "Optimized assets",
                  ],
                  takeaway: "Prod mode helps users",
                },
                {
                  title: "Why Webpack Feels Complex",
                  description: "Webpack is extremely flexible.",
                  reasons: [
                    "Many configuration options",
                    "Manual setup required",
                    "Multiple loaders and plugins",
                  ],
                  takeaway: "Power comes with complexity",
                },
                {
                  title: "Webpack vs Vite (Reality Check)",
                  description: "Modern frontend favors simplicity.",
                  comparison: [
                    "Webpack → powerful, complex",
                    "Vite → fast, simple, modern",
                  ],
                  takeaway: "Learn Webpack concepts, use Vite in practice",
                },
                {
                  title: "Why You Still Learn Webpack",
                  description: "Industry relevance.",
                  reasons: [
                    "Legacy enterprise projects",
                    "Interview discussions",
                    "Understanding modern tooling roots",
                  ],
                  takeaway: "Webpack knowledge = confidence, not daily usage",
                },
              ],

              visualStructure: `
Entry File (index.js)
        ↓
    Loaders
        ↓
     Plugins
        ↓
   Optimized Bundles
        ↓
      Browser
    `,

              commonMistakes: [
                {
                  mistake: "Trying to memorize Webpack config",
                  consequence: "Confusion and burnout",
                  fix: "Understand concepts instead",
                },
                {
                  mistake: "Mixing dev and prod configs",
                  consequence: "Unexpected behavior",
                  fix: "Separate environments logically",
                },
                {
                  mistake: "Overusing plugins",
                  consequence: "Slow builds",
                  fix: "Use only what you need",
                },
              ],

              proTips: [
                "Webpack is not mandatory to master",
                "Concepts > configuration",
                "Vite abstracts most Webpack pain",
                "Understanding Webpack makes React tooling easier",
              ],

              realWorldAnalogy:
                "Webpack is like a customizable factory where you decide every machine and process",
            },

            tasks: [
              "Install webpack and webpack-cli",
              "Create webpack.config.js",
              "Bundle JS files",
            ],

            examples: [
              "npm install webpack webpack-cli --save-dev",
              "module.exports = { entry, output }",
            ],

            useCases: [
              "Enterprise projects",
              "Custom build pipelines",
              "Advanced optimization",
              "Understanding legacy React apps",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand Webpack core concepts",
              "Differentiate loaders and plugins",
              "Know dev vs prod modes",
              "Be confident reading Webpack configs",
            ],
          },
          {
            day: 5,
            topic: "Babel & Transpilation",
            subTopics: [
              "Why Babel is needed",
              "ES6+ to ES5 conversion",
              "Babel presets",
              "Babel loader with Webpack",
              "Browser compatibility",
            ],

            explanation:
              "Babel is a JavaScript transpiler that converts modern JavaScript (ES6+) into backward-compatible code so it can run reliably across different browsers.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "ES6 to ES5 Transpilation Drill",
                description:
                  "Visually understand what Babel actually transforms.",
                tasks: [
                  "Write ES6 code using arrow functions, let/const, template literals",
                  "Manually rewrite it in ES5 syntax",
                  "Compare with Babel-transpiled output",
                ],
                validationChecklist: [
                  "Arrow functions converted",
                  "let/const replaced with var",
                  "Template literals converted",
                ],
                outcome:
                  "You’ll clearly see Babel’s real value instead of treating it as magic.",
              },
              {
                id: "pt-2",
                title: "Preset-env Configuration Exercise",
                description: "Learn how Babel decides what to transform.",
                tasks: [
                  "Configure @babel/preset-env",
                  "Define target browsers conceptually",
                  "Explain why not all syntax is transformed",
                ],
                qualityRule: "Explanation must include browser targeting logic",
                outcome:
                  "You’ll understand how Babel stays minimal and efficient.",
              },
              {
                id: "pt-3",
                title: "Babel vs Polyfill Awareness Task",
                description:
                  "Avoid one of the most common production mistakes.",
                scenarios: [
                  "Code uses fetch",
                  "Code uses Promise",
                  "Code uses optional chaining",
                ],
                tasks: [
                  "Identify what Babel handles",
                  "Identify what Babel does NOT handle",
                  "Explain where polyfills are needed",
                ],
                outcome: "You’ll stop assuming Babel fixes runtime APIs.",
              },
              {
                id: "pt-4",
                title: "Webpack + Babel Pipeline Mapping",
                description:
                  "Build a solid mental model of the build pipeline.",
                tasks: [
                  "Map file flow from source to browser",
                  "Place babel-loader correctly in pipeline",
                  "Explain order of transformation vs bundling",
                ],
                outcome: "You’ll understand builds instead of guessing fixes.",
              },
            ],

            testingInsight: {
              compatibilityReality:
                "Most browser bugs come from missing transpilation or missing polyfills.",
              checklist: [
                "Is the syntax supported by target browsers?",
                "Is Babel configured correctly?",
                "Are runtime APIs assumed to exist?",
              ],
              goldenRule: "Babel fixes syntax, not environment.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why do we need Babel?",
                "What is transpilation?",
                "Difference between Babel and Webpack?",
                "What does @babel/preset-env do?",
                "Does Babel add polyfills?",
              ],
              strongAnswerPattern:
                "Explain problem → syntax gap → transpilation → tooling flow",
              redFlag: "Saying Babel makes code faster or bundles files.",
            },

            editorGuidance: {
              careerMindset: [
                "Babel knowledge is about understanding compatibility",
                "Modern tools hide Babel, but interviews don’t",
                "Syntax support ≠ runtime support",
              ],
              seniorAdvice:
                "If you understand Babel’s limits, you’ll debug production issues faster than most devs.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "JavaScript evolves fast, but browsers update slowly. Babel acts as a bridge between modern JavaScript syntax and older browsers, allowing developers to write future-proof code without breaking users’ experiences.",

              sections: [
                {
                  title: "The Core Problem Babel Solves",
                  description:
                    "Not all browsers understand modern JavaScript features.",
                  problems: [
                    "Older browsers don’t support ES6+ syntax",
                    "Enterprise users may be on outdated browsers",
                    "Modern frameworks rely on latest JS features",
                  ],
                  takeaway: "Modern code ≠ browser support",
                },
                {
                  title: "What Is Transpilation?",
                  description:
                    "Transpilation means converting code from one version to another.",
                  explanation:
                    "Babel converts modern JavaScript into older, widely supported JavaScript.",
                  example: "Arrow functions → function expressions",
                  takeaway: "Same logic, safer syntax",
                },
                {
                  title: "ES6+ to ES5 Conversion",
                  description:
                    "ES6 introduced features like arrow functions, classes, let/const.",
                  convertedFeatures: [
                    "Arrow functions",
                    "Classes",
                    "Template literals",
                    "Optional chaining",
                  ],
                  takeaway: "You write modern JS, Babel handles compatibility",
                },
                {
                  title: "What Babel Does NOT Do (Important)",
                  description: "Babel is often misunderstood.",
                  doesNot: [
                    "Does NOT add missing APIs (like fetch)",
                    "Does NOT bundle files",
                    "Does NOT optimize performance",
                  ],
                  takeaway: "Babel transforms syntax, not runtime behavior",
                },
                {
                  title: "Babel Presets",
                  description: "Presets are predefined Babel configurations.",
                  explanation:
                    "@babel/preset-env automatically decides what syntax to convert based on target browsers.",
                  example: "presets: ['@babel/preset-env']",
                  takeaway: "Preset-env = smart, future-proof choice",
                },
                {
                  title: "Targeting Browsers",
                  description: "Babel can target specific browser versions.",
                  explanation: "This is often configured using browserslist.",
                  takeaway: "Only transform what is necessary",
                },
                {
                  title: "Babel + Webpack Relationship",
                  description:
                    "Webpack bundles files, Babel transforms JavaScript.",
                  flow: [
                    "Webpack sees JS file",
                    "babel-loader sends file to Babel",
                    "Babel transpiles code",
                    "Webpack bundles output",
                  ],
                  takeaway: "Babel = transformer, Webpack = bundler",
                },
                {
                  title: "babel-loader Explained",
                  description: "babel-loader connects Babel with Webpack.",
                  example: "use: 'babel-loader'",
                  takeaway: "Without babel-loader, Webpack cannot use Babel",
                },
                {
                  title: "Modern Reality (Vite & Babel)",
                  description: "Modern tools abstract Babel.",
                  explanation:
                    "Vite uses esbuild and Babel internally, so you rarely configure Babel manually.",
                  takeaway: "Know Babel, don’t fight Babel",
                },
                {
                  title: "Why Babel Still Matters",
                  description: "Even today, Babel knowledge is useful.",
                  reasons: [
                    "Legacy Webpack projects",
                    "Enterprise React apps",
                    "Debugging build issues",
                  ],
                  takeaway: "Babel knowledge = professional confidence",
                },
              ],

              visualStructure: `
Modern JS (ES6+)
        ↓
      Babel
        ↓
 Browser-compatible JS (ES5)
        ↓
     Webpack Bundle
        ↓
       Browser
    `,

              commonMistakes: [
                {
                  mistake: "Thinking Babel adds polyfills automatically",
                  consequence: "Runtime errors in old browsers",
                  fix: "Understand syntax vs API support",
                },
                {
                  mistake: "Over-configuring Babel",
                  consequence: "Complex setup",
                  fix: "Use preset-env",
                },
                {
                  mistake: "Using Babel without understanding why",
                  consequence: "Blind configuration",
                  fix: "Focus on the problem Babel solves",
                },
              ],

              proTips: [
                "Use @babel/preset-env for most projects",
                "Let modern tools handle Babel when possible",
                "Understand Babel for interviews and debugging",
                "Don’t manually optimize Babel configs early",
              ],

              realWorldAnalogy:
                "Babel is like a translator that converts modern language into something everyone understands",
            },

            tasks: [
              "Install Babel",
              "Configure Babel preset",
              "Integrate Babel with Webpack",
            ],

            examples: [
              "npm install @babel/core @babel/preset-env babel-loader --save-dev",
              "presets: ['@babel/preset-env']",
            ],

            useCases: [
              "Legacy browser support",
              "Using modern JS safely",
              "Stable production builds",
              "Enterprise React apps",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "40–50 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand why Babel exists",
              "Differentiate transpilation vs bundling",
              "Know how Babel integrates with Webpack",
              "Be confident reading Babel configs",
            ],
          },
          {
            day: 6,
            topic: "Environment Variables & Build Modes",
            subTopics: [
              "Environment variables",
              "Development vs production builds",
              "process.env usage",
              ".env files",
              "Security best practices",
            ],

            explanation:
              "Environment variables allow applications to behave differently in development, staging, and production without changing the source code or exposing sensitive information.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Environment Separation Drill",
                description:
                  "Understand how the same code behaves differently across environments.",
                tasks: [
                  "Create dev and prod environment variables",
                  "Log values conditionally based on NODE_ENV",
                  "Explain why behavior changes without code changes",
                ],
                validationChecklist: [
                  "Different values for dev vs prod",
                  "NODE_ENV check used correctly",
                  "No hardcoded config values",
                ],
                outcome:
                  "You’ll internalize how professionals manage multiple environments safely.",
              },
              {
                id: "pt-2",
                title: "Frontend Secrets Reality Check",
                description:
                  "Learn what can and cannot be hidden in frontend apps.",
                tasks: [
                  "Add a fake API key in .env",
                  "Build the app",
                  "Inspect bundled code",
                ],
                qualityRule:
                  "You must explain why frontend env variables are visible",
                outcome:
                  "You’ll never treat frontend env variables as secrets again.",
              },
              {
                id: "pt-3",
                title: "Build-Time vs Runtime Understanding",
                description: "Clear one of the biggest beginner confusions.",
                scenarios: [
                  "Change .env value without rebuild",
                  "Change .env value with rebuild",
                ],
                tasks: [
                  "Predict app behavior",
                  "Verify result",
                  "Explain build-time injection",
                ],
                outcome: "You’ll stop expecting env changes to work magically.",
              },
              {
                id: "pt-4",
                title: "Security-First Configuration Task",
                description:
                  "Apply real-world security mindset to frontend config.",
                tasks: [
                  "Move sensitive values out of frontend",
                  "Decide what is safe to expose",
                  "Document env usage clearly",
                ],
                mindset: "Config is public, secrets belong to backend",
                outcome:
                  "You’ll think like a production engineer, not a tutorial coder.",
              },
            ],

            testingInsight: {
              securityReality:
                "Frontend environment variables are visible to anyone who opens DevTools.",
              checklist: [
                "Is this value safe to expose?",
                "Does this variable affect build or runtime?",
                "Is rebuild required after change?",
              ],
              goldenRule: "If it reaches the browser, it is not secret.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are environment variables?",
                "Difference between dev and prod builds?",
                "Are env variables secure in frontend?",
                "What is NODE_ENV used for?",
              ],
              strongAnswerPattern:
                "Explain config separation → build-time injection → security limits",
              redFlag: "Claiming frontend env variables are private or secure.",
            },

            editorGuidance: {
              careerMindset: [
                "Configuration should never be hardcoded",
                "Frontend security has strict limits",
                "Production thinking starts with env handling",
              ],
              seniorAdvice:
                "If you don’t understand env variables, production bugs will feel random.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Every real-world application runs in multiple environments. Environment variables help separate configuration from code so that secrets, API URLs, and feature flags stay safe and manageable.",

              sections: [
                {
                  title: "What Are Environment Variables?",
                  description:
                    "Environment variables are key-value pairs provided by the system or build tool.",
                  examples: [
                    "NODE_ENV=development",
                    "API_URL=https://api.example.com",
                  ],
                  takeaway: "They change behavior without changing code",
                },
                {
                  title: "Why Environment Variables Matter",
                  description: "Hardcoding values is dangerous and unscalable.",
                  problemsWithoutEnv: [
                    "API keys exposed in code",
                    "Same config used everywhere",
                    "Risky production changes",
                  ],
                  takeaway: "Config ≠ code",
                },
                {
                  title: "Development vs Production Builds",
                  description: "Apps behave differently in dev and prod.",
                  differences: [
                    "Dev → logs, debugging, fast builds",
                    "Prod → optimized, minified, secure",
                  ],
                  takeaway: "One codebase, multiple behaviors",
                },
                {
                  title: "NODE_ENV Explained",
                  description:
                    "NODE_ENV is the most important environment variable.",
                  values: ["development", "production", "test"],
                  example: "process.env.NODE_ENV === 'production'",
                  takeaway: "Most tools rely on NODE_ENV",
                },
                {
                  title: "process.env Usage",
                  description:
                    "process.env exposes environment variables to JavaScript.",
                  example: "process.env.API_KEY",
                  importantNote: "Available at build time in frontend apps",
                  takeaway: "Env variables are injected during build",
                },
                {
                  title: ".env Files",
                  description:
                    ".env files store environment variables locally.",
                  commonFiles: [
                    ".env",
                    ".env.local",
                    ".env.development",
                    ".env.production",
                  ],
                  takeaway: "Never commit secrets to Git",
                },
                {
                  title: "Frontend Reality (Very Important)",
                  description: "Frontend env variables are NOT truly secret.",
                  explanation:
                    "Anything bundled into frontend code is visible to users.",
                  takeaway: "Never store real secrets in frontend",
                },
                {
                  title: "Env Variables in Modern Tools",
                  description:
                    "Different tools expose env variables differently.",
                  comparison: [
                    "Webpack → process.env",
                    "Vite → import.meta.env",
                    "React → prefixed variables",
                  ],
                  takeaway: "Tool-specific rules apply",
                },
                {
                  title: "Security Best Practices",
                  description:
                    "Environment variables reduce risk but don’t eliminate it.",
                  rules: [
                    "Never expose private keys",
                    "Use public API keys only",
                    "Use backend for real secrets",
                    "Add .env to .gitignore",
                  ],
                  takeaway: "Frontend security has limits",
                },
                {
                  title: "Common Mistakes",
                  description: "Most beginners misuse env variables.",
                  mistakes: [
                    "Pushing .env to GitHub",
                    "Using secrets in frontend",
                    "Assuming env vars are runtime",
                  ],
                  takeaway: "Env vars are build-time in frontend",
                },
                {
                  title: "Why This Matters for React",
                  description: "React apps heavily rely on env variables.",
                  useCases: [
                    "API base URLs",
                    "Feature flags",
                    "Analytics keys",
                  ],
                  takeaway: "Env mastery = production readiness",
                },
              ],

              visualStructure: `
.env file
   ↓
Build Tool
   ↓
Injected Variables
   ↓
Bundled Frontend Code
   ↓
Browser
    `,

              commonMistakes: [
                {
                  mistake: "Committing .env file",
                  consequence: "Leaked secrets",
                  fix: "Add .env to .gitignore",
                },
                {
                  mistake: "Using real secrets in frontend",
                  consequence: "Security breach",
                  fix: "Move secrets to backend",
                },
                {
                  mistake: "Expecting runtime env changes",
                  consequence: "Unexpected behavior",
                  fix: "Rebuild app for env changes",
                },
              ],

              proTips: [
                "Frontend env vars are public by nature",
                "Use env vars for config, not secrets",
                "Always separate dev and prod configs",
                "This concept is critical for React",
              ],

              realWorldAnalogy:
                "Environment variables are like switchboards that control how the same machine behaves in different rooms",
            },

            tasks: [
              "Create environment variables",
              "Use variables in code",
              "Configure build modes",
            ],

            examples: ["process.env.NODE_ENV", ".env.local"],

            useCases: [
              "API keys management",
              "Multiple environments",
              "Secure configurations",
              "Production-ready React apps",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "35–45 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand environment variables clearly",
              "Differentiate dev vs prod behavior",
              "Avoid common security mistakes",
              "Be ready for React environment handling",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Complete Build Setup",
            subTopics: [
              "Webpack configuration",
              "Babel integration",
              "NPM scripts",
              "Environment setup",
              "Production build",
            ],

            explanation:
              "This mini project simulates a real-world frontend build setup used in companies, combining Webpack, Babel, npm scripts, and environment variables into a complete workflow.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "End-to-End Build Pipeline Setup",
                description:
                  "Create a complete frontend build pipeline from scratch.",
                tasks: [
                  "Initialize project with npm",
                  "Configure webpack.config.js",
                  "Add Babel loader and preset",
                  "Verify build output in dist folder",
                ],
                validationChecklist: [
                  "Build runs without errors",
                  "Bundle generated successfully",
                  "Modern JS transpiled correctly",
                ],
                outcome:
                  "You’ll understand how real frontend projects are built internally.",
              },
              {
                id: "pt-2",
                title: "Environment-Aware Build Challenge",
                description:
                  "Make your build behave differently in dev and prod.",
                tasks: [
                  "Create .env.development and .env.production",
                  "Use environment variables in code",
                  "Toggle behavior using NODE_ENV",
                ],
                qualityRule:
                  "No environment-specific values should be hardcoded",
                outcome: "You’ll learn production-safe configuration patterns.",
              },
              {
                id: "pt-3",
                title: "Production Optimization Drill",
                description:
                  "Experience the difference between dev and prod builds.",
                tasks: [
                  "Generate development build",
                  "Generate production build",
                  "Compare bundle size and readability",
                ],
                mindset:
                  "Readable code is for devs, optimized code is for users",
                outcome:
                  "You’ll clearly understand why production builds exist.",
              },
              {
                id: "pt-4",
                title: "Professional Workflow Simulation",
                description: "Simulate how teams run and build projects daily.",
                tasks: [
                  "Create clean npm scripts",
                  "Run dev server using script",
                  "Generate production build using script",
                  "Explain workflow step-by-step",
                ],
                outcome: "You’ll think in workflows, not random commands.",
              },
            ],

            testingInsight: {
              buildReality:
                "If the build breaks, the app does not ship — no matter how good the code is.",
              checklist: [
                "Does dev build run reliably?",
                "Does prod build generate optimized output?",
                "Are env variables injected correctly?",
              ],
              goldenRule:
                "A reproducible build is more important than fancy code.",
            },

            interviewInsights: {
              commonQuestions: [
                "How does a frontend build process work?",
                "What role does Webpack play?",
                "How does Babel fit into the build?",
                "Difference between dev and production builds?",
              ],
              strongAnswerPattern:
                "Explain source → transpile → bundle → optimize → ship",
              redFlag:
                "Saying you rely on tools without understanding what they do.",
            },

            editorGuidance: {
              careerMindset: [
                "Tooling knowledge separates juniors from professionals",
                "Build failures are production blockers",
                "Understanding the pipeline builds confidence",
              ],
              seniorAdvice:
                "Once you understand the build pipeline, frameworks stop feeling magical.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Before jumping into React, every frontend developer must understand how modern projects are actually built, bundled, and deployed. This project connects all tooling concepts into a single, realistic setup.",

              sections: [
                {
                  title: "Project Goal",
                  description:
                    "Build a production-ready frontend build pipeline.",
                  includes: [
                    "Modern JavaScript support",
                    "Optimized production output",
                    "Environment-based configuration",
                    "Clean developer experience",
                  ],
                  takeaway:
                    "This is how real companies structure frontend builds",
                },
                {
                  title: "Project Folder Structure",
                  description: "A clean structure improves scalability.",
                  structure: `
project/
├── src/
│   ├── index.js
│   └── styles.css
├── dist/
├── .env
├── package.json
├── webpack.config.js
        `,
                  takeaway: "Structure matters more than tools",
                },
                {
                  title: "Webpack Setup",
                  description: "Webpack bundles your application.",
                  responsibilities: [
                    "Define entry point",
                    "Generate output bundle",
                    "Handle assets",
                  ],
                  takeaway: "Webpack is the build backbone",
                },
                {
                  title: "Babel Integration",
                  description: "Babel ensures browser compatibility.",
                  explanation:
                    "babel-loader transforms modern JavaScript before bundling.",
                  takeaway: "Write modern JS, ship safe JS",
                },
                {
                  title: "NPM Scripts",
                  description: "Scripts standardize how projects run.",
                  scripts: [
                    "dev → development build",
                    "build → production build",
                  ],
                  takeaway: "Scripts = consistent workflow",
                },
                {
                  title: "Environment Configuration",
                  description:
                    "Different environments need different behavior.",
                  examples: [
                    "Development → debugging enabled",
                    "Production → optimized output",
                  ],
                  takeaway: "One codebase, multiple environments",
                },
                {
                  title: "Production Build",
                  description: "Production builds focus on performance.",
                  features: [
                    "Minified bundles",
                    "Optimized assets",
                    "No debug logs",
                  ],
                  takeaway:
                    "Production users care about speed, not code readability",
                },
                {
                  title: "Common Real-World Expectations",
                  description: "This setup mirrors company workflows.",
                  expectations: [
                    "Clean Git history",
                    "Repeatable builds",
                    "Environment safety",
                  ],
                  takeaway: "Tooling literacy = job readiness",
                },
                {
                  title: "Modern Reality Check",
                  description: "Most projects today use Vite.",
                  explanation:
                    "React projects abstract most of this complexity.",
                  takeaway:
                    "You learned this to understand what happens under the hood",
                },
              ],

              visualStructure: `
Source Code
    ↓
 Babel (Transpile)
    ↓
 Webpack (Bundle)
    ↓
 Optimized Build
    ↓
 Browser
    `,

              commonMistakes: [
                {
                  mistake: "Overengineering config",
                  consequence: "Complex setup",
                  fix: "Keep config minimal",
                },
                {
                  mistake: "Pushing dist folder to Git",
                  consequence: "Messy repo",
                  fix: "Ignore build outputs",
                },
                {
                  mistake: "Hardcoding environment values",
                  consequence: "Security risks",
                  fix: "Use env variables",
                },
              ],

              proTips: [
                "Understand concepts, not configs",
                "This setup prepares you for React tooling",
                "Vite replaces most of this complexity",
                "Interviewers value build understanding",
              ],

              realWorldAnalogy:
                "This project is like assembling a factory before starting production",
            },

            tasks: [
              "Setup project with Webpack",
              "Configure Babel",
              "Add dev & build scripts",
              "Generate production build",
            ],

            examples: ["npm run dev", "npm run build"],

            useCases: [
              "Real company workflows",
              "Job interviews",
              "Production-ready projects",
              "React project foundation",
            ],

            editorType: null,

            // ⏱️ Estimated time
            estimatedTime: "60–90 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand complete frontend build flow",
              "Connect Webpack, Babel, and npm",
              "Differentiate dev and prod builds",
              "Be confident before starting React",
            ],
          },
        ],
      },
      {
        week: 18,
        weekGoal:
          "Master advanced JavaScript concepts used in real-world applications and interviews",
        weekOutcome:
          "Ability to write optimized, reusable, and scalable JavaScript code",
        miniProject: "Advanced JavaScript Utility Library",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Closures & Lexical Scope",
            subTopics: [
              "Lexical environment",
              "Closure creation",
              "Private variables using closures",
              "Use cases of closures",
              "Memory implications",
            ],

            explanation:
              "Closures allow functions to remember and access variables from their outer (lexical) scope even after that outer function has finished executing.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Closure Execution Visualization",
                description:
                  "Build mental clarity on how closures retain variables.",
                tasks: [
                  "Write an outer function with a variable",
                  "Return an inner function",
                  "Call the inner function multiple times",
                  "Log and explain why values persist",
                ],
                validationChecklist: [
                  "Outer function finishes execution",
                  "Inner function still accesses variables",
                  "Correct explanation of lexical scope",
                ],
                outcome:
                  "You’ll stop treating closures as magic and start reasoning about them.",
              },
              {
                id: "pt-2",
                title: "Private State Builder",
                description: "Use closures to simulate private variables.",
                tasks: [
                  "Create a function with private data",
                  "Expose controlled getters/setters",
                  "Ensure private variable is inaccessible directly",
                ],
                qualityRule:
                  "No direct access to internal variables from outside",
                outcome:
                  "You’ll understand how closures enable encapsulation in JS.",
              },
              {
                id: "pt-3",
                title: "Closure Bug Debugging Drill",
                description: "Identify and fix common closure-related bugs.",
                scenarios: [
                  "Closure inside a loop",
                  "Async callback capturing wrong value",
                ],
                tasks: [
                  "Predict incorrect output",
                  "Fix using let or function scope",
                  "Explain why the fix works",
                ],
                outcome: "You’ll gain confidence debugging real-world JS bugs.",
              },
              {
                id: "pt-4",
                title: "React Mindset Closure Mapping",
                description: "Connect closures directly to React behavior.",
                tasks: [
                  "Explain how state is preserved in hooks",
                  "Explain stale closures conceptually",
                  "Map closure behavior to React event handlers",
                ],
                mindset: "React bugs are often closure bugs in disguise",
                outcome:
                  "You’ll approach React with deeper conceptual clarity.",
              },
            ],

            testingInsight: {
              conceptualReality:
                "Closures are tested indirectly through behavior, not syntax.",
              checklist: [
                "Does the function remember previous values?",
                "Is state isolated per function call?",
                "Does async code capture expected values?",
              ],
              goldenRule:
                "If you can predict output without running code, you understand closures.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is a closure in JavaScript?",
                "What is lexical scope?",
                "How do closures help in real applications?",
                "Why are closures important in React?",
              ],
              strongAnswerPattern:
                "Define lexical scope → explain function memory → give real use case",
              redFlag:
                "Explaining closures without mentioning lexical environment.",
            },

            editorGuidance: {
              careerMindset: [
                "Closures are a core JavaScript concept",
                "Most senior JS bugs involve closures",
                "Understanding closures unlocks async JS",
              ],
              seniorAdvice:
                "If closures feel confusing, slow down — this concept compounds everywhere.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Closures are one of the most powerful and confusing concepts in JavaScript. They are not a special syntax, but a natural result of how JavaScript scopes and functions work.",

              sections: [
                {
                  title: "What Is Lexical Scope?",
                  description:
                    "Lexical scope means scope is determined by where code is written, not where it is called.",
                  explanation:
                    "Inner functions can access variables defined in their outer scope.",
                  example:
                    "function outer() { let x = 10; function inner() { console.log(x); } }",
                  takeaway: "Scope is decided at write time",
                },
                {
                  title: "What Is a Closure?",
                  description:
                    "A closure is created when a function remembers variables from its lexical scope.",
                  explanation:
                    "Even after the outer function finishes execution, the inner function still has access to its variables.",
                  takeaway: "Function + lexical environment = closure",
                },
                {
                  title: "Closure Creation Flow",
                  description: "Closures are created automatically.",
                  steps: [
                    "Outer function executes",
                    "Inner function is defined",
                    "Inner function is returned or referenced",
                    "Outer function finishes",
                    "Inner function still accesses outer variables",
                  ],
                  takeaway: "Closures are not manually created",
                },
                {
                  title: "Classic Closure Example – Counter",
                  description:
                    "Closures are commonly used for maintaining state.",
                  example: `
function counter() {
  let count = 0;
  return function () {
    return ++count;
  };
}
const increment = counter();
increment(); // 1
increment(); // 2
        `,
                  takeaway: "Closures preserve state",
                },
                {
                  title: "Private Variables Using Closures",
                  description: "Closures enable data hiding.",
                  explanation:
                    "Variables inside a function cannot be accessed directly from outside.",
                  takeaway: "Closures simulate private variables",
                },
                {
                  title: "Closures in Real Applications",
                  description: "Closures are used everywhere.",
                  useCases: [
                    "Event handlers",
                    "Debouncing & throttling",
                    "Factory functions",
                    "Custom hooks in React",
                  ],
                  takeaway: "Closures power modern JavaScript",
                },
                {
                  title: "Closures in React (VERY IMPORTANT)",
                  description: "React heavily relies on closures.",
                  examples: [
                    "useState keeps state via closures",
                    "Event handlers capture state",
                    "useEffect dependencies depend on closures",
                  ],
                  takeaway: "Understanding closures = understanding React",
                },
                {
                  title: "Memory Implications",
                  description: "Closures keep variables in memory.",
                  explanation:
                    "If closures are misused, memory leaks can occur.",
                  takeaway: "Closures are powerful but must be used carefully",
                },
                {
                  title: "Common Closure Mistakes",
                  description: "Most bugs come from misunderstanding scope.",
                  mistakes: [
                    "Using var in loops",
                    "Unexpected values in async code",
                    "Stale closures in React",
                  ],
                  takeaway: "Scope clarity prevents bugs",
                },
              ],

              visualStructure: `
Outer Function Scope
    ↓
Inner Function
    ↓
Closure Created
    ↓
Variables Persist in Memory
    `,

              commonMistakes: [
                {
                  mistake:
                    "Assuming variables are destroyed after function ends",
                  consequence: "Unexpected retained values",
                  fix: "Understand closure retention",
                },
                {
                  mistake: "Using var inside loops",
                  consequence: "Incorrect values",
                  fix: "Use let or closures",
                },
                {
                  mistake: "Ignoring stale closures in React",
                  consequence: "Bugs in state updates",
                  fix: "Understand dependency arrays",
                },
              ],

              proTips: [
                "Closures are not optional knowledge",
                "Every React developer uses closures daily",
                "Closures explain many JS 'magic' behaviors",
                "If you understand closures, async JS becomes easier",
              ],

              realWorldAnalogy:
                "A closure is like a backpack that a function carries, holding variables from where it was created",
            },

            tasks: [
              "Create a counter using closure",
              "Build private variables with closure",
              "Understand closure execution flow",
            ],

            examples: [
              "function counter() { let count = 0; return () => ++count; }",
            ],

            useCases: [
              "Data hiding",
              "Factory functions",
              "State management",
              "React hooks internals",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand lexical scope clearly",
              "Explain closures confidently in interviews",
              "Apply closures to real problems",
              "Be React-ready conceptually",
            ],
          },
          {
            day: 2,
            topic: "Higher-Order Functions",
            subTopics: [
              "Functions as arguments",
              "Functions returning functions",
              "map, filter, reduce revisited",
              "Custom higher-order functions",
              "Functional programming mindset",
            ],

            explanation:
              "Higher-order functions (HOFs) are functions that either take other functions as arguments or return functions as results. They enable reusable, composable, and declarative code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Build Your Own map() Function",
                description:
                  "Recreate Array.map to deeply understand how HOFs work internally.",
                tasks: [
                  "Create a custom myMap function",
                  "Accept an array and callback",
                  "Return a new transformed array",
                  "Ensure original array is not mutated",
                ],
                validationChecklist: [
                  "Callback is invoked for each item",
                  "New array is returned",
                  "Original array remains unchanged",
                ],
                outcome:
                  "You’ll stop treating map() as magic and start understanding its internals.",
              },
              {
                id: "pt-2",
                title: "Reusable Validator Factory",
                description:
                  "Use HOFs to generate configurable validation functions.",
                tasks: [
                  "Create a function that accepts validation rules",
                  "Return a function that validates input",
                  "Reuse the same validator for multiple inputs",
                ],
                qualityRule:
                  "Validation logic must be configurable via function arguments",
                outcome:
                  "You’ll understand how libraries build reusable logic using HOFs.",
              },
              {
                id: "pt-3",
                title: "Refactor Imperative Code to Declarative",
                description: "Convert loop-based logic into HOF-based logic.",
                tasks: [
                  "Take a for-loop based transformation",
                  "Rewrite using map/filter/reduce",
                  "Compare readability and clarity",
                ],
                mindset: "Less control flow, more intent",
                outcome:
                  "You’ll think like a functional programmer, not just a coder.",
              },
              {
                id: "pt-4",
                title: "React Callback Pattern Mapping",
                description: "Map HOF concepts directly to React usage.",
                tasks: [
                  "Explain setState(prev => prev + 1)",
                  "Explain why callbacks receive previous state",
                  "Relate this to HOFs + closures",
                ],
                mindset: "React APIs are higher-order by design",
                outcome:
                  "You’ll understand React patterns instead of memorizing them.",
              },
            ],

            testingInsight: {
              conceptualReality:
                "Higher-order functions are tested by behavior, not structure.",
              checklist: [
                "Is the callback executed correctly?",
                "Is data transformed without mutation?",
                "Does the function return expected output?",
              ],
              goldenRule:
                "If replacing a callback changes behavior, the HOF is doing its job.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is a higher-order function?",
                "Can you give real-world examples of HOFs?",
                "How are map, filter, reduce higher-order functions?",
                "Why does React use callbacks so heavily?",
              ],
              strongAnswerPattern:
                "Define HOF → give simple example → connect to real use case",
              redFlag:
                "Explaining HOFs without mentioning functions as first-class citizens.",
            },

            editorGuidance: {
              careerMindset: [
                "HOFs improve reusability and clarity",
                "Most modern JS APIs are functional",
                "React rewards functional thinking",
              ],
              seniorAdvice:
                "If your code is full of loops and conditionals, a higher-order function might simplify it.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In JavaScript, functions are first-class citizens. This means functions can be treated like values — passed around, returned, and composed. Higher-order functions leverage this power to create clean and scalable logic.",

              sections: [
                {
                  title: "Functions as First-Class Citizens",
                  description:
                    "JavaScript treats functions like any other value.",
                  explanation:
                    "You can store functions in variables, pass them as arguments, and return them from other functions.",
                  takeaway: "Functions are values in JavaScript",
                },
                {
                  title: "What Is a Higher-Order Function?",
                  description:
                    "A higher-order function works with other functions.",
                  definition: [
                    "Takes a function as an argument",
                    "OR returns a function",
                  ],
                  example: "function greet(fn) { fn(); }",
                  takeaway: "HOF = function + function",
                },
                {
                  title: "Functions as Arguments",
                  description: "Passing behavior into a function.",
                  explanation:
                    "This allows functions to be generic and reusable.",
                  example: `
function calculate(a, b, operation) {
  return operation(a, b);
}
calculate(2, 3, (x, y) => x + y);
        `,
                  takeaway: "Behavior can be injected",
                },
                {
                  title: "Functions Returning Functions",
                  description:
                    "This pattern enables configuration and partial application.",
                  example: `
const multiply = x => y => x * y;
const double = multiply(2);
double(5); // 10
        `,
                  takeaway: "Closures + HOFs = power",
                },
                {
                  title: "HOFs and Closures (Connection)",
                  description: "Most HOFs rely on closures.",
                  explanation:
                    "Returned functions remember variables from their creation scope.",
                  takeaway: "HOFs often create closures",
                },
                {
                  title: "map(), filter(), reduce() Revisited",
                  description: "These are built-in higher-order functions.",
                  explanation:
                    "They accept callback functions to process data declaratively.",
                  examples: [
                    "map → transform data",
                    "filter → select data",
                    "reduce → accumulate data",
                  ],
                  takeaway: "Array methods are HOFs",
                },
                {
                  title: "Custom Higher-Order Function Example",
                  description: "Creating your own HOF improves abstraction.",
                  example: `
function withLogging(fn) {
  return function (...args) {
    console.log('Calling function');
    return fn(...args);
  };
}
        `,
                  takeaway: "HOFs wrap behavior",
                },
                {
                  title: "Functional Programming Mindset",
                  description: "Focus on what to do, not how to do it.",
                  principles: [
                    "Immutability",
                    "Pure functions",
                    "Declarative code",
                  ],
                  takeaway: "Cleaner code, fewer bugs",
                },
                {
                  title: "Higher-Order Functions in React (VERY IMPORTANT)",
                  description: "React heavily uses HOF patterns.",
                  examples: [
                    "setState(prev => prev + 1)",
                    "Custom hooks returning functions",
                    "Event handlers as callbacks",
                  ],
                  takeaway: "React = functional programming",
                },
                {
                  title: "When NOT to Use HOFs",
                  description: "Overuse can reduce readability.",
                  warnings: [
                    "Too many nested functions",
                    "Hard-to-debug closures",
                  ],
                  takeaway: "Balance abstraction and clarity",
                },
              ],

              visualStructure: `
Input Data
   ↓
Higher-Order Function
   ↓
Callback Function
   ↓
Transformed Output
    `,

              commonMistakes: [
                {
                  mistake: "Confusing callbacks with execution",
                  consequence: "Unexpected behavior",
                  fix: "Pass function reference, not function call",
                },
                {
                  mistake: "Over-nesting arrow functions",
                  consequence: "Unreadable code",
                  fix: "Break logic into named functions",
                },
                {
                  mistake: "Mutating data inside HOFs",
                  consequence: "Side effects",
                  fix: "Use immutable patterns",
                },
              ],

              proTips: [
                "If you understand HOFs, React becomes natural",
                "Most JS libraries are built on HOFs",
                "Prefer map/filter/reduce over loops",
                "Readable HOFs beat clever HOFs",
              ],

              realWorldAnalogy:
                "A higher-order function is like a machine that accepts other machines to customize its behavior",
            },

            tasks: [
              "Create a custom map function",
              "Build reusable validators",
              "Refactor loops using HOFs",
            ],

            examples: ["const multiply = x => y => x * y;"],

            useCases: [
              "Reusable utilities",
              "Data transformations",
              "Cleaner codebases",
              "React hooks and callbacks",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "40–55 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand higher-order functions clearly",
              "Create custom HOFs confidently",
              "Apply functional programming patterns",
              "Be fully React-ready conceptually",
            ],
          },
          {
            day: 3,
            topic: "this Keyword, Call, Apply & Bind",
            subTopics: [
              "Global vs function context",
              "this in arrow functions",
              "call, apply, bind methods",
              "Method borrowing",
              "Common pitfalls",
            ],

            explanation:
              "`this` refers to the execution context of a function. Its value is decided at runtime based on how a function is called, not where it is written.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "`this` Binding Prediction Drill",
                description:
                  "Train yourself to predict `this` without running the code.",
                tasks: [
                  "Write functions called in different ways",
                  "Predict what `this` will point to",
                  "Run the code and verify",
                ],
                validationChecklist: [
                  "Correctly identify call-site",
                  "Explain why `this` has that value",
                  "No assumptions based on definition location",
                ],
                outcome:
                  "You’ll stop guessing and start reasoning about `this`.",
              },
              {
                id: "pt-2",
                title: "Fix Broken `this` in Callbacks",
                description:
                  "Debug real-world `this` bugs caused by callbacks.",
                tasks: [
                  "Create an object with a method",
                  "Pass method as a callback",
                  "Fix broken `this` using bind or arrow function",
                ],
                qualityRule: "Final solution must not rely on global variables",
                outcome: "You’ll understand why callbacks often lose `this`.",
              },
              {
                id: "pt-3",
                title: "Method Borrowing Practice",
                description: "Reuse methods across objects using call/apply.",
                tasks: [
                  "Create two objects with shared logic",
                  "Use call/apply to borrow a method",
                  "Explain why borrowing works",
                ],
                mindset: "Functions don’t belong to objects — execution does",
                outcome:
                  "You’ll see how flexible JavaScript functions really are.",
              },
              {
                id: "pt-4",
                title: "React Class vs Hook Mindset Shift",
                description: "Understand how React removed `this` problems.",
                tasks: [
                  "Explain why class components needed bind",
                  "Explain how hooks avoid `this`",
                  "Relate this to closures",
                ],
                mindset: "React hooks replaced `this` with closures",
                outcome:
                  "You’ll clearly understand why modern React avoids `this`.",
              },
            ],

            testingInsight: {
              conceptualReality:
                "`this` bugs are behavioral, not syntax-related.",
              checklist: [
                "Is the function called as a method or standalone?",
                "Is strict mode affecting `this`?",
                "Is arrow function capturing lexical `this`?",
              ],
              goldenRule: "Always trace the call-site before debugging `this`.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is `this` in JavaScript?",
                "How does `this` behave in arrow functions?",
                "Difference between call, apply, and bind?",
                "Why did React move away from `this`?",
              ],
              strongAnswerPattern:
                "Explain call-site → give example → mention arrow function behavior",
              redFlag: "Saying `this` refers to where the function is defined.",
            },

            editorGuidance: {
              careerMindset: [
                "`this` is one of the biggest JS interview filters",
                "Most legacy code uses `this` heavily",
                "Hooks reduced `this` complexity, not JS itself",
              ],
              seniorAdvice:
                "If `this` feels confusing, slow down and trace the call — JavaScript is never guessing.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "`this` is one of the most misunderstood parts of JavaScript because its value changes depending on how a function is invoked. Once you truly understand `this`, debugging and framework logic (especially React) becomes far easier.",

              sections: [
                {
                  title: "What Is `this`?",
                  description:
                    "`this` is an implicit parameter provided by JavaScript.",
                  explanation:
                    "It points to the object that is responsible for calling the function.",
                  takeaway: "`this` depends on the call site",
                },
                {
                  title: "Global Context",
                  description: "When `this` is used outside any function.",
                  behavior: ["Browser → `window`", "Strict mode → `undefined`"],
                  example: "console.log(this);",
                  takeaway: "Global `this` is unsafe to rely on",
                },
                {
                  title: "this Inside Regular Functions",
                  description: "`this` depends on how the function is called.",
                  examples: [
                    "Function call → undefined (strict mode)",
                    "Method call → object before dot",
                  ],
                  takeaway: "`this` is dynamic",
                },
                {
                  title: "this Inside Objects (Methods)",
                  description: "When a function is called as a method.",
                  example: `
const user = {
  name: 'Alex',
  greet() {
    return this.name;
  }
};
        `,
                  takeaway: "`this` points to the object calling the method",
                },
                {
                  title: "this in Arrow Functions (VERY IMPORTANT)",
                  description: "Arrow functions do NOT have their own `this`.",
                  explanation: "They inherit `this` from their lexical scope.",
                  example: `
const obj = {
  name: 'Sam',
  greet: () => console.log(this.name)
};
        `,
                  takeaway:
                    "Arrow functions capture `this`, they don’t bind it",
                },
                {
                  title: "Why Arrow Functions Exist",
                  description: "Arrow functions solve common `this` issues.",
                  benefits: ["No need for bind", "Predictable context"],
                  takeaway: "Arrow functions reduce `this` bugs",
                },
                {
                  title: "call() Method",
                  description:
                    "Immediately invokes a function with a specified `this`.",
                  example: "func.call(obj, arg1, arg2)",
                  takeaway: "call = execute now",
                },
                {
                  title: "apply() Method",
                  description:
                    "Same as call, but arguments are passed as an array.",
                  example: "func.apply(obj, [arg1, arg2])",
                  takeaway: "apply = call with array",
                },
                {
                  title: "bind() Method",
                  description:
                    "Returns a new function with permanently bound `this`.",
                  example: "const boundFn = fn.bind(obj)",
                  takeaway: "bind = execute later",
                },
                {
                  title: "Method Borrowing",
                  description: "Using another object’s method.",
                  example: `
const user1 = { name: 'A' };
const user2 = { name: 'B' };
function greet() { return this.name; }
greet.call(user2);
        `,
                  takeaway: "Functions are reusable",
                },
                {
                  title: "this in Event Handlers",
                  description: "`this` often causes bugs in events.",
                  behavior: [
                    "Normal function → element",
                    "Arrow function → outer scope",
                  ],
                  takeaway: "Choose function type carefully",
                },
                {
                  title: "this in React (CRITICAL)",
                  description: "Modern React avoids `this` almost completely.",
                  explanation: [
                    "Functional components use closures",
                    "Class components required binding",
                  ],
                  takeaway: "Hooks removed most `this` pain",
                },
                {
                  title: "Common `this` Pitfalls",
                  description: "Most bugs come from assumptions.",
                  pitfalls: [
                    "Losing `this` in callbacks",
                    "Using arrow functions incorrectly",
                    "Forgetting to bind methods",
                  ],
                  takeaway: "Never assume `this`",
                },
              ],

              visualStructure: `
Call Site
   ↓
this Binding
   ↓
Function Execution
   ↓
Context Used
    `,

              commonMistakes: [
                {
                  mistake: "Assuming `this` refers to the object",
                  consequence: "Undefined or wrong value",
                  fix: "Check how the function is called",
                },
                {
                  mistake: "Using arrow functions as methods",
                  consequence: "`this` becomes undefined",
                  fix: "Use normal functions for methods",
                },
                {
                  mistake: "Forgetting bind in callbacks",
                  consequence: "Broken logic",
                  fix: "Use bind or arrow functions",
                },
              ],

              proTips: [
                "`this` is runtime-bound, not compile-time",
                "Arrow functions simplify `this` handling",
                "Prefer functional patterns over `this`",
                "React Hooks avoid most `this` issues",
              ],

              realWorldAnalogy:
                "`this` is like the speaker in a conversation — it depends on who is talking, not what is being said",
            },

            tasks: [
              "Fix broken `this` examples",
              "Use bind in event handlers",
              "Borrow methods using call",
            ],

            examples: [
              "func.call(obj, arg1, arg2)",
              "const boundFn = fn.bind(context)",
            ],

            useCases: [
              "Event handling",
              "Reusable functions",
              "Framework internals",
              "Legacy React class components",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "50–65 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand `this` in all contexts",
              "Use call, apply, bind correctly",
              "Avoid common `this` bugs",
              "Be confident in interviews and React",
            ],
          },
          {
            day: 4,
            topic: "Prototypes & Inheritance",
            subTopics: [
              "Prototype chain",
              "__proto__ vs prototype",
              "Constructor functions",
              "Prototypal inheritance",
              "ES6 class vs prototype",
            ],

            explanation:
              "JavaScript uses prototype-based inheritance, where objects inherit properties and methods from other objects via the prototype chain, not from classes like traditional OOP languages.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Prototype Chain Tracing Drill",
                description:
                  "Visually trace how JavaScript resolves properties through the prototype chain.",
                tasks: [
                  "Create an object with no direct properties",
                  "Access properties from its prototype",
                  "Manually trace lookup order until null",
                ],
                validationChecklist: [
                  "Correctly explain lookup order",
                  "Identify where property is found",
                  "Explain why lookup stops at null",
                ],
                outcome:
                  "You’ll stop treating prototype lookup as magic and start seeing it as a deterministic process.",
              },
              {
                id: "pt-2",
                title: "`__proto__` vs `prototype` Reality Check",
                description:
                  "Eliminate one of the most common JavaScript confusions forever.",
                tasks: [
                  "Create a constructor function",
                  "Inspect constructor.prototype",
                  "Inspect instance.__proto__",
                  "Prove they point to the same object",
                ],
                qualityRule:
                  "You must explain this without memorizing definitions",
                outcome:
                  "You’ll never confuse `__proto__` and `prototype` again.",
              },
              {
                id: "pt-3",
                title: "Memory Optimization with Prototypes",
                description:
                  "Understand why prototypes exist from a performance perspective.",
                tasks: [
                  "Add method inside constructor",
                  "Add same method on prototype",
                  "Create multiple instances",
                  "Compare memory behavior conceptually",
                ],
                mindset: "Shared behavior belongs on the prototype",
                outcome:
                  "You’ll understand why senior developers care about prototypes.",
              },
              {
                id: "pt-4",
                title: "Class vs Prototype Under the Hood",
                description:
                  "Demystify ES6 classes by mapping them to prototypes.",
                tasks: [
                  "Write a class-based example",
                  "Rewrite it using constructor + prototype",
                  "Compare behavior and output",
                ],
                mindset:
                  "Classes don’t change JavaScript — they hide prototypes",
                outcome: "You’ll read class-based code with internal clarity.",
              },
            ],

            testingInsight: {
              coreReality: "Prototype bugs are lookup bugs, not syntax bugs.",
              checklist: [
                "Is the property on the object itself?",
                "Is it coming from the prototype?",
                "Was the prototype modified accidentally?",
              ],
              goldenRule:
                "If behavior is unexpected, inspect the prototype chain.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is the prototype chain?",
                "Difference between __proto__ and prototype?",
                "How does inheritance work in JavaScript?",
                "Are ES6 classes real classes?",
              ],
              strongAnswerPattern:
                "Explain delegation → explain prototype chain → mention class as syntax sugar",
              redFlag:
                "Saying JavaScript uses classical inheritance like Java.",
            },

            editorGuidance: {
              careerMindset: [
                "Prototypes explain 80% of JavaScript internals",
                "Frameworks rely heavily on prototype behavior",
                "Misunderstanding prototypes leads to silent bugs",
              ],
              seniorAdvice:
                "Once prototypes click, JavaScript stops feeling weird and starts feeling logical.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Unlike Java or C++, JavaScript does not use classical inheritance internally. Instead, it uses prototypes. Even ES6 classes are just syntactic sugar over prototypes.",

              sections: [
                {
                  title: "What Is a Prototype?",
                  description:
                    "Every JavaScript object has an internal link to another object called its prototype.",
                  explanation:
                    "If a property is not found on the object, JavaScript looks for it in the prototype.",
                  takeaway: "Objects inherit from objects, not classes",
                },
                {
                  title: "Prototype Chain",
                  description:
                    "JavaScript searches properties up the prototype chain.",
                  flow: [
                    "Object → Prototype",
                    "Prototype → Prototype of prototype",
                    "Ends at null",
                  ],
                  takeaway: "Property lookup travels upward",
                },
                {
                  title: "__proto__ vs prototype (VERY IMPORTANT)",
                  description:
                    "These are commonly confused but very different.",
                  comparison: [
                    "__proto__ → exists on every object (internal link)",
                    "prototype → exists on constructor functions",
                  ],
                  takeaway:
                    "__proto__ links objects, prototype defines inheritance",
                },
                {
                  title: "Constructor Functions",
                  description:
                    "Before ES6 classes, constructor functions were used.",
                  example: `
function Person(name) {
  this.name = name;
}
const p1 = new Person('Alex');
        `,
                  explanation:
                    "The new keyword links the object to the constructor’s prototype.",
                  takeaway: "Constructors + prototypes = inheritance",
                },
                {
                  title: "How new Keyword Works",
                  description: "new does more than object creation.",
                  steps: [
                    "Creates empty object",
                    "Sets __proto__ to constructor.prototype",
                    "Binds this",
                    "Returns object",
                  ],
                  takeaway: "new wires the prototype chain",
                },
                {
                  title: "Adding Methods via Prototype",
                  description: "Shared methods should go on the prototype.",
                  example: `
Person.prototype.greet = function () {
  return 'Hello ' + this.name;
};
        `,
                  takeaway: "Prototype methods save memory",
                },
                {
                  title: "Prototypal Inheritance",
                  description: "Objects can inherit from other objects.",
                  explanation:
                    "Child objects delegate behavior to parent objects.",
                  takeaway: "Inheritance is delegation in JS",
                },
                {
                  title: "ES6 Classes vs Prototypes",
                  description: "Classes are syntax sugar.",
                  truth: [
                    "class → function",
                    "methods → prototype",
                    "extends → prototype chain",
                  ],
                  takeaway: "Classes do NOT change JS inheritance model",
                },
                {
                  title: "Why Prototypes Matter in Real Life",
                  description: "Many bugs and behaviors come from prototypes.",
                  examples: [
                    "Array methods (map, filter)",
                    "Function methods (bind, call)",
                    "DOM objects",
                  ],
                  takeaway: "Everything uses prototypes",
                },
                {
                  title: "Prototypes in React Context",
                  description:
                    "Modern React hides prototypes, but they still exist.",
                  explanation: [
                    "JS language features rely on prototypes",
                    "Class components are prototype-based",
                  ],
                  takeaway: "Knowing prototypes helps debugging",
                },
                {
                  title: "Common Prototype Mistakes",
                  description: "Prototype misuse causes subtle bugs.",
                  mistakes: [
                    "Adding methods inside constructor",
                    "Modifying built-in prototypes",
                    "Confusing __proto__ and prototype",
                  ],
                  takeaway: "Use prototypes carefully",
                },
              ],

              visualStructure: `
Object
  ↓ __proto__
Prototype
  ↓
Prototype
  ↓
null
    `,

              commonMistakes: [
                {
                  mistake: "Adding methods inside constructor",
                  consequence: "Memory waste",
                  fix: "Add methods to prototype",
                },
                {
                  mistake: "Modifying Array.prototype",
                  consequence: "Unexpected bugs",
                  fix: "Avoid touching built-in prototypes",
                },
                {
                  mistake: "Thinking classes are real classes",
                  consequence: "Conceptual confusion",
                  fix: "Remember: classes = syntax sugar",
                },
              ],

              proTips: [
                "JS inheritance = delegation, not copying",
                "prototype is shared, __proto__ is per object",
                "Understanding prototypes explains JS magic",
                "Classes make code cleaner, not different",
              ],

              realWorldAnalogy:
                "Prototypes are like a shared library that objects consult when they don’t have something themselves",
            },

            tasks: [
              "Create constructor functions",
              "Extend objects via prototype",
              "Compare class vs prototype",
            ],

            examples: [
              "function Person(name) { this.name = name; }",
              "Person.prototype.greet = function() {}",
            ],

            useCases: [
              "Memory-efficient objects",
              "Framework design",
              "Understanding JS internals",
              "Debugging complex behavior",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "50–65 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand prototype chain clearly",
              "Differentiate __proto__ and prototype",
              "Explain how inheritance works in JS",
              "Read and debug class-based code confidently",
            ],
          },
          {
            day: 5,
            topic: "Currying, Composition & Memoization",
            subTopics: [
              "Currying functions",
              "Partial application",
              "Function composition",
              "Memoization technique",
              "Performance optimization",
            ],

            explanation:
              "Currying, composition, and memoization are functional programming techniques that improve code reuse, readability, and performance, especially in large-scale JavaScript applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Currying Mindset Drill",
                description:
                  "Train your brain to think in curried functions instead of multi-argument functions.",
                tasks: [
                  "Convert a normal function into a curried version",
                  "Create a partially applied function",
                  "Reuse the partially applied function in multiple places",
                ],
                validationChecklist: [
                  "Each function accepts only one argument",
                  "Partial application works without re-writing logic",
                  "Behavior remains predictable",
                ],
                outcome:
                  "You’ll stop seeing currying as syntax and start seeing it as configuration.",
              },
              {
                id: "pt-2",
                title: "Composition Pipeline Builder",
                description:
                  "Replace nested function calls with a clean composition pipeline.",
                tasks: [
                  "Create small single-purpose functions",
                  "Compose them into a pipeline",
                  "Remove nested logic",
                ],
                qualityRule: "No function should do more than one thing",
                outcome: "Your code will read top-to-bottom like a data flow.",
              },
              {
                id: "pt-3",
                title: "Memoization Performance Experiment",
                description:
                  "Visually understand how memoization improves performance.",
                tasks: [
                  "Create an expensive calculation function",
                  "Wrap it with memoization",
                  "Call it repeatedly with same inputs",
                ],
                mindset: "Cache results, not logic",
                outcome: "You’ll know exactly when memoization is worth it.",
              },
              {
                id: "pt-4",
                title: "React Optimization Mental Model",
                description: "Map functional concepts directly to React hooks.",
                tasks: [
                  "Map memoization → useMemo",
                  "Map memoized functions → useCallback",
                  "Explain composition via custom hooks",
                ],
                mindset: "React performance = functional discipline",
                outcome:
                  "React optimizations will feel logical instead of magical.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most performance issues come from repeated work, not slow code.",
              checklist: [
                "Is the function pure?",
                "Are inputs repeated?",
                "Is recomputation avoidable?",
              ],
              goldenRule:
                "Memoize behavior only after confirming it’s a bottleneck.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is currying and why is it useful?",
                "Difference between currying and partial application?",
                "What is memoization?",
                "How does React use memoization?",
              ],
              strongAnswerPattern:
                "Explain concept → give simple example → mention real-world/React usage",
              redFlag:
                "Claiming memoization should be used everywhere for performance.",
            },

            editorGuidance: {
              careerMindset: [
                "Functional patterns scale better than imperative ones",
                "Performance is about avoiding work, not faster loops",
                "Clean abstractions beat clever code",
              ],
              seniorAdvice:
                "If your optimization hurts readability, it’s probably not worth it yet.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications grow, performance and maintainability become critical. Functional techniques like currying, composition, and memoization help control complexity, avoid repeated work, and build predictable logic.",

              sections: [
                {
                  title: "What Is Currying?",
                  description:
                    "Currying transforms a function with multiple arguments into a sequence of functions each taking one argument.",
                  example: "const add = a => b => a + b;",
                  takeaway: "One argument at a time",
                },
                {
                  title: "Why Currying Exists",
                  description:
                    "Currying improves reusability and configuration.",
                  benefits: [
                    "Reusable partially applied functions",
                    "Cleaner functional pipelines",
                    "Better composability",
                  ],
                  takeaway: "Currying enables specialization",
                },
                {
                  title: "Partial Application vs Currying",
                  description: "They are related but not the same.",
                  comparison: [
                    "Currying → transforms function shape",
                    "Partial application → fixes some arguments",
                  ],
                  takeaway:
                    "Currying is structural, partial application is practical",
                },
                {
                  title: "Currying + Closures",
                  description: "Currying relies on closures.",
                  explanation:
                    "Returned functions remember earlier arguments via closures.",
                  takeaway: "Closures power currying",
                },
                {
                  title: "Function Composition",
                  description: "Combining multiple functions into one.",
                  explanation:
                    "Output of one function becomes input of the next.",
                  example: `
const compose = (f, g) => x => f(g(x));
        `,
                  takeaway: "Small functions → powerful pipelines",
                },
                {
                  title: "Why Composition Matters",
                  description: "Composition reduces complexity.",
                  benefits: [
                    "Readable data flow",
                    "Testable logic",
                    "Declarative code",
                  ],
                  takeaway: "Compose behavior, don’t nest logic",
                },
                {
                  title: "What Is Memoization?",
                  description: "Caching results of expensive function calls.",
                  explanation:
                    "If inputs repeat, return cached output instead of recomputing.",
                  example: "const memoizedFn = cache[key] || fn()",
                  takeaway: "Trade memory for speed",
                },
                {
                  title: "Memoization with Closures",
                  description: "Closures store the cache.",
                  example: `
function memoize(fn) {
  const cache = {};
  return function (arg) {
    if (cache[arg]) return cache[arg];
    return (cache[arg] = fn(arg));
  };
}
        `,
                  takeaway: "Closures + memoization = performance",
                },
                {
                  title: "When to Use Memoization",
                  description: "Not all functions should be memoized.",
                  useWhen: [
                    "Expensive calculations",
                    "Pure functions",
                    "Repeated inputs",
                  ],
                  avoidWhen: ["Side effects", "Highly dynamic inputs"],
                  takeaway: "Use memoization selectively",
                },
                {
                  title: "These Concepts in React (CRITICAL)",
                  description: "React uses these ideas everywhere.",
                  examples: [
                    "useCallback → memoized functions",
                    "useMemo → memoized values",
                    "Custom hooks → composition",
                  ],
                  takeaway: "React is functional programming in practice",
                },
                {
                  title: "Performance Optimization Mindset",
                  description: "Premature optimization is dangerous.",
                  rules: [
                    "Optimize after measuring",
                    "Prefer clarity over cleverness",
                    "Memoize only bottlenecks",
                  ],
                  takeaway: "Readable fast code > clever slow code",
                },
              ],

              visualStructure: `
Input
  ↓
Curried Functions
  ↓
Composed Pipeline
  ↓
Memoized Result
  ↓
Output
    `,

              commonMistakes: [
                {
                  mistake: "Overusing currying",
                  consequence: "Unreadable code",
                  fix: "Use where it adds clarity",
                },
                {
                  mistake: "Memoizing impure functions",
                  consequence: "Wrong results",
                  fix: "Memoize only pure functions",
                },
                {
                  mistake: "Optimizing too early",
                  consequence: "Complex code",
                  fix: "Measure first, optimize later",
                },
              ],

              proTips: [
                "Currying + composition shine in data pipelines",
                "Memoization is powerful but costly in memory",
                "React hooks are built on these ideas",
                "Understand concepts, don’t over-abstract",
              ],

              realWorldAnalogy:
                "Currying is pre-configuring tools, composition is chaining machines, memoization is remembering past results",
            },

            tasks: [
              "Implement curry function",
              "Compose multiple functions",
              "Add memoization to slow function",
            ],

            examples: [
              "const add = a => b => a + b;",
              "const memoizedFn = cache[key] || fn()",
            ],

            useCases: [
              "Optimized calculations",
              "Functional programming",
              "High-performance apps",
              "React hooks optimization",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand currying and partial application",
              "Compose functions cleanly",
              "Apply memoization safely",
              "Write performance-aware JavaScript",
            ],
          },
          {
            day: 6,
            topic: "Design Patterns & IIFE",
            subTopics: [
              "IIFE pattern",
              "Module pattern",
              "Factory pattern",
              "Singleton basics",
              "When to use patterns",
            ],

            explanation:
              "Design patterns are proven solutions to common software problems. In JavaScript, patterns like IIFE, Module, Factory, and Singleton help organize code, manage scope, and build scalable applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "IIFE Scope Isolation Drill",
                description:
                  "Understand exactly how IIFE protects variables from the global scope.",
                tasks: [
                  "Create a variable in global scope",
                  "Create the same variable name inside an IIFE",
                  "Prove they don’t conflict",
                ],
                validationChecklist: [
                  "Global variable remains unchanged",
                  "IIFE variable is inaccessible from outside",
                  "No pollution of global scope",
                ],
                outcome:
                  "You’ll clearly see why IIFE was critical before ES6 modules.",
              },
              {
                id: "pt-2",
                title: "Module Pattern API Design",
                description:
                  "Design a clean public API while keeping internal state private.",
                tasks: [
                  "Create private variables inside an IIFE",
                  "Expose only required methods",
                  "Block direct state access",
                ],
                qualityRule: "Only expose what consumers actually need",
                outcome:
                  "You’ll start thinking in terms of public vs private contracts.",
              },
              {
                id: "pt-3",
                title: "Factory vs Constructor Decision Drill",
                description:
                  "Learn when factories are a better choice than constructors.",
                tasks: [
                  "Create an object using a constructor",
                  "Create the same object using a factory",
                  "Compare flexibility and readability",
                ],
                mindset:
                  "Object creation should be simple and intention-driven",
                outcome:
                  "You’ll instinctively choose the right creation pattern.",
              },
              {
                id: "pt-4",
                title: "Singleton Smell Detection",
                description:
                  "Learn to identify when a singleton is helpful vs harmful.",
                tasks: [
                  "Create a simple singleton service",
                  "List its hidden dependencies",
                  "Identify testing limitations",
                ],
                mindset: "Shared state is powerful and dangerous",
                outcome:
                  "You’ll stop abusing singletons and use them intentionally.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most architectural bugs come from uncontrolled shared state.",
              checklist: [
                "Is state truly private?",
                "Who can modify it?",
                "Is behavior predictable?",
              ],
              goldenRule:
                "If many parts depend on hidden state, debugging becomes expensive.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is an IIFE and why was it used?",
                "Explain the module pattern in JavaScript",
                "Factory vs constructor — which would you choose?",
                "Why are singletons considered risky?",
              ],
              strongAnswerPattern:
                "Explain problem → explain pattern → explain modern alternative",
              redFlag: "Claiming design patterns are outdated or unnecessary.",
            },

            editorGuidance: {
              careerMindset: [
                "Patterns help organize thinking before code",
                "Encapsulation is more important than reuse",
                "Modern tools hide patterns, not remove them",
              ],
              seniorAdvice:
                "If you can explain why a pattern exists, you’re thinking like an architect.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications grow, unstructured code becomes hard to maintain. Design patterns provide reusable blueprints for organizing logic, managing state, and controlling object creation — without reinventing solutions.",

              sections: [
                {
                  title: "What Are Design Patterns?",
                  description:
                    "Design patterns are reusable solutions to recurring problems.",
                  explanation:
                    "They are not libraries or code snippets, but ideas and structures.",
                  takeaway: "Patterns guide structure, not syntax",
                },
                {
                  title: "IIFE (Immediately Invoked Function Expression)",
                  description:
                    "A function that runs immediately after it is defined.",
                  example: "(function () { /* private scope */ })();",
                  purpose: [
                    "Avoid global scope pollution",
                    "Create private variables",
                  ],
                  takeaway: "IIFE creates isolated scope",
                },
                {
                  title: "Why IIFE Was Important",
                  description:
                    "Before ES6 modules, JS lacked file-level scope.",
                  explanation: "IIFE was the primary way to encapsulate logic.",
                  takeaway: "IIFE solved early JavaScript limitations",
                },
                {
                  title: "Module Pattern",
                  description: "Encapsulating public and private members.",
                  explanation:
                    "Uses closures to expose only selected functionality.",
                  example: `
const Counter = (function () {
  let count = 0;
  return {
    increment() { count++; },
    getCount() { return count; }
  };
})();
        `,
                  takeaway: "Module pattern = controlled access",
                },
                {
                  title: "Why Module Pattern Matters",
                  description: "Keeps internal state safe.",
                  benefits: [
                    "Data hiding",
                    "Clear API surface",
                    "Predictable behavior",
                  ],
                  takeaway: "Encapsulation improves maintainability",
                },
                {
                  title: "Factory Pattern",
                  description: "Functions that create and return objects.",
                  explanation: "Factories abstract object creation logic.",
                  example: `
function createUser(name) {
  return { name, role: 'user' };
}
        `,
                  takeaway: "Factories simplify object creation",
                },
                {
                  title: "Factory vs Constructor",
                  description: "Two ways to create objects.",
                  comparison: [
                    "Factory → simple, flexible",
                    "Constructor → prototype-based",
                  ],
                  takeaway: "Factories are often cleaner in JS",
                },
                {
                  title: "Singleton Pattern (Basics)",
                  description: "Ensures only one instance exists.",
                  explanation: "Useful for global state or services.",
                  example: `
const Store = (function () {
  let instance;
  function create() { return {}; }
  return {
    getInstance() {
      if (!instance) instance = create();
      return instance;
    }
  };
})();
        `,
                  takeaway: "Singleton controls shared state",
                },
                {
                  title: "When Singleton Is Dangerous",
                  description: "Singletons can cause hidden dependencies.",
                  risks: ["Hard to test", "Global state bugs"],
                  takeaway: "Use singletons sparingly",
                },
                {
                  title: "Modern JS & Patterns",
                  description: "ES6 modules replaced many old patterns.",
                  explanation:
                    "import/export provides native module encapsulation.",
                  takeaway: "Patterns evolve with language features",
                },
                {
                  title: "Patterns in React (VERY IMPORTANT)",
                  description: "React uses patterns conceptually.",
                  examples: [
                    "Custom hooks → module + factory",
                    "Context → controlled singleton",
                    "Hooks composition → design patterns",
                  ],
                  takeaway: "React abstracts patterns, but ideas remain",
                },
                {
                  title: "When to Use Patterns",
                  description: "Patterns are tools, not rules.",
                  guidelines: [
                    "Use patterns to solve real problems",
                    "Avoid premature abstraction",
                    "Prefer clarity over cleverness",
                  ],
                  takeaway: "Patterns serve code, not ego",
                },
              ],

              visualStructure: `
Problem
  ↓
Design Pattern
  ↓
Reusable Structure
  ↓
Scalable Code
    `,

              commonMistakes: [
                {
                  mistake: "Using patterns everywhere",
                  consequence: "Overengineered code",
                  fix: "Apply only when needed",
                },
                {
                  mistake: "Copying patterns blindly",
                  consequence: "Poor fit",
                  fix: "Understand the problem first",
                },
                {
                  mistake: "Using IIFE in modern modules",
                  consequence: "Redundant code",
                  fix: "Use ES6 modules instead",
                },
              ],

              proTips: [
                "Patterns are about intent, not syntax",
                "Modern JS hides many old patterns",
                "React developers still use these ideas",
                "Good architecture beats clever code",
              ],

              realWorldAnalogy:
                "Design patterns are like architectural blueprints — you don’t build blindly, you follow proven designs",
            },

            tasks: [
              "Create module using IIFE",
              "Build factory function",
              "Identify pattern use cases",
            ],

            examples: ["(function() { /* private scope */ })();"],

            useCases: [
              "Code organization",
              "Large applications",
              "Reusable architecture",
              "React-style abstractions",
            ],

            editorType: "javascript",

            // ⏱️ Estimated time
            estimatedTime: "45–60 minutes",

            // 🎯 Learning objectives
            objectives: [
              "Understand core JS design patterns",
              "Use IIFE and module pattern correctly",
              "Apply factory and singleton thoughtfully",
              "Think architecturally before React",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Advanced JavaScript Utility Library",

            subTopics: [
              "Debounce function",
              "Throttle function",
              "Deep clone utility",
              "Memoized functions",
              "Reusable helpers",
            ],

            explanation:
              "This project demonstrates mastery over advanced JavaScript concepts by building a reusable utility library similar to lodash or underscore, focusing on performance, immutability, and scalability.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Debounce & Throttle from First Principles",
                description:
                  "Implement debounce and throttle without copying from libraries or blogs.",
                tasks: [
                  "Write debounce using closures and timers",
                  "Write throttle using timestamps",
                  "Test both using real DOM events (scroll/input)",
                ],
                validationChecklist: [
                  "Debounce delays execution correctly",
                  "Throttle limits execution rate",
                  "No unexpected multiple calls",
                ],
                outcome:
                  "You’ll internalize how performance utilities actually work.",
              },
              {
                id: "pt-2",
                title: "Deep Clone Reference Safety Audit",
                description:
                  "Prove that your deepClone utility truly breaks references.",
                tasks: [
                  "Clone a nested object",
                  "Modify deeply nested values in clone",
                  "Verify original object remains unchanged",
                ],
                qualityRule: "Any shared reference = failed deep clone",
                outcome: "You’ll stop trusting shallow copies blindly.",
              },
              {
                id: "pt-3",
                title: "Memoization Performance Proof",
                description:
                  "Measure real performance gains using memoization.",
                tasks: [
                  "Create an expensive calculation function",
                  "Wrap it with memoize",
                  "Log execution time before and after caching",
                ],
                mindset: "Optimization must be measurable",
                outcome:
                  "You’ll understand when memoization is worth the cost.",
              },
              {
                id: "pt-4",
                title: "Utility Library API Design",
                description:
                  "Design a clean public API like a real npm package.",
                tasks: [
                  "Create index.js barrel exports",
                  "Ensure consistent function signatures",
                  "Write usage examples for each utility",
                ],
                qualityRule: "Utilities should feel predictable and reusable",
                outcome:
                  "Your library will feel professional, not experimental.",
              },
            ],

            testingInsight: {
              coreReality:
                "Performance bugs don’t show up in small demos, only at scale.",
              checklist: [
                "Does utility behave correctly under rapid calls?",
                "Are references fully isolated?",
                "Does cache grow unbounded?",
              ],
              goldenRule:
                "A fast app with wrong behavior is worse than a slow correct one.",
            },

            interviewInsights: {
              commonQuestions: [
                "How does debounce differ from throttle internally?",
                "How would you implement deep clone?",
                "What are risks of memoization?",
                "How does this relate to React performance?",
              ],
              strongAnswerPattern:
                "Explain problem → explain naive solution → explain optimized approach",
              redFlag: "Claiming memoization always improves performance.",
            },

            editorGuidance: {
              careerMindset: [
                "Utilities reveal how deeply you understand JavaScript",
                "Performance patterns separate seniors from juniors",
                "Libraries are just well-written functions",
              ],
              seniorAdvice:
                "If you can build lodash utilities, you won’t fear any React performance bug.",
            },

            // 📚 Project Breakdown
            content: {
              introduction:
                "Modern frontend applications rely heavily on optimized utility functions to manage performance and state efficiently. In this project, you will build production-ready utilities that are commonly used in React, large-scale apps, and interviews.",

              utilities: [
                {
                  name: "Debounce",
                  purpose:
                    "Delays execution until a function stops being called for a specified time.",
                  realUseCases: [
                    "Search input optimization",
                    "API call reduction",
                    "Resize event handling",
                  ],
                  implementationConcept:
                    "Uses closures + timers to delay function execution.",
                  example: `
function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
        `,
                },
                {
                  name: "Throttle",
                  purpose:
                    "Ensures a function executes at most once within a given time interval.",
                  realUseCases: [
                    "Scroll events",
                    "Button spam prevention",
                    "Performance-sensitive interactions",
                  ],
                  implementationConcept:
                    "Controls execution rate using timestamps or timers.",
                  example: `
function throttle(fn, limit) {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      fn(...args);
    }
  };
}
        `,
                },
                {
                  name: "Deep Clone",
                  purpose:
                    "Creates a complete copy of nested objects and arrays.",
                  realUseCases: [
                    "Immutable state updates",
                    "Cloning API data",
                    "Avoiding reference bugs",
                  ],
                  implementationConcept: "Recursive cloning with type checks.",
                  example: `
function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  const copy = Array.isArray(obj) ? [] : {};
  for (let key in obj) {
    copy[key] = deepClone(obj[key]);
  }
  return copy;
}
        `,
                },
                {
                  name: "Memoization",
                  purpose: "Caches function results to improve performance.",
                  realUseCases: [
                    "Expensive calculations",
                    "Repeated data processing",
                    "Performance-critical apps",
                  ],
                  implementationConcept: "Uses closure-based cache object.",
                  example: `
function memoize(fn) {
  const cache = {};
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache[key]) return cache[key];
    return (cache[key] = fn(...args));
  };
}
        `,
                },
              ],

              folderStructure: `
utils/
 ├── debounce.js
 ├── throttle.js
 ├── deepClone.js
 ├── memoize.js
 └── index.js
    `,

              indexExample: `
export { debounce } from './debounce';
export { throttle } from './throttle';
export { deepClone } from './deepClone';
export { memoize } from './memoize';
    `,

              testingIdeas: [
                "Test debounce using input events",
                "Test throttle using scroll events",
                "Verify deep clone reference safety",
                "Measure performance gains using memoization",
              ],

              commonMistakes: [
                {
                  mistake: "Not clearing timers",
                  issue: "Memory leaks",
                  fix: "Always clearTimeout/clearInterval",
                },
                {
                  mistake: "Shallow cloning",
                  issue: "Reference bugs",
                  fix: "Recursive deep cloning",
                },
                {
                  mistake: "Over-memoization",
                  issue: "Memory growth",
                  fix: "Use selectively",
                },
              ],

              ReactConnection: [
                "debounce → input search hooks",
                "throttle → scroll & resize listeners",
                "memoization → useMemo / useCallback mindset",
                "deepClone → immutable state updates",
              ],
            },

            tasks: [
              "Build debounce and throttle utilities",
              "Create deep clone function",
              "Implement memoization",
              "Export utilities as reusable module",
              "Add demo usage examples",
            ],

            examples: ["debounce(fn, delay)", "throttle(fn, limit)"],

            useCases: [
              "Frontend performance optimization",
              "Interview showcase project",
              "Reusable internal libraries",
              "Preparation for React hooks",
            ],

            editorType: "javascript",

            estimatedTime: "90–120 minutes",

            objectives: [
              "Master closures and performance patterns",
              "Build production-grade utilities",
              "Understand React performance foundations",
              "Demonstrate senior-level JavaScript skills",
            ],
          },
        ],
      },
    ],
    questions: [
      {
        id: "m1",
        question: "What are Promises in JavaScript? How do they work?",
        difficulty: "Medium",
        topics: ["Promises", "Asynchronous JavaScript"],
      },
      {
        id: "m2",
        question:
          "Explain async/await syntax and its advantages over Promises.",
        difficulty: "Medium",
        topics: ["Async/Await"],
      },
      {
        id: "m3",
        question:
          "What is destructuring in JavaScript? Show examples for arrays and objects.",
        difficulty: "Easy",
        topics: ["ES6 Features", "Destructuring"],
      },
      {
        id: "m4",
        question: "Explain the spread operator and rest parameters.",
        difficulty: "Easy",
        topics: ["ES6 Features", "Spread/Rest"],
      },
      {
        id: "m5",
        question: "What are template literals and how are they useful?",
        difficulty: "Easy",
        topics: ["ES6 Features", "Template Literals"],
      },
      {
        id: "m6",
        question: "What are modules in JavaScript? Explain import and export.",
        difficulty: "Medium",
        topics: ["Modules", "ES6"],
      },
      {
        id: "m7",
        question:
          "What is the difference between named exports and default exports?",
        difficulty: "Easy",
        topics: ["Modules"],
      },
      {
        id: "m8",
        question:
          "Explain what npm is and its role in modern JavaScript development.",
        difficulty: "Easy",
        topics: ["NPM", "Package Management"],
      },
      {
        id: "m9",
        question:
          "What is a bundler (like Webpack or Vite)? Why do we need it?",
        difficulty: "Medium",
        topics: ["Build Tools", "Bundlers"],
      },
      {
        id: "m10",
        question: "What is the purpose of a transpiler like Babel?",
        difficulty: "Medium",
        topics: ["Transpilers", "Babel"],
      },
      {
        id: "m11",
        question: "Explain the fetch API and how to handle HTTP requests.",
        difficulty: "Medium",
        topics: ["Fetch API", "HTTP Requests"],
      },
      {
        id: "m12",
        question: "What is CORS and why do we encounter it?",
        difficulty: "Medium",
        topics: ["CORS", "HTTP"],
      },
      {
        id: "m13",
        question:
          "What are the different HTTP methods (GET, POST, PUT, DELETE) and when to use them?",
        difficulty: "Easy",
        topics: ["HTTP Methods"],
      },
      {
        id: "m14",
        question:
          "Explain what localStorage and sessionStorage are and their differences.",
        difficulty: "Easy",
        topics: ["Web Storage"],
      },
      {
        id: "m15",
        question:
          "What is the difference between synchronous and asynchronous imports?",
        difficulty: "Medium",
        topics: ["Modules", "Imports"],
      },
      {
        id: "m16",
        question: "Explain what a polyfill is and when you would use one.",
        difficulty: "Medium",
        topics: ["Polyfills", "Browser Compatibility"],
      },
      {
        id: "m17",
        question:
          "What are optional chaining (?.) and nullish coalescing (??) operators?",
        difficulty: "Easy",
        topics: ["ES2020 Features"],
      },
      {
        id: "m18",
        question: "Explain the concept of tree shaking in bundlers.",
        difficulty: "Medium",
        topics: ["Bundlers", "Optimization"],
      },
      {
        id: "m19",
        question:
          "What is the difference between Promise.all, Promise.race, and Promise.allSettled?",
        difficulty: "Hard",
        topics: ["Promises"],
      },
      {
        id: "m20",
        question: "How do you handle errors in async/await?",
        difficulty: "Medium",
        topics: ["Async/Await", "Error Handling"],
      },
      {
        id: "m21",
        question:
          "What are ES6 classes and how do they differ from constructor functions?",
        difficulty: "Medium",
        topics: ["ES6 Classes"],
      },
      {
        id: "m22",
        question: "Explain the Map and Set data structures in JavaScript.",
        difficulty: "Medium",
        topics: ["Data Structures"],
      },
      {
        id: "m23",
        question: "What are generators in JavaScript?",
        difficulty: "Hard",
        topics: ["Generators"],
      },
      {
        id: "m24",
        question: "Explain what symbols are and their use cases.",
        difficulty: "Hard",
        topics: ["Symbols"],
      },
      {
        id: "m25",
        question: "What is the difference between CommonJS and ES Modules?",
        difficulty: "Medium",
        topics: ["Modules"],
      },
      {
        id: "m26",
        question: "How do you implement debouncing in JavaScript?",
        difficulty: "Medium",
        topics: ["Debouncing", "Performance"],
      },
      {
        id: "m27",
        question: "What is AJAX and how does it differ from fetch?",
        difficulty: "Easy",
        topics: ["AJAX", "Fetch API"],
      },
      {
        id: "m28",
        question: "Explain RESTful API principles.",
        difficulty: "Medium",
        topics: ["REST API"],
      },
      {
        id: "m29",
        question:
          "What are HTTP status codes? Name common ones and their meanings.",
        difficulty: "Easy",
        topics: ["HTTP"],
      },
      {
        id: "m30",
        question:
          "How do you handle authentication tokens in frontend applications?",
        difficulty: "Medium",
        topics: ["Authentication", "Security"],
      },
      {
        id: "m31",
        question: "What is webpack and what problems does it solve?",
        difficulty: "Medium",
        topics: ["Webpack", "Bundlers"],
      },
      {
        id: "m32",
        question: "Explain the purpose of package.json and package-lock.json.",
        difficulty: "Easy",
        topics: ["NPM"],
      },
      {
        id: "m33",
        question: "What are environment variables and how do you use them?",
        difficulty: "Easy",
        topics: ["Environment Variables"],
      },
      {
        id: "m34",
        question:
          "Explain the difference between dependencies and devDependencies.",
        difficulty: "Easy",
        topics: ["NPM", "Dependencies"],
      },
      {
        id: "m35",
        question: "What is a Service Worker and what can you do with it?",
        difficulty: "Hard",
        topics: ["Service Workers", "PWA"],
      },
    ],
  },
  {
    phase: 4,
    phaseKey: "react",
    title: "React Fundamentals",
    duration: "6 Weeks",
    level: "Intermediate to Advanced",
    weeks: [
      {
        week: 19,
        weekGoal:
          "Understand React fundamentals, JSX syntax, and component-based architecture",
        weekOutcome:
          "Ability to build reusable UI components using React and JSX",
        canLearn: ["students", "beginners", "professionals"],
        miniProject: "Reusable Component Library",
        days: [
          {
            day: 1,
            topic: "React Setup & Project Structure",

            subTopics: [
              "What is React and why use it",
              "React mental model",
              "Vite vs Create React App",
              "Node.js & npm role",
              "Project folder structure",
              "Running & building React apps",
            ],

            explanation:
              "React is a component-based JavaScript library for building fast, scalable user interfaces. Understanding setup and structure is the first step before writing components.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "React App Setup Without Copy-Paste",
                description:
                  "Set up a React project using Vite while understanding each step instead of blindly following commands.",
                tasks: [
                  "Install Node.js (LTS)",
                  "Create React app using Vite",
                  "Explain what npm install actually does",
                  "Run the dev server and identify the port",
                ],
                validationChecklist: [
                  "Dev server runs without errors",
                  "You can explain what Vite does",
                  "You know why node_modules is created",
                ],
                outcome:
                  "You won’t fear project setup or new repositories anymore.",
              },
              {
                id: "pt-2",
                title: "Project Structure Deep Dive",
                description:
                  "Understand what each important file does in a real React project.",
                tasks: [
                  "Open index.html and locate #root",
                  "Trace how main.jsx renders <App />",
                  "Delete unused files and verify app still runs",
                ],
                qualityRule:
                  "You should be able to explain the bootstrapping flow verbally",
                outcome:
                  "You’ll be able to read any React repo with confidence.",
              },
              {
                id: "pt-3",
                title: "Vite vs CRA Reality Check",
                description: "Understand why modern React teams prefer Vite.",
                tasks: [
                  "Research Create React App limitations",
                  "Compare dev startup time (conceptually)",
                  "List advantages of Vite in your own words",
                ],
                mindset: "Tools evolve — developers must evolve faster",
                outcome: "You’ll stop following outdated tutorials blindly.",
              },
              {
                id: "pt-4",
                title: "Build & Preview Production App",
                description:
                  "Understand what happens when React goes to production.",
                tasks: [
                  "Run npm run build",
                  "Inspect dist folder",
                  "Run npm run preview",
                ],
                qualityRule:
                  "You should know the difference between dev and build output",
                outcome:
                  "You’ll understand what actually gets shipped to users.",
              },
            ],

            testingInsight: {
              coreReality:
                "If you don’t understand project setup, debugging React issues becomes guesswork.",
              checklist: [
                "Can you explain how React starts?",
                "Do you know where JSX is executed?",
                "Do you understand dev vs build mode?",
              ],
              goldenRule:
                "Most beginner React bugs come from poor setup understanding, not hooks.",
            },

            interviewInsights: {
              commonQuestions: [
                "How does a React application start?",
                "What role does Node.js play in React?",
                "Why is Vite preferred over Create React App?",
                "What happens when you run npm run build?",
              ],
              strongAnswerPattern:
                "Explain entry point → rendering flow → tooling role",
              redFlag: "Saying 'React runs on Node.js in production'.",
            },

            editorGuidance: {
              careerMindset: [
                "React setup is not boilerplate — it’s architecture",
                "Understanding flow > memorizing commands",
                "Good developers know what runs and why",
              ],
              seniorAdvice:
                "If you understand project structure, learning hooks and libraries becomes 10× easier.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "React is a JavaScript library created by Facebook to solve one big problem: managing complex UI efficiently. Instead of manually manipulating the DOM, React lets you describe what the UI should look like based on data (state).",

              coreConcepts: [
                {
                  title: "Why React Exists",
                  description:
                    "Before React, developers manually updated the DOM, which became slow and error-prone as applications grew.",
                  problemsBeforeReact: [
                    "Manual DOM manipulation",
                    "UI bugs due to inconsistent state",
                    "Hard-to-maintain large codebases",
                  ],
                  solutionWithReact: [
                    "Component-based architecture",
                    "Virtual DOM for performance",
                    "UI as a function of state",
                  ],
                },
                {
                  title: "React Mental Model",
                  description:
                    "In React, you don’t tell the browser HOW to update UI. You tell React WHAT the UI should look like.",
                  keyIdea: "UI = f(state)",
                  example:
                    "If state changes → React automatically re-renders UI",
                },
              ],

              setupGuide: {
                title: "Modern React Setup (Vite)",
                whyVite:
                  "Vite is faster, simpler, and now officially recommended for new React projects.",
                steps: [
                  "Install Node.js (LTS version)",
                  "Run npm create vite@latest",
                  "Choose React + JavaScript",
                  "Install dependencies",
                  "Start dev server",
                ],
                commands: [
                  "npm create vite@latest my-react-app",
                  "cd my-react-app",
                  "npm install",
                  "npm run dev",
                ],
              },

              projectStructure: {
                explanation:
                  "Understanding folder structure helps you read any real-world React codebase confidently.",
                structure: [
                  {
                    name: "index.html",
                    purpose:
                      "Single HTML file where React app mounts (SPA concept).",
                  },
                  {
                    name: "main.jsx",
                    purpose:
                      "Entry point where React connects to the DOM using createRoot().",
                  },
                  {
                    name: "App.jsx",
                    purpose: "Root component where the UI starts.",
                  },
                  {
                    name: "src/",
                    purpose: "All components, styles, assets live here.",
                  },
                ],
              },

              howReactRuns: {
                steps: [
                  "Browser loads index.html",
                  "main.jsx executes",
                  "React creates Virtual DOM",
                  "React renders <App /> into #root",
                  "UI updates automatically on state change",
                ],
                analogy:
                  "React is like Google Maps — you change destination (state), it recalculates the route (UI) automatically.",
              },

              commonMistakes: [
                {
                  mistake: "Following old Create React App tutorials",
                  issue: "CRA is slow and no longer recommended",
                  fix: "Use Vite for all new React projects",
                },
                {
                  mistake: "Thinking React is a framework",
                  issue: "React only handles UI",
                  fix: "Routing, state, data fetching come via libraries",
                },
                {
                  mistake: "Confusing Node.js with browser runtime",
                  issue: "Node is not used in production browser",
                  fix: "Node is only for development tools",
                },
              ],

              proTips: [
                "Always start new React projects with Vite",
                "Understand flow before writing JSX",
                "Don’t rush into hooks without structure clarity",
                "React is about thinking in components, not files",
              ],

              realWorldAnalogy:
                "Think of React like LEGO blocks:\n- Components are LEGO pieces\n- App.jsx is the base plate\n- State decides how blocks are arranged\n- React re-arranges blocks automatically",
            },

            tasks: [
              "Install Node.js",
              "Create React app using Vite",
              "Run development server",
              "Explore project folders",
              "Identify main.jsx and App.jsx",
            ],

            examples: ["npm create vite@latest", "npm install", "npm run dev"],

            useCases: [
              "Starting real-world React projects",
              "Understanding professional React repos",
              "Strong foundation for hooks & components",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Understand why React exists",
              "Understand how a React app starts",
              "Set up a modern React project",
              "Be ready for JSX and components",
            ],
          },
          {
            day: 2,
            topic: "JSX Syntax & Rendering",

            subTopics: [
              "JSX vs HTML differences",
              "Embedding JavaScript in JSX",
              "Expressions vs statements",
              "Fragments",
              "JSX rules and best practices",
            ],

            explanation:
              "JSX allows developers to write UI code using HTML-like syntax inside JavaScript. It makes React components easier to read, write, and maintain.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "JSX Without Errors Challenge",
                description:
                  "Write JSX from scratch and intentionally trigger common JSX errors, then fix them.",
                tasks: [
                  "Write JSX with multiple root elements and fix it",
                  "Use class instead of className and correct it",
                  "Forget to close a tag and observe the error",
                ],
                validationChecklist: [
                  "JSX compiles without errors",
                  "You can explain WHY the error occurred",
                  "You can fix JSX without Google",
                ],
                outcome:
                  "You’ll stop fearing red JSX errors during development.",
              },
              {
                id: "pt-2",
                title: "JSX Expressions Mastery",
                description:
                  "Practice what JavaScript is allowed and NOT allowed inside JSX.",
                tasks: [
                  "Render values using variables",
                  "Use ternary operator for conditional UI",
                  "Replace if/else with expressions",
                ],
                qualityRule: "No statements inside JSX braces",
                outcome: "You’ll instinctively know what belongs inside JSX.",
              },
              {
                id: "pt-3",
                title: "Fragments vs Wrapper Divs",
                description:
                  "Eliminate unnecessary DOM elements using fragments.",
                tasks: [
                  "Wrap JSX using <div> and inspect DOM",
                  "Replace with Fragment and recheck DOM",
                  "Explain when Fragment is preferred",
                ],
                mindset: "Clean DOM = professional React",
                outcome: "You’ll write cleaner, scalable component markup.",
              },
              {
                id: "pt-4",
                title: "HTML to JSX Conversion Drill",
                description: "Convert real HTML into valid JSX manually.",
                tasks: [
                  "Convert class → className",
                  "Convert inline styles to JS object",
                  "Fix event handlers to camelCase",
                ],
                qualityRule: "JSX should look intentional, not patched",
                outcome:
                  "You’ll confidently migrate HTML into React components.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most JSX bugs are syntax misunderstandings, not logic errors.",
              checklist: [
                "Is JSX returning a single parent?",
                "Are only expressions used inside {}?",
                "Are attributes camelCased?",
              ],
              goldenRule:
                "If JSX looks messy, your component logic probably is too.",
            },

            interviewInsights: {
              commonQuestions: [
                "Is JSX HTML?",
                "Why can’t we use if inside JSX?",
                "What does JSX compile into?",
                "Why are fragments useful?",
              ],
              strongAnswerPattern:
                "Explain JSX → explain expressions → explain React.createElement",
              redFlag: "Saying JSX runs directly in the browser.",
            },

            editorGuidance: {
              careerMindset: [
                "JSX is communication, not decoration",
                "Readable JSX beats clever JSX",
                "Components are read more than written",
              ],
              seniorAdvice:
                "If your JSX is clean, your component logic becomes easier by default.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "JSX (JavaScript XML) is not HTML. It is a syntax extension for JavaScript that React uses to describe UI. Browsers do not understand JSX directly — it is converted into JavaScript before execution.",

              whatIsJSX: {
                definition:
                  "JSX is syntactic sugar over React.createElement(). It allows writing UI in a declarative way.",
                behindTheScenes:
                  "<h1>Hello</h1> → React.createElement('h1', null, 'Hello')",
                whyJSX: [
                  "UI logic and structure stay together",
                  "Easier to visualize component output",
                  "Less manual DOM manipulation",
                ],
              },

              jsxVsHtml: {
                title: "JSX vs HTML (IMPORTANT DIFFERENCES)",
                differences: [
                  {
                    html: "class",
                    jsx: "className",
                    reason: "class is a reserved keyword in JavaScript",
                  },
                  {
                    html: "for",
                    jsx: "htmlFor",
                    reason: "for is reserved in JavaScript",
                  },
                  {
                    html: "onclick",
                    jsx: "onClick",
                    reason: "JSX uses camelCase event names",
                  },
                  {
                    html: "style='color:red'",
                    jsx: "style={{ color: 'red' }}",
                    reason: "JSX styles are JS objects",
                  },
                ],
              },

              embeddingJavaScript: {
                title: "Embedding JavaScript in JSX",
                rule: "Only JavaScript expressions are allowed inside JSX using {}.",
                allowed: [
                  "Variables",
                  "Function calls",
                  "Ternary operators",
                  "Logical && rendering",
                ],
                notAllowed: ["if statements", "for loops", "switch statements"],
                example: "const name = 'John';\n<h1>Hello {name}</h1>",
              },

              expressionsVsStatements: {
                title: "Expressions vs Statements",
                expression: "Something that returns a value (allowed in JSX)",
                statement:
                  "Controls flow but does not return value (not allowed in JSX)",
                examples: {
                  valid: "{isLoggedIn ? <Dashboard /> : <Login />}",
                  invalid: "{if(isLoggedIn){ return <Dashboard /> }}",
                },
              },

              fragments: {
                title: "Fragments",
                whyNeeded:
                  "JSX must return a single parent element. Fragments avoid unnecessary wrapper divs.",
                syntax: ["<></>", "<React.Fragment></React.Fragment>"],
                example: "<>\n  <h1>Title</h1>\n  <p>Description</p>\n</>",
              },

              renderingRules: {
                title: "JSX Rules (Must Remember)",
                rules: [
                  "JSX must return a single parent element",
                  "Use camelCase for attributes",
                  "Always close tags",
                  "Use {} for JavaScript expressions",
                  "JSX is case-sensitive",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Using if directly inside JSX",
                  issue: "JSX does not allow statements",
                  fix: "Use ternary or logical &&",
                },
                {
                  mistake: "Forgetting single parent element",
                  issue: "JSX compilation error",
                  fix: "Wrap elements in Fragment",
                },
                {
                  mistake: "Using class instead of className",
                  issue: "JS syntax conflict",
                  fix: "Always use className",
                },
              ],

              proTips: [
                "Keep JSX clean and readable",
                "Move complex logic outside JSX",
                "Use fragments instead of extra divs",
                "JSX readability matters in interviews",
              ],

              realWorldAnalogy:
                "JSX is like writing a design blueprint:\n- You describe WHAT UI should look like\n- React decides HOW to update the DOM\n- You focus on clarity, not DOM mechanics",
            },

            tasks: [
              "Create JSX with variables",
              "Embed expressions inside JSX",
              "Convert HTML markup into JSX",
              "Fix common JSX errors",
            ],

            examples: [
              "const name = 'John';",
              "<h1>Hello {name}</h1>",
              "{isLoggedIn && <Dashboard />}",
            ],

            useCases: [
              "Dynamic UI rendering",
              "Conditional rendering",
              "Building readable components",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Understand JSX is not HTML",
              "Write valid JSX confidently",
              "Embed JavaScript correctly in JSX",
              "Avoid common JSX mistakes",
            ],
          },
          {
            day: 3,
            topic: "Components & Props",

            subTopics: [
              "Functional components",
              "Props passing",
              "Props destructuring",
              "Children props",
              "Component reusability",
            ],

            explanation:
              "Components are reusable building blocks in React. Props are inputs passed to components that allow data flow from parent to child.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Component Extraction Drill",
                description:
                  "Break a single large JSX UI into reusable components.",
                tasks: [
                  "Start with one large JSX layout",
                  "Identify repeating UI parts",
                  "Extract them into reusable components",
                  "Pass data via props",
                ],
                validationChecklist: [
                  "No duplicated JSX",
                  "Components have clear responsibility",
                  "Props are used instead of hardcoded values",
                ],
                outcome:
                  "You’ll learn how real-world React codebases stay clean.",
              },
              {
                id: "pt-2",
                title: "Props Immutability Test",
                description:
                  "Understand why props are read-only by trying to break them.",
                tasks: [
                  "Attempt to modify props inside a component",
                  "Observe the incorrect behavior or warning",
                  "Refactor logic to respect one-way data flow",
                ],
                qualityRule: "Props should only be read, never changed",
                outcome: "You’ll clearly differentiate props vs state.",
              },
              {
                id: "pt-3",
                title: "children Prop Mastery",
                description:
                  "Build wrapper components using children properly.",
                tasks: [
                  "Create a Card or Layout component",
                  "Render children dynamically",
                  "Reuse wrapper with different JSX inside",
                ],
                mindset: "Composition over duplication",
                outcome: "You’ll unlock React’s most powerful reuse pattern.",
              },
              {
                id: "pt-4",
                title: "Reusable Button Component",
                description: "Design a highly reusable Button using props.",
                tasks: [
                  "Accept label, type, and onClick as props",
                  "Reuse Button in multiple places",
                  "Avoid hardcoded behavior",
                ],
                qualityRule: "Component should work without modification",
                outcome:
                  "You’ll think in reusable components like a professional.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most component bugs come from unclear prop boundaries.",
              checklist: [
                "Is data flowing only parent → child?",
                "Are props clearly named?",
                "Is component doing only one job?",
              ],
              goldenRule:
                "If a component needs many props, it probably does too much.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are props in React?",
                "Can a child modify props?",
                "What is children prop?",
                "How do you make components reusable?",
              ],
              strongAnswerPattern:
                "Explain props → one-way data flow → composition",
              redFlag: "Confusing props with state.",
            },

            editorGuidance: {
              careerMindset: [
                "Components should be small and predictable",
                "Props define a component’s public API",
                "Reusability beats cleverness",
              ],
              seniorAdvice:
                "A good React component reads like a sentence — clear inputs, clear output.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "React applications are built by combining small, reusable components. Each component represents a piece of UI and can receive data using props. This makes React applications scalable, maintainable, and easy to reason about.",

              whatIsComponent: {
                definition:
                  "A component is a JavaScript function that returns JSX.",
                keyRule: "Component name must start with a capital letter.",
                example: "function Header() { return <h1>Hello</h1>; }",
              },

              functionalComponents: {
                title: "Functional Components (Modern React)",
                description:
                  "Modern React uses function-based components instead of class components.",
                benefits: [
                  "Simpler syntax",
                  "Easier to test",
                  "Works naturally with hooks",
                  "Industry standard",
                ],
                example: "const Button = () => <button>Click</button>;",
              },

              propsConcept: {
                title: "Understanding Props",
                description:
                  "Props (properties) are read-only data passed from a parent component to a child component.",
                rules: [
                  "Props flow from parent → child",
                  "Props are immutable (read-only)",
                  "Components must not modify props",
                ],
                example: "<User name='Alex' age={25} />",
              },

              propsDestructuring: {
                title: "Props Destructuring",
                why: "Destructuring makes code cleaner and more readable.",
                before:
                  "function Card(props) { return <h1>{props.title}</h1> }",
                after: "function Card({ title }) { return <h1>{title}</h1> }",
              },

              childrenProps: {
                title: "Children Props",
                description:
                  "`children` is a special prop that allows components to wrap other JSX.",
                useCase: "Creating reusable layout or wrapper components.",
                example:
                  "const Card = ({ children }) => <div className='card'>{children}</div>",
                usage: "<Card><p>Hello World</p></Card>",
              },

              componentReusability: {
                title: "Component Reusability",
                idea: "Write once, reuse everywhere with different props.",
                example: "Button used with different labels and actions",
                benefits: [
                  "Less duplicated code",
                  "Consistent UI",
                  "Easy maintenance",
                ],
              },

              dataFlow: {
                title: "One-Way Data Flow (IMPORTANT)",
                explanation: "React follows one-way data flow: parent → child.",
                analogy:
                  "Like water flowing downhill — child cannot change parent directly.",
              },

              commonMistakes: [
                {
                  mistake: "Trying to modify props",
                  issue: "Props are read-only",
                  fix: "Use state instead (coming next)",
                },
                {
                  mistake: "Using lowercase component names",
                  issue: "React treats it as HTML tag",
                  fix: "Always capitalize component names",
                },
                {
                  mistake: "Too many props",
                  issue: "Component becomes hard to maintain",
                  fix: "Break into smaller components",
                },
              ],

              proTips: [
                "Keep components small and focused",
                "Prefer composition over duplication",
                "Use children for layout components",
                "If logic grows, split components",
              ],

              realWorldAnalogy:
                "Components are like LEGO blocks:\n- Each block has a shape (JSX)\n- Props are labels/colors given to blocks\n- Same block can be reused in many places",
            },

            tasks: [
              "Create multiple functional components",
              "Pass props from parent to child",
              "Destructure props in components",
              "Use children props for layout",
            ],

            examples: [
              "const Card = ({ title, children }) => <div>{children}</div>;",
              "<Card title='Profile'>Content</Card>",
            ],

            useCases: [
              "Reusable UI components",
              "Design systems",
              "Scalable React applications",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Understand component-based architecture",
              "Pass and consume props correctly",
              "Build reusable components",
              "Prepare for state and hooks",
            ],
          },
          {
            day: 4,
            topic: "Rendering Lists & Conditional Rendering",

            subTopics: [
              "Rendering lists using map",
              "Key prop importance",
              "Conditional rendering with &&",
              "Ternary operator in JSX",
              "Empty state handling",
            ],

            explanation:
              "Most real-world UIs are dynamic — lists change and elements appear or disappear based on data. React provides simple, powerful patterns to handle this using JavaScript expressions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Dynamic List Rendering Drill",
                description:
                  "Render a real-world dynamic list and handle all edge cases.",
                tasks: [
                  "Create an array of objects (id, name)",
                  "Render list using map()",
                  "Add proper key props",
                  "Update list by adding/removing items",
                ],
                validationChecklist: [
                  "Each item has stable unique key",
                  "UI updates correctly on list change",
                  "No React warnings in console",
                ],
                outcome:
                  "You’ll understand how React efficiently updates lists.",
              },
              {
                id: "pt-2",
                title: "Key Prop Bug Simulation",
                description: "Experience why using index as key is dangerous.",
                tasks: [
                  "Render list using index as key",
                  "Reorder or remove items",
                  "Observe incorrect UI behavior",
                  "Fix by using unique IDs",
                ],
                qualityRule: "Keys must represent item identity, not position",
                outcome: "You’ll never misuse key props again.",
              },
              {
                id: "pt-3",
                title: "Conditional UI States",
                description:
                  "Build UI that reacts correctly to different data states.",
                tasks: [
                  "Render loading state",
                  "Render empty state",
                  "Render populated list",
                  "Switch states using conditions",
                ],
                mindset: "Every state deserves UI",
                outcome: "You’ll build user-friendly, production-ready UIs.",
              },
              {
                id: "pt-4",
                title: "Auth-Based Rendering",
                description: "Control UI visibility using logical conditions.",
                tasks: [
                  "Create isLoggedIn boolean",
                  "Render Dashboard when true",
                  "Render Login when false",
                  "Refactor using ternary operator",
                ],
                qualityRule: "Conditions must be readable and predictable",
                outcome: "You’ll confidently control UI flow in React apps.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most UI bugs in React come from wrong keys or missing states.",
              checklist: [
                "Are keys stable and unique?",
                "Is empty state handled?",
                "Does UI update correctly on data change?",
              ],
              goldenRule: "If React warns about keys, fix it immediately.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why is key prop important in React?",
                "Can we use index as key?",
                "How does React update lists efficiently?",
                "How do you conditionally render components?",
              ],
              strongAnswerPattern:
                "Explain reconciliation → keys → conditional expressions",
              redFlag: "Saying keys are only for removing warnings.",
            },

            editorGuidance: {
              careerMindset: [
                "Dynamic UI is default, not exception",
                "Empty state is part of UX",
                "Keys define identity, not order",
              ],
              seniorAdvice:
                "If your list breaks on update, your keys are wrong — always.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "In real applications, UI is rarely static. You render lists of products, users, messages, or notifications, and you show or hide UI based on conditions. React handles this naturally using JavaScript array methods and conditional expressions.",

              renderingLists: {
                title: "Rendering Lists with map()",
                concept:
                  "React renders lists by mapping over arrays and returning JSX for each item.",
                whyMap:
                  "map() returns a new array, which React can directly render.",
                example:
                  "const items = ['Apple', 'Banana'];\n<ul>{items.map(i => <li key={i}>{i}</li>)}</ul>",
                rule: "Always return JSX from map().",
              },

              keyProp: {
                title: "Key Prop (VERY IMPORTANT)",
                why: "Keys help React identify which items have changed, been added, or removed.",
                rules: [
                  "Key must be unique among siblings",
                  "Key must be stable (not index)",
                  "Use database IDs whenever possible",
                ],
                goodExample:
                  "items.map(item => <Item key={item.id} {...item} />)",
                badExample: "items.map((item, index) => <Item key={index} />)",
                consequence: "Wrong keys cause UI bugs and poor performance.",
              },

              conditionalRendering: {
                title: "Conditional Rendering",
                description:
                  "React allows conditional rendering using JavaScript expressions.",
                methods: [
                  {
                    type: "Logical &&",
                    usage: "condition && <Component />",
                    useCase: "Show something only if condition is true",
                  },
                  {
                    type: "Ternary operator",
                    usage: "condition ? <A /> : <B />",
                    useCase: "Choose between two UIs",
                  },
                ],
                example:
                  "{isLoggedIn && <Dashboard />}\n{items.length === 0 ? <Empty /> : <List />}",
              },

              emptyStateHandling: {
                title: "Empty State Handling",
                importance:
                  "Always handle empty or loading states for better UX.",
                example: "items.length === 0 && <p>No items found</p>",
                realWorld: "Used in search results, dashboards, notifications.",
              },

              commonMistakes: [
                {
                  mistake: "Using array index as key",
                  issue: "Causes incorrect UI updates",
                  fix: "Use unique IDs instead",
                },
                {
                  mistake: "Using if inside JSX",
                  issue: "JSX does not allow statements",
                  fix: "Use ternary or &&",
                },
                {
                  mistake: "Not handling empty arrays",
                  issue: "UI looks broken or blank",
                  fix: "Always show empty state UI",
                },
              ],

              performanceNotes: {
                tips: [
                  "Keep list items small",
                  "Avoid heavy logic inside map()",
                  "Use memoization for large lists (later topic)",
                ],
              },

              realWorldAnalogy:
                "Think of React lists like a restaurant order system:\n- Each item has an ID (key)\n- React updates only changed orders\n- Conditions decide what the customer sees",
            },

            tasks: [
              "Render dynamic list using map()",
              "Add correct key props",
              "Use conditional rendering with &&",
              "Display empty state UI",
            ],

            examples: [
              "items.map(item => <li key={item.id}>{item.name}</li>)",
              "{items.length === 0 && <p>No items</p>}",
            ],

            useCases: [
              "Product listings",
              "Notification systems",
              "Dashboards",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Render dynamic lists correctly",
              "Understand importance of key prop",
              "Apply conditional rendering patterns",
              "Build user-friendly dynamic UI",
            ],
          },
          {
            day: 5,
            topic: "Component File Structure & Best Practices",

            subTopics: [
              "Component folder structure",
              "Index files",
              "Naming conventions",
              "Presentational vs container components",
              "Keeping components small",
            ],

            explanation:
              "As React projects grow, good file and component structure becomes critical. Proper organization improves readability, scalability, and team collaboration.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Refactor Messy Component Structure",
                description:
                  "Convert a flat, messy component structure into a scalable one.",
                tasks: [
                  "Start with all components in a single folder",
                  "Group related files into component-based folders",
                  "Move JSX, styles, and logic together",
                  "Verify imports still work correctly",
                ],
                validationChecklist: [
                  "Each component has its own folder",
                  "No unrelated files inside a component folder",
                  "Project structure is easy to navigate",
                ],
                outcome:
                  "You’ll experience why structure matters in real projects.",
              },
              {
                id: "pt-2",
                title: "Index File Import Cleanup",
                description: "Improve import readability using index files.",
                tasks: [
                  "Create index.js for 3–4 components",
                  "Refactor imports to use folder-level imports",
                  "Remove deep relative import paths",
                ],
                qualityRule:
                  "Imports should not expose internal file structure",
                outcome: "Your imports will look professional and clean.",
              },
              {
                id: "pt-3",
                title: "Presentational vs Container Split",
                description:
                  "Separate UI and logic into proper component roles.",
                tasks: [
                  "Identify logic-heavy component",
                  "Extract pure UI into presentational component",
                  "Move state and logic into container",
                ],
                mindset: "UI should not know where data comes from",
                outcome: "You’ll write cleaner, testable components.",
              },
              {
                id: "pt-4",
                title: "Component Size Reduction Challenge",
                description:
                  "Refactor an oversized component into smaller pieces.",
                tasks: [
                  "Find a component >200 lines",
                  "Split it into smaller focused components",
                  "Ensure data flow remains clear",
                ],
                qualityRule: "One component = one responsibility",
                outcome: "You’ll build components that scale with teams.",
              },
            ],

            testingInsight: {
              coreReality: "Bad structure slows teams more than bad code.",
              checklist: [
                "Is the component easy to locate?",
                "Can a new developer understand it quickly?",
                "Is logic separated from UI?",
              ],
              goldenRule:
                "If you need to scroll too much, the component is too big.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you structure components in React?",
                "What is presentational vs container components?",
                "Why use index files?",
                "How do you keep React components maintainable?",
              ],
              strongAnswerPattern:
                "Explain feature-based structure → small components → clean imports",
              redFlag: "Saying structure doesn’t matter for small apps.",
            },

            editorGuidance: {
              careerMindset: [
                "Code is read more than written",
                "Structure communicates intent",
                "Scalable structure saves refactor time",
              ],
              seniorAdvice:
                "Good structure lets juniors move fast and seniors sleep well.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "Small demo apps work even with messy structure, but real-world React apps fail without clear organization. Clean component structure helps developers understand, extend, and debug code efficiently.",

              folderStructure: {
                title: "Component Folder Structure",
                recommended:
                  "Group files by feature or component, not by file type.",
                example:
                  "components/\n  Button/\n    Button.jsx\n    Button.css\n    index.js",
                why: [
                  "Everything related to a component lives together",
                  "Easy to delete, move, or refactor",
                  "Scales well for large apps",
                ],
              },

              indexFiles: {
                title: "Index Files Pattern",
                purpose:
                  "Index files simplify imports and hide internal file structure.",
                example:
                  "// Button/index.js\nexport { default } from './Button';",
                usage: "import Button from '@/components/Button';",
              },

              namingConventions: {
                title: "Naming Conventions (VERY IMPORTANT)",
                rules: [
                  "Component names must be PascalCase",
                  "File name should match component name",
                  "One component per file",
                  "Avoid generic names like Component1",
                ],
                good: ["UserCard.jsx", "ProductList.jsx"],
                bad: ["usercard.jsx", "component.jsx"],
              },

              presentationalVsContainer: {
                title: "Presentational vs Container Components",
                presentational: {
                  role: "Focus on UI only",
                  characteristics: [
                    "Receive data via props",
                    "No business logic",
                    "Reusable",
                  ],
                  example: "<Button label='Save' />",
                },
                container: {
                  role: "Handle logic and data",
                  characteristics: [
                    "Manage state and effects",
                    "Pass data to UI components",
                  ],
                  example: "<UserContainer />",
                },
                note: "With hooks, this distinction is softer but still useful conceptually.",
              },

              componentSize: {
                title: "Keeping Components Small",
                guideline: "If a component does too much, split it.",
                signsToSplit: [
                  "Component exceeds ~200 lines",
                  "Multiple responsibilities",
                  "Hard to test or reuse",
                ],
                benefit:
                  "Smaller components are easier to understand, test, and reuse.",
              },

              commonMistakes: [
                {
                  mistake: "Dumping all components in one folder",
                  issue: "Hard to navigate and scale",
                  fix: "Group components by feature or responsibility",
                },
                {
                  mistake: "Huge components with mixed logic",
                  issue: "Difficult to maintain",
                  fix: "Split into smaller components",
                },
                {
                  mistake: "Inconsistent naming",
                  issue: "Confusing imports",
                  fix: "Follow strict naming conventions",
                },
              ],

              proTips: [
                "Think in features, not files",
                "Colocate styles and tests with components",
                "Use index.js for clean imports",
                "Refactor structure early, not late",
              ],

              realWorldAnalogy:
                "Component structure is like organizing a warehouse:\n- Each item has a dedicated shelf\n- Labels are clear\n- Anyone can find things quickly\n- Scaling doesn’t create chaos",
            },

            tasks: [
              "Reorganize components into folders",
              "Add index files for cleaner imports",
              "Rename components using PascalCase",
              "Split a large component into smaller ones",
            ],

            examples: [
              "components/Button/Button.jsx",
              "components/Card/Card.jsx",
              "components/Card/index.js",
            ],

            useCases: [
              "Large-scale React applications",
              "Team collaboration",
              "Professional codebases",
            ],

            editorType: "react",

            estimatedTime: "40–50 minutes",

            objectives: [
              "Design scalable component structure",
              "Follow professional naming conventions",
              "Write clean and maintainable React code",
              "Prepare for real-world team projects",
            ],
          },
          {
            day: 6,
            topic: "PropTypes & Debugging React Components",

            subTopics: [
              "Why prop validation matters",
              "Using PropTypes",
              "Default props",
              "Common React warnings",
              "Debugging JSX errors",
            ],

            explanation:
              "As React apps grow, bugs often come from incorrect props passed to components. PropTypes help validate data early, while debugging skills help quickly fix UI and logic issues.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "PropTypes Safety Net Implementation",
                description:
                  "Add PropTypes to an existing component to catch silent bugs early.",
                tasks: [
                  "Pick a reusable component (Card, Button, UserItem)",
                  "Identify all incoming props",
                  "Add PropTypes with correct types",
                  "Mark critical props as isRequired",
                ],
                validationChecklist: [
                  "Console shows no prop-type warnings",
                  "Wrong prop types trigger warnings",
                  "Component still renders correctly",
                ],
                outcome:
                  "You’ll experience how PropTypes prevent invisible bugs.",
              },
              {
                id: "pt-2",
                title: "Default Props Stability Drill",
                description: "Make components resilient to missing props.",
                tasks: [
                  "Remove one required prop from parent",
                  "Observe UI break or warning",
                  "Add defaultProps to component",
                  "Verify UI no longer breaks",
                ],
                qualityRule:
                  "Component should never crash due to missing non-critical props",
                outcome: "Your components will become production-safe.",
              },
              {
                id: "pt-3",
                title: "React Warning Cleanup Challenge",
                description: "Treat console warnings as blockers.",
                tasks: [
                  "Trigger at least 2 common React warnings",
                  "Understand the root cause of each warning",
                  "Fix warnings one by one",
                  "Confirm clean console output",
                ],
                mindset: "Warnings today are bugs tomorrow",
                outcome: "You’ll develop the habit of clean React debugging.",
              },
              {
                id: "pt-4",
                title: "JSX Error Debugging Simulation",
                description: "Practice fixing real JSX mistakes quickly.",
                tasks: [
                  "Intentionally break JSX (missing tag, invalid return)",
                  "Read the React error message",
                  "Use stack trace to locate issue",
                  "Fix error without rewriting whole component",
                ],
                qualityRule: "Fix the root cause, not the symptom",
                outcome:
                  "You’ll debug JSX errors with confidence instead of panic.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most React bugs are not logic bugs — they are bad props.",
              checklist: [
                "Are all props validated?",
                "Do optional props have defaults?",
                "Is console completely warning-free?",
              ],
              goldenRule: "If the console is noisy, your app is lying to you.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are PropTypes and why are they used?",
                "Are PropTypes used in production?",
                "How do you debug React component issues?",
                "How do you handle wrong props passed to components?",
              ],
              strongAnswerPattern:
                "Explain dev-only validation → early warnings → safer reusable components",
              redFlag: "Saying PropTypes are unnecessary or ignoring warnings.",
            },

            editorGuidance: {
              careerMindset: [
                "Warnings are early failure signals",
                "Good developers fix root causes",
                "Debugging is a core React skill",
              ],
              seniorAdvice:
                "If you can debug React confidently, you’ll never fear large codebases.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "React components rely heavily on props. When incorrect data types or missing props are passed, bugs may appear silently in UI. PropTypes act like a safety net during development by validating props and showing warnings.",

              whyPropValidation: {
                title: "Why Prop Validation Matters",
                problemsWithoutValidation: [
                  "Undefined values causing UI crashes",
                  "Wrong data types breaking layout",
                  "Hard-to-track bugs in large codebases",
                ],
                benefits: [
                  "Early warning during development",
                  "Self-documenting components",
                  "Improved team collaboration",
                ],
              },

              usingPropTypes: {
                title: "Using PropTypes",
                description:
                  "PropTypes is a development-only library used to validate the props passed to components.",
                setup: "npm install prop-types",
                basicExample:
                  "import PropTypes from 'prop-types';\n\nfunction Card({ title }) {\n  return <h2>{title}</h2>;\n}\n\nCard.propTypes = {\n  title: PropTypes.string.isRequired,\n};",
                commonTypes: [
                  "PropTypes.string",
                  "PropTypes.number",
                  "PropTypes.bool",
                  "PropTypes.array",
                  "PropTypes.object",
                  "PropTypes.func",
                  "PropTypes.node",
                  "PropTypes.element",
                ],
              },

              defaultProps: {
                title: "Default Props",
                description:
                  "Default props provide fallback values when props are not passed.",
                example: "Card.defaultProps = {\n  title: 'Default Title',\n};",
                whyUseful: [
                  "Prevents undefined UI",
                  "Improves component robustness",
                ],
              },

              commonWarnings: {
                title: "Common React Warnings (VERY IMPORTANT)",
                warnings: [
                  {
                    warning: "Each child in a list should have a unique key",
                    cause: "Missing or unstable key prop",
                    fix: "Use unique IDs as keys",
                  },
                  {
                    warning: "Failed prop type",
                    cause: "Wrong prop type passed",
                    fix: "Update PropTypes or passed value",
                  },
                  {
                    warning: "Cannot read property of undefined",
                    cause: "Missing props or incorrect destructuring",
                    fix: "Add default props or optional chaining",
                  },
                ],
              },

              debuggingJSX: {
                title: "Debugging JSX Errors",
                commonIssues: [
                  "Missing closing tags",
                  "Returning multiple elements without fragment",
                  "Using statements inside JSX",
                  "Typos in component names",
                ],
                debuggingSteps: [
                  "Read error message carefully",
                  "Check component stack trace",
                  "Log props using console.log",
                  "Temporarily simplify JSX",
                ],
              },

              devTools: {
                title: "React Developer Tools",
                importance:
                  "React DevTools helps inspect component tree, props, and state.",
                usage: [
                  "Inspect component props",
                  "Track re-renders",
                  "Debug unexpected UI behavior",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Using PropTypes in production logic",
                  issue: "PropTypes run only in development",
                  fix: "Use runtime validation if required",
                },
                {
                  mistake: "Ignoring console warnings",
                  issue: "Small warnings lead to big bugs",
                  fix: "Always fix warnings early",
                },
                {
                  mistake: "Overusing PropTypes",
                  issue: "Verbose code in small projects",
                  fix: "Use where it adds value",
                },
              ],

              proTips: [
                "Treat warnings as errors during development",
                "Use PropTypes especially in reusable components",
                "Console.log props before assuming values",
                "Use React DevTools daily",
              ],

              realWorldAnalogy:
                "PropTypes are like airport baggage checks:\n- They don’t stop flights\n- But they warn you early\n- Preventing bigger problems later",
            },

            tasks: [
              "Install and use PropTypes",
              "Validate component props",
              "Add default props",
              "Fix React warnings",
              "Debug JSX errors",
            ],

            examples: [
              "Component.propTypes = { title: PropTypes.string.isRequired };",
              "Component.defaultProps = { title: 'Hello' };",
            ],

            useCases: [
              "Bug prevention",
              "Reusable component libraries",
              "Professional React codebases",
            ],

            editorType: "react",

            estimatedTime: "40–50 minutes",

            objectives: [
              "Validate component props confidently",
              "Understand and fix React warnings",
              "Debug JSX and props-related bugs",
              "Write safer React components",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Reusable Component Library",

            subTopics: [
              "Button components",
              "Card components",
              "Modal component",
              "Alert / Toast component",
              "Props-based customization",
            ],

            explanation:
              "This mini project focuses on building a reusable UI component library — a core concept behind design systems used in real-world products like Material UI, Ant Design, and Chakra UI.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Design-System Grade Button Component",
                description:
                  "Build a highly reusable Button component with multiple variants and sizes.",
                tasks: [
                  "Create Button component folder structure",
                  "Implement variant-based styling (primary, secondary, danger)",
                  "Add size support (sm, md, lg)",
                  "Handle disabled state correctly",
                ],
                validationChecklist: [
                  "Button styles change via props",
                  "Disabled button blocks clicks",
                  "Same component works in multiple places",
                ],
                outcome:
                  "You’ll think in reusable UI primitives like real design systems.",
              },
              {
                id: "pt-2",
                title: "Flexible Card Component Using children",
                description:
                  "Create a Card component that acts as a layout wrapper.",
                tasks: [
                  "Support title prop",
                  "Render children content correctly",
                  "Add optional footer section",
                  "Ensure Card works with any JSX inside",
                ],
                qualityRule: "Card should not care what content it wraps",
                outcome: "You’ll master composition over duplication.",
              },
              {
                id: "pt-3",
                title: "Controlled Modal Component",
                description:
                  "Build a modal using controlled props (no internal state abuse).",
                tasks: [
                  "Implement isOpen prop",
                  "Handle onClose callback",
                  "Prevent rendering when closed",
                  "Add basic accessibility (role, aria)",
                ],
                mindset: "Parent controls state, component controls UI",
                outcome: "You’ll understand controlled components deeply.",
              },
              {
                id: "pt-4",
                title: "Alert / Toast Feedback Component",
                description:
                  "Create a reusable feedback component for user actions.",
                tasks: [
                  "Support type-based styles (success, error, warning)",
                  "Render message via props",
                  "Add optional close handler",
                ],
                qualityRule:
                  "Feedback components must be predictable and reusable",
                outcome: "You’ll build UI feedback like real products do.",
              },
            ],

            testingInsight: {
              componentReality:
                "Reusable components fail when props are inconsistent.",
              checklist: [
                "Do all components work with different props?",
                "Are default props handled?",
                "Does UI break when props are missing?",
              ],
              goldenRule: "If a component works only once, it’s not reusable.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you design reusable components in React?",
                "How is this similar to Material UI or Chakra?",
                "How do props help in building design systems?",
                "How do you avoid duplication in UI components?",
              ],
              strongAnswerPattern:
                "Explain base component → props-driven variants → composition via children",
              redFlag: "Hardcoding styles or logic inside components.",
            },

            editorGuidance: {
              careerMindset: [
                "Think in components, not screens",
                "Props define flexibility",
                "Design systems scale teams, not just UI",
              ],
              seniorAdvice:
                "If you can build reusable components, frameworks become easy.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "Professional frontend applications rely on reusable component libraries to maintain consistency, speed up development, and scale efficiently. In this project, you will build your own mini design system using React components and props.",

              projectGoal: {
                title: "Project Goal",
                objective:
                  "Create a small but scalable component library that can be reused across multiple projects.",
                skillsPracticed: [
                  "Component reusability",
                  "Props-driven customization",
                  "Clean file structure",
                  "Real-world React patterns",
                ],
              },

              componentsToBuild: [
                {
                  name: "Button Component",
                  description:
                    "A reusable button that supports different variants, sizes, and states.",
                  props: [
                    "variant (primary, secondary, danger)",
                    "size (sm, md, lg)",
                    "disabled",
                    "onClick",
                    "children",
                  ],
                  example: "<Button variant='primary' size='md'>Save</Button>",
                },
                {
                  name: "Card Component",
                  description:
                    "A flexible container component for grouping content.",
                  props: ["title", "children", "footer (optional)"],
                  example: "<Card title='Product'><p>Description</p></Card>",
                },
                {
                  name: "Modal Component",
                  description: "A reusable modal dialog controlled via props.",
                  props: ["isOpen", "onClose", "title", "children"],
                  example:
                    "<Modal isOpen={true} onClose={closeFn}>Content</Modal>",
                },
                {
                  name: "Alert / Toast Component",
                  description:
                    "Feedback components for showing success, error, or warning messages.",
                  props: [
                    "type (success, error, warning)",
                    "message",
                    "onClose",
                  ],
                  example:
                    "<Alert type='success' message='Saved successfully' />",
                },
              ],

              propsCustomization: {
                title: "Props-based Customization (CORE IDEA)",
                explanation:
                  "Props allow one component to support multiple use cases without duplication.",
                example:
                  "const Button = ({ variant }) => <button className={`btn-${variant}`} />",
                benefit: "One component → many styles and behaviors",
              },

              fileStructure: {
                title: "Recommended Folder Structure",
                structure:
                  "components/\n  Button/\n    Button.jsx\n    Button.css\n    index.js\n  Card/\n    Card.jsx\n    Card.css\n    index.js\n  Modal/\n  Alert/",
                why: [
                  "Scales like real design systems",
                  "Easy to maintain",
                  "Interview-friendly structure",
                ],
              },

              bestPractices: [
                "Keep components UI-focused",
                "Avoid business logic inside components",
                "Use children for flexibility",
                "Validate props using PropTypes",
                "Keep styles predictable",
              ],

              commonMistakes: [
                {
                  mistake: "Hardcoding styles instead of using props",
                  issue: "Component becomes inflexible",
                  fix: "Use variant and size props",
                },
                {
                  mistake:
                    "Overloading a component with too many responsibilities",
                  issue: "Hard to maintain",
                  fix: "Split into smaller components",
                },
                {
                  mistake: "Skipping accessibility basics",
                  issue: "Poor UX",
                  fix: "Add role, aria-labels, keyboard support",
                },
              ],

              interviewValue: {
                title: "Why Interviewers Love This Project",
                points: [
                  "Shows real-world React thinking",
                  "Demonstrates component-driven design",
                  "Highlights reusability and scalability",
                  "Closer to professional UI libraries",
                ],
              },

              realWorldAnalogy:
                "This project is like building your own LEGO set:\n- Each block (component) is reusable\n- Different colors (props) change appearance\n- Same blocks build many structures",
            },

            tasks: [
              "Create Button component with variants",
              "Build Card component with children",
              "Create Modal with open/close logic",
              "Add Alert / Toast component",
              "Validate props using PropTypes",
              "Organize files professionally",
            ],

            examples: [
              "<Button variant='primary'>Submit</Button>",
              "<Card title='Profile'>User Info</Card>",
              "<Modal isOpen={isOpen} onClose={closeModal} />",
            ],

            useCases: [
              "Design systems",
              "Portfolio showcase",
              "Reusable UI kits",
              "Preparation for professional React work",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Build reusable React components",
              "Apply props-driven customization",
              "Understand design-system thinking",
              "Create portfolio-ready React project",
            ],
          },
        ],
      },
      {
        week: 20,
        weekGoal:
          "Master React state management using hooks and build interactive UIs",
        weekOutcome:
          "Ability to manage dynamic UI state, handle events, and build real-world forms",
        canLearn: ["students", "beginners", "professionals"],
        miniProject: "E-commerce Product Page",
        days: [
          {
            day: 1,
            topic: "useState Hook Fundamentals",

            subTopics: [
              "What is state in React",
              "useState syntax",
              "Initial state values",
              "Re-rendering on state change",
              "State immutability concept",
            ],

            explanation:
              "State represents dynamic data in a React component. The useState hook allows components to remember values between renders and update the UI automatically when data changes.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Counter State Mastery Drill",
                description:
                  "Build a counter that proves you understand state updates and re-rendering.",
                tasks: [
                  "Create a counter using useState",
                  "Add increment and decrement buttons",
                  "Prevent counter from going below zero",
                  "Log renders to observe re-render behavior",
                ],
                validationChecklist: [
                  "UI updates correctly on each click",
                  "No direct state mutation",
                  "Re-render happens only on state change",
                ],
                outcome: "You’ll clearly see how state controls UI updates.",
              },
              {
                id: "pt-2",
                title: "Toggle State Component",
                description: "Build a toggle component using boolean state.",
                tasks: [
                  "Initialize boolean state with useState(false)",
                  "Toggle state on button click",
                  "Conditionally render UI based on state",
                ],
                qualityRule: "Toggle logic must be readable and predictable",
                outcome:
                  "You’ll understand boolean state patterns used everywhere in React.",
              },
              {
                id: "pt-3",
                title: "Immutability Enforcement Exercise",
                description:
                  "Learn why mutating state breaks React’s mental model.",
                tasks: [
                  "Intentionally mutate state directly",
                  "Observe UI not updating correctly",
                  "Fix by creating a new state value",
                  "Re-test UI behavior",
                ],
                mindset: "State is immutable — always replace, never modify",
                outcome: "You’ll never accidentally mutate state again.",
              },
              {
                id: "pt-4",
                title: "Asynchronous State Awareness",
                description: "Understand why state updates are not immediate.",
                tasks: [
                  "Update state using setState",
                  "Log state before and after setState",
                  "Observe stale values in same render cycle",
                ],
                qualityRule: "Never assume state updates synchronously",
                outcome: "You’ll avoid one of the most common React bugs.",
              },
            ],

            testingInsight: {
              coreReality: "If state is wrong, UI will always be wrong.",
              checklist: [
                "Is state minimal?",
                "Is state updated immutably?",
                "Is UI fully derived from state?",
              ],
              goldenRule: "Never store something in state that can be derived.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is state in React?",
                "How does useState trigger re-renders?",
                "Why should state be immutable?",
                "Is setState synchronous?",
              ],
              strongAnswerPattern:
                "Explain state → setter → re-render → UI update",
              redFlag: "Saying React updates state immediately.",
            },

            editorGuidance: {
              careerMindset: [
                "State drives UI, not the other way around",
                "Minimal state = fewer bugs",
                "Immutability is non-negotiable",
              ],
              seniorAdvice: "If you don’t respect state, React will fight you.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "Until now, our React components were static — they only displayed data passed via props. Real applications need data that changes over time, such as counters, form inputs, toggles, and UI states. This is where state comes in.",

              whatIsState: {
                title: "What is State?",
                definition:
                  "State is a built-in object that stores data which can change over time and affects what is rendered on the screen.",
                characteristics: [
                  "Local to a component",
                  "Can change over time",
                  "Triggers UI re-render on update",
                ],
                example:
                  "A counter value, form input text, modal open/close status",
              },

              useStateSyntax: {
                title: "useState Hook Syntax",
                explanation:
                  "useState is a React Hook that lets you add state to functional components.",
                syntax: "const [state, setState] = useState(initialValue);",
                breakdown: [
                  "state → current value",
                  "setState → function to update value",
                  "initialValue → value used on first render only",
                ],
                example: "const [count, setCount] = useState(0);",
              },

              initialState: {
                title: "Initial State Values",
                rules: [
                  "Initial value is used only on first render",
                  "Can be number, string, boolean, array, or object",
                  "Should match expected data type",
                ],
                examples: [
                  "useState(0)",
                  "useState('')",
                  "useState(false)",
                  "useState([])",
                  "useState({})",
                ],
              },

              reRendering: {
                title: "Re-rendering on State Change (VERY IMPORTANT)",
                explanation:
                  "Whenever state updates, React automatically re-renders the component to reflect the new state.",
                flow: [
                  "User action (click, input, etc.)",
                  "setState is called",
                  "React schedules a re-render",
                  "UI updates with new state value",
                ],
                note: "Only the affected component (and its children) re-render, not the whole page.",
              },

              immutability: {
                title: "State Immutability Concept",
                explanation:
                  "State should never be modified directly. Instead, always create a new value and update state using the setter function.",
                wrong: "count = count + 1;",
                correct: "setCount(count + 1);",
                reason:
                  "React compares old and new state references to detect changes.",
              },

              commonPatterns: {
                title: "Common useState Patterns",
                examples: [
                  {
                    pattern: "Counter",
                    code: "const [count, setCount] = useState(0);\nsetCount(count + 1);",
                  },
                  {
                    pattern: "Toggle",
                    code: "const [isOpen, setIsOpen] = useState(false);\nsetIsOpen(!isOpen);",
                  },
                ],
              },

              commonMistakes: [
                {
                  mistake: "Updating state directly",
                  issue: "UI does not re-render",
                  fix: "Always use setState function",
                },
                {
                  mistake: "Expecting immediate state update",
                  issue: "State updates are asynchronous",
                  fix: "Do not rely on updated state immediately",
                },
                {
                  mistake: "Using state when props are enough",
                  issue: "Unnecessary complexity",
                  fix: "Use state only when data changes",
                },
              ],

              proTips: [
                "Keep state minimal",
                "One state = one responsibility",
                "Avoid duplicate state",
                "Think: Does this data really change?",
              ],

              realWorldAnalogy:
                "State is like a whiteboard in a meeting room:\n- Anyone can read it (UI)\n- Only specific actions update it (setState)\n- When it changes, everyone sees the update immediately",
            },

            tasks: [
              "Create a counter using useState",
              "Increment and decrement state",
              "Create a toggle component",
              "Observe re-render behavior",
            ],

            examples: [
              "const [count, setCount] = useState(0);",
              "setCount(count + 1);",
              "setIsOpen(prev => !prev);",
            ],

            useCases: [
              "Counters",
              "Toggles",
              "Dynamic UI updates",
              "User interaction handling",
            ],

            editorType: "react",

            estimatedTime: "50–60 minutes",

            objectives: [
              "Understand what state is",
              "Use useState correctly",
              "Trigger re-renders intentionally",
              "Avoid common state mistakes",
            ],
          },
          {
            day: 2,
            topic: "Event Handling in React",

            subTopics: [
              "onClick, onChange, onSubmit events",
              "Passing arguments to handlers",
              "Synthetic events",
              "Preventing default behavior",
              "Event handler best practices",
            ],

            explanation:
              "Event handling in React allows components to respond to user actions like clicks, typing, and form submissions using a consistent, cross-browser system.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Event Handler Correctness Drill",
                description:
                  "Build components that prove you understand how React event handlers actually work.",
                tasks: [
                  "Create a button with onClick handler",
                  "Ensure handler is passed as reference",
                  "Log when handler executes",
                  "Fix accidental immediate execution bugs",
                ],
                validationChecklist: [
                  "Handler runs only on user action",
                  "No function calls inside JSX",
                  "Clean handler naming",
                ],
                outcome:
                  "You’ll stop making the most common React event mistake.",
              },
              {
                id: "pt-2",
                title: "Argument Passing Pattern Practice",
                description: "Safely pass dynamic values to event handlers.",
                tasks: [
                  "Render a list of items",
                  "Pass item id to click handler",
                  "Use arrow function pattern",
                ],
                qualityRule:
                  "No unnecessary re-renders or inline logic explosion",
                outcome:
                  "You’ll confidently pass data to handlers in real apps.",
              },
              {
                id: "pt-3",
                title: "Form Submission Control Exercise",
                description:
                  "Understand why preventDefault is mandatory in React forms.",
                tasks: [
                  "Create a form with onSubmit",
                  "Observe page reload without preventDefault",
                  "Fix using e.preventDefault()",
                  "Handle form data using state",
                ],
                mindset: "React apps never rely on browser form reloads",
                outcome: "You’ll never forget preventDefault again.",
              },
              {
                id: "pt-4",
                title: "Synthetic Event Exploration",
                description: "Explore how React normalizes browser events.",
                tasks: [
                  "Log SyntheticEvent object",
                  "Access e.target.value in input handler",
                  "Compare behavior across different elements",
                ],
                qualityRule:
                  "Treat events as React abstractions, not raw DOM events",
                outcome:
                  "You’ll understand why React events feel consistent everywhere.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most UI bugs come from incorrect event handling, not state.",
              checklist: [
                "Is handler passed, not called?",
                "Is preventDefault used where needed?",
                "Is logic separated from JSX?",
              ],
              goldenRule:
                "If an event fires unexpectedly, check how it’s passed.",
            },

            interviewInsights: {
              commonQuestions: [
                "How are events handled in React?",
                "What are Synthetic Events?",
                "How do you pass arguments to event handlers?",
                "Why do we use preventDefault?",
              ],
              strongAnswerPattern:
                "Explain handler reference → SyntheticEvent → controlled behavior",
              redFlag: "Calling event handlers directly inside JSX.",
            },

            editorGuidance: {
              careerMindset: [
                "UI reacts to events, not DOM manipulation",
                "Clean handlers = predictable UI",
                "JSX should stay readable",
              ],
              seniorAdvice:
                "If your JSX is full of logic, your event handling is wrong.",
            },

            // 📚 Detailed Reading Content
            content: {
              introduction:
                "User interaction is the soul of any application. Buttons, forms, inputs, and links all rely on events. React handles these interactions using a unified event system called Synthetic Events, which ensures consistency across browsers.",

              reactEventsBasics: {
                title: "React Event Basics",
                description:
                  "React events look similar to DOM events, but they follow JavaScript camelCase naming and are passed as functions.",
                examples: [
                  "<button onClick={handleClick}>Click</button>",
                  "<input onChange={handleChange} />",
                  "<form onSubmit={handleSubmit}>",
                ],
                rules: [
                  "Use camelCase (onClick, onChange)",
                  "Pass function reference, not function call",
                ],
              },

              commonEvents: {
                title: "Commonly Used React Events",
                events: [
                  {
                    name: "onClick",
                    usage: "Buttons, icons, actions",
                  },
                  {
                    name: "onChange",
                    usage: "Inputs, textareas, selects",
                  },
                  {
                    name: "onSubmit",
                    usage: "Forms",
                  },
                  {
                    name: "onKeyDown",
                    usage: "Keyboard interactions",
                  },
                ],
              },

              passingArguments: {
                title: "Passing Arguments to Event Handlers",
                problem: "Sometimes handlers need extra data (like id).",
                correctWays: [
                  {
                    method: "Arrow function",
                    example:
                      "<button onClick={() => handleAdd(id)}>Add</button>",
                  },
                  {
                    method: "Bind (less common now)",
                    example:
                      "<button onClick={handleAdd.bind(null, id)}>Add</button>",
                  },
                ],
                warning: "Do NOT call function directly inside JSX.",
                wrongExample: "<button onClick={handleAdd(id)}>Add</button>",
              },

              syntheticEvents: {
                title: "Synthetic Events",
                explanation:
                  "React wraps native browser events inside SyntheticEvent objects for cross-browser consistency.",
                benefits: [
                  "Same behavior across all browsers",
                  "Better performance",
                  "Normalized event properties",
                ],
                example: "function handleClick(e) { console.log(e.target); }",
              },

              preventDefault: {
                title: "Preventing Default Behavior",
                whyNeeded:
                  "Forms reload the page by default — React apps are Single Page Applications.",
                example: "function handleSubmit(e) {\n  e.preventDefault();\n}",
                useCase: "Form submission without page refresh",
              },

              eventBestPractices: {
                title: "Event Handler Best Practices",
                practices: [
                  "Keep handlers small and focused",
                  "Move heavy logic outside JSX",
                  "Name handlers clearly (handleClick, handleSubmit)",
                  "Avoid inline logic explosion",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Calling handler instead of passing reference",
                  issue: "Function runs immediately",
                  fix: "Pass function or arrow function",
                },
                {
                  mistake: "Forgetting preventDefault on forms",
                  issue: "Page reloads",
                  fix: "Always call e.preventDefault()",
                },
                {
                  mistake: "Using DOM event patterns",
                  issue: "Inconsistent behavior",
                  fix: "Follow React event system",
                },
              ],

              proTips: [
                "React events are predictable and consistent",
                "Prefer arrow functions for passing arguments",
                "Forms almost always need preventDefault",
                "Event handling + state = dynamic UI",
              ],

              realWorldAnalogy:
                "React event handling is like a receptionist system:\n- All requests go through one desk (Synthetic Events)\n- Same rules apply to everyone\n- No chaos, predictable flow",
            },

            tasks: [
              "Handle button click events",
              "Pass arguments to event handlers",
              "Handle input change events",
              "Prevent form reload on submit",
            ],

            examples: [
              "<button onClick={() => handleClick(id)}>Add</button>",
              "event.preventDefault()",
              "<input onChange={handleChange} />",
            ],

            useCases: [
              "Button actions",
              "Form submissions",
              "User interactions",
            ],

            editorType: "react",

            estimatedTime: "45–55 minutes",

            objectives: [
              "Handle user events confidently",
              "Understand React synthetic events",
              "Prevent unwanted default behavior",
              "Write clean and maintainable event handlers",
            ],
          },
          {
            day: 3,
            topic: "Forms & Controlled Components",

            subTopics: [
              "Controlled vs uncontrolled inputs",
              "Handling input value with state",
              "Multiple inputs handling",
              "Textarea and select elements",
              "Checkbox and radio inputs",
            ],

            explanation:
              "Controlled components use React state as the single source of truth for form inputs, giving full control over user input, validation, and UI behavior.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Controlled vs Uncontrolled Conversion",
                description:
                  "Convert uncontrolled inputs into fully controlled components.",
                tasks: [
                  "Create an input with defaultValue",
                  "Convert it to controlled using useState",
                  "Bind value and onChange correctly",
                  "Verify UI updates on every keystroke",
                ],
                validationChecklist: [
                  "Input value comes from state",
                  "onChange updates state",
                  "No defaultValue remains",
                ],
                outcome:
                  "You’ll clearly understand why controlled inputs are preferred.",
              },
              {
                id: "pt-2",
                title: "Multi-Input Form Architecture",
                description:
                  "Build a scalable form using a single state object.",
                tasks: [
                  "Create email, password, role inputs",
                  "Use name attribute on all inputs",
                  "Handle all changes with one handler",
                ],
                qualityRule: "No separate useState per input field",
                outcome:
                  "You’ll learn how real production forms are structured.",
              },
              {
                id: "pt-3",
                title: "Checkbox & Radio Mastery",
                description: "Handle non-text inputs correctly in React.",
                tasks: [
                  "Build a checkbox toggle",
                  "Bind checked instead of value",
                  "Create radio buttons with selected state",
                ],
                validationChecklist: [
                  "Checkbox reflects boolean state",
                  "Radio selection stays consistent",
                ],
                outcome: "You’ll stop breaking checkboxes and radios in React.",
              },
              {
                id: "pt-4",
                title: "Form Submission & Validation Drill",
                description: "Handle form submit safely and predictably.",
                tasks: [
                  "Prevent default form submission",
                  "Validate required fields",
                  "Disable submit when invalid",
                  "Log final form object",
                ],
                mindset: "Forms never reload pages in React",
                outcome: "You’ll write production-ready form logic.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most form bugs come from missing bindings, not complex logic.",
              checklist: [
                "Does every input have value/checked?",
                "Is onChange wired correctly?",
                "Is state the single source of truth?",
              ],
              goldenRule:
                "If the UI and state go out of sync, the form is wrong.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are controlled components?",
                "Why are controlled inputs preferred in React?",
                "How do you handle multiple inputs efficiently?",
                "How do checkboxes differ from text inputs?",
              ],
              strongAnswerPattern:
                "Explain single source of truth → predictable state → validation",
              redFlag: "Relying on DOM values instead of React state.",
            },

            editorGuidance: {
              careerMindset: [
                "Forms are state machines",
                "State controls UI, not the DOM",
                "Predictability beats shortcuts",
              ],
              seniorAdvice:
                "If your form logic feels messy, your state structure is wrong.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Forms are the backbone of real-world applications — login, signup, search, filters, payments. React handles forms differently from plain HTML by keeping input values inside state. This approach is called Controlled Components.",

              controlledVsUncontrolled: {
                title: "Controlled vs Uncontrolled Components",
                controlled: {
                  definition: "Input value is controlled by React state.",
                  example:
                    "<input value={email} onChange={e => setEmail(e.target.value)} />",
                  pros: [
                    "Full control over data",
                    "Easy validation",
                    "Predictable UI",
                  ],
                },
                uncontrolled: {
                  definition: "Input value is managed by the DOM itself.",
                  example: "<input defaultValue='test@example.com' />",
                  cons: ["Harder validation", "Not React-friendly"],
                },
                verdict:
                  "In professional React apps, controlled components are the default.",
              },

              basicControlledInput: {
                title: "Basic Controlled Input",
                explanation:
                  "The input value always comes from state, and onChange updates the state.",
                example: `
const [email, setEmail] = useState("");

<input
  value={email}
  onChange={(e) => setEmail(e.target.value)}
/>
      `,
                flow: "User types → onChange fires → state updates → component re-renders",
              },

              multipleInputs: {
                title: "Handling Multiple Inputs",
                explanation:
                  "Instead of creating multiple states, forms usually store values in one object.",
                example: `
const [form, setForm] = useState({
  email: "",
  password: "",
});

function handleChange(e) {
  setForm({
    ...form,
    [e.target.name]: e.target.value,
  });
}
      `,
                bestPractice: "Use name attribute to map inputs to state keys.",
              },

              textareaAndSelect: {
                title: "Textarea & Select Inputs",
                textarea: {
                  example: `
<textarea
  value={message}
  onChange={(e) => setMessage(e.target.value)}
/>
        `,
                },
                select: {
                  example: `
<select value={role} onChange={(e) => setRole(e.target.value)}>
  <option value='user'>User</option>
  <option value='admin'>Admin</option>
</select>
        `,
                },
                note: "Textarea and select behave exactly like input in controlled forms.",
              },

              checkboxAndRadio: {
                title: "Checkbox & Radio Inputs",
                checkbox: {
                  explanation: "Checkbox uses checked instead of value.",
                  example: `
<input
  type="checkbox"
  checked={isActive}
  onChange={(e) => setIsActive(e.target.checked)}
/>
        `,
                },
                radio: {
                  example: `
<input
  type="radio"
  value="male"
  checked={gender === 'male'}
  onChange={(e) => setGender(e.target.value)}
/>
        `,
                },
              },

              formSubmit: {
                title: "Form Submission",
                explanation: "Forms must prevent default browser reload.",
                example: `
function handleSubmit(e) {
  e.preventDefault();
  console.log(form);
}
      `,
              },

              validationBasics: {
                title: "Basic Validation Strategy",
                rules: [
                  "Validate before submit",
                  "Disable submit when invalid",
                  "Show error messages clearly",
                ],
                example: "Disable submit button if email or password is empty",
              },

              commonMistakes: [
                {
                  mistake: "Using value without onChange",
                  issue: "Input becomes read-only",
                  fix: "Always pair value with onChange",
                },
                {
                  mistake: "Too many useState hooks",
                  issue: "Messy form logic",
                  fix: "Use single form object",
                },
                {
                  mistake: "Forgetting checked for checkbox",
                  issue: "Checkbox not working properly",
                  fix: "Use checked instead of value",
                },
              ],

              bestPractices: [
                "Always use controlled inputs",
                "Group form data in one state object",
                "Use name attribute for scalability",
                "Keep validation logic separate",
              ],

              realWorldAnalogy:
                "Controlled forms are like driving a car with power steering — you control everything. Uncontrolled forms are like pushing the car manually.",
            },

            tasks: [
              "Create controlled input fields",
              "Handle multiple form fields with one state",
              "Build login form with email & password",
              "Handle checkbox and select input",
            ],

            examples: [
              "<input value={email} onChange={e => setEmail(e.target.value)} />",
              "<textarea value={msg} onChange={handleChange} />",
              "<input type='checkbox' checked={isChecked} />",
            ],

            useCases: [
              "Login forms",
              "Signup forms",
              "Search & filters",
              "User data collection",
            ],

            editorType: "react",

            estimatedTime: "50–60 minutes",

            objectives: [
              "Master controlled components",
              "Handle real-world forms confidently",
              "Avoid common React form bugs",
              "Prepare for advanced form libraries",
            ],
          },
          {
            day: 4,
            topic: "Updating State Correctly",

            subTopics: [
              "Functional updates in useState",
              "Updating objects in state",
              "Updating arrays in state",
              "Avoiding direct mutations",
              "Batching state updates",
            ],

            explanation:
              "React state must be updated immutably. Correct state updates ensure predictable UI, proper re-renders, and bug-free applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Functional Update Bug Fix",
                description:
                  "Fix incorrect state updates caused by stale values.",
                tasks: [
                  "Create a counter with multiple setCount calls",
                  "Observe incorrect increment using value-based updates",
                  "Refactor using functional updates",
                  "Verify correct final count",
                ],
                validationChecklist: [
                  "Uses prev => prev + 1",
                  "Multiple updates work correctly",
                ],
                outcome:
                  "You’ll fully understand why functional updates exist.",
              },
              {
                id: "pt-2",
                title: "Immutable Object Update Drill",
                description: "Safely update object and nested object state.",
                tasks: [
                  "Update a user object property",
                  "Update nested address object",
                  "Ensure no direct mutation occurs",
                ],
                qualityRule: "Every object level must be copied using spread",
                outcome: "You’ll avoid silent UI bugs in object state.",
              },
              {
                id: "pt-3",
                title: "Array State Operations",
                description:
                  "Perform add, update, and remove operations immutably.",
                tasks: [
                  "Add item using spread",
                  "Remove item using filter",
                  "Update item using map",
                ],
                antiPattern: "Never use push, pop, splice on state",
                outcome: "You’ll handle dynamic lists like a senior developer.",
              },
              {
                id: "pt-4",
                title: "Mutation Detection Challenge",
                description: "Identify and fix mutation-related bugs.",
                tasks: [
                  "Analyze broken code that mutates state",
                  "Fix using immutable patterns",
                  "Confirm React re-renders correctly",
                ],
                mindset: "If UI doesn’t update, suspect mutation first",
                outcome: "You’ll debug real-world React state issues faster.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most React bugs come from incorrect state updates, not JSX.",
              checklist: [
                "Am I mutating state?",
                "Am I relying on stale values?",
                "Did I create a new reference?",
              ],
              goldenRule: "No new reference = no guaranteed re-render.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why must React state be immutable?",
                "Difference between setState(value) and setState(fn)?",
                "What happens if you mutate state directly?",
                "How does React detect state changes?",
              ],
              strongAnswerPattern:
                "Explain reference comparison → re-render → predictability",
              redFlag: "Using push/splice or direct assignment on state.",
            },

            editorGuidance: {
              careerMindset: [
                "State updates are logic, not syntax",
                "Immutability = predictability",
                "React trusts references, not intentions",
              ],
              seniorAdvice:
                "When state logic feels complex, your data shape is wrong.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "React does NOT track changes by comparing deep values. It checks references. If you mutate existing state instead of creating new copies, React may not re-render — leading to silent bugs.",

              whyImmutabilityMatters: {
                title: "Why Immutability is Mandatory",
                explanation:
                  "React decides whether to re-render based on reference changes, not value changes.",
                badExample: `
user.name = "John";
setUser(user); // ❌ React may NOT re-render
      `,
                goodExample: `
setUser({ ...user, name: "John" }); // ✅ New reference
      `,
                keyRule:
                  "Never modify existing state directly. Always create a new copy.",
              },

              functionalUpdates: {
                title: "Functional Updates in useState",
                explanation:
                  "When new state depends on previous state, always use the functional update form.",
                problemExample: `
setCount(count + 1);
setCount(count + 1); // ❌ result may still be +1
      `,
                correctExample: `
setCount(prev => prev + 1);
setCount(prev => prev + 1); // ✅ result +2
      `,
                whenToUse: [
                  "Counters",
                  "Toggles",
                  "Queue-like updates",
                  "Multiple state updates in same event",
                ],
              },

              updatingObjects: {
                title: "Updating Objects in State",
                explanation:
                  "Objects must be copied using spread operator before updating.",
                example: `
const [user, setUser] = useState({
  name: "Alex",
  age: 25,
});

setUser(prev => ({
  ...prev,
  age: 26,
}));
      `,
                nestedObjects: {
                  title: "Updating Nested Objects",
                  example: `
setUser(prev => ({
  ...prev,
  address: {
    ...prev.address,
    city: "Delhi",
  },
}));
        `,
                },
              },

              updatingArrays: {
                title: "Updating Arrays in State",
                addItem: {
                  explanation: "Always create a new array",
                  example: `
setItems(prev => [...prev, newItem]);
        `,
                },
                removeItem: {
                  example: `
setItems(prev => prev.filter(item => item.id !== id));
        `,
                },
                updateItem: {
                  example: `
setItems(prev =>
  prev.map(item =>
    item.id === id ? { ...item, done: true } : item
  )
);
        `,
                },
                commonMistake: `
items.push(newItem);
setItems(items); // ❌ Mutation
      `,
              },

              batchingUpdates: {
                title: "Batching State Updates (React 18)",
                explanation:
                  "React batches multiple state updates into a single re-render for performance.",
                example: `
setCount(prev => prev + 1);
setCount(prev => prev + 1);
// React batches → single re-render
      `,
                importantNote:
                  "Batching works automatically in React 18, even inside async code.",
              },

              commonBugs: [
                {
                  bug: "Mutating arrays with push/splice",
                  issue: "UI not updating",
                  fix: "Use spread, filter, map",
                },
                {
                  bug: "Using stale state value",
                  issue: "Incorrect updates",
                  fix: "Use functional updates",
                },
                {
                  bug: "Updating nested objects incorrectly",
                  issue: "Partial UI updates",
                  fix: "Spread at every level",
                },
              ],

              bestPractices: [
                "Always treat state as immutable",
                "Use functional updates when state depends on previous value",
                "Prefer map/filter over push/splice",
                "Keep state shape simple",
                "Normalize complex data if needed",
              ],

              realWorldExamples: [
                {
                  case: "Shopping Cart",
                  logic:
                    "Add/remove/update items using immutable array updates",
                },
                {
                  case: "Todo App",
                  logic: "Toggle completed state using map + spread",
                },
                {
                  case: "Forms",
                  logic: "Update field values using object spread",
                },
              ],

              interviewTips: [
                "Why React state should be immutable?",
                "Difference between setState(value) and setState(fn)",
                "What happens if we mutate state directly?",
                "How does React detect state changes?",
              ],
            },

            tasks: [
              "Update object state safely using spread",
              "Add and remove items from array state",
              "Fix a bug caused by direct mutation",
              "Convert value-based updates to functional updates",
            ],

            examples: [
              "setItems(prev => [...prev, newItem])",
              "setUser(prev => ({ ...prev, name: 'John' }))",
              "setCount(prev => prev + 1)",
            ],

            useCases: [
              "Shopping carts",
              "Todo lists",
              "Dynamic lists",
              "Form state management",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Master immutable state updates",
              "Avoid silent React bugs",
              "Write predictable React logic",
              "Prepare for complex state management",
            ],
          },
          {
            day: 5,
            topic: "State vs Props & State Lifting",

            subTopics: [
              "Difference between state and props",
              "Unidirectional data flow",
              "Lifting state up",
              "Parent-child communication",
              "Callback props",
            ],

            explanation:
              "Understanding the difference between state and props — and knowing where state should live — is critical for building scalable, predictable React applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "State vs Props Identification Drill",
                description:
                  "Train your brain to decide where data should live.",
                tasks: [
                  "Analyze 3 components with mixed state & props",
                  "Identify wrongly placed state",
                  "Refactor to correct ownership",
                ],
                validationChecklist: [
                  "State exists only in owner component",
                  "Child receives data only via props",
                ],
                outcome: "You’ll stop putting state in the wrong component.",
              },
              {
                id: "pt-2",
                title: "State Lifting Refactor Challenge",
                description: "Fix duplicated state across sibling components.",
                tasks: [
                  "Create two sibling components with same state",
                  "Observe UI desync",
                  "Lift state to closest common parent",
                  "Pass data down via props",
                ],
                qualityRule: "Single source of truth must exist",
                outcome:
                  "You’ll understand when and why state lifting is required.",
              },
              {
                id: "pt-3",
                title: "Child → Parent Communication",
                description: "Implement safe upward data flow using callbacks.",
                tasks: [
                  "Create parent state",
                  "Pass callback function to child",
                  "Trigger parent state update from child",
                ],
                antiPattern: "Child must never update parent state directly",
                outcome: "You’ll master callback props — a core React skill.",
              },
              {
                id: "pt-4",
                title: "Prop Drilling Awareness Exercise",
                description: "Identify early signs of prop drilling problems.",
                tasks: [
                  "Pass props through 3+ component levels",
                  "Identify readability issues",
                  "Mark places where Context may help (later)",
                ],
                mindset: "Prop drilling is a smell, not always a bug",
                outcome: "You’ll know when architecture needs refactoring.",
              },
            ],

            testingInsight: {
              coreReality:
                "UI bugs often come from duplicated or misplaced state.",
              checklist: [
                "Is this state shared?",
                "Who truly owns this data?",
                "Is the same state stored twice?",
              ],
              goldenRule: "If two components need the same data, lift it.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between props and state?",
                "What is lifting state up?",
                "How does child update parent state?",
                "What is single source of truth?",
                "When should state NOT be lifted?",
              ],
              strongAnswerPattern:
                "Explain ownership → data flow → predictability",
              redFlag: "Duplicating the same state in multiple components.",
            },

            editorGuidance: {
              careerMindset: [
                "State location defines architecture",
                "Bad state placement creates long-term bugs",
                "Clean data flow beats clever hacks",
              ],
              seniorAdvice:
                "If props feel messy, your state hierarchy is wrong.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "One of the most common React mistakes is putting state in the wrong component. This leads to prop drilling, duplicated state, and hard-to-maintain code.",

              stateVsProps: {
                title: "State vs Props (Core Difference)",
                comparison: [
                  {
                    concept: "State",
                    description:
                      "State is local, mutable data owned and managed by a component.",
                    example: "useState inside a component",
                  },
                  {
                    concept: "Props",
                    description:
                      "Props are read-only data passed from parent to child.",
                    example: "<Child count={count} />",
                  },
                ],
                rule: "If a component owns the data → use state. If it receives data → use props.",
              },

              unidirectionalFlow: {
                title: "Unidirectional Data Flow",
                explanation:
                  "React follows one-way data flow: Parent → Child. Data never flows upward directly.",
                example: `
Parent (state)
   ↓ props
Child (UI)
      `,
                whyImportant: [
                  "Predictable UI",
                  "Easy debugging",
                  "Clear data ownership",
                ],
              },

              liftingStateUp: {
                title: "Lifting State Up",
                explanation:
                  "When multiple components need the same data, move the state to their closest common parent.",
                problemExample: `
❌ Each child has its own state
<CounterA />
<CounterB />
      `,
                solutionExample: `
✅ Lift state to parent
<Counter value={count} />
      `,
                exampleCode: `
const Parent = () => {
  const [count, setCount] = useState(0);

  return (
    <>
      <ChildA count={count} />
      <ChildB count={count} />
    </>
  );
};
      `,
                whenToLift: [
                  "Shared data between siblings",
                  "Multiple components need same state",
                  "Single source of truth required",
                ],
              },

              parentChildCommunication: {
                title: "Child → Parent Communication (Callback Props)",
                explanation:
                  "Children cannot change parent state directly. Parents pass callback functions as props.",
                example: `
const Parent = () => {
  const addItem = item => setItems(prev => [...prev, item]);

  return <Child onAdd={addItem} />;
};

const Child = ({ onAdd }) => {
  return <button onClick={() => onAdd('New Item')}>Add</button>;
};
      `,
                keyRule:
                  "State changes always happen where the state is defined.",
              },

              commonMistakes: [
                {
                  mistake: "Duplicating same state in multiple components",
                  issue: "UI goes out of sync",
                  fix: "Lift state up",
                },
                {
                  mistake: "Trying to modify props",
                  issue: "React warnings / bugs",
                  fix: "Use state in parent",
                },
                {
                  mistake: "Too much prop drilling",
                  issue: "Messy code",
                  fix: "Consider context (later)",
                },
              ],

              bestPractices: [
                "Keep state as high as necessary, but as low as possible",
                "Avoid duplicating derived data in state",
                "Prefer single source of truth",
                "Use callbacks for child → parent updates",
                "Refactor when props become hard to manage",
              ],

              realWorldExamples: [
                {
                  case: "Multi-step Form",
                  logic: "Form data stored in parent, steps as children",
                },
                {
                  case: "Shopping Cart",
                  logic: "Cart state in parent, item components receive props",
                },
                {
                  case: "Filters & Lists",
                  logic: "Filter state lifted to parent controlling list",
                },
              ],

              interviewTips: [
                "Difference between props and state?",
                "What is lifting state up?",
                "How does child communicate with parent?",
                "What is unidirectional data flow?",
                "When should state NOT be lifted?",
              ],
            },

            tasks: [
              "Lift state from child to parent",
              "Pass data from parent to child using props",
              "Implement child-to-parent update using callback",
              "Refactor duplicated state into single source",
            ],

            examples: [
              "<Child onAdd={handleAdd} />",
              "<Product price={price} />",
              "setValue(prev => prev + 1)",
            ],

            useCases: [
              "Shared data between components",
              "Forms with multiple steps",
              "Component coordination",
              "Scalable UI architecture",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Understand correct state ownership",
              "Avoid duplicated and conflicting state",
              "Build scalable React component trees",
              "Prepare for Context & state management libraries",
            ],
          },
          {
            day: 6,
            topic: "Form Validation Patterns",

            subTopics: [
              "Basic input validation",
              "Error state management",
              "Conditional error messages",
              "Disabling submit button",
              "Simple regex validation",
            ],

            explanation:
              "Client-side validation improves user experience by providing instant feedback before data is submitted to the server.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Signup Form Validation (Real UX)",
                description:
                  "Implement real-world validation exactly like production apps.",
                tasks: [
                  "Create email & password fields",
                  "Validate email using regex",
                  "Validate password length (min 6)",
                  "Show error only after user interaction",
                  "Disable submit until form is valid",
                ],
                uxRule: "Validation should guide the user, not scare them.",
                outcome:
                  "You’ll build forms that feel professional and user-friendly.",
              },
              {
                id: "pt-2",
                title: "Centralized Validation Function",
                description: "Avoid scattered validation logic across JSX.",
                tasks: [
                  "Create validateForm(formData) function",
                  "Return errors object",
                  "Update error state from one place",
                ],
                antiPattern: "Inline validation conditions inside JSX",
                outcome: "Cleaner, scalable validation architecture.",
              },
              {
                id: "pt-3",
                title: "Touched Fields & Progressive Errors",
                description:
                  "Show errors only when users interact with inputs.",
                tasks: [
                  "Track touched fields in state",
                  "Display error only after blur/change",
                  "Avoid showing errors on first render",
                ],
                mindset: "Good UX hides errors until they matter.",
                outcome: "You’ll stop building annoying forms.",
              },
              {
                id: "pt-4",
                title: "Submit Guard Pattern",
                description: "Ensure invalid data never reaches submit logic.",
                tasks: [
                  "Compute isFormValid boolean",
                  "Disable submit button",
                  "Recheck validation on submit",
                ],
                securityNote:
                  "Frontend validation improves UX, backend validation ensures safety.",
                outcome: "No accidental invalid submissions.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most form bugs come from bad error-state handling, not regex.",
              checklist: [
                "Are errors stored in state?",
                "Does UI fully depend on error state?",
                "Can invalid data still be submitted?",
              ],
              goldenRule: "Validation logic controls state, state controls UI.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you handle form validation in React?",
                "Where should validation logic live?",
                "How do you manage error state?",
                "How do you disable submit button?",
                "Frontend vs backend validation difference?",
              ],
              strongAnswerPattern:
                "Controlled inputs → validation function → error state → conditional UI",
              redFlag: "Validating only on submit or relying only on backend.",
            },

            editorGuidance: {
              careerMindset: [
                "Validation is UX, not just logic",
                "Bad validation breaks user trust",
                "Clean error-state management scales better",
              ],
              seniorAdvice:
                "If validation logic is scattered, your form architecture is wrong.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Form validation is one of the most common frontend responsibilities. A well-validated form prevents bad data, reduces server load, and improves user trust.",

              validationBasics: {
                title: "Basic Input Validation",
                explanation:
                  "Basic validation checks input values before submission, such as required fields, minimum length, and simple format rules.",
                examples: [
                  "Empty field check",
                  "Minimum password length",
                  "Email format validation",
                ],
              },

              errorStateManagement: {
                title: "Managing Error State in React",
                explanation:
                  "Errors should be stored in React state so the UI updates automatically when validation fails or passes.",
                exampleCode: `
const [errors, setErrors] = useState({});

if (!email.includes('@')) {
  setErrors(prev => ({ ...prev, email: 'Invalid email' }));
}
      `,
                keyRule: "Validation logic updates state → state controls UI",
              },

              conditionalErrors: {
                title: "Conditional Error Messages",
                explanation:
                  "Error messages should only appear when input is invalid, not permanently.",
                exampleCode: `
{errors.email && <p className='error'>{errors.email}</p>}
      `,
                bestPractice:
                  "Never show errors before user interaction (avoid bad UX)",
              },

              disablingSubmit: {
                title: "Disabling Submit Button",
                explanation:
                  "Submit buttons should be disabled when form data is invalid to prevent incorrect submissions.",
                exampleCode: `
<button disabled={!isFormValid}>Submit</button>
      `,
                commonRule:
                  "Form is valid only when all required fields pass validation",
              },

              regexValidation: {
                title: "Simple Regex Validation",
                explanation:
                  "Regex is commonly used for validating email, phone numbers, and passwords.",
                examples: [
                  "/^\\S+@\\S+\\.\\S+$/ for email",
                  "/.{6,}/ for password length",
                ],
                warning:
                  "Avoid overly complex regex on frontend — keep it readable",
              },

              validationFlow: {
                title: "Typical Validation Flow",
                steps: [
                  "User types input",
                  "onChange updates state",
                  "Validation runs",
                  "Error state updates",
                  "UI reflects valid/invalid state",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Validating only on submit",
                  issue: "Poor user experience",
                  fix: "Validate on change or blur",
                },
                {
                  mistake: "Hardcoding error messages",
                  issue: "Unmaintainable code",
                  fix: "Centralize validation logic",
                },
                {
                  mistake: "Allowing submit on invalid data",
                  issue: "Backend errors",
                  fix: "Disable submit button",
                },
              ],

              bestPractices: [
                "Keep validation logic simple",
                "Use controlled components",
                "Show clear and specific error messages",
                "Validate progressively, not aggressively",
                "Prepare validation structure for form libraries later",
              ],

              realWorldExamples: [
                {
                  case: "Signup Form",
                  validations: [
                    "Email format",
                    "Password length",
                    "Required fields",
                  ],
                },
                {
                  case: "Checkout Form",
                  validations: ["Address required", "Card number format"],
                },
                {
                  case: "Login Form",
                  validations: ["Empty fields", "Basic format checks"],
                },
              ],

              interviewTips: [
                "How do you handle form validation in React?",
                "Controlled vs uncontrolled inputs?",
                "How do you disable submit button?",
                "Where should validation logic live?",
                "Frontend vs backend validation?",
              ],
            },

            tasks: [
              "Add validation to form fields using state",
              "Show error messages conditionally",
              "Disable submit button when form is invalid",
              "Validate email and password fields",
            ],

            examples: [
              "email.includes('@')",
              "password.length < 6",
              "errors.email && <span>Error</span>",
            ],

            useCases: [
              "Signup forms",
              "Checkout forms",
              "Authentication flows",
              "User input-heavy UIs",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Build user-friendly validated forms",
              "Manage error state cleanly",
              "Prevent invalid submissions",
              "Prepare for form libraries like React Hook Form",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – E-commerce Product Page",

            subTopics: [
              "Product image gallery",
              "Variant selection (size, color)",
              "Quantity selector",
              "Add to cart state",
              "Dynamic price calculation",
            ],

            explanation:
              "This project simulates a real-world e-commerce product page using React state management, similar to what is built in production applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Product Variant State Modeling",
                description:
                  "Design clean state structure for product variants like real e-commerce apps.",
                tasks: [
                  "Create base product object",
                  "Model variants (color, size) separately",
                  "Ensure only one variant is active at a time",
                ],
                architectureRule:
                  "UI should always be driven by state, not DOM hacks.",
                outcome:
                  "You’ll learn how real product data is structured in production.",
              },
              {
                id: "pt-2",
                title: "Derived Price Calculation Challenge",
                description:
                  "Practice calculating values without storing them in state.",
                tasks: [
                  "Use base price + variant modifier",
                  "Multiply by quantity",
                  "Recalculate on every render",
                ],
                antiPattern: "Storing totalPrice in useState",
                outcome: "You’ll master derived state — a senior React skill.",
              },
              {
                id: "pt-3",
                title: "Add to Cart Payload Design",
                description:
                  "Prepare cart data exactly how backend APIs expect.",
                tasks: [
                  "Combine productId, variant, quantity, price",
                  "Create single cartItem object",
                  "Validate before adding to cart",
                ],
                qualityChecklist: [
                  "No missing variant",
                  "Quantity >= 1",
                  "Price derived, not stored",
                ],
                outcome: "You’ll think like a full-stack engineer.",
              },
              {
                id: "pt-4",
                title: "Disable Invalid User Actions",
                description: "Prevent broken UX by guarding actions.",
                tasks: [
                  "Disable Add to Cart if variant not selected",
                  "Disable quantity decrement below 1",
                  "Handle out-of-stock scenario",
                ],
                mindset: "If user can break it, user will break it.",
                outcome: "Your UI will feel production-grade.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most e-commerce bugs come from incorrect state combinations.",
              checklist: [
                "Is variant selection mandatory?",
                "Is price derived from current state?",
                "Can user add invalid product to cart?",
              ],
              goldenRule:
                "Cart state should always represent a valid purchasable item.",
            },

            interviewInsights: {
              commonQuestions: [
                "How would you design cart state in React?",
                "What is derived state and why avoid storing it?",
                "How do variants affect price calculation?",
                "How would you persist cart data?",
                "How would this scale to multiple products?",
              ],
              strongAnswerPattern:
                "Explain state shape → derived values → future scalability",
              redFlag: "Storing price and variant inconsistently in state.",
            },

            editorGuidance: {
              careerMindset: [
                "E-commerce UIs expose bad state design instantly",
                "Derived state mistakes cause real money loss",
                "Clean component boundaries scale products",
              ],
              seniorAdvice:
                "If pricing logic is in state, your architecture is already leaking.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "E-commerce product pages combine multiple React fundamentals: state management, event handling, conditional rendering, and derived state. This project mirrors how real shopping platforms like Amazon or Flipkart build product detail pages.",

              productGallery: {
                title: "Product Image Gallery",
                explanation:
                  "Users can view multiple product images and switch the main image dynamically.",
                conceptsUsed: [
                  "useState for active image",
                  "Event handling on thumbnail click",
                  "Conditional styling for active image",
                ],
                exampleLogic: `
const [activeImage, setActiveImage] = useState(images[0]);
      `,
              },

              variantSelection: {
                title: "Variant Selection (Size / Color)",
                explanation:
                  "Variants represent different versions of the same product. Selection must update state and UI instantly.",
                conceptsUsed: [
                  "State for selected variant",
                  "Mapping variant options",
                  "Conditional active styles",
                ],
                exampleLogic: `
const [selectedColor, setSelectedColor] = useState('red');
      `,
                realWorldNote: "Variants usually affect price, stock, or image",
              },

              quantitySelector: {
                title: "Quantity Selector",
                explanation:
                  "Quantity selection allows users to choose how many items they want to purchase.",
                conceptsUsed: [
                  "Numeric state",
                  "Functional state updates",
                  "Preventing negative values",
                ],
                exampleLogic: `
setQuantity(q => Math.max(1, q - 1));
      `,
              },

              addToCartState: {
                title: "Add to Cart State Management",
                explanation:
                  "Adding items to cart involves storing selected product data in state or global store.",
                conceptsUsed: [
                  "Combining multiple states into one object",
                  "Passing data through handlers",
                  "Preparing data for cart systems",
                ],
                exampleLogic: `
const handleAddToCart = () => {
  setCart({ productId, quantity, variant });
};
      `,
                interviewInsight:
                  "This logic later moves to Context, Redux, or backend APIs",
              },

              dynamicPricing: {
                title: "Dynamic Price Calculation",
                explanation:
                  "Final price depends on selected variant and quantity.",
                conceptsUsed: [
                  "Derived state",
                  "Avoiding unnecessary state",
                  "Recalculation on render",
                ],
                exampleLogic: `
const totalPrice = basePrice * quantity;
      `,
                bestPractice:
                  "Do NOT store derived values in state unless necessary",
              },

              componentBreakdown: {
                title: "Suggested Component Structure",
                components: [
                  "ProductGallery",
                  "VariantSelector",
                  "QuantitySelector",
                  "AddToCartButton",
                  "PriceDisplay",
                ],
                benefit: "Keeps components small, reusable, and testable",
              },

              commonMistakes: [
                {
                  mistake: "Storing total price in state",
                  issue: "Unnecessary re-renders",
                  fix: "Derive price during render",
                },
                {
                  mistake: "Mutating state directly",
                  issue: "React won't re-render",
                  fix: "Always use immutable updates",
                },
                {
                  mistake: "Overloading one component",
                  issue: "Hard to maintain",
                  fix: "Split into logical sub-components",
                },
              ],

              realWorldFeatures: [
                "Out-of-stock handling",
                "Disabled add-to-cart for invalid selection",
                "Variant-based image switching",
                "Price updates on quantity change",
              ],

              interviewQuestions: [
                "How do you manage cart state in React?",
                "How do you calculate derived values?",
                "How would you scale this to multiple products?",
                "How would you persist cart data?",
              ],
            },

            tasks: [
              "Build product UI using reusable components",
              "Manage selected variant using state",
              "Implement quantity selector with validation",
              "Add product to cart state",
              "Calculate price dynamically",
            ],

            examples: [
              "setQuantity(q => q + 1)",
              "setSelectedVariant(color)",
              "const total = price * quantity",
            ],

            useCases: [
              "E-commerce websites",
              "Product-based startups",
              "Portfolio showcase project",
              "Interview-ready React project",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Apply React state management in real UI",
              "Understand derived state patterns",
              "Build production-style components",
              "Prepare for global state management",
            ],
          },
        ],
      },
      {
        week: 21,
        weekGoal: "Understand and master side effects in React applications",
        weekOutcome:
          "Ability to fetch data, sync external systems, and manage lifecycle behavior in React",
        miniProject: "Data Dashboard",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "useEffect Hook Basics",

            subTopics: [
              "What are side effects in React",
              "useEffect syntax",
              "When useEffect runs",
              "Difference between render and effect",
              "Basic effect patterns",
            ],

            explanation:
              "useEffect allows React components to perform side effects such as data fetching, subscriptions, timers, or manually interacting with the DOM.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Side Effect Identification Drill",
                description:
                  "Train yourself to correctly identify what belongs in useEffect and what does not.",
                tasks: [
                  "List 5 operations and classify them as render or side effect",
                  "Move incorrect logic out of render into useEffect",
                  "Explain why JSX must stay pure",
                ],
                validationChecklist: [
                  "Render has no side effects",
                  "useEffect contains external interactions only",
                ],
                outcome:
                  "You’ll stop abusing useEffect and write cleaner components.",
              },
              {
                id: "pt-2",
                title: "Dependency Array Experiment",
                description: "Visually understand when and why useEffect runs.",
                tasks: [
                  "Create effect without dependency array",
                  "Create effect with empty array []",
                  "Create effect with specific dependency",
                  "Log execution order",
                ],
                qualityRule: "Every dependency must be intentional.",
                outcome:
                  "You’ll clearly understand useEffect execution patterns.",
              },
              {
                id: "pt-3",
                title: "Infinite Loop Prevention Exercise",
                description:
                  "Learn how incorrect dependencies cause infinite re-renders.",
                tasks: [
                  "Create state update inside useEffect",
                  "Observe infinite loop",
                  "Fix it using correct dependency logic",
                ],
                antiPattern: "Updating state blindly inside useEffect",
                outcome:
                  "You’ll know how most useEffect bugs are created — and fixed.",
              },
              {
                id: "pt-4",
                title: "Render vs Effect Separation Challenge",
                description: "Practice strict separation of responsibilities.",
                tasks: [
                  "Move calculations to render",
                  "Move side effects to useEffect",
                  "Justify each decision",
                ],
                mindset: "Render describes UI. Effects perform work.",
                outcome:
                  "Your components will become predictable and testable.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most useEffect bugs come from misunderstanding when it runs.",
              checklist: [
                "Does this logic truly need useEffect?",
                "Are all dependencies declared?",
                "Will this effect cause re-render loops?",
              ],
              goldenRule:
                "If logic doesn’t touch the outside world, it probably doesn’t need useEffect.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are side effects in React?",
                "When does useEffect run?",
                "What happens if dependency array is missing?",
                "Difference between render and effect?",
                "How do you avoid infinite loops in useEffect?",
              ],
              strongAnswerPattern:
                "Explain render purity → effect timing → dependency control",
              redFlag:
                "Using useEffect for data that can be derived during render.",
            },

            editorGuidance: {
              careerMindset: [
                "Misusing useEffect is a sign of weak React fundamentals",
                "Clean effects make components predictable",
                "Side effects should be intentional, not accidental",
              ],
              seniorAdvice:
                "If you’re confused about dependencies, your logic probably belongs outside useEffect.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In React, components should focus on rendering UI. Any work that happens outside rendering — like API calls, timers, logging, or subscriptions — is called a side effect. The useEffect hook is React’s official way to handle these side effects safely.",

              whatIsSideEffect: {
                title: "What is a Side Effect?",
                description:
                  "A side effect is any operation that affects something outside the component’s render output.",
                examples: [
                  "Fetching data from an API",
                  "Updating document title",
                  "Setting up timers or intervals",
                  "Subscribing to events",
                  "Logging analytics",
                ],
                nonExamples: ["Returning JSX", "Calculating values for render"],
              },

              useEffectSyntax: {
                title: "useEffect Syntax",
                explanation:
                  "useEffect accepts two arguments: a callback function and a dependency array.",
                syntax: `
useEffect(() => {
  // side effect logic
}, [dependencies]);
      `,
                keyPoints: [
                  "Callback runs AFTER render",
                  "Dependencies control WHEN effect runs",
                  "Empty array means run once (on mount)",
                ],
              },

              whenUseEffectRuns: {
                title: "When does useEffect run?",
                scenarios: [
                  {
                    case: "No dependency array",
                    behavior: "Runs after every render",
                    example: "useEffect(() => { ... });",
                  },
                  {
                    case: "Empty dependency array []",
                    behavior: "Runs only once after initial render",
                    example: "useEffect(() => { ... }, []);",
                  },
                  {
                    case: "With dependencies",
                    behavior: "Runs when dependency value changes",
                    example: "useEffect(() => { ... }, [count]);",
                  },
                ],
              },

              renderVsEffect: {
                title: "Render vs useEffect",
                render: [
                  "Must be pure",
                  "Should not cause side effects",
                  "Runs frequently",
                  "Returns JSX",
                ],
                effect: [
                  "Runs after render",
                  "Used for side effects",
                  "Can be async-safe",
                  "Does not return JSX",
                ],
                analogy:
                  "Render is like drawing a UI, useEffect is like doing work after the UI is on screen.",
              },

              basicPatterns: {
                title: "Basic useEffect Patterns",
                patterns: [
                  {
                    name: "Run once on mount",
                    code: `
useEffect(() => {
  console.log("Component mounted");
}, []);
          `,
                    useCase: "Initial setup, analytics, API calls",
                  },
                  {
                    name: "Run on state change",
                    code: `
useEffect(() => {
  console.log("Count changed");
}, [count]);
          `,
                    useCase: "Sync state with external systems",
                  },
                  {
                    name: "Run every render (rare)",
                    code: `
useEffect(() => {
  console.log("Every render");
});
          `,
                    warning: "Can cause performance issues",
                  },
                ],
              },

              commonMistakes: [
                {
                  mistake: "Using useEffect for everything",
                  issue: "Overcomplicates logic",
                  fix: "Only use useEffect for real side effects",
                },
                {
                  mistake: "Forgetting dependency array",
                  issue: "Unintended repeated executions",
                  fix: "Always think about dependencies",
                },
                {
                  mistake: "Updating state incorrectly inside effect",
                  issue: "Infinite re-render loop",
                  fix: "Add correct dependencies or conditions",
                },
              ],

              interviewNotes: [
                "useEffect runs after render, not before",
                "Side effects must not run during render",
                "Dependency array controls execution",
                "useEffect replaces lifecycle methods (componentDidMount, componentDidUpdate)",
              ],

              realWorldExamples: [
                "Tracking page views",
                "Fetching initial data",
                "Setting document title",
                "Initializing third-party libraries",
              ],
            },

            tasks: [
              "Create a component with useEffect",
              "Log message on component mount",
              "Experiment with dependency array",
              "Observe render vs effect behavior",
            ],

            examples: [
              "useEffect(() => { console.log('Mounted'); }, []);",
              "useEffect(() => { console.log(count); }, [count]);",
            ],

            useCases: [
              "Running code on page load",
              "Analytics tracking",
              "Initial setup logic",
              "Preparing data fetching",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Understand what side effects are",
              "Know when and why to use useEffect",
              "Avoid common beginner mistakes",
              "Prepare for data fetching and subscriptions",
            ],
          },
          {
            day: 2,
            topic: "Dependency Array & Effect Control",

            subTopics: [
              "Empty dependency array",
              "Single dependency",
              "Multiple dependencies",
              "How dependency comparison works",
              "Common dependency mistakes",
            ],

            explanation:
              "The dependency array controls when a useEffect runs. Understanding it deeply prevents infinite loops, unnecessary API calls, and performance issues.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Dependency Prediction Drill",
                description:
                  "Predict exactly when an effect will run before executing the code.",
                tasks: [
                  "Write 3 useEffect examples with different dependencies",
                  "Predict number of executions",
                  "Run code and verify prediction",
                ],
                validationChecklist: [
                  "Dependencies match used variables",
                  "Effect runs only when expected",
                ],
                outcome:
                  "You’ll stop guessing and start controlling effect execution.",
              },
              {
                id: "pt-2",
                title: "Infinite Loop Debug Challenge",
                description:
                  "Learn to spot infinite loops instantly by reading dependency logic.",
                tasks: [
                  "Create an effect that causes infinite re-render",
                  "Explain why it happens",
                  "Fix it using correct dependency strategy",
                ],
                antiPattern:
                  "State update inside effect without guard condition",
                outcome:
                  "You’ll eliminate the most common useEffect production bug.",
              },
              {
                id: "pt-3",
                title: "Object & Function Dependency Trap",
                description:
                  "Understand why objects and functions break dependency arrays.",
                tasks: [
                  "Use object in dependency array",
                  "Observe repeated effect execution",
                  "Fix using useMemo / useCallback",
                ],
                qualityRule: "Stable reference = stable dependency",
                outcome:
                  "You’ll understand why React cares about references, not values.",
              },
              {
                id: "pt-4",
                title: "Effect Responsibility Split Exercise",
                description: "Practice splitting effects by responsibility.",
                tasks: [
                  "Create one effect doing multiple things",
                  "Split into separate effects",
                  "Assign correct dependencies to each",
                ],
                mindset: "One effect = one responsibility",
                outcome: "Your effects will become readable and maintainable.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most performance bugs come from effects running more than expected.",
              checklist: [
                "Does this effect really need to re-run?",
                "Are dependencies stable references?",
                "Is this effect doing too much?",
              ],
              goldenRule:
                "If you don’t fully understand when it runs, your effect is dangerous.",
            },

            interviewInsights: {
              commonQuestions: [
                "How does dependency array control useEffect?",
                "Why do objects cause re-runs in useEffect?",
                "How does React compare dependencies?",
                "What causes infinite loops in useEffect?",
                "When should dependency array be empty?",
              ],
              strongAnswerPattern:
                "Explain comparison → reference behavior → execution control",
              redFlag:
                "Saying ‘I just add [] to stop the effect from running again’.",
            },

            editorGuidance: {
              careerMindset: [
                "Dependency array defines effect correctness",
                "Infinite loops are design bugs, not React bugs",
                "Predictable effects mean predictable apps",
              ],
              seniorAdvice:
                "If you can’t explain why an effect runs, you shouldn’t write it yet.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "The dependency array is the MOST IMPORTANT part of useEffect. Most React bugs related to performance, infinite loops, and repeated API calls come from misunderstanding dependencies.",

              dependencyArrayBasics: {
                title: "What is the Dependency Array?",
                description:
                  "The dependency array tells React WHEN to re-run an effect by watching values used inside the effect.",
                keyRule:
                  "If a value is used inside useEffect, it should usually appear in the dependency array.",
              },

              dependencyScenarios: {
                title: "Common Dependency Patterns",
                cases: [
                  {
                    type: "Empty dependency array []",
                    behavior: "Effect runs once after initial render",
                    useCase: "Initial data fetch, analytics, setup logic",
                    code: `
useEffect(() => {
  fetchData();
}, []);
          `,
                  },
                  {
                    type: "Single dependency",
                    behavior: "Effect runs when that value changes",
                    useCase: "Fetch data when ID changes",
                    code: `
useEffect(() => {
  fetchUser(userId);
}, [userId]);
          `,
                  },
                  {
                    type: "Multiple dependencies",
                    behavior: "Effect runs when ANY dependency changes",
                    useCase: "Reacting to multiple state updates",
                    code: `
useEffect(() => {
  updateUI(a, b);
}, [a, b]);
          `,
                  },
                  {
                    type: "No dependency array",
                    behavior: "Effect runs after EVERY render",
                    warning: "Rarely needed, often a mistake",
                    code: `
useEffect(() => {
  console.log("Runs every render");
});
          `,
                  },
                ],
              },

              howComparisonWorks: {
                title: "How React Compares Dependencies",
                explanation:
                  "React compares dependencies using Object.is (shallow comparison).",
                implications: [
                  "Primitive values (number, string, boolean) are safe",
                  "Objects, arrays, and functions create new references on each render",
                  "New reference = dependency changed",
                ],
                example: `
const obj = { a: 1 }; // new reference every render ❌
useEffect(() => {}, [obj]);
      `,
                fix: `
useEffect(() => {}, []); // OR memoize obj using useMemo
      `,
              },

              infiniteLoopProblem: {
                title: "Infinite Loop – MOST COMMON BUG",
                problem:
                  "Updating state inside useEffect without controlling dependencies causes infinite re-renders.",
                badExample: `
useEffect(() => {
  setCount(count + 1);
}, [count]);
      `,
                whyItHappens:
                  "State update → render → dependency changes → effect runs again",
                solutions: [
                  "Use conditional logic",
                  "Use functional updates",
                  "Re-evaluate whether effect is needed",
                ],
                fixedExample: `
useEffect(() => {
  if (count < 5) {
    setCount(c => c + 1);
  }
}, [count]);
      `,
              },

              dependencyBestPractices: {
                title: "Best Practices",
                rules: [
                  "Always include dependencies you use",
                  "Avoid objects/functions directly in dependency array",
                  "Prefer derived values instead of state inside effects",
                  "Split effects if responsibilities differ",
                ],
              },

              eslintRule: {
                title: "Exhaustive-deps ESLint Rule",
                explanation:
                  "React ESLint plugin warns when dependencies are missing.",
                advice:
                  "Do NOT blindly ignore it — understand the warning before disabling.",
              },

              commonMistakes: [
                {
                  mistake: "Leaving dependency array empty to silence warnings",
                  issue: "Leads to stale data bugs",
                  fix: "Understand and add correct dependencies",
                },
                {
                  mistake: "Adding everything blindly",
                  issue: "Unnecessary re-runs",
                  fix: "Split effects and simplify logic",
                },
                {
                  mistake: "Using objects/functions as dependencies",
                  issue: "Effect runs every render",
                  fix: "Use useCallback or useMemo",
                },
              ],

              interviewQuestions: [
                "Why does useEffect re-run?",
                "How does React compare dependencies?",
                "What causes infinite loops in useEffect?",
                "When should dependency array be empty?",
              ],

              realWorldUseCases: [
                "Fetching data when route param changes",
                "Syncing UI with selected filters",
                "Updating document title on state change",
                "Controlled analytics triggers",
              ],
            },

            tasks: [
              "Run effect only on component mount",
              "Trigger effect on state change",
              "Create and fix an infinite loop example",
              "Experiment with multiple dependencies",
            ],

            examples: [
              "useEffect(() => { fetchData(); }, [])",
              "useEffect(() => { fetchUser(userId); }, [userId])",
            ],

            useCases: [
              "Fetching data on user change",
              "Updating UI on state updates",
              "Preventing unnecessary API calls",
            ],

            editorType: "react",

            estimatedTime: "60–75 minutes",

            objectives: [
              "Master dependency array behavior",
              "Avoid infinite render loops",
              "Write predictable side effects",
              "Gain interview-level confidence in useEffect",
            ],
          },
          {
            day: 3,
            topic: "Cleanup Functions & Component Lifecycle",

            subTopics: [
              "Cleanup function syntax",
              "Component unmount behavior",
              "Cleaning subscriptions",
              "Clearing timers",
              "Memory leak prevention",
            ],

            explanation:
              "Cleanup functions prevent memory leaks by removing subscriptions, event listeners, or timers when components unmount or before an effect re-runs.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Leak Detection Drill",
                description:
                  "Learn to detect memory leaks by observing component behavior.",
                tasks: [
                  "Create an interval inside useEffect without cleanup",
                  "Unmount the component",
                  "Observe console logs continuing",
                  "Add cleanup and verify fix",
                ],
                validationChecklist: [
                  "No logs after unmount",
                  "No background timers running",
                ],
                outcome:
                  "You’ll immediately recognize memory leaks in real projects.",
              },
              {
                id: "pt-2",
                title: "Cleanup Timing Experiment",
                description: "Understand exactly WHEN cleanup functions run.",
                tasks: [
                  "Create effect with dependency",
                  "Log inside effect and cleanup",
                  "Change dependency value",
                  "Observe execution order",
                ],
                qualityRule: "Cleanup runs BEFORE next effect execution.",
                outcome: "You’ll stop guessing cleanup timing forever.",
              },
              {
                id: "pt-3",
                title: "Subscription Safety Challenge",
                description:
                  "Practice cleaning external subscriptions correctly.",
                tasks: [
                  "Simulate subscription (event / socket)",
                  "Attach listener in effect",
                  "Remove listener in cleanup",
                  "Verify no duplicate handlers",
                ],
                antiPattern: "Subscribing without unsubscribing",
                outcome: "You’ll write production-safe subscription logic.",
              },
              {
                id: "pt-4",
                title: "Effect Responsibility Split",
                description: "Prevent complex effects that are hard to clean.",
                tasks: [
                  "Create one effect with multiple side effects",
                  "Split into separate effects",
                  "Add independent cleanup to each",
                ],
                mindset: "If it needs cleanup, it deserves its own effect.",
                outcome:
                  "Your components will be easier to reason about and debug.",
              },
            ],

            testingInsight: {
              coreReality:
                "Memory leaks don’t crash apps immediately — they slowly kill performance.",
              checklist: [
                "Does every effect have a cleanup?",
                "Are listeners removed with same reference?",
                "Does cleanup run on dependency change?",
              ],
              goldenRule:
                "If you set it up in useEffect, you must tear it down in cleanup.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is a cleanup function in useEffect?",
                "When does cleanup run?",
                "What happens if cleanup is missing?",
                "How does cleanup relate to componentWillUnmount?",
                "How do you prevent memory leaks in React?",
              ],
              strongAnswerPattern:
                "Explain lifecycle → effect execution → cleanup timing",
              redFlag: "Saying cleanup runs only on unmount.",
            },

            editorGuidance: {
              careerMindset: [
                "Memory leaks separate juniors from professionals",
                "Cleanup discipline reflects engineering maturity",
                "Stable apps come from predictable lifecycles",
              ],
              seniorAdvice:
                "If your app slows down over time, your cleanup logic is broken.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real applications, components don’t live forever. They mount, update, and unmount. If we attach timers, listeners, or subscriptions and forget to remove them, the app slowly breaks. Cleanup functions are React’s way to keep your app healthy.",

              lifecycleMentalModel: {
                title: "Component Lifecycle (Mental Model)",
                stages: [
                  "Mount → component appears on screen",
                  "Update → state/props change",
                  "Unmount → component is removed",
                ],
                reactHooksMapping: {
                  mount: "useEffect with []",
                  update: "useEffect with dependencies",
                  unmount: "cleanup function inside useEffect",
                },
                note: "React Hooks do not expose lifecycle methods directly — useEffect replaces them.",
              },

              cleanupSyntax: {
                title: "Cleanup Function Syntax",
                explanation:
                  "A cleanup function is returned from inside useEffect. React automatically calls it before unmount or before the effect runs again.",
                syntax: `
useEffect(() => {
  // setup logic

  return () => {
    // cleanup logic
  };
}, [dependencies]);
      `,
                keyRule:
                  "Whatever you CREATE in effect, you must DESTROY in cleanup.",
              },

              whenCleanupRuns: {
                title: "When Does Cleanup Run?",
                cases: [
                  {
                    scenario: "Component unmounts",
                    behavior: "Cleanup runs once",
                  },
                  {
                    scenario: "Dependency changes",
                    behavior: "Cleanup runs BEFORE effect re-runs",
                  },
                  {
                    scenario: "Empty dependency array []",
                    behavior: "Cleanup runs only on unmount",
                  },
                ],
              },

              commonCleanupCases: {
                title: "Most Common Cleanup Scenarios",
                scenarios: [
                  {
                    name: "Clearing intervals",
                    problem:
                      "Intervals keep running even after component is gone",
                    solution: `
useEffect(() => {
  const id = setInterval(() => {
    console.log("tick");
  }, 1000);

  return () => clearInterval(id);
}, []);
          `,
                  },
                  {
                    name: "Removing event listeners",
                    problem: "Event listeners stack up and fire multiple times",
                    solution: `
useEffect(() => {
  window.addEventListener("resize", handleResize);

  return () => {
    window.removeEventListener("resize", handleResize);
  };
}, []);
          `,
                  },
                  {
                    name: "Subscriptions (WebSocket / observers)",
                    problem: "Leads to memory leaks & stale updates",
                    solution: "Unsubscribe in cleanup",
                  },
                ],
              },

              memoryLeakExplanation: {
                title: "What is a Memory Leak?",
                explanation:
                  "A memory leak happens when unused resources are never released, causing performance degradation over time.",
                symptoms: [
                  "App becomes slower",
                  "Unexpected console logs",
                  "Multiple API calls",
                  "Warnings in React DevTools",
                ],
                cause: "Missing cleanup in useEffect",
              },

              badVsGood: {
                title: "Bad vs Good Example",
                bad: `
useEffect(() => {
  setInterval(() => {
    console.log("Running");
  }, 1000);
}, []);
      `,
                issue: "Interval never stops even after component unmounts",
                good: `
useEffect(() => {
  const id = setInterval(() => {
    console.log("Running");
  }, 1000);

  return () => clearInterval(id);
}, []);
      `,
              },

              bestPractices: {
                title: "Best Practices",
                rules: [
                  "Always clean intervals, timeouts, and listeners",
                  "Cleanup must mirror setup logic",
                  "Avoid side effects inside render",
                  "Split effects for unrelated logic",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Forgetting cleanup",
                  impact: "Memory leaks and performance issues",
                  fix: "Always return cleanup function",
                },
                {
                  mistake: "Cleaning wrong reference",
                  impact: "Listener not removed",
                  fix: "Use same function reference",
                },
                {
                  mistake: "Overusing useEffect",
                  impact: "Complex and buggy components",
                  fix: "Use effects only for real side effects",
                },
              ],

              interviewQuestions: [
                "Why do we need cleanup in useEffect?",
                "When does cleanup run?",
                "What happens if cleanup is missing?",
                "How does useEffect replace componentWillUnmount?",
              ],

              realWorldUseCases: [
                "Timers in dashboards",
                "WebSocket live updates",
                "Window resize listeners",
                "Scroll tracking",
              ],
            },

            tasks: [
              "Add cleanup function to useEffect",
              "Clear interval when component unmounts",
              "Attach and remove event listener safely",
              "Test unmount behavior by toggling component",
            ],

            examples: [
              "useEffect(() => { const id = setInterval(fn, 1000); return () => clearInterval(id); }, []);",
              "return () => window.removeEventListener('resize', handler);",
            ],

            useCases: [
              "Intervals and timers",
              "WebSocket connections",
              "Event listeners",
              "Performance-sensitive components",
            ],

            editorType: "react",

            estimatedTime: "60–75 minutes",

            objectives: [
              "Prevent memory leaks",
              "Understand component lifecycle via hooks",
              "Write safe and clean side effects",
              "Gain production-level React confidence",
            ],
          },
          {
            day: 4,
            topic: "Data Fetching with useEffect",

            subTopics: [
              "Fetching data on mount",
              "Async functions inside useEffect",
              "Handling loading state",
              "Handling error state",
              "Dependency-based refetching",
            ],

            explanation:
              "useEffect is commonly used to fetch data from APIs when a component mounts or when specific dependencies change.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "API Fetching Reality Drill",
                description:
                  "Understand real-world API behavior beyond happy paths.",
                tasks: [
                  "Fetch data from a public API",
                  "Simulate slow network (throttle)",
                  "Observe loading UI behavior",
                  "Handle success and failure cases",
                ],
                validationChecklist: [
                  "Loading state visible before data",
                  "Error UI shown on failure",
                  "No UI crash on API error",
                ],
                outcome: "You’ll stop assuming APIs always succeed.",
              },
              {
                id: "pt-2",
                title: "Dependency-Based Refetch Challenge",
                description: "Learn when and why effects should re-run.",
                tasks: [
                  "Fetch data using dependency (id or filter)",
                  "Change dependency value",
                  "Observe automatic refetch",
                  "Verify no extra API calls",
                ],
                qualityRule:
                  "Effect should re-run only when dependency changes.",
                outcome: "You’ll master controlled refetching patterns.",
              },
              {
                id: "pt-3",
                title: "Unmount Safety Exercise",
                description: "Prevent state updates after component unmount.",
                tasks: [
                  "Trigger fetch on mount",
                  "Unmount component before fetch completes",
                  "Observe warning without cleanup",
                  "Fix using AbortController",
                ],
                antiPattern: "Updating state after component unmount",
                outcome: "You’ll eliminate a classic production warning.",
              },
              {
                id: "pt-4",
                title: "Loading–Error–Data UI Split",
                description: "Build resilient UI for all data states.",
                tasks: [
                  "Design UI for loading state",
                  "Design UI for error state",
                  "Design UI for success state",
                  "Ensure no state overlaps",
                ],
                mindset: "Data-driven UI always has 3 states, not 1.",
                outcome: "Your components will feel professional and reliable.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most bugs in data fetching come from timing, not syntax.",
              checklist: [
                "Does loading state reset correctly?",
                "Does error clear on refetch?",
                "Is effect running expected number of times?",
                "Is cleanup aborting in-flight requests?",
              ],
              goldenRule: "Never assume fetch completes before unmount.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you fetch data in React?",
                "Why can’t useEffect be async?",
                "How do you handle loading and error states?",
                "How do you refetch data on dependency change?",
                "How do you prevent memory leaks during fetch?",
              ],
              strongAnswerPattern:
                "Explain effect timing → async pattern → state handling → cleanup",
              redFlag: "Fetching data directly inside render.",
            },

            editorGuidance: {
              careerMindset: [
                "Data fetching defines app reliability",
                "Handling failure is more important than success",
                "Predictable effects create stable products",
              ],
              seniorAdvice:
                "If your UI breaks on slow network, your logic is incomplete.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Most real-world React applications depend on data from APIs. useEffect is the standard hook for performing data fetching as a side effect after rendering.",

              basicFetchFlow: {
                title: "Basic Data Fetching Flow",
                steps: [
                  "Component renders",
                  "useEffect runs after render",
                  "API request is sent",
                  "Loading state updates UI",
                  "Data or error updates state",
                  "Component re-renders with result",
                ],
              },

              asyncInUseEffect: {
                title: "Async Functions inside useEffect",
                explanation:
                  "The useEffect callback itself cannot be async, but you can define and call an async function inside it.",
                correctPattern: `
useEffect(() => {
  const fetchData = async () => {
    const res = await fetch(url);
    const data = await res.json();
    setData(data);
  };

  fetchData();
}, []);
      `,
                wrongPattern: `
useEffect(async () => {
  const res = await fetch(url); // ❌
}, []);
      `,
                reason:
                  "useEffect must return either nothing or a cleanup function, not a Promise",
              },

              loadingState: {
                title: "Handling Loading State",
                explanation:
                  "Loading state improves UX by informing users that data is being fetched.",
                pattern: `
const [loading, setLoading] = useState(true);

useEffect(() => {
  fetchData().finally(() => setLoading(false));
}, []);
      `,
                uiExample: ["Spinner", "Skeleton loaders", "Loading text"],
              },

              errorHandling: {
                title: "Handling Error State",
                explanation:
                  "APIs can fail due to network issues, server errors, or invalid responses.",
                pattern: `
const [error, setError] = useState(null);

try {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Request failed");
  const data = await res.json();
  setData(data);
} catch (err) {
  setError(err.message);
}
      `,
                uiHandling: ["Error message", "Retry button", "Fallback UI"],
              },

              dependencyBasedFetching: {
                title: "Dependency-Based Refetching",
                explanation:
                  "Effects can refetch data when dependencies like IDs or filters change.",
                example: `
useEffect(() => {
  fetchUser(userId);
}, [userId]);
      `,
                useCase:
                  "Fetching new data when route params or filters update",
              },

              abortingRequests: {
                title: "Preventing State Updates after Unmount",
                explanation:
                  "If a component unmounts before fetch completes, updating state can cause warnings.",
                solution: "Use AbortController or cleanup flags",
                example: `
useEffect(() => {
  const controller = new AbortController();

  fetch(url, { signal: controller.signal })
    .then(res => res.json())
    .then(setData)
    .catch(err => {
      if (err.name !== "AbortError") setError(err);
    });

  return () => controller.abort();
}, []);
      `,
              },

              commonMistakes: [
                {
                  mistake: "Fetching data inside render",
                  issue: "Causes repeated requests",
                  fix: "Always fetch inside useEffect",
                },
                {
                  mistake: "Ignoring loading state",
                  issue: "Bad user experience",
                  fix: "Use loading indicators",
                },
                {
                  mistake: "Missing dependencies",
                  issue: "Stale or incorrect data",
                  fix: "Add correct dependencies",
                },
                {
                  mistake: "Not handling errors",
                  issue: "App crashes or silent failures",
                  fix: "Always handle errors",
                },
              ],

              bestPractices: [
                "Keep data fetching logic clean and readable",
                "Separate concerns (fetch logic vs UI)",
                "Use environment variables for API URLs",
                "Avoid duplicate API calls",
              ],

              interviewQuestions: [
                "Why can’t useEffect be async?",
                "How do you handle loading and error states?",
                "How do you refetch data on dependency change?",
                "How do you prevent memory leaks during fetch?",
              ],

              realWorldUseCases: [
                "User profile pages",
                "Dashboard analytics",
                "Content feeds",
                "Search results",
              ],
            },

            tasks: [
              "Fetch data from a public API",
              "Display loading state",
              "Handle API errors gracefully",
              "Refetch data when dependency changes",
            ],

            examples: [
              "useEffect(() => { fetch(url).then(res => res.json()).then(setData); }, [])",
              "useEffect(() => { fetchUser(id); }, [id])",
            ],

            useCases: [
              "Dashboards",
              "User profiles",
              "Content feeds",
              "Data-driven UIs",
            ],

            editorType: "react",

            estimatedTime: "75–90 minutes",

            objectives: [
              "Fetch data safely in React",
              "Handle loading and error states",
              "Understand async flow with useEffect",
              "Build production-ready data-driven components",
            ],
          },
          {
            day: 5,
            topic: "Multiple Effects & Code Organization",

            subTopics: [
              "Separating concerns using multiple useEffects",
              "One effect per responsibility",
              "Avoiding large effects",
              "Readability best practices",
              "Refactoring effects",
            ],

            explanation:
              "Using multiple useEffect hooks improves readability, predictability, and maintainability by separating unrelated side effects.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Monolith Effect Breakdown",
                description:
                  "Learn to break one giant useEffect into clean, focused effects.",
                tasks: [
                  "Create a component with one large useEffect",
                  "List all responsibilities inside it",
                  "Split logic into multiple effects",
                  "Fix dependency arrays for each effect",
                ],
                validationChecklist: [
                  "Each effect has one responsibility",
                  "Dependencies are minimal and clear",
                ],
                outcome: "You’ll never write a monster useEffect again.",
              },
              {
                id: "pt-2",
                title: "Dependency Simplification Drill",
                description:
                  "See how smaller effects reduce dependency confusion.",
                tasks: [
                  "Observe dependency array in large effect",
                  "Split effect into smaller ones",
                  "Compare dependency arrays before and after",
                ],
                qualityRule:
                  "If dependencies confuse you, the effect is doing too much.",
                outcome:
                  "You’ll gain confidence reading and writing dependency arrays.",
              },
              {
                id: "pt-3",
                title: "Unnecessary Effect Removal",
                description: "Identify effects that shouldn’t exist.",
                tasks: [
                  "Find effects that only calculate values",
                  "Remove useEffect",
                  "Move logic to render or derived values",
                  "Verify behavior remains correct",
                ],
                antiPattern: "Using useEffect instead of thinking",
                outcome: "Your components will become simpler and faster.",
              },
              {
                id: "pt-4",
                title: "Senior Refactor Exercise",
                description: "Refactor effects the way senior engineers do.",
                tasks: [
                  "Take an existing complex component",
                  "Split effects by responsibility",
                  "Rename helper functions clearly",
                  "Improve readability without changing behavior",
                ],
                mindset: "Readable effects are more valuable than clever ones.",
                outcome:
                  "Your code will look senior-level and review-friendly.",
              },
            ],

            testingInsight: {
              coreReality:
                "Effect bugs often come from mixing unrelated responsibilities.",
              checklist: [
                "Does this effect do more than one thing?",
                "Are dependencies obvious at a glance?",
                "Can this logic live outside useEffect?",
              ],
              goldenRule:
                "Multiple small effects are safer than one big effect.",
            },

            interviewInsights: {
              commonQuestions: [
                "Is it okay to use multiple useEffect hooks?",
                "When should you split a useEffect?",
                "How do you organize side effects in large components?",
                "What are signs of an unnecessary useEffect?",
              ],
              strongAnswerPattern:
                "Talk about separation of concerns and dependency clarity",
              redFlag: "Saying you should minimize number of useEffect hooks.",
            },

            editorGuidance: {
              careerMindset: [
                "Code organization reflects thinking clarity",
                "Messy effects create hidden bugs",
                "Senior developers optimize for readability",
              ],
              seniorAdvice:
                "If you’re scared to touch an effect, it’s already too big.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A common beginner mistake is writing one giant useEffect that handles everything. In real-world React applications, effects should be small, focused, and easy to reason about.",

              separationOfConcerns: {
                title: "Why Separate Effects?",
                explanation:
                  "Each useEffect should handle ONE responsibility. This makes bugs easier to find and logic easier to modify.",
                benefits: [
                  "Better readability",
                  "Fewer dependency bugs",
                  "Easier debugging",
                  "Simpler refactoring",
                ],
              },

              badVsGoodExample: {
                title: "Bad vs Good Structure",
                bad: `
useEffect(() => {
  fetchUser();
  saveToLocalStorage();
  document.title = user.name;
}, [user]);
      `,
                issues: [
                  "Multiple responsibilities",
                  "Unclear dependencies",
                  "Hard to debug",
                ],
                good: `
useEffect(() => {
  fetchUser();
}, []);

useEffect(() => {
  saveToLocalStorage(user);
}, [user]);

useEffect(() => {
  document.title = user.name;
}, [user.name]);
      `,
                improvement:
                  "Each effect has one purpose and clear dependencies",
              },

              oneEffectRule: {
                title: "One Effect = One Responsibility",
                rule: "If an effect does more than one thing, it should probably be split.",
                examples: [
                  "One effect for data fetching",
                  "One effect for syncing localStorage",
                  "One effect for DOM updates",
                ],
              },

              dependencyClarity: {
                title: "Dependency Clarity Improves",
                explanation:
                  "Smaller effects make dependency arrays easier and safer.",
                comparison: [
                  "Large effect → confusing dependencies",
                  "Small effect → obvious dependencies",
                ],
              },

              refactoringStrategy: {
                title: "How to Refactor Large Effects",
                steps: [
                  "Identify different responsibilities",
                  "Split logic into separate effects",
                  "Adjust dependency arrays",
                  "Test each effect independently",
                ],
              },

              unnecessaryEffects: {
                title: "Identifying Unnecessary Effects",
                warningSigns: [
                  "Effect only derives state from props",
                  "Effect only calculates values",
                  "Effect runs but doesn’t cause side effects",
                ],
                fix: "Use derived values or memoization instead of useEffect",
              },

              bestPractices: {
                title: "Best Practices",
                rules: [
                  "Prefer multiple small effects over one big effect",
                  "Avoid effects that just sync state to state",
                  "Name helper functions clearly",
                  "Keep effect logic readable",
                ],
              },

              commonMistakes: [
                {
                  mistake: "One giant useEffect",
                  issue: "Hard to maintain and debug",
                  fix: "Split effects by responsibility",
                },
                {
                  mistake: "Using useEffect to calculate values",
                  issue: "Unnecessary re-renders",
                  fix: "Calculate during render or useMemo",
                },
                {
                  mistake: "Overusing useEffect",
                  issue: "Complex components",
                  fix: "Ask: Is this really a side effect?",
                },
              ],

              interviewInsights: [
                "Multiple useEffects are normal and recommended",
                "One responsibility per effect is a strong design signal",
                "Smaller effects reduce dependency bugs",
              ],

              realWorldExamples: [
                "Data fetching + analytics tracking",
                "Form autosave + validation",
                "Theme syncing + DOM updates",
              ],
            },

            tasks: [
              "Split large useEffect into smaller effects",
              "Refactor an existing component",
              "Remove unnecessary effects",
              "Verify dependency correctness",
            ],

            examples: [
              "useEffect(() => { fetchData(); }, [])",
              "useEffect(() => { saveToLocal(); }, [data])",
            ],

            useCases: [
              "Complex components",
              "Large forms",
              "Data synchronization",
              "Production-scale React apps",
            ],

            editorType: "react",

            estimatedTime: "45–60 minutes",

            objectives: [
              "Write clean and maintainable effects",
              "Avoid dependency confusion",
              "Organize side effects properly",
              "Think like a senior React developer",
            ],
          },
          {
            day: 6,
            topic: "Advanced Patterns with useEffect",

            subTopics: [
              "Avoiding infinite loops",
              "Debouncing with useEffect",
              "Throttling basics",
              "Syncing with localStorage",
              "Persisting state",
            ],

            explanation:
              "Advanced useEffect patterns help manage performance, prevent unnecessary renders, and keep application state synchronized with external systems.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Infinite Loop Debugging Drill",
                description:
                  "Learn to detect and fix infinite loops caused by incorrect dependencies.",
                tasks: [
                  "Create a useEffect that causes infinite re-renders",
                  "Identify which dependency causes the loop",
                  "Fix using conditional logic or functional updates",
                ],
                validationChecklist: [
                  "Effect does not re-run endlessly",
                  "State updates are controlled",
                ],
                outcome:
                  "You’ll confidently debug infinite loop issues in production.",
              },
              {
                id: "pt-2",
                title: "Debounce Optimization Exercise",
                description:
                  "Implement debouncing correctly using useEffect and cleanup.",
                tasks: [
                  "Build a search input without debounce",
                  "Add debounce using setTimeout",
                  "Clean up previous timers",
                  "Verify reduced API calls",
                ],
                qualityRule: "Every debounce must have a cleanup.",
                outcome:
                  "You’ll optimize expensive operations without breaking UX.",
              },
              {
                id: "pt-3",
                title: "Persistent State Implementation",
                description: "Persist React state safely using localStorage.",
                tasks: [
                  "Initialize state from localStorage",
                  "Sync state to localStorage using useEffect",
                  "Avoid unnecessary writes",
                ],
                antiPattern: "Reading from localStorage on every render",
                outcome:
                  "You’ll build state that survives page refreshes cleanly.",
              },
              {
                id: "pt-4",
                title: "Performance Pattern Refactor",
                description:
                  "Refactor effects to improve performance and readability.",
                tasks: [
                  "Identify heavy logic inside useEffect",
                  "Move non-side-effect logic to render or helpers",
                  "Optimize dependency arrays",
                ],
                mindset: "useEffect is not a dumping ground.",
                outcome:
                  "Your components will feel faster and more maintainable.",
              },
            ],

            testingInsight: {
              coreReality:
                "Performance bugs often hide inside poorly controlled effects.",
              checklist: [
                "Is this effect really necessary?",
                "Are dependencies minimal and correct?",
                "Is cleanup implemented properly?",
              ],
              goldenRule: "Every effect should be predictable and cancelable.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you debounce API calls in React?",
                "How do you persist state across refresh?",
                "What causes infinite loops in useEffect?",
                "When should you avoid useEffect?",
              ],
              strongAnswerPattern:
                "Explain dependency control, cleanup, and performance impact",
              redFlag: "Using useEffect without understanding dependencies.",
            },

            editorGuidance: {
              careerMindset: [
                "Performance is a feature, not an afterthought",
                "Side effects must be controlled, not reactive",
                "Clean effects scale better than clever hacks",
              ],
              seniorAdvice:
                "If an effect feels unpredictable, its dependencies are wrong.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world applications, useEffect is not just for fetching data. It is heavily used for performance optimizations, syncing state with browser APIs, and controlling expensive operations.",

              infiniteLoopPrevention: {
                title: "Avoiding Infinite Loops",
                explanation:
                  "Infinite loops occur when an effect updates a dependency that causes the effect to run again.",
                badExample: `
useEffect(() => {
  setCount(count + 1);
}, [count]);
      `,
                whyItLoops:
                  "State update → render → dependency changes → effect runs again",
                solutions: [
                  "Use conditional logic",
                  "Use functional updates",
                  "Re-check if useEffect is required",
                ],
                fixedExample: `
useEffect(() => {
  if (count < 5) {
    setCount(c => c + 1);
  }
}, [count]);
      `,
              },

              debouncingPattern: {
                title: "Debouncing with useEffect",
                explanation:
                  "Debouncing delays execution until the user stops performing an action. It is commonly used in search inputs.",
                example: `
useEffect(() => {
  const id = setTimeout(() => {
    search(query);
  }, 500);

  return () => clearTimeout(id);
}, [query]);
      `,
                realWorldUse: "Search bars, filters, live validation",
              },

              throttlingBasics: {
                title: "Throttling Basics",
                explanation:
                  "Throttling ensures an operation runs at most once in a given time interval.",
                note: "Throttling is often implemented outside useEffect using refs or utilities, but effects can coordinate it.",
                useCases: [
                  "Scroll events",
                  "Resize listeners",
                  "Mouse movement tracking",
                ],
              },

              localStorageSync: {
                title: "Syncing State with localStorage",
                explanation:
                  "Persisting state ensures data remains after page refresh.",
                pattern: {
                  read: `
const [data, setData] = useState(() => {
  return JSON.parse(localStorage.getItem("data")) || [];
});
        `,
                  write: `
useEffect(() => {
  localStorage.setItem("data", JSON.stringify(data));
}, [data]);
        `,
                },
                bestPractice:
                  "Read once on initialization, write on state change",
              },

              persistingState: {
                title: "Persisting User Preferences",
                examples: [
                  "Theme (dark/light)",
                  "Language selection",
                  "Form drafts",
                ],
                insight:
                  "Persistence improves UX but should be used carefully to avoid stale data",
              },

              performanceTips: {
                title: "Performance Best Practices",
                tips: [
                  "Avoid heavy logic inside effects",
                  "Debounce expensive operations",
                  "Use cleanup to cancel outdated work",
                  "Prefer derived values over syncing state",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Debouncing without cleanup",
                  issue: "Multiple timers run simultaneously",
                  fix: "Always clear timeout in cleanup",
                },
                {
                  mistake: "Syncing every render",
                  issue: "Performance degradation",
                  fix: "Use dependency array correctly",
                },
                {
                  mistake: "Overusing localStorage writes",
                  issue: "Slow UI",
                  fix: "Persist only necessary state",
                },
              ],

              interviewQuestions: [
                "How do you debounce API calls in React?",
                "How do you persist state across refresh?",
                "How do you prevent infinite loops in useEffect?",
                "When should you avoid useEffect?",
              ],

              realWorldUseCases: [
                "Search bars with debounce",
                "Autosave drafts",
                "Theme persistence",
                "User preference storage",
              ],
            },

            tasks: [
              "Implement debounced search input",
              "Sync state with localStorage",
              "Prevent infinite loop in useEffect",
              "Optimize effect dependencies",
            ],

            examples: [
              "useEffect(() => { localStorage.setItem('data', JSON.stringify(data)); }, [data])",
              "useEffect(() => { const id = setTimeout(fn, 500); return () => clearTimeout(id); }, [query])",
            ],

            useCases: [
              "Search bars",
              "Auto-save forms",
              "User preferences",
              "Performance-sensitive components",
            ],

            editorType: "react",

            estimatedTime: "75–90 minutes",

            objectives: [
              "Master advanced useEffect patterns",
              "Prevent performance issues",
              "Build responsive and optimized UIs",
              "Think in production-ready React patterns",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Data Dashboard",

            subTopics: [
              "Multiple API calls",
              "Loading & error states",
              "Auto refresh data",
              "Local storage caching",
              "Dashboard UI layout",
            ],

            explanation:
              "This project simulates a real-world data dashboard using React useEffect, similar to analytics or admin dashboards used in production applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Multi-API Coordination Challenge",
                description:
                  "Learn to manage multiple API calls without creating messy effects.",
                tasks: [
                  "Fetch data from 2 different APIs",
                  "Separate concerns using multiple useEffect hooks",
                  "Handle partial success (one API fails, other succeeds)",
                ],
                validationChecklist: [
                  "Each API has its own loading & error handling",
                  "Effects are clearly separated",
                ],
                outcome:
                  "You’ll confidently build dashboards with multiple data sources.",
              },
              {
                id: "pt-2",
                title: "Auto-Refresh with Safety Guards",
                description:
                  "Implement auto-refresh logic without memory leaks or performance issues.",
                tasks: [
                  "Add setInterval for periodic data refresh",
                  "Implement cleanup on unmount",
                  "Ensure no duplicate intervals are created",
                ],
                qualityRule: "Every interval must be cleared properly.",
                outcome:
                  "You’ll implement live dashboards safely like senior developers.",
              },
              {
                id: "pt-3",
                title: "Dashboard Caching Strategy",
                description:
                  "Optimize dashboard performance using localStorage caching.",
                tasks: [
                  "Load cached data on initial render",
                  "Update cache when fresh data arrives",
                  "Avoid stale or corrupted cache",
                ],
                antiPattern:
                  "Fetching API blindly without checking cached data",
                outcome: "Your dashboard will feel fast even on slow networks.",
              },
              {
                id: "pt-4",
                title: "Dashboard Architecture Refactor",
                description:
                  "Refactor the dashboard into clean, reusable components.",
                tasks: [
                  "Extract StatsCard, Loader, and Error components",
                  "Reduce logic inside main Dashboard component",
                  "Ensure clear data flow between components",
                ],
                mindset: "Dashboards fail due to architecture, not UI.",
                outcome:
                  "You’ll build scalable dashboards suitable for production systems.",
              },
            ],

            testingInsight: {
              coreReality:
                "Dashboard bugs usually come from async race conditions or missing cleanup.",
              checklist: [
                "Are all effects independent?",
                "Are loading & error states predictable?",
                "Is auto-refresh cleaned up properly?",
              ],
              goldenRule:
                "If data updates automatically, cleanup is non-negotiable.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you manage multiple API calls in React?",
                "How do you implement auto-refresh safely?",
                "How do you cache dashboard data?",
                "How do you optimize data-heavy React components?",
              ],
              strongAnswerPattern:
                "Explain separation of effects, cleanup, and performance trade-offs",
              redFlag: "One giant useEffect controlling the entire dashboard.",
            },

            editorGuidance: {
              careerMindset: [
                "Dashboards expose architectural weaknesses quickly",
                "Async control is a senior-level skill",
                "Readable effects matter more than clever code",
              ],
              seniorAdvice:
                "If your dashboard logic feels fragile, split the effects.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Data dashboards are common in admin panels and analytics tools. They combine multiple API calls, side effects, state synchronization, and performance optimizations. This project mirrors how real dashboards fetch, update, and display live data.",

              apiFetching: {
                title: "Fetching Multiple APIs",
                explanation:
                  "Dashboards often depend on multiple data sources such as users, stats, or reports.",
                conceptsUsed: [
                  "Multiple useEffect hooks",
                  "Async functions inside useEffect",
                  "Parallel API calls",
                ],
                exampleLogic: `
useEffect(() => {
  fetchUsers();
  fetchPosts();
}, []);
      `,
                realWorldNote:
                  "Large dashboards usually separate API logic into service files",
              },

              loadingAndError: {
                title: "Loading & Error State Management",
                explanation:
                  "Users should always know whether data is loading or something went wrong.",
                conceptsUsed: [
                  "Loading state",
                  "Error state",
                  "Conditional rendering",
                ],
                exampleLogic: `
if (loading) return <Loader />;
if (error) return <ErrorMessage />;
      `,
                bestPractice: "Never block UI without feedback",
              },

              autoRefresh: {
                title: "Auto Refresh Data",
                explanation:
                  "Some dashboards refresh data automatically after a fixed interval.",
                conceptsUsed: [
                  "setInterval",
                  "Cleanup function",
                  "useEffect lifecycle",
                ],
                exampleLogic: `
useEffect(() => {
  const id = setInterval(fetchStats, 5000);
  return () => clearInterval(id);
}, []);
      `,
                interviewInsight:
                  "Always clean intervals to avoid memory leaks",
              },

              localStorageCaching: {
                title: "Local Storage Caching",
                explanation:
                  "Caching prevents unnecessary API calls and improves performance.",
                conceptsUsed: [
                  "localStorage",
                  "State persistence",
                  "Syncing state with effects",
                ],
                exampleLogic: `
useEffect(() => {
  localStorage.setItem('dashboardData', JSON.stringify(data));
}, [data]);
      `,
                realWorldNote: "Used heavily in dashboards to reduce API cost",
              },

              dashboardLayout: {
                title: "Dashboard UI Layout",
                explanation:
                  "Clean layout improves readability of data-heavy screens.",
                conceptsUsed: [
                  "Component composition",
                  "Reusable cards",
                  "Separation of concerns",
                ],
                componentSuggestions: [
                  "StatsCard",
                  "UserList",
                  "ErrorBanner",
                  "Loader",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Fetching everything in one useEffect",
                  issue: "Hard to debug and maintain",
                  fix: "Split effects by responsibility",
                },
                {
                  mistake: "Missing cleanup in intervals",
                  issue: "Memory leaks",
                  fix: "Always return cleanup function",
                },
                {
                  mistake: "No loading state",
                  issue: "Bad user experience",
                  fix: "Show loaders and fallback UI",
                },
              ],

              realWorldFeatures: [
                "Auto-refresh analytics",
                "Cached dashboard state",
                "Graceful error handling",
                "Scalable component structure",
              ],

              interviewQuestions: [
                "How do you avoid infinite loops in useEffect?",
                "How do you manage multiple API calls?",
                "How do you cache API data in React?",
                "How would you optimize this dashboard?",
              ],
            },

            tasks: [
              "Fetch data from multiple APIs",
              "Handle loading and error states",
              "Implement auto-refresh with cleanup",
              "Cache dashboard data in localStorage",
              "Design reusable dashboard components",
            ],

            examples: [
              "useEffect(() => { fetchUsers(); fetchPosts(); }, [])",
              "useEffect(() => { localStorage.setItem('data', JSON.stringify(data)); }, [data])",
            ],

            useCases: [
              "Admin dashboards",
              "Analytics panels",
              "Monitoring systems",
              "Interview-ready React project",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Master real-world useEffect patterns",
              "Handle async data safely",
              "Prevent memory leaks",
              "Build production-style dashboards",
            ],
          },
        ],
      },
      {
        week: 22,
        weekGoal:
          "Master advanced React hooks, performance optimization, and reusable logic",
        weekOutcome:
          "Ability to manage global state, optimize performance, and build scalable custom hooks",
        miniProject: "Social Media Feed",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "useContext – Global State Management",

            subTopics: [
              "What is Context API",
              "Context vs prop drilling",
              "Creating Context and Provider",
              "Consuming context with useContext",
              "When NOT to use context",
            ],

            explanation:
              "useContext allows sharing global data like theme, authentication state, or user preferences across components without prop drilling, making React applications cleaner and more scalable.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Prop Drilling Pain Simulation",
                description:
                  "Experience why Context API becomes necessary in real apps.",
                tasks: [
                  "Create a component tree 4 levels deep",
                  "Pass the same prop through every level",
                  "Observe readability and maintenance issues",
                ],
                validationChecklist: [
                  "Props passed through unnecessary layers",
                  "Intermediate components do not use the data",
                ],
                outcome:
                  "You’ll clearly feel when prop drilling becomes a real problem.",
              },
              {
                id: "pt-2",
                title: "Context Refactor Challenge",
                description: "Refactor a prop-drilled app using Context API.",
                tasks: [
                  "Create a Context for shared data",
                  "Wrap app with Provider",
                  "Consume context using useContext",
                  "Remove redundant props",
                ],
                qualityRule:
                  "No intermediate component should receive unused props.",
                outcome:
                  "You’ll convert messy prop drilling into clean global access.",
              },
              {
                id: "pt-3",
                title: "Multiple Contexts Design Exercise",
                description:
                  "Learn to avoid the anti-pattern of a single mega-context.",
                tasks: [
                  "Create separate contexts (Theme, Auth)",
                  "Provide them independently",
                  "Consume only required context in components",
                ],
                antiPattern: "One global context holding unrelated data",
                outcome:
                  "You’ll design scalable and performant context architecture.",
              },
              {
                id: "pt-4",
                title: "Context Performance Awareness",
                description:
                  "Understand how context updates affect re-rendering.",
                tasks: [
                  "Trigger frequent context updates",
                  "Observe component re-renders",
                  "Memoize context value using useMemo",
                ],
                mindset:
                  "Context convenience comes with performance responsibility.",
                outcome: "You’ll avoid unnecessary re-renders in global state.",
              },
            ],

            testingInsight: {
              coreReality:
                "Context bugs usually come from overuse or bad separation.",
              checklist: [
                "Is this truly global state?",
                "Does every consumer really need this data?",
                "Are context values memoized?",
              ],
              goldenRule: "Context is for sharing, not for everything.",
            },

            interviewInsights: {
              commonQuestions: [
                "What problem does Context API solve?",
                "When should you avoid Context?",
                "How does Context trigger re-renders?",
                "Context vs Redux – when to use which?",
              ],
              strongAnswerPattern:
                "Explain prop drilling → global need → performance trade-offs",
              redFlag: "Using Context as a replacement for all props.",
            },

            editorGuidance: {
              careerMindset: [
                "Global state defines app architecture",
                "Overusing context hurts performance",
                "Clarity beats convenience in state management",
              ],
              seniorAdvice:
                "If everything is in context, nothing is well designed.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As React apps grow, passing props through multiple component levels becomes difficult to manage. Context API solves this problem by providing a global store that any component can access directly.",

              contextBasics: {
                title: "What is Context API?",
                explanation:
                  "Context API is React’s built-in solution for sharing data globally across the component tree.",
                conceptsUsed: [
                  "createContext",
                  "Provider pattern",
                  "Global state concept",
                ],
                realWorldNote:
                  "Context is commonly used for app-wide data like theme, language, or authentication",
              },

              propDrilling: {
                title: "Context vs Prop Drilling",
                explanation:
                  "Prop drilling happens when props are passed through multiple layers just to reach a deeply nested component.",
                conceptsUsed: [
                  "Unidirectional data flow",
                  "Component hierarchy",
                ],
                comparison: {
                  propDrillingIssue:
                    "Hard to maintain, cluttered props, fragile refactors",
                  contextBenefit: "Direct access to data wherever needed",
                },
              },

              creatingContext: {
                title: "Creating Context & Provider",
                explanation:
                  "A context must be created and provided at a high level so child components can consume it.",
                conceptsUsed: [
                  "createContext",
                  "Context.Provider",
                  "Value prop",
                ],
                exampleLogic: `
const ThemeContext = createContext();

<ThemeContext.Provider value="dark">
  <App />
</ThemeContext.Provider>
      `,
                bestPractice:
                  "Providers are usually placed near the root (App or Layout)",
              },

              consumingContext: {
                title: "Consuming Context with useContext",
                explanation:
                  "useContext hook allows any component to read context data directly.",
                conceptsUsed: ["useContext hook", "Context subscription"],
                exampleLogic: `
const theme = useContext(ThemeContext);
      `,
                interviewInsight:
                  "Component re-renders when context value changes",
              },

              whenNotToUse: {
                title: "When NOT to Use Context",
                explanation:
                  "Context is powerful but not always the right tool.",
                points: [
                  "Frequent state updates (performance issues)",
                  "Highly localized state",
                  "Complex state logic (prefer Redux / Zustand)",
                ],
                bestPractice:
                  "Use Context for app-level state, not every piece of data",
              },

              commonMistakes: [
                {
                  mistake: "Putting everything in one context",
                  issue: "Unnecessary re-renders",
                  fix: "Split contexts by responsibility",
                },
                {
                  mistake: "Using context instead of props everywhere",
                  issue: "Over-engineering",
                  fix: "Use props for local data",
                },
                {
                  mistake: "Updating context too frequently",
                  issue: "Performance degradation",
                  fix: "Memoize values or rethink state placement",
                },
              ],

              realWorldFeatures: [
                "Theme toggle (light/dark)",
                "Auth state (logged in user)",
                "Language or locale switcher",
                "Feature flags",
              ],

              interviewQuestions: [
                "What problem does Context API solve?",
                "When should you avoid Context?",
                "How does Context affect re-rendering?",
                "Context vs Redux – when and why?",
              ],
            },

            tasks: [
              "Create a ThemeContext",
              "Wrap application with Context Provider",
              "Consume context in deeply nested component",
              "Toggle context value (light/dark)",
            ],

            examples: [
              "const ThemeContext = createContext()",
              "const theme = useContext(ThemeContext)",
            ],

            useCases: [
              "Theme switching",
              "Authentication state",
              "User preferences",
              "Language selection",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand global state needs",
              "Eliminate prop drilling",
              "Use Context API correctly",
              "Prepare for advanced state management",
            ],
          },
          {
            day: 2,
            topic: "useReducer – Managing Complex State",

            subTopics: [
              "Why useReducer over useState",
              "Reducer function structure",
              "Actions and dispatch",
              "Initial state patterns",
              "Combining useReducer with Context",
            ],

            explanation:
              "useReducer is ideal for managing complex state logic where multiple state transitions, predictable updates, and centralized control are required.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "useState vs useReducer Decision Drill",
                description:
                  "Train your judgment to choose the right state tool.",
                tasks: [
                  "Review 4 scenarios (counter, form, cart, workflow)",
                  "Decide useState or useReducer for each",
                  "Justify decision in one line",
                ],
                validationChecklist: [
                  "Is state related or independent?",
                  "Are updates predictable and action-based?",
                ],
                outcome:
                  "You’ll stop overusing useReducer and use it only where it shines.",
              },
              {
                id: "pt-2",
                title: "Reducer Refactor Challenge",
                description:
                  "Convert messy multiple useState logic into a clean reducer.",
                tasks: [
                  "Create form/cart logic using multiple useState",
                  "Identify state coupling",
                  "Refactor into useReducer",
                  "Replace setters with dispatch calls",
                ],
                qualityRule:
                  "All state updates must flow through reducer actions.",
                outcome:
                  "You’ll feel the clarity reducer architecture provides.",
              },
              {
                id: "pt-3",
                title: "Action Design Exercise",
                description: "Learn to design clean, scalable action types.",
                tasks: [
                  "Define action types for a cart system",
                  "Add payload where needed",
                  "Avoid ambiguous action names",
                ],
                antiPattern: "Generic actions like SET_STATE or UPDATE_DATA",
                outcome: "You’ll write reducers that scale without chaos.",
              },
              {
                id: "pt-4",
                title: "Global State with useReducer + Context",
                description:
                  "Build a Redux-like global store using native React tools.",
                tasks: [
                  "Create global reducer",
                  "Wrap app with Context Provider",
                  "Dispatch actions from deep components",
                ],
                mindset:
                  "Reducers control state, components only describe intent.",
                outcome:
                  "You’ll understand how Redux-style architecture really works.",
              },
            ],

            testingInsight: {
              coreReality:
                "Reducer bugs usually come from bad action design, not logic.",
              checklist: [
                "Is reducer pure?",
                "Are actions explicit and predictable?",
                "Is state immutable at every step?",
              ],
              goldenRule:
                "If you can’t describe the action clearly, the reducer is wrong.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why useReducer over useState?",
                "What is a pure reducer function?",
                "How does useReducer compare to Redux?",
                "Where should side effects live with reducers?",
              ],
              strongAnswerPattern:
                "Explain complexity → predictability → centralized updates",
              redFlag: "Using useReducer just because Redux exists.",
            },

            editorGuidance: {
              careerMindset: [
                "Reducers enforce discipline in state management",
                "Actions document how your app behaves",
                "Predictable state beats quick hacks",
              ],
              seniorAdvice:
                "If state updates feel scattered, you need a reducer.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications grow, managing related pieces of state using multiple useState hooks becomes hard to reason about. useReducer provides a predictable, centralized way to manage complex state transitions, similar to Redux but built into React.",

              whyUseReducer: {
                title: "Why useReducer over useState?",
                explanation:
                  "useReducer is preferred when state logic is complex or when the next state depends heavily on the previous state.",
                conceptsUsed: [
                  "Single source of truth",
                  "Predictable state transitions",
                  "Action-driven updates",
                ],
                comparison: {
                  useState:
                    "Best for simple, independent state (toggles, counters)",
                  useReducer:
                    "Best for complex, related state (forms, carts, workflows)",
                },
                realWorldNote:
                  "Redux-style patterns internally rely on reducer logic",
              },

              reducerStructure: {
                title: "Reducer Function Structure",
                explanation:
                  "A reducer is a pure function that takes the current state and an action, and returns the next state.",
                conceptsUsed: [
                  "Pure functions",
                  "Switch-case pattern",
                  "Immutable updates",
                ],
                exampleLogic: `
function reducer(state, action) {
  switch (action.type) {
    case 'increment':
      return { count: state.count + 1 };
    case 'decrement':
      return { count: state.count - 1 };
    default:
      return state;
  }
}
      `,
                bestPractice:
                  "Reducers must be pure (no API calls, no mutations)",
              },

              actionsAndDispatch: {
                title: "Actions & Dispatch",
                explanation:
                  "Actions describe what happened, and dispatch sends that action to the reducer.",
                conceptsUsed: [
                  "Action objects",
                  "dispatch function",
                  "Event → Action → State flow",
                ],
                exampleLogic: `
dispatch({ type: 'increment' });
dispatch({ type: 'add_item', payload: item });
      `,
                interviewInsight:
                  "Clear action names make debugging and scaling easier",
              },

              initialStatePatterns: {
                title: "Initial State Patterns",
                explanation:
                  "Initial state can be a simple object or derived dynamically using a function.",
                conceptsUsed: ["Lazy initialization", "Complex default state"],
                exampleLogic: `
const initialState = { count: 0, items: [] };

useReducer(reducer, initialState);
      `,
                bestPractice:
                  "Use initializer functions for expensive state setup",
              },

              reducerWithContext: {
                title: "Combining useReducer with Context",
                explanation:
                  "useReducer + Context is a common pattern for global state without Redux.",
                conceptsUsed: [
                  "Global reducer",
                  "Context Provider",
                  "App-wide state",
                ],
                exampleLogic: `
const AppContext = createContext();

function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}
      `,
                realWorldNote:
                  "This pattern is often used for carts, auth, and dashboards",
              },

              commonMistakes: [
                {
                  mistake: "Putting side effects inside reducer",
                  issue: "Breaks reducer purity",
                  fix: "Handle side effects outside (useEffect)",
                },
                {
                  mistake: "Too many action types without structure",
                  issue: "Hard to maintain",
                  fix: "Group actions logically",
                },
                {
                  mistake: "Mutating state directly",
                  issue: "React won’t detect changes",
                  fix: "Always return new objects",
                },
              ],

              realWorldFeatures: [
                "Shopping cart state",
                "Multi-step form state",
                "Workflow/state machines",
                "Centralized UI logic",
              ],

              interviewQuestions: [
                "When would you choose useReducer over useState?",
                "What is a pure reducer function?",
                "How does useReducer compare to Redux?",
                "How do you manage side effects with reducers?",
              ],
            },

            tasks: [
              "Create reducer for counter logic",
              "Dispatch increment and decrement actions",
              "Handle multiple actions in reducer",
              "Combine useReducer with Context",
            ],

            examples: [
              "const [state, dispatch] = useReducer(reducer, initialState)",
              "dispatch({ type: 'increment' })",
            ],

            useCases: [
              "Shopping carts",
              "Forms with many fields",
              "Complex UI workflows",
              "State machines",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Manage complex state predictably",
              "Understand reducer-based architecture",
              "Prepare for Redux-like patterns",
              "Build scalable React applications",
            ],
          },
          {
            day: 3,
            topic: "useMemo & useCallback – Performance Optimization",

            subTopics: [
              "Why re-renders happen",
              "useMemo for expensive calculations",
              "useCallback for function memoization",
              "Dependency array pitfalls",
              "React.memo basics",
            ],

            explanation:
              "useMemo and useCallback help optimize React applications by preventing unnecessary recalculations and component re-renders, especially in performance-critical scenarios.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Re-render Detection Drill",
                description:
                  "Learn to spot unnecessary re-renders before optimizing.",
                tasks: [
                  "Create a parent-child component setup",
                  "Trigger parent state updates",
                  "Observe child re-renders using console logs",
                  "Identify why child is re-rendering",
                ],
                validationChecklist: [
                  "Is prop reference changing?",
                  "Is function recreated on each render?",
                ],
                outcome: "You’ll know WHEN optimization is actually needed.",
              },
              {
                id: "pt-2",
                title: "useMemo Optimization Challenge",
                description:
                  "Optimize a slow computation without breaking correctness.",
                tasks: [
                  "Create an expensive calculation (loop / filter / reduce)",
                  "Measure re-renders without useMemo",
                  "Apply useMemo correctly",
                  "Verify output consistency",
                ],
                qualityRule:
                  "Memoization must not change the result, only performance.",
                outcome:
                  "You’ll confidently use useMemo only where it adds value.",
              },
              {
                id: "pt-3",
                title: "useCallback + React.memo Combo",
                description:
                  "Prevent child re-renders caused by unstable function references.",
                tasks: [
                  "Create memoized child component using React.memo",
                  "Pass callback without useCallback (observe re-renders)",
                  "Fix re-renders using useCallback",
                ],
                antiPattern:
                  "Wrapping callbacks in useCallback without memoized children",
                outcome:
                  "You’ll understand why useCallback alone does nothing.",
              },
              {
                id: "pt-4",
                title: "Dependency Array Debugging Exercise",
                description:
                  "Fix stale closure and dependency bugs in memo hooks.",
                tasks: [
                  "Create useMemo/useCallback with missing dependencies",
                  "Observe stale values",
                  "Fix dependency arrays properly",
                  "Re-run ESLint exhaustive-deps checks",
                ],
                mindset: "Dependency arrays are contracts, not suggestions.",
                outcome: "You’ll stop fearing dependency warnings.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most performance bugs come from unstable references, not heavy logic.",
              checklist: [
                "Is this calculation actually expensive?",
                "Is the prop reference stable?",
                "Is React.memo used correctly?",
              ],
              goldenRule: "Measure first, memoize second.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between useMemo and useCallback?",
                "When should you avoid memoization?",
                "How does React.memo compare props?",
                "Why can memoization make performance worse?",
              ],
              strongAnswerPattern:
                "Explain re-render cause → reference stability → selective memoization",
              redFlag:
                "Using useMemo/useCallback everywhere without measurement.",
            },

            editorGuidance: {
              careerMindset: [
                "Optimization is about understanding behavior, not adding hooks",
                "Stable references are the real performance win",
                "Readable code beats premature optimization",
              ],
              seniorAdvice:
                "If you don’t know why it’s slow, memoization won’t save you.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "By default, React re-renders a component whenever its state or props change. In many cases this is fine, but for large lists, expensive calculations, or deeply nested components, unnecessary re-renders can hurt performance. useMemo and useCallback help control this behavior.",

              whyRerenders: {
                title: "Why Re-renders Happen in React",
                explanation:
                  "React re-renders when state, props, or context values change. Functions and objects are recreated on every render, which can trigger child re-renders.",
                conceptsUsed: [
                  "Referential equality",
                  "State updates",
                  "Props comparison",
                ],
                realWorldNote:
                  "Most performance issues come from unnecessary child re-renders, not React itself",
              },

              useMemoConcept: {
                title: "useMemo – Memoizing Expensive Calculations",
                explanation:
                  "useMemo caches the result of a computation and recalculates it only when dependencies change.",
                conceptsUsed: [
                  "Memoization",
                  "Dependency array",
                  "Derived data",
                ],
                exampleLogic: `
const expensiveValue = useMemo(() => {
  return heavyCalculation(data);
}, [data]);
      `,
                bestPractice:
                  "Use useMemo only for expensive computations, not everywhere",
                interviewInsight:
                  "Overusing useMemo can make code harder to read with little benefit",
              },

              useCallbackConcept: {
                title: "useCallback – Memoizing Functions",
                explanation:
                  "useCallback returns the same function reference unless its dependencies change. This prevents unnecessary re-renders of memoized child components.",
                conceptsUsed: [
                  "Function reference stability",
                  "Callback props",
                  "Preventing child re-render",
                ],
                exampleLogic: `
const handleClick = useCallback(() => {
  setCount(c => c + 1);
}, []);
      `,
                realWorldNote:
                  "Most useful when passing handlers to React.memo components",
              },

              dependencyPitfalls: {
                title: "Dependency Array Pitfalls",
                explanation:
                  "Incorrect dependency arrays can cause stale values, bugs, or infinite loops.",
                conceptsUsed: ["Stale closures", "Exhaustive-deps rule"],
                commonIssues: [
                  "Missing dependencies",
                  "Using objects/functions directly in dependencies",
                  "Ignoring ESLint warnings",
                ],
                bestPractice:
                  "Always follow exhaustive-deps unless you fully understand why not",
              },

              reactMemo: {
                title: "React.memo – Component Memoization",
                explanation:
                  "React.memo prevents re-rendering of a component if its props haven't changed.",
                conceptsUsed: ["Shallow prop comparison", "Pure components"],
                exampleLogic: `
const ListItem = React.memo(({ item }) => {
  return <li>{item.name}</li>;
});
      `,
                realWorldNote:
                  "React.memo works best with stable props (useMemo/useCallback)",
              },

              commonMistakes: [
                {
                  mistake: "Using useMemo everywhere",
                  issue: "Over-optimization",
                  fix: "Use only for expensive operations",
                },
                {
                  mistake: "Ignoring dependency warnings",
                  issue: "Hard-to-debug bugs",
                  fix: "Fix dependencies or refactor logic",
                },
                {
                  mistake: "Memoizing cheap functions",
                  issue: "No real performance gain",
                  fix: "Measure before optimizing",
                },
              ],

              realWorldFeatures: [
                "Optimized dashboards",
                "Large data tables",
                "Chart-heavy UIs",
                "Reusable component libraries",
              ],

              interviewQuestions: [
                "Difference between useMemo and useCallback?",
                "When should you NOT use them?",
                "How does React.memo work?",
                "What causes unnecessary re-renders?",
              ],
            },

            tasks: [
              "Optimize an expensive calculation using useMemo",
              "Prevent child re-renders using useCallback",
              "Wrap components with React.memo",
              "Fix dependency array issues",
            ],

            examples: [
              "const value = useMemo(() => compute(data), [data]);",
              "const handler = useCallback(() => fn(id), [id]);",
            ],

            useCases: [
              "Large lists",
              "Charts and dashboards",
              "Performance-critical components",
              "Complex UI trees",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Understand React re-render behavior",
              "Optimize performance safely",
              "Avoid premature optimization",
              "Build scalable, performant React apps",
            ],
          },
          {
            day: 4,
            topic: "useRef – DOM Access & Mutable Values",

            subTopics: [
              "Accessing DOM elements",
              "Persisting values across renders",
              "Difference between ref and state",
              "Forwarding refs",
              "Common ref mistakes",
            ],

            explanation:
              "useRef provides a way to directly access DOM elements or store mutable values without causing component re-renders.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Ref vs State Decision Drill",
                description:
                  "Train yourself to choose correctly between useRef and useState.",
                tasks: [
                  "List 5 values used in a component",
                  "Decide which should use state vs ref",
                  "Explain why each choice is correct",
                ],
                validationChecklist: [
                  "Does this value affect UI?",
                  "Should UI re-render on update?",
                ],
                outcome: "You’ll stop misusing refs for UI-related data.",
              },
              {
                id: "pt-2",
                title: "Previous Value Tracker",
                description:
                  "Use useRef to track previous values without re-rendering.",
                tasks: [
                  "Create a counter using useState",
                  "Store previous count using useRef",
                  "Display current vs previous value",
                ],
                qualityRule: "Ref update must not trigger re-render.",
                outcome:
                  "You’ll understand how refs persist data across renders.",
              },
              {
                id: "pt-3",
                title: "Imperative DOM Control Exercise",
                description: "Safely use refs for DOM manipulation.",
                tasks: [
                  "Focus input on button click",
                  "Scroll to a specific section using ref",
                  "Prevent accessing ref before mount",
                ],
                antiPattern: "Using refs to control UI logic instead of state",
                outcome: "You’ll use refs only for true imperative needs.",
              },
              {
                id: "pt-4",
                title: "forwardRef Integration Challenge",
                description:
                  "Build a reusable component that accepts refs from parent.",
                tasks: [
                  "Create custom Input component",
                  "Forward ref using forwardRef",
                  "Control focus from parent component",
                ],
                mindset:
                  "Refs are contracts between components, not shortcuts.",
                outcome:
                  "You’ll be comfortable using refs in component libraries.",
              },
            ],

            testingInsight: {
              coreReality:
                "Refs bypass React’s re-render cycle — misuse leads to invisible bugs.",
              checklist: [
                "Should this change re-render UI?",
                "Is this value purely imperative?",
                "Is ref being accessed safely?",
              ],
              goldenRule: "If UI depends on it, it does NOT belong in a ref.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between useRef and useState?",
                "Does updating ref cause re-render?",
                "When should refs be avoided?",
                "What is forwardRef used for?",
              ],
              strongAnswerPattern:
                "Explain UI impact → re-render behavior → imperative use cases",
              redFlag: "Using useRef as a replacement for state.",
            },

            editorGuidance: {
              careerMindset: [
                "Refs are escape hatches, not primary tools",
                "Declarative logic scales better than imperative hacks",
                "Senior engineers minimize ref usage",
              ],
              seniorAdvice:
                "If you need many refs, your component design needs review.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In React, most data is managed using state and props. However, sometimes you need direct access to DOM elements or want to store values that should persist across renders without triggering re-renders. useRef solves these problems.",

              domAccess: {
                title: "Accessing DOM Elements with useRef",
                explanation:
                  "useRef can hold a reference to a DOM element, allowing imperative actions like focusing, scrolling, or measuring.",
                conceptsUsed: [
                  "Refs",
                  "Imperative DOM actions",
                  "Ref attachment",
                ],
                exampleLogic: `
const inputRef = useRef(null);

<button onClick={() => inputRef.current.focus()}>
  Focus Input
</button>

<input ref={inputRef} />
      `,
                realWorldNote:
                  "Commonly used for focus management and scroll behavior",
              },

              persistentValues: {
                title: "Persisting Values Across Renders",
                explanation:
                  "Unlike local variables, ref values persist across renders without causing re-render when updated.",
                conceptsUsed: ["Mutable ref object", "No re-render on update"],
                exampleLogic: `
const prevCount = useRef(0);

useEffect(() => {
  prevCount.current = count;
}, [count]);
      `,
                interviewInsight:
                  "Refs are ideal for storing previous values or timers",
              },

              refVsState: {
                title: "Difference Between useRef and useState",
                comparison: [
                  "useState → causes re-render on update",
                  "useRef → does NOT cause re-render",
                  "State → used for UI data",
                  "Ref → used for non-UI, mutable values",
                ],
                bestPractice:
                  "If data affects UI → useState, otherwise → useRef",
              },

              forwardingRefs: {
                title: "Forwarding Refs",
                explanation:
                  "forwardRef allows passing refs from parent to child components, useful for reusable input components.",
                conceptsUsed: ["forwardRef", "Ref forwarding"],
                exampleLogic: `
const Input = React.forwardRef((props, ref) => {
  return <input ref={ref} {...props} />;
});
      `,
                realWorldNote:
                  "Often used in component libraries and design systems",
              },

              commonMistakes: [
                {
                  mistake: "Using useRef instead of useState for UI data",
                  issue: "UI does not update",
                  fix: "Use state for render-affecting data",
                },
                {
                  mistake: "Accessing ref before render",
                  issue: "ref.current is null",
                  fix: "Access refs after render or in event handlers",
                },
                {
                  mistake: "Overusing refs",
                  issue: "Breaks declarative React model",
                  fix: "Prefer state-driven logic when possible",
                },
              ],

              realWorldFeatures: [
                "Auto-focus login inputs",
                "Scroll-to-section navigation",
                "Interval and timeout storage",
                "Measuring element dimensions",
              ],

              interviewQuestions: [
                "Difference between useRef and useState?",
                "When should you use useRef?",
                "Does updating ref cause re-render?",
                "What is forwardRef and why is it used?",
              ],
            },

            tasks: [
              "Focus input on button click using ref",
              "Store previous value using useRef",
              "Implement scroll-to-section feature",
              "Compare ref vs state behavior",
            ],

            examples: [
              "const inputRef = useRef(null);",
              "inputRef.current.focus();",
            ],

            useCases: [
              "Form focus",
              "Scroll management",
              "Timers and intervals",
              "Previous state tracking",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand imperative vs declarative logic",
              "Use refs safely and correctly",
              "Avoid unnecessary re-renders",
              "Prepare for advanced React patterns",
            ],
          },
          {
            day: 5,
            topic: "Creating Custom Hooks",

            subTopics: [
              "What are custom hooks",
              "Rules of hooks",
              "Extracting reusable logic",
              "Naming conventions",
              "Sharing logic between components",
            ],

            explanation:
              "Custom hooks allow you to extract and reuse stateful logic across multiple components, keeping code clean, reusable, and maintainable.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Logic Extraction Identification Drill",
                description:
                  "Train yourself to recognize when logic should become a custom hook.",
                tasks: [
                  "Review a component with mixed UI + logic",
                  "Identify repeated or reusable logic",
                  "Extract that logic into a custom hook",
                ],
                validationChecklist: [
                  "Hook contains no JSX",
                  "Hook follows rules of hooks",
                  "Component becomes smaller and cleaner",
                ],
                outcome:
                  "You’ll naturally spot hook-worthy logic in real projects.",
              },
              {
                id: "pt-2",
                title: "Rules of Hooks Violation Lab",
                description:
                  "Learn hook rules by intentionally breaking and fixing them.",
                tasks: [
                  "Call a hook inside condition (break rule)",
                  "Observe error / behavior",
                  "Refactor to correct structure",
                ],
                qualityRule:
                  "Hooks must be called unconditionally at top level.",
                outcome: "You’ll never accidentally break hook rules again.",
              },
              {
                id: "pt-3",
                title: "Reusable useFetch Hook",
                description:
                  "Build a production-style data-fetching custom hook.",
                tasks: [
                  "Create useFetch hook with loading & error state",
                  "Accept URL as parameter",
                  "Return data, loading, and error",
                ],
                antiPattern: "Mixing UI rendering logic inside hooks",
                outcome: "You’ll be confident writing real-world custom hooks.",
              },
              {
                id: "pt-4",
                title: "Hook Reuse Across Components",
                description: "Prove the value of hooks by reusing logic.",
                tasks: [
                  "Use same custom hook in 2+ components",
                  "Verify behavior consistency",
                  "Remove duplicated logic",
                ],
                mindset: "Hooks scale logic, not UI.",
                outcome:
                  "You’ll experience why hooks are preferred over copy-paste.",
              },
            ],

            testingInsight: {
              coreReality: "Custom hooks don’t share state — they share logic.",
              checklist: [
                "Is this logic reusable?",
                "Does this hook hide complexity?",
                "Is state isolated per hook call?",
              ],
              goldenRule: "If logic repeats twice, consider a custom hook.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are custom hooks?",
                "Why must hooks start with `use`?",
                "Can custom hooks share state?",
                "Custom hooks vs HOCs?",
              ],
              strongAnswerPattern:
                "Explain problem → extraction → reuse → cleaner components",
              redFlag: "Returning JSX or mixing UI inside a custom hook.",
            },

            editorGuidance: {
              careerMindset: [
                "Custom hooks are architecture tools, not shortcuts",
                "Logic reuse matters more than component reuse",
                "Clean hooks lead to clean components",
              ],
              seniorAdvice:
                "If components feel bloated, extract logic into hooks.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As React applications grow, logic like form handling, API calls, toggles, and localStorage syncing often gets repeated across components. Custom hooks solve this by extracting reusable logic into functions that follow React’s hooks rules.",

              whatAreCustomHooks: {
                title: "What Are Custom Hooks?",
                explanation:
                  "Custom hooks are normal JavaScript functions that use React hooks internally and start with the `use` keyword.",
                conceptsUsed: [
                  "Hook composition",
                  "Stateful logic reuse",
                  "Separation of concerns",
                ],
                realWorldNote:
                  "Custom hooks are heavily used in real codebases and libraries like React Query",
              },

              rulesOfHooks: {
                title: "Rules of Hooks (VERY IMPORTANT)",
                explanation:
                  "Custom hooks must follow the same rules as built-in hooks to work correctly.",
                rules: [
                  "Always start hook name with `use`",
                  "Call hooks only at top level",
                  "Do not call hooks inside loops or conditions",
                  "Only call hooks from React components or custom hooks",
                ],
                interviewInsight:
                  "Violating hook rules causes unpredictable bugs and breaks React internals",
              },

              extractingLogic: {
                title: "Extracting Reusable Logic",
                explanation:
                  "If multiple components share the same logic, extract it into a custom hook.",
                exampleLogic: `
function useToggle(initial = false) {
  const [value, setValue] = useState(initial);
  const toggle = () => setValue(v => !v);
  return [value, toggle];
}
      `,
                benefit: "Components focus on UI, hooks focus on logic",
              },

              useLocalStorageHook: {
                title: "Custom Hook – useLocalStorage",
                explanation:
                  "A common real-world hook to persist state in localStorage.",
                conceptsUsed: ["useState", "useEffect", "Browser APIs"],
                exampleLogic: `
function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}
      `,
                realWorldNote: "Used for themes, auth tokens, preferences",
              },

              namingConventions: {
                title: "Naming Conventions & Structure",
                bestPractices: [
                  "Always prefix with `use`",
                  "Name should describe behavior (useAuth, useFetch)",
                  "One responsibility per hook",
                  "Avoid UI-related logic inside hooks",
                ],
                exampleNames: [
                  "useFetch",
                  "useDebounce",
                  "useTheme",
                  "useAuth",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Putting JSX inside custom hook",
                  issue: "Breaks separation of concerns",
                  fix: "Hooks should return data, not UI",
                },
                {
                  mistake: "Over-generalizing hooks",
                  issue: "Hard to maintain",
                  fix: "Keep hooks focused and simple",
                },
                {
                  mistake: "Violating hook rules",
                  issue: "Unexpected bugs",
                  fix: "Follow rules strictly",
                },
              ],

              realWorldFeatures: [
                "Reusable form handling",
                "Shared API logic",
                "Theme & auth management",
                "Cleaner component code",
              ],

              interviewQuestions: [
                "Why do custom hooks start with `use`?",
                "What problems do custom hooks solve?",
                "Difference between HOC and custom hooks?",
                "Can a custom hook return JSX?",
              ],
            },

            tasks: [
              "Create useToggle hook",
              "Create useLocalStorage hook",
              "Refactor component logic into custom hook",
              "Reuse hook across multiple components",
            ],

            examples: [
              "function useToggle(initial) { const [v, s] = useState(initial); return [v, () => s(!v)]; }",
            ],

            useCases: [
              "Form logic",
              "Reusable UI behavior",
              "State abstraction",
              "Large-scale React apps",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Write reusable React logic",
              "Follow hook rules correctly",
              "Improve code cleanliness",
              "Prepare for production-level React",
            ],
          },
          {
            day: 6,
            topic: "Advanced Custom Hooks & Best Practices",

            subTopics: [
              "useFetch hook",
              "Handling loading & error states",
              "Dependency safety in hooks",
              "Composing hooks",
              "Testing custom hooks basics",
            ],

            explanation:
              "Advanced custom hooks encapsulate async logic, side effects, and state into reusable utilities, helping keep components clean and production-ready.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade useFetch Challenge",
                description:
                  "Upgrade a basic useFetch into a safe, reusable, production-style hook.",
                tasks: [
                  "Handle loading, error, and success states",
                  "Add dependency safety for URL changes",
                  "Prevent state update after unmount",
                ],
                validationChecklist: [
                  "No memory leak warnings",
                  "Loading resets correctly on URL change",
                  "Hook returns consistent API",
                ],
                outcome:
                  "You’ll be able to write interview-level async hooks confidently.",
              },
              {
                id: "pt-2",
                title: "Dependency Safety Audit",
                description:
                  "Learn to spot unstable dependencies inside custom hooks.",
                tasks: [
                  "Pass a function as dependency and observe reruns",
                  "Stabilize it using useCallback",
                  "Document required dependencies in hook usage",
                ],
                qualityRule:
                  "Custom hooks must expose stable and predictable behavior.",
                outcome: "You’ll avoid stale data and infinite loops in hooks.",
              },
              {
                id: "pt-3",
                title: "Hook Composition Exercise",
                description:
                  "Build advanced behavior by composing smaller hooks.",
                tasks: [
                  "Create small hooks (useAuth, useFetch)",
                  "Compose them into a higher-level hook (useUserProfile)",
                  "Reuse composed hook in component",
                ],
                antiPattern: "One giant hook doing everything",
                outcome:
                  "You’ll think in composable logic, not monolithic hooks.",
              },
              {
                id: "pt-4",
                title: "Hook Testability Mindset",
                description:
                  "Design hooks that are easy to test even without writing tests.",
                tasks: [
                  "Ensure hook has deterministic output",
                  "Avoid hidden side effects",
                  "Ensure hook can run without DOM",
                ],
                mindset:
                  "If a hook is hard to test, its design is probably wrong.",
                outcome: "You’ll naturally write test-friendly hooks.",
              },
            ],

            testingInsight: {
              coreReality: "Great hooks are logic-first and UI-agnostic.",
              checklist: [
                "Does this hook hide complexity?",
                "Is async flow predictable?",
                "Are side effects cleaned up?",
              ],
              goldenRule: "Hooks should make components boring and simple.",
            },

            interviewInsights: {
              commonQuestions: [
                "How would you design a production-ready useFetch?",
                "How do you prevent race conditions in hooks?",
                "How do you ensure dependency safety?",
                "When should hooks NOT be created?",
              ],
              strongAnswerPattern:
                "Explain problem → abstraction → safety → reuse",
              redFlag:
                "Hooks that mix UI, side effects, and business logic together.",
            },

            editorGuidance: {
              careerMindset: [
                "Advanced hooks define application architecture",
                "Hooks are contracts, not helpers",
                "Stable hooks scale teams and codebases",
              ],
              seniorAdvice:
                "If your hook surprises users, it’s badly designed.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world React applications, components should focus mainly on UI. Advanced custom hooks help move complex logic such as API calls, loading states, error handling, and side effects into reusable, testable utilities.",

              useFetchHook: {
                title: "Building a useFetch Hook",
                explanation:
                  "useFetch abstracts API fetching logic and exposes data, loading, and error states to components.",
                conceptsUsed: [
                  "useState",
                  "useEffect",
                  "Async/await",
                  "Cleanup patterns",
                ],
                exampleLogic: `
function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const res = await fetch(url);
        const json = await res.json();
        if (isMounted) setData(json);
      } catch (err) {
        if (isMounted) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [url]);

  return { data, loading, error };
}
      `,
                realWorldNote:
                  "This pattern is commonly replaced by libraries like React Query, but interviews expect you to understand it",
              },

              loadingAndError: {
                title: "Handling Loading & Error States",
                explanation:
                  "Good UX requires clear loading indicators and user-friendly error messages.",
                conceptsUsed: ["Conditional rendering", "State-driven UI"],
                bestPractice:
                  "Always handle loading and error explicitly instead of assuming success",
              },

              dependencySafety: {
                title: "Dependency Safety in Custom Hooks",
                explanation:
                  "Incorrect dependencies can cause stale data or infinite loops.",
                conceptsUsed: [
                  "Dependency arrays",
                  "Stable references",
                  "useCallback / useMemo",
                ],
                commonIssues: [
                  "Forgetting dependencies",
                  "Passing unstable functions",
                  "Ignoring ESLint warnings",
                ],
                bestPractice:
                  "Expose stable APIs from hooks and document dependencies clearly",
              },

              composingHooks: {
                title: "Composing Hooks (Hooks using Hooks)",
                explanation:
                  "Custom hooks can call other custom hooks to build powerful abstractions.",
                exampleLogic: `
function useUserData(id) {
  return useFetch(\`/api/users/\${id}\`);
}
      `,
                benefit:
                  "Small hooks composed together are easier to maintain than one giant hook",
              },

              testingHooks: {
                title: "Testing Custom Hooks (Basics)",
                explanation:
                  "Hooks should be testable independently from UI components.",
                conceptsUsed: ["Logic isolation", "Deterministic behavior"],
                realWorldNote:
                  "Libraries like @testing-library/react-hooks are commonly used",
              },

              commonMistakes: [
                {
                  mistake: "Putting UI logic inside hooks",
                  issue: "Tight coupling",
                  fix: "Return data, not JSX",
                },
                {
                  mistake: "Not handling cleanup",
                  issue: "Memory leaks",
                  fix: "Always clean up async effects",
                },
                {
                  mistake: "Over-generalizing hooks",
                  issue: "Hard to understand",
                  fix: "Keep hooks focused",
                },
              ],

              realWorldFeatures: [
                "Reusable API layer",
                "Cleaner components",
                "Consistent error handling",
                "Scalable architecture",
              ],

              interviewQuestions: [
                "How would you build a useFetch hook?",
                "How do you avoid race conditions?",
                "When should you NOT create a custom hook?",
                "How do hooks help testing?",
              ],
            },

            tasks: [
              "Build useFetch hook",
              "Add loading & error handling",
              "Reuse hook across components",
              "Ensure dependency safety",
            ],

            examples: ["const { data, loading, error } = useFetch(url);"],

            useCases: [
              "API integrations",
              "Reusable data fetching",
              "Clean component code",
              "Production-scale React apps",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Write production-ready custom hooks",
              "Handle async logic safely",
              "Compose hooks effectively",
              "Prepare for real-world React architecture",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Social Media Feed",

            subTopics: [
              "Global state with Context",
              "Feed state management with useReducer",
              "Custom hooks for data fetching",
              "Infinite scroll",
              "Like & comment interactions",
            ],

            explanation:
              "This project combines advanced React hooks and patterns to build a scalable, real-world social media feed similar to Instagram, Twitter, or LinkedIn.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Feed Architecture Design",
                description:
                  "Design a scalable feed architecture before writing UI code.",
                tasks: [
                  "Decide what lives in Context vs local state",
                  "Define feed reducer state shape",
                  "List all reducer actions (LOAD, LIKE, COMMENT, PAGINATE)",
                ],
                validationChecklist: [
                  "Single source of truth for feed",
                  "No duplicated post state",
                  "Clear ownership of data",
                ],
                outcome:
                  "You’ll learn to think like a senior engineer before coding.",
              },
              {
                id: "pt-2",
                title: "Reducer-Driven Feed Logic",
                description:
                  "Handle all feed interactions using reducer actions.",
                tasks: [
                  "Implement ADD_POST and LOAD_MORE actions",
                  "Implement LIKE_POST with immutable updates",
                  "Add COMMENT_POST action",
                ],
                qualityRule: "Reducers must stay pure and predictable.",
                outcome: "You’ll gain confidence managing complex UI state.",
              },
              {
                id: "pt-3",
                title: "Infinite Scroll with Safety",
                description:
                  "Implement infinite scrolling without duplicate calls or leaks.",
                tasks: [
                  "Use IntersectionObserver with ref",
                  "Prevent duplicate fetch triggers",
                  "Clean observer on unmount",
                ],
                antiPattern: "Triggering fetch on every scroll event",
                outcome: "You’ll implement infinite scroll the production way.",
              },
              {
                id: "pt-4",
                title: "Optimistic UI for Likes",
                description:
                  "Make the UI respond instantly before server confirmation.",
                tasks: [
                  "Update UI immediately on like",
                  "Handle rollback on API failure (conceptual)",
                  "Avoid re-rendering entire feed",
                ],
                mindset: "Speed is UX — consistency comes second.",
                outcome: "You’ll understand real-world UI tradeoffs.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most feed bugs come from incorrect state normalization.",
              checklist: [
                "Are posts uniquely identified?",
                "Does one action affect only one post?",
                "Is reducer free from mutations?",
              ],
              goldenRule:
                "If one like re-renders the whole feed, the architecture is wrong.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why useReducer for a feed?",
                "How do you optimize large lists?",
                "How does infinite scroll work internally?",
                "What are optimistic UI updates?",
                "How would you persist feed state?",
              ],
              strongAnswerPattern:
                "Explain architecture → state flow → performance → scalability",
              redFlag: "Using useState everywhere for feed interactions.",
            },

            editorGuidance: {
              careerMindset: [
                "Feeds expose architectural weaknesses quickly",
                "State structure matters more than UI polish",
                "Scalability is about preventing re-renders",
              ],
              seniorAdvice:
                "If your feed logic feels messy, your state model is broken.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A social media feed is one of the best examples of real-world frontend complexity. It involves global state, async data fetching, performance optimization, and user interactions at scale. This project mirrors how production-grade feeds are built.",

              globalState: {
                title: "Global Feed State with Context",
                explanation:
                  "Feed data (posts, likes, user actions) must be shared across multiple components, making Context a natural choice.",
                conceptsUsed: [
                  "React Context",
                  "Provider pattern",
                  "Avoiding prop drilling",
                ],
                exampleLogic: `
const FeedContext = createContext();

function FeedProvider({ children }) {
  return (
    <FeedContext.Provider value={state}>
      {children}
    </FeedContext.Provider>
  );
}
      `,
                realWorldNote:
                  "Most real apps combine Context with reducers or external stores",
              },

              reducerState: {
                title: "Feed State Management with useReducer",
                explanation:
                  "useReducer provides predictable state updates for complex interactions like likes, comments, and pagination.",
                conceptsUsed: [
                  "Reducer pattern",
                  "Action-based updates",
                  "Immutable state updates",
                ],
                exampleLogic: `
function feedReducer(state, action) {
  switch(action.type) {
    case 'ADD_POST':
      return { ...state, posts: [...state.posts, action.payload] };
    case 'LIKE_POST':
      return { ...state };
    default:
      return state;
  }
}
      `,
                interviewInsight:
                  "Reducers are preferred when state transitions grow complex",
              },

              customHooks: {
                title: "Custom Hooks for Feed Logic",
                explanation:
                  "Custom hooks keep components clean by extracting logic like fetching posts or infinite scroll.",
                conceptsUsed: [
                  "useFetch pattern",
                  "Hook composition",
                  "Separation of concerns",
                ],
                exampleLogic: `
const { posts, loading } = useFeed();
      `,
                benefit: "UI components stay declarative and focused",
              },

              infiniteScroll: {
                title: "Infinite Scroll Implementation",
                explanation:
                  "Infinite scrolling loads more content as the user scrolls, improving UX and performance.",
                conceptsUsed: [
                  "IntersectionObserver",
                  "Refs",
                  "Pagination logic",
                ],
                exampleLogic: `
useEffect(() => {
  if (isVisible) dispatch({ type: 'LOAD_MORE' });
}, [isVisible]);
      `,
                realWorldNote: "Used by almost every modern content platform",
              },

              interactions: {
                title: "Like & Comment Interactions",
                explanation:
                  "User interactions update local UI immediately for responsiveness.",
                conceptsUsed: [
                  "Optimistic UI updates",
                  "Reducer actions",
                  "Derived state",
                ],
                exampleLogic: `
dispatch({ type: 'LIKE_POST', payload: postId });
      `,
                interviewInsight:
                  "Optimistic updates are a key real-world concept",
              },

              performance: {
                title: "Performance Optimization",
                explanation:
                  "Feeds can grow large, so avoiding unnecessary re-renders is critical.",
                conceptsUsed: ["React.memo", "useCallback", "useMemo"],
                bestPractice:
                  "Stabilize props and callbacks passed to post components",
              },

              componentBreakdown: {
                title: "Suggested Component Structure",
                components: [
                  "FeedProvider",
                  "FeedList",
                  "PostCard",
                  "LikeButton",
                  "CommentSection",
                  "InfiniteLoader",
                ],
                benefit: "Keeps feed scalable and easy to maintain",
              },

              commonMistakes: [
                {
                  mistake: "Storing entire feed logic in one component",
                  issue: "Unmaintainable code",
                  fix: "Split logic using Context + hooks",
                },
                {
                  mistake: "Mutating state in reducer",
                  issue: "Broken UI updates",
                  fix: "Always return new state",
                },
                {
                  mistake: "No render optimization",
                  issue: "Performance issues",
                  fix: "Use memoization wisely",
                },
              ],

              realWorldFeatures: [
                "Optimistic likes",
                "Lazy loading posts",
                "Scalable feed architecture",
                "Clean separation of logic and UI",
              ],

              interviewQuestions: [
                "Why useReducer over useState here?",
                "How would you scale this feed?",
                "How do you avoid re-rendering all posts?",
                "How would you persist feed state?",
              ],
            },

            tasks: [
              "Create global feed context",
              "Manage feed state using useReducer",
              "Implement infinite scrolling",
              "Add like and comment interactions",
              "Optimize renders using memoization",
            ],

            examples: ["useFeedContext()", "useInfiniteScroll()"],

            useCases: [
              "Social media apps",
              "Content feeds",
              "Scalable frontend architecture",
              "Interview-level React project",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Apply Context + Reducer in real app",
              "Build scalable feed architecture",
              "Optimize performance",
              "Prepare for production-level React",
            ],
          },
        ],
      },
      {
        week: 23,
        weekGoal:
          "Master client-side routing, navigation patterns, and protected routes in React",
        weekOutcome:
          "Ability to build multi-page React applications with dynamic routing and authentication flows",
        miniProject: "Multi-Page Application",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "React Router Setup & Basics",

            subTopics: [
              "What is client-side routing",
              "React Router DOM installation",
              "BrowserRouter and Routes",
              "Route component basics",
              "Single Page Application (SPA) concept",
            ],

            explanation:
              "React Router enables navigation between different views without page reloads, making React apps feel fast and seamless.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "SPA vs MPA Mental Model Drill",
                description:
                  "Understand why client-side routing exists before using React Router.",
                tasks: [
                  "Compare SPA vs traditional MPA navigation",
                  "Identify what reloads and what stays in memory",
                  "List advantages of SPA for user experience",
                ],
                validationChecklist: [
                  "No full page reload on navigation",
                  "State persists across routes",
                ],
                outcome:
                  "You’ll clearly understand why React Router is used in real apps.",
              },
              {
                id: "pt-2",
                title: "Router Setup from Scratch",
                description:
                  "Set up React Router correctly like in production projects.",
                tasks: [
                  "Install react-router-dom",
                  "Wrap app with BrowserRouter at root",
                  "Create Routes and Route components",
                ],
                qualityRule: "Router must exist only once at the app root.",
                outcome: "You’ll avoid common setup mistakes beginners make.",
              },
              {
                id: "pt-3",
                title: "Route Mapping Exercise",
                description: "Practice mapping URLs to components cleanly.",
                tasks: [
                  "Create Home, About, Contact routes",
                  "Verify correct component rendering per URL",
                  "Test direct URL access in browser",
                ],
                antiPattern: "Placing routes deep inside random components",
                outcome: "You’ll learn clean navigation structure.",
              },
              {
                id: "pt-4",
                title: "Navigation Without Reload",
                description: "Ensure navigation never causes page reload.",
                tasks: [
                  "Replace <a> tags with Link",
                  "Observe network tab on navigation",
                  "Confirm no document reload happens",
                ],
                mindset: "Reload means broken SPA experience.",
                outcome: "You’ll build truly seamless navigation.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most routing bugs come from incorrect router placement.",
              checklist: [
                "Is BrowserRouter wrapping the entire app?",
                "Are routes defined only once?",
                "Does navigation avoid full reload?",
              ],
              goldenRule:
                "If the page reloads, you are not using React Router correctly.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is client-side routing?",
                "How does React Router avoid page reload?",
                "Difference between SPA and MPA?",
                "Why BrowserRouter is required?",
              ],
              strongAnswerPattern:
                "Explain SPA concept → Router role → URL matching → component rendering",
              redFlag: "Using <a> tags for navigation in a React app.",
            },

            editorGuidance: {
              careerMindset: [
                "Routing defines app navigation architecture",
                "Wrong router setup breaks the entire app",
                "Clean routing improves scalability",
              ],
              seniorAdvice:
                "If routing feels buggy, your app structure is wrong.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Traditional websites reload the entire page on navigation. React Router enables client-side routing, where only the required components change while the page stays loaded, resulting in faster and smoother user experience.",

              spaConcept: {
                title: "Single Page Application (SPA)",
                explanation:
                  "In an SPA, the browser loads a single HTML page and dynamically updates the UI using JavaScript.",
                conceptsUsed: [
                  "No full page reload",
                  "Fast navigation",
                  "State preserved between routes",
                ],
                realWorldExamples: ["Gmail", "Instagram", "LinkedIn"],
              },

              installation: {
                title: "Installing React Router",
                explanation:
                  "React Router DOM is the official routing library for web-based React applications.",
                exampleLogic: `
npm install react-router-dom
      `,
                note: "Always use the latest stable version for new projects",
              },

              browserRouter: {
                title: "BrowserRouter",
                explanation:
                  "BrowserRouter uses the HTML5 history API to keep the UI in sync with the URL.",
                conceptsUsed: [
                  "History API",
                  "URL-based navigation",
                  "Top-level router wrapper",
                ],
                exampleLogic: `
import { BrowserRouter } from 'react-router-dom';

<BrowserRouter>
  <App />
</BrowserRouter>
      `,
                bestPractice:
                  "Wrap BrowserRouter at the root of the app (main.jsx / index.js)",
              },

              routesAndRoute: {
                title: "Routes & Route Components",
                explanation:
                  "Routes act as a container for all Route definitions. Each Route maps a URL path to a component.",
                conceptsUsed: [
                  "path matching",
                  "element rendering",
                  "component-based navigation",
                ],
                exampleLogic: `
<Routes>
  <Route path="/" element={<Home />} />
  <Route path="/about" element={<About />} />
</Routes>
      `,
                interviewInsight: "Only the first matching route is rendered",
              },

              navigationFlow: {
                title: "How Routing Works Internally",
                explanation:
                  "When the URL changes, React Router matches the path and renders the corresponding component without reloading the page.",
                flow: [
                  "User clicks link",
                  "URL changes",
                  "Router matches route",
                  "Component re-renders",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Forgetting BrowserRouter wrapper",
                  issue: "Routes won't work",
                  fix: "Wrap app with BrowserRouter",
                },
                {
                  mistake: "Using <a> instead of router links",
                  issue: "Page reloads",
                  fix: "Use Link or NavLink",
                },
              ],

              interviewQuestions: [
                "What is client-side routing?",
                "Difference between SPA and MPA?",
                "Why BrowserRouter is needed?",
                "How React Router avoids page reload?",
              ],
            },

            tasks: [
              "Install react-router-dom",
              "Wrap app with BrowserRouter",
              "Create basic routes (Home, About, Contact)",
            ],

            examples: [
              "<BrowserRouter><Routes><Route path='/' element={<Home />} /></Routes></BrowserRouter>",
            ],

            useCases: [
              "Single page applications",
              "Dashboard navigation",
              "Public websites",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand SPA navigation",
              "Setup routing correctly",
              "Prepare base for protected routes",
              "Build scalable navigation structure",
            ],
          },
          {
            day: 2,
            topic: "Link, NavLink & Navigation UI",

            subTopics: [
              "Link vs anchor tag",
              "NavLink active states",
              "Navigation bars",
              "Layout components",
              "Styling active routes",
            ],

            explanation:
              "Link and NavLink allow navigation without page reload and help create accessible navigation menus.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Link vs Anchor Reality Check",
                description: "Understand why anchor tags break SPA behavior.",
                tasks: [
                  "Navigate using <a> and observe full page reload",
                  "Replace <a> with Link",
                  "Verify state persistence between routes",
                ],
                validationChecklist: [
                  "No page reload",
                  "SPA state remains intact",
                ],
                outcome:
                  "You’ll never accidentally use <a> in React Router apps again.",
              },
              {
                id: "pt-2",
                title: "NavLink Active State Mastery",
                description:
                  "Build navigation that clearly reflects current route.",
                tasks: [
                  "Create menu using NavLink",
                  "Apply active styles using isActive",
                  "Test active states on route change",
                ],
                qualityRule: "Active route must be visually distinguishable",
                outcome: "You’ll build professional navigation UIs.",
              },
              {
                id: "pt-3",
                title: "Layout Component Refactor",
                description: "Remove duplicated navigation using layouts.",
                tasks: [
                  "Create a Layout component",
                  "Move navbar into layout",
                  "Render child routes using Outlet",
                ],
                antiPattern: "Repeating navbar in every page component",
                outcome: "You’ll understand real-world routing structure.",
              },
              {
                id: "pt-4",
                title: "Navigation UX Review",
                description: "Improve usability of navigation menus.",
                tasks: [
                  "Highlight active route clearly",
                  "Ensure navigation is keyboard accessible",
                  "Check navigation on refresh & direct URLs",
                ],
                mindset: "Navigation clarity defines app usability.",
                outcome:
                  "You’ll think like a product-focused frontend developer.",
              },
            ],

            testingInsight: {
              coreReality:
                "Broken navigation usually means broken routing structure.",
              checklist: [
                "Are Link/NavLink used instead of <a>?",
                "Does active route styling work correctly?",
                "Is navigation shared via layout?",
              ],
              goldenRule: "If navigation reloads the page, your SPA is broken.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between Link and NavLink?",
                "Why not use anchor tags in React Router?",
                "How do you highlight active routes?",
                "What is a layout component and why use it?",
              ],
              strongAnswerPattern:
                "Explain SPA behavior → Link/NavLink role → active state UX → layouts",
              redFlag: "Using <a href> for internal navigation.",
            },

            editorGuidance: {
              careerMindset: [
                "Navigation defines first impression of an app",
                "Clean routing improves maintainability",
                "Layouts reduce duplication and bugs",
              ],
              seniorAdvice:
                "If your navbar logic is scattered, your routing design is weak.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Navigation is the backbone of any real-world application. React Router provides Link and NavLink components that allow seamless navigation without reloading the page, preserving application state and performance.",

              linkVsAnchor: {
                title: "Link vs Anchor Tag (<a>)",
                explanation:
                  "Anchor tags reload the entire page, whereas Link performs client-side navigation without refresh.",
                conceptsUsed: [
                  "Client-side routing",
                  "Preserving application state",
                  "Faster navigation",
                ],
                exampleLogic: `
<Link to="/about">About</Link>
// instead of
<a href="/about">About</a>
      `,
                interviewInsight:
                  "Using <a> in React Router apps breaks SPA behavior",
              },

              navLinkBasics: {
                title: "NavLink & Active State",
                explanation:
                  "NavLink extends Link by providing information about whether the link is currently active.",
                conceptsUsed: [
                  "isActive flag",
                  "Dynamic class assignment",
                  "Route-aware UI",
                ],
                exampleLogic: `
<NavLink
  to="/blog"
  className={({ isActive }) => isActive ? "active" : ""}
>
  Blog
</NavLink>
      `,
                bestPractice:
                  "Use NavLink for menus and Link for buttons or inline navigation",
              },

              stylingActiveRoutes: {
                title: "Styling Active Routes",
                explanation:
                  "Active route styling helps users understand their current location in the app.",
                conceptsUsed: [
                  "Conditional class names",
                  "CSS-based active states",
                  "UX feedback",
                ],
                exampleLogic: `
.active {
  color: #3b82f6;
  font-weight: bold;
}
      `,
                realWorldNote:
                  "Most dashboards visually highlight active routes",
              },

              navigationBar: {
                title: "Building a Navigation Bar",
                explanation:
                  "Navigation bars usually live at the top or side of the application and persist across routes.",
                conceptsUsed: [
                  "Reusable components",
                  "NavLink lists",
                  "Layout separation",
                ],
                exampleLogic: `
<nav>
  <NavLink to="/">Home</NavLink>
  <NavLink to="/about">About</NavLink>
  <NavLink to="/contact">Contact</NavLink>
</nav>
      `,
              },

              layoutComponents: {
                title: "Layout Components",
                explanation:
                  "Layouts wrap shared UI like headers or sidebars around routed content.",
                conceptsUsed: [
                  "Outlet component",
                  "Shared UI structure",
                  "Nested routing preparation",
                ],
                exampleLogic: `
import { Outlet } from "react-router-dom";

const Layout = () => (
  <>
    <Navbar />
    <Outlet />
  </>
);
      `,
                interviewInsight:
                  "Layouts are heavily used in dashboards and admin panels",
              },

              commonMistakes: [
                {
                  mistake: "Using <a> instead of Link",
                  issue: "Full page reload",
                  fix: "Use Link / NavLink",
                },
                {
                  mistake: "Hardcoding active styles",
                  issue: "Incorrect active indication",
                  fix: "Use NavLink isActive",
                },
                {
                  mistake: "Repeating navbar in every page",
                  issue: "Code duplication",
                  fix: "Use layout components",
                },
              ],

              realWorldFeatures: [
                "Sticky navigation bar",
                "Sidebar navigation",
                "Active route highlighting",
                "Responsive mobile menu",
              ],

              interviewQuestions: [
                "Difference between Link and NavLink?",
                "Why not use anchor tags in React Router?",
                "How do you highlight active routes?",
                "What is a layout component?",
              ],
            },

            tasks: [
              "Create navbar using Link",
              "Highlight active route with NavLink",
              "Build layout component with header",
            ],

            examples: [
              "<NavLink to='/blog' className={({isActive}) => isActive ? 'active' : ''}>Blog</NavLink>",
            ],

            useCases: [
              "Website navigation",
              "Dashboards",
              "Multi-page layouts",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Build SPA-friendly navigation",
              "Understand active route styling",
              "Create reusable layout components",
              "Prepare for nested & protected routes",
            ],
          },
          {
            day: 3,
            topic: "Dynamic Routes & URL Parameters",

            subTopics: [
              "Route parameters",
              "useParams hook",
              "Dynamic route patterns",
              "Query strings basics",
              "URL-driven UI",
            ],

            explanation:
              "Dynamic routes allow rendering pages based on URL parameters, enabling content-driven pages like blogs and product details.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Static vs Dynamic Route Refactor",
                description: "Understand why hardcoded routes don’t scale.",
                tasks: [
                  "Create a static route like /post/1",
                  "Duplicate it for /post/2",
                  "Refactor into a single dynamic route using :id",
                ],
                validationChecklist: [
                  "Single route handles multiple pages",
                  "Component logic depends on URL param",
                ],
                outcome:
                  "You’ll understand why dynamic routing is mandatory in real apps.",
              },
              {
                id: "pt-2",
                title: "URL → Data Fetching Drill",
                description: "Make the URL the source of truth for data.",
                tasks: [
                  "Read id using useParams",
                  "Fetch data based on id",
                  "Refetch when id changes",
                ],
                qualityRule: "URL change must always update UI data",
                outcome: "You’ll confidently build API-driven pages.",
              },
              {
                id: "pt-3",
                title: "Type Safety Awareness Task",
                description:
                  "Avoid silent bugs caused by wrong param assumptions.",
                tasks: [
                  "Log param type from useParams",
                  "Convert id to number",
                  "Handle invalid or missing params",
                ],
                antiPattern: "Assuming route params are numbers by default",
                outcome: "You’ll avoid common production bugs.",
              },
              {
                id: "pt-4",
                title: "Query String UX Exercise",
                description: "Use URL to control optional UI state.",
                tasks: [
                  "Add query string for filters",
                  "Read values from URL",
                  "Update UI based on query params",
                ],
                mindset: "If state should survive refresh, put it in the URL.",
                outcome: "You’ll think in URL-driven UI patterns.",
              },
            ],

            testingInsight: {
              coreReality:
                "Dynamic route bugs usually come from stale params or wrong dependencies.",
              checklist: [
                "Is useParams used correctly?",
                "Is param included in useEffect dependency?",
                "Is UI updating on URL change?",
              ],
              goldenRule:
                "If URL changes but UI doesn’t, your routing logic is wrong.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are dynamic routes?",
                "How does useParams work?",
                "Difference between params and query strings?",
                "How do you fetch data using route params?",
              ],
              strongAnswerPattern:
                "Explain URL as data → dynamic route → useParams → effect dependency",
              redFlag: "Hardcoding IDs or ignoring param changes.",
            },

            editorGuidance: {
              careerMindset: [
                "URL is a first-class data source",
                "Dynamic routes are core to scalable apps",
                "Good routing enables deep linking and SEO",
              ],
              seniorAdvice:
                "If your UI doesn’t reflect the URL, your app is architecturally weak.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Dynamic routing is what transforms a static React app into a real product. Pages like `/product/12` or `/user/gaurav` are built using URL parameters. Almost every production app relies on dynamic routes.",

              routeParameters: {
                title: "Route Parameters (:id)",
                explanation:
                  "Route parameters allow parts of the URL to be dynamic. These parameters act as variables that React Router passes to your component.",
                conceptsUsed: [
                  "Dynamic path segments",
                  "URL as data source",
                  "Single component for multiple pages",
                ],
                exampleLogic: `
<Route path="/post/:id" element={<Post />} />
      `,
                realWorldNote:
                  "Used heavily in product pages, blog posts, user profiles",
              },

              useParamsHook: {
                title: "useParams Hook",
                explanation:
                  "useParams reads dynamic values from the URL so components can fetch or display relevant data.",
                conceptsUsed: [
                  "Hook-based routing data",
                  "Destructuring params",
                  "URL-driven rendering",
                ],
                exampleLogic: `
import { useParams } from "react-router-dom";

const { id } = useParams();
      `,
                interviewInsight:
                  "URL params should be treated as strings unless converted",
              },

              dynamicPatterns: {
                title: "Dynamic Route Patterns",
                explanation:
                  "One component can serve thousands of pages using different route parameters.",
                conceptsUsed: [
                  "Single-responsibility components",
                  "Reusable page logic",
                  "Scalable routing",
                ],
                exampleLogic: `
/product/:productId
/user/:username
/category/:slug
      `,
                bestPractice: "Keep dynamic routes readable and semantic",
              },

              urlDrivenUI: {
                title: "URL-Driven UI",
                explanation:
                  "The URL itself becomes the source of truth for what content is shown.",
                conceptsUsed: [
                  "Stateless navigation",
                  "Deep linking",
                  "Browser history support",
                ],
                realWorldExample:
                  "Refreshing a product page should show the same product",
              },

              queryStrings: {
                title: "Query Strings Basics",
                explanation:
                  "Query strings are used for optional data like filters, sorting, and pagination.",
                conceptsUsed: [
                  "Search params",
                  "Optional routing data",
                  "UI state from URL",
                ],
                exampleLogic: `
/products?category=shoes&page=2
      `,
                note: "React Router provides useSearchParams for advanced handling",
              },

              commonMistakes: [
                {
                  mistake: "Hardcoding IDs in components",
                  issue: "Non-scalable pages",
                  fix: "Use dynamic route params",
                },
                {
                  mistake: "Assuming param is a number",
                  issue: "Type bugs",
                  fix: "Convert using Number(id)",
                },
                {
                  mistake: "Fetching without param dependency",
                  issue: "Stale data",
                  fix: "Use param in useEffect dependency",
                },
              ],

              realWorldFeatures: [
                "SEO-friendly URLs",
                "Deep linking support",
                "Shareable pages",
                "Dynamic data fetching",
              ],

              interviewQuestions: [
                "What are dynamic routes?",
                "How does useParams work?",
                "Difference between params and query strings?",
                "How would you fetch data using route params?",
              ],
            },

            tasks: [
              "Create dynamic blog route",
              "Read params using useParams",
              "Display content based on URL",
            ],

            examples: [
              "<Route path='/post/:id' element={<Post />} />",
              "const { id } = useParams();",
            ],

            useCases: [
              "Blog post pages",
              "Product detail pages",
              "User profile pages",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand dynamic routing deeply",
              "Build URL-driven pages",
              "Prepare for API-based pages",
              "Enable scalable navigation patterns",
            ],
          },
          {
            day: 4,
            topic: "Programmatic Navigation & Query Params",

            subTopics: [
              "useNavigate hook",
              "Redirect after actions",
              "Passing state via navigation",
              "Query strings with URLSearchParams",
              "Search and filter routing",
            ],

            explanation:
              "Programmatic navigation allows redirecting users after login, form submission, or other actions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Imperative Navigation Drill",
                description:
                  "Learn when and why navigation should be controlled via logic.",
                tasks: [
                  "Create a button-based navigation using useNavigate",
                  "Navigate after a successful action",
                  "Block navigation based on a condition",
                ],
                validationChecklist: [
                  "navigate is NOT called during render",
                  "Navigation happens only after user action",
                ],
                outcome: "You’ll know exactly when to use Link vs useNavigate.",
              },
              {
                id: "pt-2",
                title: "Post-Action Redirect Flow",
                description:
                  "Simulate real-world flows like login, signup, or checkout.",
                tasks: [
                  "Submit a form",
                  "Validate success state",
                  "Redirect user programmatically",
                ],
                qualityRule: "User flow must feel automatic and intentional",
                outcome: "You’ll design real product-like navigation flows.",
              },
              {
                id: "pt-3",
                title: "Navigation State Awareness Task",
                description:
                  "Understand when passing state via navigation is safe.",
                tasks: [
                  "Pass temporary data using navigate state",
                  "Read it on the destination page",
                  "Test refresh behavior",
                ],
                antiPattern:
                  "Using navigation state for critical or persistent data",
                outcome: "You’ll avoid hidden bugs caused by refresh loss.",
              },
              {
                id: "pt-4",
                title: "Search & Filter Routing Exercise",
                description:
                  "Make search and filters URL-driven and shareable.",
                tasks: [
                  "Store search keyword in query params",
                  "Read query params on page load",
                  "Update UI when query changes",
                ],
                mindset: "If users should share it, it belongs in the URL.",
                outcome: "You’ll build reload-safe, shareable search pages.",
              },
            ],

            testingInsight: {
              coreReality:
                "Routing bugs often come from navigation happening at the wrong time.",
              checklist: [
                "Is navigate called inside event or effect?",
                "Are query params reflected in UI?",
                "Does refresh preserve expected state?",
              ],
              goldenRule: "Navigation must be intentional, not accidental.",
            },

            interviewInsights: {
              commonQuestions: [
                "When do you use useNavigate?",
                "Difference between Link and navigate?",
                "How do query params improve UX?",
                "How do you redirect after login?",
              ],
              strongAnswerPattern:
                "Explain user action → logic → navigate → URL-driven UI",
              redFlag:
                "Calling navigate during render or ignoring query params.",
            },

            editorGuidance: {
              careerMindset: [
                "Navigation defines user experience",
                "URLs are part of application state",
                "Good routing enables predictable user flows",
              ],
              seniorAdvice:
                "If navigation feels hacky, your flow design is wrong.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world applications, navigation is rarely only via links. Users are redirected after login, checkout, form submission, or based on conditions. Programmatic navigation gives React full control over routing logic.",

              useNavigateHook: {
                title: "useNavigate Hook",
                explanation:
                  "useNavigate allows navigation using JavaScript logic instead of clicking links.",
                conceptsUsed: [
                  "Imperative navigation",
                  "Navigation inside event handlers",
                  "Conditional routing",
                ],
                exampleLogic: `
import { useNavigate } from "react-router-dom";

const navigate = useNavigate();
navigate('/dashboard');
      `,
                realWorldNote:
                  "Commonly used after login, logout, or form submission",
              },

              redirectAfterActions: {
                title: "Redirect After Actions",
                explanation:
                  "After completing an action like login or signup, users are redirected automatically.",
                conceptsUsed: [
                  "Auth-based routing",
                  "Success-based redirects",
                  "User flow control",
                ],
                exampleLogic: `
const handleLogin = () => {
  loginUser();
  navigate('/dashboard');
};
      `,
                interviewInsight:
                  "Often combined with auth guards and protected routes",
              },

              passingState: {
                title: "Passing State via Navigation",
                explanation:
                  "Temporary data can be passed while navigating without storing it globally.",
                conceptsUsed: [
                  "Navigation state",
                  "Temporary UI data",
                  "No URL pollution",
                ],
                exampleLogic: `
navigate('/checkout', { state: { fromCart: true } });
      `,
                bestPractice: "Use navigation state for temporary data only",
              },

              queryParams: {
                title: "Query Params with URLSearchParams",
                explanation:
                  "Query parameters store optional data like search terms, filters, and pagination.",
                conceptsUsed: [
                  "URL-driven filters",
                  "Search-based routing",
                  "State synced with URL",
                ],
                exampleLogic: `
const params = new URLSearchParams(location.search);
const query = params.get('q');
      `,
                realWorldExample: "Search pages, filter panels, pagination",
              },

              searchRouting: {
                title: "Search & Filter Routing",
                explanation:
                  "Search results should be shareable and reload-safe using query parameters.",
                conceptsUsed: [
                  "Debounced search",
                  "Query-based filtering",
                  "Re-fetch on query change",
                ],
                exampleLogic: `
/products?category=shoes&sort=price
      `,
                bestPractice: "Always sync search state with URL",
              },

              commonMistakes: [
                {
                  mistake: "Using navigate inside render",
                  issue: "Infinite re-renders",
                  fix: "Call navigate inside events or effects",
                },
                {
                  mistake: "Storing filters only in state",
                  issue: "URL not shareable",
                  fix: "Use query params",
                },
                {
                  mistake: "Overusing navigation state",
                  issue: "Data loss on refresh",
                  fix: "Persist important data",
                },
              ],

              realWorldFeatures: [
                "Login redirects",
                "Protected page access",
                "Search result sharing",
                "Checkout navigation flows",
              ],

              interviewQuestions: [
                "What is useNavigate used for?",
                "Difference between Link and navigate?",
                "How do query params improve UX?",
                "How do you pass data during navigation?",
              ],
            },

            tasks: [
              "Redirect after form submit",
              "Navigate on button click",
              "Implement search using query params",
            ],

            examples: [
              "const navigate = useNavigate();",
              "navigate('/dashboard');",
            ],

            useCases: [
              "Login redirects",
              "Checkout flows",
              "Search results pages",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Control navigation using logic",
              "Build URL-driven search pages",
              "Implement real user flows",
              "Prepare for protected routes",
            ],
          },
          {
            day: 5,
            topic: "Nested Routes & Layouts",

            subTopics: [
              "Nested Route configuration",
              "Outlet component",
              "Shared layouts",
              "Sidebar navigation",
              "Route hierarchy",
            ],

            explanation:
              "Nested routes help organize complex applications with shared layouts and consistent UI structure.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Layout vs Page Separation Drill",
                description:
                  "Understand the architectural difference between layouts and pages.",
                tasks: [
                  "Create a DashboardLayout component",
                  "Move sidebar and header into layout",
                  "Render pages only via <Outlet />",
                ],
                validationChecklist: [
                  "Layout contains shared UI only",
                  "Pages contain no layout logic",
                ],
                outcome: "You’ll stop mixing layout and page responsibilities.",
              },
              {
                id: "pt-2",
                title: "Nested Routing Refactor Challenge",
                description:
                  "Refactor flat routes into a clean nested hierarchy.",
                tasks: [
                  "Convert /dashboard-home to /dashboard/home",
                  "Group related routes under parent",
                  "Use relative paths for child routes",
                ],
                qualityRule: "URL structure must reflect UI hierarchy",
                outcome:
                  "You’ll design scalable routing structures like real apps.",
              },
              {
                id: "pt-3",
                title: "Sidebar Navigation Awareness Task",
                description: "Build navigation that understands nesting.",
                tasks: [
                  "Add NavLinks inside layout sidebar",
                  "Use relative paths for links",
                  "Verify active states for nested routes",
                ],
                antiPattern: "Using absolute paths inside nested layouts",
                outcome: "You’ll avoid broken navigation bugs.",
              },
              {
                id: "pt-4",
                title: "Outlet Debugging Exercise",
                description:
                  "Learn to debug the most common nested routing issue.",
                tasks: [
                  "Remove Outlet intentionally",
                  "Observe child routes not rendering",
                  "Fix by restoring Outlet",
                ],
                mindset: "If children don’t render, check Outlet first.",
                outcome: "You’ll instantly debug nested routing issues.",
              },
            ],

            testingInsight: {
              coreReality:
                "Most nested routing bugs happen due to missing Outlet or wrong paths.",
              checklist: [
                "Is <Outlet /> present in layout?",
                "Are child paths relative?",
                "Does URL match route hierarchy?",
              ],
              goldenRule: "Layouts render structure, Outlet renders content.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is Outlet and why is it needed?",
                "Difference between layout and page component?",
                "How do nested routes improve scalability?",
                "When should you avoid deep nesting?",
              ],
              strongAnswerPattern:
                "Explain layout reuse → nested routes → Outlet → scalable UI",
              redFlag: "Mixing layout UI inside page components.",
            },

            editorGuidance: {
              careerMindset: [
                "Routing structure reflects application architecture",
                "Layouts are not pages",
                "Good nesting improves long-term maintainability",
              ],
              seniorAdvice:
                "If your routing feels messy, your UI architecture already is.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications grow, multiple pages start sharing common UI like headers, sidebars, or footers. Nested routes allow React Router to reuse layouts efficiently while rendering different child pages inside them.",

              nestedRouting: {
                title: "Nested Route Configuration",
                explanation:
                  "Nested routes define parent-child relationships between routes, allowing child pages to render inside parent layouts.",
                conceptsUsed: [
                  "Parent route definition",
                  "Child route nesting",
                  "Relative paths",
                ],
                exampleLogic: `
<Route path="/dashboard" element={<DashboardLayout />}>
  <Route path="home" element={<DashboardHome />} />
  <Route path="settings" element={<Settings />} />
</Route>
      `,
                realWorldNote:
                  "Most dashboards, admin panels, and SaaS apps use nested routes",
              },

              outletComponent: {
                title: "Outlet Component",
                explanation:
                  "Outlet acts as a placeholder where child routes will be rendered.",
                conceptsUsed: [
                  "Layout-based routing",
                  "Dynamic content rendering",
                  "Route composition",
                ],
                exampleLogic: `
import { Outlet } from "react-router-dom";

const DashboardLayout = () => (
  <>
    <Sidebar />
    <Outlet />
  </>
);
      `,
                bestPractice: "Only layouts should contain <Outlet />",
              },

              sharedLayouts: {
                title: "Shared Layouts",
                explanation:
                  "Layouts keep common UI consistent across multiple routes.",
                conceptsUsed: [
                  "Layout components",
                  "Code reuse",
                  "Consistent UI",
                ],
                exampleLogic: `
const Layout = () => (
  <>
    <Header />
    <Outlet />
  </>
);
      `,
                realWorldExample:
                  "Header + Sidebar + Footer shared across pages",
              },

              sidebarNavigation: {
                title: "Sidebar Navigation",
                explanation:
                  "Sidebars usually control navigation between nested routes.",
                conceptsUsed: [
                  "NavLink with nested routes",
                  "Active state styling",
                  "Relative navigation",
                ],
                exampleLogic: `
<NavLink to="settings">Settings</NavLink>
<NavLink to="profile">Profile</NavLink>
      `,
                bestPractice: "Use relative paths inside nested layouts",
              },

              routeHierarchy: {
                title: "Route Hierarchy",
                explanation:
                  "A clear route hierarchy mirrors the UI structure and improves maintainability.",
                conceptsUsed: [
                  "Logical grouping",
                  "URL consistency",
                  "Scalable routing",
                ],
                exampleLogic: `
/dashboard
  ├── /dashboard/home
  ├── /dashboard/settings
  └── /dashboard/profile
      `,
                interviewInsight:
                  "Clean routing structure is a sign of senior-level React code",
              },

              commonMistakes: [
                {
                  mistake: "Forgetting to add Outlet",
                  issue: "Child routes not rendering",
                  fix: "Always add <Outlet /> in layout",
                },
                {
                  mistake: "Using absolute paths inside nested routes",
                  issue: "Broken navigation",
                  fix: "Use relative paths",
                },
                {
                  mistake: "Over-nesting routes",
                  issue: "Complex routing logic",
                  fix: "Keep hierarchy shallow",
                },
              ],

              realWorldFeatures: [
                "Admin dashboards",
                "Authenticated user areas",
                "Multi-level navigation",
                "Shared UI layouts",
              ],

              interviewQuestions: [
                "What is the purpose of Outlet?",
                "How do nested routes work?",
                "Difference between layout and page component?",
                "How do you design scalable route structure?",
              ],
            },

            tasks: [
              "Create dashboard layout",
              "Add nested routes",
              "Use Outlet for child pages",
            ],

            examples: [
              "<Route path='/dashboard' element={<Layout />}><Route path='settings' element={<Settings />} /></Route>",
            ],

            useCases: [
              "Admin dashboards",
              "User panels",
              "Complex applications",
            ],

            editorType: "react",

            estimatedTime: "2 hours",

            objectives: [
              "Design scalable routing architecture",
              "Reuse UI using layouts",
              "Implement real dashboard navigation",
              "Prepare for protected routes",
            ],
          },
          {
            day: 6,
            topic: "Protected Routes & 404 Handling",

            subTopics: [
              "Authentication-based routing",
              "Protected route pattern",
              "Redirect unauthenticated users",
              "404 / Not Found pages",
              "Role-based access (basic)",
            ],

            explanation:
              "Protected routes restrict access to certain pages based on authentication or user roles.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Protected Route Implementation Drill",
                description:
                  "Build a reusable route guard that works across the app.",
                tasks: [
                  "Create ProtectedRoute component using Outlet",
                  "Block access when user is not authenticated",
                  "Redirect unauthenticated users to /login",
                ],
                validationChecklist: [
                  "No private page renders without auth",
                  "Redirection happens at router level",
                ],
                outcome: "You’ll implement secure routing without UI flicker.",
              },
              {
                id: "pt-2",
                title: "Login Redirect Memory Exercise",
                description: "Improve UX by remembering intended routes.",
                tasks: [
                  "Store attempted route before redirect",
                  "Redirect user back after successful login",
                  "Clear stored route after navigation",
                ],
                qualityRule: "Auth flow must feel seamless to the user",
                outcome: "You’ll build professional login redirect flows.",
              },
              {
                id: "pt-3",
                title: "Role-based Route Guard",
                description: "Restrict routes based on user roles.",
                tasks: [
                  "Create AdminRoute wrapper",
                  "Allow access only for admin role",
                  "Redirect others to /unauthorized",
                ],
                antiPattern: "Checking role inside page component",
                outcome:
                  "You’ll clearly separate authentication and authorization.",
              },
              {
                id: "pt-4",
                title: "404 & Fallback Routing Test",
                description: "Handle invalid URLs gracefully.",
                tasks: [
                  "Create NotFound page",
                  "Add wildcard (*) route at bottom",
                  "Test random URLs manually",
                ],
                mindset: "Every route must end in a fallback.",
                outcome: "You’ll avoid blank screens in production.",
              },
            ],

            testingInsight: {
              coreReality:
                "Route-level protection prevents unauthorized UI from ever rendering.",
              checklist: [
                "Is route guarded before page renders?",
                "Is auth logic centralized?",
                "Does 404 handle all unknown paths?",
              ],
              goldenRule: "Security belongs in routing, not inside pages.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you implement protected routes?",
                "Difference between authentication and authorization?",
                "Why use Outlet in protected routes?",
                "How do you handle unknown routes?",
              ],
              strongAnswerPattern:
                "Explain auth state → route guard → redirect → UX flow",
              redFlag: "Protecting pages inside components instead of router.",
            },

            editorGuidance: {
              careerMindset: [
                "Routing is the first security layer of frontend",
                "Unauthorized UI should never render",
                "Good auth UX is invisible when done right",
              ],
              seniorAdvice:
                "If auth checks live inside pages, your routing architecture is weak.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Most real-world applications have private pages like dashboards, profiles, or admin panels that should not be accessible without authentication. Protected routes enforce access control at the routing level.",

              authBasedRouting: {
                title: "Authentication-based Routing",
                explanation:
                  "Authentication-based routing decides whether a user can access a route based on login status.",
                conceptsUsed: [
                  "Auth state (isAuthenticated)",
                  "Conditional rendering",
                  "Route guarding",
                ],
                exampleLogic: `
const isAuth = Boolean(user);
      `,
                realWorldNote: "Almost every SaaS app uses auth-based routing",
              },

              protectedRoutePattern: {
                title: "Protected Route Pattern",
                explanation:
                  "A ProtectedRoute component wraps private routes and controls access.",
                conceptsUsed: [
                  "Outlet component",
                  "Conditional navigation",
                  "Reusable route guards",
                ],
                exampleLogic: `
const ProtectedRoute = ({ isAuth }) => {
  return isAuth ? <Outlet /> : <Navigate to="/login" />;
};
      `,
                bestPractice:
                  "Keep route protection logic outside page components",
              },

              redirectUnauthenticated: {
                title: "Redirecting Unauthenticated Users",
                explanation:
                  "Unauthenticated users should be redirected to login or access-denied pages.",
                conceptsUsed: [
                  "Navigate component",
                  "Redirect patterns",
                  "Preserving intended route",
                ],
                exampleLogic: `
<Navigate to="/login" replace />
      `,
                interviewInsight:
                  "Good UX remembers where the user wanted to go",
              },

              notFoundPage: {
                title: "404 / Not Found Pages",
                explanation:
                  "A 404 page is shown when no route matches the URL.",
                conceptsUsed: [
                  "Wildcard routes (*)",
                  "Fallback UI",
                  "User-friendly error handling",
                ],
                exampleLogic: `
<Route path="*" element={<NotFound />} />
      `,
                bestPractice: "Always keep 404 route at the bottom",
              },

              roleBasedAccess: {
                title: "Role-based Access (Basic)",
                explanation:
                  "Some routes should only be accessible to users with specific roles (admin, editor, etc.).",
                conceptsUsed: [
                  "Role flags",
                  "Conditional route access",
                  "Authorization vs authentication",
                ],
                exampleLogic: `
return isAuth && role === 'admin'
  ? <Outlet />
  : <Navigate to="/unauthorized" />;
      `,
                realWorldExample: "Admin dashboards, moderation panels",
              },

              commonMistakes: [
                {
                  mistake: "Protecting routes inside components",
                  issue: "Unauthorized UI flashes",
                  fix: "Protect routes at router level",
                },
                {
                  mistake: "Missing wildcard 404 route",
                  issue: "Blank screen on wrong URL",
                  fix: "Always add '*' route",
                },
                {
                  mistake: "Mixing auth and role logic",
                  issue: "Hard to maintain",
                  fix: "Separate authentication & authorization",
                },
              ],

              realWorldFeatures: [
                "Private dashboards",
                "Admin-only routes",
                "Login redirects",
                "Unauthorized access handling",
              ],

              interviewQuestions: [
                "How do you implement protected routes in React?",
                "Difference between authentication and authorization?",
                "How do you handle 404 pages?",
                "How would you scale this for roles?",
              ],
            },

            tasks: [
              "Create ProtectedRoute component",
              "Block unauthenticated access",
              "Add 404 page",
            ],

            examples: [
              "return isAuth ? <Outlet /> : <Navigate to='/login' />;",
            ],

            useCases: [
              "User dashboards",
              "Admin-only pages",
              "Secure applications",
            ],

            editorType: "react",

            estimatedTime: "2 hours",

            objectives: [
              "Secure routes in React apps",
              "Implement real authentication flow",
              "Handle invalid URLs gracefully",
              "Prepare for enterprise-level routing",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – Multi-Page Application",

            subTopics: [
              "Full routing structure",
              "Nested layouts",
              "Protected routes",
              "Dynamic routes",
              "Navigation UX",
            ],

            explanation:
              "This project combines all routing concepts to build a real-world multi-page React application.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Complete App Routing Architecture",
                description:
                  "Design a scalable routing structure like real production apps.",
                tasks: [
                  "Separate public and protected routes",
                  "Create layout-based routing structure",
                  "Centralize all routes in one file",
                ],
                validationChecklist: [
                  "Public routes accessible without auth",
                  "Protected routes wrapped correctly",
                  "Routes easy to read and extend",
                ],
                outcome:
                  "You’ll think in terms of app architecture, not just pages.",
              },
              {
                id: "pt-2",
                title: "Nested Dashboard Layout Build",
                description: "Implement dashboard-style nested routing.",
                tasks: [
                  "Create DashboardLayout with sidebar/header",
                  "Use Outlet for nested pages",
                  "Add Profile and Settings as child routes",
                ],
                qualityRule: "Layouts should not contain business logic",
                outcome: "You’ll build dashboard routing like SaaS products.",
              },
              {
                id: "pt-3",
                title: "Protected + Dynamic Routes Integration",
                description: "Combine auth protection with dynamic routing.",
                tasks: [
                  "Protect dashboard routes using ProtectedRoute",
                  "Add dynamic blog route using :id",
                  "Fetch/render data based on URL param",
                ],
                antiPattern: "Checking auth inside page components",
                outcome:
                  "You’ll confidently mix dynamic and protected routing.",
              },
              {
                id: "pt-4",
                title: "Navigation UX & 404 Handling",
                description: "Polish user navigation and edge cases.",
                tasks: [
                  "Add NavLink active states",
                  "Ensure navigation reflects current route",
                  "Add wildcard route for 404 handling",
                ],
                mindset: "Every possible URL must land somewhere.",
                outcome:
                  "You’ll ship routing that feels complete and professional.",
              },
            ],

            testingInsight: {
              coreReality: "Routing bugs are architectural, not cosmetic.",
              checklist: [
                "Can users access private routes directly via URL?",
                "Do nested routes render inside layouts correctly?",
                "Does 404 catch all unknown paths?",
              ],
              goldenRule: "If routing is clear, the app scales naturally.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you structure routing in large apps?",
                "How do nested routes work with layouts?",
                "How do you protect routes?",
                "How do dynamic routes scale?",
                "How do you handle 404 pages?",
              ],
              strongAnswerPattern:
                "Explain route hierarchy → layouts → protection → UX",
              redFlag: "All routes defined flat with no layouts or guards.",
            },

            editorGuidance: {
              careerMindset: [
                "Routing is the backbone of frontend architecture",
                "Good routing decisions reduce future refactors",
                "Navigation UX reflects engineering maturity",
              ],
              seniorAdvice:
                "If your routing file looks messy, the app will age badly.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Real-world React applications are never single-page components. They consist of multiple pages, layouts, protected areas, and dynamic routes. This mini project mirrors how production apps like blogs, dashboards, and SaaS platforms are structured.",

              routingStructure: {
                title: "Full Routing Structure",
                explanation:
                  "A clean routing structure separates public pages, private pages, and fallback routes.",
                conceptsUsed: [
                  "BrowserRouter",
                  "Routes and Route",
                  "Route hierarchy",
                ],
                exampleLogic: `
<Routes>
  <Route path="/" element={<Home />} />
  <Route path="/blog" element={<Blog />} />
  <Route path="/blog/:id" element={<BlogDetail />} />
</Routes>
      `,
                bestPractice: "Keep route definitions centralized for clarity",
              },

              nestedLayouts: {
                title: "Nested Layouts",
                explanation:
                  "Nested layouts allow shared UI like headers, sidebars, and footers across related pages.",
                conceptsUsed: ["Layout components", "Outlet", "Nested routing"],
                exampleLogic: `
<Route path="/dashboard" element={<DashboardLayout />}>
  <Route path="profile" element={<Profile />} />
</Route>
      `,
                realWorldNote: "Dashboards almost always use nested layouts",
              },

              protectedRoutes: {
                title: "Protected Routes",
                explanation:
                  "Some routes should only be accessible to authenticated users.",
                conceptsUsed: [
                  "ProtectedRoute pattern",
                  "Conditional rendering",
                  "Navigate component",
                ],
                exampleLogic: `
<Route element={<ProtectedRoute isAuth={isAuth} />}>
  <Route path="/dashboard/*" element={<Dashboard />} />
</Route>
      `,
                interviewInsight: "Route-level protection avoids UI flicker",
              },

              dynamicRoutes: {
                title: "Dynamic Routes",
                explanation:
                  "Dynamic routes render content based on URL parameters like blog IDs.",
                conceptsUsed: [
                  "Route params",
                  "useParams hook",
                  "URL-driven UI",
                ],
                exampleLogic: `
const { id } = useParams();
      `,
                realWorldExample: "Blog posts, product pages, user profiles",
              },

              navigationUX: {
                title: "Navigation & UX",
                explanation:
                  "Good navigation improves usability and user trust.",
                conceptsUsed: [
                  "NavLink active states",
                  "Consistent navigation",
                  "User feedback on routes",
                ],
                exampleLogic: `
<NavLink to="/dashboard" className={({isActive}) => isActive ? 'active' : ''}>
  Dashboard
</NavLink>
      `,
                bestPractice: "Always show users where they are",
              },

              suggestedPages: {
                title: "Suggested Pages",
                pages: [
                  "Home (public)",
                  "Blog List (public)",
                  "Blog Detail (dynamic)",
                  "Login (public)",
                  "Dashboard (protected)",
                  "Profile (nested protected)",
                  "404 Not Found",
                ],
              },

              commonMistakes: [
                {
                  mistake: "All routes in one component",
                  issue: "Hard to scale and maintain",
                  fix: "Use layouts and nesting",
                },
                {
                  mistake: "No protected routing",
                  issue: "Unauthorized access",
                  fix: "Use ProtectedRoute wrapper",
                },
                {
                  mistake: "Ignoring 404 handling",
                  issue: "Poor UX on wrong URLs",
                  fix: "Add wildcard route",
                },
              ],

              realWorldFeatures: [
                "Login-based dashboard access",
                "SEO-friendly URLs",
                "Clean navigation UX",
                "Scalable routing architecture",
              ],

              interviewQuestions: [
                "How do you structure routes in a large React app?",
                "What are nested routes?",
                "How do protected routes work?",
                "How do you handle dynamic routing?",
                "How do you handle 404 pages?",
              ],
            },

            tasks: [
              "Build home, blog, and profile pages",
              "Add dynamic blog routes",
              "Implement login-protected dashboard",
              "Add 404 page",
            ],

            examples: ["ProtectedRoute", "Blog/:id", "Dashboard/*"],

            useCases: [
              "Content platforms",
              "SaaS dashboards",
              "Production-ready React apps",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Master React Router end-to-end",
              "Build scalable multi-page apps",
              "Apply protected and dynamic routing",
              "Prepare for real production projects",
            ],
          },
        ],
      },
      {
        week: 24,
        weekGoal:
          "Build scalable frontend applications with global state management and robust API integration",
        weekOutcome:
          "Ability to manage global state, consume REST APIs, handle loading/errors, and build production-grade CRUD apps",
        miniProject: "Full-Stack Frontend (API-driven CRUD App)",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Global State Management Patterns",

            subTopics: [
              "Local state vs global state",
              "When global state is needed",
              "Common state management patterns",
              "Lifting state up",
              "State colocation best practices",
            ],

            explanation:
              "As applications grow, sharing state between distant components becomes difficult. Global state patterns solve this problem cleanly.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "State Classification Audit",
                description:
                  "Audit an existing React app and classify state correctly.",
                tasks: [
                  "List all states used in the app",
                  "Mark each as local, lifted, or global",
                  "Justify why each global state must be global",
                ],
                validationChecklist: [
                  "No UI-only state marked global",
                  "Shared state has a single source of truth",
                  "State ownership is clear",
                ],
                outcome: "You’ll stop overusing global state instinctively.",
              },
              {
                id: "pt-2",
                title: "Prop Drilling vs Context Refactor",
                description:
                  "Refactor deeply drilled props into a clean global pattern.",
                tasks: [
                  "Identify prop drilling across 3+ levels",
                  "Lift state to nearest common parent",
                  "Replace with Context only if lifting becomes messy",
                ],
                qualityRule: "Context is a tool, not a default solution",
                outcome:
                  "You’ll choose the right pattern instead of defaulting to Context.",
              },
              {
                id: "pt-3",
                title: "Global State Pattern Decision Exercise",
                description:
                  "Decide the correct global state pattern for different features.",
                tasks: [
                  "Theme → Context only",
                  "Cart → Context + useReducer",
                  "Form input → local state",
                  "Auth → Context + reducer or external store",
                ],
                antiPattern: "One giant global store for everything",
                outcome: "You’ll design state like a senior engineer.",
              },
              {
                id: "pt-4",
                title: "State Colocation Refactor",
                description: "Move state closer to where it’s actually used.",
                tasks: [
                  "Remove unused global state",
                  "Relocate state to feature-level components",
                  "Measure reduced re-renders",
                ],
                mindset: "Global state is expensive — use it deliberately.",
                outcome: "Cleaner architecture and better performance.",
              },
            ],

            testingInsight: {
              coreReality: "Bad state placement causes more bugs than bad UI.",
              checklist: [
                "Is the same data stored in more than one place?",
                "Does updating global state trigger unnecessary re-renders?",
                "Can this state be derived instead of stored?",
              ],
              goldenRule:
                "If state location feels confusing, it’s probably wrong.",
            },

            interviewInsights: {
              commonQuestions: [
                "When should state be global?",
                "What is prop drilling and how do you fix it?",
                "How do you decide where state should live?",
                "Context vs Redux – when and why?",
              ],
              strongAnswerPattern: "Explain decision-making, not just tools",
              redFlag: "Making all shared state global without reasoning.",
            },

            editorGuidance: {
              careerMindset: [
                "State placement defines application architecture",
                "Global state increases coupling and complexity",
                "Good engineers minimize shared mutable state",
              ],
              seniorAdvice: "If everything feels global, nothing is designed.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Small React apps work well with local state, but as soon as multiple distant components need the same data, state management becomes complex. Global state patterns help centralize and control shared data in a predictable way.",

              localVsGlobal: {
                title: "Local State vs Global State",
                explanation:
                  "Not all state should be global. Knowing the difference is critical for clean architecture.",
                localStateExamples: [
                  "Input value in a form",
                  "Toggle inside a single component",
                  "Modal open/close state",
                ],
                globalStateExamples: [
                  "Authenticated user",
                  "Shopping cart items",
                  "Theme (dark / light)",
                ],
                bestPractice:
                  "If state is used by more than one distant component, consider global state",
              },

              whenGlobalStateNeeded: {
                title: "When Global State Is Needed",
                explanation:
                  "Global state is required when data must be accessed or modified across unrelated parts of the component tree.",
                indicators: [
                  "Props drilling through many levels",
                  "Same state duplicated in multiple places",
                  "Hard-to-track data flow",
                ],
                realWorldExamples: [
                  "User login status",
                  "Cart count in navbar",
                  "Language or theme preference",
                ],
              },

              patternsOverview: {
                title: "Common Global State Patterns",
                patterns: [
                  {
                    name: "Lifting State Up",
                    description:
                      "Move shared state to the closest common parent",
                    useCase: "Small to medium components",
                  },
                  {
                    name: "Context API",
                    description: "Share state globally without props drilling",
                    useCase: "Theme, auth, settings",
                  },
                  {
                    name: "Context + useReducer",
                    description: "Predictable state updates with actions",
                    useCase: "Complex global logic (cart, feed)",
                  },
                  {
                    name: "External State Libraries",
                    description: "Redux, Zustand, Jotai, etc.",
                    useCase: "Large-scale applications",
                  },
                ],
              },

              liftingStateUp: {
                title: "Lifting State Up",
                explanation:
                  "Before jumping to global state, always try lifting state to the nearest common parent.",
                exampleLogic: `
const [value, setValue] = useState('');

<ChildA value={value} />
<ChildB setValue={setValue} />
      `,
                warning: "Over-lifting can create bulky parent components",
              },

              stateColocation: {
                title: "State Colocation Best Practices",
                explanation:
                  "State should live as close as possible to where it is used.",
                rules: [
                  "Keep state local by default",
                  "Only globalize when necessary",
                  "Avoid premature global state",
                ],
                benefit: "Better performance, simpler code, easier debugging",
              },

              commonMistakes: [
                {
                  mistake: "Making everything global",
                  issue: "Unnecessary complexity",
                  fix: "Start with local state first",
                },
                {
                  mistake: "Deep prop drilling",
                  issue: "Unreadable and fragile code",
                  fix: "Use Context or lift state properly",
                },
                {
                  mistake: "Multiple sources of truth",
                  issue: "Inconsistent UI",
                  fix: "Centralize shared state",
                },
              ],

              realWorldMapping: [
                "AuthContext → logged-in user",
                "CartContext → cart items & total",
                "ThemeContext → dark/light mode",
              ],

              interviewQuestions: [
                "When should state be global?",
                "What problems does prop drilling cause?",
                "How do you decide state location?",
                "What is state colocation?",
              ],
            },

            tasks: [
              "Identify global vs local state in existing project",
              "Refactor lifted state into a single place",
              "Create shared state container",
            ],

            examples: ["Global user state", "Theme or auth state"],

            useCases: [
              "Authentication state",
              "Shopping cart",
              "User preferences",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand global vs local state clearly",
              "Avoid overusing global state",
              "Design scalable state architecture",
              "Prepare for Context & Reducer usage",
            ],
          },
          {
            day: 2,
            topic: "Context API + useReducer Pattern",

            subTopics: [
              "Context API recap",
              "Reducer pattern",
              "Action types and payloads",
              "Dispatching actions",
              "Scaling Context safely",
            ],

            explanation:
              "Context + Reducer is a powerful pattern for managing predictable global state without external libraries.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Design a Global Reducer Architecture",
                description:
                  "Design a clean Context + Reducer architecture for a real feature.",
                tasks: [
                  "Choose a feature (Auth / Cart / Feed)",
                  "Define initialState clearly",
                  "List all valid action types",
                  "Write reducer with strict immutability",
                ],
                validationChecklist: [
                  "Reducer has no side effects",
                  "Each action has single responsibility",
                  "State shape is future-proof",
                ],
                outcome: "You’ll think in reducers, not random state updates.",
              },
              {
                id: "pt-2",
                title: "Action & Payload Discipline Exercise",
                description:
                  "Refactor sloppy actions into clean, predictable ones.",
                tasks: [
                  "Remove logic from components",
                  "Move all update logic into reducer",
                  "Ensure payloads are minimal and explicit",
                ],
                qualityRule:
                  "Action describes WHAT happened, not HOW to update",
                outcome: "Cleaner debugging and scalable state flow.",
              },
              {
                id: "pt-3",
                title: "Context Splitting for Performance",
                description:
                  "Prevent unnecessary re-renders by splitting contexts.",
                tasks: [
                  "Identify unrelated global state",
                  "Split into multiple contexts (Auth, Cart, Theme)",
                  "Expose custom hooks for each context",
                ],
                antiPattern: "One Context to rule them all",
                outcome: "Performance-safe global state architecture.",
              },
              {
                id: "pt-4",
                title: "Reducer-First Thinking Drill",
                description: "Design reducer BEFORE writing UI components.",
                tasks: [
                  "Write reducer and actions first",
                  "Simulate state transitions manually",
                  "Then connect UI using dispatch",
                ],
                mindset: "UI is a consumer of state, not the owner",
                outcome: "You’ll build logic-first, scalable features.",
              },
            ],

            testingInsight: {
              coreReality: "Reducers are easy to test, components are not.",
              checklist: [
                "Does the reducer return new state every time?",
                "Is the reducer pure (no API, no side effects)?",
                "Do actions cover all valid state transitions?",
              ],
              goldenRule:
                "If you can’t test your reducer in isolation, it’s doing too much.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why combine Context with useReducer?",
                "How is this different from Redux?",
                "How do you prevent unnecessary re-renders?",
                "How would you scale this in a large app?",
              ],
              strongAnswerPattern:
                "Explain predictability, separation of concerns, and scalability",
              redFlag:
                "Using Context + reducer without understanding render impact",
            },

            editorGuidance: {
              careerMindset: [
                "Reducers define the rules of your application",
                "Global state without structure becomes a liability",
                "Predictable state beats clever hacks",
              ],
              seniorAdvice:
                "If state changes feel magical, your architecture is broken.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "When global state becomes complex, Context alone is not enough. Combining Context with useReducer gives structure, predictability, and scalability—similar to Redux but without extra dependencies.",

              contextRecap: {
                title: "Context API Recap",
                explanation:
                  "Context allows sharing state globally without prop drilling.",
                keyPoints: [
                  "Provides data to entire component tree",
                  "Good for global, read-heavy state",
                  "Becomes hard to manage with complex updates alone",
                ],
                limitation: "Context does not define HOW state should change",
              },

              reducerPattern: {
                title: "Reducer Pattern",
                explanation:
                  "A reducer is a pure function that defines how state changes based on actions.",
                coreRules: [
                  "Reducer must be pure",
                  "State updates must be immutable",
                  "One action = one clear intent",
                ],
                exampleLogic: `
function reducer(state, action) {
  switch(action.type) {
    case 'LOGIN':
      return { ...state, user: action.payload };
    case 'LOGOUT':
      return { ...state, user: null };
    default:
      return state;
  }
}
      `,
                interviewNote:
                  "Reducers make state transitions predictable and debuggable",
              },

              actionTypes: {
                title: "Action Types & Payloads",
                explanation:
                  "Actions describe WHAT happened. Payload carries the data.",
                structure: {
                  type: "String that describes the action",
                  payload: "Optional data needed for update",
                },
                bestPractices: [
                  "Use uppercase action types",
                  "Keep action names meaningful",
                  "Avoid putting logic inside actions",
                ],
                example: `
dispatch({
  type: 'ADD_TO_CART',
  payload: product
});
      `,
              },

              dispatchFlow: {
                title: "Dispatching Actions Flow",
                steps: [
                  "Component dispatches action",
                  "Reducer receives action",
                  "Reducer returns new state",
                  "Context provides updated state",
                  "Subscribed components re-render",
                ],
                mentalModel: "Component → Action → Reducer → New State → UI",
              },

              contextWithReducer: {
                title: "Combining Context + useReducer",
                explanation:
                  "Context shares state globally, reducer controls updates.",
                exampleLogic: `
const [state, dispatch] = useReducer(reducer, initialState);

<AuthContext.Provider value={{ state, dispatch }}>
  {children}
</AuthContext.Provider>
      `,
                benefit: "Clear separation of state logic and UI",
              },

              scalingContext: {
                title: "Scaling Context Safely",
                strategies: [
                  "Split contexts by domain (Auth, Cart, Theme)",
                  "Avoid one mega-context",
                  "Keep reducer files separate",
                  "Use custom hooks like useAuth(), useCart()",
                ],
                realWorldTip:
                  "Large apps use multiple small contexts, not one global store",
              },

              commonMistakes: [
                {
                  mistake: "Putting logic inside components",
                  issue: "Hard to debug and scale",
                  fix: "Move logic to reducer",
                },
                {
                  mistake: "One giant context",
                  issue: "Unnecessary re-renders",
                  fix: "Split contexts by feature",
                },
                {
                  mistake: "Mutating state in reducer",
                  issue: "React won't detect changes",
                  fix: "Always return new state",
                },
              ],

              realWorldMapping: [
                "AuthContext → login / logout / user",
                "CartContext → items / quantity / total",
                "FeedContext → posts / likes / comments",
              ],

              interviewQuestions: [
                "Why useReducer over useState?",
                "Why combine Context with reducer?",
                "How do you avoid unnecessary re-renders?",
                "How would you scale this pattern?",
              ],
            },

            tasks: [
              "Create context provider",
              "Setup reducer with actions",
              "Manage global state with dispatch",
            ],

            examples: [
              "AuthContext with login/logout actions",
              "CartContext with add/remove items",
            ],

            useCases: [
              "Authentication systems",
              "Cart & checkout flows",
              "Dashboard state",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Build predictable global state",
              "Understand reducer-driven architecture",
              "Prepare for Redux-like patterns",
              "Design scalable React apps",
            ],
          },
          {
            day: 3,
            topic: "Axios & API Service Layer",

            subTopics: [
              "Axios vs Fetch",
              "Creating Axios instance",
              "Base URL and headers",
              "API service abstraction",
              "Request & response interceptors",
            ],

            explanation:
              "A centralized API service layer makes applications easier to maintain, debug, and scale.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Design a Production-Ready API Layer",
                description:
                  "Design a scalable Axios-based API architecture like real companies use.",
                tasks: [
                  "Create axios instance with baseURL & timeout",
                  "Separate services by domain (auth, users, posts)",
                  "Export only clean API functions",
                ],
                validationChecklist: [
                  "No Axios calls inside components",
                  "Single axios instance used everywhere",
                  "Service files have zero UI logic",
                ],
                outcome: "You’ll stop mixing networking logic with UI code.",
              },
              {
                id: "pt-2",
                title: "Interceptor-Driven Authentication Flow",
                description:
                  "Handle auth tokens and session expiry globally using interceptors.",
                tasks: [
                  "Attach token using request interceptor",
                  "Handle 401 errors in response interceptor",
                  "Trigger logout on token expiry",
                ],
                qualityRule: "Auth logic should exist in ONE place only",
                outcome: "Enterprise-level auth handling without duplication.",
              },
              {
                id: "pt-3",
                title: "Error Normalization Strategy",
                description:
                  "Create a consistent error format for the entire app.",
                tasks: [
                  "Intercept API errors",
                  "Map backend errors to frontend-friendly messages",
                  "Ensure components never parse raw Axios errors",
                ],
                antiPattern: "Checking error.response everywhere in UI",
                outcome: "Cleaner UI code and predictable error handling.",
              },
              {
                id: "pt-4",
                title: "Service Layer + Hook Integration",
                description: "Connect API services with custom hooks cleanly.",
                tasks: [
                  "Call service functions inside hooks",
                  "Expose data, loading, error from hooks",
                  "Keep components API-agnostic",
                ],
                mindset: "Components should not know Axios exists",
                outcome: "Perfect separation of concerns.",
              },
            ],

            testingInsight: {
              coreReality: "API services are easier to test than components.",
              checklist: [
                "Can API functions be mocked easily?",
                "Is Axios instance isolated from UI?",
                "Are side effects handled via interceptors?",
              ],
              goldenRule:
                "If mocking APIs is painful, your architecture is wrong.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why use Axios instead of Fetch?",
                "What are interceptors used for?",
                "How do you handle auth tokens globally?",
                "How do you structure API services in large apps?",
              ],
              strongAnswerPattern:
                "Talk about separation of concerns, interceptors, and scalability",
              redFlag: "Calling axios directly inside components",
            },

            editorGuidance: {
              careerMindset: [
                "Networking code is infrastructure, not UI logic",
                "Duplication in API calls is a design smell",
                "Centralized API control enables scaling",
              ],
              seniorAdvice:
                "If components know about headers or tokens, your API layer is leaking.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world applications, API calls are not scattered across components. Instead, companies use a centralized API service layer. Axios is the most commonly used library for this purpose due to its cleaner syntax and powerful features.",

              axiosVsFetch: {
                title: "Axios vs Fetch",
                explanation:
                  "Both Axios and Fetch are used for making HTTP requests, but Axios provides a better developer experience.",
                comparison: [
                  "Axios automatically parses JSON, Fetch does not",
                  "Axios supports interceptors, Fetch does not",
                  "Axios handles request cancellation easily",
                  "Axios has better error handling by default",
                ],
                interviewNote:
                  "Most production React apps prefer Axios over Fetch",
              },

              axiosInstance: {
                title: "Creating Axios Instance",
                explanation:
                  "An Axios instance allows you to define base configuration once and reuse it everywhere.",
                benefits: [
                  "Single place for baseURL",
                  "Common headers",
                  "Easier maintenance",
                ],
                exampleLogic: `
import axios from 'axios';

const api = axios.create({
  baseURL: 'https://api.example.com',
  timeout: 10000,
});
      `,
              },

              baseUrlAndHeaders: {
                title: "Base URL & Default Headers",
                explanation:
                  "Base URL avoids repeating API root. Headers are commonly used for auth and content type.",
                commonHeaders: [
                  "Authorization (Bearer token)",
                  "Content-Type: application/json",
                  "Accept headers",
                ],
                exampleLogic: `
api.defaults.headers.common['Authorization'] = 'Bearer TOKEN';
      `,
                realWorldTip:
                  "Tokens are usually injected dynamically via interceptors",
              },

              apiServiceLayer: {
                title: "API Service Abstraction",
                explanation:
                  "Service layer separates API logic from UI components.",
                structureExample: [
                  "services/api.js → Axios instance",
                  "services/user.service.js → user APIs",
                  "services/post.service.js → post APIs",
                ],
                exampleLogic: `
export const getPosts = () => api.get('/posts');
export const getUser = id => api.get(\`/users/\${id}\`);
      `,
                benefit: "Components focus on UI, not networking logic",
              },

              interceptors: {
                title: "Request & Response Interceptors",
                explanation:
                  "Interceptors run before requests and after responses globally.",
                requestUseCases: [
                  "Attach auth token",
                  "Add headers",
                  "Log outgoing requests",
                ],
                responseUseCases: [
                  "Handle global errors",
                  "Logout on 401",
                  "Transform response data",
                ],
                exampleLogic: `
api.interceptors.request.use(config => {
  config.headers.Authorization = 'Bearer TOKEN';
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response.status === 401) {
      // logout user
    }
    return Promise.reject(error);
  }
);
      `,
                interviewInsight:
                  "Interceptors are heavily used in enterprise apps",
              },

              commonMistakes: [
                {
                  mistake: "Calling Axios directly inside components",
                  issue: "Hard to scale and refactor",
                  fix: "Move logic to service layer",
                },
                {
                  mistake: "Repeating baseURL everywhere",
                  issue: "Error-prone and messy",
                  fix: "Use Axios instance",
                },
                {
                  mistake: "Handling auth in every request",
                  issue: "Code duplication",
                  fix: "Use request interceptor",
                },
              ],

              realWorldArchitecture: [
                "Axios instance → config & interceptors",
                "Service files → API endpoints",
                "Hooks → data fetching logic",
                "Components → UI only",
              ],

              interviewQuestions: [
                "Why Axios over Fetch?",
                "What are interceptors?",
                "How do you handle auth tokens globally?",
                "How do you structure API services?",
              ],
            },

            tasks: [
              "Install and configure Axios",
              "Create reusable API service file",
              "Add request interceptors",
            ],

            examples: ["axios.create({ baseURL })", "api.get('/posts')"],

            useCases: [
              "REST API consumption",
              "Microservices frontend",
              "Scalable frontend architecture",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Design centralized API layer",
              "Use Axios professionally",
              "Handle auth & errors globally",
              "Write scalable React networking code",
            ],
          },
          {
            day: 4,
            topic: "Loading, Error Handling & Retry Logic",

            subTopics: [
              "Loading states",
              "Error states",
              "Try/catch in async functions",
              "Retry strategies",
              "Error boundaries basics",
            ],

            explanation:
              "Production apps must gracefully handle slow networks, server failures, and unexpected errors.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Loading UX",
                description:
                  "Design loading states that feel fast and professional.",
                tasks: [
                  "Implement skeleton loaders instead of spinners",
                  "Disable actions during loading",
                  "Handle parallel loading states cleanly",
                ],
                outcome: "Users never feel the app is frozen.",
              },
              {
                id: "pt-2",
                title: "Centralized Error Handling Strategy",
                description:
                  "Create a consistent error handling approach across the app.",
                tasks: [
                  "Normalize API error messages",
                  "Show user-friendly fallback UI",
                  "Log technical errors separately",
                ],
                mindset: "Users should never see raw errors.",
              },
              {
                id: "pt-3",
                title: "Retry Logic with Control",
                description: "Recover from flaky networks without hurting UX.",
                tasks: [
                  "Implement retry with max attempt limit",
                  "Add retry delay",
                  "Expose manual retry button",
                ],
                antiPattern: "Infinite retries without user control",
              },
            ],

            testingInsights: {
              coreReality:
                "Loading and error states are easier to break than happy paths.",
              checklist: [
                "Does UI handle slow API responses?",
                "Are errors testable without crashing UI?",
                "Is retry logic predictable?",
              ],
              goldenRule:
                "If you can’t test failure, you’re not production-ready.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you handle loading and error states?",
                "How would you retry a failed API call?",
                "What are Error Boundaries and their limitations?",
              ],
              strongAnswerPattern: "Explain UX + technical flow together",
              redFlag: "Ignoring loading or error states in UI",
            },

            editorGuidance: {
              careerMindset: [
                "Failure handling defines app quality",
                "Happy path is easy, edge cases show experience",
                "UX during errors matters more than success",
              ],
              seniorAdvice:
                "If your app breaks on slow internet, it’s not production-ready.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world applications, users face slow networks, failed API calls, and unexpected crashes. A professional frontend must handle these cases gracefully instead of breaking the UI.",

              loadingStates: {
                title: "Loading States",
                explanation:
                  "Loading states inform users that data is being fetched and prevent confusion or repeated actions.",
                conceptsUsed: [
                  "useState for loading flag",
                  "Conditional rendering",
                  "Disabling UI during fetch",
                ],
                exampleLogic: `
const [loading, setLoading] = useState(false);

setLoading(true);
fetchData().finally(() => setLoading(false));
      `,
                bestPractice:
                  "Always show feedback for network operations longer than 300ms",
              },

              errorStates: {
                title: "Error States",
                explanation:
                  "Error states display meaningful messages when something goes wrong instead of blank screens.",
                conceptsUsed: [
                  "Error state management",
                  "Conditional UI rendering",
                  "User-friendly messages",
                ],
                exampleLogic: `
const [error, setError] = useState(null);

catch(err) {
  setError('Something went wrong');
}
      `,
                realWorldNote:
                  "Never expose raw server errors directly to users",
              },

              tryCatchAsync: {
                title: "Try / Catch in Async Functions",
                explanation:
                  "try/catch is the standard way to handle errors when using async/await.",
                conceptsUsed: [
                  "async/await",
                  "Error handling flow",
                  "finally block",
                ],
                exampleLogic: `
try {
  const res = await api.get('/posts');
  setData(res.data);
} catch (err) {
  setError(err.message);
} finally {
  setLoading(false);
}
      `,
                interviewInsight:
                  "Most companies prefer async/await + try/catch over .then().catch()",
              },

              retryLogic: {
                title: "Retry Logic",
                explanation:
                  "Retry logic helps recover from temporary failures like flaky networks.",
                strategies: [
                  "Manual retry button",
                  "Automatic retry with count limit",
                  "Retry with delay (exponential backoff)",
                ],
                exampleLogic: `
const fetchWithRetry = async (retries = 3) => {
  try {
    return await api.get('/data');
  } catch (e) {
    if (retries === 0) throw e;
    return fetchWithRetry(retries - 1);
  }
};
      `,
                realWorldUsage:
                  "Used heavily in dashboards, analytics, and mobile networks",
              },

              errorBoundaries: {
                title: "Error Boundaries (Basics)",
                explanation:
                  "Error Boundaries catch rendering errors in React components and prevent the entire app from crashing.",
                conceptsUsed: [
                  "Class-based error boundaries",
                  "componentDidCatch",
                  "Fallback UI",
                ],
                limitation:
                  "Error boundaries do NOT catch async errors or event handlers",
                exampleLogic: `
class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? <Fallback /> : this.props.children;
  }
}
      `,
                interviewNote:
                  "Error boundaries are asked frequently in senior React interviews",
              },

              commonMistakes: [
                {
                  mistake: "Not handling loading state",
                  issue: "UI feels broken",
                  fix: "Always show spinner or skeleton",
                },
                {
                  mistake: "Silent API failures",
                  issue: "Users don’t know what happened",
                  fix: "Show friendly error message",
                },
                {
                  mistake: "Infinite retry loops",
                  issue: "App freezes",
                  fix: "Limit retry count",
                },
              ],

              realWorldUXPatterns: [
                "Skeleton loaders instead of spinners",
                "Retry button on error screen",
                "Graceful fallback UI",
                "Global error boundary",
              ],

              interviewQuestions: [
                "How do you handle loading and error states?",
                "Difference between try/catch and .catch?",
                "What are Error Boundaries?",
                "How would you retry failed API calls?",
              ],
            },

            tasks: [
              "Add loading indicators",
              "Handle API errors",
              "Implement retry logic",
            ],

            examples: ["Spinner while fetching data", "Error message UI"],

            useCases: [
              "Unstable network conditions",
              "User-friendly UX",
              "Enterprise applications",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Handle async failures gracefully",
              "Improve user experience",
              "Build production-ready error handling",
              "Understand React error boundaries",
            ],
          },
          {
            day: 5,
            topic: "Optimistic Updates & Caching Strategies",

            subTopics: [
              "What are optimistic updates",
              "Updating UI before API response",
              "Rollback on failure",
              "Basic caching concepts",
              "Stale vs fresh data",
            ],

            explanation:
              "Optimistic updates make apps feel instant by updating UI before the server responds.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Optimistic Like / Action Flow",
                description:
                  "Implement instant UI updates before server confirmation.",
                tasks: [
                  "Update UI immediately on user action",
                  "Send API request in background",
                  "Keep reference of previous state",
                ],
                outcome: "UI feels instant, no waiting for server response.",
              },
              {
                id: "pt-2",
                title: "Rollback Strategy on Failure",
                description:
                  "Handle API failure without breaking UI consistency.",
                tasks: [
                  "Store previous state snapshot",
                  "Rollback UI on API failure",
                  "Show non-blocking error notification",
                ],
                mindset: "Optimistic UI without rollback is a bug.",
              },
              {
                id: "pt-3",
                title: "Simple Caching Layer",
                description: "Avoid unnecessary API calls using basic caching.",
                tasks: [
                  "Cache fetched data in memory/state",
                  "Reuse cached data when available",
                  "Decide when to invalidate cache",
                ],
                qualityRule:
                  "Cache must never become a second source of truth.",
              },
            ],

            testingInsights: {
              coreReality:
                "Optimistic updates break silently if failure paths aren’t tested.",
              checklist: [
                "Does UI rollback correctly on API failure?",
                "Is cached data updated or invalidated properly?",
                "Are users notified on failure?",
              ],
              goldenRule: "Test failure paths more than success paths.",
            },

            interviewInsights: {
              commonQuestions: [
                "What are optimistic updates?",
                "How do you rollback state on API failure?",
                "Difference between optimistic and pessimistic updates?",
                "How do you handle stale cached data?",
              ],
              strongAnswerPattern:
                "Explain UX first, then consistency & rollback logic",
              redFlag: "Updating UI optimistically without rollback handling",
            },

            editorGuidance: {
              careerMindset: [
                "Instant UX is a competitive advantage",
                "Optimistic UI is about trust + recovery",
                "Consistency matters more than speed",
              ],
              seniorAdvice:
                "Optimistic updates are easy — safe rollback is what makes you senior.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern users expect instant feedback. Optimistic updates improve perceived performance by updating the UI immediately, even before the server confirms the action. This pattern is widely used in social media and real-time apps.",

              optimisticUpdates: {
                title: "What are Optimistic Updates?",
                explanation:
                  "Optimistic updates assume that the server request will succeed and update the UI immediately.",
                flow: [
                  "User performs an action (like, add, delete)",
                  "UI updates instantly",
                  "API request is sent in background",
                  "If success → keep UI",
                  "If failure → rollback changes",
                ],
                realWorldExamples: [
                  "Like button on Instagram",
                  "Tweet posting on X",
                  "Todo added instantly",
                ],
              },

              updateBeforeResponse: {
                title: "Updating UI Before API Response",
                explanation:
                  "Instead of waiting for API response, state is updated immediately for better UX.",
                conceptsUsed: [
                  "Local state manipulation",
                  "Temporary IDs",
                  "Background API calls",
                ],
                exampleLogic: `
setTodos(prev => [...prev, newTodo]); // UI updates immediately
await api.post('/todos', newTodo);
      `,
                bestPractice:
                  "Always keep a reference to previous state for rollback",
              },

              rollbackOnFailure: {
                title: "Rollback on Failure",
                explanation:
                  "If API fails, UI must revert to previous state to maintain consistency.",
                strategies: [
                  "Store previous state snapshot",
                  "Remove optimistic item",
                  "Show error notification",
                ],
                exampleLogic: `
const prevTodos = todos;
setTodos(updated);

try {
  await api.post('/todos', todo);
} catch {
  setTodos(prevTodos); // rollback
}
      `,
                interviewInsight:
                  "Rollback handling is a strong indicator of senior-level React knowledge",
              },

              cachingBasics: {
                title: "Basic Caching Concepts",
                explanation:
                  "Caching avoids unnecessary network requests by storing previously fetched data.",
                concepts: [
                  "In-memory cache (state)",
                  "LocalStorage / SessionStorage",
                  "Cache invalidation",
                ],
                exampleLogic: `
if (cache[userId]) {
  setUser(cache[userId]);
} else {
  fetchUser();
}
      `,
                realWorldUsage:
                  "Dashboards often cache data to reduce API load",
              },

              staleVsFresh: {
                title: "Stale vs Fresh Data",
                explanation:
                  "Cached data may become outdated (stale). Apps must decide when to refetch.",
                strategies: [
                  "Time-based refresh",
                  "User-triggered refresh",
                  "Background revalidation",
                ],
                realWorldNote:
                  "This problem is why libraries like React Query exist",
              },

              commonMistakes: [
                {
                  mistake: "No rollback logic",
                  issue: "UI shows incorrect data",
                  fix: "Always store previous state",
                },
                {
                  mistake: "Over-caching",
                  issue: "Users see outdated data",
                  fix: "Invalidate cache when needed",
                },
                {
                  mistake: "Waiting for API before UI update",
                  issue: "Slow, laggy UX",
                  fix: "Use optimistic updates",
                },
              ],

              realWorldPatterns: [
                "Optimistic likes & comments",
                "Instant todo creation",
                "Background sync with server",
                "Retry on failure with rollback",
              ],

              interviewQuestions: [
                "What are optimistic updates?",
                "How do you rollback state on API failure?",
                "Difference between optimistic and pessimistic updates?",
                "How do you handle stale data?",
              ],
            },

            tasks: [
              "Implement optimistic UI update",
              "Rollback state on error",
              "Cache fetched data in state",
            ],

            examples: ["Instant like button", "Optimistic todo add"],

            useCases: [
              "Social media apps",
              "Realtime dashboards",
              "High-performance UX",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Build instant-feeling UIs",
              "Handle API failures gracefully",
              "Understand caching fundamentals",
              "Prepare for React Query / TanStack Query",
            ],
          },
          {
            day: 6,
            topic: "Pagination & Infinite Scroll",

            subTopics: [
              "Pagination strategies",
              "Cursor vs offset pagination",
              "Infinite scroll logic",
              "Intersection Observer basics",
              "Performance considerations",
            ],

            explanation:
              "Pagination and infinite scroll are critical for handling large datasets efficiently.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Pagination Strategy Implementation",
                description:
                  "Implement both page-based and cursor-based pagination to understand trade-offs.",
                tasks: [
                  "Build page-based pagination with page & limit",
                  "Implement cursor-based pagination using lastId",
                  "Compare UX and performance differences",
                ],
                outcome:
                  "You’ll know which pagination strategy fits which product.",
              },
              {
                id: "pt-2",
                title: "Infinite Scroll with Safety Guards",
                description:
                  "Build infinite scroll without duplicate API calls or UI glitches.",
                tasks: [
                  "Use Intersection Observer with sentinel element",
                  "Prevent duplicate fetches using loading flag",
                  "Stop fetching when no more data exists",
                ],
                qualityRule:
                  "Infinite scroll must never trigger parallel API calls.",
              },
              {
                id: "pt-3",
                title: "Performance Hardening Exercise",
                description: "Optimize large lists for real-world performance.",
                tasks: [
                  "Ensure stable key usage",
                  "Avoid rendering unnecessary DOM nodes",
                  "Add debounce/throttle where required",
                ],
                mindset: "Performance bugs appear only at scale.",
              },
            ],

            testingInsights: {
              coreReality:
                "Pagination bugs usually appear only with real data volume.",
              checklist: [
                "Are duplicate items appearing?",
                "Does scroll trigger multiple API calls?",
                "Does fetching stop when data ends?",
              ],
              goldenRule: "Always test pagination with large datasets.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between pagination and infinite scroll?",
                "Cursor vs offset pagination?",
                "Why use Intersection Observer instead of scroll events?",
                "How do you optimize long lists?",
              ],
              strongAnswerPattern:
                "Explain UX choice → backend support → performance impact",
              redFlag: "Using scroll events without guards for infinite scroll",
            },

            editorGuidance: {
              careerMindset: [
                "Scalability issues don’t show in small demos",
                "Pagination is a backend + frontend contract",
                "Performance is a feature, not an optimization",
              ],
              seniorAdvice:
                "If your list breaks with 10k items, it’s not production-ready.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Large datasets cannot be rendered at once without hurting performance. Pagination and infinite scroll help load data in chunks, improving speed, UX, and scalability. Almost every real-world app uses one of these patterns.",

              paginationStrategies: {
                title: "Pagination Strategies",
                explanation:
                  "Pagination splits data into pages so only a subset is fetched and rendered at a time.",
                types: [
                  "Page-based (page=1, page=2)",
                  "Offset-based (offset=0, limit=10)",
                  "Cursor-based (cursor=lastId)",
                ],
                exampleLogic: `
api.get('/products?page=2&limit=10');
      `,
                realWorldUsage:
                  "Used heavily in admin panels, tables, and reports",
              },

              cursorVsOffset: {
                title: "Cursor vs Offset Pagination",
                explanation:
                  "Different pagination methods suit different data sizes and update frequencies.",
                comparison: [
                  "Offset pagination is simple but slow for large datasets",
                  "Cursor pagination is faster and more reliable",
                  "Cursor avoids duplicate or missing items on updates",
                ],
                interviewInsight:
                  "Cursor pagination is preferred in scalable systems",
              },

              infiniteScrollLogic: {
                title: "Infinite Scroll Logic",
                explanation:
                  "Infinite scroll loads more data automatically as the user scrolls.",
                flow: [
                  "Initial data load",
                  "Detect scroll end",
                  "Fetch next batch",
                  "Append data to list",
                ],
                conceptsUsed: [
                  "State for page/cursor",
                  "Appending data immutably",
                  "Preventing duplicate fetches",
                ],
                exampleLogic: `
setItems(prev => [...prev, ...newItems]);
      `,
                realWorldExamples: [
                  "Instagram feed",
                  "Twitter timeline",
                  "YouTube home page",
                ],
              },

              intersectionObserver: {
                title: "Intersection Observer Basics",
                explanation:
                  "Intersection Observer efficiently detects when an element enters the viewport.",
                benefits: [
                  "Better than scroll event listeners",
                  "Improves performance",
                  "Battery-friendly",
                ],
                exampleLogic: `
const observer = new IntersectionObserver(entries => {
  if (entries[0].isIntersecting) loadMore();
});
      `,
                bestPractice: "Use observer on a sentinel div at list bottom",
              },

              performanceConsiderations: {
                title: "Performance Considerations",
                explanation:
                  "Improper pagination or infinite scroll can cause serious performance issues.",
                tips: [
                  "Avoid rendering thousands of DOM nodes",
                  "Use key prop correctly",
                  "Debounce API calls",
                  "Stop fetching when no more data",
                ],
                realWorldNote:
                  "Virtualization libraries (react-window) are used for very large lists",
              },

              commonMistakes: [
                {
                  mistake: "Fetching on every scroll event",
                  issue: "Too many API calls",
                  fix: "Use Intersection Observer",
                },
                {
                  mistake: "Replacing list instead of appending",
                  issue: "Data flicker",
                  fix: "Append immutably",
                },
                {
                  mistake: "No loading guard",
                  issue: "Duplicate API calls",
                  fix: "Track loading state",
                },
              ],

              realWorldPatterns: [
                "Page-based pagination in tables",
                "Infinite scroll in feeds",
                "Load more button as fallback",
                "Cursor-based APIs for scalability",
              ],

              interviewQuestions: [
                "Difference between pagination and infinite scroll?",
                "Cursor vs offset pagination?",
                "Why Intersection Observer over scroll events?",
                "How do you optimize large lists?",
              ],
            },

            tasks: [
              "Implement paginated API calls",
              "Build infinite scroll list",
              "Optimize rendering performance",
            ],

            examples: ["Product listing pages", "Social media feeds"],

            useCases: [
              "E-commerce apps",
              "Content-heavy platforms",
              "Search results pages",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Handle large datasets efficiently",
              "Implement scalable pagination",
              "Build infinite scrolling UIs",
              "Optimize performance for real users",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – API Driven CRUD Application",

            subTopics: [
              "CRUD operations",
              "Global state integration",
              "Optimistic updates",
              "Loading & error UX",
              "Scalable architecture",
            ],

            explanation:
              "This project combines state management and API integration into a real-world production-style frontend.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Full CRUD Flow with API Service Layer",
                description:
                  "Implement complete CRUD using a clean service + state architecture.",
                tasks: [
                  "Create API service functions for CRUD",
                  "Connect UI actions to service layer",
                  "Ensure state updates via reducer only",
                ],
                qualityRule: "Components must never call API directly.",
                outcome: "You’ll build production-grade CRUD architecture.",
              },
              {
                id: "pt-2",
                title: "Optimistic Update + Rollback Challenge",
                description: "Make UI instant while keeping data consistent.",
                tasks: [
                  "Apply optimistic update on create/delete",
                  "Store previous state snapshot",
                  "Rollback state on API failure",
                ],
                mindset: "Fast UI without rollback is a bug.",
              },
              {
                id: "pt-3",
                title: "Global Loading & Error UX System",
                description: "Design a reusable loading and error experience.",
                tasks: [
                  "Add global loading indicator",
                  "Show inline loaders for actions",
                  "Implement retry button on failure",
                ],
                outcome: "Your app will feel reliable under bad networks.",
              },
            ],

            testingInsights: {
              coreReality:
                "CRUD bugs usually appear when multiple actions happen fast.",
              checklist: [
                "Does UI update correctly on optimistic actions?",
                "Is rollback handled on API failure?",
                "Are loading and error states consistent?",
                "Is state ever mutated directly?",
              ],
              goldenRule:
                "Test CRUD flows under slow or failing network conditions.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you structure a CRUD frontend?",
                "Where should API logic live?",
                "How do optimistic updates work?",
                "How do you handle rollback?",
                "How do you scale CRUD apps?",
              ],
              strongAnswerPattern:
                "Explain architecture → state flow → UX handling → scalability",
              redFlag: "Updating UI only after API response for every action",
            },

            editorGuidance: {
              careerMindset: [
                "CRUD apps expose real engineering discipline",
                "Architecture matters more than UI polish",
                "Fast UX + correct state = production quality",
              ],
              seniorAdvice:
                "If your CRUD breaks on API failure, it’s not production-ready.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "CRUD (Create, Read, Update, Delete) applications are the backbone of real-world frontend systems. Almost every SaaS, admin panel, or dashboard revolves around CRUD operations powered by APIs. This project simulates how production apps manage data, state, and UX together.",

              crudOverview: {
                title: "Understanding CRUD Operations",
                explanation:
                  "CRUD represents the four fundamental operations performed on data.",
                operations: [
                  "Create – Add new data",
                  "Read – Fetch and display data",
                  "Update – Modify existing data",
                  "Delete – Remove data",
                ],
                realWorldExamples: [
                  "Task creation in project tools",
                  "Editing blog posts",
                  "Deleting inventory items",
                ],
              },

              apiIntegration: {
                title: "Connecting to REST API",
                explanation:
                  "The application communicates with backend services using REST APIs.",
                conceptsUsed: [
                  "GET for fetching data",
                  "POST for creating records",
                  "PUT/PATCH for updating",
                  "DELETE for removal",
                ],
                exampleLogic: `
api.get('/tasks');
api.post('/tasks', newTask);
api.put('/tasks/1', updatedTask);
api.delete('/tasks/1');
      `,
                bestPractice: "Always abstract API calls into a service layer",
              },

              globalStateIntegration: {
                title: "Global State Integration",
                explanation:
                  "Shared data like lists, loading states, and errors should live in global state.",
                patternsUsed: [
                  "Context API",
                  "useReducer for predictable updates",
                  "Action-based state changes",
                ],
                exampleLogic: `
dispatch({ type: 'ADD_TASK', payload: task });
dispatch({ type: 'DELETE_TASK', payload: id });
      `,
                interviewInsight:
                  "This pattern scales well before moving to Redux or Zustand",
              },

              optimisticUpdates: {
                title: "Optimistic Updates",
                explanation:
                  "Optimistic UI updates make the app feel instant by updating UI before API confirmation.",
                flow: [
                  "Update UI immediately",
                  "Send API request",
                  "Confirm or rollback on failure",
                ],
                exampleLogic: `
setTasks(prev => [...prev, newTask]); // optimistic
      `,
                realWorldUsage: "Likes, comments, task creation, toggles",
                caution: "Always handle rollback if API fails",
              },

              loadingAndErrorUX: {
                title: "Loading & Error User Experience",
                explanation:
                  "Good UX communicates system state clearly to users.",
                patterns: [
                  "Global loading spinner",
                  "Inline loading indicators",
                  "Error banners or messages",
                  "Retry actions",
                ],
                exampleLogic: `
if (loading) return <Spinner />;
if (error) return <Error message={error} />;
      `,
                realWorldNote:
                  "Poor loading UX is one of the biggest reasons users abandon apps",
              },

              scalableArchitecture: {
                title: "Scalable Frontend Architecture",
                explanation:
                  "Proper structure keeps CRUD apps maintainable as features grow.",
                suggestedStructure: [
                  "api/ (service layer)",
                  "context/ or store/",
                  "components/",
                  "pages/",
                  "hooks/",
                ],
                benefit: "Easy to test, refactor, and scale",
              },

              commonMistakes: [
                {
                  mistake: "Directly mutating state",
                  issue: "UI doesn't update",
                  fix: "Always use immutable updates",
                },
                {
                  mistake: "Handling API logic in components",
                  issue: "Messy and untestable code",
                  fix: "Move logic to service layer",
                },
                {
                  mistake: "No error handling",
                  issue: "App crashes silently",
                  fix: "Always handle error states",
                },
              ],

              realWorldFeatures: [
                "Inline editing",
                "Delete confirmation modal",
                "Optimistic UI with rollback",
                "Pagination or infinite scroll",
                "Search & filter",
              ],

              interviewQuestions: [
                "How do you design a CRUD frontend?",
                "What are optimistic updates?",
                "How do you manage global state?",
                "How do you handle API failures?",
                "How would you scale this app?",
              ],
            },

            tasks: [
              "Build CRUD UI (create, read, update, delete)",
              "Connect to REST API",
              "Handle loading and errors",
              "Use global state for data",
            ],

            examples: ["Task manager", "Blog platform", "Inventory system"],

            useCases: [
              "Admin panels",
              "SaaS products",
              "Production frontend apps",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Build production-style CRUD applications",
              "Integrate APIs with global state",
              "Implement optimistic updates",
              "Handle real-world UX scenarios",
            ],
          },
        ],
      },
    ],
    questions: [
      {
        id: "r1",
        question: "What is React and what problems does it solve?",
        difficulty: "Easy",
        topics: ["React Basics"],
      },
      {
        id: "r2",
        question: "Explain the Virtual DOM and how React uses it.",
        difficulty: "Medium",
        topics: ["Virtual DOM"],
      },
      {
        id: "r3",
        question:
          "What are React components? Explain functional vs class components.",
        difficulty: "Easy",
        topics: ["Components"],
      },
      {
        id: "r4",
        question:
          "What are props in React and how do you pass data between components?",
        difficulty: "Easy",
        topics: ["Props"],
      },
      {
        id: "r5",
        question: "Explain the useState hook with examples.",
        difficulty: "Easy",
        topics: ["Hooks", "useState"],
      },
      {
        id: "r6",
        question: "What is the useEffect hook and what are its use cases?",
        difficulty: "Medium",
        topics: ["Hooks", "useEffect"],
      },
      {
        id: "r7",
        question:
          "Explain the dependency array in useEffect. What happens with an empty array vs no array?",
        difficulty: "Medium",
        topics: ["useEffect", "Dependencies"],
      },
      {
        id: "r8",
        question:
          "What is the difference between controlled and uncontrolled components?",
        difficulty: "Medium",
        topics: ["Forms", "Controlled Components"],
      },
      {
        id: "r9",
        question:
          "Explain React component lifecycle methods (or their hooks equivalents).",
        difficulty: "Medium",
        topics: ["Lifecycle", "Hooks"],
      },
      {
        id: "r10",
        question: "What is prop drilling and how can you avoid it?",
        difficulty: "Medium",
        topics: ["Props", "State Management"],
      },
      {
        id: "r11",
        question: "Explain the Context API and when to use it.",
        difficulty: "Medium",
        topics: ["Context API", "State Management"],
      },
      {
        id: "r12",
        question: "What is the useContext hook and how does it work?",
        difficulty: "Medium",
        topics: ["Hooks", "Context API"],
      },
      {
        id: "r13",
        question: "Explain React Router and how client-side routing works.",
        difficulty: "Medium",
        topics: ["React Router", "Routing"],
      },
      {
        id: "r14",
        question: "What are keys in React and why are they important?",
        difficulty: "Easy",
        topics: ["Lists", "Keys"],
      },
      {
        id: "r15",
        question:
          "Explain the useReducer hook and when you'd use it over useState.",
        difficulty: "Hard",
        topics: ["Hooks", "useReducer"],
      },
      {
        id: "r16",
        question: "What is the useMemo hook and when should you use it?",
        difficulty: "Hard",
        topics: ["Hooks", "Performance"],
      },
      {
        id: "r17",
        question: "Explain the useCallback hook and its purpose.",
        difficulty: "Hard",
        topics: ["Hooks", "Performance"],
      },
      {
        id: "r18",
        question: "What are React fragments and why do we need them?",
        difficulty: "Easy",
        topics: ["Fragments"],
      },
      {
        id: "r19",
        question: "How do you handle forms in React?",
        difficulty: "Medium",
        topics: ["Forms"],
      },
      {
        id: "r20",
        question: "What is the difference between state and props?",
        difficulty: "Easy",
        topics: ["State", "Props"],
      },
      {
        id: "r21",
        question: "Explain React.memo and when to use it.",
        difficulty: "Hard",
        topics: ["Performance", "Memoization"],
      },
      {
        id: "r22",
        question: "What are Higher Order Components (HOCs)?",
        difficulty: "Hard",
        topics: ["HOCs", "Patterns"],
      },
      {
        id: "r23",
        question: "Explain render props pattern in React.",
        difficulty: "Hard",
        topics: ["Render Props", "Patterns"],
      },
      {
        id: "r24",
        question: "What is reconciliation in React?",
        difficulty: "Medium",
        topics: ["Reconciliation", "Virtual DOM"],
      },
      {
        id: "r25",
        question: "How do you optimize re-renders in React?",
        difficulty: "Hard",
        topics: ["Performance", "Optimization"],
      },
      {
        id: "r26",
        question: "What is React.StrictMode and why should you use it?",
        difficulty: "Medium",
        topics: ["StrictMode", "Development"],
      },
      {
        id: "r27",
        question: "Explain error boundaries in React.",
        difficulty: "Medium",
        topics: ["Error Boundaries", "Error Handling"],
      },
      {
        id: "r28",
        question: "What is the useRef hook and what are its use cases?",
        difficulty: "Medium",
        topics: ["Hooks", "useRef"],
      },
      {
        id: "r29",
        question: "How do you create custom hooks in React?",
        difficulty: "Medium",
        topics: ["Hooks", "Custom Hooks"],
      },
      {
        id: "r30",
        question:
          "What is the useLayoutEffect hook and how does it differ from useEffect?",
        difficulty: "Hard",
        topics: ["Hooks", "useLayoutEffect"],
      },
      {
        id: "r31",
        question: "Explain the concept of lifting state up in React.",
        difficulty: "Medium",
        topics: ["State Management"],
      },
      {
        id: "r32",
        question: "What are portals in React and when would you use them?",
        difficulty: "Medium",
        topics: ["Portals"],
      },
      {
        id: "r33",
        question: "How do you handle side effects in React?",
        difficulty: "Medium",
        topics: ["Side Effects", "useEffect"],
      },
      {
        id: "r34",
        question:
          "What is code splitting in React and how do you implement it?",
        difficulty: "Hard",
        topics: ["Code Splitting", "Performance"],
      },
      {
        id: "r35",
        question: "Explain React Suspense and lazy loading.",
        difficulty: "Hard",
        topics: ["Suspense", "Lazy Loading"],
      },
      {
        id: "r36",
        question: "How do you pass data from child to parent component?",
        difficulty: "Easy",
        topics: ["Props", "Component Communication"],
      },
      {
        id: "r37",
        question: "What are synthetic events in React?",
        difficulty: "Medium",
        topics: ["Events", "Synthetic Events"],
      },
      {
        id: "r38",
        question: "How do you conditionally render components in React?",
        difficulty: "Easy",
        topics: ["Conditional Rendering"],
      },
      {
        id: "r39",
        question: "What is the difference between createElement and JSX?",
        difficulty: "Medium",
        topics: ["JSX"],
      },
      {
        id: "r40",
        question: "Explain the useImperativeHandle hook.",
        difficulty: "Hard",
        topics: ["Hooks", "useImperativeHandle"],
      },
      {
        id: "r41",
        question: "How do you implement routing with React Router v6?",
        difficulty: "Medium",
        topics: ["React Router"],
      },
      {
        id: "r42",
        question: "What are protected routes and how do you implement them?",
        difficulty: "Medium",
        topics: ["React Router", "Authentication"],
      },
      {
        id: "r43",
        question:
          "Explain the difference between useEffect cleanup and component unmounting.",
        difficulty: "Hard",
        topics: ["useEffect", "Lifecycle"],
      },
      {
        id: "r44",
        question: "How do you handle global state without external libraries?",
        difficulty: "Hard",
        topics: ["State Management", "Context API"],
      },
      {
        id: "r45",
        question: "What is React Fiber and why was it introduced?",
        difficulty: "Hard",
        topics: ["React Fiber", "Architecture"],
      },
    ],
  },
  {
    phase: 5,
    phaseKey: "advanced",
    title: "Advanced Topics & Specialization",
    duration: "6 Weeks",
    level: "Advanced",
    weeks: [
      {
        week: 25,
        weekGoal:
          "Introduce strong typing into frontend applications using TypeScript",
        weekOutcome:
          "Ability to build type-safe React applications with confidence and fewer runtime bugs",
        miniProject: "TypeScript React App",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Introduction to TypeScript & Setup",

            subTopics: [
              "What is TypeScript",
              "Why TypeScript is used in frontend jobs",
              "Installing TypeScript",
              "TypeScript compiler (tsc)",
              "tsconfig.json basics",
            ],

            explanation:
              "TypeScript is a superset of JavaScript that adds static typing, helping developers catch errors early and write more maintainable code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "TypeScript Mindset Shift Exercise",
                description:
                  "Move from dynamic JS thinking to type-first thinking.",
                tasks: [
                  "Convert a small JS file to TypeScript",
                  "Add explicit types to variables and functions",
                  "Observe errors caught at compile time",
                ],
                outcome:
                  "You’ll understand how TypeScript prevents runtime bugs early.",
              },
              {
                id: "pt-2",
                title: "Strict Mode Survival Drill",
                description:
                  "Learn to work comfortably with strict TypeScript settings.",
                tasks: [
                  "Enable strict mode in tsconfig",
                  "Fix all implicit any errors",
                  "Handle null and undefined safely",
                ],
                qualityRule: "No any allowed unless justified.",
              },
            ],

            testingInsights: {
              coreReality:
                "TypeScript bugs are caught before code runs, not after.",
              checklist: [
                "Is strict mode enabled?",
                "Are types explicit where needed?",
                "Is any being misused?",
                "Does the compiler catch invalid assignments?",
              ],
              goldenRule:
                "If TypeScript is silent, JavaScript will behave predictably.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why do companies prefer TypeScript?",
                "Does TypeScript run in the browser?",
                "What happens if TypeScript types are wrong?",
                "What is strict mode and why is it important?",
              ],
              strongAnswerPattern:
                "Explain problem in JS → how TS solves it → impact on teams",
              redFlag: "TypeScript is just JavaScript with extra syntax",
            },

            editorGuidance: {
              careerMindset: [
                "TypeScript is about confidence, not verbosity",
                "Types are documentation for future you",
                "Compiler errors are friends, not blockers",
              ],
              seniorAdvice:
                "If your TypeScript feels painful, your mental model is still JavaScript.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "TypeScript is JavaScript with types. It helps developers detect bugs during development instead of at runtime. Today, almost all modern frontend companies (React, Angular, Next.js) prefer or require TypeScript for scalable applications.",

              whatIsTypeScript: {
                title: "What is TypeScript?",
                explanation:
                  "TypeScript is a statically typed superset of JavaScript that compiles to plain JavaScript.",
                keyPoints: [
                  "Adds static typing to JavaScript",
                  "Runs in development, not in browser",
                  "Compiled to JavaScript using tsc",
                  "Fully compatible with JavaScript",
                ],
                example: `
let age: number = 25;
// age = "twenty"; ❌ Type error
      `,
                realWorldNote:
                  "TypeScript does NOT replace JavaScript — it enhances it",
              },

              whyTypeScript: {
                title: "Why TypeScript is Used in Frontend Jobs",
                explanation:
                  "As applications grow, JavaScript becomes harder to maintain. TypeScript solves this by making code predictable and self-documented.",
                benefits: [
                  "Early error detection",
                  "Better IDE autocomplete",
                  "Self-documenting code",
                  "Safer refactoring",
                  "Better team collaboration",
                ],
                companiesUsingTS: [
                  "Google",
                  "Meta",
                  "Microsoft",
                  "Netflix",
                  "Most React & Next.js projects",
                ],
              },

              installation: {
                title: "Installing TypeScript",
                explanation:
                  "TypeScript can be installed globally or per project.",
                methods: [
                  "Global installation",
                  "Local (project-based) installation",
                ],
                commands: `
npm install -g typescript
// OR
npm install --save-dev typescript
      `,
                bestPractice: "Prefer local installation for real projects",
              },

              compiler: {
                title: "TypeScript Compiler (tsc)",
                explanation:
                  "The TypeScript compiler converts .ts files into .js files.",
                workflow: [
                  "Write TypeScript code",
                  "Compile using tsc",
                  "Run compiled JavaScript",
                ],
                example: `
tsc index.ts
// Generates index.js
      `,
                importantNote: "Browser never understands TypeScript directly",
              },

              tsconfig: {
                title: "tsconfig.json Basics",
                explanation:
                  "tsconfig.json defines how TypeScript compiles your code.",
                commonOptions: [
                  "target",
                  "module",
                  "strict",
                  "rootDir",
                  "outDir",
                ],
                exampleConfig: `
{
  "compilerOptions": {
    "target": "ES6",
    "module": "ESNext",
    "strict": true,
    "outDir": "dist"
  }
}
      `,
                bestPractice:
                  "Always enable strict mode for professional projects",
              },

              commonMistakes: [
                {
                  mistake: "Thinking TypeScript runs in browser",
                  issue: "Confusion about runtime",
                  fix: "TypeScript only runs at compile time",
                },
                {
                  mistake: "Disabling strict mode",
                  issue: "Losing TypeScript benefits",
                  fix: "Keep strict: true",
                },
                {
                  mistake: "Over-typing everything initially",
                  issue: "Learning friction",
                  fix: "Gradually introduce types",
                },
              ],

              interviewQuestions: [
                "What is TypeScript and why is it used?",
                "Difference between JavaScript and TypeScript?",
                "What is tsconfig.json?",
                "Does TypeScript run in browser?",
                "What is strict mode?",
              ],
            },

            tasks: [
              "Install TypeScript globally or locally",
              "Create a basic tsconfig.json file",
              "Compile a simple TypeScript file",
            ],

            examples: [
              "tsc index.ts",
              "let message: string = 'Hello TypeScript';",
            ],

            useCases: [
              "Large codebases",
              "Team-based development",
              "Reducing runtime errors",
            ],

            editorType: "typescript",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand why TypeScript exists",
              "Setup TypeScript environment",
              "Learn TypeScript compilation flow",
              "Prepare for typed React projects",
            ],
          },
          {
            day: 2,
            topic: "Basic Types & Type Inference",

            subTopics: [
              "Primitive types (string, number, boolean)",
              "Array types",
              "Any vs unknown",
              "Type inference",
              "Void and never types",
            ],

            explanation:
              "TypeScript provides built-in types that describe the shape and behavior of data, making code more predictable.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Type Safety Awareness Drill",
                description:
                  "Train yourself to think in types instead of values.",
                tasks: [
                  "Replace implicit any with explicit types",
                  "Convert loosely typed variables into strict types",
                  "Identify places where inference is enough vs explicit typing needed",
                ],
                outcome:
                  "You’ll start trusting the TypeScript compiler instead of runtime testing.",
              },
              {
                id: "pt-2",
                title: "any vs unknown Refactor Challenge",
                description: "Eliminate unsafe any usage from code.",
                tasks: [
                  "Find all any usages in a file",
                  "Replace any with unknown",
                  "Add proper type guards before usage",
                ],
                qualityRule: "No operation on unknown without type checking.",
                outcome: "You’ll understand why unknown is safer than any.",
              },
              {
                id: "pt-3",
                title: "Void & Never Control Flow Exercise",
                description: "Understand non-returning functions deeply.",
                tasks: [
                  "Create a logger function returning void",
                  "Create an error handler returning never",
                  "Use never in a switch default case",
                ],
                outcome:
                  "You’ll clearly distinguish between ‘no return’ and ‘impossible return’.",
              },
            ],

            testingInsights: {
              coreReality:
                "Most TypeScript bugs come from incorrect assumptions about data shape.",
              checklist: [
                "Are primitive types correctly defined?",
                "Are arrays typed with element types?",
                "Is any avoided or justified?",
                "Is unknown safely narrowed before use?",
                "Are void and never used intentionally?",
              ],
              goldenRule:
                "If TypeScript can’t prove safety, assume the code is unsafe.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between any and unknown?",
                "What is type inference?",
                "When should you explicitly type variables?",
                "What does never represent?",
                "How does TypeScript improve runtime safety?",
              ],
              strongAnswerPattern:
                "Explain the type → show a bug scenario → explain how TS prevents it",
              redFlag: "Using any as a shortcut instead of proper typing.",
            },

            editorGuidance: {
              careerMindset: [
                "Types describe intent, not just data",
                "Inference is power, not laziness",
                "Type errors are design feedback",
              ],
              seniorAdvice:
                "If you reach for any, pause and rethink your data model.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "TypeScript’s power starts with its type system. Understanding basic types and how TypeScript automatically infers types is essential before moving to advanced concepts like interfaces, generics, and React with TypeScript.",

              primitiveTypes: {
                title: "Primitive Types",
                explanation:
                  "Primitive types represent simple values and are the most commonly used types in TypeScript.",
                types: {
                  string: {
                    description: "Represents textual data",
                    example: `let name: string = "Gaurav";`,
                  },
                  number: {
                    description: "Represents numbers (int, float, etc.)",
                    example: `let age: number = 25;`,
                  },
                  boolean: {
                    description: "Represents true or false",
                    example: `let isLoggedIn: boolean = true;`,
                  },
                },
                realWorldUsage:
                  "Used in form inputs, API data, flags, counters, and UI conditions",
              },

              arrayTypes: {
                title: "Array Types",
                explanation:
                  "Arrays store collections of values of a specific type.",
                methods: ["Type[] syntax", "Array<Type> generic syntax"],
                examples: `
let skills: string[] = ["HTML", "CSS", "JS"];
let scores: Array<number> = [90, 85, 88];
      `,
                bestPractice:
                  "Always define what type of values an array can contain",
              },

              anyVsUnknown: {
                title: "any vs unknown",
                explanation:
                  "`any` disables type checking, while `unknown` forces you to validate before usage.",
                comparison: [
                  {
                    type: "any",
                    behavior: "No type safety",
                    risk: "Can cause runtime errors",
                  },
                  {
                    type: "unknown",
                    behavior: "Type-safe alternative",
                    benefit: "Forces validation",
                  },
                ],
                examples: `
let valueAny: any = 10;
valueAny.toUpperCase(); // ❌ runtime error

let valueUnknown: unknown = 10;
// valueUnknown.toUpperCase(); ❌ compile error
if (typeof valueUnknown === "string") {
  valueUnknown.toUpperCase(); // ✅ safe
}
      `,
                interviewNote: "Prefer unknown over any in professional code",
              },

              typeInference: {
                title: "Type Inference",
                explanation:
                  "TypeScript automatically infers types based on assigned values.",
                example: `
let count = 10; 
// inferred as number
// count = "ten"; ❌ error
      `,
                whenToUseExplicitTypes: [
                  "Function parameters",
                  "API responses",
                  "Complex objects",
                  "Public interfaces",
                ],
                bestPractice:
                  "Let TypeScript infer types when obvious, define types when unclear",
              },

              voidAndNever: {
                title: "Void & Never Types",
                explanation:
                  "These types describe functions that don’t return values or never finish execution.",
                voidExample: `
function logMessage(msg: string): void {
  console.log(msg);
}
      `,
                neverExample: `
function throwError(message: string): never {
  throw new Error(message);
}
      `,
                usage: {
                  void: "Functions without return value",
                  never: "Errors, infinite loops, unreachable code",
                },
              },

              commonMistakes: [
                {
                  mistake: "Using any everywhere",
                  issue: "Loses TypeScript benefits",
                  fix: "Use unknown or proper types",
                },
                {
                  mistake: "Over-typing obvious values",
                  issue: "Verbose code",
                  fix: "Use type inference wisely",
                },
                {
                  mistake: "Ignoring never type",
                  issue: "Unclear control flow",
                  fix: "Use never for impossible states",
                },
              ],

              interviewQuestions: [
                "What is type inference in TypeScript?",
                "Difference between any and unknown?",
                "What is void type?",
                "When is never used?",
                "How do you type arrays in TypeScript?",
              ],
            },

            tasks: [
              "Define variables with explicit types",
              "Create typed arrays",
              "Understand type inference behavior",
            ],

            examples: [
              "let age: number = 25;",
              "let skills: string[] = ['HTML', 'CSS'];",
            ],

            useCases: [
              "Form data handling",
              "API response typing",
              "Utility functions",
            ],

            editorType: "typescript",

            estimatedTime: "2 hours",

            objectives: [
              "Master basic TypeScript types",
              "Understand when TypeScript infers types",
              "Avoid unsafe typing patterns",
              "Prepare for typed functions & objects",
            ],
          },
          {
            day: 3,
            topic: "Interfaces & Type Aliases",

            subTopics: [
              "Defining interfaces",
              "Type aliases",
              "Interface vs type",
              "Optional properties",
              "Readonly properties",
            ],

            explanation:
              "Interfaces and type aliases define the structure of objects and functions, which is critical for scalable frontend applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Interface Modeling Drill",
                description: "Learn to model real-world data using interfaces.",
                tasks: [
                  "Design an interface for API user response",
                  "Add optional properties for nullable fields",
                  "Mark immutable fields as readonly",
                ],
                outcome:
                  "You’ll be able to design clean, realistic data contracts.",
              },
              {
                id: "pt-2",
                title: "Interface vs Type Decision Exercise",
                description:
                  "Build intuition for choosing between interface and type.",
                tasks: [
                  "Create same object using interface and type",
                  "Extend interface using inheritance",
                  "Create union types using type alias",
                ],
                qualityRule:
                  "Interface for extensible objects, type for unions & compositions.",
                outcome:
                  "You’ll stop guessing and start choosing deliberately.",
              },
              {
                id: "pt-3",
                title: "Readonly Safety Challenge",
                description: "Understand immutability at type level.",
                tasks: [
                  "Add readonly properties to core identifiers",
                  "Attempt mutation and observe compiler errors",
                  "Refactor logic to respect immutability",
                ],
                outcome: "You’ll prevent accidental state mutation bugs early.",
              },
            ],

            testingInsights: {
              coreReality:
                "Most production TypeScript bugs come from incorrect object shapes.",
              checklist: [
                "Are object shapes defined using interfaces or types?",
                "Are optional properties handled safely?",
                "Are readonly properties respected?",
                "Is interface vs type chosen intentionally?",
              ],
              goldenRule:
                "If data has a shape, it deserves an interface or type.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between interface and type?",
                "Can interfaces be extended?",
                "When should you prefer type alias?",
                "What does readonly protect against?",
              ],
              strongAnswerPattern:
                "Explain use-case → show limitation → justify choice",
              redFlag:
                "Using type and interface interchangeably without reasoning.",
            },

            editorGuidance: {
              careerMindset: [
                "Types are contracts between developers",
                "Interfaces make large codebases survivable",
                "Immutability starts at the type level",
              ],
              seniorAdvice:
                "If your data model is unclear, your bugs are already written.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Interfaces and type aliases are the backbone of TypeScript. They allow you to describe the shape of data clearly, making large frontend applications predictable, readable, and safe.",

              interfacesBasics: {
                title: "Defining Interfaces",
                explanation:
                  "Interfaces describe the structure of an object. They are mainly used for object shapes and contracts.",
                example: `
interface User {
  name: string;
  age: number;
  isAdmin: boolean;
}
      `,
                realWorldUsage: "User objects, API responses, component props",
              },

              typeAliasesBasics: {
                title: "Type Aliases",
                explanation:
                  "Type aliases allow creating a new name for any type, including objects, unions, and primitives.",
                example: `
type Product = {
  id: number;
  title: string;
  price: number;
};
      `,
                advantage:
                  "More flexible than interfaces for complex and union types",
              },

              interfaceVsType: {
                title: "Interface vs Type",
                explanation:
                  "Both are used to define shapes, but they have subtle differences.",
                comparison: [
                  {
                    feature: "Extensibility",
                    interface: "Supports extension & declaration merging",
                    type: "Cannot be merged",
                  },
                  {
                    feature: "Use cases",
                    interface: "Objects & classes",
                    type: "Unions, primitives, objects",
                  },
                ],
                bestPractice:
                  "Use interface for object shapes, type for unions and complex types",
              },

              optionalProperties: {
                title: "Optional Properties",
                explanation:
                  "Optional properties may or may not exist on an object.",
                example: `
interface User {
  name: string;
  age?: number;
}
      `,
                realWorldUsage:
                  "Optional API fields, partial forms, config objects",
              },

              readonlyProperties: {
                title: "Readonly Properties",
                explanation:
                  "Readonly properties cannot be modified after initialization.",
                example: `
interface Product {
  readonly id: number;
  title: string;
}
      `,
                benefit:
                  "Prevents accidental mutations and improves immutability",
              },

              functionTyping: {
                title: "Typing Functions with Interfaces",
                explanation: "Interfaces can also define function signatures.",
                example: `
interface AddFn {
  (a: number, b: number): number;
}
      `,
                usage: "Reusable function contracts and callbacks",
              },

              commonMistakes: [
                {
                  mistake: "Using any instead of interfaces",
                  issue: "Loses type safety",
                  fix: "Define proper interfaces",
                },
                {
                  mistake: "Overusing type aliases for objects",
                  issue: "Harder to extend",
                  fix: "Prefer interface for objects",
                },
                {
                  mistake: "Mutating readonly properties",
                  issue: "TypeScript error",
                  fix: "Respect immutability",
                },
              ],

              interviewQuestions: [
                "Difference between interface and type?",
                "When should you use interface?",
                "What are optional properties?",
                "What does readonly do?",
                "Can interfaces be extended?",
              ],
            },

            tasks: [
              "Create interface for user object",
              "Use optional and readonly properties",
              "Refactor JS objects to TypeScript interfaces",
            ],

            examples: [
              "interface User { name: string; age?: number; }",
              "type Product = { id: number; title: string; }",
            ],

            useCases: [
              "User profiles",
              "Product models",
              "Component props typing",
            ],

            editorType: "typescript",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Understand data modeling with interfaces",
              "Choose correctly between interface and type",
              "Write scalable and maintainable TypeScript",
              "Prepare for React + TypeScript props typing",
            ],
          },
          {
            day: 4,
            topic: "Function Typing & Type Guards",

            subTopics: [
              "Function parameter typing",
              "Return types",
              "Optional and default parameters",
              "Union types",
              "Type guards (typeof, in)",
            ],

            explanation:
              "Typing functions ensures predictable inputs and outputs, which is essential for business logic and APIs.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Public Function Contract Design",
                description:
                  "Design production-ready function signatures with strict typing.",
                tasks: [
                  "Define explicit return types for all exported functions",
                  "Add optional and default parameters safely",
                  "Refactor loosely typed functions into strict ones",
                ],
                outcome:
                  "You’ll write functions that are safe to reuse across large codebases.",
              },
              {
                id: "pt-2",
                title: "Union Types + Guard Mastery",
                description:
                  "Learn to safely handle multiple input types without runtime bugs.",
                tasks: [
                  "Create functions accepting union types",
                  "Apply typeof guards correctly",
                  "Use 'in' guard for object-based unions",
                ],
                qualityRule:
                  "Union types without guards are incomplete implementations.",
                outcome:
                  "You’ll eliminate unsafe assumptions in business logic.",
              },
              {
                id: "pt-3",
                title: "Real-world Validation Utilities",
                description: "Build validation helpers using type guards.",
                tasks: [
                  "Create isString, isNumber, isAdmin-style guards",
                  "Use guards to narrow types before logic",
                  "Refactor condition-heavy code using guards",
                ],
                outcome: "Cleaner, safer, and more readable validation logic.",
              },
            ],

            testingInsights: {
              coreReality:
                "Most runtime bugs come from functions receiving unexpected types.",
              checklist: [
                "Are function parameters explicitly typed?",
                "Are return types declared for public functions?",
                "Are union types narrowed using guards?",
                "Is any used unnecessarily?",
              ],
              goldenRule:
                "If a function accepts multiple types, it must defend itself.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why should functions have explicit return types?",
                "What is a union type and why is it risky?",
                "What is a type guard?",
                "Difference between typeof and in guard?",
              ],
              strongAnswerPattern:
                "Explain the problem → show unsafe code → show guard-based solution",
              redFlag:
                "Using union types but relying on assumptions instead of guards.",
            },

            editorGuidance: {
              careerMindset: [
                "Functions are contracts, not just logic blocks",
                "Type guards are runtime safety nets",
                "Explicit typing scales better than clever inference",
              ],
              seniorAdvice:
                "If a function can be misused, someone eventually will—guard it.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Functions are the core of business logic. TypeScript makes functions safer by enforcing correct input types, return values, and runtime checks using type guards.",

              functionParameterTyping: {
                title: "Function Parameter Typing",
                explanation:
                  "Each function parameter should have a clear type to avoid unexpected values.",
                example: `
function greet(name: string, age: number) {
  return \`Hello \${name}, age \${age}\`;
}
      `,
                realWorldUsage: "Utility functions, helpers, business rules",
              },

              returnTypes: {
                title: "Return Types",
                explanation:
                  "Explicit return types make function behavior predictable and easier to debug.",
                example: `
function add(a: number, b: number): number {
  return a + b;
}
      `,
                bestPractice:
                  "Always define return types for public and reusable functions",
              },

              optionalAndDefaultParams: {
                title: "Optional & Default Parameters",
                explanation:
                  "Optional parameters may or may not be passed. Default parameters ensure safe fallback values.",
                example: `
function createUser(name: string, role?: string) {
  return { name, role };
}

function multiply(a: number, b: number = 1) {
  return a * b;
}
      `,
                realWorldUsage: "Config objects, optional API inputs",
              },

              unionTypesInFunctions: {
                title: "Union Types in Functions",
                explanation:
                  "Union types allow a parameter to accept multiple types safely.",
                example: `
function formatId(id: number | string) {
  return \`ID-\${id}\`;
}
      `,
                warning:
                  "Union types MUST be narrowed before using type-specific methods",
              },

              typeGuards: {
                title: "Type Guards",
                explanation:
                  "Type guards help narrow union types at runtime so TypeScript knows the exact type.",
                guards: {
                  typeofGuard: {
                    title: "typeof Guard",
                    example: `
function printValue(value: string | number) {
  if (typeof value === 'string') {
    console.log(value.toUpperCase());
  } else {
    console.log(value.toFixed(2));
  }
}
          `,
                  },
                  inGuard: {
                    title: "'in' Guard",
                    example: `
type Admin = { role: 'admin'; permissions: string[] };
type User = { role: 'user'; email: string };

function checkRole(person: Admin | User) {
  if ('permissions' in person) {
    console.log(person.permissions);
  }
}
          `,
                  },
                },
              },

              commonMistakes: [
                {
                  mistake: "Not typing function parameters",
                  issue: "Unexpected runtime errors",
                  fix: "Always type parameters explicitly",
                },
                {
                  mistake: "Using union types without guards",
                  issue: "TypeScript errors",
                  fix: "Use typeof or in guards",
                },
                {
                  mistake: "Overusing any",
                  issue: "Loss of type safety",
                  fix: "Prefer unions + guards",
                },
              ],

              interviewQuestions: [
                "Why should functions have return types?",
                "What are union types?",
                "What is a type guard?",
                "Difference between typeof and in?",
                "How do type guards help TypeScript?",
              ],
            },

            tasks: [
              "Type utility functions",
              "Use union types safely",
              "Implement type guards",
            ],

            examples: [
              "function add(a: number, b: number): number { return a + b; }",
              "if (typeof value === 'string') { }",
            ],

            useCases: ["Business logic", "Validation utilities", "API helpers"],

            editorType: "typescript",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Write type-safe functions",
              "Use union types confidently",
              "Apply type guards correctly",
              "Prepare for real-world TypeScript logic",
            ],
          },
          {
            day: 5,
            topic: "Generics & Advanced Types",

            subTopics: [
              "Generic functions",
              "Generic interfaces",
              "Generic constraints",
              "Intersection types",
              "Utility types overview (Partial, Pick)",
            ],

            explanation:
              "Generics allow you to write reusable, type-safe code that works with multiple data types.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Generic Utility Builder",
                description:
                  "Build reusable helpers using generics instead of duplicating logic.",
                tasks: [
                  "Create a generic identity and wrapper function",
                  "Refactor same-logic-different-type functions into one generic",
                  "Compare generic vs non-generic implementations",
                ],
                outcome:
                  "You’ll stop duplicating logic and start writing reusable utilities.",
              },
              {
                id: "pt-2",
                title: "API Response Typing with Generics",
                description:
                  "Model real API responses using generic interfaces.",
                tasks: [
                  "Create ApiResponse<T> for success & error states",
                  "Use it with multiple data shapes (User, Product, List)",
                  "Refactor existing API typings to generic form",
                ],
                qualityRule:
                  "If API shape is same but data differs, generics are the answer.",
                outcome: "You’ll write production-grade API typings.",
              },
              {
                id: "pt-3",
                title: "Type Transformation Mastery",
                description: "Use utility types instead of rewriting types.",
                tasks: [
                  "Create update DTOs using Partial",
                  "Create UI preview models using Pick",
                  "Replace duplicated types with utility types",
                ],
                outcome: "Cleaner, DRY, and future-proof type definitions.",
              },
            ],

            testingInsights: {
              coreReality:
                "Generics fail silently if they are unconstrained or overused.",
              checklist: [
                "Is generic actually needed here?",
                "Should this generic be constrained?",
                "Can utility types replace custom types?",
                "Are intersections combining logical responsibilities?",
              ],
              goldenRule:
                "Generics should simplify reuse, not increase mental load.",
            },

            interviewInsights: {
              commonQuestions: [
                "What problem do generics solve?",
                "Difference between generics and union types?",
                "What are generic constraints?",
                "When would you avoid generics?",
                "How are generics used in React and Axios?",
              ],
              strongAnswerPattern:
                "Show duplication → introduce generic → explain safety improvement",
              redFlag: "Using generics everywhere without clear reuse benefit.",
            },

            editorGuidance: {
              careerMindset: [
                "Generics are about reuse with safety",
                "Constraints are what make generics trustworthy",
                "Utility types beat custom duplication",
              ],
              seniorAdvice:
                "If your generic needs a long explanation, it’s probably over-engineered.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Generics are one of the most powerful features of TypeScript. They allow you to write flexible, reusable logic without losing type safety. Almost every modern frontend codebase (React, Axios, libraries) heavily relies on generics.",

              genericFunctions: {
                title: "Generic Functions",
                explanation:
                  "Generic functions work with different data types while preserving the exact type passed.",
                exampleLogic: `
function identity<T>(value: T): T {
  return value;
}

identity<string>('Hello');
identity<number>(42);
      `,
                realWorldUsage:
                  "Utility helpers, reusable logic, library functions",
              },

              genericInterfaces: {
                title: "Generic Interfaces",
                explanation:
                  "Generic interfaces allow defining reusable data structures with flexible types.",
                exampleLogic: `
type ApiResponse<T> = {
  data: T;
  error?: string;
};

const userResponse: ApiResponse<{ id: number; name: string }> = {
  data: { id: 1, name: 'John' },
};
      `,
                realWorldNote: "Used heavily in API layers, services, and SDKs",
              },

              genericConstraints: {
                title: "Generic Constraints",
                explanation:
                  "Constraints limit what types can be passed to a generic, improving safety.",
                exampleLogic: `
function getLength<T extends { length: number }>(value: T) {
  return value.length;
}

getLength('Hello');
getLength([1,2,3]);
      `,
                benefit: "Prevents invalid generic usage at compile time",
              },

              intersectionTypes: {
                title: "Intersection Types (&)",
                explanation:
                  "Intersection types combine multiple types into one.",
                exampleLogic: `
type User = { id: number; name: string };
type Admin = { permissions: string[] };

type AdminUser = User & Admin;

const admin: AdminUser = {
  id: 1,
  name: 'Alex',
  permissions: ['READ', 'WRITE'],
};
      `,
                realWorldUsage:
                  "Role-based users, combined props, feature flags",
              },

              utilityTypes: {
                title: "Utility Types (Partial, Pick)",
                explanation:
                  "Utility types transform existing types instead of rewriting them.",
                utilities: {
                  partial: {
                    title: "Partial<T>",
                    example: `
type User = { id: number; name: string; email: string };

type UpdateUser = Partial<User>;
          `,
                    usage: "Update forms, PATCH APIs",
                  },
                  pick: {
                    title: "Pick<T, K>",
                    example: `
type UserPreview = Pick<User, 'id' | 'name'>;
          `,
                    usage: "Optimized API responses, UI previews",
                  },
                },
              },

              commonMistakes: [
                {
                  mistake: "Overusing generics unnecessarily",
                  issue: "Code becomes complex",
                  fix: "Use generics only when reuse is needed",
                },
                {
                  mistake: "Not constraining generics",
                  issue: "Unsafe operations",
                  fix: "Use extends keyword",
                },
                {
                  mistake: "Rewriting types instead of utility types",
                  issue: "Duplication",
                  fix: "Use Partial, Pick, Omit",
                },
              ],

              interviewQuestions: [
                "What problem do generics solve?",
                "Difference between generics and union types?",
                "What are generic constraints?",
                "Explain Partial and Pick",
                "How are generics used in React?",
              ],
            },

            tasks: [
              "Create generic helper functions",
              "Apply generics to API responses",
              "Use utility types",
            ],

            examples: [
              "function identity<T>(value: T): T { return value; }",
              "type ApiResponse<T> = { data: T; error?: string; }",
            ],

            useCases: [
              "Reusable components",
              "Typed API layers",
              "Libraries and frameworks",
            ],

            editorType: "typescript",

            estimatedTime: "2.5–3 hours",

            objectives: [
              "Master TypeScript generics",
              "Build reusable type-safe utilities",
              "Understand advanced type composition",
              "Prepare for real-world TypeScript & React usage",
            ],
          },
          {
            day: 6,
            topic: "TypeScript with React",

            subTopics: [
              "Typing React components",
              "Typing props and state",
              "Typing events",
              "Children prop typing",
              "Typing hooks (useState, useEffect)",
            ],

            explanation:
              "Using TypeScript with React greatly improves developer experience and code reliability in real-world applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Type-Safe Component Refactor",
                description:
                  "Convert existing JavaScript React components into fully typed TypeScript components.",
                tasks: [
                  "Refactor props using interface/type",
                  "Type useState with union types (null | data)",
                  "Remove all implicit any errors",
                ],
                outcome:
                  "You’ll gain confidence converting real JS React codebases to TypeScript.",
              },
              {
                id: "pt-2",
                title: "Event Handling Mastery",
                description:
                  "Handle all common React events with correct TypeScript types.",
                tasks: [
                  "Type input, button, and form events",
                  "Fix common event typing mistakes",
                  "Refactor untyped handlers to typed ones",
                ],
                qualityRule:
                  "No event handler should accept an untyped event parameter.",
                outcome: "Cleaner, safer event-driven UI logic.",
              },
              {
                id: "pt-3",
                title: "Reusable Wrapper Components",
                description:
                  "Build wrapper components using children typing correctly.",
                tasks: [
                  "Create Card / Layout component with ReactNode",
                  "Pass components, strings, and fragments as children",
                  "Avoid React.FC pitfalls",
                ],
                outcome: "You’ll write flexible, library-quality components.",
              },
            ],

            testingInsights: {
              coreReality:
                "Most TypeScript bugs in React come from untyped props and nullable state.",
              checklist: [
                "Are all props explicitly typed?",
                "Is nullable state handled safely?",
                "Are events typed correctly?",
                "Are children typed as ReactNode?",
              ],
              goldenRule:
                "If TypeScript can’t help autocomplete, your typing is incomplete.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you type props and state in React?",
                "Why avoid React.FC?",
                "How do you type children?",
                "How do you handle nullable state?",
                "How do you type events in React?",
              ],
              strongAnswerPattern:
                "Explain with small component + props + state example",
              redFlag:
                "Using any or ignoring TypeScript errors in React components.",
            },

            editorGuidance: {
              careerMindset: [
                "Typed components are easier to refactor and scale",
                "Good typing reduces code review friction",
                "TypeScript is a teammate, not a blocker",
              ],
              seniorAdvice:
                "If your component breaks on refactor, its types were weak.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "TypeScript + React is the industry standard combination today. Most production React codebases use TypeScript to prevent runtime bugs, improve refactoring, and make components self-documenting.",

              typingComponents: {
                title: "Typing React Components",
                explanation:
                  "React components can be typed using function typing with props interfaces or types.",
                exampleLogic: `
type Props = {
  title: string;
};

const Header = ({ title }: Props) => {
  return <h1>{title}</h1>;
};
      `,
                bestPractice:
                  "Prefer function components with typed props over React.FC for flexibility",
              },

              typingPropsAndState: {
                title: "Typing Props & State",
                explanation:
                  "Props describe external data, while state represents internal component data. Both should be typed explicitly.",
                exampleLogic: `
type User = {
  id: number;
  name: string;
};

const Profile = ({ user }: { user: User }) => {
  const [count, setCount] = useState<number>(0);
  return <div>{user.name} - {count}</div>;
};
      `,
                realWorldNote:
                  "Typed state prevents invalid updates and improves autocomplete",
              },

              typingEvents: {
                title: "Typing Events in React",
                explanation:
                  "React events are wrapped in SyntheticEvent types and must be typed correctly.",
                exampleLogic: `
const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  console.log(e.target.value);
};

const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();
};
      `,
                commonEvents: [
                  "React.MouseEvent",
                  "React.ChangeEvent",
                  "React.FormEvent",
                  "React.KeyboardEvent",
                ],
              },

              childrenTyping: {
                title: "Typing children Prop",
                explanation:
                  "Components that wrap other components should type the children prop properly.",
                exampleLogic: `
type CardProps = {
  children: React.ReactNode;
};

const Card = ({ children }: CardProps) => {
  return <div className='card'>{children}</div>;
};
      `,
                bestPractice:
                  "Use React.ReactNode instead of JSX.Element for flexibility",
              },

              typingHooks: {
                title: "Typing React Hooks",
                explanation:
                  "Hooks like useState and useEffect benefit from explicit typing, especially with complex data.",
                exampleLogic: `
const [user, setUser] = useState<User | null>(null);

useEffect(() => {
  fetchUser().then(setUser);
}, []);
      `,
                insight: "Union types are common when state starts as null",
              },

              commonMistakes: [
                {
                  mistake: "Using any for props",
                  issue: "Loses type safety",
                  fix: "Always define explicit types or interfaces",
                },
                {
                  mistake: "Overusing React.FC",
                  issue: "Hidden children typing & generics issues",
                  fix: "Use typed function components",
                },
                {
                  mistake: "Not typing events",
                  issue: "Incorrect event handling",
                  fix: "Use React event types",
                },
              ],

              interviewQuestions: [
                "How do you type props in React with TypeScript?",
                "Difference between React.FC and normal function?",
                "How do you type events in React?",
                "How do you type children?",
                "How do you handle nullable state?",
              ],
            },

            tasks: [
              "Convert React component to TypeScript",
              "Type props and state",
              "Handle typed events",
            ],

            examples: [
              "type Props = { title: string }",
              "const [count, setCount] = useState<number>(0)",
            ],

            useCases: [
              "Enterprise React apps",
              "Component libraries",
              "Large frontend teams",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Write type-safe React components",
              "Prevent runtime bugs using TypeScript",
              "Improve DX with autocomplete & refactoring",
              "Prepare for production React codebases",
            ],
          },
          {
            day: 7,
            topic: "Mini Project – TypeScript React Application",

            subTopics: [
              "Type-safe components",
              "Typed API services",
              "Global types",
              "Refactoring JS to TS",
              "Best practices",
            ],

            explanation:
              "This project ensures you can confidently build and refactor real-world React apps using TypeScript.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "JS → TS Full Refactor Challenge",
                description:
                  "Refactor a real JavaScript React project into a fully type-safe TypeScript codebase.",
                tasks: [
                  "Rename all .js/.jsx files to .ts/.tsx",
                  "Fix implicit any errors without using any",
                  "Extract shared types into /types folder",
                ],
                qualityRule:
                  "Project should compile with strict mode enabled and zero TS errors.",
                outcome:
                  "You’ll be confident migrating real company codebases to TypeScript.",
              },
              {
                id: "pt-2",
                title: "Typed API + UI Integration",
                description:
                  "Connect typed API services with typed React components.",
                tasks: [
                  "Create ApiResponse<T> generic",
                  "Type API services with Promise<ApiResponse<T>>",
                  "Consume API data without optional chaining hacks",
                ],
                outcome:
                  "You’ll stop guessing API data shapes in UI components.",
              },
              {
                id: "pt-3",
                title: "Type Safety Audit",
                description:
                  "Perform a senior-level type safety review on your project.",
                tasks: [
                  "Search and remove all any usages",
                  "Replace inline types with shared models",
                  "Add union types for nullable state",
                ],
                outcome:
                  "Your project will look like a professional TypeScript codebase.",
              },
            ],

            testingInsights: {
              coreReality:
                "TypeScript bugs usually come from rushed refactors and hidden any types.",
              checklist: [
                "Does the project pass strict mode?",
                "Are API responses fully typed?",
                "Is nullable state handled with unions?",
                "Are shared models reused everywhere?",
              ],
              goldenRule:
                "If TypeScript feels silent, you are not using it fully.",
            },

            interviewInsights: {
              commonQuestions: [
                "How do you migrate a React app from JS to TS?",
                "How do you type API services?",
                "How do you avoid any in large projects?",
                "How do you structure types in React apps?",
                "What problems does TypeScript solve in React?",
              ],
              strongAnswerPattern:
                "Explain migration steps + show before/after typing improvement",
              redFlag: "Using any or disabling strict mode to move faster.",
            },

            editorGuidance: {
              careerMindset: [
                "TypeScript is about confidence, not verbosity",
                "Strong typing reduces future bugs, not speed today",
                "Shared types are architectural decisions",
              ],
              seniorAdvice:
                "If your app works without TypeScript, refactor it until TS actually protects you.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "This mini project is a turning point in the roadmap. Here you combine React + TypeScript the same way it is done in real production codebases. The goal is not UI beauty, but type safety, structure, and scalability.",

              projectScope: {
                title: "Project Scope",
                explanation:
                  "You will either convert an existing JavaScript React project to TypeScript or build a fresh small app using TypeScript from day one.",
                suggestedProjects: [
                  "Type-safe Todo App",
                  "User Dashboard with API data",
                  "Product List with Filters",
                ],
                realWorldNote:
                  "Most companies expect you to refactor legacy JS code into TypeScript",
              },

              typeSafeComponents: {
                title: "Building Type-safe Components",
                explanation:
                  "Every component must have properly typed props, state, and events.",
                conceptsUsed: [
                  "Props typing using interface/type",
                  "Typed useState",
                  "Typed event handlers",
                ],
                exampleLogic: `
type Todo = {
  id: number;
  title: string;
  completed: boolean;
};

type TodoItemProps = {
  todo: Todo;
  onToggle: (id: number) => void;
};

const TodoItem = ({ todo, onToggle }: TodoItemProps) => {
  return (
    <li onClick={() => onToggle(todo.id)}>
      {todo.title}
    </li>
  );
};
      `,
                bestPractice: "Avoid 'any' completely — treat it as a bug",
              },

              globalTypes: {
                title: "Global Types & Shared Models",
                explanation:
                  "Shared types should live in a central place so the same model is reused across components, hooks, and services.",
                exampleStructure: `
src/
 ├─ types/
 │   ├─ todo.ts
 │   ├─ user.ts
 │   └─ api.ts
      `,
                exampleLogic: `
export type ApiResponse<T> = {
  data: T;
  error?: string;
};
      `,
                benefit: "Single source of truth for data models",
              },

              typedApiServices: {
                title: "Typed API Service Layer",
                explanation:
                  "API services must return typed data so components never guess response shape.",
                conceptsUsed: [
                  "Generics",
                  "Axios / Fetch typing",
                  "Promise return types",
                ],
                exampleLogic: `
import { ApiResponse } from '../types/api';

export const fetchTodos = async (): Promise<ApiResponse<Todo[]>> => {
  const res = await fetch('/api/todos');
  return res.json();
};
      `,
                interviewInsight:
                  "Typed APIs drastically reduce runtime crashes",
              },

              refactoringJsToTs: {
                title: "Refactoring JavaScript to TypeScript",
                explanation:
                  "Refactoring is done incrementally — file by file, component by component.",
                refactorSteps: [
                  "Rename .js to .tsx",
                  "Fix TypeScript errors",
                  "Add explicit types",
                  "Remove implicit any",
                ],
                exampleLogic: `
const [todos, setTodos] = useState<Todo[]>([]);
      `,
                realWorldNote: "This is exactly how legacy codebases migrate",
              },

              bestPractices: {
                title: "Production Best Practices",
                practices: [
                  "No any type",
                  "Reusable shared types",
                  "Typed custom hooks",
                  "Strict TypeScript mode",
                  "Consistent naming conventions",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Using any to fix errors quickly",
                  issue: "Kills TypeScript benefits",
                  fix: "Understand the error and type correctly",
                },
                {
                  mistake: "Duplicating types",
                  issue: "Inconsistent data models",
                  fix: "Create shared global types",
                },
                {
                  mistake: "Typing everything inline",
                  issue: "Unreadable components",
                  fix: "Extract types into separate files",
                },
              ],

              interviewQuestions: [
                "How do you migrate a React app from JS to TS?",
                "How do you type API responses?",
                "Difference between interface and type?",
                "How do you avoid using any?",
                "How does TypeScript improve React apps?",
              ],
            },

            tasks: [
              "Convert existing React project to TypeScript",
              "Type all components and props",
              "Create typed API service layer",
            ],

            examples: ["Type-safe todo app", "Typed dashboard"],

            useCases: [
              "Job-ready frontend projects",
              "Production-grade applications",
              "Interview-ready TypeScript knowledge",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Build confidence in React + TypeScript",
              "Write scalable and maintainable frontend code",
              "Refactor legacy JavaScript safely",
              "Match real-world frontend job expectations",
            ],
          },
        ],
      },
      {
        week: 26,
        weekGoal:
          "Learn frontend testing fundamentals and adopt Test-Driven Development practices",
        weekOutcome:
          "Ability to write reliable, test-covered React applications and confidently handle testing interviews",
        miniProject: "Test-Covered React Application",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Introduction to Testing & Jest Basics",
            subTopics: [
              "Why testing matters in frontend jobs",
              "Types of testing (unit, integration, e2e)",
              "What is Jest",
              "Jest setup in React projects",
              "Running and organizing tests",
            ],
            explanation:
              "Testing ensures your application works as expected and prevents bugs from reaching production. Jest is the most widely used testing framework in the React ecosystem.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Testing Mindset Bootcamp",
                description:
                  "Shift mindset from 'code chal raha hai' to 'code safe hai'.",
                tasks: [
                  "Identify 3 critical flows in your app that must never break",
                  "Decide which ones need unit vs integration tests",
                  "Write test cases only for behavior, not implementation",
                ],
                outcome:
                  "You’ll understand what is worth testing and what is not.",
              },
              {
                id: "pt-2",
                title: "Jest Hands-on Setup Drill",
                description:
                  "Setup Jest manually and understand what happens behind the scenes.",
                tasks: [
                  "Install Jest without CRA auto-setup",
                  "Run tests via CLI",
                  "Intentionally break a test and debug the failure",
                ],
                qualityRule:
                  "You must explain why the test failed, not just fix it.",
              },
            ],

            testingInsights: {
              coreReality:
                "Most production bugs happen because code was changed without tests.",
              mindsetShift: [
                "Tests are not for today, they are for future refactors",
                "Passing tests = confidence to ship",
                "No tests = fear-driven development",
              ],
              goldenRule:
                "If you’re scared to refactor, you don’t have enough tests.",
            },

            interviewInsights: {
              commonQuestions: [
                "Why is testing important in frontend?",
                "What should be tested and what should not?",
                "Difference between unit and integration testing?",
                "How do tests help during refactoring?",
              ],
              strongAnswerPattern:
                "Explain real bug → how test could have prevented it → business impact",
              redFlag: "I don’t usually write tests, QA handles that.",
            },

            editorGuidance: {
              careerMindset: [
                "Testing is a developer responsibility, not QA only",
                "Tests are part of feature completion",
                "Confidence comes from coverage, not hope",
              ],
              seniorAdvice:
                "If your PR doesn’t include tests, the feature is not done.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Testing is a critical skill for frontend developers working in production environments. Companies expect developers to write tests to ensure features work correctly, prevent regressions, and refactor code safely.",

              whyTestingMatters: {
                title: "Why Testing Matters",
                explanation:
                  "Without tests, even small changes can break existing functionality. Tests act as a safety net.",
                realWorldImpact: [
                  "Confidence while refactoring code",
                  "Fewer production bugs",
                  "Easier collaboration in teams",
                ],
                interviewInsight:
                  "Interviewers often ask how you ensure code quality — testing is a key answer",
              },

              testingTypes: {
                title: "Types of Frontend Testing",
                unit: {
                  meaning:
                    "Tests individual functions or components in isolation",
                  example: "Testing a utility function or reducer",
                },
                integration: {
                  meaning: "Tests how multiple units work together",
                  example: "Form submission with validation",
                },
                e2e: {
                  meaning: "Tests full user flow in real browser",
                  example: "Login → dashboard → logout",
                },
              },

              jestOverview: {
                title: "What is Jest?",
                explanation:
                  "Jest is a JavaScript testing framework created by Facebook and widely used with React.",
                features: [
                  "Zero-config setup (mostly)",
                  "Fast test execution",
                  "Built-in assertions and mocking",
                  "Snapshot testing support",
                ],
              },

              jestSetup: {
                title: "Jest Setup in React",
                explanation:
                  "Most React setups (CRA, Vite + RTL) come with Jest preconfigured or easy to add.",
                steps: [
                  "Install Jest and testing dependencies",
                  "Configure test environment",
                  "Create test files",
                ],
                exampleLogic: `
npm install --save-dev jest
npm test
      `,
              },

              testStructure: {
                title: "Test File Structure",
                explanation:
                  "Tests are usually placed next to the file they test or inside __tests__ folders.",
                conventions: [
                  "file.test.js",
                  "file.spec.js",
                  "__tests__/file.test.js",
                ],
                bestPractice:
                  "Keep tests close to source code for maintainability",
              },

              commonMistakes: [
                {
                  mistake: "Not writing tests at all",
                  issue: "High risk of bugs during refactor",
                  fix: "Start with critical logic tests",
                },
                {
                  mistake: "Testing implementation details",
                  issue: "Brittle tests",
                  fix: "Test behavior, not internals",
                },
              ],

              interviewQuestions: [
                "Why is testing important in frontend?",
                "Difference between unit and integration tests?",
                "Why is Jest popular with React?",
                "How do tests help during refactoring?",
              ],
            },

            tasks: [
              "Install Jest in a project",
              "Run existing test files",
              "Understand test file naming conventions",
            ],

            examples: [
              "test('adds two numbers', () => { expect(1 + 2).toBe(3); })",
            ],

            useCases: [
              "Bug prevention",
              "Safer refactoring",
              "Production stability",
            ],

            editorType: "javascript",
            objectives: [
              "Understand why testing is critical in real-world frontend applications",
              "Learn different types of frontend testing (unit, integration, e2e)",
              "Get comfortable with Jest syntax and test structure",
              "Be able to run, read, and debug test cases confidently",
              "Build testing mindset required for frontend job interviews",
            ],
          },

          {
            day: 2,
            topic: "Writing Unit Tests & Test Structure",

            subTopics: [
              "Arrange, Act, Assert pattern",
              "Matchers (toBe, toEqual, toContain)",
              "Testing pure functions",
              "Edge case testing",
              "Test readability",
            ],

            explanation:
              "Unit tests focus on testing small, isolated pieces of logic. A clear structure and meaningful assertions make tests reliable, readable, and easy to maintain.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "AAA Pattern Mastery Drill",
                description:
                  "Practice writing tests that any developer can read and understand instantly.",
                tasks: [
                  "Rewrite 3 existing tests strictly using Arrange–Act–Assert",
                  "Remove comments and check if test is still readable",
                  "Rename test descriptions to explain behavior clearly",
                ],
                outcome:
                  "You’ll naturally write clean, interview-ready unit tests.",
              },
              {
                id: "pt-2",
                title: "Edge Case Hunter",
                description: "Train your brain to think like production bugs.",
                tasks: [
                  "List all possible edge cases for one utility function",
                  "Write at least 5 failing tests first",
                  "Fix implementation only after tests fail",
                ],
                qualityRule: "If a test never fails, it’s probably useless.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Happy path tests give confidence, edge case tests give safety.",
              mindsetShift: [
                "If it can break, it will break in production",
                "Tests are executable documentation",
                "Readable tests outlive clever code",
              ],
              goldenRule:
                "A good test explains WHAT should happen, not HOW it happens.",
            },

            interviewInsights: {
              commonQuestions: [
                "Explain Arrange–Act–Assert pattern",
                "Difference between toBe and toEqual?",
                "What makes a function easy to unit test?",
                "How do you decide edge cases?",
              ],
              strongAnswerTip:
                "Use a real example where missing edge case caused a bug.",
              redFlag: "I only test when time permits.",
            },

            editorGuidance: {
              careerMindset: [
                "Tests are part of feature development, not an extra task",
                "Readable tests signal senior-level thinking",
                "Edge cases separate juniors from professionals",
              ],
              seniorAdvice:
                "If your test name doesn’t explain behavior, rewrite it.",
            },

            content: {
              introduction:
                "Unit testing is the foundation of frontend testing. In real-world applications, most bugs originate from small logic errors. Unit tests ensure that individual functions behave correctly before they are composed into larger systems.",

              aaaPattern: {
                title: "Arrange – Act – Assert (AAA) Pattern",
                explanation:
                  "AAA is the most common and recommended structure for writing clean unit tests.",
                steps: [
                  "Arrange: Prepare inputs and setup data",
                  "Act: Execute the function or logic",
                  "Assert: Verify the expected output",
                ],
                exampleLogic: `
test('adds two numbers', () => {
  // Arrange
  const a = 2;
  const b = 3;

  // Act
  const result = sum(a, b);

  // Assert
  expect(result).toBe(5);
});
      `,
                interviewNote:
                  "Interviewers look for structured tests, not just passing tests",
              },

              matchers: {
                title: "Jest Matchers",
                explanation:
                  "Matchers define how you compare actual and expected values.",
                commonMatchers: [
                  "toBe → strict equality",
                  "toEqual → deep object comparison",
                  "toContain → array/string containment",
                ],
                exampleLogic: `
expect(total).toBe(100);
expect(user).toEqual({ name: 'John' });
expect(items).toContain('apple');
      `,
                bestPractice:
                  "Choose the matcher based on data type, not convenience",
              },

              pureFunctions: {
                title: "Testing Pure Functions",
                explanation:
                  "Pure functions always return the same output for the same input and have no side effects.",
                whyEasy:
                  "They are predictable, fast to test, and ideal for unit testing.",
                exampleLogic: `
function multiply(a, b) {
  return a * b;
}

expect(multiply(2, 4)).toBe(8);
      `,
                realWorldUsage: "Business rules, calculations, validators",
              },

              edgeCases: {
                title: "Edge Case Testing",
                explanation:
                  "Edge cases test how your code behaves at boundaries or unexpected inputs.",
                examples: [
                  "Empty arrays",
                  "Null or undefined values",
                  "Zero, negative numbers",
                ],
                exampleLogic: `
expect(sum([], [])).toBe(0);
expect(getName(null)).toBe('Guest');
      `,
                whyImportant: "Most production bugs happen in edge cases",
              },

              readability: {
                title: "Test Readability & Maintainability",
                rules: [
                  "One expectation per behavior",
                  "Clear test descriptions",
                  "Avoid complex logic inside tests",
                ],
                badExample: "test('test1', () => {})",
                goodExample:
                  "test('returns total price including tax', () => {})",
              },

              commonMistakes: [
                {
                  mistake: "Testing implementation instead of behavior",
                  issue: "Tests break during refactor",
                  fix: "Test output, not internal logic",
                },
                {
                  mistake: "Too many assertions in one test",
                  issue: "Hard to debug failures",
                  fix: "Split into multiple focused tests",
                },
                {
                  mistake: "Ignoring edge cases",
                  issue: "Hidden production bugs",
                  fix: "Always test boundaries",
                },
              ],

              interviewQuestions: [
                "What is the AAA pattern?",
                "Difference between toBe and toEqual?",
                "What is a pure function?",
                "Why are edge cases important?",
              ],
            },

            tasks: [
              "Write unit tests for utility functions",
              "Follow Arrange–Act–Assert pattern",
              "Test edge cases explicitly",
              "Refactor logic to make it testable",
            ],

            examples: [
              "expect(sum(2, 3)).toBe(5)",
              "expect(user.name).toEqual('John')",
            ],

            useCases: [
              "Business logic testing",
              "Utility libraries",
              "Core calculations",
              "Interview coding rounds",
            ],

            editorType: "javascript",

            estimatedTime: "60–90 minutes",

            objectives: [
              "Understand unit testing mindset",
              "Write clean and structured Jest tests",
              "Use correct matchers for different data types",
              "Handle edge cases confidently",
              "Be interview-ready for testing questions",
            ],
          },
          {
            day: 3,
            topic: "React Testing Library – Components Testing",

            subTopics: [
              "React Testing Library philosophy",
              "render and screen",
              "Query methods (getByText, getByRole)",
              "Testing component rendering",
              "Testing props-based behavior",
            ],

            explanation:
              "React Testing Library (RTL) focuses on testing components from the user's perspective, ensuring tests remain reliable even when implementation details change.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "User-Centric Testing Drill",
                description:
                  "Rewrite component tests to think like a real user, not a developer.",
                tasks: [
                  "Remove all className and testId based queries",
                  "Rewrite tests using getByRole and visible text",
                  "Read test names aloud and verify they describe user behavior",
                ],
                outcome: "You’ll naturally write refactor-proof RTL tests.",
              },
              {
                id: "pt-2",
                title: "Props Matrix Testing",
                description:
                  "Systematically test how a component behaves with different props.",
                tasks: [
                  "List all props of a component",
                  "Create tests for at least 3 prop combinations",
                  "Ensure each test checks only one visible behavior",
                ],
                qualityRule: "One prop change → one clear expectation.",
              },
            ],

            testingInsights: {
              coreTruth:
                "If a user can’t see it, your test probably shouldn’t test it.",
              mindsetShift: [
                "UI behavior matters more than implementation",
                "Accessibility-friendly queries = better tests",
                "Refactors should not break good tests",
              ],
              goldenRule:
                "Write tests that survive refactors, not tests that mirror code.",
            },

            interviewInsights: {
              commonQuestions: [
                "What is React Testing Library philosophy?",
                "Why is getByRole preferred?",
                "Why should we avoid testing internal state?",
                "How RTL differs from Enzyme?",
              ],
              strongAnswerTip:
                "Say: RTL tests user behavior, not component internals.",
              redFlag: "I usually test class names or state directly.",
            },

            editorGuidance: {
              careerMindset: [
                "Testing UI like a user is a senior-level habit",
                "Accessibility-first tests reflect production quality",
                "Good RTL tests rarely change over time",
              ],
              seniorAdvice:
                "If you need testIds everywhere, rethink your test approach.",
            },

            content: {
              introduction:
                "React Testing Library is designed to test what users see and interact with, not how components are internally built. This mindset leads to more stable and meaningful tests compared to implementation-focused testing.",

              philosophy: {
                title: "React Testing Library Philosophy",
                explanation:
                  "RTL encourages testing behavior instead of implementation details.",
                corePrinciples: [
                  "Test what the user sees",
                  "Test how the user interacts",
                  "Avoid testing internal component logic",
                ],
                interviewNote:
                  "RTL tests rarely break during refactors if UI behavior remains same",
              },

              renderAndScreen: {
                title: "render() & screen",
                explanation:
                  "render mounts a React component into a virtual DOM for testing, while screen provides global access to query methods.",
                exampleLogic: `
render(<Button label="Click me" />);
expect(screen.getByText('Click me')).toBeInTheDocument();
      `,
                bestPractice: "Prefer screen over destructuring render result",
              },

              queryMethods: {
                title: "Query Methods",
                explanation:
                  "RTL provides different queries to find elements the way users do.",
                commonQueries: [
                  "getByText → visible text",
                  "getByRole → accessibility roles",
                  "getByLabelText → form inputs",
                ],
                exampleLogic: `
screen.getByRole('button', { name: /click/i });
screen.getByText('Submit');
      `,
                bestPractice:
                  "Prefer getByRole for accessibility-friendly tests",
              },

              componentRendering: {
                title: "Testing Component Rendering",
                explanation:
                  "Ensure that components render correctly with given props.",
                exampleLogic: `
render(<Greeting name="John" />);
expect(screen.getByText('Hello John')).toBeInTheDocument();
      `,
                realWorldUsage:
                  "Testing headings, buttons, labels, and UI visibility",
              },

              propsBehavior: {
                title: "Testing Props-Based Behavior",
                explanation:
                  "Components often change UI based on props. Tests should verify these variations.",
                exampleLogic: `
render(<Button disabled />);
expect(screen.getByRole('button')).toBeDisabled();
      `,
                interviewInsight:
                  "Props-based testing validates component reusability",
              },

              commonMistakes: [
                {
                  mistake: "Testing class names or internal state",
                  issue: "Tests break on refactor",
                  fix: "Test visible UI instead",
                },
                {
                  mistake: "Using querySelector",
                  issue: "Breaks RTL philosophy",
                  fix: "Use screen queries",
                },
                {
                  mistake: "Over-mocking components",
                  issue: "Unrealistic tests",
                  fix: "Test real user behavior",
                },
              ],

              interviewQuestions: [
                "What is React Testing Library philosophy?",
                "Difference between getByText and getByRole?",
                "Why avoid testing implementation details?",
                "Why screen is preferred over container?",
              ],
            },

            tasks: [
              "Test simple React components using RTL",
              "Verify text rendering and visibility",
              "Test conditional UI rendering",
              "Test different props behavior",
            ],

            examples: [
              "render(<Button label='Save' />)",
              "expect(screen.getByText('Save')).toBeInTheDocument()",
            ],

            useCases: [
              "UI components",
              "Design systems",
              "Reusable component libraries",
              "Production React apps",
            ],

            editorType: "react",

            estimatedTime: "60–90 minutes",

            objectives: [
              "Understand RTL testing philosophy",
              "Write user-centric component tests",
              "Use correct query methods",
              "Avoid implementation-based testing",
              "Be interview-ready for React testing",
            ],
          },
          {
            day: 4,
            topic: "Testing User Interactions & Async Logic",

            subTopics: [
              "fireEvent and userEvent",
              "Testing button clicks",
              "Form input testing",
              "Testing async behavior",
              "waitFor and findBy queries",
            ],

            explanation:
              "Frontend apps are interactive and asynchronous. Testing user actions and async flows ensures the UI behaves correctly for real users under real conditions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real User Flow Testing",
                description:
                  "Test a complete user flow instead of isolated interactions.",
                tasks: [
                  "Render a form-based component (login / signup)",
                  "Simulate typing in all inputs using userEvent",
                  "Submit the form and wait for success UI",
                  "Assert final UI state only (not internal state)",
                ],
                outcome: "You’ll think in user journeys, not event handlers.",
              },
              {
                id: "pt-2",
                title: "Async Failure & Recovery Test",
                description:
                  "Test how UI behaves during loading, success, and failure states.",
                tasks: [
                  "Mock API call with delayed promise",
                  "Assert loading state is visible",
                  "Assert success OR error UI after resolve/reject",
                  "Ensure test uses findBy / waitFor only",
                ],
                qualityRule:
                  "If test passes without await, it’s probably wrong.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Async UI testing is about waiting for behavior, not forcing time.",
              mindsetShift: [
                "User actions are async by nature",
                "Tests should follow UI timing, not assumptions",
                "Flaky tests mean incorrect waiting strategy",
              ],
              goldenRule: "Never guess when UI updates — always wait for it.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between fireEvent and userEvent?",
                "How do you test async API-driven UI?",
                "Why avoid setTimeout in tests?",
                "When to use waitFor vs findBy?",
              ],
              strongAnswerTip:
                "Say: userEvent simulates real user behavior and async flows.",
              redFlag: "I usually add setTimeout to wait for UI.",
            },

            editorGuidance: {
              careerMindset: [
                "Async testing separates juniors from seniors",
                "Stable tests reflect production thinking",
                "Waiting correctly is more important than asserting quickly",
              ],
              seniorAdvice:
                "If your test fails randomly, your waiting strategy is wrong.",
            },

            content: {
              introduction:
                "Most real-world bugs occur due to broken user interactions or async behavior. React Testing Library focuses on simulating real user behavior like clicking buttons, typing into inputs, and waiting for async UI updates.",

              fireEventVsUserEvent: {
                title: "fireEvent vs userEvent",
                explanation:
                  "fireEvent triggers events directly, while userEvent simulates real user interactions more accurately.",
                comparison: [
                  "fireEvent → low-level event trigger",
                  "userEvent → high-level user simulation",
                ],
                exampleLogic: `
fireEvent.click(button);
await userEvent.click(button);
      `,
                bestPractice: "Prefer userEvent for most interaction tests",
                interviewNote:
                  "userEvent handles focus, blur, and keyboard behavior automatically",
              },

              buttonClicks: {
                title: "Testing Button Clicks",
                explanation:
                  "Buttons often trigger state changes, API calls, or navigation. Tests must verify correct behavior after clicks.",
                conceptsUsed: [
                  "userEvent.click",
                  "State update verification",
                  "UI change assertions",
                ],
                exampleLogic: `
render(<Counter />);
await userEvent.click(screen.getByText('+'));
expect(screen.getByText('1')).toBeInTheDocument();
      `,
              },

              formInputTesting: {
                title: "Form Input Testing",
                explanation:
                  "Forms are critical user interaction points. Tests should simulate typing and submission.",
                conceptsUsed: [
                  "userEvent.type",
                  "Input value assertions",
                  "Form submit testing",
                ],
                exampleLogic: `
await userEvent.type(input, 'john@example.com');
expect(input).toHaveValue('john@example.com');
      `,
                realWorldNote:
                  "Login, signup, checkout flows depend heavily on correct form testing",
              },

              asyncBehavior: {
                title: "Testing Async Behavior",
                explanation:
                  "Async UI updates occur after API calls, timers, or delayed state updates.",
                conceptsUsed: [
                  "Async rendering",
                  "Promises and state updates",
                  "Waiting for UI changes",
                ],
                exampleLogic: `
render(<UserProfile />);
expect(screen.getByText('Loading...')).toBeInTheDocument();
      `,
              },

              waitForAndFindBy: {
                title: "waitFor & findBy Queries",
                explanation:
                  "RTL provides async utilities to wait for UI changes instead of using timeouts.",
                differences: [
                  "findBy → waits and returns element",
                  "waitFor → waits for assertion to pass",
                ],
                exampleLogic: `
await screen.findByText('Success');
await waitFor(() => {
  expect(screen.getByText('Loaded')).toBeInTheDocument();
});
      `,
                bestPractice:
                  "Avoid setTimeout in tests — always use RTL async utilities",
              },

              commonMistakes: [
                {
                  mistake: "Using fireEvent everywhere",
                  issue: "Unrealistic interaction testing",
                  fix: "Use userEvent for user flows",
                },
                {
                  mistake: "Using setTimeout in tests",
                  issue: "Flaky tests",
                  fix: "Use waitFor or findBy",
                },
                {
                  mistake: "Asserting too early",
                  issue: "Tests fail intermittently",
                  fix: "Wait for async UI updates",
                },
              ],

              interviewQuestions: [
                "Difference between fireEvent and userEvent?",
                "When should you use findBy queries?",
                "Why is waitFor preferred over setTimeout?",
                "How do you test async API-driven UI?",
              ],
            },

            tasks: [
              "Test button click interactions",
              "Test form input and submission",
              "Test async UI updates after API calls",
              "Use findBy and waitFor correctly",
            ],

            examples: [
              "await userEvent.click(button)",
              "await userEvent.type(input, 'text')",
              "await screen.findByText('Success')",
            ],

            useCases: [
              "Forms",
              "API-driven UI",
              "Interactive dashboards",
              "Real-world production apps",
            ],

            editorType: "react",

            estimatedTime: "90–120 minutes",

            objectives: [
              "Simulate real user interactions in tests",
              "Understand async UI testing patterns",
              "Write stable, non-flaky tests",
              "Test forms and API-driven flows",
              "Be interview-ready for React testing",
            ],
          },
          {
            day: 5,
            topic: "Mocking, Spies & Coverage",

            subTopics: [
              "Mocking functions",
              "Mocking API calls",
              "Jest spies",
              "Snapshot testing",
              "Coverage reports",
            ],

            explanation:
              "Mocks and spies allow you to isolate components and logic during testing by replacing real dependencies like APIs, timers, or external services.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "API Mocking Deep Dive",
                description:
                  "Practice isolating UI from backend using controlled API mocks.",
                tasks: [
                  "Mock an API success response and assert success UI",
                  "Mock API failure (reject promise) and assert error UI",
                  "Ensure no real network call is made",
                ],
                outcome:
                  "You’ll confidently test API-driven components without backend dependency.",
              },
              {
                id: "pt-2",
                title: "Spies vs Mocks Practice",
                description:
                  "Understand when to spy on real functions and when to fully mock.",
                tasks: [
                  "Spy on console.log or utility function",
                  "Assert it was called with correct arguments",
                  "Restore original implementation after test",
                ],
                qualityRule:
                  "Spy when behavior matters, mock when isolation matters.",
              },
              {
                id: "pt-3",
                title: "Coverage Reality Check",
                description:
                  "Learn to read coverage reports like a senior engineer.",
                tasks: [
                  "Generate coverage report",
                  "Identify untested branches",
                  "Write one meaningful test to improve coverage",
                ],
                warning:
                  "Never write useless tests just to increase percentage.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Mocks exist to isolate behavior, not to fake reality blindly.",
              mindsetShift: [
                "Fast tests beat realistic tests",
                "Isolation improves reliability",
                "Coverage numbers don’t equal quality",
              ],
              goldenRule: "Mock the dependency, test your logic.",
            },

            interviewInsights: {
              commonQuestions: [
                "Difference between jest.fn and jest.spyOn?",
                "When should you mock APIs?",
                "Is 100% test coverage required?",
                "When should snapshot tests be avoided?",
              ],
              strongAnswerTip: "Say: coverage is a signal, not a goal.",
              redFlag: "I try to get 100% coverage no matter what.",
            },

            editorGuidance: {
              careerMindset: [
                "Senior engineers care about test intent, not test count",
                "Mocks are tools, not shortcuts",
                "Readable tests matter more than clever tests",
              ],
              seniorAdvice:
                "If your test mocks everything, it probably tests nothing.",
            },

            content: {
              introduction:
                "In real-world frontend applications, components depend on APIs, browser APIs, and utility functions. Mocking allows you to test behavior without relying on real implementations, making tests faster, predictable, and reliable.",

              mockingFunctions: {
                title: "Mocking Functions with jest.fn",
                explanation:
                  "jest.fn() creates a mock function that can track calls, arguments, and return controlled values.",
                conceptsUsed: [
                  "Mock functions",
                  "Call tracking",
                  "Controlled return values",
                ],
                exampleLogic: `
const mockFn = jest.fn();
mockFn('test');
expect(mockFn).toHaveBeenCalledWith('test');
      `,
                interviewNote:
                  "Mock functions are essential for isolating business logic",
              },

              mockingApiCalls: {
                title: "Mocking API Calls",
                explanation:
                  "API calls should never hit real servers during unit tests. Mocking ensures consistent test results.",
                conceptsUsed: [
                  "Mocking fetch or axios",
                  "Resolved and rejected promises",
                  "Testing success and failure flows",
                ],
                exampleLogic: `
global.fetch = jest.fn(() =>
  Promise.resolve({
    json: () => Promise.resolve({ name: 'John' }),
  })
);
      `,
                realWorldNote:
                  "Most production apps mock APIs using jest mocks or MSW",
              },

              jestSpies: {
                title: "Jest Spies (jest.spyOn)",
                explanation:
                  "Spies observe existing functions without replacing their implementation completely.",
                conceptsUsed: [
                  "Spying on methods",
                  "Verifying function calls",
                  "Restoring original implementation",
                ],
                exampleLogic: `
const spy = jest.spyOn(console, 'log');
console.log('Hello');
expect(spy).toHaveBeenCalled();
spy.mockRestore();
      `,
                bestPractice: "Always restore spies after test completion",
              },

              snapshotTesting: {
                title: "Snapshot Testing",
                explanation:
                  "Snapshots capture UI output and help detect unintended UI changes over time.",
                conceptsUsed: [
                  "Snapshot creation",
                  "Snapshot updates",
                  "Regression detection",
                ],
                exampleLogic: `
const tree = renderer.create(<Button />).toJSON();
expect(tree).toMatchSnapshot();
      `,
                warning: "Overusing snapshots can make tests brittle",
              },

              coverageReports: {
                title: "Test Coverage Reports",
                explanation:
                  "Coverage reports show which parts of the codebase are tested.",
                conceptsUsed: [
                  "Statement coverage",
                  "Branch coverage",
                  "Function coverage",
                ],
                exampleLogic: `
npm test -- --coverage
      `,
                bestPractice:
                  "High coverage is good, but meaningful tests matter more",
              },

              commonMistakes: [
                {
                  mistake: "Hitting real APIs in tests",
                  issue: "Slow and flaky tests",
                  fix: "Always mock API calls",
                },
                {
                  mistake: "Overusing snapshot tests",
                  issue: "Hard-to-maintain tests",
                  fix: "Use snapshots only for stable UI",
                },
                {
                  mistake: "Not restoring spies",
                  issue: "Side effects across tests",
                  fix: "Use mockRestore()",
                },
              ],

              interviewQuestions: [
                "What is the difference between mocks and spies?",
                "Why should APIs be mocked in tests?",
                "What does test coverage measure?",
                "Is 100% coverage always necessary?",
              ],
            },

            tasks: [
              "Mock API calls in tests",
              "Use jest.fn to mock functions",
              "Spy on existing methods using jest.spyOn",
              "Generate and analyze coverage report",
            ],

            examples: [
              "jest.fn(() => Promise.resolve(data))",
              "jest.spyOn(console, 'log')",
              "npm test -- --coverage",
            ],

            useCases: [
              "API-heavy applications",
              "Error handling tests",
              "Regression prevention",
              "Enterprise-grade testing",
            ],

            editorType: "javascript",

            estimatedTime: "90–120 minutes",

            objectives: [
              "Isolate components and logic using mocks",
              "Test API success and failure scenarios",
              "Understand spies vs mocks clearly",
              "Use snapshot testing responsibly",
              "Analyze and improve test coverage",
            ],
          },
          {
            day: 6,
            topic: "Test-Driven Development (TDD)",

            subTopics: [
              "What is TDD",
              "Red → Green → Refactor cycle",
              "Writing tests before code",
              "Benefits of TDD",
              "Common TDD mistakes",
            ],

            explanation:
              "TDD flips the traditional workflow by writing tests before implementation, leading to cleaner, well-designed, and more reliable code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Pure TDD Exercise (No Peeking)",
                description:
                  "Practice true TDD by writing tests without thinking about implementation.",
                tasks: [
                  "Write test cases for a utility function (no implementation)",
                  "Run tests and observe failures (Red)",
                  "Implement minimal logic to pass tests (Green)",
                  "Refactor implementation without changing tests",
                ],
                outcome:
                  "You experience real Red → Green → Refactor discipline.",
              },
              {
                id: "pt-2",
                title: "TDD for Edge Cases",
                description:
                  "Learn how TDD naturally uncovers edge cases early.",
                tasks: [
                  "Write test for normal input",
                  "Add test for edge case (null, empty, negative)",
                  "Update implementation only when test fails",
                ],
                qualityRule:
                  "If you didn’t change code after adding test, test is useless.",
              },
              {
                id: "pt-3",
                title: "Refactor with Confidence",
                description:
                  "Prove to yourself that refactoring is safe with TDD.",
                tasks: [
                  "Refactor a working implementation",
                  "Simplify logic or improve readability",
                  "Ensure all tests remain green",
                ],
                mindset: "Tests are a safety net, not a burden.",
              },
            ],

            testingInsights: {
              coreTruth: "TDD is not about tests — it’s about design clarity.",
              mindsetShift: [
                "Requirements before implementation",
                "Behavior before code structure",
                "Confidence over speed",
              ],
              goldenRule: "If it’s hard to test, the design is probably wrong.",
            },

            interviewInsights: {
              strongSignals: [
                "Explaining Red-Green-Refactor with example",
                "Admitting TDD slows you initially but saves time later",
                "Knowing when NOT to use TDD",
              ],
              idealAnswer:
                "TDD improves design and refactoring confidence, not just test coverage.",
              redFlag: "I always write code first and tests later.",
            },

            editorGuidance: {
              careerMindset: [
                "TDD forces you to think like a user of your own code",
                "Refactoring without fear is a senior skill",
                "Passing tests are permission to improve code",
              ],
              seniorAdvice:
                "If you fear refactoring, you don’t trust your tests.",
            },

            content: {
              introduction:
                "Test-Driven Development is a disciplined development approach where tests guide design decisions. Instead of writing code first, developers write tests that define expected behavior, then implement just enough code to make tests pass.",

              whatIsTDD: {
                title: "What is Test-Driven Development",
                explanation:
                  "TDD is a development methodology where tests are written before the actual code. The code is then written to satisfy those tests.",
                conceptsUsed: [
                  "Behavior-driven thinking",
                  "Incremental development",
                  "Test-first mindset",
                ],
                realWorldNote:
                  "TDD is widely used in fintech, healthcare, and mission-critical systems",
              },

              redGreenRefactor: {
                title: "Red → Green → Refactor Cycle",
                explanation:
                  "TDD follows a strict three-step loop that ensures correctness and clean design.",
                steps: [
                  "Red: Write a test that fails",
                  "Green: Write minimum code to pass the test",
                  "Refactor: Improve code without changing behavior",
                ],
                exampleLogic: `
test('adds numbers', () => {
  expect(add(2, 3)).toBe(5);
});

// initially fails (Red)
// implement add()
// test passes (Green)
// refactor implementation safely
      `,
                bestPractice:
                  "Never skip the refactor step — that's where code quality improves",
              },

              writingTestsFirst: {
                title: "Writing Tests Before Code",
                explanation:
                  "Writing tests first forces you to think about requirements, inputs, outputs, and edge cases before implementation.",
                conceptsUsed: [
                  "Requirement clarity",
                  "API-first thinking",
                  "Edge case awareness",
                ],
                interviewInsight:
                  "TDD often results in better function signatures and simpler logic",
              },

              benefitsOfTDD: {
                title: "Benefits of TDD",
                points: [
                  "Higher code reliability",
                  "Fewer bugs in production",
                  "Confidence during refactoring",
                  "Better code design",
                  "Self-documenting tests",
                ],
                realWorldImpact:
                  "Teams using TDD ship fewer regressions and refactor more confidently",
              },

              commonMistakes: [
                {
                  mistake: "Writing large tests upfront",
                  issue: "Hard to debug failures",
                  fix: "Write small, focused tests",
                },
                {
                  mistake: "Skipping refactoring",
                  issue: "Messy code despite passing tests",
                  fix: "Always refactor after green",
                },
                {
                  mistake: "Testing implementation details",
                  issue: "Brittle tests",
                  fix: "Test behavior, not internals",
                },
              ],

              whenToUseTDD: {
                title: "When TDD Works Best",
                suitableFor: [
                  "Business-critical logic",
                  "Utility libraries",
                  "Reusable components",
                  "Long-term maintained codebases",
                ],
                notIdealFor: ["Quick prototypes", "Experimental UI spikes"],
              },

              interviewQuestions: [
                "What is the Red-Green-Refactor cycle?",
                "Is TDD always necessary?",
                "How does TDD improve design?",
                "Can TDD slow down development initially?",
              ],
            },

            tasks: [
              "Write failing tests before implementation",
              "Implement minimum code to pass tests",
              "Refactor code safely after tests pass",
              "Practice full Red-Green-Refactor cycle",
            ],

            examples: [
              "Write test → see it fail → write implementation",
              "Refactor without breaking tests",
            ],

            useCases: [
              "Critical features",
              "Scalable applications",
              "Long-term maintainability",
              "Enterprise systems",
            ],

            editorType: "javascript",

            estimatedTime: "90–120 minutes",

            objectives: [
              "Understand test-first development mindset",
              "Apply Red-Green-Refactor cycle confidently",
              "Write cleaner and safer code",
              "Refactor without fear of breaking features",
              "Adopt industry-grade development practices",
            ],
          },
          {
            day: 7,
            topic: "Final Project – Test-Covered Application",

            subTopics: [
              "Unit testing",
              "Component testing",
              "Mocking APIs",
              "Coverage optimization",
              "TDD workflow",
            ],

            explanation:
              "This final project proves you are job-ready by demonstrating strong testing fundamentals, real-world React testing, and disciplined Test-Driven Development practices.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Level Test Coverage",
                description:
                  "Turn an existing React feature into a production-grade, fully tested module.",
                tasks: [
                  "Identify one business-critical feature",
                  "Write unit tests for core logic",
                  "Write component tests for user behavior",
                  "Mock all external APIs properly",
                ],
                outcome:
                  "You can confidently say: this feature is production-ready.",
              },
              {
                id: "pt-2",
                title: "Full Feature via TDD",
                description:
                  "Build one complete feature strictly using Red → Green → Refactor.",
                tasks: [
                  "Write failing test for new feature",
                  "Implement minimal code to pass",
                  "Refactor code without changing tests",
                  "Repeat cycle for edge cases",
                ],
                qualityRule: "No test → no code.",
              },
              {
                id: "pt-3",
                title: "Coverage with Meaning",
                description:
                  "Learn to optimize coverage without writing useless tests.",
                tasks: [
                  "Generate coverage report",
                  "Identify risky untested branches",
                  "Add tests only where failure would hurt users",
                ],
                warning:
                  "High coverage with weak tests is worse than low coverage with strong tests.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Tests are not for today — they are for future-you and your teammates.",
              mindsetShift: [
                "Confidence over speed",
                "Behavior over implementation",
                "Refactoring without fear",
              ],
              goldenRule:
                "If deleting a feature scares you, your tests are not good enough.",
            },

            interviewInsights: {
              seniorSignals: [
                "Clear difference between unit, component, and integration tests",
                "Practical explanation of mocking APIs",
                "Comfort with less than 100% but meaningful coverage",
              ],
              idealAnswer:
                "I test business-critical paths first and refactor safely using tests.",
              redFlag: "I mostly rely on manual testing.",
            },

            editorGuidance: {
              careerMindset: [
                "Tested code is a professional promise",
                "Confidence in refactoring separates juniors from seniors",
                "Good tests are documentation that never lies",
              ],
              seniorAdvice:
                "In interviews, talk about how tests saved you from bugs — not how many you wrote.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real companies, code without tests is considered incomplete. This final project simulates a production-grade frontend where features are built, tested, and maintained with confidence using Jest, React Testing Library, and TDD.",

              projectGoal: {
                title: "Project Goal",
                explanation:
                  "Build or upgrade a React application where every critical feature is covered by tests and new functionality follows a TDD workflow.",
                realWorldNote:
                  "Most product teams expect 70–90% test coverage for business logic",
              },

              unitTesting: {
                title: "Unit Testing",
                explanation:
                  "Unit tests focus on small, isolated logic like utility functions, reducers, and helpers.",
                conceptsUsed: [
                  "Pure function testing",
                  "Edge case coverage",
                  "Deterministic outputs",
                ],
                exampleLogic: `
test('calculates total price', () => {
  expect(calculateTotal(2, 100)).toBe(200);
});
      `,
                bestPractice:
                  "Unit tests should be fast, simple, and independent",
              },

              componentTesting: {
                title: "Component Testing",
                explanation:
                  "Component tests verify UI behavior as a user would experience it.",
                conceptsUsed: [
                  "render and screen",
                  "Props-based rendering",
                  "Conditional UI testing",
                ],
                exampleLogic: `
render(<Button label="Save" />);
expect(screen.getByText('Save')).toBeInTheDocument();
      `,
                interviewInsight:
                  "Avoid testing internal state — test visible behavior",
              },

              mockingApis: {
                title: "Mocking APIs",
                explanation:
                  "Mocking allows testing UI and logic without relying on real backend services.",
                conceptsUsed: [
                  "jest.fn",
                  "Mock resolved/rejected promises",
                  "Isolation of external dependencies",
                ],
                exampleLogic: `
jest.fn(() => Promise.resolve({ data: [] }));
      `,
                realWorldNote:
                  "Mocking is critical for CI pipelines and offline testing",
              },

              coverageOptimization: {
                title: "Coverage Optimization",
                explanation:
                  "Coverage shows which parts of your code are tested and which are risky.",
                conceptsUsed: [
                  "Statement coverage",
                  "Branch coverage",
                  "Meaningful test cases",
                ],
                bestPractice: "Aim for meaningful coverage, not fake 100%",
              },

              tddWorkflow: {
                title: "TDD Workflow in Practice",
                explanation:
                  "New features must be built using the Red → Green → Refactor cycle.",
                steps: [
                  "Write a failing test (Red)",
                  "Implement minimum code (Green)",
                  "Refactor safely (Refactor)",
                ],
                exampleLogic: `
test('adds todo item', () => {
  // Red
});

// implement logic → Green
// clean code → Refactor
      `,
                interviewInsight:
                  "TDD improves design clarity and reduces regression bugs",
              },

              commonMistakes: [
                {
                  mistake: "Chasing 100% coverage",
                  issue: "Low-quality tests",
                  fix: "Focus on business-critical paths",
                },
                {
                  mistake: "Mocking everything",
                  issue: "Unrealistic tests",
                  fix: "Mock only external dependencies",
                },
                {
                  mistake: "Skipping TDD under pressure",
                  issue: "Bugs later",
                  fix: "At least write tests immediately after",
                },
              ],

              interviewQuestions: [
                "How do you decide what to test?",
                "Difference between unit and component tests?",
                "How do you mock APIs in React tests?",
                "What is your experience with TDD?",
              ],
            },

            tasks: [
              "Add unit and component tests to an existing React project",
              "Mock API calls for async components",
              "Achieve 80%+ meaningful code coverage",
              "Implement a new feature using full TDD cycle",
              "Refactor code confidently using tests",
            ],

            examples: [
              "Test-covered todo app",
              "Tested dashboard with mocked APIs",
            ],

            useCases: [
              "Enterprise applications",
              "High-quality production apps",
              "Frontend interviews",
              "Real company codebases",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Demonstrate job-ready testing skills",
              "Write reliable and maintainable frontend code",
              "Apply TDD in real projects",
              "Build confidence in refactoring",
              "Crack frontend interviews with strong testing knowledge",
            ],
          },
        ],
      },

      {
        week: 27,
        weekGoal:
          "Optimize frontend applications for performance, scalability, and production readiness",
        weekOutcome:
          "Ability to identify performance bottlenecks and ship fast, optimized frontend applications",
        miniProject: "Optimized Production Application",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Frontend Performance Fundamentals",

            subTopics: [
              "What is frontend performance",
              "Common performance bottlenecks",
              "Render blocking resources",
              "Reflow and repaint",
              "Browser rendering process",
            ],

            explanation:
              "Frontend performance directly impacts user experience, SEO rankings, and business metrics like conversions. Understanding how browsers load, parse, and render pages is the foundation of performance optimization.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Performance Bottleneck Audit",
                description:
                  "Analyze a real-world website and identify its top frontend performance issues.",
                tasks: [
                  "Open Chrome DevTools Performance tab",
                  "Record page load and scroll interaction",
                  "Identify long tasks (>50ms)",
                  "List top 3 JS, CSS, or rendering bottlenecks",
                ],
                outcome:
                  "You can clearly explain why a page feels slow, not just that it is slow.",
              },
              {
                id: "pt-2",
                title: "Critical Rendering Path Breakdown",
                description:
                  "Break down how a browser renders a page and where time is spent.",
                tasks: [
                  "Map DOM, CSSOM, Render Tree creation",
                  "Identify render-blocking resources",
                  "Suggest defer/async strategies",
                ],
                qualityRule:
                  "If you can’t explain the render path, you can’t optimize it.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Performance problems are invisible without measurement.",
              mindsetShift: [
                "Measure before optimizing",
                "Perceived speed matters more than raw speed",
                "Rendering cost is as important as network cost",
              ],
              goldenRule: "If you didn’t measure it, you didn’t optimize it.",
            },

            interviewInsights: {
              seniorSignals: [
                "Clear explanation of browser rendering process",
                "Ability to name real bottlenecks (JS, layout, paint)",
                "Understanding of render-blocking resources",
              ],
              idealAnswer:
                "I first analyze the critical rendering path and long tasks before touching code.",
              redFlag: "I just minify files and hope it gets faster.",
            },

            editorGuidance: {
              careerMindset: [
                "Fast apps are designed, not patched",
                "Performance is a feature, not an afterthought",
                "Understanding the browser gives you unfair advantage",
              ],
              seniorAdvice:
                "Don’t memorize metrics — understand what makes the browser work harder.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                'Frontend performance is not about making apps "fast" — it\'s about making them feel fast to users. In real-world products, even a 1-second delay can significantly reduce engagement and revenue. This day focuses on understanding how browsers work and where performance issues actually come from.',

              whatIsPerformance: {
                title: "What is Frontend Performance?",
                explanation:
                  "Frontend performance refers to how quickly and smoothly a web application loads, renders, and responds to user interactions.",
                metricsAffected: [
                  "Page load time",
                  "Time to Interactive (TTI)",
                  "First Contentful Paint (FCP)",
                  "Largest Contentful Paint (LCP)",
                ],
                realWorldNote:
                  "Google uses performance metrics as ranking factors for SEO",
              },

              bottlenecks: {
                title: "Common Performance Bottlenecks",
                explanation:
                  "Most performance issues come from unnecessary work done by the browser or JavaScript.",
                commonIssues: [
                  "Large JavaScript bundles",
                  "Too many network requests",
                  "Blocking CSS and JS",
                  "Heavy DOM manipulation",
                  "Unoptimized images",
                ],
                interviewInsight:
                  'Interviewers often ask: "What makes a website slow?"',
              },

              renderBlocking: {
                title: "Render Blocking Resources",
                explanation:
                  "CSS and JavaScript files can block the browser from rendering content until they are downloaded and processed.",
                conceptsUsed: [
                  "Critical rendering path",
                  "Blocking vs non-blocking resources",
                  "Script loading strategies",
                ],
                exampleLogic: `
<!-- Render blocking -->
<script src="app.js"></script>

<!-- Non-blocking -->
<script src="app.js" defer></script>
      `,
                bestPractice:
                  "Load critical CSS early and defer non-critical JavaScript",
              },

              reflowRepaint: {
                title: "Reflow & Repaint",
                explanation:
                  "Reflow happens when layout changes, repaint happens when visual styles change. Both are expensive operations.",
                triggers: [
                  "Changing width/height",
                  "Adding/removing DOM elements",
                  "Changing fonts",
                ],
                realWorldImpact:
                  "Excessive reflows cause UI jank and scroll lag",
              },

              browserRendering: {
                title: "Browser Rendering Process",
                steps: [
                  "HTML parsing → DOM",
                  "CSS parsing → CSSOM",
                  "DOM + CSSOM → Render Tree",
                  "Layout (Reflow)",
                  "Paint",
                  "Composite",
                ],
                interviewInsight:
                  "Understanding this flow helps debug almost every performance issue",
              },

              commonMistakes: [
                {
                  mistake: "Blaming backend for slow UI",
                  issue: "Frontend rendering itself is slow",
                  fix: "Analyze browser rendering & JS execution",
                },
                {
                  mistake: "Optimizing without measuring",
                  issue: "Wasted effort",
                  fix: "Always measure before and after changes",
                },
              ],

              interviewQuestions: [
                "What is frontend performance?",
                "What causes render blocking?",
                "Difference between reflow and repaint?",
                "How does a browser render a webpage?",
                "Why is frontend performance important for SEO?",
              ],
            },

            tasks: [
              "Analyze a slow webpage using Chrome DevTools",
              "Identify render-blocking CSS and JavaScript",
              "Inspect layout shifts and long scripting tasks",
            ],

            examples: [
              "Analyze page load timeline in DevTools",
              "Identify long scripting tasks",
            ],

            useCases: [
              "Large-scale web applications",
              "SEO-optimized websites",
              "High-traffic consumer products",
            ],

            editorType: null,

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand what frontend performance actually means",
              "Learn how browsers render web pages",
              "Identify real causes of slow frontend apps",
              "Build performance thinking mindset",
              "Prepare for performance-related interview questions",
            ],
          },
          {
            day: 2,
            topic: "React Performance Profiling & Optimization",

            subTopics: [
              "React Profiler",
              "Why components re-render",
              "React.memo",
              "useMemo and useCallback",
              "Avoiding unnecessary renders",
            ],

            explanation:
              "React applications often become slow due to unnecessary re-renders. Profiling tools and memoization techniques help identify bottlenecks and optimize component performance effectively.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real Re-render Investigation",
                description:
                  "Use React Profiler to catch unnecessary re-renders in a real component tree.",
                tasks: [
                  "Open React DevTools → Profiler",
                  "Record interaction (click, input, navigation)",
                  "Identify component with highest render count",
                  "Write down WHY it re-rendered (props, state, parent)",
                ],
                outcome:
                  "You stop guessing and start proving performance problems.",
              },
              {
                id: "pt-2",
                title: "Memoization Decision Exercise",
                description:
                  "Decide where memoization is actually worth applying.",
                tasks: [
                  "Pick one frequently rendering component",
                  "Apply React.memo",
                  "Apply useCallback / useMemo selectively",
                  "Re-profile and compare commit duration",
                ],
                qualityRule:
                  "If profiler shows no improvement, revert the optimization.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Performance optimizations must be validated, not assumed.",
              mindsetShift: [
                "Profiler is the source of truth",
                "Memoization is a tool, not a default",
                "Clean data flow reduces renders more than hooks",
              ],
              goldenRule:
                "If you didn’t re-profile, you don’t know if it helped.",
            },

            interviewInsights: {
              seniorSignals: [
                "Explains re-renders using props/state/reference changes",
                "Knows when NOT to use React.memo",
                "Uses profiler before touching code",
              ],
              idealAnswer:
                "I profile first, identify why the component re-renders, then apply memoization only if it reduces commit time.",
              redFlag: "I wrap everything in React.memo for performance.",
            },

            editorGuidance: {
              careerMindset: [
                "Performance bugs are design problems, not hook problems",
                "Stable references matter more than fewer renders",
                "Readable code with measured optimization wins",
              ],
              seniorAdvice:
                "If a component re-renders often but is cheap, don’t touch it.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world React applications, performance issues rarely come from APIs alone — most problems are caused by excessive re-renders. This day focuses on understanding *why* React re-renders components and *how* to control them using profiling and memoization techniques.",

              profiler: {
                title: "React Profiler",
                explanation:
                  "React Profiler helps measure how often components render, how long they take, and why they re-render.",
                conceptsUsed: [
                  "Commit duration",
                  "Render count",
                  "Why did this render?",
                ],
                steps: [
                  "Open React DevTools",
                  "Switch to Profiler tab",
                  "Record interactions",
                  "Analyze slow components",
                ],
                interviewInsight:
                  "Interviewers expect you to know how to *measure* performance, not just optimize blindly",
              },

              rerenders: {
                title: "Why Components Re-render",
                explanation:
                  "A component re-renders whenever its state, props, or parent component changes.",
                commonCauses: [
                  "Parent re-render",
                  "New object or function reference",
                  "State updates with same values",
                  "Context value changes",
                ],
                realWorldNote:
                  "Most unnecessary renders happen due to changing function references",
              },

              reactMemo: {
                title: "React.memo",
                explanation:
                  "React.memo prevents re-rendering of a component if its props have not changed.",
                conceptsUsed: [
                  "Shallow comparison of props",
                  "Pure component optimization",
                ],
                exampleLogic: `
const MemoComponent = React.memo(Component);
      `,
                bestPractice:
                  "Use React.memo only when re-render cost is higher than comparison cost",
              },

              useMemo: {
                title: "useMemo – Memoizing Values",
                explanation:
                  "useMemo caches the result of expensive calculations to avoid recomputation on every render.",
                conceptsUsed: [
                  "Derived values",
                  "Dependency array",
                  "Avoiding heavy recalculations",
                ],
                exampleLogic: `
const total = useMemo(() => calculateTotal(items), [items]);
      `,
                commonMistake: "Overusing useMemo for cheap calculations",
              },

              useCallback: {
                title: "useCallback – Memoizing Functions",
                explanation:
                  "useCallback prevents functions from being recreated on every render, which helps memoized child components.",
                conceptsUsed: [
                  "Stable function references",
                  "Preventing child re-renders",
                ],
                exampleLogic: `
const handleClick = useCallback(() => {
  setCount(c => c + 1);
}, []);
      `,
                realWorldNote:
                  "Most useful when passing handlers to memoized children",
              },

              optimizationFlow: {
                title: "Performance Optimization Flow",
                steps: [
                  "Profile the app",
                  "Identify slow or frequently rendering components",
                  "Apply memoization selectively",
                  "Re-profile to confirm improvement",
                ],
                bestPractice: "Never optimize without measuring first",
              },

              commonMistakes: [
                {
                  mistake: "Wrapping everything in React.memo",
                  issue: "Unnecessary complexity",
                  fix: "Memoize only expensive components",
                },
                {
                  mistake: "Using useMemo everywhere",
                  issue: "Hard-to-read code",
                  fix: "Use only for heavy calculations",
                },
                {
                  mistake: "Ignoring dependency arrays",
                  issue: "Stale values or bugs",
                  fix: "Always reason about dependencies",
                },
              ],

              interviewQuestions: [
                "Why do React components re-render?",
                "What does React.memo do?",
                "Difference between useMemo and useCallback?",
                "How do you profile a React app?",
                "When should you NOT optimize?",
              ],
            },

            tasks: [
              "Profile React components using React DevTools",
              "Identify unnecessary re-renders",
              "Optimize components using React.memo",
              "Apply useMemo and useCallback correctly",
            ],

            examples: [
              "const MemoComponent = React.memo(Component)",
              "useMemo(() => computeValue(), [deps])",
            ],

            useCases: [
              "Dashboards",
              "Complex component trees",
              "Data-heavy applications",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand why React components re-render",
              "Learn how to measure React performance",
              "Apply memoization techniques correctly",
              "Avoid common performance anti-patterns",
              "Build production-ready React optimization skills",
            ],
          },
          {
            day: 3,
            topic: "Code Splitting & Lazy Loading",

            subTopics: [
              "Why code splitting matters",
              "React.lazy and Suspense",
              "Route-based code splitting",
              "Dynamic imports",
              "Loading fallback strategies",
            ],

            explanation:
              "Code splitting improves frontend performance by reducing the initial JavaScript bundle size. Instead of loading everything at once, code is loaded only when required.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Bundle Size Reality Check",
                description:
                  "Measure how much JavaScript your users are actually downloading.",
                tasks: [
                  "Run build and open bundle analyzer (or build stats)",
                  "Identify top 3 largest chunks/files",
                  "Mark which ones are critical vs optional",
                  "Decide which parts should be lazy loaded",
                ],
                outcome:
                  "You stop guessing bundle size and start optimizing with data.",
              },
              {
                id: "pt-2",
                title: "Route-Level Lazy Loading Implementation",
                description:
                  "Apply code splitting at the most impactful level: routes.",
                tasks: [
                  "Convert at least 2 routes to React.lazy",
                  "Add meaningful Suspense fallback (not plain text)",
                  "Test navigation experience on slow network (DevTools)",
                ],
                qualityRule:
                  "If initial load JS size doesn’t reduce, splitting failed.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Code splitting success is measured in bundle size and load time, not code cleanliness.",
              mindsetShift: [
                "Initial load matters more than internal navigation",
                "Heavy features should earn their download",
                "Performance = what user downloads, not what you write",
              ],
              goldenRule: "If a user may never use it, don’t load it upfront.",
            },

            interviewInsights: {
              seniorSignals: [
                "Knows difference between lazy component vs dynamic import",
                "Splits code at route or feature boundaries",
                "Mentions bundle analysis tools before optimization",
              ],
              idealAnswer:
                "I analyze bundle size first, then apply route-based code splitting for heavy or low-traffic pages.",
              redFlag:
                "I lazy load random components without checking bundle size.",
            },

            editorGuidance: {
              careerMindset: [
                "Every KB of JavaScript has a cost",
                "Fast apps respect user bandwidth",
                "Loading strategy is part of architecture",
              ],
              seniorAdvice:
                "Code splitting is about delivery strategy, not React syntax.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world production apps, loading the entire JavaScript bundle upfront leads to slow initial page loads. Code splitting allows applications to load only what the user needs at that moment, making apps faster and more scalable.",

              whyCodeSplitting: {
                title: "Why Code Splitting Matters",
                explanation:
                  "Large bundles increase Time to Interactive (TTI). Code splitting breaks the bundle into smaller chunks that load on demand.",
                realWorldImpact: [
                  "Faster first paint",
                  "Better performance on low-end devices",
                  "Reduced network usage",
                ],
                interviewInsight:
                  "Performance optimization is incomplete without bundle size optimization",
              },

              reactLazySuspense: {
                title: "React.lazy & Suspense",
                explanation:
                  "React.lazy enables dynamic importing of components, while Suspense provides a fallback UI during loading.",
                conceptsUsed: [
                  "Dynamic import()",
                  "Lazy component loading",
                  "Fallback UI rendering",
                ],
                exampleLogic: `
const Dashboard = React.lazy(() => import('./Dashboard'));

<Suspense fallback={<Loader />}>
  <Dashboard />
</Suspense>
      `,
                bestPractice: "Always wrap lazy components inside Suspense",
              },

              routeBasedSplitting: {
                title: "Route-Based Code Splitting",
                explanation:
                  "Routes are ideal candidates for code splitting because users only visit a subset of pages.",
                conceptsUsed: [
                  "Lazy loading routes",
                  "Reducing initial bundle size",
                  "Optimized navigation",
                ],
                exampleLogic: `
const Profile = React.lazy(() => import('./Profile'));

<Route path='/profile' element={
  <Suspense fallback={<Loader />}>
    <Profile />
  </Suspense>
} />
      `,
                realWorldNote:
                  "Most large apps split code primarily at route level",
              },

              dynamicImports: {
                title: "Dynamic Imports",
                explanation:
                  "Dynamic imports allow loading modules conditionally at runtime.",
                conceptsUsed: [
                  "import() syntax",
                  "Conditional loading",
                  "Async chunk loading",
                ],
                exampleLogic: `
if (showChart) {
  import('./Chart').then(module => {
    setChart(module.default);
  });
}
      `,
                useCase: "Heavy components like charts, editors, or maps",
              },

              loadingFallbacks: {
                title: "Loading Fallback Strategies",
                explanation:
                  "Fallback UI improves user experience while chunks are loading.",
                strategies: [
                  "Skeleton loaders",
                  "Spinners",
                  "Progress indicators",
                  "Minimal placeholders",
                ],
                bestPractice:
                  "Fallback UI should be lightweight and fast to render",
              },

              optimizationFlow: {
                title: "Code Splitting Optimization Flow",
                steps: [
                  "Analyze bundle size",
                  "Identify heavy components",
                  "Apply lazy loading",
                  "Add meaningful fallbacks",
                  "Measure performance again",
                ],
                bestPractice: "Measure bundle size before and after splitting",
              },

              commonMistakes: [
                {
                  mistake: "Lazy loading everything",
                  issue: "Too many network requests",
                  fix: "Lazy load only heavy or rarely used parts",
                },
                {
                  mistake: "No fallback UI",
                  issue: "Blank screen during load",
                  fix: "Always use Suspense fallback",
                },
                {
                  mistake: "Over-splitting",
                  issue: "Increased complexity",
                  fix: "Split at logical boundaries",
                },
              ],

              interviewQuestions: [
                "What is code splitting?",
                "Difference between React.lazy and dynamic import?",
                "Why is route-based code splitting effective?",
                "What are good fallback strategies?",
                "How do you measure bundle size?",
              ],
            },

            tasks: [
              "Implement lazy loaded components",
              "Apply route-based code splitting",
              "Add meaningful loading fallbacks",
              "Analyze bundle size before and after splitting",
            ],

            examples: [
              "const Page = React.lazy(() => import('./Page'))",
              "<Suspense fallback={<Loader />}>",
            ],

            useCases: [
              "Multi-page apps",
              "Large React projects",
              "Production-scale apps",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand why code splitting is important",
              "Implement lazy loading using React.lazy and Suspense",
              "Apply route-based code splitting effectively",
              "Improve initial page load performance",
              "Build production-ready React performance skills",
            ],
          },
          {
            day: 4,
            topic: "Asset & Image Optimization",

            subTopics: [
              "Image formats (JPEG, PNG, WebP)",
              "Lazy loading images",
              "Responsive images",
              "Compression strategies",
              "Font optimization",
            ],

            explanation:
              "Images, fonts, and static assets usually contribute the largest portion of page weight. Optimizing them is one of the highest ROI performance improvements in frontend development.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real Asset Weight Audit",
                description:
                  "Identify which assets are actually slowing down the page.",
                tasks: [
                  "Open Network tab and filter by Img / Font",
                  "List top 5 heaviest assets",
                  "Check if they are critical or below-the-fold",
                  "Decide lazy-load vs preload strategy",
                ],
                outcome:
                  "You stop guessing and know exactly which assets hurt performance.",
              },
              {
                id: "pt-2",
                title: "Image Optimization Pipeline",
                description:
                  "Create a repeatable process instead of manual optimization.",
                tasks: [
                  "Convert JPG/PNG → WebP",
                  "Generate 3 responsive sizes (mobile, tablet, desktop)",
                  "Add srcset + sizes properly",
                  "Verify loaded image size on mobile vs desktop",
                ],
                qualityRule:
                  "Mobile should never download desktop-sized images.",
              },
              {
                id: "pt-3",
                title: "Font Loading Strategy Fix",
                description:
                  "Eliminate invisible text and font-based render blocking.",
                tasks: [
                  "Audit font files and variants",
                  "Remove unused weights",
                  "Add font-display: swap",
                  "Preload only critical fonts",
                ],
                outcome:
                  "Text becomes visible immediately without layout jank.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Asset optimization success is measured in KB saved and paint timings improved.",
              whatToMeasure: [
                "Total image payload",
                "Font download size",
                "LCP before vs after optimization",
              ],
              mindsetShift: [
                "Every image is code that must be optimized",
                "Assets are part of performance budget",
              ],
              goldenRule:
                "If an asset blocks rendering or is unused, it’s a performance bug.",
            },

            interviewInsights: {
              seniorSignals: [
                "Mentions WebP + responsive images together",
                "Understands font-display and preloading",
                "Talks in terms of LCP and page weight",
              ],
              idealAnswer:
                "I audit assets first, convert to modern formats, apply responsive loading, and verify improvements using Lighthouse.",
              redFlag:
                "I just enable lazy loading and hope performance improves.",
            },

            editorGuidance: {
              careerMindset: [
                "Assets usually cost more than JavaScript",
                "Fast sites respect user bandwidth",
                "Performance wins often come from basics done right",
              ],
              seniorAdvice:
                "If you want quick Lighthouse wins, optimize images and fonts before touching JavaScript.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In production-grade frontend applications, poor asset optimization leads to slow load times, higher bounce rates, and bad SEO scores. Companies aggressively optimize images and fonts to deliver fast, smooth experiences across devices.",

              imageFormats: {
                title: "Choosing the Right Image Format",
                explanation:
                  "Different image formats serve different purposes based on quality, size, and transparency needs.",
                formats: {
                  jpeg: "Best for photos with many colors, smaller size but lossy compression",
                  png: "Supports transparency, larger size, good for icons and UI assets",
                  webp: "Modern format with superior compression and quality",
                },
                bestPractice:
                  "Use WebP wherever supported, with fallback for older browsers",
                interviewInsight:
                  "Choosing the wrong image format is a common performance mistake",
              },

              lazyLoadingImages: {
                title: "Lazy Loading Images",
                explanation:
                  "Lazy loading defers loading images until they are needed (visible in viewport).",
                conceptsUsed: [
                  "Native lazy loading",
                  "Intersection Observer (advanced)",
                  "Reducing initial page load",
                ],
                exampleLogic: `
<img src="hero.webp" loading="lazy" alt="Hero Image" />
      `,
                realWorldNote:
                  "Lazy loading is essential for long pages and image-heavy feeds",
              },

              responsiveImages: {
                title: "Responsive Images",
                explanation:
                  "Responsive images ensure the browser loads the correct image size based on device screen.",
                conceptsUsed: [
                  "srcset attribute",
                  "sizes attribute",
                  "Device-based optimization",
                ],
                exampleLogic: `
<img 
  src="small.jpg"
  srcset="small.jpg 480w, medium.jpg 768w, large.jpg 1200w"
  sizes="(max-width: 768px) 100vw, 50vw"
/>
      `,
                bestPractice:
                  "Never serve desktop-sized images to mobile users",
              },

              compressionStrategies: {
                title: "Compression Strategies",
                explanation:
                  "Compression reduces file size without noticeable quality loss.",
                techniques: [
                  "Lossy vs lossless compression",
                  "Image compression tools",
                  "Build-time asset optimization",
                ],
                realWorldTools: [
                  "ImageOptim",
                  "Squoosh",
                  "Webpack image loaders",
                ],
                interviewInsight:
                  "Compression alone can reduce image size by 60–80%",
              },

              fontOptimization: {
                title: "Font Optimization",
                explanation:
                  "Fonts can block rendering if not optimized properly.",
                conceptsUsed: [
                  "Font-display property",
                  "Preloading fonts",
                  "Reducing font variants",
                ],
                exampleLogic: `
@font-face {
  font-display: swap;
}
      `,
                bestPractice: "Use font-display: swap to avoid invisible text",
              },

              optimizationChecklist: {
                title: "Asset Optimization Checklist",
                checklist: [
                  "Use modern image formats",
                  "Lazy load below-the-fold images",
                  "Serve responsive image sizes",
                  "Compress all assets",
                  "Optimize font loading strategy",
                ],
                realWorldNote:
                  "Most Lighthouse performance gains come from this checklist",
              },

              commonMistakes: [
                {
                  mistake: "Uploading uncompressed images",
                  issue: "Huge page size",
                  fix: "Always compress before deployment",
                },
                {
                  mistake: "Disabling lazy loading",
                  issue: "Slow initial load",
                  fix: "Lazy load non-critical images",
                },
                {
                  mistake: "Loading too many font variants",
                  issue: "Render blocking",
                  fix: "Limit fonts and weights",
                },
              ],

              interviewQuestions: [
                "Which image format would you choose and why?",
                "How does lazy loading improve performance?",
                "What is srcset and when should it be used?",
                "How do fonts affect page rendering?",
                "What are common asset optimization mistakes?",
              ],
            },

            tasks: [
              "Convert images to modern formats (WebP)",
              "Implement lazy loading for images",
              "Add responsive image handling",
              "Optimize font loading strategy",
              "Re-test performance using Lighthouse",
            ],

            examples: ["<img loading='lazy' />", "Use responsive image sizes"],

            useCases: [
              "Media-heavy websites",
              "E-commerce platforms",
              "Content-driven apps",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand the performance impact of assets",
              "Choose correct image formats",
              "Implement lazy loading and responsive images",
              "Optimize fonts for faster rendering",
              "Apply real-world asset optimization techniques",
            ],
          },
          {
            day: 5,
            topic: "Advanced Performance Techniques",

            subTopics: [
              "Debouncing and throttling",
              "Virtual scrolling",
              "Web Workers basics",
              "Offloading heavy computations",
              "Avoiding main thread blocking",
            ],

            explanation:
              "When applications scale, performance issues often come from excessive event handling, large DOM lists, and heavy computations blocking the main thread. Advanced performance techniques solve these real-world problems.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Event Storm Control (Debounce vs Throttle)",
                description:
                  "Identify high-frequency events and apply the correct control strategy.",
                tasks: [
                  "Audit events firing on scroll, resize, input",
                  "Decide debounce vs throttle per event",
                  "Measure call count before vs after",
                  "Verify UX doesn’t feel laggy",
                ],
                outcome:
                  "You prevent unnecessary work without breaking user experience.",
              },
              {
                id: "pt-2",
                title: "Large List Performance Fix",
                description:
                  "Optimize rendering for large datasets using virtual scrolling.",
                tasks: [
                  "Render a list of 5,000+ items normally",
                  "Measure DOM nodes & FPS",
                  "Implement virtual scrolling (windowing)",
                  "Compare memory & render time",
                ],
                qualityRule:
                  "DOM node count should remain almost constant while scrolling.",
              },
              {
                id: "pt-3",
                title: "Main Thread Offloading with Web Workers",
                description:
                  "Move heavy computation off the main thread to keep UI responsive.",
                tasks: [
                  "Identify a CPU-heavy task (loop, data transform)",
                  "Implement the same logic in Web Worker",
                  "Measure main thread blocking time",
                  "Confirm smooth scrolling during computation",
                ],
                outcome:
                  "UI stays responsive even under heavy data processing.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Performance optimizations must be validated under stress, not just visually.",
              whatToMeasure: [
                "Event handler call frequency",
                "Main thread blocking time",
                "FPS during scroll",
                "DOM node count",
              ],
              mindsetShift: [
                "Smooth UX > raw execution speed",
                "Main thread is a limited resource",
              ],
              goldenRule:
                "If scrolling stutters, the optimization is incomplete.",
            },

            interviewInsights: {
              seniorSignals: [
                "Knows when NOT to debounce",
                "Mentions DOM node count, not just JS speed",
                "Explains why Web Workers are message-based",
              ],
              idealAnswer:
                "I control event frequency, virtualize large lists, and offload CPU-heavy work to Web Workers to keep the main thread free.",
              redFlag: "I optimize only by adding debounce everywhere.",
            },

            editorGuidance: {
              careerMindset: [
                "Performance issues appear only at scale",
                "Real users scroll, type, and interact continuously",
                "Blocking the main thread is the fastest way to kill UX",
              ],
              seniorAdvice:
                "Once you master main-thread management, most performance bugs become predictable.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern frontend applications deal with real-time data, infinite lists, and complex logic. If not optimized, these can freeze the UI and ruin user experience. Advanced performance techniques ensure apps stay smooth and responsive under load.",

              debouncing: {
                title: "Debouncing",
                explanation:
                  "Debouncing ensures a function runs only after a certain delay once the user stops triggering an event.",
                conceptsUsed: [
                  "Delaying execution",
                  "Reducing API calls",
                  "Improving input performance",
                ],
                exampleLogic: `
function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
      `,
                realWorldUse: "Search inputs, autocomplete, form validations",
                interviewInsight:
                  "Debounce is commonly used to prevent API flooding",
              },

              throttling: {
                title: "Throttling",
                explanation:
                  "Throttling ensures a function executes at most once in a specified time interval.",
                conceptsUsed: [
                  "Rate limiting events",
                  "Controlling execution frequency",
                  "Performance optimization",
                ],
                exampleLogic: `
function throttle(fn, limit) {
  let inThrottle = false;
  return (...args) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
      `,
                realWorldUse:
                  "Scroll events, resize events, mouse movement tracking",
              },

              virtualScrolling: {
                title: "Virtual Scrolling (Windowing)",
                explanation:
                  "Virtual scrolling renders only the visible items instead of the entire list.",
                conceptsUsed: [
                  "Rendering subset of DOM",
                  "Viewport-based rendering",
                  "Reducing DOM nodes",
                ],
                realWorldExamples: [
                  "Chat message lists",
                  "Large tables",
                  "Infinite feeds",
                ],
                interviewInsight:
                  "Rendering thousands of DOM nodes is a common performance killer",
              },

              webWorkers: {
                title: "Web Workers",
                explanation:
                  "Web Workers allow running heavy JavaScript computations in a background thread, separate from the UI.",
                conceptsUsed: [
                  "Multi-threading in browser",
                  "Message passing",
                  "Non-blocking UI",
                ],
                exampleLogic: `
worker.postMessage(data);
worker.onmessage = e => console.log(e.data);
      `,
                bestPractice: "Use Web Workers for CPU-heavy tasks only",
                realWorldUse: "Data processing, analytics, image processing",
              },

              mainThreadOptimization: {
                title: "Avoiding Main Thread Blocking",
                explanation:
                  "The main thread handles rendering, user input, and JS execution. Blocking it causes jank and freezes.",
                techniques: [
                  "Breaking tasks into chunks",
                  "Using requestIdleCallback",
                  "Moving heavy work to Web Workers",
                ],
                realWorldNote:
                  "Smooth apps keep the main thread free as much as possible",
              },

              comparisonTable: {
                title: "Debounce vs Throttle",
                debounce: "Executes after delay once events stop",
                throttle: "Executes at regular intervals",
                usage: "Debounce → search | Throttle → scroll",
              },

              commonMistakes: [
                {
                  mistake: "Debouncing scroll events",
                  issue: "Laggy UI",
                  fix: "Use throttling instead",
                },
                {
                  mistake: "Rendering large lists fully",
                  issue: "DOM slowdown",
                  fix: "Use virtual scrolling",
                },
                {
                  mistake: "Heavy computations on main thread",
                  issue: "UI freeze",
                  fix: "Move logic to Web Workers",
                },
              ],

              interviewQuestions: [
                "Difference between debounce and throttle?",
                "When would you use Web Workers?",
                "How do you handle large lists efficiently?",
                "What causes main thread blocking?",
                "How do you keep UI responsive under load?",
              ],
            },

            tasks: [
              "Implement debounce on search input",
              "Apply throttling to scroll events",
              "Experiment with virtual list rendering",
              "Create a basic Web Worker",
              "Analyze main thread performance in DevTools",
            ],

            examples: ["debounce(fn, 300)", "throttle(fn, 200)"],

            useCases: [
              "Search-heavy apps",
              "Large lists",
              "Real-time dashboards",
            ],

            editorType: "javascript",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand advanced frontend performance bottlenecks",
              "Use debounce and throttle correctly",
              "Handle large datasets efficiently",
              "Offload heavy logic from main thread",
              "Build smooth, production-grade user experiences",
            ],
          },
          {
            day: 6,
            topic: "Lighthouse, Core Web Vitals & PWAs",

            subTopics: [
              "Lighthouse reports",
              "Core Web Vitals (LCP, FID, CLS)",
              "Service Workers basics",
              "Offline support",
              "PWA installation",
            ],

            explanation:
              "Google uses performance, accessibility, and Core Web Vitals as ranking signals. Lighthouse audits and Progressive Web Apps (PWAs) help ensure fast, reliable, and installable web experiences.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real Lighthouse Audit & Fix",
                description:
                  "Run Lighthouse on a real production website and improve weak metrics.",
                tasks: [
                  "Run Lighthouse in Incognito mode",
                  "Identify lowest scoring category",
                  "Fix at least 2 performance issues",
                  "Re-run audit and compare scores",
                ],
                outcome:
                  "You learn how small fixes impact real performance scores.",
              },
              {
                id: "pt-2",
                title: "Core Web Vitals Deep Fix",
                description:
                  "Individually improve LCP, FID, and CLS with targeted optimizations.",
                tasks: [
                  "Identify LCP element using DevTools",
                  "Optimize hero image or preload asset",
                  "Reduce long tasks affecting FID",
                  "Fix layout shifts causing CLS",
                ],
                qualityRule:
                  "All three vitals should meet Google ‘Good’ thresholds.",
              },
              {
                id: "pt-3",
                title: "Offline-First PWA Setup",
                description:
                  "Add basic PWA capabilities to make app installable and offline-ready.",
                tasks: [
                  "Create web app manifest",
                  "Register service worker",
                  "Cache static assets",
                  "Test offline mode in DevTools",
                ],
                outcome: "App loads even without network connection.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Performance scores without real-user context are misleading.",
              whatToValidate: [
                "Lighthouse lab data vs real-user data",
                "LCP element consistency",
                "CLS stability during loading",
                "Offline behavior reliability",
              ],
              mindsetShift: [
                "SEO is a performance problem",
                "Performance is a product feature",
              ],
              goldenRule:
                "If users feel slowness, Lighthouse score doesn’t matter.",
            },

            interviewInsights: {
              seniorSignals: [
                "Explains Core Web Vitals with examples",
                "Knows difference between lab and field data",
                "Understands Service Worker lifecycle",
              ],
              idealAnswer:
                "I use Lighthouse for audits, validate with Core Web Vitals, and use PWAs to improve reliability on poor networks.",
              redFlag: "I only try to get a 100 Lighthouse score.",
            },

            editorGuidance: {
              careerMindset: [
                "Google ranking is a UX signal",
                "Offline support builds user trust",
                "Performance failures hurt business metrics",
              ],
              seniorAdvice:
                "Optimize for humans first, scores second — rankings will follow.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern frontend development is not just about building features — it’s about delivering fast, reliable, and user-friendly experiences. Lighthouse and Core Web Vitals measure real-world performance, while PWAs improve reliability on slow or unstable networks.",

              lighthouse: {
                title: "Lighthouse Audits",
                explanation:
                  "Lighthouse is an automated tool by Google that audits web pages for performance, accessibility, best practices, SEO, and PWA readiness.",
                categories: [
                  "Performance",
                  "Accessibility",
                  "Best Practices",
                  "SEO",
                  "PWA",
                ],
                conceptsMeasured: [
                  "Page load speed",
                  "Interactivity",
                  "Visual stability",
                ],
                realWorldUse:
                  "Used before production releases and during SEO optimization",
                interviewInsight:
                  "Many companies require Lighthouse performance scores above a threshold",
              },

              coreWebVitals: {
                title: "Core Web Vitals",
                explanation:
                  "Core Web Vitals are real-user performance metrics that directly impact SEO and user experience.",
                metrics: {
                  LCP: {
                    meaning: "Largest Contentful Paint",
                    measures: "Loading performance",
                    goodThreshold: "< 2.5 seconds",
                    commonIssues: [
                      "Unoptimized hero images",
                      "Slow server response",
                    ],
                  },
                  FID: {
                    meaning: "First Input Delay",
                    measures: "Interactivity",
                    goodThreshold: "< 100 ms",
                    commonIssues: [
                      "Heavy JavaScript execution",
                      "Main thread blocking",
                    ],
                  },
                  CLS: {
                    meaning: "Cumulative Layout Shift",
                    measures: "Visual stability",
                    goodThreshold: "< 0.1",
                    commonIssues: [
                      "Images without dimensions",
                      "Late-loading fonts",
                    ],
                  },
                },
                realWorldNote:
                  "Poor Core Web Vitals directly affect Google search rankings",
              },

              improvingVitals: {
                title: "Improving Core Web Vitals",
                techniques: {
                  LCP: [
                    "Optimize hero images",
                    "Use CDN",
                    "Preload critical assets",
                  ],
                  FID: [
                    "Split JavaScript bundles",
                    "Reduce long tasks",
                    "Use Web Workers",
                  ],
                  CLS: [
                    "Reserve image space",
                    "Avoid layout-shifting ads",
                    "Use font-display: swap",
                  ],
                },
                bestPractice:
                  "Always measure with real-user data (CrUX, PageSpeed Insights)",
              },

              serviceWorkers: {
                title: "Service Workers",
                explanation:
                  "Service Workers run in the background and enable offline support, caching, and background sync.",
                conceptsUsed: [
                  "Background execution",
                  "Cache API",
                  "Network interception",
                ],
                exampleLogic: `
self.addEventListener('fetch', event => {
  event.respondWith(caches.match(event.request));
});
      `,
                realWorldUse: "Offline-first apps, caching API responses",
              },

              pwaConcepts: {
                title: "Progressive Web Apps (PWA)",
                explanation:
                  "PWAs combine the reach of the web with native-app-like features.",
                features: [
                  "Offline support",
                  "Installable on device",
                  "Fast loading",
                  "Reliable on poor networks",
                ],
                requirements: ["HTTPS", "Web App Manifest", "Service Worker"],
                interviewInsight:
                  "PWAs are widely used for mobile-first performance strategies",
              },

              commonMistakes: [
                {
                  mistake: "Chasing 100 Lighthouse score",
                  issue: "Ignoring real user experience",
                  fix: "Focus on Core Web Vitals and real-user metrics",
                },
                {
                  mistake: "Ignoring CLS",
                  issue: "Poor visual experience",
                  fix: "Always reserve layout space",
                },
                {
                  mistake: "Caching everything blindly",
                  issue: "Stale or broken data",
                  fix: "Use smart caching strategies",
                },
              ],

              interviewQuestions: [
                "What are Core Web Vitals?",
                "Difference between LCP and FCP?",
                "How does CLS affect UX?",
                "What problems do Service Workers solve?",
                "When should you build a PWA?",
              ],
            },

            tasks: [
              "Run Lighthouse audit on a real website",
              "Identify and fix Core Web Vitals issues",
              "Optimize LCP image",
              "Prevent layout shifts",
              "Add basic PWA support",
            ],

            examples: [
              "Improve LCP by optimizing hero image",
              "Add service worker for offline caching",
            ],

            useCases: [
              "SEO-focused products",
              "Mobile-first apps",
              "Production deployments",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand how Google measures frontend performance",
              "Improve Core Web Vitals for SEO and UX",
              "Use Lighthouse effectively",
              "Implement basic PWA features",
              "Build fast, reliable, installable web apps",
            ],
          },
          {
            day: 7,
            topic: "Final Project – Optimized Production App",

            subTopics: [
              "React profiling",
              "Code splitting",
              "Asset optimization",
              "Performance auditing",
              "Production best practices",
            ],

            explanation:
              "This final project validates your ability to take a real React application and optimize it to production-grade performance standards expected by modern companies.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Baseline Performance Audit",
                description:
                  "Measure current performance before making any changes.",
                tasks: [
                  "Run Lighthouse in Incognito mode",
                  "Record LCP, FID, CLS scores",
                  "Analyze bundle size using DevTools",
                  "Identify top 3 performance bottlenecks",
                ],
                outcome:
                  "Clear baseline metrics to compare improvements against.",
              },
              {
                id: "pt-2",
                title: "Profiling-Driven Optimization",
                description:
                  "Optimize only components proven to be slow or over-rendering.",
                tasks: [
                  "Use React Profiler to find wasted renders",
                  "Apply React.memo where justified",
                  "Stabilize handlers with useCallback",
                  "Memoize heavy calculations with useMemo",
                ],
                qualityRule:
                  "Every optimization must be justified by profiler data.",
              },
              {
                id: "pt-3",
                title: "Bundle & Asset Optimization",
                description:
                  "Reduce load time by minimizing JS and asset weight.",
                tasks: [
                  "Implement route-based code splitting",
                  "Lazy load heavy components",
                  "Convert images to WebP",
                  "Add lazy loading for below-the-fold images",
                ],
                outcome: "Noticeable improvement in initial load and LCP.",
              },
              {
                id: "pt-4",
                title: "Final Performance Report",
                description:
                  "Document improvements like a real production engineer.",
                tasks: [
                  "Capture before vs after Lighthouse scores",
                  "Explain each optimization decision",
                  "Highlight impact on Core Web Vitals",
                  "Prepare short performance summary",
                ],
                outcome: "Portfolio-ready performance case study.",
              },
            ],

            testingInsights: {
              coreTruth:
                "Performance optimization without measurement is guesswork.",
              whatToValidate: [
                "Render count reduction after memoization",
                "Bundle size decrease after code splitting",
                "Improved LCP element load time",
                "Stable CLS during page load",
              ],
              mindsetShift: [
                "Performance regressions are bugs",
                "Optimizations must be measurable",
              ],
              goldenRule:
                "If metrics don’t improve, the optimization didn’t work.",
            },

            interviewInsights: {
              seniorSignals: [
                "Starts optimization with profiling",
                "Explains trade-offs of memoization",
                "Understands bundle size vs network cost",
                "Talks in metrics, not opinions",
              ],
              idealAnswer:
                "I profile first, optimize the biggest bottleneck, then re-measure to confirm real gains.",
              redFlag: "I added React.memo everywhere to improve performance.",
            },

            editorGuidance: {
              careerMindset: [
                "Performance is a user-facing feature",
                "Optimization is part of engineering maturity",
                "Production apps must work on weak devices",
              ],
              seniorAdvice:
                "Great frontend engineers don’t guess — they measure, optimize, and validate.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world frontend roles, writing features is only half the job. Engineers are expected to optimize performance, reduce bundle size, improve loading speed, and ensure the application performs well on low-end devices and slow networks. This project simulates that real production responsibility.",

              projectGoal: {
                title: "Project Goal",
                explanation:
                  "Take an existing React application and systematically optimize it for performance, user experience, and production readiness.",
                realWorldNote:
                  "Many companies reject apps that look good but perform poorly on real devices",
              },

              reactProfiling: {
                title: "React Profiling",
                explanation:
                  "Profiling helps identify unnecessary re-renders and slow components.",
                conceptsUsed: [
                  "React Profiler",
                  "Identifying wasted renders",
                  "Component render timing",
                ],
                exampleLogic: `
<Profiler id="Dashboard" onRender={onRenderCallback}>
  <Dashboard />
</Profiler>
      `,
                bestPractice:
                  "Optimize only after measuring — never guess performance issues",
              },

              codeSplitting: {
                title: "Code Splitting & Lazy Loading",
                explanation:
                  "Code splitting reduces the initial JavaScript bundle size by loading features only when needed.",
                conceptsUsed: [
                  "React.lazy",
                  "Suspense",
                  "Route-based splitting",
                ],
                exampleLogic: `
const Dashboard = React.lazy(() => import('./Dashboard'));
      `,
                realWorldImpact:
                  "Faster initial page load and improved LCP score",
              },

              assetOptimization: {
                title: "Asset & Image Optimization",
                explanation:
                  "Large images, fonts, and assets are the biggest contributors to slow load times.",
                techniques: [
                  "Image compression (WebP)",
                  "Lazy loading images",
                  "Font preloading",
                ],
                exampleLogic: `
<img src="hero.webp" loading="lazy" />
      `,
                interviewInsight:
                  "Optimizing assets is one of the easiest performance wins",
              },

              performanceAuditing: {
                title: "Performance Auditing",
                explanation:
                  "Auditing ensures optimizations actually improve real metrics.",
                tools: [
                  "Lighthouse",
                  "Chrome DevTools Performance",
                  "Core Web Vitals",
                ],
                metricsTracked: ["LCP", "FID", "CLS"],
                bestPractice:
                  "Track before vs after metrics to validate improvements",
              },

              productionBestPractices: {
                title: "Production Best Practices",
                explanation:
                  "Production-ready apps follow strict performance and reliability guidelines.",
                practices: [
                  "Remove unused dependencies",
                  "Minify and compress bundles",
                  "Avoid blocking scripts",
                  "Graceful loading states",
                ],
                realWorldNote:
                  "Performance is a long-term responsibility, not a one-time task",
              },

              commonMistakes: [
                {
                  mistake: "Optimizing without measuring",
                  issue: "Wasted effort",
                  fix: "Always profile and audit first",
                },
                {
                  mistake: "Overusing memoization",
                  issue: "Complex code with little gain",
                  fix: "Memoize only expensive operations",
                },
                {
                  mistake: "Ignoring mobile performance",
                  issue: "Poor real-user experience",
                  fix: "Test on low-end devices",
                },
              ],

              interviewQuestions: [
                "How do you profile React performance?",
                "What is code splitting and why is it important?",
                "How do you improve Lighthouse scores?",
                "What are common frontend performance bottlenecks?",
                "How do you ensure performance in production?",
              ],
            },

            tasks: [
              "Select an existing React application",
              "Profile components using React Profiler",
              "Implement code splitting and lazy loading",
              "Optimize images, fonts, and assets",
              "Improve Lighthouse score to 90+",
              "Document performance improvements",
            ],

            examples: [
              "Optimized dashboard app",
              "Performance-improved e-commerce UI",
            ],

            useCases: [
              "Real-world production apps",
              "Senior frontend expectations",
              "Performance-focused interviews",
              "High-traffic products",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Optimize React apps for production performance",
              "Apply profiling-driven optimizations",
              "Improve real-world performance metrics",
              "Build confidence handling production apps",
              "Meet senior-level frontend performance expectations",
            ],
          },
        ],
      },
      {
        week: 28,
        weekGoal:
          "Master modern styling solutions in React and build scalable, themeable UI systems",
        weekOutcome:
          "Ability to design and maintain scalable component styling systems used in production apps",
        miniProject: "Design System with Theming Support",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Introduction to CSS-in-JS",

            subTopics: [
              "What is CSS-in-JS",
              "Why CSS-in-JS exists",
              "Styled Components vs Emotion",
              "Pros and cons of CSS-in-JS",
              "When to use CSS-in-JS",
            ],

            explanation:
              "CSS-in-JS allows developers to write component-scoped styles directly inside JavaScript, improving maintainability, scalability, and eliminating global CSS conflicts in modern React applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "CSS-in-JS Decision Analysis",
                description:
                  "Understand when CSS-in-JS is the right architectural choice.",
                tasks: [
                  "Compare a component styled with CSS vs CSS-in-JS",
                  "Identify scoping and refactorability differences",
                  "List cases where CSS-in-JS improves DX",
                ],
                outcome:
                  "Clear decision-making ability for choosing CSS-in-JS in real projects.",
              },
              {
                id: "pt-2",
                title: "Library Evaluation Exercise",
                description:
                  "Evaluate Styled Components vs Emotion from a production mindset.",
                tasks: [
                  "Check bundle size impact of both libraries",
                  "Compare syntax and theming support",
                  "Identify ecosystem usage (MUI, design systems)",
                ],
                outcome:
                  "Ability to justify library choice in interviews and code reviews.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "Styles are part of component logic, not a separate global concern.",
              keyShift: [
                "From global styles → component-scoped styles",
                "From class naming → direct component styling",
                "From static CSS → dynamic, prop-driven styles",
              ],
              tradeOff:
                "You trade runtime cost for scalability and maintainability.",
            },

            interviewInsights: {
              seniorSignals: [
                "Can explain why CSS-in-JS was created",
                "Understands runtime vs build-time styling trade-offs",
                "Knows when NOT to use CSS-in-JS",
              ],
              idealAnswer:
                "CSS-in-JS solves scaling and scoping issues in component-based UIs, but I avoid it for simple static pages.",
              redFlag: "CSS-in-JS is always better than normal CSS.",
            },

            editorGuidance: {
              careerMindset: [
                "Styling decisions are architectural decisions",
                "Maintainability matters more than syntax preference",
                "Consistency beats experimenting with many tools",
              ],
              seniorAdvice:
                "Choose CSS-in-JS for scale, not because it looks modern.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As frontend applications grow, managing large CSS files becomes difficult due to naming conflicts, unused styles, and poor scalability. CSS-in-JS emerged to solve these problems by tightly coupling styles with components.",

              whatIsCssInJs: {
                title: "What is CSS-in-JS?",
                explanation:
                  "CSS-in-JS is a styling approach where CSS is written inside JavaScript files and scoped to components.",
                keyBenefits: [
                  "Automatic scoping",
                  "No global CSS conflicts",
                  "Dynamic styling using props",
                ],
                realWorldNote:
                  "Most modern React codebases use some form of CSS-in-JS or utility-first CSS",
              },

              whyCssInJsExists: {
                title: "Why CSS-in-JS Exists",
                explanation:
                  "Traditional CSS does not scale well with large component-based applications.",
                problemsSolved: [
                  "Class name collisions",
                  "Dead/unused CSS",
                  "Hard-to-maintain global styles",
                ],
                interviewInsight:
                  "CSS-in-JS aligns styling with component-driven architecture",
              },

              librariesComparison: {
                title: "Styled Components vs Emotion",
                styledComponents: {
                  특징: "More opinionated and widely adopted",
                  usage: "Popular in design systems and startups",
                },
                emotion: {
                  특징: "More flexible and performance-focused",
                  usage: "Used in libraries like MUI",
                },
                bestPractice:
                  "Choose one and stay consistent across the project",
              },

              prosAndCons: {
                title: "Pros & Cons of CSS-in-JS",
                pros: [
                  "Scoped styles by default",
                  "Dynamic styling with props",
                  "Easier refactoring",
                  "Better component encapsulation",
                ],
                cons: [
                  "Runtime overhead",
                  "Learning curve",
                  "Not ideal for simple static sites",
                ],
              },

              whenToUse: {
                title: "When to Use CSS-in-JS",
                useCases: [
                  "Large React applications",
                  "Design systems",
                  "Highly dynamic UIs",
                ],
                avoidWhen: "Simple static websites or content-heavy pages",
              },

              commonMistakes: [
                {
                  mistake: "Overusing dynamic styles",
                  issue: "Performance overhead",
                  fix: "Use props only when necessary",
                },
                {
                  mistake: "Mixing multiple styling systems",
                  issue: "Inconsistent codebase",
                  fix: "Stick to one styling approach",
                },
              ],

              interviewQuestions: [
                "What is CSS-in-JS and why is it used?",
                "Styled-components vs Emotion?",
                "What are drawbacks of CSS-in-JS?",
                "When would you avoid CSS-in-JS?",
              ],
            },

            tasks: [
              "Install styled-components or emotion",
              "Create a styled Button component",
              "Pass props to styled components",
              "Compare CSS vs CSS-in-JS approach",
            ],

            examples: [
              "const Button = styled.button`padding: 12px;`",
              "<Button primary />",
            ],

            useCases: [
              "Component-based applications",
              "Design systems",
              "Large React codebases",
              "Scalable UI architecture",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand why CSS-in-JS exists",
              "Learn core CSS-in-JS concepts",
              "Compare popular CSS-in-JS libraries",
              "Know when to use or avoid CSS-in-JS",
              "Build foundation for styled-components and Emotion",
            ],
          },
          {
            day: 2,
            topic: "Dynamic Styling & Props-based Styles",

            subTopics: [
              "Passing props to styled components",
              "Conditional styling",
              "Variant-based components",
              "Handling hover and focus states",
              "Avoiding style duplication",
            ],

            explanation:
              "Dynamic styling allows components to adapt their appearance based on props. This is the foundation of reusable, scalable design systems used in production React applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Design-System Button Challenge",
                description:
                  "Build a single Button component that scales without style duplication.",
                tasks: [
                  "Create Button with variant prop (primary, secondary, danger)",
                  "Add size variants (sm, md, lg)",
                  "Ensure hover & focus styles are consistent across variants",
                  "Remove all boolean styling props except variant/size",
                ],
                outcome:
                  "You understand how real design-system components are structured.",
              },
              {
                id: "pt-2",
                title: "Refactor Duplication to Variants",
                description:
                  "Convert messy conditional styling into clean variant-based logic.",
                tasks: [
                  "Take a component with multiple boolean props",
                  "Replace booleans with a single variant prop",
                  "Extract shared base styles",
                  "Reduce conditional branches by at least 50%",
                ],
                qualityRule: "If styles feel hard to read, the API is wrong.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "Component API design matters more than styling syntax.",
              keyShift: [
                "From multiple booleans → single variant prop",
                "From copy-paste styles → shared base styles",
                "From ad-hoc decisions → consistent design rules",
              ],
              scalingRule:
                "If adding a new variant feels painful, the component is not scalable.",
            },

            interviewInsights: {
              seniorSignals: [
                "Uses variant-based styling instead of many boolean props",
                "Talks about API cleanliness, not just CSS",
                "Mentions accessibility for hover & focus states",
              ],
              idealAnswer:
                "I prefer variant-based components to keep styling predictable and scalable, especially in design systems.",
              redFlag:
                "I add new boolean props whenever a new style is needed.",
            },

            editorGuidance: {
              careerMindset: [
                "Styling is part of component API design",
                "Reusable components beat one-off UI fixes",
                "Consistency is a feature, not a constraint",
              ],
              seniorAdvice:
                "If your styled component needs many props, redesign the API.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world applications, UI components rarely have a single static style. Buttons, cards, and inputs change appearance based on state, variant, or user interaction. CSS-in-JS enables this dynamic behavior cleanly using props.",

              propsBasedStyling: {
                title: "Passing Props to Styled Components",
                explanation:
                  "Styled components can receive props and use them to dynamically compute styles.",
                conceptsUsed: [
                  "Props in styled components",
                  "Function-based styles",
                  "Boolean and value-based props",
                ],
                exampleLogic: `
const Button = styled.button\`
  background: \${props => props.primary ? '#3b82f6' : '#334155'};
\`;
      `,
                realWorldNote:
                  "This pattern is used heavily in design systems like Chakra UI and MUI",
              },

              conditionalStyling: {
                title: "Conditional Styling",
                explanation:
                  "Styles can be conditionally applied based on component state or props.",
                conceptsUsed: [
                  "Ternary conditions",
                  "Multiple prop checks",
                  "Readable conditional logic",
                ],
                bestPractice:
                  "Keep conditions simple to avoid unreadable style logic",
              },

              variantComponents: {
                title: "Variant-based Components",
                explanation:
                  "Variants allow one component to support multiple visual styles (primary, secondary, danger, etc.).",
                conceptsUsed: [
                  "Variant props",
                  "Centralized style decisions",
                  "Consistency across UI",
                ],
                exampleLogic: `
const Button = styled.button\`
  background: \${props => {
    if (props.variant === 'primary') return '#3b82f6';
    if (props.variant === 'secondary') return '#64748b';
    return '#334155';
  }};
\`;
      `,
                interviewInsight:
                  "Variant-based components are a core concept in design systems",
              },

              interactionStates: {
                title: "Hover & Focus States",
                explanation:
                  "Interactive states improve usability and accessibility.",
                conceptsUsed: [
                  ":hover pseudo-class",
                  ":focus-visible",
                  "Visual feedback",
                ],
                exampleLogic: `
&:hover {
  opacity: 0.9;
}

&:focus {
  outline: 2px solid #93c5fd;
}
      `,
                realWorldNote:
                  "Focus styles are critical for keyboard accessibility",
              },

              avoidingDuplication: {
                title: "Avoiding Style Duplication",
                explanation:
                  "Repeating style logic across components leads to inconsistency.",
                techniques: [
                  "Shared base components",
                  "Reusable style helpers",
                  "Variant abstraction",
                ],
                bestPractice:
                  "Create one flexible component instead of many similar ones",
              },

              commonMistakes: [
                {
                  mistake: "Too many conditional props",
                  issue: "Hard-to-maintain styles",
                  fix: "Use variants instead of multiple booleans",
                },
                {
                  mistake: "Copy-pasting styled components",
                  issue: "Inconsistent UI",
                  fix: "Abstract shared styles",
                },
              ],

              interviewQuestions: [
                "How do you implement variants in styled-components?",
                "How do props affect styling?",
                "How do you avoid duplicated styles?",
                "How do you handle hover and focus states?",
              ],
            },

            tasks: [
              "Create Button component with primary and secondary variants",
              "Apply conditional styling using props",
              "Add hover and focus states",
              "Refactor duplicated styles into reusable logic",
            ],

            examples: [
              "background: ${props => props.primary ? '#3b82f6' : '#334155'}",
              "<Button variant='primary' />",
            ],

            useCases: [
              "Reusable UI components",
              "Design systems",
              "Product UI libraries",
              "Scalable frontend architecture",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Understand dynamic styling with props",
              "Build variant-based reusable components",
              "Handle interaction states correctly",
              "Avoid style duplication in large codebases",
              "Prepare foundation for theming systems",
            ],
          },
          {
            day: 3,
            topic: "CSS Modules & Scoped Styling",

            subTopics: [
              "What are CSS Modules",
              "Local scoping of styles",
              "Class name collisions",
              "CSS Modules with React",
              "When to prefer CSS Modules",
            ],

            explanation:
              "CSS Modules provide locally scoped CSS by default, preventing class name conflicts and keeping styles predictable without runtime overhead.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "CSS Modules Refactor Drill",
                description:
                  "Refactor a globally styled component into a fully scoped CSS Module.",
                tasks: [
                  "Take a component using global CSS classes",
                  "Convert styles to .module.css",
                  "Replace className strings with styles bindings",
                  "Ensure no global class leakage remains",
                ],
                outcome:
                  "You can safely scale styles without worrying about collisions.",
              },
              {
                id: "pt-2",
                title: "Conditional Styling Without CSS-in-JS",
                description: "Handle UI states cleanly using CSS Modules only.",
                tasks: [
                  "Create active / inactive / disabled classes",
                  "Apply conditional className logic in JSX",
                  "Avoid inline styles completely",
                  "Keep class names semantic and readable",
                ],
                qualityRule:
                  "JS handles logic, CSS handles visuals — do not mix responsibilities.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "CSS Modules are about safety and predictability, not dynamism.",
              keyPrinciples: [
                "Build-time scoping beats runtime magic",
                "CSS stays CSS — no JS-driven styling logic",
                "Local styles make refactoring fearless",
              ],
              scalingRule:
                "If designers can read your CSS without JS knowledge, you’re doing it right.",
            },

            interviewInsights: {
              seniorSignals: [
                "Explains build-time scoping clearly",
                "Mentions zero runtime overhead",
                "Understands tradeoffs vs CSS-in-JS",
              ],
              idealAnswer:
                "I prefer CSS Modules when styles are mostly static and performance matters.",
              redFlag:
                "CSS Modules and global CSS are basically the same thing.",
            },

            editorGuidance: {
              careerMindset: [
                "Not every problem needs dynamic styling",
                "Predictability beats cleverness in large teams",
                "Styling strategy is an architectural decision",
              ],
              seniorAdvice:
                "If styles don’t depend on runtime state, CSS Modules are often the best choice.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications grow, global CSS becomes difficult to manage due to class name collisions and unintended overrides. CSS Modules solve this by scoping styles locally at build time, making them a popular choice in enterprise React applications.",

              cssModulesBasics: {
                title: "What are CSS Modules?",
                explanation:
                  "CSS Modules treat every CSS file as a locally scoped module. Class names are transformed into unique identifiers during build time.",
                conceptsUsed: [
                  "Build-time scoping",
                  "Hashed class names",
                  "No global namespace pollution",
                ],
                exampleLogic: `
/* Button.module.css */
.primary {
  background: #3b82f6;
  color: white;
}
      `,
                realWorldNote:
                  "CSS Modules are widely used in Next.js and enterprise React codebases",
              },

              localScoping: {
                title: "Local Scoping of Styles",
                explanation:
                  "Styles defined in a CSS Module only apply to the component that imports them.",
                conceptsUsed: [
                  "Scoped class names",
                  "Safe reuse of class names",
                  "Component-level styling",
                ],
                exampleLogic: `
import styles from './Button.module.css';

<button className={styles.primary}>Click</button>
      `,
                bestPractice:
                  "Use simple, semantic class names since scope is already local",
              },

              classCollisions: {
                title: "Avoiding Class Name Collisions",
                explanation:
                  "CSS Modules automatically generate unique class names, eliminating accidental overrides.",
                problemExample:
                  "Two different components using `.button` class in global CSS",
                solution:
                  "Each `.button` becomes uniquely scoped by the module system",
                interviewInsight:
                  "CSS Modules remove an entire category of CSS bugs",
              },

              reactIntegration: {
                title: "Using CSS Modules with React",
                explanation:
                  "React components import CSS Modules as JavaScript objects.",
                conceptsUsed: [
                  "ES module imports",
                  "className binding",
                  "Conditional class usage",
                ],
                exampleLogic: `
<div className={isActive ? styles.active : styles.inactive}></div>
      `,
                realWorldNote: "Works seamlessly with CRA, Vite, and Next.js",
              },

              whenToUse: {
                title: "When to Prefer CSS Modules",
                explanation:
                  "CSS Modules are best when teams prefer traditional CSS syntax with modern safety.",
                idealScenarios: [
                  "Large teams familiar with CSS",
                  "Performance-sensitive applications",
                  "Minimal runtime styling logic",
                ],
                comparison: {
                  cssModules: "Build-time, zero runtime cost",
                  cssInJs: "Runtime styling, dynamic but heavier",
                },
              },

              commonMistakes: [
                {
                  mistake: "Mixing global CSS and CSS Modules randomly",
                  issue: "Inconsistent styling strategy",
                  fix: "Define clear rules for global vs module styles",
                },
                {
                  mistake: "Overusing inline styles instead of modules",
                  issue: "Harder to maintain",
                  fix: "Prefer CSS Modules for layout and visuals",
                },
              ],

              interviewQuestions: [
                "What problem do CSS Modules solve?",
                "How are class names scoped in CSS Modules?",
                "CSS Modules vs styled-components?",
                "When would you NOT use CSS Modules?",
              ],
            },

            tasks: [
              "Convert a component from global CSS to CSS Modules",
              "Apply scoped styles using .module.css files",
              "Use conditional class names",
              "Compare same component styled with CSS Modules vs styled-components",
            ],

            examples: [
              "import styles from './Button.module.css'",
              "<button className={styles.primary}>",
            ],

            useCases: [
              "Enterprise React applications",
              "Performance-sensitive projects",
              "Teams preferring traditional CSS",
              "Large component-based UIs",
            ],

            editorType: "react",

            estimatedTime: "2 hours",

            objectives: [
              "Understand how CSS Modules scope styles locally",
              "Avoid class name collision issues",
              "Confidently use CSS Modules with React",
              "Know when to choose CSS Modules over CSS-in-JS",
              "Write maintainable, scalable component styles",
            ],
          },
          {
            day: 4,
            topic: "Tailwind CSS – Deep Dive",

            subTopics: [
              "Utility-first CSS philosophy",
              "Tailwind configuration",
              "Responsive utilities",
              "Custom themes and plugins",
              "Tailwind with React",
            ],

            explanation:
              "Tailwind CSS speeds up development by providing low-level utility classes that allow developers to build custom designs directly in markup without writing custom CSS.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "From Messy JSX to Clean Tailwind",
                description:
                  "Refactor an unreadable Tailwind-heavy component into a clean, reusable abstraction.",
                tasks: [
                  "Take a component with 15+ Tailwind classes",
                  "Extract it into a reusable UI component",
                  "Move repeated patterns into shared components",
                  "Keep JSX readable without losing Tailwind power",
                ],
                outcome:
                  "You learn how senior engineers keep Tailwind code maintainable.",
              },
              {
                id: "pt-2",
                title: "Design Tokens with Tailwind Config",
                description:
                  "Replace raw utility values with proper design tokens.",
                tasks: [
                  "Move colors into tailwind.config.js",
                  "Define spacing and font scale tokens",
                  "Refactor components to use token-based utilities",
                  "Remove all hardcoded hex values from JSX",
                ],
                qualityRule:
                  "If you see hex codes in JSX, your design system is leaking.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "Tailwind shifts complexity from CSS files to architectural discipline.",
              keyPrinciples: [
                "Utilities scale only with strong component boundaries",
                "Config-driven design beats ad-hoc styling",
                "Tailwind is fast — misuse makes it messy",
              ],
              scalingRule:
                "Tailwind without component abstraction becomes technical debt.",
            },

            interviewInsights: {
              seniorSignals: [
                "Talks about Tailwind config as design system",
                "Mentions clsx / classnames for conditional logic",
                "Understands tradeoffs vs CSS Modules & CSS-in-JS",
              ],
              idealAnswer:
                "Tailwind works best when combined with reusable components and design tokens.",
              redFlag: "I just keep adding classes directly in JSX.",
            },

            editorGuidance: {
              careerMindset: [
                "Speed without structure doesn’t scale",
                "Utility-first still needs architecture",
                "Design consistency is enforced, not hoped for",
              ],
              seniorAdvice:
                "If Tailwind code looks ugly, the problem is component design—not Tailwind.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Tailwind CSS follows a utility-first approach where styles are composed using small, single-purpose classes. This approach removes the need for naming CSS classes and drastically improves development speed while maintaining design consistency.",

              utilityFirst: {
                title: "Utility-First CSS Philosophy",
                explanation:
                  "Instead of writing custom CSS classes, Tailwind provides atomic utility classes that can be combined to build any UI.",
                conceptsUsed: [
                  "Single-responsibility utility classes",
                  "Composition over abstraction",
                  "No context switching between CSS and JSX",
                ],
                exampleLogic: `
<button className="bg-blue-500 text-white px-4 py-2 rounded">
  Save
</button>
      `,
                realWorldNote:
                  "Most fast-moving startups and SaaS teams use Tailwind for speed",
              },

              tailwindConfig: {
                title: "Tailwind Configuration",
                explanation:
                  "Tailwind is highly configurable using tailwind.config.js to define colors, spacing, fonts, and breakpoints.",
                conceptsUsed: [
                  "theme extension",
                  "custom colors and fonts",
                  "content scanning",
                ],
                exampleLogic: `
module.exports = {
  theme: {
    extend: {
      colors: {
        brand: '#3b82f6',
      },
    },
  },
};
      `,
                bestPractice:
                  "Always extend theme instead of overriding defaults",
              },

              responsiveUtilities: {
                title: "Responsive Utilities",
                explanation:
                  "Tailwind provides mobile-first responsive utilities using breakpoint prefixes.",
                conceptsUsed: [
                  "sm, md, lg, xl prefixes",
                  "Mobile-first design",
                  "Conditional styling by screen size",
                ],
                exampleLogic: `
<div className="p-4 md:p-8 lg:p-12"></div>
      `,
                interviewInsight:
                  "Tailwind responsiveness removes the need for media queries in most cases",
              },

              reactIntegration: {
                title: "Using Tailwind with React",
                explanation:
                  "Tailwind works seamlessly with React by applying utility classes directly to JSX.",
                conceptsUsed: [
                  "Conditional class composition",
                  "Reusable components",
                  "ClassName management",
                ],
                exampleLogic: `
<button className={isPrimary 
  ? 'bg-blue-500 text-white' 
  : 'bg-gray-200 text-black'}>
</button>
      `,
                realWorldNote: "Often combined with clsx or classnames library",
              },

              customizationAndPlugins: {
                title: "Custom Themes & Plugins",
                explanation:
                  "Tailwind allows creating custom utilities and plugins for design systems.",
                conceptsUsed: [
                  "Custom plugins",
                  "Design tokens",
                  "Reusable patterns",
                ],
                exampleLogic: `
plugins: [
  function({ addUtilities }) {
    addUtilities({
      '.center': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    })
  }
]
      `,
                bestPractice:
                  "Create design tokens instead of repeating raw values",
              },

              commonMistakes: [
                {
                  mistake: "Overloading JSX with too many classes",
                  issue: "Reduced readability",
                  fix: "Extract reusable components",
                },
                {
                  mistake: "Not using config file",
                  issue: "Inconsistent design",
                  fix: "Centralize colors, spacing, fonts",
                },
              ],

              interviewQuestions: [
                "What is utility-first CSS?",
                "Tailwind vs CSS Modules?",
                "How does Tailwind handle responsiveness?",
                "How do you manage conditional styles in Tailwind?",
              ],
            },

            tasks: [
              "Setup Tailwind CSS in a React project",
              "Build reusable UI components using Tailwind",
              "Apply responsive utilities",
              "Customize theme colors and spacing",
              "Refactor CSS-based component into Tailwind",
            ],

            examples: [
              "className='bg-blue-500 text-white px-4 py-2 rounded'",
              "md:grid-cols-3 lg:grid-cols-4",
            ],

            useCases: [
              "Rapid UI development",
              "Startup MVPs",
              "Design-consistent products",
              "Component-driven architectures",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand Tailwind’s utility-first philosophy",
              "Build responsive UIs without writing custom CSS",
              "Customize Tailwind using configuration",
              "Use Tailwind effectively with React",
              "Decide when Tailwind is better than CSS Modules or CSS-in-JS",
            ],
          },
          {
            day: 5,
            topic: "Theme Management & Dark Mode",

            subTopics: [
              "ThemeProvider in styled-components",
              "CSS variables for theming",
              "Light/Dark mode patterns",
              "Persisting theme preference",
              "System theme detection",
            ],

            explanation:
              "Theming allows applications to support multiple visual styles (like light and dark mode) while keeping components consistent, scalable, and user-friendly.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Theme Architecture",
                description:
                  "Design a scalable theming system used in real SaaS products.",
                tasks: [
                  "Create semantic design tokens (bg, surface, text, accent)",
                  "Implement theme switching using CSS variables",
                  "Wrap app with ThemeProvider only for non-CSS values",
                  "Ensure zero style duplication across themes",
                ],
                outcome:
                  "You understand how real design systems manage themes without chaos.",
              },
              {
                id: "pt-2",
                title: "Dark Mode Without Flicker",
                description:
                  "Solve the most common real-world dark mode bug: initial theme flicker.",
                tasks: [
                  "Read theme preference before first render",
                  "Sync system preference with stored theme",
                  "Prevent hydration mismatch in React",
                  "Test refresh behavior in both modes",
                ],
                qualityRule:
                  "If theme flashes on reload, the implementation is not production-ready.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "Theming is an architectural concern, not a styling feature.",
              keyPrinciples: [
                "Themes are contracts, not color collections",
                "CSS variables scale better than JS-driven styles",
                "Semantic tokens outlive visual redesigns",
              ],
              scalingRule:
                "If changing a brand color requires touching components, the theme system is broken.",
            },

            interviewInsights: {
              seniorSignals: [
                "Mentions CSS variables + Context combo",
                "Talks about theme hydration and flicker issues",
                "Uses semantic tokens instead of raw colors",
              ],
              idealAnswer:
                "I prefer CSS variables for runtime theming and use ThemeProvider only for computed or JS-driven values.",
              redFlag: "I just toggle a class and change colors manually.",
            },

            editorGuidance: {
              careerMindset: [
                "Dark mode is expected, not a feature",
                "Themes should survive redesigns",
                "Consistency beats customization",
              ],
              seniorAdvice:
                "If your components know about colors, your theme system has failed.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern applications are expected to support light and dark themes. Theme management ensures consistent colors, spacing, and typography across the app while allowing users to personalize their experience.",

              themeConcepts: {
                title: "Core Theming Concepts",
                explanation:
                  "A theme is a centralized collection of design tokens such as colors, fonts, and spacing that can be shared across components.",
                conceptsUsed: [
                  "Design tokens",
                  "Centralized style configuration",
                  "Consistent UI behavior",
                ],
                realWorldNote:
                  "Most design systems (Material UI, Chakra, Ant Design) are theme-driven",
              },

              styledComponentsTheme: {
                title: "ThemeProvider in styled-components",
                explanation:
                  "ThemeProvider allows you to pass theme values to all styled components using React context.",
                conceptsUsed: [
                  "ThemeProvider wrapper",
                  "Accessing theme via props",
                  "Multiple theme objects",
                ],
                exampleLogic: `
const theme = {
  colors: {
    primary: '#3b82f6',
    background: '#ffffff',
  },
};

<ThemeProvider theme={theme}>
  <App />
</ThemeProvider>
      `,
                bestPractice:
                  "Keep theme values minimal and semantic (primary, background, text)",
              },

              cssVariables: {
                title: "CSS Variables for Theming",
                explanation:
                  "CSS variables allow runtime theme switching without reloading styles.",
                conceptsUsed: [
                  ":root variables",
                  "Dynamic updates",
                  "Framework-agnostic theming",
                ],
                exampleLogic: `
:root {
  --bg-color: #ffffff;
  --text-color: #000000;
}

[data-theme='dark'] {
  --bg-color: #020617;
  --text-color: #ffffff;
}
      `,
                realWorldNote:
                  "CSS variables are the most scalable theming solution",
              },

              darkModePatterns: {
                title: "Light / Dark Mode Patterns",
                explanation:
                  "Dark mode can be implemented using state, context, or CSS-only approaches.",
                conceptsUsed: [
                  "Global theme state",
                  "Conditional styles",
                  "Context-based toggling",
                ],
                exampleLogic: `
const [theme, setTheme] = useState('light');
setTheme(prev => prev === 'light' ? 'dark' : 'light');
      `,
                interviewInsight:
                  "Dark mode is often implemented using Context + CSS variables",
              },

              persistence: {
                title: "Persisting Theme Preference",
                explanation:
                  "User-selected theme should persist across sessions using localStorage.",
                conceptsUsed: [
                  "localStorage",
                  "Initial theme hydration",
                  "Syncing state with storage",
                ],
                exampleLogic: `
localStorage.setItem('theme', theme);
const savedTheme = localStorage.getItem('theme');
      `,
                bestPractice: "Always read stored theme before first render",
              },

              systemTheme: {
                title: "System Theme Detection",
                explanation:
                  "Apps can automatically match the user’s system theme using media queries.",
                conceptsUsed: [
                  "prefers-color-scheme",
                  "Auto theme selection",
                  "Fallback logic",
                ],
                exampleLogic: `
const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      `,
                realWorldNote:
                  "Most production apps default to system theme on first visit",
              },

              commonMistakes: [
                {
                  mistake: "Hardcoding colors in components",
                  issue: "Theme inconsistency",
                  fix: "Always use theme variables",
                },
                {
                  mistake: "Theme flicker on load",
                  issue: "Bad UX",
                  fix: "Read theme before render",
                },
                {
                  mistake: "Too many theme values",
                  issue: "Hard to maintain",
                  fix: "Use semantic tokens",
                },
              ],

              interviewQuestions: [
                "How do you implement dark mode in React?",
                "ThemeProvider vs CSS variables?",
                "How do you persist user theme preference?",
                "How do you detect system theme?",
              ],
            },

            tasks: [
              "Create light and dark theme objects",
              "Implement theme toggle using state or context",
              "Persist selected theme in localStorage",
              "Apply theme styles across components",
              "Add system theme detection fallback",
            ],

            examples: [
              "const theme = { colors: { primary: '#3b82f6' } }",
              "data-theme='dark'",
            ],

            useCases: [
              "Modern SaaS products",
              "User-personalized applications",
              "Accessibility-focused design",
              "Design systems",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand theme management in modern frontend apps",
              "Implement light and dark mode cleanly",
              "Persist user preferences safely",
              "Use CSS variables or ThemeProvider effectively",
              "Build scalable, design-system-ready theming",
            ],
          },
          {
            day: 6,
            topic: "Styling Architecture & Best Practices",

            subTopics: [
              "Component styling patterns",
              "Design tokens",
              "Avoiding style leakage",
              "Scalable folder structures",
              "Styling performance considerations",
            ],

            explanation:
              "Good styling architecture ensures long-term maintainability, team collaboration, and consistent performance in large-scale frontend applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Design-System–First Styling Refactor",
                description:
                  "Refactor an existing UI to follow true design-system principles.",
                tasks: [
                  "Extract all colors, spacing, typography into design tokens",
                  "Replace hardcoded values with semantic tokens",
                  "Ensure components never import raw colors directly",
                  "Validate that theme changes require zero component edits",
                ],
                outcome:
                  "You think in terms of systems, not individual components.",
              },
              {
                id: "pt-2",
                title: "Style Leakage Audit (Real-World Debugging)",
                description:
                  "Simulate and fix CSS leakage issues found in legacy codebases.",
                tasks: [
                  "Identify global selectors affecting multiple components",
                  "Remove tag-based and overly generic class selectors",
                  "Migrate leaking styles to scoped solutions",
                  "Verify isolation by moving components across pages",
                ],
                qualityRule:
                  "If a component breaks when moved, your styling architecture is wrong.",
              },
              {
                id: "pt-3",
                title: "Styling Performance Review",
                description:
                  "Analyze how styling decisions impact runtime performance.",
                tasks: [
                  "Identify components with heavy dynamic styling",
                  "Replace inline styles in render loops",
                  "Reduce style recalculations caused by props churn",
                  "Measure re-renders before vs after refactor",
                ],
                outcome:
                  "You understand how styling affects performance, not just visuals.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "Styling architecture is about protecting the future, not decorating the present.",
              keyPrinciples: [
                "Components consume styles, they don’t define design",
                "Design tokens are contracts between design and code",
                "Isolation is more important than convenience",
              ],
              scalingRule:
                "If adding a new feature requires touching old component styles, the architecture does not scale.",
            },

            interviewInsights: {
              seniorSignals: [
                "Separates tokens from components",
                "Talks about style leakage as a real production issue",
                "Mentions performance impact of dynamic styling",
              ],
              idealAnswer:
                "We use design tokens as the single source of truth and keep component styles fully scoped to avoid leakage and refactor pain.",
              redFlag:
                "We mostly add styles where needed and fix conflicts later.",
            },

            editorGuidance: {
              careerMindset: [
                "Styling is an architectural decision",
                "Consistency beats creativity in large teams",
                "Good styling systems reduce meetings, bugs, and regressions",
              ],
              seniorAdvice:
                "If designers and developers don’t share tokens, you don’t have a design system.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As applications scale, poor styling decisions lead to bugs, inconsistency, and slow development. Styling architecture focuses on organizing styles in a predictable, reusable, and performance-friendly way.",

              componentStylingPatterns: {
                title: "Component Styling Patterns",
                explanation:
                  "Each component should own its styles while still following global design rules.",
                patterns: [
                  "One component = one style file",
                  "Colocate styles with components",
                  "Avoid deeply nested selectors",
                ],
                exampleStructure: `
Button/
 ├─ Button.jsx
 ├─ Button.styles.js
 └─ index.js
      `,
                bestPractice:
                  "If a component moves, its styles should move with it",
              },

              designTokens: {
                title: "Design Tokens",
                explanation:
                  "Design tokens are reusable values for colors, spacing, fonts, and shadows.",
                conceptsUsed: [
                  "Semantic naming",
                  "Single source of truth",
                  "Theme consistency",
                ],
                exampleLogic: `
export const colors = {
  primary: '#3b82f6',
  background: '#020617',
  text: '#ffffff',
};
      `,
                realWorldNote:
                  "Design tokens are shared between designers and developers in real companies",
              },

              avoidingLeakage: {
                title: "Avoiding Style Leakage",
                explanation:
                  "Style leakage happens when styles unintentionally affect other components.",
                commonCauses: [
                  "Global CSS selectors",
                  "Generic class names",
                  "Overusing !important",
                ],
                solutions: [
                  "Scoped styling (CSS Modules / CSS-in-JS)",
                  "BEM or utility-first approaches",
                  "Avoid tag selectors",
                ],
                interviewInsight:
                  "Style leakage is a common bug in legacy codebases",
              },

              folderStructure: {
                title: "Scalable Styling Folder Structures",
                explanation:
                  "A predictable structure helps teams scale without confusion.",
                exampleStructures: {
                  componentBased: `
components/
 ├─ Button/
 │   ├─ Button.jsx
 │   ├─ Button.styles.js
 │   └─ index.js
        `,
                  tokensBased: `
styles/
 ├─ tokens/
 │   ├─ colors.js
 │   ├─ spacing.js
 │   └─ typography.js
 ├─ global.css
        `,
                },
                bestPractice:
                  "Combine component-based styles with shared design tokens",
              },

              performanceConsiderations: {
                title: "Styling Performance Considerations",
                explanation:
                  "Styling choices can impact runtime performance and bundle size.",
                conceptsUsed: [
                  "Avoid unnecessary re-renders due to styles",
                  "Limit dynamic styles",
                  "Reduce CSS payload size",
                ],
                examples: [
                  "Avoid inline styles inside render loops",
                  "Prefer static classes where possible",
                ],
                realWorldNote:
                  "Performance issues often come from styling, not JavaScript logic",
              },

              commonMistakes: [
                {
                  mistake: "Mixing multiple styling systems randomly",
                  issue: "Inconsistent UI and confusion",
                  fix: "Define a single styling strategy",
                },
                {
                  mistake: "Hardcoding colors everywhere",
                  issue: "Difficult theme changes",
                  fix: "Use design tokens",
                },
                {
                  mistake: "Global CSS for everything",
                  issue: "Style conflicts",
                  fix: "Use scoped styles",
                },
              ],

              interviewQuestions: [
                "How do you structure styles in a large React app?",
                "What are design tokens?",
                "How do you prevent CSS conflicts?",
                "CSS Modules vs CSS-in-JS — when to use what?",
              ],
            },

            tasks: [
              "Refactor styles into reusable design tokens",
              "Create scalable styling folder structure",
              "Remove global style leakage",
              "Apply consistent styling patterns",
              "Review styling performance impact",
            ],

            examples: [
              "tokens/colors.js",
              "components/Button/Button.styles.js",
            ],

            useCases: [
              "Design systems",
              "Large frontend teams",
              "Enterprise products",
              "Long-term maintainable codebases",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Design scalable styling architecture",
              "Apply design tokens effectively",
              "Prevent CSS conflicts and leakage",
              "Improve styling performance awareness",
              "Write production-grade frontend styles",
            ],
          },
          {
            day: 7,
            topic: "Final Project – Design System",

            subTopics: [
              "Reusable components",
              "Theme support",
              "Styling consistency",
              "Documentation",
              "Scalable architecture",
            ],

            explanation:
              "This project showcases your ability to design, build, and maintain a professional design system that can be reused across multiple real-world applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Design System from Zero (Production Rules)",
                description:
                  "Build a real design system the way companies expect, not a UI demo.",
                tasks: [
                  "Create design tokens (colors, spacing, typography, radii)",
                  "Ensure components NEVER use raw values",
                  "Expose only approved variants and sizes",
                  "Add theme switching without touching components",
                ],
                qualityGate:
                  "If product teams can misuse components, the design system is incomplete.",
              },
              {
                id: "pt-2",
                title: "Component API Discipline",
                description:
                  "Refine component APIs to prevent misuse and inconsistency.",
                tasks: [
                  "Audit props for each component",
                  "Remove unnecessary flexibility",
                  "Convert boolean props into controlled variants",
                  "Document allowed combinations clearly",
                ],
                outcome:
                  "Your components guide developers instead of trusting them.",
              },
              {
                id: "pt-3",
                title: "Documentation-Driven Adoption",
                description:
                  "Design systems fail without adoption — documentation drives usage.",
                tasks: [
                  "Document each component’s intent, not just props",
                  "Add do/don’t usage examples",
                  "Show variant comparisons side by side",
                  "Write copy as if onboarding a new team member",
                ],
                realWorldRule:
                  "If a new developer can’t use it without asking, docs are insufficient.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "A design system is a contract, not a component collection.",
              nonNegotiables: [
                "Single source of truth via tokens",
                "No raw styles inside components",
                "One public entry point for consumers",
                "Backward compatibility mindset",
              ],
              scalingRule:
                "If adding a new component requires refactoring old ones, the system is fragile.",
            },

            interviewInsights: {
              seniorSignals: [
                "Talks about constraints, not flexibility",
                "Mentions adoption and misuse prevention",
                "Explains difference between UI kit and design system",
              ],
              strongAnswer:
                "A design system enforces consistency through tokens, constrained APIs, and documentation — not developer discipline.",
              redFlag: "We let developers customize styles as needed.",
            },

            editorGuidance: {
              careerMindset: [
                "Design systems are about trust at scale",
                "Constraints create consistency",
                "Good systems reduce cognitive load for teams",
              ],
              seniorAdvice:
                "If your design system feels flexible, it will break in production.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A design system is not just a UI kit — it is a shared language between designers and developers. Companies rely on design systems to ensure consistency, speed up development, and scale products efficiently.",

              projectGoal: {
                title: "Project Goal",
                explanation:
                  "Build a reusable, themeable React design system that includes core UI components, follows styling best practices, and is documented for developer usage.",
                realWorldNote:
                  "Most large companies (Google, Airbnb, Shopify) rely heavily on internal design systems",
              },

              reusableComponents: {
                title: "Reusable UI Components",
                explanation:
                  "Components should be generic, configurable via props, and reusable across different contexts.",
                componentsToBuild: [
                  "Button",
                  "Card",
                  "Input",
                  "Modal",
                  "Alert / Badge",
                ],
                conceptsUsed: [
                  "Props-based customization",
                  "Variant patterns",
                  "Component isolation",
                ],
                exampleLogic: `
<Button variant="primary" size="md" disabled />
      `,
                bestPractice: "A component should not assume where it is used",
              },

              themeSupport: {
                title: "Theme Support (Light / Dark)",
                explanation:
                  "The design system should support theming using a centralized theme configuration.",
                conceptsUsed: [
                  "ThemeProvider",
                  "Design tokens",
                  "CSS variables or theme objects",
                ],
                exampleLogic: `
const theme = {
  colors: {
    primary: '#3b82f6',
    background: '#020617',
  },
};
      `,
                realWorldNote:
                  "Theme switching is expected in modern SaaS products",
              },

              stylingConsistency: {
                title: "Styling Consistency",
                explanation:
                  "Consistency ensures the same spacing, colors, typography, and behavior across all components.",
                techniques: [
                  "Design tokens for colors and spacing",
                  "Consistent border radius and shadows",
                  "Shared typography rules",
                ],
                exampleTokens: `
export const spacing = {
  sm: '8px',
  md: '16px',
  lg: '24px',
};
      `,
                interviewInsight:
                  "Inconsistent UI is often a sign of weak frontend architecture",
              },

              documentation: {
                title: "Component Documentation",
                explanation:
                  "Documentation helps other developers understand how to use components correctly.",
                contentToDocument: [
                  "Component purpose",
                  "Available props",
                  "Variants and examples",
                ],
                exampleDocs: `
<Button variant="secondary">
  Cancel
</Button>
      `,
                toolsNote: "Storybook is commonly used in real teams",
              },

              architecture: {
                title: "Scalable Design System Architecture",
                explanation:
                  "A clean folder and export structure makes the design system scalable.",
                exampleStructure: `
design-system/
 ├─ components/
 │   ├─ Button/
 │   ├─ Card/
 │   └─ Modal/
 ├─ themes/
 ├─ tokens/
 └─ index.js
      `,
                bestPractice:
                  "Consumers should import from a single entry point",
              },

              commonMistakes: [
                {
                  mistake: "Hardcoding styles inside components",
                  issue: "Difficult to scale and theme",
                  fix: "Use tokens and theme values",
                },
                {
                  mistake: "Over-customizable components",
                  issue: "Inconsistent UI",
                  fix: "Limit props to approved variants",
                },
                {
                  mistake: "No documentation",
                  issue: "Low adoption by team",
                  fix: "Document every public component",
                },
              ],

              interviewQuestions: [
                "What is a design system?",
                "How do you ensure consistency across components?",
                "How do you implement theming?",
                "How would you scale this design system?",
                "Difference between UI library and design system?",
              ],
            },

            tasks: [
              "Build reusable UI components (Button, Card, Modal, Input)",
              "Implement light and dark theme support",
              "Use design tokens for styling consistency",
              "Document component usage with examples",
              "Ensure responsive and accessible components",
            ],

            examples: [
              "Button, Card, Modal, Input components",
              "Theme toggle demo",
              "Documented component usage",
            ],

            useCases: [
              "Portfolio showcase",
              "Real-world SaaS products",
              "Frontend interviews",
              "Company-wide UI consistency",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Understand what a real design system is",
              "Build scalable and reusable UI components",
              "Implement professional theming strategies",
              "Apply styling consistency at scale",
              "Showcase senior-level frontend architecture skills",
            ],
          },
        ],
      },
      {
        week: 29,
        weekGoal:
          "Master global state management using Redux Toolkit and modern lightweight alternatives",
        weekOutcome:
          "Ability to manage complex application state in scalable, production-ready React apps",
        miniProject: "Complex State Management Application",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Introduction to Global State Management",

            subTopics: [
              "What is state in React",
              "Local vs global state",
              "Problems with prop drilling",
              "When global state is needed",
              "State management patterns overview",
            ],

            explanation:
              "Global state management helps share and control data across multiple components without passing props deeply, which becomes essential in medium to large applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "State Classification Drill (Real App Thinking)",
                description:
                  "Train your brain to classify state correctly before introducing any library.",
                tasks: [
                  "List all states in a real app (auth, cart, UI, filters)",
                  "Mark each as local, lifted, or global",
                  "Justify WHY each state belongs there",
                  "Identify one state commonly misused as global",
                ],
                outcome: "You stop using global state as a default solution.",
              },
              {
                id: "pt-2",
                title: "Prop Drilling Smell Detection",
                description:
                  "Learn to detect prop drilling before it becomes a problem.",
                tasks: [
                  "Draw a component tree on paper",
                  "Mark props that pass through 2+ unnecessary layers",
                  "Decide whether lifting or global state is better",
                  "Explain trade-offs in one paragraph",
                ],
                qualityGate:
                  "If you can’t explain the trade-off, you don’t understand the state.",
              },
              {
                id: "pt-3",
                title: "Over-Engineering Awareness Exercise",
                description: "Prevent Redux-before-needed syndrome.",
                tasks: [
                  "Take a small app example",
                  "List reasons NOT to use global state",
                  "Rewrite solution using only local state + lifting",
                  "Compare complexity vs benefit",
                ],
                realWorldRule:
                  "Most apps fail due to over-engineering, not lack of tools.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "State location is an architectural decision, not a syntax choice.",
              decisionFramework: [
                "Who needs this state?",
                "How often does it change?",
                "Does it survive navigation?",
                "Does unrelated UI depend on it?",
              ],
              antiPattern: "Introducing global state before pain is felt.",
              scalingRule:
                "Start local → lift → context → external store (in that order).",
            },

            interviewInsights: {
              seniorSignals: [
                "Explains why state should NOT be global",
                "Mentions cost of global state (rerenders, complexity)",
                "Chooses simplest solution first",
              ],
              strongAnswer:
                "Global state is for shared, long-lived, cross-cutting data — not convenience.",
              redFlag: "I use Redux for all state.",
            },

            editorGuidance: {
              careerMindset: [
                "State placement defines app architecture",
                "Global state is powerful but expensive",
                "Good engineers minimize shared mutable state",
              ],
              seniorAdvice:
                "If you’re confused about state, don’t add a library — simplify the model.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As React applications grow, managing state only at component level becomes painful. Global state management solves data sharing problems that arise in real-world production apps.",

              whatIsState: {
                title: "What is State in React?",
                explanation:
                  "State represents dynamic data that affects what a component renders.",
                examples: ["Logged-in user", "Cart items", "Theme selection"],
                interviewInsight: "State changes trigger re-renders in React",
              },

              localVsGlobal: {
                title: "Local vs Global State",
                localState: {
                  meaning:
                    "State used by a single component or tightly related components",
                  examples: ["Input value", "Modal open/close"],
                },
                globalState: {
                  meaning: "State shared across many unrelated components",
                  examples: ["Auth state", "Cart data", "Theme"],
                },
                bestPractice:
                  "Not all state should be global — global state has a cost",
              },

              propDrilling: {
                title: "Problems with Prop Drilling",
                explanation:
                  "Prop drilling happens when data is passed through many layers of components that don’t actually need it.",
                issues: [
                  "Hard to maintain",
                  "Unreadable component trees",
                  "Tight coupling between components",
                ],
                exampleScenario:
                  "Passing user data from App → Layout → Sidebar → Profile",
                realWorldNote:
                  "Prop drilling is a common reason teams adopt global state solutions",
              },

              whenToUseGlobalState: {
                title: "When Global State is Needed",
                indicators: [
                  "Same data used in many distant components",
                  "State needs to persist across routes",
                  "Frequent syncing between components",
                ],
                warning:
                  "Using global state too early leads to over-engineering",
              },

              patternsOverview: {
                title: "State Management Patterns Overview",
                patterns: [
                  {
                    name: "Lifting State Up",
                    useCase: "Small apps, few shared components",
                  },
                  {
                    name: "Context API",
                    useCase: "Medium apps, theme/auth state",
                  },
                  {
                    name: "Context + useReducer",
                    useCase: "Predictable global state",
                  },
                  {
                    name: "External Libraries (Redux, Zustand)",
                    useCase: "Large-scale production apps",
                  },
                ],
                interviewInsight:
                  "Interviewers want to know WHY you choose a pattern, not just HOW",
              },

              commonMistakes: [
                {
                  mistake: "Putting everything in global state",
                  issue: "Unnecessary complexity",
                  fix: "Keep state as local as possible",
                },
                {
                  mistake: "Ignoring prop drilling early",
                  issue: "Messy architecture later",
                  fix: "Refactor shared state early",
                },
              ],

              interviewQuestions: [
                "What is global state?",
                "When should you avoid global state?",
                "What is prop drilling?",
                "How do you decide local vs global state?",
              ],
            },

            tasks: [
              "Identify global vs local state in an existing React app",
              "Refactor a prop-drilling example on paper or code",
              "List state that should NOT be global",
            ],

            examples: [
              "User authentication state",
              "Theme and language preference",
              "Shopping cart data",
            ],

            useCases: [
              "E-commerce platforms",
              "Dashboards",
              "Social media applications",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand what state really represents in React",
              "Differentiate between local and global state clearly",
              "Identify prop drilling problems confidently",
              "Know when global state is actually required",
              "Build a strong foundation before learning Context or Redux",
            ],
          },
          {
            day: 2,
            topic: "Redux Fundamentals",

            subTopics: [
              "Redux core principles",
              "Store, actions, reducers",
              "Unidirectional data flow",
              "Pure reducers",
              "Redux architecture",
            ],

            explanation:
              "Redux centralizes application state in a single store, making state changes predictable, traceable, and easier to debug in large-scale applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Redux Mental Model Exercise",
                description:
                  "Redux ko syntax nahi, data-flow system ki tarah samajhna.",
                tasks: [
                  "Draw Redux flow on paper (UI → action → reducer → store → UI)",
                  "Explain each step in plain English (no Redux terms)",
                  "Identify where bugs usually hide in this flow",
                  "Rewrite the same flow for a real feature (login or cart)",
                ],
                outcome:
                  "Redux stops feeling complex and starts feeling mechanical.",
              },
              {
                id: "pt-2",
                title: "Reducer Purity Stress Test",
                description:
                  "Learn what actually breaks Redux when reducers are impure.",
                tasks: [
                  "Write a reducer that mutates state intentionally",
                  "Observe UI + DevTools behavior",
                  "Fix it using immutable patterns",
                  "Explain why mutation breaks predictability",
                ],
                qualityGate:
                  "If you cannot explain why mutation is dangerous, Redux will confuse you later.",
              },
              {
                id: "pt-3",
                title: "Redux vs No-Redux Comparison",
                description: "Avoid Redux misuse by understanding trade-offs.",
                tasks: [
                  "Build same counter with useState",
                  "Build it again with Redux",
                  "Compare lines of code, complexity, benefits",
                  "Write 3 rules for when Redux is justified",
                ],
                realWorldRule: "Redux should solve pain, not create ceremony.",
              },
            ],

            architectureNotes: {
              coreIdea: "Redux is about predictability, not convenience.",
              mentalModel: [
                "Actions describe events, not logic",
                "Reducers describe state transitions, not behavior",
                "Store is a snapshot of app state, not a database",
              ],
              scalingRule:
                "Redux shines when many people touch the same state.",
              antiPattern: "Using Redux as a replacement for component state.",
            },

            interviewInsights: {
              seniorSignals: [
                "Explains Redux without mentioning libraries",
                "Talks about predictability and debugging",
                "Mentions trade-offs of Redux",
              ],
              strongAnswer:
                "Redux enforces discipline around how and when state changes.",
              redFlag:
                "Redux is used because the app is big (without explanation).",
            },

            editorGuidance: {
              careerMindset: [
                "Redux is an architectural constraint, not a helper",
                "Predictability beats speed in large teams",
                "Good reducers read like documentation",
              ],
              seniorAdvice:
                "If your reducer surprises you, it will surprise your teammates more.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Redux is one of the most widely used state management libraries in frontend development. It is especially popular in large teams and complex applications where predictability and debugging are critical.",

              reduxPrinciples: {
                title: "Redux Core Principles",
                principles: [
                  {
                    name: "Single Source of Truth",
                    explanation:
                      "The entire application state lives in one central store.",
                  },
                  {
                    name: "State is Read-Only",
                    explanation:
                      "State can only be changed by dispatching actions.",
                  },
                  {
                    name: "Changes via Pure Functions",
                    explanation:
                      "Reducers are pure functions that describe how state changes.",
                  },
                ],
                interviewInsight:
                  "These principles are often asked directly in Redux interviews",
              },

              reduxBuildingBlocks: {
                title: "Store, Actions & Reducers",
                store: {
                  meaning:
                    "The store holds the global state of the application.",
                  example: "Auth store, cart store",
                },
                actions: {
                  meaning:
                    "Plain JavaScript objects that describe what happened.",
                  example: "{ type: 'ADD_TO_CART', payload: product }",
                },
                reducers: {
                  meaning:
                    "Pure functions that return new state based on action.",
                  example: "(state, action) => newState",
                },
                realWorldNote: "Reducers should NEVER mutate state directly",
              },

              dataFlow: {
                title: "Unidirectional Data Flow",
                explanation:
                  "Redux follows a strict one-way data flow that makes behavior predictable.",
                flowSteps: [
                  "UI dispatches an action",
                  "Action reaches reducer",
                  "Reducer returns new state",
                  "UI re-renders from store",
                ],
                benefit:
                  "Easy debugging and time-travel debugging using Redux DevTools",
              },

              pureReducers: {
                title: "Pure Reducers",
                explanation:
                  "A pure reducer always returns the same output for the same input and causes no side effects.",
                rules: [
                  "Do not mutate state",
                  "Do not perform async operations",
                  "Do not modify external variables",
                ],
                exampleLogic: `
function counterReducer(state = 0, action) {
  switch(action.type) {
    case 'INCREMENT':
      return state + 1;
    default:
      return state;
  }
}
      `,
                interviewInsight: "Impure reducers are a common Redux mistake",
              },

              reduxArchitecture: {
                title: "Redux Architecture",
                explanation:
                  "Redux enforces a structured architecture that scales well in large applications.",
                layers: ["UI Components", "Actions", "Reducers", "Store"],
                realWorldUsage:
                  "Large teams prefer Redux because architecture stays consistent",
              },

              commonMistakes: [
                {
                  mistake: "Mutating state in reducers",
                  issue: "UI does not update correctly",
                  fix: "Always return new objects or arrays",
                },
                {
                  mistake: "Using Redux for simple local state",
                  issue: "Over-engineering",
                  fix: "Use useState or Context for small state",
                },
                {
                  mistake: "Putting async logic in reducers",
                  issue: "Breaks Redux principles",
                  fix: "Use middleware (later topic)",
                },
              ],

              interviewQuestions: [
                "What are the core principles of Redux?",
                "Why are reducers required to be pure?",
                "Explain Redux data flow",
                "When should you avoid Redux?",
              ],
            },

            tasks: [
              "Install Redux and React-Redux in a project",
              "Create a Redux store",
              "Write a simple counter reducer",
              "Dispatch actions and observe state changes",
            ],

            examples: [
              "const store = createStore(reducer)",
              "dispatch({ type: 'INCREMENT' })",
            ],

            useCases: [
              "Predictable state changes",
              "Debuggable applications",
              "Large team collaboration",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Understand why Redux exists and what problems it solves",
              "Learn Redux core concepts: store, actions, reducers",
              "Understand unidirectional data flow clearly",
              "Write pure reducers confidently",
              "Be able to explain Redux architecture in interviews",
            ],
          },
          {
            day: 3,
            topic: "Redux Toolkit (RTK)",

            subTopics: [
              "Why Redux Toolkit exists",
              "configureStore",
              "createSlice",
              "Immutable updates with Immer",
              "RTK folder structure",
            ],

            explanation:
              "Redux Toolkit (RTK) is the official, recommended way to write Redux logic. It removes boilerplate, prevents common mistakes, and enforces best practices by default.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "RTK vs Classic Redux Refactor",
                description:
                  "Same feature ko classic Redux se RTK me refactor karke difference feel karna.",
                tasks: [
                  "Create counter using classic Redux (actions + reducer + switch)",
                  "Refactor same logic using createSlice",
                  "Compare file count and lines of code",
                  "Write 3 reasons why RTK is safer",
                ],
                outcome:
                  "You understand why RTK became the default, not just how to use it.",
              },
              {
                id: "pt-2",
                title: "Immer Mental Model Drill",
                description:
                  "Clear confusion around mutation vs immutability in RTK.",
                tasks: [
                  "Write reducer that uses push(), splice(), direct assignment",
                  "Log previous and next state references",
                  "Explain how Immer creates immutable copies",
                  "Write same reducer without Immer (manual spread)",
                ],
                qualityGate:
                  "If you can explain Immer in plain JS terms, RTK will never confuse you.",
              },
              {
                id: "pt-3",
                title: "Feature-Based Store Design",
                description:
                  "Learn to structure Redux like real production codebases.",
                tasks: [
                  "Create authSlice and cartSlice",
                  "Combine them using configureStore",
                  "Identify what belongs in Redux vs component state",
                  "Document slice responsibilities in comments",
                ],
                realWorldRule:
                  "Slices represent business domains, not UI components.",
              },
            ],

            architectureNotes: {
              coreIdea: "Redux Toolkit enforces correct Redux usage by design.",
              mentalModel: [
                "Slices = state + actions + reducers in one unit",
                "configureStore = safe defaults, not configuration burden",
                "Immer = write simple logic, get immutable safety",
              ],
              scalingRule: "RTK scales by features, not by Redux concepts.",
              antiPattern: "Creating slices that mix unrelated concerns.",
            },

            interviewInsights: {
              seniorSignals: [
                "Explains RTK as an abstraction over Redux complexity",
                "Mentions Immer and safe defaults",
                "Avoids talking about switch-case reducers",
              ],
              strongAnswer:
                "Redux Toolkit reduces boilerplate and enforces best practices automatically.",
              redFlag: "Using classic Redux patterns in new projects.",
            },

            editorGuidance: {
              careerMindset: [
                "Modern Redux means Redux Toolkit, always",
                "Boilerplate is not architecture",
                "Feature-based thinking beats layer-based thinking",
              ],
              seniorAdvice:
                "If your Redux code feels verbose, you are probably not using RTK correctly.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Modern React applications rarely use plain Redux anymore. Redux Toolkit is now the industry standard, officially recommended by the Redux team for all new projects.",

              whyRTK: {
                title: "Why Redux Toolkit Exists",
                explanation:
                  "Classic Redux was powerful but required too much boilerplate and was easy to misuse.",
                problemsWithOldRedux: [
                  "Too much boilerplate",
                  "Manual immutable updates",
                  "Complex store setup",
                  "High chance of mistakes",
                ],
                howRTKSolves: [
                  "Less code",
                  "Built-in Immer",
                  "Better defaults",
                  "Opinionated but safe patterns",
                ],
                interviewInsight:
                  "Redux Toolkit is the default choice — using old Redux is considered outdated",
              },

              configureStoreSection: {
                title: "configureStore",
                explanation:
                  "configureStore replaces createStore and sets up Redux with good defaults.",
                features: [
                  "Redux DevTools enabled",
                  "Middleware included by default",
                  "Better error handling",
                ],
                exampleLogic: `
import { configureStore } from '@reduxjs/toolkit';

const store = configureStore({
  reducer: rootReducer,
});
      `,
                bestPractice:
                  "Always use configureStore for new Redux projects",
              },

              createSliceSection: {
                title: "createSlice",
                explanation:
                  "createSlice combines actions and reducers into a single, clean structure.",
                conceptsUsed: [
                  "Slice name",
                  "Initial state",
                  "Reducer functions",
                  "Auto-generated actions",
                ],
                exampleLogic: `
const counterSlice = createSlice({
  name: 'counter',
  initialState: { value: 0 },
  reducers: {
    increment(state) {
      state.value += 1;
    },
  },
});
      `,
                realWorldNote:
                  "Most Redux logic lives inside slices in production apps",
              },

              immerSection: {
                title: "Immutable Updates with Immer",
                explanation:
                  "RTK uses Immer internally, allowing you to write 'mutating' logic safely.",
                keyPoint:
                  "You are NOT mutating state — Immer handles immutability behind the scenes",
                exampleLogic: `
state.items.push(action.payload);
      `,
                interviewInsight:
                  "Immer is why Redux Toolkit feels simpler than classic Redux",
              },

              folderStructure: {
                title: "Recommended RTK Folder Structure",
                explanation:
                  "RTK encourages feature-based folder organization.",
                structure: [
                  "store/",
                  "features/auth/authSlice.js",
                  "features/cart/cartSlice.js",
                  "store.js",
                ],
                benefit: "Scales well in large teams and codebases",
              },

              commonMistakes: [
                {
                  mistake: "Mixing old Redux with RTK",
                  issue: "Unnecessary complexity",
                  fix: "Use only Redux Toolkit APIs",
                },
                {
                  mistake: "Putting async logic directly in components",
                  issue: "Messy architecture",
                  fix: "Move logic to slices or thunks",
                },
                {
                  mistake: "Overusing Redux for local UI state",
                  issue: "Overengineering",
                  fix: "Use Redux only for shared/global state",
                },
              ],

              interviewQuestions: [
                "Why is Redux Toolkit recommended over classic Redux?",
                "What problem does createSlice solve?",
                "How does Immer work in RTK?",
                "What is the role of configureStore?",
              ],
            },

            tasks: [
              "Setup Redux Toolkit in a React app",
              "Create a slice using createSlice",
              "Configure store using configureStore",
              "Connect Redux store to React app",
            ],

            examples: [
              "createSlice({ name, initialState, reducers })",
              "configureStore({ reducer })",
            ],

            useCases: [
              "Modern Redux applications",
              "Scalable frontend systems",
              "Production React apps",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand why Redux Toolkit exists",
              "Learn modern Redux patterns used in companies",
              "Write Redux logic with minimal boilerplate",
              "Use Immer-based immutable updates correctly",
              "Be interview-ready for Redux Toolkit questions",
            ],
          },
          {
            day: 4,
            topic: "Async State & Side Effects",

            subTopics: [
              "Async actions problem",
              "Redux Thunk",
              "createAsyncThunk",
              "Loading and error states",
              "Handling API calls in Redux",
            ],

            explanation:
              "Async logic allows Redux to manage API calls, loading states, and errors in a predictable and centralized way.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Component vs Redux Async Refactor",
                description:
                  "Same API logic ko pehle component me likho, phir Redux async thunk me refactor karo.",
                tasks: [
                  "useEffect ke andar API call likho",
                  "Local loading & error state manage karo",
                  "Same logic createAsyncThunk me shift karo",
                  "Component se saara async code hatao",
                ],
                outcome:
                  "Clear understanding ki async logic component ka kaam nahi hota.",
              },
              {
                id: "pt-2",
                title: "Async State Lifecycle Drill",
                description:
                  "Async state ko professional state-machine jaisa treat karna.",
                tasks: [
                  "status field add karo: idle | loading | succeeded | failed",
                  "Har stage pe UI behavior define karo",
                  "Console me state transitions observe karo",
                  "Boolean loading approach ko compare karo",
                ],
                realWorldRule:
                  "Production apps never rely on sirf loading=true/false.",
              },
            ],

            architectureNotes: {
              asyncFlow:
                "Component → dispatch async thunk → reducer updates state → UI reacts",
              stateShapeRule:
                "Har async slice me data, status aur error hona chahiye",
              scalabilityNote:
                "Centralized async logic debugging aur testing dono easy bana deta hai",
              antiPattern:
                "Har component ke andar alag-alag fetch logic likhna",
            },

            interviewInsights: {
              expectedSignals: [
                "pending / fulfilled / rejected ka mention",
                "Reducers pure hone ka reason",
                "RTK ke sath thunk by default aata hai",
              ],
              strongAnswer:
                "createAsyncThunk async logic ko predictable aur standardized banata hai.",
              redFlags: [
                "API calls directly components me",
                "Error state ignore karna",
              ],
            },

            editorGuidance: {
              careerMindset: [
                "Async logic = application behavior",
                "UI sirf state ka reflection hoti hai",
                "Consistency beats quick hacks",
              ],
              seniorAdvice:
                "Agar async code messy lag raha hai, to wo galat layer me hai.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Real-world applications constantly interact with servers. Handling async logic correctly is critical for scalability, predictability, and good user experience.",

              asyncProblem: {
                title: "The Async Actions Problem",
                explanation:
                  "Redux reducers must be pure and synchronous, but API calls are asynchronous.",
                challenges: [
                  "Where to place API calls?",
                  "How to track loading state?",
                  "How to handle errors consistently?",
                ],
                realWorldNote:
                  "Without a pattern, async logic becomes scattered across components",
              },

              reduxThunk: {
                title: "Redux Thunk Concept",
                explanation:
                  "Redux Thunk allows dispatching functions instead of plain objects, enabling async logic.",
                keyIdea:
                  "Thunk = function that runs async logic and dispatches actions",
                interviewInsight:
                  "Redux Toolkit includes Redux Thunk by default — no setup required",
              },

              createAsyncThunkSection: {
                title: "createAsyncThunk (Modern Standard)",
                explanation:
                  "createAsyncThunk is the recommended way to handle async logic in Redux Toolkit.",
                benefits: [
                  "Auto-generates pending / fulfilled / rejected actions",
                  "Standardized async pattern",
                  "Less boilerplate",
                  "Predictable state transitions",
                ],
                exampleLogic: `
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async () => {
    const res = await fetch('/api/users');
    return res.json();
  }
);
      `,
                bestPractice:
                  "Always use createAsyncThunk instead of manual async logic",
              },

              loadingErrorStates: {
                title: "Loading & Error State Management",
                explanation:
                  "Async operations require UI feedback for loading and failure cases.",
                states: [
                  "idle – before request",
                  "loading – request in progress",
                  "succeeded – data received",
                  "failed – error occurred",
                ],
                exampleLogic: `
extraReducers: builder => {
  builder
    .addCase(fetchUsers.pending, state => {
      state.status = 'loading';
    })
    .addCase(fetchUsers.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.users = action.payload;
    })
    .addCase(fetchUsers.rejected, state => {
      state.status = 'failed';
    });
}
      `,
                realWorldNote:
                  "This pattern is used in almost every production Redux app",
              },

              apiHandling: {
                title: "Handling API Calls in Redux",
                explanation:
                  "Centralizing API logic in Redux improves consistency and debuggability.",
                advantages: [
                  "Single source of truth",
                  "Easy loading/error handling",
                  "Cleaner components",
                ],
                interviewInsight:
                  "Components should trigger async actions, not manage API logic",
              },

              commonMistakes: [
                {
                  mistake: "Calling APIs directly inside components",
                  issue: "Scattered logic and duplication",
                  fix: "Move API calls to createAsyncThunk",
                },
                {
                  mistake: "Not handling loading states",
                  issue: "Poor UX",
                  fix: "Always track async status",
                },
                {
                  mistake: "Ignoring rejected state",
                  issue: "Silent failures",
                  fix: "Handle errors explicitly",
                },
              ],

              interviewQuestions: [
                "Why can’t reducers handle async logic?",
                "What does createAsyncThunk generate automatically?",
                "How do you manage loading and error states in Redux?",
                "Why is async logic better handled in Redux than components?",
              ],
            },

            tasks: [
              "Create async thunk using createAsyncThunk",
              "Fetch data from a public API",
              "Handle loading, success, and error states",
              "Display async data in UI",
            ],

            examples: [
              "createAsyncThunk('users/fetchUsers', async () => fetch())",
            ],

            useCases: [
              "API-driven applications",
              "Data-heavy dashboards",
              "Server state handling",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand why async logic is challenging in Redux",
              "Learn Redux Thunk and createAsyncThunk",
              "Manage loading and error states predictably",
              "Build scalable API-driven Redux architecture",
              "Be interview-ready for async Redux questions",
            ],
          },
          {
            day: 5,
            topic: "Selectors, DevTools & Performance",

            subTopics: [
              "useSelector hook",
              "Memoized selectors",
              "Reselect library",
              "Redux DevTools",
              "Avoiding unnecessary re-renders",
            ],

            explanation:
              "Selectors help derive and optimize state access while Redux DevTools provide powerful debugging tools to inspect, replay, and optimize state changes.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Selector Anti-Patterns Cleanup",
                description:
                  "Poor selectors ko identify karke performance-friendly selectors me convert karo.",
                tasks: [
                  "Component me inline selector likho (bad example)",
                  "Observe unnecessary re-renders",
                  "Same logic ko reselect ke sath memoize karo",
                  "Before vs after render count compare karo",
                ],
                outcome:
                  "Clear understanding ki selector design directly performance affect karta hai.",
              },
              {
                id: "pt-2",
                title: "Redux DevTools Debugging Drill",
                description:
                  "Redux DevTools ka use karke real debugging workflow practice karo.",
                tasks: [
                  "Action history inspect karo",
                  "State diff samjho",
                  "Time-travel karke UI behavior observe karo",
                  "Bug reproduce + rollback using DevTools",
                ],
                realWorldRule:
                  "Senior devs guess nahi karte — DevTools se proof nikalte hain.",
              },
            ],

            architectureNotes: {
              selectorRule:
                "Selectors sirf data read nahi karte, performance boundary define karte hain",
              derivedStateGuideline:
                "Derived data kabhi store me mat rakho, selector me calculate karo",
              performanceFlow:
                "Store update → selector run → reference compare → component render",
              scalabilityNote:
                "Large apps me selector quality Redux performance ka backbone hoti hai",
            },

            interviewInsights: {
              expectedSignals: [
                "Reference equality ka mention",
                "Memoized selectors ka real use-case",
                "DevTools se debugging workflow",
              ],
              strongAnswer:
                "Reselect unnecessary recalculations aur re-renders ko prevent karta hai.",
              redFlags: [
                "state => state (full state select karna)",
                "Selectors ke andar new object banana",
              ],
            },

            editorGuidance: {
              careerMindset: [
                "Redux performance selectors se jeeti ya haarti hai",
                "Re-render ek signal hai, ignore karna bug ko invite karta hai",
                "Debugging tools seniority ka sign hote hain",
              ],
              seniorAdvice:
                "Agar component bina reason render ho raha hai, selector suspect hai.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "As Redux applications grow, inefficient state access can cause unnecessary re-renders and performance issues. Selectors and DevTools are essential for writing fast, debuggable, production-grade Redux apps.",

              useSelectorBasics: {
                title: "useSelector Hook",
                explanation:
                  "useSelector allows React components to read data from the Redux store.",
                keyPoints: [
                  "Component re-renders when selected state changes",
                  "Selector runs on every store update",
                  "Reference equality matters",
                ],
                exampleLogic: `
const cartItems = useSelector(state => state.cart.items);
      `,
                interviewInsight:
                  "Poor selector usage is a common cause of Redux performance problems",
              },

              derivedState: {
                title: "Derived State & Selectors",
                explanation:
                  "Selectors are functions that compute derived data from the Redux store.",
                benefits: [
                  "Avoid duplicating data in store",
                  "Keep reducers simple",
                  "Centralize calculation logic",
                ],
                exampleLogic: `
const selectCartTotal = state =>
  state.cart.items.reduce((sum, i) => sum + i.price, 0);
      `,
                bestPractice:
                  "Never store derived values directly in Redux state",
              },

              memoizedSelectors: {
                title: "Memoized Selectors",
                explanation:
                  "Memoization prevents expensive recalculations when inputs haven’t changed.",
                problemWithoutMemo:
                  "Selectors recalculate on every state update, even if unrelated",
                solution:
                  "Memoized selectors return cached results when inputs are same",
                interviewInsight:
                  "Memoization is critical for performance-sensitive applications",
              },

              reselectLibrary: {
                title: "Reselect Library",
                explanation:
                  "Reselect is the standard library for creating memoized selectors in Redux.",
                benefits: [
                  "Prevents unnecessary recalculations",
                  "Improves rendering performance",
                  "Cleaner selector composition",
                ],
                exampleLogic: `
import { createSelector } from 'reselect';

const selectItems = state => state.cart.items;

export const selectCartTotal = createSelector(
  [selectItems],
  items => items.reduce((sum, i) => sum + i.price, 0)
);
      `,
                realWorldNote: "Used heavily in large Redux codebases",
              },

              reduxDevTools: {
                title: "Redux DevTools",
                explanation:
                  "Redux DevTools provide time-travel debugging and state inspection.",
                features: [
                  "Action history",
                  "State snapshots",
                  "Time-travel debugging",
                  "Action replay",
                ],
                bestPractice: "Always use DevTools during development",
                interviewInsight:
                  "Interviewers often ask how you debug Redux issues",
              },

              rerenderOptimization: {
                title: "Avoiding Unnecessary Re-renders",
                explanation:
                  "Poor selector patterns can cause components to re-render too often.",
                optimizationTips: [
                  "Select minimal state",
                  "Use memoized selectors",
                  "Avoid creating new objects in selectors",
                ],
                exampleLogic: `
useSelector(selectCartTotal); // memoized
      `,
                realWorldNote: "Performance issues often appear only at scale",
              },

              commonMistakes: [
                {
                  mistake: "Selecting entire state object",
                  issue: "Component re-renders on every change",
                  fix: "Select only required slices",
                },
                {
                  mistake: "Inline object creation in selector",
                  issue: "Breaks reference equality",
                  fix: "Use memoized selectors",
                },
                {
                  mistake: "Ignoring DevTools",
                  issue: "Hard debugging",
                  fix: "Inspect actions and state transitions",
                },
              ],

              interviewQuestions: [
                "How does useSelector trigger re-renders?",
                "What is derived state?",
                "Why use Reselect?",
                "How do Redux DevTools help debugging?",
                "How do you optimize Redux performance?",
              ],
            },

            tasks: [
              "Create selectors for derived state",
              "Implement memoized selectors using Reselect",
              "Use Redux DevTools to inspect actions",
              "Optimize components to reduce re-renders",
            ],

            examples: [
              "const cartItems = useSelector(selectCartItems)",
              "createSelector([selectItems], computeTotal)",
            ],

            useCases: [
              "Performance-sensitive apps",
              "Debugging complex flows",
              "Large Redux stores",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand how useSelector works internally",
              "Learn to build efficient memoized selectors",
              "Use Redux DevTools confidently",
              "Prevent unnecessary component re-renders",
              "Write scalable and performant Redux applications",
            ],
          },
          {
            day: 6,
            topic: "Modern Alternatives: Zustand & Jotai",

            subTopics: [
              "Why lightweight state libraries exist",
              "Zustand store creation",
              "Jotai atoms concept",
              "Comparison with Redux",
              "Choosing the right tool",
            ],

            explanation:
              "Zustand and Jotai are modern, lightweight state management libraries that reduce boilerplate and complexity compared to Redux, making them ideal for many real-world React applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Redux → Zustand Migration Drill",
                description:
                  "Existing Redux-based global state ko Zustand me refactor karo aur complexity compare karo.",
                tasks: [
                  "Redux slice ka code analyze karo",
                  "Same state ko Zustand store me likho",
                  "Reducers/actions vs direct setters compare karo",
                  "Code lines & mental complexity measure karo",
                ],
                outcome:
                  "Clear clarity milegi ki Redux kab overkill hota hai aur Zustand kab fit baithta hai.",
              },
              {
                id: "pt-2",
                title: "Atomic State with Jotai",
                description:
                  "Jotai atoms ka use karke fine-grained state updates implement karo.",
                tasks: [
                  "Multiple atoms create karo (counter, user, theme)",
                  "Atoms ko different components me consume karo",
                  "Observe re-render behavior per atom",
                  "Unrelated atoms update hone par render check karo",
                ],
                outcome:
                  "Atomic state ka real performance benefit samajh aayega.",
              },
            ],

            architectureNotes: {
              coreIdea:
                "Modern state libraries complexity ko reduce karti hain, features ko nahi",
              zustandMentalModel:
                "Store = hook + state + actions (no reducers, no actions files)",
              jotaiMentalModel:
                "State = atoms → UI sirf wahi atom re-render karta hai jo change hota hai",
              comparisonRule:
                "Redux = predictable flow | Zustand = simplicity | Jotai = granularity",
              scalabilityNote:
                "Large apps me bhi lightweight libraries ka selective use common ho raha hai",
            },

            interviewInsights: {
              expectedSignals: [
                "Tool choice justification (why not Redux?)",
                "State scope awareness (global vs feature)",
                "Re-render optimization understanding",
              ],
              strongAnswer:
                "Redux powerful hai, par har problem ke liye nahi. Zustand/Jotai simpler aur efficient ho sakte hain.",
              redFlags: [
                "Har app me Redux use karna",
                "Tool sirf trend ke basis pe choose karna",
              ],
            },

            editorGuidance: {
              careerMindset: [
                "Senior dev tool nahi, decision justify karta hai",
                "Simpler solution choose karna maturity ka sign hai",
                "Global state kam rakho, clarity zyada rakho",
              ],
              seniorAdvice:
                "Agar Redux samjha sakte ho aur Zustand/Jotai justify kar sakte ho — tum interview-ready ho.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "While Redux is powerful, it can feel heavy for smaller or medium-sized apps. Modern libraries like Zustand and Jotai emerged to provide simpler APIs, better developer experience, and less boilerplate while still handling real production use cases.",

              whyLightweightLibraries: {
                title: "Why Lightweight State Libraries Exist",
                explanation:
                  "Not every app needs a centralized Redux architecture. Many apps suffer from unnecessary complexity.",
                problemsWithHeavyState: [
                  "Too much boilerplate",
                  "Steep learning curve",
                  "Overkill for small apps",
                ],
                benefits: [
                  "Less code",
                  "Faster development",
                  "Easier mental model",
                ],
                interviewInsight:
                  "Choosing the right tool matters more than using the most popular one",
              },

              zustandBasics: {
                title: "Zustand – Store Creation",
                explanation:
                  "Zustand uses hooks and simple functions to manage global state without providers or reducers.",
                conceptsUsed: [
                  "Hook-based global store",
                  "Immutable updates",
                  "No provider needed",
                ],
                exampleLogic: `
import { create } from 'zustand';

const useStore = create(set => ({
  count: 0,
  increment: () => set(state => ({ count: state.count + 1 })),
}));
      `,
                realWorldNote:
                  "Zustand is widely used in startups and performance-sensitive apps",
              },

              jotaiBasics: {
                title: "Jotai – Atoms Concept",
                explanation:
                  "Jotai uses atomic pieces of state (atoms) that can be shared and composed.",
                conceptsUsed: [
                  "Atomic state",
                  "Fine-grained reactivity",
                  "Minimal re-renders",
                ],
                exampleLogic: `
import { atom, useAtom } from 'jotai';

const countAtom = atom(0);
      `,
                bestPractice: "Use atoms for independent, feature-level state",
              },

              reduxComparison: {
                title: "Zustand & Jotai vs Redux",
                comparison: {
                  redux: [
                    "Centralized store",
                    "Reducers and actions",
                    "More boilerplate",
                  ],
                  zustand: ["Minimal API", "No reducers", "Faster setup"],
                  jotai: [
                    "Atomic state",
                    "Highly modular",
                    "Excellent performance",
                  ],
                },
                interviewInsight:
                  "Redux is still relevant, but not always the best choice",
              },

              choosingRightTool: {
                title: "Choosing the Right Tool",
                explanation:
                  "State management should be chosen based on app size, complexity, and team experience.",
                guidelines: [
                  "Redux → Large, complex apps with heavy async flows",
                  "Zustand → Medium apps, dashboards, rapid development",
                  "Jotai → Feature-based, atomic, performance-focused apps",
                ],
                realWorldNote:
                  "Many companies mix tools depending on feature needs",
              },

              commonMistakes: [
                {
                  mistake: "Using Redux for very small apps",
                  issue: "Unnecessary complexity",
                  fix: "Use Zustand or local state",
                },
                {
                  mistake: "Globalizing all state",
                  issue: "Harder debugging",
                  fix: "Keep state as local as possible",
                },
                {
                  mistake: "Tool hype-driven decisions",
                  issue: "Wrong architecture",
                  fix: "Choose based on requirements",
                },
              ],

              interviewQuestions: [
                "Why would you choose Zustand over Redux?",
                "What are atoms in Jotai?",
                "Can Zustand replace Redux?",
                "How do you decide global vs local state?",
              ],
            },

            tasks: [
              "Build a simple global store using Zustand",
              "Create and use atoms with Jotai",
              "Refactor a Redux example using Zustand",
              "Compare re-render behavior between tools",
            ],

            examples: [
              "const useStore = create(set => ({ count: 0 }))",
              "const countAtom = atom(0)",
            ],

            useCases: [
              "Small to medium applications",
              "Rapid prototyping",
              "Feature-level state",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand why modern state libraries exist",
              "Learn Zustand and Jotai fundamentals",
              "Compare Redux with lightweight alternatives",
              "Choose the right state tool for real projects",
              "Be interview-ready on modern state management trends",
            ],
          },
          {
            day: 7,
            topic: "Week 29 Project – Complex State Application",

            subTopics: [
              "Global state design",
              "Async data handling",
              "State persistence",
              "Performance optimization",
              "Scalable architecture",
            ],

            explanation:
              "This project demonstrates your ability to design, manage, and optimize complex global state across multiple features using modern state management techniques.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Global State Architecture Blueprint",
                description:
                  "Poore app ke liye global state ka clear, feature-based blueprint design karo.",
                tasks: [
                  "Global vs local state list karo",
                  "Feature-wise state slices define karo",
                  "Async data ke liye state shape design karo",
                  "Persistence ke liye keys decide karo",
                ],
                outcome:
                  "Tumhe state design pe clarity milegi, sirf library usage nahi.",
              },
              {
                id: "pt-2",
                title: "Async + Persistence Integration",
                description:
                  "Async API flows ko persistence ke saath safely integrate karo.",
                tasks: [
                  "Async fetch with loading/error states",
                  "Selective state persistence (auth, theme)",
                  "State hydration on app load",
                  "Invalid or stale state handling",
                ],
                outcome:
                  "Production-grade async + persistence handling samajh aayegi.",
              },
              {
                id: "pt-3",
                title: "Performance Audit Challenge",
                description:
                  "Complex global state ke saath performance audit aur optimization karo.",
                tasks: [
                  "Re-render issues identify karo",
                  "Selectors/memoization apply karo",
                  "Before vs after performance compare karo",
                  "DevTools se proof collect karo",
                ],
                outcome: "Real senior-level performance mindset develop hoga.",
              },
            ],

            architectureNotes: {
              corePrinciple:
                "State architecture feature ke around design hoti hai, screens ke around nahi",
              globalStateRule:
                "Jo data multiple distant features me use ho — wahi global hona chahiye",
              asyncModel: "Har async state = data + status + error",
              persistenceRule:
                "Sab kuch persist mat karo — sirf user-critical state",
              scalabilityTip:
                "Feature-based folders long-term scale ke liye mandatory hote hain",
            },

            interviewInsights: {
              whatInterviewersCheck: [
                "State design clarity",
                "Async handling maturity",
                "Tool choice reasoning",
                "Performance awareness",
              ],
              strongSignal:
                "Candidate state ko design karta hai, sirf Redux/Zustand use nahi karta",
              commonTrap:
                "Library explain karna but architecture justify na kar paana",
              idealAnswerTone:
                "Calm, trade-off based, experience-driven explanation",
            },

            editorGuidance: {
              careerMindset: [
                "State management ek design problem hai, library problem nahi",
                "Senior engineers complexity kam karte hain",
                "Debuggability aur performance equally important hai",
              ],
              seniorAdvice:
                "Agar tum is project ko explain kar sakte ho — tum mid se senior transition ready ho.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Real-world frontend applications rarely rely on simple local state. This project simulates a production-grade application where multiple features share data, async APIs are involved, state is persisted, and performance matters.",

              projectGoal: {
                title: "Project Goal",
                explanation:
                  "Build a complex React application that uses global state effectively across features while remaining scalable, performant, and easy to debug.",
                realWorldNote:
                  "This level of state management is expected in mid-to-senior frontend roles",
              },

              globalStateDesign: {
                title: "Global State Design",
                explanation:
                  "Designing global state correctly is more important than the tool itself.",
                conceptsUsed: [
                  "Separating global vs local state",
                  "Feature-based state slices",
                  "Avoiding unnecessary global state",
                ],
                exampleLogic: `
authState → user, token
uiState → theme, sidebar
dataState → posts, projects, tasks
      `,
                bestPractice: "Keep global state minimal and meaningful",
              },

              asyncDataHandling: {
                title: "Async Data Handling",
                explanation:
                  "Production apps depend heavily on async APIs and must handle loading, success, and error states cleanly.",
                conceptsUsed: [
                  "Async actions (Thunk / custom hooks)",
                  "Loading & error states",
                  "Retry and fallback logic",
                ],
                exampleLogic: `
fetchProjects()
  → loading
  → success (data)
  → error (message)
      `,
                interviewInsight:
                  "Interviewers look for structured async state handling",
              },

              statePersistence: {
                title: "State Persistence",
                explanation:
                  "Persisting state improves user experience by retaining data across refreshes.",
                conceptsUsed: [
                  "localStorage persistence",
                  "Selective persistence",
                  "Hydrating state on load",
                ],
                exampleLogic: `
localStorage.setItem('appState', JSON.stringify(state));
      `,
                realWorldNote:
                  "Auth, theme, and user preferences are commonly persisted",
              },

              performanceOptimization: {
                title: "Performance Optimization",
                explanation:
                  "Complex state can cause unnecessary re-renders if not optimized.",
                conceptsUsed: [
                  "Memoized selectors",
                  "useMemo & useCallback",
                  "Avoiding over-rendering",
                ],
                exampleLogic: `
const visibleItems = useMemo(() => filter(items), [items]);
      `,
                bestPractice: "Optimize only after measuring with DevTools",
              },

              scalableArchitecture: {
                title: "Scalable Architecture",
                explanation:
                  "Scalable apps are structured by features, not file types.",
                structureExample: [
                  "features/auth",
                  "features/projects",
                  "features/feed",
                  "shared/hooks",
                  "shared/components",
                ],
                benefit: "Easier maintenance, testing, and onboarding",
              },

              commonMistakes: [
                {
                  mistake: "Putting everything in global state",
                  issue: "Harder debugging and performance issues",
                  fix: "Keep UI and temporary state local",
                },
                {
                  mistake: "Ignoring loading/error states",
                  issue: "Poor UX",
                  fix: "Always model async states",
                },
                {
                  mistake: "Premature optimization",
                  issue: "Complex code",
                  fix: "Optimize after profiling",
                },
              ],

              interviewQuestions: [
                "How do you design global state?",
                "When do you choose Redux vs Zustand?",
                "How do you persist state safely?",
                "How do you debug complex state issues?",
              ],
            },

            tasks: [
              "Design global state structure",
              "Implement async API flows",
              "Persist selected state using localStorage",
              "Optimize re-renders",
              "Debug state using DevTools",
            ],

            examples: [
              "Project management app",
              "Social media feed",
              "Multi-user dashboard",
            ],

            useCases: [
              "Portfolio showcase",
              "Real-world production apps",
              "Frontend interviews",
            ],

            editorType: "react",

            estimatedTime: "4–6 hours",

            objectives: [
              "Design scalable global state architecture",
              "Handle async data like production apps",
              "Persist and hydrate state correctly",
              "Optimize performance in complex React apps",
              "Demonstrate senior-level state management skills",
            ],
          },
        ],
      },

      {
        week: 30,
        weekGoal:
          "Master Next.js fundamentals and server-side rendering for SEO-friendly, production-grade apps",
        weekOutcome:
          "Ability to build scalable, SEO-optimized web applications using Next.js",
        miniProject: "Full Next.js Application",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Introduction to Next.js & Setup",

            subTopics: [
              "What is Next.js and why it exists",
              "CSR vs SSR vs SSG",
              "Next.js project setup",
              "File-based routing basics",
              "Pages vs App Router (concept overview)",
            ],

            explanation:
              "Next.js is a React framework that enables server-side rendering, static site generation, and production-ready performance out of the box.",

            premiumTasks: [
              {
                id: "pt-1",
                title: "Rendering Strategy Mapping",
                description:
                  "CSR, SSR aur SSG ko real-world use cases ke saath deeply analyze karo.",
                tasks: [
                  "CSR vs SSR vs SSG ka comparison table banao",
                  "SEO, performance aur cost impact note karo",
                  "Har rendering model ke real examples likho",
                  "Decide karo kaunsa page kis rendering ke saath best hai",
                ],
                outcome:
                  "Rendering strategies ko sirf theory nahi, decision level par samajh paoge.",
              },
              {
                id: "pt-2",
                title: "Pages Router vs App Router Decision Drill",
                description:
                  "Next.js ke Pages Router aur App Router ka practical comparison karo.",
                tasks: [
                  "Pages Router ka basic flow samjho",
                  "App Router ke layouts, loading, server components explore karo",
                  "Pros & cons likho dono ke",
                  "New project ke liye kaunsa choose karoge aur kyun?",
                ],
                outcome:
                  "Interview me App Router preference confidently justify kar paoge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "CSR vs SSR vs SSG ka clear practical understanding",
                "SEO aur performance trade-offs explain kar pana",
                "Next.js App Router awareness",
              ],
              strongAnswer:
                "Next.js powerful isliye hai kyunki ye per-page rendering strategy allow karta hai, jo SEO, performance aur scalability improve karti hai.",
              redFlags: [
                "Har page ke liye CSR use karna",
                "SSR/SSG ko sirf theoretical explain karna",
                "App Router ke baare me knowledge na hona",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Routing behavior validation",
                "Rendered HTML output verification",
                "SEO-related rendering checks",
              ],
              nextJsTestingNotes: [
                "SSR/SSG pages ke liye integration tests useful hote hain",
                "Next.js routing Playwright ya Cypress se test hoti hai",
                "Different rendering modes ka output verify karna important hota hai",
              ],
              realWorldNote:
                "Next.js testing sirf components nahi, rendering behavior ko bhi cover karti hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Framework choice architectural decision hota hai",
                "Rendering strategy UX aur business metrics affect karti hai",
                "SEO frontend engineer ki responsibility hai",
              ],
              seniorAdvice:
                "Agar tum justify kar sakte ho kaunsa page SSR, SSG ya CSR hona chahiye — tum interview-ready ho.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Next.js is used by companies to solve real-world frontend problems like SEO, slow initial load, routing complexity, and scalability. It builds on top of React and adds powerful features without extra configuration.",

              whyNextJs: {
                title: "Why Next.js Exists",
                explanation:
                  "Traditional React apps (CSR only) suffer from SEO issues and slow first paint. Next.js solves these by rendering pages on the server or at build time.",
                problemsSolved: [
                  "SEO issues in client-side rendered apps",
                  "Slow initial page load",
                  "Manual routing and code splitting",
                  "Complex performance optimizations",
                ],
                realWorldNote:
                  "Most React jobs now prefer or require Next.js experience",
              },

              renderingModels: {
                title: "CSR vs SSR vs SSG",
                csr: {
                  meaning:
                    "Client Side Rendering – page renders entirely in the browser",
                  example: "Traditional React apps using create-react-app",
                  downside: "Poor SEO and slower first contentful paint",
                },
                ssr: {
                  meaning:
                    "Server Side Rendering – page rendered on every request",
                  example: "Dynamic dashboards, authenticated pages",
                  benefit: "Better SEO and fresh data",
                },
                ssg: {
                  meaning:
                    "Static Site Generation – page rendered at build time",
                  example: "Blogs, marketing pages, documentation",
                  benefit: "Extremely fast and SEO friendly",
                },
                interviewInsight:
                  "Interviewers often ask when to use SSR vs SSG",
              },

              projectSetup: {
                title: "Next.js Project Setup",
                explanation:
                  "Next.js provides zero-config setup using official tooling.",
                steps: [
                  "Install Node.js (LTS)",
                  "Create Next.js app",
                  "Run development server",
                ],
                exampleLogic: `
npx create-next-app@latest my-app
cd my-app
npm run dev
      `,
                bestPractice:
                  "Prefer App Router for new projects (Next.js 13+)",
              },

              routingBasics: {
                title: "File-based Routing",
                explanation:
                  "Routing in Next.js is based on folder and file structure instead of manual route definitions.",
                examples: [
                  "pages/index.js → /",
                  "pages/about.js → /about",
                  "app/page.js → / (App Router)",
                ],
                benefit: "No need for react-router configuration",
              },

              pagesVsAppRouter: {
                title: "Pages Router vs App Router",
                pagesRouter: {
                  description: "Older routing system based on pages/ folder",
                  status: "Still supported but not recommended for new apps",
                },
                appRouter: {
                  description:
                    "New routing system with layouts, server components, streaming",
                  status: "Recommended for all new Next.js projects",
                },
                realWorldNote: "Companies are actively migrating to App Router",
              },

              commonMistakes: [
                {
                  mistake: "Treating Next.js like plain React",
                  issue: "Missing SSR/SSG benefits",
                  fix: "Leverage server rendering features",
                },
                {
                  mistake: "Using CSR everywhere",
                  issue: "Poor SEO and performance",
                  fix: "Choose rendering strategy per page",
                },
                {
                  mistake: "Ignoring App Router",
                  issue: "Outdated knowledge",
                  fix: "Learn App Router early",
                },
              ],

              interviewQuestions: [
                "Why do companies prefer Next.js over CRA?",
                "Difference between CSR, SSR, and SSG?",
                "What is file-based routing?",
                "Pages Router vs App Router?",
              ],
            },

            tasks: [
              "Create a Next.js project",
              "Explore folder structure",
              "Run development server",
              "Create basic pages using file-based routing",
              "Understand rendering strategies conceptually",
            ],

            examples: ["pages/index.js", "pages/about.js", "app/page.js"],

            useCases: [
              "SEO-friendly websites",
              "Content-heavy applications",
              "Marketing and landing pages",
            ],

            editorType: "react",

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand why Next.js is used in production",
              "Learn rendering strategies (CSR, SSR, SSG)",
              "Set up a Next.js project confidently",
              "Understand file-based routing",
              "Prepare foundation for App Router and advanced Next.js concepts",
            ],
          },
          {
            day: 2,
            topic: "File-Based Routing & Dynamic Routes",

            subTopics: [
              "pages directory routing",
              "Dynamic routes using [param]",
              "Nested routes",
              "Link component",
              "useRouter hook",
            ],

            explanation:
              "Next.js uses file-based routing to automatically create routes, making navigation simpler, scalable, and production-ready.",

            premiumTasks: [
              {
                id: "pt-1",
                title: "Dynamic Routing Drill",
                description:
                  "Static, dynamic aur nested routes ko real-world scenarios ke saath practice karo.",
                tasks: [
                  "Static routes (pages/about.js, pages/contact.js) create karo",
                  "Dynamic route ([slug], [id]) bana ke data render karo",
                  "Nested routes ka folder structure design karo",
                  "Slug vs ID based routing ka use-case decide karo",
                ],
                outcome:
                  "Routing structure ko scalable aur maintainable banana aa jayega.",
              },
              {
                id: "pt-2",
                title: "Navigation & Router Control",
                description:
                  "Link aur useRouter ka real navigation flows me use samjho.",
                tasks: [
                  "Link component se client-side navigation implement karo",
                  "router.push aur router.replace ka difference test karo",
                  "router.query aur router.isReady handle karo",
                  "Programmatic navigation scenarios likho (auth, redirect)",
                ],
                outcome:
                  "Next.js navigation ko production-level confidence ke saath use kar paoge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "File-based routing ka clear mental model",
                "Dynamic routes ka real-world usage",
                "Link vs <a> ka difference clearly explain karna",
                "Routing edge cases ka awareness",
              ],
              strongAnswer:
                "Next.js routing predictable aur scalable hoti hai kyunki routes file structure se automatically generate hote hain.",
              redFlags: [
                "React Router jaisa explain karna",
                "Link ke jagah <a> use karna",
                "Dynamic params lifecycle na samajhna",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Dynamic route rendering validation",
                "Route param availability checks",
                "Client-side navigation behavior",
              ],
              nextJsRoutingTests: [
                "Dynamic routes ke liye Playwright/Cypress tests",
                "router.query ke undefined cases handle karna",
                "Navigation ke baad correct page render verify karna",
              ],
              realWorldNote:
                "Routing bugs production me high-impact hote hain, isliye routing tests critical hote hain.",
            },

            editorGuidance: {
              careerMindset: [
                "Routing app ki backbone hoti hai",
                "Predictable URLs SEO aur UX dono improve karti hain",
                "Simple routing = easier scaling",
              ],
              seniorAdvice:
                "Agar tum routing structure whiteboard pe design kar sakte ho, tum real-world Next.js ke liye ready ho.",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Routing is a core feature of Next.js. Unlike React Router, Next.js generates routes automatically based on file structure, which reduces boilerplate and improves maintainability.",

              fileBasedRouting: {
                title: "File-Based Routing in Next.js",
                explanation:
                  "Each file inside the pages directory becomes a route automatically.",
                examples: [
                  "pages/index.js → /",
                  "pages/blog.js → /blog",
                  "pages/contact.js → /contact",
                ],
                benefit: "No manual route configuration required",
                realWorldNote:
                  "Large apps rely on predictable routing structures",
              },

              dynamicRoutes: {
                title: "Dynamic Routes using [param]",
                explanation:
                  "Dynamic routes allow pages to be generated based on URL parameters like IDs or slugs.",
                conceptsUsed: [
                  "Square bracket syntax",
                  "URL parameter extraction",
                  "Dynamic content rendering",
                ],
                examples: ["pages/posts/[slug].js", "pages/users/[id].js"],
                exampleLogic: `
import { useRouter } from 'next/router';

const Post = () => {
  const router = useRouter();
  const { slug } = router.query;
  return <h1>Post: {slug}</h1>;
};
      `,
                interviewInsight:
                  "Dynamic routes are commonly asked in Next.js interviews",
              },

              nestedRoutes: {
                title: "Nested Routes",
                explanation:
                  "Nested folders create nested routes automatically.",
                examples: [
                  "pages/blog/index.js → /blog",
                  "pages/blog/[slug].js → /blog/my-post",
                ],
                benefit: "Clean URL structure for large applications",
              },

              linkComponent: {
                title: "Link Component for Navigation",
                explanation:
                  "Next.js provides the Link component for client-side navigation without page reload.",
                conceptsUsed: [
                  "Client-side navigation",
                  "Prefetching routes",
                  "SEO-friendly navigation",
                ],
                exampleLogic: `
import Link from 'next/link';

<Link href="/blog">Go to Blog</Link>
      `,
                bestPractice:
                  "Always use Link instead of <a> for internal routes",
              },

              useRouterHook: {
                title: "useRouter Hook",
                explanation:
                  "useRouter provides access to route information like pathname, query params, and navigation methods.",
                commonUses: [
                  "Reading dynamic params",
                  "Conditional rendering based on route",
                  "Programmatic navigation",
                ],
                exampleLogic: `
const router = useRouter();
router.push('/login');
      `,
              },

              commonMistakes: [
                {
                  mistake: "Using <a> instead of Link",
                  issue: "Full page reload",
                  fix: "Use next/link for navigation",
                },
                {
                  mistake: "Accessing router.query too early",
                  issue: "Undefined values",
                  fix: "Check router.isReady",
                },
                {
                  mistake: "Overusing dynamic routes",
                  issue: "Hard-to-debug routing",
                  fix: "Use static routes where possible",
                },
              ],

              interviewQuestions: [
                "How does file-based routing work in Next.js?",
                "What are dynamic routes?",
                "Difference between Link and anchor tag?",
                "How do you access route parameters?",
              ],
            },

            tasks: [
              "Create static routes using pages directory",
              "Create dynamic routes using [param]",
              "Navigate using Link component",
              "Access route parameters using useRouter",
              "Create nested routes for structured pages",
            ],

            examples: [
              "pages/posts/[slug].js",
              "router.query.slug",
              "<Link href='/blog' />",
            ],

            useCases: [
              "Blog systems",
              "Product detail pages",
              "User profile pages",
            ],

            editorType: "react",

            estimatedTime: "2–2.5 hours",

            objectives: [
              "Understand file-based routing in Next.js",
              "Create and use dynamic routes confidently",
              "Navigate using Next.js Link component",
              "Access and use route parameters",
              "Build scalable routing structures for real apps",
            ],
          },
          {
            day: 3,
            topic: "Server-Side Rendering (SSR)",

            subTopics: [
              "What is SSR",
              "getServerSideProps",
              "Server vs client execution",
              "SSR performance considerations",
              "When to use SSR",
            ],

            explanation:
              "Server-Side Rendering (SSR) renders the page on every request on the server, ensuring fresh data, better SEO, and accurate content for dynamic use cases.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "SSR Decision Drill",
                description:
                  "Real-world pages ke liye decide karo ki SSR, SSG ya CSR kya use karna chahiye.",
                tasks: [
                  "3 pages list karo (dashboard, blog, pricing)",
                  "Har page ke liye rendering strategy choose karo",
                  "SSR choose karne ka reason likho",
                  "SSR avoid karne ke cases identify karo",
                ],
                outcome:
                  "Tum blindly SSR use nahi karoge, balki business + performance ke basis pe decide kar paoge.",
              },
              {
                id: "pt-2",
                title: "getServerSideProps Deep Practice",
                description:
                  "getServerSideProps ko secure aur optimized tareeke se use karna seekho.",
                tasks: [
                  "API call server-side karo (auth required)",
                  "Context se params aur cookies access karo",
                  "Unnecessary data fetch avoid karo",
                  "SSR response time analyze karo",
                ],
                outcome:
                  "Tum SSR ko production-grade tarike se implement kar paoge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "SSR vs CSR vs SSG ka clear difference",
                "Server vs client execution clarity",
                "SSR performance trade-offs samajhna",
                "Rendering strategy justify kar pana",
              ],
              strongAnswer:
                "SSR SEO aur fresh data ke liye best hai, par har page ke liye nahi. Main SSR sirf tab use karta hoon jab data request-time pe required ho.",
              redFlags: [
                "Har page pe SSR use karna",
                "Browser APIs SSR me call karna",
                "SSR ko faster than SSG bolna",
              ],
            },

            testingInsights: {
              focusAreas: [
                "SSR rendered HTML validation",
                "Server-side data availability",
                "Hydration issues detection",
              ],
              ssrTestingChecks: [
                "HTML me data present hai ya nahi",
                "Client hydration mismatch nahi ho raha",
                "SSR response time acceptable hai",
              ],
              realWorldNote:
                "SSR bugs SEO aur UX dono ko directly impact karte hain, isliye testing critical hoti hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Rendering strategy ek architectural decision hota hai",
                "SSR power hai, shortcut nahi",
                "Cost + performance + SEO sab balance karna hota hai",
              ],
              seniorAdvice:
                "Agar tum whiteboard pe explain kar sakte ho ki SSR kyun use kiya — tum Next.js interviews ke liye ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Server-Side Rendering is one of the biggest advantages of Next.js over traditional React apps. Instead of rendering HTML in the browser, SSR generates HTML on the server for each request.",

              whatIsSSR: {
                title: "What is Server-Side Rendering (SSR)",
                explanation:
                  "With SSR, the server generates HTML on every request using the latest data and sends it to the browser.",
                comparison: {
                  csr: "HTML is empty initially, data is fetched on client",
                  ssr: "HTML is generated on server with data already present",
                },
                realWorldImpact: [
                  "Better SEO",
                  "Accurate content on first load",
                  "Improved perceived performance",
                ],
              },

              getServerSideProps: {
                title: "getServerSideProps",
                explanation:
                  "getServerSideProps is a special Next.js function that runs on the server for every request.",
                conceptsUsed: [
                  "Server-only execution",
                  "Secure API calls",
                  "Request-time data fetching",
                ],
                exampleLogic: `
export async function getServerSideProps(context) {
  const res = await fetch('https://api.example.com/data');
  const data = await res.json();

  return {
    props: {
      data,
    },
  };
}
      `,
                bestPractice:
                  "Use SSR only when data must be fresh on every request",
              },

              serverVsClient: {
                title: "Server vs Client Execution",
                explanation:
                  "Understanding where code runs is critical for security and performance.",
                serverSide: [
                  "Access to secrets (tokens, API keys)",
                  "Runs on every request",
                  "No access to browser APIs",
                ],
                clientSide: [
                  "Runs in browser",
                  "Has access to window and document",
                  "Less secure for sensitive logic",
                ],
                interviewInsight:
                  "Many bugs come from mixing server-only and client-only code",
              },

              performanceConsiderations: {
                title: "SSR Performance Considerations",
                explanation:
                  "SSR improves SEO but can increase server load if overused.",
                tradeoffs: [
                  "Slower TTFB compared to SSG",
                  "Higher server cost",
                  "Every request hits the server",
                ],
                optimizationTips: [
                  "Cache API responses",
                  "Use CDN where possible",
                  "Prefer SSG or ISR when data is not real-time",
                ],
              },

              whenToUseSSR: {
                title: "When to Use SSR",
                useCases: [
                  "Authenticated dashboards",
                  "User-specific content",
                  "Real-time pricing or inventory",
                ],
                avoidWhen: [
                  "Static marketing pages",
                  "Content that rarely changes",
                  "High-traffic pages with same content",
                ],
                realWorldNote:
                  "Most companies mix SSR, SSG, and ISR in the same app",
              },

              commonMistakes: [
                {
                  mistake: "Using SSR for static content",
                  issue: "Unnecessary server load",
                  fix: "Use SSG instead",
                },
                {
                  mistake: "Calling browser APIs in SSR",
                  issue: "Runtime crashes",
                  fix: "Keep SSR code server-safe",
                },
                {
                  mistake: "Fetching too much data",
                  issue: "Slow response times",
                  fix: "Fetch only what the page needs",
                },
              ],

              interviewQuestions: [
                "What is SSR and how does it work in Next.js?",
                "Difference between SSR and CSR?",
                "When should you avoid SSR?",
                "What runs on server vs client?",
              ],
            },

            tasks: [
              "Implement getServerSideProps",
              "Fetch data securely on the server",
              "Render page using SSR data",
              "Identify SSR vs CSR execution",
              "Decide correct rendering strategy for a page",
            ],

            examples: [
              "export async function getServerSideProps() {}",
              "Authenticated dashboard page",
              "Real-time pricing UI",
            ],

            useCases: [
              "Authenticated dashboards",
              "Dynamic pricing pages",
              "Real-time content",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand how SSR works in Next.js",
              "Use getServerSideProps confidently",
              "Differentiate server and client execution",
              "Make correct rendering strategy decisions",
              "Prepare for Next.js interview questions",
            ],
          },
          {
            day: 4,
            topic: "Static Site Generation (SSG) & ISR",

            subTopics: [
              "getStaticProps",
              "getStaticPaths",
              "Static generation with data",
              "Incremental Static Regeneration (ISR)",
              "SSG vs SSR decision making",
            ],

            explanation:
              "Static Site Generation (SSG) builds pages at build time, delivering extremely fast performance and scalability. Incremental Static Regeneration (ISR) allows updating static pages after deployment.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "SSG vs ISR Decision Drill",
                description:
                  "Real-world pages ke liye decide karo SSG use karna hai ya ISR.",
                tasks: [
                  "Blog, docs, marketing pages list karo",
                  "Decide karo kaun pure SSG hoga aur kaun ISR",
                  "Revalidate time justify karo (30s, 60s, 5min)",
                  "Build-time vs runtime cost compare karo",
                ],
                outcome:
                  "Tum blindly SSG nahi karoge, balki scale aur freshness ke basis pe decision loge.",
              },
              {
                id: "pt-2",
                title: "Dynamic SSG with getStaticPaths",
                description:
                  "Dynamic routes ke saath scalable static generation implement karo.",
                tasks: [
                  "Slug-based dynamic route banao",
                  "getStaticPaths me limited paths generate karo",
                  "Fallback strategy choose karo (false / blocking)",
                  "Build time aur page count impact analyze karo",
                ],
                outcome:
                  "Large content sites ke liye optimized SSG design kar paoge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "SSG vs SSR vs ISR ka clear difference",
                "Build-time vs request-time clarity",
                "ISR ka business use-case samajhna",
                "Rendering strategy justify kar pana",
              ],
              strongAnswer:
                "SSG fastest hota hai, ISR freshness add karta hai. Main SSR tabhi use karta hoon jab request-specific data chahiye.",
              redFlags: [
                "Har jagah SSR use karna",
                "ISR ko SSR ka replacement bolna",
                "Revalidate bina soche set kar dena",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Static HTML generation verification",
                "ISR revalidation behavior",
                "Build-time failures detection",
              ],
              ssgTestingChecks: [
                "HTML me data pre-rendered hai",
                "Revalidate ke baad content update ho raha hai",
                "Fallback routes expected behavior follow kar rahe hain",
              ],
              realWorldNote:
                "SSG/ISR bugs directly SEO aur caching ko affect karte hain, testing ignore nahi hoti.",
            },

            editorGuidance: {
              careerMindset: [
                "Fastest solution hamesha best solution nahi hota",
                "SSG scale ke liye hota hai, ISR balance ke liye",
                "Rendering strategy ek product decision hai",
              ],
              seniorAdvice:
                "Agar tum explain kar sakte ho ki ISR kyun chahiye — tum Next.js production ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Static Site Generation is the most performance-friendly rendering strategy in Next.js. Pages are generated once and served via CDN, making them ideal for high-traffic and SEO-focused applications.",

              getStaticProps: {
                title: "getStaticProps",
                explanation:
                  "getStaticProps fetches data at build time and generates static HTML that is reused for every request.",
                conceptsUsed: [
                  "Build-time data fetching",
                  "Static HTML generation",
                  "CDN caching",
                ],
                exampleLogic: `
export async function getStaticProps() {
  const res = await fetch('https://api.example.com/posts');
  const posts = await res.json();

  return {
    props: {
      posts,
    },
  };
}
      `,
                bestPractice: "Use SSG when data does not change frequently",
              },

              getStaticPaths: {
                title: "getStaticPaths",
                explanation:
                  "getStaticPaths is used for dynamic routes to define which pages should be generated at build time.",
                conceptsUsed: [
                  "Dynamic route generation",
                  "Pre-rendering paths",
                  "Fallback strategies",
                ],
                exampleLogic: `
export async function getStaticPaths() {
  const res = await fetch('https://api.example.com/posts');
  const posts = await res.json();

  const paths = posts.map(post => ({
    params: { slug: post.slug },
  }));

  return {
    paths,
    fallback: false,
  };
}
      `,
                interviewInsight:
                  "getStaticPaths is only used with dynamic routes",
              },

              staticGenerationWithData: {
                title: "Static Generation with Data",
                explanation:
                  "SSG works perfectly with external APIs, CMS systems, and markdown-based content.",
                realWorldSources: [
                  "Headless CMS (Contentful, Sanity)",
                  "Markdown files",
                  "Public APIs",
                ],
                benefit:
                  "Once generated, pages are extremely fast and cheap to serve",
              },

              incrementalStaticRegeneration: {
                title: "Incremental Static Regeneration (ISR)",
                explanation:
                  "ISR allows static pages to be regenerated in the background after a specified time without rebuilding the entire app.",
                conceptsUsed: [
                  "revalidate property",
                  "Background regeneration",
                  "Fresh + cached content balance",
                ],
                exampleLogic: `
export async function getStaticProps() {
  return {
    props: {
      data,
    },
    revalidate: 60,
  };
}
      `,
                realWorldNote:
                  "ISR is widely used in e-commerce and content platforms",
              },

              ssgVsSsr: {
                title: "SSG vs SSR Decision Making",
                ssgBestFor: ["Blogs", "Landing pages", "Documentation"],
                ssrBestFor: [
                  "User-specific dashboards",
                  "Authenticated pages",
                  "Real-time data",
                ],
                interviewInsight:
                  "Choosing the correct rendering strategy is more important than using all of them",
              },

              commonMistakes: [
                {
                  mistake: "Using SSR for static content",
                  issue: "Unnecessary server cost",
                  fix: "Switch to SSG or ISR",
                },
                {
                  mistake: "Generating too many static pages",
                  issue: "Long build times",
                  fix: "Use fallback or ISR",
                },
                {
                  mistake: "Forgetting revalidation",
                  issue: "Stale content",
                  fix: "Use ISR with proper revalidate time",
                },
              ],

              interviewQuestions: [
                "What is SSG in Next.js?",
                "Difference between SSG and SSR?",
                "What problem does ISR solve?",
                "When should you use getStaticPaths?",
              ],
            },

            tasks: [
              "Build static pages using getStaticProps",
              "Create dynamic SSG routes using getStaticPaths",
              "Implement ISR with revalidation",
              "Compare SSG vs SSR for different pages",
              "Optimize build-time data fetching",
            ],

            examples: ["getStaticProps", "getStaticPaths", "revalidate: 60"],

            useCases: [
              "Blogs",
              "Documentation sites",
              "Marketing pages",
              "SEO-focused websites",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand Static Site Generation deeply",
              "Use getStaticProps and getStaticPaths correctly",
              "Apply ISR for fresh yet fast content",
              "Make correct SSR vs SSG decisions",
              "Be interview-ready for Next.js rendering strategies",
            ],
          },
          {
            day: 5,
            topic: "API Routes & Backend Logic",

            subTopics: [
              "API routes concept",
              "Creating API endpoints",
              "Request and response handling",
              "Connecting frontend to API routes",
              "Environment variables",
            ],

            explanation:
              "Next.js API routes allow you to write backend logic inside the same project without a separate server, enabling full-stack development using a single codebase.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Build Real API Route (BFF Pattern)",
                description:
                  "Frontend ke liye backend-for-frontend style API route design karo.",
                tasks: [
                  "Form submission ke liye POST API route banao",
                  "req.method ke basis pe logic handle karo",
                  "Validation + proper status codes add karo",
                  "Frontend se API route consume karo",
                ],
                outcome:
                  "Tum API routes ko sirf demo ke liye nahi, real production use ke liye design kar paoge.",
              },
              {
                id: "pt-2",
                title: "Secure Environment Variables Drill",
                description:
                  "Server-only aur public environment variables ka difference practically apply karo.",
                tasks: [
                  ".env.local me secrets add karo",
                  "Server-only variable API route me use karo",
                  "NEXT_PUBLIC variable frontend me test karo",
                  "Verify secrets browser bundle me leak nahi ho rahe",
                ],
                outcome:
                  "Security basics clear ho jaayenge jo interviews me directly pooche jaate hain.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "API routes ka server-only nature samajhna",
                "HTTP methods (GET/POST) ki clarity",
                "Environment variables security awareness",
                "API routes vs Express ka comparison",
              ],
              strongAnswer:
                "Next.js API routes BFF ke liye perfect hain, par heavy backend ke liye nahi. Secrets hamesha server side rehte hain.",
              redFlags: [
                "API routes ko full backend replacement bolna",
                "NEXT_PUBLIC bina soche use karna",
                "Heavy business logic API routes me daal dena",
              ],
            },

            testingInsights: {
              focusAreas: [
                "API response status codes",
                "Request body handling",
                "Environment variable access",
              ],
              apiTestingChecks: [
                "GET aur POST dono cases test ho rahe hain",
                "Invalid method pe proper error aa raha hai",
                "Secrets frontend bundle me visible nahi hain",
              ],
              realWorldNote:
                "API routes bugs directly data leaks aur production failures cause kar sakte hain.",
            },

            editorGuidance: {
              careerMindset: [
                "Frontend dev ko basic backend aana hi chahiye",
                "Security awareness junior vs senior ko alag karti hai",
                "API routes product speed badhaate hain",
              ],
              seniorAdvice:
                "Agar tum bata sakte ho kab API routes use na karen — tum full-stack thinking rakhte ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world applications, frontend and backend often communicate via APIs. Next.js API routes let frontend developers write backend logic such as form handling, authentication helpers, and database operations directly inside the Next.js project.",

              apiRoutesConcept: {
                title: "API Routes Concept",
                explanation:
                  "API routes are server-side functions that run only on the server and are never bundled into client-side JavaScript.",
                conceptsUsed: [
                  "Server-only execution",
                  "HTTP request/response model",
                  "No client-side exposure",
                ],
                realWorldNote:
                  "API routes are often used as Backend-for-Frontend (BFF)",
              },

              creatingApiEndpoints: {
                title: "Creating API Endpoints",
                explanation:
                  "Each file inside the pages/api directory automatically becomes an API endpoint.",
                conceptsUsed: [
                  "File-based API routing",
                  "REST-style endpoints",
                  "Separation of concerns",
                ],
                exampleLogic: `
export default function handler(req, res) {
  res.status(200).json({ message: 'Hello API' });
}
      `,
                bestPractice: "Keep API logic small and focused per route",
              },

              requestResponseHandling: {
                title: "Request & Response Handling",
                explanation:
                  "API routes receive standard HTTP request and response objects similar to Express.",
                conceptsUsed: [
                  "req.method",
                  "req.body",
                  "res.status and res.json",
                ],
                exampleLogic: `
if (req.method === 'POST') {
  const data = req.body;
  res.status(201).json({ success: true });
}
      `,
                interviewInsight:
                  "Understanding HTTP methods is critical for backend interviews",
              },

              connectingFrontendToApi: {
                title: "Connecting Frontend to API Routes",
                explanation:
                  "Frontend components can fetch data from API routes using fetch or Axios.",
                conceptsUsed: [
                  "Client-server communication",
                  "Relative API paths",
                  "Async data handling",
                ],
                exampleLogic: `
fetch('/api/posts')
  .then(res => res.json())
  .then(data => setPosts(data));
      `,
                realWorldNote:
                  "This pattern avoids exposing external APIs directly to the client",
              },

              environmentVariables: {
                title: "Environment Variables",
                explanation:
                  "Environment variables store sensitive data like API keys securely on the server.",
                conceptsUsed: [
                  ".env.local",
                  "NEXT_PUBLIC prefix",
                  "Server-only secrets",
                ],
                exampleLogic: `
process.env.DATABASE_URL
      `,
                bestPractice: "Never expose secrets without NEXT_PUBLIC prefix",
              },

              commonMistakes: [
                {
                  mistake: "Using API routes for heavy backend logic",
                  issue: "Performance and scalability limits",
                  fix: "Move heavy logic to dedicated backend",
                },
                {
                  mistake: "Exposing secrets to frontend",
                  issue: "Security risk",
                  fix: "Use server-only environment variables",
                },
                {
                  mistake: "Mixing frontend and backend logic",
                  issue: "Hard to maintain code",
                  fix: "Separate concerns clearly",
                },
              ],

              interviewQuestions: [
                "What are API routes in Next.js?",
                "How are API routes different from Express?",
                "How do you secure environment variables?",
                "When should you not use API routes?",
              ],
            },

            tasks: [
              "Create API route using pages/api",
              "Handle GET and POST requests",
              "Connect frontend component to API route",
              "Store secrets using environment variables",
              "Test API route locally",
            ],

            examples: ["pages/api/posts.js", "res.status(200).json()"],

            useCases: [
              "Form handling",
              "Server-side data processing",
              "Internal APIs",
              "Authentication helpers",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand how backend logic works in Next.js",
              "Create and consume API routes confidently",
              "Handle HTTP requests and responses",
              "Secure sensitive data using environment variables",
              "Build full-stack features without a separate backend",
            ],
          },
          {
            day: 6,
            topic: "SEO, Images & Performance Optimization",

            subTopics: [
              "next/head for SEO",
              "Meta tags and Open Graph",
              "next/image optimization",
              "Font optimization",
              "Performance best practices",
            ],

            explanation:
              "Next.js provides built-in tools for SEO and performance optimization, enabling developers to build fast-loading, search-engine-friendly applications by default.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "SEO Audit & Fix (Real Website)",
                description:
                  "Ek existing page ka SEO audit karke Next.js best practices apply karo.",
                tasks: [
                  "Unique title & meta description set karo",
                  "Open Graph tags add karo (og:title, og:image)",
                  "Missing alt attributes fix karo",
                  "Duplicate titles identify karo",
                ],
                outcome:
                  "Tum SEO ko sirf theory nahi, real ranking perspective se samajh paoge.",
              },
              {
                id: "pt-2",
                title: "Performance Optimization Drill",
                description:
                  "Images, fonts aur JS bundle ko optimize karke Lighthouse score improve karo.",
                tasks: [
                  "<img> ko next/image se replace karo",
                  "Fonts ko next/font se load karo",
                  "Unused JS identify karo",
                  "Before vs after Lighthouse score compare karo",
                ],
                outcome:
                  "Core Web Vitals improve karna practical level pe aayega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "SEO basics + implementation clarity",
                "next/image ke benefits samajhna",
                "Core Web Vitals awareness",
                "Performance vs UX tradeoffs",
              ],
              strongAnswer:
                "Next.js SEO aur performance ko default-first approach deta hai — par developer ko sahi tools sahi jagah use karne hote hain.",
              redFlags: [
                "SEO sirf meta tags tak limit karna",
                "Har jagah <img> use karna",
                "Lighthouse ko ignore karna",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Meta tags rendering",
                "Image loading behavior",
                "Font loading & CLS",
                "Overall Lighthouse metrics",
              ],
              performanceChecks: [
                "LCP image next/image se load ho raha hai",
                "CLS font shift ke bina stable hai",
                "Unused JS bundle me nahi ja raha",
              ],
              realWorldNote:
                "SEO aur performance bugs silent hote hain — par business impact heavy hota hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Fast website = better product + better ranking",
                "SEO frontend dev ka responsibility hai",
                "Performance optimization senior-level skill hai",
              ],
              seniorAdvice:
                "Agar tum bata sakte ho *kyun* next/image better hai — sirf *kaise* nahi — tum production-ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In production applications, performance and SEO are non-negotiable. Faster websites rank higher on search engines, convert better, and provide superior user experience. Next.js solves many SEO and performance problems out of the box.",

              seoWithHead: {
                title: "SEO with next/head",
                explanation:
                  "The next/head component allows you to modify the document head for each page, which is essential for SEO.",
                conceptsUsed: [
                  "Dynamic page titles",
                  "Meta descriptions",
                  "Per-page SEO control",
                ],
                exampleLogic: `
import Head from 'next/head';

<Head>
  <title>Home Page</title>
  <meta name="description" content="Best products online" />
</Head>
      `,
                realWorldNote:
                  "Each page should have a unique title and description",
              },

              metaTagsAndOG: {
                title: "Meta Tags & Open Graph",
                explanation:
                  "Open Graph and social meta tags control how links appear when shared on social platforms.",
                conceptsUsed: [
                  "og:title",
                  "og:description",
                  "og:image",
                  "Twitter cards",
                ],
                exampleLogic: `
<meta property="og:title" content="Product Page" />
<meta property="og:image" content="/cover.png" />
      `,
                interviewInsight:
                  "SEO is not only about Google — social sharing matters",
              },

              imageOptimization: {
                title: "Image Optimization with next/image",
                explanation:
                  "The next/image component automatically optimizes images for size, format, and loading behavior.",
                conceptsUsed: [
                  "Automatic image resizing",
                  "Lazy loading by default",
                  "WebP optimization",
                ],
                exampleLogic: `
import Image from 'next/image';

<Image src="/hero.png" width={400} height={300} alt="Hero" />
      `,
                bestPractice:
                  "Always provide width, height, and alt attributes",
              },

              fontOptimization: {
                title: "Font Optimization",
                explanation:
                  "Next.js optimizes fonts to prevent layout shifts and reduce loading time.",
                conceptsUsed: [
                  "next/font",
                  "Preloading fonts",
                  "Avoiding layout shifts (CLS)",
                ],
                exampleLogic: `
import { Inter } from 'next/font/google';

const inter = Inter({ subsets: ['latin'] });
      `,
                realWorldNote:
                  "Fonts are a common cause of poor Core Web Vitals",
              },

              performanceBestPractices: {
                title: "Performance Best Practices",
                explanation:
                  "Beyond built-in tools, developers must follow performance best practices.",
                conceptsUsed: [
                  "Code splitting",
                  "Avoiding large client-side bundles",
                  "Reducing unnecessary JavaScript",
                ],
                bestPractices: [
                  "Use SSR or SSG where appropriate",
                  "Lazy load non-critical components",
                  "Optimize images and fonts",
                  "Measure performance using Lighthouse",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Using <img> instead of next/image",
                  issue: "Large image sizes and slow loading",
                  fix: "Use next/image for automatic optimization",
                },
                {
                  mistake: "Same title for all pages",
                  issue: "Poor SEO ranking",
                  fix: "Set unique titles per page",
                },
                {
                  mistake: "Ignoring Core Web Vitals",
                  issue: "Lower Google ranking",
                  fix: "Monitor and optimize performance metrics",
                },
              ],

              interviewQuestions: [
                "How does Next.js help with SEO?",
                "Difference between <img> and next/image?",
                "What are Core Web Vitals?",
                "How do you improve Lighthouse score?",
              ],
            },

            tasks: [
              "Add SEO meta tags using next/head",
              "Implement Open Graph tags",
              "Replace <img> with next/image",
              "Optimize fonts using next/font",
              "Improve Lighthouse performance score",
            ],

            examples: [
              "<Head><title>Page</title></Head>",
              "<Image src='/img.png' />",
            ],

            useCases: [
              "SEO-critical websites",
              "E-commerce platforms",
              "High-performance apps",
              "Marketing websites",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand SEO fundamentals in Next.js",
              "Optimize images and fonts effectively",
              "Improve Core Web Vitals and Lighthouse score",
              "Build search-engine-friendly React applications",
              "Prepare for SEO and performance-related interview questions",
            ],
          },
          {
            day: 7,
            topic: "Week 30 Project – Full Next.js Application",

            subTopics: [
              "SSR and SSG usage",
              "Dynamic routing",
              "API routes integration",
              "SEO optimization",
              "Production deployment",
            ],

            explanation:
              "This project proves your ability to design, build, optimize, and deploy a complete production-ready Next.js application using real-world best practices.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Next.js App Build",
                description:
                  "Ek complete real-world Next.js app banao jo portfolio + interview dono me kaam aaye.",
                tasks: [
                  "At least 1 SSR page (dashboard / dynamic data)",
                  "At least 2 SSG pages (home, blog, landing)",
                  "1 dynamic route using [slug]",
                  "API route for form or data handling",
                  "SEO meta tags har page pe",
                ],
                outcome:
                  "Tumhare paas ek end-to-end production-ready Next.js project hoga.",
              },
              {
                id: "pt-2",
                title: "Deployment & Live Optimization",
                description:
                  "Application ko Vercel pe deploy karke real production issues handle karo.",
                tasks: [
                  "GitHub repo clean & structured banao",
                  "Environment variables configure karo",
                  "Production build errors fix karo",
                  "Live app ko Lighthouse se audit karo",
                ],
                outcome:
                  "Deployment + production debugging ka real experience milega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "SSR vs SSG decision clarity",
                "Real project ownership",
                "SEO + performance awareness",
                "Deployment understanding",
              ],
              strongAnswer:
                "Maine ek full Next.js app banayi jisme SSR, SSG, API routes, SEO aur production deployment sab cover kiya.",
              redFlags: [
                "Sirf tutorial-based project",
                "SSR/SSG ka reason explain na kar paana",
                "Deployment experience na hona",
              ],
            },

            testingInsights: {
              focusAreas: [
                "SSR pages ka correct data render",
                "SSG pages ka build-time behavior",
                "API routes error handling",
                "SEO meta tags presence",
              ],
              productionChecks: [
                "Broken routes nahi hone chahiye",
                "Env variables leak nahi hone chahiye",
                "Lighthouse score acceptable range me ho",
              ],
              realWorldNote:
                "Final project me bugs ka milna normal hai — unko fix karna hi senior skill hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Project ownership matters more than features count",
                "End-to-end delivery senior signal hai",
                "Production soch bina bole dikhni chahiye",
              ],
              seniorAdvice:
                "Agar tum apne project ke har decision ka reason explain kar sakte ho — tum job-ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real companies, frontend developers are expected to deliver complete applications — not just components. This project simulates a production-grade Next.js app with routing, data fetching strategies, backend APIs, SEO, and deployment.",

              projectScope: {
                title: "Project Scope",
                explanation:
                  "Build a full-featured Next.js application that demonstrates both frontend and backend capabilities using the Next.js ecosystem.",
                realWorldNote:
                  "This type of project is often used as a flagship portfolio piece",
              },

              ssrAndSsg: {
                title: "SSR & SSG Usage",
                explanation:
                  "Different pages require different data fetching strategies based on freshness, SEO, and performance needs.",
                conceptsUsed: [
                  "getServerSideProps for dynamic data",
                  "getStaticProps for static content",
                  "ISR for scalable updates",
                ],
                exampleLogic: `
export async function getServerSideProps() {
  return { props: { data } };
}

export async function getStaticProps() {
  return { props: { posts }, revalidate: 60 };
}
      `,
                bestPractice:
                  "Use SSG wherever possible, SSR only when necessary",
              },

              dynamicRouting: {
                title: "Dynamic Routing",
                explanation:
                  "Dynamic routes allow content-driven pages such as blogs, products, and profiles.",
                conceptsUsed: [
                  "File-based routing",
                  "Dynamic segments [slug]",
                  "Fallback strategies",
                ],
                exampleLogic: `
pages/posts/[slug].js
      `,
                realWorldNote:
                  "Dynamic routing is essential for scalable content platforms",
              },

              apiRoutes: {
                title: "API Routes Integration",
                explanation:
                  "API routes enable backend logic within the same project, reducing complexity.",
                conceptsUsed: [
                  "REST-style endpoints",
                  "Request/response handling",
                  "Environment variables",
                ],
                exampleLogic: `
export default function handler(req, res) {
  res.status(200).json({ success: true });
}
      `,
                interviewInsight:
                  "API routes are commonly used for forms, auth, and server-side logic",
              },

              seoOptimization: {
                title: "SEO Optimization",
                explanation:
                  "SEO is a core strength of Next.js and critical for real-world products.",
                conceptsUsed: [
                  "next/head",
                  "Meta & Open Graph tags",
                  "Dynamic titles",
                ],
                bestPractice:
                  "Every page must have unique title and description",
              },

              productionDeployment: {
                title: "Production Deployment",
                explanation:
                  "Deployment completes the product lifecycle and makes your app publicly accessible.",
                conceptsUsed: [
                  "Vercel deployment",
                  "Environment variables",
                  "Production builds",
                ],
                steps: [
                  "Push code to GitHub",
                  "Connect repository to Vercel",
                  "Configure environment variables",
                  "Deploy and test live app",
                ],
                realWorldNote:
                  "Vercel is the industry-standard platform for Next.js apps",
              },

              commonMistakes: [
                {
                  mistake: "Using SSR everywhere",
                  issue: "Slower performance",
                  fix: "Prefer SSG + ISR",
                },
                {
                  mistake: "No SEO metadata",
                  issue: "Poor search ranking",
                  fix: "Add next/head for every page",
                },
                {
                  mistake: "No error handling in APIs",
                  issue: "Unstable app",
                  fix: "Handle errors and status codes",
                },
              ],

              interviewQuestions: [
                "When would you choose SSR over SSG?",
                "How does Next.js improve SEO?",
                "How do API routes differ from traditional backend?",
                "How do you deploy a Next.js app?",
                "What would you optimize for performance?",
              ],
            },

            tasks: [
              "Build a full Next.js application",
              "Implement SSR, SSG, and ISR correctly",
              "Create dynamic routes",
              "Integrate API routes",
              "Add complete SEO optimization",
              "Deploy application on Vercel",
            ],

            examples: [
              "Blog platform",
              "E-commerce store",
              "Content-driven website",
            ],

            useCases: [
              "Portfolio flagship project",
              "Startup-grade frontend",
              "Production-ready applications",
              "Interview showcase",
            ],

            editorType: "react",

            estimatedTime: "4–6 hours",

            objectives: [
              "Build a complete production-ready Next.js app",
              "Understand real-world data fetching strategies",
              "Implement SEO and performance best practices",
              "Deploy and maintain live frontend applications",
              "Confidently explain Next.js architecture in interviews",
            ],
          },
        ],
      },
    ],
    questions: [
      {
        id: "a1",
        question:
          "Explain different state management solutions (Redux, Zustand, Recoil). When would you use each?",
        difficulty: "Hard",
        topics: ["State Management", "Redux"],
      },
      {
        id: "a2",
        question:
          "What is Redux and explain its core principles (actions, reducers, store).",
        difficulty: "Hard",
        topics: ["Redux"],
      },
      {
        id: "a3",
        question:
          "Explain the concept of middleware in Redux (like Redux Thunk or Redux Saga).",
        difficulty: "Hard",
        topics: ["Redux", "Middleware"],
      },
      {
        id: "a4",
        question: "What is TypeScript and what benefits does it provide?",
        difficulty: "Medium",
        topics: ["TypeScript"],
      },
      {
        id: "a5",
        question:
          "Explain the difference between 'any', 'unknown', and 'never' types in TypeScript.",
        difficulty: "Hard",
        topics: ["TypeScript"],
      },
      {
        id: "a6",
        question: "What are TypeScript generics and when would you use them?",
        difficulty: "Hard",
        topics: ["TypeScript", "Generics"],
      },
      {
        id: "a7",
        question:
          "Explain code splitting and lazy loading in React. How do they improve performance?",
        difficulty: "Hard",
        topics: ["Performance", "Code Splitting"],
      },
      {
        id: "a8",
        question:
          "What is server-side rendering (SSR) and when should you use it?",
        difficulty: "Hard",
        topics: ["SSR", "Performance"],
      },
      {
        id: "a9",
        question: "Explain the differences between SSR, SSG, and CSR.",
        difficulty: "Hard",
        topics: ["Rendering Strategies"],
      },
      {
        id: "a10",
        question:
          "What is Next.js and what advantages does it offer over Create React App?",
        difficulty: "Medium",
        topics: ["Next.js", "Frameworks"],
      },
      {
        id: "a11",
        question:
          "Explain how you would optimize a React application's performance.",
        difficulty: "Hard",
        topics: ["Performance Optimization"],
      },
      {
        id: "a12",
        question: "What are Web Vitals and why are they important?",
        difficulty: "Medium",
        topics: ["Performance", "Web Vitals"],
      },
      {
        id: "a13",
        question:
          "Explain different testing approaches (unit, integration, e2e) and tools (Jest, React Testing Library, Cypress).",
        difficulty: "Hard",
        topics: ["Testing"],
      },
      {
        id: "a14",
        question:
          "What is accessibility (a11y) and how do you ensure your applications are accessible?",
        difficulty: "Medium",
        topics: ["Accessibility"],
      },
      {
        id: "a15",
        question: "Explain PWA (Progressive Web Apps) and their key features.",
        difficulty: "Medium",
        topics: ["PWA"],
      },
      {
        id: "a16",
        question: "What are Web Workers and when would you use them?",
        difficulty: "Hard",
        topics: ["Web Workers", "Performance"],
      },
      {
        id: "a17",
        question:
          "Explain the concept of debouncing and throttling. When would you use each?",
        difficulty: "Medium",
        topics: ["Performance", "Optimization"],
      },
      {
        id: "a18",
        question: "What is GraphQL and how does it differ from REST?",
        difficulty: "Medium",
        topics: ["GraphQL", "API"],
      },
      {
        id: "a19",
        question: "Explain React Query (TanStack Query) and its benefits.",
        difficulty: "Hard",
        topics: ["React Query", "Data Fetching"],
      },
      {
        id: "a20",
        question: "What is React Server Components and how do they work?",
        difficulty: "Hard",
        topics: ["React Server Components"],
      },
      {
        id: "a21",
        question:
          "Explain the differences between interface and type in TypeScript.",
        difficulty: "Medium",
        topics: ["TypeScript"],
      },
      {
        id: "a22",
        question:
          "What are utility types in TypeScript? Name and explain some common ones.",
        difficulty: "Hard",
        topics: ["TypeScript", "Utility Types"],
      },
      {
        id: "a23",
        question: "How do you implement type guards in TypeScript?",
        difficulty: "Hard",
        topics: ["TypeScript", "Type Guards"],
      },
      {
        id: "a24",
        question: "What is incremental static regeneration (ISR) in Next.js?",
        difficulty: "Hard",
        topics: ["Next.js", "ISR"],
      },
      {
        id: "a25",
        question: "Explain the App Router vs Pages Router in Next.js 13+.",
        difficulty: "Medium",
        topics: ["Next.js"],
      },
      {
        id: "a26",
        question:
          "What are middleware in Next.js and what can you do with them?",
        difficulty: "Medium",
        topics: ["Next.js", "Middleware"],
      },
      {
        id: "a27",
        question: "How do you implement infinite scrolling efficiently?",
        difficulty: "Hard",
        topics: ["Performance", "UI Patterns"],
      },
      {
        id: "a28",
        question: "What is virtualization in lists and why is it important?",
        difficulty: "Hard",
        topics: ["Performance", "Virtualization"],
      },
      {
        id: "a29",
        question: "Explain critical rendering path and how to optimize it.",
        difficulty: "Hard",
        topics: ["Performance", "Browser"],
      },
      {
        id: "a30",
        question: "What are Web Components and how do they relate to React?",
        difficulty: "Medium",
        topics: ["Web Components"],
      },
      {
        id: "a31",
        question: "Explain the SOLID principles in frontend development.",
        difficulty: "Hard",
        topics: ["Design Patterns", "Best Practices"],
      },
      {
        id: "a32",
        question:
          "What is the Observer pattern and how is it used in frontend?",
        difficulty: "Hard",
        topics: ["Design Patterns"],
      },
      {
        id: "a33",
        question: "How do you implement optimistic UI updates?",
        difficulty: "Hard",
        topics: ["UI Patterns", "State Management"],
      },
      {
        id: "a34",
        question:
          "What is the difference between unit tests, integration tests, and e2e tests?",
        difficulty: "Medium",
        topics: ["Testing"],
      },
      {
        id: "a35",
        question:
          "How do you test React components with React Testing Library?",
        difficulty: "Medium",
        topics: ["Testing", "React Testing Library"],
      },
      {
        id: "a36",
        question:
          "What is test-driven development (TDD) and how do you practice it?",
        difficulty: "Medium",
        topics: ["Testing", "TDD"],
      },
      {
        id: "a37",
        question: "Explain snapshot testing and when to use it.",
        difficulty: "Medium",
        topics: ["Testing", "Jest"],
      },
      {
        id: "a38",
        question: "What are ARIA labels and roles? Why are they important?",
        difficulty: "Medium",
        topics: ["Accessibility"],
      },
      {
        id: "a39",
        question: "How do you make a website keyboard navigable?",
        difficulty: "Medium",
        topics: ["Accessibility"],
      },
      {
        id: "a40",
        question:
          "What is the difference between SSG, SSR, and ISR in Next.js?",
        difficulty: "Hard",
        topics: ["Next.js", "Rendering"],
      },
      {
        id: "a41",
        question: "Explain micro-frontends architecture.",
        difficulty: "Hard",
        topics: ["Architecture", "Micro-frontends"],
      },
      {
        id: "a42",
        question: "What is module federation in Webpack 5?",
        difficulty: "Hard",
        topics: ["Webpack", "Module Federation"],
      },
      {
        id: "a43",
        question: "How do you implement internationalization (i18n) in React?",
        difficulty: "Medium",
        topics: ["i18n", "Localization"],
      },
      {
        id: "a44",
        question: "What is the Lighthouse score and how do you improve it?",
        difficulty: "Medium",
        topics: ["Performance", "Lighthouse"],
      },
      {
        id: "a45",
        question: "Explain the concept of hydration in SSR applications.",
        difficulty: "Hard",
        topics: ["SSR", "Hydration"],
      },
    ],
  },
  {
    phase: 6,
    phaseKey: "career",
    title: "Job Preparation & Professional Skills",
    duration: "4 Weeks",
    level: "Job Ready",
    weeks: [
      {
        week: 31,
        weekGoal:
          "Build a strong personal brand and job-ready portfolio that stands out to recruiters",
        weekOutcome:
          "A production-ready portfolio, optimized GitHub profile, resume, and LinkedIn presence",
        miniProject: "Professional Portfolio Website",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Portfolio Strategy & Structure",

            subTopics: [
              "What recruiters look for in a portfolio",
              "Portfolio vs resume vs GitHub",
              "Choosing the right projects",
              "Information architecture for portfolio",
              "Common portfolio mistakes",
            ],

            explanation:
              "A good portfolio tells a clear story of your skills, problem-solving ability, decision making, and growth as a frontend developer — far beyond just showing screenshots.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Recruiter-Eye Portfolio Audit",
                description:
                  "Apne portfolio ko recruiter ke POV se audit karo (2-minute scan test).",
                tasks: [
                  "Top section 10 sec me skill + role clear hota hai ya nahi check karo",
                  "Har project ka problem statement clearly visible hai ya nahi",
                  "Live links + GitHub links working hain ya nahi",
                  "Mobile view me portfolio readable hai ya nahi",
                ],
                outcome:
                  "Tumhara portfolio first impression me clarity aur trust create karega.",
              },
              {
                id: "pt-2",
                title: "Case-Study Project Rewrite",
                description:
                  "Ek existing project ko proper case-study format me rewrite karo.",
                tasks: [
                  "Project ka real problem likho (not features)",
                  "Why this solution choose ki — explain karo",
                  "Key technical decisions mention karo",
                  "Result / learning / improvement points add karo",
                ],
                outcome:
                  "Project sirf dikhne wala nahi, explainable ban jayega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear ownership of projects",
                "Decision-making ability",
                "Trade-off awareness",
                "Growth mindset",
              ],
              strongAnswer:
                "Mera portfolio har project ko case-study ki tarah present karta hai — problem, decision aur outcome ke saath.",
              redFlags: [
                "Sirf screenshots, koi explanation nahi",
                "Copied descriptions",
                "Live demo ya code missing",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Portfolio 2 minute me samajh aa raha hai ya nahi",
                "Project descriptions me clarity hai ya buzzwords",
                "Navigation simple aur distraction-free hai ya nahi",
              ],
              validationChecks: [
                "Mobile + desktop dono pe smooth experience",
                "External links broken nahi",
                "Projects repeat ya redundant nahi",
              ],
              realWorldNote:
                "Recruiter portfolio test karta hai, admire nahi karta — clarity jeet ti hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Portfolio ek story hai, gallery nahi",
                "Kam projects, par strong explanation",
                "Decision dikhana skill se zyada important hota hai",
              ],
              seniorAdvice:
                "Agar tum apne portfolio ke har project ko 5 minute explain kar sakte ho — tum shortlist-ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real hiring processes, recruiters often spend less than 2 minutes scanning a portfolio. This day focuses on building a portfolio that communicates impact, clarity, and credibility instantly.",

              recruiterMindset: {
                title: "What Recruiters Actually Look For",
                explanation:
                  "Recruiters are not looking for perfection — they are looking for clarity, ownership, and real-world thinking.",
                keySignals: [
                  "Clear problem → solution explanation",
                  "Use of modern tools & best practices",
                  "Ability to explain trade-offs",
                  "Consistency across projects",
                ],
                interviewInsight:
                  "Many interview questions are directly picked from portfolio projects",
              },

              portfolioVsResumeVsGithub: {
                title: "Portfolio vs Resume vs GitHub",
                explanation:
                  "Each platform serves a different purpose and should complement each other.",
                comparison: {
                  portfolio: "Storytelling + visuals + project context",
                  resume: "Quick scan of skills, experience, and impact",
                  github: "Code quality, consistency, and depth",
                },
                realWorldNote:
                  "A strong portfolio can compensate for a weaker resume, but not vice versa",
              },

              choosingProjects: {
                title: "Choosing the Right Projects",
                explanation:
                  "Not all projects deserve to be showcased. Fewer strong projects are better than many weak ones.",
                selectionCriteria: [
                  "Real-world relevance",
                  "Clear problem statement",
                  "Non-trivial logic or architecture",
                  "Demonstrates growth",
                ],
                recommendedCount: "3–5 high-quality projects are ideal",
              },

              informationArchitecture: {
                title: "Portfolio Information Architecture",
                explanation:
                  "A well-structured portfolio guides the viewer naturally without confusion.",
                suggestedSections: [
                  "Hero / Introduction",
                  "About (skills + journey)",
                  "Projects (case-study format)",
                  "Blogs / Learnings (optional)",
                  "Contact / CTA",
                ],
                bestPractice:
                  "Each project should answer: What, Why, How, Result",
              },

              commonMistakes: [
                {
                  mistake: "Listing too many projects",
                  issue: "Diluted impact",
                  fix: "Showcase only strongest work",
                },
                {
                  mistake: "Only screenshots, no explanation",
                  issue: "No insight into thinking",
                  fix: "Add case-study style breakdown",
                },
                {
                  mistake: "Generic descriptions",
                  issue: "Looks copied",
                  fix: "Write in your own voice",
                },
                {
                  mistake: "No live links",
                  issue: "Low trust",
                  fix: "Always deploy projects",
                },
              ],

              interviewQuestions: [
                "Which project in your portfolio are you most proud of?",
                "What problem did this project solve?",
                "What trade-offs did you make?",
                "How would you improve this project if given more time?",
              ],
            },

            tasks: [
              "Analyze 3–5 strong frontend developer portfolios",
              "Shortlist projects worth showcasing",
              "Remove weak or redundant projects",
              "Outline portfolio sections and flow",
              "Write 1 project outline in case-study format",
            ],

            examples: [
              "Home, About, Projects, Blogs, Contact",
              "Case-study based project pages",
            ],

            useCases: [
              "Job applications",
              "Freelance clients",
              "Personal branding",
              "Interview discussions",
            ],

            editorType: null,

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Understand how recruiters evaluate portfolios",
              "Learn to differentiate portfolio from resume and GitHub",
              "Select the right projects strategically",
              "Design a clear portfolio structure",
              "Build a strong foundation for a job-ready portfolio",
            ],
          },
          {
            day: 2,
            topic: "Portfolio Website Design & UX",

            subTopics: [
              "Clean UI vs flashy UI",
              "Typography and color selection",
              "Responsive layout planning",
              "Accessibility considerations",
              "Performance-first design",
            ],

            explanation:
              "Your portfolio UI should reflect real-world product thinking, usability, and clarity — not just visual flashiness.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Recruiter-First UI Redesign",
                description:
                  "Portfolio UI ko recruiter ke usability aur readability lens se redesign karo.",
                tasks: [
                  "Hero section ko simplify karo (clear role + value)",
                  "Animations ko audit karo — kaunsa truly useful hai?",
                  "Typography scale define karo (H1 → body)",
                  "Spacing aur visual hierarchy clean karo",
                ],
                outcome:
                  "Portfolio visually calm, professional aur recruiter-friendly banega.",
              },
              {
                id: "pt-2",
                title: "Accessibility & Performance Pass",
                description:
                  "Portfolio UI par accessibility aur performance focused improvements apply karo.",
                tasks: [
                  "Color contrast WCAG standard ke against check karo",
                  "Keyboard navigation test karo",
                  "Images optimize karo (size + format)",
                  "Unnecessary animation libraries remove karo",
                ],
                outcome:
                  "Portfolio senior-level frontend standards follow karega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Design decision justification",
                "UX over visual flash",
                "Accessibility awareness",
                "Performance-first thinking",
              ],
              strongAnswer:
                "Maine clean UI choose kiya kyunki recruiter ko content aur decisions quickly samajhne hote hain — visuals unko support karte hain, distract nahi.",
              redFlags: [
                "Sirf fancy animations dikhana",
                "Accessibility ignore karna",
                "Mobile experience weak hona",
              ],
            },

            testingInsights: {
              focusAreas: [
                "2-minute usability test (scroll + scan)",
                "Mobile readability",
                "Text contrast & font size clarity",
              ],
              validationChecks: [
                "Mobile pe tap targets easy hain",
                "Text bina zoom readable hai",
                "Page load fast hai (< 2s)",
              ],
              realWorldNote:
                "Recruiter UI ko judge karta hai silently — confusion = rejection.",
            },

            editorGuidance: {
              careerMindset: [
                "Good UI loud nahi hoti, clear hoti hai",
                "Design ka kaam content ko shine karna hai",
                "Performance bhi UX ka part hai",
              ],
              seniorAdvice:
                "Agar tum apni UI decisions explain kar sakte ho — tum sirf developer nahi, product thinker ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Recruiters subconsciously judge your frontend maturity from your portfolio UI. This day focuses on designing a portfolio that feels professional, usable, accessible, and product-ready.",

              cleanVsFlashyUI: {
                title: "Clean UI vs Flashy UI",
                explanation:
                  "Most recruiters prefer clarity and readability over excessive animations and visual noise.",
                comparison: {
                  cleanUI: [
                    "Clear hierarchy",
                    "Readable typography",
                    "Focus on content",
                    "Fast loading",
                  ],
                  flashyUI: [
                    "Heavy animations",
                    "Overuse of gradients",
                    "Distracting effects",
                    "Performance issues",
                  ],
                },
                recruiterPreference:
                  "Clean, calm, and predictable UI almost always wins",
              },

              typographyAndColors: {
                title: "Typography & Color Selection",
                explanation:
                  "Typography and color choices directly affect readability, accessibility, and perception of professionalism.",
                bestPractices: [
                  "Use max 2 font families",
                  "Strong contrast between text and background",
                  "Consistent spacing and font scale",
                ],
                recommendedFonts: ["Inter", "Poppins", "Roboto", "DM Sans"],
                colorGuideline:
                  "Neutral base + 1 primary accent color works best",
              },

              responsivePlanning: {
                title: "Responsive Layout Planning",
                explanation:
                  "Your portfolio must look good on laptops, tablets, and mobile devices.",
                breakpoints: [
                  "Mobile (< 640px)",
                  "Tablet (640px – 1024px)",
                  "Desktop (> 1024px)",
                ],
                layoutTips: [
                  "Mobile-first approach",
                  "Flexible grids",
                  "Avoid fixed widths",
                ],
                realWorldNote:
                  "Many recruiters check portfolios on mobile first",
              },

              accessibility: {
                title: "Accessibility Considerations",
                explanation:
                  "Accessible UI reflects professional frontend standards and inclusivity.",
                keyPractices: [
                  "Proper heading hierarchy",
                  "Keyboard navigability",
                  "Sufficient color contrast",
                  "Accessible buttons and links",
                ],
                interviewInsight:
                  "Accessibility awareness is a strong senior-level signal",
              },

              performanceFirstDesign: {
                title: "Performance-First Design",
                explanation:
                  "A beautiful portfolio that loads slowly hurts first impressions.",
                performanceTips: [
                  "Avoid heavy background videos",
                  "Optimize images",
                  "Limit animation libraries",
                  "Prefer CSS over JS animations",
                ],
                bestPractice:
                  "Design decisions should support performance, not fight it",
              },

              commonMistakes: [
                {
                  mistake: "Over-animated UI",
                  issue: "Distracts from content",
                  fix: "Use subtle, meaningful animations",
                },
                {
                  mistake: "Poor contrast text",
                  issue: "Hard to read",
                  fix: "Follow accessibility contrast rules",
                },
                {
                  mistake: "Desktop-only design",
                  issue: "Breaks on mobile",
                  fix: "Design mobile-first",
                },
              ],

              interviewQuestions: [
                "How did you decide your portfolio UI design?",
                "How do you balance design and performance?",
                "What accessibility practices did you follow?",
                "Why did you choose this color palette?",
              ],
            },

            tasks: [
              "Design portfolio layout in Figma or directly in code",
              "Choose color palette and typography",
              "Plan responsive breakpoints",
              "Decide light/dark mode strategy",
              "Review design from recruiter perspective",
            ],

            examples: [
              "Minimal developer portfolio layouts",
              "Dark/light mode portfolios",
            ],

            useCases: [
              "First impression on recruiters",
              "Show UX thinking",
              "Demonstrate frontend maturity",
              "Product-focused portfolio",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Design a clean, professional portfolio UI",
              "Understand recruiter preferences in UI/UX",
              "Apply typography and color best practices",
              "Plan responsive and accessible layouts",
              "Balance visual design with performance",
            ],
          },
          {
            day: 3,
            topic: "Project Case Studies & Storytelling",

            subTopics: [
              "Problem → Solution → Result format",
              "Tech stack explanation",
              "Challenges and trade-offs",
              "Screenshots and demos",
              "Metrics and impact",
            ],

            explanation:
              "Recruiters care more about how you think, solve problems, and make decisions than the number of projects you have built.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Project → Case Study Conversion",
                description:
                  "Normal project description ko recruiter-grade case study me convert karo.",
                tasks: [
                  "Har project ke liye clear Problem statement likho",
                  "Solution section me architecture + approach explain karo",
                  "Result me impact ya learning quantify karo",
                  "Before/After thinking clearly mention karo",
                ],
                outcome:
                  "Projects sirf features nahi, thinking aur impact dikhayenge.",
              },
              {
                id: "pt-2",
                title: "Tech Decision Breakdown",
                description:
                  "Tech stack ko justify karo jaise interview me pucha jata hai.",
                tasks: [
                  "Har major tech choice ke alternatives likho",
                  "Explain karo kyun selected tech best tha is problem ke liye",
                  "Trade-offs clearly mention karo",
                  "Overengineering ya underengineering justify karo",
                ],
                outcome:
                  "Recruiter ko lagega tum decisions samajh ke lete ho, blindly nahi.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Problem-solving clarity",
                "Decision-making maturity",
                "Trade-offs awareness",
                "Impact-oriented thinking",
              ],
              strongAnswer:
                "Is project me main goal X problem solve karna tha. Maine Y approach choose ki kyunki scale aur complexity ke hisaab se ye best fit tha, even though Z trade-off accept karna pada.",
              redFlags: [
                "Sirf features ginana",
                "Buzzwords without explanation",
                "No measurable outcome",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Case study 2-minute readability test",
                "Problem → Solution → Result clarity",
                "Metrics credibility",
              ],
              validationChecks: [
                "Recruiter bina poora padhe core samajh sakta hai",
                "Har project ka clear end-result hai",
                "Tech explanation non-generic hai",
              ],
              realWorldNote:
                "Strong case studies interview questions khud generate kar dete hain.",
            },

            editorGuidance: {
              careerMindset: [
                "Projects se zyada important unki kahani hoti hai",
                "Engineer ka kaam sirf build karna nahi, explain karna bhi hai",
                "Clarity > complexity",
              ],
              seniorAdvice:
                "Agar tum apne project ko 3 minute me clearly explain kar sakte ho — tum already top 20% me ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "A strong project case study turns an average project into a powerful hiring signal. This day focuses on explaining your projects like a professional engineer, not a student.",

              problemSolutionResult: {
                title: "Problem → Solution → Result Framework",
                explanation:
                  "This structure helps recruiters quickly understand the value of your work.",
                structure: {
                  problem:
                    "What real problem existed? Who faced it? Why did it matter?",
                  solution:
                    "What approach did you take? What architecture or logic did you design?",
                  result:
                    "What was achieved? Performance gains, UX improvement, or learning outcomes?",
                },
                example:
                  "Problem: Slow dashboard loading → Solution: Code splitting + memoization → Result: 45% faster load time",
                recruiterInsight:
                  "Clear problem framing shows senior-level thinking",
              },

              techStackExplanation: {
                title: "Explaining the Tech Stack",
                explanation:
                  "Recruiters want to know *why* you chose a technology, not just *what* you used.",
                bestPractices: [
                  "Explain trade-offs",
                  "Mention alternatives you considered",
                  "Tie tech choice to problem",
                ],
                exampleExplanation:
                  "Used React + Vite for faster dev experience and smaller bundles compared to CRA",
                interviewInsight:
                  "This is where most candidates fail by giving generic answers",
              },

              challengesAndTradeoffs: {
                title: "Challenges & Trade-offs",
                explanation:
                  "Every real project has constraints. Acknowledging them increases credibility.",
                commonChallenges: [
                  "Performance bottlenecks",
                  "State management complexity",
                  "API limitations",
                  "Time constraints",
                ],
                tradeoffExample:
                  "Chose Context over Redux to reduce complexity at this scale",
                recruiterSignal: "Honest trade-offs show real-world experience",
              },

              screenshotsAndDemos: {
                title: "Screenshots & Live Demos",
                explanation:
                  "Visual proof builds instant trust and saves recruiter time.",
                bestPractices: [
                  "High-quality screenshots",
                  "Mobile + desktop views",
                  "Working live demo links",
                ],
                demoTips: [
                  "Avoid broken demo links",
                  "Add loading states",
                  "Use real-looking data",
                ],
              },

              metricsAndImpact: {
                title: "Metrics & Impact",
                explanation:
                  "Numbers make your work measurable and impressive.",
                exampleMetrics: [
                  "Reduced load time by 40%",
                  "Improved Lighthouse score from 65 to 92",
                  "Handled 10k+ list items smoothly",
                ],
                bestPractice:
                  "Even learning-based metrics are acceptable if explained clearly",
              },

              commonMistakes: [
                {
                  mistake: "Only listing features",
                  issue: "No insight into thinking",
                  fix: "Explain why features exist",
                },
                {
                  mistake: "Using buzzwords",
                  issue: "Sounds shallow",
                  fix: "Explain decisions in simple language",
                },
                {
                  mistake: "No result or impact",
                  issue: "Feels incomplete",
                  fix: "Always add outcome or learning",
                },
              ],

              interviewQuestions: [
                "Walk me through one of your projects",
                "Why did you choose this tech stack?",
                "What challenges did you face?",
                "What would you improve if given more time?",
              ],
            },

            tasks: [
              "Write detailed case studies for 2–3 major projects",
              "Structure each project using Problem → Solution → Result",
              "Add screenshots and live demo links",
              "Clearly explain technical decisions",
              "Highlight performance, UX, or learning impact",
            ],

            examples: [
              "Why this tech was chosen",
              "How performance was optimized",
              "What trade-offs were made",
            ],

            useCases: [
              "Technical interviews",
              "Resume shortlisting",
              "Senior-level perception",
              "Strong portfolio storytelling",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Learn to explain projects like a professional engineer",
              "Improve chances of resume shortlisting",
              "Prepare strong interview project explanations",
              "Show problem-solving and decision-making skills",
              "Differentiate yourself from tutorial-based portfolios",
            ],
          },
          {
            day: 4,
            topic: "GitHub Profile Optimization",

            subTopics: [
              "GitHub profile README",
              "Pinned repositories strategy",
              "Clean commit history",
              "README best practices",
              "Project documentation",
            ],

            explanation:
              "Your GitHub profile is your strongest technical proof. Recruiters and senior engineers judge your seriousness, consistency, and engineering maturity directly from GitHub.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Recruiter-Grade GitHub Profile README",
                description:
                  "GitHub profile README ko professional landing page jaisa banaao.",
                tasks: [
                  "Short professional intro likho (role + focus)",
                  "Primary tech stack clearly list karo",
                  "Currently building / learning section add karo",
                  "Portfolio, LinkedIn, GitHub highlights ke links add karo",
                ],
                outcome:
                  "Recruiter ko 30 seconds me clear ho jaye: tum kaun ho aur kya build kar sakte ho.",
              },
              {
                id: "pt-2",
                title: "Pinned Repositories Optimization",
                description:
                  "Pinned repos ko strategically select karke strongest signal bhejo.",
                tasks: [
                  "Top 4–6 strongest projects shortlist karo",
                  "Incomplete / tutorial repos unpin karo",
                  "At least 1 production-style app pin karo",
                  "Har pinned repo me clean README ensure karo",
                ],
                outcome:
                  "Recruiter bina scroll kiye tumhara best work dekh lega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Consistency in contributions",
                "Professional commit habits",
                "Project ownership",
                "Documentation clarity",
              ],
              strongAnswer:
                "Main apne GitHub ko portfolio ke extension ki tarah treat karta hoon. Har major project ke saath clear README, clean commits aur live demo rakhta hoon taaki reviewer ko context mil sake.",
              redFlags: [
                "Empty or generic profile",
                "No README or setup steps",
                "Random tutorial repositories pinned",
              ],
            },

            testingInsights: {
              focusAreas: [
                "30-second GitHub scan test",
                "Pinned repos relevance check",
                "README completeness",
              ],
              validationChecks: [
                "Har pinned repo run karne layak hai",
                "README se project ka purpose samajh aa jata hai",
                "Commit messages meaningful aur readable hain",
              ],
              realWorldNote:
                "Senior engineers GitHub ko resume se zyada seriously lete hain.",
            },

            editorGuidance: {
              careerMindset: [
                "GitHub tumhara engineering proof hai, gallery nahi",
                "Fewer strong repos > many weak repos",
                "Documentation bhi engineering skill hai",
              ],
              seniorAdvice:
                "Agar tumhara GitHub ek unfamiliar engineer bina pooche samajh sakta hai — tum industry-ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real hiring scenarios, GitHub is often checked before or alongside your resume. A clean, well-structured GitHub profile builds instant trust and differentiates you from tutorial-only developers.",

              profileReadme: {
                title: "GitHub Profile README",
                explanation:
                  "A profile README acts like a landing page for your developer identity.",
                mustInclude: [
                  "Short professional introduction",
                  "Primary tech stack",
                  "What you are currently building or learning",
                  "Links to portfolio, LinkedIn, Twitter",
                ],
                exampleSections: [
                  "About Me",
                  "Tech Stack",
                  "Featured Projects",
                  "Contact / Links",
                ],
                recruiterInsight:
                  "A good README answers: Who is this developer and what can they build?",
              },

              pinnedRepositories: {
                title: "Pinned Repositories Strategy",
                explanation:
                  "Pinned repositories guide recruiters to your best work without effort.",
                bestPractices: [
                  "Pin 4–6 strongest projects only",
                  "Prefer complete projects over small demos",
                  "Mix of frontend, performance, and real-world apps",
                ],
                goodPinExamples: [
                  "Production-style React app",
                  "Next.js or performance-optimized project",
                  "Well-documented mini-project",
                ],
                mistakeToAvoid:
                  "Pinning tutorial or half-complete repositories",
              },

              commitHistory: {
                title: "Clean Commit History",
                explanation:
                  "Commit history reflects how you work in real teams.",
                bestPractices: [
                  "Meaningful commit messages",
                  "Small, focused commits",
                  "Avoid committing broken code",
                ],
                goodCommitExamples: [
                  "feat: add infinite scroll to feed",
                  "fix: prevent unnecessary re-renders",
                  "refactor: split dashboard components",
                ],
                recruiterSignal:
                  "Clean commits indicate professional workflow awareness",
              },

              readmeBestPractices: {
                title: "Project README Best Practices",
                explanation:
                  "A project without a README is considered incomplete in professional environments.",
                mustHaveSections: [
                  "Project overview",
                  "Features",
                  "Tech stack",
                  "Setup instructions",
                  "Live demo link",
                ],
                exampleStructure: `
# Project Name
## Overview
## Features
## Tech Stack
## Setup
## Live Demo
      `,
                interviewInsight:
                  "Clear READMEs show communication skills — not just coding",
              },

              documentationQuality: {
                title: "Project Documentation Quality",
                explanation:
                  "Good documentation makes your project usable by others.",
                includes: [
                  "Environment setup",
                  "API usage (if any)",
                  "Folder structure overview",
                  "Key design decisions",
                ],
                realWorldNote:
                  "In companies, bad documentation slows teams more than bad code",
              },

              commonMistakes: [
                {
                  mistake: "Empty GitHub profile",
                  issue: "No credibility",
                  fix: "Add profile README and pinned repos",
                },
                {
                  mistake: "Too many weak projects",
                  issue: "Dilutes impression",
                  fix: "Show fewer but stronger projects",
                },
                {
                  mistake: "No setup instructions",
                  issue: "Recruiter can’t run project",
                  fix: "Add clear README steps",
                },
              ],

              interviewQuestions: [
                "Which project on GitHub are you most proud of?",
                "How do you structure commits in real projects?",
                "How do you document your work?",
                "What does your GitHub say about you as a developer?",
              ],
            },

            tasks: [
              "Create or improve GitHub profile README",
              "Audit all major repositories",
              "Add professional README to each project",
              "Pin top 4–6 strongest repositories",
              "Clean up commit messages where possible",
            ],

            examples: [
              "Tech stack badges",
              "Clear project setup instructions",
              "Well-documented repositories",
            ],

            useCases: [
              "Open source contributions",
              "Technical screening",
              "Developer credibility",
              "Resume shortlisting",
            ],

            editorType: null,

            estimatedTime: "1.5–2 hours",

            objectives: [
              "Make GitHub profile recruiter-ready",
              "Demonstrate professional engineering habits",
              "Improve technical credibility",
              "Increase chances of interview shortlisting",
              "Stand out from tutorial-based profiles",
            ],
          },
          {
            day: 5,
            topic: "Resume for Frontend Developers",

            subTopics: [
              "One-page resume structure",
              "Project-focused resumes",
              "ATS-friendly keywords",
              "Quantifying impact",
              "Common resume mistakes",
            ],

            explanation:
              "A strong frontend resume does not list technologies — it demonstrates impact through projects, problem-solving, and real-world outcomes.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Project-First Resume Rewrite",
                description:
                  "Resume ko tech-list se impact-based frontend resume me convert karo.",
                tasks: [
                  "Top 3 projects shortlist karo",
                  "Har project ke liye Problem → Action → Impact bullets likho",
                  "Generic tech bullets hatao (React, HTML, CSS list)",
                  "Each project me performance / UX improvement add karo",
                ],
                outcome:
                  "Resume recruiter ko clearly dikhata hai ki tum kya build kar sakte ho, sirf kya tools jaante ho nahi.",
              },
              {
                id: "pt-2",
                title: "ATS Optimization Drill",
                description:
                  "Resume ko ATS-friendly banakar rejection risk kam karo.",
                tasks: [
                  "Job description se relevant keywords extract karo",
                  "Skills & projects me natural keyword mapping karo",
                  "Tables, icons, images remove karo",
                  "Standard section headings verify karo",
                ],
                outcome:
                  "Resume ATS pass karega aur recruiter tak pahunchne ke chances badhenge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Impact-oriented project explanations",
                "Performance & UX awareness",
                "Tech choice justification",
                "Clear ownership of work",
              ],
              strongAnswer:
                "Main apne resume me sirf tools list nahi karta. Har project ke saath main problem, solution aur measurable impact explain karta hoon, jaise performance improvement ya UX gains.",
              redFlags: [
                "Sirf technologies ki list",
                "No numbers or outcomes",
                "Generic project descriptions",
              ],
            },

            testingInsights: {
              focusAreas: [
                "6–10 second resume scan test",
                "ATS keyword match",
                "Project clarity",
              ],
              validationChecks: [
                "Har project 2–3 impact-driven bullets me explain ho raha hai",
                "Numbers / metrics clearly visible hain",
                "Resume ek page me cleanly fit ho raha hai",
              ],
              realWorldNote:
                "Agar resume fast scan me clear nahi laga, recruiter next resume pe chala jata hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Resume ek marketing document hai, biography nahi",
                "Impact > responsibilities",
                "Projects experience se zyada powerful ho sakte hain",
              ],
              seniorAdvice:
                "Agar tum apne resume ke har bullet ko confidently explain kar sakte ho — tum interview ke liye ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Recruiters spend 6–10 seconds scanning a resume. Your resume must quickly communicate what you can build, how you think, and the value you bring as a frontend developer.",

              resumeStructure: {
                title: "One-Page Resume Structure",
                explanation:
                  "Modern frontend resumes should be concise, scannable, and focused on skills demonstrated through projects.",
                idealSections: [
                  "Header (Name, Role, Links)",
                  "Summary (2–3 lines max)",
                  "Skills (Frontend-focused)",
                  "Projects (MOST IMPORTANT)",
                  "Experience (if any)",
                  "Education",
                ],
                bestPractice:
                  "One page is enough for freshers and developers with <5 years experience",
              },

              projectFocusedResume: {
                title: "Project-Focused Resume",
                explanation:
                  "Projects are the strongest proof of your frontend skills.",
                whatToHighlight: [
                  "What problem the project solves",
                  "Your role and contribution",
                  "Tech stack used",
                  "Impact or outcome",
                ],
                exampleBulletPoints: [
                  "Built a React dashboard handling 10k+ records with optimized rendering",
                  "Implemented lazy loading and reduced initial load time by 30%",
                  "Designed reusable component system used across 5+ pages",
                ],
                recruiterInsight:
                  "Strong projects can compensate for lack of experience",
              },

              atsOptimization: {
                title: "ATS-Friendly Keywords",
                explanation:
                  "Most companies use Applicant Tracking Systems (ATS) to filter resumes before humans see them.",
                importantKeywords: [
                  "React, JavaScript, TypeScript",
                  "HTML, CSS, Responsive Design",
                  "Performance Optimization",
                  "REST APIs, Git, Testing",
                ],
                bestPractices: [
                  "Use standard section headings",
                  "Avoid images and tables",
                  "Match keywords from job description",
                ],
                realWorldNote:
                  "If ATS rejects your resume, no recruiter will ever see it",
              },

              quantifyingImpact: {
                title: "Quantifying Impact",
                explanation:
                  "Numbers make your resume believable and impressive.",
                examples: [
                  "Improved page load time by 30%",
                  "Reduced bundle size by 40%",
                  "Built UI used by 500+ users",
                  "Optimized rendering for lists with 10k+ items",
                ],
                bestPractice: "Even personal projects can be quantified",
              },

              commonMistakes: {
                title: "Common Resume Mistakes",
                mistakes: [
                  {
                    mistake: "Listing only technologies",
                    issue: "No proof of skill",
                    fix: "Show how you used the tech",
                  },
                  {
                    mistake: "Long paragraphs",
                    issue: "Hard to scan",
                    fix: "Use concise bullet points",
                  },
                  {
                    mistake: "Irrelevant content",
                    issue: "Dilutes focus",
                    fix: "Frontend-only highlights",
                  },
                  {
                    mistake: "Spelling / formatting errors",
                    issue: "Unprofessional impression",
                    fix: "Proofread and use clean layout",
                  },
                ],
              },

              interviewQuestions: [
                "Explain one project from your resume",
                "How did you improve performance in this project?",
                "Why did you choose this tech stack?",
                "What challenges did you face?",
              ],
            },

            tasks: [
              "Create or refine a one-page frontend resume",
              "Rewrite project descriptions with impact",
              "Add measurable results to projects",
              "Optimize resume using job descriptions",
              "Remove irrelevant or weak content",
            ],

            examples: [
              "Improved load time by 30%",
              "Built reusable component library",
              "Optimized React rendering performance",
            ],

            useCases: [
              "Job applications",
              "Campus placements",
              "Startup hiring",
              "Recruiter screening",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Create a recruiter-ready frontend resume",
              "Highlight skills through real projects",
              "Pass ATS screening confidently",
              "Increase interview shortlisting chances",
              "Communicate frontend impact clearly",
            ],
          },
          {
            day: 6,
            topic: "LinkedIn & Personal Branding",

            subTopics: [
              "LinkedIn headline optimization",
              "About section storytelling",
              "Posting technical content",
              "Networking strategies",
              "Personal brand statement",
            ],

            explanation:
              "LinkedIn is no longer just a job portal — it is a discovery engine where recruiters, founders, and developers evaluate you before ever talking to you.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Recruiter-Search Optimized Headline",
                description:
                  "LinkedIn headline ko recruiter keyword search ke hisaab se optimize karo.",
                tasks: [
                  "Target roles identify karo (Frontend / React / Next.js)",
                  "Job descriptions se primary keywords extract karo",
                  "Headline me role + stack + value add karo",
                  "Generic terms (Learner, Student) remove karo",
                ],
                outcome:
                  "Profile recruiter search results me appear karega aur click-through rate improve hoga.",
              },
              {
                id: "pt-2",
                title: "30-Day Content Visibility Sprint",
                description:
                  "Consistency ke saath technical content post karke personal brand build karo.",
                tasks: [
                  "Week me 2 technical posts plan karo",
                  "1 project breakdown post likho",
                  "1 learning / mistake-based post likho",
                  "Comments me active engagement track karo",
                ],
                outcome:
                  "Profile recall build hoga aur inbound messages aane lagenge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear personal brand positioning",
                "Public learning mindset",
                "Strong communication skills",
                "Community engagement",
              ],
              strongAnswer:
                "Main LinkedIn ko sirf job portal nahi maanta. Main apni learning, projects aur decisions share karta hoon taaki log mujhe meri thinking aur work ke through yaad rakhein.",
              redFlags: [
                "Inactive profile",
                "Sirf certificates ya achievements post karna",
                "Generic About section",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Headline keyword match",
                "About section clarity",
                "Content consistency",
              ],
              validationChecks: [
                "Headline 220 characters ke andar clear value dikhata hai",
                "About section me clear direction aur CTA hai",
                "Last 14 days me at least 2 posts published hain",
              ],
              realWorldNote:
                "Recruiter profile ko 10–15 seconds me judge karta hai — clarity wins.",
            },

            editorGuidance: {
              careerMindset: [
                "LinkedIn ek long-term asset hai, instant result nahi",
                "Visibility silence se better hai",
                "Consistency > perfection",
              ],
              seniorAdvice:
                "Agar tum apni journey explain kar sakte ho aur log tumhe repeat me dekh rahe hain — personal branding kaam kar rahi hai.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Most frontend jobs today are discovered through LinkedIn — not job portals. A strong LinkedIn profile works 24/7 for you by showcasing your skills, mindset, and growth journey.",

              headlineOptimization: {
                title: "LinkedIn Headline Optimization",
                explanation:
                  "Your headline is the most important part of your LinkedIn profile. It decides whether someone clicks your profile or scrolls past.",
                structure: [
                  "Your role or direction",
                  "Core tech stack",
                  "What you are building or learning",
                  "Outcome or value",
                ],
                exampleHeadlines: [
                  "Frontend Developer | React • TypeScript | Building Scalable UI",
                  "Aspiring Frontend Engineer | React • Next.js | Building in Public",
                  "React Developer | Performance-Focused UI | Portfolio Projects",
                ],
                bestPractice:
                  "Avoid vague titles like 'Student' or 'Learner' — be specific",
                recruiterInsight:
                  "Recruiters search by keywords, not motivation",
              },

              aboutSection: {
                title: "About Section Storytelling",
                explanation:
                  "The About section should tell a short story of who you are, what you build, and where you are going.",
                recommendedFlow: [
                  "Current focus (1–2 lines)",
                  "What you are building or learning",
                  "Core skills & strengths",
                  "Call to action",
                ],
                exampleSnippet: `
Frontend developer focused on building scalable, performance-optimized web applications.

Currently working with React, JavaScript, and modern frontend tools while building real-world projects and sharing my learning journey.

Interested in frontend roles, internships, and collaboration opportunities.
      `,
                bestPractice: "Write like a human, not like a resume",
              },

              contentPosting: {
                title: "Posting Technical Content",
                explanation:
                  "Posting consistently builds visibility, trust, and recall among recruiters and developers.",
                contentIdeas: [
                  "Project breakdowns",
                  "What you learned this week",
                  "Before vs after optimization",
                  "Mistakes you made and fixed",
                ],
                formatsThatWork: [
                  "Short text posts",
                  "Carousel slides",
                  "Code screenshots",
                ],
                algorithmTip: "Consistency beats virality",
                realWorldNote:
                  "Many developers got jobs without applying — only by posting",
              },

              networkingStrategies: {
                title: "Networking Strategies",
                explanation:
                  "Networking is about genuine connections, not random connection requests.",
                bestApproach: [
                  "Connect with developers in your tech stack",
                  "Engage with posts (comments > likes)",
                  "Send short, respectful messages",
                ],
                exampleMessage: `
Hi, I liked your post on frontend performance.
I'm currently building React projects and learning similar things.
Would love to stay connected.
      `,
                warning: "Never ask for a job in the first message",
              },

              personalBranding: {
                title: "Personal Brand Statement",
                explanation:
                  "Your personal brand is what people remember you for.",
                howToDefine: [
                  "What you build",
                  "How you think",
                  "What makes you different",
                ],
                exampleStatements: [
                  "I build clean, performance-focused React UIs",
                  "Frontend developer documenting real learning",
                  "Developer who explains complex things simply",
                ],
                bestPractice:
                  "Your brand should be consistent across posts, bio, and projects",
              },

              commonMistakes: [
                {
                  mistake: "Copy-paste generic bios",
                  issue: "No differentiation",
                  fix: "Write in your own words",
                },
                {
                  mistake: "Posting only certificates",
                  issue: "Low signal value",
                  fix: "Post learning + application",
                },
                {
                  mistake: "Being inactive for months",
                  issue: "Low visibility",
                  fix: "1–2 posts per week is enough",
                },
              ],

              interviewQuestions: [
                "How do you build your personal brand?",
                "Do you share your learning publicly?",
                "How do you stay visible in the developer community?",
              ],
            },

            tasks: [
              "Optimize LinkedIn headline with correct keywords",
              "Rewrite About section using storytelling",
              "Create and publish 2–3 technical posts",
              "Engage with developer content daily",
              "Define your personal brand statement",
            ],

            examples: [
              "Building in public post",
              "Project launch announcement",
              "Weekly learning summary",
            ],

            useCases: [
              "Inbound job opportunities",
              "Community growth",
              "Professional visibility",
              "Recruiter discovery",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Build a recruiter-attractive LinkedIn profile",
              "Increase visibility without cold applying",
              "Communicate frontend skills publicly",
              "Develop long-term personal brand",
              "Improve chances of inbound job calls",
            ],
          },
          {
            day: 7,
            topic: "Week 31 Project – Professional Portfolio",

            subTopics: [
              "End-to-end portfolio build",
              "SEO and performance optimization",
              "Deployment and custom domain",
              "Final polish and review",
              "Feedback iteration",
            ],

            explanation:
              "This portfolio becomes your primary asset for job applications, interviews, and personal branding. It represents your skills, thinking, and growth as a frontend developer.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Portfolio Build",
                description:
                  "End-to-end professional portfolio build karo jo real hiring standards meet kare.",
                tasks: [
                  "React ya Next.js choose karke portfolio setup karo",
                  "Clear navigation aur section flow implement karo",
                  "3–5 strongest projects shortlist karo",
                  "Each project ke liye case-study format follow karo",
                ],
                outcome:
                  "Portfolio ek serious, job-ready frontend engineer ka signal dega.",
              },
              {
                id: "pt-2",
                title: "SEO + Performance Benchmarking",
                description:
                  "Portfolio ko performance aur SEO ke perspective se optimize karo.",
                tasks: [
                  "Meta tags aur Open Graph add karo",
                  "Images aur fonts optimize karo",
                  "Code splitting aur lazy loading apply karo",
                  "Lighthouse score measure karke iterate karo",
                ],
                outcome:
                  "Portfolio khud ek performance showcase ban jayega (90+ scores).",
              },
              {
                id: "pt-3",
                title: "Deployment, Domain & Final Polish",
                description:
                  "Portfolio ko production-quality deployment ke saath finalize karo.",
                tasks: [
                  "Vercel / Netlify pe deploy karo",
                  "Custom domain connect karo (optional but recommended)",
                  "Broken links aur console errors check karo",
                  "Mobile + desktop UX review karo",
                ],
                outcome:
                  "Live, reliable aur professional-looking portfolio ready hoga.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "End-to-end ownership of a product",
                "Clear problem-solving & storytelling",
                "Performance and SEO awareness",
                "Attention to detail",
              ],
              strongAnswer:
                "Mera portfolio sirf projects ka collection nahi hai. Ye dikhata hai ki main real-world problems ko kaise approach karta hoon, decisions kaise leta hoon aur production quality ka dhyan rakhta hoon.",
              redFlags: [
                "No live deployment",
                "Weak or unexplained projects",
                "Poor performance scores",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Navigation flow clarity",
                "Performance metrics (Lighthouse)",
                "SEO metadata correctness",
                "Cross-device usability",
              ],
              validationChecks: [
                "Lighthouse Performance & SEO 90+",
                "All project links working",
                "No layout break on mobile",
                "Page load feels fast on slow network",
              ],
              realWorldNote:
                "Portfolio hi often first technical test hota hai — bina interview ke.",
            },

            editorGuidance: {
              careerMindset: [
                "Portfolio ek product hai, assignment nahi",
                "Quality > quantity",
                "Iteration is part of professionalism",
              ],
              seniorAdvice:
                "Agar tum apna portfolio confidently walk-through kar sakte ho, to tum interview ke liye already strong position me ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real hiring scenarios, your portfolio is often reviewed before your resume. A professional portfolio answers three questions clearly: What can you build? How do you think? Can you work at production quality?",

              projectGoal: {
                title: "Project Goal",
                explanation:
                  "Build a production-grade frontend portfolio that showcases your projects, skills, and problem-solving approach with strong performance, SEO, and UX.",
                realWorldNote:
                  "Many candidates get rejected not due to lack of skills, but due to weak portfolios",
              },

              portfolioStructure: {
                title: "Portfolio Structure",
                explanation:
                  "A clear and simple structure helps recruiters find information quickly.",
                recommendedSections: [
                  "Hero / Introduction",
                  "About Me",
                  "Projects (Case Studies)",
                  "Skills & Tech Stack",
                  "Blogs or Learnings (optional)",
                  "Contact / Social Links",
                ],
                bestPractice:
                  "Keep navigation simple — recruiters spend very little time initially",
              },

              caseStudies: {
                title: "Project Case Studies",
                explanation:
                  "Case studies show how you think, not just what you built.",
                requiredElements: [
                  "Problem statement",
                  "Your solution",
                  "Tech stack used",
                  "Challenges & trade-offs",
                  "Outcome or learning",
                ],
                exampleStructure: `
Problem: Slow dashboard rendering  
Solution: Optimized React renders using memoization  
Result: Reduced render time by ~35%
      `,
                interviewInsight:
                  "Case studies are frequently discussed in interviews",
              },

              seoAndPerformance: {
                title: "SEO & Performance Optimization",
                explanation:
                  "Your portfolio should score well on performance and SEO to reflect production-level thinking.",
                conceptsUsed: [
                  "Meta tags & Open Graph",
                  "Lighthouse optimization",
                  "Image optimization",
                  "Code splitting",
                ],
                targets: [
                  "Lighthouse Performance 90+",
                  "SEO score 90+",
                  "Mobile-friendly layout",
                ],
                bestPractice: "Your portfolio itself is a performance demo",
              },

              deployment: {
                title: "Deployment & Custom Domain",
                explanation:
                  "A live, reliable deployment is mandatory for professional portfolios.",
                options: [
                  "Vercel",
                  "Netlify",
                  "Custom domain (optional but recommended)",
                ],
                checklist: [
                  "HTTPS enabled",
                  "No console errors",
                  "Working links",
                  "Fast load time",
                ],
                realWorldNote:
                  "Broken links or slow sites create negative first impressions",
              },

              feedbackIteration: {
                title: "Feedback & Iteration",
                explanation: "Good developers iterate based on feedback.",
                sourcesOfFeedback: [
                  "Senior developers",
                  "Peer developers",
                  "Recruiters",
                  "Online communities",
                ],
                iterationExamples: [
                  "Improving project descriptions",
                  "Simplifying UI",
                  "Clarifying tech stack usage",
                ],
                mindset: "Treat portfolio as a living product",
              },

              commonMistakes: [
                {
                  mistake: "Listing too many weak projects",
                  issue: "Dilutes impact",
                  fix: "Show 3–5 strong projects only",
                },
                {
                  mistake: "No explanation of projects",
                  issue: "Hard to judge skills",
                  fix: "Add short case studies",
                },
                {
                  mistake: "Ignoring performance",
                  issue: "Looks amateur",
                  fix: "Optimize and show scores",
                },
              ],

              interviewQuestions: [
                "Walk me through your portfolio",
                "Which project are you most proud of?",
                "What challenges did you face while building this?",
                "How did you optimize performance?",
                "What would you improve next?",
              ],
            },

            tasks: [
              "Build complete portfolio using React or Next.js",
              "Add detailed case studies for major projects",
              "Optimize SEO and performance (90+ Lighthouse)",
              "Deploy portfolio with clean URL",
              "Collect and apply feedback",
            ],

            examples: [
              "Next.js portfolio with case studies",
              "React portfolio with performance optimization",
            ],

            useCases: [
              "Final job applications",
              "Interview walkthroughs",
              "Personal brand showcase",
              "Recruiter evaluation",
            ],

            editorType: "react",

            estimatedTime: "4–6 hours",

            objectives: [
              "Build a production-quality frontend portfolio",
              "Demonstrate real-world frontend thinking",
              "Increase shortlisting chances",
              "Create confidence for interviews",
              "Establish long-term personal brand asset",
            ],
          },
        ],
      },
      {
        week: 32,
        weekGoal:
          "Crack frontend technical interviews with confidence and clarity",
        weekOutcome:
          "Strong problem-solving skills, clear explanation ability, and interview-ready mindset",
        miniProject: "Interview Prep Repository",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "JavaScript Interview Fundamentals",

            subTopics: [
              "Closures and lexical scope",
              "Hoisting and execution context",
              "this keyword behavior",
              "Event loop and async JS",
              "Promises vs async/await",
            ],

            explanation:
              "Most frontend interviews deeply test JavaScript fundamentals and how the language actually works under the hood. Strong JS fundamentals often matter more than frameworks.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Output Prediction Drill (Interview Style)",
                description:
                  "Interview-level tricky JavaScript snippets ka output bina run kiye predict karo.",
                tasks: [
                  "Closures + loop + setTimeout snippets solve karo",
                  "Event loop (microtask vs macrotask) output likho",
                  "`this` based call-site questions analyze karo",
                  "Hoisting related undefined vs error cases explain karo",
                ],
                outcome:
                  "Tum confidently interview me code ka output reason ke saath bata paoge.",
              },
              {
                id: "pt-2",
                title: "Concept → Explanation → Example Mapping",
                description:
                  "Har JS concept ko simple language + real example me explain karna practice karo.",
                tasks: [
                  "Closure ko private variable ke example se samjhao",
                  "Execution context ko step-by-step breakdown karo",
                  "`this` ko call-site based rule se explain karo",
                  "Promise vs async/await ko same logic me compare karo",
                ],
                outcome:
                  "Ratta nahi, real understanding develop hogi — interviewer ko clarity dikhegi.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Output reasoning instead of guessing",
                "Clear async mental model",
                "`this` ko call-site se explain kar pana",
                "Simple language me complex concept explain karna",
              ],
              strongAnswer:
                "JavaScript me main definitions yaad nahi karta, main execution flow samajhta hoon — isliye bina run kiye code ka output predict kar sakta hoon.",
              redFlags: [
                "Sirf definitions bolna",
                "Event loop ko vague explain karna",
                "`this` ko lexical samajhna",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Event loop ordering (sync → microtask → macrotask)",
                "Closure memory behavior",
                "Hoisting edge cases",
                "`this` in arrow vs normal functions",
              ],
              validationChecks: [
                "Async output bina hesitation explain kar pa rahe ho?",
                "Closure example me memory retain hona clearly samjha pa rahe ho?",
                "`this` ka value call ke basis pe justify kar pa rahe ho?",
              ],
              realWorldNote:
                "JS fundamentals strong ho to frameworks change hone par bhi tum relevant rehte ho.",
            },

            editorGuidance: {
              careerMindset: [
                "Senior dev output guess nahi karta, reason deta hai",
                "JavaScript samajhna career insurance hai",
                "Frameworks aate-jaate rahenge, JS rehne wala hai",
              ],
              seniorAdvice:
                "Agar tum event loop aur closures confidently explain kar sakte ho, to tum mid-to-senior interviews ke liye ready ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real interviews, JavaScript is not tested by definitions but by behavior. Interviewers want to know whether you truly understand how JavaScript executes code, manages memory, and handles async operations.",

              interviewExpectations: {
                title: "What Interviewers Expect",
                explanation:
                  "Interviewers focus on clarity of thought, mental models, and your ability to reason about code execution.",
                keySignals: [
                  "Can explain concepts in simple words",
                  "Can predict output of tricky code",
                  "Can debug without running code",
                  "Understands browser + JS interaction",
                ],
                realWorldNote:
                  "Strong JS fundamentals often compensate for weaker framework knowledge",
              },

              closures: {
                title: "Closures & Lexical Scope",
                explanation:
                  "A closure is created when a function remembers variables from its lexical scope even after the outer function has finished execution.",
                conceptsUsed: [
                  "Lexical environment",
                  "Scope chain",
                  "Memory retention",
                ],
                exampleLogic: `
function counter() {
  let count = 0;
  return function () {
    return ++count;
  };
}

const inc = counter();
inc(); // 1
inc(); // 2
      `,
                interviewInsight:
                  "Closures are commonly tested via counters, private variables, and setTimeout loops",
              },

              executionContext: {
                title: "Hoisting & Execution Context",
                explanation:
                  "JavaScript executes code in two phases: memory creation and execution. Hoisting happens during memory creation.",
                conceptsUsed: [
                  "Global execution context",
                  "Function execution context",
                  "Variable environment",
                ],
                exampleLogic: `
console.log(a); // undefined
var a = 10;
      `,
                commonTrap: "Confusing hoisting with initialization",
                bestPractice: "Prefer let/const to avoid hoisting confusion",
              },

              thisKeyword: {
                title: "`this` Keyword Behavior",
                explanation:
                  "`this` depends on how a function is called, not where it is written.",
                contexts: [
                  "Global context",
                  "Object method",
                  "Arrow function",
                  "call / apply / bind",
                ],
                exampleLogic: `
const obj = {
  name: 'JS',
  show() {
    console.log(this.name);
  }
};
obj.show(); // JS
      `,
                interviewInsight:
                  "`this` questions often differentiate mid-level and senior developers",
              },

              eventLoop: {
                title: "Event Loop & Async JavaScript",
                explanation:
                  "The event loop enables non-blocking behavior by coordinating the call stack, Web APIs, microtasks, and macrotasks.",
                conceptsUsed: [
                  "Call stack",
                  "Web APIs",
                  "Microtask queue",
                  "Callback queue",
                ],
                exampleLogic: `
console.log('start');
setTimeout(() => console.log('timeout'), 0);
Promise.resolve().then(() => console.log('promise'));
console.log('end');
      `,
                expectedOutput: "start → end → promise → timeout",
                interviewInsight:
                  "Event loop questions are very common in senior interviews",
              },

              asyncPatterns: {
                title: "Promises vs Async/Await",
                explanation:
                  "Async/await is syntactic sugar over promises, but understanding promises is mandatory.",
                comparison: {
                  promises: "Chain-based, harder to read when complex",
                  asyncAwait: "Readable, looks synchronous",
                },
                exampleLogic: `
async function fetchData() {
  try {
    const res = await fetch(url);
    return await res.json();
  } catch (e) {
    console.error(e);
  }
}
      `,
                bestPractice: "Always handle errors explicitly in async code",
              },

              commonMistakes: [
                {
                  mistake: "Memorizing definitions",
                  issue: "Fails real interview questions",
                  fix: "Focus on execution and output",
                },
                {
                  mistake: "Weak async mental model",
                  issue: "Bugs in production",
                  fix: "Understand event loop deeply",
                },
                {
                  mistake: "Confusion around `this`",
                  issue: "Unexpected behavior",
                  fix: "Practice call-site based reasoning",
                },
              ],

              interviewQuestions: [
                "What is a closure? Give a real example.",
                "Explain hoisting with code.",
                "How does `this` behave in arrow functions?",
                "Explain the event loop with an example.",
                "Difference between Promise and async/await?",
              ],
            },

            tasks: [
              "Revise core JavaScript fundamentals",
              "Explain each concept verbally as if in interview",
              "Write closure, hoisting, and event loop examples",
              "Predict output of async JavaScript snippets",
              "Identify and fix common JS pitfalls",
            ],

            examples: [
              "Closure-based counter",
              "Event loop output questions",
              "Promise chaining vs async/await refactor",
            ],

            useCases: [
              "Frontend technical interviews",
              "Debugging complex bugs",
              "Senior-level JavaScript discussions",
              "Framework-independent problem solving",
            ],

            editorType: "javascript",

            estimatedTime: "2–3 hours",

            objectives: [
              "Build strong JavaScript mental models",
              "Explain JS concepts confidently in interviews",
              "Predict code execution without running it",
              "Avoid common async and scope-related bugs",
              "Prepare for mid to senior frontend interviews",
            ],
          },
          {
            day: 2,
            topic: "React Interview Concepts",

            subTopics: [
              "State vs props",
              "useState vs useReducer",
              "useEffect lifecycle behavior",
              "Keys and reconciliation",
              "Performance optimization in React",
            ],

            explanation:
              "Interviewers test how well you understand React internals, data flow, and rendering behavior — not just how fast you can write JSX.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Re-render Reasoning Drill",
                description:
                  "React components kab aur kyun re-render hote hain — isko interview-level clarity ke saath analyze karo.",
                tasks: [
                  "State update se re-render flow explain karo",
                  "Parent re-render → child re-render scenarios analyze karo",
                  "React.memo ke baad re-render cases identify karo",
                  "Props reference change vs value change compare karo",
                ],
                outcome:
                  "Tum confidently bata paoge ki React re-render kyun hua — guess nahi, reason ke saath.",
              },
              {
                id: "pt-2",
                title: "Hooks Decision-Making Practice",
                description:
                  "useState, useReducer, useEffect ka use decision ke basis pe justify karna practice karo.",
                tasks: [
                  "Simple vs complex state ke liye hook choice explain karo",
                  "useEffect dependency bugs intentionally create & fix karo",
                  "Derived state vs effect-based sync compare karo",
                  "Real scenario me useReducer justify karo",
                ],
                outcome:
                  "Tum sirf hooks use nahi karoge, unka choice defend bhi kar paoge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Re-render triggers ka clear mental model",
                "useEffect lifecycle ko confusion ke bina explain karna",
                "useState vs useReducer ka practical reasoning",
                "Performance issues ko root cause se identify karna",
              ],
              strongAnswer:
                "React slow nahi hota, hum unnecessary re-renders create kar dete hain. Main pehle render behavior samajhta hoon, phir optimize karta hoon.",
              redFlags: [
                "Har jagah useEffect use karna",
                "Blind React.memo / useCallback",
                "Keys ke liye index use karna bina reason",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Why component re-rendered?",
                "useEffect dependency correctness",
                "Keys impact on reconciliation",
                "Memoization effectiveness",
              ],
              validationChecks: [
                "Kya tum explain kar sakte ho useEffect kyun dobara run hua?",
                "Kya tum bata sakte ho kaunsa state local hona chahiye?",
                "Kya tum unnecessary re-render detect kar pa rahe ho?",
              ],
              realWorldNote:
                "Most React production bugs logic se kam, rendering misunderstanding se zyada aate hain.",
            },

            editorGuidance: {
              careerMindset: [
                "Senior dev React ko magic nahi maanta, predictable system maanta hai",
                "Performance optimize karne se pehle understanding zaroori hai",
                "Clean state flow > clever hacks",
              ],
              seniorAdvice:
                "Agar tum re-render, hooks lifecycle aur keys confidently explain kar sakte ho — tum React interviews ke liye solid ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In React interviews, most candidates know syntax, but few understand why React behaves the way it does. Interviewers focus on mental models: rendering, re-renders, state flow, and performance.",

              interviewExpectations: {
                title: "What Interviewers Expect",
                explanation:
                  "Interviewers want to see clarity around component behavior, hooks lifecycle, and performance implications.",
                keySignals: [
                  "Understands re-render triggers",
                  "Can explain hooks behavior clearly",
                  "Avoids common React anti-patterns",
                  "Can reason about performance issues",
                ],
                realWorldNote:
                  "Many production bugs come from misunderstanding React re-renders",
              },

              stateVsProps: {
                title: "State vs Props",
                explanation:
                  "Props are inputs passed from parent to child, while state is internal, mutable data owned by a component.",
                conceptsUsed: [
                  "Unidirectional data flow",
                  "Component ownership",
                  "Immutability",
                ],
                exampleLogic: `
function Child({ count }) {
  return <p>{count}</p>;
}

function Parent() {
  const [count, setCount] = useState(0);
  return <Child count={count} />;
}
      `,
                interviewInsight:
                  "A common interview follow-up: when should state be lifted up?",
              },

              useStateVsUseReducer: {
                title: "useState vs useReducer",
                explanation:
                  "useState is ideal for simple state, while useReducer is better for complex state transitions and predictable logic.",
                comparison: {
                  useState: "Simple, local state",
                  useReducer: "Complex state with multiple actions",
                },
                exampleLogic: `
const [state, dispatch] = useReducer(reducer, initialState);
dispatch({ type: 'INCREMENT' });
      `,
                realWorldNote:
                  "useReducer improves readability when state logic grows",
              },

              useEffectLifecycle: {
                title: "useEffect Lifecycle Behavior",
                explanation:
                  "useEffect runs after render. Its dependency array controls when it runs and when cleanup happens.",
                patterns: [
                  "No dependency array → runs on every render",
                  "Empty array → runs on mount",
                  "Dependencies → runs on change",
                ],
                exampleLogic: `
useEffect(() => {
  fetchData();
}, [userId]);
      `,
                commonTrap:
                  "Infinite loops due to missing or incorrect dependencies",
                interviewInsight:
                  "Interviewers often ask why an effect is running multiple times",
              },

              keysAndReconciliation: {
                title: "Keys & React Reconciliation",
                explanation:
                  "Keys help React identify which items changed, were added, or removed during reconciliation.",
                conceptsUsed: [
                  "Virtual DOM diffing",
                  "Element identity",
                  "Efficient re-rendering",
                ],
                exampleLogic: `
items.map(item => (
  <ListItem key={item.id} item={item} />
));
      `,
                commonMistake: "Using array index as key",
                interviewInsight:
                  "Wrong keys can cause subtle UI bugs, not just performance issues",
              },

              performanceOptimization: {
                title: "Performance Optimization in React",
                explanation:
                  "Performance issues usually come from unnecessary re-renders, not slow code.",
                techniques: [
                  "React.memo",
                  "useMemo",
                  "useCallback",
                  "Proper state colocation",
                ],
                exampleLogic: `
const MemoComp = React.memo(Component);
      `,
                bestPractice:
                  "Optimize only after identifying real performance issues",
              },

              commonMistakes: [
                {
                  mistake: "Overusing useEffect",
                  issue: "Unnecessary complexity",
                  fix: "Derive state instead of syncing",
                },
                {
                  mistake: "Blind memoization",
                  issue: "Harder code without benefit",
                  fix: "Measure before optimizing",
                },
                {
                  mistake: "Wrong dependency arrays",
                  issue: "Bugs and infinite loops",
                  fix: "Understand dependencies deeply",
                },
              ],

              interviewQuestions: [
                "Difference between state and props?",
                "When would you use useReducer over useState?",
                "Why does useEffect run twice in development?",
                "Why are keys important in React lists?",
                "How do you prevent unnecessary re-renders?",
              ],
            },

            tasks: [
              "Prepare clear explanations for core React concepts",
              "Explain hooks lifecycle without memorizing definitions",
              "Build small demos for useEffect behavior",
              "Fix common React interview bugs",
              "Practice answering React why-questions",
            ],

            examples: [
              "Fixing infinite useEffect loop",
              "Optimizing component with React.memo",
              "Correct vs incorrect key usage",
            ],

            useCases: [
              "Frontend interviews",
              "React machine coding rounds",
              "Production debugging",
              "Code reviews in teams",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand how React really works",
              "Explain React internals confidently in interviews",
              "Avoid common React performance pitfalls",
              "Reason about component re-renders",
              "Stand out in React-focused interview rounds",
            ],
          },
          {
            day: 3,
            topic: "Coding Challenges – Core Patterns",

            subTopics: [
              "Array and string problems",
              "Hash map patterns",
              "Two-pointer technique",
              "Sliding window",
              "Basic recursion",
            ],

            explanation:
              "Most frontend coding interviews test pattern recognition and logical thinking rather than complex algorithms or heavy mathematics.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Pattern Identification Before Coding",
                description:
                  "Code likhne se pehle problem ko correct pattern me map karna practice karo — jaise real interviews me expect hota hai.",
                tasks: [
                  "Har question ke liye pehle pattern bolo (hash map, sliding window, etc.)",
                  "Brute force approach likh kar usko optimize karo",
                  "Same problem ko 2 different patterns se solve karne ka try karo",
                  "Nested loop ko O(n) pattern me convert karo",
                ],
                outcome:
                  "Tum instantly identify kar paoge ki kaunsa pattern use hoga — bina panic ke.",
              },
              {
                id: "pt-2",
                title: "Explain While Coding Drill",
                description:
                  "Live coding round jaise environment me solution likhna aur explain karna practice karo.",
                tasks: [
                  "Code likhte time har step aloud explain karo",
                  "Time & space complexity har solution ke end me bolo",
                  "Edge cases verbally discuss karo",
                  "Interviewer ke interrupt questions ka answer do",
                ],
                outcome:
                  "Live coding rounds me clarity aur confidence dono improve honge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Pattern recognition before coding",
                "Optimized solution over brute force",
                "Clear explanation of logic",
                "Time & space complexity awareness",
              ],
              strongAnswer:
                "Main pehle problem ko pattern me break karta hoon, phir optimal solution likhta hoon aur edge cases explain karta hoon.",
              redFlags: [
                "Direct code likhna bina approach bataye",
                "Nested loops jab optimization possible ho",
                "Time complexity explain na kar pana",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Correct pattern selection",
                "Edge case handling",
                "Readable and clean code",
                "Optimization reasoning",
              ],
              validationChecks: [
                "Kya tum pattern clearly identify kar pa rahe ho?",
                "Kya brute force ko optimize kar sakte ho?",
                "Kya tum solution verbally justify kar sakte ho?",
              ],
              realWorldNote:
                "Most interview rejections galat approach ki wajah se hote hain, syntax ki wajah se nahi.",
            },

            editorGuidance: {
              careerMindset: [
                "Interview coding math nahi, thinking test hota hai",
                "Pattern samajhna = confidence",
                "Simple + optimized solution hi best hota hai",
              ],
              seniorAdvice:
                "Agar tum pattern pehle identify karke explain kar sakte ho, interviewer ko already signal mil jata hai ki tum strong ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In frontend interviews, coding rounds usually focus on common patterns that can be reused across many problems. Interviewers want to see how you break problems down, optimize solutions, and explain your thought process.",

              interviewExpectations: {
                title: "What Interviewers Look For",
                explanation:
                  "Interviewers evaluate how you approach a problem, not just whether you reach the correct answer.",
                keySignals: [
                  "Clear problem understanding",
                  "Pattern recognition",
                  "Optimized time & space complexity",
                  "Readable and maintainable code",
                  "Ability to explain logic clearly",
                ],
                realWorldNote:
                  "In real jobs, developers solve pattern-based problems daily while debugging and optimizing features",
              },

              arrayAndString: {
                title: "Array & String Problems",
                explanation:
                  "Arrays and strings form the foundation of most coding questions due to their real-world relevance.",
                conceptsUsed: [
                  "Iteration",
                  "Index-based access",
                  "Immutable updates",
                ],
                exampleLogic: `
function removeDuplicates(arr) {
  return [...new Set(arr)];
}
      `,
                interviewInsight:
                  "Follow-up questions often involve optimizing space or avoiding built-in helpers",
              },

              hashMapPattern: {
                title: "Hash Map Pattern",
                explanation:
                  "Hash maps allow constant-time lookups and are frequently used to reduce nested loops.",
                conceptsUsed: [
                  "Key-value storage",
                  "Frequency counting",
                  "Fast lookups",
                ],
                exampleLogic: `
const map = {};
for (let char of str) {
  map[char] = (map[char] || 0) + 1;
}
      `,
                realWorldNote:
                  "Used heavily in form validation, caching, and deduplication logic",
              },

              twoPointerTechnique: {
                title: "Two-Pointer Technique",
                explanation:
                  "Two pointers help solve problems efficiently by reducing time complexity from O(n²) to O(n).",
                conceptsUsed: [
                  "Left and right pointers",
                  "Sorted data traversal",
                  "In-place comparison",
                ],
                exampleLogic: `
let left = 0, right = arr.length - 1;
while (left < right) {
  // move pointers based on condition
}
      `,
                interviewInsight:
                  "Interviewers often ask why two pointers are better than nested loops",
              },

              slidingWindow: {
                title: "Sliding Window Pattern",
                explanation:
                  "Sliding window is used to process a range of elements efficiently without recalculating everything.",
                conceptsUsed: [
                  "Window expansion",
                  "Window contraction",
                  "Dynamic tracking",
                ],
                exampleLogic: `
let left = 0;
for (let right = 0; right < str.length; right++) {
  // adjust window
}
      `,
                commonUseCases:
                  "Longest substring, max sum subarray, rate-limiting logic",
              },

              recursionBasics: {
                title: "Basic Recursion",
                explanation:
                  "Recursion helps solve problems that can be broken into smaller subproblems.",
                conceptsUsed: [
                  "Base condition",
                  "Recursive call",
                  "Call stack",
                ],
                exampleLogic: `
function factorial(n) {
  if (n === 1) return 1;
  return n * factorial(n - 1);
}
      `,
                interviewInsight:
                  "Interviewers may ask you to convert recursion to iteration",
              },

              commonMistakes: [
                {
                  mistake: "Jumping straight to code",
                  issue: "Logical errors",
                  fix: "Clarify approach before coding",
                },
                {
                  mistake: "Using brute force",
                  issue: "Poor performance",
                  fix: "Look for reusable patterns",
                },
                {
                  mistake: "Not explaining logic",
                  issue: "Low interview confidence",
                  fix: "Think aloud while coding",
                },
              ],

              interviewQuestions: [
                "Which pattern fits this problem?",
                "What is the time and space complexity?",
                "Can this be optimized further?",
                "How would this behave on large inputs?",
                "Can you solve it without extra space?",
              ],
            },

            tasks: [
              "Solve 6–8 pattern-based coding problems",
              "Identify the pattern before coding",
              "Optimize brute-force solutions",
              "Practice explaining logic aloud",
              "Analyze time and space complexity",
            ],

            examples: [
              "Remove duplicates from array",
              "Longest substring without repeating characters",
              "Two sum using hash map",
            ],

            useCases: [
              "DSA interview rounds",
              "Live coding tests",
              "Frontend hiring challenges",
              "Problem-solving confidence",
            ],

            editorType: "javascript",

            estimatedTime: "3–4 hours",

            objectives: [
              "Recognize common coding patterns quickly",
              "Write optimized and readable JavaScript solutions",
              "Improve confidence in live coding rounds",
              "Explain problem-solving approach clearly",
              "Perform better in frontend DSA interviews",
            ],
          },
          {
            day: 4,
            topic: "Explaining Code & Debugging",

            subTopics: [
              "Thinking out loud",
              "Breaking problems into steps",
              "Debugging systematically",
              "Handling edge cases",
              "Writing clean readable code",
            ],

            explanation:
              "In real interviews, interviewers judge how you think, communicate, and debug far more than whether you reach the final answer.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Think-Aloud Interview Simulation",
                description:
                  "Real interview jaise environment me code likhte waqt har thought ko clearly explain karna practice karo.",
                tasks: [
                  "Problem ko apne words me restate karo",
                  "Approach choose karne ka reason bolo",
                  "Code likhte waqt har step explain karo",
                  "End me time & space complexity explain karo",
                ],
                outcome:
                  "Interviewers ko tumhara thinking process crystal clear dikhega.",
              },
              {
                id: "pt-2",
                title: "Systematic Debugging Drill",
                description:
                  "Bug ko guess karne ke bajay structured debugging approach follow karo.",
                tasks: [
                  "Bug reproduce karo",
                  "Assumptions verify karo",
                  "Console logs strategically add karo",
                  "One-by-one variables trace karo",
                ],
                outcome:
                  "Panic-free debugging aur strong problem-solving signal milega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear verbal explanation",
                "Step-by-step problem breakdown",
                "Calm debugging approach",
                "Clean and readable code",
              ],
              strongAnswer:
                "Main pehle problem ko break karta hoon, phir approach explain karta hoon aur code likhte waqt har decision clearly communicate karta hoon.",
              redFlags: [
                "Silent coding",
                "Random console.log debugging",
                "Messy variable naming",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Clarity of explanation",
                "Debugging structure",
                "Edge case awareness",
                "Code readability",
              ],
              validationChecks: [
                "Kya candidate apna approach clearly bol pa raha hai?",
                "Kya bug ko step-by-step isolate kar raha hai?",
                "Kya variable names self-explanatory hain?",
              ],
              realWorldNote:
                "Interviewers final answer se zyada tumhari thinking judge karte hain.",
            },

            editorGuidance: {
              careerMindset: [
                "Interview ek communication test hai",
                "Calm rehna skill ka hissa hai",
                "Readable code = team-ready developer",
              ],
              seniorAdvice:
                "Agar tum apna logic clearly explain kar sakte ho, interviewer tumhari side ho jata hai — even if answer perfect na ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Many candidates fail interviews not because they don’t know the answer, but because they cannot explain their thinking clearly. This day focuses on verbal clarity, structured problem-solving, and calm debugging — the exact skills interviewers look for.",

              thinkingOutLoud: {
                title: "Thinking Out Loud (Most Important Skill)",
                explanation:
                  "Interviewers expect you to speak while solving problems so they can understand your thought process.",
                howToDoIt: [
                  "Restate the problem in your own words",
                  "Explain assumptions clearly",
                  "Describe why you choose a particular approach",
                  "Narrate what each part of code is doing",
                ],
                exampleDialogue:
                  "“I’ll start with a simple approach first, then see if we can optimize it.”",
                interviewInsight:
                  "Silence is often interpreted as confusion or lack of clarity",
              },

              problemBreakdown: {
                title: "Breaking Problems into Steps",
                explanation:
                  "Good developers decompose problems into smaller, manageable steps before coding.",
                steps: [
                  "Understand inputs and outputs",
                  "Identify constraints and edge cases",
                  "Choose an approach (brute force → optimized)",
                  "Write code step by step",
                  "Test with examples",
                ],
                exampleLogic: `
1. Parse input
2. Initialize variables
3. Loop through data
4. Apply condition
5. Return result
      `,
                realWorldNote:
                  "This mirrors how tasks are broken down in real project tickets",
              },

              systematicDebugging: {
                title: "Debugging Systematically",
                explanation:
                  "Debugging is not guessing — it is a logical elimination process.",
                conceptsUsed: [
                  "Reproducing the bug",
                  "Using console logs effectively",
                  "Checking variable states",
                  "Verifying assumptions",
                ],
                exampleLogic: `
console.log('value before loop:', value);
      `,
                interviewInsight:
                  "Interviewers often intentionally give buggy code to see how you debug",
              },

              edgeCasesHandling: {
                title: "Handling Edge Cases",
                explanation:
                  "Edge cases separate average candidates from strong ones.",
                commonEdgeCases: [
                  "Empty arrays or strings",
                  "Null or undefined values",
                  "Single-element inputs",
                  "Large input sizes",
                ],
                exampleLogic: `
if (!arr || arr.length === 0) return [];
      `,
                bestPractice:
                  "Mention edge cases verbally even if you don’t code all of them",
              },

              cleanCode: {
                title: "Writing Clean & Readable Code",
                explanation:
                  "Readable code shows professionalism and team readiness.",
                principles: [
                  "Meaningful variable names",
                  "Small functions",
                  "Consistent formatting",
                  "Avoid unnecessary complexity",
                ],
                badVsGood: {
                  bad: "let x = a + b;",
                  good: "let totalPrice = basePrice + tax;",
                },
                interviewInsight:
                  "Interviewers imagine working with your code after the interview",
              },

              commonMistakes: [
                {
                  mistake: "Solving silently",
                  issue: "Interviewer can’t follow thinking",
                  fix: "Explain each decision briefly",
                },
                {
                  mistake: "Panic during bugs",
                  issue: "Loss of confidence",
                  fix: "Debug step by step calmly",
                },
                {
                  mistake: "Messy variable names",
                  issue: "Hard-to-read logic",
                  fix: "Rename for clarity",
                },
              ],

              interviewQuestions: [
                "Can you explain your approach?",
                "Why did you choose this solution?",
                "What is the time and space complexity?",
                "How would you debug this issue?",
                "What edge cases should we consider?",
              ],
            },

            tasks: [
              "Practice explaining code verbally while solving problems",
              "Debug at least 3 broken JavaScript snippets",
              "Refactor messy code into clean readable form",
              "Explain time and space complexity confidently",
              "Practice calm debugging under time pressure",
            ],

            examples: [
              "Explain time complexity of a loop",
              "Fix logical bug in condition",
              "Refactor nested loops into clean code",
            ],

            useCases: [
              "Live interviews",
              "Pair programming rounds",
              "Code reviews",
              "Team collaboration",
            ],

            editorType: "javascript",

            estimatedTime: "2–3 hours",

            objectives: [
              "Communicate problem-solving clearly",
              "Develop structured debugging mindset",
              "Handle interview pressure calmly",
              "Write clean, professional JavaScript",
              "Improve success rate in live coding interviews",
            ],
          },
          {
            day: 5,
            topic: "Take-home Assignments Strategy",

            subTopics: [
              "Understanding requirements",
              "Project structure and cleanliness",
              "README and documentation",
              "Trade-offs and assumptions",
              "Time management",
            ],

            explanation:
              "Take-home assignments are designed to simulate real-world work. Companies evaluate your engineering judgment, code quality, and communication — not just whether the app works.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Requirement Breakdown & Planning Drill",
                description:
                  "Take-home assignment start karne se pehle requirements ko engineer jaise analyze karna practice karo.",
                tasks: [
                  "Assignment ko 2–3 baar read karo",
                  "Must-have vs nice-to-have list banao",
                  "Ambiguous points note karo",
                  "High-level approach likho (no code)",
                ],
                outcome:
                  "Tum bina confuse hue structured execution start kar paoge.",
              },
              {
                id: "pt-2",
                title: "Professional README Writing Exercise",
                description:
                  "Same assignment ke liye ek reviewer-friendly README likho.",
                tasks: [
                  "Project overview clearly likho",
                  "Implemented features list karo",
                  "Setup instructions step-by-step do",
                  "Trade-offs aur assumptions mention karo",
                ],
                outcome:
                  "Reviewer ko tumhari thinking bina code dekhe samajh aa jayegi.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear requirement understanding",
                "Clean project structure",
                "Thoughtful trade-offs",
                "Strong written communication",
              ],
              strongAnswer:
                "Maine pehle requirements ko break kiya, core features prioritize kiye, aur apne trade-offs README me clearly document kiye.",
              redFlags: [
                "No README or poor documentation",
                "Over-engineered solution",
                "Unclear assumptions",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Code readability",
                "Folder structure clarity",
                "README quality",
                "Decision-making explanation",
              ],
              validationChecks: [
                "Kya project easily run ho raha hai?",
                "Kya README se approach clear hai?",
                "Kya unnecessary complexity avoid ki gayi hai?",
              ],
              realWorldNote:
                "Take-home assignments me communication ka weight code se kam nahi hota.",
            },

            editorGuidance: {
              careerMindset: [
                "Assignment ek product demo hai",
                "Documentation engineering ka hissa hai",
                "Perfect se zyada professional bano",
              ],
              seniorAdvice:
                "Agar reviewer ko tumhari thinking samajh aa gayi, to minor missing features ignore ho jaate hain.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Many candidates get rejected in take-home assignments not because their solution is wrong, but because it lacks clarity, structure, or professionalism. This day focuses on how to approach, execute, and present take-home assignments like a real engineer.",

              understandingRequirements: {
                title: "Understanding Requirements Clearly",
                explanation:
                  "The first and most critical step is correctly understanding what is required — and what is optional.",
                bestPractices: [
                  "Read the assignment multiple times",
                  "Identify must-have vs nice-to-have features",
                  "Clarify ambiguous requirements early",
                  "Understand evaluation criteria if provided",
                ],
                realWorldNote:
                  "Asking clarifying questions is seen as a positive signal, not weakness",
                interviewInsight:
                  "Companies want to see if you can work independently with unclear specs",
              },

              projectStructure: {
                title: "Project Structure & Code Cleanliness",
                explanation:
                  "Clean structure shows that you can work in real production codebases.",
                expectations: [
                  "Logical folder structure",
                  "Separation of concerns",
                  "Reusable components",
                  "Consistent naming conventions",
                ],
                exampleStructure: `
src/
 ├── components/
 ├── pages/
 ├── services/
 ├── hooks/
 ├── utils/
 └── App.jsx
      `,
                bestPractice:
                  "Over-engineering is worse than simple, clean architecture",
              },

              readmeDocumentation: {
                title: "README & Documentation (Very Important)",
                explanation:
                  "README is often the FIRST thing reviewers read before running your code.",
                shouldInclude: [
                  "Project overview",
                  "Features implemented",
                  "Tech stack used",
                  "Setup instructions",
                  "Trade-offs and assumptions",
                ],
                exampleSections: [
                  "## Project Overview",
                  "## Features",
                  "## Setup",
                  "## Trade-offs",
                ],
                interviewInsight:
                  "A good README can compensate for missing minor features",
              },

              tradeOffsAssumptions: {
                title: "Trade-offs & Assumptions",
                explanation:
                  "No take-home assignment expects a perfect solution within limited time.",
                whatToExplain: [
                  "What you chose to prioritize",
                  "What you intentionally skipped",
                  "What you would improve with more time",
                ],
                exampleExplanation:
                  "“I focused on core functionality and clean architecture over advanced animations due to time constraints.”",
                realWorldNote:
                  "Senior engineers are evaluated heavily on trade-off decisions",
              },

              timeManagement: {
                title: "Time Management Strategy",
                explanation:
                  "Companies often expect 4–8 hours of work, not a full product.",
                suggestedApproach: [
                  "Spend first 30–45 minutes planning",
                  "Implement core features first",
                  "Leave polish and extras for last",
                  "Stop when time limit is reached",
                ],
                commonMistake:
                  "Overworking the assignment at the cost of quality",
                bestPractice:
                  "A solid, incomplete solution beats a messy complete one",
              },

              commonMistakes: [
                {
                  mistake: "Skipping README",
                  issue: "Reviewer has no context",
                  fix: "Always document decisions",
                },
                {
                  mistake: "Over-engineering",
                  issue: "Unnecessary complexity",
                  fix: "Keep architecture simple",
                },
                {
                  mistake: "Ignoring edge cases silently",
                  issue: "Looks careless",
                  fix: "Mention them in README",
                },
              ],

              interviewQuestions: [
                "How did you approach the assignment?",
                "What trade-offs did you make?",
                "What would you improve with more time?",
                "How would you scale this solution?",
                "Why did you choose this architecture?",
              ],
            },

            tasks: [
              "Analyze a sample take-home assignment",
              "Break requirements into must-have vs optional",
              "Design folder structure before coding",
              "Write a professional README",
              "List trade-offs and future improvements",
            ],

            examples: [
              "Small React dashboard assignment",
              "API-based frontend task",
              "CRUD application with limited scope",
            ],

            useCases: [
              "Startup hiring",
              "Remote roles",
              "Product companies",
              "Async interview processes",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Approach take-home assignments strategically",
              "Demonstrate real-world engineering judgment",
              "Write clean, professional code",
              "Communicate decisions clearly",
              "Increase success rate in async interview rounds",
            ],
          },
          {
            day: 6,
            topic: "Live Coding Best Practices",

            subTopics: [
              "Clarifying questions",
              "Handling nervousness",
              "Incremental coding",
              "Error handling during live coding",
              "Graceful failure handling",
            ],

            explanation:
              "Live coding rounds are less about perfect solutions and more about how you think, communicate, and adapt under pressure.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Live Coding Simulation (Pressure Drill)",
                description:
                  "Real interview jaise pressure me live coding practice karo with time constraint.",
                tasks: [
                  "30–45 min timer set karo",
                  "Problem ko pehle verbally restate karo",
                  "Clarifying questions pucho",
                  "Incremental coding karo (base → logic → edge cases)",
                  "End me approach + complexity explain karo",
                ],
                outcome:
                  "Live interviews me panic kam hoga aur flow natural lagega.",
              },
              {
                id: "pt-2",
                title: "Debug-in-Public Exercise",
                description:
                  "Intentional buggy code ko live debug karna practice karo.",
                tasks: [
                  "Buggy JS function lo",
                  "Bug ko loudly identify karo",
                  "Assumptions verify karo",
                  "Step-by-step fix karo",
                  "Final solution explain karo",
                ],
                outcome:
                  "Interviewers ke saamne calm debugging confidence build hoga.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clarifying questions poochna",
                "Thinking out loud",
                "Step-by-step approach",
                "Calm reaction to errors",
              ],
              strongAnswer:
                "Main pehle problem ko clarify karta hoon, simplest case se start karta hoon aur incremental approach follow karta hoon.",
              redFlags: [
                "Silent coding",
                "Panic on small errors",
                "Rushing without clarity",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Problem understanding",
                "Communication clarity",
                "Incremental correctness",
                "Debugging mindset",
              ],
              validationChecks: [
                "Kya candidate ne assumptions clarify ki?",
                "Kya base case pehle handle hua?",
                "Kya error calmly debug hua?",
              ],
              realWorldNote:
                "Live coding me interviewer tumhe engineer ki tarah kaam karte hue dekhna chahta hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Live coding exam nahi, collaboration hai",
                "Perfect solution optional hai",
                "Thinking visible honi chahiye",
              ],
              seniorAdvice:
                "Agar tum clearly soch aur bol pa rahe ho, to incomplete solution bhi pass ho sakta hai.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In live coding interviews, interviewers are evaluating your problem-solving approach, communication skills, and debugging mindset. Even senior developers make mistakes — what matters is how you recover.",

              clarifyingQuestions: {
                title: "Asking Clarifying Questions",
                explanation:
                  "Before writing any code, clarify the problem to avoid wrong assumptions.",
                goodQuestions: [
                  "What are the expected inputs and outputs?",
                  "Are there any edge cases to consider?",
                  "Can I assume valid input?",
                  "Is performance important here?",
                ],
                realWorldNote:
                  "Asking questions shows senior-level thinking, not weakness",
                interviewInsight:
                  "Candidates who jump straight into coding often misunderstand requirements",
              },

              handlingNervousness: {
                title: "Handling Nervousness & Pressure",
                explanation: "Nervousness is normal. Interviewers expect it.",
                strategies: [
                  "Take a deep breath before starting",
                  "Repeat the problem in your own words",
                  "Think out loud while solving",
                  "Pause before rushing into code",
                ],
                mindsetShift:
                  "Treat it like a pair-programming session, not an exam",
              },

              incrementalCoding: {
                title: "Incremental Coding Strategy",
                explanation:
                  "Writing code step-by-step reduces errors and keeps the interviewer aligned with your thinking.",
                approach: [
                  "Start with function signature",
                  "Handle simplest case first",
                  "Add logic incrementally",
                  "Test with small examples verbally",
                ],
                exampleFlow: `
function solve(input) {
  // Step 1: handle base case
  // Step 2: core logic
  // Step 3: edge cases
}
      `,
                bestPractice:
                  "Correct but incomplete code is better than rushed broken code",
              },

              errorHandling: {
                title: "Handling Errors During Live Coding",
                explanation:
                  "Making mistakes is expected. How you debug matters more.",
                bestPractices: [
                  "Acknowledge the mistake calmly",
                  "Explain what went wrong",
                  "Fix it step-by-step",
                  "Do not panic or go silent",
                ],
                interviewInsight:
                  "Interviewers like candidates who debug confidently",
                realWorldNote:
                  "Production code is written with bugs — debugging is a core skill",
              },

              gracefulFailure: {
                title: "Graceful Failure Handling",
                explanation:
                  "Sometimes you won’t reach the final solution — and that’s okay.",
                whatToDo: [
                  "Explain remaining approach clearly",
                  "Discuss time/space complexity",
                  "Mention optimizations you’d add",
                  "Be honest about limitations",
                ],
                exampleStatement:
                  "“Given more time, I would optimize this using a hashmap to reduce complexity.”",
                interviewerPerspective: "Clear thinking > complete solution",
              },

              commonMistakes: [
                {
                  mistake: "Coding silently",
                  issue: "Interviewer can’t follow thinking",
                  fix: "Think out loud",
                },
                {
                  mistake: "Rushing to finish",
                  issue: "More bugs",
                  fix: "Slow and steady coding",
                },
                {
                  mistake: "Panicking on error",
                  issue: "Loss of confidence",
                  fix: "Debug calmly and explain",
                },
              ],

              interviewQuestions: [
                "How do you approach a new problem?",
                "What do you do if you get stuck?",
                "How do you debug under pressure?",
                "How do you ensure correctness while coding live?",
              ],
            },

            tasks: [
              "Simulate 30–45 min live coding sessions",
              "Practice thinking out loud while coding",
              "Code without autocomplete occasionally",
              "Practice explaining approach before coding",
              "Practice handling mistakes calmly",
            ],

            examples: [
              "Build a small utility function live",
              "Modify existing code in real time",
              "Fix a bug during live session",
            ],

            useCases: [
              "Live interviews",
              "Pair programming rounds",
              "On-site interviews",
              "Remote technical rounds",
            ],

            editorType: "javascript",

            estimatedTime: "2–3 hours",

            objectives: [
              "Build confidence for live coding interviews",
              "Communicate thought process clearly",
              "Handle pressure and mistakes professionally",
              "Demonstrate real-world debugging mindset",
              "Increase success rate in live interview rounds",
            ],
          },
          {
            day: 7,
            topic: "Week 32 Project – Interview Prep Repository",

            subTopics: [
              "Problem categorization",
              "Clear explanations",
              "Time and space analysis",
              "Consistent code style",
              "Progress tracking",
            ],

            explanation:
              "This repository becomes proof of your problem-solving ability, consistency, and interview readiness. Recruiters often value well-documented prep repositories more than random solved questions.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Interview-Ready Repo Structure Setup",
                description:
                  "Professional interview-prep repository ka clean, scalable structure design karo.",
                tasks: [
                  "Pattern-based folders banao (arrays, hashmaps, async, react)",
                  "Har problem ke liye same README template follow karo",
                  "Platform name hide rakho (LeetCode/CodeChef nahi)",
                  "Consistent naming convention apply karo",
                ],
                outcome:
                  "Repo dekhte hi interviewer ko clarity + seriousness signal milega.",
              },
              {
                id: "pt-2",
                title: "Explain Before Code Practice",
                description:
                  "Har problem ka solution likhne se pehle explanation likhne ki habit banao.",
                tasks: [
                  "Problem ko apni language me summarize karo",
                  "Approach step-by-step likho",
                  "Edge cases explicitly mention karo",
                  "Code last me add karo",
                ],
                outcome:
                  "Interview me solution explain karna natural ho jayega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Pattern-based thinking",
                "Clear written explanations",
                "Big-O awareness",
                "Consistency over volume",
              ],
              strongAnswer:
                "Main problems ko patterns ke hisaab se solve karta hoon aur har solution ka approach aur complexity document karta hoon.",
              redFlags: [
                "Sirf code dump",
                "No explanation",
                "No complexity analysis",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Explanation clarity",
                "Correct complexity reasoning",
                "Readable code",
                "Edge case awareness",
              ],
              validationChecks: [
                "Kya har solution explain hai?",
                "Kya Big-O mentioned hai?",
                "Kya patterns clearly grouped hain?",
              ],
              realWorldNote:
                "Recruiters repo ko test suite nahi, thinking proof ke liye dekhte hain.",
            },

            editorGuidance: {
              careerMindset: [
                "Interview prep bhi ek product hai",
                "Clarity > quantity",
                "Documentation engineering skill ka part hai",
              ],
              seniorAdvice:
                "Agar tum apna solution junior ko samjha sakte ho, interviewer ko bhi convince kar sakte ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Many candidates solve hundreds of problems but fail interviews because they can’t explain their thinking. This repository fixes that gap by focusing on clarity, patterns, and communication — exactly what interviews test.",

              projectGoal: {
                title: "Project Goal",
                explanation:
                  "Build a clean, well-structured interview-prep repository that demonstrates how you think, solve, and explain problems.",
                realWorldNote:
                  "Recruiters often open GitHub to check consistency and clarity, not just number of problems",
              },

              problemCategorization: {
                title: "Problem Categorization by Patterns",
                explanation:
                  "Problems should be grouped by patterns, not platforms.",
                categories: [
                  "Arrays & Strings",
                  "Hash Maps & Objects",
                  "Two Pointer & Sliding Window",
                  "Recursion & Backtracking",
                  "Promises & Async JS",
                  "React logic-based problems",
                ],
                bestPractice:
                  "Pattern recognition is more important than memorizing questions",
              },

              clearExplanations: {
                title: "Clear Problem Explanations",
                explanation:
                  "Every solution must be understandable even to someone reading it for the first time.",
                explanationStructure: [
                  "Problem summary in simple words",
                  "Approach explanation",
                  "Why this approach works",
                  "Edge cases considered",
                ],
                exampleFormat: `
## Problem
Explain problem in your own words

## Approach
Explain logic step-by-step

## Edge Cases
Empty input, null values, large input
      `,
                interviewInsight:
                  "If you can explain clearly, interviewers assume you understand deeply",
              },

              complexityAnalysis: {
                title: "Time & Space Complexity Analysis",
                explanation:
                  "Interviewers almost always ask complexity — your repo should show that habit.",
                conceptsUsed: [
                  "Big-O notation",
                  "Worst vs average case",
                  "Space vs time trade-offs",
                ],
                exampleFormat: `
Time Complexity: O(n)
Space Complexity: O(1)
      `,
                bestPractice: "Always justify complexity in one sentence",
              },

              codeConsistency: {
                title: "Consistent Code Style",
                explanation: "Readable code gives confidence to reviewers.",
                guidelines: [
                  "Meaningful variable names",
                  "Consistent function naming",
                  "Avoid unnecessary clever tricks",
                  "Add comments only where needed",
                ],
                realWorldNote: "Clean code is often preferred over short code",
              },

              progressTracking: {
                title: "Progress Tracking & Consistency",
                explanation:
                  "Tracking progress builds discipline and shows seriousness.",
                strategies: [
                  "Checklist of solved problems",
                  "Weekly progress updates",
                  "Difficulty tagging (Easy/Medium/Hard)",
                ],
                example: `
✔ Arrays – 15 problems  
✔ Strings – 10 problems  
⏳ Recursion – In progress
      `,
              },

              commonMistakes: [
                {
                  mistake: "Dumping raw solutions",
                  issue: "No learning signal",
                  fix: "Always add explanation",
                },
                {
                  mistake: "Platform-based folders only",
                  issue: "Weak pattern thinking",
                  fix: "Organize by problem patterns",
                },
                {
                  mistake: "No complexity analysis",
                  issue: "Incomplete interview prep",
                  fix: "Always mention Big-O",
                },
              ],

              interviewQuestions: [
                "How do you prepare for coding interviews?",
                "How do you decide optimal complexity?",
                "How do you explain solutions to non-technical people?",
                "How do you track your improvement?",
              ],
            },

            tasks: [
              "Create a dedicated GitHub interview-prep repository",
              "Solve 50+ frontend-focused coding problems",
              "Write clear explanations for each problem",
              "Add time and space complexity analysis",
              "Organize problems by patterns, not platforms",
              "Track progress consistently",
            ],

            examples: [
              "Arrays, strings, objects, recursion",
              "Async JavaScript interview questions",
              "React logic-based coding problems",
            ],

            useCases: [
              "Interview preparation",
              "Recruiter evaluation",
              "Self-confidence boost",
              "Structured long-term learning",
            ],

            editorType: null,

            estimatedTime: "4–6 hours (initial setup) + ongoing practice",

            objectives: [
              "Build interview-ready problem-solving mindset",
              "Improve explanation and communication skills",
              "Develop consistency and discipline",
              "Create strong GitHub proof for recruiters",
              "Increase success rate in frontend interviews",
            ],
          },
        ],
      },
      {
        week: 33,
        weekGoal:
          "Understand frontend system design and scalable architecture principles",
        weekOutcome:
          "Ability to design, explain, and document scalable frontend applications",
        miniProject: "Capstone Project Architecture Planning",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Frontend Architecture Fundamentals",

            subTopics: [
              "What is frontend architecture",
              "SPA vs MPA",
              "Client-side vs server-side rendering",
              "Data flow in frontend apps",
              "Separation of concerns",
            ],

            explanation:
              "Frontend architecture defines how UI, state, data, and business logic are structured to build scalable, maintainable, and performant applications. Strong architecture decisions separate junior developers from senior frontend engineers.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Architecture Breakdown of a Real App",
                description:
                  "Kisi real-world app (Netflix / Twitter / Amazon) ki frontend architecture ko logically break karo.",
                tasks: [
                  "UI, state, data fetching, business logic layers identify karo",
                  "SPA vs MPA choice ka reason likho",
                  "CSR / SSR / SSG ka mix kaha use ho raha hai note karo",
                  "Data flow diagram (input → state → UI) draw karo",
                ],
                outcome:
                  "System-level frontend thinking develop hogi, sirf components tak limited nahi rahoge.",
              },
              {
                id: "pt-2",
                title: "Refactor a Poorly Structured UI",
                description:
                  "Ek messy React app lo aur uski architecture improve karo.",
                tasks: [
                  "API calls ko components se bahar nikaalo",
                  "Business logic ko custom hooks / services me shift karo",
                  "State ownership clearly define karo",
                  "Folder structure ko feature-based banao",
                ],
                outcome:
                  "Samajh aayega ki bad architecture real apps me kaise pain create karta hai.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear separation of concerns",
                "Data flow clarity",
                "Rendering strategy awareness",
                "Trade-off based decision making",
              ],
              strongAnswer:
                "Frontend architecture ka goal features ko scale karna, bugs reduce karna aur team collaboration easy banana hota hai — tools secondary hote hain.",
              redFlags: [
                "Har cheez component me likhna",
                "No clear data ownership",
                "Tool-based answers without reasoning",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Data flow predictability",
                "Layer responsibility clarity",
                "Ease of adding new features",
                "Bug isolation capability",
              ],
              validationChecks: [
                "Kya UI layer sirf presentation handle kar rahi hai?",
                "Kya state ka single source of truth hai?",
                "Kya API logic easily mock / test ho sakta hai?",
              ],
              realWorldNote:
                "Architecture ka test tab hota hai jab naya feature add karna ho ya bug fix karna ho.",
            },

            editorGuidance: {
              careerMindset: [
                "Architecture code se zyada decisions ka game hai",
                "Senior dev future pain pehle sochta hai",
                "Simple but scalable structure sabse powerful hota hai",
              ],
              seniorAdvice:
                "Agar tum architecture explain kar sakte ho bina code ke — tum senior thinking ke close ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world products, frontend is not just about components and styling. Architecture determines how easy it is to scale features, onboard new developers, debug issues, and evolve the product over time.",

              whatIsArchitecture: {
                title: "What is Frontend Architecture?",
                explanation:
                  "Frontend architecture is the high-level organization of UI components, state management, data fetching, routing, and business logic.",
                keyGoals: [
                  "Scalability",
                  "Maintainability",
                  "Performance",
                  "Clear ownership of responsibilities",
                ],
                realWorldNote:
                  "Poor architecture works for demos but fails badly in long-term products",
              },

              spaVsMpa: {
                title: "SPA vs MPA",
                explanation:
                  "Choosing between Single Page Applications and Multi Page Applications impacts performance, SEO, and development complexity.",
                comparison: {
                  SPA: [
                    "Client-side routing",
                    "Fast page transitions",
                    "Heavier JS bundle",
                    "Complex state management",
                  ],
                  MPA: [
                    "Server-rendered pages",
                    "Simpler mental model",
                    "Better initial SEO",
                    "Less shared state complexity",
                  ],
                },
                interviewInsight:
                  "There is no best choice — it depends on product requirements",
              },

              renderingModels: {
                title: "Client-side vs Server-side Rendering",
                explanation:
                  "Rendering strategy determines where HTML is generated and how data reaches the UI.",
                models: [
                  {
                    type: "CSR",
                    useCase: "Dashboards, internal tools",
                    tradeoff: "Slower initial load, fast interactions",
                  },
                  {
                    type: "SSR",
                    useCase: "SEO-critical pages",
                    tradeoff: "Higher server cost",
                  },
                  {
                    type: "SSG",
                    useCase: "Blogs, docs, marketing sites",
                    tradeoff: "Less dynamic data",
                  },
                ],
                realWorldNote: "Modern apps often mix CSR, SSR, and SSG",
              },

              dataFlow: {
                title: "Data Flow in Frontend Applications",
                explanation:
                  "Clear data flow prevents bugs and makes applications predictable.",
                patterns: [
                  "Unidirectional data flow",
                  "Top-down state propagation",
                  "Event-driven updates",
                ],
                exampleFlow: [
                  "User interaction",
                  "Action / handler",
                  "State update",
                  "UI re-render",
                ],
                interviewInsight:
                  "Most frontend bugs come from unclear data flow",
              },

              separationOfConcerns: {
                title: "Separation of Concerns",
                explanation:
                  "Each layer of the app should have a single responsibility.",
                layers: [
                  "UI components (presentation)",
                  "State management",
                  "Business logic",
                  "Data fetching / API layer",
                ],
                bestPractice:
                  "Never mix API logic directly inside UI components in large apps",
              },

              commonMistakes: [
                {
                  mistake: "Everything in components",
                  issue: "Hard to scale and test",
                  fix: "Extract logic into services and hooks",
                },
                {
                  mistake: "Over-engineering early",
                  issue: "Slows down development",
                  fix: "Evolve architecture gradually",
                },
                {
                  mistake: "No clear data ownership",
                  issue: "Unpredictable bugs",
                  fix: "Define single source of truth",
                },
              ],

              interviewQuestions: [
                "What is frontend architecture?",
                "SPA vs MPA — when would you choose each?",
                "How does data flow in React?",
                "How do you structure large frontend apps?",
                "What architectural mistakes have you seen?",
              ],
            },

            tasks: [
              "Analyze architecture of a popular app (Netflix, Amazon, Twitter)",
              "Identify UI, state, and data layers",
              "Draw a high-level frontend architecture diagram",
              "Explain architecture choices in your own words",
            ],

            examples: [
              "SPA architecture diagram",
              "Frontend data flow diagram",
              "Layered frontend structure",
            ],

            useCases: [
              "System design interviews",
              "Large-scale frontend projects",
              "Team-based development",
              "Senior frontend discussions",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand frontend architecture at a system level",
              "Differentiate junior vs senior frontend thinking",
              "Build clarity around data flow and responsibilities",
              "Prepare for frontend system design interviews",
              "Make better architectural decisions in real projects",
            ],
          },
          {
            day: 2,
            topic: "Component Design Principles",

            subTopics: [
              "Atomic design methodology",
              "Presentational vs container components",
              "Reusable vs feature-specific components",
              "Single Responsibility Principle",
              "Prop-driven design",
            ],

            explanation:
              "Well-designed components are the foundation of scalable frontend applications. Strong component design reduces bugs, improves reusability, and makes teams productive as applications grow.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Atomic Breakdown of a Real Screen",
                description:
                  "Kisi real production screen (Dashboard / Checkout / Profile) ko atomic design ke hisaab se break karo.",
                tasks: [
                  "Screen ko Atoms, Molecules, Organisms me divide karo",
                  "Reusable atoms identify karo (Button, Input, Badge)",
                  "Feature-specific organisms mark karo",
                  "Over-reuse ya over-splitting points note karo",
                ],
                outcome:
                  "Samajh aayega ki atomic design theory real screens me kaise apply hoti hai.",
              },
              {
                id: "pt-2",
                title: "Component Responsibility Refactor",
                description:
                  "Ek bloated component lo aur SRP follow karte hue refactor karo.",
                tasks: [
                  "UI, state, side-effects alag-alag identify karo",
                  "Business logic ko custom hooks me extract karo",
                  "Pure presentational component banao",
                  "Prop-driven API design karo",
                ],
                outcome:
                  "Components readable, testable aur scalable ho jaayenge.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear component responsibility",
                "Reusable vs feature-specific clarity",
                "Prop API design thinking",
                "Atomic design awareness",
              ],
              strongAnswer:
                "Main pehle component ka responsibility define karta hoon, phir decide karta hoon ki ye reusable hona chahiye ya feature-specific.",
              redFlags: [
                "Har component ko reusable banana",
                "Business logic UI ke andar",
                "God components (500+ lines)",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Component isolation",
                "Prop-driven behavior",
                "Logic separation",
                "Ease of unit testing",
              ],
              validationChecks: [
                "Kya component bina data fetch ke render ho sakta hai?",
                "Kya props clearly behavior control kar rahe hain?",
                "Kya logic hooks ke bina test ho sakta hai?",
              ],
              realWorldNote:
                "Achhe components wahi hote hain jo bina context ke samajh aa jaayein.",
            },

            editorGuidance: {
              careerMindset: [
                "Component ek contract hota hai, implementation detail nahi",
                "Reusability se pehle readability aati hai",
                "Simple component > clever component",
              ],
              seniorAdvice:
                "Agar component ka naam padhke uska kaam samajh aa jaye, tum sahi direction me ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In small projects, poorly designed components still work. In real production apps with dozens of developers, bad component design quickly leads to messy codebases, duplicated logic, and hard-to-fix bugs.",

              atomicDesign: {
                title: "Atomic Design Methodology",
                explanation:
                  "Atomic design breaks UI into a hierarchy of small, reusable building blocks.",
                levels: [
                  "Atoms – basic UI elements (Button, Input, Text)",
                  "Molecules – combination of atoms (SearchBar, FormRow)",
                  "Organisms – complex UI sections (Header, ProductList)",
                  "Templates – layout structure",
                  "Pages – final screens with real data",
                ],
                realWorldNote:
                  "Most design systems (Material UI, Chakra, Ant Design) follow atomic principles",
              },

              containerVsPresentational: {
                title: "Presentational vs Container Components",
                explanation:
                  "Separating UI from logic improves readability, testability, and reuse.",
                presentational: {
                  role: "Focus only on UI",
                  characteristics: [
                    "Receive data via props",
                    "No business logic",
                    "Easy to test",
                  ],
                },
                container: {
                  role: "Handle data, state, and logic",
                  characteristics: [
                    "Fetch data",
                    "Manage state",
                    "Pass props to UI components",
                  ],
                },
                interviewInsight:
                  "This separation is less strict today but the principle still matters",
              },

              reusableVsFeatureSpecific: {
                title: "Reusable vs Feature-Specific Components",
                explanation:
                  "Not every component should be reusable. Over-generalization increases complexity.",
                reusableComponents: ["Buttons", "Inputs", "Modals", "Cards"],
                featureComponents: [
                  "CheckoutSummary",
                  "UserProfileHeader",
                  "OrderHistoryList",
                ],
                bestPractice: "Reuse behavior, not business logic",
              },

              singleResponsibility: {
                title: "Single Responsibility Principle (SRP)",
                explanation:
                  "Each component should have one clear responsibility.",
                badExample:
                  "A component that fetches data, formats it, and renders UI",
                goodExample:
                  "Data fetching in hooks, formatting in utils, rendering in component",
                realWorldNote:
                  "SRP makes refactoring and debugging much easier",
              },

              propDrivenDesign: {
                title: "Prop-driven Design",
                explanation: "Props define how components behave and render.",
                principles: [
                  "Components should be configurable via props",
                  "Avoid hard-coded behavior",
                  "Prefer composition over conditionals",
                ],
                exampleLogic: `
<Button variant="primary" size="lg" disabled />
      `,
                interviewInsight:
                  "Good prop APIs feel intuitive and hard to misuse",
              },

              commonMistakes: [
                {
                  mistake: "Huge components",
                  issue: "Hard to read and test",
                  fix: "Split into smaller components",
                },
                {
                  mistake: "Over-reusable components",
                  issue: "Too many props, unclear behavior",
                  fix: "Keep feature-specific components simple",
                },
                {
                  mistake: "Mixing UI and business logic",
                  issue: "Tightly coupled code",
                  fix: "Extract logic into hooks or services",
                },
              ],

              interviewQuestions: [
                "What is atomic design?",
                "How do you decide if a component should be reusable?",
                "Difference between container and presentational components?",
                "How do you avoid prop drilling?",
                "What makes a good component API?",
              ],
            },

            tasks: [
              "Break an existing UI into atomic components",
              "Refactor large components into smaller ones",
              "Identify reusable vs feature-specific components",
              "Redesign a component following SRP",
            ],

            examples: [
              "Button, Card, Modal as atomic components",
              "Container + UI component split",
              "Feature-based component structure",
            ],

            useCases: [
              "React interviews",
              "Design system creation",
              "Large-scale frontend projects",
              "Code scalability and maintenance",
            ],

            editorType: "react",

            estimatedTime: "2–3 hours",

            objectives: [
              "Design clean and scalable React components",
              "Apply atomic design thinking in real projects",
              "Differentiate reusable and feature-specific components",
              "Write components that are easy to test and maintain",
              "Answer component design questions confidently in interviews",
            ],
          },
          {
            day: 3,
            topic: "State Management Architecture",

            subTopics: [
              "Local vs global state",
              "When to lift state",
              "Context vs Redux vs Zustand",
              "Server state vs client state",
              "State normalization",
            ],

            explanation:
              "Choosing the right state management strategy is critical for performance, scalability, and maintainability. Poor decisions here lead to prop drilling, unnecessary re-renders, and tightly coupled code.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "State Architecture Design Drill",
                description:
                  "Ek medium-scale app (Dashboard / E-commerce / SaaS) ke liye complete state architecture design karo.",
                tasks: [
                  "Local vs global state list karo",
                  "Server state aur client/UI state separate karo",
                  "State ownership define karo (component / store)",
                  "Over-globalization risks identify karo",
                ],
                outcome:
                  "Clear clarity milegi ki kaunsa state kaha rehna chahiye aur kyun.",
              },
              {
                id: "pt-2",
                title: "Tool Decision Justification Exercise",
                description:
                  "Same app ke liye Context, Redux aur Zustand ka comparison karke decision justify karo.",
                tasks: [
                  "Context-only solution sketch karo",
                  "Redux-based architecture draw karo",
                  "Zustand feature-level store design karo",
                  "Final choice ke pros/cons likho",
                ],
                outcome:
                  "Interview-ready justification develop hoga — tool se zyada decision pe focus.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Local vs global state clarity",
                "Server vs client state separation",
                "Tool choice justification",
                "State ownership understanding",
              ],
              strongAnswer:
                "Main pehle state ka scope aur lifetime decide karta hoon, phir tool choose karta hoon — tool se architecture drive nahi karta.",
              redFlags: [
                "Har cheez Redux me daalna",
                "Server data ko permanent global state banana",
                "State ownership unclear rakhna",
              ],
            },

            testingInsights: {
              focusAreas: [
                "State isolation",
                "Predictable updates",
                "Minimal re-renders",
                "Easy mocking of state",
              ],
              validationChecks: [
                "Kya component bina global store ke test ho sakta hai?",
                "Kya server state easily mock ho rahi hai?",
                "Kya state changes predictable aur traceable hain?",
              ],
              realWorldNote:
                "Achhi state architecture testing ko easy bana deti hai — testing se nahi ladti.",
            },

            editorGuidance: {
              careerMindset: [
                "State architecture UI se zyada important hoti hai",
                "Global state kam rakho, clarity zyada rakho",
                "Har state ko owner chahiye",
              ],
              seniorAdvice:
                "Agar tum bata sakte ho ki ye state yahin kyun hai, tum architecture sahi kar rahe ho.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "State is the hardest part of frontend architecture. Most real-world bugs, performance issues, and scaling problems come from poorly designed state management rather than UI code.",

              localVsGlobal: {
                title: "Local vs Global State",
                explanation:
                  "Not all state should be global. Overusing global state increases complexity.",
                localStateExamples: [
                  "Form input values",
                  "Toggle states",
                  "Modal open/close",
                ],
                globalStateExamples: [
                  "Authenticated user",
                  "Theme and language",
                  "Shopping cart",
                ],
                bestPractice:
                  "Keep state as close as possible to where it is used",
              },

              liftingState: {
                title: "When to Lift State",
                explanation:
                  "State should be lifted only when multiple components need to share or synchronize data.",
                signalsToLift: [
                  "Same state duplicated in siblings",
                  "Need to keep components in sync",
                  "Data flows upward frequently",
                ],
                antiPattern: "Lifting state too early without real need",
                realWorldNote:
                  "Premature lifting leads to bloated parent components",
              },

              stateToolsComparison: {
                title: "Context vs Redux vs Zustand",
                explanation:
                  "Different tools solve different problems. There is no single best solution.",
                context: {
                  useWhen: ["Theme", "Auth", "Low-frequency global state"],
                  downside: "Re-renders entire subtree if misused",
                },
                redux: {
                  useWhen: [
                    "Complex global state",
                    "Predictable updates",
                    "Large teams",
                  ],
                  downside: "More boilerplate and mental overhead",
                },
                zustand: {
                  useWhen: [
                    "Lightweight global state",
                    "Minimal boilerplate",
                    "Feature-level stores",
                  ],
                  downside: "Less strict architecture",
                },
                interviewInsight:
                  "Interviewers care more about why you chose a tool than the tool itself",
              },

              serverVsClientState: {
                title: "Server State vs Client State",
                explanation:
                  "Server state comes from APIs and must be synchronized with backend data.",
                serverStateExamples: ["User list", "Orders", "Search results"],
                clientStateExamples: [
                  "Selected filters",
                  "UI preferences",
                  "Local draft data",
                ],
                bestPractice:
                  "Do not store server state permanently in Redux unless required",
                realWorldNote:
                  "Libraries like React Query handle server state better than Redux",
              },

              normalization: {
                title: "State Normalization",
                explanation:
                  "Normalization avoids deeply nested state structures that are hard to update.",
                benefits: [
                  "Simpler updates",
                  "Better performance",
                  "Predictable data access",
                ],
                exampleLogic: `
users: {
  byId: { "1": {...}, "2": {...} },
  allIds: ["1", "2"]
}
      `,
                interviewInsight:
                  "Normalization is commonly discussed in system design rounds",
              },

              commonMistakes: [
                {
                  mistake: "Putting everything in global state",
                  issue: "Unnecessary complexity",
                  fix: "Prefer local state by default",
                },
                {
                  mistake: "Using Redux for simple apps",
                  issue: "Overengineering",
                  fix: "Start with useState or Context",
                },
                {
                  mistake: "Mixing server and UI state",
                  issue: "Hard-to-debug bugs",
                  fix: "Separate responsibilities clearly",
                },
              ],

              interviewQuestions: [
                "How do you decide what state should be global?",
                "Context vs Redux — when would you use each?",
                "What is server state?",
                "How do you avoid prop drilling?",
                "What is state normalization and why is it useful?",
              ],
            },

            tasks: [
              "Design state architecture for a medium-sized app",
              "Identify global vs local state clearly",
              "Create state flow diagram",
              "Choose appropriate state management tool",
            ],

            examples: [
              "Auth state architecture",
              "Shopping cart state flow",
              "Dashboard global state design",
            ],

            useCases: [
              "Complex frontend apps",
              "System design interviews",
              "Avoiding overengineering",
              "Scalable product development",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Design scalable frontend state architecture",
              "Avoid common state management pitfalls",
              "Choose the right state management tool confidently",
              "Understand client vs server state separation",
              "Answer state-related system design questions clearly",
            ],
          },
          {
            day: 4,
            topic: "API Integration & Data Layer Design",

            subTopics: [
              "API service layer pattern",
              "Handling loading and error states",
              "Caching and revalidation",
              "Pagination and infinite scrolling",
              "API versioning basics",
            ],

            explanation:
              "A well-designed data layer separates networking logic from UI, making frontend applications easier to scale, test, and maintain.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "API Service Layer Refactor Drill",
                description:
                  "Existing component-based API calls ko proper service/data layer me refactor karo.",
                tasks: [
                  "Direct fetch/axios calls identify karo components me",
                  "Centralized api/ services folder design karo",
                  "Endpoints ko reusable functions me convert karo",
                  "UI components ko clean, data-agnostic banao",
                ],
                outcome:
                  "Clear separation milegi UI aur data layer ke beech — production-ready architecture.",
              },
              {
                id: "pt-2",
                title: "Caching & Pagination Strategy Exercise",
                description:
                  "Ek API-heavy screen (list / feed / dashboard) ke liye caching aur pagination strategy design karo.",
                tasks: [
                  "Decide karo manual cache vs React Query/SWR",
                  "Pagination vs infinite scroll choose karo with reasons",
                  "Revalidation strategy define karo",
                  "Failure & retry scenarios document karo",
                ],
                outcome:
                  "Interview-level clarity aayegi data fetching optimization aur UX trade-offs pe.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear service layer separation",
                "Loading, error, success states awareness",
                "Caching vs fresh data trade-offs",
                "API scalability thinking",
              ],
              strongAnswer:
                "Main UI ko data source se isolate karta hoon aur service layer ke through API handle karta hoon taaki change aur testing easy rahe.",
              redFlags: [
                "Components ke andar direct API calls",
                "No error or retry handling",
                "Pagination bina backend consideration ke",
              ],
            },

            testingInsights: {
              focusAreas: [
                "API mocking",
                "Error state coverage",
                "Loading state assertions",
                "Pagination edge cases",
              ],
              validationChecks: [
                "Kya API layer easily mock ho rahi hai?",
                "Kya error responses UI me gracefully handle ho rahe hain?",
                "Kya pagination/infinite scroll predictable hai?",
              ],
              realWorldNote:
                "Achha data layer testing ko simple banata hai — UI tests brittle nahi hote.",
            },

            editorGuidance: {
              careerMindset: [
                "UI clean rakho, data layer strong banao",
                "Har API call ke saath failure socho",
                "Caching UX ka tool hai, shortcut nahi",
              ],
              seniorAdvice:
                "Agar API change ho jaye aur sirf ek file touch ho — tumne data layer sahi design kiya hai.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world frontend applications, APIs are called from dozens of places. Without a proper data layer, UI components become messy, repetitive, and hard to debug.",

              serviceLayer: {
                title: "API Service Layer Pattern",
                explanation:
                  "The service layer centralizes all API calls in one place instead of spreading fetch/axios logic across components.",
                benefits: [
                  "Cleaner UI components",
                  "Easier API changes",
                  "Centralized error handling",
                ],
                exampleLogic: `
const api = axios.create({ baseURL: '/api' });

export const getUsers = () => api.get('/users');
      `,
                bestPractice: "Components should never know API URLs directly",
              },

              loadingAndError: {
                title: "Handling Loading and Error States",
                explanation:
                  "Every API call must handle loading, success, and error states explicitly.",
                patterns: [
                  "Loading spinner or skeleton",
                  "Error fallback UI",
                  "Retry mechanisms",
                ],
                realWorldNote:
                  "Most production bugs come from unhandled error states",
                interviewInsight:
                  "Always mention loading & error handling in interviews",
              },

              caching: {
                title: "Caching & Revalidation",
                explanation:
                  "Caching avoids unnecessary network calls and improves perceived performance.",
                approaches: [
                  "In-memory cache",
                  "Browser cache",
                  "Library-based caching (React Query)",
                ],
                revalidationConcept:
                  "Serve cached data first, then update in background",
                exampleTools: ["React Query", "SWR"],
                bestPractice:
                  "Do not manually cache everything unless required",
              },

              pagination: {
                title: "Pagination & Infinite Scrolling",
                explanation:
                  "Large datasets should never be loaded all at once.",
                paginationTypes: [
                  "Offset-based pagination",
                  "Cursor-based pagination",
                ],
                infiniteScrollLogic: [
                  "Track page or cursor",
                  "Append new data",
                  "Stop when no more data",
                ],
                performanceNote:
                  "Infinite scroll requires careful memory management",
              },

              apiVersioning: {
                title: "API Versioning Basics",
                explanation:
                  "APIs evolve over time. Versioning prevents breaking existing clients.",
                commonPatterns: [
                  "/api/v1/users",
                  "/api/v2/users",
                  "Header-based versioning",
                ],
                realWorldNote:
                  "Large companies never ship breaking API changes without versioning",
              },

              commonMistakes: [
                {
                  mistake: "Calling APIs directly inside components",
                  issue: "Tight coupling & duplication",
                  fix: "Use a service layer",
                },
                {
                  mistake: "Ignoring error states",
                  issue: "Broken UX in production",
                  fix: "Always design fallback UI",
                },
                {
                  mistake: "Over-caching data",
                  issue: "Stale UI",
                  fix: "Use revalidation strategies",
                },
              ],

              interviewQuestions: [
                "How do you structure API calls in frontend?",
                "How do you handle loading and error states?",
                "What is caching and why is it important?",
                "Difference between pagination and infinite scroll?",
                "How do you handle API changes safely?",
              ],
            },

            tasks: [
              "Design API service folder structure",
              "Create reusable API functions",
              "Implement loading and error handling",
              "Plan pagination or infinite scroll",
            ],

            examples: [
              "Axios service wrapper",
              "React Query data flow",
              "Centralized API layer",
            ],

            useCases: [
              "Production apps",
              "API-heavy frontend systems",
              "Scalable architecture",
              "System design interviews",
            ],

            editorType: "javascript",

            estimatedTime: "2–3 hours",

            objectives: [
              "Design clean and scalable frontend data layer",
              "Separate API logic from UI components",
              "Handle loading, error, and caching correctly",
              "Build production-ready API integration",
              "Confidently explain data layer design in interviews",
            ],
          },
          {
            day: 5,
            topic: "Folder Structure & Code Organization",

            subTopics: [
              "Feature-based vs layer-based structure",
              "Shared vs feature folders",
              "Naming conventions",
              "Scalable folder patterns",
              "Avoiding deep nesting",
            ],

            explanation:
              "Clean folder structure directly impacts developer productivity, code readability, scalability, and long-term maintenance of frontend applications.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Real Project Folder Refactor Exercise",
                description:
                  "Ek existing React/Next.js project ko feature-based folder structure me refactor karo.",
                tasks: [
                  "Layer-based folders (components, services, utils) identify karo",
                  "Features/domains define karo (auth, cart, dashboard, etc.)",
                  "Related files ko same feature folder me move karo",
                  "Shared components aur hooks ko separate shared folder me rakho",
                ],
                outcome:
                  "Codebase zyada readable, scalable aur team-friendly ho jayega.",
              },
              {
                id: "pt-2",
                title: "Scalable Folder Structure Design (Interview Style)",
                description:
                  "Interview scenario ke liye ek medium–large app ka folder structure design karo.",
                tasks: [
                  "Features, shared aur core folders define karo",
                  "Naming conventions document karo",
                  "Deep nesting ko consciously avoid karo",
                  "Explain karo why this structure scales",
                ],
                outcome:
                  "System design aur take-home assignments me strong impression padega.",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Feature-based thinking",
                "Clear shared vs feature separation",
                "Naming consistency",
                "Scalability awareness",
              ],
              strongAnswer:
                "Main feature-based structure follow karta hoon jisse related files ek jagah rahein aur features independent scale ho sakein.",
              redFlags: [
                "Everything inside components folder",
                "Very deep nested paths",
                "Inconsistent file naming",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Feature-level testing",
                "Shared components isolation",
                "Easier mocking of services",
                "Reduced brittle imports",
              ],
              validationChecks: [
                "Kya ek feature ko independently test kiya ja sakta hai?",
                "Kya shared components reusable aur isolated hain?",
                "Kya imports readable aur short hain?",
              ],
              realWorldNote:
                "Achhi folder structure testing ko naturally simple bana deti hai.",
            },

            editorGuidance: {
              careerMindset: [
                "Folder structure team ke liye hoti hai, sirf tumhare liye nahi",
                "Readable imports = readable architecture",
                "Structure grow hone ke saath evolve honi chahiye",
              ],
              seniorAdvice:
                "Agar naya developer 1 din me code samajh le, to structure sahi hai.",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world projects, bad folder structure slows teams down more than bad code. A well-organized codebase helps developers onboard faster, debug confidently, and scale features without chaos.",

              structureTypes: {
                title: "Feature-based vs Layer-based Structure",
                explanation:
                  "There are two common ways to organize frontend code: by technical layers or by business features.",
                layerBased: {
                  description:
                    "Code is grouped by type (components, services, utils, styles).",
                  downside: "Related files are spread across many folders",
                  example: `
/components
/services
/utils
/pages
        `,
                },
                featureBased: {
                  description: "Code is grouped by feature or domain.",
                  benefit: "Everything related to one feature lives together",
                  example: `
/features/cart
  CartPage.jsx
  cartSlice.js
  cartService.js
        `,
                },
                bestPractice:
                  "Feature-based structure scales better for medium to large apps",
              },

              sharedVsFeature: {
                title: "Shared vs Feature Folders",
                explanation:
                  "Not all code belongs to a feature. Some code is reused across the entire app.",
                sharedExamples: [
                  "UI components (Button, Modal)",
                  "Hooks (useAuth, useTheme)",
                  "Utilities (date, currency)",
                ],
                featureExamples: [
                  "Auth pages and logic",
                  "Cart state and UI",
                  "Dashboard-specific components",
                ],
                ruleOfThumb:
                  "If a file is used by more than one feature → move it to shared",
              },

              namingConventions: {
                title: "Naming Conventions",
                explanation:
                  "Consistent naming reduces cognitive load and prevents confusion.",
                bestPractices: [
                  "Components: PascalCase (UserCard.jsx)",
                  "Hooks: camelCase with use prefix (useAuth.js)",
                  "Utilities: camelCase (formatDate.js)",
                  "Folders: lowercase or kebab-case",
                ],
                interviewInsight:
                  "Interviewers often check naming consistency in take-home assignments",
              },

              scalablePatterns: {
                title: "Scalable Folder Patterns",
                explanation:
                  "As apps grow, structure must support scale without deep nesting.",
                recommendedPattern: `
/features
  /auth
  /cart
  /dashboard
/shared
  /components
  /hooks
  /utils
/core
  /config
  /constants
      `,
                benefit:
                  "Clear boundaries between features, shared logic, and core config",
              },

              deepNesting: {
                title: "Avoiding Deep Nesting",
                explanation:
                  "Deeply nested folders increase import complexity and reduce readability.",
                badExample: `
/features/auth/components/forms/inputs/text/EmailInput.jsx
      `,
                improvedExample: `
/features/auth/EmailInput.jsx
      `,
                bestPractice:
                  "If path becomes hard to read → flatten structure",
              },

              commonMistakes: [
                {
                  mistake: "Over-engineering structure early",
                  issue: "Unnecessary complexity",
                  fix: "Start simple, refactor as app grows",
                },
                {
                  mistake: "Mixing shared and feature code",
                  issue: "Tight coupling",
                  fix: "Define clear boundaries",
                },
                {
                  mistake: "Inconsistent naming",
                  issue: "Confusion and bugs",
                  fix: "Follow conventions strictly",
                },
              ],

              interviewQuestions: [
                "Feature-based vs layer-based structure?",
                "How do you organize a scalable React app?",
                "What goes into shared vs feature folders?",
                "How do you refactor messy codebases?",
              ],
            },

            tasks: [
              "Compare feature-based and layer-based structures",
              "Design folder structure for capstone project",
              "Refactor an existing messy project",
              "Apply naming conventions consistently",
            ],

            examples: [
              "Feature-based React folder structure",
              "Domain-driven frontend structure",
              "Scalable app architecture",
            ],

            useCases: [
              "Team projects",
              "Long-term maintenance",
              "Production codebases",
              "System design interviews",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Design clean and scalable frontend folder structures",
              "Understand feature-driven architecture",
              "Avoid common structural mistakes",
              "Improve team productivity through organization",
              "Explain folder structure decisions confidently in interviews",
            ],
          },
          {
            day: 6,
            topic: "Scalability & Performance Thinking",

            subTopics: [
              "Code splitting strategies",
              "Lazy loading routes and components",
              "Handling large lists",
              "Frontend caching strategies",
              "Micro-frontend basics",
            ],

            explanation:
              "Scalability is about designing frontend systems that continue to perform well as users, data, and features grow—without major rewrites.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Lazy Loading & Code Splitting Exercise",
                description:
                  "Ek medium-sized React/Next.js app me route-based aur feature-based code splitting implement karo.",
                tasks: [
                  "Identify heavy components and routes",
                  "Convert them to React.lazy / dynamic imports",
                  "Test fallback loaders",
                  "Measure performance improvements",
                ],
                outcome:
                  "App load time improve hoga, and user experience smooth rahega",
              },
              {
                id: "pt-2",
                title: "Large List Handling & Virtualization",
                description:
                  "Ek page me 10k+ items render karne ki simulation aur optimization karo.",
                tasks: [
                  "Implement react-window or react-virtualized",
                  "Paginate data if needed",
                  "Test scrolling performance",
                  "Check memory usage and responsiveness",
                ],
                outcome:
                  "Rendering of huge lists fast aur memory-efficient ho jayega",
              },
              {
                id: "pt-3",
                title: "Caching Strategy Implementation",
                description:
                  "API-heavy frontend app me caching strategies design aur implement karo.",
                tasks: [
                  "Implement React Query or SWR",
                  "Decide in-memory vs localStorage cache",
                  "Handle stale data and revalidation",
                  "Document caching decisions",
                ],
                outcome:
                  "Network requests optimized, perceived performance improve hoga",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Performance-aware thinking",
                "Lazy loading / code splitting knowledge",
                "Handling large datasets efficiently",
                "Cache strategy understanding",
                "Micro-frontend awareness",
              ],
              strongAnswer:
                "Main large apps ke liye route-based code splitting aur virtualization apply karta hoon, caching ke liye React Query use karta hoon, aur micro-frontends tab consider karta hoon jab multiple teams independent kaam kar rahi ho",
              redFlags: [
                "Everything loaded upfront",
                "Rendering all list items directly",
                "No caching strategy",
                "Not aware of performance bottlenecks",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Performance measurement after lazy loading",
                "Scroll and render efficiency for large lists",
                "Cache hit/miss behavior",
                "API revalidation correctness",
              ],
              validationChecks: [
                "Kya initial load fast hai?",
                "Kya scrolling smooth hai?",
                "Kya stale data handle ho raha hai?",
                "Kya performance gains measurable hain?",
              ],
              realWorldNote:
                "Performance testing and monitoring critical hai for high-traffic apps",
            },

            editorGuidance: {
              careerMindset: [
                "Optimization is for real bottlenecks, not premature",
                "Lazy loading + virtualization = scalable UX",
                "Micro-frontends consider only when team/app size demands it",
              ],
              seniorAdvice:
                "Interview me ye explain karna zaroori hai ki optimization decisions kyu liye aur kya trade-offs hain",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Most frontend apps start small but fail when traffic, features, or teams grow. Scalability thinking means anticipating growth and making architectural decisions early to avoid performance bottlenecks and rewrites later.",

              codeSplitting: {
                title: "Code Splitting Strategies",
                explanation:
                  "Code splitting breaks large bundles into smaller chunks that load only when needed.",
                strategies: [
                  "Route-based code splitting",
                  "Feature-based code splitting",
                  "Vendor chunk separation",
                ],
                exampleLogic: `
const Dashboard = React.lazy(() => import('./Dashboard'));
      `,
                realWorldNote:
                  "Large apps rarely load everything on initial page load",
                bestPractice: "Split by user journey, not by file size alone",
              },

              lazyLoading: {
                title: "Lazy Loading Routes & Components",
                explanation:
                  "Lazy loading delays loading of non-critical UI until the user actually needs it.",
                useCases: [
                  "Admin dashboards",
                  "Settings pages",
                  "Heavy charts and reports",
                ],
                exampleLogic: `
<Suspense fallback={<Loader />}>
  <Dashboard />
</Suspense>
      `,
                interviewInsight:
                  "Lazy loading is one of the easiest performance wins in React",
              },

              largeLists: {
                title: "Handling Large Lists",
                explanation:
                  "Rendering thousands of DOM nodes at once causes slow rendering and poor UX.",
                techniques: [
                  "Pagination",
                  "Virtualization (windowing)",
                  "Incremental rendering",
                ],
                tools: ["react-window", "react-virtualized"],
                exampleLogic: `
<List height={400} itemCount={10000} itemSize={35}>
      `,
                bestPractice: "Never render what the user cannot see",
              },

              cachingStrategies: {
                title: "Frontend Caching Strategies",
                explanation:
                  "Caching avoids unnecessary network requests and improves perceived performance.",
                types: [
                  "In-memory caching",
                  "LocalStorage / IndexedDB",
                  "HTTP cache headers",
                ],
                tools: ["React Query", "SWR"],
                realWorldNote:
                  "Server state caching is different from UI state",
                bestPractice: "Cache API data, not derived UI state",
              },

              microFrontends: {
                title: "Micro-Frontend Basics",
                explanation:
                  "Micro-frontends split large frontend apps into independently deployable modules.",
                whenToUse: [
                  "Multiple teams working independently",
                  "Very large enterprise apps",
                  "Different tech stacks per feature",
                ],
                approaches: [
                  "Module Federation",
                  "iframe-based",
                  "Build-time composition",
                ],
                interviewInsight:
                  "Micro-frontends are architectural solutions, not performance fixes",
              },

              commonMistakes: [
                {
                  mistake: "Premature optimization",
                  issue: "Unnecessary complexity",
                  fix: "Optimize only real bottlenecks",
                },
                {
                  mistake: "Loading entire app upfront",
                  issue: "Slow initial load",
                  fix: "Apply route-based code splitting",
                },
                {
                  mistake: "Rendering huge lists directly",
                  issue: "UI freezes",
                  fix: "Use virtualization or pagination",
                },
              ],

              interviewQuestions: [
                "How do you scale a frontend application?",
                "What is code splitting and why is it important?",
                "How do you handle large lists efficiently?",
                "What are micro-frontends and when would you use them?",
              ],
            },

            tasks: [
              "Identify scalability bottlenecks in an existing app",
              "Apply lazy loading to routes or components",
              "Design strategy for handling large datasets",
              "Plan caching approach for API-heavy frontend",
            ],

            examples: [
              "Lazy loaded dashboard modules",
              "Virtualized list implementation",
              "Micro-frontend architecture overview",
            ],

            useCases: [
              "Enterprise applications",
              "High-traffic products",
              "Long-lived frontend systems",
              "Senior frontend roles",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Think about frontend scalability before problems arise",
              "Apply performance-first architectural decisions",
              "Handle large data and user growth confidently",
              "Understand trade-offs of advanced frontend patterns",
              "Answer senior-level scalability questions in interviews",
            ],
          },
          {
            day: 7,
            topic: "Week 33 Project – Capstone Architecture Design",

            subTopics: [
              "Architecture documentation",
              "Component hierarchy planning",
              "State and data flow diagrams",
              "Tech stack justification",
              "Trade-off analysis",
            ],

            explanation:
              "This planning phase sets the foundation for your final capstone project by defining how the application will be structured, scaled, and maintained in a real-world production environment.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Capstone Architecture README",
                description:
                  "Create a detailed architecture document for your chosen capstone project, explaining components, state, and data flow.",
                tasks: [
                  "High-level system overview",
                  "UI, state, and data layer diagrams",
                  "Routing strategy explanation",
                  "Integration points documentation",
                ],
                outcome:
                  "Clear architecture documentation that can be used in interviews and guides implementation",
              },
              {
                id: "pt-2",
                title: "Component Hierarchy & Folder Structure",
                description:
                  "Design a complete component hierarchy and feature-based folder structure for your capstone app.",
                tasks: [
                  "Atomic design hierarchy planning",
                  "Container vs presentational components mapping",
                  "Shared components placement",
                  "Pages and features folder design",
                ],
                outcome:
                  "Well-structured component tree and folder organization ready for implementation",
              },
              {
                id: "pt-3",
                title: "State & Data Flow Diagrams",
                description:
                  "Create detailed diagrams showing local, global, and server state flows, including async API interactions.",
                tasks: [
                  "Map local vs global state",
                  "Design server-to-client data flow",
                  "Illustrate async fetch and update patterns",
                  "Document state ownership and responsibility",
                ],
                outcome:
                  "Prevents state-related bugs and guides scalable frontend implementation",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "System-level thinking",
                "Component & state design clarity",
                "Trade-off reasoning",
                "Folder structure and organization awareness",
                "Tech stack justification",
              ],
              strongAnswer:
                "Main atomic design follow karta hoon, state ko local or global wisely divide karta hoon, Redux/Context/Zustand ko project need ke hisaab se choose karta hoon, aur trade-offs ko document karta hoon",
              redFlags: [
                "Jumping straight to coding without plan",
                "No folder structure or component hierarchy",
                "Unclear state ownership",
                "No justification for tech choices",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Validate component hierarchy covers all screens",
                "Ensure state flow diagram matches implementation plan",
                "Check folder structure for scalability",
                "Review README clarity and completeness",
              ],
              validationChecks: [
                "Can someone else implement based on diagrams?",
                "Are all features and data flows mapped?",
                "Is component reuse and separation clear?",
              ],
              realWorldNote:
                "Planning upfront prevents major rewrites and improves interview confidence",
            },

            editorGuidance: {
              careerMindset: [
                "Think like a senior frontend engineer before coding",
                "Document all decisions with pros/cons",
                "Design architecture that can scale with features and team size",
              ],
              seniorAdvice:
                "Interviewers value your design reasoning, trade-offs, and clarity more than final code at this stage",
            },
            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Strong frontend engineers don’t start coding immediately. They first design the architecture. This capstone planning phase mirrors how real teams design systems before writing production code.",

              projectSelection: {
                title: "Capstone Project Selection",
                explanation:
                  "Choosing the right capstone idea is critical—it should be complex enough to show depth but focused enough to finish.",
                criteria: [
                  "Multiple features and routes",
                  "Realistic data flow",
                  "Auth or role-based behavior",
                  "Scalability potential",
                ],
                exampleIdeas: [
                  "SaaS dashboard",
                  "E-commerce platform",
                  "Learning management system",
                  "Project management tool",
                ],
                bestPractice:
                  "Pick a project that solves a real problem, not a toy app",
              },

              architectureDocumentation: {
                title: "Architecture Documentation",
                explanation:
                  "Architecture documentation explains how different parts of the frontend system interact.",
                includes: [
                  "High-level system overview",
                  "UI, state, and data layers",
                  "Routing strategy",
                  "Integration points",
                ],
                deliverable: "Architecture section in README or Notion doc",
                interviewInsight:
                  "Clear architecture docs signal senior-level thinking",
              },

              componentHierarchy: {
                title: "Component Hierarchy Planning",
                explanation:
                  "Planning component hierarchy prevents bloated components and messy props.",
                conceptsUsed: [
                  "Atomic design",
                  "Feature-based grouping",
                  "Container vs presentational components",
                ],
                exampleStructure: [
                  "pages/",
                  "features/",
                  "components/",
                  "layouts/",
                  "shared/",
                ],
                bestPractice: "Design hierarchy before writing JSX",
              },

              stateAndDataFlow: {
                title: "State & Data Flow Diagrams",
                explanation:
                  "State flow diagrams clarify where data lives, how it updates, and who consumes it.",
                covers: [
                  "Local vs global state",
                  "Server state vs UI state",
                  "Async data flow",
                ],
                exampleFlows: [
                  "Auth → API → Global store → UI",
                  "User action → dispatch → reducer → UI update",
                ],
                realWorldNote: "Most bugs come from unclear state ownership",
              },

              techStackJustification: {
                title: "Tech Stack Justification",
                explanation:
                  "Every technology choice should have a clear reason.",
                decisions: [
                  "Why React / Next.js",
                  "Why Redux / Context / Zustand",
                  "Why CSS strategy",
                  "Why API layer approach",
                ],
                interviewInsight:
                  "Interviewers often ask: ‘Why did you choose this stack?’",
                bestPractice: "Avoid ‘because it’s popular’ as justification",
              },

              tradeOffAnalysis: {
                title: "Trade-off Analysis",
                explanation: "Every architectural decision has pros and cons.",
                examples: [
                  {
                    decision: "Redux vs Context",
                    pros: "Predictable state, DevTools",
                    cons: "More boilerplate",
                  },
                  {
                    decision: "SSR vs CSR",
                    pros: "SEO, faster first paint",
                    cons: "Server complexity",
                  },
                ],
                realWorldNote:
                  "Senior engineers are judged by trade-offs, not perfection",
              },

              commonMistakes: [
                {
                  mistake: "Starting coding without planning",
                  issue: "Architecture rewrites later",
                  fix: "Always design first",
                },
                {
                  mistake: "Overengineering",
                  issue: "Complexity without need",
                  fix: "Match architecture to problem size",
                },
                {
                  mistake: "No documentation",
                  issue: "Hard to explain project",
                  fix: "Write README alongside design",
                },
              ],

              interviewQuestions: [
                "How did you design your frontend architecture?",
                "How do components communicate in your app?",
                "Where does state live and why?",
                "What trade-offs did you consider?",
              ],
            },

            tasks: [
              "Choose a realistic capstone project idea",
              "Create high-level frontend architecture diagram",
              "Plan component hierarchy and folder structure",
              "Design state and data flow",
              "Document architecture decisions in README",
            ],

            examples: [
              "E-commerce frontend architecture",
              "SaaS dashboard architecture",
            ],

            useCases: [
              "System design interviews",
              "Final portfolio project",
              "Real-world engineering work",
              "Senior frontend role preparation",
            ],

            editorType: null,

            estimatedTime: "3–4 hours",

            objectives: [
              "Think like a senior frontend engineer",
              "Design scalable frontend architecture",
              "Communicate technical decisions clearly",
              "Prepare for system design interviews",
              "Lay a strong foundation for final capstone build",
            ],
          },
        ],
      },
      {
        week: 34,
        weekGoal:
          "Build, polish, and deploy a production-ready capstone project",
        weekOutcome:
          "A complete real-world application ready for portfolio, interviews, and job applications",
        miniProject: "Final Capstone Project (Production Grade)",
        canLearn: ["students", "beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Project Finalization & MVP Planning",

            subTopics: [
              "Problem statement definition",
              "Target users and use cases",
              "Core features vs optional features",
              "MVP scope finalization",
              "Tech stack confirmation",
            ],

            explanation:
              "This day focuses on freezing the project idea and clearly defining what will be built as the MVP, ensuring focus, speed, and clarity—just like real startup and product teams do.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "One-Page MVP Planning Document",
                description:
                  "Apne capstone project ke liye ek single-page MVP planning document banao jo startup pitch jaisa ho.",
                tasks: [
                  "Problem statement ko 3–4 lines me compress karo",
                  "Primary user persona define karo",
                  "Core workflow diagram banao (simple steps)",
                  "Optional features consciously reject karo",
                ],
                outcome:
                  "Clear, interview-ready MVP planning document ready ho jayega",
              },
              {
                id: "pt-2",
                title: "Feature Cut Decision Exercise",
                description:
                  "Intentionally kuch features cut karke scope discipline practice karo.",
                tasks: [
                  "At least 5 features likho jo tempting hain",
                  "Unme se sirf 2–3 ko core me rakho",
                  "Har rejected feature ka reason likho",
                ],
                outcome: "Scope control aur product thinking strong hogi",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear problem articulation",
                "User-first thinking",
                "Scope discipline",
                "MVP mindset",
              ],
              strongAnswer:
                "Maine MVP ko intentionally small rakha, sirf core user problem solve karne wale features include kiye aur baaki features future iterations ke liye defer kiye.",
              redFlags: [
                "Too many features in MVP",
                "Unclear target user",
                "Tech stack without justification",
                "Problem statement vague hona",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Problem statement clarity",
                "MVP scope realism",
                "Feature-to-problem alignment",
              ],
              validationChecks: [
                "Kya project 2–3 weeks me complete ho sakta hai?",
                "Kya har core feature directly problem solve karta hai?",
                "Kya user persona clearly defined hai?",
              ],
              realWorldNote:
                "Early-stage products me testing se pehle clarity test fail hoti hai, code baad me fail hota hai",
            },

            editorGuidance: {
              careerMindset: [
                "Great engineers scope kam karte hain, badhate nahi",
                "MVP ka goal perfection nahi, learning hota hai",
                "Problem clarity > tech brilliance",
              ],
              seniorAdvice:
                "Interviewers zyada impressed hote hain jab tum batao ki kya nahi banaya aur kyun",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Most projects fail not because of poor coding, but because of unclear scope. This day mirrors how real-world teams finalize an MVP before writing a single line of production code.",

              problemStatement: {
                title: "Problem Statement Definition",
                explanation:
                  "A strong problem statement clearly defines what problem you are solving, for whom, and why it matters.",
                structure: [
                  "Who is facing the problem?",
                  "What exact problem are they facing?",
                  "Why existing solutions are insufficient",
                ],
                example:
                  "Small teams struggle to track tasks across tools, leading to missed deadlines and poor visibility.",
                interviewInsight:
                  "Interviewers often ask: ‘What problem does your project solve?’",
              },

              targetUsers: {
                title: "Target Users & Use Cases",
                explanation:
                  "Defining users helps shape features, UX decisions, and priorities.",
                userTypes: [
                  "Primary users (core users)",
                  "Secondary users (edge cases)",
                ],
                exampleUsers: [
                  "Startup founders",
                  "Team leads",
                  "Individual contributors",
                ],
                useCaseExamples: [
                  "User logs in and creates a project",
                  "User assigns tasks and tracks progress",
                ],
                bestPractice: "Design for one clear user persona first",
              },

              featurePrioritization: {
                title: "Core Features vs Optional Features",
                explanation:
                  "Not all features are equal. MVP focuses on core value, not completeness.",
                coreFeatures: [
                  "Authentication",
                  "Primary workflow",
                  "Basic data persistence",
                ],
                optionalFeatures: [
                  "Notifications",
                  "Advanced filters",
                  "Analytics",
                ],
                framework: "Must-have vs Nice-to-have",
                realWorldNote:
                  "Most startups ship with fewer features than planned",
              },

              mvpScope: {
                title: "MVP Scope Finalization",
                explanation:
                  "MVP defines the smallest usable version that delivers real value.",
                rules: [
                  "Must solve the core problem",
                  "Must be demoable",
                  "Must be finishable in limited time",
                ],
                exampleScope: [
                  "User auth (basic)",
                  "Create, read, update core entity",
                  "Simple UI without over-polish",
                ],
                bestPractice:
                  "If MVP feels uncomfortable small, it’s probably right",
              },

              techStackConfirmation: {
                title: "Tech Stack Confirmation",
                explanation:
                  "Now is the time to lock the tech stack—changing it mid-project causes delays.",
                decisions: [
                  "Framework (React / Next.js)",
                  "State management",
                  "Styling approach",
                  "API strategy",
                ],
                justificationRule: "Choose boring, proven tools for MVP",
                interviewInsight:
                  "Being able to justify your stack shows engineering maturity",
              },

              commonMistakes: [
                {
                  mistake: "Overloading MVP with features",
                  issue: "Project never finishes",
                  fix: "Aggressively cut scope",
                },
                {
                  mistake: "Vague problem statement",
                  issue: "Unclear direction",
                  fix: "Write problem in one paragraph",
                },
                {
                  mistake: "Changing stack mid-way",
                  issue: "Wasted effort",
                  fix: "Lock stack on Day 1",
                },
              ],

              interviewQuestions: [
                "How did you decide the MVP scope?",
                "Who are the target users of your project?",
                "What features did you intentionally exclude?",
                "Why did you choose this tech stack?",
              ],
            },

            tasks: [
              "Finalize capstone project idea",
              "Write a clear problem statement",
              "Define target users and key use cases",
              "List core vs optional features",
              "Freeze MVP scope and tech stack",
            ],

            examples: [
              "E-commerce MVP feature list",
              "Project management tool scope definition",
            ],

            useCases: [
              "Product thinking",
              "Startup-style development",
              "Interview project discussion",
              "Capstone project execution",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Avoid scope creep in capstone project",
              "Think like a product-focused frontend engineer",
              "Define a realistic and achievable MVP",
              "Communicate project goals clearly",
              "Lay a solid foundation for execution phase",
            ],
          },
          {
            day: 2,
            topic: "Sprint Planning & Git Workflow",

            subTopics: [
              "Agile sprints basics",
              "Task breakdown into stories",
              "Git branching strategy",
              "Commit conventions",
              "Project board setup",
            ],

            explanation:
              "This day simulates how real software teams plan work and collaborate using Agile sprints and Git workflows, ensuring clarity, accountability, and clean code history.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Sprint Planning Simulation",
                description:
                  "Apne capstone project ke liye real-world style sprint planning simulate karo.",
                tasks: [
                  "Project features ko 1-week sprint me divide karo",
                  "Sprint goal define karo (1–2 lines)",
                  "Stories ko Todo / In Progress / Done me map karo",
                  "Estimate effort (Low / Medium / High)",
                ],
                outcome:
                  "Tumhe sprint-based planning aur execution ka real feel milega",
              },
              {
                id: "pt-2",
                title: "Professional Git Workflow Setup",
                description:
                  "Industry-level Git branching aur commit discipline practice karo.",
                tasks: [
                  "main, dev, aur multiple feature branches banao",
                  "Har feature ke liye separate branch use karo",
                  "At least 10 clean, meaningful commits likho",
                  "dev se main me final merge simulate karo",
                ],
                outcome: "Clean Git history aur team-ready workflow ban jayega",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Sprint-based planning mindset",
                "Task breakdown ability",
                "Branching strategy clarity",
                "Commit discipline",
              ],
              strongAnswer:
                "Main kaam start karne se pehle tasks ko sprint me todta hoon, feature branches use karta hoon aur har logical change ke liye clean commits maintain karta hoon.",
              redFlags: [
                "Direct commits on main branch",
                "No planning before coding",
                "Large, unclear commits",
                "No experience with boards or task tracking",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Task granularity",
                "Branch isolation correctness",
                "Commit history readability",
                "Merge stability",
              ],
              validationChecks: [
                "Kya tasks small aur achievable hain?",
                "Kya har feature branch isolated hai?",
                "Kya commits readable aur logical hain?",
                "Kya merge ke baad app stable hai?",
              ],
              realWorldNote:
                "Teams often reject PRs due to poor commit history, not just bad code",
            },

            editorGuidance: {
              careerMindset: [
                "Great engineers plan before they code",
                "Git history is your professional footprint",
                "Small commits reduce bugs and stress",
              ],
              seniorAdvice:
                "Interview me Git aur planning experience confidently explain karna coding se zyada impact dalta hai",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In professional environments, writing code is only part of the job. Planning work, managing tasks, and maintaining a clean Git history are equally important. This day mirrors real startup and company workflows.",

              agileSprints: {
                title: "Agile Sprints Basics",
                explanation:
                  "A sprint is a short, fixed period (usually 1–2 weeks) where a team commits to completing a set of tasks.",
                keyConcepts: [
                  "Sprint goal",
                  "Sprint backlog",
                  "Incremental delivery",
                ],
                realWorldFlow: "Plan → Build → Review → Improve",
                bestPractice:
                  "For personal projects, treat each week as one sprint",
              },

              taskBreakdown: {
                title: "Task Breakdown into Stories",
                explanation:
                  "Large features must be broken into small, trackable tasks or user stories.",
                storyFormat: "As a user, I want to ___ so that ___",
                exampleStories: [
                  "As a user, I want to sign up so that I can access my dashboard",
                  "As a user, I want to create a task so that I can track my work",
                ],
                bestPractice:
                  "Each task should be completable in a few hours, not days",
              },

              gitWorkflow: {
                title: "Git Branching Strategy",
                explanation:
                  "Branching keeps work isolated, prevents bugs in production, and enables parallel development.",
                branches: {
                  main: "Production-ready stable code",
                  dev: "Integration branch for features",
                  feature: "Individual feature development",
                },
                exampleFlow: "feature/login → dev → main",
                interviewInsight:
                  "Interviewers often ask about your Git workflow experience",
              },

              commitConventions: {
                title: "Commit Conventions",
                explanation:
                  "Clear commit messages make code history readable and maintainable.",
                format: "type: short meaningful message",
                commonTypes: [
                  "feat: new feature",
                  "fix: bug fix",
                  "chore: setup or config",
                  "refactor: code improvement",
                ],
                exampleCommits: [
                  "feat: add user authentication flow",
                  "fix: prevent crash on empty input",
                ],
                bestPractice: "One commit = one logical change",
              },

              projectBoard: {
                title: "Project Board Setup",
                explanation:
                  "Project boards visualize progress and help track what’s pending, ongoing, or completed.",
                columns: ["Backlog", "Todo", "In Progress", "Review", "Done"],
                tools: ["GitHub Projects", "Notion", "Trello"],
                realWorldNote:
                  "Even solo developers use boards to stay organized",
              },

              commonMistakes: [
                {
                  mistake: "Working directly on main branch",
                  issue: "Risk of breaking production code",
                  fix: "Always use feature branches",
                },
                {
                  mistake: "Huge commits",
                  issue: "Hard to review and debug",
                  fix: "Commit small, logical changes",
                },
                {
                  mistake: "No task tracking",
                  issue: "Lost focus and missed scope",
                  fix: "Use a simple project board",
                },
              ],

              interviewQuestions: [
                "What Git workflow do you follow?",
                "How do you plan your tasks before coding?",
                "What makes a good commit message?",
                "Have you used Agile or sprints before?",
              ],
            },

            tasks: [
              "Break capstone project into sprint-sized tasks",
              "Create GitHub repository",
              "Setup main, dev, and feature branches",
              "Create project board with tasks",
              "Write clean, meaningful commits",
            ],

            examples: ["GitHub project board", "Feature branch workflow"],

            useCases: [
              "Team collaboration",
              "Industry-standard workflows",
              "Clean Git history",
              "Professional project execution",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Plan development work like a professional engineer",
              "Learn Agile-inspired sprint planning",
              "Use Git branches confidently",
              "Maintain clean and readable commit history",
              "Prepare project for smooth execution phase",
            ],
          },
          {
            day: 3,
            topic: "Core Feature Development (CRUD + Auth)",

            subTopics: [
              "Authentication flow",
              "Protected routes",
              "Create, Read, Update, Delete operations",
              "Form validation",
              "Error handling",
            ],

            explanation:
              "This day focuses on building the backbone of your application — authentication and CRUD features that reflect real-world, production-level frontend development.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Grade Auth Flow Implementation",
                description:
                  "Authentication ko sirf login/signup tak limited na rakho, balki production-level flow implement karo.",
                tasks: [
                  "Auth state ko refresh ke baad persist karo",
                  "Logout functionality with token cleanup add karo",
                  "Protected routes ke liye reusable guard component banao",
                  "Unauthorized access ke edge cases handle karo",
                ],
                outcome:
                  "Real-world ready authentication system jise interviews me confidently explain kar sako",
              },
              {
                id: "pt-2",
                title: "CRUD + UX Polish Exercise",
                description:
                  "CRUD operations ko professional UX ke saath implement karo.",
                tasks: [
                  "Create/Edit forms me validation errors dikhana",
                  "Delete action ke liye confirmation flow add karo",
                  "API failure ke case me user-friendly messages dikhana",
                  "Optimistic UI update (optional) try karo",
                ],
                outcome:
                  "CRUD system jo sirf functional nahi balki production-quality UX deta hai",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear understanding of auth flow",
                "Protected route implementation",
                "CRUD lifecycle clarity",
                "Error and loading state awareness",
              ],
              strongAnswer:
                "Maine authentication ke liye token-based flow use kiya, protected routes guard ke through secure kiye, aur CRUD operations ke saath proper validation, loading aur error handling implement ki.",
              redFlags: [
                "Auth logic scattered across components",
                "No protected routes",
                "No error handling",
                "Forms without validation",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Auth persistence on refresh",
                "Protected route access control",
                "CRUD happy + failure paths",
                "Form validation behavior",
              ],
              validationChecks: [
                "Kya login ke baad refresh pe user logged-in rehta hai?",
                "Kya unauthenticated user protected page access nahi kar sakta?",
                "Kya CRUD actions ke baad UI correctly update hoti hai?",
                "Kya errors clear aur understandable hain?",
              ],
              realWorldNote:
                "Production bugs ka major reason missing edge-case testing hota hai, not missing features",
            },

            editorGuidance: {
              careerMindset: [
                "Auth + CRUD mastery separates juniors from professionals",
                "UX polish matters as much as functionality",
                "Error handling is part of core logic, not an afterthought",
              ],
              seniorAdvice:
                "Interview me sirf feature nahi, edge cases aur failures kaise handle kiye ye zaroor explain karo",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "Most real applications revolve around authenticated users performing CRUD operations. This day simulates how actual products like dashboards, task managers, blogs, and admin panels are built.",

              authenticationFlow: {
                title: "Authentication Flow",
                explanation:
                  "Authentication ensures that only valid users can access protected parts of the application.",
                conceptsUsed: [
                  "Login & signup flow",
                  "Token-based authentication",
                  "Auth state management",
                ],
                typicalFlow:
                  "Signup/Login → Store token → Fetch user → Access protected routes",
                exampleLogic: `
login(email, password)
  → receive token
  → store token
  → redirect to dashboard
      `,
                realWorldNote:
                  "In production, auth is usually handled via JWT or session cookies",
              },

              protectedRoutes: {
                title: "Protected Routes",
                explanation:
                  "Protected routes restrict access to certain pages unless the user is authenticated.",
                conceptsUsed: [
                  "Auth context or global state",
                  "Conditional rendering",
                  "Redirect unauthenticated users",
                ],
                exampleLogic: `
return isAuth ? <Dashboard /> : <Navigate to="/login" />;
      `,
                interviewInsight:
                  "This is a very common interview question for React developers",
              },

              crudOperations: {
                title: "CRUD Operations",
                explanation:
                  "CRUD represents Create, Read, Update, and Delete — the core operations of most apps.",
                operations: {
                  create: "Create new item (task, post, product)",
                  read: "Fetch and display list or details",
                  update: "Edit existing item",
                  delete: "Remove item safely",
                },
                conceptsUsed: [
                  "API integration",
                  "State updates",
                  "Optimistic UI updates (optional)",
                ],
                exampleLogic: `
POST /items
GET /items
PUT /items/:id
DELETE /items/:id
      `,
                realWorldExamples: [
                  "Blog posts",
                  "Tasks in a task manager",
                  "Products in admin panel",
                ],
              },

              formValidation: {
                title: "Form Validation",
                explanation:
                  "Forms must validate input before sending data to the server to prevent errors and bad data.",
                conceptsUsed: [
                  "Controlled inputs",
                  "Client-side validation",
                  "Error messages",
                ],
                exampleRules: [
                  "Required fields",
                  "Email format validation",
                  "Password length checks",
                ],
                exampleLogic: `
if (!email.includes('@')) {
  setError('Invalid email');
}
      `,
                bestPractice:
                  "Validate on both frontend and backend in real apps",
              },

              errorHandling: {
                title: "Error Handling & Loading States",
                explanation:
                  "Production apps must gracefully handle failures like network issues or server errors.",
                conceptsUsed: [
                  "Loading indicators",
                  "Try/catch blocks",
                  "User-friendly error messages",
                ],
                exampleLogic: `
try {
  await apiCall();
} catch (err) {
  setError(err.message);
}
      `,
                realWorldNote:
                  "Good error handling separates beginner apps from professional ones",
              },

              commonMistakes: [
                {
                  mistake: "Skipping auth state handling",
                  issue: "App breaks on refresh",
                  fix: "Persist auth state safely",
                },
                {
                  mistake: "No loading states",
                  issue: "Poor user experience",
                  fix: "Always show loading indicators",
                },
                {
                  mistake: "Monolithic components",
                  issue: "Hard to maintain",
                  fix: "Split logic into reusable components",
                },
              ],

              interviewQuestions: [
                "How does authentication work in your project?",
                "How do you protect routes in React?",
                "Explain CRUD operations with an example",
                "How do you handle API errors in UI?",
              ],
            },

            tasks: [
              "Implement authentication flow (login/signup)",
              "Setup protected routes",
              "Build CRUD screens (create, list, edit, delete)",
              "Add form validation",
              "Handle loading and error states",
              "Test main user flows manually",
            ],

            examples: ["Blog CRUD system", "Task manager with authentication"],

            useCases: [
              "Real production apps",
              "Interview project walkthrough",
              "Full-stack frontend experience",
            ],

            editorType: "react",

            estimatedTime: "4–6 hours",

            objectives: [
              "Build real-world authentication flow",
              "Implement full CRUD functionality",
              "Handle edge cases and errors professionally",
              "Demonstrate production-level frontend skills",
              "Prepare strong talking points for interviews",
            ],
          },
          {
            day: 4,
            topic: "UI Polish, Responsiveness & UX",

            subTopics: [
              "Responsive layouts",
              "Mobile-first fixes",
              "Empty states and edge cases",
              "Accessibility improvements",
              "Design consistency",
            ],

            explanation:
              "A polished UI separates average projects from impressive ones. This day focuses on refining the user experience so the app feels production-ready on all devices.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Responsive UI Audit & Fixes",
                description:
                  "Application ka complete responsive audit karo aur real-device issues fix karo.",
                tasks: [
                  "Desktop, tablet, aur mobile breakpoints identify karo",
                  "At least 5 UI issues document karo (overflow, spacing, alignment)",
                  "Mobile-specific fixes apply karo",
                  "Before/after screenshots compare karo",
                ],
                outcome:
                  "App har screen size pe polished aur professional feel karega",
              },
              {
                id: "pt-2",
                title: "UX Polish & Accessibility Pass",
                description:
                  "UI ko sirf good-looking nahi, balki usable aur accessible banao.",
                tasks: [
                  "Empty states aur first-time user states add karo",
                  "Forms ke liye proper labels aur error messages ensure karo",
                  "Keyboard navigation test karo",
                  "Color contrast aur focus states verify karo",
                ],
                outcome:
                  "Accessible, user-friendly UI jo interviews aur real users dono ko impress kare",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Attention to UI/UX details",
                "Mobile responsiveness awareness",
                "Accessibility understanding",
                "User-centric thinking",
              ],
              strongAnswer:
                "Maine app ko mobile, tablet aur desktop sab pe test kiya, empty states aur accessibility improvements add kiye, aur design consistency ensure ki taaki product production-ready lage.",
              redFlags: [
                "Only desktop-focused UI",
                "No empty or loading states",
                "Ignoring accessibility",
                "Inconsistent spacing and styles",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Responsive behavior across breakpoints",
                "Empty state rendering",
                "Keyboard and screen-reader usability",
                "Visual consistency",
              ],
              validationChecks: [
                "Kya UI mobile pe comfortably usable hai?",
                "Kya zero-data cases clear guidance dete hain?",
                "Kya app bina mouse ke navigate ho sakta hai?",
                "Kya spacing aur typography consistent hai?",
              ],
              realWorldNote:
                "UI bugs often surface only on real devices, not just browser dev tools",
            },

            editorGuidance: {
              careerMindset: [
                "Great UI is invisible but impactful",
                "Users remember how smooth an app feels, not how complex it is",
                "Accessibility is a skill, not an optional feature",
              ],
              seniorAdvice:
                "Interview me batao ki tumne UX issues kaise notice kiye aur kaise improve kiye — ye strong impression deta hai",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real companies, functionality alone is not enough. Users judge products by how smooth, responsive, and intuitive they feel. This day upgrades your app from 'working' to 'professional'.",

              responsiveLayouts: {
                title: "Responsive Layouts",
                explanation:
                  "Responsive layouts ensure your application works seamlessly across mobile, tablet, and desktop screens.",
                conceptsUsed: [
                  "Flexbox and Grid",
                  "Responsive breakpoints",
                  "Fluid layouts",
                ],
                exampleApproach:
                  "Design desktop-first → adjust for tablet → refine for mobile",
                exampleLogic: `
@media (max-width: 768px) {
  .layout {
    flex-direction: column;
  }
}
      `,
                realWorldNote:
                  "Most recruiters check mobile responsiveness instantly",
              },

              mobileFirstFixes: {
                title: "Mobile-First Fixes",
                explanation:
                  "Mobile-first thinking ensures essential features work well on small screens before scaling up.",
                conceptsUsed: [
                  "Touch-friendly buttons",
                  "Readable font sizes",
                  "Avoiding horizontal scroll",
                ],
                checklist: [
                  "Buttons are tappable",
                  "Forms are easy to fill",
                  "No overflow issues",
                ],
                interviewInsight:
                  "Mobile usability is a strong signal of frontend maturity",
              },

              emptyStates: {
                title: "Empty States & Edge Cases",
                explanation:
                  "Empty states guide users when there is no data instead of showing blank screens.",
                conceptsUsed: [
                  "Conditional rendering",
                  "User-friendly messages",
                  "Visual feedback",
                ],
                exampleScenarios: [
                  "No items in list",
                  "No search results",
                  "First-time user state",
                ],
                exampleLogic: `
if (items.length === 0) {
  return <EmptyState message="No items found" />;
}
      `,
                bestPractice: "Always tell users what to do next",
              },

              accessibility: {
                title: "Accessibility Improvements",
                explanation:
                  "Accessible apps ensure everyone, including users with disabilities, can use your product.",
                conceptsUsed: [
                  "Semantic HTML",
                  "ARIA labels",
                  "Keyboard navigation",
                  "Color contrast",
                ],
                exampleFixes: [
                  "Use <label> with inputs",
                  "Add aria-label to buttons",
                  "Ensure focus styles",
                ],
                realWorldNote:
                  "Accessibility is increasingly evaluated in interviews and audits",
              },

              designConsistency: {
                title: "Design Consistency",
                explanation:
                  "Consistent design builds trust and improves usability across the application.",
                conceptsUsed: [
                  "Consistent spacing",
                  "Reusable components",
                  "Unified color palette",
                  "Typography scale",
                ],
                exampleApproach:
                  "Create a small design system (colors, spacing, font sizes)",
                bestPractice:
                  "Consistency matters more than creativity in production apps",
              },

              commonMistakes: [
                {
                  mistake: "Ignoring mobile view",
                  issue: "Poor user experience",
                  fix: "Test mobile early and often",
                },
                {
                  mistake: "No empty states",
                  issue: "Confusing blank screens",
                  fix: "Always handle zero-data cases",
                },
                {
                  mistake: "Inconsistent spacing",
                  issue: "Unprofessional look",
                  fix: "Use design tokens or spacing scale",
                },
              ],

              interviewQuestions: [
                "How do you make your app responsive?",
                "What are empty states and why are they important?",
                "How do you improve accessibility in React apps?",
                "What UI details do recruiters notice first?",
              ],
            },

            tasks: [
              "Fix mobile and tablet responsiveness",
              "Add loading skeletons and empty states",
              "Improve accessibility (labels, keyboard navigation)",
              "Refine spacing, typography, and alignment",
              "Test UI across different screen sizes",
            ],

            examples: ["Responsive dashboard", "Accessible forms and buttons"],

            useCases: [
              "Portfolio impression",
              "User experience",
              "Frontend craftsmanship",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Make the application responsive across devices",
              "Improve user experience with polished UI",
              "Handle edge cases gracefully",
              "Apply accessibility best practices",
              "Upgrade project to production-quality standards",
            ],
          },
          {
            day: 5,
            topic: "Testing, Error Monitoring & Analytics",

            subTopics: [
              "Unit and component testing",
              "Testing user flows",
              "Error monitoring (Sentry basics)",
              "Analytics integration",
              "Performance checks",
            ],

            explanation:
              "Production-ready apps are observable, testable, and reliable. This day focuses on ensuring your application behaves correctly, errors are tracked, and user behavior is measurable.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Critical User Flow Testing Suite",
                description:
                  "Application ke sabse important user flows ke liye reliable test coverage banao.",
                tasks: [
                  "Login → dashboard flow test likho",
                  "CRUD happy path test karo",
                  "API failure case ke tests add karo",
                  "Async loaders aur error states verify karo",
                ],
                outcome:
                  "Confidence milega ki core flows regressions ke bina kaam kar rahe hain",
              },
              {
                id: "pt-2",
                title: "Production Observability Setup",
                description:
                  "Testing ke saath monitoring aur analytics ko production-ready banao.",
                tasks: [
                  "Global error boundary setup karo",
                  "At least 3 intentional errors simulate karo",
                  "Key analytics events define aur track karo",
                  "Performance impact validate karo",
                ],
                outcome:
                  "App observable ho jayega — bugs silently fail nahi honge",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Testing strategy clarity",
                "User-flow based thinking",
                "Production error awareness",
                "Analytics-driven mindset",
              ],
              strongAnswer:
                "Main critical user flows ke tests likhta hoon, production me error monitoring lagata hoon aur analytics ke through user behavior measure karta hoon taaki issues aur improvements quickly identify ho sake.",
              redFlags: [
                "Only happy-path testing",
                "No production error tracking",
                "Analytics without clear purpose",
                "Fear of writing tests",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Critical path coverage",
                "Error boundary behavior",
                "Analytics event accuracy",
                "Performance after integrations",
              ],
              validationChecks: [
                "Kya important flows automated tests se covered hain?",
                "Kya runtime errors capture ho rahe hain?",
                "Kya analytics events meaningful insights dete hain?",
                "Kya performance degrade to nahi hui?",
              ],
              realWorldNote:
                "Teams testing ko bug prevention ke liye nahi, confidence aur speed ke liye use karti hain",
            },

            editorGuidance: {
              careerMindset: [
                "Untested code is a risk, not a shortcut",
                "Observability is part of product quality",
                "Analytics help justify engineering decisions",
              ],
              seniorAdvice:
                "Interview me batao ki tumne kya test nahi kiya aur kyun — ye maturity ka strong signal hota hai",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real-world products, bugs are inevitable — but untracked bugs are unacceptable. Testing ensures correctness, monitoring catches failures early, and analytics helps teams understand how users interact with the product.",

              testingFundamentals: {
                title: "Unit & Component Testing",
                explanation:
                  "Testing verifies that individual pieces of your application work as expected and continue to work after changes.",
                conceptsUsed: [
                  "Unit tests for logic",
                  "Component tests for UI",
                  "Behavior-driven testing",
                ],
                exampleLogic: `
test('renders login button', () => {
  render(<Login />);
  expect(screen.getByText('Login')).toBeInTheDocument();
});
      `,
                bestPractice: "Test critical paths, not every line of code",
              },

              userFlowTesting: {
                title: "Testing User Flows",
                explanation:
                  "User flow tests ensure that complete interactions work correctly from start to finish.",
                conceptsUsed: [
                  "Form submission testing",
                  "Navigation testing",
                  "Async behavior testing",
                ],
                exampleScenarios: [
                  "Login → dashboard",
                  "Create → edit → delete item",
                  "Error handling on failed API",
                ],
                interviewInsight:
                  "Interviewers value flow testing more than trivial unit tests",
              },

              errorMonitoring: {
                title: "Error Monitoring (Sentry Basics)",
                explanation:
                  "Error monitoring tools capture runtime errors in production and provide stack traces and context.",
                conceptsUsed: [
                  "Error boundaries",
                  "Runtime error tracking",
                  "Environment-based logging",
                ],
                exampleLogic: `
class ErrorBoundary extends React.Component {
  componentDidCatch(error, info) {
    logError(error, info);
  }
}
      `,
                realWorldNote:
                  "Most companies rely on Sentry, Bugsnag, or similar tools",
              },

              analyticsIntegration: {
                title: "Analytics Integration",
                explanation:
                  "Analytics helps teams understand user behavior, feature usage, and drop-off points.",
                conceptsUsed: [
                  "Page view tracking",
                  "Event-based analytics",
                  "User journey analysis",
                ],
                exampleEvents: [
                  "Button clicks",
                  "Form submissions",
                  "Feature usage events",
                ],
                bestPractice:
                  "Track actions that reflect business value, not everything",
              },

              performanceChecks: {
                title: "Performance Checks",
                explanation:
                  "Performance checks ensure that testing and monitoring don’t introduce slowdowns.",
                conceptsUsed: [
                  "Lighthouse performance audits",
                  "Bundle size awareness",
                  "Runtime performance validation",
                ],
                checklist: [
                  "No unnecessary re-renders",
                  "No blocking errors in console",
                  "Stable performance after analytics",
                ],
                realWorldNote:
                  "Performance regressions are often caught via monitoring tools",
              },

              commonMistakes: [
                {
                  mistake: "Over-testing trivial components",
                  issue: "High maintenance cost",
                  fix: "Focus on business-critical logic",
                },
                {
                  mistake: "No error monitoring in production",
                  issue: "Silent failures",
                  fix: "Always add error tracking early",
                },
                {
                  mistake: "Tracking meaningless analytics",
                  issue: "Noise without insight",
                  fix: "Track user intent, not clicks only",
                },
              ],

              interviewQuestions: [
                "What do you test in frontend applications?",
                "Difference between unit tests and user flow tests?",
                "How do you handle runtime errors in production?",
                "What analytics events would you track in a product?",
              ],
            },

            tasks: [
              "Write tests for critical components and flows",
              "Add error boundary to application",
              "Integrate basic analytics tracking",
              "Simulate and handle edge cases",
              "Verify performance after integrations",
            ],

            examples: [
              "React Testing Library tests",
              "Error boundary implementation",
            ],

            useCases: [
              "Production reliability",
              "Senior-level practices",
              "Confidence during interviews",
            ],

            editorType: "react",

            estimatedTime: "3–4 hours",

            objectives: [
              "Ensure application correctness with testing",
              "Detect and monitor production errors",
              "Understand user behavior through analytics",
              "Prevent silent failures in production",
              "Demonstrate senior-level frontend practices",
            ],
          },
          {
            day: 6,
            topic: "Deployment & CI/CD Basics",

            subTopics: [
              "Environment variables",
              "Production build",
              "Deployment platforms (Vercel/Netlify)",
              "CI/CD overview",
              "Domain and preview deployments",
            ],

            explanation:
              "Deploying your project proves you can take software from local development to real-world production environments, which is a must-have skill for frontend developers.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Production-Ready Deployment Setup",
                description:
                  "Project ko real-world standards ke according deploy karo with proper environment handling.",
                tasks: [
                  "Development aur production environment variables separate karo",
                  "Production build locally run karke verify karo",
                  "GitHub repo ko Vercel/Netlify se connect karo",
                  "Preview deployment URL generate aur test karo",
                ],
                outcome:
                  "Fully functional, secure aur shareable production deployment ready hoga",
              },
              {
                id: "pt-2",
                title: "Basic CI/CD Pipeline Simulation",
                description:
                  "Simple CI/CD flow samjho aur simulate karo jaise real teams karti hain.",
                tasks: [
                  "GitHub push → auto build trigger verify karo",
                  "Failing build ka scenario simulate karo",
                  "Fix ke baad redeploy observe karo",
                  "Deployment logs aur errors analyze karo",
                ],
                outcome:
                  "Deployment failures ko confidently debug aur resolve kar paoge",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Deployment confidence",
                "Environment handling awareness",
                "CI/CD basic understanding",
                "Production mindset",
              ],
              strongAnswer:
                "Main production build ko locally test karta hoon, environment variables securely manage karta hoon aur CI/CD ke through automated deployments use karta hoon taaki releases consistent aur safe rahein.",
              redFlags: [
                "Deploying without production build testing",
                "Secrets hardcoded in frontend",
                "Only manual deployments",
                "No understanding of CI/CD",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Production build correctness",
                "Environment variable resolution",
                "Deployment logs and errors",
                "Live app smoke testing",
              ],
              validationChecks: [
                "Kya production build bina errors ke run ho rahi hai?",
                "Kya correct environment variables load ho rahe hain?",
                "Kya live app expected behavior dikha rahi hai?",
                "Kya redeploy ke baad issues resolved ho gaye?",
              ],
              realWorldNote:
                "Production issues aksar environment mismatch ki wajah se aate hain, code ki wajah se nahi",
            },

            editorGuidance: {
              careerMindset: [
                "Shipping code is as important as writing code",
                "Deployment confidence builds engineering trust",
                "Automation reduces human errors",
              ],
              seniorAdvice:
                "Interview me end-to-end ownership highlight karo — from local setup to live production",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In professional environments, writing code is only half the job. Developers are expected to ship features reliably, manage environments, and ensure deployments don’t break production.",

              environmentVariables: {
                title: "Environment Variables",
                explanation:
                  "Environment variables store sensitive or environment-specific values like API keys and base URLs.",
                conceptsUsed: [
                  "Development vs production variables",
                  "Build-time vs runtime variables",
                  "Security considerations",
                ],
                exampleLogic: `
VITE_API_URL=https://api.example.com
NEXT_PUBLIC_API_URL=https://api.example.com
      `,
                bestPractice: "Never commit secrets directly to GitHub",
              },

              productionBuild: {
                title: "Production Build Process",
                explanation:
                  "Production builds optimize your code by minifying assets, removing dev-only code, and improving performance.",
                conceptsUsed: [
                  "Build optimization",
                  "Tree shaking",
                  "Minification",
                ],
                exampleCommands: ["npm run build", "npm run preview"],
                realWorldNote:
                  "Always test production build locally before deploying",
              },

              deploymentPlatforms: {
                title: "Deployment Platforms (Vercel / Netlify)",
                explanation:
                  "Modern frontend apps are commonly deployed on platforms that offer CDN, HTTPS, and preview deployments.",
                platforms: {
                  vercel: [
                    "Best for React & Next.js",
                    "Automatic deployments from GitHub",
                    "Preview URLs for every PR",
                  ],
                  netlify: [
                    "Great for static sites",
                    "Simple CI/CD pipelines",
                    "Built-in form handling",
                  ],
                },
                interviewInsight:
                  "Interviewers often ask how you deploy and manage frontend apps",
              },

              cicdOverview: {
                title: "CI/CD Overview",
                explanation:
                  "CI/CD automates testing, building, and deployment to reduce human error.",
                conceptsUsed: [
                  "Continuous Integration (CI)",
                  "Continuous Deployment (CD)",
                  "Automated pipelines",
                ],
                simpleFlow: [
                  "Push code to GitHub",
                  "Run tests automatically",
                  "Build production bundle",
                  "Deploy to hosting platform",
                ],
                realWorldNote:
                  "Even small teams rely heavily on CI/CD pipelines",
              },

              domainAndPreviews: {
                title: "Domain & Preview Deployments",
                explanation:
                  "Preview deployments allow teams to review changes before they go live, while custom domains improve professionalism.",
                conceptsUsed: [
                  "Preview URLs per commit/PR",
                  "Custom domain setup",
                  "HTTPS configuration",
                ],
                benefits: [
                  "Safer deployments",
                  "Easy stakeholder reviews",
                  "Professional portfolio presence",
                ],
              },

              commonMistakes: [
                {
                  mistake: "Deploying without testing production build",
                  issue: "App breaks in production",
                  fix: "Always run and test build locally",
                },
                {
                  mistake: "Exposing secrets in frontend",
                  issue: "Security risk",
                  fix: "Use environment variables properly",
                },
                {
                  mistake: "Manual deployments only",
                  issue: "Inconsistent releases",
                  fix: "Rely on CI/CD automation",
                },
              ],

              interviewQuestions: [
                "How do you deploy a React application?",
                "Difference between development and production builds?",
                "What are environment variables and why are they important?",
                "Explain CI/CD in simple terms",
                "How do preview deployments help teams?",
              ],
            },

            tasks: [
              "Deploy project to a production platform",
              "Configure environment variables securely",
              "Test production build locally",
              "Verify live application behavior",
              "Fix any deployment or build issues",
            ],

            examples: ["Vercel deployment", "Production environment setup"],

            useCases: [
              "Live portfolio projects",
              "Client-ready applications",
              "End-to-end development proof",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Understand full frontend deployment lifecycle",
              "Safely manage environment-specific configuration",
              "Deploy applications confidently to production",
              "Gain familiarity with CI/CD concepts",
              "Demonstrate end-to-end frontend ownership",
            ],
          },
          {
            day: 7,
            topic: "Documentation, Presentation & Wrap-Up",

            subTopics: [
              "Professional README writing",
              "Architecture explanation",
              "Feature walkthrough",
              "Challenges & learnings",
              "Future improvements",
            ],

            explanation:
              "Your ability to explain, document, and present your project clearly is critical for interviews, portfolio reviews, and real-world collaboration.",
            premiumTasks: [
              {
                id: "pt-1",
                title: "Interview-Ready Project Documentation",
                description:
                  "Project ko interview aur portfolio ke liye professionally present karne ke liye documentation polish karo.",
                tasks: [
                  "README ko problem → solution → outcome flow me rewrite karo",
                  "Architecture section me key decisions aur trade-offs likho",
                  "Screenshots + live demo link add karo",
                  "Future improvements clearly list karo",
                ],
                outcome:
                  "Interview-ready, recruiter-friendly documentation jo project ko standout banaye",
              },
              {
                id: "pt-2",
                title: "Project Walkthrough & Storytelling Practice",
                description:
                  "Apne project ko story ki tarah explain karne ki practice karo.",
                tasks: [
                  "2–3 minute ka verbal walkthrough prepare karo",
                  "Non-technical explanation likho (layman version)",
                  "Technical deep-dive points identify karo",
                  "Common interview questions ke answers rehearse karo",
                ],
                outcome:
                  "Confident project explanation jo interviews me strong impression de",
              },
            ],

            interviewInsights: {
              expectedSignals: [
                "Clear communication skills",
                "Architecture reasoning",
                "Honest reflection on challenges",
                "Future-thinking mindset",
              ],
              strongAnswer:
                "Maine project ko MVP ke saath build kiya, architecture decisions scalability aur maintainability ke liye liye, challenges ko document kiya aur future improvements clearly define kiye.",
              redFlags: [
                "Unable to explain own project",
                "No documentation or README",
                "No challenges mentioned",
                "No future roadmap thinking",
              ],
            },

            testingInsights: {
              focusAreas: [
                "Documentation completeness",
                "Explanation clarity",
                "Architecture consistency",
                "Claim vs implementation match",
              ],
              validationChecks: [
                "Kya README project ko bina code dekhe samjha deta hai?",
                "Kya architecture explanation logical aur honest hai?",
                "Kya features aur screenshots match karte hain?",
                "Kya future improvements realistic hain?",
              ],
              realWorldNote:
                "Many interview rejections happen due to poor explanation, not poor code",
            },

            editorGuidance: {
              careerMindset: [
                "Code likhna skill hai, explain karna seniority hai",
                "Great engineers document decisions, not just features",
                "Storytelling makes your work memorable",
              ],
              seniorAdvice:
                "Interview me project ko ek journey ki tarah explain karo — problem se solution tak",
            },

            // 📚 Detailed Learning Content
            content: {
              introduction:
                "In real companies, well-documented projects are easier to maintain, review, and scale. Documentation shows your communication skills, product thinking, and ownership beyond just writing code.",

              readmeWriting: {
                title: "Professional README Writing",
                explanation:
                  "A README is often the first thing recruiters, reviewers, or teammates see. It should explain what the project is, why it exists, and how to use it.",
                sectionsToInclude: [
                  "Project overview",
                  "Problem statement",
                  "Features",
                  "Tech stack",
                  "Setup instructions",
                  "Live demo link",
                ],
                exampleStructure: `
# Project Name
## Overview
## Features
## Tech Stack
## Setup
## Live Demo
      `,
                bestPractice:
                  "Keep README concise but informative — clarity matters more than length",
              },

              architectureExplanation: {
                title: "Architecture Explanation",
                explanation:
                  "Explaining architecture shows how you think about scalability, separation of concerns, and maintainability.",
                conceptsToExplain: [
                  "Component hierarchy",
                  "State management decisions",
                  "API/data flow",
                  "Folder structure",
                ],
                realWorldNote:
                  "Interviewers often ask: 'Why did you design it this way?'",
              },

              featureWalkthrough: {
                title: "Feature Walkthrough",
                explanation:
                  "A feature walkthrough explains how a user interacts with the app from start to finish.",
                walkthroughSteps: [
                  "User entry point",
                  "Main user actions",
                  "State changes",
                  "Error and edge cases",
                ],
                example: "Login → Dashboard → Create item → Update → Logout",
                interviewInsight:
                  "Clear walkthroughs show strong product and UX understanding",
              },

              challengesAndLearnings: {
                title: "Challenges & Learnings",
                explanation:
                  "Every real project has challenges. Explaining them honestly builds credibility.",
                whatToInclude: [
                  "Technical challenges faced",
                  "Trade-offs made",
                  "What you would do differently",
                ],
                example:
                  "Managing async state was challenging, solved using proper loading and error handling",
                bestPractice:
                  "Never say 'no challenges' — growth always involves problems",
              },

              futureImprovements: {
                title: "Future Improvements",
                explanation:
                  "Future improvements show long-term thinking and awareness of limitations.",
                examples: [
                  "Performance optimization",
                  "Better error handling",
                  "Additional features",
                  "Scalability improvements",
                ],
                realWorldNote:
                  "This helps interviewers assess how you think beyond MVP",
              },

              commonMistakes: [
                {
                  mistake: "No README or poor documentation",
                  issue: "Project looks unfinished",
                  fix: "Always write at least a basic professional README",
                },
                {
                  mistake: "Over-explaining code line-by-line",
                  issue: "Loses big-picture clarity",
                  fix: "Explain architecture and decisions instead",
                },
                {
                  mistake: "Ignoring challenges",
                  issue: "Appears inexperienced",
                  fix: "Share learnings honestly",
                },
              ],

              interviewQuestions: [
                "How would you explain your project to a non-technical person?",
                "Why did you choose this architecture?",
                "What were the biggest challenges?",
                "What would you improve if given more time?",
              ],
            },

            tasks: [
              "Write a detailed and professional README",
              "Add screenshots and live demo link",
              "Prepare a clear project explanation",
              "Practice explaining architecture and decisions",
              "Optionally record a short demo video",
            ],

            examples: [
              "Well-documented GitHub project",
              "Case-study style README",
            ],

            useCases: [
              "Interviews",
              "Portfolio reviews",
              "Client or recruiter evaluation",
            ],

            editorType: null,

            estimatedTime: "2–3 hours",

            objectives: [
              "Communicate project clearly and confidently",
              "Demonstrate ownership beyond coding",
              "Prepare strong interview explanations",
              "Showcase professional documentation skills",
              "Finish project with production-level polish",
            ],
          },
        ],
      },
    ],
    questions: [
      {
        id: "c1",
        question:
          "Explain your approach to debugging a complex issue in production.",
        difficulty: "Medium",
        topics: ["Debugging", "Problem Solving"],
      },
      {
        id: "c2",
        question: "How do you ensure code quality in your projects?",
        difficulty: "Medium",
        topics: ["Code Quality", "Best Practices"],
      },
      {
        id: "c3",
        question: "What is Git and explain common Git commands you use daily?",
        difficulty: "Easy",
        topics: ["Version Control", "Git"],
      },
      {
        id: "c4",
        question:
          "Explain Git branching strategies (Git Flow, trunk-based development).",
        difficulty: "Medium",
        topics: ["Git", "Workflow"],
      },
      {
        id: "c5",
        question: "What is CI/CD and why is it important?",
        difficulty: "Medium",
        topics: ["CI/CD", "DevOps"],
      },
      {
        id: "c6",
        question:
          "How do you approach learning new technologies or frameworks?",
        difficulty: "Easy",
        topics: ["Learning", "Professional Development"],
      },
      {
        id: "c7",
        question:
          "Describe a challenging project you worked on and how you overcame obstacles.",
        difficulty: "Medium",
        topics: ["Project Experience", "Problem Solving"],
      },
      {
        id: "c8",
        question:
          "How do you handle code reviews? What do you look for when reviewing code?",
        difficulty: "Medium",
        topics: ["Code Review", "Collaboration"],
      },
      {
        id: "c9",
        question: "Explain your understanding of Agile/Scrum methodologies.",
        difficulty: "Easy",
        topics: ["Agile", "Project Management"],
      },
      {
        id: "c10",
        question:
          "How do you prioritize tasks when working on multiple features?",
        difficulty: "Medium",
        topics: ["Time Management", "Prioritization"],
      },
      {
        id: "c11",
        question:
          "What security considerations do you keep in mind when developing frontend applications?",
        difficulty: "Hard",
        topics: ["Security", "Best Practices"],
      },
      {
        id: "c12",
        question: "Explain XSS and CSRF attacks. How do you prevent them?",
        difficulty: "Hard",
        topics: ["Security"],
      },
      {
        id: "c13",
        question:
          "How do you stay updated with the latest frontend technologies and trends?",
        difficulty: "Easy",
        topics: ["Professional Development"],
      },
      {
        id: "c14",
        question:
          "Describe your experience working in a team. How do you handle conflicts?",
        difficulty: "Medium",
        topics: ["Teamwork", "Communication"],
      },
      {
        id: "c15",
        question:
          "Walk me through how you would optimize a slow-loading web page.",
        difficulty: "Hard",
        topics: ["Performance", "Debugging"],
      },
      {
        id: "c16",
        question: "What is semantic versioning (semver)?",
        difficulty: "Easy",
        topics: ["Version Control"],
      },
      {
        id: "c17",
        question: "How do you resolve merge conflicts in Git?",
        difficulty: "Medium",
        topics: ["Git", "Merge Conflicts"],
      },
      {
        id: "c18",
        question: "What is the difference between git merge and git rebase?",
        difficulty: "Medium",
        topics: ["Git"],
      },
      {
        id: "c19",
        question: "Explain git cherry-pick and when you would use it.",
        difficulty: "Medium",
        topics: ["Git"],
      },
      {
        id: "c20",
        question: "What are Git hooks and how can they be useful?",
        difficulty: "Medium",
        topics: ["Git", "Automation"],
      },
      {
        id: "c21",
        question: "How do you write clean, maintainable code?",
        difficulty: "Medium",
        topics: ["Code Quality", "Best Practices"],
      },
      {
        id: "c22",
        question: "What is technical debt and how do you manage it?",
        difficulty: "Medium",
        topics: ["Technical Debt"],
      },
      {
        id: "c23",
        question: "Explain the DRY (Don't Repeat Yourself) principle.",
        difficulty: "Easy",
        topics: ["Best Practices"],
      },
      {
        id: "c24",
        question: "What is KISS (Keep It Simple, Stupid) principle?",
        difficulty: "Easy",
        topics: ["Best Practices"],
      },
      {
        id: "c25",
        question: "How do you document your code effectively?",
        difficulty: "Medium",
        topics: ["Documentation"],
      },
      {
        id: "c26",
        question: "What tools do you use for code linting and formatting?",
        difficulty: "Easy",
        topics: ["Code Quality", "Tools"],
      },
      {
        id: "c27",
        question:
          "Explain the concept of pair programming. Have you practiced it?",
        difficulty: "Easy",
        topics: ["Collaboration"],
      },
      {
        id: "c28",
        question: "How do you estimate time for tasks in development?",
        difficulty: "Medium",
        topics: ["Project Management"],
      },
      {
        id: "c29",
        question: "What is your process for learning a new codebase?",
        difficulty: "Medium",
        topics: ["Learning", "Onboarding"],
      },
      {
        id: "c30",
        question:
          "How do you handle disagreements with team members about technical decisions?",
        difficulty: "Medium",
        topics: ["Communication", "Teamwork"],
      },
      {
        id: "c31",
        question: "What is Content Security Policy (CSP)?",
        difficulty: "Medium",
        topics: ["Security"],
      },
      {
        id: "c32",
        question: "Explain HTTPS and why it's important.",
        difficulty: "Easy",
        topics: ["Security"],
      },
      {
        id: "c33",
        question: "What are JWT tokens and how do you handle them securely?",
        difficulty: "Medium",
        topics: ["Authentication", "Security"],
      },
      {
        id: "c34",
        question: "How do you prevent SQL injection in frontend applications?",
        difficulty: "Medium",
        topics: ["Security"],
      },
      {
        id: "c35",
        question:
          "What is OWASP Top 10 and why should frontend developers know it?",
        difficulty: "Medium",
        topics: ["Security"],
      },
      {
        id: "c36",
        question: "How do you stay productive when working remotely?",
        difficulty: "Easy",
        topics: ["Remote Work"],
      },
      {
        id: "c37",
        question: "Describe your ideal development workflow.",
        difficulty: "Easy",
        topics: ["Workflow"],
      },
      {
        id: "c38",
        question: "How do you handle receiving critical feedback on your code?",
        difficulty: "Medium",
        topics: ["Professional Skills"],
      },
      {
        id: "c39",
        question:
          "What metrics do you use to measure the success of a feature?",
        difficulty: "Medium",
        topics: ["Product", "Metrics"],
      },
      {
        id: "c40",
        question: "How do you balance perfectionism with meeting deadlines?",
        difficulty: "Medium",
        topics: ["Time Management"],
      },
      {
        id: "c41",
        question: "What is your experience with Docker and containerization?",
        difficulty: "Medium",
        topics: ["DevOps", "Docker"],
      },
      {
        id: "c42",
        question:
          "Explain the concept of continuous deployment vs continuous delivery.",
        difficulty: "Medium",
        topics: ["CI/CD"],
      },
      {
        id: "c43",
        question:
          "How do you monitor and log errors in production applications?",
        difficulty: "Medium",
        topics: ["Monitoring", "Debugging"],
      },
      {
        id: "c44",
        question: "What is your approach to refactoring legacy code?",
        difficulty: "Hard",
        topics: ["Refactoring"],
      },
      {
        id: "c45",
        question: "How do you contribute to open source projects?",
        difficulty: "Easy",
        topics: ["Open Source", "Community"],
      },
    ],
  },
];

export const FRONTEND_DEVELOPER_PHASES = [
  {
    key: "foundation",
    phase: 1,
    title: "Foundation Phase - HTML & CSS Fundamentals",
    duration: "6 Weeks",
    level: "Beginner",
  },
  {
    key: "javascript",
    phase: 2,
    title: "JavaScript Fundamentals",
    duration: "8 Weeks",
    level: "Beginner to Intermediate",
  },
  {
    key: "modernJs",
    phase: 3,
    title: "Modern JavaScript & Tools",
    duration: "4 Weeks",
    level: "Intermediate",
  },
  {
    key: "react",
    phase: 4,
    title: "React Fundamentals",
    duration: "6 Weeks",
    level: "Intermediate to Advanced",
  },
  {
    key: "advanced",
    phase: 5,
    title: "Advanced Topics & Specialization",
    duration: "6 Weeks",
    level: "Advanced",
  },
  {
    key: "career",
    phase: 6,
    title: "Job Preparation & Professional Skills",
    duration: "4 Weeks",
    level: "Job Ready",
  },
];
